import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, useDeferredValue, useTransition, memo, startTransition, createContext, useContext, Fragment } from "react";
import Drawer from "@mui/material/Drawer";
import MuiDivider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import AddCircleOutlineIcon from "@mui/icons-material/Add";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from "recharts";
import AdminMuiTable from "./components/AdminMuiTable";
import AdminDataGrid from "./components/AdminDataGrid";
import AuthorityLevels from "./components/admin/AuthorityLevels";
import ApprovalsTab from "./components/admin/ApprovalsTab";
import MeetAiTablePagination from "./components/MeetAiTablePagination";
import MeetAiResponsiveDatePicker from "./components/MeetAiResponsiveDatePicker";
import {
  isMeetDisplayTailJunk,
  meetingTitleFromCatalog,
  normalizeAssistantAnswerLayout,
  repairAssistantMeetTokens,
  stripMeetDisplayArtifacts,
  stripMeetLinkGarbage,
} from "./utils/assistantMeetTokenRepair";
import { ADM } from "./components/adminTheme";
import {
  AdminMetricCard,
  AdminControlBanner,
  AdminListCard,
  AdminMeetingRow,
  AdminSystemRow,
  AdminPageHero,
} from "./components/AdminUi";
import {
  UsagePageHeader,
  UsageKpiCard,
  UsagePeriodChips,
  UsageBillingAlertStatusCard,
  UsageChartSeriesToggles,
  UsagePlatformStatusCard,
  UsageSystemHealth,
  UsageKpiSkeletonRow,
} from "./components/admin/AdminUsageUi";
import { PlatformBrandIcon, MeetingListPlatformIcon, GoogleMeetMark } from "./components/admin/PlatformBrandIcon";
import {
  MeetRedesignHero,
  UpcomingMeetingsLayout,
  UpcomingMeetingsRail,
  UpcomingWeekPanel,
} from "./redesign/MeetRedesign";
import { syncMeetingReminderPrefs, ensureDefaultReminderPushOnLogin, enableMeetingReminderPush, hasLocalPushSubscription, fetchMeetingReminderPrefs, dismissMeetingReminderSoftPrompt, markMeetingRemindersSetup, setupPushPermissionFocusListener, pushSupported } from "./pushReminders";
import { getTranslation } from "./i18n";
import { useIsMobile, useIsTablet } from "./useResponsive";

export const I18nContext = createContext({
  lang: "English",
  t: (k, fallback) => fallback || k,
});

export function useI18n() {
  return useContext(I18nContext);
}

/** API base: explicit baked URL wins (per-environment builds), else same-origin
 *  `/api` on real hosts (nginx proxy), else CRA proxy default for localhost dev. */
function resolveApiBase() {
  const baked = String(process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");
  if (baked) return baked;
  if (typeof window !== "undefined") {
    const h = String(window.location.hostname || "");
    if (h && h !== "localhost" && h !== "127.0.0.1") return "/api";
  }
  return "/api";
}
const API = resolveApiBase();

/**
 * Browser OAuth must hit the same backend that GOOGLE_REDIRECT_URI points at.
 * Explicit baked REACT_APP_BACKEND_URL wins (per-environment builds).
 * Otherwise same-origin on real hosts (nginx proxies /api), :5000 on localhost.
 */
function backendOriginForOAuth() {
  const fromEnv = String(
    process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_ORIGIN || ""
  )
    .trim()
    .replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined") {
    const h = String(window.location.hostname || "");
    if (h && h !== "localhost" && h !== "127.0.0.1") {
      return window.location.origin;
    }
  }
  if (String(API).startsWith("http")) {
    return String(API)
      .replace(/\/api\/?$/, "")
      .replace(/\/$/, "");
  }
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") {
      return "http://localhost:5000";
    }
    return window.location.origin;
  }
  return "http://localhost:5000";
}

/** Sidebar / meet-tab transitions: deferred list + React transition (set REACT_APP_INSTANT_NAV=1 to disable). */
function reactEnvTruthy(name) {
  const v = String(process.env[name] ?? "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}
const SMOOTH_NAV_TRANSITION =
  !reactEnvTruthy("REACT_APP_INSTANT_NAV") &&
  !reactEnvTruthy("REACT_APP_DISABLE_NAV_TRANSITION") &&
  String(process.env.REACT_APP_SMOOTH_NAV_TRANSITION ?? "1").trim().toLowerCase() !== "0";

/** Lowercase trim for email comparisons (must match backend normalization). */
function normAppEmail(e) {
  return String(e || "").trim().toLowerCase();
}

/**
 * Bot / SMTP mailbox shown in How-to, invite copy, admin SMTP hints.
 * Must match server `BOT_EMAIL`. CRA: set `REACT_APP_BOT_EMAIL` at build time.
 */
const BOT_EMAIL_DISPLAY = normAppEmail(process.env.REACT_APP_BOT_EMAIL) || "app.testing@avaada.com";

/**
 * Default “admin contact” email for support copy when `REACT_APP_ADMIN_PORTAL_EMAIL` is unset.
 * Admin UI is opened only via `/admin` (see `canAccessAdminApis`).
 */
const ADMIN_PORTAL_GATE = normAppEmail(process.env.REACT_APP_ADMIN_PORTAL_EMAIL) || BOT_EMAIL_DISPLAY;

/** Comma-separated admin UI allowlist (must match backend `ADMIN_UI_EMAILS` / `Admin_Emails`). */
const RAW_ADMIN_UI_EMAILS =
  process.env.REACT_APP_ADMIN_UI_EMAILS || process.env.REACT_APP_ADMIN_EMAILS || '';

function parseFrontendAdminEmailSet() {
  const set = new Set();
  for (const part of String(RAW_ADMIN_UI_EMAILS || '').split(',')) {
    const e = normAppEmail(part);
    if (e) set.add(e);
  }
  return set;
}

/** Help / support mailto; defaults to bot mailbox. */
const SUPPORT_EMAIL = normAppEmail(process.env.REACT_APP_SUPPORT_EMAIL) || BOT_EMAIL_DISPLAY;

/**
 * Human-readable name for the meeting bot in UI copy (How-to, modals, chat).
 * Must align with backend `BOT_DISPLAY_NAME` / Recall. CRA: `REACT_APP_BOT_DISPLAY_NAME`.
 */
const BOT_DISPLAY_NAME =
  String(process.env.REACT_APP_BOT_DISPLAY_NAME || "").trim() || "Avaada Meet Companion";

/** Floating assistant FAB label (short; full name is BOT_DISPLAY_NAME). */
const COMPANION_FAB_LABEL = "Ask your Companion";

/** User-facing primary action — avoid internal “bot” jargon. */
const RECORD_MEETING_CTA = "Record meeting";
/** Top header CTA only — opens instant meeting modal. */
const HEADER_INSTANT_JOIN_CTA = "Instant join";

/** Companion / usage answers — where to find the full usage dashboard in the app. */
const MEETING_USAGE_UI_HINT =
  "You can also open the full breakdown anytime: click your profile picture (top right) → Meeting usage.";

function ensureMeetingUsageUiHint(answerText, question) {
  const body = String(answerText || "").trim();
  if (!body) return body;
  const q = String(question || "").toLowerCase();
  const usageQuestion =
    /\b(usage|useage|statistics|stats)\b/.test(q) ||
    (/\b(where|find|see|check|view|how)\b/.test(q) &&
      /\b(usage|useage|hours|transcript|recorded|meeting usage)\b/.test(q));
  if (!usageQuestion) return body;
  if (/\bmeeting usage\b/i.test(body) && /\b(profile|top right|user icon)\b/i.test(body)) return body;
  return `${body}\n\n${MEETING_USAGE_UI_HINT}`;
}

/** Strip vendor/internal terms from errors and hints shown in the main app (not admin). */
function userFacingMessage(msg) {
  if (msg == null || msg === "") return msg;
  return String(msg)
    .replace(/\brecall\.?ai\b/gi, "meeting service")
    .replace(/\brecall\b/gi, "meeting service")
    .replace(/\bbot service\b/gi, "recording service")
    .replace(/\bmeeting bot\b/gi, BOT_DISPLAY_NAME)
    .replace(/\bdispatch(?:ed)?\s+the\s+bot\b/gi, "start recording")
    .replace(/\bcould not dispatch the bot\b/gi, "Could not start recording")
    .replace(/\bbot dispatch\b/gi, "recording")
    .replace(/\bbot\b/gi, "assistant");
}

/** Same actors as backend `/api/admin/*`: env allowlist, legacy portal, or DB portal_admins (via /auth/me). */
function canAccessAdminApis(email, adminPortalAccessFlag) {
  if (adminPortalAccessFlag === true) return true;
  const allow = parseFrontendAdminEmailSet();
  if (allow.size > 0) return allow.has(normAppEmail(email));
  const e = normAppEmail(email);
  if (!e) return false;
  if (e === BOT_EMAIL_DISPLAY) return true;
  const portal = normAppEmail(process.env.REACT_APP_ADMIN_PORTAL_EMAIL);
  if (portal && e === portal) return true;
  return false;
}

/** Path-based admin entry (must still be signed in as an allowed admin actor). */
function isAdminAppPath(pathname) {
  return normalizePathname(pathname).toLowerCase() === "/admin";
}

/** Bot / human admin service logins — skip normal user calendar / avatar flows (same set as admin API). */
function isPrivilegedSkipUserDashboard(email, adminPortalAccessFlag) {
  return canAccessAdminApis(email, adminPortalAccessFlag);
}

const PALETTE = {
  navy: "#0C2B4E",
  cold: "#1A3D64",
  sea: "#1D546C",
  grey: "#F4F4F4",
  white: "#FFFFFF",
  /** Avaada brand bar (matches companion FAB / marketing gradient) */
  brandBlue: "#1778D4",
  brandGreen: "#0C8F55",
};

/** Remember sidebar route across refresh (sessionStorage, per tab). */
const MEETAI_USER_PAGE_KEY = "meetai_user_page";
const MEETAI_DETAIL_ID_KEY = "meetai_detail_id";
const MEETAI_DETAIL_TAB_KEY = "meetai_detail_tab";
const MEETAI_DETAIL_FROM_KEY = "meetai_detail_from";
const MEETAI_ADMIN_PAGE_KEY = "meetai_admin_page";
const MEETAI_ADMIN_THEME_KEY = "meetai_admin_theme";
const MEETAI_ADMIN_SIDEBAR_KEY = "meetai_admin_sidebar_collapsed";
const ADMIN_USAGE_POLL_MS = 5 * 60 * 1000;
/** Faster refresh while Usage tab is open so Recall all-time cache can catch up. */
const ADMIN_USAGE_TAB_POLL_MS = 60 * 1000;
const MEETAI_GCAL_CACHE_KEY = "meetai_google_events_cache_v1";
/** User-facing name for the auto-join schedule feature (nav, page title, toggles). */
const JOIN_MEET_AI_LABEL = "Invite MeetAI";
/** Tooltip when Video Rec is available (Auto Join on). */
const VIDEO_REC_ENABLE_HINT =
  "Save a recording when Invite MeetAI runs. Kept for 7 days.";
/** Tooltip when Video Rec is disabled until per-meeting Auto Join is on. */
const VIDEO_REC_NEED_AUTO_JOIN_HINT =
  `Turn on ${JOIN_MEET_AI_LABEL} first to enable Video Rec.`;
/** List-row tip: short so it does not dominate the Upcoming Meetings table. */
const VIDEO_REC_ENABLE_HINT_COMPACT = "Save recording when Invite MeetAI runs (7-day retention).";
const JOIN_MEET_AI_TOGGLE_HINT = `${JOIN_MEET_AI_LABEL} for this meeting`;
/** Tooltip when opening a stored recording from the list or detail. */
const VIDEO_REC_OPEN_HINT =
  "Recorded meeting — saved for 7 days retention. Download to keep longer.";
const ALLOWED_USER_PAGES = new Set(["dashboard", "meetings", "autojoin", "transcripts", "calendar", "actions", "howto", "detail"]);
/** User sidebar Calendar page — set true to show again later. Right-rail calendar stays available. */
const SHOW_USER_CALENDAR_NAV = true;
/** Meeting reminders + browser notification prompt. Hidden for now; set true to restore. */
const SHOW_MEETING_REMINDERS = false;
const RECALL_MEDIA_RETENTION_DAYS_DEFAULT = 2;
const ALLOWED_ADMIN_PAGES = new Set(["overview", "usage", "chatbotUsage", "calendar", "allMeetings", "transcriptsEmail", "users", "access", "summary", "smtp", "logs", "feedback", "authority", "approvals"]);
const USER_PAGE_PATHS = {
  autojoin: "/",
  meetings: "/meetings",
  transcripts: "/transcripts",
  dashboard: "/dashboard",
  calendar: "/calendar",
  actions: "/actions",
  howto: "/how-it-works",
};

const MEET_LIST_PAGES = new Set(["meetings", "autojoin", "transcripts"]);
/** List/poll payload without full transcript rows — detail uses GET /meetings/:id. */
const MEETINGS_LIST_API = "/meetings?lite=1";
const MEETINGS_LIST_POLL_MS = 20000;
const MEETINGS_LIST_POLL_ACTIVE_MS = 15000;
const MEETINGS_LIST_POLL_HIDDEN_MS = 90000;
const MEETINGS_LIST_POLL_DOWN_MS = 45000;

function meetViewFromPage(page) {
  if (page === "autojoin") return "autojoin";
  if (page === "transcripts") return "transcripts";
  return "meetings";
}

function pageFromMeetView(meetView) {
  if (meetView === "autojoin") return "autojoin";
  if (meetView === "transcripts") return "transcripts";
  return "meetings";
}

function normalizePathname(pathname) {
  const raw = String(pathname || "/").trim();
  if (!raw) return "/";
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const cleaned = withSlash.replace(/\/+$/, "");
  return cleaned || "/";
}

function isRecordingSharePath(pathname) {
  return normalizePathname(pathname).toLowerCase() === "/recording";
}

function isSharedPagePath(pathname) {
  return normalizePathname(pathname).toLowerCase() === "/shared";
}

function userPageFromPathname(pathname) {
  const p = normalizePathname(pathname).toLowerCase();
  if (p === "/admin") return null;
  if (p === "/recording") return null;
  if (p === "/shared") return null;
  if (
    p === "/" ||
    p === "/auto-join" ||
    p === "/autojoin" ||
    p === "/upcoming-meetings" ||
    p === "/upcoming"
  ) {
    return "autojoin";
  }
  if (p === "/meetings" || p === "/all-meetings") return "meetings";
  if (p === "/transcripts") return "transcripts";
  if (p === "/dashboard" || p === "/home") return "dashboard";
  if (p === "/calendar") return SHOW_USER_CALENDAR_NAV ? "calendar" : "autojoin";
  if (p === "/actions" || p === "/action-items") return "actions";
  if (p === "/how-it-works" || p === "/howto") return "howto";
  return null;
}

function pathForUserPage(page) {
  return USER_PAGE_PATHS[page] || USER_PAGE_PATHS.autojoin;
}

function readInitialUserPage() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (isRecordingSharePath(window.location.pathname)) return "dashboard";
      // URL with ?m=<meetingId> on /meetings should reopen meeting detail flow, not land on Home.
      if (sp.get("m") && !isRecordingSharePath(window.location.pathname)) return "meetings";
      const fromPath = userPageFromPathname(window.location.pathname);
      if (fromPath) return fromPath;
    }
    const p = sessionStorage.getItem(MEETAI_USER_PAGE_KEY);
    if (p && ALLOWED_USER_PAGES.has(p)) {
      if (p === "calendar" && !SHOW_USER_CALENDAR_NAV) return "autojoin";
      return p;
    }
    try {
      const legacySubTab = window.localStorage?.getItem?.("meetai.meetings.subTab");
      if (legacySubTab === "meetings" || legacySubTab === "autojoin" || legacySubTab === "transcripts") {
        return legacySubTab;
      }
    } catch (_) {}
  } catch (_) {}
  return "autojoin";
}

function readInitialAdminPage() {
  try {
    const p = sessionStorage.getItem(MEETAI_ADMIN_PAGE_KEY);
    if (p && ALLOWED_ADMIN_PAGES.has(p)) return p;
  } catch (_) {}
  return "overview";
}

function clearMeetaiNavSession() {
  try {
    sessionStorage.removeItem(MEETAI_USER_PAGE_KEY);
    sessionStorage.removeItem(MEETAI_DETAIL_ID_KEY);
    sessionStorage.removeItem(MEETAI_DETAIL_TAB_KEY);
    sessionStorage.removeItem(MEETAI_DETAIL_FROM_KEY);
    sessionStorage.removeItem(MEETAI_ADMIN_PAGE_KEY);
  } catch (_) {}
}

function readCachedGoogleEvents(email) {
  try {
    if (!email) return [];
    const raw = localStorage.getItem(MEETAI_GCAL_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || String(parsed.email || "").toLowerCase() !== String(email).toLowerCase()) return [];
    return Array.isArray(parsed.events) ? parsed.events.map(normalizeMeeting) : [];
  } catch (_) {
    return [];
  }
}

function writeCachedGoogleEvents(email, events) {
  try {
    if (!email) return;
    const safe = Array.isArray(events) ? events.map(normalizeMeeting) : [];
    localStorage.setItem(
      MEETAI_GCAL_CACHE_KEY,
      JSON.stringify({ email, events: safe, at: Date.now() })
    );
  } catch (_) {}
}

const APP_LOGO = `${process.env.PUBLIC_URL || ""}/assets/Avaada_Logo-1.png`;
const SIDEBAR_RENEWABLE_ILLUSTRATION = `${process.env.PUBLIC_URL || ""}/assets/pastel_renewable_energy_cityscape.png`;
const LOGIN_RENEWABLE_SKYLINE = `${process.env.PUBLIC_URL || ""}/assets/sustainable_green_city_panorama.png`;
const RECENT_EMPTY_IMG = `${process.env.PUBLIC_URL || ""}/assets/recent-empty.png`;
const GOOGLE_CALENDAR_ICON = `${process.env.PUBLIC_URL || ""}/assets/google-calendar-icon.png`;
const CALENDAR_HERO_ILLUSTRATION = `${process.env.PUBLIC_URL || ""}/assets/minimalist_desk_calendar.png`;
const FRIENDLY_AI_ROBOT_IMG = `${process.env.PUBLIC_URL || ""}/assets/friendly_ai_robot.png`;
/** Recurring Invite MeetAI confirm popup only — uses Friendly AI Robot.png */
const FRIENDLY_AI_ROBOT_RECURRING_IMG = `${process.env.PUBLIC_URL || ""}/assets/friendly_ai_robot_recurring.png`;
const FEEDBACK_ALLOWED_FILE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"]);
const FEEDBACK_MAX_ATTACHMENTS = 5;

/** Profile from OAuth session, or a minimal shape derived from the signed-in email (no hard-coded demo user). */
function userFromSession(loginEmail) {
  try {
    const raw = localStorage.getItem("avaada_user");
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.email) {
        const initials = u.initials || String(u.name || u.email).slice(0, 2).toUpperCase();
        const avatar_url = u.avatar_url || u.avatarUrl || u.picture || u.photo || "";
        return { ...u, initials, avatar_url };
      }
    }
  } catch (_) {}
  if (loginEmail) {
    const local = loginEmail.split("@")[0];
    const name = local.replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return { name, email: loginEmail, initials: loginEmail.slice(0, 2).toUpperCase() };
  }
  return { name: "User", email: "", initials: "?" };
}

/** Header profile subtitle: HR designation when known, else non-generic app role. */
function userHeaderSubtitle(user, designationFromDirectory = "") {
  const dir = String(designationFromDirectory || "").trim();
  if (dir) return dir;
  const direct =
    user?.designation_name ||
    user?.designation ||
    user?.job_title ||
    user?.jobTitle;
  if (direct && String(direct).trim()) return String(direct).trim();
  const role = String(user?.role || "").trim();
  if (role && !/^(user|member)$/i.test(role)) return role;
  return "";
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const [, base64 = ""] = result.split(",");
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Unable to read attachment"));
    reader.readAsDataURL(file);
  });
}

/** Align with backend/utils/actionItemAssignment.js — match action rows to the signed-in user. */
function normAssigneeName(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}
function actionItemMatchesUser(a, user) {
  const ue = String(user?.email || "").trim().toLowerCase();
  const un = normAssigneeName(user?.name);
  const ae = String(a?.email || "").trim().toLowerCase();
  const an = normAssigneeName(a?.assignee);
  if (ue && ae && ae === ue) return true;
  if (!an || an === "unassigned") return false;
  if (un && un === an) return true;
  if (un.length >= 2 && (an.startsWith(`${un} `) || an === un)) return true;
  if (an.length >= 2 && (un.startsWith(`${an} `) || un === an)) return true;
  const ut = un.split(" ").filter(Boolean);
  const at = an.split(" ").filter(Boolean);
  if (ut.length === 1 && ut[0].length >= 2 && at[0] === ut[0]) return true;
  return false;
}

function getToken(){ return localStorage.getItem("avaada_token"); }
function setToken(t){ localStorage.setItem("avaada_token",t); }
/** Cached GET /meetings/usage for dashboard + profile modal (avoid loading flash on tab switch). */
const meetAiUsageSessionCache = { usage: null, error: null, refreshKey: null };
function clearToken() {
  localStorage.removeItem("avaada_token");
  localStorage.removeItem("avaada_user");
  meetAiUsageSessionCache.usage = null;
  meetAiUsageSessionCache.error = null;
  meetAiUsageSessionCache.refreshKey = null;
}

/** Session flag: while set, browser Back must not leave MeetAI for Google OAuth. */
const MEETAI_BLOCK_OAUTH_BACK_KEY = "meetai_block_oauth_back";

function markBlockOauthBack() {
  try {
    sessionStorage.setItem(MEETAI_BLOCK_OAUTH_BACK_KEY, "1");
  } catch (_) {}
}

function clearBlockOauthBack() {
  try {
    sessionStorage.removeItem(MEETAI_BLOCK_OAUTH_BACK_KEY);
  } catch (_) {}
}

function shouldBlockOauthBack() {
  try {
    return sessionStorage.getItem(MEETAI_BLOCK_OAUTH_BACK_KEY) === "1" && !!getToken();
  } catch (_) {
    return !!getToken();
  }
}

/** Keep an extra same-URL history frame so Back cannot step onto accounts.google.com. */
function pushMeetaiHistoryTrap(extraState = {}) {
  if (typeof window === "undefined" || !getToken()) return;
  try {
    const url = `${window.location.pathname}${window.location.search || ""}`;
    window.history.pushState({ meetai: true, trap: true, ...extraState }, "", url);
  } catch (_) {}
}
function getStoredUser() {
  try {
    const raw = localStorage.getItem("avaada_user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/** "Maybe later" on meeting-reminders soft prompt — survives tab switches & re-login. */
const MEETAI_PUSH_PROMPT_DISMISSED_KEY = "meetai_push_prompt_dismissed_at";
const MEETAI_PUSH_PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
/** Set after reminders were successfully enabled on this browser (permission + subscribe). */
const MEETAI_REMINDERS_SETUP_DONE_KEY = "meetai_reminders_setup_done";

function getPushPromptDismissedAt() {
  try {
    const n = Number(localStorage.getItem(MEETAI_PUSH_PROMPT_DISMISSED_KEY) || 0);
    return Number.isFinite(n) ? n : 0;
  } catch (_) {
    return 0;
  }
}

function markPushPromptDismissed() {
  try {
    localStorage.setItem(MEETAI_PUSH_PROMPT_DISMISSED_KEY, String(Date.now()));
  } catch (_) {}
}

function isPushPromptSnoozed(dbDismissedAt = null) {
  const fromDb = dbDismissedAt ? new Date(dbDismissedAt).getTime() : 0;
  const at = Math.max(getPushPromptDismissedAt(), Number.isFinite(fromDb) ? fromDb : 0);
  if (!at) return false;
  return Date.now() - at < MEETAI_PUSH_PROMPT_SNOOZE_MS;
}

function isRemindersSetupDone(dbSetupAt = null) {
  if (dbSetupAt) return true;
  try {
    return localStorage.getItem(MEETAI_REMINDERS_SETUP_DONE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function markRemindersSetupDone() {
  try {
    localStorage.setItem(MEETAI_REMINDERS_SETUP_DONE_KEY, "1");
  } catch (_) {}
  markPushPromptDismissed();
}

function clearRemindersSetupDone() {
  try {
    localStorage.removeItem(MEETAI_REMINDERS_SETUP_DONE_KEY);
  } catch (_) {}
}

/** First paint: show meeting/calendar shells until fetches finish (avoids empty-state flash). */
function shouldStartUserShellLoading() {
  try {
    if (typeof localStorage === "undefined") return false;
    const u = getStoredUser();
    const token = getToken();
    const email = u?.email || null;
    if (!token || !email) return false;
    return !isPrivilegedSkipUserDashboard(email, u?.adminPortalAccess === true);
  } catch (_) {
    return false;
  }
}

function getStoredUserPrefs() {
  try {
    const raw = localStorage.getItem("meetai_user_prefs");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/** Match backend CALENDAR_DISPLAY_TIMEZONE (default India). */
const APP_DISPLAY_TZ =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_DISPLAY_TIMEZONE) || "Asia/Kolkata";

function ymdInAppTz(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const dp = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_DISPLAY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = dp.find((p) => p.type === "year")?.value;
  const mo = dp.find((p) => p.type === "month")?.value;
  const da = dp.find((p) => p.type === "day")?.value;
  return y && mo && da ? `${y}-${mo}-${da}` : "";
}

function hm24InAppTz(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const tp = new Intl.DateTimeFormat("en-GB", {
    timeZone: APP_DISPLAY_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const h = tp.find((p) => p.type === "hour")?.value;
  const mi = tp.find((p) => p.type === "minute")?.value;
  return h != null && mi != null ? `${h}:${mi}` : "";
}

function formatTimeInAppTz(dateLike, opts = {}) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", {
    timeZone: APP_DISPLAY_TZ,
    hour: "numeric",
    minute: "2-digit",
    ...opts,
  });
}

function formatDateInAppTz(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    timeZone: APP_DISPLAY_TZ,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTimeInAppTz(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const date = formatDateInAppTz(d);
  const time = formatTimeInAppTz(d, { hour: "2-digit", minute: "2-digit" });
  return date && time ? `${date}, ${time}` : date || time;
}

function formatTranscriptLineTime(raw) {
  const s = String(raw || "").trim();
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) || /Z$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    const formatted = formatTimeInAppTz(s);
    return formatted || s;
  }
  return s;
}

/** Map DB snake_case + fill calendar fields from created_at when date/time missing. */
function normalizeMeeting(m) {
  if (!m) return m;
  m = applyCanonicalScheduleFields(m);
  const created = m.created_at ? new Date(m.created_at) : null;
  const validCreated = created && !Number.isNaN(created.getTime());
  let date = m.date;
  let time = m.time;
  if ((!date || String(date).trim() === "") && validCreated) {
    date = ymdInAppTz(created) || `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, "0")}-${String(created.getDate()).padStart(2, "0")}`;
  }
  if ((!time || String(time).trim() === "") && validCreated) {
    time = hm24InAppTz(created) || `${String(created.getHours()).padStart(2, "0")}:${String(created.getMinutes()).padStart(2, "0")}`;
  }
  // Make day filtering resilient to inconsistent DB/API formats (trim + canonicalize to YYYY-MM-DD)
  if (date != null) {
    const raw = String(date).trim();
    // Handle full ISO strings like "2026-04-29T00:00:00.000Z"
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
      const dt = new Date(raw);
      date = dt && !Number.isNaN(dt.getTime()) ? dateToYmd(dt) : raw.slice(0, 10);
    }
    else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) date = raw;
    else {
      // Handle "YYYY-M-D", "YYYY/MM/DD", etc.
      const mm = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      date = mm ? `${mm[1]}-${pad(mm[2])}-${pad(mm[3])}` : raw;
    }
  }
  if (time != null) time = String(time).trim();
  const end =
    m.end != null && m.end !== ""
      ? m.end
      : m.end_time != null && m.end_time !== "" && m.end_time !== "-"
        ? m.end_time
        : "—";
  const status =
    m.status === "pending" ? "upcoming" : m.status;
  return {
    ...m,
    date,
    time,
    end,
    status,
    emailSent: m.emailSent ?? m.email_sent ?? false,
    emailStatusCode: m.emailStatusCode ?? m.email_status_code ?? "",
    emailStatusMessage: m.emailStatusMessage ?? m.email_status_message ?? "",
    emailStatusAt: m.emailStatusAt ?? m.email_status_at ?? null,
    emailRecipientsCount:
      Number(m.emailRecipientsCount ?? m.email_recipients_count) || 0,
    emailRecipientsLog: m.emailRecipientsLog ?? m.email_recipients_log ?? null,
    meetingUrl: m.meetingUrl ?? m.meeting_url ?? m.hangoutLink,
    hostName: m.hostName ?? m.host_name ?? "",
    hostEmail: m.hostEmail ?? m.host_email ?? "",
    ...(() => {
      const rawRecording =
        m?.recordingPersisted === true ||
        m?.recordingEnabled === true ||
        m?.recording_enabled === true;
      return {
        recordingPersisted: rawRecording,
        recordingEnabled: recordingEnabledForAutoJoin(m?.autoJoinEnabled === true, rawRecording),
      };
    })(),
    recordingRetentionDays:
      Number(m.recordingRetentionDays ?? m.recording_retention_days) || 7,
    recordingEnabledBy:
      m.recordingEnabledBy ?? m.recording_enabled_by ?? "",
    recordingEnabledAt:
      m.recordingEnabledAt ?? m.recording_enabled_at ?? null,
    recordingExpiresAt:
      m.recordingExpiresAt ?? m.recording_expires_at ?? null,
    dataExpiresAt: m.dataExpiresAt ?? m.data_expires_at ?? null,
    completedAt: m.completedAt ?? m.completed_at ?? null,
    meetingDataPurgedAt: m.meetingDataPurgedAt ?? m.meeting_data_purged_at ?? null,
    recallBotId: m.recallBotId ?? m.recall_bot_id ?? "",
    recallMediaDeletedAt: m.recallMediaDeletedAt ?? m.recall_media_deleted_at ?? null,
    recallMediaDeleteAt: m.recallMediaDeleteAt ?? m.recall_media_delete_at ?? null,
    recallMediaStatus: m.recallMediaStatus ?? m.recall_media_status ?? "",
    recallMediaDeferred: m.recallMediaDeferred === true || m.recall_media_deferred === true,
    recallMediaCapDeleteAt: m.recallMediaCapDeleteAt ?? m.recall_media_cap_delete_at ?? null,
    recallMediaRetentionDays:
      Number(m.recallMediaRetentionDays ?? m.recall_media_retention_days) ||
      RECALL_MEDIA_RETENTION_DAYS_DEFAULT,
    recordingStorageStatus:
      m.recordingStorageStatus ?? m.recording_storage_status ?? "",
    recordingS3Key: m.recordingS3Key ?? m.recording_s3_key ?? "",
    recordingDownloadUrl:
      m.recordingDownloadUrl ?? m.recording_download_url ?? "",
    presentationSummary:
      m.presentationSummary ?? m.presentation_summary ?? "",
    presentationSummaryStatus:
      m.presentationSummaryStatus ?? m.presentation_summary_status ?? "",
    transcriptRowCount:
      Number(m.transcriptRowCount ?? m.transcript_row_count) ||
      (Array.isArray(m.transcript_raw) && m.transcript_raw.length > 0
        ? m.transcript_raw.length
        : Array.isArray(m.transcript)
          ? m.transcript.length
          : 0) ||
      0,
    actionItemCount:
      Number(m.actionItemCount ?? m.action_item_count) ||
      (Array.isArray(m.actionItems) ? m.actionItems.length : 0) ||
      0,
    transcript:
      m._useProvidedTranscript === true && Array.isArray(m.transcript)
        ? m.transcript
        : Array.isArray(m.transcript_clean) && m.transcript_clean.length > 0
          ? m.transcript_clean
          : Array.isArray(m.transcript) && m.transcript.length > 0
            ? m.transcript
            : Array.isArray(m.transcript_raw)
              ? m.transcript_raw
              : [],
    transcript_raw: Array.isArray(m.transcript_raw) ? m.transcript_raw : [],
    transcript_clean: Array.isArray(m.transcript_clean) ? m.transcript_clean : [],
    transcriptSpeakers: Array.isArray(m.transcriptSpeakers)
      ? m.transcriptSpeakers
      : Array.isArray(m.transcript_speakers)
        ? m.transcript_speakers
        : undefined,
    autoJoinOptInUsers: Array.isArray(m.autoJoinOptInUsers) ? m.autoJoinOptInUsers : [],
    googleEventId: extractGoogleEventId(m),
    googleRecurringEventId:
      m.googleRecurringEventId ?? m.google_recurring_event_id ?? null,
    responseStatus: m.responseStatus ?? m.response_status ?? null,
    botDispatched: m.botDispatched === true || !!(m.recall_bot_id || m.recallBotId),
    botDispatchPending: m.botDispatchPending === true,
  };
}

function meetingRecordingRetentionDaysTotal(m) {
  const n = Number(m?.recordingRetentionDays ?? m?.recording_retention_days);
  if (Number.isFinite(n) && n > 0) return Math.round(n);
  return 7;
}

function meetingRecordingExpiresAtMs(m) {
  const raw = m?.recordingExpiresAt ?? m?.recording_expires_at;
  if (raw) {
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) return dt.getTime();
  }
  const enabledAt = m?.recordingEnabledAt ?? m?.recording_enabled_at;
  if (enabledAt) {
    const dt = new Date(enabledAt);
    if (!Number.isNaN(dt.getTime())) {
      return dt.getTime() + meetingRecordingRetentionDaysTotal(m) * 86400000;
    }
  }
  return null;
}

function meetingRecordingHasStoredFile(m) {
  return !!String(m?.recordingS3Key || m?.recording_s3_key || "").trim();
}

function meetingRecordingDaysLeft(m, nowMs = Date.now()) {
  const hasFile = meetingRecordingHasStoredFile(m);
  const recOn =
    m?.recordingEnabled === true ||
    m?.recording_enabled === true ||
    m?.recordingPersisted === true;
  if (!hasFile && !recOn) return null;
  const expiresMs = meetingRecordingExpiresAtMs(m);
  if (expiresMs == null) return hasFile ? meetingRecordingRetentionDaysTotal(m) : null;
  const ms = expiresMs - nowMs;
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86400000);
}

/** Copy + countdown for 7-day recording retention UI. */
function meetingRecordingRetentionInfo(m, nowMs = Date.now()) {
  const totalDays = meetingRecordingRetentionDaysTotal(m);
  const daysLeft = meetingRecordingDaysLeft(m, nowMs);
  const expiresMs = meetingRecordingExpiresAtMs(m);
  const expiresLabel =
    expiresMs != null
      ? formatTimeInAppTz(expiresMs, { day: "2-digit", month: "short", year: "numeric" })
      : "";
  let countdownLabel = "";
  let message = "";
  let shortMessage = "";
  let urgent = false;
  if (daysLeft == null) {
    message = `Only ${totalDays} days available — after that this recording will be permanently erased. Please download a copy if you want to keep it.`;
    shortMessage = "Download to keep";
    countdownLabel = `${totalDays}d`;
  } else if (daysLeft <= 0 && !meetingRecordingHasStoredFile(m)) {
    urgent = true;
    countdownLabel = "Erased";
    message = `This meeting recording was erased after the ${totalDays}-day retention period and is no longer available.`;
    shortMessage = "No longer available";
  } else if (daysLeft <= 0) {
    urgent = true;
    countdownLabel = "Today";
    message =
      "This recording will be permanently erased very soon. Please download a copy if you want to keep it.";
    shortMessage = "Download now";
  } else if (daysLeft === 1) {
    urgent = true;
    countdownLabel = "1d";
    message = `Only 1 day left — then this recording will be permanently erased (${totalDays}-day limit). Please download if you want to keep it.`;
    shortMessage = "Download to keep";
  } else {
    urgent = daysLeft <= 3;
    countdownLabel = `${daysLeft}d`;
    message = `Only ${totalDays} days available — then erased. ${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining. Please download if you want to keep it.`;
    shortMessage = "Download to keep";
  }
  return {
    totalDays,
    daysLeft,
    expiresMs,
    expiresLabel,
    countdownLabel,
    message,
    shortMessage,
    urgent,
  };
}

/** Friendly copy when popup / share page cannot load the recording. */
function meetingRecordingUnavailableMessage(m, fallback = "") {
  const total = meetingRecordingRetentionDaysTotal(m);
  const daysLeft = meetingRecordingDaysLeft(m);
  const hasFile = meetingRecordingHasStoredFile(m);
  if (!hasFile && daysLeft != null && daysLeft <= 0) {
    return `This meeting recording is no longer available. It was erased after the ${total}-day retention period.`;
  }
  if (!hasFile) {
    return "This meeting recording is not available yet. It may still be uploading — please try again in a few minutes.";
  }
  const raw = String(fallback || "").trim();
  if (/not available|could not be loaded/i.test(raw)) {
    if (daysLeft != null && daysLeft <= 0) {
      return `This meeting recording is no longer available. It was erased after the ${total}-day retention period.`;
    }
    return "This meeting recording is not available yet. It may still be uploading — please try again in a few minutes.";
  }
  return raw || "This meeting recording could not be loaded. Please try again later.";
}

function MeetingRecordingRetentionNotice({
  meetingMeta,
  nowMs: nowMsProp,
  compact = false,
  stack = false,
  style: wrapStyle = {},
}) {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const nowMs = nowMsProp ?? tick;
  const info = meetingRecordingRetentionInfo(meetingMeta, nowMs);
  if (!info.message) return null;

  const badgeBg = info.urgent ? "#ffedd5" : "#e0f2fe";
  const badgeColor = info.urgent ? "#c2410c" : "#0369a1";
  const badgeBorder = info.urgent ? "#fed7aa" : "#bae6fd";
  const tip = [info.message, info.expiresLabel ? `Erases on: ${info.expiresLabel}` : ""]
    .filter(Boolean)
    .join(" ");

  const badge = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        padding: compact ? "1px 6px" : "2px 8px",
        borderRadius: 6,
        fontSize: compact ? 10 : 10.5,
        fontWeight: 700,
        letterSpacing: "0.02em",
        background: badgeBg,
        color: badgeColor,
        border: `1px solid ${badgeBorder}`,
      }}
    >
      {info.countdownLabel}
    </span>
  );

  if (compact) {
    return (
      <p
        title={tip}
        style={{
          margin: 0,
          display: stack ? "flex" : "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10.5,
          lineHeight: 1.35,
          color: T.textMuted,
          whiteSpace: stack ? "normal" : "nowrap",
          flex: stack ? undefined : "0 1 auto",
          minWidth: 0,
          maxWidth: "100%",
          overflow: stack ? "visible" : "hidden",
          textOverflow: stack ? undefined : "ellipsis",
          flexWrap: stack ? "wrap" : undefined,
          ...wrapStyle,
        }}
      >
        {badge}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {info.shortMessage}
          {info.expiresLabel ? (
            <span style={{ color: T.textMuted }}> · {info.expiresLabel}</span>
          ) : null}
        </span>
      </p>
    );
  }

  return (
    <div
      title={tip}
      style={{
        marginTop: 10,
        padding: "7px 10px",
        borderRadius: 8,
        border: `1px solid ${badgeBorder}`,
        background: info.urgent ? "#fffbeb" : "#f8fafc",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 11.5,
        lineHeight: 1.45,
        color: T.textSub,
        ...wrapStyle,
      }}
    >
      {badge}
      <span style={{ flex: 1, minWidth: 0 }}>
        {info.shortMessage}
        {info.expiresLabel ? (
          <span style={{ color: T.textMuted }}> · until {info.expiresLabel}</span>
        ) : null}
      </span>
    </div>
  );
}

function isUuidLike(value) {
  const s = String(value || "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
}

/** Canonical Google Calendar event id (API may send google_event_id or gcal-… id). */
function extractGoogleEventId(m) {
  const fromField = String(m?.googleEventId || m?.google_event_id || "").trim();
  if (fromField) return fromField;
  const rawId = String(m?.id || m?._id || "").trim();
  if (rawId.toLowerCase().startsWith("gcal-")) return rawId.slice(5).trim();
  return "";
}

/** Recurring Google instance ids: …_YYYYMMDDTHHMMSSZ → calendar day/time in app TZ. */
function scheduleFromGoogleEventId(googleEventId) {
  const gid = String(googleEventId || "").trim();
  const m = gid.match(/_(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/i);
  if (!m) return null;
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const date = ymdInAppTz(dt);
  const time = hm24InAppTz(dt);
  if (!date || !time) return null;
  return { date, time };
}

function applyCanonicalScheduleFields(m) {
  if (!m) return m;
  if (String(m?.status || "").toLowerCase() === "cancelled") return m;
  const dbDate = String(m.date || "").trim().slice(0, 10);
  const dbTime = String(m.time || "").trim();
  const dbDateValid = /^\d{4}-\d{2}-\d{2}$/.test(dbDate);
  const status = String(m?.status || "").toLowerCase();
  const finished =
    ["completed", "done", "ended", "summarized"].includes(status) ||
    !!(m?.completed_at || m?.completedAt) ||
    m?.emailSent === true ||
    m?.email_sent === true;
  const doneRaw = m?.completed_at || m?.completedAt;
  const doneYmd = doneRaw ? ymdInAppTz(new Date(doneRaw)) : "";
  const today = todayStr();
  // Finished: prefer real completion day over future/wrong Oct stamps from google_event_id.
  if (finished) {
    if (doneYmd && (!dbDateValid || (today && dbDate > today) || dbDate !== doneYmd)) {
      return { ...m, date: doneYmd };
    }
    if (dbDateValid) return m;
  }
  const gid = extractGoogleEventId(m);
  const fromGid = scheduleFromGoogleEventId(gid);
  if (!fromGid) return m;

  // Recurring instance ids keep the ORIGINAL slot in the suffix
  // (…_YYYYMMDDTHHMMSSZ) even after a same-day move.
  // Example: …_20261006T110000Z (= 4:30pm IST) while start.dateTime is 6:30pm.
  // Never overwrite a real clock time with that original series time — that
  // made Daily Standup show 4:30–7:00 Live instead of 6:30–7:00.
  const timeMissingOrPlaceholder =
    !dbTime ||
    dbTime === "00:00" ||
    (dbTime === "12:00" && fromGid.time && fromGid.time !== "12:00");

  if (!dbDateValid && fromGid.date) {
    return {
      ...m,
      date: fromGid.date,
      time: !timeMissingOrPlaceholder ? dbTime : fromGid.time || dbTime,
    };
  }

  if (dbDateValid && timeMissingOrPlaceholder && fromGid.time) {
    if (!fromGid.date || fromGid.date === dbDate) {
      return { ...m, time: fromGid.time };
    }
  }

  return m;
}

function meetingAutoJoinIsOn(m) {
  return m?.autoJoinEnabled === true;
}

function meetingRecordingStoredOn(m) {
  return m?.recordingPersisted === true || m?.recordingEnabled === true || m?.recording_enabled === true;
}

/** UI toggle on Auto Join tab — only on when Auto Join is also on. */
function effectiveRecordingEnabled(m) {
  return meetingAutoJoinIsOn(m) && meetingRecordingStoredOn(m);
}

function recordingEnabledForAutoJoin(autoJoinOn, recordingOn) {
  return autoJoinOn === true && recordingOn === true;
}

/** Video Rec can only be turned on when per-meeting Auto Join is on. */
function canEnableMeetingRecording(m) {
  return meetingAutoJoinIsOn(m);
}

function resolvedAutoJoinEnabled(a, b) {
  const cal =
    String(a?.type || "").toLowerCase() === "google_calendar"
      ? a
      : String(b?.type || "").toLowerCase() === "google_calendar"
        ? b
        : null;
  if (cal && (cal.autoJoinEnabled === true || cal.autoJoinEnabled === false)) {
    return cal.autoJoinEnabled === true;
  }
  return a?.autoJoinEnabled === true || b?.autoJoinEnabled === true;
}

/** Prefer the action-item row with the newer updated_at (guards against stale list polls). */
function mergeActionItemsByUpdatedAt(prevItems, nextItems) {
  const prev = Array.isArray(prevItems) ? prevItems : [];
  const next = Array.isArray(nextItems) ? nextItems : [];
  if (!next.length) return prev;
  if (!prev.length) return next;
  const prevMap = new Map();
  for (const it of prev) {
    const id = String(it?.id ?? "").trim();
    if (id) prevMap.set(id, it);
  }
  return next.map((it) => {
    const id = String(it?.id ?? "").trim();
    const old = id ? prevMap.get(id) : null;
    if (!old) return it;
    const oldTs = Date.parse(old.updated_at || old.updatedAt || "") || 0;
    const newTs = Date.parse(it.updated_at || it.updatedAt || "") || 0;
    return oldTs > newTs ? old : it;
  });
}

/** Keep calendar Auto Join / Video Rec when detail is refreshed from GET /meetings/:id. */
function mergeMeetingDetailFromApi(calendarSnapshot, apiFresh) {
  const cal = normalizeMeeting(calendarSnapshot || {});
  const api = normalizeMeeting(apiFresh || {});
  const autoJoinEnabled = resolvedAutoJoinEnabled(cal, api);
  const recordingPersisted =
    api.recordingPersisted === true ||
    api.recordingEnabled === true ||
    cal.recordingPersisted === true;
  const apiId = String(api.id || api._id || "").trim();
  const calId = String(cal.id || cal._id || "").trim();
  const approvalLocked = apiFresh?.approvalAccess?.required === true && apiFresh?.approvalAccess?.visible === false;
  const apiTranscript = Array.isArray(api.transcript) && api.transcript.length > 0 ? api.transcript : null;
  const calTranscript = Array.isArray(cal.transcript) && cal.transcript.length > 0 ? cal.transcript : null;
  const apiRaw = Array.isArray(api.transcript_raw) && api.transcript_raw.length > 0 ? api.transcript_raw : null;
  const calRaw = Array.isArray(cal.transcript_raw) && cal.transcript_raw.length > 0 ? cal.transcript_raw : null;
  const apiAi = Array.isArray(api.aiSummaries) && api.aiSummaries.length > 0 ? api.aiSummaries : null;
  const calAi = Array.isArray(cal.aiSummaries) && cal.aiSummaries.length > 0 ? cal.aiSummaries : null;
  const sessionFields = preservedSessionScheduleFields(api);
  const timelineKeep = rescheduleTimelineKeepRow(api, cal);
  return normalizeMeeting({
    ...cal,
    ...api,
    ...sessionFields,
    id: isUuidLike(apiId) ? apiId : calId || apiId,
    googleEventId: extractGoogleEventId(cal) || extractGoogleEventId(api),
    type: mergeMeetingKindType(cal, api),
    autoJoinEnabled,
    recordingPersisted,
    recordingEnabled: recordingEnabledForAutoJoin(autoJoinEnabled, recordingPersisted),
    participants:
      Array.isArray(api.participants) && api.participants.length > 0
        ? api.participants
        : cal.participants,
    transcript: approvalLocked ? [] : (apiRaw || calRaw || apiTranscript || calTranscript || []),
    transcript_raw: approvalLocked ? [] : (apiRaw || calRaw || api.transcript_raw || cal.transcript_raw || []),
    aiSummaries: approvalLocked ? [] : (apiAi || calAi || []),
    actionItems: approvalLocked ? [] : mergeActionItemsByUpdatedAt(cal.actionItems, api.actionItems),
    transcriptSpeakers:
      (Array.isArray(api.transcriptSpeakers) && api.transcriptSpeakers.length > 0
        ? api.transcriptSpeakers
        : null) ||
      (apiRaw ? transcriptSpeakerNames(apiRaw) : null) ||
      (apiTranscript ? transcriptSpeakerNames(apiTranscript) : null) ||
      (Array.isArray(cal.transcriptSpeakers) && cal.transcriptSpeakers.length > 0
        ? cal.transcriptSpeakers
        : null) ||
      (calRaw ? transcriptSpeakerNames(calRaw) : null) ||
      (calTranscript ? transcriptSpeakerNames(calTranscript) : []) ||
      [],
    meetingUrl: joinUrlFromMeeting(cal) || joinUrlFromMeeting(api) || api.meetingUrl,
    hangoutLink: cal.hangoutLink || api.hangoutLink || "",
    title: api.title || cal.title,
    date: sessionFields.date || api.date || cal.date,
    time: sessionFields.time || api.time || cal.time,
    end: sessionFields.end ?? (api.end != null && api.end !== "" ? api.end : cal.end),
    rescheduleTimeline: accumulateRescheduleTimeline(cal, api, timelineKeep),
    rescheduleNote:
      cal.rescheduleNote ||
      api.rescheduleNote ||
      (() => {
        const timeline = accumulateRescheduleTimeline(cal, api, timelineKeep);
        return timeline.length >= 2 ? rescheduleNoteFromTimeline(timeline, timelineKeep) : "";
      })(),
    ...preservedBotSessionMeta(cal, api),
    ...preservedTranscriptMeta(cal, api),
  });
}

/** Apply lite list/poll payloads without wiping detail-loaded transcript bodies. */
function mergeMeetingsListPreserveDetail(prev, incoming) {
  const prevMap = new Map();
  for (const row of Array.isArray(prev) ? prev : []) {
    const id = String(row?.id ?? row?._id ?? "").trim();
    if (id) prevMap.set(id, row);
  }
  const next = (Array.isArray(incoming) ? incoming : []).map((raw) => {
    const m = normalizeMeeting(raw);
    const id = String(m?.id ?? m?._id ?? "").trim();
    const old = id ? prevMap.get(id) : null;
    if (!old) return m;
    return mergeMeetingDetailFromApi(old, m);
  });
  return dedupeDbMeetings(next, false);
}

/** Patch recording fields from API without clobbering calendar auto-join / participants. */
function applyRecordingSettingsPatch(existing, updated) {
  const prev = normalizeMeeting(existing);
  const patch = normalizeMeeting(updated);
  const patchId = String(patch?.id || patch?._id || "").trim();
  const prevId = String(prev?.id || prev?._id || "").trim();
  const preferId = isUuidLike(patchId) ? patchId : isUuidLike(prevId) ? prevId : patchId || prevId;
  return normalizeMeeting({
    ...prev,
    id: preferId,
    googleEventId: extractGoogleEventId(patch) || extractGoogleEventId(prev),
    recordingPersisted:
      patch.recordingPersisted === true || patch.recordingEnabled === true,
    recordingEnabled: recordingEnabledForAutoJoin(
      resolvedAutoJoinEnabled(prev, patch),
      patch.recordingPersisted === true || patch.recordingEnabled === true
    ),
    recordingRetentionDays: patch.recordingRetentionDays,
    recordingEnabledBy: patch.recordingEnabledBy,
    recordingEnabledAt: patch.recordingEnabledAt,
    recordingExpiresAt: patch.recordingExpiresAt,
    recordingStorageStatus: patch.recordingStorageStatus,
    recordingS3Key: patch.recordingS3Key || prev.recordingS3Key || "",
    recordingDownloadUrl: patch.recordingDownloadUrl || prev.recordingDownloadUrl || "",
    autoJoinEnabled: resolvedAutoJoinEnabled(prev, patch),
    participants:
      Array.isArray(prev.participants) && prev.participants.length > 0
        ? prev.participants
        : patch.participants || [],
    type:
      String(prev?.type || "").toLowerCase() === "google_calendar"
        ? "google_calendar"
        : prev.type || patch.type,
  });
}

function meetingRecordingToggleLabel({ enabled, saving, compact = false, lang = "English" }) {
  const t = (k, fb) => getTranslation(lang, k, fb);
  if (saving) return t("saving", "Saving…");
  if (compact) return enabled ? t("on", "On") : t("off", "Off");
  return enabled ? t("on", "On") : t("off", "Off");
}

function meetingRecordingRetentionLabel(m) {
  const info = meetingRecordingRetentionInfo(m);
  return info.countdownLabel || `${info.totalDays}-day retention`;
}

function meetingRecordingFileReady(m, { requireAutoJoin = false } = {}) {
  const enabled = requireAutoJoin ? effectiveRecordingEnabled(m) : meetingRecordingStoredOn(m);
  return enabled && meetingRecordingHasStoredFile(m);
}

function meetingRecordingAwaitingFile(m, { requireAutoJoin = false } = {}) {
  const enabled = requireAutoJoin ? effectiveRecordingEnabled(m) : meetingRecordingStoredOn(m);
  return enabled && !meetingRecordingHasStoredFile(m);
}

/** Admin: auto-join opt-ins for a calendar-linked MeetAI session. */
function adminMeetingAutoJoinSummary(m, optInUsers) {
  const aj = Array.isArray(optInUsers) ? optInUsers : [];
  const calLinked =
    !!String(m?.google_event_id || m?.googleEventId || "").trim() ||
    String(m?.type || "").toLowerCase() === "calendar_auto";
  if (!calLinked) {
    return {
      on: false,
      label: "N/A",
      detail: "Not linked to a Google Calendar event (manual / instant Record meeting).",
    };
  }
  if (!aj.length) {
    return {
      on: false,
      label: "No opt-ins",
      detail: "No invitee has global or per-event auto-join enabled for this event.",
    };
  }
  const names = aj
    .slice(0, 4)
    .map((p) => formatAdminPersonLabel(p))
    .join(", ");
  const more = aj.length > 4 ? ` (+${aj.length - 4} more)` : "";
  return {
    on: true,
    label: `${aj.length} opted in`,
    detail: `${names}${more}`,
  };
}

/** Admin: Video Rec + S3 + presentation summary state for a meeting row. */
function adminMeetingRecordingSummary(m) {
  const videoRecOn = meetingRecordingStoredOn(m);
  const hasFile = meetingRecordingHasStoredFile(m);
  const storage = String(m?.recordingStorageStatus || m?.recording_storage_status || "").trim();
  const presRaw = String(m?.presentationSummaryStatus || m?.presentation_summary_status || "").trim();
  const pres =
    presRaw === "ready" || presRaw === "limited"
      ? presRaw
      : presRaw === "ready_limited"
        ? "limited"
        : presRaw;
  const enabledBy = String(m?.recordingEnabledBy || m?.recording_enabled_by || "").trim();
  const enabledAt = m?.recordingEnabledAt || m?.recording_enabled_at;
  const expires = m?.recordingExpiresAt || m?.recording_expires_at;
  const retention = meetingRecordingRetentionDaysTotal(m);
  const storageLower = storage.toLowerCase();
  let statusLabel = "Video Rec off";
  if (videoRecOn && hasFile) {
    statusLabel =
      storageLower === "expired" || storageLower === "erased" || storageLower === "purged"
        ? "Video Rec · erased"
        : "Video Rec · stored";
  } else if (videoRecOn) {
    statusLabel = "Video Rec · awaiting file";
  }
  return {
    videoRecOn,
    hasFile,
    storage: storage || (hasFile ? "stored" : videoRecOn ? "pending" : "—"),
    pres: pres || "—",
    enabledBy,
    enabledAt,
    expires,
    retention,
    statusLabel,
  };
}

function adminRetentionCountdownLabel(prefix, { removed, active, pending, due, deferred, daysLeft }) {
  if (removed) return `${prefix}: data removed`;
  if (deferred) return `${prefix}: deferred (S3 pending)`;
  if (active) return `${prefix}: active`;
  if (pending) return `${prefix}: pending upload`;
  if (due || daysLeft === 0) return `${prefix}: delete due today`;
  if (daysLeft === 1) return `${prefix}: deletes in 1 day`;
  if (Number.isFinite(daysLeft) && daysLeft > 1) return `${prefix}: deletes in ${daysLeft} days`;
  return `${prefix}: —`;
}

/** Admin: S3 video file retention countdown / removed state. */
function adminMeetingS3RetentionSummary(m) {
  const rec = adminMeetingRecordingSummary(m);
  if (!rec.videoRecOn) {
    return { show: false, label: "", title: "", tone: "none", retentionDays: rec.retention, deleteAt: null };
  }

  const storageLower = String(rec.storage || "").toLowerCase();
  const purged = storageLower === "purged" || storageLower === "expired" || storageLower === "erased";
  const daysLeft = meetingRecordingDaysLeft(m);
  let expiresMs = meetingRecordingExpiresAtMs(m);
  const deleteAt = expiresMs != null ? new Date(expiresMs).toISOString() : rec.expires || null;
  const ended = isEndedMeetingStatus(effectiveMeetingStatus(m), m);
  if (expiresMs == null && ended) {
    const completedRaw = m?.completedAt ?? m?.completed_at ?? m?.updated_at ?? null;
    const completedMs = completedRaw ? new Date(completedRaw).getTime() : NaN;
    if (Number.isFinite(completedMs)) {
      expiresMs = completedMs + rec.retention * 86400000;
    }
  }

  if (purged || (ended && storageLower === "no_video_artifact" && !rec.hasFile)) {
    return {
      show: true,
      label: adminRetentionCountdownLabel("S3", { removed: true }),
      title: "Video file removed from S3 after the 7-day retention period. Transcript and summary remain in MeetAI DB.",
      tone: "deleted",
      retentionDays: rec.retention,
      deleteAt: null,
      hasFile: false,
    };
  }

  if (!rec.hasFile) {
    return {
      show: true,
      label: adminRetentionCountdownLabel("S3", { pending: true }),
      title: "Video Rec is on — waiting for Recall upload to copy the file to S3.",
      tone: "active",
      retentionDays: rec.retention,
      deleteAt: null,
      hasFile: false,
    };
  }

  const msLeft = expiresMs != null ? expiresMs - Date.now() : NaN;
  const due = Number.isFinite(msLeft) && msLeft <= 0;
  const dl =
    expiresMs != null
      ? msLeft <= 0
        ? 0
        : Math.ceil(msLeft / 86400000)
      : daysLeft != null
        ? daysLeft
        : rec.retention;

  return {
    show: true,
    label: adminRetentionCountdownLabel("S3", { due, daysLeft: dl }),
    title: (expiresMs != null ? new Date(expiresMs) : deleteAt)
      ? `S3 video deletes on ${new Date(expiresMs != null ? expiresMs : deleteAt).toLocaleString()} (${rec.retention}-day policy). DB transcript kept.`
      : `${rec.retention}-day S3 video retention. DB transcript kept.`,
    tone: due ? "due" : "scheduled",
    retentionDays: rec.retention,
    deleteAt: expiresMs != null ? new Date(expiresMs).toISOString() : deleteAt,
    hasFile: true,
  };
}

/** Admin: Recall server media delete schedule (DB transcript kept; Recall copy removed for cost). */
function adminMeetingRecallMediaSummary(m) {
  const botId = String(m?.recallBotId || m?.recall_bot_id || "").trim();
  const retentionDays =
    Number(m?.recallMediaRetentionDays ?? m?.recall_media_retention_days) ||
    RECALL_MEDIA_RETENTION_DAYS_DEFAULT;
  const statusRaw = String(m?.recallMediaStatus ?? m?.recall_media_status ?? "").trim().toLowerCase();
  const deletedAt = m?.recallMediaDeletedAt ?? m?.recall_media_deleted_at ?? null;
  const deleteAtRaw = m?.recallMediaDeleteAt ?? m?.recall_media_delete_at ?? null;
  const deferred = m?.recallMediaDeferred === true || m?.recall_media_deferred === true;
  const capDeleteAtRaw = m?.recallMediaCapDeleteAt ?? m?.recall_media_cap_delete_at ?? null;

  if (!botId) {
    return { show: false, label: "", title: "", tone: "none", retentionDays, deleteAt: null, deletedAt: null };
  }

  if (deletedAt || statusRaw === "deleted") {
    const when = deletedAt ? new Date(deletedAt).toLocaleString() : "";
    return {
      show: true,
      label: adminRetentionCountdownLabel("Recall", { removed: true }),
      title: when
        ? `Recall recording media removed on ${when}. Transcript and summary remain in MeetAI DB.`
        : "Recall recording media removed. Transcript and summary remain in MeetAI DB.",
      tone: "deleted",
      retentionDays,
      deleteAt: null,
      deletedAt,
    };
  }

  if (statusRaw === "active" || (!statusRaw && !isEndedMeetingStatus(effectiveMeetingStatus(m), m))) {
    return {
      show: true,
      label: adminRetentionCountdownLabel("Recall", { active: true }),
      title: `MeetAI session on Recall. Media auto-deletes ${retentionDays} day(s) after meeting ends (DB transcript kept).`,
      tone: "active",
      retentionDays,
      deleteAt: null,
      deletedAt: null,
    };
  }

  if (statusRaw === "deferred") {
    const capMs = capDeleteAtRaw ? new Date(capDeleteAtRaw).getTime() : NaN;
    const capLabel = Number.isFinite(capMs) ? new Date(capMs).toLocaleString() : `${retentionDays + 5}d cap`;
    const daysLeft = Number.isFinite(capMs)
      ? Math.max(0, Math.ceil((capMs - Date.now()) / 86400000))
      : null;
    return {
      show: true,
      label: adminRetentionCountdownLabel("Recall", {
        deferred: daysLeft == null || daysLeft > 0,
        due: daysLeft === 0,
        daysLeft: daysLeft != null ? daysLeft : undefined,
      }),
      title: `Video Rec is waiting for S3 upload. Recall media is held until upload completes or ${capLabel} (7-day cap). DB transcript kept.`,
      tone: daysLeft === 0 ? "due" : "scheduled",
      retentionDays,
      deleteAt: deleteAtRaw || (Number.isFinite(capMs) ? new Date(capMs).toISOString() : null),
      deletedAt: null,
    };
  }

  let deleteAtMs = deleteAtRaw ? new Date(deleteAtRaw).getTime() : NaN;
  if (!Number.isFinite(deleteAtMs)) {
    const completedRaw = m?.completedAt ?? m?.completed_at ?? m?.updated_at ?? null;
    const completedMs = completedRaw ? new Date(completedRaw).getTime() : NaN;
    if (Number.isFinite(completedMs)) {
      deleteAtMs = completedMs + retentionDays * 86400000;
    }
  }

  if (!Number.isFinite(deleteAtMs)) {
    return {
      show: true,
      label: `${adminRetentionCountdownLabel("Recall", { daysLeft: retentionDays })} (${retentionDays}d policy)`,
      title: `${retentionDays}-day Recall media retention. Transcript stays in MeetAI DB.`,
      tone: "scheduled",
      retentionDays,
      deleteAt: null,
      deletedAt: null,
    };
  }

  const now = Date.now();
  const msLeft = deleteAtMs - now;
  const daysLeft = msLeft <= 0 ? 0 : Math.ceil(msLeft / 86400000);
  const deleteAtLabel = new Date(deleteAtMs).toLocaleString();
  const due = msLeft <= 0 || statusRaw === "due";

  return {
    show: true,
    label: adminRetentionCountdownLabel("Recall", { due, daysLeft }),
    title: due
      ? `Recall media delete was scheduled for ${deleteAtLabel}. Next purge job runs Delete Media API. DB transcript kept.`
      : `Recall media deletes on ${deleteAtLabel} (${retentionDays}-day policy). MeetAI DB transcript and summary are kept.`,
    tone: due ? "due" : "scheduled",
    retentionDays,
    deleteAt: new Date(deleteAtMs).toISOString(),
    deletedAt: null,
  };
}

function adminMeetingRetentionPillStyle(tone, isLight) {
  if (tone === "deleted") {
    return {
      background: isLight ? "rgba(100,116,139,0.12)" : "rgba(148,163,184,0.12)",
      borderColor: isLight ? "#cbd5e1" : "rgba(148,163,184,0.35)",
      color: isLight ? "#475569" : "#CBD5E1",
    };
  }
  if (tone === "due") {
    return {
      background: isLight ? "rgba(234,88,12,0.12)" : "rgba(249,115,22,0.12)",
      borderColor: isLight ? "#fdba74" : "rgba(249,115,22,0.4)",
      color: isLight ? "#C2410C" : "#FDBA74",
    };
  }
  if (tone === "active") {
    return {
      background: isLight ? "rgba(14,165,233,0.1)" : "rgba(56,189,248,0.12)",
      borderColor: isLight ? "#7dd3fc" : "rgba(56,189,248,0.35)",
      color: isLight ? "#0369A1" : "#7DD3FC",
    };
  }
  return {
    background: isLight ? "rgba(139,92,246,0.1)" : "rgba(167,139,250,0.12)",
    borderColor: isLight ? "#c4b5fd" : "rgba(167,139,250,0.35)",
    color: isLight ? "#6D28D9" : "#C4B5FD",
  };
}

function adminMeetingRecallMediaPillStyle(tone, isLight) {
  return adminMeetingRetentionPillStyle(tone, isLight);
}

/** DB UUID for transcript export and other meeting-scoped APIs. */
function meetingDbIdForApi(m) {
  const raw = String(m?.id || m?._id || "").trim();
  return isUuidLike(raw) ? raw : "";
}

/** Human-readable file size for recording popup (B, KB, MB, GB). */
function formatRecordingFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let u = 0;
  let v = n;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  const decimals = u === 0 ? 0 : v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(decimals)} ${units[u]}`;
}

/** Id accepted by recording-settings / recording-download (UUID or gcal-…). */
function meetingRecordingApiId(m) {
  const raw = String(m?.id || m?._id || "").trim();
  if (isUuidLike(raw)) return raw;
  const gid = extractGoogleEventId(m);
  if (!gid) return raw;
  return raw.toLowerCase().startsWith("gcal-") ? raw : `gcal-${gid}`;
}

/** App origin for share links (never the API host). */
function appFrontendOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return String(window.location.origin).replace(/\/$/, "");
  }
  const fromEnv = String(
    process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_FRONTEND_URL || ""
  )
    .trim()
    .replace(/\/$/, "");
  return fromEnv;
}

/** Frontend public recording page (Share link) — opens branded player in the app. */
function meetingRecordingPublicPageUrl(meetingId) {
  const id = String(meetingId || "").trim();
  if (!id) return "";
  const origin = appFrontendOrigin();
  if (!origin) return "";
  return `${origin}/recording?m=${encodeURIComponent(id)}`;
}

/** Frontend public shared-meeting page — opens ONLY the scope the sharer picked. */
function meetingSharePublicPageUrl(shareId) {
  const id = String(shareId || "").trim();
  if (!id) return "";
  const origin = appFrontendOrigin();
  if (!origin) return "";
  return `${origin}/shared?s=${encodeURIComponent(id)}`;
}

/** Normalize legacy API preview URLs to frontend /recording?m=… */
function normalizeRecordingShareUrl(url, meetingIdFallback = "") {
  const raw = String(url || "").trim();
  const idFromPath = raw.match(/\/meetings\/([^/?]+)\/recording/i)?.[1];
  const id = String(meetingIdFallback || idFromPath || "").trim();
  if (/\/recording(\?|$)/i.test(raw)) {
    try {
      const parsed = new URL(raw, appFrontendOrigin() || undefined);
      const mid = parsed.searchParams.get("m") || parsed.searchParams.get("id");
      if (mid) return meetingRecordingPublicPageUrl(mid);
    } catch (_) {
      /* fall through */
    }
  }
  if (id) return meetingRecordingPublicPageUrl(id);
  return meetingRecordingPublicPageUrl(meetingIdFallback);
}

/** Branded API URL: /api/meetings/:id/recording (not raw S3). */
function meetingRecordingBrandedUrl(meetingId, { view = false, download = false } = {}) {
  const id = String(meetingId || "").trim();
  if (!id) return "";
  const token = getToken();
  const params = new URLSearchParams();
  if (token) params.set("access_token", token);
  if (view) params.set("view", "1");
  if (download) params.set("download", "1");
  const q = params.toString();
  const path = `${API}/meetings/${encodeURIComponent(id)}/recording${q ? `?${q}` : ""}`;
  if (/^https?:\/\//i.test(API)) return path;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
  }
  return path;
}

async function fetchMeetingRecordingLinks(meetingId) {
  const id = String(meetingId || "").trim();
  if (!id) throw new Error("Meeting id is required.");
  const data = await apiFetch(`/meetings/${encodeURIComponent(id)}/recording-download`);
  const streamUrl = meetingRecordingBrandedUrl(id);
  const shareUrl =
    normalizeRecordingShareUrl(data?.shareUrl || data?.previewUrl, id) ||
    meetingRecordingPublicPageUrl(id);
  const previewUrl = shareUrl;
  const downloadUrl = meetingRecordingBrandedUrl(id, { download: true });
  return {
    streamUrl,
    previewUrl,
    downloadUrl,
    shareUrl,
    title: data?.title || "",
    recordingUrl: data?.recordingUrl || streamUrl,
    recordingExpiresAt: data?.recordingExpiresAt || null,
    recordingRetentionDays: data?.recordingRetentionDays ?? 7,
    recordingEnabledAt: data?.recordingEnabledAt || null,
    recordingFileSizeBytes: data?.recordingFileSizeBytes ?? null,
    recordingFileSize:
      data?.recordingFileSize ||
      (data?.recordingFileSizeBytes != null
        ? formatRecordingFileSize(data.recordingFileSizeBytes)
        : ""),
  };
}

const meetingRecordingModalRef = { open: null };

function openMeetingRecordingModal(meetingOrId, titleHint = "") {
  if (typeof meetingRecordingModalRef.open === "function") {
    meetingRecordingModalRef.open(meetingOrId, titleHint);
    return;
  }
  const id =
    meetingOrId && typeof meetingOrId === "object"
      ? meetingRecordingApiId(meetingOrId)
      : String(meetingOrId || "").trim();
  if (id) {
    const pub = meetingRecordingPublicPageUrl(id);
    window.open(pub || meetingRecordingBrandedUrl(id, { view: true }), "_blank", "noopener,noreferrer");
  }
}

/** Premium header — Avaada logo + meeting title (modal & /recording page). */
function MeetingRecordingBrandHeader({
  title,
  eyebrow = "Meeting record",
  fileSize = "",
  onClose = null,
}) {
  return (
    <div
      style={{
        padding: "16px 20px",
        borderBottom: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        background: "linear-gradient(90deg, rgba(23,120,212,0.08), rgba(12,143,85,0.06))",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, minWidth: 0, flex: 1 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            background: "#fff",
            border: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 4px 16px rgba(12, 43, 78, 0.1)",
          }}
        >
          <img
            src={APP_LOGO}
            alt="Avaada"
            style={{ width: "80%", height: "80%", objectFit: "contain", display: "block" }}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: PALETTE.brandBlue,
              letterSpacing: 0.6,
              textTransform: "uppercase",
            }}
          >
            {eyebrow}
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              color: PALETTE.navy,
              marginTop: 4,
              lineHeight: 1.35,
            }}
          >
            {title}
          </div>
          {fileSize ? (
            <div
              style={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 8,
                background: "rgba(23, 120, 212, 0.1)",
                border: "1px solid rgba(23, 120, 212, 0.22)",
                fontSize: 12,
                fontWeight: 600,
                color: T.textSub,
              }}
            >
              <Svg d={IC.video} size={14} color={PALETTE.brandBlue} stroke={2} />
              <span>
                Video size: <strong style={{ color: PALETTE.navy }}>{fileSize}</strong>
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {typeof onClose === "function" ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            border: `1px solid ${T.border}`,
            background: "#fff",
            borderRadius: 10,
            width: 36,
            height: 36,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Svg d={IC.close} size={16} color={T.textMuted} stroke={2} />
        </button>
      ) : null}
    </div>
  );
}

const meetingRecordingPlayerShellStyle = {
  borderRadius: 14,
  overflow: "hidden",
  background: "#0f172a",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
  minHeight: 280,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const meetingRecordingPrimaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 16px",
  borderRadius: 10,
  border: "none",
  background: `linear-gradient(90deg, ${PALETTE.brandBlue} 0%, ${PALETTE.brandGreen} 100%)`,
  color: "#fff",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const meetingRecordingSecondaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 16px",
  borderRadius: 10,
  border: "1px solid #D6DEE6",
  background: "#fff",
  color: PALETTE.navy,
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const SHARE_SCOPE_META = {
  meeting: { label: "Entire meeting", hint: "Overview + transcript + actions + recording" },
  overview: { label: "Only overview", hint: "Summary & action items" },
  transcript: { label: "Only transcript", hint: "Transcript lines" },
};

function shareScopeLabel(scope) {
  return SHARE_SCOPE_META[scope]?.label || SHARE_SCOPE_META.meeting.label;
}

/** Read-only transcript lines for the public share page. */
function SharedTranscriptLines({ lines }) {
  if (!lines || !lines.length) {
    return (
      <div className="avaada-detail-summary-empty">
        <p>No transcript is available for this shared meeting.</p>
      </div>
    );
  }
  return (
    <div className="avaada-detail-transcript-preview__lines">
      {lines.map((line, idx) => (
        <div
          key={`${line.ord ?? idx}`}
          style={{
            display: "flex",
            gap: 12,
            padding: "10px 14px",
            borderBottom: `1px solid ${T.border}`,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              flexShrink: 0,
              minWidth: 92,
              fontSize: 11.5,
              fontWeight: 700,
              color: T.textMuted,
              paddingTop: 1,
            }}
          >
            {String(line.speaker || "Unknown").trim()}
            {String(line.time || "").trim() ? (
              <span style={{ display: "block", fontWeight: 500, fontSize: 10.5 }}>
                {String(line.time).trim()}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.55, color: T.text, minWidth: 0 }}>
            {String(line.text || "").trim()}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Public shared-meeting page (frontend /shared?s=<shareId>).
 * No login, no account clone — the share id alone unlocks ONLY the scope the
 * sharer picked (entire meeting / overview / transcript). Read-only.
 */
function MeetingSharePublicPage({ shareId, onGoMeetings }) {
  const id = String(shareId || "").trim();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!id) {
      setErr("This share link is invalid — no share id was provided.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr("");
    apiFetch(`/shares/${encodeURIComponent(id)}`)
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.message || "This share link could not be opened.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const shellStyle = {
    minHeight: "100vh",
    background: PALETTE.grey,
    fontFamily: "'DM Sans',system-ui,sans-serif",
    color: T.text,
  };

  if (loading) {
    return (
      <div style={{ ...shellStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 36,
              height: 36,
              border: "3px solid #D6DEE6",
              borderTopColor: PALETTE.navy,
              borderRadius: "50%",
              margin: "0 auto 16px",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Opening shared meeting…</div>
        </div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div style={{ ...shellStyle, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div
          style={{
            maxWidth: 460,
            background: "#fff",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            padding: "28px 26px",
            textAlign: "center",
            boxShadow: T.shadowMd,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: T.accentBg,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 14,
            }}
          >
            <Svg d={IC.share} size={20} color={T.accent} stroke={1.8} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>
            {err ? "This share link is unavailable" : "Shared meeting"}
          </div>
          <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.55, marginBottom: 18 }}>
            {err || "The content could not be loaded."}
          </div>
          <button
            type="button"
            className="avaada-mui-btn avaada-mui-btn--primary"
            onClick={onGoMeetings}
          >
            Go to MeetAI
          </button>
        </div>
      </div>
    );
  }

  const { share, sharedBy, meeting } = data;
  const scope = share?.scope || "meeting";
  const showOverview = scope === "meeting" || scope === "overview";
  const showTranscript = scope === "meeting" || scope === "transcript";
  // Entire meeting = the whole detail page: overview + transcript + action items + recording (if available).
  const showActions = scope === "meeting";
  const showRecording = scope === "meeting" && Boolean(meeting.hasRecording && meeting.recordingStreamPath);
  const recordingSrc = showRecording ? `${API}${meeting.recordingStreamPath}` : "";
  const hasSummary =
    String(meeting.summary || "").trim() ||
    String(meeting.presentationSummary || "").trim() ||
    (meeting.actionItems || []).length > 0;
  const hasTranscript = (meeting.transcript || []).length > 0;

  return (
    <div style={shellStyle}>
      <div
        style={{
          background: "linear-gradient(90deg,#0B2A4A,#123C66)",
          color: "#fff",
          padding: "18px 22px",
        }}
      >
        <div
          style={{
            maxWidth: 860,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Svg d={IC.meet} size={20} color="#fff" stroke={2} />
            <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em" }}>Avaada MeetAI</span>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11.5,
              fontWeight: 700,
              padding: "5px 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            <Svg d={IC.share} size={12} color="#fff" stroke={2} />
            Shared with you · {shareScopeLabel(scope)}
          </span>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "26px 22px 60px" }}>
        <div
          style={{
            background: "#fff",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            boxShadow: T.shadow,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "22px 24px", borderBottom: `1px solid ${T.border}` }}>
            <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.3 }}>
              {meeting.title || "Shared meeting"}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {meeting.date ? (
                <span style={{ fontSize: 12.5, color: T.textMuted, fontWeight: 600 }}>
                  {meeting.date}
                  {meeting.time ? ` · ${meeting.time}` : ""}
                </span>
              ) : null}
              {meeting.platform ? (
                <span style={{ fontSize: 12.5, color: T.textMuted, fontWeight: 600 }}>
                  {String(meeting.platform).toUpperCase()}
                </span>
              ) : null}
            </div>
            <div
              style={{
                marginTop: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12.5,
                fontWeight: 600,
                color: T.textSub,
                background: T.accentBg,
                border: `1px solid ${T.accentMid}`,
                borderRadius: 10,
                padding: "7px 12px",
              }}
            >
              <Avatar
                initials={(sharedBy.name || sharedBy.email || "?")
                  .split(/\s+/)
                  .map((p) => p[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
                size={22}
                color="#0B2A4A"
              />
              Shared by {sharedBy.name || sharedBy.email || "a colleague"}
              {sharedBy.email ? ` · ${sharedBy.email}` : ""}
            </div>
          </div>

          {showOverview ? (
            <div style={{ padding: "20px 24px" }}>
              <div className="avaada-meeting-detail__summary-head" style={{ padding: 0, borderBottom: "none", marginBottom: 12 }}>
                <div className="avaada-meeting-detail__summary-head-main">
                  <span className="avaada-detail-summary-head-icon" aria-hidden>
                    <Svg d={IC.actionList} size={16} color="#0875E1" stroke={2} />
                  </span>
                  <h2>Meeting Overview</h2>
                </div>
              </div>
              {hasSummary ? (
                <MeetingDetailStructuredSummary meeting={meeting} actionItems={meeting.actionItems || []} />
              ) : (
                <div className="avaada-detail-summary-empty">
                  <p>No overview is available for this shared meeting yet.</p>
                </div>
              )}
            </div>
          ) : null}

          {showTranscript ? (
            <div style={{ padding: showOverview ? "0 24px 20px" : "20px 24px" }}>
              <div className="avaada-meeting-detail__summary-head" style={{ padding: 0, borderBottom: "none", marginBottom: 12 }}>
                <div className="avaada-meeting-detail__summary-head-main">
                  <span className="avaada-detail-summary-head-icon" aria-hidden>
                    <Svg d={IC.messageSquare} size={16} color="#0875E1" stroke={2} />
                  </span>
                  <h2>Transcript</h2>
                </div>
              </div>
              <div
                style={{
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  background: T.bg,
                }}
              >
                <SharedTranscriptLines lines={meeting.transcript || []} />
              </div>
            </div>
          ) : null}

          {showActions ? (
            <div style={{ padding: "0 24px 20px" }}>
              <div className="avaada-meeting-detail__summary-head" style={{ padding: 0, borderBottom: "none", marginBottom: 12 }}>
                <div className="avaada-meeting-detail__summary-head-main">
                  <span className="avaada-detail-summary-head-icon" aria-hidden>
                    <Svg d={IC.listChecks} size={16} color="#0875E1" stroke={2} />
                  </span>
                  <h2>Action Items{(meeting.actionItems || []).length ? ` (${(meeting.actionItems || []).length})` : ""}</h2>
                </div>
              </div>
              {(meeting.actionItems || []).length ? (
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                  {(meeting.actionItems || []).map((it, idx) => {
                    const text = String(it?.text || it?.title || it?.description || it?.name || "").trim() || `Action ${idx + 1}`;
                    const meta = [it?.assignee || it?.email, it?.due, it?.status].filter(Boolean).join(" · ");
                    return (
                      <li key={it?.id ?? idx} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", background: T.bg }}>
                        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>{text}</div>
                        {meta ? <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 4 }}>{meta}</div> : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="avaada-detail-summary-empty">
                  <p>No action items were captured for this meeting.</p>
                </div>
              )}
            </div>
          ) : null}

          {showRecording ? (
            <div style={{ padding: "0 24px 20px" }}>
              <div className="avaada-meeting-detail__summary-head" style={{ padding: 0, borderBottom: "none", marginBottom: 12 }}>
                <div className="avaada-meeting-detail__summary-head-main">
                  <span className="avaada-detail-summary-head-icon" aria-hidden>
                    <Svg d={IC.playCircle} size={16} color="#0875E1" stroke={2} />
                  </span>
                  <h2>Recording</h2>
                </div>
              </div>
              <div style={{ borderRadius: 12, overflow: "hidden", background: "#0f172a", border: "1px solid rgba(255,255,255,0.08)" }}>
                <video controls playsInline preload="metadata" src={recordingSrc} style={{ width: "100%", maxHeight: 420, display: "block" }} />
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ textAlign: "center", marginTop: 18, fontSize: 12, color: T.textMuted }}>
          This is a read-only view of content shared with you via Avaada MeetAI. It was not added to your account.
        </div>
      </div>
    </div>
  );
}

/**
 * Share dialog — pick a scope (entire meeting / overview / transcript), search the
 * people directory (ai_public.emp_master) by name or email, add recipients, and
 * share. Creates a share id, emails the link, and offers a copy-link button.
 */
function MeetingShareDialog({ open, onClose, meeting, defaultScope = "meeting", sharerName = "", sharerEmail = "" }) {
  const [scope, setScope] = useState(defaultScope);
  // Single-box recipient picker (same chip UX as Instant Join): selected chips live
  // inside the search input, directory + manual emails handled by the picker.
  const [recipients, setRecipients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shared, setShared] = useState(null);
  const [copyCopied, setCopyCopied] = useState(false);

  // Contextual share choices: transcript tab offers Entire + Only transcript,
  // overview tab offers Entire + Only overview, otherwise show all three.
  const scopeOptions = useMemo(() => {
    const all = [
      { id: "meeting", icon: IC.meet },
      { id: "overview", icon: IC.actionList },
      { id: "transcript", icon: IC.messageSquare },
    ];
    if (defaultScope === "transcript") return all.filter((o) => o.id === "meeting" || o.id === "transcript");
    if (defaultScope === "overview") return all.filter((o) => o.id === "meeting" || o.id === "overview");
    return all;
  }, [defaultScope]);

  useEffect(() => {
    if (open) {
      // Keep the pre-selected scope valid for the tab that opened the dialog.
      setScope((prev) => (scopeOptions.some((o) => o.id === prev) ? prev : defaultScope));
      setRecipients([]);
      setBusy(false);
      setErr("");
      setShared(null);
      setCopyCopied(false);
    }
  }, [open, defaultScope, scopeOptions]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function handleRecipientsChange(next) {
    const seen = new Set();
    const clean = [];
    for (const p of Array.isArray(next) ? next : []) {
      const email = String(p?.email || "").trim().toLowerCase();
      if (!email || !/\S+@\S+\.\S+/.test(email) || seen.has(email)) continue;
      seen.add(email);
      clean.push({
        id: p?.id ?? email,
        email,
        name: String(p?.name || "").trim() || email.split("@")[0],
        role: String(p?.role || "").trim(),
      });
    }
    setRecipients(clean);
  }

  async function doShare() {
    if (!recipients.length || busy) return;
    setBusy(true);
    setErr("");
    try {
      const payload = await apiFetch("/shares", {
        method: "POST",
        body: {
          meetingId: meeting?.id ?? meeting?._id,
          scope,
          recipients: recipients.map((r) => ({ email: r.email, name: r.name })),
        },
      });
      setShared(payload);
    } catch (e) {
      setErr(e?.message || "Could not create the share. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function copyShareLink() {
    const url = shared?.shareUrl || "";
    if (!url) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyCopied(true);
      setTimeout(() => setCopyCopied(false), 2000);
    } catch (_) {
      window.prompt("Copy this share link:", url);
    }
  }

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10050,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Share meeting"
        style={{
          width: 480,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 12px 28px rgba(15,23,42,0.25)",
        }}
      >
        {shared ? (
          <div style={{ padding: "22px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: T.successBg,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Svg d={IC.check} size={17} color={T.success} stroke={2.2} />
              </span>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em" }}>
                Meeting shared
              </div>
            </div>
            <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.55, marginBottom: 16 }}>
              {shared.recipients?.length || recipients.length} recipient
              {(shared.recipients?.length || recipients.length) === 1 ? "" : "s"} will receive an email
              with a button to open the shared {shareScopeLabel(shared.scope || scope).toLowerCase()}.
            </div>

            <div
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                overflow: "hidden",
                display: "flex",
                alignItems: "stretch",
              }}
            >
              <input
                readOnly
                value={shared.shareUrl || ""}
                onFocus={(e) => e.target.select()}
                aria-label="Share link"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  padding: "10px 12px",
                  fontSize: 12.5,
                  color: T.textSub,
                  background: T.surface,
                }}
              />
              <button
                type="button"
                onClick={copyShareLink}
                title={copyCopied ? "Copied!" : "Copy share link"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "0 14px",
                  border: "none",
                  borderLeft: `1px solid ${T.border}`,
                  background: copyCopied ? T.successBg : "#fff",
                  color: copyCopied ? T.success : T.text,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <Svg d={copyCopied ? IC.check : IC.copy} size={14} color="currentColor" stroke={2} />
                {copyCopied ? "Copied" : "Copy"}
              </button>
            </div>

            <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(shared.recipients || recipients).map((r) => (
                <span
                  key={r.email}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.textSub,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 999,
                    padding: "4px 10px",
                  }}
                >
                  <Svg
                    d={r.status === "failed" ? IC.close : IC.check}
                    size={11}
                    color={r.status === "failed" ? "#B91C1C" : T.success}
                    stroke={2.2}
                  />
                  {r.name || r.email}
                </span>
              ))}
            </div>
            {(shared.recipients || []).some((r) => r.status === "failed") ? (
              <div style={{ marginTop: 10, fontSize: 12, color: "#B91C1C" }}>
                Some invitation emails could not be sent — recipients can still open the link above.
              </div>
            ) : null}

            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="avaada-mui-btn avaada-mui-btn--outlined"
                onClick={() => setShared(null)}
              >
                Share with more people
              </button>
              <button type="button" className="avaada-mui-btn avaada-mui-btn--primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em" }}>Share meeting</div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: T.textMuted,
                  display: "inline-flex",
                  padding: 4,
                }}
              >
                <Svg d={IC.close} size={18} color="currentColor" stroke={2} />
              </button>
            </div>
            <div style={{ fontSize: 12.5, color: T.textMuted, marginBottom: 16 }}>
              {meeting?.title || "Untitled meeting"}
            </div>

            <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: T.textMuted, marginBottom: 8 }}>
              What do you want to share?
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
              {scopeOptions.map((opt) => {
                const active = scope === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setScope(opt.id)}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                      padding: "12px 8px",
                      borderRadius: 10,
                      border: `1.5px solid ${active ? T.accent : T.border}`,
                      background: active ? T.accentBg : "#fff",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <Svg d={opt.icon} size={18} color={active ? T.accent : T.textMuted} stroke={1.9} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: active ? T.accent : T.textSub }}>
                      {SHARE_SCOPE_META[opt.id].label}
                    </span>
                    <span style={{ fontSize: 10.5, color: T.textMuted, lineHeight: 1.35 }}>
                      {SHARE_SCOPE_META[opt.id].hint}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: T.textMuted, marginBottom: 8 }}>
              Who do you want to share with?
            </div>
            {/* Single search box with inline chips (same UX as Instant Join) — directory
                search, manual emails, and selected chips all live in one field. */}
            <EmpParticipantChipPicker
              selected={recipients}
              onChange={handleRecipientsChange}
              excludeEmails={sharerEmail ? [sharerEmail] : []}
              directoryHint="Search by name or email — select to add as a chip. You can also paste a full email address."
            />

            {err ? (
              <div style={{ marginTop: 12, fontSize: 12.5, color: "#B91C1C", fontWeight: 600 }}>{err}</div>
            ) : null}

            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="avaada-mui-btn avaada-mui-btn--outlined" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="avaada-mui-btn avaada-mui-btn--primary"
                onClick={doShare}
                disabled={!recipients.length || busy}
              >
                {busy ? "Sharing…" : `Share with ${recipients.length || ""} ${recipients.length === 1 ? "person" : "people"}`.trim()}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Full-page shared recording view (frontend /recording?m=…) — premium MeetAI UI. */
function MeetingRecordingSharePreviewPage({ meetingId, onGoMeetings }) {
  const apiId = String(meetingId || "").trim();
  const [title, setTitle] = useState("Meeting recording");
  const [streamUrl, setStreamUrl] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [retentionMeta, setRetentionMeta] = useState(null);
  const [recordingFileSize, setRecordingFileSize] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dlBusy, setDlBusy] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!apiId) {
      setErr("Meeting id is missing from this link.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr("");
    Promise.all([
      apiFetch(`/meetings/${encodeURIComponent(apiId)}`).catch(() => null),
      fetchMeetingRecordingLinks(apiId),
    ])
      .then(([meeting, links]) => {
        if (cancelled) return;
        const t = String(meeting?.title || links?.title || "Meeting recording").trim();
        setTitle(t);
        setStreamUrl(links.streamUrl);
        setDownloadUrl(links.downloadUrl);
        setRecordingFileSize(links.recordingFileSize || "");
        setRetentionMeta(
          normalizeMeeting({
            ...(meeting && typeof meeting === "object" ? meeting : {}),
            id: meeting?.id || apiId,
            recordingExpiresAt: links.recordingExpiresAt,
            recordingRetentionDays: links.recordingRetentionDays,
            recordingEnabledAt: links.recordingEnabledAt,
            recordingS3Key: "stored",
          })
        );
      })
      .catch(async (e) => {
        if (cancelled) return;
        let meeting = null;
        try {
          meeting = await apiFetch(`/meetings/${encodeURIComponent(apiId)}`);
        } catch (_) {
          /* meeting meta optional for message */
        }
        const meta = normalizeMeeting({
          ...(meeting && typeof meeting === "object" ? meeting : {}),
          id: meeting?.id || apiId,
          recordingS3Key: meeting?.recording_s3_key || meeting?.recordingS3Key || "",
          recordingExpiresAt: meeting?.recording_expires_at || meeting?.recordingExpiresAt,
          recordingRetentionDays:
            meeting?.recording_retention_days ?? meeting?.recordingRetentionDays,
        });
        setRetentionMeta(meta);
        setErr(meetingRecordingUnavailableMessage(meta, e?.message));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiId]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const onDownload = async () => {
    if (!downloadUrl || dlBusy) return;
    setDlBusy(true);
    try {
      const token = getToken();
      const res = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${title.replace(/[^\w\s-]+/g, "").trim() || "meeting"}-recording.mp4`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(e?.message || "Could not download recording.");
    } finally {
      setDlBusy(false);
    }
  };

  const unavailableMsg =
    err ||
    (retentionMeta && meetingRecordingDaysLeft(retentionMeta) <= 0 && !meetingRecordingHasStoredFile(retentionMeta)
      ? meetingRecordingUnavailableMessage(retentionMeta)
      : "");

  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: "'DM Sans',system-ui,sans-serif",
        background: "linear-gradient(160deg, #EBF0F7 0%, #F2F5F9 30%, #F8FAFC 100%)",
        padding: "24px 16px 40px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "min(920px, 100%)",
          borderRadius: 18,
          overflow: "hidden",
          background: "linear-gradient(165deg, #ffffff 0%, #f0f6fc 55%, #e8f0fa 100%)",
          border: "1px solid rgba(23, 120, 212, 0.22)",
          boxShadow: "0 28px 80px rgba(12, 43, 78, 0.18)",
        }}
      >
        <MeetingRecordingBrandHeader
          title={loading ? "Loading…" : title}
          eyebrow="Meeting recording preview"
          fileSize={!loading && !err ? recordingFileSize : ""}
        />
        <div style={{ padding: 18 }}>
          {retentionMeta ? (
            <div style={{ marginBottom: 14 }}>
              <MeetingRecordingRetentionNotice meetingMeta={retentionMeta} nowMs={nowTick} />
            </div>
          ) : null}
          <div style={meetingRecordingPlayerShellStyle}>
            {loading ? (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading video…</div>
            ) : err || unavailableMsg ? (
              <div style={{ padding: "28px 24px", textAlign: "center", maxWidth: 520, margin: "0 auto" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>
                  Recording unavailable
                </div>
                <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6 }}>{err || unavailableMsg}</div>
              </div>
            ) : (
              <video
                key={streamUrl}
                controls
                playsInline
                preload="auto"
                src={streamUrl}
                style={{ width: "100%", maxHeight: "min(72vh, 640px)", display: "block" }}
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 16,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onGoMeetings}
              style={meetingRecordingSecondaryBtnStyle}
            >
              <Svg d={IC.meet} size={16} color={PALETTE.brandBlue} stroke={2} />
              Open MeetAI
            </button>
            <button
              type="button"
              onClick={onDownload}
              disabled={!downloadUrl || loading || !!err || !!unavailableMsg || dlBusy}
              style={{
                ...meetingRecordingPrimaryBtnStyle,
                cursor: downloadUrl && !loading && !err && !dlBusy ? "pointer" : "not-allowed",
                opacity: downloadUrl && !loading && !err && !dlBusy ? 1 : 0.55,
              }}
            >
              <Svg d={IC.download} size={16} color="#fff" stroke={2} />
              {dlBusy ? "Downloading…" : "Download"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Meeting detail Recording tab — inline player (no modal). */
function MeetingRecordingInlinePanel({ meeting }) {
  const m = normalizeMeeting(meeting || {});
  const apiId = meetingRecordingApiId(m);
  const title = String(m.title || "Meeting recording").trim();
  const recordingReady = meetingRecordingFileReady(m, { requireAutoJoin: false });
  const recordingPending = meetingRecordingAwaitingFile(m, { requireAutoJoin: false });
  const showRecording = meetingRecordingStoredOn(m) && (recordingReady || recordingPending);

  const [streamUrl, setStreamUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [retentionMeta, setRetentionMeta] = useState(m);
  const [recordingFileSize, setRecordingFileSize] = useState("");
  const [loading, setLoading] = useState(Boolean(recordingReady && apiId));
  const [err, setErr] = useState("");
  const [dlBusy, setDlBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!recordingReady || !apiId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr("");
    fetchMeetingRecordingLinks(apiId)
      .then((links) => {
        if (cancelled) return;
        setStreamUrl(links.streamUrl);
        setPreviewUrl(links.previewUrl);
        setShareUrl(links.shareUrl || links.previewUrl);
        setDownloadUrl(links.downloadUrl);
        setRecordingFileSize(
          links.recordingFileSize ||
            (links.recordingFileSizeBytes != null
              ? formatRecordingFileSize(links.recordingFileSizeBytes)
              : "")
        );
        setRetentionMeta((prev) =>
          normalizeMeeting({
            ...(prev && typeof prev === "object" ? prev : {}),
            recordingExpiresAt: links.recordingExpiresAt || prev?.recordingExpiresAt,
            recordingRetentionDays: links.recordingRetentionDays ?? prev?.recordingRetentionDays,
            recordingEnabledAt: links.recordingEnabledAt || prev?.recordingEnabledAt,
            recordingS3Key: prev?.recordingS3Key || "stored",
          })
        );
      })
      .catch((e) => {
        if (!cancelled) setErr(meetingRecordingUnavailableMessage(m, e?.message));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiId, recordingReady]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const onDownload = async () => {
    if (!downloadUrl || dlBusy) return;
    setDlBusy(true);
    try {
      const token = getToken();
      const res = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${title.replace(/[^\w\s-]+/g, "").trim() || "meeting"}-recording.mp4`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(e?.message || "Could not download recording.");
    } finally {
      setDlBusy(false);
    }
  };

  const onShare = async () => {
    const url = shareUrl || previewUrl || meetingRecordingPublicPageUrl(apiId);
    if (!url || loading || err) return;
    const shareTitle = `${title} — Meeting recording`;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: shareTitle, text: shareTitle, url });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
        return;
      }
    } catch (_) {
      /* fallback below */
    }
    window.prompt("Copy this recording link:", url);
  };

  if (!showRecording) {
    return (
      <p className="avaada-detail-recording-empty">
        {meetingRecordingStatusLine(m)}
      </p>
    );
  }

  if (recordingPending && !recordingReady) {
    return (
      <p className="avaada-detail-recording-empty">
        The video is still being saved. It usually shows up a few minutes after the meeting ends. This page updates
        on its own.
      </p>
    );
  }

  return (
    <div className="avaada-detail-recording-inline">
      <p className="avaada-detail-recording-empty">{meetingRecordingStatusLine(m)}</p>
      {recordingFileSize ? (
        <p className="avaada-detail-recording-meta">
          <Svg d={IC.video} size={14} color="#0875e1" stroke={2} />
          Video size: <strong>{recordingFileSize}</strong>
        </p>
      ) : null}
      {retentionMeta ? (
        <div className="avaada-detail-recording-retention">
          <MeetingRecordingRetentionNotice meetingMeta={retentionMeta} nowMs={nowTick} stack />
        </div>
      ) : null}
      <div className="avaada-detail-recording-player" style={meetingRecordingPlayerShellStyle}>
        {loading ? (
          <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading video…</div>
        ) : err ? (
          <div style={{ padding: "28px 24px", textAlign: "center", maxWidth: 520, margin: "0 auto" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>
              Recording unavailable
            </div>
            <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6 }}>{err}</div>
          </div>
        ) : (
          <video
            key={streamUrl}
            controls
            playsInline
            preload="auto"
            src={streamUrl}
            className="avaada-detail-recording-video"
          />
        )}
      </div>
      <div className="avaada-detail-recording-actions">
        <button
          type="button"
          className="avaada-mui-btn avaada-mui-btn--outlined"
          onClick={onShare}
          disabled={(!shareUrl && !previewUrl && !apiId) || loading || !!err}
        >
          <Svg d={shareCopied ? IC.check : IC.share} size={14} color="currentColor" stroke={2} />
          {shareCopied ? "Copied!" : "Share link"}
        </button>
        <button
          type="button"
          className="avaada-mui-btn avaada-mui-btn--primary"
          onClick={onDownload}
          disabled={!downloadUrl || loading || !!err || dlBusy}
        >
          <Svg d={IC.download} size={14} color="currentColor" stroke={2} />
          {dlBusy ? "Downloading…" : "Download recording"}
        </button>
      </div>
    </div>
  );
}

function MeetingRecordingPremiumModal({ payload, onClose }) {
  const apiId = String(payload?.apiId || payload?.meetingId || "").trim();
  const title = String(payload?.title || "Meeting recording").trim();
  const [streamUrl, setStreamUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [retentionMeta, setRetentionMeta] = useState(() => payload?.meeting || null);
  const [recordingFileSize, setRecordingFileSize] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dlBusy, setDlBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!apiId) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    fetchMeetingRecordingLinks(apiId)
      .then((links) => {
        if (cancelled) return;
        setStreamUrl(links.streamUrl);
        setPreviewUrl(links.previewUrl);
        setShareUrl(links.shareUrl || links.previewUrl);
        setDownloadUrl(links.downloadUrl);
        setRecordingFileSize(
          links.recordingFileSize ||
            (links.recordingFileSizeBytes != null
              ? formatRecordingFileSize(links.recordingFileSizeBytes)
              : "")
        );
        setRetentionMeta((prev) =>
          normalizeMeeting({
            ...(prev && typeof prev === "object" ? prev : {}),
            recordingExpiresAt: links.recordingExpiresAt || prev?.recordingExpiresAt,
            recordingRetentionDays: links.recordingRetentionDays ?? prev?.recordingRetentionDays,
            recordingEnabledAt: links.recordingEnabledAt || prev?.recordingEnabledAt,
            recordingS3Key: prev?.recordingS3Key || "stored",
          })
        );
      })
      .catch((e) => {
        if (!cancelled) {
          const errMeta =
            retentionMeta ||
            (payload?.meeting && typeof payload.meeting === "object" ? payload.meeting : null) ||
            { recordingRetentionDays: 7, recordingS3Key: "" };
          setErr(meetingRecordingUnavailableMessage(errMeta, e?.message));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const onDownload = async () => {
    if (!downloadUrl || dlBusy) return;
    setDlBusy(true);
    try {
      const token = getToken();
      const res = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${title.replace(/[^\w\s-]+/g, "").trim() || "meeting"}-recording.mp4`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(e?.message || "Could not download recording.");
    } finally {
      setDlBusy(false);
    }
  };

  const onShare = async () => {
    const url =
      shareUrl ||
      previewUrl ||
      meetingRecordingPublicPageUrl(apiId);
    if (!url || loading || err) return;
    const shareTitle = `${title} — Meeting recording`;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: shareTitle, text: shareTitle, url });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
        return;
      }
    } catch (_) {
      /* fallback below */
    }
    window.prompt("Copy this recording link:", url);
  };

  if (!payload) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Meeting recording: ${title}`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        background: "rgba(12, 43, 78, 0.55)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(920px, 100%)",
          maxHeight: "min(92vh, 900px)",
          borderRadius: 18,
          overflow: "hidden",
          background: "linear-gradient(165deg, #ffffff 0%, #f0f6fc 55%, #e8f0fa 100%)",
          border: "1px solid rgba(23, 120, 212, 0.22)",
          boxShadow: "0 28px 80px rgba(12, 43, 78, 0.35)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MeetingRecordingBrandHeader
          title={title}
          eyebrow="Meeting record"
          fileSize={!loading && !err ? recordingFileSize : ""}
          onClose={onClose}
        />

        <div style={{ padding: 18, flex: 1, overflow: "auto" }}>
          {retentionMeta ? (
            <div style={{ marginBottom: 14 }}>
              <MeetingRecordingRetentionNotice meetingMeta={retentionMeta} nowMs={nowTick} />
            </div>
          ) : null}
          <div style={meetingRecordingPlayerShellStyle}>
            {loading ? (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading video…</div>
            ) : err ? (
              <div style={{ padding: "28px 24px", textAlign: "center", maxWidth: 520, margin: "0 auto" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>
                  Recording unavailable
                </div>
                <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6 }}>{err}</div>
              </div>
            ) : (
              <video
                key={streamUrl}
                controls
                playsInline
                preload="auto"
                src={streamUrl}
                style={{ width: "100%", maxHeight: "52vh", display: "block" }}
              />
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 16,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={onShare}
                disabled={(!shareUrl && !previewUrl && !apiId) || loading || !!err}
                title="Copy recording preview link"
                style={{
                  ...meetingRecordingSecondaryBtnStyle,
                  border: `1px solid ${shareCopied ? "#86efac" : T.border}`,
                  background: shareCopied ? "#f0fdf4" : "#fff",
                  color: shareCopied ? "#166534" : PALETTE.navy,
                  cursor: !loading && !err ? "pointer" : "not-allowed",
                  opacity: !loading && !err ? 1 : 0.55,
                }}
              >
                <Svg
                  d={shareCopied ? IC.check : IC.share}
                  size={16}
                  color={shareCopied ? "#166534" : PALETTE.brandBlue}
                  stroke={2}
                />
                {shareCopied ? "Copied!" : "Share"}
              </button>
              <button
                type="button"
                onClick={onDownload}
                disabled={!downloadUrl || loading || !!err || dlBusy}
                title="Download MP4"
                style={{
                  ...meetingRecordingPrimaryBtnStyle,
                  cursor: downloadUrl && !loading && !err && !dlBusy ? "pointer" : "not-allowed",
                  opacity: downloadUrl && !loading && !err && !dlBusy ? 1 : 0.55,
                }}
              >
                <Svg d={IC.download} size={16} color="#fff" stroke={2} />
                {dlBusy ? "Downloading…" : "Download"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Recorded Meetings tab — Video (when ready), then Summary. */
function MeetingTranscriptActions({ m, layout = "row", onOpen }) {
  const isListLayout = layout === "list";
  const isCardLayout = layout === "card" || layout === "column" || isListLayout;
  const st = effectiveMeetingStatus(m);
  const hasData = meetingHasTranscriptData(m);
  const liveReady = meetingLiveTranscriptReady(m);
  const videoReady = meetingRecordingFileReady(m, { requireAutoJoin: false });
  const label =
    st === "live"
      ? hasData
        ? "View Live Transcript"
        : liveReady
          ? "Open Live Meeting"
          : meetingMeetAiIsJoining(m)
            ? "Open Meeting (joining)"
            : "Open Meeting"
      : "Open Meeting Summary";
  // List rows show a compact pill (icon + "Summary"); the full action text
  // appears on hover/focus via CSS (.meet-summary-btn).
  const shortLabel = st === "live" ? "Live" : "Summary";
  const openSummary = (e) => {
    e.stopPropagation();
    onOpen?.(m, st === "live" ? undefined : { preferTab: "transcript" });
  };
  const openVideo = (e) => {
    e.stopPropagation();
    onOpen?.(m, { preferTab: "recording" });
  };

  const videoBtn = videoReady ? (
    <button
      type="button"
      className="meet-video-btn"
      onClick={openVideo}
      title="Open meeting video"
      aria-label={`Open video for ${m?.title || "meeting"}`}
      style={!isListLayout && isCardLayout ? { width: "auto", justifyContent: "center" } : undefined}
    >
      <Svg d={IC.play} size={13} color="currentColor" stroke={2} />
      Video
    </button>
  ) : null;

  return (
    <div
      className={isListLayout ? "meet-list-transcript-actions" : isCardLayout ? "mcard-pro__actions" : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isListLayout ? "flex-end" : isCardLayout ? "flex-start" : "flex-end",
        flexWrap: isListLayout ? "nowrap" : "wrap",
        gap: isListLayout ? 6 : 8,
        width: isListLayout ? "100%" : isCardLayout ? "auto" : undefined,
        flex: isCardLayout ? "1 1 auto" : undefined,
        minWidth: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {isListLayout ? (
        <>
          {videoBtn}
          <button
            type="button"
            className="meet-summary-btn"
            onClick={openSummary}
            title={label}
            aria-label={`${label} for ${m?.title || "meeting"}`}
          >
            <Svg d={IC.actionList} size={13} color="currentColor" stroke={2} />
            <span className="meet-summary-short">{shortLabel}</span>
            <span className="meet-summary-full">{label}</span>
          </button>
        </>
      ) : (
      <>
      {videoBtn}
      <button
        type="button"
        onClick={openSummary}
        title={
          st === "live" && !hasData
            ? "Meeting is live — transcript lines appear after MeetAI joins and people speak"
            : "Open meeting summary"
        }
        className={isCardLayout ? "mcard-pro__summary-link" : undefined}
        style={{
          padding: isCardLayout ? "0" : "5px 6px",
          borderRadius: 8,
          border: "1px solid transparent",
          background: "transparent",
          color: PALETTE.brandBlue,
          fontSize: isCardLayout ? 12 : 11.5,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          width: isCardLayout ? "auto" : undefined,
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: isCardLayout ? "flex-start" : "center",
          gap: 4,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {label}
        </span>
        <Svg d={IC.chevR} size={12} color="currentColor" stroke={2} />
      </button>
      </>
      )}
    </div>
  );
}

/**
 * All Meetings list/grid — AUTO JOIN cell for completed/ended meetings.
 * Replaces the "—" dash with an actionable outcome:
 * - "Open Transcript" button (navigates to meeting detail/transcript) when transcript exists
 * - "MeetAI Joined" pill when the bot was in the call
 * - "MeetAI Not Joined" pill otherwise
 */
function CompletedAutoJoinCell({ m, onOpen, layout = "list" }) {
  const info = allMeetingsCompletedAutoJoinInfo(m);
  const isList = layout === "list";
  const open = (e, extra) => {
    if (e) e.stopPropagation();
    onOpen?.(m, extra);
  };
  if (info.kind === "open-transcript") {
    return (
      <button
        type="button"
        onClick={(e) => open(e, { preferTab: "transcript" })}
        title="Open meeting transcript"
        aria-label={`Open transcript for ${m?.title || "meeting"}`}
        className="meet-autojoin-outcome is-transcript"
        style={{
          padding: isList ? "4px 10px" : "6px 12px",
          borderRadius: 999,
          border: "1px solid #BFDBFE",
          background: "#EFF6FF",
          color: "#1D4ED8",
          fontSize: isList ? 11 : 11.5,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          maxWidth: "100%",
        }}
      >
        Open Transcript
        <Svg d={IC.chevR} size={12} color="currentColor" stroke={2.5} />
      </button>
    );
  }
  const joined = info.kind !== "not-joined";
  if (!joined) {
    return (
      <button
        type="button"
        onClick={(e) => open(e)}
        title="MeetAI did not join this meeting. Open for details."
        aria-label={`MeetAI did not join ${m?.title || "this meeting"}`}
        className="meet-autojoin-outcome is-not-joined"
        style={{
          padding: isList ? "3px 9px" : "5px 11px",
          borderRadius: 999,
          border: "1px solid #E2E8F0",
          background: "#F8FAFC",
          color: "#64748B",
          fontSize: isList ? 10.5 : 11,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <MeetAiNotJoinedIcon size={14} color="#94A3B8" />
        MeetAI Not Joined
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => open(e)}
      title="MeetAI joined this meeting — open details"
      aria-label={`${info.label} for ${m?.title || "meeting"}`}
      className="meet-autojoin-outcome is-joined"
      style={{
        padding: isList ? "3px 9px" : "5px 11px",
        borderRadius: 999,
        border: joined ? "1px solid #86EFAC" : "1px solid #E2E8F0",
        background: joined ? "#F0FDF4" : "#F8FAFC",
        color: joined ? "#15803D" : "#64748B",
        fontSize: isList ? 10.5 : 11,
        fontWeight: 700,
        cursor: "pointer",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        maxWidth: "100%",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: joined ? "#16A34A" : "#94A3B8",
          flexShrink: 0,
        }}
      />
      {info.label}
    </button>
  );
}

/**
 * All Meetings list — VIDEO REC column for completed/ended meetings.
 */
function allMeetingsCompletedVideoRecInfo(m) {
  if (!meetingRecordingStoredOn(m)) {
    return { kind: "none" };
  }
  if (meetingRecordingFileReady(m, { requireAutoJoin: false })) {
    return { kind: "ready", label: "Video saved" };
  }
  const daysLeft = meetingRecordingDaysLeft(m);
  if (daysLeft != null && daysLeft <= 0 && !meetingRecordingHasStoredFile(m)) {
    return { kind: "expired", label: "Recording expired" };
  }
  const endedMs = meetingEndedAtMs(m);
  const stillSaving = !endedMs || Date.now() - endedMs < 2 * 60 * 60 * 1000;
  if (meetingRecordingAwaitingFile(m, { requireAutoJoin: false }) && stillSaving) {
    return { kind: "processing", label: "Processing…" };
  }
  return { kind: "unavailable", label: "Recording not available" };
}

/** Person with an X — MeetAI was invited but did not join the call. */
function MeetAiNotJoinedIcon({ size = 14, color = "#94a3b8" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className="meet-ref-actions__not-joined-icon"
    >
      <path
        d="M15 19.5v-1.2a3.8 3.8 0 0 0-3.8-3.8H6.8A3.8 3.8 0 0 0 3 18.3v1.2"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="7.2" r="3" stroke={color} strokeWidth="2" />
      <path d="M17 9.5l4.5 4.5M21.5 9.5l-4.5 4.5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Reference All Meetings — ACTIONS column (outline / ghost controls only). */
function MeetingReferenceActions({
  m,
  onOpen,
  showAutoJoinToggle = false,
  meetingAutoJoinEnabled = false,
  meetingToggleSaving = false,
  onToggleMeetingAutoJoin,
  showRecordingToggle = false,
  meetingRecordingEnabled = false,
  meetingRecordingSaving = false,
  onToggleMeetingRecording,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
}) {
  const { t } = useI18n();
  const status = effectiveMeetingStatus(m);
  const autoJoinInfo = allMeetingsCompletedAutoJoinInfo(m);
  const videoInfo = allMeetingsCompletedVideoRecInfo(m);
  const toggleReadOnlyTitle = meetingSettingsReadOnly ? meetingSettingsReadOnlyHint : "";
  const ended = isEndedMeetingStatus(status, m);
  const botJoined = meetingBotJoinedCall(m);
  const showRecording = ended && videoInfo.kind === "ready";
  const meetAiParticipated =
    botJoined ||
    showRecording ||
    videoInfo.kind === "processing" ||
    autoJoinInfo.kind === "open-transcript" ||
    autoJoinInfo.kind === "joined";
  const showMeetAiNotJoined = ended && !meetAiParticipated;
  const showTranscript = ended && !showMeetAiNotJoined && meetingListTranscriptActionVisible(m);

  const showToggleChip = showAutoJoinToggle || showRecordingToggle;
  const stackOutcome = showTranscript && showRecording;

  return (
    <div
      className={`meet-ref-actions${stackOutcome ? " meet-ref-actions--stack" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="meet-ref-actions__controls">
        {showMeetAiNotJoined ? (
          <span
            className="meet-ref-actions__chip meet-ref-actions__chip--not-joined"
            title="MeetAI did not join this meeting. Open for details."
            aria-label="MeetAI Not Joined"
          >
            <MeetAiNotJoinedIcon size={14} color="#94A3B8" />
            MeetAI Not Joined
          </span>
        ) : null}
        {showTranscript ? (
          <button
            type="button"
            className="meet-ref-actions__transcript"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(m, { preferTab: "transcript" });
            }}
            aria-label={`Open transcript for ${m?.title || "meeting"}`}
          >
            <Svg d={IC.messageSquare} size={12} color="currentColor" stroke={2} aria-hidden />
            Transcript
          </button>
        ) : null}
        {showRecording ? (
          <button
            type="button"
            className="meet-ref-actions__chip meet-ref-actions__chip--saved"
            title="Video saved. Open to play or download."
            aria-label="Video saved. Open to play or download."
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(m, { preferTab: "recording" });
            }}
          >
            <Svg d={IC.play} size={11} color="currentColor" stroke={2} aria-hidden />
            Video saved
          </button>
        ) : null}
        {showToggleChip ? (
          <div
            className="meet-ref-actions__toggles-plain"
            onClick={(e) => e.stopPropagation()}
          >
            {showAutoJoinToggle ? (
              <span
                className={`meet-ref-actions__toggle meet-ref-actions__toggle--compact meet-toggle-chip${
                  meetingAutoJoinEnabled ? " is-on" : ""
                }${meetingToggleSaving || meetingSettingsReadOnly ? " is-disabled" : ""}`}
              >
                <ToggleBar
                  checked={!!meetingAutoJoinEnabled}
                  disabled={!!meetingToggleSaving || meetingSettingsReadOnly}
                  showLabel
                  label={JOIN_MEET_AI_LABEL}
                  switchWidth={24}
                  switchHeight={14}
                  thumbSize={10}
                  title={toggleReadOnlyTitle || JOIN_MEET_AI_TOGGLE_HINT}
                  onChange={(next) => onToggleMeetingAutoJoin?.(next)}
                  stopPropagation
                  tipHorizontal="center"
                  tipPrefer="below"
                  tipMaxWidth={220}
                />
              </span>
            ) : null}
            {showRecordingToggle ? (
              <span
                className={`meet-ref-actions__toggle meet-ref-actions__toggle--compact meet-toggle-chip${
                  meetingAutoJoinEnabled && meetingRecordingEnabled ? " is-on" : ""
                }${
                  meetingRecordingSaving || !meetingAutoJoinEnabled || meetingSettingsReadOnly
                    ? " is-disabled"
                    : ""
                }`}
              >
                <ToggleBar
                  checked={!!meetingAutoJoinEnabled && !!meetingRecordingEnabled}
                  disabled={
                    !!meetingRecordingSaving || !meetingAutoJoinEnabled || meetingSettingsReadOnly
                  }
                  showLabel
                  label="Video Rec"
                  switchWidth={24}
                  switchHeight={14}
                  thumbSize={10}
                  title={
                    toggleReadOnlyTitle ||
                    (!meetingAutoJoinEnabled
                      ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                      : VIDEO_REC_ENABLE_HINT_COMPACT)
                  }
                  onChange={(next) => {
                    if (next && !meetingAutoJoinEnabled) return;
                    onToggleMeetingRecording?.(next);
                  }}
                  stopPropagation
                  tipHorizontal="center"
                  tipPrefer="below"
                  tipMaxWidth={220}
                />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const INSTANT_JOIN_MEETING_HINT =
  "Instant join — you pasted a live meeting link to record (not a Google Calendar invite).";

function meetingIsInstantJoin(m) {
  return String(m?.type || "").toLowerCase() === "instant";
}

/** Keep instant-join rows from being re-labelled as calendar meetings during merges. */
function mergeMeetingKindType(existing, incoming) {
  const exType = String(existing?.type || "").toLowerCase();
  const inType = String(incoming?.type || "").toLowerCase();
  const exGid = extractGoogleEventId(existing);
  const inGid = extractGoogleEventId(incoming);
  if (meetingIsInstantJoin(existing) && !exGid) return "instant";
  if (exType === "google_calendar" || inType === "google_calendar") {
    if (exGid || inGid) return "google_calendar";
  }
  if (inType === "google_calendar") return "google_calendar";
  return existing?.type || incoming?.type;
}

function meetingOccurrenceKey(m) {
  const slot = meetingGidSlotKey(m, false);
  if (slot) return slot;
  const gid = extractGoogleEventId(m);
  const fromGid = gid ? scheduleFromGoogleEventId(gid) : null;
  if (fromGid?.date && fromGid?.time) {
    return `g:${gid}|${fromGid.date}|${normalizeMeetingTimeForDedupe(fromGid.time)}`;
  }
  const id = String(m?.id ?? m?._id ?? "").trim();
  return id ? `id:${id}` : null;
}

/** Platform mark (Meet / Zoom / Teams) + small instant-join badge when type is instant. */
function MeetListRowPlatformIcon({ meeting, referenceListLayout = false, detail = false }) {
  const size = detail ? 22 : referenceListLayout ? 20 : 18;
  const platform = meeting?.platform || "meet";
  const isInstant = meetingIsInstantJoin(meeting);
  const platformIcon = (
    <MeetingListPlatformIcon platform={platform} size={size} />
  );
  if (!isInstant) return platformIcon;
  const badgeSize = detail ? 9 : referenceListLayout ? 8 : 7;
  return (
    <span
      className={`meet-instant-join-icon-wrap${
        referenceListLayout ? " meet-instant-join-icon-wrap--reference" : ""
      }${detail ? " meet-instant-join-icon-wrap--detail" : ""}`}
      title={INSTANT_JOIN_MEETING_HINT}
      aria-label="Instant join meeting"
    >
      {platformIcon}
      <span className="meet-instant-join-icon-badge" aria-hidden>
        <Svg d={IC.zap} size={badgeSize} color="#FFFFFF" stroke={2.4} />
      </span>
    </span>
  );
}

function InstantJoinLabelChip({ compact = false }) {
  return (
    <span
      className={`meet-instant-join-chip${compact ? " meet-instant-join-chip--compact" : ""}`}
      title={INSTANT_JOIN_MEETING_HINT}
    >
      <Svg d={IC.zap} size={compact ? 10 : 11} color="currentColor" stroke={2.3} aria-hidden />
      Instant join
    </span>
  );
}

/** Avatar stack + hover menu listing everyone (All Meetings / Recorded / My Tasks). */
/* Participants column avatars show a single initial letter (no photo → one letter, same avatar font). */
function singleInitial(value) {
  const ch = String(value || "?").trim().charAt(0) || "?";
  return ch.toUpperCase();
}

function MeetListParticipantStack({
  participants,
  meeting,
  people: peopleProp = null,
  maxShow = 3,
  menuTitle = "Participants",
  countNoun = "participant",
  showCountLabel = true,
  avatarSize = 22,
  className = "",
  emptyClassName = "meet-ref-participant-empty",
}) {
  const ctx = attendanceContextFromMeeting(meeting);
  let allPeople = Array.isArray(peopleProp) && peopleProp.length ? peopleProp : null;
  if (!allPeople) {
    const { joined, notJoined } = splitJoinedVsNotJoinedInvitees(participants || [], ctx);
    allPeople = [...joined, ...notJoined];
    if (!allPeople.length) allPeople = displayUserProfiles(participants || []);
  }
  const people = allPeople.slice(0, maxShow);
  const total = allPeople.length;
  const extra = Math.max(0, total - people.length);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const hideTimerRef = useRef(null);

  const clearHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const showMenu = () => {
    clearHide();
    const el = anchorRef.current;
    if (!el || typeof window === "undefined") return;
    const r = el.getBoundingClientRect();
    const menuW = 220;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - menuW - 8));
    const spaceBelow = window.innerHeight - r.bottom;
    const placeAbove = spaceBelow < 220 && r.top > spaceBelow;
    setPos({
      left,
      top: placeAbove ? undefined : r.bottom + 8,
      bottom: placeAbove ? window.innerHeight - r.top + 8 : undefined,
      place: placeAbove ? "above" : "below",
    });
    setOpen(true);
  };

  const hideMenuSoon = () => {
    clearHide();
    hideTimerRef.current = setTimeout(() => setOpen(false), 140);
  };

  useEffect(() => () => clearHide(), []);

  if (total === 0) {
    return <span className={emptyClassName}>—</span>;
  }

  const countLabel = `${total} ${countNoun}${total === 1 ? "" : "s"}`;
  const menu =
    open && pos && typeof document !== "undefined"
      ? createPortal(
          <div
            className={`meet-participant-menu meet-participant-menu--${pos.place || "below"}`}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              zIndex: 10070,
            }}
            onMouseEnter={showMenu}
            onMouseLeave={hideMenuSoon}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={`${menuTitle}: ${total}`}
          >
            <div className="meet-participant-menu__caret" aria-hidden />
            <div className="meet-participant-menu__head">
              <span className="meet-participant-menu__title">{menuTitle}</span>
              <span className="meet-participant-menu__count">{total}</span>
            </div>
            <ul className="meet-participant-menu__list">
              {allPeople.map((p, i) => {
                const label = personChipDisplayName(p);
                const email = String(p?.email || "").trim();
                return (
                  <li key={`${email || label}-${i}`} className="meet-participant-menu__item" title={email || label}>
                    <Avatar
                      initials={singleInitial(p.initials || label)}
                      size={22}
                      color={["#35599a", "#566f9f", "#263d6d", "#1d4ed8", "#0f766e"][i % 5]}
                      imageUrl={p.avatar_url || p.avatarUrl || ""}
                      isBot={participantIsBot(p)}
                    />
                    <span className="meet-participant-menu__name">{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        ref={anchorRef}
        className={`meet-ref-participant-cell${showCountLabel ? "" : " meet-ref-participant-cell--compact"}${
          open ? " is-open" : ""
        }${className ? ` ${className}` : ""}`}
        onMouseEnter={showMenu}
        onMouseLeave={hideMenuSoon}
        onFocus={showMenu}
        onBlur={hideMenuSoon}
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={countLabel}
      >
        <div className="meet-ref-participant-stack">
          {people.map((p, i) => (
            <span
              key={`${p.email || p.name || i}`}
              className="meet-ref-participant-stack__avatar"
              style={{ zIndex: maxShow - i }}
            >
              <Avatar
                initials={singleInitial(p.initials || p.name || p.email)}
                size={avatarSize}
                color={["#35599a", "#566f9f", "#263d6d", "#1d4ed8"][i % 4]}
                imageUrl={p.avatar_url || p.avatarUrl || ""}
                isBot={participantIsBot(p)}
              />
            </span>
          ))}
          {extra > 0 ? (
            <span className="meet-ref-participant-stack__more">
              +{extra}
              <Svg d={IC.chevDown} size={10} color="currentColor" stroke={2.4} aria-hidden />
            </span>
          ) : null}
        </div>
        {showCountLabel ? (
          <span className="meet-ref-participant-count">{countLabel}</span>
        ) : null}
      </div>
      {menu}
    </>
  );
}

function MeetingReferenceStatusCell({ m }) {
  const status = effectiveMeetingStatus(m);
  const st = STATUS_META[status === "live" ? "live" : status] || STATUS_META.upcoming;
  const ended = status === "completed" || status === "ended";
  const statusLabel =
    status === "live"
      ? "Live"
      : status === "upcoming"
        ? "Upcoming"
        : ended
          ? "Completed"
          : status === "cancelled"
            ? "Cancelled"
            : st.label;

  const pillTone =
    status === "live" ? "live" : status === "upcoming" ? "upcoming" : ended ? "completed" : "default";

  return (
    <div className="meet-ref-status-col">
      <span
        className={`meet-ref-status-pill meet-ref-status-pill--compact meet-ref-status-pill--${pillTone}`}
        title={meetingLiveStatusTooltip(m, status) || statusLabel}
      >
        <span className="meet-ref-status-pill__dot" aria-hidden />
        <span className="meet-ref-status-pill__label">{statusLabel}</span>
      </span>
    </div>
  );
}

/** Shared list-row pill layout (size comes from CSS — scales with table width). */
const MEET_LIST_OUTCOME_BASE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 6,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  borderRadius: 999,
  fontWeight: 700,
};

function MeetListUnavailableOutcome({
  label = "Recording not available",
  title = "Video recording was not enabled or is not available for this meeting.",
}) {
  return (
    <span
      className="meet-list-outcome is-unavailable"
      title={title}
      aria-label={label}
      style={{
        ...MEET_LIST_OUTCOME_BASE,
        border: "1px solid #E2E8F0",
        background: "#F8FAFC",
        color: "#64748B",
        cursor: "default",
      }}
    >
      {label}
    </span>
  );
}

function CompletedVideoRecCell({ m, layout = "list", onOpen }) {
  const info = allMeetingsCompletedVideoRecInfo(m);
  const isList = layout === "list";
  if (info.kind === "none") {
    return (
      <MeetListUnavailableOutcome
        label="Recording not available"
        title="Video recording was not enabled for this meeting."
      />
    );
  }
  const openVideo = (e) => {
    if (e) e.stopPropagation();
    if (typeof onOpen === "function") {
      onOpen(m, { preferTab: "recording" });
      return;
    }
    openMeetingRecordingModal(m);
  };
  if (info.kind === "ready") {
    return (
      <button
        type="button"
        onClick={openVideo}
        title={VIDEO_REC_OPEN_HINT}
        aria-label={`Open recorded video for ${m?.title || "meeting"}`}
        className="meet-autojoin-outcome meet-list-outcome is-video-ready"
        style={{
          ...(isList ? MEET_LIST_OUTCOME_BASE : { padding: "6px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700 }),
          border: "1px solid #86EFAC",
          background: "#F0FDF4",
          color: "#15803D",
          cursor: "pointer",
          ...(isList ? {} : { display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", maxWidth: "100%" }),
        }}
      >
        <Svg d={IC.video} size={12} color="currentColor" stroke={2} />
        {info.label}
      </button>
    );
  }
  if (info.kind === "processing") {
    return (
      <span
        title="Video is still processing — usually ready within a few minutes after the meeting ends"
        className="meet-list-outcome is-video-pending"
        style={{
          ...(isList ? MEET_LIST_OUTCOME_BASE : { padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 700 }),
          border: "1px solid #E2E8F0",
          background: "#F8FAFC",
          color: "#64748B",
          ...(isList ? {} : { display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }),
        }}
      >
        {info.label}
      </span>
    );
  }
  const expired = info.kind === "expired";
  return (
    <span
      title={
        expired
          ? meetingRecordingUnavailableMessage(m, "Recording no longer available after 7-day retention.")
          : "Video Rec was on but no file is available for this meeting."
      }
      className="meet-list-outcome is-video-muted"
      style={{
        ...(isList ? MEET_LIST_OUTCOME_BASE : { padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 600 }),
        border: "1px solid #E2E8F0",
        background: "#F8FAFC",
        color: "#64748B",
        ...(isList ? {} : { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }),
      }}
    >
      {info.label}
    </span>
  );
}
/** Meeting detail + transcript areas — video download when Video Rec was on and file exists (or processing). */
function MeetingRecordingButton({ m, style = {} }) {
  const recordingMeetingId = meetingRecordingApiId(m);
  const recordingReady = meetingRecordingFileReady(m, { requireAutoJoin: false });
  const recordingPending = meetingRecordingAwaitingFile(m, { requireAutoJoin: false });
  const showRecording =
    meetingRecordingStoredOn(m) && (recordingReady || recordingPending);
  const [recBusy, setRecBusy] = useState(false);
  if (!showRecording) return null;

  const onMeetingRecord = () => {
    if (!recordingReady || recBusy) return;
    setRecBusy(true);
    try {
      openMeetingRecordingModal(m);
    } finally {
      setRecBusy(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 8,
        flexWrap: "wrap",
        ...style,
      }}
    >
      {recordingReady ? <MeetingRecordingRetentionNotice meetingMeta={m} compact /> : null}
      <button
        type="button"
        onClick={onMeetingRecord}
        disabled={!recordingReady || recBusy}
        title={
          recordingReady
            ? "Open meeting video recording"
            : "Video is uploading — usually ready within a few minutes after the meeting ends"
        }
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          border: `1px solid ${recordingReady ? "#86efac" : T.border}`,
          background: recordingReady ? "#f0fdf4" : "#f8fafc",
          color: recordingReady ? "#166534" : T.textMuted,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: recordingReady && !recBusy ? "pointer" : "not-allowed",
          opacity: recordingReady ? 1 : 0.72,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {recBusy ? "Opening…" : recordingReady ? "Meeting Record" : "Meeting Record…"}
      </button>
    </div>
  );
}

/** Join link for dedupe / merge (calendar may send hangoutLink only). */
function joinUrlFromMeeting(m) {
  return String(m?.meetingUrl ?? m?.meeting_url ?? m?.hangoutLink ?? "").trim();
}

/** DB rows use `is_bot`; some paths use `isBot`. */
function participantIsBot(p) {
  return p.is_bot === true || p.isBot === true;
}

function humanParticipants(parts) {
  return (parts || []).filter((p) => !participantIsBot(p));
}

/** Recall bot / download attributed this row to someone on the call. */
function participantJoinEvidenceKey(p) {
  return String(p?.recall_participant_id ?? p?.recallParticipantId ?? "").trim();
}

/** Guest-style rows (Recall placeholder or transcript-only speaker). */
function isGuestStyleMeetLinkEmail(email) {
  const e = String(email || "").trim().toLowerCase();
  return e.endsWith("@meet.link") || e.includes("guest.recall.");
}

function isAttendanceGuestParticipant(p) {
  return p?.joinedAsGuest === true || isGuestStyleMeetLinkEmail(p?.email);
}

/** Full hover text for a joined guest chip (name, email, plain-language why). */
function attendanceGuestTooltipLines(p) {
  const name = String(p?.name || "").trim();
  const email = String(p?.email || "").trim();
  const lines = [];
  if (name) lines.push(name);
  if (email) lines.push(email);
  if (p?.joinedAsGuest === true && !isGuestStyleMeetLinkEmail(email)) {
    lines.push("Joined as a guest — the calendar invite identity and Recall guest identity were matched to this person.");
    lines.push("The person joined through the meeting link without being identified by Recall as a signed-in work account.");
  } else {
    lines.push("Shown as guest — MeetAI could not match this person to someone on the Google Calendar invite for this meeting.");
    lines.push("They may have joined via the Meet invite link without signing in with their work Google account (guest / link join).");
  }
  return lines;
}

/** Shorter explanation for the (i) icon hover. */
function attendanceGuestInfoOnlyLines() {
  const lines = [
    "Why shown as guest?",
    "MeetAI could not link this joiner to the Google Calendar invite list for this meeting.",
    "Often they opened the Meet link without signing in with their @avaada.com Google account.",
  ];
  return lines;
}

/** Match calendar invite rows (email-local names) to Recall join / @meet.link rows — same person. */
function tokenizePersonForAttendance(name, email) {
  const n = String(name || "").trim();
  const fromName = n
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (fromName.length >= 2) return fromName;
  // Recall guest rows often have only a first name — ignore guest.recall.* email locals.
  if (fromName.length === 1 && isGuestStyleMeetLinkEmail(email)) return fromName;
  const local = String(email || "")
    .split("@")[0]
    .replace(/[._-]+/g, " ")
    .trim()
    .toLowerCase();
  return local.split(/\s+/).filter(Boolean);
}

function attendanceTokensOrderedSubsequenceMatch(small, large) {
  if (small.length < 2 || small.length > large.length) return false;
  let j = 0;
  for (let i = 0; i < large.length && j < small.length; i++) {
    if (large[i] === small[j]) j += 1;
  }
  return j === small.length;
}

function attendanceTokensLooselyMatch(a, b) {
  if (!a.length || !b.length) return false;
  if (a.join("") === b.join("")) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length >= 2 && short.every((t, i) => t === long[i])) return true;
  if (short.length >= 2 && attendanceTokensOrderedSubsequenceMatch(short, long)) return true;
  if (short.length === 1 && long.length === 1) return short[0] === long[0];
  // Guest first name only ("Pooja") vs invite email-local ("pooja.patwari").
  if (short.length === 1 && long.length > 1 && short[0].length >= 3 && short[0] === long[0]) return true;
  if (short.length === 1 && long.length > 1) return long.join("") === short[0];
  return false;
}

function personCompactKey(name, email) {
  const tokens = tokenizePersonForAttendance(name, email);
  if (tokens.length) return tokens.join("");
  return String(email || "")
    .split("@")[0]
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

function participantsLikelySamePerson(a, b) {
  const emA = String(a?.email || "").trim().toLowerCase();
  const emB = String(b?.email || "").trim().toLowerCase();
  if (emA && emB && emA === emB) return true;
  const gA = isGuestStyleMeetLinkEmail(a?.email);
  const gB = isGuestStyleMeetLinkEmail(b?.email);
  if (!gA && !gB && emA && emB && emA !== emB) {
    const localA = emA.split("@")[0].replace(/[^a-z0-9]/gi, "");
    const localB = emB.split("@")[0].replace(/[^a-z0-9]/gi, "");
    if (localA && localB && localA === localB) return true;
    return false;
  }
  if (gA && gB) return false;
  const ta = tokenizePersonForAttendance(a?.name, a?.email);
  const tb = tokenizePersonForAttendance(b?.name, b?.email);
  if (attendanceTokensLooselyMatch(ta, tb)) return true;
  const ca = personCompactKey(a?.name, emA);
  const cb = personCompactKey(b?.name, emB);
  return Boolean(ca && cb && ca.length >= 4 && ca === cb);
}

function attendanceParticipantScore(p) {
  let score = 0;
  if (!isGuestStyleMeetLinkEmail(p?.email)) score += 4;
  if (participantJoinEvidenceKey(p)) score += 2;
  if (/\s/.test(String(p?.name || ""))) score += 1;
  return score;
}

/** Merge guest + @avaada.com only when join/transcript evidence exists — not from opening a share link. */
function shouldMergeAttendanceParticipantRows(a, b, context = {}) {
  if (!participantsLikelySamePerson(a, b)) return false;
  const gA = isGuestStyleMeetLinkEmail(a?.email);
  const gB = isGuestStyleMeetLinkEmail(b?.email);
  if (gA && gB) return false;
  if (!gA && !gB) return true;
  const guest = gA ? a : b;
  const real = gA ? b : a;
  if (isGuestStyleMeetLinkEmail(real?.email)) return false;
  if (participantJoinEvidenceKey(real) || participantJoinEvidenceKey(guest)) return true;
  const speakers = context.transcriptSpeakers || [];
  if (participantMatchedTranscriptSpeaker(guest, speakers)) return true;
  if (participantMatchedTranscriptSpeaker(real, speakers)) return true;
  return false;
}

function dedupeAttendanceParticipants(rows, context = {}) {
  const out = [];
  for (const p of rows) {
    const idx = out.findIndex((x) => shouldMergeAttendanceParticipantRows(x, p, context));
    if (idx >= 0) {
      const current = out[idx];
      const guest = isGuestStyleMeetLinkEmail(current?.email)
        ? current
        : isGuestStyleMeetLinkEmail(p?.email)
          ? p
          : null;
      const preferred = attendanceParticipantScore(p) > attendanceParticipantScore(current) ? p : current;
      // Keep the real calendar email for display/notifications, but retain the
      // Recall guest evidence so the UI can explain "joined as a guest".
      out[idx] = guest
        ? {
            ...preferred,
            joinedAsGuest: true,
            guestParticipantId: participantJoinEvidenceKey(guest) || undefined,
            guestDisplayName: String(guest?.name || "").trim() || undefined,
            guestEmail: String(guest?.email || "").trim() || undefined,
          }
        : current?.joinedAsGuest === true || preferred?.joinedAsGuest === true
          ? { ...preferred, joinedAsGuest: true }
          : preferred;
    } else {
      out.push(p);
    }
  }
  return out;
}

function transcriptSpeakerNames(transcript) {
  const names = [];
  const seen = new Set();
  for (const row of transcript || []) {
    const sp = String(row?.speaker || "").trim();
    if (!sp || /^unknown$/i.test(sp)) continue;
    const key = sp.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(sp);
  }
  return names;
}

function attendanceContextFromMeeting(m) {
  if (!m) return {};
  const fromRows = transcriptSpeakerNames(m.transcript);
  const fromLite = Array.isArray(m.transcriptSpeakers)
    ? m.transcriptSpeakers.map((s) => String(s || "").trim()).filter(Boolean)
    : [];
  const speakers = fromRows.length > 0 ? fromRows : fromLite;
  return {
    transcriptSpeakers: speakers,
    hostEmail: String(m.hostEmail ?? m.host_email ?? "").trim(),
    hostName: String(m.hostName ?? m.host_name ?? "").trim(),
  };
}

function participantMatchedTranscriptSpeaker(p, speakers) {
  if (!Array.isArray(speakers) || !speakers.length) return false;
  return speakers.some((sp) => participantsLikelySamePerson(p, { name: sp, email: "" }));
}

function participantHasJoinEvidence(p, context = {}) {
  if (participantJoinEvidenceKey(p)) return true;
  if (isGuestStyleMeetLinkEmail(p?.email)) return true;
  if (participantMatchedTranscriptSpeaker(p, context.transcriptSpeakers)) return true;
  const hostEmail = String(context.hostEmail || "").trim().toLowerCase();
  const pEmail = String(p?.email || "").trim().toLowerCase();
  if (hostEmail && pEmail && hostEmail === pEmail && (context.transcriptSpeakers || []).length > 0) {
    return true;
  }
  return false;
}

/**
 * Humans only: "joined" = Recall join evidence, @meet.link row, or transcript speaker match.
 * Invite rows that match someone already in "joined" (duplicate DB row) are hidden from "not joined".
 */
function splitJoinedVsNotJoinedInvitees(participants, context = {}) {
  const humans = humanParticipants(participants || []);
  const joined = [];
  const notJoined = [];
  for (const p of humans) {
    const email = String(p.email || "").trim().toLowerCase();
    if (!email) continue;
    if (participantHasJoinEvidence(p, context)) joined.push(p);
    else notJoined.push(p);
  }
  const joinedDeduped = dedupeAttendanceParticipants(joined, context);
  const notJoinedDeduped = notJoined.filter(
    (invite) => !joinedDeduped.some((j) => shouldMergeAttendanceParticipantRows(invite, j, context))
  );
  return { joined: joinedDeduped, notJoined: notJoinedDeduped };
}

/**
 * Whether a meeting has actually taken place, so an empty attendance list can be
 * read as "nobody joined" rather than "not yet". Participants are only written
 * while the bot is in the call, so scheduled meetings legitimately have none.
 */
function meetingHasHappened(m) {
  const status = String(m?.status || "").trim().toLowerCase();
  return status === "completed" || status === "live";
}

/** Headcount shown on meeting cards: Joined + Not joined (same chips as attendance breakdown). */
function joinedPlusNotJoinedCount(participants, context = {}) {
  const { joined, notJoined } = splitJoinedVsNotJoinedInvitees(participants || [], context);
  return joined.length + notJoined.length;
}

/** Chip label: display name only (email on hover via PersonHoverTipProvider). */
function personChipDisplayName(p) {
  if (!p) return "—";
  const n = String(p.name || "").trim();
  const e = String(p.email || "").trim();
  const best = pickBestDisplayPersonName(n, e);
  if (best) return best;
  // Last resort so Session Registry never shows blank "—" for invite-only rows.
  return formatDisplayPersonName(n) || formatDisplayPersonName(e) || e || n || "—";
}

/** Hover popover lines: full name, then full email, then optional extras. */
function personHoverTipLines(p, extraLines = []) {
  const n = String(p?.name || "").trim();
  const e = String(p?.email || "").trim();
  const lines = [];
  if (n) lines.push(n);
  if (e && e !== "—") lines.push(e);
  for (const x of extraLines) {
    const s = String(x || "").trim();
    if (s && !lines.includes(s)) lines.push(s);
  }
  return lines;
}

function formatAdminPersonLabel(p) {
  return personChipDisplayName(p);
}

/** Chip shows name only; email shown in a custom hover tooltip (not native `title`). */
function attendanceChipNameEmail(p) {
  if (!p) return { name: "—", email: "", full: "—" };
  const n = String(p.name || "").trim();
  const e = String(p.email || "").trim();
  const name = personChipDisplayName(p);
  const full = n && e ? `${n} (${e})` : n || e || "—";
  return { name, email: e, full };
}

const PersonHoverTipContext = createContext(null);

function PersonHoverTipPortal({ tip }) {
  if (!tip) return null;
  if (tip.variant === "sidebar") {
    return createPortal(
      <div
        className="sidebar-hover-tip-anchor"
        style={{
          position: "fixed",
          left: tip.anchorRight + 10,
          top: tip.y,
          transform: "translateY(-50%)",
        }}
      >
        <div role="tooltip" className="sidebar-hover-tip">
          <span className="sidebar-hover-tip__label">{tip.label}</span>
        </div>
      </div>,
      document.body
    );
  }
  return createPortal(
    <div
      role="tooltip"
      style={{
        position: "fixed",
        left: tip.x,
        top: tip.preferAbove ? tip.anchorTop - 8 : tip.anchorBottom + 8,
        transform: tip.preferAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
        zIndex: 10050,
        pointerEvents: "none",
        padding: "8px 11px",
        borderRadius: 8,
        background: "#0F172A",
        color: "#F8FAFC",
        boxShadow: "0 10px 28px rgba(15,23,42,0.38)",
        border: "1px solid rgba(255,255,255,0.1)",
        maxWidth: "min(calc(100vw - 24px), 360px)",
        textAlign: "left",
      }}
    >
      {tip.lines.map((line, i) => {
        const trimmed = String(line || "").trim();
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
        const isTitle = i === 0 && !isEmail;
        const isNote = !isEmail && !isTitle;
        return (
          <div
            key={`${i}-${line.slice(0, 32)}`}
            style={{
              fontSize: isEmail ? 11 : isNote ? 11.5 : 12.5,
              fontWeight: isTitle ? 700 : isEmail ? 500 : 400,
              fontFamily: isEmail ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" : "inherit",
              opacity: isNote ? 0.92 : 1,
              marginTop: i > 0 ? 6 : 0,
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}
          >
            {line}
          </div>
        );
      })}
    </div>,
    document.body
  );
}

function PersonHoverTipProvider({ children }) {
  const [tip, setTip] = useState(null);
  const hideTimer = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const showPersonHoverTip = useCallback(
    (e, lines) => {
      const list = (Array.isArray(lines) ? lines : []).map((s) => String(s || "").trim()).filter(Boolean);
      if (!list.length) return;
      clearHideTimer();
      const r = e.currentTarget.getBoundingClientRect();
      const x = Math.min(window.innerWidth - 20, Math.max(20, r.left + r.width / 2));
      setTip({
        variant: "person",
        x,
        anchorTop: r.top,
        anchorBottom: r.bottom,
        lines: list,
        preferAbove: r.top > 68,
      });
    },
    [clearHideTimer]
  );

  const showSidebarHoverTip = useCallback(
    (e, label) => {
      const text = String(label || "").trim();
      if (!text) return;
      clearHideTimer();
      const r = e.currentTarget.getBoundingClientRect();
      setTip({
        variant: "sidebar",
        anchorRight: r.right,
        y: r.top + r.height / 2,
        label: text,
      });
    },
    [clearHideTimer]
  );

  const hidePersonHoverTipNow = useCallback(() => {
    clearHideTimer();
    setTip(null);
  }, [clearHideTimer]);

  const hidePersonHoverTipSoon = useCallback(() => {
    clearHideTimer();
    hideTimer.current = window.setTimeout(() => {
      setTip(null);
      hideTimer.current = null;
    }, 100);
  }, [clearHideTimer]);

  useEffect(() => {
    if (!tip) return undefined;
    const hide = () => setTip(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    window.addEventListener("blur", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("blur", hide);
    };
  }, [tip]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  const value = useMemo(
    () => ({ showPersonHoverTip, showSidebarHoverTip, hidePersonHoverTipSoon, hidePersonHoverTipNow }),
    [showPersonHoverTip, showSidebarHoverTip, hidePersonHoverTipSoon, hidePersonHoverTipNow]
  );

  return (
    <PersonHoverTipContext.Provider value={value}>
      {children}
      <PersonHoverTipPortal tip={tip} />
    </PersonHoverTipContext.Provider>
  );
}

function usePersonHoverTip() {
  const ctx = useContext(PersonHoverTipContext);
  return (
    ctx || {
      showPersonHoverTip: () => {},
      showSidebarHoverTip: () => {},
      hidePersonHoverTipSoon: () => {},
      hidePersonHoverTipNow: () => {},
    }
  );
}

/** Clears floating sidebar labels when nav width or mobile drawer changes. */
function SidebarHoverTipReset({ showSidebarTips, leftNavCollapsed, mobileNavOpen }) {
  const { hidePersonHoverTipNow } = usePersonHoverTip();
  useEffect(() => {
    if (!showSidebarTips) hidePersonHoverTipNow();
  }, [showSidebarTips, hidePersonHoverTipNow]);
  useEffect(() => {
    hidePersonHoverTipNow();
  }, [leftNavCollapsed, mobileNavOpen, hidePersonHoverTipNow]);
  return null;
}

/** Styled sidebar tooltip when labels are hidden (collapsed nav). */
function bindSidebarHoverTip(tipApi, label, enabled = true) {
  if (!enabled || !label) return {};
  const hide = tipApi.hidePersonHoverTipNow || tipApi.hidePersonHoverTipSoon;
  return {
    onMouseEnter: (e) => tipApi.showSidebarHoverTip(e, label),
    onMouseLeave: hide,
    onFocus: (e) => tipApi.showSidebarHoverTip(e, label),
    onBlur: hide,
  };
}

/** Wraps sidebar controls with hover name labels when the nav is icon-only. */
function SidebarTipTarget({ tipLabel, showTip = false, className, children, onClick, onMouseDown, ...rest }) {
  const tipApi = usePersonHoverTip();
  const hide = tipApi.hidePersonHoverTipNow || tipApi.hidePersonHoverTipSoon;
  return (
    <button
      type="button"
      className={className}
      aria-label={tipLabel || rest["aria-label"]}
      {...rest}
      {...bindSidebarHoverTip(tipApi, tipLabel, showTip)}
      onMouseDown={(e) => {
        hide();
        onMouseDown?.(e);
      }}
      onClick={(e) => {
        hide();
        onClick?.(e);
      }}
    >
      {children}
    </button>
  );
}

/** Admin-only right drawer for row details (replaces inline Collapse expansion). */
function AdminDetailDrawer({ open, onClose, title, subtitle, children, isLight, width = 440 }) {
  const light = isLight !== false;
  // One strict width for every drawer: fixed px metro, never content-driven.
  // Literal px (no min()/vw in the width path) so every meeting renders the
  // identical frame; the maxWidth cap below only engages on phone viewports.
  const strictWidth = `${width}px`;
  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      ModalProps={{ keepMounted: false, style: { zIndex: 5000 } }}
      slotProps={{
        // NOTE (MUI v9): Drawer no longer reads PaperProps — paper geometry
        // MUST go through slotProps.paper, otherwise the paper renders at
        // default auto width (content-driven: narrow for sparse meetings,
        // full-page for chip-packed ones). Plain `style` (not emotion `sx`)
        // so the frame holds even if the CSS-in-JS pipeline hiccups.
        paper: {
          style: {
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: strictWidth,
            minWidth: strictWidth,
            maxWidth: `min(${strictWidth}, calc(100vw - 24px))`,
            zIndex: 1300,
            flexShrink: 0,
            overflow: "visible",
            background: light ? "#fff" : "#0D1B2A",
            color: light ? "#0f172a" : "#E8F0F7",
            borderLeft: light ? "1px solid #e2e8f0" : "1px solid #1E3448",
            boxSizing: "border-box",
            borderRadius: 0,
            margin: 0,
          },
        },
      }}
    >
      <div style={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", maxWidth: "100%", overflow: "visible" }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          title="Close"
          className="admin-detail-drawer-x"
          style={{
            position: "absolute",
            left: -52,
            top: 14,
            zIndex: 1400,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "50%",
            border: light ? "1px solid #dce6ef" : "1px solid #1E3448",
            background: light ? "#ffffff" : "#0D1B2A",
            color: light ? "#5f7690" : "rgba(232,240,247,0.85)",
            boxShadow: "0 4px 14px rgba(11, 42, 74, 0.14)",
            cursor: "pointer",
            fontSize: 15,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: `1px solid ${light ? "#e2e8f0" : "#1E3448"}`,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.25, wordBreak: "break-word" }}>
              {title || "Details"}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 11.5, color: light ? "#64748b" : "rgba(232,240,247,0.6)", marginTop: 2 }}>
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "12px 16px 16px", minHeight: 0, width: "100%", maxWidth: "100%", overflowWrap: "anywhere" }}>
          {children}
        </div>
      </div>
    </Drawer>
  );
}

function personChipHoverHandlers(p, extraLines, tipApi) {
  const lines = personHoverTipLines(p, extraLines);
  if (!lines.length) return {};
  return {
    tabIndex: 0,
    onMouseEnter: (e) => tipApi.showPersonHoverTip(e, lines),
    onMouseLeave: tipApi.hidePersonHoverTipSoon,
    onFocus: (e) => tipApi.showPersonHoverTip(e, lines),
    onBlur: tipApi.hidePersonHoverTipSoon,
    "aria-label": lines.join(", "),
  };
}

function tipLinesHoverHandlers(lines, tipApi, { ariaLabel, stopPropagation = false } = {}) {
  const list = (Array.isArray(lines) ? lines : []).map((s) => String(s || "").trim()).filter(Boolean);
  if (!list.length) return {};
  return {
    tabIndex: 0,
    onMouseEnter: (e) => {
      if (stopPropagation) e.stopPropagation();
      tipApi.showPersonHoverTip(e, list);
    },
    onMouseLeave: (e) => {
      if (stopPropagation) e.stopPropagation();
      tipApi.hidePersonHoverTipSoon();
    },
    onFocus: (e) => {
      if (stopPropagation) e.stopPropagation();
      tipApi.showPersonHoverTip(e, list);
    },
    onBlur: tipApi.hidePersonHoverTipSoon,
    "aria-label": ariaLabel || list.join(", "),
  };
}

function AttendanceGuestInfoIcon({ participant, tipApi }) {
  const lines = attendanceGuestInfoOnlyLines(participant);
  return (
    <span
      role="img"
      {...tipLinesHoverHandlers(lines, tipApi, {
        ariaLabel: "Why is this person shown as a guest?",
        stopPropagation: true,
      })}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 15,
        height: 15,
        borderRadius: "50%",
        border: "1px solid #94A3B8",
        color: "#64748B",
        fontSize: 10,
        fontWeight: 800,
        fontStyle: "italic",
        fontFamily: "Georgia, 'Times New Roman', serif",
        lineHeight: 1,
        flexShrink: 0,
        cursor: "help",
        background: "rgba(255,255,255,0.9)",
      }}
    >
      i
    </span>
  );
}

function participantAttendanceLabel(p) {
  if (!p) return "Unknown";
  if (participantIsBot(p)) return BOT_DISPLAY_NAME;
  if (isGuestStyleMeetLinkEmail(p.email)) {
    const n = String(p.name || "").trim();
    return n ? `${n} (guest)` : "Guest";
  }
  return String(p.name || "").trim() || String(p.email || "").split("@")[0] || "Unknown";
}

/** Joined vs invite-only (not joined) — same rules as admin portal / meeting bot context. */
function AttendanceSectionToggle({ hidden, expanded, onExpand, onCollapse, referenceLayout = false }) {
  if (hidden <= 0) return null;
  const fontSize = referenceLayout ? 13 : 12;
  const color = referenceLayout ? "#0B2A4A" : "#1D4ED8";
  const mutedColor = referenceLayout ? "#64748B" : T.textMuted;
  const btnStyle = {
    marginTop: 8,
    fontSize,
    fontWeight: 600,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "2px 0",
    textDecoration: "underline",
  };
  if (!expanded) {
    return (
      <button type="button" onClick={onExpand} style={{ ...btnStyle, color }}>
        Show more (+{hidden})
      </button>
    );
  }
  return (
    <button type="button" onClick={onCollapse} style={{ ...btnStyle, color: mutedColor }}>
      Show less
    </button>
  );
}

function JoinedNotJoinedAttendance({ participants, meeting = null, context = null, compact = false, maxNames = 6, autoJoinOptInUsers, hasGoogleEventId = true, defaultDetailsOpen = false, referenceLayout = false }) {
  const [joinedExpanded, setJoinedExpanded] = useState(false);
  const [notJoinedExpanded, setNotJoinedExpanded] = useState(false);
  const [ajExpanded, setAjExpanded] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(!!defaultDetailsOpen);
  const tipApi = usePersonHoverTip();

  useEffect(() => {
    if (defaultDetailsOpen) setDetailsOpen(true);
  }, [defaultDetailsOpen]);

  const attendanceCtx = context || attendanceContextFromMeeting(meeting);
  const { joined: joinedRaw, notJoined: notJoinedRaw } = splitJoinedVsNotJoinedInvitees(
    participants || [],
    attendanceCtx
  );
  const joined = joinedRaw.filter((p) => !shouldHideMeetAiFromParticipantRoster(p));
  const notJoined = notJoinedRaw.filter((p) => !shouldHideMeetAiFromParticipantRoster(p));
  const aj = Array.isArray(autoJoinOptInUsers) ? autoJoinOptInUsers.filter(Boolean) : [];

  const joinLabels = joined.map(participantAttendanceLabel);
  const noShowLabels = notJoined.map(participantAttendanceLabel);
  const joinShownLabels = joinLabels.slice(0, maxNames);
  const noShowShownLabels = noShowLabels.slice(0, maxNames);

  if (compact) {
    if (joined.length === 0 && notJoined.length === 0) return null;
    const compactFont = referenceLayout ? 13 : 11;
    return (
      <div
        style={{ fontSize: compactFont, color: T.textMuted, lineHeight: 1.45, display: "flex", flexDirection: "column", gap: 4 }}
        onClick={(e) => e.stopPropagation()}
      >
        {joined.length > 0 ? (
          <div>
            <span style={{ fontWeight: 600, color: "#15803D" }}>Joined ({joined.length}): </span>
            <span>
              {joinShownLabels.join(", ")}
              {joined.length > maxNames ? ` +${joined.length - maxNames} more` : ""}
            </span>
          </div>
        ) : null}
        {notJoined.length > 0 ? (
          <div>
            <span style={{ fontWeight: 600, color: "#B45309" }}>Not joined ({notJoined.length}): </span>
            <span>
              {noShowShownLabels.join(", ")}
              {notJoined.length > maxNames ? ` +${notJoined.length - maxNames} more` : ""}
            </span>
          </div>
        ) : null}
      </div>
    );
  }

  if (joined.length === 0 && notJoined.length === 0 && aj.length === 0) return null;

  const joinPreview = 10;
  const noShowPreview = 10;
  const ajPreview = 12;
  const joinShown = joinedExpanded ? joined : joined.slice(0, joinPreview);
  const noShowShown = notJoinedExpanded ? notJoined : notJoined.slice(0, noShowPreview);
  const ajShown = ajExpanded ? aj : aj.slice(0, ajPreview);
  const joinHidden = Math.max(0, joined.length - joinPreview);
  const noShowHidden = Math.max(0, notJoined.length - noShowPreview);
  const ajHidden = Math.max(0, aj.length - ajPreview);

  const chipRadius = referenceLayout ? 4 : 999;
  const chipFontSize = referenceLayout ? 13 : 11.5;
  const chipBase = (overrides = {}) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: chipRadius,
    fontSize: chipFontSize,
    fontWeight: 600,
    border: `1px solid ${T.border}`,
    background: "#FFFFFF",
    color: T.text,
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "default",
    ...overrides,
  });

  const joinedChip = (p, idx) => {
    const { name } = attendanceChipNameEmail(p);
    const guest = isAttendanceGuestParticipant(p);
    const guestTipLines = guest ? attendanceGuestTooltipLines(p) : personHoverTipLines(p, []);
    const chipHandlers = guest
      ? tipLinesHoverHandlers(guestTipLines, tipApi, { ariaLabel: `${name}, guest participant` })
      : personChipHoverHandlers(p, [], tipApi);
    return (
      <span
        key={`jd-j-${idx}-${String(p.email || p.name || idx)}`}
        {...chipHandlers}
        style={chipBase({
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontWeight: 500,
          borderStyle: guest ? "dashed" : "solid",
          borderColor: guest ? "#94A3B8" : T.border,
          background: guest ? "rgba(248,250,252,0.98)" : "#FFFFFF",
          maxWidth: guest ? 240 : 200,
        })}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{name}</span>
        {guest ? <AttendanceGuestInfoIcon participant={p} tipApi={tipApi} /> : null}
      </span>
    );
  };

  const notJoinedChip = (p, idx) => {
    const { name } = attendanceChipNameEmail(p);
    return (
      <span
        key={`jd-ns-${idx}-${String(p.email || p.name || idx)}`}
        {...personChipHoverHandlers(p, ["On invite list, no join match yet."], tipApi)}
        style={chipBase({
          background: "rgba(254,243,199,0.45)",
          borderColor: "#FCD34D",
          fontWeight: 500,
        })}
      >
        {name}
      </span>
    );
  };

  const ajChip = (p, idx) => {
    const { name } = attendanceChipNameEmail(p);
    const via =
      p.via === "override"
        ? "event override"
        : p.via === "global"
          ? "global auto-join"
          : p.via
            ? String(p.via)
            : "";
    const tipLines = via ? [`Auto-join: ${via}`] : [];
    return (
      <span
        key={`jd-aj-${idx}-${String(p.email || p.name || idx)}`}
        {...personChipHoverHandlers(p, tipLines, tipApi)}
        style={chipBase({
          background: "rgba(241,245,249,0.98)",
          borderColor: "#E2E8F0",
          maxWidth: 220,
          fontWeight: 500,
        })}
      >
        {name}
        {via ? (
          <span
            style={{
              marginLeft: 6,
              fontSize: 9,
              fontWeight: 700,
              color: T.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {p.via === "override" ? "override" : p.via === "global" ? "global" : via}
          </span>
        ) : null}
      </span>
    );
  };

  const sectionHelp = { fontSize: 11, color: T.textSub, marginBottom: 8, lineHeight: 1.45 };
  const blockGap = { display: "flex", flexDirection: "column", gap: 0 };
  const attendanceGroupCard = {
    minWidth: 0,
    padding: "12px 13px",
    borderRadius: referenceLayout ? 4 : 10,
    border: `1px solid ${T.border}`,
    background: "#F8FAFC",
  };

  return (
    <section
        id={referenceLayout ? "detail-participants-attendance" : undefined}
        className={referenceLayout ? "avaada-detail-attendance-section" : undefined}
        style={
          referenceLayout
            ? undefined
            : {
                marginTop: 16,
                padding: "15px",
                borderRadius: 12,
                background: "#FFFFFF",
                border: `1px solid ${T.border}`,
                boxShadow: "0 1px 3px rgba(12,43,78,0.05)",
              }
        }
        aria-labelledby="detail-attendance-heading"
      >
      <button
        type="button"
        id="detail-attendance-heading"
        className={referenceLayout ? "avaada-detail-attendance-section__header" : undefined}
        onClick={() => setDetailsOpen((open) => !open)}
        aria-expanded={detailsOpen}
        style={
          referenceLayout
            ? undefined
            : {
                width: "100%",
                margin: 0,
                padding: 0,
                border: "none",
                background: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 700,
                color: T.text,
                letterSpacing: "-0.01em",
              }
        }
      >
        <span className={referenceLayout ? "avaada-detail-attendance-section__header-main" : undefined} style={referenceLayout ? undefined : { display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Svg d={IC.users} size={15} color="#475569" stroke={2} />
          <span className={referenceLayout ? "avaada-detail-attendance-section__title" : undefined}>Attendees</span>
          <span className={referenceLayout ? "avaada-detail-attendance-section__meta" : undefined} style={referenceLayout ? undefined : { color: T.textMuted, fontSize: 11.5, fontWeight: 500 }}>
            {joined.length} joined · {notJoined.length} not joined
          </span>
        </span>
        <span className={referenceLayout ? "avaada-detail-attendance-section__chevron" : undefined} style={referenceLayout ? undefined : { color: T.textMuted, fontSize: 11 }}>{detailsOpen ? "▴" : "▾"}</span>
      </button>

      {detailsOpen ? <div
        className={referenceLayout ? "avaada-detail-attendance-section__body" : undefined}
        style={
          referenceLayout
            ? undefined
            : {
                ...blockGap,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: 10,
                marginTop: 14,
              }
        }
      >
        <div className={referenceLayout ? "avaada-detail-attendance-group" : undefined} style={referenceLayout ? undefined : attendanceGroupCard}>
          <div className={referenceLayout ? "avaada-detail-attendance-group__title" : undefined} style={referenceLayout ? { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#0B2A4A", marginBottom: 6 } : { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: T.text, marginBottom: 6 }}>
            {referenceLayout ? `JOINED (${joined.length})` : `Joined (${joined.length})`}
          </div>
          <div className={referenceLayout ? "avaada-detail-attendance-group__help" : undefined} style={referenceLayout ? { fontSize: 12, color: "#64748B", marginBottom: 8 } : sectionHelp}>
            People detected in the call. A dashed chip indicates a guest join.
          </div>
          {joined.length === 0 ? (
            referenceLayout ? (
              <div className="avaada-detail-attendance-group__empty">
                <Svg d={IC.users} size={28} color="#94A3B8" stroke={1.6} />
                No attendees joined yet
              </div>
            ) : (
              <span style={{ fontSize: 13, color: T.textMuted }}>—</span>
            )
          ) : (
            <>
              <div className="attendance-chip-scroll" aria-label="People on the call">
                {joinShown.map((p, i) => joinedChip(p, i))}
              </div>
              <AttendanceSectionToggle
                hidden={joinHidden}
                expanded={joinedExpanded}
                onExpand={() => setJoinedExpanded(true)}
                onCollapse={() => setJoinedExpanded(false)}
                referenceLayout
              />
            </>
          )}
        </div>

        <div className={referenceLayout ? "avaada-detail-attendance-group" : undefined} style={referenceLayout ? undefined : attendanceGroupCard}>
          <div className={referenceLayout ? "avaada-detail-attendance-group__title" : undefined} style={referenceLayout ? { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#0B2A4A", marginBottom: 6 } : { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: T.text, marginBottom: 6 }}>
            {referenceLayout ? `NOT JOINED (${notJoined.length})` : `Not joined (${notJoined.length})`}
          </div>
          <div className={referenceLayout ? "avaada-detail-attendance-group__help" : undefined} style={referenceLayout ? { fontSize: 12, color: "#64748B", marginBottom: 8 } : sectionHelp}>
            Calendar invitees for whom no join was detected.
          </div>
          {notJoined.length === 0 ? (
            <span style={{ fontSize: 13, color: T.textMuted }}>—</span>
          ) : (
            <>
              <div className="attendance-chip-scroll" aria-label="Invited, no join match yet">
                {noShowShown.map((p, i) => notJoinedChip(p, i))}
              </div>
              <AttendanceSectionToggle
                hidden={noShowHidden}
                expanded={notJoinedExpanded}
                onExpand={() => setNotJoinedExpanded(true)}
                onCollapse={() => setNotJoinedExpanded(false)}
                referenceLayout
              />
            </>
          )}
        </div>

        {aj.length > 0 ? <div className={referenceLayout ? "avaada-detail-attendance-group avaada-detail-attendance-group--autojoin" : undefined} style={referenceLayout ? undefined : attendanceGroupCard}>
          <div style={referenceLayout ? { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#0B2A4A", marginBottom: 6 } : { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: T.text, marginBottom: 6 }}>
            Auto join opted in ({aj.length})
          </div>
          {aj.length === 0 ? (
            <span style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
              {hasGoogleEventId
                ? "No invitee has enabled auto-join for this meeting yet."
                : "Not linked to a Google Calendar event — auto-join needs a calendar invite."}
            </span>
          ) : (
            <>
              <div className="attendance-chip-scroll" aria-label="Auto join opted in">
                {ajShown.map((p, i) => ajChip(p, i))}
              </div>
              <AttendanceSectionToggle
                hidden={ajHidden}
                expanded={ajExpanded}
                onExpand={() => setAjExpanded(true)}
                onCollapse={() => setAjExpanded(false)}
                referenceLayout
              />
            </>
          )}
        </div> : null}
      </div> : null}
    </section>
  );
}

/** Comma-separated labels; truncates after `max` entries with "+N more". */
function summarizeAdminPeopleList(arr, max = 10) {
  const list = Array.isArray(arr) ? arr.filter(Boolean) : [];
  if (!list.length) return "";
  const parts = list.slice(0, max).map(formatAdminPersonLabel);
  if (list.length > max) parts.push(`+${list.length - max} more`);
  return parts.join(", ");
}

function isGuestMeetLinkEmail(email) {
  return String(email || "").toLowerCase().endsWith("@meet.link");
}

/** Tokens from display name or email local part — align calendar rows with @meet.link guests. */
function participantTokensForDedupe(name, email) {
  const n = String(name || "").trim();
  const fromName = n
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (fromName.length >= 2) return fromName;
  const local = String(email || "")
    .split("@")[0]
    .replace(/[._-]+/g, " ")
    .trim()
    .toLowerCase();
  return local.split(/\s+/).filter(Boolean);
}

function participantTokensOrderedSubsequenceMatch(small, large) {
  if (small.length < 2 || small.length > large.length) return false;
  let j = 0;
  for (let i = 0; i < large.length && j < small.length; i++) {
    if (large[i] === small[j]) j += 1;
  }
  return j === small.length;
}

function participantTokensLooselyMatch(a, b) {
  if (!a.length || !b.length) return false;
  if (a.join("") === b.join("")) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length >= 2 && short.every((t, i) => t === long[i])) return true;
  if (short.length >= 2 && participantTokensOrderedSubsequenceMatch(short, long)) return true;
  if (short.length === 1 && long.length === 1) return short[0] === long[0];
  if (short.length === 1 && long.length > 1) return long.join("") === short[0];
  return false;
}

function participantsLikelySameHuman(p, q) {
  const emA = String(p?.email || "").trim().toLowerCase();
  const emB = String(q?.email || "").trim().toLowerCase();
  if (emA && emB && emA === emB) return true;
  const gA = isGuestMeetLinkEmail(p?.email);
  const gB = isGuestMeetLinkEmail(q?.email);
  if (!gA && !gB && emA && emB && emA !== emB) return false;
  if (gA && gB) return false;
  const ta = participantTokensForDedupe(p?.name, p?.email);
  const tb = participantTokensForDedupe(q?.name, q?.email);
  return participantTokensLooselyMatch(ta, tb);
}

function betterParticipantDisplayName(a, b) {
  const na = String(a || "").trim();
  const nb = String(b || "").trim();
  if (!na) return nb;
  if (!nb) return na;
  const aSp = /\s/.test(na);
  const bSp = /\s/.test(nb);
  if (aSp && !bSp) return na;
  if (bSp && !aSp) return nb;
  return na.length >= nb.length ? na : nb;
}

function dedupeHumanParticipantsForDisplay(parts, context = {}) {
  const humans = humanParticipants(parts);
  const n = humans.length;
  if (n <= 1) return humans;
  const parent = humans.map((_, i) => i);
  function find(i) {
    if (parent[i] !== i) parent[i] = find(parent[i]);
    return parent[i];
  }
  function union(i, j) {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[rj] = ri;
  }
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (shouldMergeAttendanceParticipantRows(humans[i], humans[j], context)) union(i, j);
    }
  }
  const clusters = new Map();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!clusters.has(r)) clusters.set(r, []);
    clusters.get(r).push(humans[i]);
  }
  return [...clusters.values()].map((cluster) => {
    const sorted = [...cluster].sort((a, b) => {
      const score = (p) => (isGuestMeetLinkEmail(p.email) ? 1 : 0);
      return score(a) - score(b);
    });
    const base = sorted[0];
    let name = base.name;
    for (let k = 1; k < sorted.length; k++) {
      name = betterParticipantDisplayName(name, sorted[k].name);
    }
    return { ...base, name };
  });
}

function participantsForMeetingDetailChips(parts, context = {}) {
  const list = Array.isArray(parts) ? parts : [];
  const dedupedHumans = dedupeHumanParticipantsForDisplay(list, context);
  return dedupedHumans.filter((p) => !shouldHideMeetAiFromParticipantRoster(p));
}

function guestMeetLinkRowAbsorbedByCalendarParticipant(p, allParts, context = {}) {
  return humanParticipants(allParts).some(
    (other) =>
      other !== p &&
      String(other.id ?? "") !== String(p.id ?? "") &&
      !isGuestMeetLinkEmail(other.email) &&
      shouldMergeAttendanceParticipantRows(p, other, context)
  );
}

function uniqueParticipantsByEmail(parts) {
  const seen = new Set();
  const out = [];
  for (const p of parts || []) {
    const email = String(p?.email || "").trim().toLowerCase();
    const key = email || `__noemail__${String(p?.initials || "").trim()}__`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

function displayUserProfiles(parts) {
  const seen = new Set();
  const out = [];
  for (const p of humanParticipants(parts || [])) {
    const email = String(p?.email || "").trim().toLowerCase();
    if (email && isGuestMeetLinkEmail(email)) continue;
    const fallbackName = String(p?.name || "").trim().toLowerCase();
    const key = email || `name:${fallbackName || String(p?.initials || "").trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/** Known MeetAI mailbox / service accounts (calendar owner for auto-dispatch — not the human host). */
const KNOWN_BOT_SERVICE_EMAILS = new Set(
  [BOT_EMAIL_DISPLAY, "ai.team@avaada.com", "app.testing@avaada.com", "avaada.companion@avaada.com"]
    .map(normAppEmail)
    .filter(Boolean)
);

/** Google calendar / Workspace labels that are team mailboxes, not a person's name. */
function isServiceAccountDisplayName(name) {
  const t = String(name || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!t) return false;
  return (
    t === "ai team" ||
    t === "aiteam" ||
    t.startsWith("ai team ") ||
    /avaada\s*meet\s*companion/.test(t) ||
    t === "companion" ||
    t === "meetai" ||
    t === "meet ai" ||
    t === "avaada meetai"
  );
}

/** True if an email belongs to the MeetAI bot service account (never the real meeting host). */
function isBotServiceAccountEmail(email) {
  const e = normAppEmail(email);
  if (!e) return false;
  return KNOWN_BOT_SERVICE_EMAILS.has(e);
}

/** MeetAI bot rows must not appear in meeting-detail participant rosters. */
function shouldHideMeetAiFromParticipantRoster(p) {
  if (!p) return true;
  if (participantIsBot(p)) return true;
  if (isBotServiceAccountEmail(p.email)) return true;
  const name = String(p.name || "").trim();
  if (isServiceAccountDisplayName(name)) return true;
  const botLabel = String(BOT_DISPLAY_NAME || "").trim().toLowerCase();
  if (botLabel && name.toLowerCase() === botLabel) return true;
  return false;
}

/** True when a label looks like an email local-part (e.g. venkata.hemanth), not a real display name. */
function looksLikeEmailLocalName(s) {
  const t = String(s || "").trim();
  if (!t || t.includes("@") || /\s/.test(t)) return false;
  return /^[a-z0-9._+-]+$/i.test(t) && /[._+-]/.test(t);
}

/**
 * Normalize person labels for admin UI:
 * - "Banerjee, Anirudh" → "Anirudh Banerjee"
 * - "venkata.hemanth" → "Venkata Hemanth"
 * - full names left as-is
 */
function formatDisplayPersonName(raw) {
  let s = String(raw || "").trim();
  if (!s) return "";
  if (s.includes("@")) s = s.split("@")[0].trim();
  if (isServiceAccountDisplayName(s)) return "";
  if (s.includes(",")) {
    const bits = s.split(",").map((x) => x.trim()).filter(Boolean);
    if (bits.length >= 2) s = `${bits.slice(1).join(" ")} ${bits[0]}`.trim();
  }
  if (looksLikeEmailLocalName(s) || (!/\s/.test(s) && /[._+-]/.test(s))) {
    s = s
      .split(/[._+-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }
  return s;
}

function scoreDisplayPersonName(s) {
  const t = String(s || "").trim();
  if (!t) return -1;
  if (isServiceAccountDisplayName(t)) return -500;
  let score = Math.min(t.length, 40);
  if (/\s/.test(t)) score += 80;
  if (looksLikeEmailLocalName(t)) score -= 120;
  if (t.includes("@")) score -= 80;
  return score;
}

function pickBestDisplayPersonName(...candidates) {
  let best = "";
  // Must be -Infinity so email-local labels (e.g. "app.testing", "venkata.hemanth")
  // still win — they score negative vs real names but are valid chip text.
  // Starting at -1 previously left admin Session Registry showing "—" for not-joined invitees.
  let bestScore = -Infinity;
  for (const c of candidates) {
    const raw = String(c || "").trim();
    if (!raw) continue;
    if (isServiceAccountDisplayName(raw)) continue;
    const formatted = formatDisplayPersonName(raw);
    if (!formatted) continue;
    const score = scoreDisplayPersonName(raw) + (formatted.includes(" ") ? 10 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = formatted;
    }
  }
  return best;
}

/** Pick the first non-bot human participant. Used as a fallback when host_* is empty or is the bot. */
function meetingFallbackHostParticipant(m) {
  const users = humanParticipants(m?.participants || []).filter(
    (u) => !isBotServiceAccountEmail(u?.email)
  );
  return users.length > 0 ? users[0] : null;
}

function meetingHostName(m) {
  const hostName = String(m?.hostName || m?.host_name || "").trim();
  const hostEmail = String(m?.hostEmail || m?.host_email || "").trim();
  const ownerName = String(m?.owner_name || m?.ownerName || "").trim();
  const ownerEmail = String(m?.owner_email || m?.ownerEmail || "").trim();
  const hostIsBot =
    (hostEmail && isBotServiceAccountEmail(hostEmail)) || isServiceAccountDisplayName(hostName);
  const ownerIsBot =
    (ownerEmail && isBotServiceAccountEmail(ownerEmail)) || isServiceAccountDisplayName(ownerName);
  const fallback = meetingFallbackHostParticipant(m);
  // Prefer human host email → proper name when Google stamped "AI Team" on host_name.
  return pickBestDisplayPersonName(
    !hostIsBot ? hostName : "",
    !hostIsBot ? hostEmail : "",
    !ownerIsBot ? ownerName : "",
    !ownerIsBot ? ownerEmail : "",
    fallback?.name,
    fallback?.email
  );
}

/** Mirrors meetingHostName but returns the host's email (filtering out the bot service account). */
function meetingHostEmail(m) {
  const hostEmail = String(m?.hostEmail || m?.host_email || "").trim();
  if (hostEmail && !isBotServiceAccountEmail(hostEmail)) return hostEmail;
  const fallback = meetingFallbackHostParticipant(m);
  return fallback ? String(fallback.email || "").trim() : "";
}

/** Whether we should render the "Host: …" line at all. Hides service-account-only rows. */
function meetingHasDisplayableHost(m) {
  return Boolean(meetingHostName(m));
}

function meetingHostInitials(m) {
  const host = String(meetingHostName(m) || "").trim();
  if (!host) return "HM";
  const parts = host.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  return host.slice(0, 2).toUpperCase();
}

/** True if the meeting's `owner_email` is the bot service account (auto-dispatch). */
function meetingOwnerIsBotServiceAccount(m) {
  return isBotServiceAccountEmail(m?.owner_email || m?.ownerEmail);
}

function meetingAutoJoinOptInUsers(m) {
  return Array.isArray(m?.autoJoinOptInUsers) ? m.autoJoinOptInUsers.filter(Boolean) : [];
}

/** Names/emails of users who opted in to auto-join for this calendar event. */
function meetingAutoJoinTriggeredBySummary(m, max = 3) {
  const aj = meetingAutoJoinOptInUsers(m);
  if (!aj.length) return "";
  return summarizeAdminPeopleList(aj, max);
}

/**
 * One attribution line for admin overview lists: who triggered the bot.
 * Priority: auto-join opt-in(s) → calendar host → human in-app dispatcher.
 */
function meetingOverviewAttribution(m) {
  const ajSummary = meetingAutoJoinTriggeredBySummary(m, 2);
  if (ajSummary) return { label: "Auto join by", text: ajSummary };

  const hostName = meetingHostName(m);
  const hostEmail = meetingHostEmail(m);
  if (hostName || hostEmail) {
    const text = hostName
      ? hostEmail
        ? `${hostName} · ${hostEmail}`
        : hostName
      : hostEmail;
    return { label: "Host", text };
  }

  if (!meetingOwnerIsBotServiceAccount(m)) {
    const ownerName = String(m?.owner_name || "").trim();
    const ownerEmail = String(m?.owner_email || m?.ownerEmail || "").trim();
    const ownerDisplay = isServiceAccountDisplayName(ownerName) ? "" : ownerName;
    if (ownerDisplay || ownerEmail) {
      return {
        label: "Owner",
        text: ownerDisplay
          ? ownerEmail
            ? `${ownerDisplay} · ${ownerEmail}`
            : ownerDisplay
          : ownerEmail,
      };
    }
  }
  return null;
}

/** Parse "HH:MM" to minutes since midnight (24h). */
function hmClockToMinutes(hm) {
  const m = String(hm || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h < 0 || h > 23 || mi < 0 || mi > 59) return null;
  return h * 60 + mi;
}

/** Parse duration text; status placeholders like "completed" → 0 minutes. */
function durationTextToMinutes(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (!s || s === "ongoing" || s === "completed" || s === "-" || s === "call ended" || s === "ended early") return 0;
  const h = s.match(/(\d+)\s*h/);
  const m = s.match(/(\d+)\s*m/);
  if (h || m) return (h ? Number(h[1]) * 60 : 0) + (m ? Number(m[1]) : 0);
  const digits = s.match(/\d+/);
  return digits ? Number(digits[0]) : 0;
}

/** Prefer time + end_time; most DB rows store duration='completed' after finalize. */
function meetingDurationMinutes(m) {
  const start = hmClockToMinutes(m?.time);
  const end = hmClockToMinutes(m?.end || m?.end_time);
  if (start != null && end != null) {
    let diff = end - start;
    if (diff < 0) diff += 24 * 60;
    if (diff > 0 && diff < 24 * 60) return diff;
  }
  return durationTextToMinutes(m?.duration);
}

/** Admin Usage sessions table — human-readable duration from existing meeting fields. */
function formatAdminUsageMinutes(m) {
  const mins = Math.max(0, Math.round(Number(meetingDurationMinutes(m)) || 0));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  if (r === 0) return `${h} hr`;
  return `${h} hr ${r} min`;
}

function normalizeMeetingsList(arr) {
  return (Array.isArray(arr) ? arr : []).map(normalizeMeeting);
}

/** Stable key for matching Meet/Zoom/Teams links across calendar + DB rows. */
function normalizeMeetingUrlForDedupe(raw) {
  if (!raw || typeof raw !== "string") return "";
  const s = raw.trim();
  if (!s) return "";
  try {
    const u = new URL(s);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    let path = u.pathname.replace(/\/+$/, "") || "/";
    if (host === "meet.google.com") {
      return `${host}${path}`.toLowerCase();
    }
    if (host.includes("zoom.us")) {
      u.hash = "";
      return `${host}${path}${u.search}`.toLowerCase();
    }
    if (host.includes("teams.microsoft.com") || host.includes("teams.live.com")) {
      u.hash = "";
      return `${host}${path}${u.search}`.toLowerCase();
    }
    u.hash = "";
    return `${host}${path}${u.search}`.toLowerCase();
  } catch {
    return s.toLowerCase();
  }
}

function normalizeMeetingTimeForDedupe(raw) {
  const value = String(raw || "").trim().toLowerCase();
  if (!value || value === "—" || value === "-") return "__notime__";
  const parsed = parseMeetingTimeToHourMin(value);
  if (parsed?.h != null && parsed?.min != null) {
    return `${String(parsed.h).padStart(2, "0")}:${String(parsed.min).padStart(2, "0")}`;
  }
  return value;
}

function meetingDedupKey(m, multiOwner = false) {
  const gid = extractGoogleEventId(m);
  if (gid) {
    return multiOwner && m?.owner_id != null
      ? `${String(m.owner_id)}|g:${gid}`
      : `g:${gid}`;
  }
  const url = joinUrlFromMeeting(m);
  const date = m?.date;
  const time = normalizeMeetingTimeForDedupe(m?.time);
  if (!(url && date)) return null;
  const nu = normalizeMeetingUrlForDedupe(url);
  return multiOwner && m?.owner_id != null
    ? `${String(m.owner_id)}|${nu}|${date}|${time}`
    : `${nu}|${date}|${time}`;
}

/** Same visible slot (title + day + start + link) — collapses duplicate calendar/DB rows with different google_event_id. */
function meetingVisualSlotKey(m, multiOwner = false) {
  const date = String(m?.date || "").trim().slice(0, 10);
  if (!date) return null;
  const time = normalizeMeetingTimeForDedupe(m?.time);
  const title = String(m?.title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!title || time === "__notime__") return null;
  const url = normalizeMeetingUrlForDedupe(joinUrlFromMeeting(m));
  const prefix = multiOwner && m?.owner_id != null ? `${String(m.owner_id)}|` : "";
  if (url) return `${prefix}vis:${url}|${date}|${time}|${title}`;
  return `${prefix}vis:t|${title}|${date}|${time}`;
}

/**
 * Same link + day, ignoring start time — last-resort match so a same-day reschedule
 * (new start time, same google_event_id-less DB stub) still resolves to one row instead
 * of leaving a stale DB copy alongside the freshly-timed calendar row.
 */
function meetingLooseDaySlotKey(m, multiOwner = false) {
  const date = String(m?.date || "").trim().slice(0, 10);
  if (!date) return null;
  const url = normalizeMeetingUrlForDedupe(joinUrlFromMeeting(m));
  if (!url) return null;
  const prefix = multiOwner && m?.owner_id != null ? `${String(m.owner_id)}|` : "";
  return `${prefix}loose:${url}|${date}`;
}

/**
 * Same Google Calendar event id AND same scheduled start — the true identity of one real
 * session. A mid-flight reschedule (host no-show, meeting moved later) can leave two DB rows
 * sharing one google_event_id: the aborted early attempt and the actual later session. Each
 * has its own transcript/attendance and must stay its own row — collapsing them by bare
 * google_event_id (ignoring time) silently drops one side's transcript.
 */
function meetingGidSlotKey(m, multiOwner = false) {
  const gid = extractGoogleEventId(m);
  if (!gid) return null;
  const date = String(m?.date || "").trim().slice(0, 10);
  const time = normalizeMeetingTimeForDedupe(m?.time);
  const prefix = multiOwner && m?.owner_id != null ? `${String(m.owner_id)}|` : "";
  return `${prefix}g:${gid}|${date}|${time}`;
}

/** True when a detail API refresh belongs to the meeting the user opened (not another occurrence). */
function meetingDetailMatchesOpened(opened, incoming) {
  if (!opened || !incoming) return false;
  const oId = String(opened?.id ?? opened?._id ?? "").trim();
  const iId = String(incoming?.id ?? incoming?._id ?? "").trim();
  if (isUuidLike(oId) && isUuidLike(iId)) return oId === iId;
  if (oId && iId && oId === iId) return true;
  const oKey = meetingOccurrenceKey(opened);
  const iKey = meetingOccurrenceKey(incoming);
  if (oKey && iKey) return oKey === iKey;
  const oGid = extractGoogleEventId(opened);
  const iGid = extractGoogleEventId(incoming);
  if (oGid && iGid && oGid !== iGid) return false;
  return false;
}

function meetingListRowSameOccurrence(a, b) {
  if (!a || !b) return false;
  const ka = meetingOccurrenceKey(a);
  const kb = meetingOccurrenceKey(b);
  if (ka && kb) return ka === kb;
  const idA = String(a?.id ?? a?._id ?? "").trim();
  const idB = String(b?.id ?? b?._id ?? "").trim();
  return !!(idA && idB && idA === idB);
}

/** A meeting the Companion actually attended (or has captured content for) — as opposed to a
 * bare upcoming stub with no session yet. Used to decide whether a live Google Calendar refetch
 * may update a DB row's schedule in place (safe for a still-untouched stub) or must instead be
 * treated as a distinct occurrence (unsafe once a real session already happened at another time).
 */
function meetingHasRealSession(m) {
  if (!m) return false;
  if (m.recallBotId || m.recall_bot_id) return true;
  if (Array.isArray(m.transcript_raw) && m.transcript_raw.length > 0) return true;
  if (Array.isArray(m.transcript) && m.transcript.length > 0) return true;
  return false;
}

/** Google Calendar instance ids share a prefix before `_YYYYMMDD…`. */
function calendarGoogleEventIdBase(gid) {
  const s = String(gid || "").trim();
  const i = s.indexOf("_20");
  return i > 0 ? s.slice(0, i) : s;
}

function meetingGoogleRecurringId(m) {
  return String(m?.googleRecurringEventId || m?.google_recurring_event_id || "").trim();
}

/**
 * A future Upcoming row is a leftover when this same calendar event was already held
 * on an earlier day (moved up), or its reschedule history points at that finished session.
 * The next real occurrence of a series is kept.
 */
function googleInstanceOriginalYmd(gid) {
  const match = String(gid || "").match(/_(\d{4})(\d{2})(\d{2})T/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function upcomingStubAlreadyHeld(m, meetings) {
  const st = effectiveMeetingStatus(m);
  if (st !== "upcoming" && st !== "live") return false;
  if (meetingHasRealSession(m)) return false;
  const start = meetingDateTimeMs(m);
  if (!start || start <= Date.now()) return false;
  const gid = extractGoogleEventId(m);
  const rec = meetingGoogleRecurringId(m);
  const ownDay = String(m?.date || "").trim().slice(0, 10);
  const timelineDays = new Set(
    (Array.isArray(m?.rescheduleTimeline) ? m.rescheduleTimeline : [])
      .map((slot) => String(slot?.date || "").trim().slice(0, 10))
      .filter((day) => day && day !== ownDay)
  );
  for (const other of meetings || []) {
    if (!other || other === m) continue;
    const otherStart = meetingDateTimeMs(other);
    if (!otherStart || otherStart >= start) continue;
    const finished =
      isEndedMeetingStatus(effectiveMeetingStatus(other), other) || meetingHasRealSession(other);
    if (!finished) continue;
    const otherGid = extractGoogleEventId(other);
    if (gid && otherGid && gid === otherGid) return true;
    const otherRec = meetingGoogleRecurringId(other);
    const base = calendarGoogleEventIdBase(gid);
    const otherBase = calendarGoogleEventIdBase(otherGid);
    const sameSeries =
      (!!rec && otherRec === rec) ||
      (!!base && !!otherBase && base.length > 8 && base === otherBase);
    if (!sameSeries || !meetingHasRealSession(other)) continue;
    const otherDay = String(other?.date || "").trim().slice(0, 10);
    if (otherDay && timelineDays.has(otherDay)) return true;
    const otherTimelineDays = new Set(
      (Array.isArray(other?.rescheduleTimeline) ? other.rescheduleTimeline : [])
        .map((slot) => String(slot?.date || "").trim().slice(0, 10))
        .filter(Boolean)
    );
    if (ownDay && otherTimelineDays.has(ownDay) && otherDay && otherDay !== ownDay) return true;
    const originalYmd = googleInstanceOriginalYmd(otherGid);
    if (originalYmd && ownDay === originalYmd && otherDay && otherDay !== originalYmd) return true;
  }
  return false;
}

/** Same calendar series / instance on the same day (reschedule or recurring exception). */
function meetingSameCalendarSeriesDay(a, b) {
  const dateA = String(a?.date || "").trim().slice(0, 10);
  const dateB = String(b?.date || "").trim().slice(0, 10);
  if (!dateA || dateA !== dateB) return false;
  const gidA = extractGoogleEventId(a);
  const gidB = extractGoogleEventId(b);
  if (gidA && gidB && gidA === gidB) return true;
  const recA = meetingGoogleRecurringId(a);
  const recB = meetingGoogleRecurringId(b);
  if (recA && recB && recA === recB) return true;
  const baseA = calendarGoogleEventIdBase(gidA);
  const baseB = calendarGoogleEventIdBase(gidB);
  return !!(baseA && baseB && baseA.length > 4 && baseA === baseB);
}

/** DB already captured this occurrence — do not let a stale calendar slot reopen it as Live. */
function meetingSessionAlreadyFinished(m) {
  if (!m) return false;
  const raw = String(m?.status || "").toLowerCase();
  if (raw === "completed" || raw === "ended") return true;
  if (meetingHasRealSession(m) && meetingBotSessionEnded(m)) return true;
  return false;
}

/** Which row defines "current" schedule when building reschedule timelines. */
function rescheduleTimelineKeepRow(existing, incoming) {
  const a = normalizeMeeting(existing || {});
  const b = normalizeMeeting(incoming || {});
  if (meetingSessionAlreadyFinished(a) || meetingHasRealSession(a)) return a;
  if (meetingSessionAlreadyFinished(b) || meetingHasRealSession(b)) return b;
  return b;
}

function preservedSessionScheduleFields(existing) {
  if (!meetingSessionAlreadyFinished(existing) && !meetingHasRealSession(existing)) return {};
  return {
    date: existing.date,
    time: existing.time,
    end: existing.end ?? existing.end_time,
    end_time: existing.end_time ?? existing.end,
    status: existing.status,
    duration: existing.duration,
    hostName: existing.hostName ?? existing.host_name,
    host_name: existing.host_name ?? existing.hostName,
    hostEmail: existing.hostEmail ?? existing.host_email,
    host_email: existing.host_email ?? existing.hostEmail,
    recallBotId: existing.recallBotId ?? existing.recall_bot_id,
    recall_bot_id: existing.recall_bot_id ?? existing.recallBotId,
    bot_join_status_code: existing.bot_join_status_code ?? existing.botJoinStatusCode,
    botJoinStatusCode: existing.botJoinStatusCode ?? existing.bot_join_status_code,
    bot_join_status_sub_code: existing.bot_join_status_sub_code ?? existing.botJoinStatusSubCode,
    botJoinStatusSubCode: existing.botJoinStatusSubCode ?? existing.bot_join_status_sub_code,
    botDispatched: existing.botDispatched,
    bot_dispatch_pending: existing.bot_dispatch_pending,
  };
}

/** Same calendar occurrence family on one day (recurring id / series / link), not only exact event id. */
function rowMatchesReschedulePool(keep, row) {
  if (!row || !keep) return false;
  const dateK = String(keep?.date || "").trim().slice(0, 10);
  const dateR = String(row?.date || "").trim().slice(0, 10);
  if (!dateK || dateK !== dateR) return false;
  if (extractGoogleEventId(keep) && extractGoogleEventId(row) && extractGoogleEventId(keep) === extractGoogleEventId(row)) {
    return true;
  }
  if (meetingSameCalendarSeriesDay(keep, row)) return true;
  const urlK = joinUrlFromMeeting(keep);
  const urlR = joinUrlFromMeeting(row);
  return !!(urlK && urlR && urlK === urlR);
}

function flatRescheduleSourcePool(sources = {}) {
  return [
    ...(Array.isArray(sources.visible) ? sources.visible : []),
    ...(Array.isArray(sources.db) ? sources.db : []),
    ...(Array.isArray(sources.calendar) ? sources.calendar : []),
  ];
}

function buildReschedulePoolIndex(flatRows) {
  const byGidDay = new Map();
  const byUrlDay = new Map();
  const byDate = new Map();
  const all = [];
  for (const raw of flatRows) {
    if (!raw) continue;
    const row = normalizeMeeting(raw);
    const date = String(row.date || "").trim().slice(0, 10);
    if (!date) continue;
    all.push(row);
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date).push(row);
    const gid = extractGoogleEventId(row);
    if (gid) {
      const k = `${gid}|${date}`;
      if (!byGidDay.has(k)) byGidDay.set(k, []);
      byGidDay.get(k).push(row);
    }
    const url = joinUrlFromMeeting(row);
    if (url) {
      const k = `${url}|${date}`;
      if (!byUrlDay.has(k)) byUrlDay.set(k, []);
      byUrlDay.get(k).push(row);
    }
  }
  return { all, byGidDay, byUrlDay, byDate };
}

function poolRowsForRescheduleKeep(keep, index) {
  if (!keep || !index) return [];
  const date = String(keep?.date || "").trim().slice(0, 10);
  if (!date) return [];
  const seen = new Set();
  const out = [];
  const add = (p) => {
    const ms = meetingDateTimeMs(p) || 0;
    const key = `${String(p?.id || p?._id || extractGoogleEventId(p) || joinUrlFromMeeting(p) || "")}|${ms}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(p);
  };
  const gid = extractGoogleEventId(keep);
  if (gid) for (const row of index.byGidDay.get(`${gid}|${date}`) || []) add(row);
  const url = joinUrlFromMeeting(keep);
  if (url) for (const row of index.byUrlDay.get(`${url}|${date}`) || []) add(row);
  if (out.length < 2) {
    for (const row of index.byDate.get(date) || []) {
      if (rowMatchesReschedulePool(keep, row)) add(row);
    }
  }
  return out;
}

function meetingRowHasReadyRescheduleDisplay(m) {
  const tl = m?.rescheduleTimeline;
  return Array.isArray(tl) && tl.length >= 2;
}

function applyRescheduleSlotRangeLabels(slots, pools) {
  return slots.map((s) => {
    const row = mergeScheduleEndFromPool(
      { date: s.date, time: s.rawTime ?? s.time, end: s.end, end_time: s.end },
      pools
    );
    const range = meetingScheduleRangeLabel(row);
    return { ...s, range, end: row.end ?? row.end_time ?? s.end };
  });
}

/** Prefer the richest end time for this start slot from any matching pool row (e.g. 4:30–5:00 PM). */
function mergeScheduleEndFromPool(row, pools) {
  const base = normalizeMeeting(row || {});
  const startMs = meetingDateTimeMs(base);
  if (!startMs) return base;
  let best = base;
  let bestEndMs = meetingEndDateTimeMs(base);
  if (!(bestEndMs > startMs)) bestEndMs = 0;
  for (const raw of pools) {
    if (!raw) continue;
    const p = normalizeMeeting(raw);
    if (meetingDateTimeMs(p) !== startMs) continue;
    const endMs = meetingEndDateTimeMs(p);
    if (endMs > startMs && endMs >= bestEndMs) {
      bestEndMs = endMs;
      best = {
        ...base,
        end: p.end ?? p.end_time ?? base.end,
        end_time: p.end_time ?? p.end ?? base.end_time,
      };
    }
  }
  return best;
}

/**
 * Find the sibling meeting a reschedule left behind — another row sharing this one's Google
 * Calendar event id but a different scheduled start, now kept as its own row (see
 * meetingGidSlotKey above) instead of being silently merged away. Used to show a friendly
 * "Rescheduled from/to <time>" note so two same-titled cards on the same day make sense to
 * the user instead of looking like an accidental duplicate.
 * Returns null when there's no reschedule to explain.
 */
function findRescheduleSibling(meeting, allMeetings) {
  const ownMs = meetingDateTimeMs(meeting);
  if (!ownMs) return null;
  const ownId = String(meeting?.id || meeting?._id || "").trim();
  const list = Array.isArray(allMeetings) ? allMeetings : [];
  let earliestLater = null;
  let latestEarlier = null;
  for (const other of list) {
    if (!other) continue;
    const otherId = String(other?.id || other?._id || "").trim();
    if (ownId && otherId && otherId === ownId) continue;
    if (!rowMatchesReschedulePool(meeting, other)) continue;
    const otherMs = meetingDateTimeMs(other);
    if (!otherMs || otherMs === ownMs) continue;
    if (otherMs > ownMs) {
      if (!earliestLater || otherMs < earliestLater.ms) earliestLater = { ms: otherMs, m: other };
    } else if (!latestEarlier || otherMs > latestEarlier.ms) {
      latestEarlier = { ms: otherMs, m: other };
    }
  }
  // A later sibling exists → this row is the one that got superseded; point forward to the
  // occurrence that actually holds now. Otherwise, point back to what this one replaced.
  if (earliestLater) {
    return {
      direction: "to",
      time: formatMeetingTimeForDisplay(earliestLater.m.time),
      sameDay: earliestLater.m.date === meeting.date,
      date: earliestLater.m.date,
      siblingMeeting: earliestLater.m,
    };
  }
  if (latestEarlier) {
    return {
      direction: "from",
      time: formatMeetingTimeForDisplay(latestEarlier.m.time),
      sameDay: latestEarlier.m.date === meeting.date,
      date: latestEarlier.m.date,
      siblingMeeting: latestEarlier.m,
    };
  }
  return null;
}

/** Friendly one-line label from findRescheduleSibling's result, e.g. "Rescheduled to 4:30 PM"
 * or "Rescheduled from 2:30 PM, Sep 8" when the new slot fell on a different day. */
function rescheduleBadgeLabel(info) {
  if (!info) return "";
  const when = info.sameDay ? info.time : `${info.time}, ${formatYmdDisplay(info.date)}`;
  return info.direction === "to" ? `Rescheduled to ${when}` : `Rescheduled from ${when}`;
}

function meetingRowIdentity(m) {
  const id = String(m?.id || m?._id || "").trim();
  if (id) return `id:${id}`;
  const slot = meetingGidSlotKey(m, false);
  if (slot) return `slot:${slot}`;
  const dk = meetingDedupKey(m, false);
  return dk ? `key:${dk}` : `row:${String(m?.title || "")}|${String(m?.date || "")}|${String(m?.time || "")}`;
}

/** Live google_event_id + calendar-day slots from the latest Calendar fetch. */
function liveGoogleGidDaySlots(googleEvents) {
  const liveByGroup = new Map();
  for (const raw of googleEvents || []) {
    const m = normalizeMeeting(raw);
    const gid = extractGoogleEventId(m);
    const date = String(m?.date || "").trim().slice(0, 10);
    const slot = gid && date ? meetingGidSlotKey(m, false) : null;
    if (!slot) continue;
    const gk = `${gid}|${date}`;
    if (!liveByGroup.has(gk)) liveByGroup.set(gk, new Set());
    liveByGroup.get(gk).add(slot);
  }
  return liveByGroup;
}

function meetingScheduleRangeLabel(m) {
  const start = formatMeetingTimeForDisplay(m?.time);
  const endRaw = m?.end ?? m?.end_time;
  if (endRaw && String(endRaw).trim() && String(endRaw).trim() !== "—") {
    const startMs = meetingDateTimeMs(m);
    const date = String(m?.date || "").trim().slice(0, 10);
    const { h, min } = parseMeetingTimeToHourMin(endRaw);
    if (startMs && date && h != null && min != null) {
      const [y, mo, d] = date.split("-").map(Number);
      const endMs = new Date(y, mo - 1, d, h, min, 0, 0).getTime();
      if (endMs > startMs) {
        return `${start} – ${formatMeetingTimeForDisplay(endRaw)}`;
      }
    }
  }
  return start;
}

/** List/table TIME column: start only (end range lives on meeting detail). */
function meetingListStartTimeLabel(displaySchedule, m) {
  const rawStart = displaySchedule?.time ?? m?.time;
  const formatted = formatMeetingTimeForDisplay(rawStart);
  if (formatted && !/\s+[–-]\s+/.test(formatted)) return formatted;
  const range =
    displaySchedule?.range ||
    meetingScheduleRangeLabel(m);
  const startFromRange = String(range || "")
    .split(/\s+[–-]\s+/)[0]
    ?.trim();
  return startFromRange || formatted || String(range || "").trim();
}

/** Detail/list meta row: duration text (not bot-join state). */
function meetingDetailMetaDurationLabel(meeting, effectiveStatus) {
  if (effectiveStatus === "live") {
    const dur = String(meeting?.duration || "").trim();
    if (dur && !/^(completed|ended)$/i.test(dur)) return dur;
    return "ongoing";
  }
  if (effectiveStatus === "upcoming") return "Upcoming";
  if (effectiveStatus === "completed" || effectiveStatus === "ended") return "Completed";
  const dur = String(meeting?.duration || "").trim();
  if (dur && !/^(completed|ended)$/i.test(dur)) {
    return /^ongoing$/i.test(dur) ? "ended" : dur;
  }
  return dur || "";
}

function rescheduleSlotFromMeetingRow(r, keepMeeting) {
  const ms = meetingDateTimeMs(r) || 0;
  const rid = String(r?.id || r?._id || "").trim();
  const keepId = String(keepMeeting?.id || keepMeeting?._id || "").trim();
  const keepMs = meetingDateTimeMs(keepMeeting);
  return {
    time: formatMeetingTimeForDisplay(r.time),
    rawTime: r.time,
    end: r?.end ? formatMeetingTimeForDisplay(r.end) : "",
    range: meetingScheduleRangeLabel(r),
    date: r.date,
    ms,
    meetingId: rid,
    isCurrent:
      (keepId && rid && rid === keepId) ||
      (!keepId && keepMs && ms === keepMs),
  };
}

function dedupeRescheduleTimelineSlots(slots) {
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(slots) ? slots : []) {
    if (!raw?.date || !raw?.time) continue;
    const ms = Number(raw.ms) || meetingDateTimeMs({ date: raw.date, time: raw.time });
    const key = `${String(raw.date).slice(0, 10)}|${ms}`;
    if (seen.has(key)) {
      const idx = out.findIndex(
        (s) => `${String(s.date).slice(0, 10)}|${s.ms}` === key
      );
      if (idx >= 0 && raw.isCurrent) out[idx] = { ...out[idx], ...raw, isCurrent: true };
      continue;
    }
    seen.add(key);
    out.push({
      ...raw,
      ms,
      range: raw.range || raw.time,
    });
  }
  return out;
}

/** True when calendar/DB rows represent different scheduled start slots (not end-only drift). */
function meetingScheduledStartChanged(a, b) {
  const msA = meetingDateTimeMs(a);
  const msB = meetingDateTimeMs(b);
  if (!msA || !msB) return false;
  return Math.abs(msA - msB) > 60 * 1000;
}

function accumulateRescheduleTimeline(prevMeeting, nextMeeting, keepMeeting = nextMeeting) {
  const keep = normalizeMeeting(keepMeeting || nextMeeting || prevMeeting);
  const a = normalizeMeeting(prevMeeting || {});
  const b = normalizeMeeting(nextMeeting || {});
  const base = dedupeRescheduleTimelineSlots([
    ...(Array.isArray(a?.rescheduleTimeline) ? a.rescheduleTimeline : []),
    ...(Array.isArray(b?.rescheduleTimeline) ? b.rescheduleTimeline : []),
  ]);
  const gidA = extractGoogleEventId(a);
  const gidB = extractGoogleEventId(b);
  if (gidA && gidB && gidA === gidB) {
    const msA = meetingDateTimeMs(a);
    const msB = meetingDateTimeMs(b);
    if (msA && msB && meetingScheduledStartChanged(a, b)) {
      const calRow =
        String(a?.type || "").toLowerCase() === "google_calendar"
          ? a
          : String(b?.type || "").toLowerCase() === "google_calendar"
            ? b
            : null;
      const snapshotProof =
        !!(calRow?.priorSchedule || calRow?.scheduleChangedFrom || calRow?.rescheduleHint);
      const sessionEvidence = meetingHasRealSession(a) || meetingHasRealSession(b);
      const staleDbVsCalendar =
        calRow &&
        !snapshotProof &&
        !sessionEvidence &&
        !meetingSessionAlreadyFinished(a) &&
        !meetingSessionAlreadyFinished(b);
      if (!staleDbVsCalendar) {
        base.push(rescheduleSlotFromMeetingRow(a, keep));
        base.push(rescheduleSlotFromMeetingRow(b, keep));
      }
    }
  }
  return dedupeRescheduleTimelineSlots(base);
}

function collectRescheduleSlotsForMeeting(meeting, sources = {}) {
  const keep = normalizeMeeting(meeting || {});
  let slots = dedupeRescheduleTimelineSlots(
    Array.isArray(keep?.rescheduleTimeline) ? keep.rescheduleTimeline : []
  );
  const pools = sources?.poolIndex
    ? poolRowsForRescheduleKeep(keep, sources.poolIndex)
    : flatRescheduleSourcePool(sources);
  const rangePool = sources?.poolIndex?.all?.length ? sources.poolIndex.all : pools;
  if (slots.length >= 2 && meetingRowHasReadyRescheduleDisplay(keep)) {
    return applyRescheduleSlotRangeLabels(slots, rangePool);
  }
  for (const row of pools) {
    if (!row) continue;
    const enriched = mergeScheduleEndFromPool(row, rangePool);
    slots.push(rescheduleSlotFromMeetingRow(enriched, keep));
  }
  slots = dedupeRescheduleTimelineSlots(slots);
  return applyRescheduleSlotRangeLabels(slots, rangePool);
}

/** Open detail immediately from list row data — avoids scanning the full reschedule pool on click. */
function rescheduleSlotsFromRowForOpen(meeting, sources = {}) {
  const keep = normalizeMeeting(meeting || {});
  const onRow = dedupeRescheduleTimelineSlots(
    Array.isArray(keep?.rescheduleTimeline) ? keep.rescheduleTimeline : []
  );
  if (!onRow.length) return onRow;
  const rangePool = sources?.poolIndex?.all?.length
    ? sources.poolIndex.all
    : flatRescheduleSourcePool(sources);
  return applyRescheduleSlotRangeLabels(onRow, rangePool);
}

/** Detail view: use row timeline when ready; full pool collect only when needed. */
function rescheduleSlotsForDetailView(meeting, sources = {}) {
  const keep = normalizeMeeting(meeting || {});
  const onRow = dedupeRescheduleTimelineSlots(
    Array.isArray(keep?.rescheduleTimeline) ? keep.rescheduleTimeline : []
  );
  const rangePool = sources?.poolIndex?.all?.length
    ? sources.poolIndex.all
    : flatRescheduleSourcePool(sources);
  if (onRow.length >= 2 && meetingRowHasReadyRescheduleDisplay(keep)) {
    return applyRescheduleSlotRangeLabels(onRow, rangePool);
  }
  return collectRescheduleSlotsForMeeting(meeting, sources);
}

function meetingWithPriorScheduleTimeline(m) {
  let raw = m || {};
  const prior = raw.priorSchedule || raw.scheduleChangedFrom;
  if (!prior?.date || !prior?.time) return raw;
  const keep = normalizeMeeting(raw);
  const priorMs = meetingDateTimeMs({ ...keep, date: prior.date, time: prior.time });
  const curMs = meetingDateTimeMs(keep);
  const priorRow = normalizeMeeting({
    ...keep,
    date: prior.date,
    time: prior.time,
    end: prior.end ?? prior.end_time,
    end_time: prior.end ?? prior.end_time,
  });
  if (!priorMs || !curMs || priorMs === curMs || !meetingScheduledStartChanged(priorRow, keep)) {
    if (raw.priorSchedule || raw.scheduleChangedFrom || raw.rescheduleHint) {
      return normalizeMeeting({
        ...keep,
        priorSchedule: undefined,
        scheduleChangedFrom: undefined,
        rescheduleHint: undefined,
        reschedule_hint: undefined,
      });
    }
    return raw;
  }
  if (Array.isArray(keep.rescheduleTimeline) && keep.rescheduleTimeline.length >= 2) return raw;
  const slots = dedupeRescheduleTimelineSlots([
    rescheduleSlotFromMeetingRow(priorRow, keep),
    rescheduleSlotFromMeetingRow(keep, keep),
  ]);
  if (slots.length < 2) return raw;
  const note = rescheduleNoteFromTimeline(slots, keep);
  return normalizeMeeting({
    ...keep,
    rescheduleTimeline: slots,
    rescheduleNote: note || keep.rescheduleNote,
    rescheduleHint: raw.rescheduleHint || raw.reschedule_hint || "",
  });
}

/**
 * Which timeline slot is "current".
 * Upcoming/live: matches the calendar row start (active invite — may be earlier after a preponed).
 * Finished / real session: the slot that actually ran — never a later stale calendar time
 * (e.g. 4:30 PM after the meeting already completed at 10:30 AM).
 */
function pickCurrentRescheduleSlot(meeting, marked) {
  const list = Array.isArray(marked) ? marked : [];
  if (!list.length) return null;
  const finished = meetingSessionAlreadyFinished(meeting) || meetingHasRealSession(meeting);
  const meetingId = String(meeting?.id || meeting?._id || "").trim();
  const keepMs = meetingDateTimeMs(meeting);
  if (!finished && keepMs) {
    const exact = list.find((s) => s.ms === keepMs);
    if (exact) return exact;
    const day = String(meeting?.date || "").trim().slice(0, 10);
    const sameDayEarly = list.filter((s) => String(s.date || "").trim().slice(0, 10) === day);
    const pool = sameDayEarly.length ? sameDayEarly : list;
    const nearest = pool.reduce((best, s) => {
      const d = Math.abs((s.ms || 0) - keepMs);
      const bd = Math.abs((best?.ms || 0) - keepMs);
      return d < bd ? s : best;
    }, pool[0]);
    if (nearest) return nearest;
  }
  if (finished && keepMs) {
    const day = String(meeting?.date || "").trim().slice(0, 10);
    const sameDay = list.filter((s) => String(s.date || "").trim().slice(0, 10) === day);
    if (sameDay.length >= 2 && meetingHasRealSession(meeting)) {
      const sorted = [...sameDay].sort((a, b) => (a.ms || 0) - (b.ms || 0));
      const latestMs = sorted[sorted.length - 1]?.ms || 0;
      const endedMs = meetingEndedAtMs(meeting);
      // DB/calendar still on a later slot, but the session already ended earlier (e.g. 10:30 standup vs stale 4:30 row).
      if (keepMs >= latestMs && endedMs && endedMs < latestMs - 5 * 60 * 1000) {
        const ran = sorted.find((s) => {
          const slotEnd = meetingEndDateTimeMs({ date: s.date, time: s.rawTime ?? s.time, end: s.end }) || (s.ms + 3 * 3600000);
          return endedMs >= (s.ms || 0) && endedMs <= slotEnd;
        });
        if (ran) return ran;
      }
      if (endedMs) {
        const ran = sorted.find((s) => {
          const slotEnd = meetingEndDateTimeMs({ date: s.date, time: s.rawTime ?? s.time, end: s.end }) || (s.ms + 3 * 3600000);
          return endedMs >= (s.ms || 0) && endedMs <= slotEnd;
        });
        if (ran) return ran;
      }
    }
    const sessionSlot = list.find((s) => s.ms === keepMs);
    if (sessionSlot) return sessionSlot;
    if (finished && meetingId) {
      const byId = list.filter((s) => String(s.meetingId || "").trim() === meetingId);
      if (byId.length === 1 && (!keepMs || byId[0].ms === keepMs)) return byId[0];
    }
    if (sameDay.length) {
      return sameDay.reduce((best, s) => ((s.ms || 0) < (best?.ms || Infinity) ? s : best), sameDay[0]);
    }
  }
  if (finished && meetingId) {
    const byId = list.find((s) => String(s.meetingId || "").trim() === meetingId);
    if (byId) return byId;
  }
  if (keepMs) {
    const headerMatch = list.find((s) => s.ms === keepMs);
    if (headerMatch) return headerMatch;
  }
  return list.reduce(
    (best, s) => ((s.ms || 0) > (best?.ms || 0) ? s : best),
    list[0]
  );
}

/** Drop stale calendar/recurrence rows that are not earlier than the session we display. */
function filterReschedulePriorSlots(meeting, current, priors) {
  const curMs = Number(current?.ms) || 0;
  const endedMs = meetingEndedAtMs(meeting) || 0;
  return (Array.isArray(priors) ? priors : []).filter((s) => {
    const ms = Number(s?.ms) || 0;
    if (!ms || !curMs) return false;
    if (ms >= curMs - 60 * 1000) return false;
    if (endedMs && ms > endedMs + 24 * 60 * 60 * 1000) return false;
    return true;
  });
}

/** Current (calendar) schedule first, then previous times (most recent prior first). */
function orderRescheduleTimelineForDisplay(meeting, slots) {
  const deduped = dedupeRescheduleTimelineSlots(slots);
  if (deduped.length < 2) return null;
  const marked = deduped.map((s) => ({ ...s, isCurrent: false }));
  let current = pickCurrentRescheduleSlot(meeting, marked);
  if (!current) return null;
  current = { ...current, isCurrent: true };
  let priors = marked
    .filter((s) => `${s.date}|${s.ms}` !== `${current.date}|${current.ms}`)
    .sort((a, b) => (b.ms || 0) - (a.ms || 0))
    .map((s) => ({ ...s, isCurrent: false }));
  priors = filterReschedulePriorSlots(meeting, current, priors);
  if (!priors.length) return null;
  return [current, ...priors];
}

function buildRescheduleTimelineFromRows(rows, keepMeeting) {
  const sorted = [...rows].sort(
    (a, b) => (meetingDateTimeMs(a) || 0) - (meetingDateTimeMs(b) || 0)
  );
  return dedupeRescheduleTimelineSlots(
    sorted.map((r) => rescheduleSlotFromMeetingRow(r, keepMeeting))
  );
}

const RESCHEDULE_CHAIN_ARROW = " → ";

function normalizeRescheduleDisplayLine(line) {
  const s = String(line || "").trim();
  if (!s) return "";
  return s
    .replace(/\s*--->\s*/g, RESCHEDULE_CHAIN_ARROW)
    .replace(/\s*-->\s*/g, RESCHEDULE_CHAIN_ARROW)
    .replace(/\s+->\s+/g, RESCHEDULE_CHAIN_ARROW);
}

/** Split normalized chain into segments for arrow rendering between slots. */
function renderRescheduleChainText(line) {
  const normalized = normalizeRescheduleDisplayLine(line);
  if (!normalized) return null;
  const segments = normalized.split(" → ").map((p) => p.trim()).filter(Boolean);
  if (segments.length <= 1) return normalized;
  const nodes = [];
  segments.forEach((part, i) => {
    if (i > 0) {
      nodes.push(
        <span key={`resched-arr-${i}`} className="reschedule-chain-arrow" aria-hidden>
          →
        </span>
      );
    }
    nodes.push(<span key={`resched-seg-${i}`}>{part}</span>);
  });
  return nodes;
}

function RescheduleChip({ line, className = "", style = {} }) {
  const normalized = normalizeRescheduleDisplayLine(line);
  if (!normalized) return null;
  return (
    <span
      className={`meet-list-chip meet-list-chip--note meet-list-chip--reschedule reschedule-chip-tip${className ? ` ${className}` : ""}`}
      style={{
        cursor: "help",
        ...style,
      }}
      data-reschedule-tip={normalized}
      aria-label={normalized}
      title={normalized}
    >
      <Svg d={IC.clock} size={7} stroke={2} />
      Rescheduled
    </span>
  );
}

/** Chronological chain: Rescheduled 2:15pm – 2:40pm → 2:30pm – 2:45pm (supports 3+ slots). */
function formatRescheduleChainNote(timeline, keepMeeting, { compact = false } = {}) {
  if (!Array.isArray(timeline) || timeline.length < 2) return "";
  void keepMeeting;
  const chron = dedupeRescheduleTimelineSlots(timeline).sort(
    (a, b) => (a.ms || 0) - (b.ms || 0)
  );
  const labels = chron.map((t) => t.range || t.time).filter(Boolean);
  if (labels.length < 2) return "";
  if (compact && labels.length > 2) {
    return `Rescheduled ${labels[0]}${RESCHEDULE_CHAIN_ARROW}${labels[labels.length - 1]}`;
  }
  return `Rescheduled ${labels.join(RESCHEDULE_CHAIN_ARROW)}`;
}

function rescheduleNoteFromTimeline(timeline, keepMeeting) {
  return formatRescheduleChainNote(timeline, keepMeeting, { compact: false });
}

function rescheduleNoteTitleFromMeeting(m) {
  const timeline = m?.rescheduleTimeline;
  if (Array.isArray(timeline) && timeline.length >= 2) {
    return formatRescheduleChainNote(timeline, m, { compact: false });
  }
  const note = normalizeRescheduleDisplayLine(m?.rescheduleNote);
  return note || "";
}

function meetingRescheduleSlotsForDisplay(m, sources) {
  const onRow = dedupeRescheduleTimelineSlots(
    Array.isArray(m?.rescheduleTimeline) ? m.rescheduleTimeline : []
  );
  if (!sources) return onRow;
  if (onRow.length >= 2 && meetingRowHasReadyRescheduleDisplay(m)) {
    return applyRescheduleSlotRangeLabels(onRow, sources?.poolIndex?.all || flatRescheduleSourcePool(sources));
  }
  const collected = dedupeRescheduleTimelineSlots(collectRescheduleSlotsForMeeting(m, sources));
  if (collected.length >= onRow.length) return collected;
  return dedupeRescheduleTimelineSlots([...onRow, ...collected]);
}

/** List + detail: show the session/rescheduled slot (not stale original invite time). */
function meetingLatestDisplaySchedule(m, sources) {
  const timeline = meetingRescheduleSlotsForDisplay(m, sources);
  if (timeline.length >= 2) {
    const ordered = orderRescheduleTimelineForDisplay(m, timeline);
    const current = ordered?.[0];
    if (current && ordered.length >= 2) {
      return {
        time: current.rawTime ?? current.time,
        range: current.range || formatMeetingTimeForDisplay(current.rawTime ?? current.time),
        rescheduled: true,
        chainNote: formatRescheduleChainNote(ordered, m, { compact: false }),
        chainNoteCompact: formatRescheduleChainNote(ordered, m, { compact: true }),
      };
    }
  }
  const sessionFields = preservedSessionScheduleFields(m);
  const timeRaw = sessionFields.time || m?.time;
  const rangeM = sessionFields.time
    ? { ...m, ...sessionFields }
    : m;
  return {
    time: timeRaw,
    range: meetingScheduleRangeLabel(rangeM),
    rescheduled: !!normalizeRescheduleDisplayLine(m?.rescheduleNote),
    chainNote: normalizeRescheduleDisplayLine(m?.rescheduleNote),
    chainNoteCompact: normalizeRescheduleDisplayLine(m?.rescheduleNote),
  };
}

/** Which row represents the final schedule when same-day reschedule stubs were merged. */
function pickRescheduleKeepRow(rows, liveByGroup) {
  if (!rows?.length) return null;
  const withSession = rows.filter(meetingHasRealSession);
  if (withSession.length === 1) return withSession[0];
  if (withSession.length > 1) {
    return withSession.reduce((best, r) =>
      (meetingDateTimeMs(r) || 0) > (meetingDateTimeMs(best) || 0) ? r : best
    );
  }
  const endedRows = rows.filter((r) => isEndedMeetingStatus(effectiveMeetingStatus(r), r));
  if (endedRows.length) {
    return endedRows.reduce((best, r) =>
      (meetingDateTimeMs(r) || 0) > (meetingDateTimeMs(best) || 0) ? r : best
    );
  }
  const gid = extractGoogleEventId(rows[0]);
  const date = String(rows[0]?.date || "").trim().slice(0, 10);
  const live = gid && date ? liveByGroup.get(`${gid}|${date}`) : null;
  if (live?.size) {
    const liveRow = rows.find((r) => live.has(meetingGidSlotKey(r, false)));
    if (liveRow) return liveRow;
  }
  return rows.reduce((best, r) =>
    (meetingDateTimeMs(r) || 0) > (meetingDateTimeMs(best) || 0) ? r : best
  );
}

/**
 * Same Google event moved on one calendar day often leaves two list rows (old + new time)
 * with no bot/transcript on either. Show one row at the current slot and keep full history
 * on the card. Rows that each have a real session (transcript/bot) stay separate.
 */
function collapseSameEventRescheduleRows(list, googleEvents) {
  const arr = Array.isArray(list) ? list : [];
  const liveByGroup = liveGoogleGidDaySlots(googleEvents);
  const groups = new Map();
  for (const m of arr) {
    const gid = extractGoogleEventId(m);
    const date = String(m?.date || "").trim().slice(0, 10);
    if (!gid || !date) continue;
    const gk = `${gid}|${date}`;
    if (!groups.has(gk)) groups.set(gk, []);
    groups.get(gk).push(m);
  }
  const drop = new Set();
  const enrich = new Map();

  for (const [, rows] of groups) {
    if (rows.length < 2) continue;
    const distinctStarts = new Set(
      rows.map((r) => meetingDateTimeMs(r) || 0).filter((ms) => ms > 0)
    );
    if (distinctStarts.size < 2) continue;
    const withSession = rows.filter(meetingHasRealSession);
    if (withSession.length >= 2) continue;

    let keep;
    if (withSession.length === 1) {
      keep = withSession[0];
    } else {
      keep = pickRescheduleKeepRow(rows, liveByGroup);
    }

    for (const r of rows) {
      if (r !== keep) drop.add(meetingRowIdentity(r));
    }
    const timeline = buildRescheduleTimelineFromRows(rows, keep);
    enrich.set(meetingRowIdentity(keep), {
      rescheduleTimeline: timeline,
      rescheduleNote: rescheduleNoteFromTimeline(timeline, keep),
    });
  }

  return arr
    .filter((m) => !drop.has(meetingRowIdentity(m)))
    .map((m) => {
      const extra = enrich.get(meetingRowIdentity(m));
      return extra ? { ...m, ...extra } : m;
    });
}

/**
 * Drop stale ghost rows a reschedule leaves behind: same Google event + same day,
 * different scheduled start, no real session (no bot, no transcript), and the row's
 * slot is NOT backed by a live google_calendar row. Without this, the old slot
 * (a session-less stub whose time already passed) renders as a second "Done" entry
 * next to the live rescheduled occurrence. Rows with real sessions are ALWAYS kept
 * (transcript safety), and groups with no live calendar row are left untouched
 * (can't tell truth from a stale/offline calendar snapshot).
 */
function suppressSupersededRescheduleRows(list, googleEvents) {
  const arr = Array.isArray(list) ? list : [];
  const liveByGroup = liveGoogleGidDaySlots(googleEvents);
  const hasLaterActiveSibling = (m) => {
    const ownMs = meetingDateTimeMs(m);
    if (!ownMs) return false;
    const ownId = String(m?.id || m?._id || "").trim();
    for (const other of arr) {
      if (!other || other === m) continue;
      const otherId = String(other?.id || other?._id || "").trim();
      if (ownId && otherId && otherId === ownId) continue;
      if (!meetingSameCalendarSeriesDay(m, other)) continue;
      const otherMs = meetingDateTimeMs(other);
      if (!otherMs || otherMs <= ownMs) continue;
      const st = effectiveMeetingStatus(other);
      if (st === "live" || st === "upcoming") return true;
    }
    return false;
  };
  const hasEarlierFinishedSessionSibling = (m) => {
    if (meetingHasRealSession(m)) return false;
    const ownMs = meetingDateTimeMs(m);
    if (!ownMs) return false;
    const ownId = String(m?.id || m?._id || "").trim();
    for (const other of arr) {
      if (!other || other === m) continue;
      const otherId = String(other?.id || other?._id || "").trim();
      if (ownId && otherId && otherId === ownId) continue;
      if (!meetingSameCalendarSeriesDay(m, other)) continue;
      const otherMs = meetingDateTimeMs(other);
      if (!otherMs || otherMs >= ownMs) continue;
      if (meetingSessionAlreadyFinished(other) || meetingHasRealSession(other)) return true;
    }
    return false;
  };
  return arr.filter((m) => {
    if (meetingHasRealSession(m)) return true;
    if (hasEarlierFinishedSessionSibling(m)) return false;
    const gid = extractGoogleEventId(m);
    const date = String(m?.date || "").trim().slice(0, 10);
    if (!gid || !date) return true;
    const slot = meetingGidSlotKey(m, false);
    if (!slot) return true;
    if (hasLaterActiveSibling(m)) return false;
    if (liveByGroup.size === 0) return true;
    const live = liveByGroup.get(`${gid}|${date}`);
    if (!live || live.size === 0) return true;
    return live.has(slot);
  });
}

function calendarEventStableKey(ev) {
  const gid = extractGoogleEventId(ev);
  if (gid) return `g:${gid}`;
  const rawId = String(ev?.id || ev?._id || "").trim();
  if (rawId) return `i:${rawId}`;
  const fallback = meetingDedupKey(ev, false);
  return fallback ? `k:${fallback}` : null;
}

/** Merge calendar poll/refetch payloads without dropping Video Rec / Auto Join from prior snapshot. */
function mergeGoogleCalendarSnapshot(prevList, incomingList, multiOwner = false) {
  const prev = (Array.isArray(prevList) ? prevList : []).map(normalizeMeeting);
  const incoming = (Array.isArray(incomingList) ? incomingList : []).map(normalizeMeeting);
  const byStable = new Map();
  const visualToStable = new Map();

  const upsert = (ev) => {
    const stable = calendarEventStableKey(ev);
    const visual = meetingVisualSlotKey(ev, multiOwner);
    if (visual && visualToStable.has(visual)) {
      const pk = visualToStable.get(visual);
      byStable.set(pk, mergeMeetingDisplayPair(byStable.get(pk), ev));
      return;
    }
    if (stable && byStable.has(stable)) {
      byStable.set(stable, mergeMeetingDisplayPair(byStable.get(stable), ev));
      return;
    }
    const key = stable || `row:${byStable.size}`;
    byStable.set(key, ev);
    if (visual) visualToStable.set(visual, key);
  };

  for (const ev of prev) upsert(ev);
  for (const ev of incoming) upsert(ev);
  return Array.from(byStable.values());
}

/** Stable React list key — never use bare UUID twice when calendar + DB rows overlap. */
function meetingListReactKey(m, index = 0) {
  const visual = meetingVisualSlotKey(m);
  if (visual) return visual;
  const stable = calendarEventStableKey(m);
  if (stable) return stable;
  const id = String(m?.id || m?._id || "").trim();
  if (id) return `id:${id}`;
  return `row:${index}`;
}

/**
 * Calendar stubs never carry transcript content — when any merge folds rows
 * together, keep transcript evidence (count / text / speakers / action count)
 * from whichever side has it, so completed meetings don't vanish from Recorded Meetings.
 */
/** Keep bot join + recording evidence when calendar stubs merge with DB rows (lite list). */
function preservedBotSessionMeta(a, b) {
  const recallBotId = String(
    a?.recallBotId || a?.recall_bot_id || b?.recallBotId || b?.recall_bot_id || ""
  ).trim();
  const botCode = String(
    a?.bot_join_status_code ||
      a?.botJoinStatusCode ||
      b?.bot_join_status_code ||
      b?.botJoinStatusCode ||
      ""
  ).trim();
  const botSub = String(
    a?.bot_join_status_sub_code ||
      a?.botJoinStatusSubCode ||
      b?.bot_join_status_sub_code ||
      b?.botJoinStatusSubCode ||
      ""
  ).trim();
  const s3Key = String(a?.recordingS3Key || a?.recording_s3_key || b?.recordingS3Key || b?.recording_s3_key || "").trim();
  return {
    ...(recallBotId ? { recallBotId, recall_bot_id: recallBotId } : {}),
    ...(botCode ? { bot_join_status_code: botCode, botJoinStatusCode: botCode } : {}),
    ...(botSub ? { bot_join_status_sub_code: botSub, botJoinStatusSubCode: botSub } : {}),
    ...(s3Key ? { recordingS3Key: s3Key, recording_s3_key: s3Key } : {}),
  };
}

function preservedTranscriptMeta(a, b) {
  const pickCount = (...vals) => {
    let best = 0;
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n > best) best = Math.floor(n);
    }
    return best;
  };
  const rowCount = pickCount(
    a?.transcriptRowCount, a?.transcript_row_count,
    b?.transcriptRowCount, b?.transcript_row_count,
    Array.isArray(a?.transcript_raw) ? a.transcript_raw.length : 0,
    Array.isArray(b?.transcript_raw) ? b.transcript_raw.length : 0,
    Array.isArray(a?.transcript) ? a.transcript.length : 0,
    Array.isArray(b?.transcript) ? b.transcript.length : 0
  );
  const actionCount = pickCount(
    a?.actionItemCount, a?.action_item_count,
    b?.actionItemCount, b?.action_item_count,
    Array.isArray(a?.actionItems) ? a.actionItems.length : 0,
    Array.isArray(b?.actionItems) ? b.actionItems.length : 0
  );
  const speakers =
    (Array.isArray(a?.transcriptSpeakers) && a.transcriptSpeakers.length > 0 ? a.transcriptSpeakers : null) ||
    (Array.isArray(b?.transcriptSpeakers) && b.transcriptSpeakers.length > 0 ? b.transcriptSpeakers : null);
  const text = [a?.transcriptText, a?.transcript_text, b?.transcriptText, b?.transcript_text]
    .map((v) => String(v ?? ""))
    .find((s) => s.trim().length > 0) || "";
  return {
    transcriptRowCount: rowCount,
    transcript_row_count: rowCount,
    actionItemCount: actionCount,
    action_item_count: actionCount,
    ...(speakers ? { transcriptSpeakers: speakers } : {}),
    ...(text ? { transcriptText: text, transcript_text: text } : {}),
  };
}

function mergeMeetingDisplayPair(existing, incoming) {
  const a = normalizeMeeting(existing);
  const b = normalizeMeeting(incoming);
  const idA = String(a?.id || a?._id || "").trim();
  const idB = String(b?.id || b?._id || "").trim();
  const preferId = isUuidLike(idA) ? idA : isUuidLike(idB) ? idB : idA || idB;
  const partsA = Array.isArray(a?.participants) ? a.participants : [];
  const partsB = Array.isArray(b?.participants) ? b.participants : [];
  const mergedJoin = joinUrlFromMeeting(a) || joinUrlFromMeeting(b);
  const sessionSchedule =
    Object.keys(preservedSessionScheduleFields(b)).length > 0
      ? preservedSessionScheduleFields(b)
      : preservedSessionScheduleFields(a);
  return normalizeMeeting({
    ...a,
    ...b,
    ...sessionSchedule,
    id: preferId,
    googleEventId: extractGoogleEventId(b) || extractGoogleEventId(a),
    type: mergeMeetingKindType(a, b),
    meetingUrl: mergedJoin || a.meetingUrl || b.meetingUrl,
    hangoutLink: a.hangoutLink || b.hangoutLink || "",
    participants: partsB.length > partsA.length ? partsB : partsA,
    recordingPersisted:
      a.recordingPersisted === true ||
      b.recordingPersisted === true ||
      a.recordingEnabled === true ||
      b.recordingEnabled === true,
    autoJoinEnabled: resolvedAutoJoinEnabled(a, b),
    recordingEnabled: recordingEnabledForAutoJoin(
      resolvedAutoJoinEnabled(a, b),
      a.recordingPersisted === true ||
        b.recordingPersisted === true ||
        a.recordingEnabled === true ||
        b.recordingEnabled === true
    ),
    recordingS3Key: a.recordingS3Key || b.recordingS3Key || "",
    ...preservedBotSessionMeta(a, b),
    transcript:
      (Array.isArray(a?.transcript_raw) && a.transcript_raw.length > 0)
        ? a.transcript_raw
        : (Array.isArray(b?.transcript_raw) && b.transcript_raw.length > 0)
          ? b.transcript_raw
          : (Array.isArray(a?.transcript) && a.transcript.length > 0)
            ? a.transcript
            : b.transcript || [],
    transcript_raw:
      (Array.isArray(a?.transcript_raw) && a.transcript_raw.length > 0)
        ? a.transcript_raw
        : (Array.isArray(b?.transcript_raw) && b.transcript_raw.length > 0)
          ? b.transcript_raw
          : a?.transcript_raw || b?.transcript_raw || [],
    actionItems:
      (Array.isArray(a?.actionItems) && a.actionItems.length > 0)
        ? a.actionItems
        : b.actionItems || [],
    summary: a?.summary || b?.summary || null,
    // Either side may hold the transcript evidence — never let the other side wipe it.
    ...preservedTranscriptMeta(a, b),
    rescheduleTimeline: (() => {
      const keep = rescheduleTimelineKeepRow(a, b);
      return accumulateRescheduleTimeline(a, b, keep);
    })(),
    rescheduleNote: (() => {
      const keep = rescheduleTimelineKeepRow(a, b);
      const timeline = accumulateRescheduleTimeline(a, b, keep);
      return timeline.length >= 2 ? rescheduleNoteFromTimeline(timeline, keep) : a.rescheduleNote || b.rescheduleNote || "";
    })(),
  });
}

function dedupeMeetingsForDisplay(rows, multiOwner = false, opts = {}) {
  const adminList = opts.adminList === true;
  const list = Array.isArray(rows) ? rows : [];
  const seenId = new Set();
  const seenGid = new Map();
  const seenSlot = new Map();
  const seenVisual = new Map();
  const out = [];
  for (const raw of list) {
    const m = normalizeMeeting(raw);
    const id = String(m?.id || m?._id || "").trim();
    // Event id + scheduled start, not the bare event id — two rows for the same calendar
    // event at genuinely different times (a same-day reschedule) are two real sessions, not
    // duplicates of one another.
    const gidSlot = meetingGidSlotKey(m, multiOwner);
    // meetingDedupKey's gid branch collapses to a bare "g:<id>" that ignores time — reusing it
    // here for rows that already have gidSlot above would silently re-merge two same-day
    // rescheduled sessions and drop one side's transcript. Only use it as the url+date+time
    // fallback for rows with no google_event_id at all.
    const slot = gidSlot ? null : meetingDedupKey(m, multiOwner);
    const visual = meetingVisualSlotKey(m, multiOwner);
    if (id && seenId.has(id)) continue;
    if (gidSlot && seenGid.has(gidSlot)) {
      const idx = seenGid.get(gidSlot);
      out[idx] = mergeMeetingDisplayPair(out[idx], m);
      continue;
    }
    if (!adminList && visual && seenVisual.has(visual)) {
      const idx = seenVisual.get(visual);
      out[idx] = mergeMeetingDisplayPair(out[idx], m);
      continue;
    }
    if (!adminList && slot && seenSlot.has(slot)) {
      const idx = seenSlot.get(slot);
      out[idx] = mergeMeetingDisplayPair(out[idx], m);
      continue;
    }
    const idx = out.length;
    if (id) seenId.add(id);
    if (gidSlot) seenGid.set(gidSlot, idx);
    if (visual) seenVisual.set(visual, idx);
    if (slot) seenSlot.set(slot, idx);
    out.push(m);
  }
  return out;
}

/**
 * Drop duplicate DB rows (same video URL + calendar day + start time).
 * When multiOwner, scope by owner_id (admin all-meetings list).
 */
function dedupeDbMeetings(meetings, multiOwner = false, opts = {}) {
  return dedupeMeetingsForDisplay(meetings, multiOwner, opts);
}

/** Merge Google Calendar events with DB meetings without doubling the same scheduled call (URL + date + start time). */
function mergeMeetingsAndCalendar(dbMeetings, googleEvents, multiOwnerDb = false) {
  const db = dedupeDbMeetings(dbMeetings, multiOwnerDb);
  const merged = [...db];
  const keyToIndex = new Map();
  // Plain event id — time-agnostic. Only ever trusted as a match when the row it points to
  // has no real session yet (see meetingHasRealSession below): safe for refreshing a bare
  // upcoming stub onto a reschedule, unsafe once a real session already happened at another
  // time (would silently fold a second session's data onto the first, dropping one transcript).
  const gidToIndex = new Map();
  // Event id + scheduled start — the precise, always-safe identity for one real occurrence.
  const gidSlotToIndex = new Map();
  const visualToIndex = new Map();
  // Same link + day, ignoring start time — only for DB rows with no google_event_id to key
  // off of, so a reschedule (new time, same day/link) still resolves to the existing row
  // instead of leaving it as a stale duplicate alongside the freshly-timed calendar row.
  const looseToIndex = new Map();
  for (let idx = 0; idx < merged.length; idx += 1) {
    const row = merged[idx];
    const gid = extractGoogleEventId(row);
    if (gid) {
      const gk = multiOwnerDb && row?.owner_id != null ? `${String(row.owner_id)}|${gid}` : gid;
      gidToIndex.set(gk, idx);
      const gSlot = meetingGidSlotKey(row, multiOwnerDb);
      if (gSlot) gidSlotToIndex.set(gSlot, idx);
    } else {
      const loose = meetingLooseDaySlotKey(row, multiOwnerDb);
      if (loose) looseToIndex.set(loose, idx);
      // meetingDedupKey's non-gid branch (url + date + time) — the gid branch is deliberately
      // not used here; it collapses to a bare "g:<id>" that ignores time, which would let a
      // second real session at a different time merge into (and hide) the first one's data.
      const k = meetingDedupKey(row, multiOwnerDb);
      if (k) keyToIndex.set(k, idx);
    }
    const vis = meetingVisualSlotKey(row, multiOwnerDb);
    if (vis) visualToIndex.set(vis, idx);
  }

  const shouldReplaceWithGoogle = (existing, incomingGoogle) => {
    if (meetingSessionAlreadyFinished(existing) || meetingHasRealSession(existing)) return false;
    const existingStatus = effectiveMeetingStatus(existing);
    const incomingStatus = effectiveMeetingStatus(incomingGoogle);
    if ((existingStatus === "completed" || existingStatus === "ended") &&
        (incomingStatus === "upcoming" || incomingStatus === "live")) {
      return false;
    }
    // Prefer richer calendar payload when existing row is sparse.
    if (!joinUrlFromMeeting(existing) && joinUrlFromMeeting(incomingGoogle)) return true;
    return false;
  };

  for (const g of (googleEvents || []).map((row) => meetingWithPriorScheduleTimeline(normalizeMeeting(row)))) {
    const gid = extractGoogleEventId(g);
    const gk = gid ? (multiOwnerDb && g?.owner_id != null ? `${String(g.owner_id)}|${gid}` : gid) : "";
    const gSlot = meetingGidSlotKey(g, multiOwnerDb);
    const vis = meetingVisualSlotKey(g, multiOwnerDb);
    const loose = meetingLooseDaySlotKey(g, multiOwnerDb);
    // Only meaningful when g itself has no gid (rare/malformed calendar payload) — a real
    // gid always goes through gidSlotToIndex/gidToIndex instead, never this time-agnostic key.
    const k = gid ? null : meetingDedupKey(g, multiOwnerDb);
    if (!gid && !k) {
      merged.push(g);
      continue;
    }
    const plainGidIdx = gk ? gidToIndex.get(gk) : undefined;
    const plainGidMergeOk =
      plainGidIdx != null &&
      !meetingHasRealSession(merged[plainGidIdx]) &&
      !meetingIsInstantJoin(merged[plainGidIdx]);
    const existingIdx =
      (gSlot ? gidSlotToIndex.get(gSlot) : undefined) ??
      (plainGidMergeOk ? plainGidIdx : undefined) ??
      (vis ? visualToIndex.get(vis) : undefined) ??
      (k ? keyToIndex.get(k) : undefined) ??
      (loose ? looseToIndex.get(loose) : undefined);
    if (existingIdx == null) {
      const finishedSameDay = merged.find(
        (row) => meetingSameCalendarSeriesDay(row, g) && meetingSessionAlreadyFinished(row)
      );
      if (finishedSameDay && !meetingHasRealSession(g) && meetingScheduledStartChanged(finishedSameDay, g)) {
        const finIdx = merged.indexOf(finishedSameDay);
        if (finIdx >= 0) {
          const keep = merged[finIdx];
          const timeline = accumulateRescheduleTimeline(keep, g, keep);
          merged[finIdx] = normalizeMeeting({
            ...keep,
            ...preservedSessionScheduleFields(keep),
            rescheduleTimeline: timeline.length >= 2 ? timeline : keep?.rescheduleTimeline,
            rescheduleNote:
              timeline.length >= 2 ? rescheduleNoteFromTimeline(timeline, keep) : keep?.rescheduleNote || "",
          });
        }
        continue;
      }
      if (finishedSameDay && !meetingHasRealSession(g)) {
        continue;
      }
      // Rescheduled occurrence whose Google payload carries no join link (or a
      // link-less calendar row): inherit the stable meeting link from the same
      // event's DB row so Join never goes missing on the active occurrence.
      // Meeting links don't change on reschedule (same Meet/Zoom room).
      let toPush = g;
      let pushVis = vis;
      if (!joinUrlFromMeeting(g) && gk && gidToIndex.has(gk)) {
        const sib = merged[gidToIndex.get(gk)];
        const sibUrl = sib ? joinUrlFromMeeting(sib) : "";
        if (sibUrl) {
          toPush = normalizeMeeting({
            ...g,
            meetingUrl: sibUrl,
            meet_link: g.meet_link || sib.meet_link || sibUrl,
            hangoutLink: g.hangoutLink || sib.hangoutLink || sibUrl,
          });
          pushVis = meetingVisualSlotKey(toPush, multiOwnerDb);
        }
      }
      const nextIdx = merged.length;
      if (k) keyToIndex.set(k, nextIdx);
      if (gk) gidToIndex.set(gk, nextIdx);
      if (gSlot) gidSlotToIndex.set(gSlot, nextIdx);
      if (pushVis) visualToIndex.set(pushVis, nextIdx);
      merged.push(toPush);
      continue;
    }
    const existing = merged[existingIdx];
    if (shouldReplaceWithGoogle(existing, g)) {
      // Merge: Google Calendar provides fresh metadata (URL, title, status, participants),
      // but ALWAYS preserve the DB meeting's transcript, action items, and summary.
      merged[existingIdx] = {
        ...g,
        ...preservedSessionScheduleFields(existing),
        transcript:
          (Array.isArray(existing?.transcript_raw) && existing.transcript_raw.length > 0)
            ? existing.transcript_raw
            : (Array.isArray(existing?.transcript) && existing.transcript.length > 0)
              ? existing.transcript
              : (g.transcript || []),
        transcript_raw:
          (Array.isArray(existing?.transcript_raw) && existing.transcript_raw.length > 0)
            ? existing.transcript_raw
            : (g.transcript_raw || []),
        actionItems: (Array.isArray(existing?.actionItems) && existing.actionItems.length > 0) ? existing.actionItems : (g.actionItems || []),
        summary:     existing?.summary || g.summary || null,
        id:          existing?.id || g.id,
        // Calendar row carries no transcript evidence — keep the DB row's.
        ...preservedTranscriptMeta(existing, g),
        rescheduleTimeline: (() => {
          const keep = rescheduleTimelineKeepRow(existing, g);
          return accumulateRescheduleTimeline(existing, g, keep);
        })(),
        rescheduleNote: (() => {
          const keep = rescheduleTimelineKeepRow(existing, g);
          const timeline = accumulateRescheduleTimeline(existing, g, keep);
          return timeline.length >= 2 ? rescheduleNoteFromTimeline(timeline, keep) : existing?.rescheduleNote || "";
        })(),
      };
      if (gk) gidToIndex.set(gk, existingIdx);
      if (gSlot) gidSlotToIndex.set(gSlot, existingIdx);
      continue;
    }
    // Even when we keep the DB row, inherit calendar auto-join identity/state so per-event
    // toggles stay stable across reloads. Also fill meeting URL from Google when DB row has none
    // (e.g. link only in event description).
    const mergedJoin = joinUrlFromMeeting(existing) || joinUrlFromMeeting(g);
    // DB rows default to type "instant"; calendar rows are "google_calendar". Auto Join / per-event
    // toggles key off google_calendar — prefer calendar type when merging a Google event onto a DB row.
    merged[existingIdx] = normalizeMeeting({
      ...existing,
      ...g,
      ...preservedSessionScheduleFields(existing),
      id: isUuidLike(String(existing?.id || existing?._id || ""))
        ? String(existing?.id || existing?._id || "")
        : String(existing?.id || g?.id || ""),
      googleEventId: extractGoogleEventId(existing) || extractGoogleEventId(g),
      // Calendar payloads never include transcript — do not let ...g wipe DB segments.
      // Prefer raw Recall lines (full speaker names) over cleaned short names.
      transcript:
        (Array.isArray(existing?.transcript_raw) && existing.transcript_raw.length > 0)
          ? existing.transcript_raw
          : (Array.isArray(existing?.transcript) && existing.transcript.length > 0)
            ? existing.transcript
            : g.transcript || [],
      transcript_raw:
        (Array.isArray(existing?.transcript_raw) && existing.transcript_raw.length > 0)
          ? existing.transcript_raw
          : g.transcript_raw || [],
      actionItems:
        (Array.isArray(existing?.actionItems) && existing.actionItems.length > 0)
          ? existing.actionItems
          : g.actionItems || [],
      summary: existing?.summary || g.summary || null,
      // Calendar payloads never include transcript — do not let ...g wipe DB segments.
      ...preservedTranscriptMeta(existing, g),
      autoJoinEnabled:
        g?.autoJoinEnabled === false || g?.autoJoinEnabled === true
          ? g.autoJoinEnabled
          : existing?.autoJoinEnabled,
      type: mergeMeetingKindType(existing, g),
      meetingUrl: mergedJoin || existing.meetingUrl,
      hangoutLink: existing.hangoutLink || g.hangoutLink || "",
      recordingPersisted:
        existing?.recordingPersisted === true ||
        g?.recordingPersisted === true ||
        existing?.recordingEnabled === true ||
        g?.recordingEnabled === true,
      recordingEnabled: recordingEnabledForAutoJoin(
        (g?.autoJoinEnabled === false || g?.autoJoinEnabled === true
          ? g.autoJoinEnabled
          : existing?.autoJoinEnabled) === true,
        existing?.recordingPersisted === true ||
          g?.recordingPersisted === true ||
          existing?.recordingEnabled === true ||
          g?.recordingEnabled === true
      ),
      recordingS3Key: existing?.recordingS3Key || g?.recordingS3Key || "",
      rescheduleTimeline: (() => {
        const keep = rescheduleTimelineKeepRow(existing, g);
        return accumulateRescheduleTimeline(existing, g, keep);
      })(),
      rescheduleNote: (() => {
        const keep = rescheduleTimelineKeepRow(existing, g);
        const timeline = accumulateRescheduleTimeline(existing, g, keep);
        return timeline.length >= 2 ? rescheduleNoteFromTimeline(timeline, keep) : existing?.rescheduleNote || g?.rescheduleNote || "";
      })(),
    });
    if (gk) gidToIndex.set(gk, existingIdx);
    if (gSlot) gidSlotToIndex.set(gSlot, existingIdx);
    if (vis) visualToIndex.set(vis, existingIdx);
  }
  return dedupeMeetingsForDisplay(merged, multiOwnerDb);
}

/** Calendar banners: never show OAuth URLs, ETIMEDOUT, or token internals to users. */
function friendlyCalendarBannerText(raw) {
  const s = String(raw || "").trim();
  if (!s) return "Calendar couldn’t update. Tap Refresh or try again shortly.";
  const lower = s.toLowerCase();
  if (
    lower.includes("etimedout") ||
    lower.includes("econnreset") ||
    lower.includes("enotfound") ||
    lower.includes("google oauth") ||
    lower.includes("oauth2") ||
    lower.includes("no http response") ||
    lower.includes("token refresh") ||
    lower.includes("refresh token") ||
    lower.includes("firewall") ||
    lower.includes("vpn") ||
    lower.includes("unreachable")
  ) {
    return "We couldn’t reach Google Calendar. Check your internet connection, then tap Refresh.";
  }
  if (lower.includes("invalid_grant")) {
    return "Your Google sign-in needs a quick refresh. Sign out, sign in again, then tap Refresh.";
  }
  if (lower.includes("invalid_client") || lower.includes("client_secret")) {
    return "Calendar sync is temporarily unavailable. Please try again later.";
  }
  if (s.length > 240 || /https?:\/\//i.test(s)) {
    return "We couldn’t update your calendar. Tap Refresh or try again shortly.";
  }
  return userFacingMessage(s);
}

async function apiFetch(path, opts={}){
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        ...(opts.headers||{}),
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    const down = new Error("MeetAI is temporarily unreachable. Check your connection or try again shortly.");
    down.code = "NETWORK_DOWN";
    down.cause = err;
    window.dispatchEvent(new CustomEvent("meetai:api-down", { detail: { path, message: down.message } }));
    throw down;
  }
  if (res.status === 304) {
    window.dispatchEvent(new Event("meetai:api-up"));
    return { __notModified: true, etag: res.headers.get("ETag") || opts.headers?.["If-None-Match"] || "" };
  }
  let data = {};
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch (_) { data = { error: text.slice(0, 200) }; }
  }
  if (!res.ok) {
    if (data && data.code === "APP_ACCESS_PAUSED") {
      clearToken();
      window.dispatchEvent(
        new CustomEvent("meetai:access-paused", { detail: { message: data.error || "", path } })
      );
      const pausedErr = new Error(data.error || "Sorry for the inconvenience! Avaada MeetAI is temporarily unavailable. Thank you for your patience.");
      pausedErr.code = "APP_ACCESS_PAUSED";
      pausedErr.status = res.status;
      throw pausedErr;
    }
    if (res.status === 401) {
      clearToken();
      window.dispatchEvent(new Event("meetai:auth-expired"));
      throw new Error("Session expired. Please sign in again.");
    }
    if (res.status === 429) {
      const err = new Error(data.error || "Too many requests. Please wait a moment.");
      err.code = "RATE_LIMIT";
      err.status = 429;
      throw err;
    }
    // Only treat gateway/outage as "app down" — not every 500 (e.g. bad calendar id).
    if (res.status === 503) {
      const err = new Error("MeetAI is temporarily unavailable. Please retry.");
      err.code = "SERVER_DOWN";
      err.status = res.status;
      window.dispatchEvent(new CustomEvent("meetai:api-down", { detail: { path, message: err.message, status: res.status } }));
      throw err;
    }
    if (res.status >= 500) {
      const err = new Error(data.error || "Something went wrong. Please try again.");
      err.code = "SERVER_ERROR";
      err.status = res.status;
      if (data.failureKind) err.failureKind = data.failureKind;
      if (data.failureStage) err.failureStage = data.failureStage;
      throw err;
    }
    const clientErr = new Error(data.error || "Request failed");
    if (data.failureKind) clientErr.failureKind = data.failureKind;
    if (data.failureStage) clientErr.failureStage = data.failureStage;
    throw clientErr;
  }
  window.dispatchEvent(new Event("meetai:api-up"));
  const etag = res.headers.get("ETag") || "";
  if (etag) {
    if (Array.isArray(data)) data.__etag = etag;
    else if (data && typeof data === "object") data.__etag = etag;
  }
  return data;
}

/**
 * POST that consumes a server-sent event stream.
 *
 * Calls onEvent(name, payload) for each event as it arrives. Throws if the
 * connection cannot be opened at all, so callers can fall back to apiFetch.
 * Pass a signal to cancel in flight.
 */
async function apiFetchStream(path, { body, onEvent, signal } = {}) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearToken();
    window.dispatchEvent(new Event("meetai:auth-expired"));
    throw new Error("Session expired. Please sign in again.");
  }
  if (!res.ok) {
    let msg = "Request failed";
    try {
      const j = JSON.parse(await res.text());
      msg = j?.error || msg;
    } catch (_) {}
    throw new Error(msg);
  }
  if (!res.body || typeof res.body.getReader !== "function") {
    throw new Error("Streaming not supported");
  }

  window.dispatchEvent(new Event("meetai:api-up"));

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatchFrame = (frame) => {
    let name = "message";
    const dataLines = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith(":")) continue; // heartbeat
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) return;
    try {
      onEvent?.(name, JSON.parse(dataLines.join("\n")));
    } catch (_) {
      /* ignore malformed frame rather than killing the stream */
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (frame.trim()) dispatchFrame(frame);
    }
  }
  if (buffer.trim()) dispatchFrame(buffer);
}

/** Meetings list poll with ETag — returns null when unchanged (304). */
async function apiFetchMeetingsList(etag) {
  const headers = {};
  if (etag) headers["If-None-Match"] = etag;
  const data = await apiFetch(MEETINGS_LIST_API, { headers });
  if (data?.__notModified) return { notModified: true, etag: data.etag || etag || "" };
  const nextEtag = data?.__etag || "";
  const list = Array.isArray(data) ? data.slice() : [];
  return { notModified: false, etag: nextEtag, list };
}

function sanitizeDownloadFilename(s) {
  return String(s || 'meeting')
    .trim()
    .replace(/[^\w\s.-]+/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 90)
    .trim() || 'meeting';
}

async function downloadTranscriptExport({ meetingId, format, meetingTitle, includeActionItems = true }) {
  const token = getToken();
  if (!token) throw new Error('Please sign in again.');
  const fmt = String(format || '').toLowerCase();
  const res = await fetch(`${API}/meetings/${encodeURIComponent(meetingId)}/transcript/export`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ format: fmt, includeActionItems: includeActionItems === true }),
  });
  if (!res.ok) {
    let msg = "Export failed";
    try {
      const data = await res.json();
      msg = data?.error || msg;
    } catch (_) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const ext = fmt === "text" ? "txt" : fmt;
  const fallbackName = `${sanitizeDownloadFilename(meetingTitle)}.${ext}`;
  const dispo = res.headers.get("content-disposition") || "";
  const m = dispo.match(/filename=\"?([^\";]+)\"?/i);
  const name = m?.[1] ? m[1] : fallbackName;

  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}

/* ════════════════════════════════════════════════════════════════
   ACCESS PAUSED CARD — shown when payroll_company is not allowed
   ════════════════════════════════════════════════════════════════ */
const APP_PAUSED_MESSAGE =
  "Sorry for the inconvenience! Avaada MeetAI is temporarily unavailable. Thank you for your patience.";

function isPausedMessage(text) {
  const t = String(text || "").toLowerCase();
  return t.includes("temporarily unavailable") || t.includes("temporarily paused");
}

function AccessPausedCard({ message, onSignOut, compact = false, flush = false, style }) {
  const msg = String(message || APP_PAUSED_MESSAGE);
  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        width: "100%",
        maxWidth: flush ? "none" : compact ? 520 : 560,
        margin: flush ? 0 : compact ? "0 auto" : "18px auto 0",
        boxSizing: "border-box",
        background: "#FFFBEB",
        border: "1px solid #FCD34D",
        borderLeft: "5px solid #F59E0B",
        borderRadius: 14,
        padding: "16px 18px",
        boxShadow: "0 8px 24px rgba(245,158,11,0.12)",
        fontFamily: "'DM Sans',system-ui,sans-serif",
        textAlign: "left",
        ...style,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <span
          aria-hidden
          style={{
            flex: "0 0 auto",
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#FEF3C7",
            border: "1px solid #FCD34D",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Svg d={IC.clock} size={19} color="#B45309" stroke={2} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14.5, color: "#92400E", marginBottom: 4 }}>
            Avaada MeetAI is temporarily unavailable
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "#78350F", overflowWrap: "anywhere" }}>{msg}</div>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              style={{
                marginTop: 12,
                padding: "8px 16px",
                borderRadius: 10,
                border: "1px solid #E5E7EB",
                background: "#fff",
                color: "#374151",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* Paused-access popup — same brand look as the login page (illustration + logo). */
function PausedAccessModal({ open, message, onClose, onBackToSignIn }) {
  const [closeHover, setCloseHover] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  useEffect(() => {
    if (!open) setCloseHover(false);
  }, [open ]);
  if (!open) return null;
  const msg = String(message || APP_PAUSED_MESSAGE);
  // Portal to body: the login card keeps a transform from its entry animation,
  // which would trap position:fixed inside it and let branding text paint over us.
  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483646,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(12px) saturate(1.05)",
        WebkitBackdropFilter: "blur(12px) saturate(1.05)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="paused-popup-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 430,
          background: "#FFFBEB",
          border: "1px solid #FCD34D",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(120, 70, 0, 0.35)",
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 14px 10px" }}>
          <span
            style={{
              flex: "0 0 auto",
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: "0.06em",
              padding: "4px 10px",
              borderRadius: 999,
              background: "#FEF3C7",
              border: "1px solid #FCD34D",
              color: "#92400E",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <Svg d={IC.clock} size={11} color="#B45309" stroke={2.2} />
            PAUSED
          </span>
          <span
            aria-hidden={false}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "inline-flex",
              pointerEvents: "none",
            }}
          >
            <img src={APP_LOGO} alt="Avaada" style={{ height: 26, display: "block" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            onFocus={() => setCloseHover(true)}
            onBlur={() => setCloseHover(false)}
            style={{
              flex: "0 0 auto",
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: `1px solid ${closeHover ? "#FCA5A5" : "#FCD34D"}`,
              background: closeHover ? "#DC2626" : "rgba(255,255,255,0.95)",
              color: closeHover ? "#ffffff" : "#92400E",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              transition: "background 0.15s, color 0.15s, border-color 0.15s",
            }}
          >
            <Svg d={IC.close} size={14} color="currentColor" stroke={2.4} />
          </button>
        </div>
        <div style={{ position: "relative", height: 132, margin: "0 14px", borderRadius: 14, overflow: "hidden" }}>
          <img
            src={LOGIN_RENEWABLE_SKYLINE}
            alt=""
            aria-hidden
            decoding="async"
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 82%", display: "block" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
        <div style={{ padding: "14px 24px 24px", textAlign: "center" }}>
          <div id="paused-popup-title" style={{ fontSize: 18, fontWeight: 800, color: "#92400E", lineHeight: 1.35 }}>
            Avaada MeetAI is temporarily unavailable
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#78350F", marginTop: 8, overflowWrap: "anywhere" }}>
            {msg}
          </div>
          <button
            type="button"
            onClick={onBackToSignIn || onClose}
            style={{
              marginTop: 18,
              width: "100%",
              padding: "11px 18px",
              borderRadius: 12,
              border: "none",
              background: "#B45309",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Back to sign-in
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
   LOGIN PAGE — light green brand panel + white SSO panel
   ════════════════════════════════════════════════════════════════ */
const LOGIN_ACCENT_EMERALD = "#05B887";

function LoginPage({ adminEntry, returnPath = "" } = {}) {
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const streamFull = adminEntry ? "authorised accounts only." : "built for Avaada teams.";
  const [streamText, setStreamText] = useState("");

  useEffect(() => {
    setTimeout(() => setMounted(true), 50);
  }, []);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) {
      setStreamText(streamFull);
      return undefined;
    }
    setStreamText("");
    let i = 0;
    let phase = "typing";
    let pauseLeft = 0;
    const tick = setInterval(() => {
      if (phase === "pause") {
        pauseLeft -= 1;
        if (pauseLeft <= 0) phase = "clear";
        return;
      }
      if (phase === "clear") {
        setStreamText("");
        i = 0;
        phase = "typing";
        return;
      }
      i += 1;
      setStreamText(streamFull.slice(0, i));
      if (i >= streamFull.length) {
        phase = "pause";
        pauseLeft = 36;
      }
    }, 42);
    return () => clearInterval(tick);
  }, [streamFull]);

  function handleGoogleLogin() {
    setLoading(true);
    const base = backendOriginForOAuth();
    // Prefer explicit returnPath; otherwise keep current deep link (e.g. /meetings?m=<id> from email).
    const fromUrl =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "";
    const deepFromUrl = fromUrl && /[?&]m=/.test(fromUrl) ? fromUrl : "";
    const nextTarget = adminEntry ? "/admin" : String(returnPath || deepFromUrl || "").trim();
    const next = nextTarget ? `?next=${encodeURIComponent(nextTarget)}` : "";
    // replace (not assign) so the login page is not left behind Google consent in history.
    window.location.replace(`${base}/api/auth/google${next}`);
  }

  useEffect(() => {
    const raw = window.location.hash?.replace(/^#/, "") || "";
    if (!raw.includes("oauth_error=")) return;
    const sp = new URLSearchParams(raw);
    const err = sp.get("oauth_error");
    if (err) setError(decodeURIComponent(err));
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  /** Prefer fit; allow vertical scroll on short / mobile viewports. */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflowX: html.style.overflowX,
      bodyOverflowX: body.style.overflowX,
    };
    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";
    return () => {
      html.style.overflowX = prev.htmlOverflowX;
      body.style.overflowX = prev.bodyOverflowX;
    };
  }, []);

  const iconProps = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  return (
    <div className="lp-stage login-page" data-mounted={mounted ? "1" : "0"}>
      <div className="lp-card login-card">
        <aside className="lp-left branding-panel brand-panel">
          <img
            className="renewable-energy-illustration"
            src={LOGIN_RENEWABLE_SKYLINE}
            alt="Renewable energy landscape"
            decoding="async"
            draggable={false}
          />
          <div className="lp-left-fade" aria-hidden />
          <div className="branding-content brand-content">
            <div className="lp-brand-section">
              <div className="lp-brand-logo-wrap">
                <div className="lp-brand-logo">
                  <img src={APP_LOGO} alt="Avaada" />
                </div>
              </div>

              <h1 className="lp-title">
                {adminEntry ? "Admin access," : "Meeting intelligence,"}
                <br />
                <span className="lp-title-accent">
                  <span className="lp-title-stream" aria-label={streamFull}>
                    {streamText}
                  </span>
                  <span className="lp-title-caret" aria-hidden />
                </span>
              </h1>
              <p className="lp-tagline">
                {adminEntry
                  ? "Sign in with Google using an email on the admin allowlist. You will return to /admin after authentication."
                  : "MeetAI auto-joins, transcribes, and summarises every Google Meet call — delivering decisions and action items directly to attendees."}
              </p>

              <div className="lp-features">
                <div className="lp-feature">
                  <span className="lp-feature-icon" aria-hidden>
                    <svg {...iconProps}>
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                  <span className="lp-feature-copy">
                    <span className="lp-feature-text">Automatic calendar integration</span>
                    <span className="lp-feature-sub">Seamlessly connects to your Google Calendar.</span>
                  </span>
                </div>
                <div className="lp-feature">
                  <span className="lp-feature-icon" aria-hidden>
                    <svg {...iconProps}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </span>
                  <span className="lp-feature-copy">
                    <span className="lp-feature-text">Speaker-attributed transcripts &amp; AI-generated minutes</span>
                    <span className="lp-feature-sub">Accurate notes with clear speaker identification.</span>
                  </span>
                </div>
                <div className="lp-feature">
                  <span className="lp-feature-icon" aria-hidden>
                    <svg {...iconProps}>
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                    </svg>
                  </span>
                  <span className="lp-feature-copy">
                    <span className="lp-feature-text">Action items emailed instantly to every attendee</span>
                    <span className="lp-feature-sub">Keep your teams aligned and productive.</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="lp-compliance">
              <div className="lp-compliance-label">Security and compliance</div>
              <div className="lp-compliance-grid">
                <div className="lp-comply">
                  <span className="lp-comply-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </span>
                  <span className="lp-comply-text">Avaada Google sign-in</span>
                </div>
                <div className="lp-comply">
                  <span className="lp-comply-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <span className="lp-comply-text">Encrypted connection</span>
                </div>
                <div className="lp-comply">
                  <span className="lp-comply-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                    </svg>
                  </span>
                  <span className="lp-comply-text">All data encrypted</span>
                </div>
                <div className="lp-comply">
                  <span className="lp-comply-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="8" rx="2" />
                      <rect x="2" y="14" width="20" height="8" rx="2" />
                      <line x1="6" y1="6" x2="6.01" y2="6" />
                      <line x1="6" y1="18" x2="6.01" y2="18" />
                    </svg>
                  </span>
                  <span className="lp-comply-text">Hosted on Avaada server</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="lp-right signin-panel">
          <div className="signin-content">
            <div className="lp-eyebrow">{adminEntry ? "Admin portal" : "Sign in"}</div>
            <h2 className="lp-welcome">{adminEntry ? "Admin sign-in" : "Sign in to MeetAI"}</h2>
            <p className="lp-welcome-sub">
              {adminEntry
                ? "Use the Google account your organisation added to the admin allowlist."
                : "Use your Avaada account. Authorised users only."}
            </p>

            <button
              type="button"
              className="lp-google"
              onClick={handleGoogleLogin}
              disabled={loading}
              aria-label="Continue with Avaada account"
            >
              {loading ? (
                <span className="lp-spinner" aria-hidden />
              ) : (
                <span className="lp-google-g" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                </span>
              )}
              <span className="lp-google-label">{loading ? "Signing in…" : "Continue with Avaada account"}</span>
              {!loading ? (
                <span className="lp-google-arrow" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </span>
              ) : null}
            </button>

            <div className="lp-sso">Secure SSO</div>

            <div className="lp-sec-note">
              <div className="lp-sec-icon-wrap" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={LOGIN_ACCENT_EMERALD} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="lp-sec-note-text">
                <strong>Secure access.</strong> Sign-in is handled by Google. Your password is never shared with Avaada MeetAI.
              </div>
            </div>

            {error && !isPausedMessage(error) ? (
              <div className="lp-error" role="alert">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            ) : null}
            <PausedAccessModal
              open={isPausedMessage(error)}
              message={APP_PAUSED_MESSAGE}
              onClose={() => { setError(""); setLoading(false); }}
              onBackToSignIn={() => { setError(""); setLoading(false); }}
            />

            <div className="lp-helper">
              {adminEntry ? (
                <>
                  <a href="/">← Main MeetAI sign-in</a>
                  <span style={{ margin: "0 8px", opacity: 0.4 }}>·</span>
                  <a href="mailto:ai.team@avaada.com">AI Team</a>
                </>
              ) : (
                <>
                  Need help? Reach out to the <a href="mailto:ai.team@avaada.com">AI Team</a>.
                </>
              )}
            </div>

            <div className="lp-rightfoot">
              <span className="lp-rightfoot-dot" aria-hidden />
              <span>Avaada Group</span>
              <span className="lp-rightfoot-sep" aria-hidden />
              <span>AI Team</span>
              <span className="lp-rightfoot-sep" aria-hidden />
              <span>Internal use</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/** In-app sign-out confirmation (not browser `confirm`). */
function SignOutConfirmModal({ open, onCancel, onConfirm, dark }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const cardBg = dark ? "#112236" : "#ffffff";
  const cardBorder = dark ? "1px solid #1E3448" : "1px solid #e2e8f0";
  const titleC = dark ? "#F1F5F9" : "#0f172a";
  const subC = dark ? "rgba(255,255,255,0.6)" : "#64748B";
  const cancelBg = dark ? "transparent" : "#ffffff";
  const cancelBorder = dark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #e2e8f0";
  const cancelColor = dark ? "rgba(255,255,255,0.75)" : "#475569";
  const dangerBg = "#DC2626";
  const dangerHover = "#B91C1C";

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        /* Neutral dimmer — avoids blue cast over the page */
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="signout-dialog-title"
        style={{
          width: "100%",
          maxWidth: 400,
          background: cardBg,
          border: cardBorder,
          borderRadius: 16,
          boxShadow: dark ? "0 20px 50px rgba(0,0,0,0.45)" : "0 20px 50px rgba(0,0,0,0.12)",
          padding: "26px 24px 20px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div
            aria-hidden
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              background: dark ? "rgba(220,38,38,0.16)" : "#FEF2F2",
              border: dark ? "1px solid rgba(220,38,38,0.30)" : "1px solid #FECACA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Svg d={IC.logout} size={20} color="#DC2626" stroke={1.9} />
          </div>
          <p id="signout-dialog-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: titleC, lineHeight: 1.4, letterSpacing: "-0.02em" }}>
            Are you sure you want to logout?
          </p>
        </div>
        <p style={{ margin: "0 0 0 52px", fontSize: 13, color: subC, lineHeight: 1.5 }}>
          You will be signed out of MeetAI and need to sign in again to continue.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "10px 18px",
              borderRadius: 10,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
              background: cancelBg,
              border: cancelBorder,
              color: cancelColor,
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: "10px 18px",
              borderRadius: 10,
              fontSize: 13.5,
              fontWeight: 650,
              cursor: "pointer",
              background: dangerBg,
              border: "none",
              color: "#fff",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              transition: "background .15s",
              boxShadow: "0 1px 2px rgba(220,38,38,0.30)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = dangerHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = dangerBg;
            }}
          >
            <Svg d={IC.logout} size={15} color="#fff" stroke={2} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

/** After HTTP 401 — user acknowledges, then continues on the sign-in screen. */
function SessionExpiredModal({ open, onOk }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === "Escape") onOk();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOk]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
      onClick={onOk}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
          padding: "22px 22px 18px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <p id="session-expired-title" style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#0f172a", lineHeight: 1.45 }}>
          Session expired
        </p>
        <p style={{ margin: "12px 0 0", fontSize: 13.5, color: "#475569", lineHeight: 1.55 }}>
          Your session is no longer valid. Please sign in again to continue.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 22 }}>
          <button
            type="button"
            onClick={onOk}
            style={{
              padding: "9px 22px",
              borderRadius: 9,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
              background: PALETTE.navy,
              border: "none",
              color: "#fff",
              fontFamily: "inherit",
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function UserInfoModal({
  open,
  mode,
  user,
  directoryDesignation = "",
  meetings,
  myActions,
  calendarWarning,
  preferences,
  onSaveProfile,
  onSavePreferences,
  onClose,
}) {
  const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [isEditing, setIsEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.name || "");
  const [timezoneDraft, setTimezoneDraft] = useState(preferences?.timezone || defaultTimezone);
  const [remindersEnabled, setRemindersEnabled] = useState(preferences?.remindersEnabled ?? true);
  const [reminderMinutes, setReminderMinutes] = useState(preferences?.reminderMinutes ?? 10);
  const [uiLanguage, setUiLanguage] = useState(preferences?.uiLanguage || "English");
  const [browserPerm, setBrowserPerm] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"));
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setIsEditing(false);
    setNameDraft(user?.name || "");
    setTimezoneDraft(preferences?.timezone || defaultTimezone);
    setRemindersEnabled(preferences?.remindersEnabled ?? true);
    setReminderMinutes(preferences?.reminderMinutes ?? 10);
    setUiLanguage(preferences?.uiLanguage || "English");
    setBrowserPerm(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  }, [open, user?.name, preferences, defaultTimezone]);

  // A reminder is only really on when the user wants it *and* the browser allows
  // it; either half missing means no notification will ever arrive.
  const remindersActive = remindersEnabled && browserPerm === "granted";
  const [savedNotice, setSavedNotice] = useState("");

  useEffect(() => {
    if (!savedNotice) return undefined;
    const timer = setTimeout(() => setSavedNotice(""), 3000);
    return () => clearTimeout(timer);
  }, [savedNotice]);

  const handleEnableBrowserPush = async () => {
    setPushBusy(true);
    try {
      const res = await enableMeetingReminderPush(apiFetch);
      const perm = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
      setBrowserPerm(perm);
      if (res?.ok) {
        window.alert("Notifications allowed successfully on this device!");
      } else if (res?.reason === "not_configured") {
        window.alert("Meeting reminders are not set up on this server yet. Please contact your administrator.");
      } else if (res?.reason === "unsupported") {
        window.alert("This browser does not support push notifications.");
      } else if (perm === "denied") {
        window.alert("Browser notifications are blocked. To allow: click the lock/settings icon next to the URL in your browser address bar → set Notifications to Allow → refresh the page.");
      }
    } catch (err) {
      window.alert("Notification permission error: " + (err?.message || err));
    } finally {
      setPushBusy(false);
    }
  };

  if (!open) return null;

  const safeMeetings = Array.isArray(meetings) ? meetings : [];
  const safeActions = Array.isArray(myActions) ? myActions : [];
  const upcomingCount = safeMeetings.filter((m) => effectiveMeetingStatus(m) === "upcoming").length;
  const completedCount = safeMeetings.filter((m) => {
    const st = effectiveMeetingStatus(m);
    return st === "completed" || st === "ended";
  }).length;
  const pendingActions = safeActions.filter((a) => String(a.status || "").toLowerCase() === "pending").length;
  const timezoneLabel = preferences?.timezone || defaultTimezone;
  const workspaceEmailDomain = String(user?.email || "").split("@")[1] || "not set";
  const isSettings = mode === "settings";

  // Check if any settings or profile fields have been modified compared to saved props
  const origName = user?.name || "";
  const origTimezone = preferences?.timezone || defaultTimezone;
  const origRemindersEnabled = preferences?.remindersEnabled ?? true;
  const origReminderMinutes = Number(preferences?.reminderMinutes ?? 10);
  const origLanguage = preferences?.uiLanguage || "English";

  const isSettingsDirty = isSettings && (
    remindersEnabled !== origRemindersEnabled ||
    Number(reminderMinutes) !== origReminderMinutes ||
    uiLanguage !== origLanguage
  );

  const isProfileDirty = !isSettings && (
    (nameDraft || "") !== origName ||
    (timezoneDraft || defaultTimezone) !== origTimezone
  );

  const hasUnsavedChanges = isSettings ? isSettingsDirty : (isEditing && isProfileDirty);

  const modalLang = isSettings ? uiLanguage : (isEditing ? uiLanguage : (preferences?.uiLanguage || "English"));
  const t = (k, fb) => getTranslation(modalLang, k, fb);
  const title = isSettings ? t("settings", "Settings") : t("profile", "Profile");

  const profileItems = [
    { label: "Full name", value: user?.name || "Not provided" },
    { label: "Email", value: user?.email || "Not available" },
    { label: "Role", value: userHeaderSubtitle(user, directoryDesignation) || "Meeting participant" },
    { label: "Workspace", value: workspaceEmailDomain },
    { label: "Timezone", value: timezoneLabel },
  ];

  const handleSave = () => {
    const trimmedName = String(nameDraft || "").trim();
    onSaveProfile({
      name: trimmedName || user?.name || "User",
      timezone: String(timezoneDraft || defaultTimezone).trim() || defaultTimezone,
    });
    onSavePreferences({
      timezone: String(timezoneDraft || defaultTimezone).trim() || defaultTimezone,
      remindersEnabled,
      reminderMinutes: Number(reminderMinutes) || 10,
      uiLanguage: String(uiLanguage || "English"),
    });
    setIsEditing(false);
    setSavedNotice(t("settingsSaved", "Settings updated"));
  };

  const handleCancelChanges = () => {
    setIsEditing(false);
    setNameDraft(user?.name || "");
    setTimezoneDraft(preferences?.timezone || defaultTimezone);
    setRemindersEnabled(preferences?.remindersEnabled ?? true);
    setReminderMinutes(preferences?.reminderMinutes ?? 10);
    setUiLanguage(preferences?.uiLanguage || "English");
  };

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0, 0, 0, 0.46)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{title}</p>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#64748b" }}>
              {isSettings ? t("manageSettingsSubtitle", "Manage your meeting preferences and defaults") : "Your account and workspace information"}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!isSettings && (
              <button
                type="button"
                onClick={() => setIsEditing((v) => !v)}
                style={{
                  border: "1px solid #cbd5e1",
                  background: isEditing ? "#eff6ff" : "#ffffff",
                  color: "#1e3a8a",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontWeight: 600,
                  padding: "7px 10px",
                }}
              >
                {isEditing ? t("viewing", "Viewing") : t("edit", "Edit")}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                border: "none",
                background: "transparent",
                color: "#64748b",
                cursor: "pointer",
                padding: "6px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width={18} height={18} fill="none" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        {!isSettings && (
          <div style={{ padding: "16px 20px 0", display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10 }}>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: "#f8fafc" }}>
              <p style={{ margin: 0, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".4px" }}>Upcoming</p>
              <p style={{ margin: "5px 0 0", fontSize: 17, fontWeight: 700, color: "#1e3a8a" }}>{upcomingCount}</p>
            </div>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: "#f8fafc" }}>
              <p style={{ margin: 0, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".4px" }}>Completed</p>
              <p style={{ margin: "5px 0 0", fontSize: 17, fontWeight: 700, color: "#1e3a8a" }}>{completedCount}</p>
            </div>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: "#f8fafc" }}>
              <p style={{ margin: 0, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".4px" }}>Pending actions</p>
              <p style={{ margin: "5px 0 0", fontSize: 17, fontWeight: 700, color: "#1e3a8a" }}>{pendingActions}</p>
            </div>
          </div>
        )}

        <div style={{ padding: "16px 20px 20px" }}>
          {!isSettings && (
            <>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Full name</p>
                {isEditing ? (
                  <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} className="input" style={{ padding: "8px 10px", fontSize: 13.5 }} />
                ) : (
                  <p style={{ margin: 0, fontSize: 13.5, color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{profileItems[0].value}</p>
                )}
              </div>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Email</p>
                <p style={{ margin: 0, fontSize: 13.5, color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{profileItems[1].value}</p>
              </div>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Role</p>
                <p style={{ margin: 0, fontSize: 13.5, color: "#0f172a", fontWeight: 600 }}>{profileItems[2].value}</p>
              </div>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Workspace</p>
                <p style={{ margin: 0, fontSize: 13.5, color: "#0f172a", fontWeight: 600 }}>{profileItems[3].value}</p>
              </div>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Timezone</p>
                {isEditing ? (
                  <input value={timezoneDraft} onChange={(e) => setTimezoneDraft(e.target.value)} className="input" style={{ padding: "8px 10px", fontSize: 13.5 }} placeholder="Asia/Kolkata" />
                ) : (
                  <p style={{ margin: 0, fontSize: 13.5, color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{profileItems[4].value}</p>
                )}
              </div>
            </>
          )}

          {isSettings && (
            <>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#64748b", fontWeight: 500 }}>{t("calendarSync", "Calendar sync")}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: calendarWarning ? "#f59e0b" : "#10b981", display: "inline-block" }} />
                  <p style={{ margin: 0, fontSize: 13.5, color: "#0f172a", fontWeight: 600 }}>
                    {calendarWarning ? t("calendarNeedsAttention", "Needs attention") : t("calendarConnected", "Connected")}
                  </p>
                </div>
              </div>
              <div className="settings-row" style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 10, alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#64748b", fontWeight: 500 }}>{t("language", "Language")}</p>
                <select
                  className="input"
                  value={uiLanguage}
                  onChange={(e) => setUiLanguage(e.target.value)}
                  style={{ width: 220, padding: "7px 10px", fontSize: 13, borderRadius: 6, fontWeight: 500 }}
                >
                  <option value="English">English (Default)</option>
                  <option value="Hindi">Hindi (हिंदी)</option>
                  <option value="Marathi">Marathi (मराठी)</option>
                  <option value="Gujarati">Gujarati (ગુજરાતી)</option>
                  <option value="Telugu">Telugu (తెలుగు)</option>
                  <option value="Tamil">Tamil (தமிழ்)</option>
                  <option value="Kannada">Kannada (ಕನ್ನಡ)</option>
                  <option value="Bengali">Bengali (বাংলা)</option>
                </select>
              </div>
            </>
          )}

          {isSettings && calendarWarning && (
            <div style={{ marginTop: 12, border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", borderRadius: 10, padding: "10px 12px", fontSize: 12.5 }}>
              Calendar warning: {calendarWarning}
            </div>
          )}

          {/* Lives outside the button row: saving clears hasUnsavedChanges, which
              unmounts that row along with anything nested inside it. */}
          {savedNotice && !hasUnsavedChanges && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 7,
                fontSize: 12.5,
                fontWeight: 600,
                color: "#047857",
              }}
            >
              <span aria-hidden="true">✓</span>
              <span role="status">{savedNotice}</span>
            </div>
          )}

          {hasUnsavedChanges && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={handleCancelChanges}
                style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {t("cancel", "Cancel")}
              </button>
              <button
                type="button"
                onClick={handleSave}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#1d4ed8", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 6px rgba(29, 78, 216, 0.3)" }}
              >
                {t("saveChanges", "Save changes")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function useMeetAiUsage(enabled, refreshKey = 0) {
  const [usage, setUsage] = useState(() => meetAiUsageSessionCache.usage);
  const [error, setError] = useState(() => meetAiUsageSessionCache.error);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    if (meetAiUsageSessionCache.usage != null) {
      setUsage(meetAiUsageSessionCache.usage);
      setError(meetAiUsageSessionCache.error);
    }
    setFetching(true);
    apiFetch("/meetings/usage")
      .then((d) => {
        const local = d?.local && typeof d.local === "object" ? d.local : null;
        meetAiUsageSessionCache.usage = local;
        meetAiUsageSessionCache.error = null;
        meetAiUsageSessionCache.refreshKey = refreshKey;
        if (!cancelled) {
          setUsage(local);
          setError(null);
        }
      })
      .catch((e) => {
        const msg = userFacingMessage(e?.message || "Could not load usage");
        meetAiUsageSessionCache.error = msg;
        if (!cancelled) {
          setError(msg);
          if (!meetAiUsageSessionCache.usage) setUsage(null);
        }
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshKey]);

  /** Only block UI when there is nothing cached to show yet. */
  const loading = enabled && fetching && !usage && !error;
  return { usage, error, loading, fetching };
}

const ADMIN_USERS_DIRECTORY_NOTE =
  "People who signed in to Avaada MeetAI. Counts are MeetAI dispatch sessions only — not plain calendar events.";

function useAdminUserMeetingUsage(userId, enabled) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !userId) {
      setUsage(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUsage(null);
    apiFetch(`/admin/users/${encodeURIComponent(String(userId))}/meeting-usage`)
      .then((d) => {
        if (!cancelled) setUsage(d?.local && typeof d.local === "object" ? d.local : null);
      })
      .catch((e) => {
        if (!cancelled) {
          setUsage(null);
          setError(userFacingMessage(e?.message || "Could not load meeting usage"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, enabled]);

  return { usage, loading, error };
}

/** Four meeting-usage stat chips in a 2×2 grid (admin user drawer). */
function AdminUserMeetingUsageGrid({
  usage,
  loading,
  error,
  isLight,
  adminBorder,
  adminTextMain,
  adminTextMuted,
}) {
  const cells = usage
    ? [
        { label: "Recorded meetings", val: usage.meetings, sub: "Sessions with a recorder" },
        { label: "Usage hours", val: usage.usageHours, sub: "From meeting duration" },
        { label: "Transcript lines", val: usage.transcriptRows, sub: "Captured lines" },
        { label: "Action items", val: usage.actionItems, sub: "Generated tasks" },
      ]
    : [];
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: adminTextMuted, marginBottom: 8 }}>
        Meeting usage
      </div>
      {error ? (
        <div style={{ fontSize: 12, color: "#B45309", background: isLight ? "#FFFBEB" : "rgba(245,158,11,0.12)", border: `1px solid ${isLight ? "#FDE68A" : "rgba(245,158,11,0.35)"}`, borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
          {error}
        </div>
      ) : null}
      {loading ? (
        <div style={{ fontSize: 12.5, color: adminTextMuted, padding: "8px 0" }}>Loading meeting usage…</div>
      ) : null}
      {usage ? (
        <div className="adm-user-meeting-usage-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {cells.map((cell) => (
            <div
              key={cell.label}
              style={{
                background: isLight ? "#F8FBFF" : "rgba(255,255,255,0.04)",
                border: `1px solid ${adminBorder}`,
                borderRadius: 10,
                padding: "12px 12px",
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: adminTextMain, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                {typeof cell.val === "number" ? cell.val.toLocaleString("en-IN") : cell.val}
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: adminTextMain, marginTop: 6 }}>{cell.label}</div>
              <div style={{ fontSize: 10.5, color: adminTextMuted, marginTop: 2, lineHeight: 1.35 }}>{cell.sub}</div>
            </div>
          ))}
        </div>
      ) : null}
      {!loading && !error && !usage ? (
        <div style={{ fontSize: 12, color: adminTextMuted }}>No recorded MeetAI sessions for this user yet.</div>
      ) : null}
    </div>
  );
}

function MeetAiUsageContent({ usage, error, loading, embedded = false }) {
  return (
    <>
      {!embedded ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(37,99,235,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Svg d={IC.task} color="#2563EB" size={18} />
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>MeetAI usage</div>
            <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 1 }}>
              Recorded meetings you own or participate in
            </div>
          </div>
        </div>
      ) : null}
      {error && (
        <div style={{ fontSize: 12, color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px" }}>
          {error}
        </div>
      )}
      {loading && !usage ? <div style={{ fontSize: 12.5, color: T.textMuted }}>Loading usage…</div> : null}
      {usage ? (
        <div className="stat-grid-4 dashboard-usage-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
          <style>{`
            .usage-stat-cell{transition:border-color .14s ease,box-shadow .14s ease,transform .14s ease;}
            .usage-stat-cell:hover{border-color:#93B8F0!important;box-shadow:0 4px 14px rgba(37,99,235,0.12);transform:translateY(-1px);}
          `}</style>
          {[
            { label: "Recorded meetings", val: usage.meetings, sub: "Sessions with a recorder", tip: "Meetings where a bot or recorder captured the call, all-time.", icon: IC.video, icoClass: "usage-stat-ico--meetings", color: "#0875E1" },
            { label: "Usage hours", val: usage.usageHours, sub: "From meeting duration", tip: "Total hours across all recorded meetings, based on each meeting's start/end time.", icon: IC.clock, icoClass: "usage-stat-ico--hours", color: "#05B887" },
            { label: "Transcript lines", val: usage.transcriptRows, sub: "Captured lines", tip: "Total transcript lines captured across all recorded meetings.", icon: IC.actionList, icoClass: "usage-stat-ico--transcript", color: "#7C3AED" },
            { label: "Action items", val: usage.actionItems, sub: "Generated tasks", tip: "Total action items generated from meeting summaries, all-time.", icon: IC.listChecks, icoClass: "usage-stat-ico--actions", color: "#EA580C" },
          ].map((cell) => (
            <div
              key={cell.label}
              className="usage-stat-cell"
              title={cell.tip}
              style={{
                background: embedded ? "#FFFFFF" : "#F8FBFF",
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: "12px 12px",
                minWidth: 0,
              }}
            >
              {embedded ? (
                <span className={`usage-stat-ico ${cell.icoClass}`} aria-hidden>
                  <Svg d={cell.icon} size={18} color={cell.color} stroke={2} />
                </span>
              ) : null}
              <div className="usage-stat-body">
                <div className="usage-stat-val" style={{ fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                  {cell.val}
                </div>
                <div className="usage-stat-label" style={{ fontSize: 11.5, fontWeight: 600, color: T.text, marginTop: 6 }}>{cell.label}</div>
                <div className="usage-stat-sub" style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2, lineHeight: 1.35 }}>{cell.sub}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

function MeetAiUsageModal({ open, onClose, refreshKey }) {
  const { usage, error, loading } = useMeetAiUsage(open, refreshKey);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0, 0, 0, 0.46)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Meeting usage"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 720,
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Meeting usage</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              color: "#64748b",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width={18} height={18} fill="none" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div style={{ padding: "16px 18px 18px" }}>
          <MeetAiUsageContent usage={usage} error={error} loading={loading} />
        </div>
      </div>
    </div>
  );
}

const PLATFORM_META = {
  zoom:  { label: "Zoom",     short: "Z",   color: "#2D8CFF", bg: "#E8F3FF" },
  teams: { label: "Teams",    short: "T",   color: "#5059C9", bg: "#EEEFFA" },
  meet:  { label: "Meet",     short: "G",   color: "#4B6075", bg: "#F3F6F9" },
  gcal:  { label: "Calendar", short: "Cal", color: "#0C2B4E", bg: "#EAF0F5" },
};
const STATUS_META = {
  live:      { label: "Live",      dot: "#05B887", bg: "#EAF8F0", fg: "#057A5A" },
  upcoming:  { label: "Upcoming",  dot: "#0875E1", bg: "#EAF3FF", fg: "#0875E1" },
  cancelled: { label: "Cancelled", dot: "#94A3B8", bg: "#F8FAFC", fg: "#64748B" },
  ended:     { label: "Ended",     dot: "#D98A16", bg: "#FFF8EB", fg: "#B45309" },
  completed: { label: "Completed", dot: "#5F7690", bg: "#F3F8FC", fg: "#5F7690" },
  instant:   { label: "Instant",   dot: "#7C3AED", bg: "#EDE9FE", fg: "#6D28D9" },
  /** Admin Session register only — in window / dispatched but MeetAI not in call yet. */
  bot_pending: { label: "MeetAI pending", dot: "#D97706", bg: "#FEF3C7", fg: "#B45309" },
};

/* ── helpers ── */
function pad(n){ return String(n).padStart(2,"0"); }
function daysInMonth(y,m){ return new Date(y,m+1,0).getDate(); }
function firstDayOfMonth(y,m){ return new Date(y,m,1).getDay(); }

/** Month grid is always 6 weeks × 7 days = 42 cells (includes prev/next month padding). */
const CALENDAR_MONTH_GRID_CELLS = 42;
/** User calendar month cell: 2 events then “+N more” (original UI). */
const CAL_MONTH_DAY_EVENT_PREVIEW = 2;
/** Admin calendar: keep month cells compact (same idea as Week: 3 + See more). */
const CAL_MONTH_DAY_EVENT_PREVIEW_ADMIN = 3;

function buildMonthCalendarCells(year, month) {
  const totalDays = daysInMonth(year, month);
  const startDay = firstDayOfMonth(year, month);
  const prevTotal = daysInMonth(year, month === 0 ? 11 : month - 1);
  const cells = [];
  for (let i = 0; i < startDay; i += 1) {
    cells.push({ day: prevTotal - startDay + 1 + i, cur: false, slot: "prev" });
  }
  for (let i = 1; i <= totalDays; i += 1) {
    cells.push({ day: i, cur: true, slot: "cur" });
  }
  let nextMoDay = 1;
  while (cells.length < CALENDAR_MONTH_GRID_CELLS) {
    cells.push({ day: nextMoDay++, cur: false, slot: "next" });
  }
  return cells;
}

/** Real YYYY-MM-DD for every visible cell (including gray padding days). */
function ymdForMonthCell(cell, year, month) {
  if (!cell) return null;
  if (cell.slot === "cur") {
    return `${year}-${pad(month + 1)}-${pad(cell.day)}`;
  }
  if (cell.slot === "prev") {
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    return `${py}-${pad(pm + 1)}-${pad(cell.day)}`;
  }
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  return `${ny}-${pad(nm + 1)}-${pad(cell.day)}`;
}

/** First/last dates on the 42-cell month grid (for API fetch + filters). */
function visibleMonthGridYmdRange(year, month) {
  const cells = buildMonthCalendarCells(year, month);
  const ymds = cells.map((c) => ymdForMonthCell(c, year, month)).filter(Boolean);
  ymds.sort();
  return { from: ymds[0] || "", to: ymds[ymds.length - 1] || "", cells };
}

function formatMonth(y,m){ return new Date(y,m,1).toLocaleString("en-IN",{month:"long",year:"numeric"}); }
function todayStr(){ const d=new Date(); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function defaultEventStartHm() {
  const d = new Date();
  const mins = d.getHours() * 60 + d.getMinutes();
  const next = Math.min(Math.ceil((mins + 15) / 30) * 30, 23 * 60 + 30);
  return `${pad(Math.floor(next / 60))}:${pad(next % 60)}`;
}
function addMinutesToHm(hm, delta) {
  const [h, m] = String(hm || "09:00").split(":").map(Number);
  const total = Math.max(0, Math.min(24 * 60 - 1, h * 60 + m + delta));
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}
function appDisplayTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}
function ymdToDate(ymd){
  if(!ymd || typeof ymd !== "string") return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if(!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m-1, d);
}
function dateToYmd(date){
  if(!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
}
/** Format a YYYY-MM-DD (or any parsable date) into the DD-MM-YYYY display style. */
function formatYmdDisplay(ymd){
  const raw = String(ymd || "").trim();
  if(!raw) return "";
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if(m) return `${m[3]}-${m[2]}-${m[1]}`;
  const dt = new Date(raw);
  if(dt instanceof Date && !Number.isNaN(dt.getTime())){
    return `${pad(dt.getDate())}-${pad(dt.getMonth()+1)}-${dt.getFullYear()}`;
  }
  return raw;
}

const DETAIL_HEADER_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatDetailHeaderDate(ymd) {
  const raw = String(ymd || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const month = DETAIL_HEADER_MONTHS[Number(m[2]) - 1];
    if (month) return `${m[3]} ${month} ${m[1]}`;
  }
  return formatYmdDisplay(ymd);
}

function addDaysYmd(ymd, deltaDays){
  const dt = ymdToDate(ymd);
  if(!dt) return ymd;
  dt.setDate(dt.getDate() + deltaDays);
  return dateToYmd(dt);
}

/** Local midnight for YYYY-MM-DD (for forward-window filters). */
function startOfLocalDayMs(ymd) {
  const raw = String(ymd || "").trim();
  const m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return 0;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return 0;
  return new Date(y, mo - 1, d, 0, 0, 0, 0).getTime();
}

/** Inclusive end of local calendar day for YYYY-MM-DD. */
function endOfLocalDayMs(ymd) {
  const s = startOfLocalDayMs(ymd);
  return s ? s + 24 * 60 * 60 * 1000 - 1 : 0;
}

function startOfWeekYmd(ymd){
  const dt = ymdToDate(ymd);
  if(!dt) return ymd;
  // Week starts on Sunday (matches our existing DNAMES order).
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - dow);
  return dateToYmd(dt);
}
function endOfWeekYmd(ymd){
  return addDaysYmd(startOfWeekYmd(ymd), 6);
}

/** Google Calendar API window for the current page + view (month grid clicks must not change this). */
function googleCalendarFetchRange(page, calView, calYear, calMonth, calSelDate) {
  if (page !== "calendar") {
    const tStr = todayStr();
    return { from: addDaysYmd(tStr, -30), to: addDaysYmd(tStr, 30) };
  }
  const curY = calYear;
  const curM = calMonth;
  const safeSel = calSelDate || todayStr();
  if (calView === "month") {
    return visibleMonthGridYmdRange(curY, curM);
  }
  if (calView === "year") {
    return { from: `${curY}-01-01`, to: `${curY}-12-31` };
  }
  if (calView === "day") {
    return { from: safeSel, to: safeSel };
  }
  if (calView === "week") {
    return { from: startOfWeekYmd(safeSel), to: endOfWeekYmd(safeSel) };
  }
  if (calView === "schedule") {
    return { from: safeSel, to: addDaysYmd(safeSel, 30) };
  }
  if (calView === "fourDays") {
    return { from: safeSel, to: addDaysYmd(safeSel, 3) };
  }
  const last = daysInMonth(curY, curM);
  return {
    from: `${curY}-${pad(curM + 1)}-01`,
    to: `${curY}-${pad(curM + 1)}-${pad(last)}`,
  };
}

/** Pull a start time from titles like "Daily standup | 11:30 AM" when time column is empty. */
function inferTimeFromMeetingTitle(title) {
  const t = String(title || "").trim();
  if (!t) return { h: null, min: null };
  const m =
    t.match(/\|\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i) ||
    t.match(/\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i) ||
    t.match(/\b(\d{1,2}):(\d{2})\s*(am|pm)\b/i);
  if (!m) return { h: null, min: null };
  let h = parseInt(m[1], 10);
  const min = m[2] != null ? parseInt(m[2], 10) : 0;
  const mer = m[3] ? String(m[3]).toLowerCase() : null;
  if (mer === "pm" && h < 12) h += 12;
  if (mer === "am" && h === 12) h = 0;
  return { h: Number.isFinite(h) ? h : null, min: Number.isFinite(min) ? min : null };
}

/** Sort / filter key: local date+time for a meeting row. */
function meetingDateTimeMs(m) {
  if (!m?.date || typeof m.date !== "string") return 0;
  const parts = m.date.split("-").map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return 0;
  const [y, mo, d] = parts;
  let { h, min } = parseMeetingTimeToHourMin(m?.time);
  if (h == null || min == null) {
    const fromTitle = inferTimeFromMeetingTitle(m?.title);
    h = fromTitle.h;
    min = fromTitle.min;
  }
  if (h == null || min == null) return 0;
  return new Date(y, mo - 1, d, h, min, 0, 0).getTime();
}

function sortMeetingsChronologically(list) {
  return [...(list || [])].sort((a, b) => {
    const da = meetingDateTimeMs(a);
    const db = meetingDateTimeMs(b);
    if (da !== db) return da - db;
    return String(a?.title || "").localeCompare(String(b?.title || ""));
  });
}

/** Admin lists: newest meetings first (matches user expectation on All Meetings). */
function sortMeetingsNewestFirst(list) {
  return [...(list || [])].sort((a, b) => {
    const da = meetingDateTimeMs(a);
    const db = meetingDateTimeMs(b);
    if (da !== db) return db - da;
    return String(b?.title || "").localeCompare(String(a?.title || ""));
  });
}

/** User All Meetings order: recent completed meetings first, then upcoming soonest first. */
function sortMeetingsRecentThenUpcoming(list, nowMs = Date.now()) {
  return [...(list || [])].sort((a, b) => {
    const da = meetingDateTimeMs(a);
    const db = meetingDateTimeMs(b);
    const aUpcoming = da >= nowMs;
    const bUpcoming = db >= nowMs;
    if (aUpcoming !== bUpcoming) return aUpcoming ? 1 : -1;
    if (aUpcoming) return da - db;
    return db - da;
  });
}

function indexMeetingsByDate(meetings) {
  const mByDate = {};
  (meetings || []).forEach((m) => {
    const d = m?.date;
    if (!d) return;
    if (!mByDate[d]) mByDate[d] = [];
    mByDate[d].push(m);
  });
  Object.keys(mByDate).forEach((d) => {
    mByDate[d] = sortMeetingsChronologically(mByDate[d]);
  });
  return mByDate;
}

/**
 * Parse times like:
 * - "10:15"
 * - "10:15am" / "10:15 pm"
 * - "4:30 - 5pm" (uses the first time segment)
 * - "5pm" (minutes default to 00)
 */
function parseMeetingTimeToHourMin(t) {
  const raw = String(t || "").trim();
  if (!raw || raw === "—" || raw === "-") return { h: null, min: null };

  // Extract first 2 time tokens to support "4:30 - 5pm" (pm only on second token).
  const tokens = [];
  const re = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/ig;
  let match;
  while ((match = re.exec(raw)) && tokens.length < 2) {
    const h = parseInt(match[1], 10);
    const min = match[2] != null ? parseInt(match[2], 10) : 0;
    const mer = match[3] ? String(match[3]).toLowerCase() : null;
    if (Number.isFinite(h) && Number.isFinite(min)) tokens.push({ h, min, mer });
  }
  if (tokens.length === 0) return { h: null, min: null };

  const first = tokens[0];
  const second = tokens[1];

  let h = first.h;
  const min = first.min;
  const merOverride = first.mer ? first.mer : second?.mer ? second.mer : null;

  // If meridiem can be inferred/exists, interpret the first token as 12-hour.
  if (merOverride) {
    if (merOverride === "pm" && h < 12) h += 12;
    if (merOverride === "am" && h === 12) h = 0;
  }
  // Else: no meridiem anywhere -> treat as 24-hour.

  return { h: Number.isFinite(h) ? h : null, min };
}

/**
 * Display formatter: always prefer 12-hour `am/pm` output.
 * Handles inputs like:
 * - "16:30" -> "4:30pm"
 * - "10:15am" / "10:15 am" -> "10:15am"
 * - "5pm" -> "5:00pm"
 * - "4:30 - 5pm" -> "4:30pm - 5:00pm"
 */
function formatMeetingTimeForDisplay(t) {
  const raw = String(t || "").trim();
  if (!raw || raw === "—") return "";
  if (raw.toLowerCase() === "all day") return "All day";

  const tokens = [];
  const re = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/ig;
  let match;
  while ((match = re.exec(raw)) && tokens.length < 2) {
    const h = parseInt(match[1], 10);
    const min = match[2] != null ? parseInt(match[2], 10) : 0;
    const mer = match[3] ? String(match[3]).toLowerCase() : null;
    if (Number.isFinite(h) && Number.isFinite(min)) tokens.push({ h, min, mer });
  }

  const formatToken = (tok, suffixOverride) => {
    const pad2 = (n) => String(n).padStart(2, "0");
    let suffix;
    let hour12;

    if (suffixOverride) {
      suffix = suffixOverride;
      hour12 = tok.h;
      if (!Number.isFinite(hour12) || hour12 <= 0) hour12 = 12;
      if (hour12 > 12) hour12 = ((hour12 - 1) % 12) + 1;
      return `${hour12}:${pad2(tok.min)}${suffix}`;
    }

    if (tok.mer) {
      suffix = tok.mer;
      hour12 = tok.h;
      if (!Number.isFinite(hour12) || hour12 <= 0) hour12 = 12;
      if (hour12 > 12) hour12 = ((hour12 - 1) % 12) + 1;
      return `${hour12}:${pad2(tok.min)}${suffix}`;
    }

    // No am/pm provided: treat as 24-hour.
    suffix = tok.h >= 12 ? "pm" : "am";
    hour12 = tok.h % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${pad2(tok.min)}${suffix}`;
  };

  if (tokens.length === 0) return raw;
  if (tokens.length === 1) return formatToken(tokens[0], null);

  const t0 = tokens[0];
  const t1 = tokens[1];

  // Infer missing am/pm in a range, e.g. "4:30 - 5pm"
  const suffix0 = t0.mer ? null : t1.mer ? t1.mer : null;
  const suffix1 = t1.mer ? null : t0.mer ? t0.mer : null;

  return `${formatToken(t0, suffix0)} - ${formatToken(t1, suffix1)}`;
}

/** All Meetings reference table — start time only, with space before am/pm. */
function formatReferenceListTime(timeStr) {
  const full = formatMeetingTimeForDisplay(timeStr);
  if (!full) return "";
  const start = String(full).split(/\s*-\s*/)[0].trim();
  return start.replace(/(\d)(am|pm)\b/gi, "$1 $2");
}

/** All Meetings reference table — dd-mm-yyyy under time. */
function formatReferenceDateLabel(ymdOrDisplay) {
  const raw = String(ymdOrDisplay || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = raw.match(/^(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (dmy) return raw;
  return raw;
}

/** Admin overview meta: "10:30am – 11:00am IST" (times already stored in app display TZ). */
function formatMeetingTimeRangeIst(m) {
  const start = formatMeetingTimeForDisplay(m?.time);
  const endRaw = m?.end ?? m?.end_time ?? m?.endTime;
  const end =
    endRaw != null && String(endRaw).trim() && String(endRaw).trim() !== "—"
      ? formatMeetingTimeForDisplay(endRaw)
      : "";
  if (start && end) return `${start} – ${end} IST`;
  if (start) return `${start} IST`;
  if (end) return `${end} IST`;
  return "";
}

const EVENT_COLOR_PALETTE = [
  // Avaada brand — blue / green only (no purple/orange rainbow)
  { bg: "#E8F3FC", bgHover: "#D7EAF9", border: PALETTE.brandBlue, text: "#0C4A8A" },
  { bg: "#E6F6EF", bgHover: "#D4EFE3", border: PALETTE.brandGreen, text: "#0A6B40" },
  { bg: "#EAF0F5", bgHover: "#DCE6EF", border: PALETTE.navy, text: PALETTE.navy },
  { bg: "#E7F0F3", bgHover: "#D5E4EA", border: PALETTE.sea, text: "#154556" },
];
function hashString(str) {
  let h = 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function meetingBotDispatchPending(m) {
  if (m?.botDispatchPending === true) {
    // Past meetings are missed/ended — not still "pending dispatch".
    if (isEndedMeetingStatus(effectiveMeetingStatus(m), m)) return false;
    return true;
  }
  const hasBot = String(m?.recall_bot_id || m?.recallBotId || "").trim();
  if (hasBot) return false;
  // Invite MeetAI must be on — otherwise "pending/joining" is misleading.
  if (!meetingAutoJoinIsOn(m)) return false;
  const gid = String(m?.googleEventId || m?.google_event_id || "").trim();
  const st = String(m?.status || "").toLowerCase();
  const eff = effectiveMeetingStatus(m);
  if (!(!!gid && (st === "upcoming" || st === "live" || eff === "upcoming" || eff === "live"))) {
    return false;
  }
  if (isEndedMeetingStatus(eff, m)) return false;
  return true;
}

/** True when UI should show "Joining…" / "MeetAI joining" (not when Invite MeetAI is off). */
function meetingMeetAiIsJoining(m) {
  if (!m || meetingBotJoinedCall(m) || meetingBotJoinFailed(m)) return false;
  const eff = effectiveMeetingStatus(m);
  if (isEndedMeetingStatus(eff, m)) return false;
  const { code, sub } = botJoinFailureCodes(m);
  if (BOT_JOIN_PENDING_CODES.has(code) || BOT_JOIN_PENDING_CODES.has(sub)) return true;
  if (m?.botDispatchPending === true) return true;
  // Live call with Invite MeetAI on, but bot not confirmed in-call yet.
  if (eff === "live" && meetingAutoJoinIsOn(m)) return true;
  return false;
}

function meetingOnCalendarMonth(m, year, monthIndex) {
  const d = String(m?.date || "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  const [y, mo] = d.split("-").map(Number);
  return y === year && mo - 1 === monthIndex;
}

/** ISO week number for a YYYY-MM-DD (local date). */
function isoWeekNumberFromYmd(ymd) {
  const dt = ymdToDate(ymd);
  if (!dt) return 1;
  const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/** Platform chip for calendar timeline rows (always Google / Teams / Zoom). */
function meetingTimelineCategory(m) {
  const key = String(m?.platform || "meet").toLowerCase();
  if (key === "zoom") return { label: "Zoom", bg: "#E8F3FF", fg: "#1565C0" };
  if (key === "teams") return { label: "Teams", bg: "#EEEFFA", fg: "#3F51B5" };
  if (key === "gcal") return { label: "Calendar", bg: "#EAF0F5", fg: "#0C2B4E" };
  return { label: "Google", bg: "#E8F0FE", fg: "#1A73E8" };
}

function meetingTimelineAttendeeCount(m) {
  return humanParticipants(m?.participants || []).length;
}

function meetingTimelineOrganizer(m) {
  const host = meetingHostName(m);
  if (host) return host;
  const owner = String(m?.owner_name || m?.ownerName || "").trim();
  if (owner && !isServiceAccountDisplayName(owner)) return owner;
  const parts = humanParticipants(m?.participants || []);
  const first = parts[0];
  if (first) return String(first.name || first.email || "").trim() || "Organizer";
  return "Organizer";
}

/** Avaada theme: live → green, upcoming → blue; else brand blue/green palette. */
function eventColorForMeeting(m, opts = {}) {
  if (opts?.adminBotPending === true && meetingBotDispatchPending(m)) {
    return {
      bg: "#E8F3FC",
      bgHover: "#D7EAF9",
      border: PALETTE.brandBlue,
      text: "#0C4A8A",
    };
  }
  const st = String(effectiveMeetingStatus(m) || "").toLowerCase();
  if (st === "live") {
    return {
      bg: "#E6F6EF",
      bgHover: "#D4EFE3",
      border: PALETTE.brandGreen,
      text: "#0A6B40",
    };
  }
  if (st === "cancelled") {
    return {
      bg: "#F8FAFC",
      bgHover: "#F1F5F9",
      border: "#94A3B8",
      text: "#64748B",
    };
  }
  if (st === "upcoming") {
    return {
      bg: "#E8F3FC",
      bgHover: "#D7EAF9",
      border: PALETTE.brandBlue,
      text: "#0C4A8A",
    };
  }
  const key = String(
    m?.googleEventId ||
      m?.google_event_id ||
      m?.id ||
      m?._id ||
      `${m?.title || ""}|${m?.date || ""}|${m?.time || ""}`
  );
  return EVENT_COLOR_PALETTE[hashString(key) % EVENT_COLOR_PALETTE.length];
}
function meetingEndDateTimeMs(m) {
  const startMs = meetingDateTimeMs(m);
  if (!startMs) return 0;
  const isAllDay =
    m?.duration === "All day" ||
    (String(m?.time || "") === "00:00" && String(m?.end || "") === "23:59");
  if (isAllDay) {
    const [y, mo, d] = String(m.date).split("-").map(Number);
    return new Date(y, mo - 1, d, 23, 59, 59, 999).getTime();
  }
  const endRaw = m?.end ?? m?.end_time ?? m?.endTime;
  if (endRaw && typeof endRaw === "string" && endRaw !== "—" && endRaw !== "-") {
    const [y, mo, d] = String(m.date).split("-").map(Number);
    const { h, min } = parseMeetingTimeToHourMin(endRaw);
    if (h == null || min == null) {
      // Instant / open-ended: no real calendar end — don't invent a 1h "still live" window.
      if (String(m?.type || "").toLowerCase() === "instant") return 0;
      return startMs + 60 * 60 * 1000;
    }
    let endMs = new Date(y, mo - 1, d, h, min, 0, 0).getTime();
    // Bad rows sometimes store end before start (e.g. 15:00–14:58). Don't treat as overnight.
    if (endMs <= startMs) {
      if (String(m?.type || "").toLowerCase() === "instant") return 0;
      return startMs + 60 * 60 * 1000;
    }
    return endMs;
  }
  // Instant MeetAI sessions have no scheduled end — status must follow bot/DB, not a fake hour.
  if (String(m?.type || "").toLowerCase() === "instant") return 0;
  // Fallback window when end time is unavailable (scheduled calendar stubs).
  return startMs + 60 * 60 * 1000;
}

/** Actual end for "Ended X ago": completed_at first, else scheduled/recorded end — never start. */
function meetingEndedAtMs(m) {
  const completedRaw = m?.completedAt ?? m?.completed_at;
  const startMs = meetingDateTimeMs(m);
  if (completedRaw) {
    const ms = new Date(completedRaw).getTime();
    if (Number.isFinite(ms) && ms > 0) {
      // A stamp from another occurrence in the series must not say this slot "ended 5 days ago".
      if (!startMs || Math.abs(ms - startMs) <= 36 * 60 * 60 * 1000) return ms;
    }
  }
  return meetingEndDateTimeMs(m) || 0;
}

function formatRelativeDurationMins(mins) {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

/** Starts in / Live now / Ended X ago chip for meeting detail headers. */
function meetingRelativeTimeChip(m, nowMs = Date.now()) {
  const effectiveStatus = effectiveMeetingStatus(m, nowMs);
  if (effectiveStatus === "cancelled") return null;
  if (effectiveStatus === "live") return { label: "Live now", tone: "live" };

  const meetingStartMs = meetingDateTimeMs(m);
  if (effectiveStatus === "ended" || effectiveStatus === "completed") {
    const endMs = meetingEndedAtMs(m) || meetingStartMs;
    if (!endMs) return null;
    const absMin = Math.max(0, Math.round((nowMs - endMs) / 60000));
    return { label: `Ended ${formatRelativeDurationMins(absMin)} ago`, tone: "past" };
  }

  if (!meetingStartMs) return null;
  const diffMs = meetingStartMs - nowMs;
  const absMin = Math.round(Math.abs(diffMs) / 60000);
  if (diffMs > 0) {
    if (absMin <= 5) return { label: `Starting in ${absMin} min`, tone: "imminent" };
    return { label: `Starts in ${formatRelativeDurationMins(absMin)}`, tone: "upcoming" };
  }
  if (effectiveStatus === "upcoming") return { label: "Starting now", tone: "imminent" };
  return null;
}
function meetingHasTranscriptData(m) {
  if (Array.isArray(m?.transcript) && m.transcript.length > 0) return true;
  const rowCount = Number(m?.transcriptRowCount ?? m?.transcript_row_count);
  if (Number.isFinite(rowCount) && rowCount > 0) return true;
  return String(m?.transcriptText ?? m?.transcript_text ?? "").trim().length > 0;
}

/**
 * All Meetings / Recorded list: show Transcript action only when there is something to open.
 * Do NOT show it solely because MeetAI joined — that used to look like a transcript existed
 * when the bot joined but lines/summary were still empty.
 */
function meetingListTranscriptActionVisible(m) {
  if (meetingHasTranscriptData(m) || meetingHasSummaryData(m)) return true;
  const st = effectiveMeetingStatus(m);
  if (
    isEndedMeetingStatus(st, m) &&
    meetingRecordingStoredOn(m) &&
    (meetingRecordingFileReady(m, { requireAutoJoin: false }) ||
      meetingRecordingAwaitingFile(m, { requireAutoJoin: false }))
  ) {
    // Recording exists — allow opening detail from the transcript/actions column.
    return true;
  }
  return false;
}

/** Live transcript CTA is honest only when bot is in-call or lines already exist. */
function meetingLiveTranscriptReady(m) {
  if (meetingHasTranscriptData(m)) return true;
  return meetingBotJoinedCall(m);
}

function meetingHasSummaryData(m) {
  const summary = m?.summary;
  if (!summary) return false;
  if (typeof summary === "string") return summary.trim().length > 0;
  if (typeof summary === "object") return Object.keys(summary).length > 0;
  return false;
}

/**
 * Recall.ai sub_code / code → admin-friendly explanation. Mirrors
 * backend/services/recallWebhookParse.js JOIN_FAILURE_SUB_CODES so unknown
 * codes degrade gracefully to the raw `bot_join_status_message` we stored.
 */
const BOT_JOIN_FAILURE_LABELS = {
  bot_rejected: "Host denied entry",
  rejected_by_platform: "Rejected by platform",
  meeting_full: "Meeting was full",
  recording_permission_denied: "Recording permission denied",
  participant_in_waiting_room: "Waiting in room (never admitted)",
  in_waiting_room: "Waiting in room (never admitted)",
  timeout_exceeded_waiting_room: "Timed out in waiting room",
  timeout_exceeded_only_bot_in_call: "MeetAI alone, timed out",
  meeting_not_started: "Meeting never started",
  meeting_link_invalid: "Invalid meeting link",
  invalid_meeting_url: "Invalid meeting URL",
  unable_to_join_meeting: "Could not connect",
  zoom_local_recording_disabled: "Zoom local recording disabled",
  zoom_sdk_app_not_approved: "Zoom App approval pending",
  zoom_email_required: "Zoom requires authenticated email",
  zoom_host_only_recording: "Host-only recording",
  meeting_disconnected: "MeetAI disconnected",
  removed_by_platform: "Removed by platform",
  removed_by_host: "Removed by host",
  kicked_from_call: "Kicked from call",
  bot_kicked_from_waiting_room: "Kicked from waiting room",
  internal_error: "Internal MeetAI error",
  api_request: "Stopped via API",
};

function botJoinFailureCodes(m) {
  const sub = String(m?.bot_join_status_sub_code || m?.botJoinStatusSubCode || "").toLowerCase().trim();
  const code = String(m?.bot_join_status_code || m?.botJoinStatusCode || "").toLowerCase().trim();
  return { code, sub };
}

/**
 * Status codes / sub_codes that prove MeetAI was in the call (even if it later left,
 * was kicked, or finished analysis). Must stay aligned with backend admin wasInCallCodes.
 */
const BOT_WAS_IN_CALL_CODES = new Set([
  "in_call_recording",
  "in_call_not_recording",
  "recording_permission_allowed",
  "done",
  "call_ended",
  "recording_done",
  "analysis_done",
  "media_expired",
  "kicked_from_call",
  "bot_kicked_from_call",
  "removed_by_host",
  "removed_by_platform",
  "meeting_disconnected",
  "timeout_exceeded_only_bot_in_call",
  "timeout_exceeded_in_call_recording",
  "timeout_exceeded_in_call_not_recording",
  "api_request",
  "recording_permission_denied",
  "call_ended_by_host",
  "call_ended_by_platform_idle",
  "call_ended_by_platform_max_length",
]);

/** Dispatched / connecting — not yet admitted into the call. */
const BOT_JOIN_PENDING_CODES = new Set([
  "joining_call",
  "in_waiting_room",
  "participant_in_waiting_room",
  "recording_permission_pending",
  "ready",
]);

/** Short label shown next to the "MeetAI Didn't Join" pill (e.g. "Host denied entry"). */
function getMeetingBotJoinReasonShort(m) {
  const { code, sub } = botJoinFailureCodes(m);
  if (sub && BOT_JOIN_FAILURE_LABELS[sub]) return BOT_JOIN_FAILURE_LABELS[sub];
  if (code && BOT_JOIN_FAILURE_LABELS[code]) return BOT_JOIN_FAILURE_LABELS[code];
  const raw = String(m?.bot_join_status_message || "").trim();
  if (raw) return raw.length > 60 ? `${raw.slice(0, 57)}…` : raw;
  if (sub) return sub.replace(/_/g, " ");
  if (code) return code.replace(/_/g, " ");
  return "";
}

/** Long sentence shown in the expanded detail panel. */
function getMeetingBotJoinReasonLong(m) {
  const raw = String(m?.bot_join_status_message || "").trim();
  if (raw) return raw;
  const short = getMeetingBotJoinReasonShort(m);
  return short || "MeetAI didn't join the meeting and no failure reason was reported.";
}

/**
 * Person who controls Admit / Deny for MeetAI (Google Meet host).
 * Recall usually does not send the individual's name on reject — host is the actionable party.
 */
function meetAiHostActorDetails(m) {
  const name = String(meetingHostName(m) || "").trim();
  const email = String(meetingHostEmail(m) || "").trim();
  if (!name && !email) return null;
  return {
    name: name || (email.includes("@") ? formatDisplayPersonName(email) : email),
    email,
  };
}

function meetAiHostActorLabel(m) {
  const d = meetAiHostActorDetails(m);
  if (!d) return "";
  if (d.name && d.email && d.name.toLowerCase() !== d.email.toLowerCase()) {
    return `${d.name} · ${d.email}`;
  }
  return d.name || d.email || "";
}

const MEETAI_OPPOSE_CODES = new Set([
  "bot_rejected",
  "removed_by_host",
  "kicked_from_call",
  "bot_kicked_from_waiting_room",
  "recording_permission_denied",
]);
const MEETAI_ADMIT_NEEDED_CODES = new Set([
  "participant_in_waiting_room",
  "in_waiting_room",
  "timeout_exceeded_waiting_room",
]);

/**
 * Action headline for admin UI (without embedding host — host is shown as full details separately).
 */
function getMeetAiOppositionAction(m, nowMs = Date.now()) {
  if (!m || meetingBotJoinedCall(m)) return "";
  const { code, sub } = botJoinFailureCodes(m);
  const key = sub || code;
  if (MEETAI_OPPOSE_CODES.has(key)) return "opposed";
  if (MEETAI_ADMIT_NEEDED_CODES.has(key)) return "admit";
  if (meetingBotDispatchPending(m) || adminSessionRegisterStatus(m, nowMs) === "bot_pending") {
    return "admit";
  }
  if (isEndedMeetingStatus(effectiveMeetingStatus(m, nowMs), m)) {
    return "check";
  }
  return "admit";
}

/**
 * People who can Admit MeetAI (host first; then other human owner / auto-join people).
 * Each entry: { name, email, label } where label is "Name · email" when both exist.
 */
function meetAiAdmitFromPeople(m) {
  const seen = new Set();
  const people = [];
  const push = (name, email) => {
    const e = String(email || "").trim();
    const n = String(name || "").trim() || (e ? formatDisplayPersonName(e) : "");
    if (!n && !e) return;
    if (isBotServiceAccountEmail(e) || isServiceAccountDisplayName(n)) return;
    const key = normAppEmail(e) || String(n).toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    const label =
      n && e && n.toLowerCase() !== e.toLowerCase()
        ? `${n} · ${e}`
        : n || e;
    people.push({ name: n, email: e, label });
  };

  const host = meetAiHostActorDetails(m);
  if (host) push(host.name, host.email);

  const ownerName = String(m?.owner_name || m?.ownerName || "").trim();
  const ownerEmail = String(m?.owner_email || m?.ownerEmail || "").trim();
  if (!isBotServiceAccountEmail(ownerEmail) && !isServiceAccountDisplayName(ownerName)) {
    push(formatDisplayPersonName(ownerName) || ownerName, ownerEmail);
  }

  for (const p of meetingAutoJoinOptInUsers(m)) {
    push(formatDisplayPersonName(p?.name) || p?.name, p?.email);
  }

  return people;
}

function meetAiAdmitFromNames(m) {
  return meetAiAdmitFromPeople(m).map((p) => p.label);
}

/**
 * Line for admin UI: who must admit MeetAI, or who opposed / removed it.
 * Returns "" when MeetAI already joined or no host is known.
 */
function getMeetAiOppositionLine(m, nowMs = Date.now()) {
  if (!m || meetingBotJoinedCall(m)) return "";
  const actor = meetAiHostActorLabel(m);
  if (!actor) return "";
  const kind = getMeetAiOppositionAction(m, nowMs);
  if (kind === "opposed") return `Who opposed / removed MeetAI: ${actor}`;
  if (kind === "check") {
    const reason = getMeetingBotJoinReasonShort(m);
    return reason ? `Host to check · ${reason}: ${actor}` : `Host to check: ${actor}`;
  }
  return `Admit from: ${actor}`;
}

/** Compact host + admit-from block for MeetAI status panels. */
function MeetAiHostDetailBlock({ meeting, nowMs = Date.now(), tone = "warn" }) {
  const host = meetAiHostActorDetails(meeting);
  const admitPeople = meetAiAdmitFromPeople(meeting);
  if (!host && admitPeople.length === 0) return null;
  const joined = meetingBotJoinedCall(meeting);
  const kind = joined ? "joined" : getMeetAiOppositionAction(meeting, nowMs);
  const border = tone === "ok" ? "#BBF7D0" : "#FDBA74";
  const bg = tone === "ok" ? "rgba(240,253,244,0.85)" : "rgba(255,247,237,0.95)";
  const labelColor = tone === "ok" ? "#166534" : "#9A3412";
  const row = (label, value) => (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 5, lineHeight: 1.35 }}>
      <span style={{ fontSize: 12, fontWeight: 750, color: labelColor, flexShrink: 0, minWidth: 92 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 650, color: "#0F172A", wordBreak: "break-word" }}>
        {value}
      </span>
    </div>
  );
  const admitLabel =
    kind === "opposed" ? "Opposed by :" : kind === "check" ? "Host check :" : "Admit from :";
  const admitValue = admitPeople.map((p) => p.label).join(", ");
  return (
    <div
      style={{
        marginTop: 10,
        padding: "10px 11px",
        borderRadius: 10,
        border: `1px solid ${border}`,
        background: bg,
      }}
    >
      {host?.name || host?.email ? row("Host :", host.name || host.email) : null}
      {host?.email && host?.name ? row("Email :", host.email) : null}
      {admitPeople.length > 0 ? row(admitLabel, admitValue) : null}
    </div>
  );
}

/**
 * Recap-email outcome codes set by recallMeetingFinalize → short admin labels.
 * Used by the "Email not sent" / "Emailed" pill and the expanded panel.
 */
const EMAIL_STATUS_LABELS = {
  sent: "Sent",
  no_discussion: "No discussion notice",
  smtp_queued: "Queued / processing",
  finalizing: "Processing",
  no_transcript: "No transcript",
  no_participants: "No participants",
  no_deliverable_recipients: "No Avaada recipient",
  smtp_failed: "SMTP failed",
  unknown: "Unknown",
  pending: "Not sent (no status)",
};

const EMAIL_STATUS_DEFAULT_MESSAGES = {
  no_discussion:
    "Very little speech was captured. A short no-discussion notice was emailed instead of a full AI summary.",
  no_transcript:
    "Meeting ended with no captured transcript, so no recap was generated. Common causes: MeetAI did not stay in the call, no speech was detected, or recording did not finish.",
  no_participants:
    "No participants were found on the meeting — there was no recipient to send the recap to.",
  no_deliverable_recipients:
    "Transcript was captured but no participant had a deliverable @avaada.com email (guest/link-only join or external attendees only).",
  smtp_failed:
    "SMTP delivery failed when sending the recap. Check SMTP credentials, recipient addresses, and Gmail sending limits.",
  smtp_queued:
    "SMTP daily call cap was reached. Recap email was queued and will retry automatically.",
  finalizing:
    "Meeting has ended and the recap is being prepared. Email will send when summary processing finishes.",
  unknown:
    "Recap email could not be sent — no participants matched delivery rules.",
  pending:
    "Recap email was not sent and no detailed reason was stored. This meeting may have completed before email status tracking was added, or finalize did not record an outcome.",
};

function meetingEmailStatusCode(m) {
  const raw = String(m?.status || "").toLowerCase();
  const botLeft = meetingBotSessionEnded(m);

  // While MeetAI is still in the call, never show email processing — even if a stale code exists.
  if (!botLeft && (raw === "live" || raw === "upcoming")) return "";

  const code = String(m?.emailStatusCode || m?.email_status_code || "").toLowerCase().trim();
  if (code) return code;
  if (m?.email_sent || m?.emailSent) return "sent";
  if (m?.emailStatusAt || m?.email_status_at) return "pending";

  // Bot left/kicked — show processing even if calendar end has not arrived yet.
  if (botLeft || raw === "ended" || raw === "completed") {
    if (raw === "live" || raw === "upcoming") return "finalizing";
    const completedRaw = m?.completedAt || m?.completed_at;
    const completedMs = completedRaw ? new Date(completedRaw).getTime() : 0;
    if (raw === "ended" || code === "finalizing") return "finalizing";
    if (completedMs && Date.now() - completedMs < 30 * 60 * 1000) return "finalizing";
    if (botLeft && raw !== "completed") return "finalizing";
    return "pending";
  }

  if (isEndedMeetingStatus(effectiveMeetingStatus(m), m)) {
    return "pending";
  }
  return "";
}

function getMeetingEmailReasonShort(m) {
  const code = meetingEmailStatusCode(m);
  if (code === "sent") return EMAIL_STATUS_LABELS.sent;
  if (EMAIL_STATUS_LABELS[code]) return EMAIL_STATUS_LABELS[code];
  return code.replace(/_/g, " ");
}

function getMeetingEmailReasonLong(m) {
  const raw = String(m?.emailStatusMessage || m?.email_status_message || "").trim();
  if (raw) return raw;
  const code = meetingEmailStatusCode(m);
  if (code === "sent") return "Recap email was delivered.";
  if (code === "no_discussion") return EMAIL_STATUS_DEFAULT_MESSAGES.no_discussion;
  if (EMAIL_STATUS_DEFAULT_MESSAGES[code]) return EMAIL_STATUS_DEFAULT_MESSAGES[code];
  return EMAIL_STATUS_DEFAULT_MESSAGES.unknown;
}

/** Recipients count from the main meeting row (API snake_case or normalized camelCase). */
function meetingEmailRecipientsCount(m) {
  const fromLog = meetingRecapSentToList(m).length;
  if (fromLog > 0) return fromLog;
  const n = Number(m?.emailRecipientsCount ?? m?.email_recipients_count);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

/** Parse per-recipient recap log stored on the meeting row (same shape as admin SMTP). */
function meetingRecapSentToList(m) {
  const fromApi = m?.recapSentTo ?? m?.recap_sent_to;
  if (Array.isArray(fromApi) && fromApi.length) return fromApi;
  let log = m?.emailRecipientsLog ?? m?.email_recipients_log;
  if (typeof log === "string") {
    try {
      log = JSON.parse(log);
    } catch {
      log = null;
    }
  }
  if (!Array.isArray(log)) return [];
  return log
    .filter((e) => String(e?.outcome || "").toLowerCase() === "sent")
    .map((e) => ({
      name: String(e?.name || "").trim() || "Recipient",
      email: String(e?.deliverTo || e?.email || "").trim(),
      reason: String(e?.reason || "").trim(),
    }))
    .filter((e) => e.email);
}

function meetingEmailStatusAtLabel(m) {
  const raw = m?.emailStatusAt ?? m?.email_status_at;
  if (!raw) return "";
  try {
    return new Date(raw).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
}

/** One combined summary block: transcript summary + video-rec presentation summary. */
function MeetingDetailSummarySection({ meeting, referenceLayout = false, actionItems = null }) {
  const presStatus = normalizePresentationSummaryStatus(
    meeting?.presentationSummaryStatus || meeting?.presentation_summary_status
  );
  const presSummaryVisible = presentationSummarySafeToShow(meeting);
  const mainSummary = String(meeting?.summary || "").trim();
  const recordingOn = meetingRecordingStoredOn(meeting);
  const recordingReady = meetingRecordingFileReady(meeting, { requireAutoJoin: false });
  const recordingPending = meetingRecordingAwaitingFile(meeting, { requireAutoJoin: false });
  const hasRecording = recordingOn && (recordingReady || recordingPending);
  const presLegacyHidden =
    presStatus === "ready" &&
    String(meeting?.presentationSummary || meeting?.presentation_summary || "").trim() &&
    !presSummaryVisible;
  const presWaiting =
    hasRecording &&
    !presSummaryVisible &&
    presStatus !== "skipped" &&
    presStatus !== "processing" &&
    presStatus !== "failed" &&
    !presLegacyHidden;
  const hasAny =
    !!mainSummary ||
    presSummaryVisible ||
    presStatus === "processing" ||
    presWaiting;
  if (!hasAny && !referenceLayout) return null;

  if (referenceLayout) {
    return (
      <>
        {!hasAny ? (
          <div className="avaada-detail-summary-empty">
            <span className="avaada-detail-summary-empty__icon" aria-hidden>
              <Svg d={IC.info} size={16} color="#0875E1" stroke={2} />
            </span>
            <p>Summary will appear here when MeetAI finishes processing this meeting.</p>
          </div>
        ) : (
          <MeetingDetailStructuredSummary meeting={meeting} actionItems={actionItems} />
        )}
        {!presSummaryVisible && presStatus === "processing" ? (
          <div className="avaada-detail-summary-note">
            Screen-share summary from your meeting recording is being generated. This page refreshes automatically.
          </div>
        ) : null}
        {presWaiting ? (
          <div className="avaada-detail-summary-note">
            Screen-share summary from the recording will appear here when ready.
          </div>
        ) : null}
      </>
    );
  }

  if (!hasAny) return null;

  const sectionHeadStyle = {
    fontSize: 11,
    color: T.textMuted,
    fontWeight: 500,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };
  const combinedSummaryText = buildCombinedMeetingSummaryDisplay(meeting);
  const showMainSummary = !!combinedSummaryText.trim();

  return (
    <>
      <Divider />
      <div>
        <div style={{ ...sectionHeadStyle, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>Meeting Summary</span>
          {presSummaryVisible && presStatus === "limited" ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(251,191,36,0.15)",
                color: "#B45309",
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              Recording summary — on-screen not verified
            </span>
          ) : null}
        </div>
        {showMainSummary ? <MeetingSummaryBlock text={combinedSummaryText} /> : null}
        {!presSummaryVisible && presStatus === "processing" ? (
          <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.55, marginTop: showMainSummary ? 12 : 0 }}>
            Screen-share summary from your meeting recording is being generated (what was shown on
            screen and said during share). This page refreshes automatically.
          </div>
        ) : null}
        {presWaiting ? (
          <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.55, marginTop: showMainSummary ? 12 : 0 }}>
            Screen-share summary from the recording will appear here when ready.
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Recap email status from the main meeting record (same fields as admin). */
function MeetingRecapEmailBanner({ meeting }) {
  const status = effectiveMeetingStatus(meeting);
  const rawStatus = String(meeting?.status || "").toLowerCase();
  const botLeft = meetingBotSessionEnded(meeting);
  // Hide only while the call is still active for MeetAI (bot still in / not left).
  if (!botLeft && (rawStatus === "live" || status === "live" || status === "upcoming")) return null;
  if (!botLeft && !isEndedMeetingStatus(status, meeting) && rawStatus !== "ended") return null;
  const sent = !!meeting?.emailSent;
  const code = meetingEmailStatusCode(meeting);
  if (!sent && !code) return null;
  const recapSentTo = meetingRecapSentToList(meeting);
  const recipientsCount = meetingEmailRecipientsCount(meeting);
  const reasonLong = getMeetingEmailReasonLong(meeting);
  const statusAt = meetingEmailStatusAtLabel(meeting);
  const hasTranscript = meetingHasTranscriptData(meeting);
  if (!sent && !code && !reasonLong && !hasTranscript) return null;

  const isSuccess = sent || code === "sent" || code === "no_discussion";
  const isWarning =
    !isSuccess &&
    (code === "no_transcript" || code === "smtp_queued" || code === "pending" || code === "finalizing");
  const bg = isSuccess ? T.successBg : isWarning ? "#FFFBEB" : "#FEF2F2";
  const border = isSuccess ? "#BBCFEE" : isWarning ? "#FDE68A" : "#FECACA";
  const color = isSuccess ? T.success : isWarning ? "#B45309" : "#B91C1C";
  const iconColor = isSuccess ? T.success : isWarning ? "#D97706" : "#DC2626";

  let headline = isSuccess
    ? code === "no_discussion"
      ? recipientsCount > 0
        ? `No-discussion notice emailed to ${recipientsCount} recipient${recipientsCount === 1 ? "" : "s"}`
        : "No-discussion notice delivered"
      : recipientsCount > 0
        ? `Recap emailed to ${recipientsCount} recipient${recipientsCount === 1 ? "" : "s"}`
        : "Recap email delivered"
    : code === "smtp_queued"
      ? "Recap email processing / queued"
      : code === "finalizing"
        ? "Recap email processing"
        : getMeetingEmailReasonShort(meeting) || "Recap email not delivered";

  return (
    <div
      style={{
        marginTop: 14,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        fontSize: 12,
        color,
        background: bg,
        padding: "8px 13px",
        borderRadius: 8,
        border: `1px solid ${border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <Svg d={IC.mail} color={iconColor} size={14} />
        <div style={{ flex: 1, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600 }}>{headline}</div>
          {reasonLong && (!isSuccess || recipientsCount === 0) ? (
            <div style={{ marginTop: 2, opacity: 0.92 }}>{reasonLong}</div>
          ) : isSuccess ? (
            <div style={{ marginTop: 2, opacity: 0.88 }}>
              {recapSentTo.length > 0 ? (
                <>
                  {recapSentTo.slice(0, 5).map((r, i) => (
                    <span key={`${r.email}-${i}`} style={{ display: "block" }}>
                      {r.name} · {r.email}
                    </span>
                  ))}
                  {recapSentTo.length > 5 ? (
                    <span style={{ display: "block", marginTop: 2 }}>
                      +{recapSentTo.length - 5} more
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  Sent to participants with an @avaada.com address on the invite or roster (guest
                  emails you add above count too).
                </>
              )}
            </div>
          ) : null}
          {statusAt ? (
            <div style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>Last attempt: {statusAt}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** True when Recall explicitly reported a join failure (sub_code/code in dictionary). */
function meetingBotJoinFailed(m) {
  const { code, sub } = botJoinFailureCodes(m);
  return Boolean((sub && BOT_JOIN_FAILURE_LABELS[sub]) || (code && BOT_JOIN_FAILURE_LABELS[code]));
}

/**
 * True when Recall bot was in the call (transcript / MoM / explicit success or left-after-join code).
 * Prefer the new `bot_join_status_*` fields when present — they're authoritative.
 * Stored video or a Recall bot id also prove join (lite list rows may omit transcript/status).
 *
 * Important: final codes like `call_ended` / `analysis_done` / kick-after-join mean MeetAI
 * *was* in the meeting. Do not treat those as "MeetAI Not Joined".
 */
function meetingBotJoinedCall(m) {
  const { code, sub } = botJoinFailureCodes(m);
  // 1) Explicit in-call / left-after-join status (authoritative).
  if (BOT_WAS_IN_CALL_CODES.has(code) || BOT_WAS_IN_CALL_CODES.has(sub)) return true;
  // 2) Evidence of capture (proves MeetAI was in the meeting).
  if (meetingRecordingHasStoredFile(m)) return true;
  if (meetingHasTranscriptData(m) || meetingHasSummaryData(m)) return true;
  // 3) Hard never-joined failures (waiting room / reject) — before treating bot id as joined.
  if (meetingBotJoinFailed(m)) return false;
  // 4) Still connecting / waiting room — not joined yet (keeps Calendar "Joining…" accurate).
  if (BOT_JOIN_PENDING_CODES.has(code) || BOT_JOIN_PENDING_CODES.has(sub)) return false;
  // 5) Recall bot id only counts when not failed and not still pending admission.
  if (String(m?.recallBotId || m?.recall_bot_id || "").trim()) return true;
  // 6) Webhook duration phrases written after MeetAI left an in-call session.
  const dur = String(m?.duration || "").toLowerCase().trim();
  if (dur === "call ended" || dur === "ended early" || dur.startsWith("bot left")) {
    return true;
  }
  return false;
}

/** Empty transcript copy — different when MeetAI never joined vs joined but lines are not ready. */
function meetingTranscriptEmptyMessage(meeting, status) {
  const st = String(status || effectiveMeetingStatus(meeting) || "").toLowerCase();
  if (st === "live") {
    return "MeetAI is in this call. What people say will show up here as they speak. This page updates on its own.";
  }
  if (st === "upcoming" || st === "scheduled" || st === "pending") {
    return "The transcript will show here after the meeting, once MeetAI joins and people start speaking.";
  }
  if (!meetingBotJoinedCall(meeting)) {
    return "MeetAI did not join this meeting, so there is no transcript. Next time, turn on Invite MeetAI before the call if you want a transcript and notes.";
  }
  return "MeetAI joined this meeting, but the transcript is not ready yet. If the call just ended, tap Refresh in a minute. If it stays blank, the conversation may not have been captured.";
}

/** Empty recording copy — missed join, Video Rec off, or still waiting. */
function meetingRecordingEmptyMessage(m) {
  const status = String(effectiveMeetingStatus(m) || "").toLowerCase();
  if (status === "upcoming" || status === "scheduled" || status === "pending") {
    return "The video will show here after the meeting, if Video Rec is turned on and MeetAI joins the call.";
  }
  if (status === "live") {
    return "The video will show here after the meeting ends, if Video Rec is turned on.";
  }
  if (!meetingBotJoinedCall(m)) {
    return "MeetAI did not join this meeting, so no video was recorded. A recording is saved only when MeetAI is in the call and Video Rec is on.";
  }
  if (!meetingRecordingStoredOn(m)) {
    return "MeetAI joined this meeting, but Video Rec was off, so no video was saved.";
  }
  return "The video is not ready yet. If the meeting just ended, check back in a few minutes.";
}

/** One line on the recording tab: whether Video Rec was on, and whether a file is ready. */
function meetingRecordingStatusLine(m) {
  const status = String(effectiveMeetingStatus(m) || "").toLowerCase();
  if (status === "upcoming" || status === "scheduled" || status === "pending" || status === "live") {
    return meetingRecordingEmptyMessage(m);
  }
  if (!meetingBotJoinedCall(m)) {
    return "MeetAI did not join this meeting, so no video was recorded.";
  }
  if (!meetingRecordingStoredOn(m)) {
    return "Video Rec was off for this meeting, so no video was saved.";
  }
  if (meetingRecordingFileReady(m, { requireAutoJoin: false })) {
    return "Video Rec was on. The recording is ready to play and download below.";
  }
  if (meetingRecordingAwaitingFile(m, { requireAutoJoin: false })) {
    return "Video Rec was on. The file is still being saved and will appear here in a few minutes.";
  }
  return "Video Rec was on, but no recording file is available for this meeting.";
}

function isEndedMeetingStatus(effectiveStatus, m) {
  const raw = String(m?.status || "").toLowerCase();
  return (
    effectiveStatus === "completed" ||
    effectiveStatus === "ended" ||
    raw === "ended" ||
    raw === "completed"
  );
}

/**
 * All Meetings → AUTO JOIN column for completed/ended meetings.
 * Never returns a dash: caller shows a button/pill instead.
 * - transcript exists → "open-transcript" (button navigates to meeting detail/transcript)
 * - bot was in call (or summary exists) → "joined"
 * - otherwise → "not-joined"
 */
function allMeetingsCompletedAutoJoinInfo(m) {
  // Prefer an honest label: Transcript CTA only when transcript/summary exists.
  if (meetingHasTranscriptData(m) || meetingHasSummaryData(m)) {
    return { kind: "open-transcript", label: "Open Transcript" };
  }
  if (meetingBotJoinedCall(m)) {
    return { kind: "joined", label: "MeetAI Joined" };
  }
  return { kind: "not-joined", label: "MeetAI Not Joined" };
}

/** Recall says MeetAI left/was kicked — do not keep UI "Live" just because calendar end is later. */
function meetingBotSessionEnded(m) {
  const raw = String(m?.status || "").toLowerCase();
  if (raw === "ended" || raw === "completed") return true;
  const dur = String(m?.duration || "").toLowerCase().trim();
  if (
    dur === "call ended" ||
    dur === "ended early" ||
    dur.startsWith("bot left") ||
    dur === "completed"
  ) {
    return true;
  }
  const code = String(m?.bot_join_status_code || m?.botJoinStatusCode || "").toLowerCase().trim();
  const sub = String(m?.bot_join_status_sub_code || m?.botJoinStatusSubCode || "").toLowerCase().trim();
  const leftCodes = new Set([
    "call_ended",
    "done",
    "fatal",
    "analysis_done",
    "media_expired",
    "recording_done",
  ]);
  const leftSubs = new Set([
    "kicked_from_call",
    "removed_by_host",
    "removed_by_platform",
    "meeting_disconnected",
    "timeout_exceeded_only_bot_in_call",
    "api_request",
    "recording_permission_denied",
  ]);
  if (leftCodes.has(code)) return true;
  if (leftSubs.has(sub) || leftSubs.has(code)) return true;
  return false;
}

/** True while Recall reports MeetAI is actively in the call right now. */
function meetingBotStillInCall(m) {
  const code = String(m?.bot_join_status_code || m?.botJoinStatusCode || "").toLowerCase().trim();
  return code === "in_call_recording" || code === "in_call_not_recording";
}

/**
 * Admin Session register chip only — does NOT change calendar/auto-join/dispatch logic.
 * "Live" only when MeetAI is actually in the call (or transcript already streaming).
 * In-window / dispatched-but-not-joined → "MeetAI pending" (not Live).
 */
function adminSessionRegisterStatus(m, nowMs = Date.now()) {
  const eff = effectiveMeetingStatus(m, nowMs);
  if (eff === "cancelled") return "cancelled";
  if (eff === "completed" || eff === "ended") return "completed";
  if (eff === "live") {
    if (meetingLiveTranscriptReady(m)) return "live";
    const botId = String(m?.recallBotId || m?.recall_bot_id || "").trim();
    if (botId || m?.botDispatched === true || m?.botDispatchPending === true) return "bot_pending";
    return "upcoming";
  }
  return eff || "upcoming";
}

function effectiveMeetingStatus(m, nowMs = Date.now()) {
  const raw = String(m?.status || "").toLowerCase();
  if (raw === "cancelled" || raw === "canceled") return "cancelled";

  const startMs = meetingDateTimeMs(m);
  // A slot that has not started yet is still upcoming. A previous bot "done" code on a
  // recurring series must not mark tomorrow's occurrence as ended days ago.
  if (
    startMs &&
    startMs > nowMs + 5 * 60 * 1000 &&
    raw !== "completed" &&
    raw !== "ended" &&
    !meetingBotStillInCall(m)
  ) {
    return "upcoming";
  }
  const endMs = meetingEndDateTimeMs(m);
  const isInstant = String(m?.type || "").toLowerCase() === "instant";
  /** Orphan `live` rows with no usable end time — force ended after this from start. */
  const STALE_LIVE_MS = 8 * 60 * 60 * 1000;

  // Recall can briefly publish a completed DB status while its bot is still
  // connected to the call. The active in-call signal is more authoritative
  // until the bot actually leaves.
  if (raw === "completed") return meetingBotStillInCall(m) ? "live" : "completed";
  if (raw === "ended") {
    if (startMs && startMs > nowMs) return "upcoming";
    return "ended";
  }

  // Bot kicked/left / duration already marked ended — leave Live immediately.
  if (meetingBotSessionEnded(m)) {
    return raw === "completed" ? "completed" : "ended";
  }

  if (meetingSessionAlreadyFinished(m) && !meetingBotStillInCall(m)) {
    return raw === "completed" ? "completed" : "ended";
  }

  // Instant sessions: trust DB status (no calendar end hour). Stale `live` without
  // an active in-call signal ages out so the list doesn't stay Live forever.
  if (isInstant) {
    if (raw === "live") {
      if (!meetingBotStillInCall(m) && startMs && nowMs - startMs > STALE_LIVE_MS) return "ended";
      return "live";
    }
    if (raw === "pending" || raw === "scheduled" || raw === "upcoming" || raw === "") return "upcoming";
    return raw || "upcoming";
  }

  // Scheduled: inside calendar window → live (even if DB still says upcoming).
  if (startMs && endMs && nowMs >= startMs && nowMs <= endMs) {
    return "live";
  }

  // Past scheduled end: show Ended unless MeetAI is still actively in the call (overrun).
  if (endMs && nowMs > endMs) {
    if (raw === "live" && meetingBotStillInCall(m)) return "live";
    return "ended";
  }

  // DB still `live` with no end (or end unknown): keep live while in window/start, else stale-out.
  if (raw === "live") {
    if (startMs && startMs > nowMs) return "upcoming";
    if (startMs && nowMs - startMs > STALE_LIVE_MS && !meetingBotStillInCall(m)) return "ended";
    return "live";
  }

  if (endMs && endMs < nowMs) return "ended";
  if (raw === "pending" || raw === "scheduled" || raw === "upcoming" || raw === "") return "upcoming";
  return raw || "upcoming";
}

function meetingLiveStatusTooltip(m, status) {
  if (status !== "live") return "";
  const endMs = meetingEndDateTimeMs(m);
  if (endMs && Date.now() > endMs && meetingBotStillInCall(m)) {
    return "Scheduled meeting time has ended, but MeetAI is still in the meeting.";
  }
  if (meetingBotStillInCall(m)) {
    return "MeetAI is still in the meeting and is recording or processing the live session.";
  }
  return "The meeting is currently live.";
}
function detectPlatform(url=""){
  if(url.includes("zoom.us"))      return "zoom";
  if(url.includes("teams.microsoft") || url.includes("teams.live")) return "teams";
  if(url.includes("meet.google"))  return "meet";
  return null;
}

/* ── design tokens — navy/cold/sea + neutral grey theme ── */
const T = {
  bg:PALETTE.white,
  surface:PALETTE.grey,
  surfaceHover:"#E9EDF1",
  border:"#D6DEE6",
  borderMid:"#C4CEDA",
  text:PALETTE.navy,
  textSub:PALETTE.cold,
  textMuted:"#6A7F95",
  accent:PALETTE.navy,
  accentDark:PALETTE.cold,
  accentBg:"#EAF0F5",
  accentMid:"#C3D3E1",
  accent2:PALETTE.sea,
  accent2Bg:"#E7F0F3",
  accent2Mid:"#BED2DA",
  danger:PALETTE.navy, dangerBg:"#EAF0F5",
  success:PALETTE.sea, successBg:"#E7F0F3",
  warning:PALETTE.cold, warningBg:"#EAF0F5",
  amber:PALETTE.cold, amberBg:"#EAF0F5",
  purple:PALETTE.sea, purpleBg:"#E7F0F3",
  sidebar:PALETTE.navy,
  sidebarBorder:"rgba(255,255,255,0.14)",
  sidebarText:"rgba(255,255,255,0.72)",
  sidebarActive:"rgba(255,255,255,0.12)",
  radius:"10px", radiusLg:"14px",
  shadow:"0 1px 3px rgba(12,43,78,0.09),0 1px 2px rgba(12,43,78,0.06)",
  shadowMd:"0 8px 18px rgba(12,43,78,0.12),0 2px 6px rgba(12,43,78,0.06)",
  shadowLg:"0 16px 36px rgba(12,43,78,0.16),0 6px 14px rgba(12,43,78,0.08)",
};

/** Sidebar nav — outline icons matched to each section. */
const NAV_ICONS = {
  /* Lucide calendar-clock — set in AutoJoinNavIcon (not a single path) */
  autojoin: null,
  /* Lucide circle-play — recorded meetings */
  transcripts: "M21 12a9 9 0 11-18 0 9 9 0 0118 0z M10 8l6 4-6 4V8z",
  /* 2×2 grid — dashboard overview */
  dashboard: "M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 018.25 20.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z",
  /* Lucide calendar-days */
  calendar: "M8 2v4M16 2v4M5 4h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2zM3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01",
  /* Video camera — meetings */
  meetings: "M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z",
  /* Clipboard checklist — action items */
  actions: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
};

const IC = {
  lock:"M7 10V8a5 5 0 0110 0v2m-9 0h8a2 2 0 012 2v7a2 2 0 01-2 2H8a2 2 0 01-2-2v-7a2 2 0 012-2z",
  home:"M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  cal:"M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  /* Calendar with forward arrow — upcoming meetings (User View dashboard + summary) */
  upcomingCal:"M18.385 21h-2.827q-.214 0-.357-.143t-.143-.357t.143-.357t.357-.143h2.826q.231 0 .424-.192t.192-.424v-8.768H5V14q0 .214-.143.357T4.5 14.5t-.357-.143T4 14V6.616q0-.691.463-1.153T5.616 5h1.769V3.308q0-.233.153-.386t.385-.153t.386.153t.153.386V5h7.154V3.27q0-.214.143-.358t.357-.143t.356.143t.144.357V5h1.769q.69 0 1.153.463T20 6.616v12.769q0 .69-.462 1.153T18.384 21m-8.017-1.5H2q-.213 0-.357-.143T1.5 19t.143-.357T2 18.5h8.367l-2.72-2.765q-.141-.14-.144-.332q-.003-.191.143-.337q.14-.141.345-.141q.203 0 .344.14l3.388 3.37q.243.242.243.565t-.243.566l-3.389 3.388q-.14.14-.34.133t-.348-.153q-.14-.14-.14-.334t.14-.334z",
  clock:"M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  history:"M12 6v6h4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  /* Video call / meeting (replaces generic “users” glyph for Meetings nav) */
  meet:"M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
  task:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  /* Document with lines — action items / task list (sidebar & empty states) */
  actionList:"M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  playCircle:"M21 12a9 9 0 11-18 0 9 9 0 0118 0z M10 9.75l4.5 2.25-4.5 2.25V9.75z",
  /* Circle-R — recorded meetings */
  recordedR:"M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2m0 2a8 8 0 1 0 0 16a8 8 0 0 0 0-16m.5 3a3.5 3.5 0 0 1 1.82 6.49L16.868 17h-2.472l-2.18-3H10v3H8V7zm0 2H10v3h2.5a1.5 1.5 0 0 0 1.493-1.355L14 10.5A1.5 1.5 0 0 0 12.5 9",
  listBullets:"M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01",
  layoutDashboard:"M3 3h7v7H3V3zm11 0h7v5h-7V3zM3 14h7v7H3v-7zm10 3h7v4h-7v-4z",
  messageSquare:"M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z",
  playFill:"M8 5v14l11-7L8 5z",
  play:"M6 4l12 8-12 8V4z",
  listChecks:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  info:"M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  help:"M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  bell:"M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0",
  /* Single-person glyph — avatar placeholder when there is no photo or initials. */
  user:"M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
  mic:"M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z",
  mail:"M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  chevR:"M9 5l7 7-7 7", chevL:"M15 19l-7-7 7-7", chevDown:"M19 9l-7 7-7-7", chevUp:"M5 15l7-7 7 7",
  close:"M6 18L18 6M6 6l12 12",
  check:"M5 13l4 4L19 7",
  star:"M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z",
  menu:"M4 6h16M4 12h16M4 18h16",
  plus:"M12 4v16m8-8H4",
  bot:"M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2",
  chat:"M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  link:"M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
  zap:"M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
  video:"M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
  videoOff:"M10.66 6H14a2 2 0 012 2v8a2 2 0 01-2 2H8.48m0 0L2 19.34m6.48-5.34V8a2 2 0 012-2h1.34m4.18 8.18L22 22M16 16l-6-6",
  /** Open in new tab / join intent icon for meeting CTA buttons. */
  launch:"M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6m0 0v6m0-6L10 14",
  copy:"M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
  users:"M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  refresh:"M4 4v6h6M20 20v-6h-6M20 10A8 8 0 006.34 6.34L4 10m0 4a8 8 0 0013.66 3.66L20 14",
  sun:"M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
  moon:"M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z",
  settings:"M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  logout:"M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
  shield:"M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  gavel:"M14.5 4.5l5 5m-8.5-2l7 7m-11-1l-4 4m2-6l4 4m-7 3l4 4m-6-1h12",
  fileText:"M6 2h9l3 3v17H6a2 2 0 01-2-2V4a2 2 0 012-2zm3 7h6m-6 4h6m-6 4h4",
  download:"M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4",
  search:"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  filter:"M3 4h18M7 8h10M10 12h4M12 16h0",
  sort:"M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4",
  dotsVertical:"M12 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z",
  flag:"M4 4v16M4 4h12l-2.5 4L16 12H4",
  save:"M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8",
  share:"M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z",
  eye:"M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
  eyeOff:"M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c1.841 0 3.573-.487 5.07-1.34m2.062-2.063a10.45 10.45 0 002.072-3.097M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88",
};

/* ── atoms ── */
function Avatar({initials,size=28,color="#6B7280",isBot=false,imageUrl,title=""}){
  return (
    <div
      title={title || undefined}
      style={{width:size,height:size,borderRadius:"50%",overflow:"hidden",background:isBot?T.accent:color,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.36,fontWeight:600,fontFamily:"inherit",flexShrink:0,letterSpacing:0,position:"relative"}}
    >
      {isBot ? (
        <Svg d={IC.bot} color="#fff" size={Math.round(size * 0.52)} stroke={1.6} />
      ) : imageUrl ? (
        <>
          <span>{initials}</span>
          <img
            src={imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            style={{width:"100%",height:"100%",objectFit:"cover",display:"block",position:"absolute",inset:0}}
            onError={(e)=>{ e.currentTarget.style.display="none"; }}
          />
        </>
      ) : (
        initials
      )}
    </div>
  );
}
function Badge({ label, bg, color, dot, showDot = true }) {
  const useDot = showDot && !!dot;
  return (
    <span
      className="ui-status-chip"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: useDot ? 5 : 0,
        height: 20,
        fontSize: 10.5,
        fontWeight: 600,
        padding: "0 8px",
        borderRadius: 999,
        background: bg,
        color,
        border: `1px solid ${color}40`,
        letterSpacing: "0.02em",
        lineHeight: 1,
        boxSizing: "border-box",
        whiteSpace: "nowrap",
      }}
    >
      {useDot ? (
        <span
          className="ui-status-chip__dot"
          aria-hidden
          style={{
            width: 5,
            height: 5,
            minWidth: 5,
            minHeight: 5,
            maxWidth: 5,
            maxHeight: 5,
            borderRadius: "50%",
            background: dot,
            display: "inline-block",
            flex: "0 0 5px",
            padding: 0,
            margin: 0,
            boxSizing: "border-box",
          }}
        />
      ) : null}
      {label}
    </span>
  );
}
function PlatformChip({platform,small}){
  const m=PLATFORM_META[platform]; if(!m) return null;
  return <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,fontWeight:600,padding:small?"2px 7px":"3px 9px",borderRadius:20,background:m.bg,color:m.color,border:`1px solid ${m.color}22`}}><span style={{fontWeight:700}}>{m.short}</span>{m.label}</span>;
}
function Divider({margin="16px 0"}){ return <div style={{borderTop:`1px solid ${T.border}`,margin}}/>; }
function Svg({d,size=16,color="currentColor",stroke=1.7}){
  return <svg width={size} height={size} fill="none" viewBox="0 0 24 24"><path d={d} stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
/** How It Works — person + question-mark badge (help-for-you pattern used by Google/Slack). */
function HowItWorksIcon({ size = 18, color = "currentColor", stroke = 1.75 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8.25 7.25a2.75 2.75 0 11-5.5 0 2.75 2.75 0 015.5 0z"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.6 19.2c.55-3.05 2.55-5.05 5.4-5.05 1.18 0 2.24.35 3.12.96"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="16.6" cy="10.4" r="5.15" stroke={color} strokeWidth={stroke} />
      <path
        d="M15.15 8.55c.32-.7 1.18-1.18 2.18-1.18 1.28 0 2.28.78 2.28 1.78 0 .82-.72 1.5-1.72 1.7-.32.06-.58.32-.58.64v.55"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="16.6" cy="14.35" r="0.7" fill={color} />
    </svg>
  );
}
/** Auto Join nav icon — gear with play triangle inside (matches product reference). */
function AutoJoinNavIcon({ size = 18, color = "currentColor", stroke = 1.85 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      {/* Lucide calendar-clock — upcoming meetings */}
      <path
        d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 2v4M8 2v4M3 10h5"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 10a6 6 0 110 12 6 6 0 010-12z"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 14v2l1.5 1.5"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function SectionHead({title,action,compact}){
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:compact?8:10}}>
      <h2 style={{fontSize:compact?13:14,fontWeight:500,color:T.text}}>{title}</h2>
      {action&&<button onClick={action.fn} style={{fontSize:12,color:T.accent,background:"none",border:"none",cursor:"pointer"}}>{action.label} →</button>}
    </div>
  );
}
function MeetingEmptyIllustration({color = "#9CA3AF"}) {
  // Simple inline "meeting room empty" illustration (desk + two chairs).
  // Kept inline so Home doesn't depend on extra image assets.
  return (
    <svg width="140" height="96" viewBox="0 0 140 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="44" y="14" width="52" height="10" rx="5" fill={color} opacity="0.18" />
      <rect x="34" y="64" width="72" height="10" rx="5" fill={color} opacity="0.18" />

      {/* Desk */}
      <rect x="18" y="52" width="104" height="12" rx="6" fill={color} opacity="0.22" />
      <rect x="30" y="40" width="80" height="12" rx="6" fill={color} opacity="0.18" />
      <path
        d="M30 40H110"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.35"
      />

      {/* Left chair */}
      <path
        d="M38 56c0-9 7-16 16-16h1c9 0 16 7 16 16v2H38v-2z"
        fill={color}
        opacity="0.16"
      />
      <path d="M51 40v-9c0-3 2-5 5-5h4c3 0 5 2 5 5v9" stroke={color} strokeWidth="2" opacity="0.32" strokeLinecap="round" />
      <path d="M48 58l-6 10M68 58l6 10" stroke={color} strokeWidth="2" opacity="0.32" strokeLinecap="round" />

      {/* Right chair */}
      <path
        d="M72 56c0-9 7-16 16-16h1c9 0 16 7 16 16v2H72v-2z"
        fill={color}
        opacity="0.16"
      />
      <path d="M85 40v-9c0-3 2-5 5-5h4c3 0 5 2 5 5v9" stroke={color} strokeWidth="2" opacity="0.32" strokeLinecap="round" />
      <path d="M82 58l-6 10M102 58l6 10" stroke={color} strokeWidth="2" opacity="0.32" strokeLinecap="round" />

      {/* Floor */}
      <path d="M16 78H124" stroke={color} strokeWidth="2" opacity="0.22" strokeLinecap="round" />
    </svg>
  );
}
function PriBadge({p}){
  const MAP={
    high:{bg:"#EAF0F5",c:PALETTE.navy},
    medium:{bg:PALETTE.grey,c:PALETTE.cold},
    low:{bg:"#E7F0F3",c:PALETTE.sea},
  };
  const s=MAP[p]||MAP.medium;
  return <span style={{fontSize:10,fontWeight:500,padding:"2px 8px",borderRadius:5,background:s.bg,color:s.c,textTransform:"capitalize"}}>{p}</span>;
}
function Empty({icon,text}){
  return (
    <div style={{textAlign:"center",padding:"36px 0",color:T.textMuted}}>
      <div style={{marginBottom:10,display:"flex",justifyContent:"center",alignItems:"center"}}>
        <Svg d={icon} color={T.textMuted} size={28} stroke={1.5} />
      </div>
      <div style={{fontSize:13}}>{text}</div>
    </div>
  );
}

/** Sliding pill indicator — measures active tab and animates thumb position. */
function useSlidingTabIndicator(activeKey, remeasureKey) {
  const containerRef = useRef(null);
  const tabRefsMap = useRef({});
  const tabRefCallbacks = useRef({});
  const activeKeyRef = useRef(activeKey);
  activeKeyRef.current = activeKey;
  const [thumbStyle, setThumbStyle] = useState({ left: 0, width: 0, top: 0, height: 0, opacity: 0 });

  const updateIndicator = useCallback(() => {
    const container = containerRef.current;
    const key = activeKeyRef.current;
    const tab = key != null ? tabRefsMap.current[key] : null;
    if (!container || !tab) {
      setThumbStyle((s) => (s.opacity === 0 ? s : { ...s, opacity: 0 }));
      return;
    }
    const cRect = container.getBoundingClientRect();
    const tRect = tab.getBoundingClientRect();
    if (tRect.width <= 0 || tRect.height <= 0) {
      setThumbStyle((s) => (s.opacity === 0 ? s : { ...s, opacity: 0 }));
      return;
    }
    const next = {
      left: tRect.left - cRect.left + container.scrollLeft,
      width: tRect.width,
      top: tRect.top - cRect.top + container.scrollTop,
      height: tRect.height,
      opacity: 1,
    };
    setThumbStyle((s) =>
      s.left === next.left &&
      s.width === next.width &&
      s.top === next.top &&
      s.height === next.height &&
      s.opacity === next.opacity
        ? s
        : next
    );
  }, []);

  const scheduleRemeasure = useCallback(() => {
    updateIndicator();
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        updateIndicator();
        requestAnimationFrame(updateIndicator);
      });
    }
  }, [updateIndicator]);

  const registerTab = useCallback((key) => {
    if (!tabRefCallbacks.current[key]) {
      tabRefCallbacks.current[key] = (node) => {
        if (node) tabRefsMap.current[key] = node;
        else delete tabRefsMap.current[key];
      };
    }
    return tabRefCallbacks.current[key];
  }, []);

  useLayoutEffect(() => {
    scheduleRemeasure();
  }, [scheduleRemeasure, activeKey, remeasureKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleRemeasure) : null;
    ro?.observe(container);
    const tab = activeKey != null ? tabRefsMap.current[activeKey] : null;
    if (tab) ro?.observe(tab);
    window.addEventListener("resize", scheduleRemeasure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", scheduleRemeasure);
    };
  }, [scheduleRemeasure, activeKey, remeasureKey]);

  return { containerRef, registerTab, thumbStyle, remeasure: scheduleRemeasure };
}

function SlidingTabThumb({ style, className = "sliding-tab-thumb" }) {
  if (!style || style.opacity === 0) return null;
  return (
    <span
      className={className}
      aria-hidden
      style={{
        left: style.left,
        width: style.width,
        top: style.top,
        height: style.height,
        transition: "left 280ms cubic-bezier(0.22, 1, 0.36, 1), top 280ms cubic-bezier(0.22, 1, 0.36, 1), width 220ms ease, height 220ms ease, opacity 180ms ease",
        willChange: "left, top, width, height",
      }}
    />
  );
}

const MEET_SECTION_TABS = [
  {
    id: "meetings",
    label: "All Meetings",
    icon: IC.meet,
    aria: "All Meetings — full calendar list",
  },
  {
    id: "autojoin",
    label: "Upcoming meetings",
    icon: IC.zap,
    aria: "Upcoming meetings — MeetAI on/off",
  },
  {
    id: "transcripts",
    label: "Recorded Meetings",
    icon: IC.mic,
    aria: "Recorded Meetings — past meetings with summaries",
  },
];

function MeetSectionsNav({ meetView, onMeetViewChange }) {
  const { containerRef, registerTab, thumbStyle } = useSlidingTabIndicator(meetView);
  return (
    <div className="meet-sections-wrap" data-tour="meet-sections">
      <div
        ref={containerRef}
        className="meet-sections sliding-tabs-host"
        role="tablist"
        aria-label="Meeting views"
      >
        <SlidingTabThumb style={thumbStyle} className="sliding-tab-thumb sliding-tab-thumb--brand" />
        {MEET_SECTION_TABS.map((tab) => {
          const active = meetView === tab.id;
          return (
            <button
              key={tab.id}
              ref={registerTab(tab.id)}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={tab.aria}
              title={tab.aria}
              className={`meet-section-tab sliding-tab-btn${active ? " active" : ""}`}
              data-tour={`meet-tab-${tab.id}`}
              onClick={() => onMeetViewChange?.(tab.id)}
            >
              <Svg d={tab.icon} size={14} color={active ? "#FFFFFF" : "#64748B"} stroke={active ? 2.2 : 1.9} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MeetingsPageSkeleton({ meetView = "meetings" } = {}) {
  return (
    <div className="page app-page-shell meet-page-root">
      {meetView === "autojoin" ? (
      <div
        className="card auto-join-premium"
        style={{ marginBottom: 14, padding: "14px 18px 14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
      >
        <div style={{ flex: 1, maxWidth: 420 }}>
          <div className="meet-skel" style={{ height: 14, width: "55%", borderRadius: 6, marginBottom: 10 }} />
          <div className="meet-skel" style={{ height: 10, width: "88%", borderRadius: 6 }} />
        </div>
        <div className="meet-skel" style={{ width: 52, height: 28, borderRadius: 999 }} />
      </div>
      ) : null}
      <div className="meet-well">
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div className="meet-skel" style={{ flex: "1 1 220px", height: 42, borderRadius: 10 }} />
          <div className="meet-skel" style={{ width: 196, height: 42, borderRadius: 10 }} />
          <div className="meet-skel" style={{ width: 300, height: 42, borderRadius: 11 }} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="meet-skel" />
          ))}
        </div>
      </div>
    </div>
  );
}

function DashHomeSkeleton() {
  return (
    <div className="page app-page-shell">
      <div className="meet-skel" style={{ height: 52, borderRadius: 12, marginBottom: 22, maxWidth: 420 }} />
      <div className="content-well">
        <div className="stat-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 20 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="meet-skel" style={{ minHeight: 92, borderRadius: 12 }} />
          ))}
        </div>
        <div className="meet-skel" style={{ minHeight: 340, borderRadius: 14 }} />
      </div>
    </div>
  );
}

function CalendarTabSkeleton() {
  return (
    <div className="page app-page-shell">
      <div className="content-well">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div className="meet-skel" style={{ width: 72, height: 40, borderRadius: 10 }} />
            <div className="meet-skel" style={{ width: 76, height: 40, borderRadius: 10 }} />
            <div className="meet-skel" style={{ width: 220, height: 22, borderRadius: 8 }} />
          </div>
          <div className="meet-skel" style={{ width: 200, height: 36, borderRadius: 11 }} />
        </div>
        <div className="meet-skel" style={{ minHeight: 440, borderRadius: 14 }} />
      </div>
    </div>
  );
}

/** Admin portal Calendar tab — hero, search, month grid + sidebar placeholders. */
function AdminCalendarSkeleton({ isLight = true }) {
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="apage adm-premium-page adm-cal-skel-page" aria-busy="true" aria-label="Loading calendar">
      <div className="adm-skel" style={{ height: 88, borderRadius: 14, marginBottom: 18, maxWidth: 720 }} />
      <div className="adm-skel" style={{ height: 44, borderRadius: 12, marginBottom: 20, maxWidth: "100%" }} />
      <div
        className="adm-cal-skel-shell"
        style={{
          background: isLight ? "#ffffff" : "#F4F7FB",
          borderRadius: 16,
          padding: 20,
          border: isLight ? "1px solid #e2e8f0" : "1px solid #1E3448",
          width: "100%",
          maxWidth: "100%",
          margin: 0,
          boxShadow: isLight ? "0 8px 32px rgba(15,23,42,0.06)" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div className="adm-skel" style={{ width: 64, height: 36, borderRadius: 8 }} />
            <div className="adm-skel" style={{ width: 72, height: 36, borderRadius: 8 }} />
            <div className="adm-skel" style={{ width: 140, height: 22, borderRadius: 6 }} />
            <div className="adm-skel" style={{ width: 72, height: 22, borderRadius: 999 }} />
          </div>
          <div className="adm-skel" style={{ width: 220, height: 36, borderRadius: 11 }} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="adm-skel" style={{ width: 108, height: 28, borderRadius: 999 }} />
          ))}
        </div>
        <div className="adm-cal-skel-body" style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 520px", minWidth: 0 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 6,
                marginBottom: 6,
              }}
            >
              {dayLabels.map((d) => (
                <div key={d} className="adm-skel adm-skel--flat" style={{ height: 14, borderRadius: 4 }} />
              ))}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 6,
              }}
            >
              {Array.from({ length: 42 }).map((_, i) => (
                <div
                  key={i}
                  className="adm-skel adm-skel--cell"
                  style={{
                    minHeight: 72,
                    borderRadius: 8,
                    opacity: i < 7 || i >= 35 ? 0.45 : 1,
                  }}
                />
              ))}
            </div>
          </div>
          <div style={{ flex: "0 0 260px", width: 260, maxWidth: "100%" }}>
            <div className="adm-skel" style={{ height: 200, borderRadius: 12, marginBottom: 14 }} />
            <div className="adm-skel" style={{ height: 120, borderRadius: 12, marginBottom: 14 }} />
            <div className="adm-skel" style={{ height: 88, borderRadius: 12 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionsTabSkeleton() {
  return (
    <div className="page app-page-shell">
      <div className="content-well">
        <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
          <div className="meet-skel" style={{ flex: "1 1 200px", height: 42, borderRadius: 10 }} />
          <div className="meet-skel" style={{ width: 280, height: 42, borderRadius: 11 }} />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="meet-skel" style={{ height: 68, borderRadius: 12, marginBottom: 10 }} />
        ))}
      </div>
    </div>
  );
}

function MeetingsEmptyState({ hasSourceMeetings, hasSearch, onClearSearch, onWidenFilters, selectedDateYmd, meetView }) {
  const { t } = useI18n();
  if (hasSourceMeetings) {
    if (meetView === "transcripts") {
      return (
        <div className="meet-empty meet-empty--recorded" style={{ gridColumn: "1 / -1" }}>
          <div className="meet-empty-h">{t("noRecordedMeetings", "No recorded meetings")}</div>
          <p className="meet-empty-p">
            {t(
              "noTranscriptsMatchDesc",
              "Transcripts appear here after MeetAI joins a meeting and captures speech. Yesterday or last week may simply have no recorded meetings — try All days, or reset below."
            )}
          </p>
          <div className="meet-empty-actions">
            {hasSearch ? (
              <button type="button" className="ghost-btn" onClick={onClearSearch}>
                {t("clearSearch", "Clear search")}
              </button>
            ) : null}
            <button type="button" className="ghost-btn" onClick={onWidenFilters}>
              {t("allDaysNewestFirst", "All days · Newest first")}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="meet-empty" style={{ gridColumn: "1 / -1" }}>
        <div className="meet-empty-h">{t("noMeetingsMatchFilters", "No meetings match your filters")}</div>
        <p className="meet-empty-p">
          {meetView === "autojoin"
            ? t("noMeetingsMatchDescAutojoin", `${JOIN_MEET_AI_LABEL} only shows upcoming Google Calendar events (not past or ended meetings). Open All Meetings or Recorded Meetings to see your other meetings, or widen filters below.`)
            : selectedDateYmd
              ? t("noMeetingsScheduledOnDate", `No meetings are scheduled on ${selectedDateYmd}. Choose another date or reset filters.`).replace("{date}", selectedDateYmd)
              : t("noMeetingsMatchDescGeneral", "Adjust the day, status tab, or search. Reset everything below if you want the full list again.")}
        </p>
        <div className="meet-empty-actions">
          {hasSearch ? (
            <button type="button" className="ghost-btn" onClick={onClearSearch}>
              {t("clearSearch", "Clear search")}
            </button>
          ) : null}
          <button type="button" className="ghost-btn" onClick={onWidenFilters}>
            {t("allDaysAllStatuses", "All days · All statuses")}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={`meet-empty${meetView === "transcripts" ? " meet-empty--recorded" : ""}`} style={{ gridColumn: "1 / -1" }}>
      <div className="meet-empty-h">
        {meetView === "transcripts"
          ? t("noRecordedMeetings", "No recorded meetings")
          : t("noMeetingsInView", "No meetings in view")}
      </div>
      <p className="meet-empty-p">
        {meetView === "autojoin" ? (
          <>
            <strong>{t("joinMeetAi", JOIN_MEET_AI_LABEL)}</strong> lists only <strong>upcoming</strong> Google Calendar events you can configure (today through the next 30 days).{" "}
            <strong>Past and completed meetings</strong> do not appear here — open <strong>{t("allMeetings", "All Meetings")}</strong> or <strong>{t("allTranscripts", "Recorded Meetings")}</strong> for those. Use <strong>{t("instantJoin", RECORD_MEETING_CTA)}</strong> for an instant recording without linking calendar.
          </>
        ) : (
          <>
            Calendar-linked meetings and recorded sessions appear when they are in the list window (recent past through the next 30 days). Use <strong>{t("instantJoin", RECORD_MEETING_CTA)}</strong> for instant recordings.
          </>
        )}
      </p>
    </div>
  );
}

const EMP_DIRECTORY_COLORS = ["#1a73e8", "#d93025", "#9334e6", "#188038", "#e37400", "#0b8043", "#3b5998"];

function empDirectoryInitials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
}

function empDirectoryAvatarColor(email) {
  const e = String(email || "");
  let h = 0;
  for (let i = 0; i < e.length; i++) h = (h * 31 + e.charCodeAt(i)) >>> 0;
  return EMP_DIRECTORY_COLORS[h % EMP_DIRECTORY_COLORS.length];
}

function highlightDirectoryMatch(text, query) {
  const src = String(text || "");
  const q = String(query || "").trim();
  if (!q) return src;
  const lower = src.toLowerCase();
  const qLower = q.toLowerCase();
  const idx = lower.indexOf(qLower);
  if (idx < 0) return src;
  return (
    <>
      {src.slice(0, idx)}
      <strong style={{ fontWeight: 700 }}>{src.slice(idx, idx + q.length)}</strong>
      {src.slice(idx + q.length)}
    </>
  );
}

/** Compact chip — name only; hover popup shows email + role (Gmail-style). */
function EmpDirectoryParticipantChip({ participant, onRemove }) {
  const [hover, setHover] = useState(false);
  const chipRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!hover || !chipRef.current) return;
    const r = chipRef.current.getBoundingClientRect();
    setPopupPos({
      top: r.bottom + 6,
      left: Math.min(r.left, window.innerWidth - 300),
    });
  }, [hover]);

  const role = String(participant.role || "").trim();

  return (
    <>
      <div
        ref={chipRef}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          maxWidth: "100%",
          padding: "2px 6px 2px 3px",
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          background: hover ? T.surface : T.bg,
          flexShrink: 0,
          position: "relative",
        }}
      >
        <Avatar
          initials={empDirectoryInitials(participant.name)}
          size={20}
          color={empDirectoryAvatarColor(participant.email)}
        />
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: T.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 150,
          }}
        >
          {participant.name}
        </span>
        <button
          type="button"
          aria-label={`Remove ${participant.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            color: T.textMuted,
            padding: 2,
            borderRadius: 4,
            display: "flex",
            flexShrink: 0,
          }}
        >
          <Svg d={IC.close} size={11} />
        </button>
      </div>
      {hover
        ? createPortal(
            <div
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              style={{
                position: "fixed",
                top: popupPos.top,
                left: popupPos.left,
                zIndex: 10050,
                width: 280,
                background: T.bg,
                border: `1px solid ${T.border}`,
                borderRadius: 12,
                boxShadow: "0 12px 40px rgba(0,0,0,.14)",
                padding: "14px 14px 12px",
                pointerEvents: "auto",
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <Avatar
                  initials={empDirectoryInitials(participant.name)}
                  size={44}
                  color={empDirectoryAvatarColor(participant.email)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: T.text,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {participant.name}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: T.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {participant.email}
                  </div>
                </div>
              </div>
              {role ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: T.surface,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <Svg d={IC.users} color={T.textMuted} size={15} />
                  <span style={{ fontSize: 12.5, color: T.textSub, lineHeight: 1.45 }}>{role}</span>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/** One search bar + chip cards — Google-style Instant Join participant picker (emp_master). */
function EmpParticipantChipPicker({ selected = [], onChange, excludeEmails = [], directoryHint, className }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [directoryError, setDirectoryError] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  const excludeSet = useMemo(
    () => new Set((excludeEmails || []).map((e) => String(e || "").trim().toLowerCase()).filter(Boolean)),
    [excludeEmails]
  );

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      setDirectoryError(null);
      return undefined;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      try {
        const data = await apiFetch(`/employees/search?q=${encodeURIComponent(q)}&limit=12`);
        if (reqId !== reqIdRef.current) return;
        setDirectoryError(null);
        const list = (data.results || []).filter((r) => !excludeSet.has(String(r.email || "").toLowerCase()));
        if (/\S+@\S+\.\S+/.test(q) && !list.some((r) => r.email.toLowerCase() === q.toLowerCase())) {
          list.push({
            name: q.split("@")[0].replace(/[._-]+/g, " ").trim() || q,
            email: q.toLowerCase(),
            initials: empDirectoryInitials(q.split("@")[0]),
            manual: true,
          });
        }
        setResults(list);
        setActiveIdx(0);
        setOpen(list.length > 0);
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        const msg = String(err?.message || "");
        if (/session expired|sign in/i.test(msg)) {
          setDirectoryError(null);
        } else if (/failed to fetch|network/i.test(msg)) {
          setDirectoryError("Could not reach the employee directory. Check your connection or try again.");
        } else {
          setDirectoryError(msg || "Employee directory is unavailable. You can still type a full email address.");
        }
        const list = [];
        if (/\S+@\S+\.\S+/.test(q) && !excludeSet.has(q.toLowerCase())) {
          list.push({
            name: q.split("@")[0].replace(/[._-]+/g, " ").trim() || q,
            email: q.toLowerCase(),
            initials: empDirectoryInitials(q.split("@")[0]),
            manual: true,
          });
        }
        setResults(list);
        setActiveIdx(0);
        setOpen(list.length > 0);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, 280);
    return () => clearTimeout(timerRef.current);
  }, [query, excludeSet]);

  useEffect(() => {
    function onDocDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  function pick(item) {
    const email = String(item.email || "").trim().toLowerCase();
    if (!email || excludeSet.has(email)) return;
    onChange([
      ...selected,
      {
        id: Date.now(),
        name: String(item.name || "").trim(),
        email,
        role: String(item.role || "").trim(),
      },
    ]);
    setQuery("");
    setResults([]);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function removeParticipant(id) {
    onChange(selected.filter((p) => p.id !== id));
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  return (
    <div ref={wrapRef} className={className || undefined} style={{ position: "relative" }}>
      {/* single search bar with inline small chips (Gmail-style) */}
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 6,
          minHeight: 42,
          padding: "6px 8px",
          border: `1.5px solid ${open ? T.accent : T.border}`,
          borderRadius: 8,
          background: T.bg,
          cursor: "text",
        }}
      >
        {selected.map((p) => (
          <EmpDirectoryParticipantChip
            key={p.id}
            participant={p}
            onRemove={() => removeParticipant(p.id)}
          />
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !query && selected.length) {
              removeParticipant(selected[selected.length - 1].id);
              return;
            }
            if (!open || !results.length) {
              if (e.key === "Enter") e.preventDefault();
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              pick(results[activeIdx]);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder={selected.length ? "" : "Search participants by name or email…"}
          autoComplete="off"
          style={{
            flex: "1 1 140px",
            minWidth: 120,
            border: "none",
            outline: "none",
            fontSize: 13,
            color: T.text,
            background: "transparent",
            fontFamily: "inherit",
            padding: "4px 2px",
          }}
        />
        {loading ? (
          <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>…</span>
        ) : null}
      </div>

      {open && results.length > 0 ? (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 30,
            background: T.bg,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,.12)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {results.map((r, i) => {
            const alreadyAdded = excludeSet.has(String(r.email || "").toLowerCase());
            return (
              <button
                key={`${r.email}-${i}`}
                type="button"
                disabled={alreadyAdded}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => !alreadyAdded && pick(r)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  border: "none",
                  borderBottom: i < results.length - 1 ? `1px solid ${T.border}` : "none",
                  background: i === activeIdx ? T.surface : T.bg,
                  cursor: alreadyAdded ? "default" : "pointer",
                  textAlign: "left",
                  opacity: alreadyAdded ? 0.55 : 1,
                }}
              >
                <Avatar
                  initials={r.initials || empDirectoryInitials(r.name)}
                  size={32}
                  color={empDirectoryAvatarColor(r.email)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.35 }}>
                    {highlightDirectoryMatch(r.name, query)}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.35 }}>
                    {highlightDirectoryMatch(r.email, query)}
                  </div>
                  {r.role ? (
                    <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.35, marginTop: 1 }}>
                      {r.role}
                    </div>
                  ) : null}
                </div>
                {alreadyAdded ? (
                  <Svg d={IC.check} color={T.textMuted} size={14} />
                ) : r.manual ? (
                  <span style={{ fontSize: 10, color: T.textMuted, flexShrink: 0 }}>Use email</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
      {directoryError ? (
        <div className="emp-picker-error" style={{ fontSize: 11.5, color: T.accent, marginTop: 6, lineHeight: 1.5 }}>
          {directoryError}
        </div>
      ) : (
        <div className="emp-picker-hint" style={{ fontSize: 11.5, color: T.textMuted, marginTop: 6, lineHeight: 1.5 }}>
          {directoryHint || "Search by name or email — results come from the Darwin employee directory."}
        </div>
      )}
    </div>
  );
}

/** Manage who can open the admin portal — stored in DB; env is temporary fallback. */
function PortalAdminsModal({ open, onClose, sessionEmail }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [pendingPick, setPendingPick] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/admin/portal-admins");
      setAdmins(Array.isArray(data?.admins) ? data.admins : []);
    } catch (err) {
      setError(err?.message || "Could not load admins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setOkMsg(null);
    setPendingPick([]);
    load();
    return undefined;
  }, [open, load]);

  const excludeEmails = useMemo(
    () => admins.map((a) => String(a.email || "").toLowerCase()).filter(Boolean),
    [admins]
  );

  async function addFromPicker(nextSelected) {
    const last = nextSelected[nextSelected.length - 1];
    setPendingPick([]);
    if (!last?.email) return;
    setSaving(true);
    setError(null);
    setOkMsg(null);
    try {
      await apiFetch("/admin/portal-admins", {
        method: "POST",
        body: { email: last.email, name: last.name || "" },
      });
      setOkMsg(`Added ${last.name || last.email}`);
      await load();
    } catch (err) {
      setError(err?.message || "Could not add admin");
    } finally {
      setSaving(false);
    }
  }

  async function removeAdmin(email) {
    if (!email) return;
    if (String(email).toLowerCase() === String(sessionEmail || "").toLowerCase()) {
      setError("You cannot remove your own access.");
      return;
    }
    setSaving(true);
    setError(null);
    setOkMsg(null);
    try {
      await apiFetch(`/admin/portal-admins/${encodeURIComponent(email)}`, { method: "DELETE" });
      setOkMsg(`Removed ${email}`);
      await load();
    } catch (err) {
      setError(err?.message || "Could not remove admin");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Manage admins"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 6000,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          maxHeight: "min(84vh, 720px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          boxShadow: "0 20px 48px rgba(15,23,42,0.22)",
          padding: "18px 20px 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f2a4a", letterSpacing: "-0.02em" }}>
              Manage admins
            </div>
            <div style={{ marginTop: 4, fontSize: 12.5, color: "#64748b", lineHeight: 1.45 }}>
              Choose who can open the MeetAI admin portal. Search by name or email from the employee directory, then add them.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "1px solid #e2e8f0",
              background: "#fff",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              color: "#475569",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width={16} height={16} fill="none" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div style={{ marginTop: 16, flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 6 }}>Add admin</div>
          <EmpParticipantChipPicker
            selected={pendingPick}
            onChange={addFromPicker}
            excludeEmails={excludeEmails}
            directoryHint="Type a name or email to find someone, then select them to grant access."
          />
        </div>

        {error ? (
          <div
            style={{
              marginTop: 12,
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(220,38,38,0.08)",
              border: "1px solid rgba(220,38,38,0.2)",
              color: "#b91c1c",
              fontSize: 12.5,
              lineHeight: 1.4,
              flexShrink: 0,
            }}
          >
            {error}
          </div>
        ) : null}
        {okMsg ? (
          <div
            style={{
              marginTop: 12,
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(22,163,74,0.08)",
              border: "1px solid rgba(22,163,74,0.2)",
              color: "#15803d",
              fontSize: 12.5,
              lineHeight: 1.4,
              flexShrink: 0,
            }}
          >
            {okMsg}
          </div>
        ) : null}

        <div style={{ marginTop: 16, flexShrink: 0, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
              flexShrink: 0,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Current admins</div>
            {loading || saving ? (
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                {saving ? "Saving…" : "Loading…"}
              </span>
            ) : null}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              height: 220,
              maxHeight: 220,
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 4,
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
            }}
          >
            {!loading && admins.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "#64748b", padding: "10px 0" }}>
                No admins added yet. Use search above to add someone.
              </div>
            ) : null}
            {admins.map((a) => {
              const locked = a.locked === true || a.source === "env";
              const isSelf =
                String(a.email || "").toLowerCase() === String(sessionEmail || "").toLowerCase();
              return (
                <div
                  key={a.email}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: "linear-gradient(145deg,#1778D4 0%,#1D546C 55%,#0C8F55 100%)",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {empDirectoryInitials(a.name || a.email)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 650,
                        color: "#0f2a4a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.name || a.email?.split("@")[0] || "Admin"}
                      {isSelf ? (
                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: "#64748b" }}>
                          (you)
                        </span>
                      ) : null}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "#64748b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.email}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: locked ? "#e2e8f0" : "rgba(23,120,212,0.12)",
                      color: locked ? "#475569" : "#1d4ed8",
                      flexShrink: 0,
                    }}
                    title={locked ? "Managed by system settings (fallback)" : "Saved in the admin list"}
                  >
                    {locked ? "System" : "Added"}
                  </span>
                  {!locked ? (
                    <button
                      type="button"
                      disabled={saving || isSelf}
                      onClick={() => removeAdmin(a.email)}
                      title={isSelf ? "Cannot remove yourself" : "Remove"}
                      style={{
                        border: "1px solid #fecaca",
                        background: "#fff",
                        color: "#b91c1c",
                        borderRadius: 8,
                        padding: "5px 8px",
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: saving || isSelf ? "not-allowed" : "pointer",
                        opacity: saving || isSelf ? 0.55 : 1,
                        flexShrink: 0,
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
   SOFT NOTIFICATION PERMISSION BANNER / MODAL
   Industry standard (Google Calendar/Slack style):
   Shows a clean in-app prompt to prevent accidental browser hard-blocks.
════════════════════════════════════════════════════════════════ */
function SoftNotificationPrompt({ open, onAllow, onDismiss, blocked = false }) {
  const { t } = useI18n();
  if (!open) return null;

  // Once the browser is on "denied" it will not surface its own dialog again,
  // however many times we ask. The only honest thing to offer is the manual
  // route, so the prompt becomes instructions rather than a dead button.
  const title = blocked
    ? t("remindersBlockedTitle", "Meeting reminders are blocked")
    : t("turnOnRemindersPrompt", "Turn on Meeting Reminders");
  const description = blocked
    ? t(
        "remindersBlockedPromptDesc",
        "Your browser is blocking notifications for this site. Click the icon on the left of the address bar, set Notifications to Allow, then reload."
      )
    : t(
        "turnOnRemindersDesc",
        "Get notified 10 minutes before your meetings start so you never miss a discussion."
      );

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 99999,
        maxWidth: 380,
        background: "#ffffff",
        borderRadius: 14,
        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08)",
        border: "1px solid #e2e8f0",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        animation: "slideInUp 0.3s ease-out",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: blocked ? "#fef2f2" : "#eff6ff",
            color: blocked ? "#dc2626" : "#2563eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {blocked ? "🔕" : "🔔"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{title}</div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3, lineHeight: 1.4 }}>{description}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginTop: 2 }}>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            padding: "6px 12px",
            fontSize: 12.5,
            fontWeight: 600,
            color: "#64748b",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            borderRadius: 6,
          }}
        >
          {t("maybeLater", "Maybe later")}
        </button>
        {blocked ? null : (
        <button
          type="button"
          onClick={onAllow}
          style={{
            padding: "7px 14px",
            fontSize: 12.5,
            fontWeight: 700,
            color: "#ffffff",
            background: "#0284c7",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(2, 132, 199, 0.25)",
          }}
        >
          {t("enableReminders", "Enable reminders")}
        </button>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   INSTANT MEETING MODAL  — correct flow:
   user fills link + participants → we dispatch bot → bot joins
   → bot transcribes → on meeting end: AI generates MoM + actions
   → emails sent to every participant automatically
════════════════════════════════════════════════════════════════ */
function CreateCalendarEventModal({ open, onClose, defaultDate, onCreated, onReauth, currentUser }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate || todayStr());
  const [startTime, setStartTime] = useState(() => defaultEventStartHm());
  const [endTime, setEndTime] = useState(() => addMinutesToHm(defaultEventStartHm(), 30));
  const [description, setDescription] = useState("");
  const [participants, setParticipants] = useState([]);
  const [addGoogleMeet, setAddGoogleMeet] = useState(true);
  const [autoJoin, setAutoJoin] = useState(false);
  const [videoRec, setVideoRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [panelEntered, setPanelEntered] = useState(false);
  const [panelExiting, setPanelExiting] = useState(false);
  const closeTimerRef = useRef(null);

  const excludedParticipantEmails = useMemo(() => {
    const emails = [normAppEmail(currentUser?.email)];
    for (const p of participants) {
      const em = normAppEmail(p.email);
      if (em) emails.push(em);
    }
    return emails;
  }, [currentUser?.email, participants]);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDate(defaultDate || todayStr());
    const start = defaultEventStartHm();
    setStartTime(start);
    setEndTime(addMinutesToHm(start, 30));
    setDescription("");
    setParticipants([]);
    setAddGoogleMeet(true);
    setAutoJoin(false);
    setVideoRec(false);
    setBusy(false);
    setError("");
  }, [open, defaultDate]);

  useEffect(() => {
    if (!open) {
      setPanelEntered(false);
      setPanelExiting(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPanelEntered(true));
    });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [open]);

  const requestClose = useCallback(() => {
    if (busy || panelExiting) return;
    setPanelExiting(true);
    setPanelEntered(false);
    closeTimerRef.current = setTimeout(() => {
      onClose?.();
    }, 200);
  }, [busy, panelExiting, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, requestClose]);

  if (!open) return null;

  async function handleCreate() {
    const trimmed = String(title || "").trim();
    if (!trimmed) {
      setError("Event title is required.");
      return;
    }
    if (!date || !startTime || !endTime) {
      setError("Date and time are required.");
      return;
    }
    if (endTime <= startTime) {
      setError("End time must be after start time.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await apiFetch("/calendar/events", {
        method: "POST",
        body: {
          title: trimmed,
          date,
          startTime,
          endTime,
          timeZone: appDisplayTimeZone(),
          description: description.trim(),
          attendees: (() => {
            const emails = participants.map((p) => String(p.email || "").trim()).filter(Boolean);
            const me = String(currentUser?.email || "").trim();
            if (me && !emails.some((e) => normAppEmail(e) === normAppEmail(me))) {
              emails.unshift(me);
            }
            return emails;
          })(),
          addGoogleMeet,
        },
      });
      let created = normalizeMeeting(data.event);
      const gid = extractGoogleEventId(created);
      const wantAutoJoin = autoJoin === true;
      const wantVideoRec = wantAutoJoin && videoRec === true;

      if (gid && wantAutoJoin) {
        try {
          await apiFetch(`/calendar/auto-join/google-event/${encodeURIComponent(gid)}`, {
            method: "PUT",
            body: { enabled: true },
          });
          created = { ...created, autoJoinEnabled: true };
        } catch (ajErr) {
          console.warn("[Create event] Auto Join failed:", ajErr?.message || ajErr);
        }
      }

      if (gid && wantVideoRec) {
        try {
          const recId = meetingRecordingApiId({ ...created, googleEventId: gid }) || `gcal-${gid}`;
          await apiFetch(`/meetings/${encodeURIComponent(recId)}/recording-settings`, {
            method: "PATCH",
            body: {
              enabled: true,
              retentionDays: 7,
              meetingMeta: {
                title: created.title || trimmed,
                date: created.date || date,
                time: created.time || startTime,
                endTime: created.endTime || endTime,
                meetingUrl: created.meetingUrl || created.meeting_url || "",
                googleEventId: gid,
                googleRecurringEventId: created.googleRecurringEventId || "",
                platform: created.platform || "",
                status: created.status || "",
              },
            },
          });
          created = {
            ...created,
            autoJoinEnabled: true,
            recordingEnabled: true,
            recordingPersisted: true,
          };
        } catch (recErr) {
          console.warn("[Create event] Video Rec failed:", recErr?.message || recErr);
        }
      }

      onCreated?.(created);
      setPanelExiting(true);
      setPanelEntered(false);
      closeTimerRef.current = setTimeout(() => {
        onClose?.();
      }, 200);
    } catch (err) {
      const msg = userFacingMessage(err.message || "Could not create event.");
      setError(msg);
      if (/sign out|sign in with google|permission/i.test(msg)) {
        onReauth?.();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`instant-join-overlay create-meeting-overlay${panelEntered && !panelExiting ? " is-open" : ""}${panelExiting ? " is-closing" : ""}`}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) requestClose();
      }}
    >
      <div className="instant-join-shell">
        <button
          type="button"
          className="instant-join-x instant-join-x--outside"
          aria-label="Close"
          onClick={requestClose}
          disabled={busy}
        >
          <Svg d={IC.close} size={16} color="currentColor" />
        </button>
        <div
          className="instant-join-panel create-meeting-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-meeting-title"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="instant-join-head create-meeting-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div className="instant-join-head-icon create-meeting-head-icon" aria-hidden>
              <Svg d={IC.cal} color="#0875E1" size={16} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 id="create-meeting-title">Create Meeting</h2>
              <p>Adds to your Google Calendar{addGoogleMeet ? " with a Meet link" : ""}.</p>
            </div>
          </div>
        </div>
        <div className="instant-join-body">
          <div className="create-meeting-pad">
            <label className="create-meeting-field">
              <span className="create-meeting-label">
                Title <span className="req">*</span>
              </span>
              <input
                className="create-meeting-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Meeting title"
                autoFocus
              />
            </label>
            <label className="create-meeting-field">
              <span className="create-meeting-label">
                Date <span className="req">*</span>
              </span>
              <div className="create-meeting-input-wrap">
                <input className="create-meeting-input has-end-ico" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                <span className="create-meeting-end-ico" aria-hidden>
                  <Svg d={IC.cal} color="#71869B" size={16} />
                </span>
              </div>
            </label>
            <div className="create-meeting-time-row">
              <label className="create-meeting-field">
                <span className="create-meeting-label">
                  Start time <span className="req">*</span>
                </span>
                <div className="create-meeting-input-wrap">
                  <input
                    className="create-meeting-input has-end-ico"
                    type="time"
                    value={startTime}
                    onChange={(e) => {
                      const next = e.target.value;
                      setStartTime(next);
                      if (next && endTime <= next) setEndTime(addMinutesToHm(next, 30));
                    }}
                  />
                  <span className="create-meeting-end-ico" aria-hidden>
                    <Svg d={IC.clock} color="#71869B" size={16} />
                  </span>
                </div>
              </label>
              <label className="create-meeting-field">
                <span className="create-meeting-label">
                  End time <span className="req">*</span>
                </span>
                <div className="create-meeting-input-wrap">
                  <input className="create-meeting-input has-end-ico" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                  <span className="create-meeting-end-ico" aria-hidden>
                    <Svg d={IC.clock} color="#71869B" size={16} />
                  </span>
                </div>
              </label>
            </div>
            <div className="create-meeting-field">
              <span className="create-meeting-label">Participants</span>
              {currentUser?.email ? (
                <div className="create-meeting-you">
                  <Avatar
                    initials={currentUser.initials || String(currentUser.name || currentUser.email).slice(0, 2).toUpperCase()}
                    size={26}
                    color="#0875E1"
                    imageUrl={currentUser.avatar_url || currentUser.avatarUrl || ""}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="create-meeting-you-name">
                      {currentUser.name || currentUser.email}
                      <span className="create-meeting-you-badge">You</span>
                    </div>
                    <div className="create-meeting-you-mail">{currentUser.email}</div>
                  </div>
                  <Svg d={IC.check} color="#0875E1" size={14} />
                </div>
              ) : null}
              <div className="create-meeting-search-wrap">
                <span className="create-meeting-search-ico" aria-hidden>
                  <Svg d={IC.search} color="#71869B" size={15} />
                </span>
                <EmpParticipantChipPicker
                  className="create-meeting-picker"
                  selected={participants}
                  onChange={setParticipants}
                  excludeEmails={excludedParticipantEmails}
                  directoryHint="Type 2+ characters to search Darwin directory — pick a name to add, or enter a full email."
                />
              </div>
            </div>
            <label className="create-meeting-field">
              <span className="create-meeting-label">Description (optional)</span>
              <textarea
                className="create-meeting-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Agenda or notes"
              />
            </label>
            <div className="create-meeting-options">
              <label className="create-meeting-opt">
                <input type="checkbox" checked={addGoogleMeet} onChange={(e) => setAddGoogleMeet(e.target.checked)} />
                <span className="create-meeting-opt-ico" aria-hidden>
                  <GoogleMeetMark size={16} title="" />
                </span>
                <div className="create-meeting-opt-copy">
                  <div className="create-meeting-opt-title">Add Google Meet video conferencing</div>
                  <div className="create-meeting-opt-help">A Meet link will be added to the calendar invite</div>
                </div>
              </label>
              <label className="create-meeting-opt">
                <input
                  type="checkbox"
                  checked={autoJoin}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setAutoJoin(on);
                    if (!on) setVideoRec(false);
                  }}
                />
                <span className="create-meeting-opt-ico" aria-hidden>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                    <path
                      d="M11.2 2.6c.2-.6 1.1-.6 1.3 0L14 7.2c.1.3.3.5.6.6l4.6 1.5c.6.2.6 1.1 0 1.3L14.6 12c-.3.1-.5.3-.6.6L12.5 17.2c-.2.6-1.1.6-1.3 0L9.7 12.6c-.1-.3-.3-.5-.6-.6L4.5 10.6c-.6-.2-.6-1.1 0-1.3L9.1 7.8c.3-.1.5-.3.6-.6l1.5-4.6z"
                      fill="#0875E1"
                    />
                    <path
                      d="M18.4 13.2c.14-.4.7-.4.84 0l.72 2.16c.06.18.2.32.38.38l2.16.72c.4.14.4.7 0 .84l-2.16.72c-.18.06-.32.2-.38.38l-.72 2.16c-.14.4-.7.4-.84 0l-.72-2.16a.6.6 0 00-.38-.38l-2.16-.72c-.4-.14-.4-.7 0-.84l2.16-.72c.18-.06.32-.2.38-.38l.72-2.16z"
                      fill="#05B887"
                    />
                    <path
                      d="M5.35 14.15c.1-.3.52-.3.62 0l.5 1.5c.05.14.16.25.3.3l1.5.5c.3.1.3.52 0 .62l-1.5.5a.45.45 0 00-.3.3l-.5 1.5c-.1.3-.52.3-.62 0l-.5-1.5a.45.45 0 00-.3-.3l-1.5-.5c-.3-.1-.3-.52 0-.62l1.5-.5c.14-.05.25-.16.3-.3l.5-1.5z"
                      fill="#7C5CFC"
                    />
                  </svg>
                </span>
                <div className="create-meeting-opt-copy">
                  <div className="create-meeting-opt-title">{JOIN_MEET_AI_LABEL}</div>
                  <div className="create-meeting-opt-help">Allow MeetAI to join and process this meeting</div>
                </div>
              </label>
              <label className={`create-meeting-opt${!autoJoin ? " is-disabled" : ""}`}>
                <input
                  type="checkbox"
                  checked={videoRec}
                  disabled={!autoJoin}
                  onChange={(e) => setVideoRec(e.target.checked)}
                />
                <span className="create-meeting-opt-ico" aria-hidden>
                  <Svg d={IC.video} color={autoJoin ? "#0875E1" : "#71869B"} size={16} />
                </span>
                <div className="create-meeting-opt-copy">
                  <div className="create-meeting-opt-title">Video Recording</div>
                  <div className="create-meeting-opt-help">Record the meeting video</div>
                </div>
              </label>
              {autoJoin && videoRec ? (
                <div className="create-meeting-note" role="note">
                  <strong>Video Recording:</strong> The meeting video will be available in the app for{" "}
                  <strong>7 days only</strong>. Download it within 7 days if you need to keep it longer.
                </div>
              ) : null}
            </div>
            {error ? <div className="create-meeting-err">{error}</div> : null}
          </div>
        </div>
        <div className="instant-join-foot create-meeting-foot">
          <button type="button" className="create-meeting-cancel" onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="instant-join-cta create-meeting-submit" onClick={handleCreate} disabled={busy}>
            <Svg d={IC.cal} color="#fff" size={15} />
            {busy ? "Creating…" : "Create event"}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

function InstantMeetingModal({onClose, onSuccess, currentUser}){
  const [step,setStep]         = useState(1); // 1=form  2=dispatching  3=confirmed  4=error
  const [dispatchError,setDispatchError] = useState(null);
  const [url,setUrl]           = useState("");
  const [title,setTitle]       = useState("");
  const [participants,setParticipants] = useState([]);
  const [videoRec, setVideoRec] = useState(false);
  const [errors,setErrors]     = useState({});
  const [platform,setPlatform] = useState(null);
  const [progress,setProgress] = useState(0);
  const [dispatchStep,setDispatchStep] = useState(0);
  const [dispatchedMeeting, setDispatchedMeeting] = useState(null);
  const timerRef               = useRef(null);
  const pollRef                = useRef(null);
  const closeTimerRef          = useRef(null);
  const urlInputRef            = useRef(null);
  const [panelEntered, setPanelEntered] = useState(false);
  const [panelExiting, setPanelExiting] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPanelEntered(true));
    });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => urlInputRef.current?.focus?.(), 220);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const requestClose = useCallback(() => {
    if (panelExiting || step === 2) return;
    setPanelExiting(true);
    setPanelEntered(false);
    closeTimerRef.current = setTimeout(() => {
      onClose?.();
    }, 200);
  }, [panelExiting, step, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  useEffect(()=>{ setPlatform(detectPlatform(url)); setErrors(e=>({...e,url:null})); },[url]);

  useEffect(() => {
    if (step !== 3 || !dispatchedMeeting?.id) return undefined;
    let cancelled = false;
    const mid = dispatchedMeeting.id;
    const tick = () => {
      apiFetch(`/meetings/${encodeURIComponent(mid)}`)
        .then((fresh) => {
          if (cancelled || !fresh?.id) return;
          setDispatchedMeeting((prev) => mergeMeetingDetailFromApi(prev || fresh, fresh));
        })
        .catch(() => {});
    };
    tick();
    pollRef.current = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [step, dispatchedMeeting?.id]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const excludedParticipantEmails = useMemo(()=>{
    const emails = [normAppEmail(currentUser?.email)];
    for (const p of participants) {
      const em = normAppEmail(p.email);
      if (em) emails.push(em);
    }
    return emails;
  }, [currentUser?.email, participants]);

  function validate(){
    const e={};
    if(!url.trim()) e.url="Paste a meeting link to continue.";
    else if(!platform) e.url="Couldn't detect platform — use a Zoom, Teams, or Google Meet link.";
    const badEmails = participants.filter(p=>p.email.trim() && !/\S+@\S+\.\S+/.test(p.email));
    if(badEmails.length) e.participants="One or more email addresses look invalid.";
    setErrors(e);
    return Object.keys(e).length===0;
  }

  const DISPATCH_STEPS = [
    {label:"Sending meeting link to Meet AI"},
    {label:`${BOT_DISPLAY_NAME} requesting to join meeting`},
    {label: videoRec ? "Joined — transcript + video recording active" : "Joined — live transcription active"},
    {label:"Real-time transcription started"},
    {label:"Participant list registered for email delivery"},
  ];

  const dispatchLockRef = useRef(false);

  async function handleDispatch(){
    if(!validate() || dispatchLockRef.current) return;
    dispatchLockRef.current = true;
    setDispatchError(null);
    setStep(2);
    setDispatchStep(0);
    setProgress(0);
    setDispatchedMeeting(null);

    // Animate steps while calling real API
    let idx=0;
    const animateStep=()=>{
      idx++;
      setDispatchStep(idx);
      setProgress(Math.round((idx/DISPATCH_STEPS.length)*100));
      if(idx<DISPATCH_STEPS.length-1) timerRef.current=setTimeout(animateStep, 600+Math.random()*300);
    };
    timerRef.current=setTimeout(animateStep, 500);

    try {
      // Call real backend — dispatches meeting bot (+ optional Video Rec)
      const participantEmails = participants.filter(p=>p.email.trim()).map(p=>p.email.trim());
      const { meeting } = await apiFetch("/bot/dispatch", {
        method:"POST",
        body:{
          meetingUrl:url,
          title:title.trim()||undefined,
          participants:participantEmails,
          recordVideo: videoRec === true,
        }
      });
      clearTimeout(timerRef.current);
      setDispatchStep(DISPATCH_STEPS.length);
      setProgress(100);
      const nm = normalizeMeeting({ ...meeting, id: meeting._id || meeting.id, transcript: meeting.transcript || [] });
      setDispatchedMeeting(nm);
      timerRef._meeting = nm;
      setTimeout(()=>{ setStep(3); timerRef.current=null; }, 400);
    } catch(err){
      clearTimeout(timerRef.current);
      timerRef.current = null;
      timerRef._meeting = undefined;
      dispatchLockRef.current = false;
      console.error("Bot dispatch failed:", err);
      setDispatchError(userFacingMessage(err.message || "Could not start recording. Check that the API is reachable and recording is configured."));
      setStep(4);
    }
  }

  function handleGoLive(){
    const realMeeting = dispatchedMeeting || timerRef._meeting;
    if (!realMeeting) {
      requestClose();
      return;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    onSuccess({ ...realMeeting, id: realMeeting._id || realMeeting.id });
    requestClose();
  }

  const instantJoinHeroArt = `${process.env.PUBLIC_URL || ""}/assets/friendly_ai_robot.png`;
  const PLT = platform ? PLATFORM_META[platform] : null;
  const filledCount = participants.filter(p=>p.email.trim()).length;
  const liveReady = meetingLiveTranscriptReady(dispatchedMeeting);
  const liveHasLines = meetingHasTranscriptData(dispatchedMeeting);
  const canDismiss = step === 1 || step === 3 || step === 4;
  const headerSub =
    step === 1
      ? "Paste a live meeting link and Meet Companion joins you"
      : step === 2
        ? "Connecting to your meeting…"
        : step === 4
          ? "Could not start recording"
          : liveReady
            ? `${BOT_DISPLAY_NAME} is in your meeting`
            : `${BOT_DISPLAY_NAME} is joining…`;

  return (
    <div
      className={`instant-join-overlay${panelEntered && !panelExiting ? " is-open" : ""}${panelExiting ? " is-closing" : ""}`}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && canDismiss) requestClose();
      }}
    >
      <div className="instant-join-shell">
        {canDismiss ? (
          <button
            type="button"
            className="instant-join-x instant-join-x--outside"
            aria-label="Close"
            onClick={requestClose}
          >
            <Svg d={IC.close} size={16} color="currentColor" />
          </button>
        ) : null}
        <div
          className="instant-join-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="instant-join-title"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="instant-join-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div className="instant-join-head-icon" aria-hidden>
              <Svg d={IC.zap} color="#0875E1" size={16} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 id="instant-join-title">Instant Join</h2>
              <p>{headerSub}</p>
            </div>
          </div>
        </div>

        <div className="instant-join-body">
          {step === 1 && (
            <div className="instant-join-pad instant-join-step-fade">
              <div className="instant-join-hero">
                <div className="instant-join-hero-copy">
                  <div className="instant-join-hero-kicker">MEET AI / TRANSCRIPTION</div>
                  <div className="instant-join-hero-title">AI meeting capture is always on</div>
                  <p className="instant-join-hero-desc">
                    Meet Companion joins your meeting, captures the live transcript, and generates MoM + action items when the meeting ends.
                  </p>
                  <p className="instant-join-hero-note">Video Rec is optional and retained for 7 days.</p>
                </div>
                <div className="instant-join-hero-art-wrap" aria-hidden>
                  <img className="instant-join-hero-art" src={instantJoinHeroArt} alt="" loading="lazy" decoding="async" />
                </div>
              </div>

              <div className="instant-join-field">
                <label>
                  Meeting link <span className="req">*</span>
                </label>
                <div className="instant-join-input-wrap">
                  <span className="ij-ico">
                    <Svg d={IC.link} color="#71869B" size={15} />
                  </span>
                  <input
                    ref={urlInputRef}
                    className={`instant-join-input has-ico${errors.url ? " is-error" : platform ? " is-ok" : ""}`}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://meet.google.com/... or zoom.us/j/..."
                    autoComplete="off"
                  />
                  {platform ? (
                    <div
                      className="instant-join-plat"
                      style={{
                        background: PLATFORM_META[platform].bg,
                        color: PLATFORM_META[platform].color,
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{PLATFORM_META[platform].short}</span>
                      {PLATFORM_META[platform].label}
                      <Svg d={IC.check} color={PLATFORM_META[platform].color} size={12} stroke={2.2} />
                    </div>
                  ) : null}
                </div>
                {errors.url ? <div className="instant-join-err">{errors.url}</div> : null}
                {!errors.url && !platform ? (
                  <div className="instant-join-hint">Zoom, Google Meet, or Microsoft Teams</div>
                ) : null}
              </div>

              <div className="instant-join-field">
                <label>
                  Meeting title <span className="opt">(optional)</span>
                </label>
                <input
                  className="instant-join-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Quick sync with design team"
                />
              </div>

              <div className="instant-join-field">
                <label>
                  Participants <span className="opt">(emailed transcript &amp; actions)</span>
                </label>
                <div className="instant-join-you">
                  <Avatar
                    initials={currentUser.initials}
                    size={26}
                    color="#0875E1"
                    imageUrl={currentUser.avatar_url}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0B2A4A" }}>
                      {currentUser.name}{" "}
                      <span
                        style={{
                          fontSize: 10.5,
                          color: "#0875E1",
                          background: "#C8DFFF",
                          padding: "1px 6px",
                          borderRadius: 4,
                          marginLeft: 4,
                          fontWeight: 700,
                        }}
                      >
                        You
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: "#71869B" }}>{currentUser.email}</div>
                  </div>
                  <Svg d={IC.check} color="#0875E1" size={14} />
                </div>
                <EmpParticipantChipPicker
                  selected={participants}
                  onChange={setParticipants}
                  excludeEmails={excludedParticipantEmails}
                />
                {errors.participants ? <div className="instant-join-err">{errors.participants}</div> : null}
              </div>

              <div className="instant-join-video">
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0B2A4A" }}>Video Rec</div>
                  <div style={{ fontSize: 11.5, color: "#71869B", marginTop: 2, lineHeight: 1.4 }}>
                    Optional — save meeting video (7-day retention)
                  </div>
                </div>
                <button
                  type="button"
                  className={`instant-join-switch${videoRec ? " is-on" : ""}`}
                  role="switch"
                  aria-checked={videoRec}
                  aria-label="Enable Video Rec"
                  onClick={() => setVideoRec((v) => !v)}
                >
                  <span className="instant-join-switch-thumb" />
                </button>
              </div>
              {videoRec ? (
                <div className="instant-join-video-warn" role="note">
                  <strong>Video Recording:</strong> File is kept for <strong>7 days only</strong>. Download if you need
                  it longer. Separate from transcript / MoM.
                </div>
              ) : (
                <div style={{ height: 8 }} />
              )}

              <div className="instant-join-steps">
                <div className="instant-join-steps-title">After you start</div>
                {[
                  `${BOT_DISPLAY_NAME} joins with your link`,
                  "Real-time transcription",
                  "When the meeting ends — MoM & action items",
                  "Email meeting summary",
                ].map((text, i) => (
                  <div key={i} className="instant-join-step-row">
                    <div className="instant-join-step-num">{String(i + 1).padStart(2, "0")}</div>
                    <span style={{ fontSize: 12.5, color: "#40566E", lineHeight: 1.4 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="instant-join-pad instant-join-step-fade" style={{ textAlign: "center", paddingTop: 32, paddingBottom: 32 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 16,
                  background: "#EAF3FF",
                  border: "1px solid #D4E6F8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 18px",
                  position: "relative",
                }}
              >
                <Svg d={IC.mic} color="#0875E1" size={30} />
                <div
                  style={{
                    position: "absolute",
                    inset: -3,
                    borderRadius: 18,
                    border: "2px solid #0875E1",
                    opacity: 0.28,
                    animation: "spin 2s linear infinite",
                  }}
                />
              </div>
              <div style={{ fontWeight: 700, fontSize: 17, color: "#0B2A4A", marginBottom: 4 }}>Connecting…</div>
              <div style={{ fontSize: 13, color: "#5F7690", marginBottom: 24 }}>
                {title || "Instant Meeting"}
                {PLT ? ` · ${PLT.label}` : ""}
              </div>
              <div style={{ background: "#F3F6F9", borderRadius: 99, height: 5, marginBottom: 22, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    background: "#0875E1",
                    borderRadius: 99,
                    width: `${progress}%`,
                    transition: "width .45s ease",
                  }}
                />
              </div>
              <div style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 11 }}>
                {DISPATCH_STEPS.map((s, i) => {
                  const done = i < dispatchStep;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: done ? "#05B887" : i === dispatchStep ? "#EAF3FF" : "#F7FAFC",
                          border: `1.5px solid ${done ? "#05B887" : i === dispatchStep ? "#0875E1" : "#DCE6EF"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all .35s ease",
                        }}
                      >
                        {done ? (
                          <Svg d={IC.check} color="#fff" size={12} stroke={2.5} />
                        ) : i === dispatchStep ? (
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#0875E1",
                              animation: "spin 1s linear infinite",
                            }}
                          />
                        ) : (
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#CBD8E4" }} />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          color: done ? "#0B2A4A" : i === dispatchStep ? "#0875E1" : "#71869B",
                          fontWeight: done || i === dispatchStep ? 600 : 400,
                          transition: "color .3s",
                        }}
                      >
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  marginTop: 24,
                  padding: "10px 14px",
                  background: "#F7FAFC",
                  border: "1px solid #DCE6EF",
                  borderRadius: 10,
                  fontSize: 12,
                  color: "#5F7690",
                  textAlign: "left",
                  lineHeight: 1.6,
                }}
              >
                <strong style={{ color: "#24425F" }}>Powered by {BOT_DISPLAY_NAME}</strong> — admit{" "}
                <em>&quot;{BOT_DISPLAY_NAME}&quot;</em> if your {PLT?.label || "meeting"} shows a waiting room.
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="instant-join-pad instant-join-step-fade">
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    background: "#E8F8EF",
                    border: "2px solid #BFE9D0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 14px",
                  }}
                >
                  <Svg d={IC.check} color="#05B887" size={30} stroke={2.5} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 17, color: "#0B2A4A", marginBottom: 6 }}>
                  {liveReady ? "Recording in progress" : "MeetAI dispatched"}
                </div>
                <div style={{ fontSize: 13, color: "#5F7690", lineHeight: 1.65 }}>
                  {liveHasLines ? (
                    <>
                      {BOT_DISPLAY_NAME} is in <strong>{title || "your meeting"}</strong>
                      {PLT ? ` on ${PLT.label}` : ""} and live transcript lines are available.
                    </>
                  ) : liveReady ? (
                    <>
                      {BOT_DISPLAY_NAME} has joined <strong>{title || "your meeting"}</strong>
                      {PLT ? ` on ${PLT.label}` : ""}. Transcript lines appear as people speak.
                    </>
                  ) : (
                    <>
                      {BOT_DISPLAY_NAME} was sent to <strong>{title || "your meeting"}</strong>
                      {PLT ? ` on ${PLT.label}` : ""}. Admit MeetAI if prompted — transcript appears after it joins.
                    </>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
                {[
                  {
                    icon: IC.mic,
                    text: liveHasLines
                      ? "Live transcript lines are arriving"
                      : liveReady
                        ? "MeetAI is in the call — waiting for speech"
                        : "MeetAI is joining — admit from waiting room if needed",
                    color: liveReady ? "#05B887" : "#0875E1",
                  },
                  { icon: IC.users, text: "Speaker identification once transcription starts", color: "#0875E1" },
                  { icon: IC.star, text: "Action items & decisions when the meeting ends", color: "#0875E1" },
                  {
                    icon: IC.mail,
                    text: `Auto-email to ${filledCount + 1} participant${filledCount > 0 ? "s" : ""}`,
                    color: "#0875E1",
                  },
                ].map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 11,
                      padding: "10px 12px",
                      background: "#F7FAFC",
                      border: "1px solid #DCE6EF",
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: `${r.color}14`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Svg d={r.icon} color={r.color} size={15} />
                    </div>
                    <span style={{ fontSize: 12.5, color: "#40566E", lineHeight: 1.45 }}>{r.text}</span>
                  </div>
                ))}
              </div>

              {participants.filter((p) => p.email.trim()).length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#71869B",
                      marginBottom: 8,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Email will be sent to
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[{ name: currentUser.name, email: currentUser.email }, ...participants.filter((p) => p.email.trim())].map(
                      (p, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                            padding: "7px 11px",
                            background: "#F7FAFC",
                            borderRadius: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              background: "rgba(8,117,225,0.12)",
                              color: "#0875E1",
                              fontSize: 9,
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {(p.name || p.email).slice(0, 2).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0B2A4A" }}>
                              {p.name || p.email.split("@")[0]}
                            </div>
                            <div style={{ fontSize: 11, color: "#71869B" }}>{p.email}</div>
                          </div>
                          <Svg d={IC.check} color="#05B887" size={13} />
                        </div>
                      )
                    )}
                  </div>
                </div>
              ) : null}

              <div className="instant-join-steps" style={{ marginBottom: 16 }}>
                <strong>To stop early:</strong> Remove &quot;{BOT_DISPLAY_NAME}&quot; from participants in your{" "}
                {PLT?.label || "meeting"} app. {BOT_DISPLAY_NAME} also leaves when the meeting ends.
              </div>

              <button type="button" className="instant-join-cta" onClick={handleGoLive}>
                <Svg d={IC.mic} color="#fff" size={15} />
                {liveHasLines
                  ? "View Live Transcript"
                  : liveReady
                    ? "Open Meeting (transcript updating)"
                    : "Open Meeting (waiting for MeetAI)"}
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="instant-join-pad instant-join-step-fade">
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    background: "#FEF2F2",
                    border: "2px solid #FECACA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 14px",
                  }}
                >
                  <Svg d={IC.close} color="#DC2626" size={26} stroke={2.2} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8, color: "#0B2A4A" }}>
                  Could not start recording
                </div>
                <div style={{ fontSize: 13, color: "#5F7690", lineHeight: 1.65, maxWidth: 400, margin: "0 auto" }}>
                  {userFacingMessage(dispatchError)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  type="button"
                  className="instant-join-cta"
                  onClick={() => {
                    setDispatchError(null);
                    setStep(1);
                  }}
                >
                  Try again
                </button>
                <button type="button" className="instant-join-sec-btn" onClick={requestClose}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {step === 1 ? (
          <div className="instant-join-foot">
            <button type="button" className="instant-join-cta" onClick={handleDispatch}>
              <Svg d={IC.zap} color="#fff" size={16} />
              {videoRec ? `${RECORD_MEETING_CTA} + Video` : RECORD_MEETING_CTA}
            </button>
            <div className="instant-join-cta-sub">
              {videoRec
                ? "Starts Meet AI transcription and Video Recording"
                : "Starts Meet AI transcription only"}
            </div>
          </div>
        ) : null}
      </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN APP
   ════════════════════════════════════════════════════════════════ */

// Admin Usage cache (localStorage): never show blank/0 cards while the fresh
// /api/admin/usage loads or fails — show previous values, then swap in live
// data when it arrives. Per-bot rows are dropped to stay under quota.
const ADMIN_USAGE_CACHE_KEY = "meetai.adminUsage.v1";
function loadCachedUsageApi() {
  try {
    const raw = localStorage.getItem(ADMIN_USAGE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}
function saveCachedUsageApi(data) {
  try {
    if (!data || typeof data !== "object") return;
    const slim = {
      ...data,
      recallBotUsage: data.recallBotUsage
        ? { ...data.recallBotUsage, items: undefined, raw: undefined }
        : data.recallBotUsage,
      _cachedAt: new Date().toISOString(),
    };
    localStorage.setItem(ADMIN_USAGE_CACHE_KEY, JSON.stringify(slim));
  } catch (_) {}
}
let _gmailShellStylesCssCache = null;
function getGmailShellStylesCss() {
  if (_gmailShellStylesCssCache) return _gmailShellStylesCssCache;
  _gmailShellStylesCssCache = `        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Ramaraja&family=Tangerine:wght@700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-thumb{background:rgba(12,43,78,0.18);border-radius:999px;}
        ::-webkit-scrollbar-thumb:hover{background:rgba(12,43,78,0.3);}
        ::-webkit-scrollbar-track{background:transparent;}
        *{scrollbar-width:thin;scrollbar-color:rgba(12,43,78,0.18) transparent;-ms-overflow-style:-ms-autohiding-scrollbar;}
        input,button{font-family:inherit;}
        input::placeholder{color:${T.textMuted};}
        @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideInRight{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.25}}
        @keyframes shimmer{
          0%{background-position:-200% 0}
          100%{background-position:200% 0}
        }
        @keyframes scaleIn{from{opacity:0;transform:scale(0.97)}to{opacity:1;transform:scale(1)}}
        @keyframes badgePop{0%{transform:scale(0.4)}70%{transform:scale(1.15)}100%{transform:scale(1)}}

        .app-page-shell,.meet-page-root{width:100%;max-width:none;margin:0;padding:0;box-sizing:border-box;}
        .content-well,.meet-well{
          margin-top:4px;
          padding:14px 16px 18px;
          background:rgba(255,255,255,0.96);
          border:1px solid rgba(12,43,78,0.09);
          border-radius:16px;
          box-shadow:0 2px 12px rgba(12,43,78,0.07), 0 1px 3px rgba(12,43,78,0.04);
          backdrop-filter:blur(4px);
        }
        .auto-join-premium{
          position:relative;overflow:hidden;
          border:1px solid #D4DEE8;
          border-radius:12px;
          background:linear-gradient(165deg,#FFFFFF 0%,#F9FBFE 38%,#EFF6FF 100%);
          box-shadow:
            0 1px 2px rgba(15,23,42,0.04),
            0 5px 16px rgba(15,23,42,0.08),
            inset 0 1px 0 rgba(255,255,255,0.95);
        }
        .auto-join-premium::after{
          content:"";position:absolute;left:0;top:0;bottom:0;width:4px;
          background:linear-gradient(180deg,#2563EB,#1D4ED8);
          border-radius:2px 0 0 2px;
        }
        .card.mcard-pro{
          background:linear-gradient(165deg,#FFFFFF 0%,#F9FBFE 38%,#EFF6FF 100%);
          border-radius:12px;
        }
        .mcard-pro{
          position:relative;overflow:hidden;
          border-radius:12px;
          border:1px solid #D4DEE8;
          background:linear-gradient(165deg,#FFFFFF 0%,#F9FBFE 38%,#EFF6FF 100%);
          box-shadow:
            0 1px 2px rgba(15,23,42,0.04),
            0 5px 16px rgba(15,23,42,0.08),
            inset 0 1px 0 rgba(255,255,255,0.95);
          transition:box-shadow .2s ease,border-color .2s ease,transform .15s ease !important;
        }
        .mcard-pro::before{
          content:"";position:absolute;left:0;top:0;bottom:0;width:3px;
          background:linear-gradient(180deg,#3B82F6 0%,#2563EB 100%);
          border-radius:12px 0 0 12px;
          opacity:0.85;
        }
        .mcard-pro:hover,
        .mcard-pro.hover-card:hover{
          background:linear-gradient(165deg,#FFFFFF 0%,#F9FBFE 38%,#EFF6FF 100%);
          box-shadow:
            0 6px 20px rgba(15,23,42,0.10),
            0 2px 6px rgba(15,23,42,0.05),
            inset 0 1px 0 rgba(255,255,255,0.95) !important;
          border-color:#C5D0DC !important;
          transform:translateY(-2px) !important;
        }
        .mcard-meeting-title{
          font-weight:700;font-size:14.5px;line-height:1.35;
          color:#0F172A;letter-spacing:-0.015em;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;
        }
        .mcard-meeting-title.is-cancelled{
          text-decoration:line-through;color:${T.textMuted};font-weight:500;
        }
        .mcard-pro__head{display:flex;align-items:center;justify-content:space-between;gap:8px;min-width:0;}
        .mcard-pro__title-wrap{display:flex;align-items:center;gap:8px;min-width:0;flex:1;}
        .mcard-pro__icon{
          width:26px;height:26px;border-radius:8px;background:#DBEAFE;color:#1E40AF;
          display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;
        }
        .mcard-pro__status{display:inline-flex;align-items:center;gap:6px;flex-shrink:0;}
        .mcard-pro__body{min-width:0;display:flex;flex-direction:column;gap:4px;}
        .mcard-pro__meta{
          display:flex;align-items:center;flex-wrap:wrap;gap:4px 8px;
          font-size:12px;color:${T.textMuted};line-height:1.25;
        }
        .mcard-pro__meta-item{display:inline-flex;align-items:center;gap:4px;white-space:nowrap;}
        .mcard-pro__platform{
          font-size:9.5px;font-weight:700;padding:1px 6px;border-radius:999px;
          background:#E8F7E9;color:#1F7A1F;letter-spacing:.35px;text-transform:uppercase;
        }
        .mcard-pro__host{
          font-size:12px;color:${T.textMuted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
        }
        .mcard-pro__footer{
          display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;
          margin-top:2px;padding-top:8px;border-top:1px solid #E2E8F0;
        }
        .actions-page-header{
          margin-bottom:12px;padding-bottom:0;border-bottom:none;
        }
        .actions-page-header h1{
          margin:0;font-size:18px;font-weight:600;color:#0c2b4e;letter-spacing:-0.01em;
        }
        .actions-page-toolbar{
          width:100%;min-width:0;margin-bottom:12px;
          display:flex;justify-content:flex-start;flex-wrap:wrap;gap:10px;align-items:center;
        }
        .actions-bulk-bar{
          display:flex;align-items:center;flex-wrap:wrap;gap:10px;
          width:100%;padding:10px 12px;margin-bottom:14px;
          border:1px solid #dbe4ef;border-radius:10px;background:#f7fafd;
        }
        .actions-bulk-select{
          display:inline-flex;align-items:center;gap:8px;
          font-size:12.5px;font-weight:600;color:#0c2b4e;cursor:pointer;user-select:none;
        }
        .actions-bulk-select input{
          width:16px;height:16px;accent-color:#1778D4;cursor:pointer;
        }
        .actions-bulk-meta{font-size:12px;color:#64748b;font-weight:500;}
        .actions-bulk-close{
          margin-left:auto;border:none;border-radius:8px;padding:8px 14px;
          background:#0C2B4E;color:#fff;font-size:12.5px;font-weight:700;
          cursor:pointer;font-family:inherit;
        }
        .actions-bulk-close:disabled{opacity:.45;cursor:not-allowed;}
        .actions-bulk-clear{
          border:1px solid #cbd5e1;background:#fff;color:#475569;
          border-radius:8px;padding:7px 12px;font-size:12px;font-weight:600;
          cursor:pointer;font-family:inherit;
        }
        .actions-item-row.is-selected{
          border-color:#93c5fd !important;background:#eff6ff !important;
        }
        .actions-item-select{
          width:16px;height:16px;margin-top:2px;flex-shrink:0;
          accent-color:#1778D4;cursor:pointer;
        }
        .actions-page-filters.seg-group{
          display:inline-flex;width:auto;max-width:100%;min-width:0;
          gap:2px;padding:3px;flex-wrap:nowrap;
          border-radius:9px;
          overflow:visible;
        }
        .actions-page-filters.sliding-tabs-host{overflow:visible;}
        .actions-page-filters .sliding-tab-thumb--blue{border-radius:7px;}
        .actions-page-filters .seg-pill{
          flex:0 0 auto;
          display:inline-flex;align-items:center;justify-content:center;
          gap:5px;padding:5px 8px;
          font-size:11px;font-weight:500;line-height:1.2;
          border-radius:7px;white-space:nowrap;overflow:visible;
        }
        .actions-page-filters .actions-filter-label{display:inline-block;}
        .actions-page-filters .actions-filter-bubble{
          display:inline-flex;align-items:center;justify-content:center;
          min-width:17px;height:17px;padding:0 5px;
          border-radius:999px;font-size:9.5px;font-weight:700;line-height:1;
          background:rgba(12,43,78,0.1);color:#475569;
          flex-shrink:0;
        }
        .actions-page-filters .seg-pill.active .actions-filter-bubble{
          background:rgba(255,255,255,0.24);color:#fff;
        }
        .actions-page-filters .seg-pill.active .actions-filter-label{color:#fff;}
        .actions-page-list{display:flex;flex-direction:column;gap:14px;}
        .actions-meeting-card{
          border:1px solid rgba(12,43,78,0.12)!important;
          border-radius:14px!important;
          box-shadow:0 2px 8px rgba(12,43,78,0.06)!important;
          transition:box-shadow .18s ease,border-color .15s ease;
          overflow:hidden;background:#fff;
        }
        .actions-meeting-card:hover{
          border-color:rgba(21,101,192,0.28)!important;
          box-shadow:0 4px 16px rgba(12,43,78,0.1)!important;
        }
        .actions-meeting-card.is-open{
          border-color:rgba(21,101,192,0.35)!important;
          box-shadow:0 4px 18px rgba(21,101,192,0.12)!important;
        }
        .actions-meeting-head{
          display:flex;align-items:center;gap:12px;
          padding:16px 18px;background:#fff;
          transition:background .15s ease;
        }
        .actions-meeting-card.is-open .actions-meeting-head{
          background:linear-gradient(180deg,rgba(21,101,192,0.07) 0%,rgba(21,101,192,0.03) 100%);
        }
        .actions-meeting-toggle{
          flex:1;display:flex;align-items:center;gap:14px;
          min-width:0;cursor:pointer;border:none;background:transparent;
          font:inherit;color:inherit;text-align:left;padding:0;
        }
        .actions-meeting-icon{
          width:42px;height:42px;border-radius:12px;
          background:#fff;border:1px solid rgba(12,43,78,0.1);
          display:flex;align-items:center;justify-content:center;flex-shrink:0;
          box-shadow:0 1px 4px rgba(12,43,78,0.08);
        }
        .actions-meeting-icon img{width:28px;height:28px;object-fit:contain;display:block;}
        .actions-meeting-title{
          font-size:15px;font-weight:700;color:#0c2b4e;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
          line-height:1.35;letter-spacing:-0.01em;
        }
        .actions-meeting-meta{
          margin-top:5px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;
          font-size:12.5px;color:#475569;font-weight:500;
        }
        .actions-meeting-meta-date{
          display:inline-flex;align-items:center;gap:5px;
        }
        .actions-item-due{
          display:inline-flex;align-items:center;gap:4px;
          font-size:11.5px;color:#64748b;font-weight:500;
        }
        .actions-meeting-meta-sep{color:#cbd5e1;}
        .actions-meeting-badge{
          font-size:11px;font-weight:700;padding:3px 10px;border-radius:999;
          letter-spacing:0.01em;
        }
        .actions-meeting-badge--open{background:#FEF3C7;color:#92400E;border:1px solid #FDE68A;}
        .actions-meeting-badge--done{background:#E6F4EA;color:#1E8E3E;border:1px solid #B7E1C7;}
        .actions-meeting-badge--all{background:#EEF2FF;color:#4338CA;border:1px solid #C7D2FE;}
        .actions-meeting-chev{
          display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;
          width:36px;height:36px;padding:0;margin:0;border:none;border-radius:10px;
          background:transparent;color:#64748b;cursor:pointer;
          transition:transform .18s ease,background .15s ease;
        }
        .actions-meeting-chev:hover{background:rgba(12,43,78,0.06);}
        .actions-meeting-chev.is-open{transform:rotate(90deg);}
        .actions-meeting-open-btn{
          display:inline-flex;align-items:center;gap:5px;
          padding:8px 14px;border-radius:999;
          font-size:12.5px;font-weight:700;color:#1565C0;
          background:#fff;border:1px solid rgba(21,101,192,0.35);
          cursor:pointer;white-space:nowrap;flex-shrink:0;
          transition:background .15s ease,border-color .15s ease,box-shadow .15s ease;
        }
        .actions-meeting-open-btn:hover{
          background:rgba(21,101,192,0.08);border-color:rgba(21,101,192,0.55);
          box-shadow:0 2px 6px rgba(21,101,192,0.15);
        }
        .actions-meeting-body{
          padding:14px 18px 18px;border-top:1px solid rgba(12,43,78,0.08);
          background:#f8fafc;
        }
        .actions-item-row{
          display:flex;align-items:flex-start;gap:12px;
          padding:13px 16px;background:#fff;
          border:1px solid rgba(12,43,78,0.1);border-radius:12px;
          box-shadow:0 1px 3px rgba(12,43,78,0.04);
        }
        .actions-item-row.is-done{background:#f8fafc;border-color:rgba(12,43,78,0.07);}
        .actions-item-row.is-focus{
          border-color:rgba(23,120,212,0.55)!important;
          box-shadow:0 0 0 3px rgba(23,120,212,0.18),0 4px 14px rgba(23,120,212,0.12);
          background:#F0F7FF;
        }
        .actions-item-text{font-size:14px;color:#0c2b4e;line-height:1.55;word-break:break-word;margin-bottom:5px;}
        .actions-item-text.is-done{color:#64748b;text-decoration:line-through;}
        .meet-skel{
          border-radius:14px;
          min-height:158px;
          background:linear-gradient(90deg,#EEF2F6 0%,#F1F5F9 20%,#E2E8F0 40%,#F1F5F9 60%,#EEF2F6 100%);
          background-size:200% 100%;
          animation:shimmer 1.15s ease-in-out infinite;
          border:1px solid ${T.border};
        }
        .meet-empty{
          text-align:center;padding:48px 24px 40px;
          background:linear-gradient(180deg,rgba(255,255,255,0.7) 0%,${T.surface} 100%);
          border:1px dashed ${T.borderMid};
          border-radius:14px;
        }
        .meet-empty-h{font-size:15px;font-weight:600;color:${T.text};margin:0 0 8px;letter-spacing:-.02em;}
        .meet-empty-p{font-size:13px;color:${T.textMuted};line-height:1.6;max-width:360px;margin:0 auto 16px;}
        .meet-empty-actions{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;}
        .ghost-btn{
          font-family:inherit;font-size:12.5px;font-weight:600;
          padding:8px 14px;border-radius:9px;cursor:pointer;
          border:1.5px solid ${T.border};background:${T.bg};color:${T.textSub};
          transition:background .15s,border-color .15s,color .15s,transform .1s;
        }
        .ghost-btn:hover{border-color:#93C5FD;background:#EFF6FF;color:#1D4ED8;}
        .ghost-btn:focus-visible{outline:2px solid #2563eb;outline-offset:1px;}
        .ghost-btn:active{transform:scale(0.98);}

        .card{
          background:${T.bg};
          border:1px solid rgba(12,43,78,0.09);
          border-radius:${T.radiusLg};
          box-shadow:0 1px 3px rgba(12,43,78,0.07),0 1px 2px rgba(12,43,78,0.04);
        }
        .hover-card{transition:box-shadow .2s ease,border-color .2s ease,transform .2s ease;cursor:pointer;}
        .hover-card:not(.mcard-pro):hover{border-color:rgba(37,99,235,0.3);box-shadow:${T.shadowMd};transform:translateY(-2px);}

        /* ── App topbar — white bar; gray page background starts below ── */
        .app-topbar{
          position:sticky;top:0;z-index:20;flex-shrink:0;box-sizing:border-box;
          min-height:64px;
          display:grid;
          grid-template-columns:minmax(0,auto) minmax(0,1fr) minmax(0,auto);
          align-items:center;
          column-gap:20px;
          padding:0 24px;
          background:#FFFFFF;
          border-bottom:1px solid rgba(12,43,78,0.12);
          box-shadow:0 1px 0 rgba(255,255,255,0.8) inset,0 2px 8px rgba(12,43,78,0.05);
        }
        .app-topbar-brand{
          display:flex;align-items:center;justify-self:start;min-width:0;padding-right:2px;
        }
        .app-topbar-logo{
          display:block;width:auto;height:auto;
          max-height:48px;max-width:196px;min-width:132px;
          object-fit:contain;
        }
        .app-header-nav{
          display:flex;align-items:center;justify-content:center;
          justify-self:center;min-width:0;max-width:100%;
          overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none;
        }
        .app-header-nav::-webkit-scrollbar{display:none;}
        .app-nav-group{
          display:inline-flex;align-items:center;gap:2px;
          padding:5px;border-radius:999px;
          background:#F1F5F9;
          border:1px solid rgba(12,43,78,0.10);
          box-shadow:none;
        }
        .top-nav-btn{
          display:inline-flex;align-items:center;gap:7px;
          padding:10px 17px;border:none;border-radius:999px;cursor:pointer;
          background:transparent;font-size:13px;font-weight:600;color:#64748B;
          letter-spacing:.015em;
          transition:background .16s ease,color .16s ease,transform .12s ease,box-shadow .16s ease;
          white-space:nowrap;line-height:1;position:relative;
        }
        .top-nav-btn:hover:not(.active){
          background:#F1F5F9;
          color:${PALETTE.navy};
          box-shadow:none;
        }
        .top-nav-btn.active{
          color:#FFFFFF;
          background:linear-gradient(135deg,${PALETTE.brandBlue} 0%,#129A6E 52%,${PALETTE.brandGreen} 100%);
          font-weight:700;
          box-shadow:0 2px 14px rgba(23,120,212,0.28),0 1px 4px rgba(12,143,85,0.18),inset 0 1px 0 rgba(255,255,255,0.22);
        }
        .top-nav-btn.active svg{filter:none;}
        .top-nav-btn:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .top-nav-btn:active{transform:scale(0.97);}
        .nav-badge{
          display:inline-flex;align-items:center;justify-content:center;
          min-width:17px;height:17px;padding:0 5px;border-radius:999px;
          background:#EF4444;color:#fff;font-size:10px;font-weight:700;line-height:1;
          margin-left:2px;
          box-shadow:none;
        }
        .top-nav-btn.active .nav-badge{
          background:#FFFFFF;color:#DC2626;
          box-shadow:none;
        }
        .app-toolbar{
          display:flex;align-items:center;justify-content:flex-end;
          gap:8px;flex-wrap:nowrap;justify-self:end;min-width:0;
        }
        .toolbar-sync{
          display:inline-flex;align-items:center;justify-content:center;
          padding:0;margin:0;border:none;background:transparent;box-shadow:none;
          flex-shrink:0;
        }
        .refresh-meta{
          font-size:11.5px;color:#64748b;font-weight:500;
          white-space:nowrap;letter-spacing:.01em;line-height:1.3;
        }
        .refresh-meta strong{color:#334155;font-weight:600;}
        .refresh-btn{
          font-family:inherit;display:inline-flex;align-items:center;justify-content:center;
          width:36px;height:36px;padding:0;margin:0;border:1px solid rgba(12,43,78,0.14);
          border-radius:50%;cursor:pointer;flex-shrink:0;
          background:rgba(255,255,255,0.92);color:#1D4ED8;
          box-shadow:0 1px 2px rgba(12,43,78,0.05),inset 0 1px 0 rgba(255,255,255,0.9);
          transition:background .16s ease,border-color .16s ease,transform .12s ease,box-shadow .16s ease;
        }
        @media (hover:hover) and (pointer:fine){
          .refresh-btn:hover{
            background:linear-gradient(180deg,#ffffff 0%,#F1F6FF 100%);
            border-color:rgba(37,99,235,0.40);
            box-shadow:0 4px 14px rgba(37,99,235,0.18),inset 0 1px 0 rgba(255,255,255,0.95);
            transform:translateY(-1px);
          }
          /* Spin the arrows toward the user so the button previews what it does. */
          .refresh-btn:hover .refresh-icon{transform:rotate(-120deg);}
          .refresh-btn.is-loading:hover .refresh-icon{transform:none;}
        }
        .refresh-btn:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .refresh-btn:active{transform:scale(0.94);}
        .refresh-btn .refresh-icon{display:inline-flex;line-height:0;transition:transform .45s cubic-bezier(.4,0,.2,1);}
        .refresh-btn.is-loading{background:linear-gradient(180deg,#ffffff 0%,#EEF4FF 100%);border-color:rgba(37,99,235,0.34);}
        .refresh-btn.is-loading .refresh-icon{animation:spin .7s linear infinite;}
        .btn-record{
          font-family:inherit;display:inline-flex;align-items:center;justify-content:center;gap:7px;
          min-height:36px;padding:0 14px;
          background:#0875E1;
          border:none;border-radius:8px;cursor:pointer;
          font-size:13px;font-weight:600;color:#fff;letter-spacing:.01em;
          transition:background .15s ease,transform .12s ease;
          box-shadow:none;
          white-space:nowrap;
        }
        .btn-record:hover{
          background:#0662C2;
        }
        .btn-record:focus-visible{outline:2px solid #93c5fd;outline-offset:2px;}
        .btn-record:active{transform:scale(0.97);}
        .btn-outline{
          font-family:inherit;display:inline-flex;align-items:center;justify-content:center;gap:6px;
          min-height:38px;padding:0 15px;
          background:rgba(255,255,255,0.92);color:#0C2B4E;
          border:1px solid rgba(12,43,78,0.14);border-radius:11px;cursor:pointer;
          font-size:13px;font-weight:600;letter-spacing:.01em;
          transition:background .16s ease,border-color .16s ease,transform .12s ease,box-shadow .16s ease;
          box-shadow:0 1px 2px rgba(12,43,78,0.05),inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .btn-outline:hover{
          background:#fff;border-color:rgba(37,99,235,0.28);
          box-shadow:0 2px 10px rgba(12,43,78,0.08);
          color:#1D4ED8;
        }
        .btn-outline:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .btn-outline:active{transform:scale(0.97);}
        .toolbar-divider{
          width:1px;height:26px;background:rgba(12,43,78,0.10);flex-shrink:0;margin:0 2px;
        }
        .user-menu-wrap{position:relative;flex-shrink:0;z-index:2;}
        .user-menu{
          position:absolute;top:calc(100% + 8px);right:0;
          width:280px;min-width:280px;max-width:min(280px,82vw);
          background:#fff;border:1px solid rgba(12,43,78,0.10);border-radius:16px;
          box-shadow:0 4px 8px rgba(12,43,78,0.05),0 22px 52px rgba(12,43,78,0.14);
          z-index:300;overflow:hidden;
          display:flex;flex-direction:column;
          max-height:min(72vh, calc(100vh - 72px));
          animation:userMenuIn .2s cubic-bezier(.22,1,.36,1);
        }
        .user-menu-head{flex-shrink:0;}
        .user-menu-scroll{
          overflow-y:auto;overflow-x:hidden;min-height:0;flex:1;
          -webkit-overflow-scrolling:touch;scrollbar-width:thin;
        }
        .user-menu-scroll::-webkit-scrollbar{width:6px;}
        .user-menu-scroll::-webkit-scrollbar-thumb{background:rgba(148,163,184,0.45);border-radius:999px;}
        @keyframes userMenuIn{
          from{opacity:0;transform:translateY(-8px) scale(0.97);}
          to{opacity:1;transform:translateY(0) scale(1);}
        }
        .user-menu-head{
          display:flex;align-items:center;gap:12px;
          padding:14px 16px 12px;
          background:#F3FAF6;
          border-bottom:1px solid rgba(12,43,78,0.08);
        }
        .user-menu-head-avatar{
          width:40px;height:40px;border-radius:50%;flex-shrink:0;
          background:#16A34A;color:#fff;
          display:flex;align-items:center;justify-content:center;
          font-size:13px;font-weight:700;overflow:hidden;
        }
        .user-menu-head-text{
          min-width:0;flex:1;overflow:hidden;
        }
        .user-menu-head-name{
          font-size:14px;font-weight:700;color:#0B2A4A;line-height:1.25;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;
        }
        .user-menu-head-email{
          font-size:12px;color:#64748b;margin-top:2px;line-height:1.3;font-weight:400;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;
        }
        .user-menu-head-role{
          margin-top:3px;font-size:11px;font-weight:700;color:#16A34A;
          letter-spacing:0.06em;text-transform:uppercase;line-height:1.2;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;
        }
        .user-menu-section{padding:6px 8px;}
        .user-menu-item{
          width:100%;display:flex;align-items:flex-start;gap:12px;
          text-align:left;background:none;border:none;
          padding:10px 10px;font-size:14px;font-weight:600;color:#0B2A4A;
          cursor:pointer;border-radius:10px;font-family:inherit;
          transition:background .14s ease,color .14s ease;
        }
        .user-menu-item:hover{background:#F8FAFC;color:#0B2A4A;}
        .user-menu-item:focus-visible{outline:none;background:#E8EEF7;box-shadow:inset 0 0 0 2px #2563eb;}
        .user-menu-item:active{background:#E2E8F0;}
        .user-menu-item-icon{
          width:22px;height:22px;margin-top:2px;border-radius:0;flex-shrink:0;
          background:transparent;display:flex;align-items:center;justify-content:center;
          color:#64748B;
        }
        .user-menu-item:hover .user-menu-item-icon{background:transparent;}
        .user-menu-item-copy{
          min-width:0;flex:1;display:flex;flex-direction:column;align-items:flex-start;gap:2px;
          overflow:hidden;
        }
        .user-menu-item-title{
          font-size:14px;font-weight:600;color:#0B2A4A;line-height:1.25;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;
        }
        .user-menu-item-sub{
          font-size:12px;font-weight:400;color:#94A3B8;line-height:1.3;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;
        }
        .user-menu-divider{height:1px;background:#E8EEF4;margin:2px 12px 4px;}
        .user-menu-item--admin .user-menu-item-icon{background:transparent;}
        .user-menu-item--admin:hover .user-menu-item-icon{background:transparent;}
        .user-menu-item--danger{color:#EF4444;align-items:center;}
        .user-menu-item--danger .user-menu-item-icon{background:transparent;}
        .user-menu-item--danger .user-menu-item-title{color:#EF4444;font-weight:600;}
        .user-menu-item--danger:hover{background:#FEF2F2;color:#DC2626;}
        .user-menu-item--danger:hover .user-menu-item-icon{background:transparent;}
        .user-menu-item--danger:hover .user-menu-item-title{color:#DC2626;}

        .avatar-menu-btn{
          transition:transform .12s ease,border-color .15s ease;
          box-shadow:none;
          border:none;
          background:${PALETTE.navy};
          color:#fff;
        }
        .avatar-menu-btn.has-photo{
          background:transparent;
          border:1px solid rgba(12,43,78,0.12);
        }
        .avatar-menu-btn:hover{transform:scale(1.03);}
        .avatar-menu-btn.is-open{transform:scale(1.02);}
        .avatar-menu-btn.is-open.has-photo{
          border-color:rgba(12,43,78,0.22);
        }
        .avatar-menu-btn:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .avatar-menu-btn:active{transform:scale(0.97);}
        .header-profile-btn{
          display:inline-flex;align-items:center;gap:8px;
          padding:2px 6px 2px 2px;height:40px;min-height:40px;
          width:148px;min-width:148px;max-width:148px;
          border-radius:12px;border:1px solid transparent;
          background:transparent;color:#0C2B4E;cursor:pointer;
          transition:background .14s ease,border-color .14s ease;
          box-shadow:none;flex-shrink:0;overflow:hidden;
          box-sizing:border-box;
        }
        .header-profile-btn:hover{background:rgba(12,43,78,0.05);}
        .header-profile-btn.is-open{background:rgba(12,43,78,0.07);}
        .header-profile-btn:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .header-profile-btn:active{transform:scale(0.99);}
        .header-profile-avatar{
          width:32px;height:32px;border-radius:50%;flex-shrink:0;
          overflow:hidden;display:flex;align-items:center;justify-content:center;
          background:${PALETTE.navy};color:#fff;font-size:12px;font-weight:600;
        }
        .header-profile-avatar.has-photo{
          background:transparent;border:1px solid rgba(12,43,78,0.12);
        }
        .header-profile-text{
          flex:1 1 auto;width:auto;min-width:0;max-width:100%;
          display:flex;flex-direction:column;align-items:flex-start;gap:1px;
          overflow:hidden;
        }
        .header-profile-name{
          display:block;width:100%;max-width:100%;
          font-size:12px;font-weight:700;color:#0C2B4E;line-height:1.2;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
        }
        .header-profile-role{
          display:block;width:100%;max-width:100%;
          font-size:10.5px;font-weight:400;color:#64748B;line-height:1.25;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
        }

        .tab{
          padding:12px 20px;border:none;background:transparent;cursor:pointer;font-size:13px;font-weight:500;
          color:${T.textMuted};border-bottom:3px solid transparent;border-radius:10px 10px 0 0;
          transition:color .18s ease,background .18s ease,border-color .18s ease,transform .12s ease;
          font-family:inherit;
        }
        .tab:hover:not(.active){color:#0f172a;background:rgba(12,43,78,0.06);}
        .tab.active{
          color:#1D4ED8;
          font-weight:700;
          background:linear-gradient(180deg,#EFF6FF 0%,#DBEAFE 100%);
          border-bottom-color:#2563EB;
          box-shadow:0 3px 12px rgba(37,99,235,0.15);
        }
        .tab:focus-visible{outline:2px solid ${T.accent};outline-offset:-1px;}
        .tab:active{transform:scale(0.99);}

        .seg-group{
          display:flex;gap:4px;align-items:stretch;
          padding:4px;
          background:#F7FAFC;
          border:1px solid #DCE6EF;
          border-radius:11px;
          box-shadow:none;
        }
        .seg-pill{
          padding:8px 14px;border-radius:8px;border:none;cursor:pointer;font-size:12;font-family:inherit;
          font-weight:500;background:transparent;color:#5F7690;
          transition:background .18s ease,color .18s ease,box-shadow .18s ease,transform .12s ease;
        }
        .seg-pill:hover:not(.active){color:#0f172a;background:rgba(255,255,255,0.65);box-shadow:0 1px 2px rgba(12,43,78,0.06);}
        .seg-pill.active{
          font-weight:600;
          color:#fff;
          background:#0875E1;
          box-shadow:0 1px 3px rgba(8,117,225,0.28);
        }
        .seg-pill:focus-visible{outline:2px solid #2563eb;outline-offset:1px;}
        .seg-pill:active{transform:scale(0.96);}
        .seg-pill.cap{text-transform:capitalize;}

        /* Page-level meeting views — compact pill tabs (same language as top nav). */
        .meet-sections-wrap{margin:0 0 14px;}
        .meet-sections{
          display:inline-flex;align-items:center;gap:2px;
          max-width:100%;flex-wrap:wrap;
          padding:4px;
          background:#F1F5F9;
          border:1px solid rgba(12,43,78,0.10);
          border-radius:999px;
        }
        .meet-section-tab{
          display:inline-flex;align-items:center;gap:6px;
          padding:8px 14px;
          border-radius:999px;border:none;
          font-family:inherit;font-size:12.5px;font-weight:600;
          line-height:1;color:#64748B;
          cursor:pointer;background:transparent;white-space:nowrap;
          transition:background .15s ease,color .15s ease,box-shadow .15s ease,transform .12s ease;
        }
        .meet-section-tab:hover:not(.active){
          background:rgba(255,255,255,0.7);
          color:${PALETTE.navy};
        }
        .meet-section-tab.active{
          color:#FFFFFF;
          background:linear-gradient(135deg,${PALETTE.brandBlue} 0%,#129A6E 52%,${PALETTE.brandGreen} 100%);
          box-shadow:0 2px 10px rgba(23,120,212,0.22),inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .meet-section-tab:focus-visible{outline:2px solid #2563EB;outline-offset:2px;}
        .meet-section-tab:active{transform:scale(0.97);}
        @media (max-width:520px){
          .meet-section-tab{padding:7px 10px;font-size:11.5px;gap:5px;}
        }

        /* Sliding tab indicator — shared across nav, meetings sub-tabs, filters. */
        .sliding-tabs-host{position:relative;}
        .sliding-tab-thumb{
          position:absolute;z-index:0;pointer-events:none;
          transition:
            left .32s cubic-bezier(0.34,1.12,0.64,1),
            width .32s cubic-bezier(0.34,1.12,0.64,1),
            top .32s cubic-bezier(0.34,1.12,0.64,1),
            height .32s cubic-bezier(0.34,1.12,0.64,1),
            opacity .18s ease;
        }
        .sliding-tab-thumb--brand{
          border-radius:999px;
          background:linear-gradient(135deg,${PALETTE.brandBlue} 0%,#129A6E 52%,${PALETTE.brandGreen} 100%);
          box-shadow:0 2px 10px rgba(23,120,212,0.22),inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .sliding-tab-thumb--blue{
          border-radius:8px;
          background:#0875E1;
          box-shadow:0 1px 3px rgba(8,117,225,0.28);
        }
        .sliding-tab-thumb--view{
          border-radius:7px;
          background:#0875E1;
          box-shadow:0 1px 3px rgba(8,117,225,0.28);
        }
        .sliding-tab-btn{position:relative;z-index:1;}
        .sliding-tabs-host .meet-section-tab.active,
        .sliding-tabs-host .top-nav-btn.active,
        .sliding-tabs-host .seg-pill.active,
        .sliding-tabs-host .meet-view-toggle-btn.active{
          background:transparent !important;
          box-shadow:none !important;
        }
        .meet-sections.sliding-tabs-host{flex-wrap:nowrap;}

        .howto-mode-seg{
          display:grid;grid-template-columns:1fr 1fr;gap:4px;
          padding:4px;margin-bottom:12px;
          background:#F1F5F9;border:1px solid rgba(12,43,78,0.10);border-radius:12px;
        }
        .howto-mode-tab{
          display:flex;align-items:center;gap:10px;
          padding:12px 14px;border:none;border-radius:9px;cursor:pointer;
          font-family:inherit;text-align:left;background:transparent;
          transition:color .15s ease,transform .12s ease;
          position:relative;z-index:1;
        }
        .howto-mode-tab:hover:not(.active){background:rgba(255,255,255,0.55);}
        .sliding-tab-thumb--howto{
          border-radius:9px;
          background:linear-gradient(165deg,#2563EB 0%,#1D4ED8 100%);
          box-shadow:0 2px 10px rgba(37,99,235,0.38),inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .sliding-tabs-host .howto-mode-tab.active{background:transparent !important;}
        .howto-mode-tab.active .howto-mode-tab-title{color:#FFFFFF;}
        .howto-mode-tab.active .howto-mode-tab-sub{color:rgba(255,255,255,0.88);}
        .howto-mode-tab.active .howto-mode-tab-icon{background:rgba(255,255,255,0.2) !important;}
        .howto-mode-tab:focus-visible{outline:2px solid #2563EB;outline-offset:2px;}
        .howto-mode-tab:active{transform:scale(0.98);}
        .howto-mode-tab-title{font-size:13px;font-weight:600;color:${T.text};}
        .howto-mode-tab-sub{font-size:11.5px;color:${T.textMuted};margin-top:2px;}
        .howto-steps-slide{animation:tabContentSlide .34s cubic-bezier(0.22,0.68,0,1);}
        @keyframes tabContentSlide{
          from{opacity:0;transform:translateX(14px);}
          to{opacity:1;transform:translateX(0);}
        }

        .app-tour-cta{
          margin-bottom:20px;padding:16px 18px;
          background:linear-gradient(165deg,#FFFFFF 0%,#F0F7FF 55%,#E8F2FC 100%);
          border:1px solid #C5D9F0;border-radius:14px;
          display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;
        }
        .app-tour-start-btn{
          font-family:inherit;cursor:pointer;border:none;border-radius:10px;
          padding:11px 18px;font-size:13px;font-weight:700;color:#fff;
          background:linear-gradient(135deg,${PALETTE.brandBlue} 0%,#129A6E 52%,${PALETTE.brandGreen} 100%);
          box-shadow:0 4px 14px rgba(23,120,212,0.28);
          transition:transform .12s ease,filter .15s ease;
        }
        .app-tour-start-btn:hover{filter:brightness(1.05);transform:translateY(-1px);}
        .app-tour-start-btn:focus-visible{outline:2px solid #2563EB;outline-offset:2px;}
        .app-tour-start-btn:active{transform:translateY(0);}
        .app-tour-cta-copy{font-size:12.5px;color:${T.textSub};line-height:1.55;max-width:420px;margin:0;}
        .app-tour-root{position:fixed;inset:0;z-index:10100;pointer-events:none;}
        .app-tour-backdrop--full{
          position:fixed;inset:0;background:rgba(15,23,42,0.58);pointer-events:auto;
        }
        .app-tour-spotlight{
          position:fixed;border-radius:12px;pointer-events:none;z-index:10101;
          box-shadow:0 0 0 9999px rgba(15,23,42,0.58);
          border:2px solid rgba(255,255,255,0.92);
          transition:top .18s ease,left .18s ease,width .18s ease,height .18s ease;
        }
        .app-tour-card{
          pointer-events:auto;
          position:relative;
          background:#fff;border:1px solid #D4DEE8;border-radius:14px;
          padding:14px 16px 12px;
          box-shadow:0 20px 50px rgba(15,23,42,0.22);
          max-height:min(72vh, 440px);
          display:flex;flex-direction:column;
          overflow:auto;
          box-sizing:border-box;
        }
        .app-tour-caret{
          position:absolute;width:12px;height:12px;background:#fff;
          border:1px solid #D4DEE8;transform:rotate(45deg);z-index:0;
          pointer-events:none;
        }
        .app-tour-card[data-tour-placement="right"] .app-tour-caret{
          left:-7px;top:50%;margin-top:-6px;border-right:none;border-top:none;
        }
        .app-tour-card[data-tour-placement="left"] .app-tour-caret{
          right:-7px;top:50%;margin-top:-6px;border-left:none;border-bottom:none;
        }
        .app-tour-card[data-tour-placement="bottom"] .app-tour-caret,
        .app-tour-card[data-tour-placement="bottom-safe"] .app-tour-caret{
          top:-7px;left:50%;margin-left:-6px;border-bottom:none;border-right:none;
        }
        .app-tour-card[data-tour-placement="top"] .app-tour-caret{
          bottom:-7px;left:50%;margin-left:-6px;border-top:none;border-left:none;
        }
        .app-tour-card[data-tour-placement="center"] .app-tour-caret{display:none;}
        .app-tour-card-head{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          margin-bottom:8px;flex-shrink:0;position:relative;z-index:1;
        }
        .app-tour-card-head-right{display:flex;align-items:center;gap:2px;flex-shrink:0;}
        .app-tour-step-pill{
          font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
          color:#475569;background:#F1F5F9;border:1px solid #E2E8F0;
          padding:3px 8px;border-radius:999px;white-space:nowrap;
        }
        .app-tour-skip{
          font-family:inherit;border:none;background:transparent;cursor:pointer;
          font-size:12px;font-weight:600;color:#64748B;padding:4px 8px;border-radius:6px;
          line-height:1.2;
        }
        .app-tour-skip:hover{background:#F1F5F9;color:#0F172A;}
        .app-tour-card-title{
          font-size:15px;font-weight:700;color:#0F172A;margin-bottom:6px;
          letter-spacing:-.02em;line-height:1.35;flex-shrink:0;
        }
        .app-tour-card-body{
          font-size:13px;color:${T.textSub};line-height:1.6;margin:0 0 12px;
          flex:1 1 auto;min-height:0;
        }
        .app-tour-card-hint{font-size:11.5px;color:${T.textMuted};margin:0 0 10px;line-height:1.5;flex-shrink:0;}
        .app-tour-card-actions{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          flex-shrink:0;padding-top:2px;border-top:1px solid #F1F5F9;margin-top:auto;
        }
        .app-tour-card-actions-right{display:flex;gap:8px;margin-left:auto;}
        .app-tour-btn{
          font-family:inherit;cursor:pointer;border-radius:9px;padding:8px 14px;
          font-size:12.5px;font-weight:600;transition:background .15s ease,transform .1s ease;
          white-space:nowrap;
        }
        .app-tour-btn--ghost{
          border:1px solid ${T.border};background:#fff;color:${T.textSub};
        }
        .app-tour-btn--ghost:hover:not(:disabled){background:#F8FAFC;}
        .app-tour-btn--ghost:disabled{opacity:0.45;cursor:not-allowed;}
        .app-tour-btn--primary{
          border:none;background:linear-gradient(165deg,#2563EB 0%,#1D4ED8 100%);color:#fff;
          box-shadow:0 2px 8px rgba(37,99,235,0.35);
          min-width:72px;
        }
        .app-tour-btn--primary:hover{filter:brightness(1.05);}
        .app-tour-card--featured{
          border:2px solid #16a34a;
          box-shadow:0 20px 50px rgba(22,163,74,0.18),0 0 0 1px rgba(22,163,74,0.12);
        }
        .app-tour-featured-badge{
          display:inline-flex;align-items:center;gap:5px;
          font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
          color:#166534;background:#DCFCE7;border:1px solid #86EFAC;
          padding:3px 8px;border-radius:999px;margin-bottom:8px;flex-shrink:0;
        }
        .app-tour-spotlight--featured{
          border:2px solid #22C55E;
          box-shadow:0 0 0 9999px rgba(15,23,42,0.58),0 0 0 4px rgba(34,197,94,0.35);
        }
        @media (max-width:640px){
          .app-tour-card{
            max-height:min(62vh, 380px);
            padding:12px 14px 10px;
          }
          .app-tour-card-title{font-size:14px;}
          .app-tour-card-body{font-size:12.5px;}
          .app-tour-btn{padding:8px 12px;font-size:12px;}
        }

        /* Meeting detail — FLAT join action row (no nested card). Lives directly
           inside the header card so primary CTA owns the page visually. */
        .join-row{
          display:flex;align-items:center;gap:10px;flex-wrap:wrap;
          margin-bottom:18px;
        }
        .btn-join-primary{
          font-family:inherit;cursor:pointer;
          display:inline-flex;align-items:center;gap:8px;
          padding:11px 20px;
          background:linear-gradient(180deg,#2563EB 0%,#1D4ED8 100%);
          color:#FFFFFF;font-size:13.5px;font-weight:700;letter-spacing:.005em;
          border:none;border-radius:10px;
          box-shadow:0 4px 12px rgba(37,99,235,0.30),inset 0 1px 0 rgba(255,255,255,0.18);
          transition:transform .12s ease,box-shadow .18s ease,filter .18s ease;
        }
        .btn-join-primary:hover{
          filter:brightness(1.05);
          box-shadow:0 6px 18px rgba(37,99,235,0.40),inset 0 1px 0 rgba(255,255,255,0.22);
          transform:translateY(-1px);
        }
        .btn-join-primary:focus-visible{outline:2px solid #93C5FD;outline-offset:2px;}
        .btn-join-primary:active{transform:translateY(0) scale(0.98);}
        .btn-join-primary.is-disabled,
        .btn-join-primary:disabled{
          background:linear-gradient(180deg,#94A3B8,#64748B);
          box-shadow:none;cursor:not-allowed;transform:none;filter:none;
        }
        .btn-join-primary.is-disabled:hover,
        .btn-join-primary:disabled:hover{
          filter:none;transform:none;box-shadow:none;
        }
        .btn-icon-copy{
          font-family:inherit;cursor:pointer;
          display:inline-flex;align-items:center;justify-content:center;
          width:38px;height:38px;
          background:#FFFFFF;color:#475569;
          border:1px solid ${T.border};border-radius:10px;
          transition:background .15s ease,border-color .15s ease,transform .12s ease,box-shadow .15s ease;
        }
        .btn-icon-copy:hover{background:#F8FAFC;border-color:${T.borderMid};box-shadow:0 1px 3px rgba(12,43,78,0.08);}
        .btn-icon-copy:focus-visible{outline:2px solid #2563EB;outline-offset:2px;}
        .btn-icon-copy:active{transform:scale(0.94);}
        .join-url-inline{
          display:inline-flex;align-items:center;gap:6px;min-width:0;
          padding:6px 10px;border-radius:8px;
          font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
          font-size:11.5px;color:${T.textMuted};
          text-decoration:none;
          background:transparent;border:1px solid transparent;
          max-width:100%;
          transition:background .15s ease,color .15s ease,border-color .15s ease;
        }
        .join-url-inline span{
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:340px;
        }
        .join-url-inline:hover{background:#F8FAFC;color:${T.textSub};border-color:${T.border};}
        .join-url-inline.is-muted-reference{
          color:#94A3B8;cursor:pointer;
        }
        .join-url-inline.is-muted-reference:hover{
          background:rgba(148,163,184,0.12);color:#64748B;border-color:rgba(148,163,184,0.35);
        }
        /* Meeting detail — attendance chip areas: cap height + scroll */
        .attendance-chip-scroll{
          min-height:0;
          max-height:min(42vh,280px);
          overflow-y:auto;
          overscroll-behavior:contain;
          -webkit-overflow-scrolling:touch;
          border-radius:8px;
          border:1px solid rgba(148,163,184,0.22);
          background:rgba(255,255,255,0.55);
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          align-items:center;
          align-content:flex-start;
          padding:8px 10px;
        }
        @media (max-width: 640px){
          .join-row{flex-direction:column;align-items:stretch;}
          .join-row .btn-join-primary{width:100%;justify-content:center;}
          .join-row .btn-icon-copy{align-self:flex-start;}
          .join-row .join-url-inline{flex:1 1 auto;max-width:100%;}
          .join-row .join-url-inline span{max-width:100%;}
        }

        /* Time-to-meeting hero chip — sits under the status badge, gives the
           single most useful piece of info on a meeting screen. */
        .time-chip{
          display:inline-flex;align-items:center;gap:5px;
          padding:4px 10px;border-radius:999px;
          font-size:11.5px;font-weight:600;letter-spacing:.005em;
          line-height:1;white-space:nowrap;
        }
        .time-chip-dot{
          width:6px;height:6px;border-radius:50%;
          background:#DC2626;
          animation:pulse 1.4s ease infinite;
        }
        .time-chip-upcoming{
          background:#EFF6FF;color:#1D4ED8;border:1px solid #DBEAFE;
        }
        .time-chip-imminent{
          background:#FFF7ED;color:#B45309;border:1px solid #FED7AA;
        }
        .time-chip-live{
          background:#FEE2E2;color:#B91C1C;border:1px solid #FECACA;
        }
        .time-chip-past{
          background:#F1F5F9;color:#475569;border:1px solid ${T.border};
        }

        .bot-join-chip{
          display:inline-flex;align-items:center;gap:6px;
          padding:5px 12px;border-radius:999px;
          font-size:12px;font-weight:700;letter-spacing:.01em;
          line-height:1;white-space:nowrap;
          box-shadow:0 1px 2px rgba(15,23,42,.06);
        }
        .bot-join-chip-joined{
          background:#DCFCE7;color:#15803D;border:1.5px solid #4ADE80;
        }
        .bot-join-chip-missed{
          background:#FEF9C3;color:#A16207;border:1.5px solid #FACC15;
        }

        /* Stacked participant avatars — Google Calendar / Meet style. Replaces
           the loose row of pill chips with one tight visual cluster. */
        .people-stack{
          display:inline-flex;align-items:center;
          padding-left:6px;
        }
        .people-stack-item{
          display:inline-flex;
          margin-left:-8px;
          border-radius:50%;
          background:#FFFFFF;
          box-shadow:0 0 0 2px #FFFFFF;
          transition:transform .15s ease,box-shadow .15s ease,z-index 0s;
          position:relative;z-index:1;
        }
        .people-stack-item:hover{
          transform:translateY(-2px);
          box-shadow:0 0 0 2px #FFFFFF,0 4px 12px rgba(12,43,78,0.15);
          z-index:5;
        }
        .people-stack-item.is-bot{
          box-shadow:0 0 0 2px ${T.accentBg};
        }
        .people-stack-more{
          display:inline-flex;align-items:center;justify-content:center;
          width:30px;height:30px;margin-left:-8px;
          border-radius:50%;
          background:#F1F5F9;color:${T.textSub};
          font-size:11px;font-weight:700;
          box-shadow:0 0 0 2px #FFFFFF;
          cursor:default;
        }

        /* Auto Join footer strip — inside the main card, soft blue accent so it
           feels like a system tip, not a floating disconnected dashed orphan. */
        .autojoin-footer-strip{
          margin-top:18px;
          display:flex;align-items:flex-start;gap:9px;
          padding:10px 14px;
          background:#EFF6FF;
          border:1px solid #DBEAFE;
          border-left:3px solid #2563EB;
          border-radius:8px;
          font-size:12.5px;color:#1E3A8A;line-height:1.5;
        }
        .autojoin-footer-strip strong{color:#1E40AF;font-weight:600;}

        /* Unified toolbar: search + date filter + status filter live in one
           bordered card so they read as a single "filters" zone. */
        .meet-toolbar{
          display:flex;flex-wrap:wrap;align-items:center;gap:10px;
          padding:10px 12px;margin-bottom:16px;
          background:${T.bg};
          border:1px solid ${T.border};
          border-radius:12px;
          box-shadow:0 1px 3px rgba(12,43,78,0.05);
        }
        .meet-toolbar .input{
          background:#F8FAFC;border:1px solid transparent;
          transition:background .15s ease,border-color .15s ease,box-shadow .15s ease;
        }
        .meet-toolbar .input:hover{background:#FFFFFF;border-color:${T.borderMid};}
        .meet-toolbar .input:focus{background:#FFFFFF;border-color:#93C5FD;box-shadow:0 0 0 3px rgba(37,99,235,0.15);}
        .meet-toolbar .date-filter-shell{border-width:1px;background:#FFFFFF;}
        .meet-toolbar .date-filter-shell:hover{background:#FFFFFF;border-color:rgba(8,117,225,0.28);}

        /* Grid/List view toggle — sits in the toolbar, icon-only segments. */
        .meet-view-toggle{
          display:inline-flex;align-items:stretch;gap:2px;padding:3px;
          background:#FFFFFF;border:1px solid #DCE6EF;
          border-radius:10px;flex-shrink:0;
        }
        .meet-view-toggle-btn{
          display:inline-flex;align-items:center;gap:6px;font-family:inherit;cursor:pointer;
          padding:6px 11px;border:none;background:transparent;color:#5F7690;
          font-size:12px;font-weight:600;border-radius:7px;
          transition:background .15s ease,color .15s ease,box-shadow .15s ease;
        }
        .meet-view-toggle-btn:hover:not(.active){color:#0f172a;background:rgba(255,255,255,0.7);}
        .meet-view-toggle-btn.active{
          color:#fff;background:#0875E1;
          box-shadow:0 1px 3px rgba(8,117,225,0.28);
        }
        .meet-view-toggle-btn:focus-visible{outline:2px solid #2563EB;outline-offset:1px;}

        /* List + grid views — grouped by Today / Tomorrow / date sections. */
        .meet-list-wrap,.meet-grid-wrap{display:flex;flex-direction:column;gap:22px;}
        .meet-list-section,.meet-grid-section{display:flex;flex-direction:column;gap:12px;}
        .meet-list-section-title,.meet-grid-section-title{
          display:flex;align-items:center;flex-wrap:wrap;gap:8px;
          padding:0 2px 10px;margin:0;
          border-bottom:1px solid #DDE3EC;
        }
        .meet-grid-section-label{
          font-size:12px;font-weight:700;letter-spacing:.07em;
          text-transform:uppercase;color:#334155;
        }
        .meet-list-section-title .meet-grid-section-date,
        .meet-grid-section-title .meet-grid-section-date{
          font-size:13px;font-weight:500;color:#0F172A;
          letter-spacing:0;text-transform:none;
        }
        .meet-grid-section-count{
          font-size:12px;font-weight:600;letter-spacing:0;text-transform:none;
          color:#475569;margin-left:auto;
          padding:2px 9px;border-radius:999px;
          background:#EEF2F6;border:1px solid #D8DEE8;
        }
        .meet-grid-section .meetings-card-grid{
          display:grid;
          grid-template-columns:repeat(auto-fill,minmax(300px,1fr));
          gap:18px;
        }
        .meet-list-table{
          background:linear-gradient(165deg,#FFFFFF 0%,#F9FBFE 42%,#F4F8FD 100%);
          border:1px solid #D4DEE8;border-radius:12px;
          box-shadow:
            0 1px 2px rgba(15,23,42,0.04),
            0 5px 16px rgba(15,23,42,0.07);
          overflow:hidden;
        }
        .meet-list-row{
          display:grid;
          grid-template-columns: minmax(220px, 2.6fr) 110px minmax(140px, 1.3fr) 130px 100px 132px;
          align-items:center;gap:14px;padding:12px 16px;
          border-bottom:1px solid ${T.border};
          font-size:12.5px;color:${T.text};
          transition:background .12s ease;
        }
        .meet-list-row:last-child{border-bottom:none;}
        .meet-list-head{
          background:#F8FAFC;color:${T.textMuted};
          font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;
          padding-top:10px;padding-bottom:10px;
        }
        .meet-list-row.is-clickable{cursor:pointer;}
        .meet-list-row.is-clickable:hover{background:#F8FAFC;}
        .meet-list-row.is-cancelled{opacity:0.72;}
        .meet-list-cell{min-width:0;display:flex;align-items:center;gap:8px;}
        .meet-list-cell.muted{color:${T.textMuted};}
        .meet-list-cell .truncate{
          min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .meet-list-meeting-icon{
          width:30px;height:30px;border-radius:8px;display:flex;
          align-items:center;justify-content:center;flex-shrink:0;
        }
        .meet-list-meeting-text{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1;}
        .meet-list-meeting-title{
          flex:1;min-width:0;font-weight:700;font-size:11.5px;color:#0F172A;letter-spacing:-0.01em;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;line-height:1.25;
        }
        .meet-list-meeting-title.is-cancelled{text-decoration:line-through;color:${T.textMuted};}
        .meet-list-meeting-sub{
          font-size:11px;color:${T.textMuted};
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .meet-list-meeting-tag{
          font-size:9px;font-weight:700;padding:1px 5px;border-radius:4px;flex-shrink:0;
          letter-spacing:.04em;text-transform:uppercase;
        }
        .meet-list-meeting-tag.google{background:#F4F7FB;color:#4B6075;border:1px solid #DCE6EF;text-transform:uppercase;letter-spacing:.04em;}
        .meet-list-meeting-tag.instant{background:#E3EEFF;color:#1565C0;}
        .meet-list-reschedule-note{
          display:inline-flex;align-items:center;gap:3px;margin-top:2px;
          font-size:9.5px;font-weight:600;line-height:1.35;color:#B45309;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
          max-width:100%;
        }
        .meet-list-reschedule-note .reschedule-chain-arrow,
        .meet-list-chip--reschedule .reschedule-chain-arrow{
          display:inline-flex;align-items:center;justify-content:center;
          opacity:0.92;font-weight:800;font-size:10px;line-height:1;
          padding:0 3px;color:#D97706;flex-shrink:0;
        }
        .meet-list-chip.meet-list-chip--reschedule{
          font-size:8px!important;padding:0 4px!important;line-height:1.25;
          font-weight:600!important;letter-spacing:0;text-transform:none;
          gap:2px!important;border-radius:4px!important;
          width:fit-content!important;max-width:max-content!important;
          flex:0 0 auto!important;align-self:flex-start;
          background:#FFFBEB!important;color:#A16207!important;
          border:1px solid #FDE68A!important;
        }
        .meet-list-meeting-text > .reschedule-chip-tip{align-self:flex-start;}
        .reschedule-chip-tip{position:relative;z-index:1;flex-shrink:0;width:fit-content;max-width:max-content;}
        .reschedule-chip-tip::after{
          content:attr(data-reschedule-tip);
          position:absolute;left:0;top:calc(100% + 5px);
          z-index:120;padding:6px 9px;border-radius:7px;
          background:#422006;color:#FFFBEB;
          font-size:10.5px;font-weight:600;line-height:1.35;
          white-space:nowrap;text-align:left;
          max-width:min(420px,94vw);overflow:hidden;text-overflow:ellipsis;
          box-shadow:0 6px 16px rgba(15,23,42,.2);
          opacity:0;visibility:hidden;pointer-events:none;
          transition:opacity .12s ease,visibility .12s ease;
        }
        .reschedule-chip-tip:hover{z-index:130;}
        .reschedule-chip-tip:hover::after,
        .reschedule-chip-tip:focus-visible::after{
          opacity:1;visibility:visible;
        }
        .meet-list-time-rescheduled{
          color:#B45309!important;font-weight:700!important;
        }
        .meet-list-cell.col-time .meet-list-time-rescheduled,
        .meet-datetime-time.meet-list-time-rescheduled{color:#CA8A04!important;font-weight:700!important;}
        .meeting-schedule-slot-list{display:flex;flex-direction:column;gap:8px;margin:0;padding:0;list-style:none;}
        .meeting-schedule-slot{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          padding:6px 10px;border-radius:8px;border:1px solid transparent;
          transition:background .15s ease,border-color .15s ease,box-shadow .15s ease;
        }
        .meeting-schedule-slot--prior{opacity:0.88;}
        .meeting-schedule-slot--prior:hover{background:transparent!important;border-color:transparent!important;box-shadow:none!important;}
        .meeting-schedule-slot--current{
          padding:8px 10px;background:rgba(254,243,199,0.55);border-color:#FDE68A;
          box-shadow:0 1px 0 rgba(180,83,9,0.06);cursor:default;
        }
        .meeting-schedule-slot--current:hover{
          background:rgba(254,243,199,0.92)!important;border-color:#F59E0B!important;
          box-shadow:0 2px 10px rgba(180,83,9,0.14)!important;
        }
        .meet-list-participants{
          display:inline-flex;align-items:center;gap:6px;
          color:${T.textSub};font-weight:600;
        }
        .meet-list-auto-join-dash{color:${T.textMuted};font-size:13px;}
        .meet-autojoin-outcome{font-family:inherit;transition:filter .15s ease, transform .12s ease, box-shadow .15s ease;}
        .meet-autojoin-outcome--icon{
          width:28px;height:28px;padding:0;border-radius:8px;border:1px solid #E2E8F0;background:#F8FAFC;
          display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
        }
        .meet-autojoin-outcome:hover{filter:brightness(0.97);box-shadow:0 1px 6px rgba(15,23,42,0.12);}
        .meet-autojoin-outcome:active{transform:scale(0.97);}
        .meet-autojoin-outcome:focus-visible{outline:2px solid #2563EB;outline-offset:1px;}
        .meet-list-table--toggles{
          width:100%;min-width:0;max-width:100%;overflow:hidden;
        }
        .meet-list-table--toggles{
          min-width:min(100%, 820px);
        }
        .meet-list-table--toggles .meet-list-row{
          grid-template-columns:
            minmax(0,1.65fr)
            minmax(56px,.58fr)
            minmax(0,.82fr)
            minmax(52px,.62fr)
            minmax(84px,.58fr)
            minmax(108px,.88fr)
            minmax(96px,.82fr);
          gap:10px;min-width:0;width:100%;padding:7px 10px;
        }
        .meet-list-table--toggles .meet-list-head{letter-spacing:.02em;font-size:10px;}
        .meet-list-table--toggles .meet-list-cell{overflow:hidden;min-width:0;}
        .meet-list-table--toggles .col-status{
          overflow:hidden;min-width:0;max-width:100%;
          justify-content:flex-start;isolation:isolate;
        }
        .meet-list-table--toggles .meet-list-status-badge-wrap{
          display:inline-flex;align-items:center;max-width:100%;min-width:0;overflow:hidden;
        }
        .meet-list-table--toggles .meet-list-status-badge-wrap > span{
          max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
          flex-shrink:1;min-width:0;
        }
        .meet-list-cell.col-autojoin-toggle,
        .meet-list-cell.col-video-rec-toggle{
          justify-content:flex-start;min-width:0;overflow:hidden;
          padding-left:2px;box-sizing:border-box;
        }
        .meet-list-rec-badge{
          display:inline-flex;align-items:center;justify-content:center;
          margin-left:6px;padding:1px 5px;border-radius:4px;
          font-size:9px;font-weight:700;letter-spacing:0.04em;
          background:#FEF2F2;color:#B91C1C;border:1px solid #FECACA;
          line-height:1.2;flex-shrink:0;
        }
        .meet-list-table--transcripts .meet-list-row{
          grid-template-columns:minmax(150px,2fr) minmax(104px,1fr) minmax(110px,1.3fr) 76px minmax(210px,1.8fr);
        }
        .meet-list-cell.col-datetime{flex-direction:column;align-items:flex-start;justify-content:center;gap:1px;}
        .meet-datetime-date{font-weight:600;font-size:12px;color:${T.textSub};max-width:100%;}
        .meet-datetime-time{font-size:12px;font-weight:400;color:${T.textSub};max-width:100%;}
        .meet-list-table{container-type:inline-size;}
        .meet-list-head .meet-list-cell{
          min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;
        }
        .meet-list-head .col-participants-head{
          font-size:clamp(8px,1.1cqw,10px);letter-spacing:clamp(0em,.03cqw,.025em);
        }
        .meet-list-cell.col-transcript-actions{
          align-items:center;flex-direction:row;justify-content:flex-end;min-width:0;
        }
        .meet-list-transcript-actions{
          width:auto;max-width:100%;display:inline-flex;align-items:center;justify-content:flex-end;
          flex-wrap:nowrap;flex-direction:row;gap:6px;
        }
        .meet-list-transcript-actions button{max-width:none;flex-shrink:0;min-width:0;width:auto;}
        /* Transcripts: compact "Summary" pill — full action text on hover/focus. */
        .meet-summary-btn{
          display:inline-flex;align-items:center;gap:6px;
          padding:5px 11px;border-radius:999px;
          border:1px solid #E2E8F0;background:#F8FAFC;color:#1D4ED8;
          font-family:inherit;font-size:11px;font-weight:700;cursor:pointer;
          white-space:nowrap;max-width:100%;flex-shrink:1;min-width:0;
          transition:background .15s ease,border-color .15s ease;
        }
        .meet-summary-btn:hover{border-color:#BFDBFE;background:#EFF6FF;}
        .meet-summary-btn:focus-visible{outline:2px solid #2563EB;outline-offset:1px;}
        .meet-summary-btn .meet-summary-full{display:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;}
        .meet-summary-btn .meet-summary-short{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;}
        .meet-summary-btn:hover .meet-summary-full,.meet-summary-btn:focus-visible .meet-summary-full{display:inline;}
        .meet-summary-btn:hover .meet-summary-short,.meet-summary-btn:focus-visible .meet-summary-short{display:none;}
        .meet-video-btn{
          display:inline-flex;align-items:center;gap:6px;
          padding:5px 11px;border-radius:8px;
          border:1px solid #86EFAC;background:#F0FDF4;color:#15803D;
          font-family:inherit;font-size:11px;font-weight:700;cursor:pointer;
          white-space:nowrap;flex-shrink:0;
        }
        .meet-video-btn:hover{background:#E8F8EF;border-color:#6EE7B7;}
        .meet-list-transcript-actions:has(.meet-video-btn) .meet-summary-full{display:none;}
        .meet-list-transcript-actions:has(.meet-video-btn) .meet-summary-short{display:inline;}
        .meet-list-table--transcripts .meet-list-head .col-summary-head{justify-content:flex-end;}
        /* Transcripts: unified small chips (Instant / Google / Large / Rescheduled). */
        .meet-list-chip{
          display:inline-flex;align-items:center;gap:4px;
          font-size:9.5px;font-weight:700;padding:2px 7px;border-radius:999px;
          letter-spacing:.04em;text-transform:uppercase;line-height:1.5;
          flex-shrink:0;white-space:nowrap;max-width:100%;
          overflow:hidden;text-overflow:ellipsis;
        }
        .meet-list-chip--note{text-transform:none;letter-spacing:0;font-weight:600;}
        /* Transcripts: "Meet" + chips share one line under the name. */
        .meet-list-sub-row{display:flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0;}
        .meet-list-tags-row{display:flex;align-items:center;gap:4px;flex-wrap:wrap;min-width:0;}
        /* Transcripts: title is the strongest element; platform sits below in muted grey. */
        .meet-list-table--transcripts .meet-list-meeting-title{font-size:13.5px;font-weight:800;color:#0F172A;}
        .meet-list-table--transcripts .meet-list-meeting-sub{color:#94A3B8;}
        /* Transcripts: compact host (avatar + first name). */
        .meet-host-compact{display:inline-flex;align-items:center;gap:6px;min-width:0;max-width:100%;font-size:12px;font-weight:600;color:#0F172A;}
        .meet-host-compact .meet-host-first{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;}
        /* Transcripts: large meetings stand out. */
        .meet-list-participants--large{color:#0F172A;font-weight:800;}
        /* Transcripts: sort ("Newest") lives inside the date shell — one merged control. */
        .date-filter-shell:has(.transcript-sort-btn-inshell){width:auto;}
        .transcript-sort-btn-inshell{
          display:inline-flex;align-items:center;gap:6px;flex-shrink:0;
          padding:0 12px;background:transparent;border:none;border-left:1px solid ${T.border};
          color:${T.text};font-family:inherit;font-size:12.5px;font-weight:600;
          cursor:pointer;white-space:nowrap;
        }
        .transcript-sort-btn-inshell:hover{background:rgba(37,99,235,0.08);}
        .transcript-sort-btn-inshell:focus-visible{outline:2px solid #2563EB;outline-offset:-2px;}
        .meet-grid-section-count--inline{margin-left:0;}
        /* Invite MeetAI: chip on the right — toggle + label */
        .meet-heading-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;}
        .meet-heading-row h1{margin:0;}
        .meet-invite-chip{
          display:inline-flex;align-items:center;gap:0;
          height:32px;padding:0 12px 0 10px;margin:0;
          border-radius:999px;box-sizing:border-box;
          background:#EFF6FF;border:1px solid #BFDBFE;color:#1D4ED8;
          white-space:nowrap;flex-shrink:0;
        }
        .meet-invite-chip.is-on{
          background:#ECFDF5;border-color:#A7F3D0;color:#047857;
        }
        .meet-invite-chip.is-disabled{opacity:.72;}
        .meet-invite-chip label{
          gap:8px !important;height:100%;margin:0;
          font-size:12px !important;font-weight:700 !important;color:inherit !important;
        }
        /* Per-row Invite MeetAI / Video Rec — compact chips, not bare action toggles */
        .meet-toggle-chip{
          display:inline-flex;align-items:center;min-width:0;
          height:26px;padding:0 8px 0 6px;box-sizing:border-box;
          border-radius:999px;background:#F1F5F9;border:1px solid #E2E8F0;color:#475569;
          white-space:nowrap;
        }
        .meet-toggle-chip.is-on{
          background:#ECFDF5;border-color:#A7F3D0;color:#047857;
        }
        .meet-toggle-chip.is-disabled{opacity:.7;}
        .meet-toggle-chip label{
          gap:6px !important;height:100%;margin:0;
          font-size:11px !important;font-weight:700 !important;color:inherit !important;
        }
        .meet-future-chip{
          display:inline-flex;align-items:center;gap:6px;margin-top:8px;
          font-size:12px;font-weight:700;color:#1D4ED8;
          padding:4px 12px;border-radius:999px;background:#EFF6FF;border:1px solid #BFDBFE;
          white-space:nowrap;
        }
        .meet-transcript-btn-row button{
          flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;
        }
        @media (max-width: 1100px){
          .meet-list-row{
            grid-template-columns:minmax(0,2fr) minmax(0,.72fr) minmax(0,1fr) minmax(0,.75fr) minmax(0,.7fr) minmax(90px,1fr);
            gap:7px;padding:9px 10px;font-size:11.5px;
          }
          .meet-list-table--transcripts .meet-list-row{
            grid-template-columns:minmax(140px,2fr) minmax(96px,1fr) minmax(84px,1fr) 82px minmax(136px,1.6fr);
          }
          .meet-list-table--toggles .meet-list-row{
            grid-template-columns:
              minmax(0,1.55fr)
              minmax(52px,.55fr)
              minmax(0,.78fr)
              minmax(48px,.58fr)
              minmax(80px,.55fr)
              minmax(100px,.85fr)
              minmax(92px,.8fr);
            gap:8px;min-width:0;padding:6px 8px;
          }
          .meet-list-meeting-title{font-size:10.5px;}
          .meet-list-meeting-sub{font-size:10px;}
          .meet-list-meeting-icon{width:26px;height:26px;border-radius:7px;}
          .meet-list-cell{gap:5px;}
          .meet-list-participants{gap:3px;}
        }
        @media (max-width: 880px){
          .meet-list-row{
            grid-template-columns: 1fr;gap:8px;padding:12px;
          }
          .meet-list-table--transcripts .meet-list-row{grid-template-columns:1fr;}
          .meet-list-table--toggles .meet-list-row{
            grid-template-columns:1fr;
            gap:8px;min-width:0;padding:12px;
          }
          .meet-list-table--toggles .meet-list-head{display:none;}
          .meet-list-head{display:none;}
          .meet-list-cell.col-status::before{content:"Status: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-host::before{content:"Host: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-datetime::before{content:"When: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-time::before{content:"Time: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-participants::before{content:"Attendees: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-autojoin-toggle::before{content:"${JOIN_MEET_AI_LABEL}: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-video-rec-toggle::before{content:"Video Rec: ";color:${T.textMuted};font-weight:600;margin-right:4px;}
          .meet-list-cell.col-transcript-actions::before{
            content:"Meeting Summary: ";color:${T.textMuted};font-weight:600;margin-right:4px;
            display:block;width:100%;margin-bottom:6px;
          }
          .meet-list-cell.col-transcript-actions{flex-direction:column;align-items:flex-start;justify-content:center;}
        }

        .icon-btn-t{
          display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:9px;
          border:1.5px solid ${T.border};background:${T.bg};cursor:pointer;padding:0;font-family:inherit;
          transition:border-color .18s ease,background .18s ease,transform .12s ease,box-shadow .18s ease;
        }
        .icon-btn-t:hover{
          border-color:#60A5FA;background:#EFF6FF;
          box-shadow:0 2px 10px rgba(37,99,235,0.22);
          transform:translateY(-2px);
        }
        .icon-btn-t:focus-visible{outline:2px solid ${T.accent};outline-offset:1px;}
        .icon-btn-t:active{transform:translateY(0) scale(0.94);}

        .date-filter-shell{
          position:relative;display:flex;align-items:stretch;flex-shrink:0;width:188px;height:40px;padding:0;overflow:hidden;
          box-sizing:border-box;background:#FFFFFF;
          border:1px solid #DCE6EF;
          border-radius:8px;
          box-shadow:0 1px 2px rgba(12,43,78,0.04);
          transition:border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }
        .date-filter-shell:hover{
          border-color:rgba(8,117,225,0.28);
          box-shadow:0 1px 3px rgba(8,117,225,0.10);
        }
        .date-filter-shell:focus-within{
          border-color:#0875E1;
          box-shadow:0 0 0 3px rgba(8,117,225,0.16), 0 1px 3px rgba(8,117,225,0.10);
        }
        .strip-cal-btn{
          display:flex;align-items:center;justify-content:center;width:40px;height:100%;flex-shrink:0;
          margin:0;padding:0;cursor:pointer;border:none;color:#5F7690;
          border-right:1px solid #DCE6EF;
          background:#F0F4F8;
          transition:background .16s ease, color .16s ease, transform .12s ease;
        }
        .strip-cal-btn.picked{background:rgba(8,117,225,0.12);color:#0875E1;}
        .date-filter-shell:hover .strip-cal-btn{background:#EAF4FC;color:#0875E1;}
        .date-filter-shell:hover .strip-cal-btn.picked{background:rgba(8,117,225,0.18);color:#0875E1;}
        .strip-cal-btn:focus-visible{z-index:1;outline:2px solid #0875E1;outline-offset:-2px;}
        .strip-cal-btn:active{transform:scale(0.94);}
        .strip-cal-btn svg{display:block;flex-shrink:0;}

        .link-back{
          display:inline-flex;align-items:center;gap:5px;border:none;background:transparent;cursor:pointer;color:${T.textSub};
          font-size:13px;margin:0 0 14px;padding:4px 2px;border-radius:6px;font-family:inherit;
          transition:color .12s ease,background .12s ease;
        }
        .link-back:hover{color:${T.text};background:rgba(12,43,78,0.05);}
        .link-back:focus-visible{outline:2px solid ${T.accent};outline-offset:0;}

        .cal-tb-btn{
          font-family:inherit;min-height:2.25rem;height:auto;padding:0.45rem 0.875rem;border:1.5px solid ${T.border};background:${T.bg};border-radius:0.5rem;
          cursor:pointer;font-size:0.8125rem;font-weight:500;color:${T.textSub};
          display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;line-height:1.2;
          transition:border-color .15s ease,background .15s ease,color .15s ease,transform .1s ease;
        }
        .cal-tb-btn:hover{color:${T.text};border-color:${T.accentMid};background:${T.surface};}
        .cal-tb-btn:focus-visible{outline:2px solid ${T.accent};outline-offset:1px;}
        .cal-tb-btn:active{transform:scale(0.98);}
        .cal-tb-create-short{display:none;}
        .cal-tb-create-full{display:inline;}
        @media (max-width:640px){
          .cal-tb-create-full{display:none;}
          .cal-tb-create-short{display:inline;}
        }

        .cal-nav-icon{
          font-family:inherit;min-height:2.5rem;min-width:2.5rem;height:auto;width:auto;aspect-ratio:1;padding:0.4rem;border:1.5px solid ${T.border};background:${T.bg};cursor:pointer;color:${T.textSub};
          display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;
          transition:border-color .15s ease,background .15s ease,transform .1s ease;
        }
        .join-meet-ai-cal-wrap .cal-nav-icon{min-height:2.625rem;min-width:2.625rem;}
        .cal-nav-icon:hover{background:${T.surface};border-color:${T.accentMid};}
        .cal-nav-icon:focus-visible{outline:2px solid ${T.accent};outline-offset:1px;}
        .cal-nav-icon:active{transform:scale(0.95);}
        .cal-nav-split-l{border-right:none;border-radius:0.5rem 0 0 0.5rem;}
        .cal-nav-split-r{border-radius:0 0.5rem 0.5rem 0;margin-left:-1px;}

        .cal-view-seg{
          font-family:inherit;border:none;cursor:pointer;font-size:0.78125rem;font-weight:500;color:#64748b;background:transparent;
          padding:0.35rem 0.75rem;min-height:1.75rem;height:auto;border-radius:0.5rem;
          display:inline-flex;align-items:center;justify-content:center;line-height:1.2;
          transition:background .18s ease,color .18s ease,transform .12s ease,box-shadow .18s ease;
        }
        .cal-view-seg:hover:not(.active){color:#0f172a;background:rgba(255,255,255,0.85);}
        .cal-view-seg.active{
          font-weight:700;color:#fff;
          background:linear-gradient(165deg,#2563EB 0%,#1D4ED8 100%);
          box-shadow:0 2px 8px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.15);
        }
        .cal-view-seg:focus-visible{outline:2px solid ${T.accent};outline-offset:1px;}
        .cal-view-seg:active{transform:scale(0.96);}

        .howto-mode-btn{font-family:inherit;border-radius:12px;cursor:pointer;text-align:left;}
        .howto-mode-btn:hover{transform:translateY(-1px);box-shadow:${T.shadowMd};}
        .howto-mode-btn:focus-visible{outline:2px solid ${T.accent};outline-offset:2px;}
        .howto-mode-btn:active{transform:translateY(0) scale(0.995);}

        .link-accent{
          font-family:inherit;font-size:11;color:${T.accent};background:none;border:none;cursor:pointer;
          padding:4px 8px;border-radius:6px;transition:background .12s ease,color .12s ease;
        }
        .link-accent:hover{background:${T.accentBg};}
        .link-accent:focus-visible{outline:2px solid ${T.accent};outline-offset:0;}

        .mini-cal-nav{
          border:none;background:transparent;cursor:pointer;padding:6px;border-radius:8px;color:${T.textSub};
          width:34px;height:34px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;
          transition:background .12s ease,color .12s ease;
        }
        .mini-cal-nav:hover{background:rgba(12,43,78,0.07);color:${T.text};}
        .mini-cal-nav:focus-visible{outline:2px solid ${T.accent};outline-offset:1px;}

        .cal-dash{
          display:flex;flex-direction:column;gap:clamp(0.75rem,1.8vw,1.25rem);min-height:0;flex:0 0 auto;height:auto;
          width:100%;max-width:100%;min-width:0;box-sizing:border-box;
          container-type:inline-size;container-name:cal-dash;
        }
        .cal-dash-head{
          display:flex;flex-direction:column;align-items:stretch;gap:14px;
          flex-shrink:0;padding:clamp(14px,1.6vw,18px);border-radius:18px;
          background:
            linear-gradient(135deg,rgba(23,120,212,0.10) 0%,rgba(255,255,255,0.92) 42%,rgba(12,143,85,0.08) 100%),
            #fff;
          border:1px solid rgba(12,43,78,0.08);
          box-shadow:0 8px 24px rgba(12,43,78,0.05);
          box-sizing:border-box;
        }
        .cal-dash-head-main{min-width:0;width:100%;}
        .cal-dash-kicker{
          font-size:11px;font-weight:800;letter-spacing:0.16em;text-transform:uppercase;color:${PALETTE.brandBlue};margin:0 0 8px;
        }
        .cal-dash-title-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:0 0 10px;}
        .cal-dash-title{margin:0;font-size:clamp(1.25rem,2.2vw,1.75rem);font-weight:800;letter-spacing:-0.03em;color:${T.text};line-height:1.15;}
        .cal-dash-wk{
          display:inline-flex;align-items:center;padding:5px 11px;border-radius:999px;
          background:rgba(12,43,78,0.08);color:${PALETTE.navy};font-size:11px;font-weight:800;letter-spacing:0.04em;
        }
        .cal-dash-stats{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0;}
        .cal-dash-stat{
          display:inline-flex;align-items:center;gap:6px;padding:6px 11px;border-radius:999px;
          background:rgba(255,255,255,0.88);border:1px solid rgba(12,43,78,0.08);
          font-size:12.5px;font-weight:600;color:${T.textSub};line-height:1.2;white-space:nowrap;
        }
        .cal-dash-stat strong{color:${T.text};font-weight:800;}
        .cal-dash-stat--accent{border-color:rgba(23,120,212,0.22);background:rgba(23,120,212,0.08);color:#0C4A8A;}
        .cal-dash-stat--warn{border-color:rgba(180,83,9,0.22);background:rgba(254,243,199,0.7);color:#92400E;}
        .cal-dash-toolbar{
          display:flex;align-items:center;justify-content:space-between;gap:10px 14px;
          flex-wrap:wrap;width:100%;min-width:0;
        }
        .cal-dash-controls{
          display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-left:0;min-width:0;
        }
        .cal-dash-controls .cal-nav-pair{display:inline-flex;align-items:center;min-height:2.25rem;}
        .cal-dash-view-switch{flex-shrink:0;margin-left:0;}
        .cal-dash-view-switch.seg-group{min-height:2.25rem;height:auto;align-items:center;box-sizing:border-box;}
        .cal-dash-filter-wrap{position:relative;display:inline-flex;align-items:center;min-height:2.25rem;}
        .cal-dash-filter-menu{
          position:absolute;left:0;right:auto;top:calc(100% + 6px);z-index:30;min-width:220px;padding:10px;
          background:#fff;border:1px solid ${T.border};border-radius:12px;box-shadow:${T.shadowMd};
          display:flex;flex-direction:column;gap:6px;box-sizing:border-box;
          max-width:min(280px, calc(100vw - 24px));
        }
        .cal-dash-filter-item{
          display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;
          border:1px solid ${T.border};background:${T.surface};border-radius:10px;padding:8px 10px;
          font-size:12px;font-weight:600;color:${T.textSub};cursor:pointer;text-align:left;
        }
        .cal-dash-filter-item.is-on{border-color:${T.accentMid};background:${T.accentBg};color:${T.text};}
        .cal-dash-body{
          display:grid;grid-template-columns:minmax(16rem,18rem) minmax(0,1fr);
          gap:clamp(0.75rem,1.6vw,1rem);align-items:start;
          flex:0 0 auto;min-height:0;min-width:0;width:100%;
        }
        .cal-dash-side{
          display:flex;flex-direction:column;gap:0.625rem;
          min-width:16rem;max-width:18rem;width:100%;min-height:auto;
          overflow:visible;padding:0;box-sizing:border-box;position:relative;z-index:1;
          flex:0 0 auto;
        }
        .cal-dash-card{
          background:#fff;border:1px solid rgba(12,43,78,0.09);border-radius:14px;
          box-shadow:0 4px 14px rgba(12,43,78,0.04);
          padding:12px;flex-shrink:0;width:100%;max-width:100%;
          box-sizing:border-box;overflow:visible;
        }
        .cal-dash-card--mini{
          padding:0;overflow:hidden;position:relative;
          background:
            linear-gradient(180deg,#FFFFFF 0%,#F8FBFE 100%);
          border:1px solid rgba(23,120,212,0.14);
          box-shadow:
            0 2px 10px rgba(12,43,78,0.05),
            inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .cal-dash-card--overview{padding:10px 12px 12px;flex:0 0 auto;}
        .cal-dash-card-title{
          margin:0 0 8px;font-size:11px;font-weight:800;color:${T.textMuted};
          letter-spacing:0.08em;text-transform:uppercase;
        }
        .cal-dash-mini-head{
          display:flex;align-items:center;justify-content:space-between;gap:6px;
          margin:0;padding:12px 10px 8px;
          background:linear-gradient(180deg,rgba(23,120,212,0.07) 0%,transparent 100%);
        }
        .cal-dash-mini-month{
          font-size:13px;font-weight:800;color:${T.text};text-align:center;flex:1;min-width:0;letter-spacing:-0.02em;
        }
        .cal-dash-mini-jump{
          display:flex;align-items:center;justify-content:center;gap:8px;
          padding:0 10px 8px;
        }
        .cal-dash-mini-today-btn{
          border:1px solid rgba(23,120,212,0.28);background:rgba(23,120,212,0.08);
          color:#0C4A8A;font-size:10.5px;font-weight:800;letter-spacing:0.04em;text-transform:uppercase;
          padding:4px 10px;border-radius:999px;cursor:pointer;font-family:inherit;
          transition:background .15s ease,transform .12s ease,box-shadow .15s ease;
        }
        .cal-dash-mini-today-btn:hover{
          background:rgba(23,120,212,0.14);transform:translateY(-1px);
          box-shadow:0 4px 10px rgba(23,120,212,0.16);
        }
        .cal-dash-mini-today-btn:active{transform:translateY(0) scale(0.98);}
        .cal-dash-mini-grid{
          display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:1px 0;width:100%;
          padding:0 8px 4px;
        }
        .cal-dash-mini-dow{
          text-align:center;font-size:9px;font-weight:800;color:#8A9BB0;
          padding:2px 0 6px;text-transform:uppercase;letter-spacing:0.06em;
        }
        .cal-dash-mini-day{
          position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;
          height:1.875rem;min-height:1.875rem;padding:0;border:none;background:transparent;cursor:pointer;border-radius:0.625rem;
          color:${T.text};font-size:0.72rem;font-weight:600;width:100%;min-width:0;box-sizing:border-box;
          transition:background .14s ease,transform .14s ease;
        }
        .cal-dash-mini-day:hover:not(.is-out){
          background:rgba(23,120,212,0.08);transform:translateY(-1px);
        }
        .cal-dash-mini-day:active:not(.is-out){transform:translateY(0) scale(0.96);}
        .cal-dash-mini-day.is-out{color:#C2CAD4;cursor:default;font-weight:500;opacity:0.72;}
        .cal-dash-mini-day.is-out:hover{background:transparent;transform:none;}
        .cal-dash-mini-day.is-weekend:not(.is-out):not(.is-sel) .cal-dash-mini-num{color:#5B6B7C;}
        .cal-dash-mini-day.is-sel .cal-dash-mini-num{
          background:linear-gradient(145deg,#1778D4 0%,#0C5FA8 55%,#0A4F8C 100%);
          color:#fff;font-weight:800;
          box-shadow:0 4px 12px rgba(23,120,212,0.4), inset 0 1px 0 rgba(255,255,255,0.25);
          animation:calMiniPop .28s ease;
        }
        .cal-dash-mini-day.is-today:not(.is-sel) .cal-dash-mini-num{
          box-shadow:inset 0 0 0 1.5px #1778D4;color:#1778D4;font-weight:800;
          background:rgba(23,120,212,0.08);
        }
        .cal-dash-mini-day.is-today.is-sel .cal-dash-mini-num{
          box-shadow:0 0 0 3px rgba(23,120,212,0.2), 0 4px 12px rgba(23,120,212,0.4);
        }
        .cal-dash-mini-num{
          width:1.5rem;height:1.5rem;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;
          flex-shrink:0;line-height:1;transition:background .14s ease,box-shadow .14s ease,color .14s ease,transform .14s ease;
        }
        .cal-dash-mini-day:hover:not(.is-out):not(.is-sel) .cal-dash-mini-num{
          background:rgba(255,255,255,0.9);transform:scale(1.06);
        }
        .cal-dash-mini-dots{display:none;}
        .cal-dash-mini-dot{display:none;}
        .cal-dash-mini-foot{
          margin:4px 8px 10px;padding:8px 10px;border-radius:10px;
          background:linear-gradient(135deg,rgba(23,120,212,0.08) 0%,rgba(12,143,85,0.07) 100%);
          border:1px solid rgba(23,120,212,0.12);
          display:flex;align-items:center;justify-content:space-between;gap:8px;
        }
        .cal-dash-mini-foot-label{font-size:10.5px;font-weight:700;color:${T.textSub};line-height:1.3;min-width:0;}
        .cal-dash-mini-foot-label strong{display:block;color:${T.text};font-size:11.5px;font-weight:800;margin-bottom:1px;}
        .cal-dash-mini-foot-count{
          flex-shrink:0;font-size:10px;font-weight:800;letter-spacing:0.04em;text-transform:uppercase;
          color:#0C4A8A;background:#fff;border:1px solid rgba(23,120,212,0.18);
          padding:4px 8px;border-radius:999px;white-space:nowrap;
        }
        @keyframes calMiniPop{
          0%{transform:scale(0.82);}
          70%{transform:scale(1.08);}
          100%{transform:scale(1);}
        }
        .cal-dash-metrics{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;}
        .cal-dash-metric{
          padding:8px 10px;border-radius:10px;background:linear-gradient(180deg,#F7FBFF 0%,#EEF5FB 100%);
          border:1px solid rgba(23,120,212,0.12);box-sizing:border-box;
        }
        .cal-dash-metric--green{
          background:linear-gradient(180deg,#F3FBF7 0%,#E8F6EF 100%);
          border-color:rgba(12,143,85,0.14);
        }
        .cal-dash-metric-val{font-size:18px;font-weight:800;letter-spacing:-0.03em;color:${T.text};line-height:1.1;margin:0 0 2px;}
        .cal-dash-metric-label{font-size:10.5px;font-weight:600;color:${T.textMuted};line-height:1.25;margin:0;}
        .cal-dash-overview{font-size:11.5px;line-height:1.45;color:${T.textSub};margin:0;overflow-wrap:anywhere;word-break:break-word;}
        .cal-dash-overview strong{color:${T.text};font-weight:700;}
        .cal-dash-timeline{
          background:#fff;border:1px solid rgba(12,43,78,0.09);border-radius:1.125rem;
          box-shadow:0 6px 20px rgba(12,43,78,0.05);
          display:flex;flex-direction:column;min-width:0;min-height:min(28rem,70vh);height:auto;width:100%;overflow:hidden;
          box-sizing:border-box;position:relative;z-index:1;align-self:stretch;
        }
        .cal-dash-timeline-head{
          flex-shrink:0;padding:16px 20px 14px;border-bottom:1px solid ${T.border};
          display:flex;align-items:center;justify-content:space-between;gap:10px;
        }
        .cal-dash-timeline-head-title{font-size:15px;font-weight:800;color:${T.text};margin:0;}
        .cal-dash-timeline-head-meta{font-size:12px;font-weight:600;color:${T.textMuted};}
        .cal-dash-timeline-scroll{
          flex:1 1 auto;min-height:12rem;max-height:min(60vh,40rem);overflow-y:auto;overflow-x:hidden;
          padding:0.25rem 0.75rem 2.25rem;-webkit-overflow-scrolling:touch;scrollbar-gutter:stable;
          scroll-padding-top:52px;scroll-padding-bottom:16px;scroll-snap-type:y proximity;
        }
        .cal-dash-timeline-scroll::-webkit-scrollbar{width:8px;}
        .cal-dash-timeline-scroll::-webkit-scrollbar-track{background:transparent;margin:6px 0;}
        .cal-dash-timeline-scroll::-webkit-scrollbar-thumb{background:#C5D0DC;border-radius:999px;border:2px solid #fff;}
        .cal-dash-day-group{padding-bottom:8px;margin-bottom:4px;border-bottom:1px solid #F0F3F7;}
        .cal-dash-day-group:last-child{border-bottom:none;margin-bottom:0;padding-bottom:16px;}
        .cal-dash-day-head{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          padding:14px 10px 8px;position:sticky;top:0;background:rgba(255,255,255,0.97);
          backdrop-filter:blur(8px);z-index:2;
        }
        .cal-dash-day-label{font-size:13px;font-weight:700;color:${T.text};}
        .cal-dash-now{display:none;}
        .cal-dash-evt{
          display:grid;grid-template-columns:76px 4px minmax(0,1fr) auto auto;gap:12px;align-items:center;
          padding:11px 12px;margin:0 2px 8px;border-radius:12px;cursor:pointer;transition:background .12s ease;
          width:auto;box-sizing:border-box;min-width:0;
          scroll-snap-align:start;scroll-margin-top:52px;scroll-margin-bottom:8px;
        }
        .cal-dash-day-group .cal-dash-evt:last-child{margin-bottom:2px;}
        .cal-dash-evt:hover{background:#F5F8FB;}
        .cal-dash-evt-time{font-size:12.5px;font-weight:600;color:${T.textMuted};font-variant-numeric:tabular-nums;}
        .cal-dash-evt-bar{width:4px;align-self:stretch;min-height:38px;border-radius:999px;}
        .cal-dash-evt-title{font-size:14px;font-weight:700;color:${T.text};line-height:1.35;margin:0 0 3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .cal-dash-evt-meta{font-size:12px;color:${T.textMuted};line-height:1.4;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .cal-dash-evt-tag{flex-shrink:0;font-size:11px;font-weight:700;letter-spacing:0.01em;padding:4px 10px;border-radius:8px;text-transform:none;white-space:nowrap;}
        .cal-dash-evt-tags{display:inline-flex;align-items:center;gap:6px;flex-shrink:0;min-width:0;}
        .cal-join-pill{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:700;padding:3px 9px;border-radius:999px;white-space:nowrap;flex-shrink:0;}
        .cal-join-pill--joined{background:#F0FDF4;color:#15803D;border:1px solid #86EFAC;}
        .cal-join-pill--not{background:#F8FAFC;color:#64748B;border:1px solid #E2E8F0;}
        .cal-join-pill--missed{padding:3px 8px;gap:5px;border-radius:999px;}
        .cal-join-pill--joining{background:#FFFBEB;color:#B45309;border:1px solid #FDE68A;}
        .cal-join-pill--nolink{background:#FEF2F2;color:#B91C1C;border:1px dashed #FCA5A5;}
        .cal-join-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
        .cal-dash-evt-toggle{flex-shrink:0;display:flex;flex-direction:column;align-items:flex-end;justify-content:center;gap:6px;}
        .cal-dash-empty{padding:20px 14px 8px;text-align:left;color:${T.textMuted};font-size:13px;}
        .gmail-page-flat .cal-dash-page{
          height:auto;min-height:100%;min-width:0;width:100%;display:flex;flex-direction:column;
          background:
            radial-gradient(900px 320px at 0% -10%,rgba(23,120,212,0.10),transparent 55%),
            radial-gradient(700px 280px at 100% 0%,rgba(12,143,85,0.08),transparent 50%),
            #F3F6FA;
        }
        .gmail-page-flat .content-well.cal-dash-well{
          flex:0 0 auto;min-height:0;min-width:0;width:100%;display:flex;flex-direction:column;
          overflow:visible;box-sizing:border-box;margin:0;
          padding:clamp(1rem,2.2vw,1.5rem) !important;
          padding-bottom:clamp(4rem,8vh,6.5rem) !important;
          border:none !important;border-radius:0 !important;box-shadow:none !important;
          background:transparent !important;
        }
        @container cal-dash (max-width: 54rem){
          .cal-dash-body{grid-template-columns:1fr;overflow:visible;gap:clamp(0.75rem,2vw,1rem);}
          .cal-dash-side{min-width:0;max-width:none;width:100%;overflow:visible;flex:0 0 auto;}
          .cal-dash-timeline{min-height:16rem;height:auto;max-height:none;}
          .cal-dash-timeline-scroll{max-height:none;min-height:0;}
        }
        /* Stack only on narrow screens — never on height alone (large Display Size
           shrinks CSS viewport height and was wrongly removing the side panel). */
        @media (max-width:1100px){
          .cal-dash-body{
            grid-template-columns:1fr;min-height:0;overflow:visible;gap:1rem;
          }
          .cal-dash-side{
            min-width:0;max-width:none;width:100%;overflow:visible;gap:0.875rem;flex:0 0 auto;
            display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,0.85fr);align-items:stretch;
          }
          .cal-dash-card--mini,.cal-dash-card--overview{min-width:0;height:100%;}
          .cal-dash-timeline{min-height:18rem;height:auto;max-height:none;border-radius:1rem;}
          .cal-dash-timeline-scroll{max-height:none;min-height:12rem;}
          .cal-dash-title{font-size:clamp(1.15rem,3.5vw,1.5rem);}
          .cal-dash-toolbar{gap:10px;}
          .cal-dash-view-switch{margin-left:0;width:100%;justify-content:stretch;}
          .cal-dash-view-switch .cal-view-seg{flex:1 1 0;text-align:center;}
          .gmail-page-flat .content-well.cal-dash-well{
            overflow:visible;padding:clamp(0.875rem,3vw,1.25rem) !important;
            padding-bottom:clamp(4rem,10vh,6.5rem) !important;
          }
          .gmail-page-flat .cal-dash-page{height:auto;min-height:100%;}
          .cal-dash{gap:0.875rem;height:auto;}
        }
        /* Desktop / large system display: always keep mini-calendar side + timeline. */
        @media (min-width:1101px){
          .cal-dash-body{
            display:grid !important;
            grid-template-columns:minmax(16rem,18rem) minmax(0,1fr) !important;
            align-items:start !important;
          }
          .cal-dash-side{
            display:flex !important;
            flex-direction:column !important;
            min-width:16rem !important;
            max-width:18rem !important;
            width:100% !important;
            overflow:visible !important;
            flex:0 0 auto !important;
          }
          .cal-dash-card--mini{display:block !important;}
          .cal-dash-timeline{
            min-height:min(28rem,70vh);
            height:auto;
          }
        }
        /* Tablet phone-width: single-column mini-cal + overview, then timeline. */
        @media (max-width:768px){
          .cal-dash-side{grid-template-columns:1fr !important;gap:0.75rem;}
          .cal-dash-head{padding:14px;gap:12px;border-radius:14px;}
          .cal-dash-kicker{margin-bottom:6px;font-size:10px;}
          .cal-dash-title-row{margin-bottom:8px;gap:8px;}
          .cal-dash-stats{gap:6px;}
          .cal-dash-stat{font-size:11.5px;padding:5px 9px;}
          .cal-dash-controls{width:100%;gap:6px;}
          .cal-dash-toolbar{align-items:stretch;flex-direction:column;}
          .cal-dash-view-switch{width:100%;margin-left:0;}
          .cal-dash-filter-menu{
            left:0;right:auto;max-width:calc(100vw - 24px);min-width:0;
            width:min(260px,calc(100vw - 24px));
          }
          .cal-dash-head,.cal-dash-controls{overflow:visible;}
          .cal-dash-mini-day{height:2.25rem;min-height:2.25rem;}
          .cal-dash-mini-num{width:1.75rem;height:1.75rem;font-size:0.8rem;}
          .cal-dash-mini-dow{font-size:10px;padding-bottom:8px;}
          .cal-dash-timeline-head{padding:14px 14px 12px;flex-wrap:wrap;}
          .cal-dash-evt{
            grid-template-columns:52px 3px minmax(0,1fr);gap:8px 10px;
            padding:10px;margin:0 0 8px;border:1px solid rgba(12,43,78,0.06);
            background:#F8FAFC;
          }
          .cal-dash-evt:hover{background:#F1F5F9;}
          .cal-dash-evt-tag{grid-column:3;justify-self:start;margin-top:0;}
          .cal-dash-evt-tags{grid-column:3;justify-self:start;flex-wrap:wrap;}
          .cal-dash-evt-toggle{grid-column:3;justify-self:start;margin-top:2px;}
          .cal-dash-evt-title{white-space:normal;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;}
          .cal-dash-evt-meta{white-space:normal;}
          .cal-dash-metrics{grid-template-columns:1fr 1fr;}
          .gmail-page-flat .content-well.cal-dash-well{padding:12px !important;padding-bottom:4.5rem !important;}
          .cal-dash{gap:12px;}
          .cal-tb-btn,.cal-nav-icon{min-height:2.5rem;}
        }
        @media (max-width:640px){
          .cal-dash-metrics{grid-template-columns:1fr 1fr;gap:8px;}
          .cal-dash-stat--warn{order:3;}
          .cal-dash-card--overview{padding:10px 12px;}
          .cal-dash-mini-foot{flex-wrap:wrap;}
          .cal-dash-timeline-head-title{font-size:14px;}
          .cal-dash-timeline-head-meta{font-size:11px;width:100%;}
        }
        @media (max-width:480px){
          .cal-dash-title{font-size:clamp(1.05rem,5.5vw,1.35rem);}
          .cal-dash-wk{font-size:10px;padding:4px 8px;}
          .cal-dash-stat{font-size:11px;padding:5px 8px;}
          .cal-dash-controls .cal-tb-btn{padding:0.4rem 0.65rem;font-size:0.75rem;}
          .cal-dash-evt-time{font-size:11px;}
          .cal-dash-empty{padding:16px 10px;font-size:12.5px;}
        }
        /* Short desktop viewport (Display Size Large): keep side layout, tighten. */
        @media (min-width:1101px) and (max-height:720px){
          .cal-dash{gap:0.65rem;}
          .cal-dash-head{padding:12px 14px;gap:10px;}
          .cal-dash-timeline{min-height:min(18rem,52vh);}
          .cal-dash-timeline-scroll{max-height:min(48vh,26rem);min-height:10rem;}
          .cal-dash-card--overview{padding:8px 10px;}
          .cal-dash-metric-val{font-size:16px;}
        }

        .cal-gmail-layout{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(300px,360px);
          gap:0;
          align-items:stretch;
          min-height:calc(100vh - 220px);
          border:1px solid ${T.border};
          border-radius:0;
          overflow:hidden;
          background:#fff;
        }
        .cal-gmail-inbox{
          display:flex;
          flex-direction:column;
          min-width:0;
          border-right:1px solid ${T.border};
          background:#fff;
        }
        .cal-gmail-inbox-scroll{
          flex:1;
          overflow-y:auto;
          overflow-x:hidden;
          -webkit-overflow-scrolling:touch;
        }
        .cal-gmail-row{
          display:grid;
          grid-template-columns:36px minmax(140px,1fr) minmax(0,2fr) 72px;
          gap:8px;
          align-items:center;
          padding:10px 14px;
          border-bottom:1px solid #f0f0f0;
          cursor:pointer;
          transition:background .12s ease,box-shadow .12s ease;
        }
        .cal-gmail-row:hover{background:#f5f7fa;}
        .cal-gmail-row.is-selected{background:#e8f0fe;box-shadow:inset 3px 0 0 #1a73e8;}
        .cal-gmail-row.is-sel-day{background:#f8fbff;}
        .cal-gmail-side{
          display:flex;
          flex-direction:column;
          min-width:0;
          min-height:0;
          height:100%;
          background:#fff;
          max-height:calc(100vh - 220px);
        }
        .cal-gmail-side-head{
          flex-shrink:0;
          padding:14px 16px 10px;
          border-bottom:1px solid ${T.border};
        }
        .cal-gmail-side-head-top{
          display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;
        }
        .cal-gmail-side-close{
          width:32px;height:32px;border:none;border-radius:50%;background:transparent;
          cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;
          transition:background .15s ease;
        }
        .cal-gmail-side-close:hover{background:rgba(60,64,67,0.08);}
        .cal-gmail-create-evt{
          display:flex;align-items:center;gap:10px;width:calc(100% + 32px);
          margin:10px -16px -10px;padding:12px 16px;border:none;
          border-top:1px solid ${T.border};background:#fff;
          color:#1a73e8;font-size:14px;font-weight:500;cursor:pointer;
          text-align:left;transition:background .15s ease;
        }
        .cal-gmail-create-evt:hover{background:#f8fbff;}
        .cal-gmail-create-evt-icon{
          width:22px;height:22px;border-radius:8px;
          display:inline-flex;align-items:center;justify-content:center;
          font-size:0;line-height:0;font-weight:500;color:#1a73e8;background:#eaf3ff;
        }
        .cal-gmail-side-scroll{
          flex:1 1 auto;
          min-height:0;
          overflow-y:auto;
          overflow-x:hidden;
          padding:8px 12px 16px;
          -webkit-overflow-scrolling:touch;
          scroll-snap-type:y proximity;
        }
        .cal-gmail-day-label{
          font-size:12px;
          font-weight:700;
          color:#3c4043;
          padding:12px 4px 8px;
          scroll-snap-align:start;
        }
        .cal-gmail-evt{
          border:1px solid rgba(23,120,212,0.22);
          border-left:3px solid #1778D4;
          border-radius:12px;
          padding:10px 12px;
          margin-bottom:8px;
          background:linear-gradient(180deg,#f7fbff 0%,#eaf4fc 55%,#f5fafd 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.9),
            inset 0 -4px 10px rgba(23,120,212,0.05);
          cursor:pointer;
          transition:background .15s ease,border-color .15s ease,box-shadow .15s ease;
        }
        .cal-gmail-evt:hover{
          background:linear-gradient(180deg,#eef7ff 0%,#dceefb 55%,#eef6fc 100%);
          border-color:rgba(23,120,212,0.35);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.95),
            inset 0 -6px 12px rgba(23,120,212,0.08);
        }
        .cal-gmail-evt.is-active{
          background:linear-gradient(135deg,#1778D4 0%,#0C8F55 100%);
          border-color:#0C8F55;color:#fff;
          border-left-color:#fff;
          box-shadow:inset 0 2px 8px rgba(12,43,78,0.22);
        }
        .cal-gmail-evt.is-active .cal-gmail-evt-time{color:rgba(255,255,255,0.92);}
        .cal-gmail-evt-title{font-size:13px;font-weight:600;color:#0C2B4E;line-height:1.35;}
        .cal-gmail-evt.is-active .cal-gmail-evt-title{color:#fff;}
        .cal-gmail-evt-time{font-size:12px;color:#1778D4;margin-top:4px;font-weight:600;}
        .cal-gmail-evt.is-live{
          border-left-color:#0C8F55;
          background:linear-gradient(180deg,#f4fcf8 0%,#e6f6ef 55%,#f2faf6 100%);
        }
        .cal-gmail-evt.is-live .cal-gmail-evt-time{color:#0C8F55;}
        .cal-gmail-evt.is-live:not(.is-active):hover{
          background:linear-gradient(180deg,#eaf9f1 0%,#d8f0e4 55%,#eaf7f0 100%);
          border-color:rgba(12,143,85,0.35);
        }
        .cal-gmail-now-line{
          display:flex;
          align-items:center;
          gap:0;
          margin:6px 0 10px;
          height:12px;
        }
        .cal-gmail-now-line::before{
          content:"";
          width:10px;height:10px;border-radius:50%;background:#ea4335;flex-shrink:0;
        }
        .cal-gmail-now-line::after{
          content:"";
          flex:1;height:2px;background:#ea4335;
        }
        @media (max-width: 1024px){
          .cal-gmail-layout{grid-template-columns:1fr;min-height:auto;}
          .cal-gmail-inbox{border-right:none;border-bottom:1px solid ${T.border};max-height:50vh;}
          .cal-gmail-side{max-height:50vh;}
        }
        @media (max-width: 640px){
          .cal-gmail-row{grid-template-columns:28px minmax(0,1fr) 56px;gap:6px;padding:10px 10px;}
          .cal-gmail-row-snippet{display:none;}
        }

        .gmail-app-shell{
          display:flex;flex-direction:column;height:100vh;overflow:hidden;
          background:#F3F8FC;
          width:100%;max-width:100vw;min-width:0;
          box-sizing:border-box;
        }
        /* Mobile Chrome/Safari report 100vh as the height with the toolbar
           retracted, so the bottom of the shell sits under the browser chrome.
           dvh tracks the actually-visible area; the vh above stays as fallback. */
        @supports (height: 100dvh){
          .gmail-app-shell{height:100dvh;}
        }
        .gmail-topbar{
          display:grid;
          grid-template-columns:minmax(280px,1fr) minmax(280px,560px) minmax(220px,1fr);
          align-items:center;
          gap:12px;
          min-height:56px;padding:6px 16px 6px 0;
          background:#FFFFFF;
          border-bottom:1px solid #DCE6EF;flex-shrink:0;
          position:relative;z-index:200;overflow:visible;
          box-shadow:0 1px 3px rgba(12,43,78,0.06);
        }
        .gmail-topbar::before{
          content:"";
          position:absolute;
          left:232px;top:0;bottom:0;width:1px;
          background:#DCE6EF;
          pointer-events:none;z-index:3;
          transition:left .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-topbar.is-nav-collapsed::before{left:72px;}
        .gmail-topbar.is-user-menu-open{z-index:1400;}
        .gmail-topbar-brand{
          display:flex;align-items:center;gap:8px;flex-shrink:0;min-width:0;
          justify-self:start;
        }
        .gmail-topbar-logo{
          display:flex;align-items:center;justify-content:center;
          flex-shrink:0;padding:0 20px;box-sizing:border-box;
          width:232px;min-width:232px;max-width:232px;
          border:none;background:transparent;cursor:pointer;
          font:inherit;color:inherit;
          transition:width .32s cubic-bezier(0.4,0,0.2,1),min-width .32s cubic-bezier(0.4,0,0.2,1),max-width .32s cubic-bezier(0.4,0,0.2,1),padding .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-topbar-logo:focus-visible{
          outline:2px solid #0875E1;outline-offset:2px;border-radius:8px;
        }
        .gmail-topbar.is-nav-collapsed .gmail-topbar-logo{
          width:72px;min-width:72px;max-width:72px;padding:0 8px;justify-content:center;
        }
        .gmail-topbar-logo img,
        .gmail-topbar-logo .app-topbar-logo{
          display:block;width:auto;height:auto;
          max-height:28px !important;max-width:108px !important;min-width:0 !important;
          object-fit:contain;
        }
        .gmail-topbar.is-nav-collapsed .gmail-topbar-logo img,
        .gmail-topbar.is-nav-collapsed .gmail-topbar-logo .app-topbar-logo{
          max-height:22px !important;max-width:52px !important;
        }
        .gmail-topbar-brand .gmail-menu-btn{margin-right:0;}
        .gmail-topbar-vdivider{
          height:26px !important;align-self:center;margin:0 6px;border-color:#DCE6EF;
        }
        .gmail-search-wrap{
          width:100%;min-width:0;max-width:560px;margin:0 auto;position:relative;
          justify-self:center;
        }
        .gmail-search{
          width:100%;height:40px;border-radius:20px;border:1px solid #DCE6EF;
          background:#F7FAFC;padding:0 72px 0 44px;font-size:13.5px;color:#0B2A4A;
          outline:none;
          transition:border-color .15s ease,box-shadow .15s ease,background .15s ease;
        }
        .gmail-search:focus{
          background:#fff;border-color:rgba(23,120,212,0.3);
          box-shadow:0 0 0 2px rgba(23,120,212,0.1);
        }
        .gmail-search-icon{position:absolute;left:18px;top:50%;transform:translateY(-50%);pointer-events:none;}
        .gmail-search-kbd{
          position:absolute;right:14px;top:50%;transform:translateY(-50%);
          display:inline-flex;align-items:center;gap:3px;
          padding:2px 7px;border-radius:6px;
          font-size:11px;font-weight:600;line-height:1.35;
          color:#64748B;background:rgba(255,255,255,0.85);
          border:1px solid rgba(15,23,42,0.10);
          pointer-events:none;user-select:none;font-family:inherit;
        }
        .gmail-topbar-actions{
          display:flex;align-items:center;gap:8px;flex-shrink:0;margin-left:0;
          justify-self:end;
          position:relative;z-index:201;overflow:visible;
        }
        .gmail-topbar-utils{
          display:flex;align-items:center;gap:6px;flex-shrink:0;
        }
        .gmail-topbar-icon-btn{
          width:36px;height:36px;padding:0;border-radius:50%;line-height:1;
          display:inline-flex;align-items:center;justify-content:center;
          flex-shrink:0;color:#0875E1;cursor:pointer;
          border:1px solid #DCE6EF;background:#F7FAFC;
          position:relative;
          transition:background .16s ease,border-color .16s ease,color .16s ease,transform .12s ease,box-shadow .16s ease;
        }
        .gmail-topbar-icon-btn:hover{
          background:linear-gradient(180deg,#ffffff 0%,#F1F6FF 100%);
          border-color:rgba(37,99,235,0.40);
          box-shadow:0 4px 14px rgba(37,99,235,0.18),inset 0 1px 0 rgba(255,255,255,0.95);
          transform:translateY(-1px);
          color:#1D4ED8;
        }
        .gmail-topbar-icon-btn.is-active{
          background:rgba(23,120,212,0.12);border-color:rgba(23,120,212,0.35);color:#0C4A8A;
        }
        .gmail-topbar-icon-btn:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .gmail-topbar-icon-btn--bell-dot::after{
          content:"";position:absolute;top:7px;right:8px;
          width:7px;height:7px;border-radius:50%;
          background:#EF4444;border:1.5px solid #fff;
        }
        .gmail-topbar-text-btn{
          padding:6px 12px;font-size:13px;font-weight:500;border-radius:8px;
          white-space:nowrap;
        }
        .gmail-howto-btn{
          width:36px;height:36px;padding:0;border-radius:50%;line-height:1;
          display:inline-flex;align-items:center;justify-content:center;
          flex-shrink:0;color:#0875E1;
          transition:background .16s ease,border-color .16s ease,color .16s ease,transform .12s ease,box-shadow .16s ease;
        }
        @media (hover:hover) and (pointer:fine){
          .gmail-howto-btn:hover{
            background:linear-gradient(180deg,#ffffff 0%,#F1F6FF 100%);
            border-color:rgba(37,99,235,0.40);
            box-shadow:0 4px 14px rgba(37,99,235,0.18),inset 0 1px 0 rgba(255,255,255,0.95);
            transform:translateY(-1px);
            color:#1D4ED8;
          }
        }
        .gmail-howto-btn.is-active{
          background:rgba(23,120,212,0.12);border-color:rgba(23,120,212,0.35);color:#0C4A8A;
        }
        .gmail-instant-join-btn{flex-shrink:0;}
        @media (max-width:720px){
          .gmail-instant-join-btn{width:36px;min-width:36px;padding:0;border-radius:50%;}
          .gmail-instant-join-btn .gmail-instant-join-label{display:none;}
        }
        .gmail-app-body{display:flex;flex:1;min-height:0;min-width:0;overflow:hidden;position:relative;z-index:1;}
        .gmail-sidebar{
          width:232px;flex:0 0 232px;flex-shrink:0;min-width:232px;
          background:#EEF6FC;
          border-right:1px solid #DCE6EF;
          display:flex;flex-direction:column;
          padding:0;overflow:hidden;
          position:relative;
          z-index:2;
          transition:width .32s cubic-bezier(0.4,0,0.2,1),min-width .32s cubic-bezier(0.4,0,0.2,1),flex-basis .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar.is-nav-width-animating .gmail-sidebar-content{pointer-events:none;}
        .gmail-sidebar--collapsed{
          width:72px;flex:0 0 72px;min-width:72px;padding:0;
        }
        .sidebar-renewable-illustration{
          position:absolute;left:0;right:0;bottom:0;
          width:100%;height:44%;
          opacity:0.16;pointer-events:none;user-select:none;z-index:0;
          display:block;object-fit:cover;object-position:center bottom;
          border:none;outline:none;background:transparent;
        }
        .gmail-sidebar--collapsed .sidebar-renewable-illustration{
          display:none;
        }
        .gmail-sidebar-content{
          position:relative;z-index:1;
          display:flex;flex-direction:column;flex:1 1 auto;min-height:0;height:100%;
          padding:12px 10px 10px;overflow-y:auto;overflow-x:hidden;
          transition:padding .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar--collapsed .gmail-sidebar-content{padding:8px 0 10px;}
        .gmail-sidebar--collapsed .gmail-nav-list{
          align-items:center;width:100%;
        }
        .gmail-sidebar-foot{
          margin-top:auto;padding:8px 4px 4px;flex-shrink:0;
          position:relative;z-index:2;
        }
        .gmail-nav-list{
          display:flex;flex-direction:column;gap:0;flex:0 0 auto;min-height:0;width:100%;
          padding-bottom:12px;
        }
        .gmail-menu-btn{
          border:none;background:transparent;cursor:pointer;
          display:inline-flex;align-items:center;justify-content:center;
          color:#5f6368;border-radius:50%;
          transition:background .15s ease,color .15s ease;
        }
        .gmail-menu-btn{
          width:40px;height:40px;flex-shrink:0;margin-right:4px;color:#0875E1;
          border:none;background:transparent;border-radius:0;
        }
        .gmail-menu-btn:hover{background:rgba(8,117,225,0.08);border:none;color:#0875E1;}
        .gmail-sidebar-user{
          display:flex;align-items:center;gap:10px;width:100%;
          padding:8px 10px;border-radius:12px;cursor:default;
          transition:background .15s ease,width .32s cubic-bezier(0.4,0,0.2,1),padding .32s cubic-bezier(0.4,0,0.2,1),gap .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar-user:hover{background:rgba(11,42,74,0.06);}
        .gmail-sidebar-user-copy{
          display:flex;flex-direction:column;min-width:0;overflow:hidden;
          max-width:150px;opacity:1;
          transition:opacity .2s ease,max-width .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar-user-name{
          font-size:13px;font-weight:600;color:#0B2A4A;line-height:1.3;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
        }
        .gmail-sidebar-user-role{
          font-size:11px;font-weight:500;color:#5f6368;line-height:1.3;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;letter-spacing:.02em;
        }
        /* Collapsed: keep the avatar visible (small), hide only the text */
        .gmail-sidebar--collapsed .gmail-sidebar-user{
          justify-content:center;padding:8px 0;gap:0;
        }
        .gmail-sidebar--collapsed .gmail-sidebar-user:hover{
          background:transparent;
        }
        .gmail-sidebar--collapsed .gmail-sidebar-user-copy{
          max-width:0;opacity:0;pointer-events:none;
        }
        .gmail-compose{
          display:inline-flex;align-items:center;justify-content:center;gap:10px;
          width:calc(100% - 8px);margin:4px 4px 20px;padding:0 20px;height:58px;border-radius:14px;
          border:none;
          background:linear-gradient(90deg,#05B887 0%,#0875E1 100%);
          color:#fff;font-size:14px;font-weight:700;letter-spacing:0.01em;
          cursor:pointer;
          box-shadow:0 8px 18px rgba(8,117,225,0.22),0 2px 6px rgba(5,184,135,0.16);
          transition:transform .15s ease,box-shadow .2s ease,filter .15s ease,width .32s cubic-bezier(0.4,0,0.2,1),height .32s cubic-bezier(0.4,0,0.2,1),border-radius .32s cubic-bezier(0.4,0,0.2,1),margin .32s cubic-bezier(0.4,0,0.2,1),padding .32s cubic-bezier(0.4,0,0.2,1),gap .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar--collapsed .gmail-compose{
          width:48px;height:48px;padding:0;margin:8px auto 16px;border-radius:50%;
          position:relative;overflow:hidden;gap:0;
        }
        .gmail-sidebar--collapsed .gmail-compose::after{
          content:"";
          position:absolute;inset:0;border-radius:50%;pointer-events:none;
          background:linear-gradient(135deg,rgba(255,255,255,0.28) 0%,transparent 55%);
          opacity:0;transition:opacity .22s ease;
        }
        .gmail-sidebar--collapsed .gmail-compose:hover::after{opacity:1;}
        .gmail-compose-label{
          display:inline-block;white-space:nowrap;overflow:hidden;
          max-width:140px;opacity:1;
          transition:opacity .2s ease,max-width .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar--collapsed .gmail-compose-label{
          max-width:0;opacity:0;pointer-events:none;position:absolute;width:0;height:0;overflow:hidden;
        }
        .gmail-compose:hover{
          filter:brightness(1.05);
          box-shadow:0 10px 22px rgba(8,117,225,0.28),0 2px 6px rgba(5,184,135,0.18);
          transform:translateY(-1px);
        }
        .gmail-compose:active{transform:translateY(0);}
        .gmail-compose svg{color:#fff !important;}
        .gmail-nav-item{
          display:flex;align-items:center;gap:14px;width:100%;
          margin:2px 0;padding:0 14px 0 12px;height:44px;border:none;
          border-radius:0 22px 22px 0;
          background:transparent;color:#0B2A4A;font-size:14px;font-weight:500;
          cursor:pointer;text-align:left;
          transition:background .15s ease,color .15s ease,width .32s cubic-bezier(0.4,0,0.2,1),height .32s cubic-bezier(0.4,0,0.2,1),padding .32s cubic-bezier(0.4,0,0.2,1),margin .32s cubic-bezier(0.4,0,0.2,1),border-radius .32s cubic-bezier(0.4,0,0.2,1),gap .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-nav-label{
          flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
          opacity:1;max-width:180px;
          transition:opacity .2s ease,max-width .32s cubic-bezier(0.4,0,0.2,1);
        }
        .gmail-sidebar--collapsed .gmail-nav-item{
          padding:0;justify-content:center;align-items:center;gap:0;border-radius:12px;
          width:48px;height:48px;margin:4px auto;position:relative;overflow:visible;
        }
        .gmail-sidebar--collapsed .gmail-nav-label{
          max-width:0;opacity:0;flex:0 0 0;pointer-events:none;
          position:absolute;width:0;height:0;overflow:hidden;
        }
        .gmail-sidebar--collapsed .gmail-nav-icon{
          margin:0;width:48px;height:48px;border-radius:12px;
        }
        .gmail-sidebar--collapsed .gmail-nav-badge{
          margin-left:0;position:absolute;top:2px;right:2px;
          min-width:16px;height:16px;font-size:9px;z-index:1;
        }
        .gmail-nav-icon{
          width:34px;height:34px;border-radius:10px;flex-shrink:0;
          display:flex;align-items:center;justify-content:center;
          background:transparent;
          transition:background .2s ease,box-shadow .2s ease,transform .2s ease,color .15s ease;
        }
        .gmail-nav-item:hover{background:rgba(11,42,74,0.08);color:#0B2A4A;}
        .gmail-nav-item:hover .gmail-nav-icon{background:transparent;}
        .gmail-sidebar--collapsed .gmail-nav-item:hover{
          background:rgba(11,42,74,0.08);color:#0B2A4A;
        }
        .gmail-sidebar--collapsed .gmail-nav-item:hover .gmail-nav-icon{
          background:transparent;box-shadow:none;transform:none;
        }
        .gmail-sidebar--collapsed .gmail-nav-item.active:hover{
          background:#D9EAFE;
        }
        .gmail-nav-item.active{
          background:#D9EAFE;font-weight:600;color:#0B2A4A;
          box-shadow:none;
        }
        .gmail-nav-item.active .gmail-nav-icon{
          background:transparent;
        }
        .gmail-nav-badge{
          margin-left:auto;min-width:20px;height:20px;padding:0 6px;border-radius:10px;
          background:#EF4444;color:#fff;font-size:11px;font-weight:700;
          display:inline-flex;align-items:center;justify-content:center;
        }
        .gmail-nav-item.active .gmail-nav-badge{background:#EF4444;}
        @media (prefers-reduced-motion: reduce){
          .gmail-sidebar,
          .gmail-sidebar-content,
          .gmail-sidebar-user,
          .gmail-sidebar-user-copy,
          .gmail-compose,
          .gmail-compose-label,
          .gmail-nav-item,
          .gmail-nav-label,
          .gmail-topbar-logo,
          .gmail-topbar::before{
            transition-duration:0.01s !important;
          }
        }
        .sidebar-hover-tip-anchor{
          z-index:10060;
          pointer-events:none;
        }
        .sidebar-hover-tip{
          position:relative;
          width:max-content;
          max-width:min(calc(100vw - 24px),280px);
          padding:7px 12px;
          border-radius:8px;
          background:#0F172A;
          color:#F8FAFC;
          font-family:'DM Sans',system-ui,sans-serif;
          font-size:13px;
          font-weight:600;
          letter-spacing:0.01em;
          line-height:1.35;
          white-space:nowrap;
          overflow:visible;
          border:none;
          box-shadow:0 8px 22px rgba(15,23,42,0.28);
          animation:sidebarTipIn .18s ease-out;
        }
        /* One triangle via ::before — no separate arrow/shine nodes (avoids “double” look) */
        .sidebar-hover-tip::before{
          content:"";
          position:absolute;
          left:-6px;top:50%;
          width:0;height:0;
          transform:translateY(-50%);
          border-style:solid;
          border-width:6px 6px 6px 0;
          border-color:transparent #0F172A transparent transparent;
          pointer-events:none;
        }
        .sidebar-hover-tip__shine,
        .sidebar-hover-tip__arrow{display:none !important;}
        .sidebar-hover-tip__label{
          position:relative;z-index:1;
          display:block;
          overflow:visible;
        }
        .ui-simple-tip{
          position:relative;
          box-sizing:border-box;
          width:max-content;
          max-width:min(calc(100vw - 24px), 240px);
          padding:7px 10px;
          border-radius:8px;
          background:#0F172A;
          color:#F8FAFC;
          border:none;
          box-shadow:0 8px 22px rgba(15,23,42,0.22);
          font-family:inherit;
          font-size:12px;
          font-weight:500;
          line-height:1.4;
          letter-spacing:0.01em;
          text-align:left;
          white-space:normal;
          overflow-wrap:anywhere;
          word-break:break-word;
          animation:uiSimpleTipIn .15s ease-out;
        }
        .ui-simple-tip--compact{
          max-width:min(calc(100vw - 24px), 220px);
          padding:6px 9px;
          font-size:11.5px;
          line-height:1.35;
        }
        .ui-simple-tip__text{
          display:block;position:relative;z-index:1;
          white-space:normal;
          overflow-wrap:anywhere;
          word-break:break-word;
        }
        /* Single CSS triangle — avoid rotated square (looked like a double arrow) */
        .ui-simple-tip__arrow{
          position:absolute;left:50%;width:0;height:0;
          background:transparent;border-style:solid;border-color:transparent;
          transform:translateX(-50%);
          pointer-events:none;box-shadow:none;
        }
        .ui-simple-tip--above .ui-simple-tip__arrow{
          bottom:-6px;top:auto;
          border-width:6px 6px 0 6px;
          border-top-color:#0F172A;
          border-bottom-color:transparent;
        }
        .ui-simple-tip--below .ui-simple-tip__arrow{
          top:-6px;bottom:auto;
          border-width:0 6px 6px 6px;
          border-bottom-color:#0F172A;
          border-top-color:transparent;
        }
        @keyframes uiSimpleTipIn{
          from{opacity:0;}
          to{opacity:1;}
        }
        @keyframes sidebarTipIn{
          from{opacity:0;transform:translateX(-8px) scale(0.95);}
          to{opacity:1;transform:translateX(0) scale(1);}
        }
        @keyframes sidebarTipShine{
          0%,68%{left:-120%;}
          100%{left:140%;}
        }
        .gmail-main{
          flex:1 1 0%;min-width:0;max-width:100%;
          display:flex;flex-direction:column;overflow:hidden;
          background:transparent;padding:8px 16px 8px 16px;
          box-sizing:border-box;
        }
        .gmail-main-scroll{
          flex:1 1 auto;overflow:auto;min-height:0;min-width:0;max-width:100%;
          background:#FFFFFF;
          border-radius:12px;
          border:1px solid #DCE6EF;
          box-shadow:none;
          display:flex;flex-direction:column;
          -webkit-overflow-scrolling:touch;
        }
        /* Calendar must scroll with system display/font scaling — never lock overflow:hidden. */
        .gmail-main-scroll.is-cal-fill{
          overflow-x:hidden;
          overflow-y:auto;
          -webkit-overflow-scrolling:touch;
          background:#F3F6FA;
        }
        .gmail-main-scroll.is-nav-pending{opacity:0.98;transition:opacity 0.12s ease;}
        .meet-page-list-transition{min-height:240px;}
        .gmail-right-stack{
          display:flex;flex:0 0 auto;flex-shrink:0;height:100%;min-height:0;align-items:stretch;
          max-width:min(calc(var(--gmail-drawer-width, 380px) + 74px),48vw);
          padding:10px 10px 10px 0;gap:8px;
          background:transparent;
        }
        .gmail-right-drawer{
          width:var(--gmail-drawer-width,380px);flex:0 0 auto;max-width:100%;
          border:1px solid rgba(12,43,78,0.08);
          background:rgba(255,255,255,0.82);
          backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
          display:flex;flex-direction:column;min-height:0;overflow:hidden;
          border-radius:16px;
          box-shadow:0 4px 24px rgba(12,43,78,0.08);
          position:relative;
        }
        .gmail-right-resize-handle{
          position:absolute;left:-5px;top:16px;bottom:16px;width:10px;z-index:20;
          cursor:col-resize;touch-action:none;
          display:flex;align-items:center;justify-content:center;
        }
        .gmail-right-resize-handle::after{
          content:"";width:3px;height:42px;border-radius:999px;
          background:#cbd5e1;opacity:0;transition:opacity .15s ease,background .15s ease;
        }
        .gmail-right-drawer:hover .gmail-right-resize-handle::after,
        .gmail-right-resize-handle:focus-visible::after,
        .gmail-right-resize-handle.is-resizing::after{
          opacity:1;background:#7c9bc4;
        }
        .gmail-right-drawer .cal-gmail-side{
          flex:1;height:100%;max-height:none;background:transparent;
        }
        .gmail-right-drawer .cal-gmail-side-head{
          background:transparent;border-bottom:1px solid rgba(12,43,78,0.08);
          border-radius:16px 16px 0 0;
        }
        .gmail-right-drawer .cal-gmail-side-scroll{padding:8px 10px 14px;}
        .gmail-right-drawer .cal-gmail-evt{
          margin-bottom:10px;border-radius:14px;
          background:linear-gradient(180deg,#f7fbff 0%,#eaf4fc 55%,#f5fafd 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.9),
            inset 0 -4px 10px rgba(23,120,212,0.05);
        }
        .gmail-right-drawer .cal-gmail-evt:hover{
          background:linear-gradient(180deg,#eef7ff 0%,#dceefb 55%,#eef6fc 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.95),
            inset 0 -6px 12px rgba(23,120,212,0.08);
        }
        .gmail-right-drawer .cal-gmail-evt.is-active{
          background:linear-gradient(135deg,#1778D4 0%,#0C8F55 100%);
          border-color:#0C8F55;
          box-shadow:inset 0 2px 8px rgba(12,43,78,0.22);
        }
        .gmail-right-drawer .cal-gmail-evt.is-active .cal-gmail-evt-title,
        .gmail-right-drawer .cal-gmail-evt.is-active .cal-gmail-evt-time{color:#fff;}
        .gmail-right-drawer .cal-gmail-evt:last-child{margin-bottom:4px;}
        .gmail-right-drawer .cal-gmail-evt.is-live:not(.is-active){
          background:linear-gradient(180deg,#f4fcf8 0%,#e6f6ef 55%,#f2faf6 100%);
        }
        .gmail-right-drawer .cal-tb-btn{border-radius:8px;padding:6px 14px;}
        .gmail-right-drawer .workspace-assistant-drawer{flex:1;min-height:0;border-radius:16px;}
        .gmail-right-rail{
          width:56px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;
          padding:20px 0 10px;
          background:transparent;border:none;gap:4px;
          position:relative;z-index:5;
        }
        .gmail-rail-btn{
          width:40px;height:40px;border:none;border-radius:50%;background:transparent;
          cursor:pointer;display:flex;align-items:center;justify-content:center;
          transition:background .15s ease;padding:0;
        }
        .gmail-rail-btn:hover{background:rgba(60,64,67,0.08);}
        .gmail-rail-btn.active{background:rgba(211,227,253,0.92);}
        .gmail-rail-cal-glyph{
          display:flex;align-items:center;justify-content:center;
          width:24px;height:24px;flex-shrink:0;
        }
        .gmail-rail-cal-glyph img{
          width:100%;height:100%;object-fit:contain;display:block;
        }
        .gmail-rail-ai-icon{
          width:28px;height:28px;border-radius:50%;overflow:hidden;
          border:1px solid #c7d8ea;box-shadow:0 1px 3px rgba(60,64,67,0.2);
          background:#0f172a;
        }
        .gmail-rail-ai-icon video{width:100%;height:100%;object-fit:cover;display:block;}
        .gmail-rail-spacer{flex:1;min-height:8px;}
        .gmail-rail-collapse{
          width:40px;height:36px;border:none;border-radius:50%;background:transparent;
          cursor:pointer;display:flex;align-items:center;justify-content:center;
          color:#5f6368;margin-top:4px;
        }
        .gmail-rail-collapse:hover{background:rgba(60,64,67,0.08);color:#3c4043;}
        .gmail-inbox-page{display:flex;flex-direction:column;height:100%;min-height:0;}
        .gmail-inbox-page .cal-gmail-inbox-scroll{flex:1;max-height:none;min-height:0;}
        .gmail-inbox-toolbar{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          padding:10px 16px;border-bottom:1px solid #f0f0f0;background:#fff;flex-shrink:0;
        }
        .gmail-page-flat .meet-page-root,
        .gmail-page-flat .meet-page-shell{
          height:100%;display:flex;flex-direction:column;min-height:0;background:#fff;
        }
        .gmail-page-flat .meet-well{
          flex:1;min-height:0;min-width:0;overflow:auto;display:flex;flex-direction:column;
          margin:0;padding:0;border:none;border-radius:0;box-shadow:none;background:#fff;
        }
        .gmail-page-flat .content-well{
          margin:0;padding:0;border:none;border-radius:0;box-shadow:none;background:#fff;
        }
        .gmail-page-flat.gmail-page-meeting-detail .content-well,
        .gmail-page-flat.gmail-page-meeting-detail .avaada-meeting-detail__content-well{
          margin:0 !important;padding:0 !important;border:none !important;border-radius:0 !important;
          box-shadow:none !important;background:transparent !important;
        }
        .gmail-page-flat .content-well.cal-dash-well{
          padding:clamp(16px,2.2vw,24px) !important;
          background:transparent !important;
          border:none !important;
          box-shadow:none !important;
        }
        .gmail-page-flat .content-well.actions-page{
          padding:16px 20px 20px;
          box-sizing:border-box;
          min-width:0;
          overflow:visible;
        }
        .gmail-page-flat .app-page-shell{max-width:none;padding:0;margin:0;}
        .gmail-page-flat .meet-shell-page-header{
          display:flex;align-items:center;justify-content:space-between;gap:16px;
          padding:10px 20px;border-bottom:1px solid #f0f0f0;
          background:#fff;flex-shrink:0;position:relative;
        }
        .gmail-page-flat .meet-shell-page-header--hero{
          background:linear-gradient(125deg,#f0f9ff 0%,#ffffff 38%,#f0fdf8 100%);
        }
        .gmail-page-flat .meet-shell-page-header--hero::after{
          content:"";position:absolute;top:-50px;right:-30px;width:200px;height:200px;
          border-radius:50%;pointer-events:none;
          background:radial-gradient(circle,rgba(23,120,212,0.14),transparent 68%);
        }
        .gmail-page-flat .meet-shell-page-header--autojoin{
          align-items:center;padding:10px 20px;
          border-bottom:1px solid #f0f0f0;background:#fff;
        }
        .gmail-page-flat .meet-shell-page-header--autojoin h1{
          margin:0;font-size:14px;font-weight:600;color:#001d35;line-height:1.3;
        }
        .gmail-page-flat .meet-shell-page-header--autojoin .meet-shell-page-header-sub{
          display:block;margin:4px 0 0;font-size:12px;font-weight:400;color:#5f6368;line-height:1.45;max-width:640px;
        }
        .gmail-page-flat .meet-shell-page-header--autojoin .meet-shell-page-header-sub strong{color:#202124;font-weight:600;}
        .gmail-page-flat .meet-shell-page-header--autojoin .meet-shell-page-header-action{padding-top:0;}
        .gmail-page-flat .meet-autojoin-tip{
          margin:0;padding:10px 20px;font-size:12.5px;line-height:1.5;color:#065F46;
          background:#ECFDF5;border-bottom:1px solid #A7F3D0;
        }
        .gmail-page-flat .meet-autojoin-tip strong{font-weight:600;color:#047857;}
        .gmail-page-flat .meet-shell-page-header--meetings,
        .gmail-page-flat .meet-shell-page-header--transcripts{
          background:#fff;
        }
        .gmail-page-flat .meet-shell-hero-leading{
          display:flex;align-items:flex-start;gap:14px;flex:1;min-width:0;position:relative;z-index:1;
        }
        .gmail-page-flat .meet-shell-hero-icon{
          width:46px;height:46px;border-radius:14px;flex-shrink:0;
          display:flex;align-items:center;justify-content:center;
          background:linear-gradient(145deg,#fff,#e8f4ff);
          border:1px solid rgba(23,120,212,0.18);
          box-shadow:0 4px 14px rgba(23,120,212,0.14);
        }
        .gmail-page-flat .meet-shell-page-header--autojoin .meet-shell-hero-icon{
          background:linear-gradient(145deg,#ecfdf5,#bbf7d0);
          border-color:rgba(12,143,85,0.25);
          box-shadow:0 4px 14px rgba(12,143,85,0.15);
        }
        .gmail-page-flat .meet-shell-page-header--transcripts .meet-shell-hero-icon{
          background:linear-gradient(145deg,#ede9fe,#ddd6fe);
          border-color:rgba(124,58,237,0.2);
          box-shadow:0 4px 14px rgba(124,58,237,0.12);
        }
        .gmail-page-flat .meet-shell-page-header--simple{align-items:center;}
        .gmail-page-flat .meet-shell-page-header-text{min-width:0;flex:1;position:relative;z-index:1;}
        .gmail-page-flat .meet-shell-page-header h1{
          margin:0 0 6px;font-size:24px;font-weight:700;color:#0B2A4A;
          line-height:1.2;letter-spacing:-0.02em;
        }
        .gmail-page-flat .meet-shell-page-header p{
          margin:0;font-size:13.5px;color:#5F7690;line-height:1.5;max-width:640px;
        }
        .gmail-page-flat .meet-shell-page-header-note{
          margin-top:6px !important;font-size:12px !important;color:#64748b !important;
        }
        .gmail-page-flat .meet-shell-page-header-action{flex-shrink:0;padding-top:6px;position:relative;z-index:1;}
        .gmail-page-flat .auto-join-premium::after{display:none;}
        .gmail-page-flat .auto-join-premium,
        .gmail-page-flat .meet-shell-page-header{
          margin:0;border-radius:0;border:none;box-shadow:none;
          background:#fff;
        }
        .gmail-page-flat .meet-shell-page-header--simple{
          padding:10px 20px 8px;border-bottom:1px solid #f0f0f0;
        }
        .gmail-page-flat .meet-shell-page-header--simple h1{
          margin:0;font-size:16px;font-weight:600;color:#001d35;
        }
        .gmail-page-flat .meet-shell-page-header--simple p{display:none;}
        .gmail-page-flat .meet-toolbar{
          flex-shrink:0;padding:8px 20px;margin:0;
          border:none;border-bottom:1px solid #DCE6EF;border-radius:0;box-shadow:none;
          background:#FFFFFF;gap:8px;
        }
        .gmail-page-flat .meet-shell-count-chip{
          display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;
          font-size:12px;font-weight:600;color:#5F7690;
          background:#F0F4F8;
          border:1px solid #DCE6EF;white-space:nowrap;
        }
        .gmail-page-flat .meet-toolbar--shell .date-filter-shell{flex:0 0 auto;min-width:148px;}
        .gmail-page-flat .meet-toolbar .date-filter-shell{
          height:38px;border-radius:8px;
          background:#fff;border:1px solid #DCE6EF;
          box-shadow:0 1px 2px rgba(12,43,78,0.04);
        }
        .gmail-page-flat .meet-toolbar .date-filter-shell:hover{border-color:rgba(8,117,225,0.28);}
        .gmail-page-flat .meet-toolbar .date-filter-shell .strip-cal-btn{
          background:#F0F4F8;border-right:1px solid #DCE6EF;color:#5F7690;
        }
        .gmail-page-flat .meet-toolbar .date-filter-shell .strip-cal-btn.picked,
        .gmail-page-flat .meet-toolbar .date-filter-shell:hover .strip-cal-btn{
          background:#EAF4FC;color:#0875E1;
        }
        .gmail-page-flat .meet-view-toggle{
          background:#FFFFFF;border:1px solid #DCE6EF;border-radius:10px;padding:3px;
          box-shadow:0 1px 2px rgba(12,43,78,0.04);
        }
        .gmail-page-flat .meet-view-toggle-btn{
          padding:6px 14px;font-size:13px;font-weight:600;border-radius:8px;color:#5F7690;
        }
        .gmail-page-flat .meet-view-toggle-btn.active{
          color:#fff;
          background:#0875E1;
          box-shadow:0 1px 3px rgba(8,117,225,0.28);
        }
        .gmail-page-flat .meet-list-wrap{
          gap:14px;padding:10px 12px 16px;background:#F3F8FC;
        }
        .gmail-page-flat .meet-grid-wrap{gap:0;padding:0;}
        .gmail-page-flat .meet-list-section{
          gap:0;padding:0;margin:0;
          overflow:hidden;background:#FFFFFF;
          border:1px solid #DCE6EF;
          border-radius:12px;
          box-shadow:0 1px 2px rgba(11,42,74,0.04);
        }
        .gmail-page-flat .meet-grid-section{
          gap:0;padding:0;margin:0;
        }
        .gmail-page-flat .meet-list-section-title{
          position:relative;padding:11px 14px 11px 16px;margin:0;
          border:none;border-bottom:1px solid #DCE6EF;
          background:#FFFFFF;
        }
        .gmail-page-flat .meet-list-section-title::before{
          content:none;
        }
        .gmail-page-flat .meet-list-section:nth-child(3n+2) .meet-list-section-title,
        .gmail-page-flat .meet-list-section:nth-child(3n) .meet-list-section-title{
          background:#FFFFFF;
        }
        .gmail-page-flat .meet-list-section:nth-child(3n+2) .meet-list-section-title::before,
        .gmail-page-flat .meet-list-section:nth-child(3n) .meet-list-section-title::before{
          background:transparent;
        }
        .gmail-page-flat .meet-grid-section-title{
          padding:10px 16px 6px;margin:0;border:none;background:transparent;
        }
        .gmail-page-flat .meet-grid-section-label{
          display:inline-flex;align-items:center;gap:6px;
          font-size:11px;font-weight:700;color:#0B2A4A;letter-spacing:0.05em;
          text-transform:uppercase;
        }
        .gmail-page-flat .meet-grid-section-label::before{display:none;}
        .gmail-page-flat .meet-grid-section-date{
          font-size:13px;font-weight:500;color:#5F7690;letter-spacing:0;
        }
        .gmail-page-flat .meet-grid-section-count{
          background:#F0F4F8;border:1px solid #DCE6EF;
          color:#5F7690;font-weight:700;
          padding:3px 10px;border-radius:999px;font-size:11px;
        }
        .gmail-page-flat .meet-list-table{
          background:#fff;border:none;
          border-radius:0;box-shadow:none;margin:0;overflow:hidden;width:100%;
        }
        .gmail-page-flat .meet-list-table.meet-list-table--toggles{
          width:100%;min-width:0;max-width:100%;overflow:hidden;
        }
        .gmail-page-flat .meet-list-table--toggles .meet-list-row{min-height:0;padding:6px 10px;}
        .meet-list-table-scroll{
          overflow-x:auto;overflow-y:visible;max-width:100%;min-width:0;
          -webkit-overflow-scrolling:touch;
        }
        .meet-list-table-scroll .meet-list-table--toggles,
        .meet-list-table-scroll .meet-list-table--transcripts{width:100%;min-width:0;}
        .gmail-page-flat .meet-list-table-scroll{
          margin:0;border-radius:0;
        }
        .gmail-app-body--panel-open .gmail-right-stack{
          max-width:min(calc(var(--gmail-drawer-width, 380px) + 74px),48vw);
        }
        .gmail-app-body--panel-open .meet-shell-page-header{
          flex-wrap:wrap;padding:16px 18px 14px;gap:12px;
        }
        .gmail-app-body--panel-open .meet-shell-page-header h1{font-size:21px;}
        .gmail-app-body--panel-open .meet-toolbar{padding:10px 14px;flex-wrap:wrap;}
        .gmail-page-flat .meet-list-head{
          padding:9px 10px;
          background:#F7FAFC;
          border-bottom:1px solid #DCE6EF;
          color:#5F7690;font-size:clamp(8px,1.1cqw,10px);font-weight:700;
          letter-spacing:clamp(0em,.03cqw,.035em);
        }
        .gmail-page-flat .meet-list-row{
          padding:10px;border-bottom:1px solid #DCE6EF;
          gap:8px;min-height:52px;font-size:12px;transition:background .15s ease,box-shadow .15s ease;
        }
        .gmail-page-flat .meet-list-row:nth-child(even):not(.meet-list-head),
        .gmail-page-flat .meet-list-row:nth-child(odd):not(.meet-list-head){
          background:#FFFFFF;
        }
        .gmail-page-flat .meet-list-row.is-clickable:hover{
          background:#F4F8FC;
          box-shadow:none;
        }
        .gmail-page-flat .meet-list-meeting-title{
          font-size:11px;font-weight:600;color:#0B2A4A;
        }
        .gmail-page-flat .meet-list-meeting-sub{font-size:10px;color:#71869B;}
        .gmail-page-flat .meet-list-meeting-icon{
          width:32px;height:32px;border-radius:8px;
          background:#FFFFFF;
          box-shadow:inset 0 0 0 1px #DCE6EF;
        }
        .gmail-page-flat .meet-list-meeting-icon .platform-brand-icon__img{
          width:18px;height:18px;
        }
        .gmail-page-flat .meet-list-meeting-tag.google{
          background:#F4F7FB;color:#4B6075;border:1px solid #DCE6EF;
          text-transform:uppercase;letter-spacing:.04em;font-size:9px;
        }
        .gmail-page-flat .meetings-card-grid{padding:0 4px 8px;gap:14px;}
         @media (max-width: 1180px){
           .gmail-right-stack{
             max-width:min(calc(var(--gmail-drawer-width, 380px) + 74px), 60vw);
           }
           .gmail-right-drawer{
             width:min(320px, var(--gmail-drawer-width, 380px));
           }
         }
         @media (max-width: 1024px){
           .gmail-right-stack{
             display:none;
           }
           .gmail-right-drawer{display:none;}
         }
         .mini-cal-nav:active{transform:scale(0.92);}

        .panel-scroll{
          overflow-y:auto;
          overflow-x:hidden;
          scrollbar-width:thin;
          scrollbar-color:#B8C6D4 transparent;
        }
        .panel-scroll::-webkit-scrollbar{
          width:8px;
          height:8px;
          display:block;
        }
        .panel-scroll::-webkit-scrollbar-track{
          background:transparent;
        }
        .panel-scroll::-webkit-scrollbar-thumb{
          background:#C3D3E1;
          border-radius:999px;
          border:2px solid transparent;
          background-clip:padding-box;
        }
        .panel-scroll::-webkit-scrollbar-thumb:hover{
          background:#AFC1D2;
          background-clip:padding-box;
        }

        [role="switch"]:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}

        .input{width:100%;padding:10px 12px;border:1.5px solid ${T.border};border-radius:9px;font-size:13px;color:${T.text};background:${T.bg};outline:none;font-family:inherit;transition:border .15s;}
        .input:focus{border-color:${T.accent};box-shadow:0 0 0 3px ${T.accentBg};}

        .page{animation:fadeIn .28s cubic-bezier(.22,.68,0,1.2);}

        .card-appear{animation:scaleIn .22s cubic-bezier(.22,.68,0,1.2);}
        .card-appear:nth-child(1){animation-delay:.03s;}
        .card-appear:nth-child(2){animation-delay:.06s;}
        .card-appear:nth-child(3){animation-delay:.09s;}
        .card-appear:nth-child(4){animation-delay:.12s;}
        .card-appear:nth-child(5){animation-delay:.15s;}
        .card-appear:nth-child(6){animation-delay:.18s;}

        .stat-card{
          background:${T.bg};border:1px solid ${T.border};
          border-radius:${T.radiusLg};box-shadow:${T.shadow};
          padding:18px 20px;
          transition:box-shadow .18s ease,transform .15s ease,border-color .18s ease;
        }
        .stat-card:hover{box-shadow:${T.shadowMd};transform:translateY(-2px);}

        .section-label{font-size:11px;font-weight:600;color:${T.textMuted};text-transform:uppercase;letter-spacing:.7px;margin-bottom:12px;}

        @media (max-width: 1280px){
          /* "Last updated" is the longest text in the right cluster — drop it first so the
             nav + buttons keep breathing room before we resort to stacking. */
          .refresh-meta{display:none !important;}
        }
        @media (max-width: 1024px){
          .app-page-shell,.meet-page-root{max-width:100%;padding:0 2px;}
          .content-well,.meet-well{padding:12px 12px 14px;border-radius:14px;}
          .gmail-page-flat .content-well,
          .gmail-page-flat .meet-well{
            padding:0;border:none;border-radius:0;box-shadow:none;margin:0;
          }
          .gmail-page-flat .content-well.cal-dash-well{
            padding:clamp(14px,3vw,20px) !important;
            background:transparent !important;
          }
          .gmail-page-flat .content-well.actions-page{
            padding:16px 18px 20px;
          }
          .dashboard-stat-grid,.dashboard-usage-grid,.admin-overview-stat-grid{grid-template-columns:repeat(2,minmax(0,1fr)) !important;}
          .dashboard-feed-grid,.cal-main-grid,.smtp-grid,.action-summary-grid{grid-template-columns:1fr !important;}
        }
        @media (max-width: 820px){
          .app-topbar-logo{max-height:40px;max-width:168px;min-width:120px;}
        }
        @media (max-width: 1180px){
          /* Stack the topbar early so the centre nav can never collide with the right cluster. */
          .app-topbar{
            grid-template-columns:1fr !important;
            row-gap:10px;
            padding:10px 16px !important;
          }
          .app-header-nav{
            justify-content:flex-start !important;
            justify-self:stretch !important;
            width:100%;
            padding-bottom:2px;
            overflow-x:auto;
          }
          .app-toolbar{justify-content:flex-end !important;width:100%;flex-wrap:wrap;}
        }
        @media (max-width: 820px){
          .app-topbar{padding:10px 14px !important;}
          .app-toolbar{justify-content:flex-start;}
          .toolbar-divider{display:none;}
          .dashboard-stat-grid,.dashboard-usage-grid,.admin-overview-stat-grid,.admin-system-grid{grid-template-columns:1fr !important;}
          .meetings-card-grid{grid-template-columns:repeat(auto-fill,minmax(220px,1fr)) !important;}
        }
        @media (max-width: 640px){
          .content-well,.meet-well{padding:10px 10px 12px;border-radius:12px;}
          .gmail-page-flat .content-well,
          .gmail-page-flat .meet-well{
            padding:0;border:none;border-radius:0;box-shadow:none;margin:0;
          }
          .gmail-page-flat .content-well.actions-page{
            padding:12px 12px 16px;
          }
          .seg-group{overflow-x:auto;max-width:100%;}
          .cal-view-seg,.seg-pill{padding:7px 10px;}
          .meetings-card-grid{grid-template-columns:1fr !important;}
          .dashboard-feed-grid{gap:10px !important;padding:12px !important;}
          .actions-meeting-head{flex-wrap:wrap;align-items:flex-start;padding:12px;}
          .actions-meeting-toggle{align-items:flex-start;}
          .actions-meeting-title{white-space:normal;}
          .actions-meeting-open-btn{width:100%;justify-content:center;margin-top:4px;}
          .actions-page-header h1{font-size:18px;}
          .actions-page-toolbar{width:100%;}
          .actions-page-filters.seg-group{
            display:flex;flex-wrap:wrap;width:100%;max-width:100%;
            overflow:visible;
          }
          .actions-page-filters .seg-pill{padding:6px 8px;font-size:11px;gap:4px;}
          .actions-bulk-bar{align-items:stretch;}
          .actions-bulk-close{margin-left:0;width:100%;}
          .meet-toolbar--shell{flex-wrap:wrap;}
          .meet-view-toggle{margin-left:auto;}
        }

        /* ── Tablet / compact system (≤1024px) ─────────────────────────────
           Off-canvas nav + full-width main. Phones keep extra rules at 768. */
        @media (max-width: 1024px){
          .gmail-topbar{
            display:flex;align-items:center;gap:8px;
            grid-template-columns:unset;
            padding-left:8px;
          }
          .gmail-topbar::before{display:none;}
          .gmail-topbar-brand{justify-self:unset;}
          .gmail-search-wrap{justify-self:unset;flex:1;max-width:none;margin:0;}
          .gmail-topbar-actions{justify-self:unset;margin-left:auto;}
          .gmail-topbar-logo,
          .gmail-topbar.is-nav-collapsed .gmail-topbar-logo{
            width:auto !important;min-width:0 !important;max-width:none !important;
            padding:0 6px;justify-content:flex-start;
          }
          .gmail-sidebar{
            position:absolute;top:0;bottom:0;left:0;
            width:min(84vw,300px) !important;
            flex:0 0 auto !important;
            min-width:0 !important;
            background:#f7fafd;
            box-shadow:2px 0 24px rgba(8,20,35,0.18);
            transform:translateX(-100%);
            transition:transform .32s cubic-bezier(0.4,0,0.2,1);
            z-index:1200;
            padding:0 !important;
          }
          .gmail-sidebar.is-mobile-open{transform:translateX(0);}
          .gmail-sidebar--collapsed{
            width:min(84vw,300px) !important;
            padding:0 !important;
          }
          .gmail-sidebar-content,
          .gmail-sidebar--collapsed .gmail-sidebar-content{
            padding:12px 10px calc(10px + env(safe-area-inset-bottom));
          }
          .sidebar-renewable-illustration{display:none;}
          .gmail-sidebar--collapsed .gmail-nav-label,
          .gmail-sidebar--collapsed .gmail-compose-label{
            display:inline !important;
            max-width:none !important;
            opacity:1 !important;
            flex:1 1 auto !important;
            pointer-events:auto !important;
          }
          .gmail-sidebar--collapsed .gmail-nav-item{
            padding:0 14px;justify-content:flex-start;
            width:auto;height:44px;margin:0 0 4px;border-radius:22px;
          }
          .gmail-sidebar--collapsed .gmail-compose{
            width:calc(100% - 8px);height:52px;padding:0 20px;
            margin:4px 4px 20px;border-radius:14px;
          }
          .gmail-sidebar--collapsed .gmail-nav-badge{
            margin-left:auto;position:static;min-width:20px;height:20px;font-size:11px;
          }
          .gmail-sidebar-foot{display:none !important;}
          .gmail-nav-backdrop{
            position:absolute;inset:0;background:rgba(8,20,35,0.45);
            z-index:1199;border:none;padding:0;
            animation:gmailBackdropIn .18s ease-out;
          }
          @keyframes gmailBackdropIn{from{opacity:0}to{opacity:1}}
          .gmail-right-rail{display:none !important;}
          .gmail-right-stack{display:none !important;}
          .gmail-main{padding:6px 6px 6px 0;}
          .gmail-nav-item{min-height:44px;}
          .meet-shell-page-header,
          .meet-toolbar,
          .actions-page-toolbar,
          .actions-page-header,
          .cal-dash-toolbar,
          .cal-dash-controls{flex-wrap:wrap !important;max-width:100%;}
          .meet-list-cell, .adm-cell-wrap{white-space:normal !important;}
          .cal-dash-side{min-width:0 !important;max-width:none !important;}
          .cal-gmail-layout{grid-template-columns:minmax(0,1fr) !important;min-height:0;max-height:none;overflow:visible;}
          .cal-gmail-inbox,.cal-gmail-side{max-height:none;}
          .mobile-scroll-x{
            overflow-x:auto;-webkit-overflow-scrolling:touch;
            max-width:100%;
          }
          .mobile-scroll-x > *{min-width:560px;}
          .stat-grid-4{grid-template-columns:repeat(2,minmax(0,1fr)) !important;}
          .content-well{padding-left:12px !important;padding-right:12px !important;}
          .gmail-nav-item, .gmail-compose, .adm-nav{min-height:44px;}
          .seg-pill{min-height:32px;}
          .date-filter-shell{width:min(188px,100%);max-width:100%;}
          .app-page-shell,.meet-page-root,.page,.gmail-page-flat{min-width:0;max-width:100%;}
        }

        /* ── Mobile shell (<=768px) ────────────────────────────────────────
           Phone chrome: compact topbar + expandable search overlay. */
        @media (max-width: 768px){
          .gmail-topbar-text-btn{white-space:normal !important;}
          .gmail-topbar{
            display:flex;align-items:center;
            grid-template-columns:unset;
            padding:6px 8px 6px 4px;gap:4px;
          }
          .gmail-topbar-logo{padding:0;}
          .gmail-topbar-logo img,
          .gmail-topbar-logo .app-topbar-logo{max-height:24px !important;max-width:92px !important;min-width:0 !important;}
          .gmail-search-wrap{
            position:absolute;left:0;right:0;top:0;bottom:0;
            max-width:none;margin:0;padding:8px 10px;
            background:#fff;z-index:210;
            display:none;align-items:center;
            justify-self:unset;
          }
          .gmail-search-wrap.is-open{display:flex;}
          .gmail-search{height:38px;}
          .gmail-search-icon{left:26px;}
          .gmail-mobile-search-btn{display:inline-flex !important;}
          .gmail-search-kbd{display:none !important;}
          .gmail-search{padding-right:16px !important;}
          .header-profile-text{display:none;}
          .header-profile-btn{width:36px;min-width:36px;max-width:36px;padding:0;min-height:36px;height:36px;border-radius:50%;}
          .gmail-topbar-vdivider{display:none !important;}
          .gmail-search-close{
            border:none;background:transparent;cursor:pointer;color:#5f6368;
            width:38px;height:38px;flex-shrink:0;border-radius:50%;
            display:inline-flex;align-items:center;justify-content:center;
          }
          .settings-row{
            grid-template-columns:1fr !important;
            gap:6px !important;
            align-items:start !important;
          }
        }
        @media (max-width: 480px){
          .stat-grid-4{grid-template-columns:1fr !important;}
        }
        /* Large Display Size / short CSS viewport on desktop: tighten chrome,
           keep side-by-side Calendar (never stack on height alone). */
        @media (min-width: 1101px) and (max-height: 720px){
          .gmail-topbar{min-height:48px;padding-top:4px;padding-bottom:4px;}
          .gmail-main{padding:4px 4px 4px 0;}
          .cal-dash-head{padding:clamp(10px,1.2vw,14px);}
          .cal-dash-timeline{min-height:min(18rem,52vh);}
          .cal-dash-timeline-scroll{max-height:min(48vh,28rem);}
          .gmail-compose{height:44px;margin-bottom:12px;}
          .gmail-nav-item{height:40px;}
        }
        .gmail-mobile-search-btn{display:none;}

        /* ── MOBILE ONLY (<=767px) — enterprise SaaS mobile shell ─────────── */
        @media (max-width: 767px){
          /* Header: 64px white, logo left, no hamburger, keep search/refresh/help/profile */
          .gmail-topbar{
            min-height:64px !important;
            height:64px !important;
            padding:0 10px 0 8px !important;
            gap:8px !important;
            background:#FFFFFF !important;
            border-bottom:1px solid #DCE6EF !important;
            box-shadow:0 1px 3px rgba(12,43,78,0.06) !important;
          }
          .gmail-topbar-brand .gmail-menu-btn{display:none !important;}
          .gmail-topbar::before{display:none !important;}
          /* Remove sidebar entirely on mobile */
          .gmail-sidebar{display:none !important;}
          .gmail-nav-backdrop{display:none !important;}
          .gmail-app-body{overflow:hidden; background:#F3F8FC;}
          .gmail-main{
            padding:0 !important;
            background:#F3F8FC !important;
          }
          .gmail-main-scroll{
            background:#F3F8FC !important;
            border:none !important;
            border-radius:0 !important;
            box-shadow:none !important;
            /* reserve space for fixed bottom nav + safe area - 100px per spec */
            padding-bottom:calc(100px + env(safe-area-inset-bottom)) !important;
          }
          .gmail-main-scroll.is-cal-fill{
            padding-bottom:calc(100px + env(safe-area-inset-bottom)) !important;
          }
          /* Ensure page header is visible below fixed header */
          .gmail-page-flat .meet-shell-page-header,
          .gmail-page-flat .meet-shell-page-header--autojoin,
          .gmail-page-flat .meet-shell-page-header--hero{
            display:flex !important;
            visibility:visible !important;
            opacity:1 !important;
            position:relative !important;
            z-index:1 !important;
          }
          /* page wells: white cards with #DCE6EF border */
          .gmail-page-flat .content-well,
          .gmail-page-flat .meet-well{
            background:transparent !important;
          }
          /* Prevent horizontal scroll */
          html,body,.gmail-app-shell{overflow-x:hidden !important; max-width:100vw;}
          .gmail-app-shell{overflow-x:hidden;}
          .gmail-app-shell *{min-width:0; box-sizing:border-box;}
          /* Full-width fluid - remove all desktop max-width / centering on mobile */
          .gmail-main, .gmail-main-scroll,
          .gmail-page-flat, .gmail-page-flat .meet-well, .gmail-page-flat .content-well,
          .gmail-page-flat .content-well.cal-dash-well, .gmail-page-flat .content-well.actions-page,
          .page, .page.app-page-shell, .meet-page-root, .app-page-shell,
          .meet-list-wrap, .meet-list-section, .meet-list-table, .meet-list-table-scroll,
          .meetings-card-grid, .daily-dashboard, .cal-dash, .cal-dash-well, .meet-well{
            width:100% !important;
            max-width:none !important;
            margin-left:0 !important;
            margin-right:0 !important;
            box-sizing:border-box !important;
          }
          .gmail-main, .gmail-main-scroll{padding-left:0 !important; padding-right:0 !important;}
          .meet-list-table,
          .meet-list-table-scroll,
          .meet-shell-page-header,
          .meet-toolbar,
          .actions-page-toolbar,
          .cal-dash-toolbar{
            max-width:100% !important;
            min-width:0;
            width:100% !important;
            box-sizing:border-box !important;
          }
          /* Explicitly hide desktop table header on mobile - must not appear inside date section */
          .mobile .table-header,
          .mobile .meeting-table-header,
          .mobile thead,
          .meet-list-head,
          .meet-list-table .meet-list-head,
          .meet-list-table .meet-list-row.meet-list-head{display:none !important; height:0 !important; min-height:0 !important; padding:0 !important; margin:0 !important; border:none !important; overflow:hidden !important;}
          /* Convert desktop table rows into clean vertical cards - exclude header */
          .meet-list-table,
          .meet-list-table--toggles,
          .meet-list-table--transcripts,
          .meet-list-table--reference{
            background:transparent !important;
            border:none !important;
            box-shadow:none !important;
            overflow:visible !important;
          }
          .meet-list-table .meet-list-row:not(.meet-list-head){
            display:block !important;
            width:100% !important;
            height:auto !important;
            min-height:0 !important;
            padding:12px 14px !important;
            margin:0 0 10px !important;
            background:#FFFFFF !important;
            border:1px solid #DCE6EF !important;
            border-radius:12px !important;
            box-shadow:0 1px 6px rgba(12,43,78,0.06) !important;
            box-sizing:border-box !important;
          }
          .meet-list-table-scroll{
            overflow:visible !important;
          }
          .meet-list-row > *{min-width:0; max-width:100%; box-sizing:border-box;}
          /* Remove duplicated table-header pseudo labels inside cards */
          .meet-list-cell::before{display:none !important; content:none !important;}
          /* Small field labels - compact 11px/600 #71869B, values 13-14px #0B2A4A */
          .meet-list-cell.col-time::before, .meet-list-cell.col-datetime::before{content:"TIME" !important; display:block !important; font-size:11px !important; color:#71869B !important; font-weight:600 !important; letter-spacing:0.04em !important; margin-bottom:2px !important; width:100% !important;}
          .meet-list-cell.col-host::before{content:"HOST" !important; display:block !important; font-size:11px !important; color:#71869B !important; font-weight:600 !important; letter-spacing:0.04em !important; margin-bottom:2px !important; width:100% !important;}
          .meet-list-cell.col-participants::before{content:"PARTICIPANTS" !important; display:block !important; font-size:11px !important; color:#71869B !important; font-weight:600 !important; letter-spacing:0.04em !important; margin-bottom:4px !important; width:100% !important;}
          .meet-list-cell.col-status::before{content:"STATUS" !important; display:block !important; font-size:11px !important; color:#71869B !important; font-weight:600 !important; letter-spacing:0.04em !important; margin-bottom:2px !important; width:100% !important;}
          .meet-list-cell.col-autojoin-toggle::before{content:"Auto Join" !important; display:inline !important; font-size:11px !important; color:#71869B !important; font-weight:600 !important; margin-right:6px !important;}
          .meet-list-cell.col-video-rec-toggle::before{content:"Video Rec" !important; display:inline !important; font-size:11px !important; color:#71869B !important; font-weight:600 !important; margin-right:6px !important;}
          /* Stack cells vertically - compact 4-6px between fields */
          .meet-list-cell{display:flex !important; flex-wrap:wrap !important; align-items:center !important; gap:4px !important; padding:2px 0 !important; border:none !important; width:100% !important;}
          .meet-list-cell.col-host, .meet-list-cell.col-status{flex-direction:column !important; align-items:flex-start !important; gap:2px !important;}
          .meet-list-cell.col-meeting{padding:0 0 6px !important; gap:10px !important; align-items:flex-start !important; border-bottom:1px solid #F1F5F9 !important; margin-bottom:4px !important;}
          .meet-list-cell.col-time, .meet-list-cell.col-datetime{font-size:13px !important; color:#0B2A4A !important; font-weight:600 !important; padding:2px 0 !important; flex-direction:column !important; align-items:flex-start !important;}
          .meet-list-cell.col-participants{flex-direction:column !important; align-items:flex-start !important; gap:0 !important;}
          .meet-list-cell.col-host .truncate, .meet-list-cell.col-participants .meet-list-participants, .meet-list-cell.col-status .meet-list-status-badge-wrap{font-size:13px !important; color:#0B2A4A !important;}
          .meet-list-cell.col-autojoin-toggle, .meet-list-cell.col-video-rec-toggle{justify-content:space-between !important; border-top:1px solid #F1F5F9 !important; padding-top:8px !important; margin-top:4px !important;}
          /* Date section header: TOMORROW — 9 Oct 2026  3 meetings — compact white 48px */
          .meet-list-section-title{
            padding:0 12px !important;
            margin:8px 0 8px !important;
            font-size:11px !important;
            font-weight:700 !important;
            letter-spacing:0.06em !important;
            text-transform:uppercase !important;
            color:#5F7690 !important;
            display:flex !important;
            justify-content:space-between !important;
            align-items:center !important;
            gap:8px !important;
            height:48px !important;
            min-height:48px !important;
            background:#FFFFFF !important;
            border:1px solid #DCE6EF !important;
            border-bottom:1px solid #DCE6EF !important;
            border-radius:8px !important;
            box-sizing:border-box !important;
          }
          .meet-grid-section-count,
          .meet-list-section-title .meet-grid-section-count{
            background:#F1F5F9 !important;
            border:1px solid #DCE6EF !important;
            color:#5F7690 !important;
            font-size:11px !important;
            font-weight:600 !important;
            padding:2px 8px !important;
            border-radius:999px !important;
            text-transform:none !important;
            letter-spacing:0 !important;
          }
          /* Controls wrap cleanly */
          .meet-toolbar,
          .meet-shell-page-header,
          .actions-page-toolbar,
          .cal-dash-toolbar,
          .cal-dash-controls{
            flex-wrap:wrap !important;
            gap:8px !important;
          }
          .meet-toolbar .seg-group,
          .actions-page-toolbar .seg-group,
          .cal-dash-view-switch{
            flex-wrap:wrap !important;
            max-width:100%;
          }
          /* Pagination usable */
          .meet-pagination,
          .MeetAiTablePagination{flex-wrap:wrap !important; gap:8px !important;}
          /* Left/right gutters 12px, cards fill 92-94% width, no large blank space */
          .gmail-page-flat .content-well,
          .gmail-page-flat .meet-well,
          .gmail-page-flat .content-well.cal-dash-well,
          .gmail-page-flat .content-well.actions-page,
          .page.app-page-shell,
          .meet-page-root{
            padding-left:12px !important;
            padding-right:12px !important;
            width:100% !important;
            max-width:none !important;
            margin:0 !important;
            box-sizing:border-box !important;
          }
          /* Page header - Upcoming meetings 22px/700, subtitle 13px, compact, Invite toggle not overlapping */
          .gmail-page-flat .meet-shell-page-header,
          .gmail-page-flat .meet-shell-page-header--autojoin{
            padding:12px 0 10px !important;
            gap:8px !important;
            background:transparent !important;
            border-bottom:none !important;
            flex-wrap:wrap !important;
            align-items:flex-start !important;
          }
          .gmail-page-flat .meet-shell-page-header h1{
            font-size:22px !important;
            line-height:1.2 !important;
            font-weight:700 !important;
            color:#0B2A4A !important;
            letter-spacing:-0.02em !important;
            margin:0 !important;
          }
          .gmail-page-flat .meet-shell-page-header p{
            font-size:13px !important;
            line-height:1.45 !important;
            color:#5F7690 !important;
            display:block !important;
            margin:4px 0 0 !important;
          }
          .meet-shell-page-header .meet-shell-page-header-action,
          .gmail-page-flat .meet-shell-page-header--autojoin .meet-shell-page-header-action{
            width:100% !important;
            display:flex !important;
            justify-content:flex-start !important;
            padding-top:6px !important;
            flex-shrink:0 !important;
          }
          /* Controls - Row1 Today/Week/Monthly, Row2 List/Grid, 34-36px compact */
          .meet-toolbar{
            padding:8px 0 !important;
            background:transparent !important;
            border-bottom:none !important;
            gap:8px !important;
            flex-direction:column !important;
            align-items:stretch !important;
          }
          .meet-toolbar .seg-group{
            width:100% !important;
            max-width:100% !important;
            display:flex !important;
            gap:6px !important;
            padding:3px !important;
            background:#FFFFFF !important;
            border:1px solid #DCE6EF !important;
            border-radius:10px !important;
            height:36px !important;
            min-height:36px !important;
            box-sizing:border-box !important;
          }
          .meet-toolbar .seg-pill{
            flex:1 1 0 !important;
            height:28px !important;
            min-height:28px !important;
            padding:0 10px !important;
            font-size:12.5px !important;
            font-weight:600 !important;
            border-radius:7px !important;
            justify-content:center !important;
            border:none !important;
            background:transparent !important;
            color:#5F7690 !important;
          }
          .meet-toolbar .seg-pill.active{
            background:#0875E1 !important;
            color:#fff !important;
            box-shadow:none !important;
          }
          /* Cards full-width  */
          .meet-list-table .meet-list-row,
          .mcard-pro,
          .card{
            margin-left:0 !important;
            margin-right:0 !important;
            width:100% !important;
            max-width:none !important;
            box-sizing:border-box !important;
          }
          .gmail-page-flat .meet-list-meeting-title{font-size:14px !important; line-height:1.35 !important; color:#0B2A4A !important; white-space:normal !important; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;}
          .gmail-page-flat .meet-list-meeting-sub{white-space:normal !important;}
          /* Dashboard stacked */
          .dashboard-stat-grid,.dashboard-usage-grid,.admin-overview-stat-grid,.admin-system-grid,
          .dashboard-feed-grid,.stat-grid-4{
            grid-template-columns:1fr !important;
            gap:12px !important;
          }
          .cal-dash-body{grid-template-columns:1fr !important;}
          .cal-dash-side{grid-template-columns:1fr !important;}
          /* My Tasks / actions stacked, keep 31 SVG */
          .actions-page-list{gap:12px !important;}
          .actions-meeting-card{border-radius:14px !important;}
          .actions-meeting-head{padding:14px !important;}
          /* Ensure cards never cause horizontal scroll */
          .meetings-card-grid{grid-template-columns:1fr !important; padding:0 !important; gap:12px !important;}
          .gmail-page-flat .meetings-card-grid{padding:0 !important;}
          /* ── FIX: mobile content visibility — ensure real data renders ── */
          .meet-list-row,
          .meet-list-cell,
          .meet-list-meeting-text,
          .meet-list-meeting-title,
          .meet-list-meeting-sub,
          .meet-datetime-date,
          .meet-datetime-time,
          .meet-host-first,
          .meet-list-participants,
          .meet-list-status-badge-wrap,
          .meet-list-outcome-wrap,
          .meet-toggle-chip,
          .daily-feature,
          .daily-quick-card,
          .actions-meeting-card,
          .cal-dash-card,
          .cal-dash-timeline,
          .avaada-detail-card{
            height:auto !important;
            max-height:none !important;
            min-height:0 !important;
            overflow:visible !important;
            visibility:visible !important;
            opacity:1 !important;
          }
          .meet-list-cell{
            display:flex !important;
            flex-wrap:wrap !important;
            align-items:center !important;
            gap:4px !important;
            padding:2px 0 !important;
          }
          .meet-list-cell.col-meeting{
            align-items:flex-start !important;
          }
          .meet-list-cell.col-transcript-actions,
          .meet-list-cell.col-autojoin-toggle,
          .meet-list-cell.col-video-rec-toggle{
            display:flex !important;
            flex-wrap:wrap !important;
            gap:4px !important;
          }
          .meet-list-cell.col-status,
          .meet-list-cell.col-participants,
          .meet-list-cell.col-host,
          .meet-list-cell.col-datetime,
          .meet-list-cell.col-time{
            display:flex !important;
            flex-direction:column !important;
            align-items:flex-start !important;
            gap:2px !important;
          }
          .meet-list-meeting-text{
            display:flex !important;
            flex-direction:column !important;
            gap:4px !important;
          }
          .meet-list-meeting-title{
            display:block !important;
            white-space:normal !important;
            overflow:visible !important;
            text-overflow:clip !important;
            -webkit-line-clamp:unset !important;
            max-height:none !important;
            line-height:1.35 !important;
            color:#0B2A4A !important;
            font-size:14px !important;
            font-weight:700 !important;
          }
          .meet-list-meeting-sub,
          .meet-datetime-date,
          .meet-datetime-time,
          .meet-host-first,
          .meet-ref-time-main,
          .meet-ref-time-sub{
            white-space:normal !important;
            overflow:visible !important;
            text-overflow:clip !important;
            color:#5F7690 !important;
            font-size:12.5px !important;
          }
          .truncate{
            white-space:normal !important;
            overflow:visible !important;
            text-overflow:clip !important;
          }
          .meet-list-participants,
          .meet-list-status-badge-wrap{
            display:inline-flex !important;
            flex-wrap:wrap !important;
            overflow:visible !important;
          }
          /* Dashboard: allow title wrap, not clipped */
          .daily-feature h2{
            white-space:normal !important;
            overflow:visible !important;
            text-overflow:clip !important;
            -webkit-line-clamp:unset !important;
          }
          .daily-feature-meta,
          .daily-head-sub{
            white-space:normal !important;
            overflow:visible !important;
          }
          .daily-top{grid-template-columns:1fr !important;}
          .daily-feature{padding:16px !important;}
          /* Calendar timeline not clipped */
          .cal-dash-timeline-scroll{max-height:none !important; overflow:visible !important;}
          .cal-dash-timeline{min-height:0 !important; overflow:visible !important;}
          /* Meeting details & other cards */
          .gmail-page-flat .content-well > *,
          .gmail-page-flat .meet-well > *{min-width:0; max-width:100%;}
          /* Pagination after records */
          .MeetAiTablePagination, .meet-pagination{margin-top:8px !important;}
          /* Recorded/All Meetings - use same card design as Upcoming (no extra fixed styles) */
          .meet-list-table--transcripts .meet-list-transcript-actions{
            display:flex !important; flex-wrap:wrap !important; justify-content:flex-start !important;
            gap:8px !important; width:100% !important; max-width:100% !important;
          }
          .meet-list-table--transcripts .meet-list-transcript-actions button{flex:0 1 auto !important; min-width:0 !important; max-width:100% !important;}
          /* Filters - keep All/sorting/ListGrid within viewport - same as Upcoming */
          .meet-toolbar, .meet-toolbar .seg-group{
            flex-wrap:wrap !important; min-width:0 !important; max-width:100% !important;
            overflow:visible !important; box-sizing:border-box !important;
          }
          .meet-toolbar{overflow-x:hidden !important;}
          /* FINAL ROOT WIDTH FIX - ensure every shared wrapper is full-width on mobile */
          .gmail-app-shell, .gmail-app-body, .gmail-main, .gmail-main-scroll,
          .gmail-page-flat, .page, .page.app-page-shell, .meet-page-root, .app-page-shell,
          .meet-well, .content-well, .meet-list-wrap, .meet-list-section,
          .meet-list-table, .meet-list-table-scroll, .meetings-card-grid,
          .daily-dashboard, .cal-dash, .cal-dash-well, .admin-page, .cu-page, .adm-stack,
          .avaada-page-dashboard, .avaada-page-calendar, .avaada-meeting-detail,
          .how-it-works-page, .avaada-how-it-works{
            width:100% !important;
            max-width:none !important;
            min-width:0 !important;
            margin-left:0 !important;
            margin-right:0 !important;
            box-sizing:border-box !important;
          }
          html body .gmail-app-shell, html body .gmail-app-body, html body .gmail-main, html body .gmail-main-scroll{
            padding:0 !important; margin:0 !important;
            max-width:100% !important;
          }
          .gmail-page-flat .content-well,
          .gmail-page-flat .meet-well{
            padding:0 12px !important;
            width:100% !important; max-width:none !important; margin:0 !important;
            background:transparent !important; border:none !important;
          }
          .page, .page.app-page-shell, .meet-page-root, .daily-dashboard, .cal-dash, .adm-stack, .cu-page{
            padding-left:0 !important; padding-right:0 !important;
          }
          /* Meeting cards - prevent overlap */
          .meet-list-row, .mcard-pro, .card, .actions-meeting-card, .daily-feature, .cal-dash-card, .adm-card, .meet-list-section{
            position:static !important; transform:none !important; float:none !important; clear:none !important;
            height:auto !important; min-height:0 !important; max-height:none !important;
            overflow:visible !important; width:100% !important; max-width:none !important;
            box-sizing:border-box !important;
          }
          .meet-list-table, .meet-list-wrap, .meet-list-section{display:block !important; width:100% !important; max-width:none !important;}
          .meet-list-row{margin:0 0 10px !important; padding:12px 14px !important; gap:8px !important;}
          .meet-list-row + .meet-list-row{margin-top:0 !important;}
          /* Filters and controls - keep within viewport, wrap */
          .meet-toolbar, .adm-meetings-list-controls, .cal-dash-toolbar, .actions-page-toolbar,
          .meet-toolbar .seg-group, .seg-group, .adm-card-header{
            flex-wrap:wrap !important; min-width:0 !important; max-width:100% !important;
            overflow:visible !important; gap:8px !important; width:100% !important;
            box-sizing:border-box !important;
          }
          .meet-toolbar .seg-pill, .adm-meetings-list-controls input, .adm-meetings-list-controls select{
            min-width:0 !important; max-width:100% !important; flex-shrink:1 !important;
          }
          /* Pagination - compact, no overlap */
          .MeetAiTablePagination, .adm-list-pagination, .meet-pagination{
            display:flex !important; flex-wrap:wrap !important; justify-content:space-between !important;
            align-items:center !important; gap:8px !important; padding:12px 0 !important;
            width:100% !important; max-width:100% !important; position:static !important;
            border-top:1px solid #DCE6EF !important; margin-top:8px !important; clear:both !important;
            box-sizing:border-box !important;
          }
          /* Companion above bottom nav, never covering card content */
          [data-tour="workspace-chat-fab"]{
            bottom:calc(84px + env(safe-area-inset-bottom)) !important; right:12px !important;
            z-index:1099 !important; position:fixed !important;
          }
          .avaada-mobile-more-sheet{z-index:1300 !important;}
        }
        @media (min-width: 768px){
          .avaada-mobile-bottom-nav,
          .avaada-mobile-more-backdrop,
          .avaada-mobile-more-sheet{display:none !important;}
        }
        /* Bottom nav — mobile only — 5 items, Instant Join slightly elevated */
        .avaada-mobile-bottom-nav{
          position:fixed;
          left:0; right:0; bottom:0;
          z-index:1100;
          display:flex;
          align-items:flex-end;
          justify-content:space-between;
          height:72px;
          max-height:72px;
          padding:4px 4px calc(4px + env(safe-area-inset-bottom));
          background:#FFFFFF;
          border-top:1px solid #DCE6EF;
          box-shadow:0 -4px 20px rgba(12,43,78,0.08), 0 -1px 3px rgba(12,43,78,0.04);
          box-sizing:border-box;
        }
        .avaada-mobile-bottom-nav.avaada-mobile-bottom-nav--hidden{display:none !important;}
        .avaada-mobile-bottom-nav__item{
          flex:1 1 0;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          gap:4px;
          border:none;
          background:transparent;
          cursor:pointer;
          padding:6px 2px 4px;
          color:#5F7690;
          font-family:inherit;
          font-size:10px;
          font-weight:600;
          letter-spacing:0.02em;
          line-height:1;
          transition:color .15s ease, background .15s ease;
          min-width:0;
          position:relative;
          border-radius:10px;
        }
        .avaada-mobile-bottom-nav__item.is-active{color:#0875E1;}
        .avaada-mobile-bottom-nav__item:active{background:rgba(8,117,225,0.06);}
        .avaada-mobile-bottom-nav__item svg{flex-shrink:0;}
        .avaada-mobile-bottom-nav__label{white-space:nowrap; font-size:10px; font-weight:600; letter-spacing:0.02em;}
        .avaada-mobile-bottom-nav__icon-wrap{
          width:26px; height:26px;
          display:flex; align-items:center; justify-content:center;
          position:relative;
          border-radius:8px;
        }
        .avaada-mobile-bottom-nav__item--instant{
          transform:translateY(-8px);
          margin-top:-4px;
          padding-top:0;
        }
        .avaada-mobile-bottom-nav__item--instant .avaada-mobile-bottom-nav__icon-wrap{
          width:52px; height:52px;
          border-radius:50%;
          background:linear-gradient(135deg,#0875E1 0%, #08A67A 55%, #0AB58D 100%);
          box-shadow:0 6px 18px rgba(8,117,225,0.32), 0 2px 6px rgba(5,184,135,0.28);
          border:3px solid #fff;
        }
        .avaada-mobile-bottom-nav__item--instant:active .avaada-mobile-bottom-nav__icon-wrap{transform:scale(0.96);}
        .avaada-mobile-bottom-nav__item--instant .avaada-mobile-bottom-nav__label{color:#0B2A4A; font-weight:700; margin-top:2px;}
        .avaada-mobile-bottom-nav__item--instant.is-active .avaada-mobile-bottom-nav__label{color:#0875E1;}
        .avaada-mobile-bottom-nav__badge{
          position:absolute;
          top:-4px; right:-6px;
          min-width:16px; height:16px;
          padding:0 4px;
          border-radius:999px;
          background:#EF4444;
          color:#fff;
          font-size:9px;
          font-weight:800;
          line-height:16px;
          text-align:center;
          border:1.5px solid #fff;
          box-sizing:border-box;
        }
        /* More bottom sheet — clean white, 18px top radius, drag handle */
        .avaada-mobile-more-backdrop{
          position:fixed;
          inset:0;
          z-index:1290;
          background:rgba(8,20,35,0.42);
          border:none;
          padding:0;
          animation:avaadaSheetBackdropIn .18s ease-out;
        }
        @keyframes avaadaSheetBackdropIn{from{opacity:0}to{opacity:1}}
        .avaada-mobile-more-sheet{
          position:fixed;
          left:0; right:0; bottom:0;
          z-index:1300;
          background:#FFFFFF;
          border-top:1px solid #DCE6EF;
          border-radius:18px 18px 0 0;
          box-shadow:0 -12px 40px rgba(12,43,78,0.16), 0 -2px 8px rgba(12,43,78,0.06);
          padding:8px 0 calc(12px + env(safe-area-inset-bottom));
          animation:avaadaSheetIn .24s cubic-bezier(.32,1,.36,1);
          max-height:min(56vh, 440px);
          overflow:auto;
          -webkit-overflow-scrolling:touch;
        }
        @keyframes avaadaSheetIn{from{transform:translateY(100%)}to{transform:translateY(0)}}
        .avaada-mobile-more-sheet__handle{
          width:36px; height:4px;
          border-radius:999px;
          background:#DCE6EF;
          margin:2px auto 12px;
        }
        .avaada-mobile-more-sheet__item{
          width:100%;
          display:flex;
          align-items:center;
          gap:14px;
          padding:14px 18px;
          border:none;
          background:#fff;
          cursor:pointer;
          font-family:inherit;
          text-align:left;
          transition:background .14s ease;
          border-bottom:1px solid #F1F5F9;
        }
        .avaada-mobile-more-sheet__item:last-child{border-bottom:none;}
        .avaada-mobile-more-sheet__item::after{
          content:"›"; flex:0 0 auto; margin-left:10px; color:#8aa0b4;
          font-size:26px; font-weight:400; line-height:1;
        }
        .avaada-mobile-more-sheet__label,
        .avaada-mobile-more-sheet__sub{display:block; white-space:normal;}
        .avaada-mobile-more-sheet__item:hover{background:#F3F8FC;}
        .avaada-mobile-more-sheet__item:active{background:#EFF6FF;}
        .avaada-mobile-more-sheet__icon{
          width:38px; height:38px;
          border-radius:11px;
          background:#F1F6FF;
          border:1px solid #DCE6EF;
          display:flex; align-items:center; justify-content:center;
          flex-shrink:0;
          color:#0875E1;
        }
        .avaada-mobile-more-sheet__text{flex:1; min-width:0;}
        .avaada-mobile-more-sheet__label{font-size:14px; font-weight:700; color:#0B2A4A; line-height:1.25; letter-spacing:-0.01em;}
        .avaada-mobile-more-sheet__sub{font-size:12px; color:#5F7690; line-height:1.35; margin-top:2px;}
        .avaada-mobile-more-sheet__badge{
          min-width:22px; height:22px;
          padding:0 6px;
          border-radius:999px;
          background:#EF4444;
          color:#fff;
          font-size:11px; font-weight:800;
          display:inline-flex; align-items:center; justify-content:center;
          flex-shrink:0;
        }
        @media (max-width: 767px){
          [data-tour="workspace-chat-fab"]{
            bottom:calc(84px + env(safe-area-inset-bottom)) !important;
            right:12px !important;
          }
        }
 `;
  return _gmailShellStylesCssCache;
}

function AvaadaMobileBottomNav({ page, detailOpenedFrom, badgeCount, onNavigate, onInstantJoin, onMore, hidden }) {
  const isActive = (id) => {
    if (page === "detail" && detailOpenedFrom === id) return true;
    return page === id;
  };
  const itemColor = (active) => (active ? "#0875E1" : "#5F7690");
  const strokeW = (active) => (active ? 2.1 : 1.75);
  return (
    <nav className={`avaada-mobile-bottom-nav${hidden ? " avaada-mobile-bottom-nav--hidden" : ""}`} aria-label="Mobile navigation">
      <button
        type="button"
        className={`avaada-mobile-bottom-nav__item${isActive("autojoin") ? " is-active" : ""}`}
        onClick={() => onNavigate("autojoin")}
        aria-label="Upcoming"
      >
        <span className="avaada-mobile-bottom-nav__icon-wrap">
          <Svg d={IC.upcomingCal} size={20} color={itemColor(isActive("autojoin"))} stroke={isActive("autojoin") ? 1.7 : 1.5} />
        </span>
        <span className="avaada-mobile-bottom-nav__label">Upcoming</span>
      </button>
      <button
        type="button"
        className={`avaada-mobile-bottom-nav__item${isActive("transcripts") ? " is-active" : ""}`}
        onClick={() => onNavigate("transcripts")}
        aria-label="Recorded"
      >
        <span className="avaada-mobile-bottom-nav__icon-wrap">
          <Svg d={IC.recordedR} size={20} color={itemColor(isActive("transcripts"))} stroke={isActive("transcripts") ? 1.7 : 1.5} />
        </span>
        <span className="avaada-mobile-bottom-nav__label">Recorded</span>
      </button>
      <button
        type="button"
        className={`avaada-mobile-bottom-nav__item avaada-mobile-bottom-nav__item--instant${isActive("autojoin") && false ? " is-active" : ""}`}
        onClick={onInstantJoin}
        aria-label="Instant Join"
      >
        <span className="avaada-mobile-bottom-nav__icon-wrap">
          <Svg d={IC.zap} size={22} color="#fff" stroke={2.3} />
        </span>
        <span className="avaada-mobile-bottom-nav__label">Instant Join</span>
      </button>
      <button
        type="button"
        className={`avaada-mobile-bottom-nav__item${isActive("dashboard") ? " is-active" : ""}`}
        onClick={() => onNavigate("dashboard")}
        aria-label="Dashboard"
      >
        <span className="avaada-mobile-bottom-nav__icon-wrap">
          <Svg d={IC.layoutDashboard} size={20} color={itemColor(isActive("dashboard"))} stroke={strokeW(isActive("dashboard"))} />
        </span>
        <span className="avaada-mobile-bottom-nav__label">Dashboard</span>
      </button>
      <button
        type="button"
        className="avaada-mobile-bottom-nav__item"
        onClick={onMore}
        aria-label="More"
        aria-haspopup="dialog"
      >
        <span className="avaada-mobile-bottom-nav__icon-wrap">
          <span aria-hidden style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#5F7690" }} />
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#5F7690" }} />
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#5F7690" }} />
          </span>
          {badgeCount > 0 ? <span className="avaada-mobile-bottom-nav__badge">{badgeCount > 99 ? "99+" : badgeCount}</span> : null}
        </span>
        <span className="avaada-mobile-bottom-nav__label">More</span>
      </button>
    </nav>
  );
}

function AvaadaMobileMoreSheet({ open, onClose, onNavigate, badgeCount }) {
  if (!open) return null;
  return (
    <>
      <button type="button" className="avaada-mobile-more-backdrop" aria-label="Close menu" onClick={onClose} />
      <div className="avaada-mobile-more-sheet" role="dialog" aria-modal="true" aria-label="More navigation">
        <div className="avaada-mobile-more-sheet__handle" aria-hidden />
        <button
          type="button"
          className="avaada-mobile-more-sheet__item"
          onClick={() => {
            onClose();
            onNavigate("calendar");
          }}
        >
          <span className="avaada-mobile-more-sheet__icon"><Svg d={IC.cal} size={18} color="#0875E1" stroke={2} /></span>
          <span className="avaada-mobile-more-sheet__text">
            <span className="avaada-mobile-more-sheet__label">Calendar</span>
            <span className="avaada-mobile-more-sheet__sub">Google synced meetings</span>
          </span>
        </button>
        <button
          type="button"
          className="avaada-mobile-more-sheet__item"
          onClick={() => {
            onClose();
            onNavigate("meetings");
          }}
        >
          <span className="avaada-mobile-more-sheet__icon"><Svg d={IC.meet} size={18} color="#0875E1" stroke={2} /></span>
          <span className="avaada-mobile-more-sheet__text">
            <span className="avaada-mobile-more-sheet__label">All Meetings</span>
            <span className="avaada-mobile-more-sheet__sub">Every session across views</span>
          </span>
        </button>
        <button
          type="button"
          className="avaada-mobile-more-sheet__item"
          onClick={() => {
            onClose();
            onNavigate("actions");
          }}
        >
          <span className="avaada-mobile-more-sheet__icon"><Svg d={IC.task} size={18} color="#0875E1" stroke={2} /></span>
          <span className="avaada-mobile-more-sheet__text">
            <span className="avaada-mobile-more-sheet__label">My Tasks</span>
            <span className="avaada-mobile-more-sheet__sub">Action items assigned to you</span>
          </span>
          {badgeCount > 0 ? <span className="avaada-mobile-more-sheet__badge">{badgeCount > 99 ? "99+" : badgeCount}</span> : null}
        </button>
      </div>
    </>
  );
}

export default function App(){
  const [pathTick, setPathTick] = useState(0);
  useEffect(() => {
    const bump = () => setPathTick((t) => t + 1);
    window.addEventListener("popstate", bump);
    return () => window.removeEventListener("popstate", bump);
  }, []);

  const [oauthPending, setOauthPending] = useState(() =>
    typeof window !== "undefined" && /[#&]token=/.test(window.location.hash || "")
  );
  const [loginEmail,setLoginEmail] = useState(()=>{
    // Restore session from localStorage on page load
    const u = getStoredUser();
    return u?.email || null;
  });
  const [loggedInUser,setLoggedInUser] = useState(()=>{
    const u = getStoredUser();
    if (!u) return null;
    try {
      return { ...u, avatar_url: u.avatar_url || u.avatarUrl || u.picture || u.photo || "" };
    } catch (_) {
      return null;
    }
  });
  const [page,setPage]           = useState(readInitialUserPage);
  const [navPending, startNavTransition] = useTransition();
  const goToPage = useCallback((next) => {
    if (SMOOTH_NAV_TRANSITION) startNavTransition(() => setPage(next));
    else setPage(next);
  }, []);
  useEffect(() => {
    if (!SHOW_USER_CALENDAR_NAV && page === "calendar") setPage("autojoin");
  }, [page]);
  const [selMeeting,setSelMeeting] = useState(null);
  const [recordingModalPayload, setRecordingModalPayload] = useState(null);
  // Which sub-tab (autojoin | transcripts | meetings | null) opened the detail view.
  // Drives a slim "join-only" detail when opened from Auto Join.
  const [detailOpenedFrom, setDetailOpenedFrom] = useState(() => {
    try {
      const from = sessionStorage.getItem(MEETAI_DETAIL_FROM_KEY);
      if (from && ALLOWED_USER_PAGES.has(from) && from !== "detail") return from;
    } catch (_) {}
    return null;
  });
  const [activeTab,setActiveTab] = useState(() => {
    try {
      const t = sessionStorage.getItem(MEETAI_DETAIL_TAB_KEY);
      if (["overview", "transcript", "recording", "action-items"].includes(t)) return t;
    } catch (_) {}
    return "overview";
  });
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourAutoStartedRef = useRef(false);
  const startAppTour = useCallback((fromStep = 0) => {
    setTourStep(fromStep);
    setTourOpen(true);
    const step = APP_TOUR_STEPS[fromStep];
    if (step?.page) setPage(step.page);
    else if (step?.meetView) setPage(pageFromMeetView(step.meetView));
  }, []);
  const navigateAppTourStep = useCallback((step) => {
    if (!step) return;
    if (step.page) setPage(step.page);
    else if (step.meetView) setPage(pageFromMeetView(step.meetView));
    const sel = String(step.target || "");
    const needsNav =
      sel.includes("app-nav") ||
      sel.includes("nav-") ||
      sel.includes("btn-record") ||
      sel.includes("auto-join-banner");
    if (needsNav && typeof window !== "undefined" && window.innerWidth <= 1024) {
      setMobileNavOpen(true);
    } else if (!needsNav) {
      setMobileNavOpen(false);
    }
  }, []);
  const [calYear,setCalYear]     = useState(new Date().getFullYear());
  const [calMonth,setCalMonth]   = useState(new Date().getMonth());
  const [calView,setCalView]     = useState("day");
  const [calSelDate,setCalSelDate] = useState(()=>todayStr());
  const [highlightMeetingId, setHighlightMeetingId] = useState(null);
  const [actionsFocus, setActionsFocus] = useState(null);
  const [gmailSearch, setGmailSearch] = useState("");
  const [rightRailDrawerOpen, setRightRailDrawerOpen] = useState(() => {
    try {
      return window.localStorage?.getItem?.("meetai.gmail.rightRail.open") !== "0";
    } catch (_) {}
    return true;
  });
  const [rightRailPanel, setRightRailPanel] = useState(() => {
    try {
      const p = window.localStorage?.getItem?.("meetai.gmail.rightRail.panel");
      return p === "ai" ? "ai" : "calendar";
    } catch (_) {}
    return "calendar";
  });
  const leftNavAnimTimerRef = useRef(null);
  const [leftNavAnimating, setLeftNavAnimating] = useState(false);
  const [leftNavCollapsed, setLeftNavCollapsed] = useState(() => {
    try {
      const stored = window.localStorage?.getItem?.("meetai.gmail.leftNav.collapsed");
      if (stored === "1") return true;
      if (stored === "0") return false;
      return typeof window !== "undefined" && window.innerWidth < 900;
    } catch (_) {}
    return false;
  });
  /* Drawer at ≤1024 so tablets + large Display Size laptops get full-width content.
     Phone-only UI (search overlay) still uses useIsMobile (≤768). */
  const isMobileViewport = useIsTablet();
  const isPhoneViewport = useIsMobile();
  // Kept separate from leftNavCollapsed: that persists a desktop width preference,
  // whereas the drawer must always start closed on a phone/tablet.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [avaadaMobileMoreOpen, setAvaadaMobileMoreOpen] = useState(false);
  useEffect(() => {
    if (!isMobileViewport) {
      setMobileNavOpen(false);
    }
    if (!isPhoneViewport) {
      setMobileSearchOpen(false);
    }
  }, [isMobileViewport, isPhoneViewport]);
  // The drawer sits above the page, so the page behind it must not scroll.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);
  // Mobile full-width gutters - ensure Upcoming/Recorded/All use same 12px gutters at <=767
  useEffect(() => {
    const applyMobileLayout = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      const isMobile = window.innerWidth <= 767;
      const gm = document.querySelector('.gmail-main');
      const gms = document.querySelector('.gmail-main-scroll');
      if (isMobile) {
        if (gm) gm.style.setProperty('padding','0','important');
        if (gms) gms.style.setProperty('padding','0','important');
        document.querySelectorAll('.gmail-page-flat .content-well, .gmail-page-flat .meet-well, .content-well, .meet-well').forEach(el=>{
          el.style.setProperty('padding-left','12px','important');
          el.style.setProperty('padding-right','12px','important');
          el.style.setProperty('padding-top','0','important');
          el.style.setProperty('padding-bottom','0','important');
        });
        // Ensure Upcoming/Recorded/All Meetings cards share same design
        document.querySelectorAll('.meet-list-row').forEach(el=>{
          el.style.setProperty('background','#FFFFFF','important');
          el.style.setProperty('border','1px solid #DCE6EF','important');
          el.style.setProperty('border-radius','10px','important');
        });
      } else {
        if (gm) gm.style.removeProperty('padding');
        if (gms) gms.style.removeProperty('padding');
        document.querySelectorAll('.gmail-page-flat .content-well, .gmail-page-flat .meet-well, .content-well, .meet-well').forEach(el=>{
          el.style.removeProperty('padding-left');
          el.style.removeProperty('padding-right');
          el.style.removeProperty('padding-top');
          el.style.removeProperty('padding-bottom');
        });
      }
    };
    applyMobileLayout();
    window.addEventListener('resize', applyMobileLayout);
    const observer = new MutationObserver(applyMobileLayout);
    observer.observe(document.body, {childList:true, subtree:true});
    return () => {
      window.removeEventListener('resize', applyMobileLayout);
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    try {
      window.localStorage?.setItem?.("meetai.gmail.rightRail.open", rightRailDrawerOpen ? "1" : "0");
      window.localStorage?.setItem?.("meetai.gmail.rightRail.panel", rightRailPanel);
    } catch (_) {}
  }, [rightRailDrawerOpen, rightRailPanel]);
  useEffect(() => {
    try {
      window.localStorage?.setItem?.("meetai.gmail.leftNav.collapsed", leftNavCollapsed ? "1" : "0");
    } catch (_) {}
  }, [leftNavCollapsed]);
  useEffect(() => () => {
    if (leftNavAnimTimerRef.current != null) {
      window.clearTimeout(leftNavAnimTimerRef.current);
    }
  }, []);
  const toggleLeftNavCollapsed = useCallback(() => {
    setLeftNavAnimating(true);
    if (leftNavAnimTimerRef.current != null) {
      window.clearTimeout(leftNavAnimTimerRef.current);
    }
    leftNavAnimTimerRef.current = window.setTimeout(() => {
      setLeftNavAnimating(false);
      leftNavAnimTimerRef.current = null;
    }, 340);
    setLeftNavCollapsed((v) => !v);
  }, []);
  const [showInstant,setShowInstant] = useState(false);
  const [createCalEventOpen, setCreateCalEventOpen] = useState(false);
  const [meetings,setMeetings]   = useState([]);
  const [loadingMeetings,setLoadingMeetings] = useState(shouldStartUserShellLoading);
  const [apiDownBanner, setApiDownBanner] = useState("");
  /** Browser-level connectivity. Retrying while offline can only fail again. */
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" && navigator.onLine === false
  );
  const meetingsListEtagRef = useRef("");
  const apiDownRef = useRef(false);
  /** True while Google Calendar events are fetching (not 30s background polls — avoids UI flicker). */
  const [calendarEventsLoading, setCalendarEventsLoading] = useState(false);
  const [googleCalendarEvents,setGoogleCalendarEvents] = useState([]);
  const [googleCalendarHint,setGoogleCalendarHint] = useState(null);
  const [googleCalendarWarning,setGoogleCalendarWarning] = useState(null);
  const [profileAvatarObjectUrl, setProfileAvatarObjectUrl] = useState("");
  const deepLinkMeetingConsumedRef = useRef(false);
  const deepLinkDetailFetchAttemptedRef = useRef(false);
  const calSelDateAffectsGoogleFetch =
    page === "calendar" &&
    (calView === "day" || calView === "week" || calView === "schedule" || calView === "fourDays");
  const googleCalFetchRange = useMemo(
    () => googleCalendarFetchRange(page, calView, calYear, calMonth, calSelDate),
    [page, calView, calYear, calMonth, calSelDateAffectsGoogleFetch ? calSelDate : null]
  );
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);
  const [accessPaused, setAccessPaused] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [headerUserDesignation, setHeaderUserDesignation] = useState("");
  const gmailSearchInputRef = useRef(null);
  const [userInfoMode, setUserInfoMode] = useState(null);
  const [meetAiUsageOpen, setMeetAiUsageOpen] = useState(false);
  const [userPrefs, setUserPrefs] = useState(() => getStoredUserPrefs() || {});
  const [autoJoinSaving, setAutoJoinSaving] = useState(false);
  const [meetingAutoJoinSaving, setMeetingAutoJoinSaving] = useState({});
  const [meetingRecordingSaving, setMeetingRecordingSaving] = useState({});
  const toggleMeetingRecordingRef = useRef(null);
  const meetPageOpenHandlerRef = useRef(() => {});
  const handleMeetPageOpen = useCallback((m, extra) => meetPageOpenHandlerRef.current(m, extra), []);
  /**
   * Confirmation dialog for recurring-series toggles. Set when a user flips a per-meeting
   * Auto Join toggle on a Google recurring event that has multiple upcoming instances.
   * Shape: { eventTitle, count, eventIds, currentEid, nextEnabled }
   */
  const [recurringPrompt, setRecurringPrompt] = useState(null);
  const [recurringRecordingPrompt, setRecurringRecordingPrompt] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now());
  const googleFetchSeqRef = useRef(0);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const userMenuRef = useRef(null);
  const feedbackBtnRef = useRef(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackAnchor, setFeedbackAnchor] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackAttachments, setFeedbackAttachments] = useState([]);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackStatusText, setFeedbackStatusText] = useState("");
  const [feedbackStatusKind, setFeedbackStatusKind] = useState("info");
  const [feedbackMeta, setFeedbackMeta] = useState({ developerEmail: "", developerExtension: "" });

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    const handleAuthExpired = () => {
      setSessionExpiredModalOpen(true);
      clearBlockOauthBack();
      clearToken();
      deepLinkMeetingConsumedRef.current = false;
      deepLinkDetailFetchAttemptedRef.current = false;
      clearMeetaiNavSession();
      setLoginEmail(null);
      setLoggedInUser(null);
      setMeetings([]);
      setGoogleCalendarEvents([]);
      setCalendarEventsLoading(false);
      setGoogleCalendarHint(null);
      setGoogleCalendarWarning(null);
    };
    window.addEventListener("meetai:auth-expired", handleAuthExpired);
    return () => window.removeEventListener("meetai:auth-expired", handleAuthExpired);
  }, []);

  // Company access paused — old sessions are signed out and shown a friendly card.
  useEffect(() => {
    const handlePaused = (e) => {
      const message = e?.detail?.message || APP_PAUSED_MESSAGE;
      setAccessPaused({ message });
      clearBlockOauthBack();
      clearToken();
      deepLinkMeetingConsumedRef.current = false;
      deepLinkDetailFetchAttemptedRef.current = false;
      clearMeetaiNavSession();
      setLoginEmail(null);
      setLoggedInUser(null);
      setMeetings([]);
      setGoogleCalendarEvents([]);
      setCalendarEventsLoading(false);
    };
    window.addEventListener("meetai:access-paused", handlePaused);
    return () => window.removeEventListener("meetai:access-paused", handlePaused);
  }, []);

  useEffect(() => {
    if (!loginEmail || isPrivilegedSkipUserDashboard(loginEmail)) return;
    setGoogleCalendarEvents((prev) => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      const cached = readCachedGoogleEvents(loginEmail);
      return cached.length > 0 ? cached : prev;
    });
  }, [loginEmail]);

  // Google OAuth return: #token=... in hash
  useEffect(() => {
    const raw = window.location.hash?.replace(/^#/, "") || "";
    const sp = new URLSearchParams(raw);
    const token = sp.get("token");
    if (!token) {
      setOauthPending(false);
      return;
    }
    setToken(token);
    try { sessionStorage.setItem('meetai_oauth_profile_loaded', '1'); } catch (_) {}
    markBlockOauthBack();
    // Drop #token and plant trap frames so Back cannot reach Google consent.
    const cleanUrl = window.location.pathname + window.location.search;
    try {
      window.history.replaceState({ meetai: true, oauthGate: true }, "", cleanUrl);
      window.history.pushState({ meetai: true, trap: true }, "", cleanUrl);
      window.history.pushState({ meetai: true, trap: true }, "", cleanUrl);
      window.history.pushState({ meetai: true, trap: true }, "", cleanUrl);
    } catch (_) {}
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) {
          if (data && data.code === "APP_ACCESS_PAUSED") {
            setAccessPaused({ message: data.error || APP_PAUSED_MESSAGE });
            clearToken();
            throw new Error(data.error || "paused");
          }
          throw new Error(data.error || "Session failed");
        }
        const normalizedUser = {
          ...data.user,
          avatar_url: data?.user?.avatar_url || data?.user?.avatarUrl || data?.user?.picture || data?.user?.photo || "",
          adminPortalAccess: data?.adminPortalAccess === true || data?.user?.adminPortalAccess === true,
        };
        localStorage.setItem("avaada_user", JSON.stringify(normalizedUser));
        setUserPrefs((prev) => {
          const next = { ...(prev || {}), autoJoinEnabled: normalizedUser?.autoJoinEnabled === true };
          try { localStorage.setItem("meetai_user_prefs", JSON.stringify(next)); } catch (_) {}
          return next;
        });
        setLoginEmail(normalizedUser.email);
        setLoggedInUser(normalizedUser);
      })
      .catch(() => {
        clearToken();
        try { sessionStorage.removeItem("meetai_oauth_profile_loaded"); } catch (_) {}
        setLoginEmail(null);
        setLoggedInUser(null);
      })
      .finally(() => setOauthPending(false));
  }, []);

  // Keep local user profile fresh (name/avatar) on normal reloads too.
  useEffect(() => {
    const token = getToken();
    if (!token || oauthPending) return;
    let oauthProfileLoaded = false;
    try { oauthProfileLoaded = sessionStorage.getItem('meetai_oauth_profile_loaded') === '1'; } catch (_) {}
    if (oauthProfileLoaded) return;
    if (loggedInUser?.email && loginEmail) return;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        const data = await r.json();
        if (data && data.code === "APP_ACCESS_PAUSED") {
          setAccessPaused({ message: data.error || APP_PAUSED_MESSAGE });
          clearToken();
          setLoginEmail(null);
          setLoggedInUser(null);
          return;
        }
        if (!r.ok || !data?.user?.email) {
          if (r.status === 401 || r.status === 403) {
            clearToken();
            try { sessionStorage.removeItem("meetai_oauth_profile_loaded"); } catch (_) {}
            setLoginEmail(null);
            setLoggedInUser(null);
          }
          return;
        }
        const normalizedUser = {
          ...data.user,
          avatar_url: data?.user?.avatar_url || data?.user?.avatarUrl || data?.user?.picture || data?.user?.photo || "",
          adminPortalAccess: data?.adminPortalAccess === true || data?.user?.adminPortalAccess === true,
        };
        localStorage.setItem("avaada_user", JSON.stringify(normalizedUser));
        setUserPrefs((prev) => {
          const next = { ...(prev || {}), autoJoinEnabled: normalizedUser?.autoJoinEnabled === true };
          try { localStorage.setItem("meetai_user_prefs", JSON.stringify(next)); } catch (_) {}
          return next;
        });
        setLoggedInUser(normalizedUser);
        setLoginEmail(normalizedUser.email);
      })
      .catch(() => {});
  }, [loginEmail, loggedInUser, oauthPending]);

  useEffect(() => {
    const token = getToken();
    if (!token || !loginEmail || oauthPending) return;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        const data = await r.json();
        if (data && data.code === "APP_ACCESS_PAUSED") {
          setAccessPaused({ message: data.error || APP_PAUSED_MESSAGE });
          clearToken();
          setLoginEmail(null);
          setLoggedInUser(null);
          return;
        }
        if (!r.ok || !data?.user?.email) {
          if (r.status === 401 || r.status === 403) {
            clearToken();
            try { sessionStorage.removeItem("meetai_oauth_profile_loaded"); } catch (_) {}
            setLoginEmail(null);
            setLoggedInUser(null);
          }
          return;
        }
        const normalizedUser = {
          ...data.user,
          avatar_url: data?.user?.avatar_url || data?.user?.avatarUrl || data?.user?.picture || data?.user?.photo || "",
          adminPortalAccess: data?.adminPortalAccess === true || data?.user?.adminPortalAccess === true,
        };
        localStorage.setItem("avaada_user", JSON.stringify(normalizedUser));
        setUserPrefs((prev) => {
          const next = { ...(prev || {}), autoJoinEnabled: normalizedUser?.autoJoinEnabled === true };
          try { localStorage.setItem("meetai_user_prefs", JSON.stringify(next)); } catch (_) {}
          return next;
        });
        setLoggedInUser(normalizedUser);
        if (!loginEmail) setLoginEmail(normalizedUser.email);
      })
      .catch(() => {});
  }, [loginEmail, oauthPending]);

  // Reminder prefs from DB (source of truth for soft prompt + Settings).
  useEffect(() => {
    if (!loginEmail || !getToken() || oauthPending) return;
    let cancelled = false;
    fetchMeetingReminderPrefs(apiFetch)
      .then((prefs) => {
        if (cancelled || !prefs) return;
        setUserPrefs((prev) => {
          const next = {
            ...(prev || {}),
            remindersEnabled: prefs.remindersEnabled,
            reminderMinutes: prefs.reminderMinutes,
            timezone: prefs.timezone || prev?.timezone,
            softPromptDismissedAt: prefs.softPromptDismissedAt,
            remindersSetupAt: prefs.remindersSetupAt,
            hasPushSubscription: prefs.hasSubscription,
            pushConfigured: prefs.pushConfigured,
          };
          try {
            localStorage.setItem("meetai_user_prefs", JSON.stringify(next));
          } catch (_) {}
          return next;
        });
        if (prefs.remindersSetupAt || prefs.hasSubscription) {
          markRemindersSetupDone();
        }
        if (prefs.softPromptDismissedAt) {
          try {
            localStorage.setItem(
              MEETAI_PUSH_PROMPT_DISMISSED_KEY,
              String(new Date(prefs.softPromptDismissedAt).getTime())
            );
          } catch (_) {}
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loginEmail, oauthPending]);

  // Load meetings from backend for any signed-in user (including admin-allowlist accounts).
  useEffect(()=>{
    if(loginEmail){
      setLoadingMeetings(true);
      apiFetchMeetingsList(meetingsListEtagRef.current)
        .then((result) => {
          if (result.etag) meetingsListEtagRef.current = result.etag;
          if (!result.notModified) {
            setMeetings((prev) => mergeMeetingsListPreserveDetail(prev, result.list));
          }
          setLastUpdatedAt(Date.now());
          apiDownRef.current = false;
          setApiDownBanner("");
        })
        .catch((err) => {
          console.error("Failed to load meetings:", err);
          // Keep last good data when API/DB is down — don't wipe the UI.
          setMeetings((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : []));
          setApiDownBanner(err?.message || "MeetAI is temporarily unavailable.");
          apiDownRef.current = true;
        })
        .finally(()=>setLoadingMeetings(false));
    }
  },[loginEmail, refreshTick]);

  useEffect(() => {
    const onDown = (ev) => {
      apiDownRef.current = true;
      setApiDownBanner(ev?.detail?.message || "MeetAI is temporarily unavailable.");
    };
    const onUp = () => {
      if (!apiDownRef.current) return;
      apiDownRef.current = false;
      setApiDownBanner("");
    };
    // Losing the network raises no request error until something is fetched, so
    // flag it here instead of leaving a normal-looking UI on a dead connection.
    const onOffline = () => {
      apiDownRef.current = true;
      setIsOffline(true);
      setApiDownBanner("You're offline. Check your internet connection.");
    };
    // Coming back online refetches: waiting for the next poll leaves stale data
    // on screen with no sign that it is stale.
    const onOnline = () => {
      setIsOffline(false);
      if (!apiDownRef.current) return;
      setApiDownBanner("");
      setRefreshTick((v) => v + 1);
    };
    window.addEventListener("meetai:api-down", onDown);
    window.addEventListener("meetai:api-up", onUp);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    if (typeof navigator !== "undefined" && navigator.onLine === false) onOffline();
    return () => {
      window.removeEventListener("meetai:api-down", onDown);
      window.removeEventListener("meetai:api-up", onUp);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  useEffect(() => {
    tourAutoStartedRef.current = false;
  }, [loginEmail]);

  /** First login per user (per browser): auto-start full app tour once (Auto Join / Video Rec highlighted). */
  useEffect(() => {
    if (!loginEmail || loadingMeetings || oauthPending || tourOpen) return;
    const key = appTourStorageKey(loginEmail);
    try {
      if (localStorage.getItem(key) === "1") return;
    } catch (_) {}
    if (tourAutoStartedRef.current) return;
    tourAutoStartedRef.current = true;
    const t = window.setTimeout(() => startAppTour(0), 900);
    return () => window.clearTimeout(t);
  }, [loginEmail, loadingMeetings, oauthPending, tourOpen, startAppTour]);

  useEffect(() => {
    meetingRecordingModalRef.open = (meetingOrId, titleHint = "") => {
      const apiId =
        meetingOrId && typeof meetingOrId === "object"
          ? meetingRecordingApiId(meetingOrId)
          : String(meetingOrId || "").trim();
      if (!apiId) return;
      const title =
        (meetingOrId && typeof meetingOrId === "object" ? meetingOrId.title : "") ||
        titleHint ||
        "Meeting recording";
      setRecordingModalPayload({
        apiId,
        title,
        meeting:
          meetingOrId && typeof meetingOrId === "object" ? normalizeMeeting(meetingOrId) : null,
      });
    };
    return () => {
      meetingRecordingModalRef.open = null;
    };
  }, []);

  // Peak-safe lite poller: ETag 304s, slower when tab hidden / API down.
  useEffect(()=>{
    if(!loginEmail || isPrivilegedSkipUserDashboard(loginEmail)) return;
    if (page === "howto") return;
    let cancelled = false;
    let timer = null;

    const baseInterval = () => {
      if (typeof document !== "undefined" && document.hidden) return MEETINGS_LIST_POLL_HIDDEN_MS;
      if (apiDownRef.current) return MEETINGS_LIST_POLL_DOWN_MS;
      const needsFasterPoll =
        page === "transcripts" ||
        page === "detail" ||
        page === "actions" ||
        page === "autojoin" ||
        page === "dashboard" ||
        page === "meetings" ||
        page === "calendar";
      return needsFasterPoll ? MEETINGS_LIST_POLL_ACTIVE_MS : MEETINGS_LIST_POLL_MS;
    };

    const tick = () => {
      apiFetchMeetingsList(meetingsListEtagRef.current)
        .then((result) => {
          if (cancelled) return;
          if (result.etag) meetingsListEtagRef.current = result.etag;
          if (!result.notModified) {
            setMeetings((prev) => mergeMeetingsListPreserveDetail(prev, result.list));
            setLastUpdatedAt(Date.now());
          }
          apiDownRef.current = false;
          setApiDownBanner("");
        })
        .catch(() => {
          if (!cancelled) apiDownRef.current = true;
        })
        .finally(() => {
          if (cancelled) return;
          timer = setTimeout(tick, baseInterval());
        });
    };

    timer = setTimeout(tick, baseInterval());
    const onVisibility = () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && !document.hidden) {
        clearTimeout(timer);
        timer = setTimeout(tick, 1000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  },[loginEmail, page]);

  // Google Calendar: only tabs that merge calendar into the UI (not Transcripts / Tasks / Detail / How-to).
  useEffect(() => {
    if (!loginEmail) return;
    const needsGoogle =
      page === "calendar" ||
      page === "dashboard" ||
      page === "meetings" ||
      page === "autojoin";
    if (!needsGoogle) return;
    const { from, to } = googleCalFetchRange;
    let active = true;
    const fetchGoogleEvents = (showLoadingUi) => {
      if (showLoadingUi) setCalendarEventsLoading(true);
      const fetchSeq = ++googleFetchSeqRef.current;
      apiFetch(`/calendar/google-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
        .then((data) => {
          if (!active) return;
          if (fetchSeq !== googleFetchSeqRef.current) return; // ignore stale response
          const raw = Array.isArray(data.events) ? data.events : [];
          const normalized = raw.map(normalizeMeeting);
          const fromMs = startOfLocalDayMs(from);
          const toMs = endOfLocalDayMs(to);
          const clamped =
            fromMs && toMs
              ? normalized.filter((ev) => {
                  const ms = meetingDateTimeMs(ev);
                  if (!ms) return true;
                  return ms >= fromMs && ms <= toMs;
                })
              : normalized;
          setGoogleCalendarEvents((prev) => {
            // Avoid sudden UI drops when API briefly returns an empty payload.
            // Keep last known good snapshot unless calendar is explicitly unlinked.
            if (clamped.length === 0 && data?.linked !== false && Array.isArray(prev) && prev.length > 0) {
              return prev;
            }
            // Some refreshes can return a partial subset temporarily.
            // Merge with previous snapshot so cards don't disappear for a moment.
            if (data?.linked !== false && Array.isArray(prev) && prev.length > 0 && clamped.length > 0) {
              const mergedEvents = mergeGoogleCalendarSnapshot(prev, clamped, false).filter((ev) => {
                const ms = meetingDateTimeMs(ev);
                if (!ms || !fromMs || !toMs) return true;
                return ms >= fromMs && ms <= toMs;
              });
              writeCachedGoogleEvents(loginEmail, mergedEvents);
              return mergedEvents;
            }
            writeCachedGoogleEvents(loginEmail, clamped);
            return clamped;
          });
          setLastUpdatedAt(Date.now());
          if (!data.linked) {
            setGoogleCalendarHint(data.message || null);
            setGoogleCalendarWarning(null);
          } else {
            setGoogleCalendarHint(null);
            setGoogleCalendarWarning(data.warning ? friendlyCalendarBannerText(data.warning) : null);
          }
        })
        .catch((err) => {
          if (!active) return;
          if (fetchSeq !== googleFetchSeqRef.current) return; // ignore stale response
          // Keep the last successful calendar snapshot instead of blanking the UI
          // on transient API/network errors. This prevents "upcoming appears then disappears".
          setGoogleCalendarHint(null);
          setGoogleCalendarWarning(friendlyCalendarBannerText(err.message));
        })
        .finally(() => {
          if (fetchSeq !== googleFetchSeqRef.current) return;
          setCalendarEventsLoading(false);
        });
    };
    // Full-page skeleton only on first calendar tab visit without cache (not Home/Meetings).
    const showCalendarLoadingUi =
      page === "calendar" &&
      googleCalendarEvents.length === 0 &&
      readCachedGoogleEvents(loginEmail).length === 0;
    fetchGoogleEvents(showCalendarLoadingUi);
    // Calendar tab needs fresher sync; other tabs can poll less often; pause when tab hidden.
    const googlePollMs = page === "calendar" ? 30000 : 60000;
    const refreshId = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (apiDownRef.current) return;
      fetchGoogleEvents(false);
    }, googlePollMs);
    return () => {
      active = false;
      clearInterval(refreshId);
    };
  }, [loginEmail, page, googleCalFetchRange.from, googleCalFetchRange.to, refreshTick]);
  
  useEffect(() => {
    if (!manualRefreshing) return;
    const t = setTimeout(() => setManualRefreshing(false), 800);
    return () => clearTimeout(t);
  }, [manualRefreshing]);

  // Backward compatibility for approval emails sent before the meeting
  // deep-link fix. `/transcripts/:id` was never a detail route; normalize it
  // before login or page selection so old emails cannot fall back to Home.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const match = normalizePathname(window.location.pathname).match(/^\/transcripts\/([^/]+)$/i);
    if (!match) return;
    const meetingId = decodeURIComponent(match[1]);
    if (!meetingId) return;
    const params = new URLSearchParams(window.location.search || "");
    params.set("m", meetingId);
    window.history.replaceState(null, "", `/meetings?${params.toString()}`);
  }, []);

  // Email / share link ?m=<meetingId>&recording=1 opens detail + recording modal once after load
  // Note: must run for ALL logged-in users, including admin/bot-listed emails — recap emails
  // link real users into this deep link regardless of admin allowlist membership.
  useEffect(() => {
    if (!loginEmail) return;
    if (typeof window !== "undefined" && isAdminAppPath(window.location.pathname)) return;
    if (typeof window !== "undefined" && isRecordingSharePath(window.location.pathname)) return;
    if (loadingMeetings) return;
    if (deepLinkMeetingConsumedRef.current) return;
    const sp = new URLSearchParams(window.location.search);
    const mid = sp.get("m");
    if (!mid) return;
    const openFromMeeting = (m) => {
      deepLinkMeetingConsumedRef.current = true;
      const norm = normalizeMeeting(m);
      const openRecording =
        sp.get("recording") === "1" || String(sp.get("recording") || "").toLowerCase() === "true";
      const fromPath = userPageFromPathname(window.location.pathname);
      const from =
        fromPath && ALLOWED_USER_PAGES.has(fromPath) && fromPath !== "detail"
          ? fromPath
          : "meetings";
      setSelMeeting(norm);
      setPage("detail");
      setActiveTab("overview");
      setDetailOpenedFrom(from);
      try { sessionStorage.setItem(MEETAI_DETAIL_FROM_KEY, from); } catch (_) {}
      if (openRecording) {
        queueMicrotask(() => openMeetingRecordingModal(norm));
      }
      try {
        const clean = new URLSearchParams();
        clean.set("m", String(norm.id ?? norm._id ?? mid));
        window.history.replaceState(null, "", `${pathForUserPage(from)}?${clean.toString()}`);
      } catch (_) {}
      apiFetch(`/meetings/${encodeURIComponent(norm.id ?? mid)}`)
        .then((fresh) => {
          if (!fresh?.id || !meetingDetailMatchesOpened(norm, fresh)) return;
          setSelMeeting((prev) => mergeMeetingDetailFromApi(prev || norm, fresh));
        })
        .catch(() => {});
    };
    const m = meetings.find((x) => String(x.id) === String(mid) || String(x._id) === String(mid));
    if (m) {
      openFromMeeting(m);
      return;
    }
    if (deepLinkDetailFetchAttemptedRef.current) return;
    deepLinkDetailFetchAttemptedRef.current = true;
    let cancelled = false;
    apiFetch(`/meetings/${encodeURIComponent(mid)}`)
      .then((fresh) => {
        if (cancelled) return;
        if (fresh?.id) {
          openFromMeeting(fresh);
          return;
        }
        deepLinkMeetingConsumedRef.current = true;
        try {
          const clean = new URLSearchParams(window.location.search || "");
          clean.delete("m");
          clean.delete("recording");
          const q = clean.toString();
          window.history.replaceState(null, "", `/meetings${q ? `?${q}` : ""}`);
        } catch (_) {}
      })
      .catch(() => {
        if (cancelled) return;
        deepLinkMeetingConsumedRef.current = true;
        try {
          const clean = new URLSearchParams(window.location.search || "");
          clean.delete("m");
          clean.delete("recording");
          const q = clean.toString();
          window.history.replaceState(null, "", `/meetings${q ? `?${q}` : ""}`);
        } catch (_) {}
      });
    return () => { cancelled = true; };
  }, [loginEmail, loadingMeetings, meetings]);

  // Restore meeting detail after refresh (when last route was detail)
  useLayoutEffect(() => {
    if (!loginEmail || isPrivilegedSkipUserDashboard(loginEmail)) return;
    if (loadingMeetings) return;
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("m")) return;
      const savedPage = sessionStorage.getItem(MEETAI_USER_PAGE_KEY);
      const mid = sessionStorage.getItem(MEETAI_DETAIL_ID_KEY);
      if (savedPage !== "detail" || !mid) return;
      const curId = selMeeting?.id ?? selMeeting?._id;
      if (curId != null && String(curId) === String(mid)) return;
      const m = meetings.find((x) => String(x.id) === String(mid) || String(x._id) === String(mid));
      if (m) {
        setSelMeeting(normalizeMeeting(m));
        const from = sessionStorage.getItem(MEETAI_DETAIL_FROM_KEY);
        const tab = sessionStorage.getItem(MEETAI_DETAIL_TAB_KEY);
        const restoreTab =
          from === "transcripts"
            ? tab === "transcript"
              ? "transcript"
              : "overview"
            : from === "autojoin"
              ? ["overview", "transcript", "recording", "action-items"].includes(tab)
                ? tab
                : "action-items"
              : "overview";
        if (["overview", "transcript", "recording", "action-items"].includes(restoreTab)) {
          setActiveTab(restoreTab);
        }
        if (from && ALLOWED_USER_PAGES.has(from) && from !== "detail") setDetailOpenedFrom(from);
        apiFetch(`/meetings/${encodeURIComponent(mid)}`)
          .then((fresh) => {
            if (fresh?.id) setSelMeeting((prev) => mergeMeetingDetailFromApi(prev || m, fresh));
          })
          .catch(() => {});
      } else {
        setPage("autojoin");
        sessionStorage.setItem(MEETAI_USER_PAGE_KEY, "autojoin");
        sessionStorage.removeItem(MEETAI_DETAIL_ID_KEY);
        sessionStorage.removeItem(MEETAI_DETAIL_TAB_KEY);
        sessionStorage.removeItem(MEETAI_DETAIL_FROM_KEY);
      }
    } catch (_) {}
  }, [loginEmail, loadingMeetings, meetings]);

  // Persist current route for refresh
  useEffect(() => {
    if (!loginEmail || isPrivilegedSkipUserDashboard(loginEmail)) return;
    try {
      sessionStorage.setItem(MEETAI_USER_PAGE_KEY, page);
      if (page === "detail" && selMeeting) {
        const id = selMeeting.id ?? selMeeting._id;
        if (id != null) {
          sessionStorage.setItem(MEETAI_DETAIL_ID_KEY, String(id));
          sessionStorage.setItem(MEETAI_DETAIL_TAB_KEY, activeTab);
        }
        if (detailOpenedFrom && ALLOWED_USER_PAGES.has(detailOpenedFrom) && detailOpenedFrom !== "detail") {
          sessionStorage.setItem(MEETAI_DETAIL_FROM_KEY, detailOpenedFrom);
        }
      } else if (page !== "detail") {
        sessionStorage.removeItem(MEETAI_DETAIL_ID_KEY);
        sessionStorage.removeItem(MEETAI_DETAIL_TAB_KEY);
        sessionStorage.removeItem(MEETAI_DETAIL_FROM_KEY);
      }
    } catch (_) {}
  }, [loginEmail, page, selMeeting, activeTab, detailOpenedFrom]);

  // Keep URL in sync with page state so refresh/back-forward keep context.
  // Note: must run for ALL logged-in users, including admin/bot-listed emails — otherwise
  // the address bar never updates while navigating (Meetings -> Dashboard -> Recorded Meetings, etc).
  useEffect(() => {
    if (!loginEmail) return;
    if (typeof window !== "undefined" && isAdminAppPath(window.location.pathname)) return;
    if (typeof window !== "undefined" && isRecordingSharePath(window.location.pathname)) return;
    const currentPath = normalizePathname(window.location.pathname);
    // Keep detail under the originating tab's path so Back/refresh stay in context
    // (was always /meetings — which sent users to All Meetings).
    const detailOrigin =
      detailOpenedFrom && ALLOWED_USER_PAGES.has(detailOpenedFrom) && detailOpenedFrom !== "detail"
        ? detailOpenedFrom
        : null;
    const targetPath =
      page === "detail"
        ? pathForUserPage(detailOrigin || "meetings")
        : pathForUserPage(page);
    const sp = new URLSearchParams(window.location.search || "");
    if (page === "detail" && selMeeting) {
      const mid = selMeeting.id ?? selMeeting._id;
      if (mid != null) sp.set("m", String(mid));
    } else {
      // Do NOT strip ?m= until the email/deep-link effect has opened the meeting.
      // Otherwise we race to `/meetings` and lose the meeting id.
      const pendingDeepLink = Boolean(sp.get("m")) && !deepLinkMeetingConsumedRef.current;
      if (!pendingDeepLink) sp.delete("m");
    }
    // Keep ?recording=1 until deep-link effect consumes it (opens recording modal).
    const pendingRecording =
      Boolean(sp.get("m")) &&
      !deepLinkMeetingConsumedRef.current &&
      (sp.get("recording") === "1" || String(sp.get("recording") || "").toLowerCase() === "true");
    if (!pendingRecording) sp.delete("recording");
    const nextSearch = sp.toString();
    const nextUrl = `${targetPath}${nextSearch ? `?${nextSearch}` : ""}`;
    const curUrl = `${currentPath}${window.location.search || ""}`;
    if (curUrl === nextUrl) return;
    const prevState =
      typeof window.history.state === "object" && window.history.state ? { ...window.history.state } : {};
    // Always replaceState for SPA routes — browser Back is trapped separately so it
    // cannot fall through to Google OAuth. In-app Back uses setPage, not history.back().
    window.history.replaceState(
      { ...prevState, meetai: true, page, detailFrom: detailOrigin || null },
      "",
      nextUrl
    );
  }, [loginEmail, page, selMeeting?.id, selMeeting?._id, detailOpenedFrom]);

  // Handle browser back/forward + hard-block return to Google OAuth after login.
  useEffect(() => {
    if (!loginEmail) return;
    markBlockOauthBack();
    // Seed trap frames while session is active.
    pushMeetaiHistoryTrap({ page });

    const syncPageFromUrl = () => {
      const sp = new URLSearchParams(window.location.search || "");
      if (sp.get("m")) {
        setPage("detail");
        const fromPath = userPageFromPathname(window.location.pathname);
        if (fromPath && ALLOWED_USER_PAGES.has(fromPath) && fromPath !== "detail") {
          setDetailOpenedFrom(fromPath);
          try { sessionStorage.setItem(MEETAI_DETAIL_FROM_KEY, fromPath); } catch (_) {}
        }
        return;
      }
      const nextPage = userPageFromPathname(window.location.pathname);
      if (nextPage) setPage(nextPage);
    };

    const onPopState = () => {
      // Critical: re-push current MeetAI URL so Back never reaches accounts.google.com.
      if (shouldBlockOauthBack()) {
        pushMeetaiHistoryTrap();
      }
      syncPageFromUrl();
    };

    const onPageShow = (ev) => {
      // bfcache restore after leaving to Google — reseal immediately.
      if (ev.persisted && shouldBlockOauthBack()) {
        pushMeetaiHistoryTrap();
      }
    };

    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [loginEmail]);

  // Detail page: merge lite list updates without wiping full transcript from GET /:id
  useEffect(() => {
    if (page !== "detail" || !selMeeting) return;
    const id = selMeeting.id ?? selMeeting._id;
    if (id == null) return;
    const updated = meetings.find((m) => (m.id ?? m._id) === id);
    if (updated) {
      setSelMeeting((prev) => {
        if (!prev || !meetingDetailMatchesOpened(prev, updated)) return prev;
        return mergeMeetingDetailFromApi(prev, updated);
      });
    }
  }, [meetings, page, selMeeting?.id, selMeeting?._id]);

  function handleLogout(){
    setSessionExpiredModalOpen(false);
    deepLinkMeetingConsumedRef.current = false;
    deepLinkDetailFetchAttemptedRef.current = false;
    clearMeetaiNavSession();
    clearBlockOauthBack();
    clearToken();
    // Keep reminders snooze across logout so "Maybe later" is not asked again immediately.
    setLoginEmail(null);
    setLoggedInUser(null);
    setMeetings([]);
    setGoogleCalendarEvents([]);
    setCalendarEventsLoading(false);
    setGoogleCalendarHint(null);
    setGoogleCalendarWarning(null);
    try {
      if (typeof window !== "undefined" && isAdminAppPath(window.location.pathname)) {
        window.history.replaceState(null, "", "/");
      }
    } catch (_) {}
  }

  async function openFeedbackPopover(anchorRect) {
    const rect = anchorRect || feedbackBtnRef.current?.getBoundingClientRect?.();
    if (rect) {
      setFeedbackAnchor({
        top: Math.round(rect.bottom + 8),
        right: Math.round(Math.max(10, window.innerWidth - rect.right)),
      });
    } else {
      setFeedbackAnchor({ top: 74, right: 20 });
    }
    setFeedbackStatusText("");
    setFeedbackStatusKind("info");
    setFeedbackOpen(true);
    try {
      const meta = await apiFetch("/feedback/meta");
      const developerEmail = String(meta?.developerEmail || "").trim();
      const developerExtension = String(meta?.developerExtension || "").trim();
      setFeedbackMeta({
        developerEmail,
        developerExtension,
      });
      if (!developerEmail && !developerExtension) {
        setFeedbackStatusText("Developer details not configured in backend .env.");
        setFeedbackStatusKind("info");
      }
    } catch {
      setFeedbackMeta({ developerEmail: "", developerExtension: "" });
      setFeedbackStatusText("Unable to load developer details. Check backend restart.");
      setFeedbackStatusKind("error");
    }
  }

  function closeFeedbackPopover() {
    if (feedbackBusy) return;
    setFeedbackOpen(false);
    setFeedbackHoverRating(0);
    setFeedbackStatusText("");
    setFeedbackStatusKind("info");
  }

  async function onPickFeedbackFiles(fileList) {
    const selected = Array.from(fileList || []);
    if (!selected.length) return;
    const remaining = FEEDBACK_MAX_ATTACHMENTS - feedbackAttachments.length;
    if (remaining <= 0) {
      setFeedbackStatusText("Maximum 5 attachments allowed.");
      setFeedbackStatusKind("error");
      return;
    }
    const validFiles = selected.filter((f) => FEEDBACK_ALLOWED_FILE_TYPES.has(String(f.type || "").toLowerCase()));
    const invalidCount = selected.length - validFiles.length;
    const limited = validFiles.slice(0, remaining);
    if (!limited.length) {
      setFeedbackStatusText("Only JPG, JPEG, and PNG files are supported.");
      setFeedbackStatusKind("error");
      return;
    }
    try {
      const parsed = await Promise.all(
        limited.map(async (file) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          filename: file.name || "screenshot.png",
          contentType: String(file.type || "image/png").toLowerCase(),
          contentBase64: await readFileAsBase64(file),
        }))
      );
      setFeedbackAttachments((prev) => [...prev, ...parsed].slice(0, FEEDBACK_MAX_ATTACHMENTS));
      if (invalidCount > 0) {
        setFeedbackStatusText(`Added ${parsed.length} file(s). Skipped ${invalidCount} unsupported file(s).`);
        setFeedbackStatusKind("info");
      } else if (validFiles.length > remaining) {
        setFeedbackStatusText(`Added ${parsed.length} file(s). Maximum 5 attachments allowed.`);
        setFeedbackStatusKind("info");
      } else {
        setFeedbackStatusText("");
        setFeedbackStatusKind("info");
      }
    } catch {
      setFeedbackStatusText("Unable to attach file. Please try again.");
      setFeedbackStatusKind("error");
    }
  }

  function removeFeedbackAttachment(id) {
    setFeedbackAttachments((prev) => prev.filter((f) => f.id !== id));
  }

  async function submitFeedback() {
    if (!feedbackRating || feedbackBusy) return;
    setFeedbackBusy(true);
    setFeedbackStatusText("");
    setFeedbackStatusKind("info");
    try {
      const meetingId = selMeeting?.id ?? selMeeting?._id;
      await apiFetch("/feedback/submit", {
        method: "POST",
        body: {
          rating: feedbackRating,
          message: feedbackMessage.trim(),
          page: meetingId != null ? `${page} (meeting:${meetingId})` : page,
          developerEmail: feedbackMeta?.developerEmail || "",
          developerExtension: feedbackMeta?.developerExtension || "",
          attachments: feedbackAttachments.map((f) => ({
            filename: f.filename,
            contentType: f.contentType,
            contentBase64: f.contentBase64,
          })),
        },
      });
      setFeedbackStatusText("Thank you for your feedback! We will sincerely review your rating.");
      setFeedbackStatusKind("success");
      setFeedbackMessage("");
      setFeedbackRating(0);
      setFeedbackHoverRating(0);
      setFeedbackAttachments([]);
    } catch (err) {
      setFeedbackStatusText(err?.message || "Failed to send feedback.");
      setFeedbackStatusKind("error");
    } finally {
      setFeedbackBusy(false);
    }
  }

  const activeUser = loggedInUser || userFromSession(loginEmail);
  const saveUserProfile = ({ name, timezone }) => {
    const nextUser = {
      ...(activeUser || {}),
      ...(loggedInUser || {}),
      name: name || activeUser?.name || "User",
    };
    setLoggedInUser(nextUser);
    try {
      localStorage.setItem("avaada_user", JSON.stringify(nextUser));
    } catch (_) {}
    setUserPrefs((prev) => {
      const next = { ...(prev || {}), timezone: timezone || prev?.timezone };
      try {
        localStorage.setItem("meetai_user_prefs", JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  const saveUserPreferences = (updates) => {
    setUserPrefs((prev) => {
      const next = { ...(prev || {}), ...(updates || {}) };
      try {
        localStorage.setItem("meetai_user_prefs", JSON.stringify(next));
      } catch (_) {}
      // Persist reminder prefs + (un)subscribe Web Push (VAPID) — works when tab/Chrome closed.
      syncMeetingReminderPrefs(apiFetch, next).catch((err) => {
        console.warn("[push] reminder sync failed:", err?.message || err);
      });
      return next;
    });
  };

  const [notificationSoftPromptOpen, setNotificationSoftPromptOpen] = useState(false);
  const [notificationPromptBlocked, setNotificationPromptBlocked] = useState(false);

  // Auto-subscribe when tab focuses if permission was flipped to 'granted' in browser settings
  useEffect(() => {
    if (!SHOW_MEETING_REMINDERS) return;
    if (!loggedInUser?.email || !getToken()) return;
    const cleanup = setupPushPermissionFocusListener(apiFetch, (newPerm) => {
      if (newPerm === "granted") {
        setNotificationSoftPromptOpen(false);
      }
    });
    return cleanup;
  }, [loggedInUser?.email]);

  // Soft Pre-Prompt — driven by DB prefs (reminders_setup_at / soft_prompt_dismissed_at).
  useEffect(() => {
    if (!SHOW_MEETING_REMINDERS) return;
    if (!loggedInUser?.email || !getToken()) return;
    if (userPrefs?.remindersEnabled === false) {
      clearRemindersSetupDone();
      setNotificationSoftPromptOpen(false);
      return;
    }
    if (!pushSupported()) return;

    let cancelled = false;
    let timer = null;

    (async () => {
      const perm = typeof Notification !== "undefined" ? Notification.permission : "denied";
      const setupDone =
        isRemindersSetupDone(userPrefs?.remindersSetupAt) ||
        userPrefs?.hasPushSubscription === true;
      const snoozed = isPushPromptSnoozed(userPrefs?.softPromptDismissedAt);

      if (setupDone || snoozed) {
        if (!cancelled) setNotificationSoftPromptOpen(false);
        if (setupDone) markRemindersSetupDone();
      }

      if (perm === "granted") {
        markRemindersSetupDone();
        if (!userPrefs?.remindersSetupAt) {
          markMeetingRemindersSetup(apiFetch, userPrefs).catch(() => {});
        }
        if (!cancelled) setNotificationSoftPromptOpen(false);
        const prefs = {
          remindersEnabled: true,
          reminderMinutes: Number(userPrefs?.reminderMinutes) || 10,
          timezone: userPrefs?.timezone,
        };
        ensureDefaultReminderPushOnLogin(apiFetch, prefs).catch(() => {});
        return;
      }

      if (setupDone || snoozed) return;

      if (await hasLocalPushSubscription()) {
        markRemindersSetupDone();
        markMeetingRemindersSetup(apiFetch, userPrefs).catch(() => {});
        if (!cancelled) setNotificationSoftPromptOpen(false);
        return;
      }

      if (cancelled) return;
      if (perm !== "default" && perm !== "denied") return;
      setNotificationPromptBlocked(perm === "denied");
      timer = setTimeout(() => {
        if (
          cancelled ||
          isRemindersSetupDone(userPrefs?.remindersSetupAt) ||
          isPushPromptSnoozed(userPrefs?.softPromptDismissedAt) ||
          userPrefs?.hasPushSubscription === true
        ) {
          return;
        }
        setNotificationSoftPromptOpen(true);
      }, 1500);
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    loggedInUser?.email,
    userPrefs?.remindersEnabled,
    userPrefs?.reminderMinutes,
    userPrefs?.remindersSetupAt,
    userPrefs?.softPromptDismissedAt,
    userPrefs?.hasPushSubscription,
  ]);

  const handleAllowFromSoftPrompt = async () => {
    setNotificationSoftPromptOpen(false);
    try {
      const result = await enableMeetingReminderPush(apiFetch);
      const granted =
        typeof Notification !== "undefined" && Notification.permission === "granted";
      if (result?.ok || granted) {
        markRemindersSetupDone();
        const saved = await markMeetingRemindersSetup(apiFetch, userPrefs).catch(() => null);
        if (saved) {
          setUserPrefs((prev) => ({
            ...(prev || {}),
            remindersEnabled: true,
            remindersSetupAt: saved.remindersSetupAt || new Date().toISOString(),
            hasPushSubscription: saved.hasSubscription === true || result?.ok === true,
          }));
        }
      } else {
        markPushPromptDismissed();
        const saved = await dismissMeetingReminderSoftPrompt(apiFetch, userPrefs).catch(() => null);
        if (saved?.softPromptDismissedAt) {
          setUserPrefs((prev) => ({
            ...(prev || {}),
            softPromptDismissedAt: saved.softPromptDismissedAt,
          }));
        }
      }
    } catch (_) {
      markPushPromptDismissed();
      dismissMeetingReminderSoftPrompt(apiFetch, userPrefs).catch(() => {});
    }
  };

  const handleDismissSoftPrompt = () => {
    setNotificationSoftPromptOpen(false);
    markPushPromptDismissed();
    dismissMeetingReminderSoftPrompt(apiFetch, userPrefs)
      .then((saved) => {
        if (!saved?.softPromptDismissedAt) return;
        setUserPrefs((prev) => ({
          ...(prev || {}),
          softPromptDismissedAt: saved.softPromptDismissedAt,
        }));
      })
      .catch(() => {});
  };


  // Source of truth for global auto-join is backend user profile (/auth/me, /auth/auto-join).
  const autoJoinEnabled = loggedInUser?.autoJoinEnabled === true;

  const applyGlobalAutoJoin = async (enabled, { alsoVideoRec = false } = {}) => {
    if (autoJoinSaving) return;
    setAutoJoinSaving(true);
    try {
      const data = await apiFetch("/auth/auto-join", {
        method: "PUT",
        body: { enabled: enabled === true },
      });
      // Persist global preference immediately; per-event overrides sync is best-effort.
      const nextUser = {
        ...(loggedInUser || activeUser || {}),
        ...(data?.user || {}),
      };
      setLoggedInUser(nextUser);
      setUserPrefs((prev) => {
        const next = { ...(prev || {}), autoJoinEnabled: Boolean(data?.user?.autoJoinEnabled) };
        try {
          localStorage.setItem("meetai_user_prefs", JSON.stringify(next));
        } catch (_) {}
        return next;
      });
      try {
        localStorage.setItem("avaada_user", JSON.stringify(nextUser));
      } catch (_) {}

      const eventIdsSet = new Set(
        (Array.isArray(googleCalendarEvents) ? googleCalendarEvents : [])
          .map((ev) => String(ev?.googleEventId || "").trim())
          .filter(Boolean)
      );
      for (const m of Array.isArray(meetings) ? meetings : []) {
        const eid = String(m?.googleEventId || "").trim();
        if (eid) eventIdsSet.add(eid);
      }
      // Ensure global toggle applies to the active schedule window (not multi-year dump).
      const start = new Date();
      start.setDate(start.getDate() - 30);
      const end = new Date();
      end.setDate(end.getDate() + 60);
      const from = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      const to = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
      try {
        const fullWindow = await apiFetch(`/calendar/google-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
        const idsFromWindow = (Array.isArray(fullWindow?.events) ? fullWindow.events : [])
          .map((ev) => String(ev?.googleEventId || ev?.id || "").trim())
          .filter(Boolean);
        for (const eid of idsFromWindow) eventIdsSet.add(eid);
      } catch (_) {
        // Keep best-effort behavior using locally available IDs.
      }
      const eventIds = Array.from(eventIdsSet);

      // Master-switch semantics: global toggle propagates the same value to every
      // per-meeting override in the active window. ON -> all ON, OFF -> all OFF.
      if (eventIds.length > 0) {
        try {
          await apiFetch("/calendar/auto-join/google-events", {
            method: "PUT",
            body: { enabled: enabled === true, eventIds },
          });
        } catch (bulkErr) {
          console.warn(
            "Auto-join event sync failed (global setting saved):",
            bulkErr?.message || bulkErr
          );
        }
      }

      // Mirror the same value into local state + cache so every card flips together.
      setGoogleCalendarEvents((prev) => {
        const next = (Array.isArray(prev) ? prev : []).map((ev) => ({
          ...ev,
          autoJoinEnabled: enabled === true,
        }));
        writeCachedGoogleEvents(loginEmail, next);
        return next;
      });
      setMeetings((prev) =>
        (Array.isArray(prev) ? prev : []).map((m) =>
          (m?.type === "google_calendar" || m?.type === "calendar_auto")
            ? { ...m, autoJoinEnabled: enabled === true }
            : m
        )
      );

      if (enabled === true && alsoVideoRec === true && eventIds.length > 0) {
        await enableVideoRecForEventIds(eventIds);
      }

      if (!enabled && eventIds.length > 0 && typeof toggleMeetingRecordingRef.current === "function") {
        const snapshot = [
          ...(Array.isArray(googleCalendarEvents) ? googleCalendarEvents : []),
          ...(Array.isArray(meetings) ? meetings : []),
        ];
        for (const eid of eventIds) {
          const row = snapshot.find((m) => extractGoogleEventId(m) === eid);
          if (meetingRecordingStoredOn(row)) {
            await toggleMeetingRecordingRef.current(row, false, {
              skipRecurringPrompt: true,
              silentError: true,
            });
          }
        }
      }
    } catch (err) {
      window.alert(err?.message || "Failed to update global auto-join preference.");
    } finally {
      setAutoJoinSaving(false);
    }
  };

  // Global Auto Join ON → ask about Video Rec first. OFF applies immediately.
  const toggleAutoJoin = async (enabled) => {
    if (autoJoinSaving) return;
    if (enabled === true) {
      setRecurringPrompt({
        mode: "global",
        eventTitle: "all Google Calendar meetings",
        nextEnabled: true,
        alsoVideoRec: false,
      });
      return;
    }
    await applyGlobalAutoJoin(false);
  };

  // Persist auto-join state for one or many event ids and mirror it into local state.
  // After a successful write we also refetch the calendar window so the UI stays
  // authoritative even for series instances that weren't in the original viewport.
  const applyMeetingAutoJoin = async (eventIds, enabled, options = {}) => {
    const ids = Array.from(new Set((Array.isArray(eventIds) ? eventIds : []).filter(Boolean)));
    if (ids.length === 0) return;
    const isOn = enabled === true;
    const alsoVideoRec = options.alsoVideoRec === true;
    setMeetingAutoJoinSaving((prev) => {
      const next = { ...(prev || {}) };
      for (const e of ids) next[e] = true;
      return next;
    });
    try {
      if (ids.length === 1) {
        await apiFetch(`/calendar/auto-join/google-event/${encodeURIComponent(ids[0])}`, {
          method: "PUT",
          body: { enabled: isOn },
        });
      } else {
        await apiFetch("/calendar/auto-join/google-events", {
          method: "PUT",
          body: { enabled: isOn, eventIds: ids },
        });
      }
      const idSet = new Set(ids);
      setGoogleCalendarEvents((prev) => {
        const next = (Array.isArray(prev) ? prev : []).map((ev) =>
          idSet.has(extractGoogleEventId(ev)) ? { ...ev, autoJoinEnabled: isOn } : ev
        );
        writeCachedGoogleEvents(loginEmail, next);
        return next;
      });
      setMeetings((prev) =>
        (Array.isArray(prev) ? prev : []).map((m) =>
          idSet.has(extractGoogleEventId(m)) ? { ...m, autoJoinEnabled: isOn } : m
        )
      );
      setSelMeeting((prev) => {
        if (!prev) return prev;
        const eid = extractGoogleEventId(prev);
        return eid && idSet.has(eid) ? { ...prev, autoJoinEnabled: isOn } : prev;
      });

      const recordingCascadeSnapshot = [
        ...(Array.isArray(googleCalendarEvents) ? googleCalendarEvents : []),
        ...(Array.isArray(meetings) ? meetings : []),
      ];
      if (typeof toggleMeetingRecordingRef.current === "function") {
        for (const eid of ids) {
          const row = recordingCascadeSnapshot.find((m) => extractGoogleEventId(m) === eid);
          if (!row || !meetingRecordingStoredOn(row)) continue;
          if (!isOn) {
            await toggleMeetingRecordingRef.current(row, false, {
              skipRecurringPrompt: true,
              silentError: true,
            });
          } else if (!alsoVideoRec) {
            // Invite MeetAI alone must not resurrect Video Rec from stale series/DB flags.
            await toggleMeetingRecordingRef.current(
              { ...row, autoJoinEnabled: true },
              false,
              { skipRecurringPrompt: true, silentError: true }
            );
          }
        }
      }

      // Optimistic UI is in place — refetch a wide window in the background so every
      // series instance (including ones outside the current viewport) reflects the
      // server's authoritative state.
      try {
        const start = new Date();
        start.setDate(start.getDate() - 180);
        const end = new Date();
        end.setDate(end.getDate() + 365);
        const from = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
        const to = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
        const data = await apiFetch(`/calendar/google-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
        const events = Array.isArray(data?.events) ? data.events : [];
        if (events.length > 0) {
          const normalized = events.map(normalizeMeeting);
          setGoogleCalendarEvents((prev) => {
            const merged = mergeGoogleCalendarSnapshot(prev, normalized, false);
            writeCachedGoogleEvents(loginEmail, merged);
            return merged;
          });
        }
      } catch (_) {
        // Best-effort refresh; the optimistic update already shows the new state.
      }
    } catch (err) {
      window.alert(err?.message || "Failed to update meeting auto-join.");
    } finally {
      setMeetingAutoJoinSaving((prev) => {
        const next = { ...(prev || {}) };
        for (const e of ids) next[e] = false;
        return next;
      });
    }
  };

  /** After Auto Join is on, optionally enable Video Rec for the given Google event ids. */
  const enableVideoRecForEventIds = async (eventIds, sourceMeeting = null) => {
    const ids = Array.from(new Set((Array.isArray(eventIds) ? eventIds : []).filter(Boolean)));
    if (ids.length === 0) return;
    let failed = 0;
    for (const eid of ids) {
      const found = findMeetingByGoogleEventId(eid, googleCalendarEvents, meetings);
      const fallback = {
        ...(sourceMeeting || {}),
        ...(found || {}),
        id: String(found?.id || found?._id || sourceMeeting?.id || "").trim() || `gcal-${eid}`,
        googleEventId: String(found?.googleEventId || eid || "").trim(),
        googleRecurringEventId: String(
          found?.googleRecurringEventId || sourceMeeting?.googleRecurringEventId || ""
        ).trim(),
        title: found?.title || sourceMeeting?.title || "Meeting",
        autoJoinEnabled: true,
      };
      const toggleFn = toggleMeetingRecordingRef.current;
      const ok =
        typeof toggleFn === "function"
          ? await toggleFn(fallback, true, {
              skipRecurringPrompt: true,
              silentError: true,
            })
          : false;
      if (!ok) failed += 1;
    }
    if (failed > 0) {
      window.alert(
        `Video Rec failed for ${failed} meeting${failed === 1 ? "" : "s"}. You can retry from the meeting detail page.`
      );
    }
  };

  /** Per-meeting Invite MeetAI — recurring series asks once: this instance or all. */
  const toggleMeetingAutoJoin = async (googleEventId, enabled, meetingMetaOptional = null) => {
    const eid = String(googleEventId || "").trim();
    if (!eid) return;
    if (meetingAutoJoinSaving[eid]) return;
    const meetingMeta =
      meetingMetaOptional && typeof meetingMetaOptional === "object"
        ? meetingMetaOptional
        : findMeetingByGoogleEventId(eid, googleCalendarEvents, meetings);
    const recurringId = String(
      meetingMeta?.googleRecurringEventId || meetingMeta?.google_recurring_event_id || ""
    ).trim();
    if (recurringId) {
      const cachedSeries = (Array.isArray(googleCalendarEvents) ? googleCalendarEvents : []).filter(
        (ev) =>
          String(ev?.googleRecurringEventId || ev?.google_recurring_event_id || "") === recurringId &&
          extractGoogleEventId(ev)
      );
      const cachedEids = Array.from(
        new Set(cachedSeries.map((ev) => extractGoogleEventId(ev)).filter(Boolean))
      );
      if (!cachedEids.includes(eid)) cachedEids.unshift(eid);
      setRecurringPrompt({
        eventTitle: meetingMeta?.title || "this recurring meeting",
        recurringId,
        eventIds: cachedEids,
        currentEid: eid,
        nextEnabled: enabled === true,
        currentMeeting: meetingMeta,
        alsoVideoRec: false,
      });
      return;
    }
    await applyMeetingAutoJoin([eid], enabled === true);
  };

  const toggleMeetingRecording = async (meetingInput, enabled, options = {}) => {
    const meetingMeta = meetingInput && typeof meetingInput === "object" ? meetingInput : null;
    const id = meetingMeta ? meetingRecordingApiId(meetingMeta) : String(meetingInput || "").trim();
    if (!id) return false;
    if (enabled === true && !canEnableMeetingRecording(meetingMeta)) {
      if (!options?.silentError) {
        window.alert(`Turn on ${JOIN_MEET_AI_LABEL} for this meeting before enabling Video Rec.`);
      }
      return false;
    }
    const skipRecurringPrompt = options?.skipRecurringPrompt === true;
    const recurringId = String(meetingMeta?.googleRecurringEventId || meetingMeta?.google_recurring_event_id || "").trim();
    // Turning Video Rec OFF is always this calendar instance only (no series dialog).
    if (!skipRecurringPrompt && recurringId && enabled === true) {
      const currentEid = extractGoogleEventId(meetingMeta);
      if (currentEid) {
        const cachedSeries = (Array.isArray(googleCalendarEvents) ? googleCalendarEvents : []).filter(
          (ev) =>
            String(ev?.googleRecurringEventId || ev?.google_recurring_event_id || "") === recurringId &&
            extractGoogleEventId(ev)
        );
        const cachedEids = Array.from(
          new Set(cachedSeries.map((ev) => extractGoogleEventId(ev)).filter(Boolean))
        );
        if (!cachedEids.includes(currentEid)) cachedEids.unshift(currentEid);
        setRecurringRecordingPrompt({
          eventTitle: meetingMeta?.title || "this recurring meeting",
          recurringId,
          eventIds: cachedEids,
          currentEid,
          nextEnabled: enabled === true,
          currentMeeting: meetingMeta,
        });
        return false;
      }
    }
    if (meetingRecordingSaving[id]) return false;
    setMeetingRecordingSaving((prev) => ({ ...(prev || {}), [id]: true }));
    let savedUpdatedId = "";
    try {
      const retentionDays = 7;
      const metaGid = extractGoogleEventId(meetingMeta);
      const data = await apiFetch(`/meetings/${encodeURIComponent(id)}/recording-settings`, {
        method: "PATCH",
        body: {
          enabled: enabled === true,
          retentionDays,
          meetingMeta: meetingMeta
            ? {
                title: meetingMeta.title || "",
                date: meetingMeta.date || "",
                time: meetingMeta.time || "",
                endTime: meetingMeta.endTime || meetingMeta.end_time || "",
                meetingUrl: meetingMeta.meetingUrl || meetingMeta.meeting_url || "",
                googleEventId: metaGid,
                googleRecurringEventId:
                  meetingMeta.googleRecurringEventId || meetingMeta.google_recurring_event_id || "",
                platform: meetingMeta.platform || "",
                status: meetingMeta.status || "",
              }
            : undefined,
        },
      });
      const updated = normalizeMeeting(data?.meeting || data || {});
      const updatedId = String(updated?.id || updated?._id || "").trim();
      savedUpdatedId = updatedId;
      const updatedGid = extractGoogleEventId(updated);
      const matchesRecordingPatch = (m) => {
        const mid = String(m?.id || m?._id || "").trim();
        const mgid = extractGoogleEventId(m);
        return mid === id || (updatedId && mid === updatedId) || (updatedGid && mgid === updatedGid);
      };
      const patchRecording = (m) =>
        matchesRecordingPatch(m) ? applyRecordingSettingsPatch(m, updated) : m;
      const dropShadowGcalFromMeetings = (m) => {
        if (!updatedId || !updatedGid) return true;
        const mid = String(m?.id || m?._id || "").trim();
        const mgid = extractGoogleEventId(m);
        if (mgid !== updatedGid) return true;
        if (mid.toLowerCase().startsWith("gcal-") && mid !== updatedId) return false;
        return true;
      };
      setMeetings((prev) =>
        dedupeDbMeetings(
          dedupeMeetingsForDisplay(
            (Array.isArray(prev) ? prev : [])
              .filter(dropShadowGcalFromMeetings)
              .map(patchRecording),
            false
          ),
          false
        )
      );
      setGoogleCalendarEvents((prev) =>
        dedupeMeetingsForDisplay((Array.isArray(prev) ? prev : []).map(patchRecording), false)
      );
      setSelMeeting((prev) => {
        if (!prev) return prev;
        const prevGid = extractGoogleEventId(prev);
        const matches =
          String(prev?.id || prev?._id || "") === id ||
          (updatedId && String(prev?.id || prev?._id || "") === updatedId) ||
          (updatedGid && prevGid && prevGid === updatedGid);
        return matches ? applyRecordingSettingsPatch(prev, updated) : prev;
      });
      return true;
    } catch (err) {
      if (!options?.silentError) window.alert(err?.message || "Failed to update recording setting.");
      return false;
    } finally {
      setMeetingRecordingSaving((prev) => {
        const next = { ...(prev || {}) };
        delete next[id];
        if (savedUpdatedId && savedUpdatedId !== id) delete next[savedUpdatedId];
        return next;
      });
    }
  };
  toggleMeetingRecordingRef.current = toggleMeetingRecording;

  // Resolve every instance of a recurring series from the server. We widen the window
  // to cover roughly 18 months past + 18 months future so series whose visible
  // instances are months-old (e.g. test data) still match.
  const resolveSeriesEventIds = async (recurringId, fallbackIds) => {
    const fallback = Array.isArray(fallbackIds)
      ? fallbackIds.map((v) => String(v || "").trim()).filter(Boolean)
      : [];
    if (!recurringId) return Array.from(new Set(fallback));
    try {
      const start = new Date();
      start.setDate(start.getDate() - 540);
      const end = new Date();
      end.setDate(end.getDate() + 540);
      const from = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      const to = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
      const data = await apiFetch(`/calendar/google-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      const events = Array.isArray(data?.events) ? data.events : [];
      const matched = events
        .filter((ev) => String(ev?.googleRecurringEventId || "") === recurringId)
        .map((ev) => String(ev?.googleEventId || "").trim())
        .filter(Boolean);
      const combined = Array.from(new Set([...fallback, ...matched]));
      // Debug aid: surface the resolved count in devtools so issues are easy to diagnose.
      if (typeof console !== "undefined") {
        try {
          console.log("[auto-join] series resolved", {
            recurringId,
            cachedFallback: fallback.length,
            serverMatched: matched.length,
            total: combined.length,
          });
        } catch (_) {}
      }
      return combined.length > 0 ? combined : fallback;
    } catch (_) {
      return Array.from(new Set(fallback));
    }
  };

  const activeUserAvatarSrc = activeUser.avatar_url || activeUser.avatarUrl || activeUser.picture || "";
  const activeUserAvatarDisplaySrc = profileAvatarObjectUrl || activeUserAvatarSrc;

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [activeUserAvatarDisplaySrc]);

  const headerProfileSubtitle = userHeaderSubtitle(activeUser, headerUserDesignation);
  const headerProfileName = activeUser.name || activeUser.email?.split("@")[0] || "User";
  const headerMenuRole = canAccessAdminApis(
    activeUser?.email || loginEmail,
    activeUser?.adminPortalAccess || loggedInUser?.adminPortalAccess
  )
    ? "ADMIN"
    : headerProfileSubtitle;

  useEffect(() => {
    const email = String(activeUser?.email || loginEmail || "").trim().toLowerCase();
    if (!email || email.length < 2 || !getToken()) {
      setHeaderUserDesignation("");
      return undefined;
    }
    const cached =
      activeUser?.designation_name ||
      activeUser?.designation ||
      activeUser?.job_title ||
      activeUser?.jobTitle;
    if (cached && String(cached).trim()) {
      setHeaderUserDesignation(String(cached).trim());
      return undefined;
    }
    let cancelled = false;
    apiFetch(`/employees/search?q=${encodeURIComponent(email)}&limit=8`)
      .then((data) => {
        if (cancelled) return;
        const results = Array.isArray(data?.results) ? data.results : [];
        const hit =
          results.find((r) => String(r?.email || "").trim().toLowerCase() === email) ||
          results[0];
        const des = hit?.designation || hit?.role || "";
        setHeaderUserDesignation(String(des || "").trim());
      })
      .catch(() => {
        if (!cancelled) setHeaderUserDesignation("");
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeUser?.email,
    loginEmail,
    activeUser?.designation_name,
    activeUser?.designation,
    activeUser?.job_title,
    activeUser?.jobTitle,
  ]);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || String(e.key || "").toLowerCase() !== "k") return;
      const tag = String(e.target?.tagName || "").toLowerCase();
      const inEditable =
        tag === "textarea" ||
        tag === "select" ||
        e.target?.isContentEditable ||
        (tag === "input" && e.target !== gmailSearchInputRef.current);
      if (inEditable) return;
      e.preventDefault();
      e.stopPropagation();
      if (isPhoneViewport) setMobileSearchOpen(true);
      const input = gmailSearchInputRef.current;
      if (!input) return;
      input.focus?.();
      try {
        input.select?.();
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isPhoneViewport]);

  useEffect(() => {
    const token = getToken();
    if (!token || !loginEmail || isPrivilegedSkipUserDashboard(loginEmail)) {
      setProfileAvatarObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
      return;
    }

    let cancelled = false;
    let objectUrlToRevoke = "";

    fetch(`${API}/auth/avatar`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error("avatar-unavailable");
        const blob = await r.blob();
        const objectUrl = URL.createObjectURL(blob);
        objectUrlToRevoke = objectUrl;
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setProfileAvatarObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objectUrl;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setProfileAvatarObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return "";
        });
      });

    return () => {
      cancelled = true;
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
    };
  }, [loginEmail, activeUserAvatarSrc]);

  // Must run on every render (before any early return) — same hook order logged-in vs logged-out.
  const deferredMeetings = useDeferredValue(meetings);
  const deferredGoogleCalendarEvents = useDeferredValue(googleCalendarEvents);
  const mergeMeetingsInputsStale =
    deferredMeetings !== meetings || deferredGoogleCalendarEvents !== googleCalendarEvents;

  const allVisibleMeetings = useMemo(() => {
    const merged = mergeMeetingsAndCalendar(deferredMeetings, deferredGoogleCalendarEvents, false);
    const collapsed = collapseSameEventRescheduleRows(merged, deferredGoogleCalendarEvents);
    const poolIndex = buildReschedulePoolIndex([
      ...collapsed,
      ...deferredMeetings,
      ...deferredGoogleCalendarEvents,
    ]);
    const noted = collapsed.map((m) => {
      if (m.rescheduleNote) return m;
      const sibling = findRescheduleSibling(m, poolRowsForRescheduleKeep(m, poolIndex));
      return sibling ? { ...m, rescheduleNote: rescheduleBadgeLabel(sibling) } : m;
    });
    const visible = suppressSupersededRescheduleRows(noted, deferredGoogleCalendarEvents).filter(
      (m) => !upcomingStubAlreadyHeld(m, noted)
    );
    return visible.map((m) => {
      const withPrior = meetingWithPriorScheduleTimeline(m);
      const session = preservedSessionScheduleFields(withPrior);
      if (!Object.keys(session).length) return withPrior;
      return normalizeMeeting({ ...withPrior, ...session });
    });
  }, [deferredMeetings, deferredGoogleCalendarEvents]);
  const deferredVisibleMeetings = useDeferredValue(allVisibleMeetings);
  const rescheduleSourcesForDetail = useMemo(
    () => ({
      visible: allVisibleMeetings,
      db: meetings,
      calendar: googleCalendarEvents,
      poolIndex: buildReschedulePoolIndex([
        ...allVisibleMeetings,
        ...meetings,
        ...googleCalendarEvents,
      ]),
    }),
    [allVisibleMeetings, meetings, googleCalendarEvents]
  );
  const detailRescheduleInfo = useMemo(() => {
    if (!selMeeting) return null;
    return findRescheduleSibling(selMeeting, allVisibleMeetings);
  }, [selMeeting, allVisibleMeetings]);
  const gmailFilteredMeetings = useMemo(() => {
    const q = gmailSearch.trim().toLowerCase();
    const base = allVisibleMeetings || [];
    if (!q) return base;
    return base.filter((m) => {
      const title = String(m?.title || "").toLowerCase();
      const host = (meetingHasDisplayableHost(m) ? meetingHostName(m) : "Avaada MeetAI").toLowerCase();
      return title.includes(q) || host.includes(q);
    });
  }, [allVisibleMeetings, gmailSearch]);

  const currentLang = userPrefs?.uiLanguage || "English";
  const t = useCallback((key, fallback) => getTranslation(currentLang, key, fallback), [currentLang]);
  const i18nValue = useMemo(() => ({ lang: currentLang, t }), [currentLang, t]);

  const gmailSearchPlaceholder = page === "actions" ? t("myTasks", "Search my tasks") : t("searchMeetings", "Search meetings");
  const gmailSearchAriaLabel = page === "actions" ? t("myTasks", "Search my tasks") : t("searchMeetings", "Search meetings");

  function goCalToday() {
    const n = new Date();
    setCalSelDate(todayStr());
    setCalYear(n.getFullYear());
    setCalMonth(n.getMonth());
  }

  const myActionsForNav = meetings.flatMap((m) =>
    (m.actionItems || [])
      .filter((a) => actionItemMatchesUser(a, activeUser))
      .map((a) => ({ ...a, meetingId: m._id || m.id, meetingTitle: m.title, meetingDate: m.date }))
  );
  const NAV = [
    { id: "autojoin", icon: NAV_ICONS.autojoin, label: t("upcomingMeetings", "Upcoming meetings") },
    { id: "transcripts", icon: NAV_ICONS.transcripts, label: t("allTranscripts", "Recorded Meetings") },
    { id: "dashboard", icon: NAV_ICONS.dashboard, label: t("dashboard", "Dashboard") },
    ...(SHOW_USER_CALENDAR_NAV
      ? [{ id: "calendar", icon: NAV_ICONS.calendar, label: t("calendar", "Calendar") }]
      : []),
    { id: "meetings", icon: NAV_ICONS.meetings, label: t("allMeetings", "All Meetings") },
    {
      id: "actions",
      icon: NAV_ICONS.actions,
      label: t("myTasks", "My Tasks"),
      badge: myActionsForNav.filter((a) => a.status === "pending").length,
    },
  ];
  const activeNavKey =
    page === "detail"
      ? (detailOpenedFrom && NAV.some((n) => n.id === detailOpenedFrom) ? detailOpenedFrom : "autojoin")
      : NAV.some((n) => n.id === page) ? page : null;
  const { containerRef: navTabsRef, registerTab: registerNavTab, thumbStyle: navThumbStyle } =
    useSlidingTabIndicator(activeNavKey, loginEmail);

  // OAuth redirect in progress
  if (oauthPending) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',system-ui,sans-serif", background: PALETTE.grey, color: PALETTE.cold }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 36, height: 36, border: "3px solid #D6DEE6", borderTopColor: PALETTE.navy, borderRadius: "50%", margin: "0 auto 16px", animation: "spin 0.8s linear infinite" }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Signing you in…</div>
          <div style={{ fontSize: 13, color: "#6A7F95", marginTop: 6 }}>Completing Google sign-in</div>
        </div>
      </div>
    );
  }

  void pathTick;
  const adminPath = typeof window !== "undefined" && isAdminAppPath(window.location.pathname);
  const recordingSharePath =
    typeof window !== "undefined" && isRecordingSharePath(window.location.pathname);
  const recordingReturnPath =
    recordingSharePath && typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "";

  if (recordingSharePath) {
    const sp = new URLSearchParams(window.location.search);
    const recordingMeetingId = sp.get("m") || sp.get("id") || "";
    if (!loginEmail) {
      return <LoginPage returnPath={recordingReturnPath} />;
    }
    return (
      <MeetingRecordingSharePreviewPage
        meetingId={recordingMeetingId}
        onGoMeetings={() => window.location.assign("/")}
      />
    );
  }

  // Public shared-meeting page — anyone with the link can open it (read-only).
  // No login, no account clone: the share id alone unlocks the scoped content.
  if (typeof window !== "undefined" && isSharedPagePath(window.location.pathname)) {
    const sp = new URLSearchParams(window.location.search);
    const shareId = sp.get("s") || sp.get("id") || "";
    return (
      <MeetingSharePublicPage
        shareId={shareId}
        onGoMeetings={() => window.location.assign("/")}
      />
    );
  }

  // Company access paused — popup over a branded login-style backdrop (old sessions land here).
  if (accessPaused) {
    const goSignIn = () => {
      clearToken();
      setAccessPaused(null);
      window.location.assign("/");
    };
    return (
      <div
        style={{
          minHeight: "100vh",
          fontFamily: "'DM Sans',system-ui,sans-serif",
          background: "#0F2A44",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <img
          src={LOGIN_RENEWABLE_SKYLINE}
          alt=""
          aria-hidden
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.5 }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <PausedAccessModal
          open
          message={accessPaused.message}
          onClose={goSignIn}
          onBackToSignIn={goSignIn}
        />
      </div>
    );
  }

  // Not logged in → login (dedicated copy on /admin)
  if (!loginEmail) {
    const adminLogin = typeof window !== "undefined" && isAdminAppPath(window.location.pathname);
    const deepLinkReturnPath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "";
    // Preserve email CTA /meetings?m=<id> (and similar) through Google OAuth.
    const loginReturnPath =
      !adminLogin && deepLinkReturnPath && /[?&]m=/.test(deepLinkReturnPath)
        ? deepLinkReturnPath
        : "";
    return <LoginPage adminEntry={adminLogin} returnPath={loginReturnPath} />;
  }

  if (adminPath && canAccessAdminApis(loginEmail, loggedInUser?.adminPortalAccess)) {
    return (
      <PersonHoverTipProvider>
        <BotAdminPortal
          sessionEmail={loginEmail}
          onLogout={handleLogout}
          onOpenMainApp={() => window.location.assign("/")}
        />
      </PersonHoverTipProvider>
    );
  }

  if (adminPath) {
    return (
      <PersonHoverTipProvider>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'DM Sans',system-ui,sans-serif",
            background: PALETTE.grey,
            padding: 24,
          }}
        >
          <div style={{ maxWidth: 440, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: PALETTE.navy, marginBottom: 8 }}>Admin portal</div>
            <div style={{ fontSize: 14, color: PALETTE.cold, lineHeight: 1.6, marginBottom: 20 }}>
              This Google account is not on the admin allowlist. Sign out and try another account, or open the main app.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  window.location.assign("/admin");
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "none",
                  background: PALETTE.navy,
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Sign out, try again
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.assign("/");
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: `1px solid ${PALETTE.cold}`,
                  background: "#fff",
                  color: PALETTE.cold,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Main app home
              </button>
            </div>
          </div>
        </div>
      </PersonHoverTipProvider>
    );
  }

  // Regular user → normal dashboard
  const openMeeting=(m, ctx)=>{
    const visibleMatch = (allVisibleMeetings || []).find((row) => {
      const rid = String(m?.id ?? m?._id ?? "").trim();
      const rowId = String(row?.id ?? row?._id ?? "").trim();
      if (rid && rowId && rid === rowId) return true;
      const g1 = extractGoogleEventId(m);
      const g2 = extractGoogleEventId(row);
      if (!g1 || g1 !== g2) return false;
      return meetingGidSlotKey(m, false) === meetingGidSlotKey(row, false);
    });
    const base = normalizeMeeting({
      ...m,
      ...(visibleMatch?.rescheduleTimeline
        ? { rescheduleTimeline: visibleMatch.rescheduleTimeline }
        : {}),
      ...(visibleMatch?.rescheduleNote ? { rescheduleNote: visibleMatch.rescheduleNote } : {}),
      ...(visibleMatch?.priorSchedule ? { priorSchedule: visibleMatch.priorSchedule } : {}),
      ...(visibleMatch?.scheduleChangedFrom
        ? { scheduleChangedFrom: visibleMatch.scheduleChangedFrom }
        : {}),
    });
    const openSources = rescheduleSourcesForDetail;
    const slotsForOpen = rescheduleSlotsFromRowForOpen(base, openSources);
    const timeline = slotsForOpen.length
      ? orderRescheduleTimelineForDisplay(base, slotsForOpen)
      : null;
    const nm = normalizeMeeting({
      ...base,
      ...(timeline?.length ? { rescheduleTimeline: timeline } : {}),
    });
    const eff = effectiveMeetingStatus(nm);
    const sourceRaw = ctx && typeof ctx === "object" ? ctx.source : null;
    const isAutoJoinOpen =
      sourceRaw === "autojoin" ||
      page === "autojoin" ||
      (ctx && typeof ctx === "object" && ctx.source === "autojoin");
    const preferTab =
      ctx && typeof ctx === "object" && ctx.preferTab
        ? ctx.preferTab
        : isAutoJoinOpen
          ? eff === "completed" || eff === "ended"
            ? "transcript"
            : "action-items"
          : sourceRaw === "transcripts" || page === "transcripts"
            ? "transcript"
            : "overview";
    // Prefer the live tab (page) so Back returns where the user opened from.
    const source =
      page && ALLOWED_USER_PAGES.has(page) && page !== "detail"
        ? page
        : sourceRaw && ALLOWED_USER_PAGES.has(sourceRaw) && sourceRaw !== "detail"
          ? sourceRaw
          : null;
    const openKey =
      meetingGidSlotKey(nm, false) || String(nm?.id ?? nm?._id ?? extractGoogleEventId(nm) ?? "");
    const openDetail = () => {
      setSelMeeting(nm);
      setHighlightMeetingId(nm?.id ?? nm?._id ?? null);
      setPage("detail");
      setActiveTab(preferTab);
      setDetailOpenedFrom(source);
    };
    if (SMOOTH_NAV_TRANSITION) startNavTransition(openDetail);
    else openDetail();
    try {
      if (source) sessionStorage.setItem(MEETAI_DETAIL_FROM_KEY, source);
      else sessionStorage.removeItem(MEETAI_DETAIL_FROM_KEY);
    } catch (_) {}
    if (!meetingRowHasReadyRescheduleDisplay(nm)) {
      const enrich = () => {
        try {
          const collected = collectRescheduleSlotsForMeeting(nm, openSources);
          if (!collected?.length) return;
          const enrichedTimeline = orderRescheduleTimelineForDisplay(nm, collected);
          if (!enrichedTimeline?.length) return;
          setSelMeeting((prev) => {
            if (!prev) return prev;
            const prevKey =
              meetingGidSlotKey(prev, false) ||
              String(prev?.id ?? prev?._id ?? extractGoogleEventId(prev) ?? "");
            if (openKey && prevKey && openKey !== prevKey) return prev;
            return normalizeMeeting({ ...prev, rescheduleTimeline: enrichedTimeline });
          });
        } catch (_) {}
      };
      if (typeof requestIdleCallback === "function") requestIdleCallback(enrich, { timeout: 800 });
      else setTimeout(enrich, 0);
    }
    // Background-refresh the full meeting (transcript + action items) from the API.
    // Uses the DB id if available; skips for pure calendar-only entries without a DB record.
    const mid = nm?.id;
    if (mid) {
      apiFetch(`/meetings/${encodeURIComponent(mid)}`)
        .then((fresh) => {
          if (!fresh?.id || !meetingDetailMatchesOpened(nm, fresh)) return;
          const nf = mergeMeetingDetailFromApi(nm, fresh);
          setSelMeeting((prev) =>
            prev && !meetingDetailMatchesOpened(prev, nf) ? prev : nf
          );
          setMeetings((prev) =>
            dedupeDbMeetings(
              prev.map((x) =>
                meetingListRowSameOccurrence(x, nm) || meetingListRowSameOccurrence(x, nf)
                  ? mergeMeetingDetailFromApi(x, nf)
                  : x
              ),
              false
            )
          );
        })
        .catch(() => {});
    }
  };
  meetPageOpenHandlerRef.current = (m, extra) => {
    // Always return to the tab the user is on (do not let nested source: overrides steal Back).
    const fromPage =
      page && ALLOWED_USER_PAGES.has(page) && page !== "detail" ? page : null;
    openMeeting(m, {
      ...(extra && typeof extra === "object" ? extra : {}),
      source: fromPage || (extra && extra.source) || "autojoin",
    });
  };
  const addInstantMeeting=(m)=>{ const nm=normalizeMeeting(m); setMeetings(prev=>dedupeDbMeetings([nm,...prev], false)); openMeeting(nm); };
  const liveMeeting = meetings.find(m=>m.status==="live");

  const myActions = myActionsForNav;
  // One Companion FAB for the whole app (including Recorded Meetings / meeting detail).
  // Hide only while the right-rail AI drawer already shows the same companion.
  const showWorkspaceAssistantFab =
    page !== "howto" && !(rightRailDrawerOpen && rightRailPanel === "ai");
  const meetListPage = MEET_LIST_PAGES.has(page);
  const currentMeetView = meetViewFromPage(page);
  const showSidebarNavTips = leftNavCollapsed && !isMobileViewport;
  const instantJoinLabel = t("instantJoin", HEADER_INSTANT_JOIN_CTA);
  const gmailMainScrollClass = [
    "gmail-main-scroll gmail-page-flat",
    page === "detail" ? " gmail-page-meeting-detail" : "",
    page === "dashboard" ? " gmail-page-dashboard" : "",
    page === "autojoin" ? " gmail-page-upcoming" : "",
    page === "transcripts" ? " gmail-page-recorded" : "",
    page === "meetings" ? " gmail-page-all-meetings" : "",
    page === "actions" ? " gmail-page-actions" : "",
    page === "howto" ? " gmail-page-howto" : "",
    SHOW_USER_CALENDAR_NAV && page === "calendar" ? " gmail-page-calendar is-cal-fill" : "",
    SMOOTH_NAV_TRANSITION && (navPending || mergeMeetingsInputsStale) ? " is-nav-pending" : "",
  ].join("");
  const gmailMainScrollBusy =
    SMOOTH_NAV_TRANSITION && (navPending || mergeMeetingsInputsStale) ? true : undefined;

  return (
    <PersonHoverTipProvider>
    <SidebarHoverTipReset
      showSidebarTips={showSidebarNavTips}
      leftNavCollapsed={leftNavCollapsed}
      mobileNavOpen={mobileNavOpen}
    />
    <I18nContext.Provider value={i18nValue}>
    <div className="gmail-app-shell" style={{fontFamily:"'DM Sans',system-ui,sans-serif",color:T.text,fontSize:14}}>
      {apiDownBanner ? (
        <div
          role="status"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2000,
            background: "#FEF3C7",
            color: "#92400E",
            borderBottom: "1px solid #F59E0B",
            padding: "8px 14px",
            fontSize: 12.5,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>
            {apiDownBanner}
            {meetings.length > 0 ? " Showing your last loaded data." : ""}
          </span>
          <button
            type="button"
            disabled={isOffline}
            title={isOffline ? "Reconnects automatically once you're back online" : "Reload now"}
            onClick={() => {
              setApiDownBanner("");
              setRefreshTick((v) => v + 1);
            }}
            style={{
              border: "1px solid #D97706",
              background: "#FFFBEB",
              color: "#92400E",
              borderRadius: 8,
              padding: "4px 10px",
              fontWeight: 700,
              cursor: isOffline ? "not-allowed" : "pointer",
              opacity: isOffline ? 0.55 : 1,
              fontSize: 12,
              whiteSpace: "nowrap",
            }}
          >
            {isOffline ? "Waiting for network…" : "Retry"}
          </button>
        </div>
      ) : null}
      <style>{getGmailShellStylesCss()}</style>

      <header className={`gmail-topbar${leftNavCollapsed && !isMobileViewport ? " is-nav-collapsed" : ""}${userMenuOpen ? " is-user-menu-open" : ""}`}>
        <div className="gmail-topbar-brand">
          <button
            type="button"
            className="gmail-topbar-logo"
            aria-label="Go to Upcoming meetings"
            title="Upcoming meetings"
            onClick={() => {
              setSelMeeting(null);
              setDetailOpenedFrom(null);
              goToPage("autojoin");
              setMobileNavOpen(false);
            }}
          >
            <img src={APP_LOGO} alt="Avaada logo" width={108} height={28} className="app-topbar-logo" />
          </button>
          <IconButton
            type="button"
            className="gmail-menu-btn"
            aria-label={isMobileViewport ? (mobileNavOpen ? "Close navigation" : "Open navigation") : (leftNavCollapsed ? "Expand navigation" : "Collapse navigation")}
            aria-expanded={isMobileViewport ? mobileNavOpen : undefined}
            title={isMobileViewport ? "Menu" : leftNavCollapsed ? "Expand menu" : "Collapse menu"}
            onClick={() => {
              if (isMobileViewport) setMobileNavOpen((v) => !v);
              else toggleLeftNavCollapsed();
            }}
            sx={{
              p: 0,
              width: 40,
              height: 40,
              borderRadius: 0,
              border: "none",
              boxShadow: "none",
              bgcolor: "transparent",
              color: "#0875E1",
              "&:hover": { bgcolor: "rgba(8,117,225,0.08)", border: "none" },
            }}
          >
            <Svg d={IC.menu} size={22} color="currentColor" stroke={2} />
          </IconButton>
        </div>
        <button
          type="button"
          className="gmail-menu-btn gmail-mobile-search-btn"
          aria-label={gmailSearchAriaLabel}
          onClick={() => setMobileSearchOpen(true)}
        >
          <Svg d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={20} color="currentColor" stroke={2} />
        </button>
        <div className={`gmail-search-wrap${mobileSearchOpen ? " is-open" : ""}`}>
          <span className="gmail-search-icon" aria-hidden>
            <Svg d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={18} color="#5F7690" stroke={2} />
          </span>
          <input
            ref={gmailSearchInputRef}
            type="search"
            className="gmail-search"
            placeholder={gmailSearchPlaceholder}
            value={gmailSearch}
            onChange={(e) => setGmailSearch(e.target.value)}
            aria-label={gmailSearchAriaLabel}
            autoFocus={mobileSearchOpen}
          />
          {!mobileSearchOpen ? (
            <kbd className="gmail-search-kbd" aria-hidden="true">
              {typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? "⌘ K" : "Ctrl K"}
            </kbd>
          ) : null}
          {mobileSearchOpen ? (
            <button
              type="button"
              className="gmail-search-close"
              aria-label="Close search"
              onClick={() => {
                setGmailSearch("");
                setMobileSearchOpen(false);
              }}
            >
              <Svg d="M18 6L6 18M6 6l12 12" size={18} color="currentColor" stroke={2} />
            </button>
          ) : null}
        </div>
        <div className="gmail-topbar-actions">
          <MuiDivider orientation="vertical" flexItem className="gmail-topbar-vdivider" />
          <div className="gmail-topbar-utils">
            <Tooltip title={`${t("refresh", "Refresh")} • Last updated ${formatTimeInAppTz(lastUpdatedAt, { second: "2-digit" })}`}>
              <span className="toolbar-sync">
                <IconButton
                  type="button"
                  className={`refresh-btn${manualRefreshing ? " is-loading" : ""}`}
                  onClick={() => {
                    setManualRefreshing(true);
                    setRefreshTick((v) => v + 1);
                  }}
                  aria-label={t("refresh", "Refresh meetings")}
                  sx={{
                    width: 36,
                    height: 36,
                    border: "1px solid #DCE6EF",
                    bgcolor: "#F7FAFC",
                    color: "#0875E1",
                    "&:hover": { bgcolor: "#F1F6FF", borderColor: "rgba(37,99,235,0.40)" },
                  }}
                >
                  <span className="refresh-icon">
                    <Svg d={IC.refresh} size={16} stroke={2.4} color="#0875E1" />
                  </span>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("howItWorks", "How It Works")}>
              <IconButton
                type="button"
                className={`gmail-topbar-icon-btn gmail-howto-btn${page === "howto" ? " is-active" : ""}`}
                onClick={() => goToPage("howto")}
                aria-label={t("howItWorks", "How It Works")}
                sx={{
                  width: 36,
                  height: 36,
                  border: "1px solid #DCE6EF",
                  bgcolor: "#F7FAFC",
                  color: "#0875E1",
                  "&:hover": { bgcolor: "#F1F6FF", borderColor: "rgba(37,99,235,0.40)" },
                }}
              >
                <Svg d={IC.help} size={18} stroke={1.9} color="currentColor" />
              </IconButton>
            </Tooltip>
          </div>
          <MuiDivider orientation="vertical" flexItem className="gmail-topbar-vdivider header-profile-divider" />
          <div ref={userMenuRef} className="user-menu-wrap">
            <button
              type="button"
              className={`header-profile-btn${userMenuOpen ? " is-open" : ""}`}
              onClick={() => {
                setUserMenuOpen((v) => {
                  if (!v) setRightRailDrawerOpen(false);
                  return !v;
                });
              }}
              aria-label={`Open account menu for ${headerProfileName}`}
              aria-expanded={userMenuOpen}
            >
              <span
                className={`header-profile-avatar${activeUserAvatarDisplaySrc && !avatarLoadFailed ? " has-photo" : ""}`}
              >
                {activeUserAvatarDisplaySrc && !avatarLoadFailed ? (
                  <img
                    src={activeUserAvatarDisplaySrc}
                    alt=""
                    referrerPolicy="no-referrer"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={() => setAvatarLoadFailed(true)}
                  />
                ) : (activeUser.initials || activeUser.name) ? (
                  (activeUser.initials || activeUser.name.slice(0, 2).toUpperCase()).slice(0, 2)
                ) : (
                  <Svg d={IC.user} size={19} stroke={1.7} color="#fff" />
                )}
              </span>
              <span className="header-profile-text">
                <span className="header-profile-name" title={headerProfileName}>
                  {headerProfileName}
                </span>
                {headerProfileSubtitle ? (
                  <span className="header-profile-role" title={headerProfileSubtitle}>
                    {headerProfileSubtitle}
                  </span>
                ) : null}
              </span>
            </button>
            {userMenuOpen && (
              <div className="user-menu" role="menu" aria-label="Account menu">
                <div className="user-menu-head">
                  <div className="user-menu-head-avatar">
                    {activeUserAvatarDisplaySrc && !avatarLoadFailed ? (
                      <img src={activeUserAvatarDisplaySrc} alt="" referrerPolicy="no-referrer" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={() => setAvatarLoadFailed(true)} />
                    ) : (
                      (activeUser.initials || activeUser.name?.slice(0, 1).toUpperCase() || "U").slice(0, 1)
                    )}
                  </div>
                  <div className="user-menu-head-text">
                    <div className="user-menu-head-name" title={activeUser.name || "User"}>{activeUser.name || "User"}</div>
                    <div className="user-menu-head-email" title={activeUser.email || loginEmail || ""}>{activeUser.email || loginEmail || ""}</div>
                    {headerMenuRole ? (
                      <div className="user-menu-head-role" title={headerMenuRole}>
                        {headerMenuRole}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="user-menu-scroll">
                  <div className="user-menu-section">
                    <button type="button" className="user-menu-item" role="menuitem" onClick={()=>{ setUserMenuOpen(false); setUserInfoMode("profile"); }}>
                      <span className="user-menu-item-icon"><Svg d={IC.users} color="#64748B" size={18} stroke={1.8} /></span>
                      <span className="user-menu-item-copy">
                        <span className="user-menu-item-title">{t("profile", "Profile")}</span>
                        <span className="user-menu-item-sub">Your account details</span>
                      </span>
                    </button>
                    <button type="button" className="user-menu-item" role="menuitem" onClick={()=>{ setUserMenuOpen(false); setMeetAiUsageOpen(true); }}>
                      <span className="user-menu-item-icon"><Svg d={IC.task} color="#64748B" size={18} stroke={1.8} /></span>
                      <span className="user-menu-item-copy">
                        <span className="user-menu-item-title">{t("meetingUsage", "Meeting usage")}</span>
                        <span className="user-menu-item-sub">Usage across meetings</span>
                      </span>
                    </button>
                    <button
                      ref={feedbackBtnRef}
                      type="button"
                      className="user-menu-item"
                      role="menuitem"
                      onClick={() => {
                        const rect = feedbackBtnRef.current?.getBoundingClientRect?.();
                        setUserMenuOpen(false);
                        openFeedbackPopover(rect);
                      }}
                    >
                      <span className="user-menu-item-icon"><Svg d={IC.chat} color="#64748B" size={18} stroke={1.8} /></span>
                      <span className="user-menu-item-copy">
                        <span className="user-menu-item-title">{t("feedback", "Feedback")}</span>
                        <span className="user-menu-item-sub">Share your feedback</span>
                      </span>
                    </button>
                    <button type="button" className="user-menu-item" role="menuitem" onClick={()=>{ setUserMenuOpen(false); startAppTour(0); }}>
                      <span className="user-menu-item-icon"><Svg d={IC.zap} color="#64748B" size={18} stroke={1.8} /></span>
                      <span className="user-menu-item-copy">
                        <span className="user-menu-item-title">{t("appTour", "App tour")}</span>
                        <span className="user-menu-item-sub">Guided walkthrough</span>
                      </span>
                    </button>
                    <button type="button" className="user-menu-item" role="menuitem" onClick={()=>{ setUserMenuOpen(false); setUserInfoMode("settings"); }}>
                      <span className="user-menu-item-icon"><Svg d={IC.settings} color="#64748B" size={18} stroke={1.8} /></span>
                      <span className="user-menu-item-copy">
                        <span className="user-menu-item-title">{t("settings", "Settings")}</span>
                        <span className="user-menu-item-sub">Preferences and reminders</span>
                      </span>
                    </button>
                    {canAccessAdminApis(activeUser?.email || loginEmail, activeUser?.adminPortalAccess || loggedInUser?.adminPortalAccess) && (
                      <button type="button" className="user-menu-item user-menu-item--admin" role="menuitem" onClick={() => { setUserMenuOpen(false); window.location.assign("/admin"); }}>
                        <span className="user-menu-item-icon"><Svg d={IC.shield} color="#64748B" size={18} stroke={1.8} /></span>
                        <span className="user-menu-item-copy">
                          <span className="user-menu-item-title">{t("adminPortal", "Admin portal")}</span>
                          <span className="user-menu-item-sub">Manage workspace</span>
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="user-menu-divider" aria-hidden="true" />
                  <div className="user-menu-section" style={{ paddingTop: 2, paddingBottom: 8 }}>
                    <button type="button" className="user-menu-item user-menu-item--danger" role="menuitem" onClick={()=>{ setUserMenuOpen(false); setSignOutConfirmOpen(true); }}>
                      <span className="user-menu-item-icon"><Svg d={IC.logout} color="#EF4444" size={18} stroke={1.8} /></span>
                      <span className="user-menu-item-copy">
                        <span className="user-menu-item-title">{t("logout", "Logout")}</span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="gmail-app-body">
        {isMobileViewport && mobileNavOpen ? (
          <div
            className="gmail-nav-backdrop"
            role="presentation"
            onClick={() => setMobileNavOpen(false)}
          />
        ) : null}
        <aside
          className={`gmail-sidebar${leftNavCollapsed ? " gmail-sidebar--collapsed" : ""}${leftNavAnimating ? " is-nav-width-animating" : ""}${mobileNavOpen ? " is-mobile-open" : ""}`}
          aria-label="Primary navigation"
          aria-hidden={isMobileViewport && !mobileNavOpen}
          data-tour="app-nav"
        >
          <img
            className="sidebar-renewable-illustration"
            src={SIDEBAR_RENEWABLE_ILLUSTRATION}
            alt=""
            aria-hidden="true"
            draggable="false"
          />
          <div className="gmail-sidebar-content">
          <SidebarTipTarget
            className="gmail-compose"
            data-tour="btn-record"
            tipLabel={instantJoinLabel}
            showTip={showSidebarNavTips}
            onClick={() => {
              setMobileNavOpen(false);
              setShowInstant(true);
            }}
          >
            <Svg d={IC.zap} color="#fff" size={18} stroke={2.2} />
            <span className="gmail-compose-label">{instantJoinLabel}</span>
          </SidebarTipTarget>
          <div className="gmail-nav-list" data-tour="app-nav-menu" role="navigation" aria-label="App sections">
          {NAV.map((n) => {
            const isActive = page === n.id || (page === "detail" && detailOpenedFrom === n.id);
            return (
              <SidebarTipTarget
                key={n.id}
                className={`gmail-nav-item${isActive ? " active" : ""}`}
                data-tour={`nav-${n.id}`}
                tipLabel={n.label}
                showTip={showSidebarNavTips}
                onClick={() => {
                  setSelMeeting(null);
                  setDetailOpenedFrom(null);
                  goToPage(n.id);
                  setMobileNavOpen(false);
                }}
              >
                <span className="gmail-nav-icon" aria-hidden>
                  {n.id === "autojoin" ? (
                    <AutoJoinNavIcon size={18} color={isActive ? "#0875E1" : "#18558A"} stroke={isActive ? 2.1 : 1.85} />
                  ) : (
                    <Svg d={n.icon} size={18} color={isActive ? "#0875E1" : "#18558A"} stroke={isActive ? 2.1 : 1.85} />
                  )}
                </span>
                <span className="gmail-nav-label">{n.label}</span>
                {n.badge > 0 ? <span className="gmail-nav-badge">{n.badge > 99 ? "99+" : n.badge}</span> : null}
              </SidebarTipTarget>
            );
          })}
          </div>
          <div className="gmail-sidebar-foot">
            <div
              className="gmail-sidebar-user"
              title={`${headerProfileName}${headerProfileSubtitle ? ` · ${headerProfileSubtitle}` : ""}`}
            >
              <Avatar
                initials={(() => {
                  const n = String(headerProfileName || "").trim();
                  if (n) {
                    const parts = n.split(/\s+/).filter(Boolean);
                    if (parts.length >= 2) return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
                    return n.slice(0, 2).toUpperCase();
                  }
                  return "?";
                })()}
                size={34}
                imageUrl={activeUserAvatarDisplaySrc || undefined}
                title={headerProfileName}
              />
              <span className="gmail-sidebar-user-copy">
                <span className="gmail-sidebar-user-name">{headerProfileName}</span>
                {headerProfileSubtitle ? (
                  <span className="gmail-sidebar-user-role">{headerProfileSubtitle}</span>
                ) : null}
              </span>
            </div>
          </div>
          </div>
        </aside>

        <main className="gmail-main">
          <div className={gmailMainScrollClass} aria-busy={gmailMainScrollBusy}>
          {loadingMeetings && page === "dashboard" && <DashHomeSkeleton />}
          {SHOW_USER_CALENDAR_NAV && calendarEventsLoading && page === "calendar" && <CalendarTabSkeleton />}
          {loadingMeetings && page === "actions" && <ActionsTabSkeleton />}
          {!loadingMeetings && page === "dashboard" && (
            <DashboardOverviewPage
              user={activeUser}
              meetings={meetings}
              mergedMeetings={allVisibleMeetings}
              myActions={myActions}
              onOpen={(m) => openMeeting(m, { source: "dashboard" })}
              navigate={setPage}
              onOpenAction={(a) => {
                setActionsFocus({
                  meetingId: String(a?.meetingId ?? ""),
                  actionId: String(a?.id ?? ""),
                });
                goToPage("actions");
              }}
              onCreateEvent={() => setCreateCalEventOpen(true)}
              onToggleMeetingAutoJoin={toggleMeetingAutoJoin}
              meetingAutoJoinSaving={meetingAutoJoinSaving}
              onToggleMeetingRecording={toggleMeetingRecording}
              meetingRecordingSaving={meetingRecordingSaving}
            />
          )}
          {loadingMeetings && meetListPage && <MeetingsPageSkeleton meetView={currentMeetView} />}
          {!loadingMeetings && meetListPage && (
            <MeetPage
              loading={loadingMeetings}
              calendarSyncing={calendarEventsLoading}
              meetings={deferredVisibleMeetings}
              onOpen={handleMeetPageOpen}
              meetView={currentMeetView}
              showSectionNav={false}
              shellLayout
              externalSearch={gmailSearch}
              onExternalSearchChange={setGmailSearch}
              autoJoinEnabled={autoJoinEnabled}
              autoJoinSaving={autoJoinSaving}
              onToggleAutoJoin={toggleAutoJoin}
              meetingAutoJoinSaving={meetingAutoJoinSaving}
              onToggleMeetingAutoJoin={toggleMeetingAutoJoin}
              meetingRecordingSaving={meetingRecordingSaving}
              onToggleMeetingRecording={toggleMeetingRecording}
              onInstantJoin={() => setShowInstant(true)}
              onScheduleMeeting={() => setCreateCalEventOpen(true)}
            />
          )}
          {SHOW_USER_CALENDAR_NAV && !calendarEventsLoading && page === "calendar" && (
            <CalPage meetings={gmailFilteredMeetings} myActions={myActions} calendarHint={googleCalendarHint} calendarWarning={googleCalendarWarning} onOpen={(m) => openMeeting(m, { source: "calendar" })} year={calYear} month={calMonth} setYear={setCalYear} setMonth={setCalMonth} view={calView} setView={setCalView} selDate={calSelDate} setSelDate={setCalSelDate} onToggleMeetingAutoJoin={toggleMeetingAutoJoin} meetingAutoJoinSaving={meetingAutoJoinSaving} onToggleMeetingRecording={toggleMeetingRecording} meetingRecordingSaving={meetingRecordingSaving} onCreateEvent={() => setCreateCalEventOpen(true)} />
          )}
          {!loadingMeetings && page === "actions" && (
            <ActionsPage
              myActions={myActions}
              meetings={meetings}
              onOpen={(m) => openMeeting(m, { source: "actions" })}
              externalSearch={gmailSearch}
              focusTarget={actionsFocus}
              onFocusConsumed={() => setActionsFocus(null)}
              onMeetingUpdated={(m) => {
                const nm = normalizeMeeting(m);
                const mid = String(nm.id ?? nm._id ?? "");
                if (!mid) return;
                setMeetings((prev) =>
                  dedupeDbMeetings(
                    (Array.isArray(prev) ? prev : []).map((x) =>
                      String(x.id ?? x._id ?? "") === mid
                        ? mergeMeetingDetailFromApi(x, nm)
                        : x
                    ),
                    false
                  )
                );
              }}
              onReloadMeetings={() =>
                apiFetchMeetingsList("")
                  .then((result) => {
                    if (result.etag) meetingsListEtagRef.current = result.etag;
                    if (!result.notModified) {
                      setMeetings((prev) => mergeMeetingsListPreserveDetail(prev, result.list));
                    }
                  })
                  .catch(() => {})
              }
            />
          )}
          {page === "howto" && <HowToPage onStartAppTour={() => startAppTour(0)} />}
          {page === "detail" && selMeeting && (
            <DetailPage shellLayout meeting={selMeeting} activeTab={activeTab} setActiveTab={setActiveTab} onBack={() => {
              // Always SPA navigate — never history.back() (that can hit Google OAuth).
              let storedFrom = null;
              try {
                storedFrom = sessionStorage.getItem(MEETAI_DETAIL_FROM_KEY);
              } catch (_) {}
              const backTo =
                detailOpenedFrom && ALLOWED_USER_PAGES.has(detailOpenedFrom) && detailOpenedFrom !== "detail"
                  ? detailOpenedFrom
                  : storedFrom && ALLOWED_USER_PAGES.has(storedFrom) && storedFrom !== "detail"
                    ? storedFrom
                    : "autojoin";
              setSelMeeting(null);
              setDetailOpenedFrom(null);
              goToPage(backTo);
            }} currentUser={activeUser} openedFrom={detailOpenedFrom} onMeetingUpdated={(m) => {
              const nm = normalizeMeeting(m);
              setSelMeeting((prev) => {
                if (!prev || !meetingDetailMatchesOpened(prev, nm)) return prev;
                return mergeMeetingDetailFromApi(prev, nm);
              });
              setMeetings((prev) =>
                dedupeDbMeetings(
                  prev.map((x) =>
                    meetingListRowSameOccurrence(x, nm)
                      ? mergeMeetingDetailFromApi(x, nm)
                      : x
                  ),
                  false
                )
              );
            }} onToggleMeetingAutoJoin={toggleMeetingAutoJoin} meetingAutoJoinSaving={meetingAutoJoinSaving} onToggleMeetingRecording={toggleMeetingRecording} meetingRecordingSaving={meetingRecordingSaving} rescheduleInfo={detailRescheduleInfo} rescheduleSources={rescheduleSourcesForDetail} />
          )}
          </div>
        </main>
      </div>

      {/* Mobile bottom nav — fixed, only visible <=767 via CSS; hidden while Instant Join drawer is open */}
      <AvaadaMobileBottomNav
        page={page}
        detailOpenedFrom={detailOpenedFrom}
        badgeCount={Array.isArray(myActions) ? myActions.filter((a) => a.status === "pending").length : 0}
        hidden={showInstant}
        onNavigate={(next) => {
          setAvaadaMobileMoreOpen(false);
          setSelMeeting(null);
          setDetailOpenedFrom(null);
          goToPage(next);
          setMobileNavOpen(false);
        }}
        onInstantJoin={() => setShowInstant(true)}
        onMore={() => setAvaadaMobileMoreOpen(true)}
      />
      <AvaadaMobileMoreSheet
        open={avaadaMobileMoreOpen}
        onClose={() => setAvaadaMobileMoreOpen(false)}
        badgeCount={Array.isArray(myActions) ? myActions.filter((a) => a.status === "pending").length : 0}
        onNavigate={(next) => {
          setSelMeeting(null);
          setDetailOpenedFrom(null);
          goToPage(next);
        }}
      />

      {showWorkspaceAssistantFab ? (
        <WorkspaceAssistantFab
          user={activeUser}
          meetings={meetings}
          calendarMeetings={allVisibleMeetings}
          myActions={myActions}
          currentMeeting={page === "detail" ? selMeeting : null}
          onOpenMeeting={(m, extra) =>
            openMeeting(m, {
              ...(extra && typeof extra === "object" ? extra : {}),
              source:
                page === "detail"
                  ? detailOpenedFrom || "autojoin"
                  : page && ALLOWED_USER_PAGES.has(page) && page !== "detail"
                    ? page
                    : "autojoin",
            })
          }
          onViewAllWithContent={() => goToPage("transcripts")}
          launcherHidden={userMenuOpen || avaadaMobileMoreOpen || showInstant}
        />
      ) : null}

      {showInstant&&<InstantMeetingModal currentUser={activeUser} onClose={()=>setShowInstant(false)} onSuccess={addInstantMeeting}/>}
      <CreateCalendarEventModal
        open={createCalEventOpen}
        defaultDate={calSelDate}
        currentUser={activeUser}
        onClose={() => setCreateCalEventOpen(false)}
        onCreated={(ev) => {
          if (ev?.date) setCalSelDate(ev.date);
          if (ev) {
            setGoogleCalendarEvents((prev) =>
              mergeGoogleCalendarSnapshot(Array.isArray(prev) ? prev : [], [ev], false)
            );
          }
          setRefreshTick((v) => v + 1);
        }}
        onReauth={() => {
          const base = backendOriginForOAuth();
          const next = encodeURIComponent(window.location.pathname || "/");
          // Intentional calendar re-link: force consent once; replace to avoid stacking OAuth in history.
          window.location.replace(
            `${base}/api/auth/google?force_consent=1&next=${next}`
          );
        }}
      />

      {recordingModalPayload ? (
        <MeetingRecordingPremiumModal
          payload={recordingModalPayload}
          onClose={() => setRecordingModalPayload(null)}
        />
      ) : null}

      <RecurringAutoJoinConfirm
        prompt={recurringPrompt}
        featureLabel={JOIN_MEET_AI_LABEL}
        onAlsoVideoRecChange={(checked) => {
          setRecurringPrompt((prev) => (prev ? { ...prev, alsoVideoRec: checked === true } : prev));
        }}
        onCancel={() => {
          if (recurringPrompt?.busy) return;
          setRecurringPrompt(null);
        }}
        onApplyOne={async () => {
          if (!recurringPrompt || recurringPrompt.busy) return;
          const { mode, currentEid, nextEnabled, alsoVideoRec, currentMeeting } = recurringPrompt;
          setRecurringPrompt((prev) => prev ? { ...prev, busy: true, busyMode: "one", busyCount: 1 } : prev);
          try {
            if (mode === "global") {
              await applyGlobalAutoJoin(true, { alsoVideoRec: alsoVideoRec === true });
            } else {
              await applyMeetingAutoJoin([currentEid], nextEnabled, {
                alsoVideoRec: nextEnabled === true && alsoVideoRec === true,
              });
              if (nextEnabled && alsoVideoRec) {
                await enableVideoRecForEventIds([currentEid], currentMeeting);
              }
            }
          } finally {
            setRecurringPrompt(null);
          }
        }}
        onApplyAll={async () => {
          if (!recurringPrompt || recurringPrompt.busy) return;
          const { recurringId, eventIds, currentEid, nextEnabled, alsoVideoRec, currentMeeting } =
            recurringPrompt;
          setRecurringPrompt((prev) => prev ? { ...prev, busy: true, busyMode: "all", busyCount: (eventIds?.length || 1) } : prev);
          try {
            const resolved = await resolveSeriesEventIds(recurringId, eventIds || []);
            const targets = resolved.length > 0 ? resolved : [currentEid].filter(Boolean);
            setRecurringPrompt((prev) => prev ? { ...prev, busyCount: targets.length } : prev);
            await applyMeetingAutoJoin(targets, nextEnabled, {
              alsoVideoRec: nextEnabled === true && alsoVideoRec === true,
            });
            if (nextEnabled && alsoVideoRec) {
              await enableVideoRecForEventIds(targets, currentMeeting);
            }
          } finally {
            setRecurringPrompt(null);
          }
        }}
      />

      <RecurringAutoJoinConfirm
        prompt={recurringRecordingPrompt}
        featureLabel="Video Rec"
        onCancel={() => {
          if (recurringRecordingPrompt?.busy) return;
          setRecurringRecordingPrompt(null);
        }}
        onApplyOne={async () => {
          if (!recurringRecordingPrompt || recurringRecordingPrompt.busy) return;
          const { currentMeeting, currentEid, recurringId, eventTitle, nextEnabled } = recurringRecordingPrompt;
          setRecurringRecordingPrompt((prev) => prev ? { ...prev, busy: true, busyMode: "one", busyCount: 1 } : prev);
          try {
            const fallback = {
              ...(currentMeeting || {}),
              id: String(currentMeeting?.id || currentMeeting?._id || "").trim() || `gcal-${currentEid}`,
              googleEventId: String(currentMeeting?.googleEventId || currentEid || "").trim(),
              googleRecurringEventId: String(currentMeeting?.googleRecurringEventId || recurringId || "").trim(),
              title: currentMeeting?.title || eventTitle || "Recurring meeting",
            };
            await toggleMeetingRecording(fallback, nextEnabled, { skipRecurringPrompt: true });
          } finally {
            setRecurringRecordingPrompt(null);
          }
        }}
        onApplyAll={async () => {
          if (!recurringRecordingPrompt || recurringRecordingPrompt.busy) return;
          const { recurringId, eventIds, currentEid, currentMeeting, eventTitle, nextEnabled } = recurringRecordingPrompt;
          setRecurringRecordingPrompt((prev) => prev ? { ...prev, busy: true, busyMode: "all", busyCount: (eventIds?.length || 1) } : prev);
          try {
            const resolved = await resolveSeriesEventIds(recurringId, eventIds || []);
            const targets = resolved.length > 0 ? resolved : [currentEid].filter(Boolean);
            setRecurringRecordingPrompt((prev) => prev ? { ...prev, busyCount: targets.length } : prev);
            let failed = 0;
            for (const eid of targets) {
              const found = findMeetingByGoogleEventId(eid, googleCalendarEvents, meetings);
              const fallback = {
                ...(currentMeeting || {}),
                ...(found || {}),
                id: String(found?.id || found?._id || "").trim() || `gcal-${eid}`,
                googleEventId: String(found?.googleEventId || eid || "").trim(),
                googleRecurringEventId: String(found?.googleRecurringEventId || recurringId || "").trim(),
                title: found?.title || currentMeeting?.title || eventTitle || "Recurring meeting",
              };
              const ok = await toggleMeetingRecording(fallback, nextEnabled, {
                skipRecurringPrompt: true,
                silentError: true,
              });
              if (!ok) failed += 1;
            }
            if (failed > 0) {
              window.alert(`Video Rec update failed for ${failed} meeting${failed === 1 ? "" : "s"}. Please retry.`);
            }
          } finally {
            setRecurringRecordingPrompt(null);
          }
        }}
      />


      <SignOutConfirmModal
        open={signOutConfirmOpen}
        onCancel={()=>setSignOutConfirmOpen(false)}
        onConfirm={()=>{ setSignOutConfirmOpen(false); handleLogout(); }}
        dark={false}
      />
      <SessionExpiredModal open={sessionExpiredModalOpen} onOk={() => setSessionExpiredModalOpen(false)} />
      {/* Settings already exposes the reminders checkbox, so the floating prompt
          would be a second control for the same thing sitting on top of it. */}
      <SoftNotificationPrompt
        open={notificationSoftPromptOpen && !userInfoMode}
        blocked={notificationPromptBlocked}
        onAllow={handleAllowFromSoftPrompt}
        onDismiss={handleDismissSoftPrompt}
      />
      <UserInfoModal
        open={Boolean(userInfoMode)}
        mode={userInfoMode}
        user={activeUser}
        directoryDesignation={headerUserDesignation}
        meetings={meetings}
        myActions={myActions}
        calendarWarning={googleCalendarWarning}
        preferences={userPrefs}
        onSaveProfile={saveUserProfile}
        onSavePreferences={saveUserPreferences}
        onClose={() => setUserInfoMode(null)}
      />
      <MeetAiUsageModal
        open={meetAiUsageOpen}
        onClose={() => setMeetAiUsageOpen(false)}
        refreshKey={meetings.length}
      />
      <AppTourOverlay
        open={tourOpen}
        stepIndex={tourStep}
        onStepIndex={setTourStep}
        onClose={() => setTourOpen(false)}
        onNavigate={navigateAppTourStep}
        onComplete={() => setTourOpen(false)}
        storageKey={appTourStorageKey(loginEmail)}
      />
      <FeedbackPopover
        open={feedbackOpen}
        onClose={closeFeedbackPopover}
        onSubmit={submitFeedback}
        rating={feedbackRating}
        hoverRating={feedbackHoverRating}
        setRating={setFeedbackRating}
        setHoverRating={setFeedbackHoverRating}
        message={feedbackMessage}
        setMessage={setFeedbackMessage}
        attachments={feedbackAttachments}
        onPickFiles={onPickFeedbackFiles}
        onRemoveFile={removeFeedbackAttachment}
        loading={feedbackBusy}
        developerEmail={feedbackMeta.developerEmail}
        developerExtension={feedbackMeta.developerExtension}
        anchor={feedbackAnchor}
        anchorRef={feedbackBtnRef}
        statusText={feedbackStatusText}
        statusKind={feedbackStatusKind}
      />
    </div>
    </I18nContext.Provider>
    </PersonHoverTipProvider>
  );
}

/* ════════════════════════════════════════════════════════════════
   DASHBOARD
════════════════════════════════════════════════════════════════ */
function DashboardOverviewPage({
  user,
  meetings,
  mergedMeetings,
  myActions,
  onOpen,
  navigate,
  onOpenAction,
  onCreateEvent,
  onToggleMeetingAutoJoin,
  meetingAutoJoinSaving,
  onToggleMeetingRecording,
  meetingRecordingSaving,
}) {
  const { usage, error, loading } = useMeetAiUsage(true, meetings.length);
  const now = Date.now();
  const today = todayStr();
  const source = mergedMeetings?.length ? mergedMeetings : meetings;
  const schedule = source
    .filter((m) => String(m?.date || "") === today && effectiveMeetingStatus(m, now) !== "cancelled")
    .sort((a, b) => meetingDateTimeMs(a) - meetingDateTimeMs(b));
  const live =
    schedule.find((m) => effectiveMeetingStatus(m, now) === "live") ||
    meetings.find((m) => effectiveMeetingStatus(m, now) === "live");
  const next = schedule.find((m) => meetingDateTimeMs(m) >= now);
  const featured = live || next || schedule[0] || null;
  const featuredTitle = String(
    featured?.title ||
      featured?.summary ||
      featured?.name ||
      featured?.eventTitle ||
      featured?.event_title ||
      ""
  )
    .replace(/\s+/g, " ")
    .trim();
  const featuredDisplayTitle = featured
    ? featuredTitle || "Untitled meeting"
    : "Your schedule is clear";
  const actions = myActions.filter((a) => String(a?.status || "pending").toLowerCase() !== "completed");
  const overdue = actions.filter((a) => {
    const due = a?.due ? new Date(a.due).getTime() : NaN;
    return Number.isFinite(due) && due < now;
  });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = String(user?.name || "there").trim().split(/\s+/)[0] || "there";
  const dateLabel = new Intl.DateTimeFormat("en-IN", {
    weekday: "short", day: "numeric", month: "long", year: "numeric", timeZone: APP_DISPLAY_TZ,
  }).format(new Date());
  const timeRange = (m) => {
    const start = formatMeetingTimeForDisplay(m?.time) || "Time not set";
    const end = formatMeetingTimeForDisplay(m?.endTime || m?.end_time || m?.end);
    return end && end !== start ? `${start} – ${end}` : start;
  };
  const statusFor = (m) => {
    const status = effectiveMeetingStatus(m, now);
    if (status === "live") return ["Now", "now"];
    if (status === "completed" || status === "ended") return ["Done", "done"];
    return ["Upcoming", "upcoming"];
  };
  const participantCount = (m) =>
    joinedPlusNotJoinedCount(m?.participants, attendanceContextFromMeeting(m));
  const openOrJoin = (m, event) => {
    event?.stopPropagation?.();
    const url = joinUrlFromMeeting(m);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else onOpen?.(m);
  };
  const nextText = (() => {
    if (live) return "Happening now";
    if (!next) return "No more meetings";
    const minutes = Math.max(0, Math.round((meetingDateTimeMs(next) - now) / 60000));
    if (minutes < 60) return `In ${minutes} min`;
    return `In ${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;
  })();
  const liveId = live ? String(live.id ?? live._id ?? "") : "";
  const nextUp =
    live && next && String(next.id ?? next._id ?? "") !== liveId ? next : null;
  const nextNote = live
    ? nextUp
      ? `Now: ${live.title || "Current meeting"} · Next ${formatMeetingTimeForDisplay(nextUp.time) || ""} ${nextUp.title || ""}`.replace(/\s+/g, " ").trim()
      : (featuredDisplayTitle ? `Now: ${featuredDisplayTitle}` : "In progress")
    : next
      ? `${formatMeetingTimeForDisplay(next.time)} · ${next.title}`
      : `${schedule.length} meetings today`;

  return (
    <div className="page app-page-shell daily-dashboard avaada-page-dashboard">
      <style>{`
        .daily-dashboard{max-width:none!important;width:100%!important;margin:0!important;padding:8px 16px 24px!important;box-sizing:border-box;color:#0B2A4A;background:transparent!important}
        .daily-dashboard button{font-family:inherit}
        .daily-head{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin-bottom:12px}
        .daily-date{font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:#71869B;margin-bottom:2px}
        .daily-head h1{margin:0;font-size:24px;line-height:30px;font-weight:700;letter-spacing:-.3px;color:#0B2A4A;display:inline-flex;align-items:center;gap:8px}
        .daily-head-sun{display:inline-flex;color:#F59E0B}
        .daily-head-sub{margin:3px 0 0;font-size:12px;font-weight:400;line-height:1.4;color:#5F7690;max-width:40rem}
        .daily-new{height:36px;padding:0 14px;border:0;border-radius:8px;background:#0875E1;color:#fff;font-size:12px;font-weight:600;cursor:pointer;box-shadow:none;flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;gap:8px}
        .daily-new:hover{filter:brightness(1.04)}
        .daily-new .ui-btn-ico{display:inline-flex;line-height:0}
        .daily-top{display:grid;grid-template-columns:minmax(0,1.65fr) minmax(260px,1fr);gap:14px;margin-bottom:14px}
        .daily-feature{position:relative;min-height:176px;height:auto;max-height:none;padding:18px 20px;border:1px solid #C8DDF5;border-radius:12px;background:#EAF3FF;overflow:hidden;display:flex;flex-direction:column;cursor:pointer;box-sizing:border-box;box-shadow:none}
        .daily-feature:after{content:"";position:absolute;width:200px;height:200px;border:28px solid rgba(255,255,255,.42);border-radius:50%;right:-48px;top:-78px;pointer-events:none;opacity:.9;z-index:0}
        .daily-kicker{position:relative;z-index:1;display:flex;align-items:center;gap:7px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#139B5B;margin-bottom:8px;flex-shrink:0}
        .daily-kicker:before{content:"";width:6px;height:6px;border-radius:50%;background:#139B5B;flex-shrink:0}
        .daily-feature-main{position:relative;z-index:1;flex:1 1 auto;min-width:0;min-height:0}
        .daily-feature h2{margin:0 0 6px;font-size:20px;font-weight:700;line-height:1.3;color:#0B2A4A;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%}
        .daily-feature-meta{font-size:13px;line-height:1.35;color:#5F7690}
        .daily-feature-foot{position:relative;z-index:1;display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin-top:14px;padding-top:0;flex-shrink:0}
        .daily-feature-cta{display:flex;flex-direction:row;align-items:center;gap:10px;flex-shrink:0}
        .daily-feature-platform{width:40px;height:40px;border-radius:10px;border:1px solid #DCE6EF;background:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 1px 2px rgba(11,42,74,.05)}
        .daily-avatars{display:flex;align-items:center;min-height:34px}
        .daily-avatars>span{margin-left:-8px;border:2px solid #EAF3FF;border-radius:50%;line-height:0}
        .daily-avatars>span:first-child{margin-left:0}
        .daily-join{min-width:118px;height:40px;padding:0 14px;border:0;border-radius:8px;background:#0875E1;color:#fff;font-size:13px;font-weight:600;cursor:pointer;box-shadow:none;flex-shrink:0}
        .daily-join:hover{filter:brightness(1.04)}
        .daily-quick{display:grid;grid-template-rows:1fr 1fr;gap:14px;min-height:0}
        .daily-quick-card{min-height:92px;padding:14px;border:1px solid #DCE6EF;border-radius:10px;background:#fff;box-shadow:none;text-align:left;cursor:pointer;box-sizing:border-box}
        .daily-quick-card:hover{border-color:#C8DDF5}
        .daily-quick-label{display:block;font-size:11px;font-weight:600;color:#71869B}
        .daily-quick-value{display:block;font-size:20px;line-height:1.1;font-weight:700;color:#0B2A4A;letter-spacing:-.02em}
        .daily-quick-note{display:block;font-size:11px;color:#D66A6A;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .daily-quick-note.neutral{color:#71869B}
        .daily-grid{display:grid;grid-template-columns:minmax(0,2.1fr) minmax(260px,1fr);gap:16px;align-items:stretch}
        .daily-panel{border:1px solid #DCE6EF;border-radius:10px;background:#fff;overflow:hidden;box-shadow:none;display:flex;flex-direction:column;min-height:0}
        .daily-grid > .daily-panel{height:auto;max-height:none;min-height:0}
        .daily-panel-head{height:48px;min-height:48px;padding:0 16px;border-bottom:1px solid #E8EEF4;background:#F7FAFC;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0;box-sizing:border-box}
        .daily-panel-title{font-size:14px;font-weight:600;color:#0B2A4A}
        .daily-panel-body{flex:1 1 auto;min-height:0;max-height:min(300px,44vh);overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch}
        .daily-panel-body::-webkit-scrollbar{width:6px}
        .daily-panel-body::-webkit-scrollbar-thumb{background:#C5D0DC;border-radius:999px}
        .daily-link{border:0;background:none;padding:4px;color:#0875E1;font-size:12px;font-weight:600;cursor:pointer}
        .daily-link:hover{text-decoration:underline}
        .daily-row{height:auto;min-height:66px;padding:10px 16px;border-bottom:1px solid #E8EEF4;display:grid;grid-template-columns:118px 34px minmax(0,1fr) auto;gap:12px;align-items:center;cursor:pointer;box-sizing:border-box}
        .daily-row:last-child,.daily-action:last-child{border-bottom:none}
        .daily-row:hover,.daily-action:hover{background:#F7FAFC}
        .daily-time{position:relative;font-size:11px;font-weight:500;color:#71869B;white-space:nowrap;padding-right:10px}
        .daily-time:after{content:"";position:absolute;top:50%;right:0;transform:translateY(-50%);width:1px;height:28px;background:#E8EEF4}
        .daily-row-icon{width:34px;height:34px;border-radius:7px;border:1px solid #DCE6EF;background:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;box-sizing:border-box}
        .daily-row-main{display:flex;flex-direction:column;gap:3px;min-width:0}
        .daily-row-title{display:block;font-size:13px;font-weight:600;color:#0B2A4A;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .daily-sub{display:block;font-size:11px;color:#71869B;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .daily-status{padding:4px 9px;border-radius:999px;font-size:10px;font-weight:600;white-space:nowrap}
        .daily-status.now{background:#EAF3FF;color:#0875E1}
        .daily-status.done{background:#F1F5F9;color:#526A82}
        .daily-status.upcoming{background:#EAF3FF;color:#0875E1}
        .daily-row-aside{display:inline-flex;align-items:center;justify-content:flex-end;gap:8px;min-width:0;flex-shrink:0}
        .daily-meetai-chip{display:inline-flex;align-items:center;gap:5px;height:auto;min-height:24px;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:600;line-height:1.25;white-space:nowrap;box-sizing:border-box;border:1px solid transparent;overflow:visible;flex-shrink:0}
        .daily-meetai-chip.is-joined{background:#F0FDF4;color:#15803D;border-color:#86EFAC}
        .daily-meetai-chip.is-missed{background:#F8FAFC;color:#64748B;border-color:#E2E8F0}
        .daily-meetai-chip .daily-meetai-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;background:currentColor}
        .daily-row-toggles{display:inline-flex;flex-direction:row;align-items:center;gap:14px;flex-shrink:0}
        .daily-row-toggle{display:inline-flex;align-items:center;flex-shrink:0}
        .daily-row-toggle .ui-toggle-label,.daily-row-toggle label>span:last-child{font-size:11.5px!important;font-weight:600!important;color:#24425F!important;white-space:nowrap}
        .daily-action{height:54px;min-height:54px;padding:0 16px;border-bottom:1px solid #E8EEF4;display:grid;grid-template-columns:16px minmax(0,1fr) auto;gap:10px;align-items:center;cursor:pointer;box-sizing:border-box}
        .daily-check{width:16px;height:16px;border:1.5px solid #CBD5E1;border-radius:4px;box-sizing:border-box;background:#fff}
        .daily-action-title{font-size:12px;color:#24425F;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .daily-due{padding:3px 7px;border-radius:999px;background:#FFF1F1;color:#D66A6A;font-size:9.5px;font-weight:600;white-space:nowrap}
        .daily-empty{padding:28px 16px;color:#71869B;font-size:12px;text-align:center}
        .daily-usage{margin-top:14px;height:auto!important;max-height:none!important}
        .daily-usage .daily-panel-head{height:46px;min-height:46px}
        .daily-usage-body{padding:14px 16px 16px}
        @media(max-width:1100px){.daily-dashboard{padding:16px 8px 28px!important}.daily-top,.daily-grid{grid-template-columns:minmax(0,1.85fr) minmax(220px,1fr);gap:14px}}
        @media(max-width:900px){.daily-top,.daily-grid{grid-template-columns:1fr}.daily-grid > .daily-panel{height:auto;max-height:none}.daily-quick{grid-template-columns:1fr 1fr;grid-template-rows:auto;gap:12px}.daily-quick-card{height:auto;min-height:88px}.daily-feature{max-height:none;height:auto;min-height:160px}}
        @media(max-width:768px){.daily-dashboard{padding:12px 8px 28px!important}.daily-head{align-items:flex-start;flex-wrap:wrap}.daily-head h1{font-size:24px;line-height:30px}.daily-new{height:36px;padding:0 14px}.daily-quick{grid-template-columns:1fr}.daily-row{grid-template-columns:84px 32px minmax(0,1fr);gap:10px}.daily-row-aside,.daily-status{grid-column:2 / -1;justify-self:start;margin-top:2px}.daily-time:after{display:none}.daily-dashboard .dashboard-usage-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}}
        @media(max-width:480px){.daily-dashboard .dashboard-usage-grid{grid-template-columns:1fr!important}}
      `}</style>

      <header className="daily-head">
        <div>
          <div className="daily-date">{dateLabel}</div>
          <h1>
            {greeting}, {firstName}
            {hour < 17 ? (
              <span className="daily-head-sun" aria-hidden>
                <Svg d={IC.sun} size={22} color="#F59E0B" stroke={2} />
              </span>
            ) : null}
          </h1>
          <p className="daily-head-sub">Here&apos;s what&apos;s happening with your meetings today.</p>
        </div>
        <button
          type="button"
          className="daily-new"
          onClick={() => {
            if (typeof onCreateEvent === "function") onCreateEvent();
            else navigate?.("autojoin");
          }}
        >
          <span className="ui-btn-ico" aria-hidden>
            <Svg d={IC.plus} size={16} color="currentColor" stroke={2.25} />
          </span>
          Create Meeting
        </button>
      </header>

      <div className="daily-top">
        <section className="daily-feature" onClick={() => featured && onOpen?.(featured)}>
          <div className="daily-kicker">{live ? "Happening now" : featured ? "Up next" : "Today"}</div>
          <div className="daily-feature-main">
            <h2 title={featuredDisplayTitle}>{featuredDisplayTitle}</h2>
            <div className="daily-feature-meta">
              {featured
                ? `${timeRange(featured)} · ${participantCount(featured)} participants`
                : "No meetings are scheduled for today."}
            </div>
          </div>
          {featured ? (
            <div className="daily-feature-foot">
              <div className="daily-avatars">
                {displayUserProfiles(featured.participants || []).slice(0, 3).map((p, i) => (
                  <span key={`${p.email || p.name || "person"}-${i}`}>
                    <Avatar initials={p.initials || String(p.name || "?").slice(0,2).toUpperCase()} size={34} color={["#35599a","#566f9f","#263d6d"][i % 3]} imageUrl={p.avatar_url || p.avatarUrl || ""} title={p.name || p.email || "Participant"} />
                  </span>
                ))}
              </div>
              <div className="daily-feature-cta">
                <span className="daily-feature-platform" aria-hidden>
                  <MeetingListPlatformIcon platform={featured?.platform || "meet"} size={22} />
                </span>
                <button type="button" className="daily-join" onClick={(e) => openOrJoin(featured, e)}>
                  {joinUrlFromMeeting(featured) ? "Join meeting" : "Open meeting"}
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <div className="daily-quick">
          <button type="button" className="daily-quick-card" onClick={() => navigate?.("actions")}>
            <span className="daily-quick-ico daily-quick-ico--actions" aria-hidden>
              <Svg d={IC.actionList} size={18} color="#EA580C" stroke={2} />
            </span>
            <span className="daily-quick-label">Open action items</span>
            <span className="daily-quick-value">{actions.length}</span>
            <span className={`daily-quick-note${overdue.length ? "" : " neutral"}`}>
              {overdue.length
                ? `${overdue.length} overdue of ${actions.length} open`
                : actions.length
                  ? `${actions.length} open · none overdue`
                  : "No open actions"}
            </span>
            <span className="daily-quick-chev" aria-hidden>
              <Svg d={IC.chevR} size={16} color="#94A3B8" stroke={2} />
            </span>
          </button>
          <button type="button" className="daily-quick-card" onClick={() => navigate?.("autojoin")}>
            <span className="daily-quick-ico daily-quick-ico--next" aria-hidden>
              <Svg d={IC.clock} size={18} color="#0875E1" stroke={2} />
            </span>
            <span className="daily-quick-label">{live ? "Live now" : "Next meeting"}</span>
            <span className="daily-quick-value">{nextText}</span>
            <span className="daily-quick-note neutral" title={nextNote}>
              {nextNote}
            </span>
            <span className="daily-quick-chev" aria-hidden>
              <Svg d={IC.chevR} size={16} color="#94A3B8" stroke={2} />
            </span>
          </button>
        </div>
      </div>

      <div className="daily-grid">
        <section className="daily-panel">
          <div className="daily-panel-head"><span className="daily-panel-title">Today&apos;s schedule</span><button type="button" className="daily-link" onClick={() => navigate?.("autojoin")}>View all meetings</button></div>
          <div className="daily-panel-body">
            {schedule.length ? schedule.map((m) => {
              const [label, type] = statusFor(m);
              const googleEventId = extractGoogleEventId(m);
              const isUpcomingOrLive = type === "upcoming" || type === "now";
              const canToggle = isUpcomingOrLive && !!googleEventId && typeof onToggleMeetingAutoJoin === "function";
              const toggleSaving = !!meetingAutoJoinSaving?.[googleEventId];
              const recApiId = meetingRecordingApiId(m);
              const recordingSaving = !!meetingRecordingSaving?.[recApiId];
              const autoJoinOn = meetingAutoJoinIsOn(m);
              const recordingOn = effectiveRecordingEnabled(m);
              const botJoined = meetingBotJoinedCall(m);
              return (
                <div key={meetingListReactKey(m)} className="daily-row" onClick={() => onOpen?.(m)}>
                  <span className="daily-time">{timeRange(m)}</span>
                  <span className="daily-row-icon" aria-hidden>
                    <MeetListRowPlatformIcon meeting={m} />
                  </span>
                  <span className="daily-row-main">
                    <span className="daily-row-title">{m.title || "Untitled meeting"}</span>
                    <span className="daily-sub">{participantCount(m)} participants</span>
                  </span>
                  <span className="daily-row-aside" onClick={(e) => e.stopPropagation()}>
                    {type === "done" ? (
                      <span
                        className={`daily-meetai-chip ${botJoined ? "is-joined" : "is-missed"}`}
                        title={botJoined ? "MeetAI joined this meeting" : "MeetAI did not join this meeting. Open for details."}
                        aria-label={botJoined ? "MeetAI joined this meeting" : "MeetAI did not join this meeting"}
                      >
                        {botJoined ? (
                          <span className="daily-meetai-dot" aria-hidden />
                        ) : (
                          <MeetAiNotJoinedIcon size={12} color="#94A3B8" />
                        )}
                        {botJoined ? "MeetAI joined" : "MeetAI Not Joined"}
                      </span>
                    ) : canToggle ? (
                      <>
                        {type === "now" ? <span className={`daily-status ${type}`}>{label}</span> : null}
                        <span className="daily-row-toggles">
                          <span className="daily-row-toggle">
                            <ToggleBar
                              checked={autoJoinOn}
                              disabled={toggleSaving}
                              label={JOIN_MEET_AI_LABEL}
                              switchWidth={26}
                              switchHeight={15}
                              thumbSize={11}
                              title={JOIN_MEET_AI_TOGGLE_HINT}
                              onChange={(next) => onToggleMeetingAutoJoin?.(googleEventId, next, m)}
                              stopPropagation
                              tipHorizontal="center"
                              tipPrefer="below"
                              tipMaxWidth={220}
                            />
                          </span>
                          <span className="daily-row-toggle">
                            <ToggleBar
                              checked={autoJoinOn && recordingOn}
                              disabled={recordingSaving || !autoJoinOn}
                              label="Video Rec"
                              switchWidth={26}
                              switchHeight={15}
                              thumbSize={11}
                              title={
                                !autoJoinOn
                                  ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                                  : VIDEO_REC_ENABLE_HINT_COMPACT
                              }
                              onChange={(next) => {
                                if (next && !autoJoinOn) return;
                                onToggleMeetingRecording?.(m, next);
                              }}
                              stopPropagation
                              tipHorizontal="center"
                              tipPrefer="below"
                              tipMaxWidth={220}
                            />
                          </span>
                        </span>
                      </>
                    ) : (
                      <span className={`daily-status ${type}`}>{label}</span>
                    )}
                  </span>
                </div>
              );
            }) : <div className="daily-empty">No meetings scheduled today.</div>}
          </div>
        </section>

        <section className="daily-panel">
          <div className="daily-panel-head"><span className="daily-panel-title">Actions due</span><button type="button" className="daily-link" onClick={() => navigate?.("actions")}>See all</button></div>
          <div className="daily-panel-body">
            {actions.length ? actions.map((a, i) => (
              <div
                key={a.id || `${a.meetingId || "action"}-${i}`}
                className="daily-action"
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (typeof onOpenAction === "function") onOpenAction(a);
                  else navigate?.("actions");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (typeof onOpenAction === "function") onOpenAction(a);
                    else navigate?.("actions");
                  }
                }}
              >
                <span className="daily-check" aria-hidden /><span className="daily-action-title">{a.text || a.task || a.title || "Untitled action item"}</span>
                {a.due ? <span className="daily-due">{String(a.due)}</span> : null}
              </div>
            )) : <div className="daily-empty">No open action items.</div>}
          </div>
        </section>
      </div>

      <section className="daily-panel daily-usage">
        <div className="daily-panel-head"><span className="daily-panel-title">Meeting usage</span></div>
        <div className="daily-usage-body"><MeetAiUsageContent usage={usage} error={error} loading={loading} embedded /></div>
      </section>
    </div>
  );
}

function DashPage({user,meetings,mergedMeetings,myActions,onOpen,navigate,onToggleMeetingAutoJoin,meetingAutoJoinSaving}){
  const [statPopup, setStatPopup] = useState(null);
  const { usage: meetUsage, error: meetUsageError, loading: meetUsageLoading } = useMeetAiUsage(true, meetings.length);
  const liveMeeting = meetings.find((m) => effectiveMeetingStatus(m) === "live");
  const nowMs = Date.now();
  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const endToday = new Date(startToday);
  endToday.setHours(23, 59, 59, 999);

  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  const endTomorrow = new Date(startTomorrow);
  endTomorrow.setHours(23, 59, 59, 999);

  const futureMeetings = (mergedMeetings || meetings)
    .filter((m) => {
      const st = effectiveMeetingStatus(m);
      if (st === "cancelled") return false;
      const ms = meetingDateTimeMs(m);
      return ms >= nowMs;
    })
    .sort((a, b) => meetingDateTimeMs(a) - meetingDateTimeMs(b));

  const upcomingToday = futureMeetings
    // Only show meetings that fall on today's local date.
    .filter((m) => {
      const ms = meetingDateTimeMs(m);
      return ms >= startToday.getTime() && ms <= endToday.getTime();
    })
    .slice(0, 3);

  const upcomingTomorrow = futureMeetings
    // Only show meetings that fall on tomorrow's local date.
    .filter((m) => {
      const ms = meetingDateTimeMs(m);
      return ms >= startTomorrow.getTime() && ms <= endTomorrow.getTime();
    })
    .slice(0, 3);

  // If Today and Tomorrow are empty, still show nearest future meetings.
  const upcoming = upcomingToday.length > 0
    ? upcomingToday
    : upcomingTomorrow.length > 0
      ? upcomingTomorrow
      : futureMeetings.slice(0, 3);
  const recent = meetings
    .filter((m) => {
      const st = effectiveMeetingStatus(m);
      if (!(st === "completed" || st === "ended")) return false;
      const ms = meetingDateTimeMs(m);
      return ms >= startToday.getTime() && ms <= endToday.getTime();
    })
    .sort((a, b) => meetingDateTimeMs(b) - meetingDateTimeMs(a))
    .slice(0, 12);
  const recentByDate = recent.reduce((acc, m) => {
    const key = m?.date || "Unknown date";
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});
  const recentDateKeys = Object.keys(recentByDate).sort((a, b) => {
    const aMs = ymdToDate(a)?.getTime?.() || 0;
    const bMs = ymdToDate(b)?.getTime?.() || 0;
    return bMs - aMs;
  });
  const dayStartMs = startToday.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const labelRecentDay = (ymd) => {
    const dt = ymdToDate(ymd);
    if (!dt) return String(ymd || "Unknown date");
    const d = new Date(dt);
    d.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((dayStartMs - d.getTime()) / dayMs);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return dt.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: APP_DISPLAY_TZ,
    });
  };
  const myPending   = myActions.filter(a=>a.status==="pending");
  const weekAgoMs = nowMs - 7 * 24 * 60 * 60 * 1000;
  const thisWeek = meetings.filter((m) => {
    const ms = meetingDateTimeMs(m);
    return ms > weekAgoMs && ms <= nowMs;
  }).length;
  const emailsSent = meetings.filter(m=>m.emailSent).length;
  const meetingsThisWeekList = meetings
    .filter((m) => {
      const ms = meetingDateTimeMs(m);
      return ms > weekAgoMs && ms <= nowMs;
    })
    .sort((a, b) => meetingDateTimeMs(b) - meetingDateTimeMs(a));
  const emailedMeetingsList = meetings
    .filter((m) => m.emailSent)
    .sort((a, b) => meetingDateTimeMs(b) - meetingDateTimeMs(a));
  const upcomingAllList = futureMeetings.slice(0, 25);

  const statCards = [
    { key: "week",    label: "Meetings this week", val: thisWeek,         icon: IC.cal,        color: "#2563EB", iconBg: "#DBEAFE" },
    { key: "emails",  label: "Emails Delivered",   val: emailsSent,       icon: IC.mail,       color: "#16A34A", iconBg: "#DCFCE7" },
    { key: "actions", label: "My Open Actions",    val: myPending.length, icon: IC.actionList, color: "#D97706", iconBg: "#FEF3C7" },
    { key: "upcoming",label: "Upcoming",           val: futureMeetings.length,  icon: IC.upcomingCal, color: "#7C3AED", iconBg: "#EDE9FE" },
  ];

  return (
    <div className="page app-page-shell">

      {/* ── Live now banner ── */}
      {liveMeeting&&(
        <div onClick={()=>onOpen(liveMeeting)} style={{
          background:"linear-gradient(120deg,#0D1B2A 0%,#1A3560 100%)",
          borderRadius:14,padding:"18px 22px",marginBottom:20,
          display:"flex",alignItems:"center",gap:16,cursor:"pointer",
          boxShadow:"0 4px 20px rgba(37,99,235,0.2)",
          border:"1px solid rgba(96,165,250,0.2)",
          transition:"transform .15s",
        }}
          onMouseEnter={e=>e.currentTarget.style.transform="translateY(-1px)"}
          onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
          <div style={{position:"relative",flexShrink:0}}>
            <div style={{width:44,height:44,borderRadius:12,background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Svg d={IC.mic} color="#F87171" size={20}/>
            </div>
            <div style={{position:"absolute",top:-3,right:-3,width:10,height:10,borderRadius:"50%",background:"#1565C0",border:"2px solid #0D1B2A",animation:"pulse 1.4s ease infinite"}}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:600,color:"rgba(239,68,68,0.8)",letterSpacing:".08em",textTransform:"uppercase",marginBottom:3}}>
              {meetingHasTranscriptData(liveMeeting)
                ? "Live · Transcript updating"
                : meetingBotJoinedCall(liveMeeting)
                  ? "Live · MeetAI in call"
                  : meetingMeetAiIsJoining(liveMeeting)
                    ? "Live · MeetAI joining"
                    : "Live · Invite MeetAI off"}
            </div>
            <div style={{fontSize:15,fontWeight:600,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{liveMeeting.title}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:2}}>Started at {liveMeeting.time} · {joinedPlusNotJoinedCount(liveMeeting.participants, attendanceContextFromMeeting(liveMeeting))} participants</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            <div style={{fontSize:12.5,fontWeight:500,color:"#BBCFEE",background:"rgba(37,99,235,0.2)",padding:"6px 14px",borderRadius:20,border:"1px solid rgba(37,99,235,0.3)"}}>
              {meetingHasTranscriptData(liveMeeting)
                ? "View transcript"
                : meetingBotJoinedCall(liveMeeting)
                  ? "Open meeting"
                  : meetingMeetAiIsJoining(liveMeeting)
                    ? "Waiting for MeetAI"
                    : "Open meeting"}
            </div>
            <Svg d={IC.chevR} color="rgba(255,255,255,0.3)" size={16}/>
          </div>
        </div>
      )}

      <div className="content-well">
      {/* ── Stat row ── */}
      <div className="dashboard-stat-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {statCards.map(s=>(
          <button
            key={s.label}
            type="button"
            onClick={() => setStatPopup(s.key)}
            style={{
            background:T.bg,border:`1px solid ${T.border}`,borderRadius:12,
            padding:"16px 18px",display:"flex",alignItems:"center",gap:14,width:"100%",
            boxShadow:T.shadow,
            cursor:"pointer",textAlign:"left",transition:"transform .12s ease, box-shadow .18s ease, border-color .18s ease",
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 12px 28px ${s.color}1A, 0 4px 8px rgba(15,23,42,0.06)`;
              e.currentTarget.style.borderColor = s.color+"44";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = T.shadow;
              e.currentTarget.style.borderColor = T.border;
            }}
          >
            <div style={{width:44,height:44,borderRadius:12,background:s.iconBg||s.color+"14",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 2px 8px ${s.color}22`}}>
              <Svg d={s.icon} color={s.color} size={20}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:26,fontWeight:800,color:T.text,lineHeight:1,marginBottom:4,letterSpacing:"-0.02em"}}>{s.val}</div>
              <div style={{fontSize:12,color:T.textMuted,lineHeight:1.3,fontWeight:500}}>{s.label}</div>
            </div>
            <div style={{width:6,height:36,borderRadius:3,background:s.color,opacity:0.18,flexShrink:0}}/>

          </button>
        ))}
      </div>

      {/* ── Meeting usage ── */}
      <div
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: "16px 18px",
          marginBottom: 20,
          boxShadow: T.shadow,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>Meeting usage</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
            Recorded meetings you own or participate in
          </div>
        </div>
        <MeetAiUsageContent
          usage={meetUsage}
          error={meetUsageError}
          loading={meetUsageLoading}
          embedded
        />
      </div>

      {statPopup && (
        <div
          role="presentation"
          onClick={() => setStatPopup(null)}
          style={{position:"fixed",inset:0,zIndex:10030,background:"rgba(15,23,42,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Dashboard details"
            onClick={(e) => e.stopPropagation()}
            style={{width:"min(940px,96vw)",maxHeight:"84vh",overflow:"hidden",background:"#fff",borderRadius:14,border:"1px solid #dbe4f0",boxShadow:"0 28px 56px rgba(15,23,42,0.25)"}}
          >
            <div style={{padding:"14px 18px",borderBottom:"1px solid #e6edf7",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div>
                <div style={{fontSize:15.5,fontWeight:700,color:"#0f172a"}}>
                  {statPopup === "week" ? "Meetings this week" : statPopup === "emails" ? "Emails Delivered" : statPopup === "actions" ? "My Open Actions" : "Upcoming meetings"}
                </div>
                <div style={{fontSize:12,color:"#64748b",marginTop:2}}>
                  {statPopup === "week" ? `${meetingsThisWeekList.length} meeting records in the last 7 days` : statPopup === "emails" ? `${emailedMeetingsList.length} meetings with delivered summary emails` : statPopup === "actions" ? `${myPending.length} pending action items assigned` : `${upcomingAllList.length} upcoming meetings in your queue`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStatPopup(null)}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#64748b",
                  cursor: "pointer",
                  padding: "6px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width={18} height={18} fill="none" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="panel-scroll" style={{maxHeight:"calc(84vh - 72px)",overflowY:"auto",padding:16,background:"#f8fbff",paddingRight:18}}>
              {statPopup === "week" && (meetingsThisWeekList.length === 0 ? (
                <div style={{fontSize:13,color:"#64748b"}}>No meetings found in the last 7 days.</div>
              ) : meetingsThisWeekList.map((m) => (
                <div key={`week-${m._id || m.id}`} onClick={() => { setStatPopup(null); onOpen(m); }} style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 14px",marginBottom:10,cursor:"pointer"}}>
                  <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a",marginBottom:4}}>{m.title || "Untitled meeting"}</div>
                  <div style={{fontSize:12,color:"#475569"}}>{m.date ? formatYmdDisplay(m.date) : "No date"} {formatMeetingTimeForDisplay(m.time) || ""} · {effectiveMeetingStatus(m)}</div>
                </div>
              )))}
              {statPopup === "emails" && (emailedMeetingsList.length === 0 ? (
                <div style={{fontSize:13,color:"#64748b"}}>No delivered meeting emails found yet.</div>
              ) : emailedMeetingsList.map((m) => {
                const recipients = uniqueParticipantsByEmail(humanParticipants(m.participants || []))
                  .map((p) => {
                    const email = String(p?.email || "").trim();
                    const name = String(p?.name || "").trim();
                    // Never show synthetic guest placeholders like `guest.<hash>@meet.link`.
                    if (email && isGuestMeetLinkEmail(email)) return name || "Guest participant";
                    return email || name || "";
                  })
                  .filter(Boolean);
                return (
                  <div key={`mail-${m._id || m.id}`} style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:4}}>
                      <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a"}}>{m.title || "Untitled meeting"}</div>
                      <div style={{fontSize:11,color:"#1d4ed8",background:"#e0ecff",border:"1px solid #bfdbfe",padding:"3px 8px",borderRadius:999}}>Delivered</div>
                    </div>
                    <div style={{fontSize:12,color:"#475569",marginBottom:6}}>{m.date ? formatYmdDisplay(m.date) : "No date"} {formatMeetingTimeForDisplay(m.time) || ""}</div>
                    <div style={{fontSize:11.5,color:"#64748b",marginBottom:3}}>Sent to:</div>
                    <div style={{fontSize:12.5,color:"#0f172a"}}>
                      {recipients.length > 0 ? recipients.join(", ") : "Participant emails not available"}
                    </div>
                  </div>
                );
              }))}
              {statPopup === "actions" && (myPending.length === 0 ? (
                <div style={{fontSize:13,color:"#64748b"}}>No pending action items right now.</div>
              ) : myPending.map((a) => (
                <div key={`act-${a.id}`} style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                  <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a",marginBottom:4}}>{a.text || a.task || a.title || "Untitled action item"}</div>
                  <div style={{fontSize:12,color:"#475569"}}>
                    Meeting: {a.meetingTitle || "Unknown meeting"} {a.meetingDate ? `· ${formatYmdDisplay(a.meetingDate)}` : ""}
                  </div>
                  {a.owner && <div style={{fontSize:12,color:"#64748b",marginTop:4}}>Owner: {a.owner}</div>}
                </div>
              )))}
              {statPopup === "upcoming" && (upcomingAllList.length === 0 ? (
                <div style={{fontSize:13,color:"#64748b"}}>No upcoming meetings found.</div>
              ) : upcomingAllList.map((m) => (
                <div key={`up-${m._id || m.id}`} onClick={() => { setStatPopup(null); onOpen(m); }} style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 14px",marginBottom:10,cursor:"pointer"}}>
                  <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a",marginBottom:4}}>{m.title || "Untitled meeting"}</div>
                  <div style={{fontSize:12,color:"#475569"}}>{m.date ? formatYmdDisplay(m.date) : "No date"} {formatMeetingTimeForDisplay(m.time) || ""} · {joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m))} participants</div>
                </div>
              )))}
            </div>
          </div>
        </div>
      )}

      {/* ── Dashboard Feed ── */}
      <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden"}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13.5,fontWeight:700,color:T.text,marginBottom:2}}>Dashboard Feed</div>
            <div style={{fontSize:12,color:T.textMuted}}>Upcoming + recently finished meetings</div>
          </div>
        </div>

        <div className="dashboard-feed-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,padding:18,alignItems:"start"}}>
          {/* Left card: Upcoming */}
          <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:"#F8FBFF"}}>
              <div style={{fontSize:13.5,fontWeight:700,color:T.accentDark}}>Upcoming</div>
            </div>
            {upcoming.length===0 ? (
              <div style={{padding:"18px 18px",color:T.textMuted,fontSize:13}}>No upcoming meetings</div>
            ) : (
              upcoming.map((m) => {
                const { joined: upJoined, notJoined: upNotJoined } = splitJoinedVsNotJoinedInvitees(
                  m.participants || [],
                  attendanceContextFromMeeting(m)
                );
                const people = uniqueParticipantsByEmail([...upJoined, ...upNotJoined]);
                const first = people.slice(0, 3);
                const more = Math.max(0, people.length - first.length);
                const effSt = effectiveMeetingStatus(m, nowMs);
                const googleEventId = String(m?.googleEventId || "").trim();
                const showAutoJoin =
                  (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
                  !!googleEventId &&
                  effSt !== "cancelled" &&
                  (effSt === "upcoming" || effSt === "live");
                const saving = !!meetingAutoJoinSaving?.[googleEventId];
                const autoOn = m?.autoJoinEnabled === true;
                return (
                  <div
                    key={m._id || m.id}
                    onClick={()=>onOpen(m)}
                    style={{padding:"14px 14px",borderBottom:`1px solid ${T.border}`,cursor:"pointer",transition:"background .1s"}}
                    onMouseEnter={(e)=>{e.currentTarget.style.background=T.surface;}}
                    onMouseLeave={(e)=>{e.currentTarget.style.background="transparent";}}
                  >
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                      <div style={{minWidth:0,flex:1}}>
                        {/* Match screenshot: title chip */}
                        <div style={{
                          display:"inline-flex",
                          alignItems:"center",
                          background:"#EEF4FF",
                          color:"#0D47A1",
                          border:"1px solid #CFE0FF",
                          padding:"4px 11px",
                          borderRadius:999,
                          fontSize:11.5,
                          fontWeight:700,
                          lineHeight:1.1,
                          marginBottom:5,
                          maxWidth:"100%",
                          overflow:"hidden",
                          textOverflow:"ellipsis",
                          whiteSpace:"nowrap"
                        }}>
                          {m.title}
                        </div>
                        <div style={{fontSize:11.5,color:"#64748B",marginTop:0}}>
                          {formatYmdDisplay(m.date)} {formatMeetingTimeForDisplay(m.time)} — Scheduled
                        </div>
                        {showAutoJoin ? (
                          <div style={{marginTop:8}} onClick={(e)=>e.stopPropagation()}>
                            <ToggleBar
                              checked={autoOn}
                              disabled={saving}
                              label={saving ? "Saving…" : autoOn ? JOIN_MEET_AI_LABEL + " on" : JOIN_MEET_AI_LABEL + " off"}
                              onChange={(next)=>onToggleMeetingAutoJoin?.(googleEventId, next, m)}
                              stopPropagation
                            />
                          </div>
                        ) : null}
                      </div>

                      {/* Overlapping avatars on the right */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",flexShrink:0}}>
                        {first.map((p,idx) => (
                          <div
                            key={p.email || p.initials}
                            style={{
                              marginLeft: idx === 0 ? 0 : -10,
                              borderRadius:999,
                              border: `2px solid ${T.bg}`,
                              boxShadow:"0 2px 10px rgba(15,23,42,0.06)",
                            }}
                          >
                            <Avatar
                              initials={p.initials || (p.name ? String(p.name).slice(0, 2).toUpperCase() : "")}
                              size={26}
                              color="#9CA3AF"
                              isBot={false}
                              imageUrl={p.avatar_url || p.avatarUrl || p.picture || ""}
                              title={`${p.name || "User"}${p.email ? ` (${p.email})` : ""}`}
                            />
                          </div>
                        ))}
                        {more>0 && (
                          <div style={{fontSize:12,color:"#64748B",fontWeight:700,marginLeft:8}}>
                            +{more}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Right card: Recent */}
          <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:"#F8FBFF"}}>
              <div style={{fontSize:13.5,fontWeight:700,color:T.accentDark}}>Recent</div>
            </div>

            {recent.length === 0 ? (
              <div style={{padding:"18px 18px",textAlign:"center"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",marginBottom:10}}>
                  <img
                    src={RECENT_EMPTY_IMG}
                    alt="No MOMs generated today"
                    style={{ width: 170, height: "auto", objectFit: "contain", opacity: 0.98, margin: "0 auto" }}
                  />
                </div>
                <div style={{fontSize:14,fontWeight:800,color:T.text,marginBottom:4}}>No MOMs generated today</div>
                <div style={{fontSize:11.5,color:T.textMuted,lineHeight:1.5}}>
                  Wrap-ups from today's meetings will appear here.
                </div>
              </div>
            ) : (
              <div
                className="panel-scroll"
                style={{
                  display:"flex",
                  flexDirection:"column",
                  gap:0,
                  padding:"10px 14px 14px",
                  maxHeight:520,
                  overflowY:"auto",
                }}
              >
                {recentDateKeys.map((dateKey, groupIdx) => (
                  <div key={dateKey} style={{borderBottom:groupIdx<recentDateKeys.length-1?`1px solid ${T.border}`:"none",padding:"6px 0 10px"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 8px 6px"}}>
                      <div style={{fontSize:11.5,fontWeight:700,color:T.accentDark,textTransform:"uppercase",letterSpacing:.3}}>
                        {labelRecentDay(dateKey)}
                      </div>
                      <div style={{fontSize:11,color:"#64748B",fontWeight:600}}>
                        {recentByDate[dateKey].length} meeting{recentByDate[dateKey].length>1?"s":""}
                      </div>
                    </div>
                    {recentByDate[dateKey]
                      .slice()
                      .sort((a,b)=>meetingDateTimeMs(b)-meetingDateTimeMs(a))
                      .map((m) => {
                        const profilePreview = displayUserProfiles(m.participants || []).slice(0, 3);
                        return (
                          <div
                            key={m._id||m.id}
                            onClick={()=>onOpen(m)}
                            style={{
                              cursor:"pointer",
                              margin:"4px 0",
                              padding:"10px 10px",
                              border:`1px solid ${T.border}`,
                              borderRadius:10,
                              transition:"background .15s ease,border-color .15s ease"
                            }}
                            onMouseEnter={(e)=>{e.currentTarget.style.background="#FAFCFF"; e.currentTarget.style.borderColor="#BBCFEE";}}
                            onMouseLeave={(e)=>{e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor=T.border;}}
                          >
                            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:12.8,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {m.title}
                                </div>
                                <div style={{fontSize:11.5,color:"#64748B",marginTop:2}}>
                                  {formatMeetingTimeForDisplay(m.time) || "Time not set"}
                                </div>
                                <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
                                  {profilePreview.length===0 ? (
                                    <div style={{fontSize:11,color:T.textMuted}}>No participants found</div>
                                  ) : (
                                    profilePreview.map((p, idx) => (
                                      <div
                                        key={`${m._id||m.id}-profile-${idx}`}
                                        style={{
                                          display:"flex",
                                          alignItems:"center",
                                          gap:9,
                                          minWidth:0,
                                          background:"#F8FBFF",
                                          border:"1px solid #E2E8F0",
                                          borderRadius:9,
                                          padding:"6px 8px",
                                        }}
                                      >
                                        <Avatar
                                          initials={p.initials || (p.name ? String(p.name).slice(0, 2).toUpperCase() : "")}
                                          size={30}
                                          color="#9CA3AF"
                                          isBot={false}
                                          imageUrl={p.avatar_url || p.avatarUrl || ""}
                                          title={`${p.name || "User"}${p.email ? ` (${p.email})` : ""}`}
                                        />
                                        <div style={{minWidth:0}}>
                                          <div style={{fontSize:12,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                            {p.name || "Unknown user"}
                                          </div>
                                          <div style={{fontSize:10.8,color:T.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:1}}>
                                            {p.email || "No email"}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

/**
 * Confirmation dialog shown when the user toggles Auto Join (or Video Rec on a series).
 * Turning Auto Join ON can include a "Also enable Video Rec?" toggle.
 * Recurring: selectable Only this one / All recurring meetings → primary Invite MeetAI.
 * Single enable: Turn on Auto Join.
 */
function RecurringAutoJoinConfirm({
  prompt,
  onCancel,
  onApplyOne,
  onApplyAll,
  onAlsoVideoRecChange,
  featureLabel = JOIN_MEET_AI_LABEL,
}) {
  const [scope, setScope] = useState("one");

  useEffect(() => {
    if (!prompt) return;
    setScope("one");
    const onKey = (e) => {
      if (e.key === "Escape" && !prompt?.busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prompt, onCancel]);

  if (!prompt) return null;
  const { eventTitle, nextEnabled, busy, busyMode, mode, alsoVideoRec } = prompt;
  const isSingle = mode === "single";
  const isGlobal = mode === "global";
  const showVideoCheckbox = featureLabel === JOIN_MEET_AI_LABEL && nextEnabled === true;
  const inviteMeetAiSeries = featureLabel === JOIN_MEET_AI_LABEL && !isGlobal && !isSingle;
  const isSeriesChoice = !isGlobal && !isSingle;
  const titleText = isGlobal
    ? `Turn on ${featureLabel} for all meetings?`
    : isSingle
      ? `Turn on ${featureLabel}?`
      : nextEnabled
        ? inviteMeetAiSeries
          ? "Invite MeetAI to all recurring meetings?"
          : `Apply ${featureLabel} to all upcoming?`
        : inviteMeetAiSeries
          ? "Turn off Invite MeetAI for recurring meetings?"
          : `Turn off ${featureLabel} for all upcoming?`;
  const busyText = busy
    ? busyMode === "all"
      ? "Applying to all upcoming instances…"
      : isGlobal
        ? "Saving global Auto Join…"
        : "Saving…"
    : "";

  const primaryLabel = isGlobal
    ? "Turn on for all"
    : isSingle
      ? `Turn on ${JOIN_MEET_AI_LABEL}`
      : nextEnabled
        ? inviteMeetAiSeries
          ? "Invite MeetAI"
          : featureLabel === "Video Rec"
            ? "Apply Video Rec"
            : `Apply ${featureLabel}`
        : inviteMeetAiSeries
          ? "Turn off Invite MeetAI"
          : featureLabel === "Video Rec"
            ? "Turn off Video Rec"
            : `Turn off ${featureLabel}`;

  const runPrimary = () => {
    if (busy) return;
    if (isGlobal || isSingle) {
      onApplyOne?.();
      return;
    }
    if (scope === "all") onApplyAll?.();
    else onApplyOne?.();
  };

  return createPortal(
    <div
      role="presentation"
      className="recurring-aj-overlay"
      onClick={busy ? undefined : onCancel}
    >
      <style>{`@keyframes recurring-spinner{to{transform:rotate(360deg)}}@keyframes recurring-aj-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-busy={busy ? "true" : "false"}
        aria-labelledby="recurring-dialog-title"
        className={`recurring-aj-modal${isSeriesChoice ? " recurring-aj-modal--series" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="recurring-aj-close"
          aria-label="Close"
          disabled={!!busy}
          onClick={onCancel}
        >
          <Svg d={IC.close} size={18} color="currentColor" stroke={2} />
        </button>

        <div className="recurring-aj-header">
          <div className="recurring-aj-header-icon" aria-hidden>
            <img
              src={FRIENDLY_AI_ROBOT_RECURRING_IMG}
              alt=""
              className="recurring-aj-header-robot"
              draggable={false}
            />
          </div>
          <div className="recurring-aj-header-text">
            <h2 id="recurring-dialog-title" className="recurring-aj-title">
              {titleText}
            </h2>
            <p className="recurring-aj-desc">
              {isGlobal ? (
                <>Enable {featureLabel} for all invited Google Calendar meetings?</>
              ) : isSingle ? (
                <>
                  Enable {featureLabel} for{" "}
                  <strong className="recurring-aj-meeting-name">&quot;{eventTitle}&quot;</strong>?
                </>
              ) : inviteMeetAiSeries ? (
                <>
                  <strong className="recurring-aj-meeting-name">&quot;{eventTitle}&quot;</strong> repeats
                  on your calendar.
                  {nextEnabled
                    ? " Do you want MeetAI to join only this occurrence, or all upcoming instances in the series?"
                    : " Turn off Invite MeetAI for only this occurrence, or for all upcoming instances?"}
                </>
              ) : (
                <>
                  <strong className="recurring-aj-meeting-name">&quot;{eventTitle}&quot;</strong> is a
                  recurring meeting. Apply this change to every upcoming instance, or only this one?
                </>
              )}
            </p>
          </div>
        </div>

        {isSeriesChoice ? (
          <div className="recurring-aj-options" role="radiogroup" aria-label="Apply to">
            <button
              type="button"
              role="radio"
              aria-checked={scope === "one"}
              disabled={!!busy}
              className={`recurring-aj-option${scope === "one" ? " is-selected" : ""}`}
              onClick={() => setScope("one")}
            >
              <span className="recurring-aj-option-top">
                <span className="recurring-aj-option-icon" aria-hidden>
                  <Svg d={IC.cal} size={18} color="#0875E1" stroke={2} />
                </span>
                <span className={`recurring-aj-radio${scope === "one" ? " is-on" : ""}`} aria-hidden />
              </span>
              <span className="recurring-aj-option-title">Only this one</span>
              <span className="recurring-aj-option-desc">
                {nextEnabled
                  ? inviteMeetAiSeries
                    ? "Invite MeetAI to join this specific occurrence only."
                    : `Apply ${featureLabel} to this specific occurrence only.`
                  : `Turn off ${featureLabel} for this specific occurrence only.`}
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={scope === "all"}
              disabled={!!busy}
              className={`recurring-aj-option${scope === "all" ? " is-selected" : ""}`}
              onClick={() => setScope("all")}
            >
              <span className="recurring-aj-option-top">
                <span className="recurring-aj-option-icon" aria-hidden>
                  <Svg d={IC.refresh} size={18} color="#0875E1" stroke={2} />
                </span>
                <span className={`recurring-aj-radio${scope === "all" ? " is-on" : ""}`} aria-hidden />
              </span>
              <span className="recurring-aj-option-title">
                {inviteMeetAiSeries ? "All recurring meetings" : "Apply to all"}
              </span>
              <span className="recurring-aj-option-desc">
                {nextEnabled
                  ? inviteMeetAiSeries
                    ? "Invite MeetAI to join all upcoming instances in this series."
                    : `Apply ${featureLabel} to all upcoming instances in this series.`
                  : `Turn off ${featureLabel} for all upcoming instances in this series.`}
              </span>
            </button>
          </div>
        ) : null}

        {showVideoCheckbox ? (
          <div className="recurring-aj-video">
            <span className="recurring-aj-video-icon" aria-hidden>
              <Svg d={IC.video} size={18} color="#0875E1" stroke={2} />
            </span>
            <div className="recurring-aj-video-copy">
              <div className="recurring-aj-video-title">Also enable Video Rec</div>
              <div className="recurring-aj-video-desc">
                {isGlobal
                  ? "Save a recording (REC) for meetings when Auto Join runs."
                  : "Save a recording (REC) for this meeting when Auto Join runs."}
              </div>
            </div>
            <div className="recurring-aj-video-toggle">
              <ToggleBar
                checked={alsoVideoRec === true}
                disabled={!!busy}
                showLabel={false}
                title="Also enable Video Rec"
                switchWidth={40}
                switchHeight={22}
                thumbSize={16}
                onChange={(next) => onAlsoVideoRecChange?.(next)}
              />
            </div>
          </div>
        ) : null}

        {busy ? (
          <div className="recurring-aj-busy" aria-live="polite">
            <span className="recurring-aj-spinner" aria-hidden />
            <span>{busyText}</span>
          </div>
        ) : null}

        <div className="recurring-aj-footer">
          <button
            type="button"
            className="recurring-aj-btn recurring-aj-btn--cancel"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="recurring-aj-btn recurring-aj-btn--primary"
            disabled={busy}
            onClick={runPrimary}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function findMeetingByGoogleEventId(eventId, googleCalendarEvents, meetings) {
  const eid = String(eventId || "").trim();
  if (!eid) return null;
  return (
    (Array.isArray(googleCalendarEvents) ? googleCalendarEvents : []).find(
      (m) => extractGoogleEventId(m) === eid
    ) ||
    (Array.isArray(meetings) ? meetings : []).find(
      (m) => extractGoogleEventId(m) === eid
    ) ||
    null
  );
}

/* ════════════════════════════════════════════════════════════════
   MEETINGS PAGE
════════════════════════════════════════════════════════════════ */
const MeetPage = memo(function MeetPage({
  loading = false,
  calendarSyncing = false,
  meetings,
  onOpen,
  meetView = "meetings",
  showSectionNav = false,
  shellLayout = false,
  externalSearch = "",
  onExternalSearchChange,
  onMeetViewChange,
  autoJoinEnabled,
  autoJoinSaving,
  onToggleAutoJoin,
  meetingAutoJoinSaving,
  onToggleMeetingAutoJoin,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
  rescheduleSources = null,
  onScheduleMeeting,
  onInstantJoin,
}) {
  const { lang, t } = useI18n();
  /** Re-render so Live → Ended flips when scheduled end passes (no manual refresh). */
  const [, setStatusClock] = useState(0);
  useEffect(() => {
    const tick = () => setStatusClock((n) => n + 1);
    tick();
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      tick();
    }, 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const [filter,setFilter]=useState("all");
  const [q,setQ]=useState("");
  const effectiveQ = shellLayout ? String(externalSearch || "").trim() : q;
  /** List is always the default; Grid is session-only (not saved — fresh login/reload → List). */
  const [viewMode, setViewMode] = useState("list");
  useEffect(() => {
    try {
      window.localStorage?.removeItem?.("meetai.meetings.viewMode");
    } catch {
      /* ignore */
    }
  }, []);
  /** Per-tab date filters prevent one tab from polluting another. */
  const [quickDateFilterByView, setQuickDateFilterByView] = useState({
    meetings: "all",
    transcripts: "all",
    autojoin: "week",
  });
  const [calendarPickedByView, setCalendarPickedByView] = useState({
    meetings: null,
    transcripts: null,
    autojoin: null,
  });
  /** Recorded Meetings sort order — newest first by default, toggleable via the toolbar icon. */
  const [transcriptSortOrder, setTranscriptSortOrder] = useState(() => {
    try {
      if (typeof window === "undefined") return "desc";
      const stored = window.localStorage?.getItem?.("meetai.transcripts.sortOrder");
      return stored === "asc" ? "asc" : "desc";
    } catch {
      return "desc";
    }
  });
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem?.("meetai.transcripts.sortOrder", transcriptSortOrder === "asc" ? "asc" : "desc");
      }
    } catch {
      /* storage may be disabled (private mode); ignore. */
    }
  }, [transcriptSortOrder]);
  const quickDateFilter = quickDateFilterByView?.[meetView] || (meetView === "autojoin" ? "week" : "all");
  const calendarPickedYmd = calendarPickedByView?.[meetView] || null;
  const setQuickDateFilter = (next) =>
    setQuickDateFilterByView((prev) => ({ ...(prev || {}), [meetView]: next }));
  const setCalendarPickedYmd = (next) =>
    setCalendarPickedByView((prev) => ({ ...(prev || {}), [meetView]: next || null }));
  const autoJoinRangeIds = new Set(["today", "week", "monthly"]);
  const effectiveQuickDateFilter =
    meetView === "autojoin"
      ? autoJoinRangeIds.has(quickDateFilter)
        ? quickDateFilter
        : "week"
      : quickDateFilter;
  const { containerRef: statusTabsRef, registerTab: registerStatusTab, thumbStyle: statusThumbStyle } =
    useSlidingTabIndicator(meetView === "meetings" ? filter : null);
  const { containerRef: viewTabsRef, registerTab: registerViewTab, thumbStyle: viewThumbStyle } =
    useSlidingTabIndicator(viewMode);
  const { containerRef: autoJoinRangeTabsRef, registerTab: registerAutoJoinRangeTab, thumbStyle: autoJoinRangeThumbStyle } =
    useSlidingTabIndicator(meetView === "autojoin" ? effectiveQuickDateFilter : null);

  const filters = [
    { id: "all", label: t("all", "All") },
    { id: "live", label: t("live", "Live") },
    { id: "upcoming", label: t("upcoming", "Upcoming") },
    { id: "done", label: t("done", "Done") },
  ];
  const autoJoinRangeFilters = [
    { id: "today", label: t("today", "Today") },
    { id: "week", label: t("week", "Week") },
    { id: "monthly", label: t("monthly", "Monthly") },
  ];
  const todayDateStr = todayStr();
  const dateFilteringEnabled = true;
  const isAutoJoinWeek = meetView === "autojoin" && effectiveQuickDateFilter === "week";
  const isAutoJoinMonthly = meetView === "autojoin" && effectiveQuickDateFilter === "monthly";
  const autoJoinWeekFrom = startOfWeekYmd(todayDateStr);
  const autoJoinWeekTo = endOfWeekYmd(todayDateStr);
  const autoJoinMonthPrefix = String(todayDateStr).slice(0, 7);
  const presetFilterYmd =
    effectiveQuickDateFilter === "all" ||
    effectiveQuickDateFilter === "week" ||
    effectiveQuickDateFilter === "monthly" ||
    effectiveQuickDateFilter === "this_week" ||
    effectiveQuickDateFilter === "last_week"
      ? null
      : effectiveQuickDateFilter === "custom"
        ? null
      : effectiveQuickDateFilter === "yesterday"
        ? addDaysYmd(todayDateStr, -1)
        : effectiveQuickDateFilter === "tomorrow"
          ? addDaysYmd(todayDateStr, 1)
          : todayDateStr;
  const activeCalendarPickedYmd = dateFilteringEnabled ? calendarPickedYmd : null;
  const filterDateYmd =
    effectiveQuickDateFilter === "custom" && activeCalendarPickedYmd && String(activeCalendarPickedYmd).trim()
      ? String(activeCalendarPickedYmd).trim()
      : presetFilterYmd;
  const forceAllStatusesForToday =
    effectiveQuickDateFilter === "today" && !(activeCalendarPickedYmd && String(activeCalendarPickedYmd).trim());
  const sourceMeetings = Array.isArray(meetings) ? meetings : [];
  const deferredMeetView = useDeferredValue(meetView);
  const filterMeetView = SMOOTH_NAV_TRANSITION ? deferredMeetView : meetView;
  /** Default Meetings grid: last 30 local days through end of (today + 30 days) so yesterday's meetings stay visible. */
  const meetingsGridWindowStartMs = startOfLocalDayMs(addDaysYmd(todayDateStr, -30));
  const meetingsForwardEndMs = endOfLocalDayMs(addDaysYmd(todayDateStr, 30));
  /** Auto Join: future calendar days start tomorrow; today only if still live/upcoming. */
  const autoJoinTomorrowStartMs = startOfLocalDayMs(addDaysYmd(todayDateStr, 1));
  const autoJoinWindowEndMs = endOfLocalDayMs(addDaysYmd(todayDateStr, 30));
  /** Recorded Meetings date filter — normalized so stale/unknown ids fall back to "all". */
  const transcriptDateFilterId = ["all", "today", "yesterday", "this_week", "last_week", "custom"].includes(effectiveQuickDateFilter)
    ? effectiveQuickDateFilter
    : "all";
  const transcriptWeekFrom = startOfWeekYmd(todayDateStr);
  const transcriptWeekTo = endOfWeekYmd(todayDateStr);
  const transcriptLastWeekFrom = addDaysYmd(transcriptWeekFrom, -7);
  const transcriptLastWeekTo = addDaysYmd(transcriptWeekFrom, -1);
  const transcriptYesterdayYmd = addDaysYmd(todayDateStr, -1);
  const shown = useMemo(() => {
    if (filterMeetView !== "meetings") return [];
    const shownUnsorted = sourceMeetings
      .filter((m) => {
        if (!filterDateYmd) return true;
        const ms = meetingDateTimeMs(m);
        if (ms) {
          const ymdFromMs = dateToYmd(new Date(ms));
          return ymdFromMs === String(filterDateYmd).trim();
        }
        return String(m?.date || "").trim() === String(filterDateYmd).trim();
      })
      .filter((m) => {
        if (forceAllStatusesForToday) return true;
        const status = effectiveMeetingStatus(m);
        if (filter === "all") return true;
        if (filter === "done") return status === "completed" || status === "ended";
        if (filter === "upcoming") return status === "upcoming";
        return status === filter;
      })
      .filter((m) => {
        const title = String(m?.title || "").toLowerCase();
        return !effectiveQ || title.includes(effectiveQ.toLowerCase());
      })
      .filter((m) => {
        if (activeCalendarPickedYmd && String(activeCalendarPickedYmd).trim()) return true;
        if (effectiveQuickDateFilter !== "all") return true;
        const ms = meetingDateTimeMs(m);
        if (!ms) return true;
        return ms >= meetingsGridWindowStartMs && ms <= meetingsForwardEndMs;
      });
    return sortMeetingsRecentThenUpcoming(shownUnsorted);
  }, [
    filterMeetView,
    sourceMeetings,
    filterDateYmd,
    forceAllStatusesForToday,
    filter,
    effectiveQ,
    activeCalendarPickedYmd,
    effectiveQuickDateFilter,
    meetingsGridWindowStartMs,
    meetingsForwardEndMs,
  ]);
  const transcriptShown = useMemo(() => {
    if (filterMeetView !== "transcripts") return [];
    const transcriptDayYmd = (m) => {
      const ms = meetingDateTimeMs(m);
      if (ms) return dateToYmd(new Date(ms));
      return String(m?.date || "").trim().slice(0, 10);
    };
    const matchesTranscriptDateFilter = (m) => {
      if (transcriptDateFilterId === "all") return true;
      if (transcriptDateFilterId === "custom") {
        const picked = activeCalendarPickedYmd && String(activeCalendarPickedYmd).trim();
        if (!picked) return true;
        return transcriptDayYmd(m) === picked;
      }
      if (transcriptDateFilterId === "today") return transcriptDayYmd(m) === todayDateStr;
      if (transcriptDateFilterId === "yesterday") return transcriptDayYmd(m) === transcriptYesterdayYmd;
      if (transcriptDateFilterId === "this_week") {
        const d = transcriptDayYmd(m);
        return !!d && d >= transcriptWeekFrom && d <= transcriptWeekTo;
      }
      if (transcriptDateFilterId === "last_week") {
        const d = transcriptDayYmd(m);
        return !!d && d >= transcriptLastWeekFrom && d <= transcriptLastWeekTo;
      }
      return true;
    };
    return sourceMeetings
      .filter((m) => {
        const needle = String(effectiveQ || "").trim().toLowerCase();
        if (!needle) return true;
        if (String(m?.title || "").toLowerCase().includes(needle)) return true;
        return String(meetingHostName(m) || "").toLowerCase().includes(needle);
      })
      .filter((m) => meetingHasTranscriptData(m))
      .filter((m) => matchesTranscriptDateFilter(m))
      .sort((a, b) => {
        const d = meetingDateTimeMs(b) - meetingDateTimeMs(a);
        if (d !== 0) return transcriptSortOrder === "asc" ? -d : d;
        return transcriptSortOrder === "asc"
          ? String(a?.title || "").localeCompare(String(b?.title || ""))
          : String(b?.title || "").localeCompare(String(a?.title || ""));
      });
  }, [
    filterMeetView,
    sourceMeetings,
    effectiveQ,
    transcriptSortOrder,
    transcriptDateFilterId,
    activeCalendarPickedYmd,
    todayDateStr,
    transcriptWeekFrom,
    transcriptWeekTo,
    transcriptLastWeekFrom,
    transcriptLastWeekTo,
    transcriptYesterdayYmd,
  ]);
  const autoJoinDayYmd = useCallback((m) => {
    const ms = meetingDateTimeMs(m);
    if (ms) return dateToYmd(new Date(ms));
    return String(m?.date || "").trim().slice(0, 10);
  }, []);
  const autoJoinEligible = useMemo(() => {
    if (filterMeetView !== "autojoin") return [];
    return sourceMeetings
      .filter((m) => {
        // Calendar poller rows use `calendar_auto` after the meeting is materialized
        // in the shared DB. They are still upcoming Google Calendar meetings and
        // must remain visible to invited users alongside calendar stubs.
        if (m?.type !== "google_calendar" && m?.type !== "calendar_auto") return false;
        if (upcomingStubAlreadyHeld(m, sourceMeetings)) return false;
        const status = effectiveMeetingStatus(m);
        if (status !== "upcoming" && status !== "live") return false;
        const startMs = meetingDateTimeMs(m);
        if (!startMs || startMs > autoJoinWindowEndMs) return false;
        const dayYmd = String(m?.date || "").trim().slice(0, 10);
        if (dayYmd === todayDateStr) return true;
        return startMs >= autoJoinTomorrowStartMs;
      })
      .filter((m) => {
        const title = String(m?.title || "").toLowerCase();
        return !effectiveQ || title.includes(effectiveQ.toLowerCase());
      })
      .sort((a, b) => {
        const ra = effectiveMeetingStatus(a);
        const rb = effectiveMeetingStatus(b);
        const score = (s) => (s === "live" ? 0 : s === "upcoming" ? 1 : 2);
        const sd = score(ra) - score(rb);
        if (sd !== 0) return sd;
        return meetingDateTimeMs(a) - meetingDateTimeMs(b);
      });
  }, [
    filterMeetView,
    sourceMeetings,
    effectiveQ,
    autoJoinWindowEndMs,
    autoJoinTomorrowStartMs,
    todayDateStr,
  ]);
  const autoJoinShown = useMemo(() => {
    if (filterMeetView !== "autojoin") return [];
    return autoJoinEligible.filter((m) => {
      const dayYmd = autoJoinDayYmd(m);
      if (!dayYmd) return false;
      if (isAutoJoinWeek) return dayYmd >= autoJoinWeekFrom && dayYmd <= autoJoinWeekTo;
      if (isAutoJoinMonthly) return dayYmd.startsWith(autoJoinMonthPrefix);
      return dayYmd === todayDateStr;
    });
  }, [
    filterMeetView,
    autoJoinEligible,
    autoJoinDayYmd,
    isAutoJoinWeek,
    isAutoJoinMonthly,
    autoJoinWeekFrom,
    autoJoinWeekTo,
    autoJoinMonthPrefix,
    todayDateStr,
  ]);
  const autoJoinCalendarRows = useMemo(() => {
    if (filterMeetView !== "autojoin") return [];
    const autoJoinHistoryStartYmd = addDaysYmd(todayDateStr, -56);
    const byKey = new Map();
    const remember = (m) => {
      const k = String(m?.id || m?.googleEventId || meetingListReactKey(m));
      if (!byKey.has(k)) byKey.set(k, m);
    };
    for (const m of autoJoinEligible) remember(m);
    for (const m of sourceMeetings) {
      if (m?.type !== "google_calendar") continue;
      const status = effectiveMeetingStatus(m);
      if (status !== "completed" && status !== "ended") continue;
      const dayYmd = autoJoinDayYmd(m);
      if (!dayYmd || dayYmd < autoJoinHistoryStartYmd) continue;
      const ms = meetingDateTimeMs(m);
      if (ms && ms > autoJoinWindowEndMs + 86400000) continue;
      remember(m);
    }
    return sortMeetingsChronologically([...byKey.values()]);
  }, [
    filterMeetView,
    autoJoinEligible,
    sourceMeetings,
    autoJoinDayYmd,
    todayDateStr,
    autoJoinWindowEndMs,
  ]);
  const visibleRows = useMemo(() => {
    if (filterMeetView === "transcripts") return transcriptShown;
    if (filterMeetView === "autojoin") return autoJoinShown;
    return shown;
  }, [filterMeetView, transcriptShown, autoJoinShown, shown]);
  const visibleRowsStableRef = useRef(visibleRows);
  if (filterMeetView === meetView) visibleRowsStableRef.current = visibleRows;
  const listViewTransitioning = SMOOTH_NAV_TRANSITION && filterMeetView !== meetView;
  const rowsForList = listViewTransitioning ? visibleRowsStableRef.current : visibleRows;
  const transcriptDateFilterLabel = (() => {
    if (meetView !== "transcripts") return "";
    if (transcriptDateFilterId === "custom") {
      return (activeCalendarPickedYmd && String(activeCalendarPickedYmd).trim()) || "Custom date";
    }
    if (transcriptDateFilterId === "today") return "Today";
    if (transcriptDateFilterId === "yesterday") return "Yesterday";
    if (transcriptDateFilterId === "this_week") return "This Week";
    if (transcriptDateFilterId === "last_week") return "Last Week";
    return "All days";
  })();
  const autoJoinGridEmpty = meetView === "autojoin" && viewMode === "grid" && autoJoinEligible.length === 0;
  const listOrCardEmpty =
    !(meetView === "autojoin" && viewMode === "grid") && rowsForList.length === 0 && !listViewTransitioning;
  const meetingsPagingResetKey = [
    meetView,
    filter,
    effectiveQuickDateFilter,
    String(filterDateYmd || ""),
    String(effectiveQ || ""),
  ].join(":");

  /** Gmail-style redesign applies to All Meetings only; Upcoming keeps the prior shell UI. */
  const redesignShell = shellLayout && meetView === "meetings";
  const allMeetingsRedesignShell = redesignShell && meetView === "meetings";
  const recordedPageShell = shellLayout && meetView === "transcripts";
  const autoJoinWeekAttended = useMemo(() => {
    return autoJoinShown.filter((m) => meetingBotJoinedCall(m)).length;
  }, [autoJoinShown]);

  if (loading) {
    return <MeetingsPageSkeleton meetView={meetView} />;
  }

  const widenFilters = () => {
    if (!shellLayout) setQ("");
    setFilter("all");
    setQuickDateFilter(meetView === "autojoin" ? "week" : "all");
    setCalendarPickedYmd(null);
    if (meetView === "transcripts") setTranscriptSortOrder("desc");
  };

  const summaryStatIcon = (kind, size) => {
    const map = { video: IC.video, live: IC.meet, clock: IC.upcomingCal, check: IC.check };
    return <Svg d={map[kind] || IC.info} size={size} color="currentColor" stroke={2} />;
  };

  const MeetWellWrapper = allMeetingsRedesignShell || recordedPageShell ? Fragment : "div";
  const meetWellWrapperProps = allMeetingsRedesignShell || recordedPageShell ? {} : { className: "meet-well" };
  const AllMeetingsFrame = allMeetingsRedesignShell || recordedPageShell ? Fragment : "div";
  const allMeetingsFrameProps = allMeetingsRedesignShell || recordedPageShell
    ? {}
    : { className: redesignShell ? "avaada-meet-content-card" : undefined };

  return (
    <div
      className={`page app-page-shell meet-page-root${shellLayout ? " meet-page-shell" : ""}${
        redesignShell && meetView === "meetings" ? " avaada-page-meetings" : ""
      }${redesignShell && meetView === "autojoin" ? " avaada-page-upcoming" : ""}${
        recordedPageShell ? " avaada-page-recorded" : ""
      }`}
    >
      {showSectionNav ? (
        <MeetSectionsNav meetView={meetView} onMeetViewChange={onMeetViewChange} />
      ) : null}

      {!redesignShell && meetView === "autojoin" ? (
        <div
          className={
            shellLayout
              ? "meet-shell-page-header meet-shell-page-header--autojoin"
              : "auto-join-premium card"
          }
          data-tour="auto-join-banner"
          style={
            shellLayout
              ? undefined
              : {
                  marginBottom: 14,
                  padding: "14px 16px 14px 22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }
          }
        >
          {shellLayout ? (
            <>
              <div className="meet-shell-page-header-text">
                <h1>Upcoming meetings</h1>
                <p className="meet-shell-page-header-sub">
                  {t(
                    "joinMeetAiSubheading",
                    "Showing meetings synced from your calendar. Instant meetings are available under All Meetings."
                  )}
                </p>
              </div>
              <div className="meet-shell-page-header-action">
                <div
                  className={`meet-invite-chip${autoJoinEnabled ? " is-on" : ""}${
                    autoJoinSaving || meetingSettingsReadOnly ? " is-disabled" : ""
                  }`}
                >
                  <ToggleBar
                    checked={!!autoJoinEnabled}
                    disabled={!!autoJoinSaving || meetingSettingsReadOnly}
                    label={autoJoinSaving ? t("saving", "Saving…") : t("joinMeetAi", JOIN_MEET_AI_LABEL)}
                    title={
                      meetingSettingsReadOnly
                        ? meetingSettingsReadOnlyHint
                        : t("joinMeetAiGlobalTooltip", "This will auto join all your meetings")
                    }
                    onChange={(next) => onToggleAutoJoin?.(next)}
                    switchWidth={28}
                    switchHeight={16}
                    thumbSize={12}
                    labelStyle={{ fontSize: 12, fontWeight: 700, color: "inherit" }}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>Upcoming meetings</div>
                <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.45, maxWidth: 560 }}>
                  {t(
                    "joinMeetAiSubheading",
                    "Showing meetings synced from your calendar. Instant meetings are available under All Meetings."
                  )}
                </div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                  Video recordings use secure 7-day retention by default.
                </div>
              </div>
              <div
                className={`meet-invite-chip${autoJoinEnabled ? " is-on" : ""}${
                  autoJoinSaving || meetingSettingsReadOnly ? " is-disabled" : ""
                }`}
              >
                <ToggleBar
                  checked={!!autoJoinEnabled}
                  disabled={!!autoJoinSaving || meetingSettingsReadOnly}
                  label={autoJoinSaving ? t("saving", "Saving…") : t("joinMeetAi", JOIN_MEET_AI_LABEL)}
                  title={
                    meetingSettingsReadOnly
                      ? meetingSettingsReadOnlyHint
                      : t("joinMeetAiGlobalTooltip", "This will auto join all your meetings")
                  }
                  onChange={(next) => onToggleAutoJoin?.(next)}
                  switchWidth={28}
                  switchHeight={16}
                  thumbSize={12}
                  labelStyle={{ fontSize: 12, fontWeight: 700, color: "inherit" }}
                />
              </div>
            </>
          )}
        </div>
      ) : null}
      {!redesignShell && meetView === "autojoin" ? null : !redesignShell && shellLayout && meetView === "meetings" ? (
        <div className="meet-shell-page-header meet-shell-page-header--simple">
          <h1>{t("allMeetings", "All Meetings")}</h1>
        </div>
      ) : !redesignShell && shellLayout && meetView === "transcripts" ? (
        <div className="meet-shell-page-header meet-shell-page-header--simple meet-shell-page-header--recorded">
          <h1>{t("allTranscripts", "Recorded Meetings")}</h1>
          <div className="meet-recorded-header-actions" role="group" aria-label="Recorded meetings filters">
            {dateFilteringEnabled ? (
              <div className="date-filter-shell meet-recorded-header-filter">
                <MeetAiResponsiveDatePicker
                  valueYmd={
                    effectiveQuickDateFilter === "custom" && activeCalendarPickedYmd
                      ? activeCalendarPickedYmd
                      : null
                  }
                  onChangeYmd={(ymd) => {
                    setCalendarPickedYmd(ymd);
                    setQuickDateFilter("custom");
                  }}
                  picked={!!calendarPickedYmd}
                  iconSize={15}
                  title={t("pickDate", "Pick a date")}
                />
                <select
                  value={
                    effectiveQuickDateFilter === "custom" && activeCalendarPickedYmd
                      ? "__cal__"
                      : transcriptDateFilterId
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__cal__") return;
                    setCalendarPickedYmd(null);
                    setQuickDateFilter(v);
                  }}
                  aria-label="Filter meetings by day"
                >
                  <option value="all">{t("all", "All")}</option>
                  <option value="today">{t("today", "Today")}</option>
                  <option value="yesterday">{t("yesterday", "Yesterday")}</option>
                  <option value="this_week">{t("thisWeek", "This Week")}</option>
                  <option value="last_week">{t("lastWeek", "Last Week")}</option>
                  {activeCalendarPickedYmd ? (
                    <option value="__cal__">{activeCalendarPickedYmd}</option>
                  ) : null}
                </select>
              </div>
            ) : null}
            <button
              type="button"
              className="transcript-sort-btn-inshell meet-recorded-header-sort"
              onClick={() => setTranscriptSortOrder((p) => (p === "asc" ? "desc" : "asc"))}
              title={
                transcriptSortOrder === "asc"
                  ? "Oldest first — switch to newest first"
                  : "Newest first — switch to oldest first"
              }
              aria-label={
                transcriptSortOrder === "asc"
                  ? "Sort transcripts oldest first, activate to sort newest first"
                  : "Sort transcripts newest first, activate to sort oldest first"
              }
              aria-pressed={transcriptSortOrder === "asc"}
            >
              <Svg
                d="M7 4v12m0 0L4 13m3 3l3-3M17 20V8m0 0l-3 3m3-3l3 3"
                size={13}
                color="currentColor"
                stroke={2}
              />
              {transcriptSortOrder === "asc" ? t("oldest", "Oldest") : t("newest", "Newest")}
            </button>
            <div
              ref={viewTabsRef}
              className="meet-view-toggle sliding-tabs-host meet-recorded-header-view"
              role="tablist"
              aria-label="Switch meetings layout"
            >
              <SlidingTabThumb style={viewThumbStyle} className="sliding-tab-thumb sliding-tab-thumb--view" />
              <button
                type="button"
                ref={registerViewTab("list")}
                role="tab"
                aria-selected={viewMode === "list"}
                className={`meet-view-toggle-btn sliding-tab-btn${viewMode === "list" ? " active" : ""}`}
                onClick={() => setViewMode("list")}
                title={t("list", "List view")}
              >
                <Svg d="M4 6h16M4 12h16M4 18h16" size={13} color="currentColor" stroke={2.2} />
                {t("list", "List")}
              </button>
              <button
                type="button"
                ref={registerViewTab("grid")}
                role="tab"
                aria-selected={viewMode === "grid"}
                className={`meet-view-toggle-btn sliding-tab-btn${viewMode === "grid" ? " active" : ""}`}
                onClick={() => setViewMode("grid")}
                title={t("grid", "Grid view")}
              >
                <Svg
                  d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"
                  size={13}
                  color="currentColor"
                  stroke={2}
                />
                {t("grid", "Grid")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {redesignShell && meetView === "meetings" ? (
        <div className="avaada-meet-page avaada-meet-page--hero">
          <MeetRedesignHero
            title={t("allMeetings", "All Meetings")}
            subtitle="View, join, manage and explore all your meetings in one place."
            onScheduleMeeting={onScheduleMeeting}
          />
        </div>
      ) : null}

      {redesignShell && meetView === "autojoin" ? (
        <div className="avaada-meet-page">
          <MeetRedesignHero
            title="Upcoming Meetings"
            subtitle={t(
              "joinMeetAiSubheading",
              "Showing meetings synced from your calendar. Instant meetings are available under All Meetings."
            )}
            onScheduleMeeting={onScheduleMeeting}
            extraActions={
              <div
                className={`meet-invite-chip${autoJoinEnabled ? " is-on" : ""}${
                  autoJoinSaving || meetingSettingsReadOnly ? " is-disabled" : ""
                }`}
              >
                <ToggleBar
                  checked={!!autoJoinEnabled}
                  disabled={!!autoJoinSaving || meetingSettingsReadOnly}
                  label={JOIN_MEET_AI_LABEL}
                  title={
                    meetingSettingsReadOnly
                      ? meetingSettingsReadOnlyHint
                      : t("joinMeetAiGlobalTooltip", "This will auto join all your meetings")
                  }
                  onChange={(next) => onToggleAutoJoin?.(next)}
                  switchWidth={28}
                  switchHeight={16}
                  thumbSize={12}
                  labelStyle={{ fontSize: 12, fontWeight: 700, color: "inherit" }}
                />
              </div>
            }
          />
          <UpcomingMeetingsLayout
            weekPanel={
              isAutoJoinWeek ? (
                <UpcomingWeekPanel
                  meetings={autoJoinShown}
                  weekFromYmd={autoJoinWeekFrom}
                  weekToYmd={autoJoinWeekTo}
                  todayYmd={todayDateStr}
                  onOpen={onOpen}
                  formatTimeLabel={(m) => formatMeetingTimeForDisplay(m?.time) || m?.time || ""}
                />
              ) : null
            }
            rail={
              <UpcomingMeetingsRail
                weekCount={autoJoinShown.length}
                attendedCount={autoJoinWeekAttended}
                totalEligible={autoJoinShown.length}
                nextMeetings={autoJoinEligible}
                onOpen={onOpen}
                onInstantJoin={onInstantJoin}
                onJoinUrl={(url) => {
                  if (/^https?:\/\//i.test(url)) window.open(url, "_blank", "noopener,noreferrer");
                  else onInstantJoin?.();
                }}
                formatTimeLabel={(m) => formatMeetingTimeForDisplay(m?.time) || m?.time || ""}
                inviteMeetAiToggle={
                  <div
                    className={`meet-invite-chip${autoJoinEnabled ? " is-on" : ""}${
                      autoJoinSaving || meetingSettingsReadOnly ? " is-disabled" : ""
                    }`}
                  >
                    <ToggleBar
                      checked={!!autoJoinEnabled}
                      disabled={!!autoJoinSaving || meetingSettingsReadOnly}
                      label={JOIN_MEET_AI_LABEL}
                      title={
                        meetingSettingsReadOnly
                          ? meetingSettingsReadOnlyHint
                          : t("joinMeetAiGlobalTooltip", "This will auto join all your meetings")
                      }
                      onChange={(next) => onToggleAutoJoin?.(next)}
                      switchWidth={28}
                      switchHeight={16}
                      thumbSize={12}
                      labelStyle={{ fontSize: 12, fontWeight: 700, color: "inherit" }}
                    />
                  </div>
                }
              />
            }
          >
            <span aria-hidden style={{ display: "none" }} />
          </UpcomingMeetingsLayout>
        </div>
      ) : null}

      <AllMeetingsFrame {...allMeetingsFrameProps}>
      <MeetWellWrapper {...meetWellWrapperProps}>
      <div
        className={`meet-toolbar${shellLayout ? " meet-toolbar--shell" : ""}${
          redesignShell && meetView === "meetings" ? " avaada-all-meetings-toolbar" : ""
        }`}
        data-tour="meet-toolbar"
      >
        {!shellLayout ? (
        <div style={{flex:"1 1 200px",position:"relative",minWidth:0}}>
          <div style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)"}}><Svg d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" color={T.textMuted} size={14}/></div>
          <input className="input" value={q} onChange={e=>setQ(e.target.value)} placeholder={meetView === "transcripts" ? t("searchTranscripts", "Search meetings or hosts...") : t("searchMeetings", "Search meetings...")} style={{paddingLeft:32}}/>
        </div>
        ) : null}
        {dateFilteringEnabled && meetView === "autojoin" ? (
        <div
          ref={autoJoinRangeTabsRef}
          className="seg-group sliding-tabs-host"
          style={{ flexShrink: 0 }}
          role="tablist"
          aria-label="Filter by Today, Week, or Monthly"
        >
          <SlidingTabThumb style={autoJoinRangeThumbStyle} className="sliding-tab-thumb sliding-tab-thumb--blue" />
          {autoJoinRangeFilters.map((f) => (
            <button
              key={f.id}
              ref={registerAutoJoinRangeTab(f.id)}
              type="button"
              role="tab"
              aria-selected={effectiveQuickDateFilter === f.id}
              className={`seg-pill sliding-tab-btn${effectiveQuickDateFilter === f.id ? " active" : ""}`}
              onClick={() => {
                setCalendarPickedYmd(null);
                setQuickDateFilter(f.id);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        ) : meetView === "transcripts" ? null : dateFilteringEnabled ? (
        <div className="date-filter-shell">
          <MeetAiResponsiveDatePicker
            valueYmd={
              effectiveQuickDateFilter === "custom" && activeCalendarPickedYmd
                ? activeCalendarPickedYmd
                : null
            }
            onChangeYmd={(ymd) => {
              setCalendarPickedYmd(ymd);
              setQuickDateFilter("custom");
            }}
            picked={!!calendarPickedYmd}
            iconSize={16}
            title={t("pickDate", "Pick a date")}
          />
          <select
            value={
              effectiveQuickDateFilter === "custom" && activeCalendarPickedYmd
                ? "__cal__"
                : effectiveQuickDateFilter
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__cal__") return;
              setCalendarPickedYmd(null);
              setQuickDateFilter(v);
            }}
            aria-label="Filter meetings by day"
            style={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              border: "none",
              background: "transparent",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 500,
              color: T.text,
              cursor: "pointer",
              paddingLeft: 8,
              paddingRight: 22,
              outline: "none",
              WebkitAppearance: "none",
              appearance: "none",
              MozAppearance: "none",
            }}
          >
            <option value="all">{t("all", "All")}</option>
            <option value="yesterday">{t("yesterday", "Yesterday")}</option>
            <option value="today">{t("today", "Today")}</option>
            <option value="tomorrow">{t("tomorrow", "Tomorrow")}</option>
            {activeCalendarPickedYmd ? (
              <option value="__cal__">{activeCalendarPickedYmd}</option>
            ) : null}
          </select>
        </div>
        ) : (
        <div className="date-filter-shell" style={{opacity:0.7}}>
          <button type="button" className="strip-cal-btn is-disabled" aria-label="Date filter disabled" disabled>
            <Svg d={IC.cal} color="#5F7690" size={16} />
          </button>
          <div style={{flex:1,minWidth:0,height:"100%",display:"flex",alignItems:"center",paddingLeft:8,fontSize:13,fontWeight:500,color:T.textMuted}}>
            {t("all", "All days")}
          </div>
        </div>
        )}
        {meetView === "meetings" ? (
        <div
          ref={statusTabsRef}
          className="seg-group sliding-tabs-host"
          style={{ flexShrink: 0 }}
          role="tablist"
          aria-label="Meeting status"
        >
          <SlidingTabThumb style={statusThumbStyle} className="sliding-tab-thumb sliding-tab-thumb--blue" />
          {filters.map((f)=>(
            <button
              key={f.id}
              ref={registerStatusTab(f.id)}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`seg-pill sliding-tab-btn${filter === f.id ? " active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        ) : null}
        {meetView === "transcripts" ? null : (
        <div
          ref={viewTabsRef}
          className="meet-view-toggle sliding-tabs-host"
          role="tablist"
          aria-label="Switch meetings layout"
          style={meetView === "autojoin" ? undefined : { marginLeft: "auto" }}
        >
          <SlidingTabThumb style={viewThumbStyle} className="sliding-tab-thumb sliding-tab-thumb--view" />
          <button
            type="button"
            ref={registerViewTab("list")}
            role="tab"
            aria-selected={viewMode === "list"}
            className={`meet-view-toggle-btn sliding-tab-btn${viewMode === "list" ? " active" : ""}`}
            onClick={() => setViewMode("list")}
            title={t("list", "List view")}
          >
            <Svg
              d="M4 6h16M4 12h16M4 18h16"
              size={13}
              color="currentColor"
              stroke={2.2}
            />
            {t("list", "List")}
          </button>
          <button
            type="button"
            ref={registerViewTab("grid")}
            role="tab"
            aria-selected={viewMode === "grid"}
            className={`meet-view-toggle-btn sliding-tab-btn${viewMode === "grid" ? " active" : ""}`}
            onClick={() => setViewMode("grid")}
            title={t("grid", "Grid view")}
          >
            <Svg
              d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"
              size={13}
              color="currentColor"
              stroke={2}
            />
            {t("grid", "Grid")}
          </button>
        </div>
        )}
      </div>
      {/* Dev-only diagnostics — hidden unless ?debug=1 is in the URL, so users on
          localhost don't see internal counters during normal browsing. */}
      {typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1" ? (
        <div
          style={{
            fontSize: 11,
            color: T.textMuted,
            marginBottom: 10,
            padding: "4px 8px",
            background: "rgba(12,43,78,0.04)",
            border: "1px dashed rgba(12,43,78,0.15)",
            borderRadius: 6,
            fontFamily: "ui-monospace,monospace",
            display: "inline-block",
          }}
        >
          <strong style={{ color: T.textSub }}>DEBUG</strong> · source={sourceMeetings.length} · shown={visibleRows.length} · list={rowsForList.length} · pending={listViewTransitioning ? "yes" : "no"}
        </div>
      ) : null}
      {calendarSyncing ? (
        <div
          style={{
            fontSize: 12,
            color: T.textSub,
            marginBottom: 12,
            padding: "8px 12px",
            borderRadius: 10,
            background: "rgba(37,99,235,0.06)",
            border: "1px solid rgba(37,99,235,0.12)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid rgba(37,99,235,0.25)",
              borderTopColor: "#2563eb",
              animation: "spin 0.7s linear infinite",
            }}
          />
          <span>{t("loadingGoogleCalendar", "Loading Google Calendar…")}</span>
        </div>
      ) : null}
      {calendarSyncing ? (
        <div
          className="meetings-card-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`cal-skel-${i}`} className="meet-skel" style={{ minHeight: 130, borderRadius: 12 }} />
          ))}
        </div>
      ) : autoJoinGridEmpty || listOrCardEmpty ? (
        <div
          className="meetings-card-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          <MeetingsEmptyState
            hasSourceMeetings={meetings.length > 0}
            hasSearch={!!effectiveQ.trim()}
            onClearSearch={() => {
              if (shellLayout) onExternalSearchChange?.("");
              else setQ("");
            }}
            onWidenFilters={widenFilters}
            selectedDateYmd={effectiveQuickDateFilter === "custom" ? (activeCalendarPickedYmd || null) : null}
            meetView={meetView}
          />
        </div>
      ) : listViewTransitioning ? (
        <div className="meet-page-list-transition" aria-busy="true">
          <MeetingsPageSkeleton meetView={meetView} />
        </div>
      ) : viewMode === "list" ? (
        <MeetingsListView
          rows={rowsForList}
          onOpen={onOpen}
          meetView={meetView}
          meetingAutoJoinSaving={meetingAutoJoinSaving}
          onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
          meetingRecordingSaving={meetingRecordingSaving}
          onToggleMeetingRecording={onToggleMeetingRecording}
          meetingSettingsReadOnly={meetingSettingsReadOnly}
          meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
          transcriptSortOrder={transcriptSortOrder}
          referenceListLayout={redesignShell && meetView === "meetings"}
          listViewTransitioning={listViewTransitioning}
          pagingResetKey={meetingsPagingResetKey}
        />
      ) : meetView === "autojoin" ? (
        <JoinMeetAICalendarGrid
          rows={autoJoinCalendarRows}
          onOpen={onOpen}
          rangeMode={effectiveQuickDateFilter}
          meetingAutoJoinSaving={meetingAutoJoinSaving}
          onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
          meetingRecordingSaving={meetingRecordingSaving}
          onToggleMeetingRecording={onToggleMeetingRecording}
          meetingSettingsReadOnly={meetingSettingsReadOnly}
          meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
        />
      ) : (
        <MeetingsGridView
          rows={rowsForList}
          onOpen={onOpen}
          meetView={meetView}
          meetingAutoJoinSaving={meetingAutoJoinSaving}
          onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
          meetingRecordingSaving={meetingRecordingSaving}
          onToggleMeetingRecording={onToggleMeetingRecording}
          meetingSettingsReadOnly={meetingSettingsReadOnly}
          meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
          transcriptSortOrder={transcriptSortOrder}
          pagingResetKey={meetingsPagingResetKey}
        />
      )}
      </MeetWellWrapper>
      </AllMeetingsFrame>
    </div>
  );
});

/** Ensures only one ToggleBar tip is visible (avoids stacked/overlapping tips in meeting lists). */
const TOGGLE_TIP_HIDE_EVENT = "meetai:hide-toggle-tips";
let toggleTipSeq = 0;

function ToggleBar({
  checked,
  disabled,
  label,
  title,
  onChange,
  stopPropagation = false,
  showLabel = true,
  switchWidth = 28,
  switchHeight = 16,
  thumbSize = 12,
  labelStyle,
  /** Keeps tooltip over the toggle column — avoids overlapping STATUS badges. */
  tipHorizontal = "center",
  /** Prefer below the switch in tight table rows. */
  tipPrefer = "auto",
  /** Compact tips for dense list cells. */
  tipMaxWidth = 240,
  /** "after" (default) = switch then label; "before" = label then switch */
  labelPosition = "after",
}) {
  const pad = 2;
  const travel = Math.max(0, switchWidth - thumbSize - pad * 2);
  const tipText = String(title || "").trim();
  const tipIdRef = useRef(`toggle-bar-tip-${++toggleTipSeq}`);
  const rootRef = useRef(null);
  const switchRef = useRef(null);
  const tipRef = useRef(null);
  const [tipPos, setTipPos] = useState(null);

  const hideTip = useCallback(() => setTipPos(null), []);

  const layoutTip = useCallback(() => {
    if (!tipText || !switchRef.current || !tipRef.current) return;
    const r = switchRef.current.getBoundingClientRect();
    const el = tipRef.current;
    const gap = 8;
    const margin = 12;
    const maxWidth = Math.min(Number(tipMaxWidth) || 240, window.innerWidth - 24);
    const tipW = el.offsetWidth || Math.min(maxWidth, Math.max(120, Math.min(tipText.length * 6.2, maxWidth)));
    const tipH = el.offsetHeight || 40;
    const anchorCenterX = r.left + r.width / 2;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    let place = "below";
    if (tipPrefer === "above") place = "above";
    else if (tipPrefer === "below") place = spaceBelow >= tipH + gap ? "below" : "above";
    else if (spaceBelow < tipH + gap && spaceAbove > spaceBelow) place = "above";

    let left = anchorCenterX - tipW / 2;
    if (tipHorizontal === "start") left = r.left;
    else if (tipHorizontal === "end") left = r.right - tipW;
    left = Math.max(margin, Math.min(window.innerWidth - margin - tipW, left));
    const arrowLeftPx = Math.max(14, Math.min(tipW - 14, anchorCenterX - left));
    const top = place === "below" ? r.bottom + gap : r.top - gap - tipH;
    setTipPos({ left, top, place, maxWidth, arrowLeftPx });
  }, [tipText, tipHorizontal, tipPrefer, tipMaxWidth]);

  const showTip = useCallback(() => {
    if (!tipText || !switchRef.current) return;
    // Close every other ToggleBar tip so only one card shows.
    window.dispatchEvent(
      new CustomEvent(TOGGLE_TIP_HIDE_EVENT, { detail: { exceptId: tipIdRef.current } })
    );
    const r = switchRef.current.getBoundingClientRect();
    const maxWidth = Math.min(Number(tipMaxWidth) || 240, window.innerWidth - 24);
    setTipPos({
      left: r.left + r.width / 2,
      top: r.bottom + 8,
      place: "below",
      maxWidth,
      arrowLeftPx: null,
      measuring: true,
    });
  }, [tipText, tipMaxWidth]);

  useLayoutEffect(() => {
    if (!tipPos?.measuring) return;
    layoutTip();
    requestAnimationFrame(layoutTip);
  }, [tipPos?.measuring, layoutTip]);

  useEffect(() => {
    const onHideOthers = (e) => {
      if (e?.detail?.exceptId === tipIdRef.current) return;
      hideTip();
    };
    window.addEventListener(TOGGLE_TIP_HIDE_EVENT, onHideOthers);
    return () => window.removeEventListener(TOGGLE_TIP_HIDE_EVENT, onHideOthers);
  }, [hideTip]);

  useEffect(() => {
    if (!tipPos) return undefined;
    const hide = () => hideTip();
    window.addEventListener("resize", hide);
    window.addEventListener("scroll", hide, true);
    return () => {
      window.removeEventListener("resize", hide);
      window.removeEventListener("scroll", hide, true);
    };
  }, [tipPos, hideTip]);

  return (
    <>
      <label
        ref={rootRef}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onFocus={showTip}
        onBlur={hideTip}
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 11.5,
          color: T.textSub,
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
        }}
      >
        {labelPosition === "before" && !!label && showLabel ? <span style={labelStyle}>{label}</span> : null}
        <button
          ref={switchRef}
          type="button"
          role="switch"
          aria-checked={!!checked}
          aria-label={tipText || label || "Toggle"}
          aria-describedby={tipPos ? tipIdRef.current : undefined}
          disabled={!!disabled}
          onClick={(e) => {
            e.stopPropagation();
            hideTip();
            if (!disabled) onChange?.(!checked);
          }}
          style={{
            width: switchWidth,
            height: switchHeight,
            borderRadius: 999,
            border: "none",
            padding: pad,
            background: checked ? "#16A765" : "#D3DEE8",
            transition: "background .16s ease",
            position: "relative",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span
            style={{
              display: "block",
              width: thumbSize,
              height: thumbSize,
              borderRadius: "50%",
              background: "#fff",
              transform: checked ? `translateX(${travel}px)` : "translateX(0px)",
              transition: "transform .16s ease",
              boxShadow: "0 1px 2px rgba(0,0,0,.2)",
            }}
          />
        </button>
        {labelPosition === "after" && !!label && showLabel ? <span style={labelStyle}>{label}</span> : null}
      </label>
      {tipPos && tipText && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={tipRef}
              id={tipIdRef.current}
              role="tooltip"
              className={`ui-simple-tip ui-simple-tip--compact ui-simple-tip--${tipPos.place || "below"}`}
              style={{
                position: "fixed",
                left: tipPos.left,
                top: tipPos.top,
                visibility: tipPos.measuring ? "hidden" : "visible",
                zIndex: 10060,
                pointerEvents: "none",
                maxWidth: tipPos.maxWidth || tipMaxWidth || 240,
              }}
            >
              <span
                className="ui-simple-tip__arrow"
                aria-hidden
                style={
                  tipPos?.arrowLeftPx != null
                    ? { left: tipPos.arrowLeftPx, transform: "translateX(-50%)" }
                    : { visibility: "hidden" }
                }
              />
              <span className="ui-simple-tip__text">{tipText}</span>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/* ── meeting card ── */
function MCard({
  m,
  onOpen,
  hasMeetingToggle,
  meetingToggleSaving,
  meetingAutoJoinEnabled,
  onToggleMeetingAutoJoin,
  showRecordingToggle,
  recordingToggleDisabled,
  recordingToggleSaving,
  recordingEnabled,
  onToggleMeetingRecording,
  showAttendanceBreakdown = false,
  showTranscriptActions = false,
  tourToggleAnchor = false,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
}){
  const toggleReadOnlyTitle = meetingSettingsReadOnly ? meetingSettingsReadOnlyHint : "";
  const { lang, t } = useI18n();
  const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
  const status = effectiveMeetingStatus(m);
  const statusKey = m.type==="instant"&&status==="live" ? "live" : status;
  const st=STATUS_META[statusKey]||STATUS_META.upcoming;
  const statusLabel = status === "upcoming" ? t("upcoming", "Upcoming") : status === "live" ? t("live", "Live") : (status === "completed" || status === "ended") ? t("completed", "Completed") : status === "cancelled" ? t("cancelled", "Cancelled") : st.label;
  const cancelled = status === "cancelled";
  const joinUrl = joinUrlFromMeeting(m);
  const canJoin = !cancelled && joinUrl && /^https?:\/\//i.test(joinUrl);
  const joinIconPath =
    m.platform === "teams"
      ? IC.users
      : m.platform === "zoom"
        ? IC.video
        : m.platform === "meet" || m.platform === "gcal"
          ? IC.meet
          : IC.video;
  const displaySchedule = meetingLatestDisplaySchedule(m);
  const rescheduleLine =
    displaySchedule.chainNoteCompact ||
    displaySchedule.chainNote ||
    normalizeRescheduleDisplayLine(m?.rescheduleNote);
  return (
    <div
      className={`card hover-card mcard-pro card-appear${showTranscriptActions ? " mcard-pro--recorded" : ""}`}
      onClick={() => onOpen(m)}
      style={{
        padding: "12px 14px 12px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        opacity: cancelled ? 0.85 : 1,
        minHeight: showTranscriptActions ? 0 : 120,
      }}
    >
      <div className="mcard-pro__head">
        <div className="mcard-pro__title-wrap">
          <span className="mcard-pro__icon" aria-hidden>
            <Svg d={IC.cal} color="#1E40AF" size={13} stroke={2} />
          </span>
          <div
            className={`mcard-meeting-title${cancelled ? " is-cancelled" : ""}`}
            title={m.title}
          >
            {m.title}
          </div>
        </div>
        <div className="mcard-pro__status" title={meetingLiveStatusTooltip(m, status) || undefined}>
          {status === "live" ? (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: T.accent,
                display: "inline-block",
                animation: "pulse 1.4s ease infinite",
              }}
            />
          ) : null}
          <Badge label={statusLabel} bg={st.bg} color={st.fg} dot={st.dot} />
        </div>
      </div>
      <div className="mcard-pro__body">
        <div className="mcard-pro__meta">
          {rescheduleLine ? (
            <RescheduleChip line={rescheduleNoteTitleFromMeeting(m) || rescheduleLine} />
          ) : null}
          <span className="mcard-pro__meta-item">
            <Svg d={IC.cal} size={11} color={T.textMuted} />
            {formatYmdDisplay(m.date)}
          </span>
          <span className="mcard-pro__meta-item">
            <Svg d={IC.clock} size={11} color={displaySchedule.rescheduled ? "#CA8A04" : T.textMuted} />
            <span style={displaySchedule.rescheduled ? { color: "#CA8A04", fontWeight: 700 } : undefined}>
              {formatMeetingTimeForDisplay(displaySchedule.time) || displaySchedule.range}
            </span>
          </span>
          <span className="mcard-pro__meta-item">
            <Svg d={IC.users} size={11} color={T.textMuted} />
            {joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m))}
          </span>
          {m.type === "google_calendar" || m.type === "calendar_auto" ? (
            <span className="mcard-pro__platform">Google</span>
          ) : null}
          {meetingIsInstantJoin(m) ? <InstantJoinLabelChip compact /> : null}
        </div>
        {meetingHasDisplayableHost(m) ? (
          <div className="mcard-pro__host">Host: {meetingHostName(m)}</div>
        ) : null}
      </div>
      {showAttendanceBreakdown ? (
        <JoinedNotJoinedAttendance participants={m.participants || []} meeting={m} compact maxNames={4} />
      ) : null}
      {showTranscriptActions ? (
        <div className="mcard-pro__footer mcard-pro__footer--recorded">
          <MeetingTranscriptActions m={m} layout="card" onOpen={onOpen} />
          {m.emailSent ? (
            <span
              className="mcard-pro__mail"
              title={
                meetingEmailRecipientsCount(m) > 0
                  ? `Recap emailed to ${meetingEmailRecipientsCount(m)} recipient${
                      meetingEmailRecipientsCount(m) === 1 ? "" : "s"
                    }`
                  : getMeetingEmailReasonLong(m)
              }
            >
              <Svg d={IC.mail} color={T.success} size={14} />
            </span>
          ) : null}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginTop: "auto",
            paddingTop: 8,
            borderTop: "0.5px solid var(--color-border-tertiary, #E2E8F0)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 8, minWidth: 0, flex: 1, alignItems: "center" }}>
            {hasMeetingToggle ? (
              <>
                <span
                  className={`meet-toggle-chip${meetingAutoJoinEnabled ? " is-on" : ""}${
                    meetingToggleSaving || meetingSettingsReadOnly ? " is-disabled" : ""
                  }`}
                  {...(tourToggleAnchor ? { "data-tour": "meeting-autojoin-toggle", "data-tour-primary": "" } : {})}
                  style={{ flexShrink: 0 }}
                >
                  <ToggleBar
                    checked={!!meetingAutoJoinEnabled}
                    disabled={!!meetingToggleSaving || meetingSettingsReadOnly}
                    label={JOIN_MEET_AI_LABEL}
                    title={toggleReadOnlyTitle || JOIN_MEET_AI_TOGGLE_HINT}
                    onChange={(next) => onToggleMeetingAutoJoin?.(next)}
                    stopPropagation
                    tipHorizontal="center"
                    tipPrefer="below"
                    tipMaxWidth={220}
                    switchWidth={26}
                    switchHeight={15}
                    thumbSize={11}
                    labelStyle={{ fontSize: 11, fontWeight: 700, color: "inherit" }}
                    labelPosition="before"
                  />
                </span>
                <span
                  className={`meet-toggle-chip${
                    meetingAutoJoinEnabled && recordingEnabled ? " is-on" : ""
                  }${
                    recordingToggleSaving ||
                    recordingToggleDisabled ||
                    !meetingAutoJoinEnabled ||
                    meetingSettingsReadOnly
                      ? " is-disabled"
                      : ""
                  }`}
                  {...(tourToggleAnchor ? { "data-tour": "meeting-video-rec-toggle" } : {})}
                  style={{ flexShrink: 0 }}
                >
                  <ToggleBar
                    checked={!!meetingAutoJoinEnabled && !!recordingEnabled}
                    disabled={
                      !!recordingToggleSaving ||
                      !!recordingToggleDisabled ||
                      !meetingAutoJoinEnabled ||
                      meetingSettingsReadOnly
                    }
                    label="Video Rec"
                    title={
                      toggleReadOnlyTitle ||
                      (!meetingAutoJoinEnabled
                        ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                        : VIDEO_REC_ENABLE_HINT_COMPACT)
                    }
                    onChange={(next) => {
                      if (next && !meetingAutoJoinEnabled) return;
                      onToggleMeetingRecording?.(next);
                    }}
                    stopPropagation
                    tipHorizontal="center"
                    tipPrefer="below"
                    tipMaxWidth={220}
                    switchWidth={26}
                    switchHeight={15}
                    thumbSize={11}
                    labelStyle={{ fontSize: 11, fontWeight: 700, color: "inherit" }}
                    labelPosition="before"
                  />
                </span>
              </>
            ) : showRecordingToggle ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap", maxWidth: "100%" }}>
                <span
                  {...(tourToggleAnchor ? { "data-tour": "meeting-video-rec-toggle", "data-tour-primary": "" } : {})}
                  style={{ display: "inline-flex" }}
                >
                  <ToggleBar
                    checked={!!meetingAutoJoinEnabled && !!recordingEnabled}
                    disabled={!!recordingToggleSaving || !!recordingToggleDisabled || !meetingAutoJoinEnabled}
                    label="Video Rec"
                    title={
                      recordingToggleDisabled || !meetingAutoJoinEnabled
                        ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                        : "Video Rec"
                    }
                    onChange={(next) => {
                      if (next && !meetingAutoJoinEnabled) return;
                      onToggleMeetingRecording?.(next);
                    }}
                    stopPropagation
                    switchWidth={36}
                    switchHeight={20}
                    thumbSize={14}
                    labelStyle={{ fontSize: 11, fontWeight: 400, color: "var(--color-text-secondary, #64748B)" }}
                  />
                </span>
              </div>
            ) : isEndedMeetingStatus(status, m) ? (
              <div style={{ display: "flex", alignItems: "center", maxWidth: "100%" }}>
                <CompletedAutoJoinCell m={m} onOpen={onOpen} layout="card" />
              </div>
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {canJoin ? (
              <button
                type="button"
                className="icon-btn-t"
                title="Join meeting"
                aria-label="Join meeting in new tab"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(joinUrl, "_blank", "noopener,noreferrer");
                }}
              >
                <Svg d={joinIconPath} color={pl.color} size={14} stroke={2} />
              </button>
            ) : null}
            {meetingRecordingFileReady(m, { requireAutoJoin: true }) ? (
              <button
                type="button"
                className="icon-btn-t"
                title="Download recording"
                aria-label="Download recording"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const id = String(m?.id || m?._id || "").trim();
                    if (!id) return;
                    openMeetingRecordingModal(m);
                  } catch (err) {
                    window.alert(err?.message || "Recording download link is not available yet.");
                  }
                }}
              >
                <Svg d={IC.download} color={recordingEnabled ? "#16a34a" : T.textMuted} size={14} stroke={2} />
              </button>
            ) : null}
            {m.emailSent ? (
              <span
                title={
                  meetingEmailRecipientsCount(m) > 0
                    ? `Recap emailed to ${meetingEmailRecipientsCount(m)} recipient${meetingEmailRecipientsCount(m) === 1 ? "" : "s"}`
                    : getMeetingEmailReasonLong(m)
                }
              >
                <Svg d={IC.mail} color={T.success} size={14} />
              </span>
            ) : null}
            <Svg d={IC.chevR} color={T.textMuted} size={14} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── List/Table view ─────────────────────────────
   Mirrors MCard's interaction rules (click to open, auto-join toggle on
   eligible Google Calendar rows, attendance count = joined + not joined) but
   renders rows in a tabular layout grouped by date sections. Section order
   preserves whatever sort the caller passed in, so each tab stays consistent
   with its existing grid order (ascending for Meetings/Auto Join, descending
   for Transcripts). */

/** Local-date day diff between a meeting's start and today (rounded to days). */
function meetingDayDiffFromToday(m) {
  const ms = meetingDateTimeMs(m);
  if (!ms) return null;
  const md = new Date(ms); md.setHours(0,0,0,0);
  const td = new Date();  td.setHours(0,0,0,0);
  return Math.round((md.getTime() - td.getTime()) / 86400000);
}

/** Midnight local time for a timestamp. */
function meetingLocalDayStartMs(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Sunday-start calendar week (matches app calendar widgets). */
function meetingWeekStartMs(ms) {
  const d = new Date(meetingLocalDayStartMs(ms));
  d.setDate(d.getDate() - d.getDay());
  return d.getTime();
}

/** Bucket a meeting by calendar week — not raw day-diff.
 *  e.g. Friday → Sunday is "next_week", not "this_week". */
function meetingSectionKeyForMeeting(m) {
  const ms = meetingDateTimeMs(m);
  if (!ms) return "unscheduled";

  const diff = meetingDayDiffFromToday(m);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";

  const todayMs = meetingLocalDayStartMs(Date.now());
  const meetingDayMs = meetingLocalDayStartMs(ms);
  const todayWeekStart = meetingWeekStartMs(todayMs);
  const meetingWeekStart = meetingWeekStartMs(meetingDayMs);

  if (meetingDayMs > todayMs) {
    if (meetingWeekStart === todayWeekStart) return "this_week";
    if (meetingWeekStart === todayWeekStart + 7 * 86400000) return "next_week";
    return "later";
  }

  if (meetingWeekStart === todayWeekStart) return "earlier_this_week";
  if (meetingWeekStart === todayWeekStart - 7 * 86400000) return "last_week";
  return "older";
}

function meetingSectionLabelBase(key, lang = "English") {
  const t = (k, fb) => getTranslation(lang, k, fb);
  switch (key) {
    case "today": return t("today", "Today").toUpperCase();
    case "tomorrow": return t("tomorrow", "Tomorrow").toUpperCase();
    case "yesterday": return t("yesterday", "Yesterday").toUpperCase();
    case "this_week": return t("thisWeek", "This Week").toUpperCase();
    case "next_week": return t("nextWeek", "Next Week").toUpperCase();
    case "earlier_this_week": return t("earlierThisWeek", "Earlier This Week").toUpperCase();
    case "last_week": return t("lastWeek", "Last Week").toUpperCase();
    case "later": return t("later", "Later").toUpperCase();
    case "older": return t("earlier", "Earlier").toUpperCase();
    default: return t("unscheduled", "Unscheduled").toUpperCase();
  }
}

/** "22 May 2026" — short, locale-friendly. */
function formatSectionDate(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

/** "24–26 May 2026" if same month/year, otherwise full range. */
function formatSectionDateRange(minMs, maxMs) {
  if (!minMs || !maxMs) return "";
  try {
    const a = new Date(minMs);
    const b = new Date(maxMs);
    const sameDay =
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (sameDay) return formatSectionDate(minMs);
    const sameMonthYear =
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
    if (sameMonthYear) {
      const month = a.toLocaleDateString("en-IN", { month: "short" });
      return `${a.getDate()}–${b.getDate()} ${month} ${a.getFullYear()}`;
    }
    const sameYear = a.getFullYear() === b.getFullYear();
    const start = a.toLocaleDateString("en-IN", sameYear
      ? { day: "numeric", month: "short" }
      : { day: "numeric", month: "short", year: "numeric" });
    const end = b.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    return `${start} – ${end}`;
  } catch {
    return "";
  }
}

/** Group an already-sorted list of meetings into sections by date bucket.
 *  Sections are created in the order they're first encountered, so the caller's
 *  sort order (asc/desc) is preserved across the headers too. */
function buildMeetingListSections(rows, lang = "English") {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  const byKey = new Map();
  for (const m of list) {
    const key = meetingSectionKeyForMeeting(m);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = { key, items: [], minMs: null, maxMs: null };
      byKey.set(key, bucket);
      out.push(bucket);
    }
    bucket.items.push(m);
    const ms = meetingDateTimeMs(m);
    if (ms) {
      if (bucket.minMs === null || ms < bucket.minMs) bucket.minMs = ms;
      if (bucket.maxMs === null || ms > bucket.maxMs) bucket.maxMs = ms;
    }
  }
  return out.map((b) => {
    const base = meetingSectionLabelBase(b.key, lang);
    const singleDayKeys = new Set(["today", "tomorrow", "yesterday"]);
    const subtitle = singleDayKeys.has(b.key)
      ? formatSectionDate(b.minMs)
      : formatSectionDateRange(b.minMs, b.maxMs);
    return { ...b, title: base, subtitle };
  });
}

/**
 * Recorded Meetings section order — deterministic so Today / Yesterday / Earlier This Week /
 * Last Week / Older always read newest-first (or oldest-first when toggled),
 * regardless of encounter order in the grouped rows.
 */
function orderTranscriptSections(sections, order = "desc") {
  const rank = (key) => {
    switch (key) {
      case "tomorrow": return -3;
      case "today": return -2;
      case "yesterday": return -1;
      case "this_week":
      case "earlier_this_week": return 0;
      case "last_week": return 1;
      case "next_week":
      case "later": return -4;
      case "older": return 2;
      default: return 3;
    }
  };
  const arr = Array.isArray(sections) ? [...sections] : [];
  arr.sort((a, b) => {
    // Meetings without a usable date always stay last, in both orders.
    if (a?.key === "unscheduled" && b?.key !== "unscheduled") return 1;
    if (b?.key === "unscheduled" && a?.key !== "unscheduled") return -1;
    const d = rank(a?.key) - rank(b?.key);
    if (d !== 0) return order === "asc" ? -d : d;
    const ma = a?.maxMs ?? a?.minMs ?? 0;
    const mb = b?.maxMs ?? b?.minMs ?? 0;
    return order === "asc" ? ma - mb : mb - ma;
  });
  return arr;
}

function useMeetAiListPaging(count, resetKey) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  useEffect(() => {
    setPage(0);
  }, [resetKey]);
  const size = Math.max(1, Number(rowsPerPage) || 5);
  const total = Math.max(0, Number(count) || 0);
  const maxPage = Math.max(0, Math.ceil(total / size) - 1);
  const safePage = Math.min(Math.max(0, Number(page) || 0), maxPage);
  const start = safePage * size;
  return {
    page: safePage,
    rowsPerPage: size,
    setPage,
    setRowsPerPage,
    pagedItems: (arr) => (Array.isArray(arr) ? arr.slice(start, start + size) : []),
  };
}

function JoinMeetAICalendarGrid({
  rows,
  onOpen,
  rangeMode = "week",
  meetingAutoJoinSaving,
  onToggleMeetingAutoJoin,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
}) {
  const toggleReadOnlyTitle = meetingSettingsReadOnly ? meetingSettingsReadOnlyHint : "";
  const { lang, t } = useI18n();
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const mode = rangeMode === "today" || rangeMode === "monthly" ? rangeMode : "week";
  const today = todayStr();
  const [anchorYmd, setAnchorYmd] = useState(today);
  const [dayPopupYmd, setDayPopupYmd] = useState(null);
  const nowMs = Date.now();

  useEffect(() => {
    setAnchorYmd(today);
  }, [mode, today]);

  const anchorDt = ymdToDate(anchorYmd) || new Date();
  const year = anchorDt.getFullYear();
  const month = anchorDt.getMonth();

  const weekStart = startOfWeekYmd(anchorYmd || today);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysYmd(weekStart, i)),
    [weekStart]
  );

  const monthCells = useMemo(
    () =>
      buildMonthCalendarCells(year, month).map((c) => ({
        ...c,
        ymd: ymdForMonthCell(c, year, month),
      })),
    [year, month]
  );

  const mByDate = useMemo(() => {
    const map = {};
    for (const m of rows || []) {
      const ds = String(m?.date || "").trim().slice(0, 10);
      if (!ds) continue;
      if (!map[ds]) map[ds] = [];
      map[ds].push(m);
    }
    for (const ds of Object.keys(map)) {
      map[ds] = sortMeetingsChronologically(map[ds]);
    }
    return map;
  }, [rows]);

  const todayMeetings = useMemo(
    () => sortMeetingsChronologically(mByDate[today] || []),
    [mByDate, today]
  );
  const todayPaging = useMeetAiListPaging(
    todayMeetings.length,
    `today:${todayMeetings.length}:${meetingListReactKey(todayMeetings[0] || {})}`
  );
  const pagedToday = todayPaging.pagedItems(todayMeetings);

  const shiftRange = (delta) => {
    if (mode === "week") {
      setAnchorYmd(addDaysYmd(anchorYmd || today, delta * 7));
      return;
    }
    if (mode === "monthly") {
      const dt = ymdToDate(anchorYmd || today) || new Date();
      dt.setMonth(dt.getMonth() + delta);
      setAnchorYmd(dateToYmd(dt));
    }
  };

  const fmtPopupDate = (ymd) => {
    const dt = ymdToDate(ymd);
    if (!dt) return ymd;
    return dt.toLocaleDateString("en-IN", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: APP_DISPLAY_TZ,
    });
  };

  const headerTitle =
    mode === "today"
      ? fmtPopupDate(today)
      : mode === "week"
        ? `${formatYmdDisplay(weekStart)} – ${formatYmdDisplay(endOfWeekYmd(anchorYmd || today))}`
        : `${MONTHS[month]} ${year}`;

  const openDayPopup = (ymd) => {
    if (ymd) setDayPopupYmd(ymd);
  };

  const renderChip = (m, mi, { compact = false, dayYmd = null, showToggle = false } = {}) => {
    const cancelled = effectiveMeetingStatus(m, nowMs) === "cancelled";
    const status = effectiveMeetingStatus(m, nowMs);
    const isDone = status === "completed" || status === "ended";
    const googleEventId = String(m?.googleEventId || "").trim();
    const canToggle =
      showToggle &&
      (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
      !!googleEventId &&
      (status === "upcoming" || status === "live");
    const meetingToggleSaving = !!meetingAutoJoinSaving?.[googleEventId];
    const meetingAutoJoinEnabled = m?.autoJoinEnabled === true;
    const recApiId = meetingRecordingApiId(m);
    const recordingToggleSaving = !!meetingRecordingSaving?.[recApiId];
    const recordingEnabled = effectiveRecordingEnabled(m);
    // Avaada status colors on the main Invite MeetAI cards (not right sidebar).
    const evtColor = eventColorForMeeting(m);
    const accent = cancelled
      ? "#94A3B8"
      : isDone
        ? "#6B7280"
      : status === "live"
        ? PALETTE.brandGreen
        : meetingAutoJoinEnabled
          ? PALETTE.brandBlue
          : evtColor.border;
    const chipBg = cancelled ? "#F8FAFC" : evtColor.bg;
    const titleColor = cancelled ? T.textMuted : evtColor.text;
    const timeColor = cancelled ? T.textMuted : accent;
    const popupYmd = dayYmd || String(m?.date || "").trim().slice(0, 10) || today;

    const openChip = () => {
      if (showToggle) {
        onOpen?.(m, { source: "autojoin" });
      } else {
        openDayPopup(popupYmd);
      }
    };

    return (
      <div
        key={meetingListReactKey(m, mi)}
        role="button"
        tabIndex={0}
        title={m.title}
        onClick={(e) => {
          e.stopPropagation();
          openChip();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            openChip();
          }
        }}
        style={{
          fontSize: compact ? 12 : 10.5,
          fontWeight: 600,
          padding: compact ? "10px 12px" : "5px 7px",
          borderRadius: compact ? 10 : 6,
          marginBottom: compact ? 8 : 4,
          background: chipBg,
          border: `1px solid ${cancelled ? T.border : `${accent}33`}`,
          borderLeft: `3px solid ${accent}`,
          color: titleColor,
          lineHeight: 1.35,
          boxShadow: "none",
          cursor: "pointer",
          minWidth: 0,
          display: canToggle || (compact && isDone) ? "flex" : "block",
          alignItems: canToggle || (compact && isDone) ? "center" : undefined,
          justifyContent: canToggle || (compact && isDone) ? "space-between" : undefined,
          gap: canToggle || (compact && isDone) ? 10 : undefined,
        }}
      >
        <div
          style={{
            minWidth: 0,
            flex: canToggle || (compact && isDone) ? 1 : undefined,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: cancelled ? "line-through" : "none",
          }}
        >
          <span style={{ color: timeColor, fontWeight: 700, fontSize: compact ? 11 : 9.5, marginRight: 5 }}>
            {formatMeetingTimeForDisplay(m.time)}
          </span>
          {m.title}
        </div>
{canToggle ? (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0, display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6 }}
            >
              <ToggleBar
                checked={meetingAutoJoinEnabled}
                disabled={meetingToggleSaving || meetingSettingsReadOnly}
                label={JOIN_MEET_AI_LABEL}
                title={toggleReadOnlyTitle || JOIN_MEET_AI_TOGGLE_HINT}
                onChange={(next) => onToggleMeetingAutoJoin?.(googleEventId, next, m)}
                stopPropagation
                tipHorizontal="center"
                tipPrefer="below"
                tipMaxWidth={220}
                switchWidth={compact ? 32 : 30}
                switchHeight={compact ? 18 : 16}
                thumbSize={compact ? 12 : 11}
                labelStyle={{
                  fontSize: compact ? 10.5 : 10,
                  fontWeight: 600,
                  color: T.textMuted,
                  maxWidth: compact ? 88 : 80,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                labelPosition="before"
              />
              <ToggleBar
                checked={recordingEnabled}
                disabled={recordingToggleSaving || !meetingAutoJoinEnabled || meetingSettingsReadOnly}
                label="Video Rec"
                title={
                  toggleReadOnlyTitle ||
                  (!meetingAutoJoinEnabled
                    ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                    : VIDEO_REC_ENABLE_HINT_COMPACT)
                }
                onChange={(next) => {
                  if (next && !meetingAutoJoinEnabled) return;
                  onToggleMeetingRecording?.(m, next);
                }}
                stopPropagation
                tipHorizontal="center"
                tipPrefer="below"
                tipMaxWidth={220}
                switchWidth={compact ? 32 : 30}
                switchHeight={compact ? 18 : 16}
                thumbSize={compact ? 12 : 11}
                labelStyle={{
                  fontSize: compact ? 10 : 9.5,
                  fontWeight: 600,
                  color: T.textMuted,
                  maxWidth: compact ? 72 : 68,
                }}
                labelPosition="before"
              />
            </div>
        ) : compact && isDone ? (
          <span style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 650,
                color: "#6B7280",
                padding: "2px 8px",
                borderRadius: 999,
                background: "#F3F4F6",
              }}
            >
              {t("done", "Completed")}
            </span>
            <span style={{ fontSize: 10, fontWeight: 650, color: meetingRecordingStoredOn(m) ? "#047857" : "#64748b" }}>
              {meetingRecordingStoredOn(m) ? "Video Rec on" : "Video Rec off"}
            </span>
          </span>
        ) : null}
      </div>
    );
  };

  if (mode === "today") {
    let tourToggleAnchorAssigned = false;
    return (
      <div className="join-meet-ai-cal-wrap meet-grid-wrap" style={{ marginTop: 4 }}>
        <div className="meet-grid-section">
          <div className="meet-grid-section-title">
            <span className="meet-grid-section-label">{headerTitle}</span>
            <span className="meet-grid-section-count">{todayMeetings.length}</span>
          </div>
          {todayMeetings.length === 0 ? (
            <div
              style={{
                padding: "28px 16px",
                textAlign: "center",
                color: T.textMuted,
                fontSize: 13,
                background: T.bg,
                border: `1px solid ${T.border}`,
                borderRadius: 12,
              }}
            >
              {t("noMeetingsToday", "No meetings today")}
            </div>
          ) : (
            <div className="meetings-card-grid">
              {pagedToday.map((m) => {
                const googleEventId = String(m?.googleEventId || "").trim();
                const cardStatus = effectiveMeetingStatus(m, nowMs);
                const isEligibleForToggle =
                  (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
                  !!googleEventId &&
                  (cardStatus === "upcoming" || cardStatus === "live");
                const meetingToggleSaving = !!meetingAutoJoinSaving?.[googleEventId];
                const meetingAutoJoinEnabled = m?.autoJoinEnabled === true;
                const tourToggleAnchor = isEligibleForToggle && !tourToggleAnchorAssigned;
                if (tourToggleAnchor) tourToggleAnchorAssigned = true;
                const recApiId = meetingRecordingApiId(m);
                return (
                  <MCard
                    key={meetingListReactKey(m)}
                    m={m}
                    onOpen={(meeting) => onOpen?.(meeting, { source: "autojoin" })}
                    hasMeetingToggle={isEligibleForToggle}
                    meetingToggleSaving={meetingToggleSaving}
                    meetingAutoJoinEnabled={meetingAutoJoinEnabled}
                    onToggleMeetingAutoJoin={(enabled) =>
                      onToggleMeetingAutoJoin?.(googleEventId, enabled, m)
                    }
                    showRecordingToggle={isEligibleForToggle}
                    recordingToggleDisabled={!meetingAutoJoinEnabled}
                    recordingToggleSaving={!!meetingRecordingSaving?.[recApiId]}
                    recordingEnabled={effectiveRecordingEnabled(m)}
                    onToggleMeetingRecording={(enabled) => onToggleMeetingRecording?.(m, enabled)}
                    tourToggleAnchor={tourToggleAnchor}
                    meetingSettingsReadOnly={meetingSettingsReadOnly}
                    meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
                  />
                );
              })}
            </div>
          )}
          {todayMeetings.length > 0 ? (
            <MeetAiTablePagination
              count={todayMeetings.length}
              page={todayPaging.page}
              rowsPerPage={todayPaging.rowsPerPage}
              onPageChange={(_, p) => todayPaging.setPage(p)}
              onRowsPerPageChange={(e) => {
                todayPaging.setRowsPerPage(parseInt(e.target.value, 10) || 5);
                todayPaging.setPage(0);
              }}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="join-meet-ai-cal-wrap" style={{ marginTop: 4 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="cal-nav-pair" style={{ display: "flex" }}>
            <button
              type="button"
              className="cal-nav-icon cal-nav-split-l"
              aria-label={mode === "week" ? "Previous week" : "Previous month"}
              title={mode === "week" ? "Previous week" : "Previous month"}
              onClick={() => shiftRange(-1)}
            >
              <Svg d={IC.chevL} size={20} color="currentColor" stroke={2.25} />
            </button>
            <button
              type="button"
              className="cal-nav-icon cal-nav-split-r"
              aria-label={mode === "week" ? "Next week" : "Next month"}
              title={mode === "week" ? "Next week" : "Next month"}
              onClick={() => shiftRange(1)}
            >
              <Svg d={IC.chevR} size={20} color="currentColor" stroke={2.25} />
            </button>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: "-0.02em" }}>
            {headerTitle}
          </div>
        </div>
        <button
          type="button"
          className="btn-outline"
          onClick={() => setAnchorYmd(today)}
          style={{ padding: "6px 12px", fontSize: 12 }}
        >
          Today
        </button>
      </div>

      <div
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        }}
      >
        {mode === "week" ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {weekDays.map((ymd, i) => {
              const dt = ymdToDate(ymd);
              const dayMeets = mByDate[ymd] || [];
              const isToday = ymd === today;
              const isWknd = i === 0 || i === 6;
              const weekday = dt
                ? dt.toLocaleDateString("en-IN", { weekday: "short", timeZone: APP_DISPLAY_TZ })
                : DAYS[i];
              const dayNum = dt ? dt.getDate() : Number(String(ymd).slice(8, 10));
              const monthShort = dt
                ? dt.toLocaleDateString("en-IN", { month: "short", timeZone: APP_DISPLAY_TZ })
                : "";
              return (
                <div
                  key={ymd}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "112px minmax(0,1fr)",
                    borderBottom: i < 6 ? `1px solid ${T.border}` : "none",
                    background: isToday ? "#E8F4FD" : isWknd ? "#FAFAFA" : T.bg,
                    boxShadow: isToday ? "inset 3px 0 0 #1565C0" : "none",
                    minHeight: dayMeets.length ? undefined : 64,
                  }}
                >
                  <div
                    style={{
                      padding: "14px 12px",
                      borderRight: `1px solid ${T.border}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 4,
                      background: isToday ? "rgba(21,101,192,0.06)" : T.surface,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: isWknd ? "#C0C4D0" : T.textMuted,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                      }}
                    >
                      {weekday}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          fontWeight: isToday ? 700 : 600,
                          background: isToday ? T.accent : "transparent",
                          color: isToday ? "#fff" : T.text,
                        }}
                      >
                        {dayNum}
                      </span>
                      <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>{monthShort}</span>
                    </div>
                    {dayMeets.length > 0 ? (
                      <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>
                        {dayMeets.length} meeting{dayMeets.length === 1 ? "" : "s"}
                      </div>
                    ) : null}
                  </div>
                  <div
                    style={{
                      padding: "10px 12px",
                      minWidth: 0,
                    }}
                  >
                    {dayMeets.length === 0 ? (
                      <div style={{ fontSize: 12, color: T.textMuted, padding: "10px 2px" }}>{t("noMeetings", "No meetings")}</div>
                    ) : (
                      dayMeets.map((m, mi) => renderChip(m, mi, { compact: true, dayYmd: ymd, showToggle: true }))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Seven day-columns squeeze to ~50px on a phone, which is unreadable.
          // Both grids share one scroll container so the headers stay aligned
          // with the cells as it pans.
          <div className="mobile-scroll-x">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,minmax(0,1fr))",
                background: T.surface,
                borderBottom: `1px solid ${T.border}`,
              }}
            >
              {DAYS.map((d, i) => (
                <div
                  key={d}
                  style={{
                    textAlign: "center",
                    padding: "10px 0",
                    minWidth: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    color: i === 0 || i === 6 ? "#C0C4D0" : T.textMuted,
                    letterSpacing: 0.7,
                    textTransform: "uppercase",
                    borderRight: i < 6 ? `1px solid ${T.border}` : "none",
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))" }}>
              {monthCells.map((c, i) => {
                const ds = c.ymd;
                const dayMeets = ds ? mByDate[ds] || [] : [];
                const isToday = ds === today;
                const isWknd = i % 7 === 0 || i % 7 === 6;
                const col = i % 7;
                const previewCap = CAL_MONTH_DAY_EVENT_PREVIEW;
                const hiddenCount = Math.max(0, dayMeets.length - previewCap);
                const dayNum = ds ? Number(String(ds).slice(8, 10)) : c.day;
                const cellBg =
                  c.cur === false ? "#FAFBFC" : isToday ? "#E8F4FD" : isWknd ? "#FAFAFA" : T.bg;
                return (
                  <div
                    key={`${ds || i}-${c.slot || "m"}`}
                    onClick={() => ds && dayMeets.length > 0 && openDayPopup(ds)}
                    style={{
                      minHeight: 118,
                      minWidth: 0,
                      overflow: "hidden",
                      borderRight: col < 6 ? `1px solid ${T.border}` : "none",
                      borderBottom: i < 35 ? `1px solid ${T.border}` : "none",
                      background: cellBg,
                      boxShadow: isToday ? "inset 0 0 0 1px rgba(21,101,192,0.2)" : "none",
                      cursor: c.cur !== false && dayMeets.length ? "pointer" : "default",
                      padding: "6px 5px 4px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 4 }}>
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12.5,
                          fontWeight: isToday ? 700 : 400,
                          background: isToday ? T.accent : "transparent",
                          color: isToday ? "#fff" : c.cur === false ? T.textMuted : T.text,
                        }}
                      >
                        {dayNum}
                      </span>
                    </div>
                    {dayMeets.slice(0, previewCap).map((m, mi) => renderChip(m, mi, { dayYmd: ds, showToggle: false }))}
                    {hiddenCount > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDayPopup(ds);
                        }}
                        style={{
                          border: "none",
                          background: "none",
                          padding: "2px 4px",
                          fontSize: 10,
                          color: T.accent,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        +{hiddenCount} more
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <CalDayMeetingsPopup
        open={!!dayPopupYmd}
        dateYmd={dayPopupYmd}
        meetings={dayPopupYmd ? mByDate[dayPopupYmd] || [] : []}
        onClose={() => setDayPopupYmd(null)}
        onOpen={(meeting) => {
          setDayPopupYmd(null);
          onOpen?.(meeting, { source: "autojoin" });
        }}
        nowMs={nowMs}
        onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
        meetingAutoJoinSaving={meetingAutoJoinSaving}
        fmtDate={fmtPopupDate}
      />
    </div>
  );
}

function MeetingsGridSection({
  sec,
  pagingResetKey,
  meetView,
  onOpen,
  meetingAutoJoinSaving,
  onToggleMeetingAutoJoin,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  meetingSettingsReadOnly,
  meetingSettingsReadOnlyHint,
  claimTourToggle,
}) {
  const paging = useMeetAiListPaging(
    (sec.items || []).length,
    `${pagingResetKey}:${sec.key}`
  );
  const pageItems = paging.pagedItems(sec.items);
  return (
    <div className="meet-grid-section">
      <div className="meet-grid-section-title">
        <span className="meet-grid-section-label">{sec.title}</span>
        {sec.subtitle ? <span className="meet-grid-section-date">— {sec.subtitle}</span> : null}
        <span className="meet-grid-section-count meet-grid-section-count--inline">
          {sec.items.length} {sec.items.length === 1 ? "meeting" : "meetings"}
        </span>
      </div>
      <div className="meetings-card-grid">
        {pageItems.map((m) => {
          const googleEventId = String(m?.googleEventId || "").trim();
          const meetingId = meetingRecordingApiId(m);
          const cardStatus = effectiveMeetingStatus(m);
          const isEligibleForToggle =
            (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
            !!googleEventId &&
            (cardStatus === "upcoming" || cardStatus === "live") &&
            meetView !== "transcripts";
          const meetingToggleSaving = !!meetingAutoJoinSaving?.[googleEventId];
          const meetingAutoJoinEnabled = m?.autoJoinEnabled === true;
          const recordingToggleSaving = !!meetingRecordingSaving?.[meetingId];
          const recordingEnabled = effectiveRecordingEnabled(m);
          const recordingToggleDisabled = !meetingAutoJoinEnabled;
          const tourToggleAnchor = isEligibleForToggle && claimTourToggle();
          return (
            <MCard
              key={meetingListReactKey(m)}
              m={m}
              onOpen={onOpen}
              hasMeetingToggle={isEligibleForToggle}
              meetingToggleSaving={meetingToggleSaving}
              meetingAutoJoinEnabled={meetingAutoJoinEnabled}
              onToggleMeetingAutoJoin={(enabled) =>
                onToggleMeetingAutoJoin?.(googleEventId, enabled, m)
              }
              showRecordingToggle={isEligibleForToggle}
              recordingToggleDisabled={recordingToggleDisabled}
              recordingToggleSaving={recordingToggleSaving}
              recordingEnabled={recordingEnabled}
              onToggleMeetingRecording={(enabled) => onToggleMeetingRecording?.(m, enabled)}
              showTranscriptActions={meetView === "transcripts"}
              tourToggleAnchor={tourToggleAnchor}
              meetingSettingsReadOnly={meetingSettingsReadOnly}
              meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
            />
          );
        })}
      </div>
      <MeetAiTablePagination
        count={(sec.items || []).length}
        page={paging.page}
        rowsPerPage={paging.rowsPerPage}
        onPageChange={(_, p) => paging.setPage(p)}
        onRowsPerPageChange={(e) => {
          paging.setRowsPerPage(parseInt(e.target.value, 10) || 5);
          paging.setPage(0);
        }}
      />
    </div>
  );
}

function MeetingsGridView({
  rows,
  onOpen,
  meetView,
  meetingAutoJoinSaving,
  onToggleMeetingAutoJoin,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
  transcriptSortOrder = "desc",
  pagingResetKey = "",
}) {
  const { lang } = useI18n();
  const sections = useMemo(() => {
    const built = buildMeetingListSections(rows, lang);
    return meetView === "transcripts" ? orderTranscriptSections(built, transcriptSortOrder) : built;
  }, [rows, lang, meetView, transcriptSortOrder]);
  if ((rows || []).length === 0) return null;
  let tourToggleAnchorAssigned = false;
  const claimTourToggle = () => {
    if (tourToggleAnchorAssigned) return false;
    tourToggleAnchorAssigned = true;
    return true;
  };
  return (
    <div className="meet-grid-wrap">
      {sections.map((sec) => (
        <MeetingsGridSection
          key={sec.key}
          sec={sec}
          pagingResetKey={pagingResetKey || meetView}
          meetView={meetView}
          onOpen={onOpen}
          meetingAutoJoinSaving={meetingAutoJoinSaving}
          onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
          meetingRecordingSaving={meetingRecordingSaving}
          onToggleMeetingRecording={onToggleMeetingRecording}
          meetingSettingsReadOnly={meetingSettingsReadOnly}
          meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
          claimTourToggle={claimTourToggle}
        />
      ))}
    </div>
  );
}

function MeetingsListSection({
  sec,
  pagingResetKey,
  meetView,
  onOpen,
  meetingAutoJoinSaving,
  onToggleMeetingAutoJoin,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  referenceListLayout,
  meetingSettingsReadOnly,
  meetingSettingsReadOnlyHint,
  claimTourToggle,
  t,
}) {
  const paging = useMeetAiListPaging(
    (sec.items || []).length,
    `${pagingResetKey}:${sec.key}`
  );
  const pageItems = paging.pagedItems(sec.items);
  return (
    <div className="meet-list-section" data-section={sec.key}>
      <div className="meet-list-section-title">
        <span className="meet-grid-section-label">{sec.title}</span>
        {sec.subtitle ? <span className="meet-grid-section-date">— {sec.subtitle}</span> : null}
        <span className="meet-grid-section-count meet-grid-section-count--inline">
          {sec.items.length} {sec.items.length === 1 ? "meeting" : "meetings"}
        </span>
      </div>
      <div className="meet-list-table-scroll">
      <div
        className={`meet-list-table${
          meetView === "transcripts"
            ? " meet-list-table--transcripts"
            : referenceListLayout
              ? " meet-list-table--reference"
              : " meet-list-table--toggles"
        }`}
        role="table"
        aria-label={`${sec.title} meetings`}
      >
        <div className="meet-list-row meet-list-head" role="row">
          <div className="meet-list-cell" role="columnheader">{t("colMeeting", "MEETING")}</div>
          {meetView === "transcripts" ? (
            <div className="meet-list-cell col-datetime" role="columnheader">{t("colDateTime", "DATE & TIME")}</div>
          ) : (
            <div className="meet-list-cell" role="columnheader">{t("colTime", "TIME")}</div>
          )}
          <div className="meet-list-cell" role="columnheader">{t("colHost", "HOST")}</div>
          <div className="meet-list-cell col-participants-head" role="columnheader">{t("colParticipants", "PARTICIPANTS")}</div>
          {meetView === "transcripts" ? null : (
            <div className="meet-list-cell" role="columnheader">{t("colStatus", "STATUS")}</div>
          )}
          {meetView === "transcripts" ? (
            <div className="meet-list-cell col-summary-head" role="columnheader">
              {t("meetingSummary", "Meeting Summary")}
            </div>
          ) : referenceListLayout ? (
            <div className="meet-list-cell col-actions-head" role="columnheader">
              {t("colActions", "ACTIONS")}
            </div>
          ) : (
            <>
              <div className="meet-list-cell col-autojoin-toggle" role="columnheader">
                {t("colAutoJoin", JOIN_MEET_AI_LABEL)}
              </div>
              <div className="meet-list-cell col-video-rec-toggle" role="columnheader">
                {t("colVideoRec", "Video Rec")}
              </div>
            </>
          )}
        </div>
        {pageItems.map((m) => {
          const googleEventId = String(m?.googleEventId || "").trim();
          const status = effectiveMeetingStatus(m);
          const isEligibleForToggle =
            (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
            !!googleEventId &&
            (status === "upcoming" || status === "live") &&
            meetView !== "transcripts";
          const tourToggleAnchor = isEligibleForToggle && claimTourToggle();
          const recApiId = meetingRecordingApiId(m);
          return (
            <MeetingsListRow
              key={meetingListReactKey(m)}
              m={m}
              sectionKey={sec.key}
              onOpen={onOpen}
              showAutoJoinToggle={isEligibleForToggle}
              meetingAutoJoinEnabled={m?.autoJoinEnabled === true}
              meetingToggleSaving={!!meetingAutoJoinSaving?.[googleEventId]}
              onToggleMeetingAutoJoin={(enabled) =>
                onToggleMeetingAutoJoin?.(googleEventId, enabled, m)
              }
              showRecordingToggle={isEligibleForToggle}
              recordingToggleDisabled={!m?.autoJoinEnabled}
              meetingRecordingEnabled={effectiveRecordingEnabled(m)}
              meetingRecordingSaving={!!meetingRecordingSaving?.[recApiId]}
              onToggleMeetingRecording={(enabled) => onToggleMeetingRecording?.(m, enabled)}
              showTranscriptActions={meetView === "transcripts"}
              tourToggleAnchor={tourToggleAnchor}
              referenceListLayout={referenceListLayout}
              meetingSettingsReadOnly={meetingSettingsReadOnly}
              meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
            />
          );
        })}
      </div>
      </div>
      <MeetAiTablePagination
        count={(sec.items || []).length}
        page={paging.page}
        rowsPerPage={paging.rowsPerPage}
        onPageChange={(_, p) => paging.setPage(p)}
        onRowsPerPageChange={(e) => {
          paging.setRowsPerPage(parseInt(e.target.value, 10) || 5);
          paging.setPage(0);
        }}
      />
    </div>
  );
}

const MeetingsListView = memo(function MeetingsListView({
  rows,
  onOpen,
  meetView,
  meetingAutoJoinSaving,
  onToggleMeetingAutoJoin,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  transcriptSortOrder = "desc",
  referenceListLayout = false,
  listViewTransitioning = false,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
  rescheduleSources,
  pagingResetKey = "",
}) {
  const { lang, t } = useI18n();
  const sections = useMemo(
    () =>
      meetView === "transcripts"
        ? orderTranscriptSections(buildMeetingListSections(rows, lang), transcriptSortOrder)
        : buildMeetingListSections(rows, lang),
    [rows, lang, meetView, transcriptSortOrder]
  );
  if ((rows || []).length === 0) return null;
  let tourToggleAnchorAssigned = false;
  const claimTourToggle = () => {
    if (tourToggleAnchorAssigned) return false;
    tourToggleAnchorAssigned = true;
    return true;
  };
  return (
    <div className={`meet-list-wrap${listViewTransitioning ? " is-list-view-pending" : ""}`} aria-busy={listViewTransitioning || undefined}>
      {sections.map((sec) => (
        <MeetingsListSection
          key={sec.key}
          sec={sec}
          pagingResetKey={pagingResetKey || meetView}
          meetView={meetView}
          onOpen={onOpen}
          meetingAutoJoinSaving={meetingAutoJoinSaving}
          onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
          meetingRecordingSaving={meetingRecordingSaving}
          onToggleMeetingRecording={onToggleMeetingRecording}
          referenceListLayout={referenceListLayout}
          meetingSettingsReadOnly={meetingSettingsReadOnly}
          meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
          claimTourToggle={claimTourToggle}
          t={t}
        />
      ))}
    </div>
  );
});

/** Transcripts list: meetings at/above this size get a "Large" chip + heavier count. */
const LARGE_MEETING_PARTICIPANTS = 50;

const MeetingsListRow = memo(function MeetingsListRow({
  m,
  sectionKey,
  onOpen,
  rescheduleSources,
  showAutoJoinToggle,
  meetingAutoJoinEnabled,
  meetingToggleSaving,
  onToggleMeetingAutoJoin,
  showRecordingToggle,
  recordingToggleDisabled,
  meetingRecordingEnabled,
  meetingRecordingSaving,
  onToggleMeetingRecording,
  showTranscriptActions = false,
  tourToggleAnchor = false,
  meetingSettingsReadOnly = false,
  meetingSettingsReadOnlyHint = "",
  referenceListLayout = false,
}) {
  const { lang, t } = useI18n();
  const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
  const status = effectiveMeetingStatus(m);
  const toggleReadOnlyTitle = meetingSettingsReadOnly ? meetingSettingsReadOnlyHint : "";
  const statusKey = m.type === "instant" && status === "live" ? "live" : status;
  const st = STATUS_META[statusKey] || STATUS_META.upcoming;
  const statusLabel = status === "upcoming" ? t("upcoming", "Upcoming") : status === "live" ? t("live", "Live") : (status === "completed" || status === "ended") ? t("completed", "Completed") : status === "cancelled" ? t("cancelled", "Cancelled") : st.label;
  const cancelled = status === "cancelled";
  const tourAutoJoinAttrs = tourToggleAnchor
    ? { "data-tour": "meeting-autojoin-toggle", "data-tour-primary": "" }
    : {};
  // For multi-day sections, prefix the per-row subtitle with the meeting's
  // own date so the table still reads correctly when one section spans days.
  // Transcripts have a dedicated DATE column, so the subtitle is just the
  // platform tag in muted grey.
  const inSingleDay = sectionKey === "today" || sectionKey === "tomorrow" || sectionKey === "yesterday";
  const dayPrefix = inSingleDay ? "" : formatYmdDisplay(m.date);
  const referencePlatformSubtitle =
    m.type === "instant"
      ? t("instantJoin", "Instant join")
      : m.platform === "gcal"
        ? t("googleCalendar", "Google Calendar")
        : m.platform === "teams"
          ? t("microsoftTeams", "Microsoft Teams")
          : m.platform === "zoom"
            ? "Zoom"
            : t("googleMeet", "Google Meet");
  const subtitleParts = [];
  if (!showTranscriptActions && !referenceListLayout && dayPrefix) subtitleParts.push(dayPrefix);
  subtitleParts.push(referenceListLayout ? referencePlatformSubtitle : pl.label);
  const subtitle = subtitleParts.join(" · ");
  const participantCount = joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m));
  const isLargeMeeting = participantCount >= LARGE_MEETING_PARTICIPANTS;
  const rowDateMs = meetingDateTimeMs(m);
  const dateLabel = formatYmdDisplay(m.date) || (rowDateMs ? formatYmdDisplay(dateToYmd(new Date(rowDateMs))) : "");
  const hostFullName = meetingHostName(m);
  const displaySchedule = meetingLatestDisplaySchedule(m, null);
  const rescheduleLine =
    displaySchedule.chainNoteCompact ||
    displaySchedule.chainNote ||
    String(m?.rescheduleNote || "").trim();
  const rescheduleTitle = rescheduleNoteTitleFromMeeting(m) || rescheduleLine;
  const displayTimeLabel = meetingListStartTimeLabel(displaySchedule, m);
  const showRescheduledTimeStyle = displaySchedule.rescheduled;
  const hasTranscriptTags = showTranscriptActions && (
    (m.type === "google_calendar" || m.type === "calendar_auto") || m.type === "instant" || isLargeMeeting || !!rescheduleLine
  );
  const handleClick = () => onOpen?.(m);
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(m);
    }
  };
  return (
    <div
      role="row"
      tabIndex={0}
      className={`meet-list-row is-clickable${cancelled ? " is-cancelled" : ""}${referenceListLayout ? " meet-list-row--reference" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`Open ${m.title}`}
    >
      <div className="meet-list-cell col-meeting" role="cell">
        <div
          className={`meet-list-meeting-icon${
            referenceListLayout ? " meet-list-meeting-icon--reference meet-list-meeting-icon--brand" : ""
          }`}
          style={
            referenceListLayout
              ? undefined
              : { background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #DCE6EF" }
          }
          aria-hidden={!referenceListLayout && !meetingIsInstantJoin(m)}
          aria-label={
            referenceListLayout
              ? meetingIsInstantJoin(m)
                ? "Instant join"
                : pl.label
              : undefined
          }
        >
            <MeetListRowPlatformIcon meeting={m} referenceListLayout={referenceListLayout} />
        </div>
        <div className="meet-list-meeting-text">
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexWrap: "wrap", rowGap: 4 }}>
            <span
              className={`meet-list-meeting-title${cancelled ? " is-cancelled" : ""}${referenceListLayout ? " meet-list-meeting-title--reference" : ""}`}
              title={m.title}
            >
              {m.title}
            </span>
            {(showTranscriptActions || referenceListLayout) ? (
              <span className="meet-list-title-chip" aria-label={statusLabel}>
                <Badge label={statusLabel} bg={st.bg} color={st.fg} dot={st.dot} showDot={false} />
              </span>
            ) : null}
            {!showTranscriptActions && !referenceListLayout && (m.type === "google_calendar" || m.type === "calendar_auto") ? (
              <span className="meet-list-meeting-tag google">Google</span>
            ) : null}
            {!showTranscriptActions && !referenceListLayout && m.type === "instant" ? (
              <span className="meet-list-meeting-tag instant">Instant</span>
            ) : null}
            {referenceListLayout && meetingIsInstantJoin(m) ? (
              <InstantJoinLabelChip compact />
            ) : null}
          </div>
          <div className="meet-list-sub-row">
            <span className="meet-list-meeting-sub" title={subtitle}>{subtitle}</span>
            {hasTranscriptTags ? (
            <div className="meet-list-tags-row">
              {m.type === "google_calendar" || m.type === "calendar_auto" ? (
                <span className="meet-list-chip" style={{ background: "#E6F4EA", color: "#0F9D58", border: "1px solid #BFE6C9" }}>Google</span>
              ) : null}
              {m.type === "instant" ? (
                <span className="meet-list-chip" style={{ background: "#DBEAFE", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>Instant</span>
              ) : null}
              {isLargeMeeting ? (
                <span
                  className="meet-list-chip"
                  style={{ background: "#E2E8F0", color: "#0F172A", border: "1px solid #CBD5E1" }}
                  title={`${participantCount} participants — large meeting`}
                >
                  Large
                </span>
              ) : null}
              {displaySchedule.rescheduled || rescheduleLine ? (
                <RescheduleChip
                  line={rescheduleTitle || rescheduleLine || displaySchedule.chainNoteCompact}
                  direction={displaySchedule.direction}
                  label={displaySchedule.chipLabel}
                />
              ) : null}
            </div>
            ) : null}
          </div>
          {!showTranscriptActions && (displaySchedule.rescheduled || rescheduleLine) ? (
            <RescheduleChip
              line={rescheduleTitle || rescheduleLine || displaySchedule.chainNoteCompact}
              direction={displaySchedule.direction}
              label={displaySchedule.chipLabel}
              style={{ marginTop: 2 }}
            />
          ) : null}
        </div>
      </div>
      {showTranscriptActions ? (
        <div
          className="meet-list-cell col-datetime"
          role="cell"
          title={
            displaySchedule.rescheduled
              ? `${dateLabel || ""} · ${displaySchedule.range}${rescheduleTitle ? ` · ${rescheduleTitle}` : ""}`
              : `${dateLabel || ""}${dateLabel && m.time ? " · " : ""}${formatMeetingTimeForDisplay(m.time) || ""}`
          }
        >
          <span className="meet-datetime-date truncate">{dateLabel || "—"}</span>
          {displayTimeLabel ? (
            <span
              className={`meet-datetime-time truncate${showRescheduledTimeStyle ? " meet-list-time-rescheduled" : ""}`}
            >
              {displayTimeLabel}
            </span>
          ) : null}
        </div>
      ) : referenceListLayout ? (
        <div className="meet-list-cell col-time col-time--reference" role="cell">
          <span
            className={`meet-ref-time-main${showRescheduledTimeStyle ? " meet-ref-time-main--rescheduled" : ""}`}
          >
            {formatReferenceListTime(m?.time) || displayTimeLabel}
          </span>
          {dateLabel ? (
            <span className="meet-ref-time-sub">{formatReferenceDateLabel(m?.date || dateLabel)}</span>
          ) : null}
        </div>
      ) : (
        <div
          className="meet-list-cell col-time"
          role="cell"
          title={
            showRescheduledTimeStyle
              ? `${displaySchedule.range}${rescheduleTitle ? ` · ${rescheduleTitle}` : ""}`
              : displayTimeLabel
          }
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0 }}>
            {displaySchedule.rescheduled ? (
              <RescheduleChip
                line={rescheduleTitle || rescheduleLine || displaySchedule.chainNoteCompact}
                direction={displaySchedule.direction}
                label={displaySchedule.chipLabel}
              />
            ) : null}
            <span className={`truncate${showRescheduledTimeStyle ? " meet-list-time-rescheduled" : ""}`}>
              {displaySchedule.rescheduled
                ? displaySchedule.range || displayTimeLabel
                : displayTimeLabel}
            </span>
          </div>
        </div>
      )}
      <div className="meet-list-cell col-host" role="cell">
        {showTranscriptActions || referenceListLayout ? (
          meetingHasDisplayableHost(m) ? (
            <span className="meet-host-compact meet-host-compact--reference" title={hostFullName}>
              <Avatar initials={meetingHostInitials(m)} size={referenceListLayout ? 26 : 22} color="#6B7280" />
              <span className="meet-host-first">{hostFullName}</span>
            </span>
          ) : (
            <span className="truncate" style={{ color: T.textMuted, opacity: 0.55 }}>—</span>
          )
        ) : meetingHasDisplayableHost(m) ? (
          <span className="truncate" title={meetingHostName(m)}>{meetingHostName(m)}</span>
        ) : (
          <span className="truncate" style={{ color: T.textMuted, opacity: 0.55 }}>—</span>
        )}
      </div>
      <div className="meet-list-cell col-participants" role="cell">
        <MeetListParticipantStack
          participants={m.participants}
          meeting={m}
          showCountLabel
          avatarSize={referenceListLayout || showTranscriptActions ? 22 : 20}
        />
      </div>
      {showTranscriptActions ? null : referenceListLayout ? (
        <div className="meet-list-cell col-status" role="cell" title={meetingLiveStatusTooltip(m, status) || undefined}>
          <MeetingReferenceStatusCell m={m} />
        </div>
      ) : (
        <div className="meet-list-cell col-status" role="cell" title={meetingLiveStatusTooltip(m, status) || undefined}>
          <span className="meet-list-status-badge-wrap">
            <Badge label={statusLabel} bg={st.bg} color={st.fg} dot={st.dot} />
          </span>
        </div>
      )}
      {showTranscriptActions ? (
        <div className="meet-list-cell col-transcript-actions" role="cell">
          <MeetingTranscriptActions m={m} layout="list" onOpen={onOpen} />
        </div>
      ) : referenceListLayout ? (
        <div className="meet-list-cell col-actions" role="cell">
          <MeetingReferenceActions
            m={m}
            onOpen={onOpen}
            showAutoJoinToggle={showAutoJoinToggle}
            meetingAutoJoinEnabled={meetingAutoJoinEnabled}
            meetingToggleSaving={meetingToggleSaving}
            onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
            showRecordingToggle={showRecordingToggle}
            meetingRecordingEnabled={meetingRecordingEnabled}
            meetingRecordingSaving={meetingRecordingSaving}
            onToggleMeetingRecording={onToggleMeetingRecording}
            meetingSettingsReadOnly={meetingSettingsReadOnly}
            meetingSettingsReadOnlyHint={meetingSettingsReadOnlyHint}
          />
        </div>
      ) : (
        <>
          <div className="meet-list-cell col-autojoin-toggle" role="cell" {...tourAutoJoinAttrs}>
            <div className="meet-list-outcome-wrap">
            {showAutoJoinToggle ? (
              <span
                className={`meet-toggle-chip${meetingAutoJoinEnabled ? " is-on" : ""}${
                  meetingToggleSaving || meetingSettingsReadOnly ? " is-disabled" : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <ToggleBar
                  checked={!!meetingAutoJoinEnabled}
                  disabled={!!meetingToggleSaving || meetingSettingsReadOnly}
                  label={meetingRecordingToggleLabel({
                    enabled: !!meetingAutoJoinEnabled,
                    saving: !!meetingToggleSaving,
                    compact: true,
                    lang,
                  })}
                  title={toggleReadOnlyTitle || JOIN_MEET_AI_TOGGLE_HINT}
                  onChange={(next) => onToggleMeetingAutoJoin?.(next)}
                  stopPropagation
                  tipHorizontal="center"
                  tipPrefer="below"
                  tipMaxWidth={220}
                  switchWidth={26}
                  switchHeight={15}
                  thumbSize={11}
                />
              </span>
            ) : isEndedMeetingStatus(status, m) ? (
              <CompletedAutoJoinCell m={m} onOpen={onOpen} layout="list" />
            ) : (
              <MeetListUnavailableOutcome
                label="Auto join not available"
                title="Auto join applies to upcoming Google Calendar meetings only."
              />
            )}
            </div>
          </div>
          <div className="meet-list-cell col-video-rec-toggle" role="cell">
            <div className="meet-list-outcome-wrap">
            {showAutoJoinToggle ? (
              <span
                className={`meet-toggle-chip${
                  meetingAutoJoinEnabled && meetingRecordingEnabled ? " is-on" : ""
                }${
                  meetingRecordingSaving || !meetingAutoJoinEnabled || meetingSettingsReadOnly
                    ? " is-disabled"
                    : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <ToggleBar
                  checked={!!meetingAutoJoinEnabled && !!meetingRecordingEnabled}
                  disabled={!!meetingRecordingSaving || !meetingAutoJoinEnabled || meetingSettingsReadOnly}
                  label={meetingRecordingToggleLabel({
                    enabled: !!(meetingAutoJoinEnabled && meetingRecordingEnabled),
                    saving: !!meetingRecordingSaving,
                    compact: true,
                    lang,
                  })}
                  title={
                    toggleReadOnlyTitle ||
                    (!meetingAutoJoinEnabled
                      ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                      : VIDEO_REC_ENABLE_HINT_COMPACT)
                  }
                  onChange={(next) => {
                    if (next && !meetingAutoJoinEnabled) return;
                    onToggleMeetingRecording?.(next);
                  }}
                  stopPropagation
                  tipHorizontal="center"
                  tipPrefer="below"
                  tipMaxWidth={220}
                  switchWidth={26}
                  switchHeight={15}
                  thumbSize={11}
                />
              </span>
            ) : isEndedMeetingStatus(status, m) ? (
              <CompletedVideoRecCell m={m} layout="list" onOpen={onOpen} />
            ) : (
              <MeetListUnavailableOutcome
                label="Recording not available"
                title="Video recording is only available for eligible calendar meetings."
              />
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

function formatChatUsd(n) {
  const v = Number(n) || 0;
  if (v === 0) return "$0.0000";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  if (v < 1) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

/** Admin Usage — Remaining Credits hero card (KPI-style gradients by balance). */
function recallCreditBalanceUi(creditUsd) {
  const neutralHero = {
    cardBg: "linear-gradient(135deg, #f6f8fb 0%, #eef2f7 100%)",
    border: "#e3e8ef",
    titleColor: "#6b7688",
    valueColor: "#0f1a2b",
    subColor: "#6b7688",
    statLabel: "#6b7688",
    statValue: "#0f1a2b",
    divider: "#e3e8ef",
    badgeBorder: "#cbd5e1",
    badgeBg: "rgba(255,255,255,0.85)",
    badgeText: "#6b7688",
    iconBg: "rgba(148,163,184,0.2)",
    iconFg: "#64748b",
  };
  if (creditUsd == null || !Number.isFinite(creditUsd)) {
    return {
      alert: { label: "Balance unavailable", color: "#6b7688", tone: "neutral" },
      hero: neutralHero,
      metricTone: "slate",
    };
  }
  if (creditUsd > 20) {
    return {
      alert: { label: "Credit balance healthy", color: "#0f9d8f", tone: "ok" },
      hero: {
        cardBg: "linear-gradient(135deg, #ffffff 0%, #f0fdf9 50%, #ccfbf1 100%)",
        border: "rgba(15,157,143,0.38)",
        titleColor: "#0f766e",
        valueColor: "#047857",
        subColor: "rgba(4,120,87,0.82)",
        statLabel: "rgba(4,120,87,0.65)",
        statValue: "#065f46",
        divider: "rgba(15,157,143,0.22)",
        badgeBorder: "rgba(15,157,143,0.45)",
        badgeBg: "rgba(255,255,255,0.72)",
        badgeText: "#047857",
        iconBg: "rgba(15,157,143,0.16)",
        iconFg: "#0f9d8f",
      },
      metricTone: "green",
    };
  }
  if (creditUsd > 15) {
    return {
      alert: { label: "Credit balance $15 – $20", color: "#c98a2c", tone: "warning" },
      hero: {
        cardBg: "linear-gradient(135deg, #ffffff 0%, #fff7ed 52%, #ffedd5 100%)",
        border: "rgba(201,138,44,0.42)",
        titleColor: "#c2410c",
        valueColor: "#9a3412",
        subColor: "rgba(154,52,18,0.82)",
        statLabel: "rgba(154,52,18,0.65)",
        statValue: "#7c2d12",
        divider: "rgba(201,138,44,0.25)",
        badgeBorder: "rgba(201,138,44,0.5)",
        badgeBg: "rgba(255,255,255,0.72)",
        badgeText: "#9a3412",
        iconBg: "rgba(201,138,44,0.16)",
        iconFg: "#c98a2c",
      },
      metricTone: "amber",
    };
  }
  if (creditUsd > 10) {
    return {
      alert: { label: "Credit balance $10 – $15", color: "#d97706", tone: "warning" },
      hero: {
        cardBg: "linear-gradient(135deg, #ffffff 0%, #fffbeb 52%, #fef3c7 100%)",
        border: "rgba(217,119,6,0.38)",
        titleColor: "#b45309",
        valueColor: "#d97706",
        subColor: "rgba(180,83,9,0.82)",
        statLabel: "rgba(180,83,9,0.65)",
        statValue: "#92400e",
        divider: "rgba(217,119,6,0.22)",
        badgeBorder: "rgba(217,119,6,0.45)",
        badgeBg: "rgba(255,255,255,0.72)",
        badgeText: "#b45309",
        iconBg: "rgba(251,191,36,0.22)",
        iconFg: "#d97706",
      },
      metricTone: "amber",
    };
  }
  return {
    alert: {
      label: creditUsd <= 0 ? "Credits exhausted" : "Critical credit balance ≤ $10",
      color: "#b0403f",
      tone: "danger",
    },
    hero: {
      cardBg: "linear-gradient(135deg, #ffffff 0%, #fef2f2 52%, #fee2e2 100%)",
      border: "rgba(176,64,63,0.42)",
      titleColor: "#b91c1c",
      valueColor: "#b0403f",
      subColor: "rgba(185,28,28,0.82)",
      statLabel: "rgba(185,28,28,0.65)",
      statValue: "#991b1b",
      divider: "rgba(176,64,63,0.22)",
      badgeBorder: "rgba(176,64,63,0.45)",
      badgeBg: "rgba(255,255,255,0.72)",
      badgeText: "#b0403f",
      iconBg: "rgba(176,64,63,0.12)",
      iconFg: "#b0403f",
    },
    metricTone: "red",
  };
}

function formatChatTokens(n) {
  return (Number(n) || 0).toLocaleString("en-IN");
}

function formatChatRecentAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "";
  }
}

function chatUserInitials(name, email) {
  const n = String(name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  const em = String(email || "").split("@")[0] || "?";
  return em.slice(0, 2).toUpperCase();
}

const CHAT_PROVIDER_THEME = {
  openai: {
    label: "OpenAI",
    accent: "#10a37f",
    accentSoft: "rgba(16,163,127,0.14)",
    gradient: "linear-gradient(135deg, #10a37f 0%, #0d9488 55%, #0891b2 100%)",
  },
  anthropic: {
    label: "Anthropic",
    accent: "#d97706",
    accentSoft: "rgba(217,119,6,0.14)",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #c2410c 100%)",
  },
};

/**
 * Shared admin chart scroller — every admin tab graph uses this so months /
 * points can grow without squeezing labels: content keeps a readable width
 * per point and the panel scrolls horizontally instead of crushing the x-axis.
 */
function AdminChartScroll({ points = 0, pointWidth = 90, minVisible = 6, minWidth = 600, children }) {
  const n = Math.max(0, Number(points) || 0);
  const wide = n > minVisible;
  return (
    <div className="adm-chart-scroll">
      <div className="adm-chart-scroll-inner" style={{ minWidth: wide ? Math.max(minWidth, n * pointWidth) : "100%" }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Dual-axis usage chart: token bars (left) + cost line (right).
 * Bars vs line stay visually distinct even when cost tracks tokens proportionally
 * (two normalized lines would overlap). Hover shows date / tokens / cost.
 */
function DualAxisUsageChart({ series, isLight, accent = "#2563eb", accentCost = "#10b981", note = "" }) {
  const points = Array.isArray(series) ? series.filter((p) => p && p.label) : [];
  const emptyColor = isLight ? "#94a3b8" : "rgba(255,255,255,0.35)";
  if (points.length === 0) {
    return (
      <div className="cu-chart-empty" style={{ color: emptyColor }}>
        <Svg d={IC.task} size={26} color={emptyColor} />
        <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600 }}>No chart data yet</div>
        <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.55, maxWidth: 320, margin: "4px auto 0" }}>
          Trends appear after free-form chatbot questions that call OpenAI or Anthropic.
        </div>
      </div>
    );
  }
  const axisColor = isLight ? "#64748b" : "rgba(255,255,255,0.55)";
  const gridColor = isLight ? "#e8eef4" : "rgba(255,255,255,0.08)";
  const tipBg = isLight ? "#ffffff" : "#0f172a";
  const tipFg = isLight ? "#0f172a" : "#f8fafc";
  const tipBorder = isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.16)";
  // Dynamic scaling: token axis reads in raw/K/M depending on the actual data range,
  // instead of a fixed unit that under- or over-compresses small vs. large workspaces.
  const formatAxisTok = (v) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
    return String(Math.round(v));
  };
  const formatAxisCost = (v) => (v < 0.01 && v > 0 ? `$${v.toFixed(3)}` : `$${v.toFixed(2)}`);
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload || {};
    return (
      <div
        style={{
          minWidth: 160,
          padding: "10px 12px",
          borderRadius: 10,
          background: tipBg,
          color: tipFg,
          border: `1px solid ${tipBorder}`,
          boxShadow: isLight ? "0 12px 28px rgba(15,23,42,0.16)" : "0 10px 28px rgba(2,6,23,0.45)",
          fontSize: 12,
          lineHeight: 1.45,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 4 }}>
          <span style={{ color: isLight ? "#64748b" : "rgba(248,250,252,0.7)" }}>Tokens</span>
          <span style={{ fontWeight: 700, color: accent }}>{formatChatTokens(row.tokens)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
          <span style={{ color: isLight ? "#64748b" : "rgba(248,250,252,0.7)" }}>Cost</span>
          <span style={{ fontWeight: 700, color: accentCost }}>{formatChatUsd(row.costUsd)}</span>
        </div>
      </div>
    );
  };
  const gradId = `cu-tok-bar-${String(accent).replace("#", "")}`;
  const manyPoints = points.length > 8;
  return (
    <div className="cu-chart-wrap">
      <AdminChartScroll points={points.length} pointWidth={84}>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={points} margin={{ top: 10, right: 8, left: 0, bottom: 4 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={isLight ? 0.85 : 0.9} />
              <stop offset="100%" stopColor={accent} stopOpacity={isLight ? 0.35 : 0.45} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={gridColor} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: axisColor }}
            axisLine={{ stroke: gridColor }}
            tickLine={false}
            interval={0}
            angle={manyPoints ? -35 : 0}
            textAnchor={manyPoints ? "end" : "middle"}
            height={manyPoints ? 48 : 30}
          />
          <YAxis
            yAxisId="tokens"
            tick={{ fontSize: 9.5, fill: axisColor }}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatAxisTok}
            domain={[0, (dataMax) => Math.max(4, dataMax)]}
            width={40}
            label={{ value: "Tokens", angle: -90, position: "insideLeft", fontSize: 10, fill: accent, style: { textAnchor: "middle" } }}
          />
          <YAxis
            yAxisId="cost"
            orientation="right"
            tick={{ fontSize: 9.5, fill: axisColor }}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatAxisCost}
            domain={[0, (dataMax) => Math.max(0.01, dataMax)]}
            width={54}
            label={{ value: "Cost (USD)", angle: 90, position: "insideRight", fontSize: 10, fill: accentCost, style: { textAnchor: "middle" } }}
          />
          <RechartsTooltip content={<CustomTooltip />} />
          <RechartsLegend align="center" verticalAlign="top" wrapperStyle={{ fontSize: 11, color: axisColor }} iconType="square" iconSize={10} />
          <Bar yAxisId="tokens" dataKey="tokens" name="Tokens" fill={`url(#${gradId})`} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />
          <Line
            yAxisId="cost"
            dataKey="costUsd"
            name="Cost (USD)"
            type="monotone"
            stroke={accentCost}
            strokeWidth={2.5}
            strokeDasharray="7 5"
            dot={{ r: 4.5, strokeWidth: 2, stroke: isLight ? "#fff" : "#0f2032", fill: accentCost }}
            activeDot={{ r: 6.5, strokeWidth: 2, stroke: isLight ? "#fff" : "#0f2032", fill: accentCost }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      </AdminChartScroll>
      {note ? (
        <div className="adm-chart-note" style={{ color: axisColor }}>
          {note}
        </div>
      ) : null}
    </div>
  );
}

function ChatUsageKpi({ label, value, sub, accent, tone = "blue", iconD, onClick, active = false, tip }) {
  const clickable = typeof onClick === "function";
  const card = (
    <AdminMetricCard
      tone={tone}
      icon={<Svg d={iconD || IC.task} size={18} color="currentColor" stroke={2} />}
      value={value}
      label={label}
      sublabel={sub}
      sparkColor={accent}
      showSpark={false}
    />
  );
  // Hover lift/highlight comes from AdminMetricCard itself; the title tooltip adds
  // the extra context the dashboard KPIs were missing.
  const wrapped = tip ? (
    <div title={tip} style={{ borderRadius: 14, height: "100%" }}>
      {card}
    </div>
  ) : (
    card
  );
  if (!clickable) return wrapped;
  return (
    <div
      role="button"
      tabIndex={0}
      title={tip}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        cursor: "pointer",
        borderRadius: 14,
        outline: active ? `2px solid ${accent || ADM.blue}` : "none",
        outlineOffset: 2,
      }}
    >
      {card}
    </div>
  );
}

function normalizeAdminSearchQuery(q) {
  return String(q || "").trim().toLowerCase();
}

/** True when query is empty or every token appears somewhere in parts (case-insensitive). */
function adminSearchMatches(query, ...parts) {
  const needle = normalizeAdminSearchQuery(query);
  if (!needle) return true;
  const hay = parts
    .flat()
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v).toLowerCase())
    .join(" ");
  return adminSearchMatchesHaystack(needle, hay);
}

function adminSearchMatchesHaystack(queryOrNeedle, hayLower) {
  const needle = normalizeAdminSearchQuery(queryOrNeedle);
  if (!needle) return true;
  const hay = String(hayLower || "");
  const tokens = needle.split(/\s+/).filter(Boolean);
  if (tokens.length <= 1) return hay.includes(needle);
  return tokens.every((t) => hay.includes(t));
}

function buildActivityLogFilterIndex(logs) {
  const list = Array.isArray(logs) ? logs : [];
  return list.map((log, sourceIdx) => ({
    sourceIdx,
    log,
    searchHay: logAdminSearchParts(log)
      .flat()
      .filter((v) => v != null && String(v).trim() !== "")
      .map((v) => String(v).toLowerCase())
      .join(" "),
    ownerHay: logOwnerFilterHaystack(log),
    meetingHay: logMeetingFilterHaystack(log),
    timeMs: log?.time ? new Date(log.time).getTime() : NaN,
  }));
}

function meetingAdminSearchParts(m) {
  const parts = [
    m?.title,
    m?.status,
    m?.platform,
    m?.type,
    m?.date,
    m?.time,
    m?.owner_name,
    m?.owner_email,
    m?.recall_bot_id,
    m?.google_event_id,
    m?.summary,
  ];
  for (const p of m?.participants || []) {
    parts.push(p?.name, p?.email, p?.initials);
  }
  for (const a of m?.autoJoinOptInUsers || []) {
    parts.push(a?.name, a?.email, a?.via);
  }
  return parts;
}

function meetingAdminSearchHaystack(m) {
  return meetingAdminSearchParts(m)
    .flat()
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v).toLowerCase())
    .join(" ");
}

/** Precomputed haystacks — avoids rebuilding participant strings on every keystroke. */
function buildMeetingAdminSearchIndex(meetings) {
  const list = Array.isArray(meetings) ? meetings : [];
  return list.map((m) => ({ m, searchHay: meetingAdminSearchHaystack(m) }));
}

function userAdminSearchParts(u) {
  if (typeof u === "string") return [u];
  return [u?.name, u?.email, u?.id, u?.role, u?.initials];
}

function adminUserOwnedCount(u) {
  return Number(u?.autoJoinMeetings != null ? u.autoJoinMeetings : u?.ownedMeetings || 0) || 0;
}

function adminUserAttendedCount(u) {
  return Number(u?.attendedMeetings || 0) || 0;
}

function adminUserTotalCount(u) {
  if (Number.isFinite(Number(u?.meetingCount))) return Number(u.meetingCount);
  return Math.max(adminUserOwnedCount(u), adminUserAttendedCount(u));
}

/** Normalize DB roles for filters: admin vs member (user/member/etc.). */
function adminUserRoleBucket(u) {
  const r = String(u?.role || "user").trim().toLowerCase();
  if (r === "admin" || r === "administrator" || r === "superadmin" || r === "owner") return "admin";
  return "member";
}

function adminUserCreatedAtMs(u) {
  const t = Date.parse(u?.created_at || "");
  return Number.isFinite(t) ? t : null;
}

function adminUserDisplayRole(u) {
  const bucket = adminUserRoleBucket(u);
  if (bucket === "admin") return "Admin";
  const raw = String(u?.role || "user").trim();
  if (/^member$/i.test(raw)) return "Member";
  if (/^user$/i.test(raw) || !raw) return "Member";
  return raw;
}

function logAdminSearchParts(log) {
  const parts = [
    log?.action,
    log?.detail,
    log?.type,
    log?.time,
    log?.ownerName,
    log?.ownerEmail,
    log?.googleEventId,
    log?.meetingTitle,
    log?.meetingId,
    log?.meetingDate,
    log?.hostName,
    log?.hostEmail,
    log?.scheduledStart,
    log?.scheduledEnd,
    log?.actualStart,
    log?.actualEnd,
    log?.platform,
  ];
  for (const p of log?.participants || []) {
    parts.push(p?.name, p?.email);
  }
  for (const p of log?.humanParticipants || []) {
    parts.push(p?.name, p?.email);
  }
  for (const a of log?.autoJoinOptInUsers || []) {
    parts.push(a?.name, a?.email, a?.via);
  }
  return parts;
}

function logOwnerFilterHaystack(log) {
  const parts = [log?.ownerName, log?.ownerEmail, log?.hostName, log?.hostEmail];
  for (const p of log?.participants || []) {
    parts.push(p?.name, p?.email);
  }
  for (const p of log?.humanParticipants || []) {
    parts.push(p?.name, p?.email);
  }
  for (const a of log?.autoJoinOptInUsers || []) {
    parts.push(a?.name, a?.email);
  }
  return parts
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function logMeetingFilterHaystack(log) {
  return [
    log?.meetingTitle,
    log?.meetingId,
    log?.detail,
    log?.googleEventId,
    log?.meetingUrl,
    log?.meetingDate,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ")
    .toLowerCase();
}

/** Activity log row date/time — prefer event timestamp, fall back to scheduled meeting day. */
function formatActivityLogDateTime(log) {
  const raw = log?.time;
  if (raw != null && raw !== "") {
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isFinite(d.getTime())) {
      return {
        date: formatDateInAppTz(d),
        time: formatTimeInAppTz(d, { hour: "2-digit", minute: "2-digit" }),
      };
    }
  }
  const md = String(log?.meetingDate || "").trim().slice(0, 10);
  const st = String(log?.scheduledStart || log?.scheduled_start || "").trim();
  if (md) {
    return {
      date: formatYmdDisplay(md),
      time: st ? formatMeetingTimeForDisplay(st) : "—",
    };
  }
  return { date: "—", time: "—" };
}

function logActivityDateBounds(ymdFrom, ymdTo) {
  let fromMs = null;
  let toMs = null;
  if (ymdFrom) {
    const start = new Date(`${ymdFrom}T00:00:00`);
    if (Number.isFinite(start.getTime())) fromMs = start.getTime();
  }
  if (ymdTo) {
    const end = new Date(`${ymdTo}T23:59:59.999`);
    if (Number.isFinite(end.getTime())) toMs = end.getTime();
  }
  return { fromMs, toMs };
}

function chatUsageUserSearchParts(u) {
  const parts = [u?.name, u?.email];
  for (const r of u?.recent || []) {
    parts.push(r?.provider, r?.model, r?.user_name);
  }
  return parts;
}

function meetingAdminSortYmd(m) {
  const d = String(m?.date || "").trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  for (const raw of [m?.completedAt, m?.completed_at, m?.updated_at, m?.created_at]) {
    if (!raw) continue;
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) return dateToYmd(dt); // local calendar day (not UTC)
  }
  return "";
}

/** Same calendar day as date filter, plus meeting time — keeps Admin sort aligned with From/To. */
function meetingAdminSortMs(m) {
  const status = String(m?.status || "").toLowerCase();
  const completedRaw = m?.completedAt || m?.completed_at;
  // Completed rows sometimes keep a wrong/future calendar `date` (bad google_event_id stamp).
  // "Latest first" should follow real completion time, not that stale schedule field.
  if (
    completedRaw &&
    (status === "completed" || status === "done" || status === "ended" || status === "summarized")
  ) {
    const doneMs = new Date(completedRaw).getTime();
    if (Number.isFinite(doneMs)) {
      const ymd = meetingAdminSortYmd(m);
      const today = todayStr();
      if (!ymd || ymd > today) return doneMs;
    }
  }
  const ymd = meetingAdminSortYmd(m);
  if (!ymd) return 0;
  const parts = ymd.split("-").map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return 0;
  const [y, mo, d] = parts;
  let { h, min } = parseMeetingTimeToHourMin(m?.time);
  if (h == null || min == null) {
    const fromTitle = inferTimeFromMeetingTitle(m?.title);
    h = fromTitle.h;
    min = fromTitle.min;
  }
  if (h == null || min == null) return new Date(y, mo - 1, d, 0, 0, 0, 0).getTime();
  return new Date(y, mo - 1, d, h, min, 0, 0).getTime();
}

function meetingInAdminDateRange(m, from, to) {
  const fromY = String(from || "").trim().slice(0, 10);
  const toY = String(to || "").trim().slice(0, 10);
  if (!fromY && !toY) return true;
  const ymd = meetingAdminSortYmd(m);
  if (!ymd) return false;
  if (fromY && ymd < fromY) return false;
  if (toY && ymd > toY) return false;
  return true;
}

function sortAdminMeetingsNewestFirst(list) {
  return [...(list || [])].sort((a, b) => {
    const da = meetingAdminSortMs(a);
    const db = meetingAdminSortMs(b);
    if (da !== db) return db - da;
    return String(b?.title || "").localeCompare(String(a?.title || ""));
  });
}

function sortAdminMeetingsOldestFirst(list) {
  return [...(list || [])].sort((a, b) => {
    const da = meetingAdminSortMs(a);
    const db = meetingAdminSortMs(b);
    if (da !== db) return da - db;
    return String(a?.title || "").localeCompare(String(b?.title || ""));
  });
}

/** Clearer than DD-MM-YYYY for Admin register (avoids month/day mix-ups). */
function formatAdminMeetingDateLabel(ymd) {
  const raw = String(ymd || "").trim().slice(0, 10);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return formatYmdDisplay(ymd) || "—";
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(dt.getTime())) return formatYmdDisplay(ymd) || "—";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/** Keep admin From/To date filters in order (YYYY-MM-DD strings). */
function adminDateRangeOnFromChange(nextFrom, dateTo, setFrom, setTo) {
  const from = String(nextFrom || "").trim();
  setFrom(from);
  const to = String(dateTo || "").trim();
  if (from && to && to < from) setTo(from);
}

function adminDateRangeOnToChange(dateFrom, nextTo, setFrom, setTo) {
  const to = String(nextTo || "").trim();
  setTo(to);
  const from = String(dateFrom || "").trim();
  if (from && to && to < from) setFrom(to);
}

/**
 * Org-wide email counts — same source as Admin → SMTP → SMTP Usage (period overall).
 * Sent / not-sent come from API totals; not-sent is split failed vs pending via breakdown.
 */
function adminEmailDeliveryFilterCounts(smtpReport) {
  const all = Number(smtpReport?.totals?.completedMeetings) || 0;
  const sent = Number(smtpReport?.totals?.sent) || 0;
  const notSent = Number(smtpReport?.totals?.notSent) || 0;
  const breakdown = Array.isArray(smtpReport?.statusBreakdown) ? smtpReport.statusBreakdown : [];
  let failed = 0;
  for (const row of breakdown) {
    const c = String(row?.code || "").toLowerCase();
    const n = Number(row?.count) || 0;
    if (!n) continue;
    if (c.includes("fail") || c === "smtp_failed") failed += n;
  }
  if (failed > notSent) failed = notSent;
  let pending = Math.max(0, notSent - failed);
  if (all > 0 && sent + pending + failed !== all) {
    pending = Math.max(0, all - sent - failed);
  }
  return { all, sent, failed, pending };
}

/** Same completed / sent headline numbers on Transcripts & Email and SMTP tabs. */
function adminSmtpHeadlineMeta(smtpReport) {
  const counts = adminEmailDeliveryFilterCounts(smtpReport);
  const listReturned =
    Number(smtpReport?.totals?.listReturned) ||
    (Array.isArray(smtpReport?.sentMeetings) ? smtpReport.sentMeetings.length : 0) +
      (Array.isArray(smtpReport?.notSentMeetings) ? smtpReport.notSentMeetings.length : 0);
  return { ...counts, listReturned };
}

function AdminMeetingsListControls({
  dateFrom,
  dateTo,
  sortOrder,
  onDateFromChange,
  onDateToChange,
  onSortOrderChange,
  onClearDates,
  isLight,
  adminBorder,
  adminTextMuted,
  adminTextMain,
}) {
  const hasDates = !!(String(dateFrom || "").trim() || String(dateTo || "").trim());
  const fieldStyle = {
    fontSize: 12,
    padding: "6px 8px",
    borderRadius: 8,
    border: `1px solid ${adminBorder}`,
    background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
    color: adminTextMain,
    minHeight: 32,
  };
  return (
    <div
      className="adm-meetings-list-controls"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: 10,
        marginLeft: "auto",
        flexShrink: 0,
      }}
    >
      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10.5, fontWeight: 600, color: adminTextMuted }}>
        From
        <input
          type="date"
          className="adm-input"
          value={dateFrom || ""}
          onChange={(e) =>
            adminDateRangeOnFromChange(e.target.value, dateTo, onDateFromChange, onDateToChange)
          }
          max={dateTo || undefined}
          style={fieldStyle}
          aria-label="Filter meetings from date"
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10.5, fontWeight: 600, color: adminTextMuted }}>
        To
        <input
          type="date"
          className="adm-input"
          value={dateTo || ""}
          onChange={(e) =>
            adminDateRangeOnToChange(dateFrom, e.target.value, onDateFromChange, onDateToChange)
          }
          min={dateFrom || undefined}
          style={fieldStyle}
          aria-label="Filter meetings to date"
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10.5, fontWeight: 600, color: adminTextMuted }}>
        Sort
        <select
          className="adm-input"
          value={sortOrder === "oldest" ? "oldest" : "newest"}
          onChange={(e) => onSortOrderChange(e.target.value === "oldest" ? "oldest" : "newest")}
          style={{ ...fieldStyle, minWidth: 128, cursor: "pointer" }}
          aria-label="Sort meetings by date"
        >
          <option value="newest">Latest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </label>
      {hasDates ? (
        <button
          type="button"
          className="adm-ghost-btn"
          onClick={onClearDates}
          style={{ fontSize: 11.5, padding: "7px 11px", marginBottom: 1 }}
          title="Show all sessions (no date filter)"
        >
          All dates
        </button>
      ) : (
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 650,
            color: adminTextMuted,
            padding: "8px 4px 7px",
          }}
          title="Showing all sessions — pick From/To to filter by date"
        >
          All dates
        </span>
      )}
    </div>
  );
}

const ADMIN_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500];
const ADMIN_MEETINGS_PAGE_SIZE_DEFAULT = 20;
const ADMIN_LOGS_PAGE_SIZE_DEFAULT = 50;
const ADMIN_USERS_PAGE_SIZE_DEFAULT = 20;
/** Admin Calendar API page size (server max 100) — keep fetch chunk separate from UI list size. */
const ADMIN_CALENDAR_FETCH_PAGE_SIZE = 100;
const ADMIN_CALENDAR_LIST_PAGE_SIZE_DEFAULT = 20;
const ADMIN_TRANSCRIPTS_PAGE_SIZE_DEFAULT = 20;
/** SMTP Usage tables (Recap sent / Recap not sent) — UI-only rows choice. */
const SMTP_TABLE_PAGE_SIZE_OPTIONS = [10, 50, 100];
const SMTP_TABLE_PAGE_SIZE_DEFAULT = 10;
/**
 * Busy orgs can have 50–60+ meetings/day. Cap was 8×50=400 and cut month grids
 * mid-month (later days looked empty). Allow enough pages for a dense month.
 */
const ADMIN_CALENDAR_MAX_PAGES = 40;
/** Parallel page fetches after page 1 (keeps month loads reasonable). */
const ADMIN_CALENDAR_PAGE_CONCURRENCY = 4;

function normalizeAdminMeetingsPayload(data) {
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: 1,
      pageSize: data.length || ADMIN_CALENDAR_FETCH_PAGE_SIZE,
    };
  }
  if (data && typeof data === "object") {
    const items = Array.isArray(data.items)
      ? data.items
      : Array.isArray(data.meetings)
        ? data.meetings
        : [];
    return {
      items,
      total: Number(data.total != null ? data.total : items.length) || 0,
      page: Number(data.page) || 1,
      pageSize: Number(data.pageSize) || ADMIN_CALENDAR_FETCH_PAGE_SIZE,
    };
  }
  return { items: [], total: 0, page: 1, pageSize: ADMIN_CALENDAR_FETCH_PAGE_SIZE };
}

// The non-calendar admin endpoint is capped when called without pagination.
// Fetch every page so All Meetings and the shared Transcripts view show the
// database total instead of the first 500 rows only.
async function fetchAllAdminMeetings() {
  const pageSize = 100;
  const first = normalizeAdminMeetingsPayload(
    await apiFetch(`/admin/meetings?lite=1&page=1&pageSize=${pageSize}`)
  );
  const items = first.items.slice();
  const totalPages = Math.max(1, Math.ceil(first.total / pageSize));
  for (let page = 2; page <= totalPages; page += 1) {
    const next = normalizeAdminMeetingsPayload(
      await apiFetch(`/admin/meetings?lite=1&page=${page}&pageSize=${pageSize}`)
    );
    if (!next.items.length) break;
    items.push(...next.items);
  }
  return items;
}

function AdminListPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = ADMIN_PAGE_SIZE_OPTIONS,
  isLight,
  adminBorder,
  adminTextMuted,
  adminTextMain,
  label = "items",
}) {
  const size = Math.max(1, Number(pageSize) || 20);
  const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / size));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * size + 1;
  const to = Math.min(total, safePage * size);
  const showPager = total > size;
  const showSize = typeof onPageSizeChange === "function" && Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0;

  if (total <= 0 && !showSize) return null;
  if (total > 0 && total <= size && !showSize) {
    return (
      <div style={{ fontSize: 12, color: adminTextMuted, padding: "8px 2px 2px" }}>
        Showing {total} {label}
      </div>
    );
  }

  const btnStyle = (disabled) => ({
    fontSize: 12,
    fontWeight: 600,
    padding: "7px 12px",
    borderRadius: 8,
    border: `1px solid ${adminBorder}`,
    background: disabled ? (isLight ? "#F1F5F9" : "rgba(255,255,255,0.04)") : isLight ? "#fff" : "rgba(255,255,255,0.08)",
    color: disabled ? adminTextMuted : adminTextMain,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.65 : 1,
  });
  const selectStyle = {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 8px",
    borderRadius: 8,
    border: `1px solid ${adminBorder}`,
    background: isLight ? "#fff" : "rgba(255,255,255,0.08)",
    color: adminTextMain,
    cursor: "pointer",
    minWidth: 72,
  };

  return (
    <div
      className="adm-list-pagination"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "12px 2px 4px",
        borderTop: `1px solid ${adminBorder}`,
        marginTop: 4,
      }}
    >
      <div style={{ fontSize: 12, color: adminTextMuted }}>
        {total === 0 ? (
          <>No {label}</>
        ) : (
          <>
            Showing <strong style={{ color: adminTextMain }}>{from}–{to}</strong> of{" "}
            <strong style={{ color: adminTextMain }}>{total}</strong> {label}
          </>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {showSize ? (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: adminTextMuted }}>
            <span style={{ whiteSpace: "nowrap" }}>Rows</span>
            <select
              value={size}
              aria-label="Rows per page"
              onChange={(e) => {
                const next = Number(e.target.value) || size;
                onPageSizeChange(next);
                if (typeof onPageChange === "function") onPageChange(1);
              }}
              style={selectStyle}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {showPager ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button type="button" style={btnStyle(safePage <= 1)} disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)}>
              Previous
            </button>
            <span style={{ fontSize: 12, fontWeight: 700, color: adminTextMain, minWidth: 72, textAlign: "center" }}>
              Page {safePage} / {totalPages}
            </span>
            <button
              type="button"
              style={btnStyle(safePage >= totalPages)}
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AdminMeetingsListSkeleton({ isLight = true, rows = 4 }) {
  return (
    <div aria-busy="true" aria-label="Loading meetings" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "4px 0" }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="adm-skel"
          style={{
            height: 88,
            borderRadius: 12,
            background: isLight ? "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)" : undefined,
          }}
        />
      ))}
    </div>
  );
}

const ADM_SEARCH_ICON_D = "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z";

/**
 * Search that lives in a card header rather than in a row of its own: an icon
 * button until clicked, then an inline input that expands in place. Consumes no
 * vertical space in either state.
 */
/** Admin-protected asset URL (Bearer via query — works with plain <img src>). */
function adminAuthAssetUrl(path) {
  if (!path) return "";
  const token = getToken();
  const base = path.startsWith("http")
    ? path
    : `${API}${path.startsWith("/") ? "" : "/"}${path}`;
  if (!token) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}access_token=${encodeURIComponent(token)}`;
}

/** Loads an admin-protected image (screenshots in Feedback, etc.). */
function AdminAuthImage({ path, alt = "", style, onClick, className = "" }) {
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const src = useMemo(() => adminAuthAssetUrl(path), [path]);

  useEffect(() => {
    setStatus(path ? "loading" : "error");
  }, [path]);

  if (!path || status === "error") {
    return (
      <span
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F1F5F9",
          color: "#64748B",
          border: "1px solid #E2E8F0",
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 600,
          minHeight: 72,
          minWidth: 96,
          ...style,
        }}
      >
        {path ? "Image unavailable" : "No image"}
      </span>
    );
  }

  return (
    <span className={className} style={{ display: "inline-block", position: "relative", ...style }}>
      {status === "loading" ? (
        <span
          aria-hidden
          style={{
            display: "block",
            width: "100%",
            minHeight: style?.minHeight || style?.height || 72,
            background: "linear-gradient(90deg,#F1F5F9 0%,#E2E8F0 50%,#F1F5F9 100%)",
            borderRadius: 8,
          }}
        />
      ) : null}
      <img
        src={src}
        alt={alt}
        onLoad={() => setStatus("ok")}
        onError={() => setStatus("error")}
        style={{
          display: status === "loading" ? "none" : "block",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          borderRadius: 8,
          cursor: onClick ? "pointer" : "default",
          background: "#F8FAFC",
        }}
        onClick={onClick}
      />
    </span>
  );
}

const ADMIN_SEARCH_DEBOUNCE_MS = 180;

function AdminSearchToolbar({
  value,
  onChange,
  placeholder,
  adminTextMuted,
  filteredCount,
  totalCount,
  alwaysExpanded = false,
  debounceMs = 0,
}) {
  const committed = String(value ?? "");
  const [draft, setDraft] = useState(committed);
  const debounceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  useEffect(() => {
    if (!debounceMs) return undefined;
    if (draft === committed) return undefined;
    debounceRef.current = window.setTimeout(() => {
      startTransition(() => onChangeRef.current?.(draft));
    }, debounceMs);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [draft, committed, debounceMs]);

  const inputValue = debounceMs ? draft : committed;
  const needle = String(inputValue || "").trim();
  const showCount = needle && Number.isFinite(filteredCount) && Number.isFinite(totalCount);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  // An active query keeps the field visible: a filtered list with no visible
  // search box looks like missing data.
  const expanded = alwaysExpanded || open || !!needle;

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const close = () => {
    setDraft("");
    onChange?.("");
    if (!alwaysExpanded) setOpen(false);
  };

  const handleInputChange = (next) => {
    if (debounceMs) setDraft(next);
    else onChange?.(next);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        className="adm-search-icon-btn"
        onClick={() => setOpen(true)}
        title={placeholder}
        aria-label={placeholder}
      >
        <Svg d={ADM_SEARCH_ICON_D} color={adminTextMuted || ADM.textMuted} size={16} />
      </button>
    );
  }

  return (
    <div className={`adm-search-inline${alwaysExpanded ? " adm-search-inline--static" : ""}`}>
      <div className="adm-search-inline-field">
        <Svg d={ADM_SEARCH_ICON_D} color={adminTextMuted || ADM.textMuted} size={15} />
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
          onBlur={() => {
            if (debounceMs && draft !== committed) {
              onChangeRef.current?.(draft);
            }
            if (!alwaysExpanded && !String(inputValue || "").trim()) setOpen(false);
          }}
          placeholder={placeholder}
          aria-label={placeholder}
        />
      </div>
      {showCount ? <span className="adm-search-inline-count">{filteredCount} of {totalCount}</span> : null}
      {!alwaysExpanded ? (
        <button
          type="button"
          className="adm-search-icon-btn"
          // Without this the input's onBlur collapses the toolbar on mousedown and
          // unmounts this button before the click lands.
          onMouseDown={(e) => e.preventDefault()}
          onClick={close}
          title="Close search"
          aria-label="Close search"
        >
          <Svg d="M18 6L6 18M6 6l12 12" color={adminTextMuted || ADM.textMuted} size={15} />
        </button>
      ) : null}
    </div>
  );
}

function AdminPremiumHero({ badge, badgeIconD, title, desc, meta, isLight }) {
  return (
    <AdminPageHero
      badge={badge}
      badgeIcon={badgeIconD ? <Svg d={badgeIconD} size={14} color={ADM.teal} /> : null}
      title={title}
      desc={desc}
      meta={meta}
    />
  );
}

/** Org form panel: section title + optional subtitle wrapping list/table content. */
function AdminOrgPanel({ title, subtitle, headerRight, toolbar, children, className = "" }) {
  return (
    <div className={`adm-card adm-org-panel ${className}`.trim()} style={{ overflow: "hidden" }}>
      {(title || headerRight || toolbar) ? (
        <div
          className="adm-card-header adm-org-panel-head"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            {title}
            {subtitle ? <div className="adm-card-sub">{subtitle}</div> : null}
          </div>
          {(toolbar || headerRight) ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {toolbar || null}
              {headerRight || null}
            </div>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** Recap email outcome chip for All Meetings (uses stored status codes, not raw emailSent alone). */
function AdminMeetingEmailStatusChip({ meeting: m, isLight }) {
  const raw = String(m?.status || "").toLowerCase();
  const botLeft = meetingBotSessionEnded(m);
  // Never show email outcome chips while MeetAI is still in the call.
  if (!botLeft && (raw === "live" || raw === "upcoming")) return null;
  const ended = isEndedMeetingStatus(effectiveMeetingStatus(m), m) || botLeft;
  const code = meetingEmailStatusCode(m);
  if (!ended && code !== "sent" && code !== "no_discussion") return null;

  const emailReasonShort = getMeetingEmailReasonShort(m);
  const emailReasonLong = getMeetingEmailReasonLong(m);
  const recipientsCount = meetingEmailRecipientsCount(m);
  const baseChip = (overrides) => ({
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: 3,
    maxWidth: 320,
    ...overrides,
  });

  if (code === "sent" || code === "no_discussion" || m.emailSent) {
    return (
      <span
        style={baseChip({
          background: isLight ? "#F0FDF4" : "rgba(34,197,94,0.12)",
          color: isLight ? "#166534" : "#86EFAC",
          border: isLight ? "1px solid #BBF7D0" : "1px solid rgba(34,197,94,0.35)",
        })}
        title={
          emailReasonLong ||
          `Recap email sent${recipientsCount ? ` to ${recipientsCount} recipient${recipientsCount === 1 ? "" : "s"}` : ""}.`
        }
      >
        {code === "no_discussion" ? "Notice" : "Emailed"}
        {recipientsCount > 0 ? (
          <span style={{ marginLeft: 5, fontWeight: 500, opacity: 0.85 }}>· {recipientsCount}</span>
        ) : null}
      </span>
    );
  }

  if (code === "smtp_queued" || code === "finalizing") {
    return (
      <span
        style={baseChip({
          background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.05)",
          color: isLight ? "#475569" : "rgba(232,240,247,0.75)",
          border: isLight ? "1px solid #D0D7E2" : "1px solid #334155",
        })}
        title={
          emailReasonLong ||
          (code === "finalizing"
            ? "Meeting ended — recap email is being prepared."
            : "Recap email is queued or still processing.")
        }
      >
        Processing
      </span>
    );
  }

  if (code === "no_transcript" || (ended && adminMeetingTranscriptCount(m) === 0 && !code)) {
    return (
      <span
        style={baseChip({
          background: isLight ? "#FAFAF9" : "rgba(251,191,36,0.1)",
          color: isLight ? "#78716C" : "#FCD34D",
          border: isLight ? "1px solid #E7E5E4" : "1px solid rgba(251,191,36,0.35)",
        })}
        title={emailReasonLong || "No transcript captured — recap email not sent."}
      >
        No transcript
      </span>
    );
  }

  if (ended) {
    const label =
      code && EMAIL_STATUS_LABELS[code] && code !== "pending"
        ? EMAIL_STATUS_LABELS[code]
        : emailReasonShort && emailReasonShort !== "Sent"
          ? emailReasonShort
          : "Not emailed";
    return (
      <span
        style={baseChip({
          background: isLight ? "#FEF2F2" : "rgba(185,28,28,0.15)",
          color: isLight ? "#991B1B" : "#FCA5A5",
          border: isLight ? "1px solid #FECACA" : "1px solid rgba(252,165,165,0.35)",
        })}
        title={emailReasonLong || "Recap email was not delivered."}
      >
        {label}
      </span>
    );
  }

  return null;
}

/** Map page period chip → provider chart series (same Today / Week / Month / Overall as top filters). */
function chatProviderChartForPeriod(block, activePeriod) {
  const period = activePeriod === "lifetime" ? "overall" : activePeriod || "overall";
  if (period === "today") {
    return { period: "today", caption: "Today · by hour", series: block?.chartToday || [] };
  }
  if (period === "weekly") {
    return { period: "weekly", caption: "This week · by day", series: block?.chartWeekly || [] };
  }
  if (period === "monthly") {
    return { period: "monthly", caption: "This month · by day", series: block?.chartThisMonth || [] };
  }
  return {
    period: "overall",
    caption: "Overall · by month",
    series: block?.chartOverall || block?.chartMonthly || [],
  };
}

function LlmProviderUsageCard({
  providerKey = "openai",
  title,
  block,
  isLight,
  adminTextMain,
  adminTextMuted,
  adminBorder,
  activePeriod,
  onPeriodSelect,
}) {
  if (!block) return null;
  const theme = CHAT_PROVIDER_THEME[providerKey] || CHAT_PROVIDER_THEME.openai;
  const displayTitle = title || `${theme.label} Usage & Cost`;
  const life = block.lifetime || {};
  const lifeTokens = life.totalTokens ?? (Number(life.inputTokens) || 0) + (Number(life.outputTokens) || 0);
  const rows = [
    { key: "today", label: "Today", data: block.today },
    { key: "weekly", label: "This Week", data: block.weekly },
    { key: "monthly", label: "This Month", data: block.monthly },
    { key: "overall", label: "Overall", data: life },
  ];
  const chartPick = chatProviderChartForPeriod(block, activePeriod);
  const periodKey = chartPick.period;
  const cardBg = isLight ? "#ffffff" : "rgba(255,255,255,0.03)";
  return (
    <div
      className="adm-card cu-provider-card"
      style={{
        "--cu-accent": theme.accent,
        "--cu-gradient": theme.gradient,
        padding: 0,
        marginBottom: 0,
        overflow: "hidden",
        background: cardBg,
        border: `1px solid ${adminBorder}`,
        height: "100%",
      }}
    >
      <div className="cu-provider-accent-bar" />
      <div className="cu-provider-inner">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="cu-provider-icon" style={{ background: theme.accentSoft }}>
            <Svg d={IC.bot} size={20} color={theme.accent} />
          </div>
          <div>
            <div className="cu-provider-title" style={{ color: adminTextMain }}>{displayTitle}</div>
            <div style={{ fontSize: 11.5, color: adminTextMuted, marginTop: 2 }}>
              Lifetime: {formatChatTokens(lifeTokens)} tokens · {formatChatUsd(life.costUsd)}
            </div>
          </div>
        </div>
        <div className="cu-model-pill" style={{ color: adminTextMain }}>
          <span style={{ color: adminTextMuted }}>Model</span>
          <code>{block.model || "—"}</code>
        </div>
      </div>
      <div className="cu-period-grid">
        {rows.map((row) => {
          const d = row.data || {};
          const tok = d.totalTokens ?? (Number(d.inputTokens) || 0) + (Number(d.outputTokens) || 0);
          const isActive = row.key === periodKey;
          return (
            <button
              key={row.key}
              type="button"
              className={`cu-period-stat${isActive ? " cu-period-stat--active" : ""}`}
              onClick={() => onPeriodSelect?.(row.key)}
              title={`Show ${row.label} chart`}
              style={{ cursor: "pointer", font: "inherit", textAlign: "left", width: "100%" }}
            >
              <div className="cu-period-label">{row.label}</div>
              <div className="cu-period-tokens">{formatChatTokens(tok)}</div>
              <div className="cu-period-meta">
                <span className="cu-period-meta-label">Tokens</span>
                <span className="cu-period-cost">{formatChatUsd(d.costUsd)}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="cu-chart-stack-scroll cu-chart-stack-scroll--single" aria-label={`${chartPick.caption} chart`}>
        <div className="cu-chart-stack-title" style={{ color: adminTextMain }}>
          {chartPick.caption}
        </div>
        <div className="cu-chart-panel cu-chart-panel--nested">
          <DualAxisUsageChart
            series={chartPick.series}
            isLight={isLight}
            accent={theme.accent}
            accentCost={providerKey === "anthropic" ? "#22c55e" : "#0ea5e9"}
          />
        </div>
      </div>
      <div className="cu-footnote" style={{ color: adminTextMuted }}>
        Approximate cost from API-reported token usage.
      </div>
      </div>
    </div>
  );
}

const WORKSPACE_CHAT_DEFAULT_MODELS = {
  anthropic: "claude-sonnet-4-6",
  openai: "gpt-4o",
};

const WORKSPACE_CHAT_ROUTER_DEFAULTS = {
  anthropic: "claude-3-5-haiku-20241022",
  openai: "gpt-4o-mini",
};

function workspaceChatProviderCardFromApi(apiProv, summaryProv, id) {
  const meta = apiProv?.apiKeyMeta || summaryProv?.apiKeyMeta || {};
  return {
    enabled: apiProv?.enabled === true || summaryProv?.enabled === true,
    model: String(apiProv?.model || WORKSPACE_CHAT_DEFAULT_MODELS[id] || "").trim(),
    routerModel: String(apiProv?.routerModel || WORKSPACE_CHAT_ROUTER_DEFAULTS[id] || "").trim(),
    apiKeyMeta: meta,
    apiKey: String(summaryProv?.apiKey || "").trim(),
    models: [],
    modelsSource: "none",
    modelsError: null,
  };
}

function workspaceChatFormFromApi(data, summaryData) {
  const summaryForm = summaryData ? summarySettingsFromApi(summaryData) : null;
  const primary = String(data?.primaryProvider || data?.provider || "anthropic").toLowerCase();
  return {
    primaryProvider: primary === "openai" ? "openai" : "anthropic",
    fallbackEnabled: data?.fallbackEnabled !== false,
    routerEnabled: data?.routerEnabled !== false,
    providers: {
      anthropic: workspaceChatProviderCardFromApi(
        data?.providers?.anthropic,
        summaryForm?.providers?.anthropic,
        "anthropic"
      ),
      openai: workspaceChatProviderCardFromApi(
        data?.providers?.openai,
        summaryForm?.providers?.openai,
        "openai"
      ),
    },
    updatedAt: data?.updatedAt || null,
    updatedBy: data?.updatedBy || null,
    keysNote: data?.keysNote || "",
  };
}

function workspaceChatProviderHasKey(p) {
  return summaryProviderHasAdminKey(p);
}

/** List + optional ping on server (up to ~30s); browser abort slightly above that. */
const ADMIN_SUMMARY_TEST_TIMEOUT_MS = 32000;

function summarySettingsTestApiKey(p) {
  const raw = String(p?.apiKey || "").trim();
  if (!raw || raw.includes("***")) return undefined;
  return raw;
}

function formatSummaryProviderTestError(err, { aborted = false } = {}) {
  if (aborted) {
    return "Timed out waiting for MeetAI backend (32s). If the backend was restarting (nodemon), try again. Otherwise click Refresh models — if that works, the API key is fine.";
  }
  const msg = String(err?.message || err || "Test failed").trim();
  const kind = err?.failureKind || err?.code || "";
  if (kind === "auth" || /invalid.*api key|authentication|401|403/i.test(msg)) {
    return `${msg} — Save a valid key (disk icon), or Clear key and paste a new one.`;
  }
  if (kind === "timeout" || /timed out|did not respond/i.test(msg)) {
    return `${msg} — Often caused by VPN/firewall blocking outbound HTTPS from this server, or a slow provider response. Try Refresh models on this card.`;
  }
  if (kind === "network" || /unreachable|network_down|econnrefused/i.test(msg)) {
    return `${msg} — Browser could not reach your MeetAI API (${API.replace(/\/api\/?$/, "")}). Keep \`npm run dev\` running on port 5000.`;
  }
  if (kind === "model") {
    return `${msg} — Click Refresh models and pick a model from the list.`;
  }
  return msg;
}

function AdminProviderTestStatus({ test, onDismiss }) {
  if (test?.ok !== true && test?.ok !== false) return null;
  return (
    <div
      role="status"
      style={{
        marginTop: 10,
        padding: "8px 10px 8px 12px",
        borderRadius: 8,
        fontSize: 12,
        lineHeight: 1.45,
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        background: test.ok ? "rgba(34,197,94,0.1)" : "rgba(220,38,38,0.1)",
        border: test.ok ? "1px solid rgba(34,197,94,0.25)" : "1px solid rgba(220,38,38,0.25)",
        color: test.ok ? "#15803D" : "#DC2626",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
        <strong>{test.ok ? "Connection successful" : "Connection failed"}</strong>
        {test.msg ? ` — ${test.msg}` : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss test message"
        title="Dismiss"
        onClick={onDismiss}
        style={{
          flexShrink: 0,
          border: "none",
          background: "rgba(0,0,0,0.06)",
          cursor: "pointer",
          padding: 4,
          marginTop: 1,
          borderRadius: 6,
          lineHeight: 0,
          color: "inherit",
          opacity: 0.9,
        }}
      >
        <Svg d={IC.close} size={16} stroke={2} color="currentColor" />
      </button>
    </div>
  );
}

/** Admin — workspace chatbot models (Summary-style provider cards). */
function AdminWorkspaceChatAiSettings({
  isLight,
  adminTextMain,
  adminTextMuted,
  adminBorder,
}) {
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const [modelsLoading, setModelsLoading] = useState({});
  const [testState, setTestState] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const testDismissTimersRef = useRef({});
  const formRef = useRef(null);
  const SUMMARY_TEST_MSG_MS = 3 * 60 * 1000;
  const cardBg = isLight ? "#fff" : "rgba(15,23,42,0.55)";
  const hintColor = isLight ? adminTextMuted : "rgba(255,255,255,0.45)";
  const adminChipBg = isLight ? "#f1f5f9" : "rgba(255,255,255,0.06)";

  formRef.current = form;

  function clearTestDismiss(id) {
    const t = testDismissTimersRef.current[id];
    if (t) {
      clearTimeout(t);
      delete testDismissTimersRef.current[id];
    }
  }

  function dismissTestMessage(id) {
    clearTestDismiss(id);
    setTestState((s) => ({ ...s, [id]: { ...(s[id] || {}), ok: null, msg: "" } }));
  }

  function scheduleTestDismiss(id) {
    clearTestDismiss(id);
    testDismissTimersRef.current[id] = setTimeout(() => {
      setTestState((s) => ({ ...s, [id]: { ...(s[id] || {}), ok: null, msg: "" } }));
      delete testDismissTimersRef.current[id];
    }, SUMMARY_TEST_MSG_MS);
  }

  useEffect(() => {
    const timers = testDismissTimersRef.current;
    return () => {
      Object.keys(timers).forEach((k) => {
        clearTimeout(timers[k]);
        delete timers[k];
      });
    };
  }, []);

  const fetchProviderModels = useCallback(async (id, formOverride) => {
    const p = (formOverride || formRef.current)?.providers?.[id];
    if (!p || !workspaceChatProviderHasKey(p)) return;
    setModelsLoading((s) => ({ ...s, [id]: true }));
    try {
      const data = await apiFetch("/admin/summary-settings/models", {
        method: "POST",
        body: {
          provider: id,
          apiKey: String(p.apiKey || "").trim() || undefined,
          model: p.model || undefined,
        },
      });
      if (data?.source !== "api" || !Array.isArray(data?.models) || !data.models.length) {
        setForm((prev) => {
          if (!prev) return prev;
          const cur = prev.providers[id];
          const keep = String(cur?.model || "").trim();
          return {
            ...prev,
            providers: {
              ...prev.providers,
              [id]: {
                ...cur,
                models: keep ? [keep] : [],
                modelsSource: data?.source || "error",
                modelsError: data?.error || "No models returned",
              },
            },
          };
        });
        return;
      }
      setForm((prev) => {
        if (!prev) return prev;
        const cur = prev.providers[id];
        const models = [...data.models];
        const prefer = String(cur?.model || "").trim();
        const nextModel = prefer && models.includes(prefer) ? prefer : models[0] || prefer;
        const preferRouter = String(cur?.routerModel || "").trim();
        const nextRouter =
          preferRouter && models.includes(preferRouter) ? preferRouter : cur?.routerModel || preferRouter;
        return {
          ...prev,
          providers: {
            ...prev.providers,
            [id]: {
              ...cur,
              models,
              model: nextModel,
              routerModel: nextRouter,
              modelsSource: "api",
              modelsError: null,
            },
          },
        };
      });
    } catch (e) {
      setForm((prev) => {
        if (!prev) return prev;
        const cur = prev.providers[id];
        const keep = String(cur?.model || "").trim();
        return {
          ...prev,
          providers: {
            ...prev.providers,
            [id]: {
              ...cur,
              models: keep ? [keep] : [],
              modelsSource: "error",
              modelsError: e.message || "Failed to fetch models",
            },
          },
        };
      });
    } finally {
      setModelsLoading((s) => ({ ...s, [id]: false }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [chatData, summaryData] = await Promise.all([
          apiFetch("/admin/workspace-assistant-settings"),
          apiFetch("/admin/summary-settings"),
        ]);
        if (cancelled) return;
        const next = workspaceChatFormFromApi(chatData, summaryData);
        setForm(next);
        for (const pid of ["anthropic", "openai"]) {
          if (workspaceChatProviderHasKey(next.providers[pid])) {
            fetchProviderModels(pid, next);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load chatbot model settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchProviderModels]);

  function patchProvider(id, patch) {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        providers: {
          ...prev.providers,
          [id]: { ...prev.providers[id], ...patch },
        },
      };
    });
  }

  async function saveSettings(e) {
    e?.preventDefault?.();
    if (!form) return;
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const body = {
        primaryProvider: form.primaryProvider,
        fallbackEnabled: form.fallbackEnabled,
        routerEnabled: form.routerEnabled,
        providers: {
          anthropic: {
            model: form.providers.anthropic.model,
            routerModel: form.providers.anthropic.routerModel,
          },
          openai: {
            model: form.providers.openai.model,
            routerModel: form.providers.openai.routerModel,
          },
        },
      };
      const data = await apiFetch("/admin/workspace-assistant-settings", { method: "PUT", body });
      const summaryData = await apiFetch("/admin/summary-settings");
      setForm(workspaceChatFormFromApi(data, summaryData));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function testProvider(id) {
    clearTestDismiss(id);
    setTestState((s) => ({ ...s, [id]: { loading: true, ok: null, msg: "" } }));
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), ADMIN_SUMMARY_TEST_TIMEOUT_MS);
    try {
      const p = form?.providers?.[id];
      const model = String(p?.model || "").trim();
      const data = await apiFetch("/admin/summary-settings/test", {
        method: "POST",
        signal: controller.signal,
        body: {
          provider: id,
          model,
          apiKey: summarySettingsTestApiKey(p),
        },
      });
      const methodNote =
        data?.testMethod === "models"
          ? " (checked via provider model catalog)"
          : data?.testMethod === "ping"
            ? " (checked via tiny chat request)"
            : "";
      setTestState((s) => ({
        ...s,
        [id]: {
          loading: false,
          ok: true,
          msg:
            (data?.message ||
              "Connection successful. API key valid and model available for workspace chat.") +
            methodNote,
        },
      }));
      scheduleTestDismiss(id);
    } catch (e) {
      const aborted = e?.name === "AbortError" || controller.signal.aborted;
      setTestState((s) => ({
        ...s,
        [id]: {
          loading: false,
          ok: false,
          msg: formatSummaryProviderTestError(e, { aborted }),
        },
      }));
      scheduleTestDismiss(id);
    } finally {
      clearTimeout(abortTimer);
    }
  }

  function chatProviderModelList(p) {
    if (summaryProviderModelsReady(p)) return [...p.models];
    const m = String(p?.model || "").trim();
    const r = String(p?.routerModel || "").trim();
    return [...new Set([m, r].filter(Boolean))];
  }

  if (loading) {
    return (
      <div className="adm-card" style={{ padding: 20, marginBottom: 14, color: hintColor, fontSize: 13 }}>
        Loading chatbot AI settings…
      </div>
    );
  }

  if (!form) {
    return (
      <div className="adm-card" style={{ padding: 20, marginBottom: 14, color: "#FCA5A5", fontSize: 13 }}>
        {err || "Could not load chatbot settings"}
      </div>
    );
  }

  const primaryMeta = SUMMARY_PROVIDER_META[form.primaryProvider] || SUMMARY_PROVIDER_META.anthropic;
  const primaryProv = form.providers[form.primaryProvider];
  const collapsedSummary = primaryProv?.model
    ? `Primary ${primaryMeta.short} · ${primaryProv.model}`
    : `Primary ${primaryMeta.short}`;

  return (
    <form onSubmit={saveSettings} style={{ marginBottom: 14 }}>
      <div className="adm-card" style={{ padding: 0, overflow: "hidden", marginBottom: 0 }}>
        <button
          type="button"
          onClick={() => setSettingsOpen((o) => !o)}
          aria-expanded={settingsOpen}
          style={{
            width: "100%",
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: settingsOpen ? adminChipBg : "transparent",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: isLight ? adminTextMain : "#E8F0F7" }}>
              Chatbot AI models
            </div>
            <div style={{ fontSize: 11.5, color: hintColor, marginTop: 4, lineHeight: 1.45 }}>
              {settingsOpen
                ? "Primary provider, models, and connection tests — keys from Admin → Summary AI."
                : collapsedSummary}
            </div>
          </div>
          <Svg d={settingsOpen ? IC.chevUp : IC.chevDown} size={16} color={adminTextMuted} stroke={2} />
        </button>

        {settingsOpen ? (
          <div style={{ padding: "0 18px 18px", borderTop: `1px solid ${adminBorder}` }}>
            <div style={{ paddingTop: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 6 }}>
                Primary provider
              </div>
              <div style={{ fontSize: 12, color: hintColor, marginBottom: 12, lineHeight: 1.5 }}>
                Used first for workspace chat. API keys are saved in{" "}
                <strong style={{ color: isLight ? adminTextMain : "#E8F0F7" }}>Admin → Summary AI</strong>.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {["anthropic", "openai"].map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`adm-sec-chip${form.primaryProvider === id ? " active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, primaryProvider: id }))}
                  >
                    {SUMMARY_PROVIDER_META[id].short}
                  </button>
                ))}
              </div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 13,
                  color: isLight ? adminTextMain : "#E8F0F7",
                  cursor: "pointer",
                  marginBottom: 10,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.fallbackEnabled !== false}
                  onChange={(e) => setForm((f) => ({ ...f, fallbackEnabled: e.target.checked }))}
                />
                Allow fallback to the other enabled provider if primary fails
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 13,
                  color: isLight ? adminTextMain : "#E8F0F7",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.routerEnabled !== false}
                  onChange={(e) => setForm((f) => ({ ...f, routerEnabled: e.target.checked }))}
                />
                Enable fast router for hi / small talk (uses router model below)
              </label>
              {form.keysNote ? (
                <div className="adm-form-hint" style={{ marginTop: 12 }}>
                  {form.keysNote}
                </div>
              ) : null}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: 14,
                alignItems: "start",
              }}
            >
        {["anthropic", "openai", "gemini"].map((id) => {
          if (id === "gemini") {
            const meta = SUMMARY_PROVIDER_META.gemini;
            return (
              <div key={id} className="adm-card" style={{ padding: 18, background: cardBg, opacity: 0.85 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7" }}>
                  {meta.label}
                </div>
                <div style={{ fontSize: 11, color: hintColor, marginTop: 4 }}>
                  Google Gemini API
                </div>
                <div className="adm-form-hint" style={{ marginTop: 14, lineHeight: 1.5 }}>
                  Workspace chat supports Claude and OpenAI only. Gemini remains available for meeting summaries in Summary AI.
                </div>
              </div>
            );
          }

          const p = form.providers[id];
          const meta = SUMMARY_PROVIDER_META[id];
          const models = chatProviderModelList(p);
          const modelsBusy = modelsLoading[id] === true;
          const isPrimary = form.primaryProvider === id;
          const keyMeta = p.apiKeyMeta;
          const test = testState[id];
          const showModels = workspaceChatProviderHasKey(p);

          return (
            <div
              key={id}
              className="adm-card"
              style={{
                padding: 18,
                background: cardBg,
                border: isPrimary ? "1px solid rgba(37,99,235,0.35)" : undefined,
                overflow: "visible",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7" }}>
                    {meta.label}
                    {isPrimary ? (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#2563EB",
                          textTransform: "uppercase",
                        }}
                      >
                        Primary
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 11, color: hintColor, marginTop: 4 }}>
                    {id === "anthropic" ? "Anthropic Claude API" : "OpenAI Chat Completions"}
                  </div>
                </div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    color: hintColor,
                    flexShrink: 0,
                  }}
                  title="Enable provider in Admin → Summary AI"
                >
                  <input type="checkbox" checked={p.enabled === true} readOnly disabled />
                  On
                </label>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="adm-form-label">API key</label>
                {keyMeta?.configured ? (
                  <div className="adm-form-hint" style={{ color: keyMeta.readableOnServer === false ? "#B45309" : "#15803D" }}>
                    {keyMeta.readableOnServer === false
                      ? "Key stored but unreadable on this server — re-save in Summary AI."
                      : `Stored in database (Summary AI)${keyMeta.hint ? ` · ${keyMeta.hint}` : ""}.`}
                  </div>
                ) : (
                  <div className="adm-form-hint" style={{ color: "#B45309" }}>
                    No key saved. Open Summary AI, paste your API key, and save.
                  </div>
                )}
              </div>

              {showModels ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <label className="adm-form-label" style={{ margin: 0 }}>
                        Main model (tools + answers)
                      </label>
                      <button
                        type="button"
                        className="adm-ghost-btn"
                        style={{ padding: "2px 8px", fontSize: 11 }}
                        disabled={modelsBusy}
                        onClick={() => fetchProviderModels(id)}
                      >
                        {modelsBusy ? "Loading…" : "Refresh"}
                      </button>
                    </div>
                    {modelsBusy && !models.length ? (
                      <div className="adm-form-hint">Fetching models…</div>
                    ) : (
                      <>
                        <SummaryModelPicker
                          provider={id}
                          models={models.length ? models : [p.model].filter(Boolean)}
                          value={p.model}
                          onChange={(m) => patchProvider(id, { model: m })}
                          isLight={isLight}
                          adminTextMain={adminTextMain}
                          adminTextMuted={adminTextMuted}
                          adminBorder={adminBorder}
                        />
                        <div className="adm-form-hint" style={{ marginTop: 6 }}>
                          {summaryProviderModelsReady(p)
                            ? `${models.length} model${models.length === 1 ? "" : "s"} from your API key`
                            : "Saved model shown — click Refresh to load the full list."}
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label className="adm-form-label">Fast router model</label>
                    <SummaryModelPicker
                      provider={id}
                      models={models.length ? models : [p.routerModel].filter(Boolean)}
                      value={p.routerModel}
                      onChange={(m) => patchProvider(id, { routerModel: m })}
                      isLight={isLight}
                      adminTextMain={adminTextMain}
                      adminTextMuted={adminTextMuted}
                      adminBorder={adminBorder}
                    />
                  </div>
                </>
              ) : (
                <div className="adm-form-hint" style={{ marginBottom: 12 }}>
                  Save an API key in Summary AI to load models here.
                </div>
              )}

              <button
                type="button"
                className="adm-ghost-btn"
                disabled={!p.enabled || !showModels || test?.loading}
                onClick={() => testProvider(id)}
              >
                {test?.loading ? `Testing ${meta.label}…` : "Test connection"}
              </button>
              <AdminProviderTestStatus test={test} onDismiss={() => dismissTestMessage(id)} />
            </div>
          );
        })}
            </div>

            {err ? (
              <div
                style={{
                  marginTop: 14,
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: "rgba(220,38,38,0.12)",
                  color: "#DC2626",
                  fontSize: 12.5,
                }}
              >
                {err}
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
              <button type="submit" className="adm-primary-btn" disabled={saving}>
                {saving ? "Saving…" : saved ? "Saved!" : "Save chatbot settings"}
              </button>
              {form.updatedAt ? (
                <span style={{ fontSize: 11.5, color: hintColor, alignSelf: "center" }}>
                  Updated {new Date(form.updatedAt).toLocaleString()}
                  {form.updatedBy ? ` · ${form.updatedBy}` : ""}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </form>
  );
}

function formatChatDrawerTimeOnly(iso) {
  if (!iso) return "";
  try {
    return formatTimeInAppTz(iso, { hour12: true }).replace(/\s/g, " ").toLowerCase();
  } catch (_) {
    return "";
  }
}

function formatChatDrawerLastAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: APP_DISPLAY_TZ,
    });
  } catch (_) {
    return "";
  }
}

function chatLogYmdFromIso(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return ymdInAppTz(d);
  } catch (_) {
    return "";
  }
}

/** Section heading for admin chat log — Today, Yesterday, or calendar date (app TZ). */
function formatChatLogDayHeading(iso) {
  const ymd = chatLogYmdFromIso(iso);
  if (!ymd) return "Unknown date";
  const todayYmd = ymdInAppTz(new Date());
  if (ymd === todayYmd) return "Today";
  const todayDt = ymdToDate(todayYmd);
  const dayDt = ymdToDate(ymd);
  if (todayDt && dayDt) {
    const dayMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.round((todayDt.getTime() - dayDt.getTime()) / dayMs);
    if (diffDays === 1) return "Yesterday";
  }
  try {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) return formatDateInAppTz(d);
  } catch (_) {
    /* fall through */
  }
  return ymd;
}

function groupAdminChatLogByDay(items) {
  const list = Array.isArray(items) ? items : [];
  const groups = [];
  let currentYmd = "";
  for (const row of list) {
    const ymd = chatLogYmdFromIso(row?.at) || "unknown";
    if (ymd !== currentYmd) {
      currentYmd = ymd;
      groups.push({ ymd, label: formatChatLogDayHeading(row?.at), items: [] });
    }
    groups[groups.length - 1].items.push(row);
  }
  return groups;
}

function chatAnswerPreviewLine(text, maxLen = 140) {
  const t = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen).trim()}…`;
}

/** Plain preview for collapsed admin chat cards (no raw [[meet:…]] tokens). */
function adminChatAnswerPreview(text, maxLen = 160) {
  let s = repairAssistantMeetTokens(String(text || ""));
  s = s.replace(/\[\[meet:[^\]|]+\|([^\]]+)\]\]/g, "$1");
  s = s.replace(/\[\[meet:([^\]]+)\]\]/g, "Meeting");
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/__([^_]+)__/g, "$1");
  s = s.replace(/`([^`]+)`/g, "$1");
  s = s.replace(/^#{1,6}\s+/gm, "");
  return chatAnswerPreviewLine(s, maxLen);
}

/** One Q&A turn in admin chat drawer — user on the right, assistant (LLM or Rule) on the left. */
function AdminChatDrawerExchange({
  row,
  userLabel,
  isLight,
  adminBorder,
  adminTextMuted,
  adminTextSoft,
}) {
  const [answerOpen, setAnswerOpen] = useState(false);
  const atTime = formatChatDrawerTimeOnly(row.at);
  const qRaw = String(row.question || "").trim();
  const q = qRaw || "(no query recorded)";
  const a = String(row.answerPreview || "").trim();
  const hasAnswer = Boolean(a) || row.ruled;
  const preview = a
    ? adminChatAnswerPreview(a)
    : row.ruled
      ? "Rule-based reply (no text stored for this entry)…"
      : "";
  const tok = (row.inputTokens || 0) + (row.outputTokens || 0);
  const typeLabel = row.ruled ? "Rule" : String(row.provider || "LLM").toUpperCase();
  const modelLabel = row.ruled ? "—" : row.model || "—";
  const assistantTitle = row.ruled ? "Rule-based answer" : "AI answer";

  return (
    <div className="cu-chat-exchange">
      <div className="cu-chat-user-block">
        <div className="cu-chat-user-label" style={{ color: adminTextMuted }}>
          <span className="cu-chat-user-name">{userLabel}</span>
          {atTime ? <span className="cu-chat-user-time">{atTime}</span> : null}
        </div>
        <div className="cu-chat-user-row">
          <div className="cu-chat-user-bubble">{q}</div>
          <div className="cu-chat-user-avatar" aria-hidden>
            <Svg d={IC.user} size={16} stroke={1.8} color="#fff" />
          </div>
        </div>
      </div>

      {hasAnswer ? (
        <div className="cu-chat-ai-row">
          <div className="cu-chat-ai-avatar" aria-hidden title="MeetAI assistant">
            <Svg d={IC.chat} size={15} stroke={2} color="#fff" />
          </div>
          <div
            className="cu-chat-ai-bubble"
            style={{
              borderColor: adminBorder,
              background: isLight ? "#ffffff" : "rgba(15,23,42,0.55)",
              color: isLight ? "#0f172a" : "#E8F0F7",
            }}
          >
            <button
              type="button"
              className="cu-chat-ai-bubble-toggle"
              aria-expanded={answerOpen}
              onClick={() => setAnswerOpen((o) => !o)}
              style={{ color: isLight ? "#0f172a" : "#E8F0F7" }}
            >
              <div className="cu-chat-ai-bubble-head">
                <span className="cu-chat-ai-bubble-title">
                  {answerOpen ? assistantTitle : `${assistantTitle} · tap to expand`}
                </span>
                <span className="cu-chat-ai-meta-chips">
                  <span className={`cu-chat-meta-chip${row.ruled ? " cu-chat-meta-chip--rule" : ""}`}>{typeLabel}</span>
                  {!row.ruled && tok > 0 ? (
                    <span className="cu-chat-meta-chip cu-chat-meta-chip--muted">{formatChatTokens(tok)} tok</span>
                  ) : null}
                </span>
                <Svg d={answerOpen ? IC.chevUp : IC.chevDown} size={14} color={adminTextMuted} stroke={2} />
              </div>
            </button>
            {answerOpen ? (
              <>
                <div className="cu-chat-ai-meta-line" style={{ color: adminTextMuted }}>
                  {modelLabel !== "—" ? <span>{modelLabel}</span> : null}
                  {modelLabel !== "—" && atTime ? <span className="cu-chat-meta-dot">·</span> : null}
                  {atTime ? <span>{atTime}</span> : null}
                  {!row.ruled ? (
                    <>
                      <span className="cu-chat-meta-dot">·</span>
                      <span>{formatChatUsd(row.costUsd)}</span>
                    </>
                  ) : null}
                </div>
                {a ? (
                  <div className="cu-chat-ai-body-text cu-chat-ai-body-text--rich">
                    <AssistantChatMessageBody text={a} onOpenMeeting={undefined} />
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: adminTextSoft, fontStyle: "italic", padding: "0 2px 4px" }}>
                    Rule-based reply — answer text was not stored for this older log entry.
                  </div>
                )}
              </>
            ) : preview ? (
              <div className="cu-chat-ai-preview" style={{ color: adminTextSoft }}>
                {preview}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="cu-chat-ai-row">
          <div className="cu-chat-ai-avatar" aria-hidden title="MeetAI assistant">
            <Svg d={IC.chat} size={15} stroke={2} color="#fff" />
          </div>
          <div className="cu-chat-ai-legacy" style={{ color: adminTextSoft, borderColor: adminBorder }}>
            Answer not stored (legacy log) · {formatChatTokens(tok)} tok · {formatChatUsd(row.costUsd)}
          </div>
        </div>
      )}
    </div>
  );
}


function chatUsageUserLastUsedMs(u) {
  const at = u?.recent?.[0]?.at;
  if (!at) return 0;
  const ms = new Date(at).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

function parseChatUsageUserSort(sortKey) {
  const raw = String(sortKey || "cost-desc");
  const [field, dir] = raw.split("-");
  const normalizedField = field === "tokens" ? "tokens" : field === "recent" ? "recent" : "cost";
  const normalizedDir = dir === "asc" ? "asc" : "desc";
  return { field: normalizedField, dir: normalizedDir };
}

function sortChatUsageUsers(list, sortKey, periodKey = "overall") {
  const arr = [...(Array.isArray(list) ? list : [])];
  const period = periodKey === "overall" ? "overall" : periodKey;
  const { field, dir } = parseChatUsageUserSort(sortKey);
  arr.sort((a, b) => {
    const ra = a?.ranges?.[period] || a?.ranges?.overall || {};
    const rb = b?.ranges?.[period] || b?.ranges?.overall || {};
    let diff = 0;
    if (field === "recent") {
      diff = chatUsageUserLastUsedMs(b) - chatUsageUserLastUsedMs(a);
    } else if (field === "tokens") {
      const ta = (ra.inputTokens || 0) + (ra.outputTokens || 0);
      const tb = (rb.inputTokens || 0) + (rb.outputTokens || 0);
      diff = tb - ta;
    } else {
      diff = (rb.costUsd || 0) - (ra.costUsd || 0);
    }
    return dir === "asc" ? -diff : diff;
  });
  return arr;
}

function chatUsageUserMatchesProvider(u, providerFilter) {
  if (!providerFilter || providerFilter === "all") return true;
  const recent = Array.isArray(u?.recent) ? u.recent : [];
  return recent.some((r) => r?.provider === providerFilter);
}

/** When “Recent used” filter is on — keep users with chatbot activity in the selected KPI period. */
function chatUsageUserMatchesRecentUsed(u, kpiPeriod, recentUsedOnly) {
  if (!recentUsedOnly) return true;
  const period = kpiPeriod === "overall" ? "overall" : kpiPeriod;
  return (u?.ranges?.[period]?.requests || 0) > 0;
}

const ADMIN_CHATBOT_RECENT_PAGE_SIZE_DEFAULT = 20;
const ADMIN_CHATBOT_RECENT_PAGE_SIZE_OPTIONS = [20, 50, 100];
function AdminChatbotUsagePanel({
  report,
  isLight,
  adminTextMain,
  adminTextMuted,
  adminTextSoft,
  adminBorder,
  adminChipBg,
  approvalRows = [],
  adminPanelBg,
}) {
  const [drawerUserEmail, setDrawerUserEmail] = useState(null);
  const [drawerTab, setDrawerTab] = useState("usage");
  const [drawerChatPage, setDrawerChatPage] = useState(1);
  const [drawerChatPageSize, setDrawerChatPageSize] = useState(ADMIN_CHATBOT_RECENT_PAGE_SIZE_DEFAULT);
  const [drawerChatLog, setDrawerChatLog] = useState({
    items: [],
    total: 0,
    sessionCount: 0,
    loading: false,
    error: null,
  });
  const drawerChatScrollRef = useRef(null);
  /** Date-range filter driving KPI cards + provider charts — Today / This Week / This Month / Overall. */
  const [kpiPeriod, setKpiPeriod] = useState("overall");
  /** Provider filter — narrows both which provider card shows and the user table below. */
  const [providerFilter, setProviderFilter] = useState("all");
  /** Only users with at least one request in the selected KPI period (Today / week / month / overall). */
  const [recentUsedFilter, setRecentUsedFilter] = useState(false);
  const [userListSearch, setUserListSearch] = useState("");
  const deferredUserListSearch = useDeferredValue(userListSearch);
  const [userSort, setUserSort] = useState("cost-desc");

  const allUsers = Array.isArray(report?.users) ? report.users : [];
  const users = useMemo(() => {
    let list = allUsers.filter((u) => {
      if (!adminSearchMatches(userListSearch, chatUsageUserSearchParts(u))) return false;
      if (!chatUsageUserMatchesProvider(u, providerFilter)) return false;
      if (!chatUsageUserMatchesRecentUsed(u, kpiPeriod, recentUsedFilter)) return false;
      return true;
    });
    const sortField = parseChatUsageUserSort(userSort).field;
    const sortPeriod = sortField === "cost" ? "overall" : kpiPeriod;
    list = sortChatUsageUsers(list, userSort, sortPeriod);
    return list;
  }, [
    allUsers,
    userListSearch,
    providerFilter,
    recentUsedFilter,
    kpiPeriod,
    userSort,
  ]);

  const drawerUser = useMemo(
    () => allUsers.find((u) => String(u.email || "") === String(drawerUserEmail || "")) || null,
    [allUsers, drawerUserEmail]
  );

  useEffect(() => {
    setDrawerChatPage(1);
    setDrawerTab("usage");
  }, [drawerUserEmail]);

  const loadDrawerChatLog = useCallback(
    (opts = {}) => {
      const silent = opts.silent === true;
      if (!drawerUserEmail) return undefined;
      let cancelled = false;
      if (!silent) setDrawerChatLog((s) => ({ ...s, loading: true, error: null }));
      const q = new URLSearchParams({
        email: drawerUserEmail,
        page: String(drawerChatPage),
        pageSize: String(drawerChatPageSize),
      });
      apiFetch(`/admin/assistant-usage/chats?${q}`)
        .then((data) => {
          if (cancelled) return;
          setDrawerChatLog({
            items: Array.isArray(data?.items) ? data.items : [],
            total: Number(data?.total) || 0,
            sessionCount: Number(data?.sessionCount) || 0,
            loading: false,
            error: null,
          });
        })
        .catch((e) => {
          if (cancelled) return;
          setDrawerChatLog({
            items: [],
            total: 0,
            sessionCount: 0,
            loading: false,
            error: e?.message || "Failed to load chat",
          });
        });
      return () => {
        cancelled = true;
      };
    },
    [drawerUserEmail, drawerChatPage, drawerChatPageSize]
  );

  useEffect(() => {
    if (!drawerUserEmail || drawerTab !== "chat") return undefined;
    const cancel = loadDrawerChatLog({ silent: false });
    const poll = setInterval(() => loadDrawerChatLog({ silent: true }), 20000);
    return () => {
      clearInterval(poll);
      cancel?.();
    };
  }, [drawerUserEmail, drawerTab, drawerChatPage, drawerChatPageSize, loadDrawerChatLog]);

  /** Newest API rows first — show oldest→newest top→bottom like a chat thread. */
  const drawerChatItemsChronological = useMemo(() => {
    const items = Array.isArray(drawerChatLog.items) ? drawerChatLog.items : [];
    return items.length > 1 ? [...items].reverse() : items;
  }, [drawerChatLog.items]);

  const drawerChatDayGroups = useMemo(
    () => groupAdminChatLogByDay(drawerChatItemsChronological),
    [drawerChatItemsChronological]
  );

  const drawerChatLastAt = useMemo(() => {
    const items = drawerChatLog.items;
    if (!items?.length) return null;
    return items[0]?.at || null;
  }, [drawerChatLog.items]);

  useEffect(() => {
    if (drawerTab !== "chat" || drawerChatLog.loading) return undefined;
    const el = drawerChatScrollRef.current;
    if (!el) return undefined;
    const t = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(t);
  }, [drawerTab, drawerChatDayGroups, drawerChatLog.loading]);

  const openai = report?.providers?.openai;
  const anthropic = report?.providers?.anthropic;
  const showOpenai = (openai?.hasData || Number(openai?.lifetime?.requests) > 0) && providerFilter !== "anthropic";
  const showAnthropic = (anthropic?.hasData || Number(anthropic?.lifetime?.requests) > 0) && providerFilter !== "openai";

  function openUserDrawer(email) {
    setDrawerUserEmail(email);
  }

  function closeUserDrawer() {
    setDrawerUserEmail(null);
  }

  // combinedLlm keys today/weekly/monthly/lifetime; the KPI period selector uses "overall"
  // to mean lifetime, so map that one name difference and read the rest directly.
  // When a provider filter is active the KPIs read that provider's own period buckets
  // so the cards stay consistent with the provider section and user table below.
  const kpiRangeKey = kpiPeriod === "overall" ? "lifetime" : kpiPeriod;
  const kpiProviderBlock =
    providerFilter === "openai" ? openai : providerFilter === "anthropic" ? anthropic : null;
  const kpiProviderBucket = kpiProviderBlock ? kpiProviderBlock[kpiRangeKey] || {} : null;
  const llmPeriod = kpiProviderBucket || report?.combinedLlm?.[kpiRangeKey] || {};
  const llmTok = (llmPeriod.inputTokens || 0) + (llmPeriod.outputTokens || 0);
  // report.overall[period] is every request (LLM + rule-based) for that period, with the
  // same llmRequests/ruledRequests split addToBucket already tracks.
  const overallPeriod = kpiProviderBucket || report?.overall?.[kpiPeriod] || {};
  const totalReq = overallPeriod.requests ?? 0;
  const activeUsersForPeriod = users.filter((u) => (u.ranges?.[kpiPeriod]?.requests || 0) > 0).length;
  const kpiPeriodLabel =
    kpiPeriod === "today"
      ? "Today"
      : kpiPeriod === "weekly"
        ? "This week"
        : kpiPeriod === "monthly"
          ? "This month"
          : "Overall";
  const kpiScopeLabel =
    providerFilter === "openai" ? "OpenAI only" : providerFilter === "anthropic" ? "Anthropic only" : "All providers";

  return (
    <div className="cu-page">
      <AdminPremiumHero
        badge="Avaada · Chatbot"
        badgeIconD={IC.chat}
        title="Workspace assistant usage"
        desc={report?.pricingNote || "Token and cost tracking for the secured workspace chatbot."}
        isLight={isLight}
        meta={
          <>
            <span>{report?.llmRequestCount ?? 0} LLM calls</span>
            <span className="adm-hero-dot" />
            <span>{report?.ruleRequestCount ?? 0} rule-based ($0)</span>
            <span className="adm-hero-dot" />
            <span>TZ {report?.timezone || "—"}</span>
          </>
        }
      />

      <AdminWorkspaceChatAiSettings
        isLight={isLight}
        adminTextMain={adminTextMain}
        adminTextMuted={adminTextMuted}
        adminBorder={adminBorder}
      />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { id: "today", label: "Today" },
            { id: "weekly", label: "This Week" },
            { id: "monthly", label: "This Month" },
            { id: "overall", label: "Overall" },
          ].map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setKpiPeriod(p.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: kpiPeriod === p.id ? `1px solid ${isLight ? "#1d4ed8" : "#93C5FD"}` : `1px solid ${adminBorder}`,
                background: kpiPeriod === p.id ? (isLight ? "rgba(37,99,235,0.16)" : "rgba(37,99,235,0.22)") : adminChipBg,
                color: kpiPeriod === p.id ? (isLight ? "#1d4ed8" : "#BFDBFE") : adminTextMuted,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 11.5, color: adminTextMuted, fontWeight: 600 }}>Filter</span>
            <button
              type="button"
              onClick={() => setRecentUsedFilter((on) => !on)}
              title={`Show only users with chatbot activity in the selected period (${kpiPeriodLabel.toLowerCase()})`}
              style={{
                padding: "5px 11px",
                borderRadius: 999,
                border: recentUsedFilter ? `1px solid ${isLight ? "#0f766e" : "#5eead4"}` : `1px solid ${adminBorder}`,
                background: recentUsedFilter ? (isLight ? "rgba(15,118,110,0.14)" : "rgba(45,212,191,0.18)") : adminChipBg,
                color: recentUsedFilter ? (isLight ? "#0f766e" : "#99f6e4") : adminTextMuted,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Recent used
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 11.5, color: adminTextMuted, fontWeight: 600 }}>Provider</span>
            {[
              { id: "all", label: "All" },
              { id: "openai", label: "OpenAI" },
              { id: "anthropic", label: "Anthropic" },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setProviderFilter(p.id)}
                style={{
                  padding: "5px 11px",
                  borderRadius: 999,
                  border: providerFilter === p.id ? `1px solid ${isLight ? "#1d4ed8" : "#93C5FD"}` : `1px solid ${adminBorder}`,
                  background: providerFilter === p.id ? (isLight ? "rgba(37,99,235,0.16)" : "rgba(37,99,235,0.22)") : adminChipBg,
                  color: providerFilter === p.id ? (isLight ? "#1d4ed8" : "#BFDBFE") : adminTextMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="adm-kpi-grid admin-overview-stat-grid">
        <ChatUsageKpi
          label="Total requests"
          value={totalReq.toLocaleString("en-IN")}
          sub={`${overallPeriod.llmRequests ?? 0} LLM · ${overallPeriod.ruledRequests ?? 0} rule · ${kpiPeriodLabel}`}
          accent={ADM.teal}
          tone="teal"
          iconD={IC.chat}
          tip={`Every chatbot request in the selected period (${kpiPeriodLabel.toLowerCase()}, ${kpiScopeLabel.toLowerCase()}), including free rule-based answers.`}
        />
        <ChatUsageKpi
          label="LLM tokens"
          value={formatChatTokens(llmTok)}
          sub={`In ${formatChatTokens(llmPeriod.inputTokens)} · Out ${formatChatTokens(llmPeriod.outputTokens)}`}
          accent={ADM.green}
          tone="green"
          iconD={IC.zap}
          tip={`Input + output tokens consumed by ${kpiScopeLabel.toLowerCase()} LLM calls in the selected period (${kpiPeriodLabel.toLowerCase()}).`}
        />
        <ChatUsageKpi
          label="LLM cost"
          value={formatChatUsd(llmPeriod.costUsd)}
          sub={`${kpiScopeLabel} · ${kpiPeriodLabel}`}
          accent={ADM.amber}
          tone="amber"
          iconD={IC.task}
          tip={`Approximate spend from API-reported token usage in the selected period (${kpiPeriodLabel.toLowerCase()}). Rule-based answers cost $0.`}
        />
        <ChatUsageKpi
          label="Active users"
          value={String(activeUsersForPeriod)}
          sub={`With chat history · ${kpiPeriodLabel}`}
          accent={ADM.violet}
          tone="violet"
          iconD={IC.users}
          tip={`Users with at least one chatbot request in the selected period (${kpiPeriodLabel.toLowerCase()}), matching the current search and provider filter.`}
        />
      </div>

      {(showOpenai || showAnthropic) ? (
        <div
          className="adm-side-grid"
          style={{
            display: "grid",
            gridTemplateColumns: showOpenai && showAnthropic ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
            gap: 12,
            marginBottom: 16,
            alignItems: "stretch",
          }}
        >
          {showOpenai ? (
            <LlmProviderUsageCard
              providerKey="openai"
              block={openai}
              isLight={isLight}
              adminTextMain={adminTextMain}
              adminTextMuted={adminTextMuted}
              adminBorder={adminBorder}
              activePeriod={kpiPeriod}
              onPeriodSelect={setKpiPeriod}
            />
          ) : null}
          {showAnthropic ? (
            <LlmProviderUsageCard
              providerKey="anthropic"
              block={anthropic}
              isLight={isLight}
              adminTextMain={adminTextMain}
              adminTextMuted={adminTextMuted}
              adminBorder={adminBorder}
              activePeriod={kpiPeriod}
              onPeriodSelect={setKpiPeriod}
            />
          ) : null}
        </div>
      ) : null}

      {!showOpenai && !showAnthropic ? (
        <div className="adm-card" style={{ padding: 24, marginBottom: 16, textAlign: "center", color: adminTextMuted, fontSize: 13 }}>
          No OpenAI or Anthropic usage logged yet. Ask the chatbot a free-form question (not “meetings today” / “how to instant join”) to trigger an LLM call.
        </div>
      ) : null}

      {report?.rowsTruncated ? (
        <div
          className="adm-card"
          style={{
            padding: "10px 14px",
            marginBottom: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: isLight ? "#92400e" : "#FCD34D",
            background: isLight ? "#FFFBEB" : "rgba(245,158,11,0.12)",
            border: `1px solid ${isLight ? "#FDE68A" : "rgba(245,158,11,0.35)"}`,
          }}
        >
          Usage report is capped at the newest {Number(report?.totalRows || 0).toLocaleString("en-IN")} logged requests.
          Older history is omitted from totals — use search and filters below, or ask ops to export from the database for full audit.
        </div>
      ) : null}

      <div className="adm-card cu-users-card adm-card--flush" style={{ padding: 0, overflow: "hidden" }}>
        <div className="adm-card-header adm-card-header--search cu-users-head">
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <div className="cu-users-title" style={{ color: adminTextMain }}>Usage by user</div>
            <div className="cu-users-sub adm-card-sub" style={{ color: adminTextMuted, marginTop: 2 }}>
              Search below — click a row to open usage details in the side drawer
            </div>
          </div>
          <div className="cu-users-search-sort">
            <AdminSearchToolbar
              alwaysExpanded
              value={userListSearch}
              onChange={setUserListSearch}
              placeholder="Search name, email, model, provider…"
              adminTextMuted={adminTextMuted}
              filteredCount={users.length}
              totalCount={allUsers.length}
            />
            <label className="cu-users-sort" style={{ color: adminTextMuted }}>
              <span className="cu-users-sort-label">Sort</span>
              <select
                className="cu-users-sort-select"
                value={userSort}
                onChange={(e) => setUserSort(e.target.value)}
                aria-label="Sort users"
                style={{
                  borderColor: adminBorder,
                  background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                  color: adminTextMain,
                }}
              >
                <option value="recent-desc">Recent used · newest first</option>
                <option value="recent-asc">Recent used · oldest first</option>
                <option value="cost-desc">Cost · high to low (overall)</option>
                <option value="cost-asc">Cost · low to high (overall)</option>
                <option value="tokens-desc">Tokens · high to low ({kpiPeriodLabel})</option>
                <option value="tokens-asc">Tokens · low to high ({kpiPeriodLabel})</option>
              </select>
            </label>
          </div>
        </div>
        {deferredUserListSearch.trim() || providerFilter !== "all" || recentUsedFilter ? (
          <div
            className="cu-users-toolbar"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
              padding: "8px 16px",
              borderBottom: `1px solid ${adminBorder}`,
              background: isLight ? "#f8fafc" : adminPanelBg,
            }}
          >
            {recentUsedFilter ? (
              <span style={{ fontSize: 11.5, color: adminTextMuted }}>
                Showing users active in <strong style={{ color: adminTextMain }}>{kpiPeriodLabel.toLowerCase()}</strong>
              </span>
            ) : null}
            <button
              type="button"
              className="adm-ghost-btn"
              style={{ padding: "6px 12px", fontSize: 12, marginLeft: "auto" }}
              onClick={() => {
                setUserListSearch("");
                setProviderFilter("all");
                setRecentUsedFilter(false);
              }}
            >
              Clear filters
            </button>
          </div>
        ) : null}
        {allUsers.length === 0 ? (
          <div className="cu-empty" style={{ color: adminTextMuted }}>No chatbot requests yet.</div>
        ) : users.length === 0 ? (
          <div className="cu-empty" style={{ color: adminTextMuted }}>No users match your search or filters.</div>
        ) : (
          <AdminDataGrid
            rows={users}
            isLight={isLight}
            height={520}
            pageSize={20}
            pageSizeOptions={[20, 50, 100]}
            getRowId={(row) => row.email}
            onRowClick={(params) => openUserDrawer(params.row.email)}
            columns={[
              {
                field: "user",
                headerName: "User",
                minWidth: 240,
                flex: 1.4,
                sortable: false,
                renderCell: ({ row }) => (
                  <div className="cu-users-table-user">
                    <span className="cu-user-avatar cu-user-avatar--sm">{chatUserInitials(row.name, row.email)}</span>
                    <span className="cu-users-table-user-text">
                      <span className="cu-user-name">{row.name || row.email}</span>
                      <span className="cu-user-email" style={{ color: adminTextMuted }}>{row.email}</span>
                    </span>
                  </div>
                ),
              },
              { field: "last_used", headerName: "Last used", minWidth: 145, flex: .8, valueGetter: (_value, row) => formatChatRecentAt(row.recent?.[0]?.at) || "—" },
              { field: "requests", headerName: "Requests", type: "number", width: 100, valueGetter: (_value, row) => row.ranges?.overall?.requests ?? 0 },
              { field: "tokens", headerName: "Tokens", minWidth: 105, flex: .65, valueGetter: (_value, row) => formatChatTokens((row.ranges?.overall?.inputTokens || 0) + (row.ranges?.overall?.outputTokens || 0)) },
              { field: "cost", headerName: "Cost (USD)", minWidth: 115, flex: .7, valueGetter: (_value, row) => formatChatUsd(row.ranges?.overall?.costUsd) },
              { field: "open", headerName: "", width: 42, sortable: false, renderCell: () => <Svg d={IC.chevR} size={14} color={adminTextMuted} /> },
            ]}
          />
        )}
      </div>

      <AdminDetailDrawer
        open={!!drawerUser}
        onClose={closeUserDrawer}
        isLight={isLight}
        width={drawerTab === "chat" ? 560 : 520}
        title={drawerUser?.name || drawerUser?.email || "User usage"}
        subtitle={drawerUser?.email || ""}
      >
        {drawerUser ? (
          <>
            <div className="cu-drawer-tabs" style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { id: "usage", label: "Usage" },
                { id: "chat", label: "Chat" },
              ].map((tab) => {
                const active = drawerTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`cu-drawer-tab${active ? " cu-drawer-tab--active" : ""}`}
                    onClick={() => setDrawerTab(tab.id)}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: active
                        ? `1.5px solid ${isLight ? "#1d4ed8" : "#93C5FD"}`
                        : `1.5px solid ${isLight ? "#cbd5e1" : "#334155"}`,
                      background: active
                        ? (isLight ? "#eff6ff" : "rgba(37,99,235,0.22)")
                        : (isLight ? "#ffffff" : "rgba(255,255,255,0.06)"),
                      color: active
                        ? (isLight ? "#1d4ed8" : "#BFDBFE")
                        : (isLight ? "#334155" : "#E8F0F7"),
                      fontSize: 12.5,
                      fontWeight: active ? 700 : 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {drawerTab === "usage" ? (
              <>
            <div className="stat-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8, marginBottom: 14, fontSize: 11 }}>
              {["today", "weekly", "monthly", "overall"].map((rk) => {
                const b = drawerUser.ranges?.[rk] || {};
                const tok = (b.inputTokens || 0) + (b.outputTokens || 0);
                const isActive = rk === kpiPeriod;
                const periodLabel =
                  rk === "today"
                    ? "Today"
                    : rk === "weekly"
                      ? "This week"
                      : rk === "monthly"
                        ? "This month"
                        : "Overall";
                return (
                  <div
                    key={rk}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: `1px solid ${isActive ? (isLight ? "#1d4ed8" : "#93C5FD") : adminBorder}`,
                      background: isActive
                        ? (isLight ? "rgba(37,99,235,0.08)" : "rgba(37,99,235,0.14)")
                        : (isLight ? "#f8fafc" : "rgba(255,255,255,0.04)"),
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 10, textTransform: "uppercase", color: adminTextMuted, marginBottom: 6 }}>
                      {periodLabel}
                    </div>
                    <div style={{ color: adminTextMain, fontWeight: 600 }}>{b.requests ?? 0} req · {formatChatUsd(b.costUsd)}</div>
                    <div style={{ color: adminTextSoft, marginTop: 4, fontSize: 11 }}>{formatChatTokens(tok)} tokens</div>
                  </div>
                );
              })}
            </div>
            {Array.isArray(drawerUser.modelsUsed) && drawerUser.modelsUsed.length > 0 ? (
              <div style={{ fontSize: 11, color: adminTextMuted, marginBottom: 12 }}>
                Models:{" "}
                <span style={{ color: adminTextMain }}>{drawerUser.modelsUsed.join(", ")}</span>
              </div>
            ) : null}
            {Array.isArray(drawerUser.recent) && drawerUser.recent.length > 0 ? (
              <div className="cu-drawer-recent">
                <div style={{ fontSize: 12, fontWeight: 700, color: adminTextMain, marginBottom: 8 }}>
                  Recent requests
                  <span style={{ fontWeight: 600, color: adminTextMuted, marginLeft: 6 }}>(newest {drawerUser.recent.length})</span>
                </div>
                <AdminDataGrid
                  rows={drawerUser.recent.map((row, index) => ({ ...row, id: `${row.at || "request"}-${index}` }))}
                  isLight={isLight}
                  height={360}
                  pageSize={20}
                  pageSizeOptions={ADMIN_CHATBOT_RECENT_PAGE_SIZE_OPTIONS}
                  columns={[
                    { field: "at", headerName: "When", minWidth: 140, flex: 1, valueGetter: (value) => formatChatRecentAt(value) || "—" },
                    { field: "type", headerName: "Type", width: 95, sortable: false, renderCell: ({ row }) => <span className={`cu-recent-badge${row.ruled ? " cu-recent-badge--rule" : " cu-recent-badge--llm"}`}>{row.ruled ? "rule" : row.provider || "llm"}</span> },
                    { field: "model", headerName: "Model", minWidth: 120, flex: 1, valueGetter: (_value, row) => row.ruled ? "—" : row.model || "—" },
                    { field: "tokens", headerName: "Tokens", minWidth: 95, flex: .75, valueGetter: (_value, row) => formatChatTokens((row.inputTokens || 0) + (row.outputTokens || 0)) },
                    { field: "cost", headerName: "Cost", minWidth: 95, flex: .75, valueGetter: (_value, row) => formatChatUsd(row.costUsd) },
                  ]}
                />
              </div>
            ) : (
              <div style={{ fontSize: 12, color: adminTextMuted }}>No recent requests logged for this user.</div>
            )}
              </>
            ) : (
              <div className="cu-drawer-chat">
                <div className="cu-drawer-chat-banner" style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ fontSize: 11.5, color: adminTextMuted, lineHeight: 1.45 }}>
                      Secure dashboard view · read-only · auto-refreshes every 20s
                    </div>
                    <button
                      type="button"
                      style={{
                        fontSize: 11.5,
                        padding: "2px 10px",
                        borderRadius: 6,
                        border: `1px solid ${adminBorder}`,
                        background: "transparent",
                        color: adminTextMain,
                        cursor: drawerChatLog.loading ? "wait" : "pointer",
                      }}
                      disabled={drawerChatLog.loading}
                      onClick={() => loadDrawerChatLog({ silent: false })}
                    >
                      Refresh
                    </button>
                  </div>
                  {drawerChatLog.total > 0 ? (
                    <div style={{ fontSize: 11.5, color: adminTextMain, marginTop: 6, fontWeight: 600, lineHeight: 1.45 }}>
                      {drawerChatLog.total.toLocaleString("en-IN")} chats
                      {drawerChatLog.sessionCount > 0
                        ? ` · ${drawerChatLog.sessionCount.toLocaleString("en-IN")} sessions`
                        : ""}
                      {drawerChatLastAt
                        ? ` · last ${formatChatDrawerLastAt(drawerChatLastAt)}`
                        : ""}
                    </div>
                  ) : null}
                </div>
                {drawerChatLog.loading ? (
                  <div style={{ fontSize: 12, color: adminTextMuted, padding: "12px 0" }}>Loading chat…</div>
                ) : drawerChatLog.error ? (
                  <div style={{ fontSize: 12, color: "#DC2626", padding: "8px 0" }}>{drawerChatLog.error}</div>
                ) : drawerChatLog.total === 0 ? (
                  <div style={{ fontSize: 12, color: adminTextMuted }}>No chatbot requests logged for this user yet.</div>
                ) : (
                  <>
                    {drawerChatLog.items.every((r) => !String(r.question || "").trim() && !String(r.answerPreview || "").trim()) ? (
                      <div
                        style={{
                          marginBottom: 10,
                          padding: "10px 12px",
                          borderRadius: 10,
                          fontSize: 11.5,
                          lineHeight: 1.45,
                          color: isLight ? "#92400e" : "#FCD34D",
                          background: isLight ? "#FFFBEB" : "rgba(245,158,11,0.12)",
                          border: `1px solid ${isLight ? "#FDE68A" : "rgba(245,158,11,0.35)"}`,
                        }}
                      >
                        <strong>{drawerChatLog.total.toLocaleString("en-IN")} requests</strong> are in the database for this user, but
                        question/answer text is only stored for <strong>new</strong> workspace assistant messages (after the backend was updated).
                        You will still see time, tokens, and cost below. Ask the chatbot once from the user portal, then refresh this tab to see Q&A.
                      </div>
                    ) : null}
                    <div className="cu-drawer-chat-scroll" ref={drawerChatScrollRef}>
                      {drawerChatDayGroups.map((group) => (
                        <section key={group.ymd} className="cu-drawer-chat-day" aria-label={group.label}>
                          <div
                            className="cu-drawer-chat-day-pill"
                            style={{
                              color: adminTextMuted,
                              borderColor: adminBorder,
                              background: isLight ? "rgba(248,250,252,0.95)" : "rgba(15,23,42,0.65)",
                            }}
                          >
                            {group.label}
                          </div>
                          {group.items.map((row, idx) => (
                            <AdminChatDrawerExchange
                              key={row.id || `${group.ymd}-${row.at}-${idx}`}
                              row={row}
                              userLabel={drawerUser?.name?.trim() || drawerUser?.email?.split("@")[0] || "User"}
                              isLight={isLight}
                              adminBorder={adminBorder}
                              adminTextMuted={adminTextMuted}
                              adminTextSoft={adminTextSoft}
                            />
                          ))}
                        </section>
                      ))}
                    </div>
                    <AdminListPagination
                      page={drawerChatPage}
                      pageSize={drawerChatPageSize}
                      total={drawerChatLog.total}
                      onPageChange={setDrawerChatPage}
                      onPageSizeChange={setDrawerChatPageSize}
                      pageSizeOptions={ADMIN_CHATBOT_RECENT_PAGE_SIZE_OPTIONS}
                      isLight={isLight}
                      adminBorder={adminBorder}
                      adminTextMuted={adminTextMuted}
                      adminTextMain={adminTextMain}
                      label="messages"
                    />
                  </>
                )}
              </div>
            )}
          </>
        ) : null}
      </AdminDetailDrawer>
    </div>
  );
}
function adminSessionProfile(email, fallback = "Admin") {
  const full = String(email || "").trim().toLowerCase();
  const local = full.split("@")[0] || "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const initials =
    parts.length >= 2
      ? `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase()
      : local.slice(0, 2).toUpperCase() || "A";
  const displayName = parts.length
    ? parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ")
    : local || fallback;
  return {
    initials,
    displayName,
    email: full || String(fallback || "").trim().toLowerCase(),
  };
}

function adminMeetingTranscriptCount(m) {
  const n = Number(m?.transcriptRowCount ?? m?.transcript_row_count);
  if (Number.isFinite(n) && n >= 0) return n;
  return Array.isArray(m?.transcript) ? m.transcript.length : 0;
}

/**
 * Admin Transcripts tab — why View is enabled/disabled (plain-language for non-technical admins).
 * @returns {{ state: 'available'|'processing'|'failed'|'unavailable', label: string, reason: string, canView: boolean }}
 */
function adminTranscriptAvailability(m, nowMs = Date.now()) {
  const lines = adminMeetingTranscriptCount(m);
  if (lines > 0) {
    return {
      state: "available",
      label: "Available",
      reason: "Transcript is ready to view.",
      canView: true,
    };
  }

  const eff = effectiveMeetingStatus(m, nowMs);
  const joined = meetingBotJoinedCall(m);
  const joinFailed = meetingBotJoinFailed(m);
  const botId = String(m?.recallBotId || m?.recall_bot_id || "").trim();
  const dispatched = !!(botId || m?.botDispatched === true || m?.botDispatchPending === true);
  const shortReason = getMeetingBotJoinReasonShort(m);
  const emailCode = String(m?.emailStatusCode || m?.email_status_code || "").toLowerCase().trim();

  if (eff === "live") {
    if (joined || meetingLiveTranscriptReady(m)) {
      return {
        state: "processing",
        label: "Processing",
        reason: "Meeting is in progress. Transcript will appear once MeetAI captures speech.",
        canView: false,
      };
    }
    return {
      state: "processing",
      label: "Processing",
      reason: "MeetAI is joining or waiting to be admitted. Transcript is not available yet.",
      canView: false,
    };
  }

  if (eff === "upcoming" || eff === "cancelled") {
    if (eff === "cancelled") {
      return {
        state: "unavailable",
        label: "Cancelled",
        reason: "This meeting was cancelled, so no transcript is available.",
        canView: false,
      };
    }
    return {
      state: "unavailable",
      label: "Not started",
      reason: "Meeting has not started yet, so no transcript is available.",
      canView: false,
    };
  }

  // completed / ended
  if (joinFailed) {
    return {
      state: "failed",
      label: shortReason || "MeetAI did not join",
      reason: shortReason
        ? `View unavailable — ${shortReason}. No transcript was captured.`
        : "View unavailable — MeetAI did not join this meeting, so no transcript is available.",
      canView: false,
    };
  }

  if (!joined && !dispatched) {
    return {
      state: "unavailable",
      label: "MeetAI did not join",
      reason: "View unavailable — MeetAI did not join this meeting, so no transcript is available.",
      canView: false,
    };
  }

  if (emailCode === "no_discussion" || emailCode === "empty_transcript") {
    return {
      state: "unavailable",
      label: "No speech captured",
      reason: "MeetAI joined, but no discussion was captured — so there is nothing to view.",
      canView: false,
    };
  }

  if (joined || dispatched) {
    return {
      state: "unavailable",
      label: "Transcript not generated",
      reason: "MeetAI joined this meeting, but no transcript was generated.",
      canView: false,
    };
  }

  return {
    state: "unavailable",
    label: "Unavailable",
    reason: "View unavailable — no transcript is available for this meeting.",
    canView: false,
  };
}

function adminTranscriptAvailabilityPillStyle(state, isLight) {
  if (state === "available") {
    return {
      background: isLight ? "rgba(220,252,231,0.95)" : "rgba(34,197,94,0.15)",
      color: isLight ? "#166534" : "#86EFAC",
      border: isLight ? "1px solid #86EFAC" : "1px solid rgba(134,239,172,0.35)",
    };
  }
  if (state === "processing") {
    return {
      background: isLight ? "rgba(219,234,254,0.95)" : "rgba(59,130,246,0.15)",
      color: isLight ? "#1D4ED8" : "#93C5FD",
      border: isLight ? "1px solid #93C5FD" : "1px solid rgba(147,197,253,0.35)",
    };
  }
  if (state === "failed") {
    return {
      background: isLight ? "rgba(254,226,226,0.95)" : "rgba(239,68,68,0.12)",
      color: isLight ? "#B91C1C" : "#FCA5A5",
      border: isLight ? "1px solid #FCA5A5" : "1px solid rgba(252,165,165,0.35)",
    };
  }
  return {
    background: isLight ? "rgba(254,243,199,0.9)" : "rgba(251,191,36,0.12)",
    color: isLight ? "#92400E" : "#FCD34D",
    border: isLight ? "1px solid #FCD34D" : "1px solid rgba(252,211,77,0.35)",
  };
}

function adminMeetingActionCount(m) {
  const n = Number(m?.actionItemCount ?? m?.action_item_count);
  if (Number.isFinite(n) && n >= 0) return n;
  return Array.isArray(m?.actionItems) ? m.actionItems.length : 0;
}

/** Start of local calendar day for a Date/ms. */
function overviewDayStartMs(raw) {
  const d = raw instanceof Date ? new Date(raw.getTime()) : new Date(raw);
  if (Number.isNaN(d.getTime())) return NaN;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Timestamp used to place an overview item on the sparkline. */
function overviewItemDayMs(item, mode = "count") {
  if (!item) return NaN;
  if (mode === "users") {
    const raw = item.created_at || item.createdAt || item.joined_at || item.joinedAt;
    return raw ? overviewDayStartMs(raw) : NaN;
  }
  if (mode === "email") {
    const raw =
      item.emailStatusAt ||
      item.email_status_at ||
      item.emailSentAt ||
      item.email_sent_at ||
      item.date ||
      item.created_at ||
      item.updated_at;
    return raw ? overviewDayStartMs(raw) : NaN;
  }
  const raw = item.date || item.created_at || item.updated_at;
  return raw ? overviewDayStartMs(raw) : NaN;
}

function overviewItemMatchesMode(item, mode = "count") {
  if (mode === "live") return String(item?.status || "").toLowerCase() === "live";
  if (mode === "email") return !!(item?.emailSent || item?.email_sent);
  if (mode === "users") return true;
  return true;
}

/**
 * Real daily buckets for Overview sparklines (oldest → newest).
 * Only counts items with a valid date inside the window — never invents placeholder points.
 */
function overviewDailyBuckets(list, mode = "count", days = 7) {
  const n = Math.max(1, Math.min(31, Number(days) || 7));
  const buckets = Array.from({ length: n }, () => 0);
  const todayMs = overviewDayStartMs(Date.now());
  if (!Number.isFinite(todayMs)) return buckets;
  const dayMs = 86400000;
  const arr = Array.isArray(list) ? list : [];
  for (const item of arr) {
    if (!overviewItemMatchesMode(item, mode)) continue;
    const itemDay = overviewItemDayMs(item, mode);
    if (!Number.isFinite(itemDay)) continue;
    const daysAgo = Math.floor((todayMs - itemDay) / dayMs);
    if (daysAgo < 0 || daysAgo >= n) continue;
    buckets[n - 1 - daysAgo] += 1;
  }
  return buckets;
}

/** Last-7-day sparkline series from real data (zeros allowed; no dummy fallback). */
function overviewSparkFromMeetings(list, mode = "count") {
  return overviewDailyBuckets(list, mode, 7);
}

/**
 * Week-over-week % from real counts (this week vs previous week).
 * Returns undefined when there is nothing meaningful to compare.
 */
function overviewWeekOverWeekTrend(list, mode = "count") {
  if (mode === "live") return undefined;
  const fourteen = overviewDailyBuckets(list, mode, 14);
  const thisWeek = fourteen.slice(7).reduce((a, b) => a + b, 0);
  const lastWeek = fourteen.slice(0, 7).reduce((a, b) => a + b, 0);
  if (thisWeek === 0 && lastWeek === 0) return undefined;
  if (lastWeek === 0) return thisWeek > 0 ? "▲ new" : undefined;
  const pct = Math.round(((thisWeek - lastWeek) / lastWeek) * 100);
  if (pct === 0) return "— 0%";
  if (pct > 0) return `▲ ${pct}%`;
  return `▼ ${Math.abs(pct)}%`;
}

function overviewMeetingChip(m) {
  const eff = effectiveMeetingStatus(m);
  const lines = adminMeetingTranscriptCount(m);
  // Match topbar "N live" — time-window live, not stale DB status=live.
  if (eff === "live") {
    const ready = meetingLiveTranscriptReady(m);
    return {
      label: ready ? "Live" : "MeetAI pending",
      variant: ready ? "live" : "processing",
    };
  }
  if (lines > 0 && (eff === "completed" || eff === "ended" || m?.summary || m?.emailSent || m?.email_sent)) {
    return { label: "Summarized", variant: "summarized" };
  }
  if (eff === "completed" || eff === "ended" || lines > 0) {
    return { label: "Processing", variant: "processing" };
  }
  if (eff === "upcoming") return { label: "Upcoming", variant: "standby" };
  const status = String(m?.status || "").toLowerCase();
  return { label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "Scheduled", variant: "standby" };
}

/** Keep visited admin tabs mounted (hidden) so data survives switches; unvisited tabs stay unmounted. */
/** Small pager used by admin Availability tables — page buttons + rows-per-page. */
function AdminPager({ page, totalPages, total, onGo, adminTextMuted, adminBorder, adminTextMain, pageSize, onPageSize }) {
  const nums = [];
  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) nums.push(i);
    else if (nums[nums.length - 1] !== "…") nums.push("…");
  }
  const btn = (disabled, label, go, active = false) => (
    <button
      key={String(label) + String(go)}
      type="button"
      disabled={disabled}
      onClick={() => go != null && onGo(go)}
      style={{
        minWidth: 30,
        padding: "6px 10px",
        borderRadius: 8,
        border: `1px solid ${adminBorder}`,
        background: active ? "#0F2A44" : "transparent",
        color: active ? "#fff" : adminTextMain,
        fontWeight: 700,
        fontSize: 12,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", marginTop: 10, flexWrap: "wrap" }}>
      {typeof pageSize === "number" && typeof onPageSize === "function" ? (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: adminTextMuted, fontWeight: 600 }}>
          Rows per page
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value) || 10)}
            style={{
              padding: "6px 8px",
              borderRadius: 8,
              border: `1px solid ${adminBorder}`,
              background: "transparent",
              color: adminTextMain,
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
              outline: "none",
            }}
          >
            {[10, 20, 50].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      ) : (
        <span />
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {btn(page <= 1, "‹ Prev", page - 1)}
        {nums.map((n, idx) =>
          n === "…" ? (
            <span key={`ellipsis-${idx}`} style={{ fontSize: 12, color: adminTextMuted }}>…</span>
          ) : (
            btn(false, String(n), n, n === page)
          )
        )}
        {btn(page >= totalPages, "Next ›", page + 1)}
        <span style={{ fontSize: 12, color: adminTextMuted, marginLeft: 4 }}>
          Page {page} of {totalPages} · {total} total
        </span>
      </div>
    </div>
  );
}

/** Initials avatar for people cards. */
function PersonAvatar({ name, size = 40 }) {
  const initials = String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
  return (
    <span
      aria-hidden
      style={{
        flex: "0 0 auto",
        width: size,
        height: size,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #0F2A44, #059669)",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: Math.max(12, Math.round(size * 0.36)),
      }}
    >
      {initials}
    </span>
  );
}

/** Admin Availability tab — who can use MeetAI (payroll_company + single-person overrides). */
function AdminAccessTab({ active, isLight, adminTextMain, adminTextMuted, adminTextSoft, adminBorder, adminPanelBg }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [savedTick, setSavedTick] = useState("");
  const [saving, setSaving] = useState({});
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [compPage, setCompPage] = useState(1);
  const [compPageSize, setCompPageSize] = useState(10);
  const [overrides, setOverrides] = useState([]);
  const [ovLoading, setOvLoading] = useState(false);
  const [ovQuery, setOvQuery] = useState("");
  const [ovPage, setOvPage] = useState(1);
  const [ovPageSize, setOvPageSize] = useState(10);
  const [addEmail, setAddEmail] = useState("");
  const [addReason, setAddReason] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [empResults, setEmpResults] = useState([]);
  const [empBusy, setEmpBusy] = useState(false);
  const [pausedMsg, setPausedMsg] = useState("");
  const [pausedMsgDefault, setPausedMsgDefault] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgSaving, setMsgSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await apiFetch("/admin/access/companies");
      setCompanies(Array.isArray(data.companies) ? data.companies : []);
    } catch (e) {
      setErr(e.message || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOverrides = useCallback(async (search = "") => {
    setOvLoading(true);
    try {
      const data = await apiFetch(`/admin/access/overrides${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setOverrides(Array.isArray(data.overrides) ? data.overrides : []);
    } catch (_) {
      setOverrides([]);
    } finally {
      setOvLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) {
      load();
      loadOverrides();
      loadPausedMsg();
    }
  }, [active, load, loadOverrides]);

  const loadPausedMsg = useCallback(async () => {
    setMsgLoading(true);
    try {
      const data = await apiFetch("/admin/access/settings");
      setPausedMsg(String(data.pausedMessage || ""));
      setPausedMsgDefault(String(data.defaultMessage || ""));
    } catch (_) {}
    finally {
      setMsgLoading(false);
    }
  }, []);

  const savePausedMsg = async (reset = false) => {
    const text = reset ? pausedMsgDefault : pausedMsg.trim();
    if (!text) {
      setErr("Message cannot be empty.");
      return;
    }
    setMsgSaving(true);
    setErr("");
    try {
      const data = await apiFetch("/admin/access/settings", {
        method: "PUT",
        body: { pausedMessage: text },
      });
      setPausedMsg(String(data.pausedMessage || text));
      setSavedTick(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(e.message || "Failed to save message");
    } finally {
      setMsgSaving(false);
    }
  };

  const saveCompany = async (row, patch) => {
    const key = row.companyName;
    setSaving((p) => ({ ...p, [key]: true }));
    try {
      const cur = {
        loginAllowed: row.loginAllowed === true,
        botAllowed: row.botAllowed !== false && row.botAllowed !== null ? row.botAllowed === true : row.loginAllowed === true,
        mailAllowed: row.mailAllowed !== false && row.mailAllowed !== null ? row.mailAllowed === true : row.loginAllowed === true,
        ...patch,
      };
      // First-time enable: turn all three on together unless admin unchecked.
      await apiFetch("/admin/access/companies", {
        method: "PUT",
        body: { companyName: key, ...cur },
      });
      await load();
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving((p) => ({ ...p, [key]: false }));
    }
  };

  const searchEmp = async (q) => {
    setOvQuery(q);
    if (String(q || "").trim().length < 2) {
      setEmpResults([]);
      return;
    }
    setEmpBusy(true);
    try {
      const data = await apiFetch(`/admin/access/employees/search?q=${encodeURIComponent(q.trim())}`);
      setEmpResults(Array.isArray(data.results) ? data.results : []);
    } catch (_) {
      setEmpResults([]);
    } finally {
      setEmpBusy(false);
    }
  };

  const grantPerson = async (email, reason = "") => {
    const em = String(email || addEmail || "").trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(em)) {
      setErr("Enter a valid email to grant access.");
      return;
    }
    setAddBusy(true);
    try {
      await apiFetch("/admin/access/overrides", {
        method: "POST",
        body: { email: em, loginAllowed: true, botAllowed: true, mailAllowed: true, reason: reason || addReason || "Admin granted (Electro exception)" },
      });
      setAddEmail("");
      setAddReason("");
      setEmpResults([]);
      setOvQuery("");
      await loadOverrides();
      await load();
    } catch (e) {
      setErr(e.message || "Grant failed");
    } finally {
      setAddBusy(false);
    }
  };

  const removeOverride = async (email) => {
    try {
      await apiFetch(`/admin/access/overrides/${encodeURIComponent(email)}`, { method: "DELETE" });
      await loadOverrides();
      await load();
    } catch (e) {
      setErr(e.message || "Remove failed");
    }
  };

  const allowedCount = companies.filter((c) => c.loginAllowed === true).length;
  const blockedCount = companies.filter((c) => c.loginAllowed === false).length;

  const card = { background: adminPanelBg, border: `1px solid ${adminBorder}`, borderRadius: 14, padding: 16 };
  const inputStyle = {
    padding: "9px 12px",
    borderRadius: 10,
    border: `1px solid ${adminBorder}`,
    background: isLight ? "#fff" : "#0F2233",
    color: adminTextMain,
    fontSize: 13,
    outline: "none",
  };
  const Toggle = ({ on, onChange, disabled, label }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on === true}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        background: on ? "#059669" : "#CBD5E1",
        position: "relative",
        opacity: disabled ? 0.6 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: on ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {[
          { label: "Companies in emp_master", value: companies.length, icon: IC.users, tint: "#EEF2FF", fg: "#3730A3" },
          { label: "Login allowed", value: allowedCount, icon: IC.check, tint: "#D1FAE5", fg: "#065F46" },
          { label: "Paused", value: blockedCount, icon: IC.close, tint: "#FEE2E2", fg: "#991B1B" },
          { label: "Single-person exceptions", value: overrides.length, icon: IC.user, tint: "#FEF3C7", fg: "#92400E" },
        ].map((s) => (
          <div key={s.label} style={{ ...card, minWidth: 150, flex: "1 1 150px", display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ flex: "0 0 auto", width: 36, height: 36, borderRadius: 10, background: s.tint, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Svg d={s.icon} size={17} color={s.fg} stroke={2} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 20, fontWeight: 800, color: adminTextMain, lineHeight: 1.2 }}>{s.value}</span>
              <span style={{ display: "block", fontSize: 12, color: adminTextMuted }}>{s.label}</span>
            </span>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
          <span style={{ flex: "0 0 auto", width: 34, height: 34, borderRadius: 10, background: isLight ? "#EEF2FF" : "#1E3448", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Svg d={IC.settings} size={16} color={isLight ? "#3730A3" : adminTextMain} stroke={1.8} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: adminTextMain }}>Paused message</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: isLight ? "#EEF2FF" : "#1E3448", color: adminTextMuted }}>EDITABLE</span>
              {savedTick ? (
                <span style={{ fontSize: 12, color: "#059669", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Svg d={IC.check} size={12} color="currentColor" stroke={2.5} /> Saved {savedTick}
                </span>
              ) : null}
            </span>
            <span style={{ display: "block", fontSize: 12.5, color: adminTextMuted, marginTop: 2 }}>
              Shown to paused-company users on login and inside the app.
            </span>
          </span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: adminTextMuted, marginBottom: 6 }}>Message text</div>
            <textarea
              value={pausedMsg}
              onChange={(e) => setPausedMsg(e.target.value.slice(0, 500))}
              rows={3}
              maxLength={500}
              placeholder={msgLoading ? "Loading…" : "Type the paused message…"}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5 }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => savePausedMsg(false)}
                disabled={msgSaving}
                style={{ padding: "8px 16px", borderRadius: 10, border: "none", background: "#059669", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
              >
                {msgSaving ? "Saving…" : "Save message"}
              </button>
              <button
                type="button"
                onClick={() => savePausedMsg(true)}
                disabled={msgSaving || !pausedMsgDefault}
                style={{ padding: "8px 14px", borderRadius: 10, border: `1px solid ${adminBorder}`, background: "transparent", color: adminTextMuted, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
              >
                Reset to default
              </button>
              <span style={{ fontSize: 12, color: adminTextSoft }}>{pausedMsg.length}/500</span>
            </div>
          </div>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: adminTextMuted, marginBottom: 6 }}>Live preview — exactly how users see it</div>
            <AccessPausedCard message={pausedMsg || APP_PAUSED_MESSAGE} flush />
          </div>
        </div>
      </div>

      {err ? (
        <div style={{ ...card, borderColor: "#FCA5A5", background: "#FEF2F2", color: "#B91C1C", fontSize: 13 }}>
          {err}
        </div>
      ) : null}

      <div style={card}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4 }}>
          <span style={{ flex: "0 0 auto", width: 34, height: 34, borderRadius: 10, background: isLight ? "#F0FDF4" : "#1E3448", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Svg d={IC.shield} size={16} color={isLight ? "#059669" : adminTextMain} stroke={1.8} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 800, color: adminTextMain }}>Companies</span>
            <span style={{ display: "block", fontSize: 12.5, color: adminTextMuted, marginTop: 2 }}>
              Payroll companies from ai_public.emp_master. App ON also turns Bot + Mail ON.
            </span>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            padding: "10px 0",
            marginBottom: 4,
            borderBottom: `1px solid ${adminBorder}`,
          }}
        >
          <div style={{ display: "inline-flex", gap: 6, background: isLight ? "#F1F5F9" : "#1E3448", borderRadius: 999, padding: 3 }}>
            {["all", "allowed", "paused"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => { setStatusFilter(f); setCompPage(1); }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  border: "none",
                  background: statusFilter === f ? (isLight ? "#fff" : "#0F2233") : "transparent",
                  boxShadow: statusFilter === f ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                  color: statusFilter === f ? adminTextMain : adminTextMuted,
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={{ position: "relative", flex: "1 1 200px", display: "flex", maxWidth: 320, marginLeft: "auto" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", display: "inline-flex", color: adminTextSoft }}>
              <Svg d={IC.search} size={14} color="currentColor" stroke={2} />
            </span>
            <input
              placeholder="Search companies…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setCompPage(1); }}
              style={{ ...inputStyle, flex: 1, paddingLeft: 32 }}
            />
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh companies"
            aria-label="Refresh companies"
            style={{ padding: "9px 12px", borderRadius: 10, border: `1px solid ${adminBorder}`, background: isLight ? "#fff" : "transparent", color: adminTextMain, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
          >
            <Svg d={IC.refresh} size={15} color="currentColor" stroke={2} />
          </button>
        </div>
        {(() => {
          const list = companies.filter((c) => {
            const q = query.trim().toLowerCase();
            if (q && !String(c.companyName || "").toLowerCase().includes(q)) return false;
            if (statusFilter === "allowed" && c.loginAllowed !== true) return false;
            if (statusFilter === "paused" && c.loginAllowed === true) return false;
            return true;
          });
          const totalPages = Math.max(1, Math.ceil(list.length / compPageSize));
          const safePage = Math.min(compPage, totalPages);
          const pageItems = list.slice((safePage - 1) * compPageSize, safePage * compPageSize);
          return (
            <>
              {pageItems.length ? (
                <AdminMuiTable
                  isLight={isLight}
                  columns={[
                    { id: "company", label: "Company", width: "40%" },
                    { id: "employees", label: "Employees", width: 95, align: "right" },
                    { id: "status", label: "Status", width: 115 },
                    { id: "app", label: "App login", width: 95, align: "center" },
                    { id: "bot", label: "Bot join", width: 95, align: "center" },
                    { id: "mail", label: "Mail", width: 95, align: "center" },
                  ]}
                  rows={pageItems.map((c) => {
                    const busy = saving[c.companyName];
                    const loginOn = c.loginAllowed === true;
                    return {
                      key: c.companyName,
                      cells: [
                        <span key="c" title={c.companyName} style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, maxWidth: "100%" }}>
                          <span style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.companyName}
                          </span>
                          {c.overrideCount ? (
                            <span
                              title={`${c.overrideCount} single-person exception${c.overrideCount === 1 ? "" : "s"}`}
                              style={{
                                flex: "0 0 auto",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                                fontSize: 11,
                                fontWeight: 800,
                                borderRadius: 999,
                                padding: "2px 8px",
                                background: "#FEF3C7",
                                color: "#92400E",
                              }}
                            >
                              <Svg d={IC.user} size={10} color="currentColor" stroke={2.2} />
                              {c.overrideCount}
                            </span>
                          ) : null}
                        </span>,
                        <span key="e" style={{ fontSize: 13 }}>{c.employeeCount}</span>,
                        <span
                          key="s"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            fontWeight: 800,
                            borderRadius: 999,
                            padding: "3px 10px",
                            background: loginOn ? "#D1FAE5" : "#FEE2E2",
                            color: loginOn ? "#065F46" : "#991B1B",
                          }}
                        >
                          <Svg d={loginOn ? IC.check : IC.close} size={11} color="currentColor" stroke={2.5} />
                          {loginOn ? "Allowed" : "Paused"}
                        </span>,
                        <span key="a" style={{ display: "inline-flex", justifyContent: "center", width: "100%" }}>
                          <Toggle
                            on={loginOn}
                            disabled={busy}
                            label={`App login for ${c.companyName}`}
                            onChange={(v) => saveCompany(c, { loginAllowed: v, botAllowed: v, mailAllowed: v })}
                          />
                        </span>,
                        <span key="b" style={{ display: "inline-flex", justifyContent: "center", width: "100%" }}>
                          <Toggle
                            on={c.botAllowed === true || (c.botAllowed === null && loginOn)}
                            disabled={busy || !loginOn}
                            label={`Bot join for ${c.companyName}`}
                            onChange={(v) => saveCompany(c, { loginAllowed: true, botAllowed: v, mailAllowed: c.mailAllowed === true || (c.mailAllowed === null && loginOn) })}
                          />
                        </span>,
                        <span key="m" style={{ display: "inline-flex", justifyContent: "center", width: "100%" }}>
                          <Toggle
                            on={c.mailAllowed === true || (c.mailAllowed === null && loginOn)}
                            disabled={busy || !loginOn}
                            label={`Mail for ${c.companyName}`}
                            onChange={(v) => saveCompany(c, { loginAllowed: true, botAllowed: c.botAllowed === true || (c.botAllowed === null && loginOn), mailAllowed: v })}
                          />
                        </span>,
                      ],
                    };
                  })}
                />
              ) : (
                <div style={{ padding: 16, color: adminTextSoft, textAlign: "center", fontSize: 13 }}>
                  {loading ? "Loading…" : "No companies found."}
                </div>
              )}
              {totalPages > 1 || list.length > 10 ? (
                <AdminPager page={safePage} totalPages={totalPages} total={list.length} onGo={setCompPage} adminTextMuted={adminTextMuted} adminBorder={adminBorder} adminTextMain={adminTextMain} pageSize={compPageSize} onPageSize={(s) => { setCompPageSize(s); setCompPage(1); }} />
              ) : (
                <div style={{ fontSize: 12, color: adminTextSoft, marginTop: 8 }}>{list.length} companies</div>
              )}
            </>
          );
        })()}
        <div style={{ display: "flex", gap: 7, alignItems: "flex-start", fontSize: 12, color: adminTextSoft, marginTop: 10, lineHeight: 1.5 }}>
          <span style={{ display: "inline-flex", marginTop: 1 }}>
            <Svg d={IC.info} size={13} color="currentColor" stroke={2} />
          </span>
          <span>App ON also turns Bot + Mail ON. Turn Bot / Mail OFF individually to block only bots or recap mails for a company.</span>
        </div>
      </div>

      <div style={card}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
          <span style={{ flex: "0 0 auto", width: 34, height: 34, borderRadius: 10, background: isLight ? "#FEF3C7" : "#1E3448", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Svg d={IC.user} size={16} color={isLight ? "#92400E" : adminTextMain} stroke={1.8} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 800, color: adminTextMain }}>Single-person access <span style={{ fontWeight: 400, fontSize: 11, color: adminTextSoft }}>(ADMIN ONLY)</span></span>
            <span style={{ display: "block", fontSize: 12.5, color: adminTextMuted, marginTop: 2 }}>
              Grant one Electro person access while their company stays paused.
            </span>
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div style={{ position: "relative", flex: "1 1 220px", display: "flex" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", display: "inline-flex", color: adminTextSoft }}>
              <Svg d={IC.search} size={14} color="currentColor" stroke={2} />
            </span>
            <input
              placeholder="Search name or email (min 2 chars)…"
              value={ovQuery}
              onChange={(e) => { searchEmp(e.target.value); setOvPage(1); }}
              style={{ ...inputStyle, flex: 1, paddingLeft: 32 }}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowManualAdd((v) => !v)}
            style={{ padding: "9px 14px", borderRadius: 10, border: "none", background: "#0F2A44", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Svg d={showManualAdd ? IC.close : IC.plus} size={13} color="#fff" stroke={2.2} />
            {showManualAdd ? "Close" : "Add"}
          </button>
        </div>
        {showManualAdd ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10, padding: 10, border: `1px dashed ${adminBorder}`, borderRadius: 10 }}>
            <input
              placeholder="email@avaada.com"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              style={{ ...inputStyle, flex: "1 1 200px" }}
            />
            <input
              placeholder="Reason (optional)"
              value={addReason}
              onChange={(e) => setAddReason(e.target.value)}
              style={{ ...inputStyle, flex: "1 1 200px" }}
            />
            <button
              type="button"
              onClick={() => grantPerson()}
              disabled={addBusy}
              style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: "#059669", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              {addBusy ? "Granting…" : "Grant access"}
            </button>
          </div>
        ) : null}
        {empBusy ? <div style={{ fontSize: 12, color: adminTextSoft }}>Searching…</div> : null}
        {!empBusy && String(ovQuery || "").trim().length >= 2 && !empResults.length ? (
          <div style={{ fontSize: 12.5, color: adminTextSoft, padding: "10px 2px", marginBottom: 8 }}>
            No people found for “{String(ovQuery).trim()}”. Use <b>Add</b> to grant by email directly.
          </div>
        ) : null}
        {empResults.length ? (
          <div style={{ marginBottom: 12 }}>
            <AdminMuiTable
              isLight={isLight}
              columns={[
                { id: "person", label: "Person", width: "38%" },
                { id: "company", label: "Payroll company", width: "30%" },
                { id: "access", label: "Access", width: 130 },
                { id: "action", label: "", width: 110, align: "right" },
              ]}
              rows={empResults.map((p) => ({
                key: p.email,
                cells: [
                  <span key="p" style={{ display: "inline-flex", alignItems: "center", gap: 9, minWidth: 0, maxWidth: "100%" }}>
                    <PersonAvatar name={p.name} size={30} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.name}>
                        {p.name}
                      </span>
                      <span style={{ display: "block", fontSize: 11.5, color: adminTextMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.email}>
                        {p.email}
                      </span>
                    </span>
                  </span>,
                  <span key="c" title={p.payrollCompany || ""} style={{ fontSize: 12.5, color: adminTextMuted }}>
                    {p.payrollCompany || "—"}
                  </span>,
                  p.hasAccessOverride ? (
                    <span
                      key="a"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11,
                        fontWeight: 800,
                        borderRadius: 999,
                        padding: "3px 10px",
                        background: "#D1FAE5",
                        color: "#065F46",
                      }}
                    >
                      <Svg d={IC.check} size={11} color="currentColor" stroke={2.5} /> Granted
                    </span>
                  ) : (
                    <span key="a" style={{ fontSize: 12, color: adminTextSoft }}>—</span>
                  ),
                  !p.hasAccessOverride ? (
                    <button
                      key="g"
                      type="button"
                      onClick={() => grantPerson(p.email, `Admin granted (${p.payrollCompany || "exception"})`)}
                      style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#0F2A44", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                    >
                      Grant
                    </button>
                  ) : (
                    <span key="g" />
                  ),
                ],
              }))}
            />
          </div>
        ) : null}
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 8px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: adminTextMain }}>
            Granted people ({overrides.length})
          </div>
          {ovLoading ? <span style={{ fontSize: 12, color: adminTextSoft }}>Loading…</span> : null}
        </div>
        {(() => {
          const totalPages = Math.max(1, Math.ceil(overrides.length / ovPageSize));
          const safePage = Math.min(ovPage, totalPages);
          const pageItems = overrides.slice((safePage - 1) * ovPageSize, safePage * ovPageSize);
          const accessPill = (on, label) => (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 11,
                fontWeight: 800,
                borderRadius: 999,
                padding: "2px 8px",
                background: on ? "#D1FAE5" : "#FEE2E2",
                color: on ? "#065F46" : "#991B1B",
              }}
            >
              <Svg d={on ? IC.check : IC.close} size={10} color="currentColor" stroke={2.5} />
              {label}
            </span>
          );
          return (
            <>
              {pageItems.length ? (
                <div className="adm-scroll-panel" style={{ maxHeight: 380, overflowY: "auto" }}>
                  <AdminMuiTable
                    isLight={isLight}
                    stickyHeader
                    maxHeight={380}
                    columns={[
                      { id: "person", label: "Person", width: "30%" },
                      { id: "company", label: "Payroll company", width: "24%" },
                      { id: "access", label: "Access", width: 200 },
                      { id: "reason", label: "Reason", width: "20%" },
                      { id: "action", label: "", width: 90, align: "right" },
                    ]}
                    rows={pageItems.map((o) => ({
                      key: o.email,
                      cells: [
                        <span key="p" style={{ display: "inline-flex", alignItems: "center", gap: 9, minWidth: 0, maxWidth: "100%" }}>
                          <PersonAvatar name={o.full_name || o.email} size={30} />
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.full_name || o.email}>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.full_name || o.email}</span>
                              <span title="Access granted" style={{ display: "inline-flex", color: "#D97706", flex: "0 0 auto" }}>
                                <Svg d={IC.star} size={13} color="currentColor" stroke={2} />
                              </span>
                            </span>
                            <span style={{ display: "block", fontSize: 11.5, color: adminTextMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.email}>
                              {o.email}
                            </span>
                          </span>
                        </span>,
                        <span key="c" title={o.payroll_company || ""} style={{ fontSize: 12.5, color: adminTextMuted }}>
                          {o.payroll_company || "—"}
                        </span>,
                        <span key="a" style={{ display: "inline-flex", gap: 5, flexWrap: "wrap" }}>
                          {accessPill(o.login_allowed, "Login")}
                          {accessPill(o.bot_allowed, "Bot")}
                          {accessPill(o.mail_allowed, "Mail")}
                        </span>,
                        <span key="r" title={o.reason || ""} style={{ fontSize: 12, color: adminTextSoft, fontStyle: o.reason ? "italic" : "normal" }}>
                          {o.reason || "—"}
                        </span>,
                        <button
                          key="x"
                          type="button"
                          onClick={() => removeOverride(o.email)}
                          style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#fff", color: "#B91C1C", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                        >
                          Revoke
                        </button>,
                      ],
                    }))}
                  />
                </div>
              ) : (
                <div style={{ fontSize: 12, color: adminTextSoft, padding: 8 }}>
                  {ovLoading ? "Loading…" : "No exceptions yet — search above to grant one Electro person."}
                </div>
              )}
              <AdminPager page={safePage} totalPages={totalPages} total={overrides.length} onGo={setOvPage} adminTextMuted={adminTextMuted} adminBorder={adminBorder} adminTextMain={adminTextMain} pageSize={ovPageSize} onPageSize={(s) => { setOvPageSize(s); setOvPage(1); }} />
            </>
          );
        })()}
      </div>
    </div>
  );
}

function AdminTabPanel({ active, className = "", style, children }) {
  return (
    <div
      className={className}
      style={{ display: active ? "block" : "none", ...style }}
      aria-hidden={!active}
      data-admin-tab-active={active ? "1" : "0"}
    >
      {children}
    </div>
  );
}

/** Admin: org-wide transcripts + recap email delivery (from DB). */
/** Read-only admin preview that mirrors the user meeting detail page. */
function AdminMeetingUserView({ meeting, transcriptPreviewRequest = 0, onPreviewTranscript, currentUserEmail = "", currentUserName = "" }) {
  const [activeTab, setActiveTab] = useState("overview");
  if (!meeting) return null;
  const effectiveStatus = effectiveMeetingStatus(meeting);
  const st = STATUS_META[effectiveStatus] || STATUS_META.completed || { label: effectiveStatus || "—", bg: "#F1F5F9", fg: "#475569" };
  const pl = PLATFORM_META[meeting.platform] || PLATFORM_META.meet || { bg: "#EEF2FF", color: "#3730A3", short: "?" };
  const timeChip = meetingRelativeTimeChip(meeting);
  const showBotJoinChip = isEndedMeetingStatus(effectiveStatus, meeting);
  const botJoinedCall = meetingBotJoinedCall(meeting);
  const attendanceContext = attendanceContextFromMeeting(meeting);
  const rosterPeople = participantsForMeetingDetailChips(meeting.participants || [], attendanceContext);
  const { joined: rosterJoined, notJoined: rosterNotJoined } = splitJoinedVsNotJoinedInvitees(
    meeting.participants || [],
    attendanceContext
  );
  const hasTranscript = meetingHasTranscriptData(meeting);
  const showPreviewTranscript =
    hasTranscript ||
    (botJoinedCall && isEndedMeetingStatus(effectiveStatus, meeting));
  const actionCount = Array.isArray(meeting.actionItems) ? meeting.actionItems.length : 0;
  const meetingId = meeting.id ?? meeting._id;
  const adminSeriesPath = `/admin/meetings/${encodeURIComponent(String(meetingId || ""))}/series-tasks`;
  const actionOwnerEmail = meeting.owner_email || meeting.ownerEmail || currentUserEmail || "";
  const actionOwnerName = meeting.owner_name || meeting.ownerName || currentUserName || "";

  const adminDetailTabs = [
    { id: "overview", label: "Overview", icon: IC.layoutDashboard, badge: null },
    { id: "transcript", label: "Transcript", icon: IC.messageSquare, badge: null },
    { id: "recording", label: "Recording", icon: IC.play, badge: null },
    { id: "tasks", label: "Action Items", icon: IC.listChecks, badge: actionCount > 0 ? actionCount : null },
  ];

  return (
    <div
      className="admin-meeting-user-view"
      style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 900, margin: "0 auto", width: "100%" }}
    >
      <style>{`
        .admin-meeting-user-view .people-stack{
          display:inline-flex;align-items:center;padding-left:6px;
        }
        .admin-meeting-user-view .people-stack-item{
          display:inline-flex;margin-left:-8px;border-radius:50%;
          background:#FFFFFF;box-shadow:0 0 0 2px #FFFFFF;
          position:relative;z-index:1;
        }
        .admin-meeting-user-view .people-stack-item.is-bot{
          box-shadow:0 0 0 2px #E3EEFF;
        }
        .admin-meeting-user-view .time-chip{
          display:inline-flex;align-items:center;gap:5px;
          padding:4px 9px;border-radius:999px;font-size:10.5px;font-weight:600;
        }
        .admin-meeting-user-view .time-chip-upcoming{background:#EFF6FF;color:#1D4ED8;border:1px solid #BFDBFE;}
        .admin-meeting-user-view .time-chip-imminent{background:#FFFBEB;color:#B45309;border:1px solid #FDE68A;}
        .admin-meeting-user-view .time-chip-live{background:#ECFDF5;color:#047857;border:1px solid #A7F3D0;}
        .admin-meeting-user-view .time-chip-past{background:#F1F5F9;color:#475569;border:1px solid #E2E8F0;}
        .admin-meeting-user-view .time-chip-dot{
          width:6px;height:6px;border-radius:50%;background:#10B981;flex-shrink:0;
        }
        .admin-meeting-user-view .avaada-meeting-detail{
          padding:0;
          background:transparent;
          color:inherit;
        }
        .admin-meeting-user-view .admin-detail-quick{
          display:inline-flex;align-items:center;gap:7px;min-height:34px;
          padding:7px 12px;border-radius:8px;border:1px solid #172554;
          background:#172554;color:#FFFFFF;font-size:12px;font-weight:700;
          cursor:pointer;font-family:inherit;
        }
        .admin-meeting-user-view .admin-detail-quick--ghost{
          background:#FFFFFF;color:#172554;border:1px solid #cbd5e1;
        }
        .admin-meeting-user-view .admin-detail-extras{
          display:flex;flex-direction:column;gap:16px;
          padding:16px 20px 20px;background:#ffffff;
          border-top:1px solid #e2e8f0;
        }
      `}</style>
      <div
        style={{
          padding: "20px 22px",
          borderRadius: 14,
          border: "1px solid rgba(12,43,78,0.12)",
          background: "linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%)",
          boxShadow: "0 4px 18px rgba(12,43,78,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 18, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: "1 1 420px" }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: pl.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 20,
                color: pl.color,
                flexShrink: 0,
                position: "relative",
              }}
            >
              {meeting.platform === "gcal" || meeting.platform === "meet" ? (
                <Svg d={IC.cal} color={pl.color} size={24} stroke={2} />
              ) : (
                pl.short
              )}
              {meeting.type === "instant" ? (
                <div
                  style={{
                    position: "absolute",
                    bottom: -5,
                    right: -5,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "#F59E0B",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Svg d={IC.zap} color="#fff" size={10} />
                </div>
              ) : null}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: -0.2, color: "#0f172a" }}>
                  {meeting.title || "Untitled Meeting"}
                </h2>
                {meeting.type === "instant" ? (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5, background: "#E3EEFF", color: "#1565C0" }}>
                    INSTANT
                  </span>
                ) : null}
              </div>
              <div style={{ fontSize: 13, color: "#64748b", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span>{meeting.date ? formatYmdDisplay(meeting.date) : "—"}</span>
                <span style={{ color: "#cbd5e1" }}>·</span>
                <span>
                  {formatMeetingTimeForDisplay(meeting.time)}
                  {meeting.end && String(meeting.end).trim() && meeting.end !== "—"
                    ? ` – ${formatMeetingTimeForDisplay(meeting.end)}`
                    : meeting.end_time
                      ? ` – ${formatMeetingTimeForDisplay(meeting.end_time)}`
                      : ""}
                </span>
                {meeting.duration && !/^(completed|ended)$/i.test(String(meeting.duration).trim()) ? (
                  <>
                    <span style={{ color: "#cbd5e1" }}>·</span>
                    <span>{meeting.duration}</span>
                  </>
                ) : effectiveStatus ? (
                  <>
                    <span style={{ color: "#cbd5e1" }}>·</span>
                    <span style={{ textTransform: "capitalize" }}>{effectiveStatus}</span>
                  </>
                ) : null}
                {meetingHasDisplayableHost(meeting) ? (
                  <>
                    <span style={{ color: "#cbd5e1" }}>·</span>
                    <span>Host: {meetingHostName(meeting)}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
{showBotJoinChip ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 9px",
                borderRadius: 7,
                border: botJoinedCall ? "1px solid #D1EBDD" : "1px solid #F1DFC1",
                background: botJoinedCall ? "#EEF9F2" : "#FFF8EB",
                color: botJoinedCall ? "#237A4B" : "#A16207",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Svg d={botJoinedCall ? IC.check : IC.close} size={11} color="currentColor" stroke={2.2} />
              {st.label} · {botJoinedCall ? "MeetAI joined" : "MeetAI didn't join"}
            </span>
            ) : (
              <Badge label={st.label} bg={st.bg} color={st.fg} dot={st.dot} />
            )}
            {timeChip ? (
              <span className={`time-chip time-chip-${timeChip.tone}`}>
                {timeChip.tone === "live" ? <span className="time-chip-dot" /> : null}
                <Svg d={IC.clock} size={11} color="currentColor" stroke={2.1} />
                {timeChip.label}
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ display: "none", alignItems: "center", flexWrap: "wrap", gap: 8, paddingTop: 14, marginTop: 16, borderTop: "1px solid #e2e8f0" }}>
          <button
            type="button"
            className="admin-detail-quick"
            onClick={() => {
              setActiveTab("transcript");
              onPreviewTranscript?.();
            }}
          >
            <Svg d={IC.mic} size={14} color="currentColor" stroke={2} />
            {hasTranscript ? "View transcript" : "Transcript status"}
          </button>
          <button
            type="button"
            className={`admin-detail-quick admin-detail-quick--ghost${activeTab === "recording" ? " is-active" : ""}`}
            onClick={() => setActiveTab("recording")}
          >
            <Svg d={IC.play} size={14} color="currentColor" stroke={2} />
            Recording
          </button>
          <button
            type="button"
            className="admin-detail-quick admin-detail-quick--ghost"
            onClick={() => setActiveTab("tasks")}
          >
            <Svg d={IC.listChecks} size={14} color="currentColor" stroke={2} />
            Action items{actionCount > 0 ? ` · ${actionCount}` : ""}
          </button>
        </div>
      </div>

      <div className="avaada-meeting-detail" style={{ padding: 0 }}>
        <div className="avaada-meeting-detail__tab-shell">
          <div
            className="avaada-meeting-detail__tabs avaada-meeting-detail__tabs--nav"
            role="tablist"
            aria-label="Meeting detail sections"
          >
            {adminDetailTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`admin-detail-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={`admin-detail-section-${tab.id}`}
                  tabIndex={isActive ? 0 : -1}
                  className={`avaada-meeting-detail__tab${isActive ? " is-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="avaada-detail-tab-icon" aria-hidden>
                    <Svg d={tab.icon} size={18} color="currentColor" stroke={1.75} />
                  </span>
                  <span className="avaada-detail-tab-label">{tab.label}</span>
                  {tab.badge != null ? (
                    <span className={`avaada-detail-tab-badge${isActive ? " is-active" : ""}`}>{tab.badge}</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="avaada-meeting-detail__tab-body">
            {activeTab === "overview" ? (
              <div
                id="admin-detail-section-overview"
                className="avaada-meeting-detail__panel avaada-meeting-detail__summary-panel"
                role="tabpanel"
                aria-labelledby="admin-detail-tab-overview"
              >
                <div className="avaada-meeting-detail__summary-head">
                  <div className="avaada-meeting-detail__summary-head-main">
                    <span className="avaada-detail-summary-head-icon" aria-hidden>
                      <Svg d={IC.actionList} size={16} color="#0875E1" stroke={2} />
                    </span>
                    <h2>Meeting Summary</h2>
                  </div>
                  <div className="avaada-detail-summary-head-actions">
                    <span className="avaada-meeting-detail__meetai-badge">
                      <span className="avaada-meeting-detail__meetai-badge-dot" aria-hidden />
                      Generated by MeetAI
                    </span>
                  </div>
                </div>
                <div className="avaada-detail-summary-body">
                  <MeetingDetailSummarySection
                    meeting={meeting}
                    referenceLayout
                    actionItems={meeting.actionItems || []}
                  />
                </div>
                <div className="admin-detail-extras">
                {rosterPeople.length > 0 ? (
                  <section aria-label={`Roster, ${rosterPeople.length} participants`}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0B2A4A" }}>
                        Roster · {rosterPeople.length} participant{rosterPeople.length === 1 ? "" : "s"}
                      </span>
                      <span style={{ fontSize: 12, color: "#64748B" }}>Hover a chip for details</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, marginBottom: 11 }}>
                      <div className="people-stack" aria-hidden>
                        {rosterPeople.slice(0, 6).map((p, index) => (
                          <span
                            key={p.id || p.email || `roster-${index}`}
                            className={`people-stack-item${participantIsBot(p) ? " is-bot" : ""}`}
                          >
                            <Avatar
                              initials={p.initials || String(p.name || p.email || "?").slice(0, 2).toUpperCase()}
                              size={30}
                              color="#9CA3AF"
                              isBot={participantIsBot(p)}
                              imageUrl={p.avatar_url || p.avatarUrl || p.picture || ""}
                            />
                          </span>
                        ))}
                      </div>
                      <span
                        style={{
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          color: "#64748B",
                        }}
                        title={rosterPeople.map((p) => p.name || p.email || "Participant").join(", ")}
                      >
                        {rosterPeople
                          .map((p) =>
                            participantIsBot(p)
                              ? BOT_DISPLAY_NAME
                              : `${p.name || p.email || "Participant"}${isGuestMeetLinkEmail(p.email) ? " (guest)" : ""}`
                          )
                          .join(", ")}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      {rosterJoined.map((p, index) => {
                        const guest = isAttendanceGuestParticipant(p);
                        return (
                          <span
                            key={`roster-joined-${p.id || p.email || index}`}
                            title={`${p.name || p.email || "Participant"} joined`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "5px 9px",
                              borderRadius: 7,
                              border: guest ? "1px dashed #34A16F" : "1px solid #D1EBDD",
                              background: guest ? "#F4FBF7" : "#EEF9F2",
                              color: "#237A4B",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            <Svg d={guest ? IC.users : IC.check} size={11} color="currentColor" stroke={2} />
                            {p.name || p.email || "Participant"}
                            {guest ? " · guest" : ""}
                          </span>
                        );
                      })}
                      {rosterNotJoined.map((p, index) => (
                        <span
                          key={`roster-missed-${p.id || p.email || index}`}
                          title={`${p.name || p.email || "Participant"} did not join`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "5px 9px",
                            borderRadius: 7,
                            border: "1px solid #F1DFC1",
                            background: "#FFF8EB",
                            color: "#A16207",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          <Svg d={IC.clock} size={11} color="currentColor" stroke={2} />
                          {p.name || p.email || "Participant"} · not joined
                        </span>
                      ))}
                    </div>
                  </section>
                ) : null}

                <JoinedNotJoinedAttendance
                  participants={meeting.participants || []}
                  meeting={meeting}
                  autoJoinOptInUsers={meeting.autoJoinOptInUsers || []}
                  hasGoogleEventId={Boolean(String(meeting.google_event_id || meeting.googleEventId || "").trim())}
                  referenceLayout
                  defaultDetailsOpen
                />

                <MeetingRecapEmailBanner meeting={meeting} />
                </div>
              </div>
            ) : null}

            {activeTab === "transcript" ? (
              <div
                id="admin-detail-section-transcript"
                className="avaada-meeting-detail__panel avaada-meeting-detail__transcript-panel"
                role="tabpanel"
                aria-labelledby="admin-detail-tab-transcript"
              >
                <ActionTab
                  embedInAvaadaDetail
                  referenceTranscriptLayout
                  panelMode="transcript-only"
                  items={meeting.actionItems || []}
                  transcript={meeting.transcript || []}
                  meeting={meeting}
                  meetingTitle={meeting.title || "Meeting transcript"}
                  status={effectiveStatus}
                  meetingId={meetingId}
                  onMeetingUpdated={undefined}
                  currentUserEmail={actionOwnerEmail}
                  currentUserName={actionOwnerName}
                  participants={meeting.participants || []}
                  previewRequest={transcriptPreviewRequest}
                  seriesTasksPath={adminSeriesPath}
                  readOnly
                />
              </div>
            ) : null}

            {activeTab === "recording" ? (
              <div
                id="admin-detail-section-recording"
                className="avaada-meeting-detail__panel avaada-meeting-detail__recording-panel"
                role="tabpanel"
                aria-labelledby="admin-detail-tab-recording"
              >
                <div className="avaada-meeting-detail__summary-head">
                  <div className="avaada-meeting-detail__summary-head-main">
                    <span className="avaada-detail-summary-head-icon" aria-hidden>
                      <Svg d={IC.playCircle} size={16} color="#0875E1" stroke={2} />
                    </span>
                    <h2>Recording</h2>
                  </div>
                </div>
                <div className="avaada-detail-recording-body">
                  {meeting?.approvalAccess?.required && !meeting?.approvalAccess?.visible ? (
                    <div role="status" style={{ display: "flex", minHeight: 150, alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, border: "1px dashed #93C5FD", borderRadius: 12, background: "#F8FBFF", color: "#1E3A8A", textAlign: "center", padding: 24 }}><LockClockOutlinedIcon sx={{ fontSize: 30, color: "#0875E1" }} /><strong>Recording locked</strong><span style={{ fontSize: 13, color: "#4B6B8F" }}>Recording access will be available after approval.</span></div>
                  ) : <MeetingRecordingInlinePanel meeting={meeting} />}
                </div>
              </div>
            ) : null}

            {activeTab === "tasks" ? (
              <section
                id="admin-detail-section-tasks"
                className="avaada-meeting-detail__panel avaada-meeting-detail__action-panel"
                role="tabpanel"
                aria-labelledby="admin-detail-tab-tasks"
                aria-label="Action items"
              >
                {meeting?.approvalAccess?.required && !meeting?.approvalAccess?.visible ? (
                  <div role="status" style={{ display: "flex", minHeight: 150, alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, margin: 18, border: "1px dashed #93C5FD", borderRadius: 12, background: "#F8FBFF", color: "#1E3A8A", textAlign: "center", padding: 24 }}><LockClockOutlinedIcon sx={{ fontSize: 30, color: "#0875E1" }} /><strong>Action items locked</strong><span style={{ fontSize: 13, color: "#4B6B8F" }}>Action items will be available after approval.</span></div>
                ) : <ActionTab
                  embedInAvaadaDetail
                  panelMode="tasks-only"
                  initialScope="all"
                  meetingActionCount={actionCount}
                  items={meeting.actionItems || []}
                  transcript={meeting.transcript || []}
                  meeting={meeting}
                  meetingTitle={meeting.title || "Meeting transcript"}
                  status={effectiveStatus}
                  meetingId={meetingId}
                  onMeetingUpdated={undefined}
                  currentUserEmail={actionOwnerEmail}
                  currentUserName={actionOwnerName}
                  participants={meeting.participants || []}
                  previewRequest={transcriptPreviewRequest}
                  seriesTasksPath={adminSeriesPath}
                  readOnly
                />}
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Admin email preview only — wire the recap CTA to open the linked transcript in-app. */
function wireAdminRecapEmailPreviewHtml(html, meetingId) {
  if (!html || !meetingId) return html;
  const mid = String(meetingId).replace(/"/g, "");
  return html.replace(
    /<a\b([^>]*)\bhref="[^"]*"([^>]*)>\s*Open dashboard[^<]*<\/a>/i,
    `<a$1href="#" data-meetai-action="open-transcript" data-meeting-id="${mid}"$2>Open dashboard — full transcript &amp; overview</a>`
  );
}

function AdminTranscriptsEmailTab({
  meetings = [],
  smtpReport,
  smtpLoading,
  smtpError,
  isLight,
  adminTextMain,
  adminTextMuted,
  adminTextSoft,
  adminBorder,
  adminSearch,
  setAdminSearch,
  tabActive = true,
  sessionEmail = "",
  sessionName = "",
}) {
  const [subTab, setSubTab] = useState("transcripts");
  const [emailFilter, setEmailFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [transcriptPreviewRequest, setTranscriptPreviewRequest] = useState(0);
  const [transcriptAdminView, setTranscriptAdminView] = useState("clean");
  const [reprocessBusy, setReprocessBusy] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState("");
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(ADMIN_TRANSCRIPTS_PAGE_SIZE_DEFAULT);
  const [emailPage, setEmailPage] = useState(1);
  const [emailPageSize, setEmailPageSize] = useState(ADMIN_TRANSCRIPTS_PAGE_SIZE_DEFAULT);
  // Keep the search input itself snappy; defer the value that drives the (potentially large)
  // transcript/email filtering so typing doesn't stall on every keystroke.
  const deferredAdminSearch = useDeferredValue(adminSearch);

  const transcriptMeetings = useMemo(() => {
    const list = Array.isArray(meetings) ? meetings : [];
    return sortMeetingsNewestFirst(list);
  }, [meetings]);

  // Full-meeting lookup for email/SMTP projection rows (which may only carry
  // owner fields) so Host resolves to the original calendar host, never the
  // "AI Team" system owner.
  const meetingById = useMemo(() => {
    const map = new Map();
    for (const m of Array.isArray(meetings) ? meetings : []) {
      const id = String(m?.id || m?._id || "");
      if (id && !map.has(id)) map.set(id, m);
    }
    return map;
  }, [meetings]);

  const filteredTranscriptMeetings = useMemo(
    () => {
      if (!tabActive) return transcriptMeetings;
      return transcriptMeetings.filter((m) =>
        adminSearchMatches(deferredAdminSearch, meetingAdminSearchParts(m))
      );
    },
    [transcriptMeetings, deferredAdminSearch, tabActive]
  );

  const emailRows = useMemo(() => {
    const sent = Array.isArray(smtpReport?.sentMeetings)
      ? smtpReport.sentMeetings
      : Array.isArray(smtpReport?.sentList)
        ? smtpReport.sentList
        : [];
    const notSent = Array.isArray(smtpReport?.notSentMeetings)
      ? smtpReport.notSentMeetings
      : Array.isArray(smtpReport?.notSentList)
        ? smtpReport.notSentList
        : [];
    const merged = [...sent, ...notSent];
    const seen = new Set();
    const out = [];
    for (const row of merged) {
      const id = String(row?.id || "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(row);
    }
    return out;
  }, [smtpReport]);

  const filteredEmailRows = useMemo(() => {
    if (!tabActive) return emailRows;
    return emailRows.filter((m) => {
      const code = String(m.emailStatusCode || "").toLowerCase();
      const sentForFilter =
        m.emailSent === true || code === "sent" || code === "no_discussion";
      if (emailFilter === "sent" && !sentForFilter) return false;
      if (emailFilter === "failed" && !(code.includes("fail") || code === "smtp_failed")) return false;
      if (emailFilter === "pending" && (sentForFilter || code.includes("fail"))) return false;
      return adminSearchMatches(deferredAdminSearch, [
        m.title,
        m.ownerName,
        m.ownerEmail,
        m.emailStatusLabel,
        m.emailStatusMessage,
        ...(Array.isArray(m.recapSentTo) ? m.recapSentTo.map((p) => `${p.name || ""} ${p.email || ""}`) : []),
      ]);
    });
  }, [emailRows, emailFilter, deferredAdminSearch, tabActive]);

  // The email endpoint intentionally returns only the latest rows, but its
  // totals are calculated over every completed MeetAI session in the DB.
  const emailDbTotal = Number(smtpReport?.totals?.completedMeetings ?? emailRows.length);
  const emailFilterCounts = useMemo(
    () => adminEmailDeliveryFilterCounts(smtpReport),
    [smtpReport]
  );

  useEffect(() => {
    setTxPage(1);
  }, [adminSearch, subTab]);
  useEffect(() => {
    setEmailPage(1);
  }, [adminSearch, emailFilter, subTab]);

  const txTotalPages = Math.max(1, Math.ceil(filteredTranscriptMeetings.length / txPageSize));
  const txSafePage = Math.min(txPage, txTotalPages);
  const pagedTranscriptMeetings = useMemo(() => {
    const start = (txSafePage - 1) * txPageSize;
    return filteredTranscriptMeetings.slice(start, start + txPageSize);
  }, [filteredTranscriptMeetings, txSafePage, txPageSize]);

  const emailTotalPages = Math.max(1, Math.ceil(filteredEmailRows.length / emailPageSize));
  const emailSafePage = Math.min(emailPage, emailTotalPages);
  const pagedEmailRows = useMemo(() => {
    const start = (emailSafePage - 1) * emailPageSize;
    return filteredEmailRows.slice(start, start + emailPageSize).map((m) =>
      applyCanonicalScheduleFields({
        ...m,
        status: m.status || "completed",
        completed_at: m.completedAt || m.completed_at || null,
        google_event_id: m.googleEventId || m.google_event_id || "",
        emailSent: m.emailSent === true || m.email_sent === true,
      })
    );
  }, [filteredEmailRows, emailSafePage, emailPageSize]);

  function closeDetail() {
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
    setTranscriptPreviewRequest(0);
  }

  async function openTranscript(meetingId, { switchToTranscriptsTab = false, view = transcriptAdminView } = {}) {
    if (!meetingId) return;
    const viewMode = view === "raw" ? "raw" : "clean";
    if (switchToTranscriptsTab) setSubTab("transcripts");
    setTranscriptAdminView(viewMode);
    setDetailLoading(true);
    setDetailError(null);
    setReprocessMsg("");
    setTranscriptPreviewRequest(0);
    setDetail({ kind: "transcript", meetingId, meeting: null, lines: [], transcriptView: viewMode });
    try {
      const qs = viewMode === "raw" ? "?view=raw" : "";
      const data = await apiFetch(`/admin/meetings/${encodeURIComponent(meetingId)}/transcript${qs}`);
      const meetingRaw = data?.meeting || null;
      const lines = Array.isArray(data?.lines)
        ? data.lines
        : Array.isArray(meetingRaw?.transcript)
          ? meetingRaw.transcript
          : [];
      const meeting = meetingRaw
        ? normalizeMeeting({
            ...meetingRaw,
            transcript: lines,
            _useProvidedTranscript: true,
            participants: meetingRaw.participants || [],
            actionItems: meetingRaw.actionItems || meetingRaw.action_items || [],
          })
        : null;
      setDetail({
        kind: "transcript",
        meetingId,
        meeting,
        lines,
        lineCount: Number(data?.lineCount) || lines.length,
        transcriptView: meetingRaw?.transcript_view || viewMode,
      });
    } catch (err) {
      setDetailError(err?.message || "Could not load transcript");
    } finally {
      setDetailLoading(false);
    }
  }

  async function reprocessMeetingSummaryAdmin() {
    const meetingId = detail?.meetingId;
    if (!meetingId || reprocessBusy) return;
    setReprocessBusy(true);
    setReprocessMsg("");
    setDetailError(null);
    try {
      const data = await apiFetch(`/admin/meetings/${encodeURIComponent(meetingId)}/reprocess-summary`, {
        method: "POST",
      });
      setReprocessMsg(
        `Summary reprocessed (${data?.actionItemCount ?? "?"} action items, middleware: ${data?.middlewareSource || "n/a"}).`
      );
      await openTranscript(meetingId, { view: detail?.transcriptView || transcriptAdminView });
    } catch (err) {
      setDetailError(err?.message || "Reprocess failed");
    } finally {
      setReprocessBusy(false);
    }
  }

  async function openEmailPreview(row) {
    const meetingId = row?.id;
    if (!meetingId) return;
    const previewTo =
      (Array.isArray(row.recapSentTo) && row.recapSentTo[0]?.email) ||
      row.ownerEmail ||
      "";
    const rawPreviewOwner = String(row.ownerName || "").trim();
    const previewName =
      (Array.isArray(row.recapSentTo) && row.recapSentTo[0]?.name) ||
      (!isServiceAccountDisplayName(rawPreviewOwner) ? rawPreviewOwner : "") ||
      "";
    setDetailLoading(true);
    setDetailError(null);
    setDetail({ kind: "email", meetingId, meeting: { title: row.title }, subject: "", html: "" });
    try {
      const q = new URLSearchParams();
      if (previewTo) q.set("to", previewTo);
      if (previewName) q.set("name", previewName);
      const qs = q.toString() ? `?${q.toString()}` : "";
      const data = await apiFetch(`/admin/meetings/${encodeURIComponent(meetingId)}/recap-email-preview${qs}`);
      setDetail({
        kind: "email",
        meetingId,
        meeting: data?.meeting || { title: row.title },
        subject: data?.subject || "",
        html: data?.html || "",
        previewAs: data?.previewAs || null,
      });
    } catch (err) {
      setDetailError(err?.message || "Could not load email template");
    } finally {
      setDetailLoading(false);
    }
  }

  function handleEmailPreviewBodyClick(e) {
    const el = e.target?.closest?.('[data-meetai-action="open-transcript"]');
    if (!el) return;
    e.preventDefault();
    const meetingId = el.getAttribute("data-meeting-id") || detail?.meetingId;
    if (meetingId) openTranscript(meetingId, { switchToTranscriptsTab: true });
  }

  /**
   * Flip between the Transcripts and Email delivery tabs inside the detail
   * drawer without dismissing it. Reuses the same loaders the row actions use
   * so the body swaps in place instead of reopening a popup.
   */
  function switchDetailKind(kind) {
    if (!detail || detail.kind === kind) return;
    if (kind === "transcript") {
      openTranscript(detail.meetingId, { view: detail.transcriptView || transcriptAdminView });
      return;
    }
    const row =
      emailRows.find((r) => String(r?.id || "") === String(detail.meetingId)) || {
        id: detail.meetingId,
        title: detail.meeting?.title,
      };
    openEmailPreview(row);
  }

  const pill = (active) => ({
    border: `1px solid ${active ? ADM.mintStrong : ADM.border}`,
    background: active ? ADM.mint : "#fff",
    color: active ? ADM.tealDark : ADM.textMuted,
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  });

  const viewBtnStyle = (enabled) => ({
    border: `1px solid ${adminBorder}`,
    background: isLight ? "#fff" : "rgba(255,255,255,0.04)",
    color: enabled ? adminTextMain : adminTextSoft,
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 11.5,
    fontWeight: 650,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.55,
  });

  return (
    <>
      <AdminPremiumHero
        badge="Avaada · Transcripts"
        badgeIconD={IC.mic}
        title="Transcripts & email"
        desc="Secured archive of meeting transcripts and recap email delivery across the organization."
        isLight={isLight}
        meta={
          <>
            <span>{transcriptMeetings.filter((m) => adminMeetingTranscriptCount(m) > 0).length} with transcript</span>
            <span className="adm-hero-dot" />
            <span>
              {smtpReport
                ? `${adminSmtpHeadlineMeta(smtpReport).all} MeetAI sessions (recap eligible)`
                : `${transcriptMeetings.length} meetings in archive`}
            </span>
            <span className="adm-hero-dot" />
            <span>
              {smtpReport
                ? `${adminSmtpHeadlineMeta(smtpReport).sent} recaps sent`
                : "— recaps sent"}
            </span>
          </>
        }
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <button type="button" style={pill(subTab === "transcripts")} onClick={() => setSubTab("transcripts")}>
          Transcripts
        </button>
        <button type="button" style={pill(subTab === "email")} onClick={() => setSubTab("email")}>
          Email delivery
        </button>
      </div>

      {subTab === "transcripts" ? (
        <div className="adm-card" style={{ overflow: "hidden" }}>
          <div className="adm-card-header adm-card-header--search">
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              Transcripts (
              {adminSearch.trim()
                ? `${filteredTranscriptMeetings.length} of ${transcriptMeetings.length}`
                : transcriptMeetings.length}
              )
              <div className="adm-card-sub">
                Every session shows transcript availability. Use View when Available — disabled buttons explain why (MeetAI did not join, still processing, etc.).
              </div>
            </div>
            <AdminSearchToolbar
              value={adminSearch}
              onChange={setAdminSearch}
              placeholder="Search transcripts by meeting, host, platform…"
              adminTextMuted={adminTextMuted}
              filteredCount={filteredTranscriptMeetings.length}
              totalCount={transcriptMeetings.length}
            />
          </div>
          {filteredTranscriptMeetings.length === 0 ? (
            <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 12.5, color: adminTextSoft }}>
              {transcriptMeetings.length === 0 ? "No MeetAI sessions yet." : "No sessions match your search."}
            </div>
          ) : (
            <div className="adm-scroll-panel" style={{ maxHeight: 480 }}>
              <AdminMuiTable
                isLight={isLight}
                stickyHeader
                maxHeight={480}
                minWidth={780}
                columns={[
                  { id: "meeting", label: "Meeting", width: 280, cellSx: { overflow: "hidden", maxWidth: 280 } },
                  { id: "host", label: "Host", width: 160 },
                  { id: "status", label: "Status", width: 100 },
                  { id: "avail", label: "Transcript", width: 168 },
                  { id: "view", label: "", width: 96, align: "right" },
                ]}
                rows={pagedTranscriptMeetings.map((m, i) => {
                  // Never fall back to raw owner_* — auto-dispatched meetings
                  // are owned by the "AI Team" system user; the helpers filter
                  // that stamp and "—" is correct when no human is known.
                  const host = meetingHostName(m) || meetingHostEmail(m) || "—";
                  const avail = adminTranscriptAvailability(m);
                  const pillStyle = adminTranscriptAvailabilityPillStyle(avail.state, isLight);
                  return {
                    key: m.id || i,
                    cells: [
                      <div key="t" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }} title={m.title || "Untitled Meeting"}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                          {m.title || "Untitled Meeting"}
                        </div>
                        <div style={{ fontSize: 10.5, color: adminTextSoft, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                          {(m.date ? formatYmdDisplay(m.date) : "—")} · {formatMeetingTimeForDisplay(m.time)} ·{" "}
                          {String(m.platform || "unknown").replace(/_/g, " ")}
                        </div>
                      </div>,
                      <div key="h" title={host} style={{ fontSize: 12.5, minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{host}</div>,
                      <span key="s" style={{ fontSize: 12, textTransform: "capitalize" }}>{m.status || "—"}</span>,
                      <span
                        key="a"
                        title={avail.reason}
                        style={{
                          display: "inline-block",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 999,
                          padding: "3px 9px",
                          ...pillStyle,
                        }}
                      >
                        {avail.label}
                      </span>,
                      <button
                        key="v"
                        type="button"
                        disabled={!avail.canView}
                        onClick={() => openTranscript(m.id)}
                        title={avail.canView ? "Open transcript" : avail.reason}
                        aria-label={avail.canView ? "View transcript" : avail.reason}
                        style={viewBtnStyle(avail.canView)}
                      >
                        View
                      </button>,
                    ],
                  };
                })}
              />
            </div>
          )}
          <div style={{ padding: "0 14px 12px" }}>
            <AdminListPagination
              page={txSafePage}
              pageSize={txPageSize}
              total={filteredTranscriptMeetings.length}
              onPageChange={setTxPage}
              onPageSizeChange={setTxPageSize}
              isLight={isLight}
              adminBorder={adminBorder}
              adminTextMuted={adminTextMuted}
              adminTextMain={adminTextMain}
              label="transcripts"
            />
          </div>
        </div>
      ) : (
        <div className="adm-card" style={{ overflow: "hidden" }}>
          <div className="adm-card-header adm-card-header--search">
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              Email delivery (
              {adminSearch.trim() || emailFilter !== "all"
                ? `${filteredEmailRows.length} of ${emailDbTotal}`
                : emailDbTotal}
              )
              <div className="adm-card-sub">
                Recap emails recorded for MeetAI sessions. Use View to preview the template users receive.
              </div>
            </div>
            <AdminSearchToolbar
              value={adminSearch}
              onChange={setAdminSearch}
              placeholder="Search emails by meeting, host, recipient, status…"
              adminTextMuted={adminTextMuted}
              filteredCount={filteredEmailRows.length}
              totalCount={emailDbTotal}
            />
          </div>
          <div
            role="tablist"
            aria-label="Email delivery filters"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px 12px",
              borderBottom: `1px solid ${adminBorder}`,
              background: isLight ? "rgba(248,250,252,0.85)" : "rgba(255,255,255,0.02)",
            }}
          >
            {[
              { id: "all", label: "All", count: emailFilterCounts.all || emailDbTotal },
              { id: "sent", label: "Sent", count: emailFilterCounts.sent },
              { id: "pending", label: "Pending", count: emailFilterCounts.pending },
              { id: "failed", label: "Failed", count: emailFilterCounts.failed },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={emailFilter === f.id}
                style={{
                  ...pill(emailFilter === f.id),
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                }}
                onClick={() => setEmailFilter(f.id)}
              >
                {f.label}
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    opacity: 0.75,
                    minWidth: 16,
                    textAlign: "center",
                  }}
                >
                  {f.count}
                </span>
              </button>
            ))}
            {(smtpReport?.totals?.completedMeetings ?? 0) > emailRows.length ? (
              <span style={{ fontSize: 11, color: adminTextSoft, alignSelf: "center" }}>
                Counts above are org-wide · table lists latest {emailRows.length} of{" "}
                {smtpReport.totals.completedMeetings} sessions
              </span>
            ) : null}
          </div>
          {smtpLoading && !smtpReport ? (
            <div style={{ padding: 20, fontSize: 12.5, color: adminTextSoft }}>Loading email delivery…</div>
          ) : smtpError && !smtpReport ? (
            <div style={{ padding: 20, fontSize: 12.5, color: "#b91c1c" }}>{smtpError}</div>
          ) : filteredEmailRows.length === 0 ? (
            <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 12.5, color: adminTextSoft }}>
              {emailRows.length === 0 ? "No email delivery records yet." : "No emails match your filters."}
            </div>
          ) : (
            <div className="adm-scroll-panel" style={{ maxHeight: 480 }}>
              <AdminMuiTable
                isLight={isLight}
                stickyHeader
                maxHeight={480}
                minWidth={760}
                columns={[
                  { id: "meeting", label: "Meeting", width: 240, cellSx: { overflow: "hidden", maxWidth: 240 } },
                  { id: "host", label: "Host", width: 150, hideOnPhone: true },
                  { id: "status", label: "Status", width: 118 },
                  { id: "to", label: "Sent to", align: "right", width: 88, hideOnPhone: true },
                  { id: "when", label: "When", width: 128, hideOnPhone: true },
                  { id: "view", label: "", width: 88, align: "right" },
                ]}
                rows={pagedEmailRows.map((m, i) => {
                  const code = String(m.emailStatusCode || "").toLowerCase();
                  const sent = m.emailSent === true || code === "sent" || code === "no_discussion";
                  const failed = code.includes("fail") || code === "smtp_failed";
                  const statusLabel = m.emailStatusLabel || (sent ? "Sent" : failed ? "Failed" : "Pending");
                  const pillTone = adminTranscriptAvailabilityPillStyle(
                    sent ? "available" : failed ? "failed" : "unavailable",
                    isLight
                  );
                  const recipientCount =
                    Number(m.emailRecipientsCount) ||
                    (Array.isArray(m.recapSentTo) ? m.recapSentTo.length : 0) ||
                    0;
                  const whenShort = (() => {
                    if (!m.emailStatusAt) return "—";
                    const d = new Date(m.emailStatusAt);
                    if (Number.isNaN(d.getTime())) return "—";
                    return d.toLocaleString("en-IN", {
                      timeZone: APP_DISPLAY_TZ,
                      day: "2-digit",
                      month: "short",
                      hour: "numeric",
                      minute: "2-digit",
                    });
                  })();
                  const whenFull = (() => {
                    if (!m.emailStatusAt) return "";
                    const d = new Date(m.emailStatusAt);
                    if (Number.isNaN(d.getTime())) return "";
                    return d.toLocaleString("en-IN", {
                      timeZone: APP_DISPLAY_TZ,
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit",
                    });
                  })();
                  const hostSrc = meetingById.get(String(m.id)) || m;
                  const hostEmail = meetingHostEmail(hostSrc) || "";
                  const hostName = meetingHostName(hostSrc) || hostEmail || "—";
                  return {
                    key: m.id || i,
                    cells: [
                      <div key="t" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }} title={m.title || "Meeting"}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.title || "Meeting"}
                        </div>
                        <div style={{ fontSize: 10.5, color: adminTextSoft, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(m.date ? formatYmdDisplay(m.date) : "—")}
                          {m.emailStatusMessage ? ` · ${m.emailStatusMessage}` : ""}
                        </div>
                      </div>,
                      <div key="h" style={{ fontSize: 12.5, minWidth: 0 }} title={hostEmail && hostEmail !== hostName ? `${hostName} (${hostEmail})` : hostName}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hostName}</div>
                        {hostEmail && hostEmail !== hostName ? (
                          <div style={{ fontSize: 10.5, color: adminTextSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {hostEmail}
                          </div>
                        ) : null}
                      </div>,
                      <span
                        key="s"
                        title={m.emailStatusMessage || statusLabel}
                        style={{
                          display: "inline-block",
                          maxWidth: 110,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 999,
                          padding: "3px 9px",
                          ...pillTone,
                        }}
                      >
                        {statusLabel}
                      </span>,
                      <span
                        key="n"
                        title={
                          recipientCount
                            ? `${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`
                            : "No recipients recorded"
                        }
                        style={{
                          display: "inline-block",
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: adminTextMain,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {recipientCount || "—"}
                      </span>,
                      <span
                        key="w"
                        title={whenFull || undefined}
                        style={{
                          display: "inline-block",
                          fontSize: 11.5,
                          color: adminTextMuted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                        }}
                      >
                        {whenShort}
                      </span>,
                      <button
                        key="v"
                        type="button"
                        onClick={() => openEmailPreview(m)}
                        style={viewBtnStyle(true)}
                        title="Preview email template"
                      >
                        View
                      </button>,
                    ],
                  };
                })}
              />
            </div>
          )}
          <div style={{ padding: "0 14px 12px" }}>
            <AdminListPagination
              page={emailSafePage}
              pageSize={emailPageSize}
              total={filteredEmailRows.length}
              onPageChange={setEmailPage}
              onPageSizeChange={setEmailPageSize}
              isLight={isLight}
              adminBorder={adminBorder}
              adminTextMuted={adminTextMuted}
              adminTextMain={adminTextMain}
              label="emails"
            />
          </div>
        </div>
      )}

      {detail ? (
        <Drawer
          anchor="right"
          open
          onClose={closeDetail}
          ModalProps={{ keepMounted: false, style: { zIndex: 5000 } }}
          slotProps={{
            paper: {
              style: {
                position: "fixed",
                top: 0,
                right: 0,
                height: "100vh",
                width: "760px",
                minWidth: "760px",
                maxWidth: "min(760px, 100vw)",
                zIndex: 5001,
                background: "#fff",
                borderLeft: "1px solid #e2e8f0",
                boxSizing: "border-box",
                borderRadius: 0,
                margin: 0,
                overflow: "hidden",
              },
            },
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={detail.kind === "email" ? "Recap email preview" : "Meeting transcript"}
            className="adm-detail-drawer"
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              width: "100%",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <div
              className="adm-detail-head"
              style={{
                padding: "14px 16px 0",
                borderBottom: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>
                    {detail.kind === "email" ? "Email template preview" : "User meeting view"}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0f2a4a" }}>
                    {detail.meeting?.title || (detail.kind === "email" ? "Recap email" : "Meeting transcript")}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#64748b" }}>
                    {detail.kind === "email" ? (
                      <>
                        {detail.subject ? <span style={{ display: "block", marginBottom: 2 }}>Subject: {detail.subject}</span> : null}
                        {detail.previewAs?.email
                          ? `Preview as ${detail.previewAs.name || detail.previewAs.email} · ${detail.previewAs.email}`
                          : (meetingHostName(detail.meeting || {}) || meetingHostEmail(detail.meeting || {}) || "")}
                      </>
                    ) : (
                      <>
                        {meetingHostName(detail.meeting || {}) || meetingHostEmail(detail.meeting || {}) || ""}
                        {detail.lineCount != null ? ` · ${detail.lineCount} lines` : ""}
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {detail.kind === "transcript" ? (
                    <>
                      <div
                        role="group"
                        aria-label="Transcript source"
                        style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}
                      >
                        {["clean", "raw"].map((v) => (
                          <button
                            key={v}
                            type="button"
                            disabled={detailLoading}
                            onClick={() => openTranscript(detail.meetingId, { view: v })}
                            style={{
                              border: "none",
                              padding: "6px 10px",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: detailLoading ? "wait" : "pointer",
                              background: (detail.transcriptView || transcriptAdminView) === v ? "#0f2a4a" : "#fff",
                              color: (detail.transcriptView || transcriptAdminView) === v ? "#fff" : "#475569",
                            }}
                          >
                            {v === "clean" ? "Clean" : "Raw"}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        disabled={reprocessBusy || detailLoading}
                        onClick={reprocessMeetingSummaryAdmin}
                        style={{
                          border: "1px solid #e2e8f0",
                          background: "#fff",
                          borderRadius: 8,
                          padding: "6px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: reprocessBusy ? "wait" : "pointer",
                          color: "#0f2a4a",
                        }}
                      >
                        {reprocessBusy ? "Reprocessing…" : "Reprocess summary"}
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={closeDetail}
                    aria-label="Close"
                    title="Close"
                    style={{
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      borderRadius: 8,
                      width: 32,
                      height: 32,
                      cursor: "pointer",
                      color: "#475569",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width={16} height={16} fill="none" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
              {detail.meeting?.approvalSummary ? <div aria-label="Approval status" style={{ marginTop: 12, padding: "10px 12px", border: "1px solid #BFDBFE", borderRadius: 10, background: "#EFF6FF", color: "#1E3A8A", fontSize: 12 }}><strong>Approval: {detail.meeting.approvalSummary.status || "pending"}</strong><div style={{ marginTop: 4 }}>Approver: {detail.meeting.approvalSummary.approverName || detail.meeting.approvalSummary.approverEmail || "—"}</div>{String(detail.meeting.approvalSummary.status || "").toLowerCase() === "approved" ? <div style={{ marginTop: 4 }}>Sent to: {Array.isArray(detail.meeting.approvalSummary.recipients) && detail.meeting.approvalSummary.recipients.length ? detail.meeting.approvalSummary.recipients.join(", ") : "No recipients recorded"}</div> : null}</div> : null}
              {/* In-drawer tabs: flip content without dismissing the drawer. */}
              <div role="tablist" aria-label="Session detail views" style={{ display: "flex", gap: 6, padding: "12px 0" }}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={detail.kind !== "email"}
                  disabled={detailLoading}
                  style={{ ...pill(detail.kind !== "email"), opacity: detailLoading ? 0.6 : 1 }}
                  onClick={() => switchDetailKind("transcript")}
                >
                  Transcripts
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={detail.kind === "email"}
                  disabled={detailLoading}
                  style={{ ...pill(detail.kind === "email"), opacity: detailLoading ? 0.6 : 1 }}
                  onClick={() => switchDetailKind("email")}
                >
                  Email delivery
                </button>
              </div>
            </div>
            {detail.kind === "transcript" && reprocessMsg ? (
              <div style={{ padding: "8px 16px", fontSize: 12, color: "#047857", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
                {reprocessMsg}
              </div>
            ) : null}
            <div
              className="adm-detail-body"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: detail.kind === "email" ? "hidden" : "auto",
                padding: detail.kind === "email" ? 0 : 16,
                background: detail.kind === "email" ? "#F1F5F9" : "#F4F7FB",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {detailLoading ? (
                <div style={{ fontSize: 13, color: "#64748b", padding: 16 }}>
                  {detail.kind === "email" ? "Loading email template…" : "Loading transcript…"}
                </div>
              ) : detailError ? (
                <div style={{ fontSize: 13, color: "#b91c1c", padding: 16 }}>{detailError}</div>
              ) : detail.kind === "email" ? (
                detail.html ? (
                  <div
                    role="document"
                    aria-label="Recap email template"
                    className="adm-email-preview"
                    onClick={handleEmailPreviewBodyClick}
                    dangerouslySetInnerHTML={{
                      __html: wireAdminRecapEmailPreviewHtml(detail.html, detail.meetingId),
                    }}
                    style={{
                      flex: 1,
                      width: "100%",
                      minHeight: 520,
                      overflow: "auto",
                      background: "#F1F5F9",
                    }}
                  />
                ) : (
                  <div style={{ fontSize: 13, color: "#64748b", padding: 16 }}>No email HTML available for this meeting.</div>
                )
              ) : (
                <AdminMeetingUserView
                  meeting={detail.meeting}
                  transcriptPreviewRequest={transcriptPreviewRequest}
                  onPreviewTranscript={() => setTranscriptPreviewRequest((v) => v + 1)}
                  currentUserEmail={sessionEmail}
                  currentUserName={sessionName}
                />
              )}
            </div>
          </div>
        </Drawer>
      ) : null}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   BOT ADMIN PORTAL — open `/admin` while signed in as an account allowed
   by backend admin (`BOT_EMAIL` or `ADMIN_PORTAL_EMAIL` / frontend env mirrors).
════════════════════════════════════════════════════════════════ */
function BotAdminPortal({ sessionEmail, onLogout, onOpenMainApp }){
  const tipApi = usePersonHoverTip();
  const [page,setPage]=useState(readInitialAdminPage);
  const [visitedPages, setVisitedPages] = useState(() => new Set([readInitialAdminPage()]));
  const goAdminPage = useCallback((id) => {
    if (!ALLOWED_ADMIN_PAGES.has(id)) return;
    startTransition(() => {
      setVisitedPages((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setPage(id);
    });
  }, []);
  const adminTabSeen = useCallback((id) => page === id || visitedPages.has(id), [page, visitedPages]);
  const [adminTheme] = useState("light");
  const [adminSidebarCollapsed, setAdminSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(MEETAI_ADMIN_SIDEBAR_KEY) === "1";
    } catch (_) {
      return false;
    }
  });
  // 1024px matches user-shell tablet breakpoint — sidebar becomes an off-canvas drawer.
  const isAdminMobile = useIsTablet();
  const [adminMobileNavOpen, setAdminMobileNavOpen] = useState(false);
  useEffect(() => {
    if (!isAdminMobile) setAdminMobileNavOpen(false);
  }, [isAdminMobile]);
  useEffect(() => {
    if (!isAdminMobile || !adminMobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isAdminMobile, adminMobileNavOpen]);
  const [signOutConfirmOpen,setSignOutConfirmOpen]=useState(false);
  const [adminUserMenuOpen, setAdminUserMenuOpen] = useState(false);
  const [adminLiveListOpen, setAdminLiveListOpen] = useState(false);
  const [portalAdminsOpen, setPortalAdminsOpen] = useState(false);
  const adminUserMenuRef = useRef(null);
  const adminUserBtnRef = useRef(null);
  const adminLiveListRef = useRef(null);
  const adminLiveBtnRef = useRef(null);
  const [adminUserMenuPos, setAdminUserMenuPos] = useState({ top: 0, right: 0 });
  const [adminLiveListPos, setAdminLiveListPos] = useState({ top: 0, right: 0 });
  const [adminCalDetailMeeting, setAdminCalDetailMeeting] = useState(null);
  const [allMeetings,setAllMeetings]=useState([]);
  /** Calendar tab: dispatched + upcoming scheduled (bot not sent yet). */
  const [adminCalendarSessions, setAdminCalendarSessions] = useState([]);
  const adminCalendarLoadedKeyRef = useRef("");
  const adminCalendarSyncedAtRef = useRef(new Map());
  /** Mirrors adminCalendarError so connectivity listeners can read it without re-subscribing. */
  const adminCalendarErroredRef = useRef(false);
  const [allUsers,setAllUsers]=useState([]);
  const [status,setStatus]=useState(null);
   const [logs,setLogs]=useState([]);
   const [logsLoading, setLogsLoading] = useState(true);
   const [logFilterAction, setLogFilterAction] = useState("all");
   const [logFilterType, setLogFilterType] = useState("all");
   const [logFilterOwner, setLogFilterOwner] = useState("");
   const [logFilterMeeting, setLogFilterMeeting] = useState("");
   const [logFilterDateFrom, setLogFilterDateFrom] = useState("");
   const [logFilterDateTo, setLogFilterDateTo] = useState("");
  const [expandedLogIdx, setExpandedLogIdx] = useState(null);
  const [adminLogsPage, setAdminLogsPage] = useState(1);
  const [adminLogsPageSize, setAdminLogsPageSize] = useState(ADMIN_LOGS_PAGE_SIZE_DEFAULT);
  /** Accordion: only one meeting detail open (avoids re-rendering heavy trees). */
  const [allMeetingsOpenId, setAllMeetingsOpenId] = useState(null);
  /** Accordion: only one "show full lists" expansion. */
  const [allMeetingsListsId, setAllMeetingsListsId] = useState(null);
  const [adminMeetingDrawerKey, setAdminMeetingDrawerKey] = useState(null);
  const [adminUserDrawerId, setAdminUserDrawerId] = useState(null);
  const [usageApi,setUsageApi]=useState(loadCachedUsageApi);
  const [usageApiLoading, setUsageApiLoading] = useState(false);
  const [usageApiErr, setUsageApiErr] = useState(null);
  const [assistantUsageApi, setAssistantUsageApi] = useState(null);
  const [smtpUsageApi, setSmtpUsageApi] = useState(null);
  const [smtpUsageLoading, setSmtpUsageLoading] = useState(false);
  const [smtpUsageErr, setSmtpUsageErr] = useState(null);
  const smtpUsageApiRef = useRef(null);
  smtpUsageApiRef.current = smtpUsageApi;
  const usageTabVisitedRef = useRef(false);
  const smtpTabVisitedRef = useRef(false);
  const chatbotTabVisitedRef = useRef(false);
  const feedbackTabVisitedRef = useRef(false);
  const [adminFeedbackItems, setAdminFeedbackItems] = useState([]);
  const [adminFeedbackTotal, setAdminFeedbackTotal] = useState(0);
  const [adminFeedbackLoading, setAdminFeedbackLoading] = useState(false);
  const [adminFeedbackErr, setAdminFeedbackErr] = useState(null);
  const [adminFeedbackOpenId, setAdminFeedbackOpenId] = useState(null);
  const [adminFeedbackPreview, setAdminFeedbackPreview] = useState(null);
  const [loading,setLoading]=useState(true);
  const [adminReady, setAdminReady] = useState(false);
  const [adminRefreshing, setAdminRefreshing] = useState(false);
  const adminBootstrappedRef = useRef(false);
  const [adminError,setAdminError]=useState(null);
  const [calYear,setCalYear]=useState(()=>new Date().getFullYear());
  const [calMonth,setCalMonth]=useState(()=>new Date().getMonth());
  const [calView,setCalView]=useState("day");
  const [calSelDate,setCalSelDate]=useState(()=>todayStr());
  const [usageRange, setUsageRange] = useState("today");
  const [usageDateFrom, setUsageDateFrom] = useState(() => dateToYmd(new Date()));
  const [usageDateTo, setUsageDateTo] = useState(() => dateToYmd(new Date()));
  const [usageChartShowMeetings, setUsageChartShowMeetings] = useState(true);
  const [usageChartShowCost, setUsageChartShowCost] = useState(true);
  const [usageChartShowTranscript, setUsageChartShowTranscript] = useState(false);
  const [usageChartShowBotHours, setUsageChartShowBotHours] = useState(true);
  const [adminReloadTick, setAdminReloadTick] = useState(0);
  const [budgetInput, setBudgetInput] = useState("");
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetErr, setBudgetErr] = useState(null);
  const [budgetHistoryOpen, setBudgetHistoryOpen] = useState(false);
  const [recallCreditHistoryOpen, setRecallCreditHistoryOpen] = useState(false);
  const [recallCreditHistoryOpenedAt, setRecallCreditHistoryOpenedAt] = useState(null);
  const [recallCreditRefreshing, setRecallCreditRefreshing] = useState(false);
  const [recallCreditRefreshErr, setRecallCreditRefreshErr] = useState(null);
  const [recallMcpFeaturesOpen, setRecallMcpFeaturesOpen] = useState(false);
  const [budgetHistory, setBudgetHistory] = useState(null);
  const [budgetHistoryLoading, setBudgetHistoryLoading] = useState(false);
  const [budgetHistoryErr, setBudgetHistoryErr] = useState(null);
  const [creditThresholdEditorOpen, setCreditThresholdEditorOpen] = useState(false);
  const [creditThresholdDraft, setCreditThresholdDraft] = useState("");
  const [creditThresholdSaving, setCreditThresholdSaving] = useState(false);
  const [creditThresholdErr, setCreditThresholdErr] = useState(null);
  const [creditRecipientsOpen, setCreditRecipientsOpen] = useState(false);
  const [creditRecipients, setCreditRecipients] = useState([]);
  const [creditRecipientsLoading, setCreditRecipientsLoading] = useState(false);
  const [creditRecipientErr, setCreditRecipientErr] = useState(null);
  const [recipientSaving, setRecipientSaving] = useState(false);
  const [recipientPickerQuery, setRecipientPickerQuery] = useState("");
  const [adminUsersForPicker, setAdminUsersForPicker] = useState([]);
  const [creditAlertHistoryOpen, setCreditAlertHistoryOpen] = useState(false);
  const [creditAlertHistory, setCreditAlertHistory] = useState([]);
  const [creditAlertHistoryLoading, setCreditAlertHistoryLoading] = useState(false);
  const [creditAlertHistoryErr, setCreditAlertHistoryErr] = useState(null);
  const [monthlyUsageFilter, setMonthlyUsageFilter] = useState("");
  const [monthlyUsagePage, setMonthlyUsagePage] = useState(0);
  const [topHostsLimit, setTopHostsLimit] = useState("all");
  const [usageSessionsPage, setUsageSessionsPage] = useState(0);
  const [usageSessionsPageSize, setUsageSessionsPageSize] = useState(5);
  const [usageStatusFilter, setUsageStatusFilter] = useState("");
  // Per-tab search: a single shared query leaked keystrokes across every admin
  // tab (typing in Meetings also filtered Users/Logs/Usage). Keep one query
  // per tab; each tab's input reads/writes only its own entry, so switching
  // tabs preserves that tab's query without affecting the others.
  const [adminSearchByTab, setAdminSearchByTab] = useState({});
  const setAdminTabSearch = useCallback((tabId, arg) => {
    const tid = String(tabId || "").trim();
    if (!tid) return;
    if (typeof arg === "function") {
      setAdminSearchByTab((prev) => {
        const cur = prev[tid] ?? "";
        const next = arg(cur);
        return next === cur ? prev : { ...prev, [tid]: next };
      });
      return;
    }
    const next =
      arg && typeof arg === "object" && "target" in arg
        ? String(arg.target?.value ?? "")
        : String(arg ?? "");
    setAdminSearchByTab((prev) => ((prev[tid] ?? "") === next ? prev : { ...prev, [tid]: next }));
  }, []);
  const overviewAdminSearch = adminSearchByTab.overview ?? "";
  const usageHostsAdminSearch = adminSearchByTab.usageHosts ?? "";
  const usageSessionsAdminSearch = adminSearchByTab.usageSessions ?? "";
  const allMeetingsAdminSearch = adminSearchByTab.allMeetings ?? "";
  const calendarAdminSearch = adminSearchByTab.calendar ?? "";
  const usersAdminSearch = adminSearchByTab.users ?? "";
  const logsAdminSearch = adminSearchByTab.logs ?? "";
  const feedbackAdminSearch = adminSearchByTab.feedback ?? "";
  const transcriptsEmailAdminSearch = adminSearchByTab.transcriptsEmail ?? "";
  const deferredOverviewSearch = useDeferredValue(overviewAdminSearch);
  const deferredUsageHostsSearch = useDeferredValue(usageHostsAdminSearch);
  const deferredUsageSessionsSearch = useDeferredValue(usageSessionsAdminSearch);
  const deferredAllMeetingsSearch = useDeferredValue(allMeetingsAdminSearch);
  const deferredCalendarSearch = useDeferredValue(calendarAdminSearch);
  const deferredUsersSearch = useDeferredValue(usersAdminSearch);
  const deferredLogsSearch = useDeferredValue(logsAdminSearch);
  const deferredFeedbackSearch = useDeferredValue(feedbackAdminSearch);
  const deferredTranscriptsEmailSearch = useDeferredValue(transcriptsEmailAdminSearch);
  /** Active nav tab search (toolbar on current page only). */
  const adminSearch = adminSearchByTab[page] ?? "";
  const setAdminSearch = useCallback((arg) => setAdminTabSearch(page, arg), [page, setAdminTabSearch]);
  const deferredLogFilterOwner = useDeferredValue(logFilterOwner);
  const deferredLogFilterMeeting = useDeferredValue(logFilterMeeting);
  const logsTabActive = page === "logs";
  const [adminMeetingsDateFrom, setAdminMeetingsDateFrom] = useState("");
  const [adminMeetingsDateTo, setAdminMeetingsDateTo] = useState("");
  const [adminMeetingsSortOrder, setAdminMeetingsSortOrder] = useState("newest");
  const [adminMeetingsLoading, setAdminMeetingsLoading] = useState(true);
  const [adminMeetingsError, setAdminMeetingsError] = useState(null);
  const [adminMeetingsPage, setAdminMeetingsPage] = useState(1);
  const [adminUsersPage, setAdminUsersPage] = useState(1);
  const [adminMeetingsPageSize, setAdminMeetingsPageSize] = useState(ADMIN_MEETINGS_PAGE_SIZE_DEFAULT);
  const [adminUsersPageSize, setAdminUsersPageSize] = useState(ADMIN_USERS_PAGE_SIZE_DEFAULT);
  const [adminUsersSortBy, setAdminUsersSortBy] = useState("user");
  const [adminUsersSortDir, setAdminUsersSortDir] = useState("asc");
  const [adminUsersSinceFrom, setAdminUsersSinceFrom] = useState("");
  const [adminUsersSinceTo, setAdminUsersSinceTo] = useState("");
  const [adminCalendarListPage, setAdminCalendarListPage] = useState(1);
  const [adminCalendarListPageSize, setAdminCalendarListPageSize] = useState(ADMIN_CALENDAR_LIST_PAGE_SIZE_DEFAULT);
  const [adminCalendarFetching, setAdminCalendarFetching] = useState(false);
  const [adminCalendarSyncing, setAdminCalendarSyncing] = useState(false);
  const [adminCalendarError, setAdminCalendarError] = useState(null);
  const [adminCalendarLoadedKey, setAdminCalendarLoadedKey] = useState("");
  const [adminCalendarTotal, setAdminCalendarTotal] = useState(0);

  useEffect(() => {
    try {
      sessionStorage.setItem(MEETAI_ADMIN_PAGE_KEY, page);
    } catch (_) {}
  }, [page]);

  useEffect(() => {
    if (!adminUserMenuOpen) return undefined;
    const place = () => {
      const btn = adminUserBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setAdminUserMenuPos({
        top: Math.round(r.bottom + 8),
        right: Math.round(Math.max(8, window.innerWidth - r.right)),
      });
    };
    place();
    const onDoc = (e) => {
      const t = e.target;
      if (adminUserMenuRef.current?.contains(t)) return;
      if (adminUserBtnRef.current?.contains(t)) return;
      setAdminUserMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAdminUserMenuOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [adminUserMenuOpen]);

  useEffect(() => {
    if (!adminLiveListOpen) return undefined;
    const place = () => {
      const btn = adminLiveBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setAdminLiveListPos({
        top: Math.round(r.bottom + 8),
        right: Math.round(Math.max(8, window.innerWidth - r.right)),
      });
    };
    place();
    const onDoc = (e) => {
      const t = e.target;
      if (adminLiveListRef.current?.contains(t)) return;
      if (adminLiveBtnRef.current?.contains(t)) return;
      setAdminLiveListOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAdminLiveListOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [adminLiveListOpen]);

  useEffect(() => {
    // Each tab keeps its own search query (see adminSearchByTab) — only reset
    // pagination/expansion on switch, never another tab's query.
    setExpandedLogIdx(null);
    setAdminMeetingsPage(1);
    setAllMeetingsOpenId(null);
    setAllMeetingsListsId(null);
    setAdminUsersPage(1);
  }, [page]);

  useEffect(() => {
    if (page !== "usage") return;
    setUsageStatusFilter((prev) =>
      prev === "upcoming" || prev === "cancelled" ? "" : prev
    );
  }, [page]);

  useEffect(() => {
    setExpandedLogIdx(null);
  }, [adminSearch]);
  useEffect(() => {
    setAdminMeetingsPage(1);
    setAllMeetingsOpenId(null);
    setAllMeetingsListsId(null);
  }, [allMeetingsAdminSearch, adminMeetingsDateFrom, adminMeetingsDateTo, adminMeetingsSortOrder]);
  useEffect(() => {
    try {
      localStorage.setItem(MEETAI_ADMIN_THEME_KEY, adminTheme);
    } catch (_) {}
  }, [adminTheme]);
  useEffect(() => {
    try {
      localStorage.setItem(MEETAI_ADMIN_SIDEBAR_KEY, adminSidebarCollapsed ? "1" : "0");
    } catch (_) {}
    (tipApi.hidePersonHoverTipNow || tipApi.hidePersonHoverTipSoon)();
  }, [adminSidebarCollapsed, tipApi]);

  // Manual refresh / poll tick — allow calendar soft-sync again.
  useEffect(() => {
    adminCalendarSyncedAtRef.current.clear();
  }, [adminReloadTick]);

  // Load admin data — phased requests so one slow endpoint does not block the whole portal.
  useEffect(() => {
    let cancelled = false;
    async function loadAdminData() {
      const isFirstLoad = !adminBootstrappedRef.current;
      if (isFirstLoad) setLoading(true);
      else setAdminRefreshing(true);

      const failMsgs = [];
      let anyCoreOk = false;

      try {
        // Start meetings immediately — do not wait for users/status/logs to finish.
        const meetingsPromise = fetchAllAdminMeetings();
         setAdminMeetingsLoading(true);
         setLogsLoading(true);

         const [usersR, statusR, logsR] = await Promise.allSettled([
          apiFetch("/admin/users"),
          apiFetch("/admin/status"),
          apiFetch("/admin/logs"),
        ]);

        if (cancelled) return;

        if (usersR.status === "fulfilled") {
          setAllUsers(Array.isArray(usersR.value) ? usersR.value : []);
          anyCoreOk = true;
        } else if (isFirstLoad) failMsgs.push(usersR.reason?.message || "Users");

        if (statusR.status === "fulfilled") {
          setStatus(statusR.value);
          anyCoreOk = true;
        } else if (isFirstLoad) failMsgs.push(statusR.reason?.message || "Status");

        if (logsR.status === "fulfilled") {
          setLogs(Array.isArray(logsR.value) ? logsR.value : []);
          anyCoreOk = true;
        } else if (isFirstLoad) failMsgs.push(logsR.reason?.message || "Logs");
        setLogsLoading(false);

        if (anyCoreOk) {
          adminBootstrappedRef.current = true;
          setAdminReady(true);
          setAdminError(null);
          if (isFirstLoad) setLoading(false);
        }

        const meetingsR = await Promise.allSettled([meetingsPromise]);
        if (cancelled) return;

        if (meetingsR[0].status === "fulfilled") {
          setAllMeetings(
            Array.isArray(meetingsR[0].value) ? meetingsR[0].value.map(normalizeMeeting) : []
          );
          setAdminMeetingsError(null);
          adminBootstrappedRef.current = true;
          setAdminReady(true);
          setAdminError(null);
        } else {
          const msg = meetingsR[0].reason?.message || "Meetings";
          if (isFirstLoad && !anyCoreOk) failMsgs.push(msg);
          else setAdminMeetingsError(msg);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Admin data load failed:", err);
        if (isFirstLoad && !adminBootstrappedRef.current) {
          failMsgs.push(err.message || "Could not load admin data");
        } else {
          setAdminMeetingsError(err.message || "Could not load meetings");
        }
      } finally {
        if (!cancelled) {
          if (isFirstLoad && !adminBootstrappedRef.current) {
            setAllMeetings([]);
            setAllUsers([]);
            setStatus(null);
            setLogs([]);
            setLogFilterAction("all");
            setLogFilterType("all");
            setLogFilterOwner("");
            setLogFilterMeeting("");
            setLogFilterDateFrom("");
            setLogFilterDateTo("");
            const msg = failMsgs.filter(Boolean).join(" · ") || "Could not load admin data from the API.";
            setAdminError(msg);
          }
          setAdminMeetingsLoading(false);
          setLoading(false);
          setAdminRefreshing(false);
          setLogsLoading(false);
        }
      }
    }
    loadAdminData();
    const t = setInterval(loadAdminData, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [adminReloadTick]);

  // Calendar tab — paint DB rows immediately; Google sync runs in background (no long block).
  // Loads by visible view range (day/week/schedule/month) with server pagination to avoid timeouts.
  useEffect(() => {
    if (!adminReady || page !== "calendar") return undefined;
    let cancelled = false;
    let retryTimer = 0;
    const CAL_SOFT_TTL_MS = 90 * 1000;
    const CAL_MAX_RETRIES = 3;
    const CAL_RETRY_BASE_MS = 1200; // 1.2s, 2.4s, 4.8s

    async function fetchAdminCalendarPages(from, to, { skipSync } = {}) {
      const base = `for=calendar&lite=1&calendarSlim=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&pageSize=${ADMIN_CALENDAR_FETCH_PAGE_SIZE}`;
      const syncFlag = skipSync ? "&skipSync=1" : "";
      const first = normalizeAdminMeetingsPayload(
        await apiFetch(`/admin/meetings?${base}&page=1${syncFlag}`)
      );
      let items = first.items.slice();
      const total = first.total;
      const totalPages = Math.min(
        ADMIN_CALENDAR_MAX_PAGES,
        Math.max(1, Math.ceil(Math.max(0, total) / ADMIN_CALENDAR_FETCH_PAGE_SIZE))
      );
      for (let start = 2; start <= totalPages; start += ADMIN_CALENDAR_PAGE_CONCURRENCY) {
        if (cancelled) break;
        const batch = [];
        for (
          let p = start;
          p < start + ADMIN_CALENDAR_PAGE_CONCURRENCY && p <= totalPages;
          p += 1
        ) {
          batch.push(
            apiFetch(`/admin/meetings?${base}&page=${p}&skipSync=1`).then(normalizeAdminMeetingsPayload)
          );
        }
        const pages = await Promise.all(batch);
        let hitEmpty = false;
        for (const next of pages) {
          if (!next.items.length) {
            hitEmpty = true;
            break;
          }
          items = items.concat(next.items);
        }
        if (hitEmpty) break;
      }
      const truncated =
        items.length < total ||
        (total > ADMIN_CALENDAR_MAX_PAGES * ADMIN_CALENDAR_FETCH_PAGE_SIZE &&
          items.length >= ADMIN_CALENDAR_MAX_PAGES * ADMIN_CALENDAR_FETCH_PAGE_SIZE);
      return { items, total, truncated };
    }

    async function loadAdminCalendar(attempt = 0) {
      const { from, to } = googleCalendarFetchRange(
        "calendar",
        calView,
        calYear,
        calMonth,
        calSelDate
      );
      const rangeKey = `${from}|${to}|${calView}`;
      const alreadyPainted = adminCalendarLoadedKeyRef.current === rangeKey;
      const lastSyncAt = adminCalendarSyncedAtRef.current.get(rangeKey) || 0;
      const syncFresh = Date.now() - lastSyncAt < CAL_SOFT_TTL_MS;

      setAdminCalendarError(null);
      adminCalendarErroredRef.current = false;

      // Returning to the same range: keep UI instant; optional quiet refresh only.
      if (alreadyPainted && syncFresh) {
        setAdminCalendarFetching(false);
        setAdminCalendarSyncing(false);
        return;
      }

      try {
        if (!alreadyPainted) {
          setAdminCalendarFetching(true);
          setAdminCalendarSyncing(false);
          const fast = await fetchAdminCalendarPages(from, to, { skipSync: true });
          if (cancelled) return;
          setAdminCalendarSessions(fast.items.map(normalizeMeeting));
          setAdminCalendarTotal(fast.total);
          setAdminCalendarLoadedKey(rangeKey);
          adminCalendarLoadedKeyRef.current = rangeKey;
          setAdminCalendarFetching(false);
        }

        // Kick Google stub sync without holding the UI (server wait default 0).
        setAdminCalendarSyncing(true);
        try {
          void apiFetch(
            `/admin/meetings?for=calendar&lite=1&calendarSlim=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&page=1&pageSize=${ADMIN_CALENDAR_FETCH_PAGE_SIZE}`
          ).catch((syncErr) => {
            if (!cancelled) console.warn("Admin calendar sync kick failed:", syncErr);
          });
          await new Promise((r) => setTimeout(r, alreadyPainted ? 600 : 900));
          if (cancelled) return;
          const again = await fetchAdminCalendarPages(from, to, { skipSync: true });
          if (!cancelled) {
            setAdminCalendarSessions(again.items.map(normalizeMeeting));
            setAdminCalendarTotal(again.total);
            setAdminCalendarLoadedKey(rangeKey);
            adminCalendarLoadedKeyRef.current = rangeKey;
          }
          adminCalendarSyncedAtRef.current.set(rangeKey, Date.now());
        } finally {
          if (!cancelled) setAdminCalendarSyncing(false);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Admin calendar load failed:", err);

        const transient = err?.code === "NETWORK_DOWN" || err?.code === "SERVER_DOWN";
        if (transient && attempt < CAL_MAX_RETRIES) {
          const delay = CAL_RETRY_BASE_MS * 2 ** attempt;
          retryTimer = setTimeout(() => {
            if (!cancelled) loadAdminCalendar(attempt + 1);
          }, delay);
          return;
        }

        setAdminCalendarError(err.message || "Could not load calendar sessions");
        adminCalendarErroredRef.current = true;
        setAdminCalendarFetching(false);
        setAdminCalendarSyncing(false);
      }
    }
    loadAdminCalendar();

    const onBackOnline = () => {
      if (cancelled || !adminCalendarErroredRef.current) return;
      adminCalendarErroredRef.current = false;
      adminCalendarSyncedAtRef.current.clear();
      loadAdminCalendar();
    };
    window.addEventListener("meetai:api-up", onBackOnline);
    window.addEventListener("online", onBackOnline);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      window.removeEventListener("meetai:api-up", onBackOnline);
      window.removeEventListener("online", onBackOnline);
      setAdminCalendarFetching(false);
      setAdminCalendarSyncing(false);
    };
  }, [adminReady, page, calYear, calMonth, calView, calSelDate, adminReloadTick]);

  // Usage data — load on first visit of Usage OR Meetings (meeting cards show
  // per-meeting actuals from it), then cache + background poll.
  useEffect(() => {
    if (!adminReady) return undefined;
    const needsUsage = page === "usage" || page === "meetings";
    if (!needsUsage && !usageTabVisitedRef.current) return undefined;
    if (page === "usage") usageTabVisitedRef.current = true;
    let cancelled = false;
    let usageAbort = null;
    async function loadUsageData(background = false) {
      if (!cancelled && !background) setUsageApiLoading(true);
      // Hard-stop hung /admin/usage (big Recall MCP) so "Fetching…" never sticks.
      usageAbort?.abort?.();
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      usageAbort = controller;
      const abortTimer = controller
        ? setTimeout(() => controller.abort(), 45000)
        : null;
      try {
        const usageData = await apiFetch("/admin/usage", controller ? { signal: controller.signal } : {});
        if (!cancelled) {
          setUsageApi(usageData || null);
          setUsageApiErr(null);
          saveCachedUsageApi(usageData);
        }
      } catch (err) {
        if (!cancelled) {
          const aborted = err?.name === "AbortError" || controller?.signal?.aborted;
          console.error("Admin usage load failed:", err);
          setUsageApiErr(
            aborted
              ? "Recall usage refresh timed out — showing last cached values."
              : err.message || "Failed to load usage data"
          );
        }
      } finally {
        if (abortTimer) clearTimeout(abortTimer);
        if (usageAbort === controller) usageAbort = null;
        if (!cancelled && !background) setUsageApiLoading(false);
      }
    }
    loadUsageData(false);
    const pollMs = page === "usage" ? ADMIN_USAGE_TAB_POLL_MS : ADMIN_USAGE_POLL_MS;
    const t = setInterval(() => loadUsageData(true), pollMs);
    return () => {
      cancelled = true;
      clearInterval(t);
      usageAbort?.abort?.();
    };
  }, [adminReady, page, adminReloadTick]);

  useEffect(() => {
    if (!adminReady) return undefined;
    if (page !== "smtp" && page !== "transcriptsEmail" && !smtpTabVisitedRef.current) return undefined;
    if (page === "smtp" || page === "transcriptsEmail") smtpTabVisitedRef.current = true;
    let cancelled = false;
    async function loadSmtpUsage() {
      const hasCache = Boolean(smtpUsageApiRef.current);
      setSmtpUsageLoading(true);
      if (!hasCache) setSmtpUsageErr(null);
      try {
        const data = await apiFetch("/admin/smtp/usage");
        if (!cancelled) {
          setSmtpUsageApi(data || null);
          setSmtpUsageErr(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Admin SMTP usage load failed:", err);
          setSmtpUsageErr(err.message || "Failed to load SMTP usage");
        }
      } finally {
        if (!cancelled) setSmtpUsageLoading(false);
      }
    }
    loadSmtpUsage();
    const t = setInterval(loadSmtpUsage, ADMIN_USAGE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [adminReady, page, adminReloadTick]);

  useEffect(() => {
    if (!adminReady) return undefined;
    if (page !== "usage" && !usageTabVisitedRef.current) return undefined;
    let cancelled = false;
    async function loadRecipientsCount() {
      try {
        const data = await apiFetch("/admin/credit-alert-recipients");
        if (!cancelled && Array.isArray(data?.recipients)) {
          setCreditRecipients(data.recipients);
        }
      } catch (_) {
        if (!cancelled) setCreditRecipients([]);
      }
    }
    loadRecipientsCount();
    return () => {
      cancelled = true;
    };
  }, [adminReady, page, adminReloadTick]);

  useEffect(() => {
    if (!adminReady) return undefined;
    if (page !== "chatbotUsage" && !chatbotTabVisitedRef.current) return undefined;
    if (page === "chatbotUsage") chatbotTabVisitedRef.current = true;
    let cancelled = false;
    async function loadChatUsageData() {
      try {
        const chatUsageData = await apiFetch("/admin/assistant-usage");
        if (!cancelled) setAssistantUsageApi(chatUsageData || null);
      } catch (err) {
        if (!cancelled) console.error("Admin chatbot usage load failed:", err);
      }
    }
    loadChatUsageData();
    const t = setInterval(loadChatUsageData, ADMIN_USAGE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [adminReady, page, adminReloadTick]);

  useEffect(() => {
    if (!adminReady) return undefined;
    if (page !== "feedback" && !feedbackTabVisitedRef.current) return undefined;
    if (page === "feedback") feedbackTabVisitedRef.current = true;
    let cancelled = false;
    async function loadFeedback() {
      setAdminFeedbackLoading(true);
      try {
        const data = await apiFetch("/admin/feedback?limit=200");
        if (cancelled) return;
        setAdminFeedbackItems(Array.isArray(data?.items) ? data.items : []);
        setAdminFeedbackTotal(Number(data?.total) || 0);
        setAdminFeedbackErr(null);
      } catch (err) {
        if (!cancelled) {
          console.error("Admin feedback load failed:", err);
          setAdminFeedbackErr(err.message || "Failed to load feedback");
        }
      } finally {
        if (!cancelled) setAdminFeedbackLoading(false);
      }
    }
    loadFeedback();
    return () => {
      cancelled = true;
    };
  }, [adminReady, page, adminReloadTick]);

  const liveMeetings = useMemo(() => {
    const now = Date.now();
    return dedupeDbMeetings(allMeetings, true, { adminList: true })
      .filter((m) => effectiveMeetingStatus(m, now) === "live")
      .sort((a, b) => {
        const aReady = meetingLiveTranscriptReady(a) ? 0 : 1;
        const bReady = meetingLiveTranscriptReady(b) ? 0 : 1;
        if (aReady !== bReady) return aReady - bReady;
        return meetingDateTimeMs(a) - meetingDateTimeMs(b);
      });
  }, [allMeetings]);
  const dedupedAllMeetings = useMemo(
    () => dedupeDbMeetings(allMeetings, true, { adminList: true }),
    [allMeetings]
  );
  const allMeetingsSearchIndex = useMemo(
    () => buildMeetingAdminSearchIndex(dedupedAllMeetings),
    [dedupedAllMeetings]
  );
  const adminCalendarVisibleRange = useMemo(
    () => googleCalendarFetchRange("calendar", calView, calYear, calMonth, calSelDate),
    [calView, calYear, calMonth, calSelDate]
  );
  const adminCalendarMeetings = useMemo(() => {
    const deduped = dedupeDbMeetings(adminCalendarSessions, true);
    const { from, to } = adminCalendarVisibleRange;
    if (!from || !to) return deduped;
    return deduped.filter((m) => {
      const d = String(m?.date || "").trim().slice(0, 10);
      return d >= from && d <= to;
    });
  }, [adminCalendarSessions, adminCalendarVisibleRange]);
  const recentFive = useMemo(
    () => sortMeetingsNewestFirst(dedupedAllMeetings).slice(0, 5),
    [dedupedAllMeetings]
  );

  const filteredAllMeetings = useMemo(() => {
    if (page !== "allMeetings") return dedupedAllMeetings;
    const needle = normalizeAdminSearchQuery(deferredAllMeetingsSearch);
    let list = needle
      ? allMeetingsSearchIndex
          .filter((row) => adminSearchMatchesHaystack(needle, row.searchHay))
          .map((row) => row.m)
      : allMeetingsSearchIndex.map((row) => row.m);
    if (adminMeetingsDateFrom || adminMeetingsDateTo) {
      list = list.filter((m) =>
        meetingInAdminDateRange(m, adminMeetingsDateFrom, adminMeetingsDateTo)
      );
    }
    return adminMeetingsSortOrder === "oldest"
      ? sortAdminMeetingsOldestFirst(list)
      : sortAdminMeetingsNewestFirst(list);
  }, [
    page,
    dedupedAllMeetings,
    allMeetingsSearchIndex,
    deferredAllMeetingsSearch,
    adminMeetingsDateFrom,
    adminMeetingsDateTo,
    adminMeetingsSortOrder,
  ]);
  const adminMeetingsTotalPages = Math.max(
    1,
    Math.ceil(filteredAllMeetings.length / adminMeetingsPageSize)
  );
  const adminMeetingsSafePage = Math.min(adminMeetingsPage, adminMeetingsTotalPages);
  const pagedAllMeetings = useMemo(() => {
    const start = (adminMeetingsSafePage - 1) * adminMeetingsPageSize;
    return filteredAllMeetings.slice(start, start + adminMeetingsPageSize);
  }, [filteredAllMeetings, adminMeetingsSafePage, adminMeetingsPageSize]);
  const filteredRecentFive = useMemo(
    () =>
      page === "overview"
        ? recentFive.filter((m) => adminSearchMatches(deferredOverviewSearch, meetingAdminSearchParts(m)))
        : recentFive,
    [page, recentFive, deferredOverviewSearch]
  );
  const filteredAllUsers = useMemo(() => {
    if (page !== "users") return allUsers;
    const fromMs = adminUsersSinceFrom ? startOfLocalDayMs(adminUsersSinceFrom) : null;
    const toMs = adminUsersSinceTo ? endOfLocalDayMs(adminUsersSinceTo) : null;
    return allUsers.filter((u) => {
      if (!adminSearchMatches(deferredUsersSearch, userAdminSearchParts(u))) return false;
      if (fromMs != null || toMs != null) {
        const created = adminUserCreatedAtMs(u);
        if (created == null) return false;
        if (fromMs != null && created < fromMs) return false;
        if (toMs != null && created > toMs) return false;
      }
      return true;
    });
  }, [page, allUsers, deferredUsersSearch, adminUsersSinceFrom, adminUsersSinceTo]);
  const adminUsersFiltersActive =
    !!adminUsersSinceFrom ||
    !!adminUsersSinceTo ||
    !!String(usersAdminSearch || "").trim();
  const sortedAllUsers = useMemo(() => {
    if (page !== "users") return allUsers;
    const list = filteredAllUsers.slice();
    const dir = adminUsersSortDir === "desc" ? -1 : 1;
    const nameOf = (u) =>
      String(u?.name || (typeof u === "string" ? u.split("@")[0] : u?.email || "") || "").toLowerCase();
    const emailOf = (u) => String(u?.email || (typeof u === "string" ? u : "") || "").toLowerCase();
    list.sort((a, b) => {
      let cmp = 0;
      switch (adminUsersSortBy) {
        case "role":
          cmp = adminUserDisplayRole(a).localeCompare(adminUserDisplayRole(b));
          break;
        case "owned":
          cmp = adminUserOwnedCount(a) - adminUserOwnedCount(b);
          break;
        case "attended":
          cmp = adminUserAttendedCount(a) - adminUserAttendedCount(b);
          break;
        case "total":
          cmp = adminUserTotalCount(a) - adminUserTotalCount(b);
          break;
        case "since": {
          const ca = adminUserCreatedAtMs(a) || 0;
          const cb = adminUserCreatedAtMs(b) || 0;
          cmp = ca - cb;
          break;
        }
        case "user":
        default: {
          cmp = nameOf(a).localeCompare(nameOf(b));
          if (!cmp) cmp = emailOf(a).localeCompare(emailOf(b));
          break;
        }
      }
      if (!cmp && adminUsersSortBy !== "user") {
        cmp = nameOf(a).localeCompare(nameOf(b));
      }
      return cmp * dir;
    });
    return list;
  }, [page, allUsers, filteredAllUsers, adminUsersSortBy, adminUsersSortDir]);
  const adminUsersTotalPages = Math.max(
    1,
    Math.ceil(sortedAllUsers.length / adminUsersPageSize)
  );
  const adminUsersSafePage = Math.min(adminUsersPage, adminUsersTotalPages);
  const pagedAllUsers = useMemo(() => {
    const start = (adminUsersSafePage - 1) * adminUsersPageSize;
    return sortedAllUsers.slice(start, start + adminUsersPageSize);
  }, [sortedAllUsers, adminUsersSafePage, adminUsersPageSize]);
  const adminUserDrawer = useMemo(
    () => allUsers.find((u) => String(u.id) === String(adminUserDrawerId)) || null,
    [allUsers, adminUserDrawerId]
  );
  const {
    usage: adminUserDrawerMeetingUsage,
    loading: adminUserDrawerMeetingUsageLoading,
    error: adminUserDrawerMeetingUsageError,
  } = useAdminUserMeetingUsage(adminUserDrawer?.id, page === "users" && Boolean(adminUserDrawer?.id));
  useEffect(() => {
    if (page !== "users") setAdminUserDrawerId(null);
  }, [page]);
  useEffect(() => {
    setAdminUsersPage(1);
  }, [
    usersAdminSearch,
    adminUsersSortBy,
    adminUsersSortDir,
    adminUsersSinceFrom,
    adminUsersSinceTo,
  ]);
  const adminLogsFiltersActive = useMemo(
    () =>
      logFilterAction !== "all" ||
      logFilterType !== "all" ||
      Boolean(logFilterOwner.trim()) ||
      Boolean(logFilterMeeting.trim()) ||
      Boolean(logFilterDateFrom) ||
      Boolean(logFilterDateTo),
    [
      logFilterAction,
      logFilterType,
      logFilterOwner,
      logFilterMeeting,
      logFilterDateFrom,
      logFilterDateTo,
    ]
  );
  const activityLogFilterIndex = useMemo(
    () => (logsTabActive ? buildActivityLogFilterIndex(logs) : []),
    [logs, logsTabActive]
  );
  const filteredLogs = useMemo(() => {
    if (!logsTabActive) return logs;
    const ownerNeedle = deferredLogFilterOwner.trim().toLowerCase();
    const meetingNeedle = deferredLogFilterMeeting.trim().toLowerCase();
    const { fromMs, toMs } = logActivityDateBounds(logFilterDateFrom, logFilterDateTo);
    const out = [];
    for (const row of activityLogFilterIndex) {
      const l = row.log;
      if (!adminSearchMatchesHaystack(deferredLogsSearch, row.searchHay)) continue;
      if (logFilterAction !== "all" && l.action !== logFilterAction) continue;
      if (logFilterType !== "all" && l.type !== logFilterType) continue;
      if (ownerNeedle && !adminSearchMatchesHaystack(ownerNeedle, row.ownerHay)) continue;
      if (meetingNeedle && !adminSearchMatchesHaystack(meetingNeedle, row.meetingHay)) continue;
      if (fromMs != null || toMs != null) {
        if (!Number.isFinite(row.timeMs)) continue;
        if (fromMs != null && row.timeMs < fromMs) continue;
        if (toMs != null && row.timeMs > toMs) continue;
      }
      out.push(l);
    }
    return out;
  }, [
    logs,
    logsTabActive,
    activityLogFilterIndex,
    deferredLogsSearch,
    logFilterAction,
    logFilterType,
    deferredLogFilterOwner,
    deferredLogFilterMeeting,
    logFilterDateFrom,
    logFilterDateTo,
  ]);
  const adminLogsTotalPages = Math.max(1, Math.ceil(filteredLogs.length / adminLogsPageSize));
  const adminLogsSafePage = Math.min(adminLogsPage, adminLogsTotalPages);
  const pagedFilteredLogs = useMemo(() => {
    if (!logsTabActive) return filteredLogs;
    const start = (adminLogsSafePage - 1) * adminLogsPageSize;
    return filteredLogs.slice(start, start + adminLogsPageSize);
  }, [filteredLogs, adminLogsSafePage, adminLogsPageSize, logsTabActive]);
  useEffect(() => {
    setAdminLogsPage(1);
  }, [
    deferredLogsSearch,
    logFilterAction,
    logFilterType,
    deferredLogFilterOwner,
    deferredLogFilterMeeting,
    logFilterDateFrom,
    logFilterDateTo,
    adminLogsPageSize,
  ]);
  useEffect(() => {
    setExpandedLogIdx(null);
  }, [
    deferredLogsSearch,
    logFilterAction,
    logFilterType,
    deferredLogFilterOwner,
    deferredLogFilterMeeting,
    logFilterDateFrom,
    logFilterDateTo,
    adminLogsSafePage,
  ]);
  const filteredAdminFeedback = useMemo(() => {
    if (page !== "feedback") return adminFeedbackItems;
    const q = String(deferredFeedbackSearch || "").trim().toLowerCase();
    if (!q) return adminFeedbackItems;
    return adminFeedbackItems.filter((f) => {
      const parts = [
        f.userName,
        f.userEmail,
        f.message,
        f.page,
        String(f.rating),
        f.emailSent ? "sent" : "pending",
        f.emailError,
      ];
      return parts.some((p) => String(p || "").toLowerCase().includes(q));
    });
  }, [page, adminFeedbackItems, deferredFeedbackSearch]);
  const filteredCalendarMeetings = useMemo(
    () =>
      page === "calendar"
        ? adminCalendarMeetings.filter((m) =>
            adminSearchMatches(deferredCalendarSearch, meetingAdminSearchParts(m))
          )
        : adminCalendarMeetings,
    [page, adminCalendarMeetings, deferredCalendarSearch]
  );
  const adminCalendarListTotalPages = Math.max(
    1,
    Math.ceil(filteredCalendarMeetings.length / adminCalendarListPageSize)
  );
  const adminCalendarListSafePage = Math.min(adminCalendarListPage, adminCalendarListTotalPages);
  const pagedCalendarMeetings = useMemo(() => {
    const start = (adminCalendarListSafePage - 1) * adminCalendarListPageSize;
    return sortMeetingsChronologically(filteredCalendarMeetings).slice(
      start,
      start + adminCalendarListPageSize
    );
  }, [filteredCalendarMeetings, adminCalendarListSafePage, adminCalendarListPageSize]);

  useEffect(() => {
    setAdminCalendarListPage(1);
  }, [adminCalendarLoadedKey, calendarAdminSearch, calView]);

  const adminCalendarRangeKey = `${adminCalendarVisibleRange.from || ""}|${adminCalendarVisibleRange.to || ""}|${calView}`;
  const adminCalendarDataReady = adminCalendarLoadedKey === adminCalendarRangeKey;
  const adminCalendarBootLoading =
    page === "calendar" &&
    ((loading && !adminReady) || (adminReady && !adminCalendarDataReady && !adminCalendarError));
  const adminCalendarRefreshing =
    page === "calendar" && adminReady && adminCalendarDataReady && (adminCalendarFetching || adminCalendarSyncing);
  // Keep topbar calm — calendar soft-sync uses its own chip, not a global "Updating…".
  const adminTopbarUpdating =
    adminRefreshing || (adminMeetingsLoading && (page === "overview" || page === "allMeetings" || page === "usage" || page === "transcriptsEmail"));

  function adminCalendarOpen(m) {
    if (!m) return;
    setAdminCalDetailMeeting(m);
  }

  function adminCalendarOpenJoinLink(m) {
    const join = joinUrlFromMeeting(m);
    if (join) window.open(join, "_blank", "noopener,noreferrer");
  }

  function adminCalendarOpenCalendarLink(m) {
    const link = String(m?.htmlLink || m?.html_link || "").trim();
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    adminCalendarOpenJoinLink(m);
  }

  useEffect(() => {
    const b = usageApi?.budget;
    if (!b || b.error) return;
    if (Number.isFinite(b.budgetUsd)) setBudgetInput(String(b.budgetUsd));
  }, [usageApi?.budget]);

  async function saveUsageBudget() {
    setBudgetSaving(true);
    setBudgetErr(null);
    try {
      const v = parseFloat(String(budgetInput).replace(/,/g, ""));
      if (!Number.isFinite(v) || v < 0) throw new Error("Enter a valid budget amount (USD).");
      const budget = await apiFetch("/admin/usage/budget", {
        method: "PUT",
        body: { budgetUsd: v },
      });
      if (budget && !budget.error) {
        setUsageApi((prev) => (prev ? { ...prev, budget } : { budget }));
        if (Number.isFinite(budget.budgetUsd)) setBudgetInput(String(budget.budgetUsd));
      }
    } catch (e) {
      setBudgetErr(e.message || "Save failed");
    } finally {
      setBudgetSaving(false);
    }
    if (budgetHistoryOpen) loadBudgetHistory();
  }

  async function loadBudgetHistory() {
    setBudgetHistoryLoading(true);
    setBudgetHistoryErr(null);
    try {
      const data = await apiFetch("/admin/usage/budget/history");
      setBudgetHistory(Array.isArray(data?.history) ? data.history : []);
    } catch (e) {
      setBudgetHistoryErr(e.message || "Could not load budget history");
    } finally {
      setBudgetHistoryLoading(false);
    }
  }

  async function loadCreditAlertRecipients() {
    setCreditRecipientsLoading(true);
    setCreditRecipientErr(null);
    try {
      const data = await apiFetch("/admin/credit-alert-recipients");
      setCreditRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
    } catch (e) {
      setCreditRecipientErr(e.message || "Could not load recipients");
    } finally {
      setCreditRecipientsLoading(false);
    }
  }

  async function loadAdminUsersForPicker() {
    try {
      const data = await apiFetch("/admin/portal-admins");
      const list = Array.isArray(data?.admins) ? data.admins : Array.isArray(data) ? data : [];
      setAdminUsersForPicker(
        list
          .map((u) => ({ name: String(u?.name || "").trim(), email: String(u?.email || "").trim() }))
          .filter((u) => u.email)
      );
    } catch (_) {
      setAdminUsersForPicker([]);
    }
  }

  async function addRecipientByEmail(email, name) {
    const target = String(email || "").trim();
    if (!target) {
      setCreditRecipientErr("Type a name or email to find someone, then add them.");
      return;
    }
    setRecipientSaving(true);
    setCreditRecipientErr(null);
    try {
      await apiFetch("/admin/credit-alert-recipients", {
        method: "POST",
        body: { email: target, name: String(name || "").trim() },
      });
      setRecipientPickerQuery("");
      await loadCreditAlertRecipients();
    } catch (e) {
      setCreditRecipientErr(e.message || "Could not add recipient");
    } finally {
      setRecipientSaving(false);
    }
  }

  async function addNewRecipientFromSearch() {
    const q = String(recipientPickerQuery || "").trim();
    if (!q) return;
    const match = (adminUsersForPicker || []).find(
      (u) =>
        String(u?.email || "").toLowerCase() === q.toLowerCase() ||
        String(u?.name || "").toLowerCase() === q.toLowerCase()
    );
    if (match) {
      await addRecipientByEmail(match.email, match.name || "");
      return;
    }
    await addRecipientByEmail(q, "");
  }

  async function removeCreditAlertRecipient(email) {
    const target = String(email || "").trim();
    if (!target) return;
    setRecipientSaving(true);
    setCreditRecipientErr(null);
    try {
      await apiFetch(`/admin/credit-alert-recipients/${encodeURIComponent(target)}`, {
        method: "DELETE",
      });
      await loadCreditAlertRecipients();
    } catch (e) {
      setCreditRecipientErr(e.message || "Could not remove recipient");
    } finally {
      setRecipientSaving(false);
    }
  }

  async function loadCreditAlertHistory() {
    setCreditAlertHistoryLoading(true);
    setCreditAlertHistoryErr(null);
    try {
      const data = await apiFetch("/admin/credit-alert-history?limit=100");
      setCreditAlertHistory(Array.isArray(data?.history) ? data.history : []);
    } catch (e) {
      setCreditAlertHistoryErr(e.message || "Could not load alert history");
    } finally {
      setCreditAlertHistoryLoading(false);
    }
  }

  async function saveCreditAlertThresholds() {
    const rawParts = String(creditThresholdDraft || "").split(",");
    const invalidPart = rawParts.some((part) => {
      const value = String(part).trim();
      return value !== "" && !Number.isFinite(Number(value));
    });
    if (invalidPart) {
      setCreditThresholdErr("Use comma-separated numbers from 0 to 100000.");
      return;
    }
    const parsed = rawParts
      .map((part) => String(part).trim())
      .filter((value) => value !== "")
      .map((value) => Number(value));
    if (parsed.some((n) => n < 0 || n > 100000)) {
      setCreditThresholdErr("Thresholds must be between 0 and 100000.");
      return;
    }
    if (parsed.length > 7) {
      setCreditThresholdErr("You can add up to 7 thresholds.");
      return;
    }
    setCreditThresholdSaving(true);
    setCreditThresholdErr(null);
    try {
      const data = await apiFetch("/admin/credit-alert-thresholds", {
        method: "PUT",
        body: { thresholds: parsed },
      });
      if (data && Array.isArray(data.thresholds)) {
        setCreditThresholdDraft(data.thresholds.join(", "));
      }
      setCreditThresholdEditorOpen(false);
      setAdminReloadTick((v) => v + 1);
    } catch (e) {
      setCreditThresholdErr(e.message || "Could not save thresholds");
    } finally {
      setCreditThresholdSaving(false);
    }
  }

  /**
   * Force a LIVE Recall MCP fetch (?refresh=1) so a just-made credit top-up is
   * captured immediately instead of waiting out the cache TTL. Also force-writes
   * a fresh snapshot + ledger row server-side. Used by the credits-history dialog.
   */
  async function refreshRecallCreditsNow() {
    if (recallCreditRefreshing) return;
    setRecallCreditRefreshing(true);
    setRecallCreditRefreshErr(null);
    let controller = null;
    let abortTimer = null;
    try {
      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      abortTimer = controller ? setTimeout(() => controller.abort(), 60000) : null;
      const data = await apiFetch("/admin/usage?refresh=1", controller ? { signal: controller.signal } : {});
      if (data && !data.error) {
        setUsageApi(data);
        setUsageApiErr(null);
        saveCachedUsageApi(data);
      } else {
        setRecallCreditRefreshErr(data?.error || "Refresh failed");
      }
    } catch (e) {
      const aborted = e?.name === "AbortError" || controller?.signal?.aborted;
      setRecallCreditRefreshErr(
        aborted ? "Recall MCP refresh timed out — try again." : e.message || "Refresh failed"
      );
    } finally {
      if (abortTimer) clearTimeout(abortTimer);
      setRecallCreditRefreshing(false);
    }
  }

  const ADMIN_NAV_GROUPS = [
    {
      label: "Monitor",
      items: [
        { id: "overview", icon: IC.home, label: "Overview" },
        { id: "calendar", icon: IC.cal, label: "Calendar" },
        { id: "allMeetings", icon: IC.meet, label: "All Meetings" },
        { id: "transcriptsEmail", icon: IC.actionList, label: "Transcripts & Email" },
      ],
    },
    {
      label: "Manage",
      items: [
        { id: "users", icon: IC.users, label: "Users" },
        { id: "access", icon: IC.shield, label: "Availability" },
        { id: "feedback", icon: IC.star, label: "Feedback" },
        { id: "usage", icon: IC.task, label: "Usage" },
        { id: "chatbotUsage", icon: IC.chat, label: "Chatbot Usage" },
        { id: "summary", icon: IC.task, label: "Summary" },
        { id: "authority", icon: IC.gavel, label: "Authority" },
      ],
    },
    {
      label: "System",
      items: [
        { id: "approvals", icon: IC.fileText, label: "Approvals" },
        { id: "smtp", icon: IC.mail, label: "SMTP" },
        { id: "logs", icon: IC.clock, label: "Activity Logs" },
      ],
    },
  ];
  const ADMIN_NAV = ADMIN_NAV_GROUPS.flatMap((g) => g.items);

  /** Bot-dispatched sessions only (same scope as GET /admin/meetings + /admin/usage) —
   * must match the backend's recall_bot_id filter, otherwise every client-computed figure
   * (Usage Trend chart, its cost estimate, Monthly Usage fallback) silently includes
   * meetings MeetAI never joined/billed for and reads higher than the backend KPIs. */
  const usageMeetings = useMemo(
    () => dedupedAllMeetings.filter((m) => String(m?.recall_bot_id || m?.recallBotId || "").trim()),
    [dedupedAllMeetings]
  );
  const usageLocal = usageApi?.local && !usageApi.local.error ? usageApi.local : null;

  /** Billable owner = calendar host, then human owner (not auto-join opt-in, not bot). */
  const usageOwnerFromMeeting = (m) => {
    const hostEmail = normAppEmail(meetingHostEmail(m));
    const hostName = String(meetingHostName(m) || "").trim();
    if (hostEmail && !isBotServiceAccountEmail(hostEmail)) {
      return { email: hostEmail, name: hostName || hostEmail.split("@")[0] };
    }
    const ownerEmail = normAppEmail(m?.owner_email || m?.ownerEmail);
    const ownerName = String(m?.owner_name || "").trim();
    if (ownerEmail && !isBotServiceAccountEmail(ownerEmail)) {
      return { email: ownerEmail, name: ownerName || ownerEmail.split("@")[0] };
    }
    return null;
  };

  const clientUsageTotals = usageMeetings.reduce(
    (acc, m) => {
      const transcriptRows = adminMeetingTranscriptCount(m);
      const actionRows = adminMeetingActionCount(m);
      const statusKey = String(effectiveMeetingStatus(m) || "upcoming").toLowerCase();
      acc.totalMeetings += 1;
      acc.totalParticipants += joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m));
      acc.totalTranscriptRows += transcriptRows;
      acc.totalActionItems += actionRows;
      acc.totalMinutes += meetingDurationMinutes(m);
      if (statusKey === "completed" || statusKey === "ended") acc.completed += 1;
      else if (statusKey === "live") acc.live += 1;
      else if (statusKey === "cancelled" || statusKey === "canceled") acc.cancelled += 1;
      else acc.upcoming += 1;
      return acc;
    },
    {
      totalMeetings: 0,
      totalParticipants: 0,
      totalTranscriptRows: 0,
      totalActionItems: 0,
      totalMinutes: 0,
      completed: 0,
      live: 0,
      upcoming: 0,
      cancelled: 0,
    }
  );

  // Prefer authoritative /admin/usage local aggregates when loaded.
  const usageTotals = usageLocal
    ? {
        totalMeetings: Number(usageLocal.meetings) || 0,
        totalMinutes: Number(usageLocal.usageMinutes) || 0,
        totalTranscriptRows:
          usageLocal.transcriptRows != null
            ? Number(usageLocal.transcriptRows) || 0
            : clientUsageTotals.totalTranscriptRows,
        totalActionItems:
          usageLocal.actionItems != null
            ? Number(usageLocal.actionItems) || 0
            : clientUsageTotals.totalActionItems,
        totalParticipants: clientUsageTotals.totalParticipants,
        completed: Number(usageLocal.byStatus?.completed) || 0,
        live: Number(usageLocal.byStatus?.live) || 0,
        upcoming: Number(usageLocal.byStatus?.upcoming) || 0,
        cancelled: Number(usageLocal.byStatus?.cancelled) || 0,
      }
    : clientUsageTotals;

  const usageLocalDurationSublabel = (() => {
    const mins = Math.max(0, Math.round(Number(usageTotals.totalMinutes) || 0));
    const hPart = Math.floor(mins / 60);
    const mPart = mins % 60;
    const pretty = hPart > 0 ? `${hPart}h ${mPart}m` : `${mPart}m`;
    const apiH = Number(usageLocal?.usageHours);
    const hours =
      Number.isFinite(apiH) && apiH >= 0 ? apiH : Math.round((mins / 60) * 100) / 100;
    return `≈ ${pretty} (${Number(hours).toLocaleString(undefined, { maximumFractionDigits: 2 })} h) · MeetAI DB`;
  })();

  const usageByPlatform =
    usageLocal?.byPlatform && typeof usageLocal.byPlatform === "object"
      ? usageLocal.byPlatform
      : usageMeetings.reduce((acc, m) => {
          const key = String(m.platform || "unknown").toLowerCase();
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});

  const clientUsageByOwner = usageMeetings.reduce((acc, m) => {
    const attr = usageOwnerFromMeeting(m);
    if (!attr?.email) return acc;
    if (!acc[attr.email]) acc[attr.email] = { name: attr.name, email: attr.email, meetings: 0, minutes: 0 };
    acc[attr.email].meetings += 1;
    acc[attr.email].minutes += meetingDurationMinutes(m);
    return acc;
  }, {});
  const allUsageOwnersRanked = Array.isArray(usageLocal?.topOwners) && usageLocal.topOwners.length
    ? usageLocal.topOwners
        .map((u) => ({
          email: String(u.email || "").toLowerCase(),
          name: String(u.name || u.email || "").trim(),
          meetings: Number(u.meetings) || 0,
          minutes: Number(u.minutes) || 0,
        }))
        .filter((u) => u.email)
    : Object.values(clientUsageByOwner)
        .sort((a, b) => b.minutes - a.minutes || b.meetings - a.meetings);
  const topUsageOwners =
    topHostsLimit === "all" ? allUsageOwnersRanked : allUsageOwnersRanked.slice(0, topHostsLimit);

  const usageMeetingStatusBucket = useCallback((m) => {
    const statusKey = String(effectiveMeetingStatus(m) || "upcoming").toLowerCase();
    if (statusKey === "completed" || statusKey === "ended") return "completed";
    if (statusKey === "live") return "live";
    if (statusKey === "cancelled" || statusKey === "canceled") return "cancelled";
    return "upcoming";
  }, []);

  const filteredUsageMeetings = useMemo(
    () =>
      page === "usage"
        ? usageMeetings.filter((m) => {
            if (!adminSearchMatches(deferredUsageSessionsSearch, meetingAdminSearchParts(m))) return false;
            if (usageStatusFilter && usageMeetingStatusBucket(m) !== usageStatusFilter) return false;
            return true;
          })
        : usageMeetings,
    [page, usageMeetings, deferredUsageSessionsSearch, usageStatusFilter, usageMeetingStatusBucket]
  );
  const filteredTopUsageOwners = useMemo(
    () =>
      page === "usage"
        ? topUsageOwners.filter((u) => adminSearchMatches(deferredUsageHostsSearch, u.name, u.email))
        : topUsageOwners,
    [page, topUsageOwners, deferredUsageHostsSearch]
  );
  const usageSessionsPageCount = Math.max(
    1,
    Math.ceil(filteredUsageMeetings.length / Math.max(1, usageSessionsPageSize))
  );
  const usageSessionsPageSafe = Math.min(usageSessionsPage, usageSessionsPageCount - 1);
  const pagedUsageMeetings = filteredUsageMeetings.slice(
    usageSessionsPageSafe * usageSessionsPageSize,
    usageSessionsPageSafe * usageSessionsPageSize + usageSessionsPageSize
  );

  // Local estimate (duration × rate) — same formula used everywhere else in this page,
  // since Recall's real billing API has no per-past-month breakdown to pull an exact figure from.
  // Bot time bills regardless of transcript outcome; transcription only bills for meetings that
  // actually produced a transcript.
  const monthlyBotRatePerHour = Number(usageApi?.pricing?.usageRatePerHourUsd ?? 0.5);
  const monthlyTranscriptionRatePerHour = Number(usageApi?.pricing?.transcriptionRatePerHourUsd ?? 0.15);
  const monthlyEstRatePerHour = monthlyBotRatePerHour + monthlyTranscriptionRatePerHour;
  // Recall actuals from Postgres snapshots (recall_mcp_snapshots via
  // /api/admin/usage → recallMcpHistory). MCP only reports CURRENT whole-account
  // totals (Dev+QA share one key), so per-bucket we show BURN = new billed usage
  // inside the bucket (same-source consecutive snapshot deltas, positives only).
  // get_billed_usage returns billed_hours = 0 for workspaces where it fails
  // account-side (balance still works) — 0 is treated as missing, never as real.
  const mcpHistorySnaps = (() => {
    const raw = Array.isArray(usageApi?.recallMcpHistory) ? usageApi.recallMcpHistory : [];
    const out = [];
    for (const s of raw) {
      const t = s?.fetchedAt ? new Date(s.fetchedAt).getTime() : NaN;
      const billed = Number(s?.billedHours);
      const bot = Number(s?.botTotalHours);
      let h = null;
      let field = null;
      if (Number.isFinite(billed) && billed > 0) {
        h = billed;
        field = "billed";
      } else if (Number.isFinite(bot) && bot > 0) {
        h = bot;
        field = "bot";
      }
      if (!Number.isFinite(t) || h == null) continue;
      out.push({ t, cost: Math.round(h * monthlyBotRatePerHour * 100) / 100, field });
    }
    out.sort((a, b) => a.t - b.t);
    return out;
  })();
  // New billed cost inside [startTs, endTs]; null = not measurable yet (need an
  // opening snapshot + one in-window snapshot from the same MCP source).
  const mcpBurnIn = (startTs, endTs) => {
    const inWin = mcpHistorySnaps.filter((s) => s.t >= startTs && s.t <= endTs);
    let opening = null;
    for (const s of mcpHistorySnaps) {
      if (s.t < startTs) opening = s;
      else break;
    }
    const seq = [...(opening ? [opening] : []), ...inWin];
    if (seq.length < 2) return null;
    let burn = 0;
    for (let i = 1; i < seq.length; i++) {
      if (seq[i].field !== seq[i - 1].field) continue;
      const d = seq[i].cost - seq[i - 1].cost;
      if (d > 0) burn = Math.round((burn + d) * 100) / 100;
    }
    return burn;
  };
  const hasMcpActuals = mcpHistorySnaps.length > 0;
  // Precise metered burn from stored per-bot rows (recall_mcp_bot_usage via
  // /api/admin/usage → recallMcpDailyBurn). Day-granular sums of actual billed
  // seconds — unlike snapshot deltas these attribute burn to the exact day.
  // Days before coverageStartDay → null (gap); covered days with no rows → 0.
  const mcpDailyBurn = usageApi?.recallMcpDailyBurn || null;
  const mcpDailyMap = (() => {
    const m = new Map();
    for (const d of mcpDailyBurn?.days || []) m.set(d.day, Number(d.costUsd) || 0);
    return m;
  })();
  // Same days, bot-hours series for the second line on the trend graph.
  const mcpDailyHoursMap = (() => {
    const m = new Map();
    for (const d of mcpDailyBurn?.days || []) m.set(d.day, Number(d.hours) || 0);
    return m;
  })();
  const mcpMonthlyBurnMap = (() => {
    const m = new Map();
    for (const row of usageApi?.recallMcpMonthlyBurn?.months || []) {
      const key = String(row?.month || "");
      if (/^\d{4}-\d{2}$/.test(key)) m.set(key, Number(row.costUsd) || 0);
    }
    return m;
  })();
  const mcpBurnCoverage = mcpDailyBurn?.coverageStartDay || null;
  const mcpMonthlyCoverage = usageApi?.recallMcpMonthlyBurn?.coverageStartMonth || null;
  const hasMcpDaily = mcpDailyMap.size > 0;
  const mcpDayBurn = (dayStr) => {
    if (!mcpBurnCoverage || dayStr < mcpBurnCoverage) return null;
    if (!mcpDailyMap.has(dayStr)) return 0;
    return mcpDailyMap.get(dayStr);
  };
  const mcpDayHours = (dayStr) => {
    if (!mcpBurnCoverage || dayStr < mcpBurnCoverage) return null;
    if (!mcpDailyHoursMap.has(dayStr)) return 0;
    return mcpDailyHoursMap.get(dayStr);
  };
  const mcpMonthBurn = (monthKey) => {
    const key = String(monthKey || "");
    if (!/^\d{4}-\d{2}$/.test(key)) return null;
    if (mcpMonthlyBurnMap.has(key)) return mcpMonthlyBurnMap.get(key);
    if (!mcpBurnCoverage || key < String(mcpBurnCoverage).slice(0, 7)) {
      if (mcpMonthlyCoverage && key >= mcpMonthlyCoverage) return 0;
      return null;
    }
    let sum = 0;
    let any = false;
    for (const [day, cost] of mcpDailyMap) {
      if (String(day).slice(0, 7) === key) {
        sum += cost;
        any = true;
      }
    }
    return any ? Math.round(sum * 100) / 100 : 0;
  };
  const mcpRangeBurn = (startTs, endTs) => {
    const d = new Date(startTs);
    d.setHours(0, 0, 0, 0);
    const end = new Date(endTs);
    end.setHours(0, 0, 0, 0);
    let anyCovered = false;
    let sum = 0;
    while (d <= end) {
      const v = mcpDayBurn(dayKey(d));
      if (v != null) {
        anyCovered = true;
        sum += v;
      }
      d.setDate(d.getDate() + 1);
    }
    return anyCovered ? Math.round(sum * 100) / 100 : null;
  };
  // Bot-hours twin of mcpRangeBurn — sums metered hours (not dollars) inside
  // the bucket so the trend graph can draw a Bot Hours line next to cost.
  const mcpRangeHours = (startTs, endTs) => {
    const d = new Date(startTs);
    d.setHours(0, 0, 0, 0);
    const end = new Date(endTs);
    end.setHours(0, 0, 0, 0);
    let anyCovered = false;
    let sum = 0;
    while (d <= end) {
      const v = mcpDayHours(dayKey(d));
      if (v != null) {
        anyCovered = true;
        sum += v;
      }
      d.setDate(d.getDate() + 1);
    }
    return anyCovered ? Math.round(sum * 100) / 100 : null;
  };
  // Peak spend hours from stored MCP actuals: consecutive-snapshot billed-cost
  // deltas (positive only = real burn) bucketed by hour-of-day 0–23.
  const mcpPeakHours = (() => {
    const burns = new Array(24).fill(0);
    let pairs = 0;
    for (let i = 1; i < mcpHistorySnaps.length; i++) {
      if (mcpHistorySnaps[i].field !== mcpHistorySnaps[i - 1].field) continue;
      const d = mcpHistorySnaps[i].cost - mcpHistorySnaps[i - 1].cost;
      if (d > 0) {
        const h = new Date(mcpHistorySnaps[i].t).getHours();
        burns[h] = Math.round((burns[h] + d) * 100) / 100;
        pairs += 1;
      }
    }
    let peakHour = -1;
    let peakBurn = 0;
    burns.forEach((b, h) => {
      if (b > peakBurn) {
        peakBurn = b;
        peakHour = h;
      }
    });
    const totalBurn = Math.round(burns.reduce((a, b) => a + b, 0) * 100) / 100;
    return { burns, pairs, peakHour, peakBurn, totalBurn };
  })();
  const mcpValueEvents = Array.isArray(usageApi?.mcpValueEvents) ? usageApi.mcpValueEvents : [];
  const recallCreditSnapshotHistory = (() => {
    const raw = Array.isArray(usageApi?.recallMcpHistory) ? usageApi.recallMcpHistory : [];
    const withBal = raw
      .filter((s) => s?.fetchedAt && typeof s.creditBalanceUsd === "number")
      .sort((a, b) => new Date(b.fetchedAt).getTime() - new Date(a.fetchedAt).getTime());
    return withBal.map((cur, i) => {
      const older = withBal[i + 1];
      const prevBal = older?.creditBalanceUsd != null ? Number(older.creditBalanceUsd) : null;
      const bal = Number(cur.creditBalanceUsd);
      const deltaUsd =
        prevBal != null ? Math.round((bal - prevBal) * 100) / 100 : null;
      return {
        fetchedAt: cur.fetchedAt,
        creditBalanceUsd: bal,
        previousBalanceUsd: prevBal,
        deltaUsd,
        billedHours: cur.billedHours != null ? Number(cur.billedHours) : null,
        source: cur.source || null,
      };
    });
  })();
  const clientUsageByMonth = usageMeetings.reduce((acc, m) => {
    const key = String(m.date || "").slice(0, 7) || "unknown";
    if (!acc[key]) acc[key] = { month: key, meetings: 0, minutes: 0, transcriptRows: 0, costUsd: 0 };
    const mins = meetingDurationMinutes(m);
    const hasTranscript = adminMeetingTranscriptCount(m) > 0;
    acc[key].meetings += 1;
    acc[key].minutes += mins;
    acc[key].transcriptRows += adminMeetingTranscriptCount(m);
    acc[key].costUsd +=
      (mins / 60) * monthlyBotRatePerHour + (hasTranscript ? (mins / 60) * monthlyTranscriptionRatePerHour : 0);
    return acc;
  }, {});
  const usageMonths =
    Array.isArray(usageLocal?.months) && usageLocal.months.length
      ? usageLocal.months.map((m) => ({
          month: m.month,
          meetings: Number(m.meetings) || 0,
          minutes: Number(m.minutes) || 0,
          transcriptRows:
            Number(clientUsageByMonth[m.month]?.transcriptRows) || 0,
          costUsd:
            Math.round(
              (Number(m.costUsd) || Number(clientUsageByMonth[m.month]?.costUsd) || 0) * 100
            ) / 100,
        }))
      : Object.values(clientUsageByMonth)
          .filter((m) => m.month !== "unknown")
          .map((m) => ({ ...m, costUsd: Math.round(m.costUsd * 100) / 100 }))
          .sort((a, b) => String(b.month).localeCompare(String(a.month)));

  const MONTHLY_USAGE_PAGE_SIZE = 12;
  const filteredUsageMonths = monthlyUsageFilter
    ? usageMonths.filter((m) => m.month === monthlyUsageFilter)
    : usageMonths;
  const monthlyUsagePageCount = Math.max(1, Math.ceil(filteredUsageMonths.length / MONTHLY_USAGE_PAGE_SIZE));
  const monthlyUsagePageSafe = Math.min(monthlyUsagePage, monthlyUsagePageCount - 1);
  const pagedUsageMonths = filteredUsageMonths.slice(
    monthlyUsagePageSafe * MONTHLY_USAGE_PAGE_SIZE,
    monthlyUsagePageSafe * MONTHLY_USAGE_PAGE_SIZE + MONTHLY_USAGE_PAGE_SIZE
  );

  function parseMeetingDate(m) {
    const raw = String(m?.date || "").trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const d = new Date(`${raw}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  function dayKey(dateObj) {
    return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;
  }
  function startOfWeekLocal(d) {
    const x = new Date(d);
    const day = x.getDay();
    const shift = day === 0 ? 6 : day - 1; // Monday start
    x.setDate(x.getDate() - shift);
    x.setHours(0, 0, 0, 0);
    return x;
  }
  function endOfWeekLocal(d) {
    const s = startOfWeekLocal(d);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
  }

   const nowDate = new Date();
   const startToday = new Date(nowDate);
   startToday.setHours(0, 0, 0, 0);
   const endToday = new Date(nowDate);
   endToday.setHours(23, 59, 59, 999);
   const weekStart = startOfWeekLocal(nowDate);
   const weekEnd = endOfWeekLocal(nowDate);
   const monthStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1, 0, 0, 0, 0);
   const monthEnd = new Date(nowDate.getFullYear(), nowDate.getMonth() + 1, 0, 23, 59, 59, 999);

   const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
   const trendMonthLabel = (key) => {
     const [y, mo] = String(key || "").split("-");
     const mi = Number(mo) - 1;
     return mi >= 0 && mi < 12 ? `${MONTH_LONG[mi]} ${y}` : key;
   };
   /** Shorter month label for tight Monthly Usage table cells (e.g. Oct 2026). */
   const usageMonthTableLabel = (key) => {
     const [y, mo] = String(key || "").split("-");
     const mi = Number(mo) - 1;
     return mi >= 0 && mi < 12 ? `${MONTH_LONG[mi].slice(0, 3)} ${y}` : key;
   };
   // Single source of truth for monthly numbers: reuse `usageMonths` (backend-preferred,
   // same rows as the Monthly Usage table), sorted oldest → newest with gap months filled
   // as zeros so the chart and the table can never disagree.
   const trendMonthList = (() => {
     const byKey = new Map();
     for (const m of usageMonths) {
       const key = String(m?.month || "");
       if (!/^\d{4}-\d{2}$/.test(key)) continue;
       byKey.set(key, {
         month: key,
         meetings: Number(m.meetings) || 0,
         minutes: Number(m.minutes) || 0,
         transcriptRows: Number(m.transcriptRows) || 0,
         costUsd: Number(m.costUsd) || 0,
       });
     }
     const keys = [...byKey.keys()].sort();
     if (!keys.length) return [];
     const [y0, m0] = keys[0].split("-").map(Number);
     const [y1, m1] = keys[keys.length - 1].split("-").map(Number);
     const out = [];
     const d = new Date(y0, m0 - 1, 1);
     const e = new Date(y1, m1 - 1, 1);
     while (d <= e) {
       const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
       out.push(byKey.get(k) || { month: k, meetings: 0, minutes: 0, transcriptRows: 0, costUsd: 0 });
       d.setMonth(d.getMonth() + 1);
     }
     return out;
   })();

    const usageDateFromY = String(usageDateFrom || "").trim().slice(0, 10);
    const usageDateToY = String(usageDateTo || usageDateFromY || "").trim().slice(0, 10);
    const usageIsSingleDay = !!(usageDateFromY && usageDateToY && usageDateFromY === usageDateToY);
    const usageChartDayMode = usageRange === "today" || (usageRange === "custom" && usageIsSingleDay);

    const rangeMeetings = usageMeetings.filter((m) => {
      if (usageRange === "overall") return true;
      return meetingInAdminDateRange(m, usageDateFromY, usageDateToY);
    });

   const estMeetingCostUsd = (m) => {
     const hours = meetingDurationMinutes(m) / 60;
     const hasTranscript = adminMeetingTranscriptCount(m) > 0;
     return hours * monthlyBotRatePerHour + (hasTranscript ? hours * monthlyTranscriptionRatePerHour : 0);
   };

    const { rangeSeries, rangeCostSeries, rangeTranscriptSeries } = (() => {
      if (usageRange === "overall") {
        const labels = trendMonthList.map((t) => trendMonthLabel(t.month));
        return {
          rangeSeries: labels.map((label, i) => ({ label, value: trendMonthList[i].meetings })),
          rangeCostSeries: labels.map((label, i) => ({ label, value: Math.round(trendMonthList[i].costUsd * 100) / 100 })),
          rangeTranscriptSeries: labels.map((label, i) => ({
            label,
            value: trendMonthList[i].transcriptRows,
          })),
        };
      }
      if (usageRange === "monthly") {
        // This calendar month, one bucket per day — rangeMeetings is already month-filtered.
        const y = monthStart.getFullYear();
        const mi = monthStart.getMonth();
        const daysInMonth = new Date(y, mi + 1, 0).getDate();
        const shortMon = MONTH_LONG[mi] ? MONTH_LONG[mi].slice(0, 3) : "";
        const labels = Array.from({ length: daysInMonth }, (_, i) => `${shortMon} ${i + 1}`);
        const counts = new Array(daysInMonth).fill(0);
        const costs = new Array(daysInMonth).fill(0);
        const transcripts = new Array(daysInMonth).fill(0);
        for (const m of rangeMeetings) {
          const d = parseMeetingDate(m);
          if (!d) continue;
          const idx = d.getDate() - 1;
          if (idx < 0 || idx >= daysInMonth) continue;
          counts[idx] += 1;
          costs[idx] += estMeetingCostUsd(m);
          transcripts[idx] += adminMeetingTranscriptCount(m);
        }
        return {
          rangeSeries: labels.map((label, i) => ({ label, value: counts[i] })),
          rangeCostSeries: labels.map((label, i) => ({ label, value: Math.round(costs[i] * 100) / 100 })),
          rangeTranscriptSeries: labels.map((label, i) => ({ label, value: transcripts[i] })),
        };
      }
      if (usageRange === "custom" && !usageIsSingleDay && usageDateFromY && usageDateToY) {
        const startD = ymdToDate(usageDateFromY);
        const endD = ymdToDate(usageDateToY);
        if (startD && endD && endD >= startD) {
          const labels = [];
          const counts = [];
          const costs = [];
          const transcripts = [];
          const dayMap = new Map();
          const cursor = new Date(startD);
          while (cursor <= endD) {
            const key = dayKey(cursor);
            const idx = labels.length;
            dayMap.set(key, idx);
            labels.push(
              cursor.toLocaleDateString("en-US", { month: "short", day: "numeric" })
            );
            counts.push(0);
            costs.push(0);
            transcripts.push(0);
            cursor.setDate(cursor.getDate() + 1);
          }
          for (const m of rangeMeetings) {
            const d = parseMeetingDate(m);
            if (!d) continue;
            const idx = dayMap.get(dayKey(d));
            if (idx == null) continue;
            counts[idx] += 1;
            costs[idx] += estMeetingCostUsd(m);
            transcripts[idx] += adminMeetingTranscriptCount(m);
          }
          return {
            rangeSeries: labels.map((label, i) => ({ label, value: counts[i] })),
            rangeCostSeries: labels.map((label, i) => ({ label, value: Math.round(costs[i] * 100) / 100 })),
            rangeTranscriptSeries: labels.map((label, i) => ({ label, value: transcripts[i] })),
          };
        }
      }
     const bucketOf = (() => {
        if (usageChartDayMode) {
          return (m) => {
            const timeRaw = String(m?.time || "").trim();
            const hourMatch = timeRaw.match(/(\d{1,2})/);
            const h = hourMatch ? Number(hourMatch[1]) : 12;
            return h < 6 ? 0 : h < 12 ? 1 : h < 15 ? 2 : h < 18 ? 3 : 4;
          };
        }
       return (m) => {
         const d = parseMeetingDate(m);
         if (!d) return -1;
         const day = d.getDay();
         return day === 0 ? 6 : day - 1;
       };
     })();
      const labels = usageChartDayMode
          ? ["00–06", "06–12", "12–15", "15–18", "18–24"]
          : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
     const counts = new Array(labels.length).fill(0);
     const costs = new Array(labels.length).fill(0);
     const transcripts = new Array(labels.length).fill(0);
     for (const m of rangeMeetings) {
       const idx = bucketOf(m);
       if (idx === undefined || idx === null || idx < 0 || idx >= labels.length) continue;
       counts[idx] += 1;
       costs[idx] += estMeetingCostUsd(m);
       transcripts[idx] += adminMeetingTranscriptCount(m);
     }
     return {
       rangeSeries: labels.map((label, i) => ({ label, value: counts[i] })),
       rangeCostSeries: labels.map((label, i) => ({ label, value: Math.round(costs[i] * 100) / 100 })),
       rangeTranscriptSeries: labels.map((label, i) => ({ label, value: transcripts[i] })),
     };
   })();

  // One combined dataset for the Recharts multi-line view — meetings and estimated
  // Recall cost share the same x-axis (hour/day/month) but get their own
  // y-axis (left = count, right = USD) since the two are wildly different scales.
  // actualUsd = MCP burn inside the bucket (null = not measurable yet → gap).
  const rangeActualWindows = (() => {
    const nowTs = Date.now();
    if (usageRange === "overall") {
      return trendMonthList.map((t) => {
        const m = String(t.month || "").match(/^(\d{4})-(\d{2})$/);
        if (!m) return null;
        const start = new Date(Number(m[1]), Number(m[2]) - 1, 1, 0, 0, 0, 0).getTime();
        if (start > nowTs) return null;
        return {
          start,
          end: Math.min(new Date(Number(m[1]), Number(m[2]), 0, 23, 59, 59, 999).getTime(), nowTs),
        };
      });
    }
    if (usageRange === "monthly") {
      const y = monthStart.getFullYear();
      const mi = monthStart.getMonth();
      const daysInMonth = new Date(y, mi + 1, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const start = new Date(y, mi, i + 1, 0, 0, 0, 0).getTime();
        if (start > nowTs) return null;
        return { start, end: Math.min(new Date(y, mi, i + 1, 23, 59, 59, 999).getTime(), nowTs) };
      });
    }
    if (usageChartDayMode) {
      const bounds = [0, 6, 12, 15, 18, 24];
      const dayAnchor = usageIsSingleDay && usageDateFromY ? ymdToDate(usageDateFromY) : startToday;
      const anchor = dayAnchor ? new Date(dayAnchor) : startToday;
      anchor.setHours(0, 0, 0, 0);
      return bounds.slice(0, 5).map((h, i) => {
        const start = new Date(anchor);
        start.setHours(h, 0, 0, 0);
        if (start.getTime() > nowTs) return null;
        const end = new Date(anchor);
        if (bounds[i + 1] >= 24) end.setHours(23, 59, 59, 999);
        else end.setHours(bounds[i + 1], 0, 0, 0);
        return { start: start.getTime(), end: Math.min(end.getTime(), nowTs) };
      });
    }
    if (usageRange === "custom" && !usageIsSingleDay && usageDateFromY && usageDateToY) {
      const startD = ymdToDate(usageDateFromY);
      const endD = ymdToDate(usageDateToY);
      if (startD && endD && endD >= startD) {
        const out = [];
        const cursor = new Date(startD);
        while (cursor <= endD) {
          const start = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 0, 0, 0, 0).getTime();
          if (start > nowTs) {
            out.push(null);
          } else {
            const end = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 23, 59, 59, 999).getTime();
            out.push({ start, end: Math.min(end, nowTs) });
          }
          cursor.setDate(cursor.getDate() + 1);
        }
        return out;
      }
    }
    return [0, 1, 2, 3, 4, 5, 6].map((i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
      if (start > nowTs) return null;
      d.setHours(23, 59, 59, 999);
      return { start, end: Math.min(d.getTime(), nowTs) };
    });
  })();
  const rangeChartData = rangeSeries.map((p, i) => {
    const w = rangeActualWindows[i];
    let actual = null;
    let botHours = null;
    // Fallback: derive hours from metered cost when exact day-rows are absent
    // (intraday today view, or snapshot-delta path). Combined rate matches the
    // backend burn math (bot + transcription), so hours ≈ cost / rate.
    const mcpCostToHours = (usd) => {
      const rate = Number(mcpDailyBurn?.ratePerHour) || 0.65;
      if (usd == null || !(rate > 0)) return null;
      return Math.round((Number(usd) / rate) * 100) / 100;
    };
    if (w) {
      // Day-granular metered sums for day+ buckets; intraday snapshot deltas
      // for the today view (needs sub-day resolution).
      actual =
        usageChartDayMode
          ? hasMcpActuals
            ? mcpBurnIn(w.start, w.end)
            : null
          : hasMcpDaily
            ? mcpRangeBurn(w.start, w.end)
            : hasMcpActuals
              ? mcpBurnIn(w.start, w.end)
              : null;
      // Bot-hours line follows the same buckets: exact metered hours for day+
      // buckets, cost-derived hours everywhere else so the purple line mirrors
      // the cost area instead of vanishing.
      botHours =
        !usageChartDayMode && hasMcpDaily
          ? mcpRangeHours(w.start, w.end)
          : mcpCostToHours(actual);
    }
    return {
      label: p.label,
      meetings: p.value,
      transcriptLines: rangeTranscriptSeries[i]?.value ?? 0,
      costUsd: rangeCostSeries[i]?.value ?? 0,
      actualUsd: actual,
      botHours,
    };
  });

  const rangeTotals = usageRange === "overall"
    ? trendMonthList.reduce((acc, t) => {
        acc.meetings += t.meetings;
        acc.minutes += t.minutes;
        acc.transcriptRows += t.transcriptRows;
        return acc;
      }, { meetings: 0, minutes: 0, transcriptRows: 0 })
    : rangeMeetings.reduce((acc, m) => {
        acc.meetings += 1;
        acc.minutes += meetingDurationMinutes(m);
        acc.transcriptRows += adminMeetingTranscriptCount(m);
        return acc;
      }, { meetings: 0, minutes: 0, transcriptRows: 0 });
  const rangeParticipants = rangeMeetings.reduce(
    (sum, m) => sum + joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m)),
    0
  );
  const rangeActionItems = rangeMeetings.reduce(
    (sum, m) => sum + adminMeetingActionCount(m),
    0
  );
  let rangeInfoMessage = "";
  if (rangeTotals.meetings === 0) {
    rangeInfoMessage = "No MeetAI sessions in the selected range.";
  } else if (rangeParticipants === 0 && rangeTotals.minutes === 0) {
    rangeInfoMessage = "MeetAI was dispatched in this range, but no joined participants or duration yet.";
  } else if (rangeTotals.transcriptRows === 0 && rangeActionItems === 0) {
    rangeInfoMessage = "Sessions in range — transcript/action data may still be processing.";
  }

  const usageBudget = usageApi?.budget && !usageApi.budget.error ? usageApi.budget : null;
  const budgetBarPct = usageBudget ? Math.min(100, Math.max(0, Number(usageBudget.percent) || 0)) : 0;

  const isLight = adminTheme === "light";
  const adminProfile = adminSessionProfile(sessionEmail, ADMIN_PORTAL_GATE);
  const adminTextMain = ADM.text;
  const adminTextMuted = ADM.textMuted;
  const adminTextSoft = ADM.textSoft;
  const adminBorder = ADM.border;
  const adminChipBg = ADM.borderSoft;
  const adminPanelBg = ADM.card;
  // Recall MCP usage is live now; keep the UI visible and let backend status
  // fields explain partial/cached states.
  const SHOW_RECALL_MCP_USAGE = true;
  /** Per-bot list_bot_usage table on Usage tab — hidden; billed/credits KPIs stay. */
  const SHOW_RECALL_PER_BOT_MCP_USAGE = false;
  const recallMcp = usageApi?.recallMcp || null;
  const recallMcpConfigured = Boolean(recallMcp?.configured);
  const recallMcpConnected = Boolean(recallMcp?.connected);
  const recallMcpBillingOk = Boolean(recallMcp?.health?.billingTool?.ok);
  const recallMcpPerBotOk = Boolean(recallMcp?.health?.perBotUsageTool?.ok);
  const recallMcpHasRealBilling = Boolean(usageApi?.recallBilled?.connected && recallMcpBillingOk);
  const recallMcpCreditUsd = typeof usageApi?.recallBilled?.creditBalanceUsd === "number"
    ? Number(usageApi.recallBilled.creditBalanceUsd)
    : null;
  const recallMcpUsageRows = (() => {
    const raw = usageApi?.recallBilled?.raw;
    if (Array.isArray(raw?.usage)) return raw.usage;
    if (raw && typeof raw === "object" && Array.isArray(raw.metrics)) return raw.metrics;
    return [];
  })();
  const recallMcpBotUsageHours = (() => {
    const fromMetric = recallMcpUsageRows.find((r) => r?.metric === "bot_usage_2core")?.value;
    if (typeof fromMetric === "number" && Number.isFinite(fromMetric)) return fromMetric;
    const billed = usageApi?.recallBilled?.billedHours;
    if (typeof billed === "number" && Number.isFinite(billed) && billed > 0) return billed;
    const botTotal = usageApi?.recallBotUsage?.totalHours;
    if (typeof botTotal === "number" && Number.isFinite(botTotal) && botTotal > 0) return botTotal;
    if (typeof billed === "number" && Number.isFinite(billed)) return billed;
    return null;
  })();
  const recallMcpStorageHours =
    recallMcpUsageRows.find((r) => r?.metric === "recording_retention_storage")?.value ?? null;
  const recallMcpLastUpdated =
    usageApi?.recallBilled?.fetchedAt ||
    usageApi?.recallBotUsage?.fetchedAt ||
    usageApi?._cachedAt ||
    null;
  const recallMcpLastUpdatedLabel = recallMcpLastUpdated
    ? new Date(recallMcpLastUpdated).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "Not updated yet";
  const recallMcpRedis = recallMcp?.cache?.redis || null;
  const recallMcpRedisLabel = !usageApi
    ? "Waiting"
    : recallMcpRedis?.configured
      ? `Redis cache ${recallMcpRedis.coolingDown ? "cooling down" : "active"}`
      : "Redis not configured";
  const recallMcpRetryAfter = Number(recallMcp?.runtime?.retryAfterSeconds || 0);
  const recallMcpRetryLabel = recallMcpRetryAfter > 0
    ? `Retry in ${recallMcpRetryAfter}s`
    : `Auto retry every ${recallMcp?.runtime?.minIntervalSeconds || 60}s`;
  const recallMcpCacheLabel = usageApi?.recallBilled?.cache?.stale || usageApi?.recallBotUsage?.cache?.stale
    ? "Serving cached data"
    : "Live data cached";
  const recallCreditUi = recallCreditBalanceUi(recallMcpCreditUsd);
  const realBillingAlertStatus = recallCreditUi.alert;
  const recallCreditHero = recallCreditUi.hero;
  /** While /usage + MCP billing is in flight, show MCP hero skeleton — not manual budget tracker. */
  const recallMcpBillingLoading =
    SHOW_RECALL_MCP_USAGE &&
    !recallMcpHasRealBilling &&
    !usageApiErr &&
    (usageApiLoading || !usageApi);
  const recallCreditHeroLoading = recallCreditBalanceUi(null).hero;
  const recallMcpStateLabel =
    !usageApi
      ? "Waiting for usage API"
      : recallMcp?.state === "connected_with_balance"
      ? "Connected"
      : recallMcp?.state === "connected_partial"
        ? "Partially connected"
        : recallMcp?.state === "configured_but_unavailable"
          ? "Configured, unavailable"
          : "Not configured";
  const recallMcpStateColor =
    !usageApi
      ? "#6b7688"
      : recallMcp?.state === "connected_with_balance"
      ? "#0f9d8f"
      : recallMcp?.state === "connected_partial"
        ? "#c98a2c"
        : "#b0403f";
  const recallMcpCreditSublabel = recallMcpBillingOk
    ? "Recall MCP get_billed_usage"
    : !usageApi
      ? "Waiting for usage API"
      : recallMcpConfigured
      ? "Billing tool unavailable"
      : "MCP key not configured";
  const recallMcpBilledHoursSublabel = recallMcpBillingOk
    ? "Invoice-grade billing"
    : recallMcpPerBotOk
      ? "Summed from live per-bot usage"
      : !usageApi
        ? "Waiting for usage API"
      : "MCP billing unavailable";
  const recallMcpFeatureGroups = Array.isArray(recallMcp?.featureGroups) ? recallMcp.featureGroups : [];
  const recallApiStatusLabel = !usageApi
    ? "Waiting for usage API"
    : usageApi?.recall?.connected
      ? "Connected"
      : "Not connected";
  const recallRegionLabel = !usageApi ? "Waiting for usage API" : (usageApi?.recall?.region || "—");
  const recallPricingLabel = !usageApi
    ? "Waiting for usage API"
    : `bot $${usageApi?.pricing?.usageRatePerHourUsd ?? 0}/hr, transcription $${usageApi?.pricing?.transcriptionRatePerHourUsd ?? 0}/hr`;

  const usageHeaderRangeLabel = (() => {
    const fmt = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (usageRange === "overall") return "All time";
    const fromD = usageDateFromY ? ymdToDate(usageDateFromY) : null;
    const toD = usageDateToY ? ymdToDate(usageDateToY) : fromD;
    if (fromD && toD) {
      if (usageDateFromY === usageDateToY) return fmt(fromD);
      return `${fmt(fromD)} – ${fmt(toD)}`;
    }
    if (usageRange === "today") return `${fmt(startToday)} – ${fmt(endToday)}`;
    if (usageRange === "weekly") return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
    if (usageRange === "monthly") return `${fmt(monthStart)} – ${fmt(monthEnd)}`;
    return "Pick dates";
  })();

  const applyUsagePeriod = (preset) => {
    setUsageRange(preset);
    const now = new Date();
    if (preset === "today") {
      const t = dateToYmd(now);
      setUsageDateFrom(t);
      setUsageDateTo(t);
    } else if (preset === "weekly") {
      setUsageDateFrom(dateToYmd(weekStart));
      setUsageDateTo(dateToYmd(weekEnd));
    } else if (preset === "monthly") {
      setUsageDateFrom(dateToYmd(monthStart));
      setUsageDateTo(dateToYmd(monthEnd));
    } else if (preset === "overall") {
      setUsageDateFrom("");
      setUsageDateTo("");
    }
  };

  const onUsageDateFromChange = (nextFrom) => {
    adminDateRangeOnFromChange(nextFrom, usageDateTo, setUsageDateFrom, setUsageDateTo);
    setUsageRange("custom");
  };

  const onUsageDateToChange = (nextTo) => {
    adminDateRangeOnToChange(usageDateFrom, nextTo, setUsageDateFrom, setUsageDateTo);
    setUsageRange("custom");
  };
  const usagePricingBotLabel = !usageApi
    ? "—"
    : `bot $${usageApi?.pricing?.usageRatePerHourUsd ?? 0}/hr`;
  const usagePricingTranscriptionLabel = !usageApi
    ? ""
    : `transcription $${usageApi?.pricing?.transcriptionRatePerHourUsd ?? 0}/hr`;
  const usageKpiSessionsTrend = (() => {
    const d = new Date();
    const curKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const prevKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
    const dayOfMonth = d.getDate();
    const countSessionsMtdThroughDay = (monthKey, throughDay) => {
      const [y, mo] = String(monthKey).split("-").map(Number);
      if (!y || !mo) return 0;
      let n = 0;
      for (const mtg of usageMeetings) {
        const pd = parseMeetingDate(mtg);
        if (!pd || pd.getFullYear() !== y || pd.getMonth() + 1 !== mo) continue;
        if (pd.getDate() <= throughDay) n += 1;
      }
      return n;
    };
    const curCount = countSessionsMtdThroughDay(curKey, dayOfMonth);
    const prevCount = countSessionsMtdThroughDay(prevKey, dayOfMonth);
    if (prevCount <= 0) return null;
    const pct = Math.round(((curCount - prevCount) / prevCount) * 100);
    if (!Number.isFinite(pct)) return null;
    const short = pct >= 0 ? `+${pct}% vs last month` : `${pct}% vs last month`;
    const title = `${short} · ${curCount.toLocaleString()} sessions (${trendMonthLabel(curKey)}, days 1–${dayOfMonth}) vs ${prevCount.toLocaleString()} (${trendMonthLabel(prevKey)}, days 1–${dayOfMonth})`;
    return { short, title, pct };
  })();
  const usageKpiEstimatedCost = (() => {
    const d = new Date();
    const curKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const mcp = mcpMonthBurn(curKey);
    if (mcp != null && Number.isFinite(Number(mcp))) {
      return { display: `$${Number(mcp).toFixed(2)}`, sub: "This month · MCP metered" };
    }
    const est = usageApi?.recall?.estimatedMonthCostUsd;
    if (typeof est === "number" && Number.isFinite(est)) {
      return { display: `$${Number(est).toFixed(2)}`, sub: "This month · estimated" };
    }
    const cur = (usageMonths || []).find((m) => m.month === curKey);
    if (cur && Number(cur.costUsd) > 0) {
      return { display: `$${Number(cur.costUsd).toFixed(2)}`, sub: "This month · local estimate" };
    }
    if (usageBudget?.spentUsd != null) {
      return { display: `$${Number(usageBudget.spentUsd).toFixed(2)}`, sub: "This month · budget tracker" };
    }
    return { display: "—", sub: "This month" };
  })();
  const usageCreditProgressPct =
    recallMcpCreditUsd == null ? 0 : Math.min(100, Math.round((recallMcpCreditUsd / 20) * 100));
  const usageCreditThresholdRows = (usageApi?.recallCreditAlerts?.thresholds || [20, 15, 10, 5, 2]).map(
    (amount) => ({
      amount,
      emailedAt: usageApi?.recallCreditAlerts?.sent?.[String(amount)] || null,
    })
  );
  const usageLiveHeaderLabel =
    usageTotals.live > 0 ? `Live · ${usageTotals.live}` : liveMeetings.length > 0 ? `Live · ${liveMeetings.length}` : "Live";
  const usageLiveHeaderTone = usageTotals.live > 0 || liveMeetings.length > 0 ? "ok" : "idle";
  const usageKpiSparkMonths = (trendMonthList || []).slice(-8);
  const usageKpiSparkMeetings = usageKpiSparkMonths.map((t) => t.meetings);
  const usageKpiSparkCost = usageKpiSparkMonths.map((t) => Math.round((t.costUsd || 0) * 100) / 100);
  const usageKpiSparkTranscripts = usageKpiSparkMonths.map((t) => t.transcriptRows);
  const usageKpiSparkCredits = recallCreditSnapshotHistory
    .slice(0, 8)
    .reverse()
    .map((s) => Number(s.creditBalanceUsd) || 0);
  const usageCreditHealthBlurb =
    recallMcpCreditUsd == null
      ? "Waiting for live MCP credit balance."
      : recallMcpCreditUsd > 20
        ? "Credit balance remains healthy for current Recall MCP usage."
        : recallMcpCreditUsd > 10
          ? "Balance is approaching alert thresholds — monitor usage closely."
          : "Balance is low — alert thresholds may trigger admin emails.";

  return (
    <div className="bot-admin-root admin-theme-light" style={{display:"flex",height:"100vh",maxHeight:"100dvh",fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:14,overflow:"hidden",width:"100%",maxWidth:"100vw"}}>
      <SidebarHoverTipReset showSidebarTips={adminSidebarCollapsed && !isAdminMobile} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        *{scrollbar-width:thin;scrollbar-color:rgba(125,149,170,0.5) transparent;}
        *::-webkit-scrollbar{width:7px;height:7px;}
        *::-webkit-scrollbar-track{background:transparent;}
        *::-webkit-scrollbar-thumb{background:rgba(125,149,170,0.5);border-radius:999px;}
        *::-webkit-scrollbar-thumb:hover{background:rgba(148,175,199,0.7);}
        input,button{font-family:inherit;}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulseA{0%,100%{opacity:1}50%{opacity:.3}}
        .apage{
          animation:fadeIn .2s ease;
          color:#0F172A;
          width:100%;
          max-width:1180px;
          margin:0 auto;
          display:flex;
          flex-direction:column;
          gap:14px;
        }
        .adm-nav{
          display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:10px;cursor:pointer;border:none;
          background:transparent;width:100%;text-align:left;font-size:13px;font-weight:500;
          color:#aab6c8;transition:background .15s ease,color .15s ease;
          white-space:nowrap;position:relative;
        }
        .adm-nav:hover{
          background:rgba(23,120,212,0.18);
          color:#E8F0F7;
        }
        .adm-nav.active{
          background:rgba(37,99,235,0.2);
          color:#FFFFFF;font-weight:650;
          box-shadow:inset 3px 0 0 #2563EB;
        }
        .adm-nav:focus-visible{outline:2px solid #60A5FA;outline-offset:2px;}
        .adm-nav:active{transform:none;}
        .adm-nav-icon{
          width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;
          flex-shrink:0;background:rgba(255,255,255,0.06);color:rgba(232,240,247,0.7);
          transition:background .2s ease,color .2s ease,box-shadow .2s ease,transform .2s ease;
        }
        .adm-nav:hover .adm-nav-icon{background:rgba(23,120,212,0.28);color:#BFDBFE;}
        .bot-admin-sidebar.is-collapsed .adm-nav{overflow:hidden;}
        .bot-admin-sidebar.is-collapsed .adm-nav::after{
          content:"";
          position:absolute;inset:0;border-radius:10px;pointer-events:none;
          background:linear-gradient(135deg,rgba(23,120,212,0.2) 0%,rgba(12,143,85,0.12) 100%);
          opacity:0;transition:opacity .22s ease;
        }
        .bot-admin-sidebar.is-collapsed .adm-nav:hover::after{opacity:1;}
        .bot-admin-sidebar.is-collapsed .adm-nav:hover .adm-nav-icon{
          background:linear-gradient(145deg,rgba(23,120,212,0.42) 0%,rgba(12,143,85,0.28) 100%);
          box-shadow:0 2px 10px rgba(23,120,212,0.28),inset 0 1px 0 rgba(255,255,255,0.2);
          transform:scale(1.06);
          color:#fff;
        }
        .adm-nav.active .adm-nav-icon{
          background:rgba(23,120,212,0.4);
          color:#FFFFFF;box-shadow:none;
        }
        .adm-nav-label{flex:1;min-width:0;letter-spacing:-0.01em;}
        .adm-card{
          background:#FFFFFF;
          border:1px solid #e3e8ef;
          border-radius:10px;
          box-shadow:none;
        }
        .adm-card-header{
          padding:14px 18px;
          border-bottom:1px solid #F1F5F9;
          font-size:14px;
          font-weight:800;
          color:#0F172A;
          letter-spacing:-0.02em;
        }
        .adm-card-sub{
          font-size:11px;
          font-weight:500;
          color:#64748B;
          margin-top:4px;
        }
        .adm-card-header--meetings{align-items:flex-start;}
        .adm-meetings-list-controls input[type="date"]{color-scheme:light;}
        .bot-admin-root.admin-theme-light .adm-meetings-list-controls input[type="date"]{color-scheme:light;}
        .adm-table-row{
          display:flex;
          align-items:center;
          gap:12px;
          padding:11px 18px;
          transition:background .12s ease;
        }
        .adm-table-row:hover{background:rgba(37,99,235,0.04);}
        .adm-ghost-btn{
          padding:10px 16px;
          background:#fff;
          border:1px solid ${ADM.border};
          border-radius:10px;
          font-size:12.5px;
          color:#334155;
          cursor:pointer;
          transition:all .14s ease;
        }
        .adm-ghost-btn:hover{background:rgba(255,255,255,0.08);color:#E8F0F7;}
        .adm-primary-btn{
          flex:1;
          padding:10px 14px;
          background:#1565C0;
          color:#fff;
          border:none;
          border-radius:10px;
          font-size:13px;
          font-weight:700;
          cursor:pointer;
          transition:filter .14s ease;
        }
        .adm-primary-btn:hover{filter:brightness(1.06);}
        /* .adm-btn — action button used by the SMTP recap drawer and the
           transcript glossary panel. These classes had no CSS at all, so the
           buttons rendered as raw unstyled browser controls. */
        .adm-btn{
          display:inline-flex;align-items:center;justify-content:center;gap:6px;
          padding:7px 14px;
          background:#fff;
          border:1px solid #CBD5E1;
          border-radius:9px;
          font-family:inherit;
          font-size:12.5px;
          font-weight:650;
          line-height:1.25;
          color:#334155;
          cursor:pointer;
          white-space:nowrap;
          transition:background .14s ease,border-color .14s ease,color .14s ease,box-shadow .14s ease,transform .06s ease;
        }
        .adm-btn:hover:not(:disabled){background:#F8FAFC;border-color:#94A3B8;color:#0F172A;}
        .adm-btn:active:not(:disabled){transform:translateY(1px);}
        .adm-btn:focus-visible{outline:2px solid #1565C0;outline-offset:2px;}
        .adm-btn:disabled{opacity:.55;cursor:not-allowed;}
        .adm-btn--primary,
        .adm-btn-primary{
          background:#1565C0;
          border-color:#1565C0;
          color:#fff;
          box-shadow:0 1px 2px rgba(21,101,192,0.24);
        }
        .adm-btn--primary:hover:not(:disabled),
        .adm-btn-primary:hover:not(:disabled){
          background:#1256A8;border-color:#1256A8;color:#fff;
          box-shadow:0 4px 12px rgba(21,101,192,0.30);
        }
        .adm-btn-ghost{
          background:transparent;
          border-color:transparent;
          color:#64748B;
          box-shadow:none;
        }
        .adm-btn-ghost:hover:not(:disabled){
          background:rgba(100,116,139,0.12);
          border-color:transparent;
          color:#0F172A;
        }
        .adm-btn--danger{
          background:#fff;border-color:#FECACA;color:#B91C1C;box-shadow:none;
        }
        .adm-btn--danger:hover:not(:disabled){
          background:#FEF2F2;border-color:#FCA5A5;color:#991B1B;
        }
        .adm-input{width:100%;padding:10px 13px;border:1.5px solid #1E3448;border-radius:9px;font-size:13px;color:#E8F0F7;background:#0D1B2A;outline:none;font-family:inherit;transition:border .15s;}
        .adm-input:focus:not(.adm-search-input){border-color:#1565C0;box-shadow:0 0 0 3px rgba(21,101,192,0.15);}
        .adm-input::placeholder{color:#5C7A8C;}
        .panel-scroll{
          overflow-y:auto;
          overflow-x:hidden;
          -webkit-overflow-scrolling:touch;
        }
        .bot-admin-root.admin-theme-light{
          --adm-bg:#F5F7F9;
          --adm-surface:#ffffff;
          --adm-border:#E5E9EE;
          --adm-text:#0f172a;
          --adm-muted:#64748b;
          --adm-accent:#1F6B63;
        }
        .bot-admin-root.admin-theme-light .adm-card{
          background:#FFFFFF;
          border:1px solid #e3e8ef;
          border-radius:10px;
          box-shadow:none;
        }
        .bot-admin-root.admin-theme-light .adm-stat-card{
          position:relative;
          overflow:hidden;
        }
        .bot-admin-root.admin-theme-light .adm-stat-card::before{
          content:"";
          position:absolute;
          top:0;left:0;right:0;
          height:3px;
          background:linear-gradient(90deg,#1F6B63 0%,#0C8F55 100%);
        }
        .bot-admin-root.admin-theme-light .adm-stat-card:hover{
          transform:none;
          box-shadow:0 2px 8px rgba(15,23,42,0.08);
        }
        .bot-admin-root.admin-theme-light .adm-stat-val{
          font-size:28px !important;
          font-weight:800 !important;
          color:#0F172A !important;
          letter-spacing:-0.03em;
        }
        .bot-admin-root.admin-theme-light .adm-stat-label{
          color:#64748B !important;
          font-weight:500 !important;
        }
        .bot-admin-root.admin-theme-light .adm-stat-sub{
          color:#1F6B63 !important;
          font-weight:600 !important;
        }
        .bot-admin-root.admin-theme-light .adm-card-header{
          border-bottom:1px solid #F1F5F9;
          color:#0F172A;
          font-size:14px;
          letter-spacing:-0.02em;
          font-weight:800;
        }
        .bot-admin-root.admin-theme-light .adm-card-sub{color:#64748b;}
        .cu-page{display:flex;flex-direction:column;gap:0;width:100%;max-width:1180px;margin:0 auto;}
        .cu-hero{position:relative;padding:0;margin-bottom:16px;overflow:hidden;border-radius:16px!important;}
        .cu-hero-glow{position:absolute;inset:0;background:var(--cu-gradient,linear-gradient(135deg,rgba(29,78,216,0.12) 0%,rgba(14,165,233,0.08) 50%,transparent 100%));pointer-events:none;}
        .bot-admin-root:not(.admin-theme-light) .cu-hero-glow{background:linear-gradient(135deg,rgba(59,130,246,0.15) 0%,rgba(16,185,129,0.08) 100%);}
        .cu-hero-content{position:relative;padding:22px 24px 20px;}
        .cu-hero-badge{display:inline-flex;align-items:center;gap:7px;padding:5px 11px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.02em;background:rgba(31,107,99,0.12);color:#1F6B63;margin-bottom:10px;}
        .bot-admin-root:not(.admin-theme-light) .cu-hero-badge{background:rgba(31,107,99,0.2);color:#A7F3D0;}
        .cu-hero-title{margin:0;font-size:22px;font-weight:800;letter-spacing:-0.03em;line-height:1.2;}
        .cu-hero-desc{margin:8px 0 0;font-size:13px;line-height:1.55;max-width:640px;}
        .cu-hero-meta{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:14px;font-size:11.5px;font-weight:500;}
        .cu-dot{width:4px;height:4px;border-radius:50%;background:currentColor;opacity:.45;}
        .cu-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:18px;}
        @media(max-width:900px){.cu-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
        .cu-kpi{position:relative;padding:16px 18px;border-radius:14px;background:var(--adm-surface,#112236);border:1px solid var(--adm-border,#1E3448);overflow:hidden;}
        .cu-kpi::before{content:"";position:absolute;top:0;left:0;right:0;height:3px;background:var(--cu-accent,#3b82f6);}
        .bot-admin-root.admin-theme-light .cu-kpi{box-shadow:0 1px 3px rgba(15,23,42,0.06);}
        .cu-kpi-label{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--adm-muted,#64748b);}
        .cu-kpi-value{margin-top:6px;font-size:26px;font-weight:800;letter-spacing:-0.03em;color:var(--adm-text,#0f172a);line-height:1.1;}
        .bot-admin-root:not(.admin-theme-light) .cu-kpi-value{color:#E8F0F7;}
        .cu-kpi-sub{margin-top:6px;font-size:11px;color:var(--adm-muted,#64748b);font-weight:500;}
        .cu-provider-card{border-radius:16px!important;}
        .cu-provider-accent-bar{height:4px;background:var(--cu-gradient,linear-gradient(90deg,var(--cu-accent),transparent));}
        .cu-provider-inner{padding:20px 22px 18px;}
        .cu-provider-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .cu-provider-title{font-size:17px;font-weight:800;letter-spacing:-0.02em;}
        .cu-model-pill{display:flex;flex-direction:column;gap:3px;padding:8px 12px;border-radius:10px;border:1px solid var(--adm-border,#e2e8f0);font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;}
        .cu-model-pill code{font-size:12px;font-weight:600;text-transform:none;letter-spacing:0;font-family:ui-monospace,monospace;}
        .cu-period-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:16px;}
        .cu-period-grid--3{grid-template-columns:repeat(3,minmax(0,1fr));}
        @media(max-width:900px){.cu-period-grid{grid-template-columns:repeat(2,minmax(0,1fr));}.cu-period-grid--3{grid-template-columns:repeat(3,minmax(0,1fr));}}
        @media(max-width:640px){.cu-period-grid,.cu-period-grid--3{grid-template-columns:1fr;}}
        .cu-chart-stack-scroll{
          max-height:min(520px,62vh);overflow-y:auto;overflow-x:hidden;
          -webkit-overflow-scrolling:touch;margin-top:4px;padding-right:4px;
          display:flex;flex-direction:column;gap:18px;
        }
        .cu-chart-stack-scroll--single{max-height:none;overflow-y:visible;gap:8px;}
        .cu-chart-stack-scroll::-webkit-scrollbar{width:8px;}
        .cu-chart-stack-scroll::-webkit-scrollbar-thumb{background:rgba(100,116,139,0.35);border-radius:4px;}
        .cu-chart-stack-section{flex-shrink:0;}
        .cu-chart-stack-title{font-size:12px;font-weight:800;letter-spacing:0.04em;text-transform:uppercase;margin-bottom:8px;color:var(--adm-muted,#64748b);}
        .cu-chart-panel--nested{margin-top:0;padding:12px 14px 10px;}
        .cu-period-stat{padding:14px 16px;border-radius:12px;border:1px solid var(--adm-border,#e2e8f0);background:rgba(248,250,252,0.6);transition:border-color .15s,box-shadow .15s;min-width:0;overflow:hidden;box-sizing:border-box;}
        button.cu-period-stat{appearance:none;-webkit-appearance:none;}
        button.cu-period-stat:hover{border-color:color-mix(in srgb,var(--cu-accent) 40%,var(--adm-border,#e2e8f0));}
        .bot-admin-root:not(.admin-theme-light) .cu-period-stat{background:rgba(255,255,255,0.03);}
        .cu-period-stat--active{border-color:var(--cu-accent,#3b82f6);box-shadow:0 0 0 1px color-mix(in srgb,var(--cu-accent) 25%,transparent);}
        .cu-period-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--adm-muted,#64748b);}
        .cu-period-tokens{margin-top:6px;font-size:clamp(15px,4.5cqi,20px);font-weight:800;letter-spacing:-0.02em;line-height:1.2;color:var(--adm-text,#0f172a);max-width:100%;overflow-wrap:anywhere;word-break:break-word;}
        .bot-admin-root:not(.admin-theme-light) .cu-period-tokens{color:#E8F0F7;}
        .cu-period-meta{display:flex;flex-direction:column;align-items:flex-start;gap:2px;margin-top:8px;font-size:11px;color:var(--adm-muted,#64748b);max-width:100%;}
        .cu-period-meta-label{font-weight:600;line-height:1.2;}
        .cu-period-cost{font-size:12px;font-weight:700;line-height:1.25;color:var(--cu-accent,#1d4ed8);max-width:100%;overflow-wrap:anywhere;}
        .cu-chart-panel{margin-top:4px;padding:16px;border-radius:12px;background:rgba(248,250,252,0.5);border:1px solid var(--adm-border,#e2e8f0);}
        .bot-admin-root:not(.admin-theme-light) .cu-chart-panel{background:rgba(0,0,0,0.15);}
        .cu-chart-wrap{width:100%;position:relative;overflow:visible;}
        .cu-chart-legend{display:flex;gap:18px;margin-bottom:10px;font-size:11px;font-weight:600;}
        .cu-legend-item{display:flex;align-items:center;gap:6px;}
        .cu-legend-dot{width:10px;height:10px;border-radius:3px;}
        .cu-chart-svg{display:block;border-radius:8px;}
        .cu-chart-empty{padding:32px 20px;text-align:center;}
        .adm-chart-scroll{overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;max-width:100%;}
        .adm-chart-scroll-inner{width:100%;}
        .adm-chart-scroll::-webkit-scrollbar{height:8px;}
        .adm-chart-scroll::-webkit-scrollbar-thumb{background:rgba(100,116,139,0.35);border-radius:4px;}
        .adm-chart-scroll::-webkit-scrollbar-track{background:transparent;}
        .adm-chart-note{font-size:10.5px;text-align:center;margin-top:6px;line-height:1.5;}
        .cu-chart-tooltip{pointer-events:none;}
        .bot-admin-root.admin-theme-light .cu-chart-tooltip{color:#0f172a !important;}
        .bot-admin-root.admin-theme-light .cu-chart-tooltip-title{color:#0f172a !important;}
        .bot-admin-root.admin-theme-light .cu-chart-tooltip-muted{color:#64748b !important;}
        .bot-admin-root:not(.admin-theme-light) .cu-chart-tooltip{color:#F8FAFC !important;}
        .bot-admin-root:not(.admin-theme-light) .cu-chart-tooltip-title{color:#FFFFFF !important;}
        .bot-admin-root:not(.admin-theme-light) .cu-chart-tooltip-muted{color:rgba(248,250,252,0.72) !important;}
        .smtp-line-chart-wrap{width:100%;position:relative;}
        .smtp-line-legend{display:flex;justify-content:center;flex-wrap:wrap;gap:20px;margin-bottom:14px;font-size:12px;font-weight:600;color:var(--adm-muted,#64748b);}
        .smtp-line-legend-item{display:flex;align-items:center;gap:8px;}
        .smtp-line-legend-swatch{width:16px;height:8px;border-radius:2px;border:2px solid currentColor;background:transparent;}
        .smtp-chart-tooltip{
          position:absolute;z-index:8;pointer-events:none;
          background:rgba(15,23,42,0.92);color:#F8FAFC;
          border:1px solid rgba(255,255,255,0.16);border-radius:8px;
          padding:7px 10px;min-width:140px;max-width:min(78vw,280px);
          box-shadow:0 8px 22px rgba(2,6,23,0.42);
          transform:translate(-50%,-120%);font-size:11.5px;line-height:1.35;text-align:left;
        }
        .smtp-chart-tooltip,.smtp-chart-tooltip *{color:#F8FAFC !important;}
        .smtp-chart-tooltip-title{font-weight:700;margin-bottom:3px;color:#FFFFFF !important;}
        .smtp-chart-tooltip-row{display:flex;align-items:center;gap:6px;color:#E2E8F0 !important;}
        .smtp-chart-tooltip-dot{width:8px;height:8px;border-radius:999px;flex-shrink:0;}
        .smtp-chart-segment{display:inline-flex;background:var(--adm-chip,#f1f5f9);border-radius:999px;padding:4px;gap:2px;margin:16px auto 0;}
        .bot-admin-root:not(.admin-theme-light) .smtp-chart-segment{background:rgba(255,255,255,0.08);}
        .smtp-chart-segment button{border:none;background:transparent;padding:7px 20px;border-radius:999px;font-size:12px;font-weight:500;color:var(--adm-muted,#64748b);cursor:pointer;transition:background .15s,box-shadow .15s,color .15s;}
        .smtp-chart-segment button.active{background:#fff;color:var(--adm-text,#0f172a);font-weight:600;box-shadow:0 1px 4px rgba(15,23,42,0.12);}
        .bot-admin-root:not(.admin-theme-light) .smtp-chart-segment button.active{background:rgba(30,41,59,0.95);color:#E8F0F7;box-shadow:0 1px 4px rgba(0,0,0,0.35);}
        .smtp-line-chart-footer{display:flex;justify-content:center;}
        .smtp-usage-list-toolbar{padding:10px 14px;border-bottom:1px solid var(--adm-border,#e2e8f0);}
        .smtp-usage-list-scroll{max-height:min(420px,55vh);overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;}
        .smtp-usage-list-scroll::-webkit-scrollbar{width:8px;}
        .smtp-usage-list-scroll::-webkit-scrollbar-thumb{background:rgba(100,116,139,0.35);border-radius:4px;}
        .bot-admin-root:not(.admin-theme-light) .smtp-usage-list-scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.2);}
        .smtp-meeting-card{border-bottom:1px solid var(--adm-border,#e2e8f0);}
        .smtp-meeting-card:last-child{border-bottom:none;}
        .smtp-meeting-card-btn{width:100%;display:flex;align-items:center;gap:10px;padding:10px 12px;border:none;background:transparent;cursor:pointer;text-align:left;transition:background .12s ease;}
        .smtp-meeting-card-btn:hover{background:rgba(37,99,235,0.04);}
        .bot-admin-root:not(.admin-theme-light) .smtp-meeting-card-btn:hover{background:rgba(255,255,255,0.04);}
        .smtp-meeting-card-btn--open{background:rgba(248,250,252,0.85);}
        .bot-admin-root:not(.admin-theme-light) .smtp-meeting-card-btn--open{background:rgba(255,255,255,0.05);}
        .smtp-meeting-card-body{padding:0 12px 12px 38px;}
        .smtp-icon-chip{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;padding:3px 7px;border-radius:6px;white-space:nowrap;flex-shrink:0;}
        .smtp-recipient-chip-scroll{
          display:flex;flex-wrap:wrap;gap:6px;align-items:center;align-content:flex-start;
          max-height:min(36vh,220px);overflow-y:auto;overscroll-behavior:contain;
          -webkit-overflow-scrolling:touch;padding:8px 10px;border-radius:8px;
          border:1px solid rgba(147,197,253,0.45);background:rgba(239,246,255,0.75);
        }
        .bot-admin-root:not(.admin-theme-light) .smtp-recipient-chip-scroll{
          border-color:rgba(59,130,246,0.25);background:rgba(37,99,235,0.08);
        }
        .smtp-recipient-pill{
          display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;
          font-size:11.5;font-weight:500;max-width:min(100%,280px);overflow:hidden;
          text-overflow:ellipsis;white-space:nowrap;cursor:default;
        }
        .cu-footnote{margin-top:12px;font-size:10.5px;font-style:italic;opacity:.85;}
        .cu-users-card{border-radius:16px!important;}
        .cu-users-table-wrap{display:flex;flex-direction:column;min-height:0;}
        .cu-users-table-scroll{overflow:auto;max-height:min(480px,56vh);-webkit-overflow-scrolling:touch;}
        .cu-users-table{width:100%;border-collapse:collapse;font-size:12.5px;}
        .cu-users-table thead th{position:sticky;top:0;z-index:2;text-align:left;padding:10px 16px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;background:var(--adm-surface,#fff);}
        .bot-admin-root:not(.admin-theme-light) .cu-users-table thead th{background:#112236;}
        .cu-users-table tbody td{padding:12px 16px;vertical-align:middle;color:var(--adm-text,#0f172a);}
        .bot-admin-root:not(.admin-theme-light) .cu-users-table tbody td{color:#E8F0F7;}
        .cu-users-table-row{cursor:pointer;transition:background .12s ease;border-bottom:1px solid var(--adm-border,#edf0f4);}
        .cu-users-table-row:hover{background:rgba(37,99,235,0.05);}
        .bot-admin-root:not(.admin-theme-light) .cu-users-table-row:hover{background:rgba(255,255,255,0.04);}
        .cu-users-table-row--active{background:rgba(37,99,235,0.08)!important;}
        .cu-users-table-user{display:flex;align-items:center;gap:10px;min-width:0;}
        .cu-users-table-user-text{display:flex;flex-direction:column;gap:2px;min-width:0;}
        .cu-users-table-user-text .cu-user-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .cu-users-table-user-text .cu-user-email{font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .cu-user-avatar--sm{width:32px;height:32px;font-size:11px;}
        .cu-users-table-cost{font-weight:700;color:#1d4ed8;}
        .cu-users-table--compact{font-size:11.5px;}
        .cu-users-table--compact thead th,.cu-users-table--compact tbody td{padding:8px 10px;}
        .cu-drawer-recent{margin-top:4px;}
        .cu-drawer-recent-scroll{overflow:auto;max-height:min(360px,45vh);margin:8px 0;border:1px solid var(--adm-border,#e2e8f0);border-radius:10px;-webkit-overflow-scrolling:touch;}
        .cu-drawer-chat-banner{padding:10px 12px;border-radius:10px;background:rgba(248,250,252,0.9);border:1px solid var(--adm-border,#e2e8f0);}
        .bot-admin-root:not(.admin-theme-light) .cu-drawer-chat-banner{background:rgba(255,255,255,0.04);}
        .cu-drawer-chat-scroll{display:flex;flex-direction:column;gap:18px;max-height:min(520px,62vh);overflow:auto;margin:10px 0 4px;padding:8px 6px 10px;-webkit-overflow-scrolling:touch;}
        .cu-drawer-chat-day{display:flex;flex-direction:column;gap:14px;}
        .cu-drawer-chat-day-pill{align-self:center;font-size:11px;font-weight:700;letter-spacing:.03em;padding:5px 14px;border-radius:999px;border:1px solid var(--adm-border,#e2e8f0);position:sticky;top:0;z-index:2;backdrop-filter:blur(6px);}
        .cu-chat-exchange{display:flex;flex-direction:column;gap:8px;width:100%;}
        .cu-chat-user-block{display:flex;flex-direction:column;align-items:flex-end;gap:4px;width:100%;margin-left:auto;}
        .cu-chat-user-label{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:600;justify-content:flex-end;}
        .cu-chat-user-row{display:flex;align-items:flex-end;gap:8px;max-width:100%;justify-content:flex-end;}
        .cu-chat-user-bubble{background:linear-gradient(135deg,#16a34a,#15803d);color:#fff;font-size:13px;line-height:1.45;padding:10px 14px;border-radius:16px 16px 4px 16px;max-width:min(88%,340px);word-break:break-word;white-space:pre-wrap;box-shadow:0 2px 8px rgba(22,163,74,0.22);}
        .cu-chat-user-avatar{width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,#1d4ed8,#0ea5e9);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .cu-chat-ai-row{display:flex;align-items:flex-start;gap:8px;width:100%;max-width:100%;justify-content:flex-start;}
        .cu-chat-ai-avatar{width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,#0f766e,#14b8a6);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px;box-shadow:0 2px 6px rgba(15,118,110,0.25);}
        .cu-chat-ai-bubble{flex:1;min-width:0;max-width:min(88%,420px);border:1px solid var(--adm-border,#e2e8f0);border-radius:14px 14px 14px 4px;padding:8px 12px 10px;box-shadow:0 1px 3px rgba(15,23,42,0.06);}
        .cu-chat-ai-bubble-toggle{width:100%;display:block;border:none;background:transparent;padding:0;margin:0;cursor:pointer;font:inherit;text-align:left;}
        .cu-chat-ai-bubble-head{display:flex;align-items:center;justify-content:flex-start;gap:8px;flex-wrap:wrap;width:100%;}
        .cu-chat-ai-bubble-title{font-size:11px;font-weight:800;letter-spacing:.02em;text-transform:uppercase;opacity:.85;flex:1;min-width:120px;}
        .cu-chat-ai-meta-chips{display:flex;flex-wrap:wrap;gap:4px;align-items:center;margin-left:auto;}
        .cu-chat-ai-preview{padding:2px 0 4px;font-size:12px;line-height:1.5;opacity:.92;}
        .cu-chat-meta-chip{font-size:10px;font-weight:700;padding:2px 7px;border-radius:999px;background:rgba(37,99,235,0.12);color:#1d4ed8;text-transform:uppercase;letter-spacing:.02em;}
        .cu-chat-meta-chip--rule{background:rgba(148,163,184,0.2);color:#64748b;}
        .cu-chat-meta-chip--muted{text-transform:none;font-weight:600;background:rgba(148,163,184,0.15);color:#64748b;}
        .cu-chat-ai-meta-line{display:flex;flex-wrap:wrap;align-items:center;gap:4px;font-size:10.5px;margin-bottom:8px;font-weight:600;}
        .cu-chat-meta-dot{opacity:.55;}
        .cu-chat-ai-body-text--rich .assistant-chat-body{font-size:12.5px;line-height:1.55;}
        .cu-chat-ai-body-text--rich .assistant-meet-link{cursor:default;text-decoration:underline;text-underline-offset:2px;}
        .cu-chat-ai-legacy{flex:1;min-width:0;max-width:min(88%,420px);padding:10px 12px;border-radius:14px 14px 14px 4px;border:1px dashed;font-size:11.5;}
        .cu-drawer-tab{line-height:1.2;box-shadow:none;}
        .cu-drawer-tab--active{box-shadow:0 1px 2px rgba(29,78,216,0.12);}
        .cu-users-head{padding:16px 20px;}
        .cu-users-search-sort{display:flex;flex-wrap:wrap;align-items:center;gap:10px 12px;flex:1 1 280px;min-width:0;justify-content:flex-end;}
        .cu-users-search-sort .adm-search-inline--static{flex:1 1 240px;min-width:min(100%,220px);max-width:480px;}
        .cu-users-search-sort .adm-search-inline--static .adm-search-inline-field{flex:1;min-width:0;width:100%;}
        .cu-users-search-sort .adm-search-inline--static .adm-search-inline-field input{width:100%;max-width:none;flex:1;}
        .cu-users-sort{display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:600;flex-shrink:0;}
        .cu-users-sort-label{white-space:nowrap;}
        .cu-users-sort-select{font-size:12px;font-weight:600;padding:8px 10px;border-radius:8px;border:1px solid var(--adm-border,#e2e8f0);cursor:pointer;max-width:min(100%,240px);}
        .cu-users-title{font-size:15px;font-weight:800;letter-spacing:-0.02em;}
        .cu-users-sub{margin-top:4px;font-size:11.5px;}
        .cu-empty{padding:32px 20px;text-align:center;font-size:13px;}
        .cu-user-btn{width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 20px;border:none;cursor:pointer;text-align:left;transition:background .15s ease;}
        .cu-user-btn:hover{background:rgba(37,99,235,0.04)!important;}
        .bot-admin-root:not(.admin-theme-light) .cu-user-btn:hover{background:rgba(255,255,255,0.04)!important;}
        .cu-user-left{display:flex;align-items:center;gap:12px;min-width:0;}
        .cu-user-avatar{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;color:#fff;background:linear-gradient(135deg,#1d4ed8,#0ea5e9);flex-shrink:0;letter-spacing:.02em;}
        .cu-user-info{min-width:0;}
        .cu-user-name{font-weight:700;font-size:13.5px;letter-spacing:-0.01em;}
        .cu-user-email{font-size:11px;font-family:ui-monospace,monospace;opacity:.85;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .cu-user-stats{display:flex;flex-wrap:wrap;gap:6px;justify-content:flex-end;flex-shrink:0;}
        .cu-stat-pill{padding:5px 10px;border-radius:999px;font-size:11px;font-weight:600;background:rgba(148,163,184,0.15);color:var(--adm-muted,#64748b);}
        .cu-stat-pill--cost{background:rgba(37,99,235,0.12);color:#1d4ed8;}
        .bot-admin-root:not(.admin-theme-light) .cu-stat-pill--cost{background:rgba(59,130,246,0.2);color:#93c5fd;}
        .cu-recent-block{margin-top:12px;border:1px solid var(--adm-border,#e2e8f0);border-radius:12px;overflow:hidden;background:rgba(248,250,252,0.45);}
        .bot-admin-root:not(.admin-theme-light) .cu-recent-block{background:rgba(0,0,0,0.12);}
        .cu-recent-toggle{width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 14px;border:none;background:transparent;cursor:pointer;font:inherit;transition:background .12s ease;}
        .cu-recent-toggle:hover{background:rgba(37,99,235,0.05);}
        .bot-admin-root:not(.admin-theme-light) .cu-recent-toggle:hover{background:rgba(255,255,255,0.04);}
        .cu-recent-toggle-label{display:inline-flex;align-items:center;gap:8px;font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;}
        .cu-recent-count{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 6px;border-radius:999px;font-size:10px;font-weight:800;background:rgba(37,99,235,0.12);color:#1d4ed8;}
        .bot-admin-root:not(.admin-theme-light) .cu-recent-count{background:rgba(59,130,246,0.22);color:#BFDBFE;}
        .cu-recent-scroll{max-height:220px;overflow-y:auto;overflow-x:hidden;padding:4px 14px 10px;-webkit-overflow-scrolling:touch;scrollbar-width:thin;}
        .cu-recent-scroll::-webkit-scrollbar{width:6px;}
        .cu-recent-scroll::-webkit-scrollbar-thumb{background:rgba(148,163,184,0.45);border-radius:999px;}
        .cu-recent-row{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:10px 0;font-size:11px;}
        .cu-recent-row-left{display:flex;flex-direction:column;gap:3px;min-width:0;flex:1;}
        .cu-recent-badge{display:inline-flex;align-self:flex-start;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;}
        .cu-recent-badge--rule{background:rgba(148,163,184,0.2);color:#64748b;}
        .cu-recent-badge--llm{background:rgba(16,163,127,0.15);color:#0f766e;}
        .bot-admin-root:not(.admin-theme-light) .cu-recent-badge--llm{background:rgba(16,163,127,0.25);color:#6ee7b7;}
        .cu-recent-model{font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:280px;}
        .cu-recent-time{font-size:10px;opacity:.85;}
        .cu-recent-metrics{flex-shrink:0;font-weight:700;font-size:11px;white-space:nowrap;}
        .apage.adm-premium-page{animation:fadeIn .35s ease;max-width:none;margin:0;width:100%;}
        .adm-hero{position:relative;padding:0;margin-bottom:0;overflow:hidden;border-radius:16px!important;}
        .adm-hero-glow{position:absolute;inset:0;background:var(--adm-hero-gradient);pointer-events:none;}
        .adm-hero-inner{position:relative;padding:22px 24px 20px;}
        .adm-hero-badge{display:inline-flex;align-items:center;gap:7px;padding:5px 11px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.02em;background:rgba(31,107,99,0.12);color:#1F6B63;margin-bottom:10px;}
        .bot-admin-root:not(.admin-theme-light) .adm-hero-badge{background:rgba(31,107,99,0.2);color:#A7F3D0;}
        .adm-hero-title{margin:0;font-size:22px;font-weight:800;letter-spacing:-0.03em;line-height:1.2;color:var(--adm-text,#0F172A);}
        .adm-hero-desc{margin:8px 0 0;font-size:13px;line-height:1.55;max-width:680px;color:var(--adm-muted,#64748b);}
        .adm-hero-meta{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:14px;font-size:11.5px;font-weight:500;color:var(--adm-muted,#64748b);}
        .adm-hero-dot{width:4px;height:4px;border-radius:50%;background:currentColor;opacity:.45;}
        .adm-stack{display:flex;flex-direction:column;gap:14px;width:100%;max-width:1180px;margin:0 auto;}
        .bot-admin-root .adm-stack > .adm-search-toolbar{margin-bottom:0!important;}
        .bot-admin-root .adm-stack > .MuiPaper-root{margin-bottom:0!important;}
        .bot-admin-root .adm-stack > .adm-card,
        .bot-admin-root .adm-stack > .adm-kpi-grid,
        .bot-admin-root .adm-stack > .adm-side-grid{margin-bottom:0;}
        .adm-kpi-grid,.cu-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:16px;width:100%;align-items:stretch;}
        .adm-kpi-grid--6{grid-template-columns:repeat(3,minmax(0,1fr));}
        @media(max-width:900px){.adm-kpi-grid,.cu-kpi-grid,.adm-kpi-grid--6{grid-template-columns:repeat(2,minmax(0,1fr));}}
        /* Two columns leaves ~180px per card on a phone, which truncates the
           larger figures. adm-kpi-grid--4 already drops to one at 520px; these
           variants were missed. */
        @media(max-width:480px){.adm-kpi-grid,.cu-kpi-grid,.adm-kpi-grid--6{grid-template-columns:1fr;}}
        .bot-admin-root .adm-stack > .adm-kpi-grid,
        .bot-admin-root .adm-stack > .cu-kpi-grid{margin-bottom:0;}
        .bot-admin-root .adm-premium-page > .adm-card,
        .bot-admin-root .adm-premium-page .adm-org-panel,
        .bot-admin-root .adm-premium-page .adm-cal-shell,
        .bot-admin-root .adm-premium-page .adm-stack > .adm-card,
        .bot-admin-root .adm-premium-page .MuiPaper-root{
          width:100%;
          max-width:100%;
          box-sizing:border-box;
        }
        .bot-admin-root .adm-premium-page .adm-kpi-grid > *,
        .bot-admin-root .adm-premium-page .admin-overview-stat-grid > *{
          min-width:0;
          width:100%;
          height:100%;
        }
        /* Keep Usage / admin KPI rows 4-across; ignore user-portal .admin-overview-stat-grid ≤1024 overrides */
        .bot-admin-root .adm-premium-page .adm-kpi-grid.adm-kpi-grid--4{
          display:grid !important;
          grid-template-columns:repeat(4,minmax(0,1fr)) !important;
          gap:14px !important;
          width:100%;
          margin:0;
          align-items:stretch;
        }
        @media(max-width:900px){
          .bot-admin-root .adm-premium-page .adm-kpi-grid.adm-kpi-grid--4{
            grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          }
        }
        @media(max-width:520px){
          .bot-admin-root .adm-premium-page .adm-kpi-grid.adm-kpi-grid--4{
            grid-template-columns:1fr !important;
          }
        }
        .adm-mcp-status-grid{
          display:grid;
          grid-template-columns:repeat(4,minmax(0,1fr));
          gap:10px;
          width:100%;
        }
        @media(max-width:900px){.adm-mcp-status-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
        @media(max-width:520px){.adm-mcp-status-grid{grid-template-columns:1fr;}}
        .adm-side-grid{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(0,1fr);
          gap:12px;
          width:100%;
          align-items:start;
          margin:0;
        }
        .adm-side-grid > .adm-card{
          min-width:0;
          min-height:0;
          display:flex;
          flex-direction:column;
        }
        .adm-usage-columns{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(0,1fr);
          gap:12px;
          width:100%;
          align-items:stretch;
        }
        .adm-usage-column{
          min-width:0;
          display:contents;
        }
        .adm-usage-column > .adm-card{
          min-width:0;
          height:100%;
          display:flex;
          flex-direction:column;
        }
        .adm-usage-column:first-child > .adm-card:nth-child(1){
          grid-column:1;
          grid-row:1;
        }
        .adm-usage-column:nth-child(2) > .adm-card:nth-child(1){
          grid-column:2;
          grid-row:1;
        }
        .adm-usage-column:first-child > .adm-card:nth-child(2){
          grid-column:1;
          grid-row:2;
        }
        .adm-usage-column:nth-child(2) > .adm-card:nth-child(2){
          grid-column:2;
          grid-row:2;
        }
        .adm-usage-table-card{
          min-height:0;
        }
        .adm-usage-table-card > .adm-scroll-panel{
          flex:1;
        }
        @media(max-width:960px){
          .adm-side-grid{grid-template-columns:1fr !important;}
          .adm-usage-columns{grid-template-columns:1fr !important;}
          .adm-usage-column > .adm-card{
            grid-column:auto !important;
            grid-row:auto !important;
          }
          .adm-usage-table-card{min-height:auto;}
        }
        .adm-kpi,.cu-kpi{position:relative;padding:16px 18px;border-radius:14px;background:var(--adm-surface,#112236);border:1px solid var(--adm-border,#1E3448);overflow:hidden;}
        .adm-kpi::before,.cu-kpi::before{content:"";position:absolute;top:0;left:0;right:0;height:3px;background:var(--adm-accent,var(--cu-accent,#3b82f6));}
        .bot-admin-root.admin-theme-light .adm-kpi,.bot-admin-root.admin-theme-light .cu-kpi{box-shadow:0 1px 3px rgba(15,23,42,0.06);}
        .adm-kpi-label,.cu-kpi-label{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--adm-muted,#64748b);}
        .adm-kpi-value,.cu-kpi-value{margin-top:6px;font-size:26px;font-weight:800;letter-spacing:-0.03em;color:var(--adm-text,#0f172a);line-height:1.1;}
        .bot-admin-root:not(.admin-theme-light) .adm-kpi-value,.bot-admin-root:not(.admin-theme-light) .cu-kpi-value{color:#E8F0F7;}
        .adm-kpi-sub,.cu-kpi-sub{margin-top:6px;font-size:11px;color:var(--adm-muted,#64748b);font-weight:500;}
        .bot-admin-root .adm-card{border-radius:10px!important;box-shadow:none!important;transition:border-color .2s ease;}
        .bot-admin-root.admin-theme-light .adm-card{box-shadow:none!important;}
        .bot-admin-root.admin-theme-light .adm-card:hover{box-shadow:none!important;border-color:#e3e8ef;}
        .adm-card-header{padding:14px 18px!important;font-size:13.5px!important;font-weight:650!important;letter-spacing:-0.01em;}
        /* Visible keyboard focus in admin — not on search fields (plain gray border only). */
        .bot-admin-root :focus-visible:not(.adm-search-icon-btn){
          outline:2px solid #2563eb;
          outline-offset:2px;
          border-radius:6px;
        }
        .bot-admin-root .adm-users-search:focus-within,
        .bot-admin-root .adm-users-search input:focus,
        .bot-admin-root .adm-users-search input:focus-visible,
        .bot-admin-root .adm-search-inline-field:focus-within,
        .bot-admin-root .adm-search-inline-field input:focus,
        .bot-admin-root .adm-search-inline-field input:focus-visible,
        .bot-admin-root .adm-input.adm-search-input:focus,
        .bot-admin-root .adm-input.adm-search-input:focus-visible{
          outline:none!important;
          outline-offset:0!important;
          box-shadow:none!important;
        }
        .adm-card-sub{margin-top:5px!important;font-size:11.5px!important;line-height:1.45;}
        .adm-status-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}
        @media(max-width:640px){.adm-status-grid{grid-template-columns:1fr;}}
        .adm-status-item{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px;border-radius:12px;border:1px solid var(--adm-border,#1E3448);background:rgba(248,250,252,0.5);}
        .bot-admin-root:not(.admin-theme-light) .adm-status-item{background:rgba(255,255,255,0.03);}
        .adm-status-label{font-size:12px;font-weight:600;color:var(--adm-muted,#64748b);}
        .adm-status-detail{font-size:10px;font-family:ui-monospace,monospace;opacity:.75;margin-top:2px;}
        .adm-status-pill{font-size:11px;font-weight:700;display:inline-flex;align-items:center;gap:5px;}
        .adm-status-pill--ok{color:#15803d;}
        .adm-status-pill--err{color:#b91c1c;}
        .bot-admin-root:not(.admin-theme-light) .adm-status-pill--ok{color:#86EFAC;}
        .bot-admin-root:not(.admin-theme-light) .adm-status-pill--err{color:#F87171;}
        .adm-status-dot{width:6px;height:6px;border-radius:50%;}
        .adm-scroll-panel{max-height:min(52vh,520px);overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;scrollbar-width:thin;}
        .adm-scroll-panel--meetings{max-height:min(62vh,640px);}
        .adm-scroll-panel::-webkit-scrollbar{width:6px;}
        .adm-scroll-panel::-webkit-scrollbar-thumb{background:rgba(148,163,184,0.45);border-radius:999px;}
        .adm-list-pagination button:hover:not(:disabled){filter:brightness(0.97);}
        .adm-table-row{padding:14px 20px!important;transition:background .12s ease;}
        .adm-topbar{backdrop-filter:saturate(1.2) blur(8px);}
        .bot-admin-root.admin-theme-light .adm-topbar{box-shadow:0 1px 0 rgba(15,23,42,0.04);}
        .adm-page-scroll{padding:22px 26px!important;}
        /* ---- Usage & Billing redesign tokens (scoped to .adm-usage-redesign) ---- */
        .adm-usage-redesign{font-family:Inter,system-ui,-apple-system,"Segoe UI",sans-serif;}
        .bot-admin-root .adm-usage-redesign .adm-card{
          background:#FFFFFF;
          border:1px solid #E6EBF2;
          border-radius:15px !important;
          box-shadow:0 1px 3px rgba(15,23,42,0.04);
        }
        .bot-admin-root.admin-theme-light .adm-usage-redesign .adm-card{box-shadow:none;}
        .bot-admin-root.admin-theme-light .adm-usage-redesign .adm-card:hover{box-shadow:none;}
        .adm-usage-redesign .ub-num{font-weight:650;}
        .adm-usage-redesign .ub-label{font-weight:400;color:#6b7688;}
        .ub-hero{display:grid;grid-template-columns:minmax(0,1.55fr) minmax(0,1fr);gap:12px;width:100%;}
        @media(max-width:900px){.ub-hero{grid-template-columns:1fr;}}
        .ub-hero-primary{
          border-radius:10px;
          background:linear-gradient(135deg,#0b1728 0%,#122238 60%,#1b2f47 100%);
          color:#FFFFFF;
          padding:18px 20px 16px;
          border:1px solid #0b1728;
          min-width:0;
          display:flex;
          flex-direction:column;
        }
        .ub-hero-primary--tiered{background:none;border-width:1px;border-style:solid;}
        .ub-hero-primary--loading .ub-hero-balance{animation:ubHeroPulse 1.15s ease-in-out infinite;}
        @keyframes ubHeroPulse{0%,100%{opacity:0.35}50%{opacity:1}}
        .ub-hero-badge{
          display:inline-flex;align-items:center;gap:6px;align-self:flex-start;
          font-size:11px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;
          padding:4px 10px;border-radius:999px;
          border:1px solid rgba(255,255,255,0.25);background:rgba(255,255,255,0.08);
        }
        .ub-hero-badge-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
        .ub-hero-balance{margin-top:10px;font-size:34px;font-weight:650;letter-spacing:-0.03em;line-height:1.1;color:#FFFFFF;}
        .ub-hero-sub{margin-top:2px;font-size:12px;font-weight:400;color:rgba(255,255,255,0.65);}
        .ub-hero-stats{
          margin-top:14px;padding-top:12px;
          border-top:1px solid rgba(255,255,255,0.16);
          display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;
        }
        @media(max-width:520px){.ub-hero-stats{grid-template-columns:1fr;}}
        .ub-hero-stat{min-width:0;}
        .ub-hero-stat + .ub-hero-stat{border-left:1px solid rgba(255,255,255,0.16);padding-left:10px;}
        @media(max-width:520px){.ub-hero-stat + .ub-hero-stat{border-left:none;padding-left:0;}}
        .ub-hero-stat-label{font-size:10.5px;font-weight:400;color:rgba(255,255,255,0.6);text-transform:uppercase;letter-spacing:0.05em;}
        .ub-hero-stat-value{margin-top:4px;font-size:16px;font-weight:650;color:#FFFFFF;line-height:1.2;overflow-wrap:anywhere;}
        .ub-hero-side{display:grid;grid-template-columns:1fr;gap:12px;min-width:0;align-content:start;}
        .ub-side-card{padding:14px 16px;min-width:0;}
        .ub-side-title{font-size:12px;font-weight:700;color:#0F172A;letter-spacing:-0.01em;}
        .ub-side-sub{margin-top:3px;font-size:11.5px;font-weight:400;color:#6b7688;line-height:1.5;}
        .ub-threshold-row{display:flex;align-items:center;gap:8px;padding:7px 0;border-bottom:1px solid #F1F5F9;font-size:12px;}
        .ub-threshold-row:last-child{border-bottom:none;}
        .ub-threshold-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
        .ub-threshold-val{font-weight:650;color:#0F172A;}
        .ub-threshold-state{margin-left:auto;font-size:11px;font-weight:700;flex-shrink:0;}
        .ub-mini-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:10px;}
        .ub-mini-icon{
          width:30px;height:30px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;
        }
        .ub-mini-value{margin-top:8px;font-size:20px;font-weight:650;color:#0F172A;letter-spacing:-0.02em;line-height:1.1;}
        .ub-mini-label{margin-top:3px;font-size:11px;font-weight:400;color:#6b7688;}
        .ub-peak-chart{
          display:flex;align-items:stretch;gap:4px;
          height:96px;width:100%;
          border-bottom:2px solid #e3e8ef;
          padding-bottom:0;
        }
        .ub-peak-bar-wrap{flex:1 1 0;min-width:0;display:flex;align-items:flex-end;height:100%;}
        .ub-peak-bar{width:100%;min-height:3px;border-radius:3px 3px 0 0;background:#cbd5e1;}
        .ub-peak-bar--peak{background:#0f9d8f;}
        .ub-peak-axis{display:flex;justify-content:space-between;margin-top:4px;font-size:10.5px;color:#6b7688;font-weight:400;}
        .ub-note{
          margin-top:10px;padding:8px 12px;border-radius:8px;font-size:12px;line-height:1.5;
          color:#0F172A;background:rgba(15,157,143,0.08);border:1px solid rgba(15,157,143,0.28);
        }
        .ub-note--amber{background:rgba(201,138,44,0.08);border-color:rgba(201,138,44,0.32);}
        .ub-legend{display:flex;align-items:center;gap:16px;flex-wrap:wrap;margin-top:2px;font-size:12px;font-weight:400;color:#6b7688;}
        .ub-legend-item{display:inline-flex;align-items:center;gap:6px;}
        .ub-legend-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0;}
        .ub-event-row{display:flex;align-items:flex-start;gap:10px;padding:9px 0;border-bottom:1px solid #F1F5F9;}
        .ub-event-row:last-child{border-bottom:none;}
        .ub-event-main{flex:1 1 auto;min-width:0;}
        .ub-event-top{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
        .ub-event-amount{font-size:13px;font-weight:650;color:#0F172A;}
        .ub-event-delta{margin-top:3px;font-size:11.5px;font-weight:400;color:#6b7688;}
        .ub-event-time{flex-shrink:0;margin-left:8px;font-size:11px;font-weight:400;color:#6b7688;text-align:right;white-space:nowrap;padding-top:2px;}
        /* ---- Panel-wide compact status chips (2-4 short values, equal width) ---- */
        .ub-chip-row{display:grid;gap:10px;width:100%;}
        .ub-chip-row--2{grid-template-columns:repeat(2,minmax(0,1fr));}
        .ub-chip-row--3{grid-template-columns:repeat(3,minmax(0,1fr));}
        .ub-chip-row--4{grid-template-columns:repeat(4,minmax(0,1fr));}
        @media(max-width:640px){.ub-chip-row--3,.ub-chip-row--4{grid-template-columns:repeat(2,minmax(0,1fr));}}
        @media(max-width:420px){.ub-chip-row--2,.ub-chip-row--3,.ub-chip-row--4{grid-template-columns:1fr;}}
        .ub-status-chip{
          display:flex;align-items:center;gap:10px;min-width:0;
          padding:12px 14px;border-radius:10px;
          background:#FFFFFF;border:1px solid #e3e8ef;
          font:inherit;text-align:left;width:100%;cursor:pointer;
          transition:background .12s ease,border-color .12s ease,box-shadow .12s ease;
        }
        .ub-status-chip:hover{background:#f8fafc;border-color:#cbd5e1;}
        .ub-status-chip.is-active{
          border-color:#2563EB;
          box-shadow:0 0 0 1px rgba(37,99,235,0.25);
          background:rgba(37,99,235,0.06);
        }
        .ub-status-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0;}
        .ub-status-num{font-size:19px;font-weight:650;color:#0f1a2b;letter-spacing:-0.02em;line-height:1.1;}
        .ub-status-label{font-size:11px;font-weight:400;color:#6b7688;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ub-inline-chip{
          display:inline-flex;align-items:center;gap:7px;min-width:0;max-width:100%;
          padding:6px 12px;border-radius:999px;font-size:12px;font-weight:400;color:#0f1a2b;
          background:#FFFFFF;border:1px solid #e3e8ef;white-space:nowrap;overflow:hidden;
        }
        .ub-inline-chip strong{font-weight:650;}
        .ub-inline-chip .ub-status-dot{width:7px;height:7px;}
        .adm-usage-redesign.apage{max-width:min(1600px,100%);background:transparent;}
        .bot-admin-root .adm-usage-redesign .adm-stack{max-width:100%;gap:16px;}
        @media(max-width:1366px){
          .bot-admin-root .adm-usage-redesign .adm-stack{gap:14px;}
          .ub-section-card,.ub-trend-chart-card,.ub-billing-alert-card{padding:12px 14px!important;}
          .ub-kpi-card{padding:12px 14px;}
        }
        .ub-page-header{padding:10px 14px;border-radius:14px!important;background:#fff!important;border:1px solid #e3e8ef!important;box-shadow:0 2px 8px rgba(15,23,42,0.04)!important;}
        .ub-page-header-main{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:nowrap;}
        .ub-page-header-titles{min-width:0;flex:0 1 auto;}
        .ub-page-title-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;}
        .ub-page-title{font-size:17px;font-weight:750;letter-spacing:-0.02em;color:#0f172a;margin:0;line-height:1.2;}
        .ub-page-date-trigger{display:inline-flex;align-items:center;gap:6px;padding:5px 10px 5px 8px;border-radius:10px;border:1px solid #e2e8f0;background:#f8fafc;color:#334155;font-size:11.5px;font-weight:650;cursor:pointer;font-family:inherit;max-width:min(100%,320px);min-width:0;}
        .ub-page-date-trigger:hover{border-color:#cbd5e1;background:#fff;}
        .ub-page-date-trigger.is-open{border-color:#2563eb;background:rgba(37,99,235,0.06);color:#1d4ed8;}
        .ub-page-date-summary{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;color:#0f172a;}
        .ub-page-date-summary--open{color:#1d4ed8;font-weight:700;}
        .ub-page-subtitle{margin:2px 0 0;font-size:11px;color:#64748b;line-height:1.35;max-width:420px;}
        .ub-page-header-actions,.ub-page-header-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:nowrap;justify-content:flex-end;flex:0 0 auto;}
        .ub-page-header-toolbar{width:auto;max-width:none;}
        .ub-usage-date-range{display:inline-flex;align-items:center;gap:4px;padding:4px 8px;border-radius:10px;border:1px solid #e2e8f0;background:#f8fafc;max-width:100%;}
        .ub-usage-date-input{font-size:11px;font-weight:600;padding:3px 4px;border-radius:6px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;font-family:inherit;min-width:0;width:118px;max-width:118px;}
        .ub-usage-date-input:disabled{opacity:.55;cursor:not-allowed;}
        .ub-usage-date-sep{font-size:11px;color:#94a3b8;font-weight:700;}
        .ub-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
        .ub-date-range-label{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:min(420px,100%);}
        .ub-date-range-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border-radius:10px;border:1px solid #e2e8f0;background:#f8fafc;color:#0f172a;font-size:12px;font-weight:650;cursor:default;font-family:inherit;}
        .ub-date-caret{font-size:9px;color:#94a3b8;}
        .ub-period-chips{display:inline-flex;gap:4px;flex-wrap:wrap;}
        .ub-period-chip{padding:6px 12px;border-radius:9px;border:1px solid transparent;background:#f1f5f9;color:#64748b;font-size:11.5px;font-weight:700;cursor:pointer;font-family:inherit;transition:background .12s,border-color .12s,color .12s;}
        .ub-period-chip.is-active{border-color:#2563eb;background:rgba(37,99,235,0.12);color:#1d4ed8;box-shadow:inset 0 0 0 1px rgba(37,99,235,0.2);}
        .ub-period-chip:hover:not(.is-active){background:#f8fafc;}
        .ub-header-icon-btn{width:34px;height:34px;border-radius:10px;border:1px solid #e2e8f0;background:#fff;color:#334155;font-size:16px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-family:inherit;}
        .ub-header-spinner{width:14px;height:14px;border:2px solid #cbd5e1;border-top-color:#2563eb;border-radius:50%;animation:ubSpin .7s linear infinite;}
        @keyframes ubSpin{to{transform:rotate(360deg)}}
        .ub-live-pill{display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border-radius:999px;font-size:11px;font-weight:700;border:1px solid #e2e8f0;background:#fff;color:#64748b;}
        .ub-live-pill--ok{border-color:rgba(15,157,143,0.35);background:rgba(15,157,143,0.08);color:#047857;}
        .ub-live-dot{width:7px;height:7px;border-radius:50%;background:currentColor;}
        .ub-live-pill--ok .ub-live-dot{animation:pulseA 1.4s ease infinite;}
        .ub-header-text-btn{padding:7px 12px;border-radius:10px;border:1px solid #e2e8f0;background:#fff;color:#334155;font-size:11.5px;font-weight:700;cursor:pointer;font-family:inherit;}
        .ub-header-avatar{width:34px;height:34px;border-radius:50%;border:none;background:linear-gradient(145deg,#1F6B63,#0C8F55);color:#fff;font-size:11px;font-weight:800;cursor:pointer;font-family:inherit;}
        .ub-header-refresh-note{margin-top:10px;font-size:11.5px;color:#64748b;font-weight:600;}
        .ub-kpi-row{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;width:100%;}
        .ub-kpi-card{text-align:left;padding:12px 14px;border-radius:15px;border:1px solid #E6EBF2;background:#fff;box-shadow:0 1px 3px rgba(15,23,42,0.04);transition:border-color .14s,box-shadow .14s;min-width:0;font-family:inherit;width:100%;min-height:0;}
        button.ub-kpi-card,.ub-kpi-card--interactive{cursor:pointer;}
        .ub-kpi-card:hover{border-color:#d8dee8;box-shadow:0 2px 8px rgba(15,23,42,0.06);}
        .ub-kpi-card--mint{background:#F0FCF8;border-color:#D6F3EA;}
        .ub-kpi-card--blue{background:#F3F7FF;border-color:#DCE8FF;}
        .ub-kpi-card--purple{background:#F8F5FF;border-color:#EAE2FF;}
        .ub-kpi-card--orange{background:#FFF8F1;border-color:#FFEBDD;}
        .ub-kpi-card--rose{background:#FFF5F7;border-color:#FFE2E8;}
        .ub-kpi-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:6px;}
        .ub-kpi-history-btn{width:22px;height:22px;border-radius:6px;border:1px solid #e2e8f0;background:#fff;color:#64748b;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0;font-family:inherit;transition:border-color .12s,background .12s,color .12s;}
        .ub-kpi-history-btn:hover{border-color:#cbd5e1;background:#f8fafc;color:#0f172a;}
        .ub-kpi-card--mint .ub-kpi-history-btn:hover{border-color:rgba(15,174,155,0.35);color:#0d9488;}
        .ub-kpi-icon{width:34px;height:34px;border-radius:10px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
        .ub-kpi-title-row{display:flex;align-items:center;justify-content:space-between;gap:6px;min-width:0;}
        .ub-kpi-trend-badge{
          flex:0 1 auto;
          min-width:0;
          max-width:58%;
          font-size:9px;
          font-weight:700;
          color:#047857;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
          padding:2px 6px;
          border-radius:999px;
          background:rgba(4,120,87,0.1);
          line-height:1.3;
          cursor:help;
        }
        .ub-kpi-trend-badge.is-down{color:#b45309;background:rgba(180,83,9,0.12);}
        .ub-kpi-icon--mint{background:#DDF7EF;color:#0FAE9B;}
        .ub-kpi-icon--blue{background:#E3EDFF;color:#2F80ED;}
        .ub-kpi-icon--purple{background:#EEE7FF;color:#7C3AED;}
        .ub-kpi-icon--orange{background:#FFEBDD;color:#F97316;}
        .ub-kpi-icon--rose{background:#FFE5EA;color:#EF476F;}
        .ub-kpi-title{font-size:11.5px;font-weight:600;color:#64748B;line-height:1.3;min-width:0;flex:1 1 auto;}
        .ub-kpi-value{margin-top:4px;font-size:20px;font-weight:750;letter-spacing:-0.03em;color:#111827;line-height:1.1;}
        .ub-kpi-sub{margin-top:3px;font-size:10.5px;color:#94A3B8;line-height:1.3;}
        .ub-trend-layout{display:grid;grid-template-columns:1fr;gap:14px;width:100%;align-items:stretch;}
        .ub-billing-health-grid{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:14px;
          width:100%;
          align-items:stretch;
        }
        .ub-billing-health-grid > .adm-card{
          height:100%;
          min-height:0;
          display:flex;
          flex-direction:column;
        }
        .ub-trend-side-stack{display:flex;flex-direction:column;gap:12px;min-width:0;height:100%;}
        .ub-credit-balance-card,.ub-alert-thresholds-card{padding:14px 16px;border-radius:14px!important;}
        .ub-credit-balance-head{display:flex;align-items:center;justify-content:space-between;gap:8px;}
        .ub-credit-balance-card-title{font-size:13px;font-weight:800;color:#0f172a;}
        .ub-credit-healthy-pill{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:999px;border:1px solid;font-size:11px;font-weight:700;}
        .ub-credit-blurb{margin:8px 0 0;font-size:11.5px;color:#64748b;line-height:1.45;}
        .ub-credit-balance-hero{margin-top:12px;display:flex;flex-direction:column;gap:2px;}
        .ub-credit-balance-label{font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.04em;}
        .ub-credit-balance-amount{font-size:26px;font-weight:750;color:#0f172a;letter-spacing:-0.03em;}
        .ub-credit-progress-row{display:flex;align-items:center;gap:10px;margin-top:12px;}
        .ub-credit-progress-row .ub-credit-progress-track{flex:1;margin:0;}
        .ub-credit-progress-pct{font-size:12px;font-weight:700;color:#64748b;min-width:36px;text-align:right;}
        .ub-credit-history-link{margin-top:10px;}
        .ub-alert-thresholds-title{font-size:13px;font-weight:800;color:#0f172a;}
        .ub-alert-thresholds-card .ub-credit-threshold-list{margin-top:8px;}
        .ub-chart-legend-pills{display:flex;flex-wrap:wrap;gap:8px;margin:10px 0 4px;}
        .ub-legend-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:999px;font-size:11.5px;font-weight:600;border:1px solid #e2e8f0;background:#fff;color:#334155;}
        .ub-legend-pill--meetings{border-color:rgba(37,99,235,0.25);background:rgba(37,99,235,0.06);}
        .ub-legend-pill--cost{border-color:rgba(15,157,143,0.28);background:rgba(15,157,143,0.06);}
        .ub-kpi-spark-wrap{margin-top:auto;padding-top:10px;height:38px;}
        .ub-spark{display:block;width:100%;height:100%;}
        .ub-spark--empty{background:linear-gradient(90deg,#f1f5f9 0%,#f8fafc 50%,#f1f5f9 100%);border-radius:6px;}
        .ub-kpi-card{display:flex;flex-direction:column;min-height:0;}
        .ub-period-chip{padding:5px 10px;font-size:11px;}
        .ub-bottom-tables-grid{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:14px;
          width:100%;
          align-items:stretch;
        }
        .ub-bottom-table-card{display:flex;flex-direction:column;height:100%;min-height:0;}
        .ub-bottom-table-body{display:flex;flex-direction:column;flex:1 1 auto;min-height:0;}
        .ub-bottom-tables-grid .ub-usage-table-scroll{flex:1 1 auto;min-height:280px;max-height:360px;}
        .ub-table-card-footer{flex-shrink:0;margin-top:auto;border-top:1px solid #e2e8f0;min-height:40px;box-sizing:border-box;}
        .ub-usage-table-scroll .MuiTableContainer-root{overflow-x:auto!important;-webkit-overflow-scrolling:touch;}
        .ub-monthly-legend-compact{font-size:10.5px!important;gap:6px 12px!important;}
        .ub-monthly-legend-compact .ub-legend-long{display:none;}
        @media(min-width:900px){.ub-monthly-legend-compact .ub-legend-long{display:inline;}}
        .ub-status-ring-layout{display:flex;align-items:center;gap:16px;flex-wrap:wrap;}
        .ub-status-ring{width:108px;height:108px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .ub-status-ring-hole{width:78px;height:78px;border-radius:50%;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;box-shadow:inset 0 0 0 1px #e2e8f0;}
        .ub-status-ring-num{font-size:18px;font-weight:800;color:#0f172a;line-height:1.1;}
        .ub-status-ring-label{font-size:10px;font-weight:600;color:#64748b;margin-top:2px;}
        .ub-status-ring-side{display:flex;flex-direction:column;gap:8px;flex:1;min-width:140px;}
        .ub-status-pill{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:10px;border:1px solid #e2e8f0;background:#fff;font-size:12px;color:#334155;cursor:pointer;font-family:inherit;text-align:left;width:100%;}
        .ub-status-pill.is-active{border-color:#2563eb;background:rgba(37,99,235,0.06);}
        .ub-status-pill strong{font-weight:800;color:#0f172a;}
        .ub-platform-name-wrap{display:inline-flex;align-items:center;gap:8px;min-width:0;}
        .ub-platform-icon{width:28px;height:28px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
        .ub-platform-icon .ub-platform-brand-icon svg{display:block;max-width:100%;max-height:100%;}
        .adm-usage-redesign .ub-platform-icon{background:#fff!important;border:1px solid rgba(15,23,42,0.06);}
        .ub-platform-letter{font-size:12px;font-weight:800;color:#334155;}
        .ub-top-hosts-card .adm-card-header{font-size:13px;font-weight:800;}
        .ub-top-hosts-card tbody tr:nth-child(3) td{background:rgba(37,99,235,0.06)!important;}
        .ub-top-hosts-card tbody tr:hover td{background:rgba(248,250,252,0.95);}
        .ub-trend-chart-card{padding:14px 16px;min-width:0;}
        .ub-trend-subtitle{margin-top:2px;font-size:12px;color:#64748b;}
        .ub-credit-panel{padding:14px 16px;display:flex;flex-direction:column;gap:10px;height:100%;}
        .ub-credit-panel-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;}
        .ub-credit-panel-title{font-size:12px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em;}
        .ub-credit-balance{font-size:28px;font-weight:700;color:#0f172a;letter-spacing:-0.03em;margin-top:4px;}
        .ub-credit-status{display:inline-flex;align-items:center;gap:6px;margin-top:6px;font-size:12px;font-weight:700;}
        .ub-credit-status-dot{width:8px;height:8px;border-radius:50%;}
        .ub-credit-history-btn{font-size:11px;font-weight:700;padding:5px 10px;border-radius:8px;border:1px solid #e2e8f0;background:#f8fafc;color:#334155;cursor:pointer;font-family:inherit;}
        .ub-credit-progress-track{height:8px;border-radius:999px;background:#e2e8f0;overflow:hidden;}
        .ub-credit-progress-fill{height:100%;border-radius:999px;transition:width .25s ease;}
        .ub-credit-meta{display:flex;flex-direction:column;gap:4px;font-size:11px;color:#64748b;}
        .ub-credit-thresholds{border-top:1px solid #f1f5f9;padding-top:10px;margin-top:4px;}
        .ub-credit-thresholds-head{display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:12px;font-weight:700;color:#0f172a;}
        .ub-credit-thresholds-note{margin:4px 0 6px;font-size:11px;color:#94a3b8;line-height:1.4;}
        .ub-link-btn{border:none;background:transparent;padding:0;font-size:11px;font-weight:700;color:#2563eb;cursor:pointer;font-family:inherit;}
        .ub-breakdown-grid{display:grid;grid-template-columns:minmax(0,min(400px,36%)) minmax(0,1fr);gap:12px;width:100%;align-items:stretch;}
        .ub-breakdown-grid > .ub-platform-status-card,.ub-breakdown-grid > .ub-top-hosts-card{display:flex;flex-direction:column;min-height:260px;max-height:none;}
        .ub-breakdown-grid .ub-top-hosts-card{max-height:360px;}
        .ub-breakdown-grid .ub-top-hosts-card .adm-scroll-panel{flex:1;min-height:0;}
        .ub-platform-status-card{padding:12px 14px;display:flex;flex-direction:column;gap:0;max-width:400px;width:100%;}
        .ub-platform-status-pane{min-width:0;}
        .ub-platform-status-pane--status{
          margin-top:12px;padding-top:12px;border-top:1px solid #f1f5f9;
          display:flex;flex-direction:column;flex:1;min-height:0;
        }
        .ub-platform-status-pane-head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:4px;}
        .ub-platform-status-pane-head .ub-platform-status-pane-title{margin-bottom:0;}
        .ub-platform-status-donut-foot{
          margin-top:8px;display:flex;align-items:center;justify-content:flex-start;gap:12px;flex-shrink:0;flex-wrap:wrap;
        }
        .ub-platform-status-donut-foot .ub-status-stats-col{flex:1;min-width:100px;gap:4px;}
        .ub-platform-status-details-btn{padding:4px 10px!important;font-size:10.5px!important;flex-shrink:0;}
        .ub-status-ring--md{width:80px;height:80px;flex-shrink:0;}
        .ub-status-ring-hole--md{width:58px;height:58px;}
        .ub-status-ring--md .ub-status-ring-center-num{font-size:14px;}
        .ub-status-ring--md .ub-status-ring-center-label{font-size:8.5px;}
        .ub-status-ring-btn{border:none;background:transparent;padding:0;cursor:pointer;font-family:inherit;border-radius:50%;}
        .ub-status-ring-btn.is-active .ub-status-ring{box-shadow:0 0 0 2px rgba(37,99,235,0.35);}
        .ub-platform-status-pane-title{margin:0 0 8px;font-size:12px;font-weight:800;color:#111827;}
        .ub-platform-status-pane--status .ub-platform-status-pane-title{margin-bottom:0;}
        .ub-platform-status-card .ub-platform-row--compact .ub-platform-row-top{font-size:11px;}
        .ub-platform-status-card .ub-platform-bar-track--compact{height:5px;}
        .ub-platform-status-filter-hint{margin:0 0 6px!important;font-size:11px!important;}
        .ub-top-hosts-card .adm-card-header{font-size:12px;line-height:1.35;}
        .ub-top-hosts-toolbar{display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:0 14px 10px;width:100%;box-sizing:border-box;}
        .ub-top-hosts-chips{display:flex;gap:4px;flex-wrap:wrap;align-items:center;flex-shrink:0;}
        .ub-top-hosts-chip{
          padding:4px 10px;border-radius:999px;border:1px solid #e2e8f0;background:#f8fafc;
          color:#64748b;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;
        }
        .ub-top-hosts-chip.is-active{border-color:#2563eb;background:rgba(37,99,235,0.12);color:#1d4ed8;}
        .ub-top-hosts-toolbar .adm-search-inline{flex:1;min-width:min(100%,160px);max-width:280px;margin-left:auto;}
        .ub-top-hosts-toolbar .adm-search-inline-field{min-width:0;width:100%;}
        @media(max-width:520px){
          .ub-top-hosts-toolbar{flex-direction:column;align-items:stretch;}
          .ub-top-hosts-toolbar .adm-search-inline{max-width:none;margin-left:0;}
        }
        .ub-section-card{padding:14px 16px;}
        .ub-section-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:12px;}
        .ub-section-title{font-size:14px;font-weight:800;color:#0f172a;}
        .ub-section-sub{font-size:12px;color:#64748b;margin-top:3px;line-height:1.4;}
        .ub-section-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
        .ub-platform-bars{display:flex;flex-direction:column;gap:12px;}
        .ub-platform-row-head{display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:12px;margin-bottom:5px;}
        .ub-platform-name{font-weight:650;color:#0f172a;text-transform:capitalize;}
        .ub-platform-stats{color:#64748b;font-variant-numeric:tabular-nums;font-size:11.5px;}
        .ub-platform-bar-track{height:8px;border-radius:999px;background:#eef2f7;overflow:hidden;}
        .ub-platform-bar-fill{height:100%;border-radius:999px;background:linear-gradient(90deg,#2563eb,#60a5fa);}
        .ub-empty{padding:16px 4px;font-size:12.5px;color:#94a3b8;}
        .ub-system-health{padding:14px 16px;}
        .ub-system-health-title{font-size:14px;font-weight:800;color:#0f172a;margin-bottom:10px;}
        .ub-system-health-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 16px;}
        @media(max-width:640px){.ub-system-health-grid{grid-template-columns:1fr;}}
        .ub-health-row{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;font-size:12px;padding:6px 0;border-bottom:1px solid #f1f5f9;}
        .ub-health-label{color:#64748b;font-weight:600;flex-shrink:0;}
        .ub-health-value{color:#0f172a;font-weight:650;text-align:right;}
        .ub-health-value--wrap{max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .ub-status-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 8px;border-radius:999px;font-size:11px;font-weight:700;border:1px solid transparent;}
        .ub-status-badge-dot{width:6px;height:6px;border-radius:50%;}
        .ub-metrics-more{margin-top:0;}
        .ub-metrics-more summary{
          cursor:pointer;
          font-size:12px;
          font-weight:700;
          color:#475569;
          padding:10px 16px;
          list-style:none;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          user-select:none;
        }
        .ub-metrics-more summary::-webkit-details-marker{display:none;}
        .ub-metrics-more-chevron{
          font-size:11px;
          font-weight:800;
          color:#94a3b8;
          line-height:1;
          transition:transform .15s ease;
          transform:rotate(0deg);
          flex-shrink:0;
        }
        .ub-metrics-more[open] .ub-metrics-more-chevron{transform:rotate(180deg);}
        .ub-system-health-wrap{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          grid-auto-rows:1fr;
          gap:6px;
          flex:1 1 auto;
          min-height:0;
          align-content:stretch;
          overflow:hidden;
        }
        .ub-system-health-chip{
          min-width:0;
          min-height:0;
          height:100%;
          padding:5px 7px;
          border-radius:7px;
          border:1px solid #eef2f7;
          background:#f8fafc;
          display:flex;
          flex-direction:column;
          justify-content:center;
          gap:2px;
          overflow:hidden;
        }
        .ub-system-health-chip-label{
          font-size:8.5px;
          font-weight:700;
          color:#94a3b8;
          text-transform:uppercase;
          letter-spacing:.04em;
          line-height:1.15;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .ub-system-health-chip-value{
          font-size:10px;
          font-weight:650;
          color:#0f172a;
          line-height:1.25;
          min-width:0;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .ub-system-health-chip-value .ub-status-badge{
          font-size:9px;
          padding:1px 5px;
          white-space:nowrap;
          max-width:100%;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .ub-system-health--horizontal{padding:14px 16px!important;overflow:hidden;flex:1 1 auto;}
        .ub-system-health--horizontal .ub-system-health-title{font-size:13px;font-weight:800;margin-bottom:10px;flex-shrink:0;}
        .ub-billing-health-grid .ub-billing-alert-card .ub-billing-alert-thresholds{display:flex;flex-direction:column;flex:1 1 auto;min-height:0;}
        .ub-billing-health-grid .ub-billing-alert-card .ub-credit-threshold-list--compact{flex:1 1 auto;}
        .adm-usage-fullwidth{grid-column:1/-1;width:100%;}
        .adm-page-scroll--usage{background:#F7F9FC!important;}
        .ub-kpi-skeleton{pointer-events:none;}
        .ub-kpi-skeleton-icon{width:32px;height:32px;border-radius:9px;background:linear-gradient(90deg,#eef2f7 25%,#f8fafc 50%,#eef2f7 75%);background-size:200% 100%;animation:ubShimmer 1.2s ease infinite;}
        .ub-kpi-skeleton-line{height:10px;border-radius:6px;margin-top:10px;background:linear-gradient(90deg,#eef2f7 25%,#f8fafc 50%,#eef2f7 75%);background-size:200% 100%;animation:ubShimmer 1.2s ease infinite;}
        .ub-kpi-skeleton-line--lg{height:22px;width:72%;margin-top:14px;}
        .ub-kpi-skeleton-line--sm{width:55%;margin-top:6px;height:8px;}
        @keyframes ubShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        .ub-sessions-toolbar{display:flex;align-items:center;justify-content:flex-end;gap:8px;flex-wrap:wrap;padding:0 14px 10px;}
        .adm-usage-redesign .ub-sessions-pager{
          display:grid;
          grid-template-columns:auto minmax(0,1fr) auto auto;
          align-items:center;
          gap:6px;
          padding:6px 10px;
          overflow:hidden;
        }
        .adm-usage-redesign .ub-table-card-footer .ub-sessions-pager{border-top:none;padding:6px 10px;}
        .adm-usage-redesign .ub-sessions-pager-meta{
          min-width:0;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
          font-size:10.5px;
          font-weight:600;
          color:#64748b;
        }
        .adm-usage-redesign .ub-sessions-pager-btn{
          font-size:10.5px;
          font-weight:700;
          padding:4px 7px;
          border-radius:7px;
          border:1px solid #e2e8f0;
          background:#f8fafc;
          color:#64748b;
          cursor:pointer;
          font-family:inherit;
          white-space:nowrap;
        }
        .adm-usage-redesign .ub-sessions-pager-btn:disabled{cursor:not-allowed;opacity:.5;}
        .adm-usage-redesign .ub-sessions-pager-size{
          font-size:10.5px;
          font-weight:600;
          padding:3px 5px;
          border-radius:7px;
          border:1px solid #e2e8f0;
          background:#f8fafc;
          color:#64748b;
          font-family:inherit;
          max-width:72px;
        }
        .ub-meeting-cell{display:flex;align-items:flex-start;gap:10px;min-width:0;}
        .ub-meeting-icon{width:32px;height:32px;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
        .adm-usage-redesign .adm-stack{gap:12px;}
        .ub-inline-refresh-note{font-size:11.5px;color:#64748b;font-weight:600;padding:0 2px;margin:-4px 0 0;}
        .ub-chart-series-toggles{display:inline-flex;flex-wrap:wrap;gap:6px;align-items:center;}
        .ub-chart-series-toggle{display:inline-flex;align-items:center;gap:6px;padding:5px 11px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#64748b;font-size:11px;font-weight:650;cursor:pointer;font-family:inherit;transition:opacity .12s,border-color .12s,background .12s;}
        .ub-chart-series-toggle.is-on{border-color:#cbd5e1;color:#0f172a;background:#f8fafc;}
        .ub-chart-series-toggle .ub-legend-dot{width:8px;height:8px;border-radius:50%;display:inline-block;}
        .ub-billing-alert-card{padding:14px 16px;border-radius:14px!important;height:100%;display:flex;flex-direction:column;}
        .ub-billing-alert-head{display:flex;align-items:center;justify-content:space-between;gap:8px;}
        .ub-billing-alert-title{font-size:13px;font-weight:800;color:#0f172a;}
        .ub-billing-alert-rows{display:flex;flex-direction:column;gap:8px;margin-top:12px;padding-bottom:10px;border-bottom:1px solid #f1f5f9;}
        .ub-billing-alert-row{display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:12px;}
        .ub-billing-alert-label{color:#64748b;font-weight:600;}
        .ub-billing-alert-thresholds{margin-top:10px;flex:1;}
        .ub-credit-threshold-list--compact .ub-threshold-row{padding:3px 0;}
        .ub-kpi-card--mint .ub-kpi-value{font-size:26px;}
        .ub-kpi-card--rose .ub-kpi-value{font-size:25px;}
        .ub-platform-bars.ub-platform-bars--compact,.ub-platform-bars{gap:8px;}
        .ub-platform-row--compact .ub-platform-row-top{display:flex;align-items:center;gap:8px;font-size:12px;}
        .ub-platform-row--compact .ub-platform-name{flex:1;min-width:0;}
        .ub-platform-row--compact .ub-platform-count{font-weight:700;color:#0f172a;font-variant-numeric:tabular-nums;}
        .ub-platform-row--compact .ub-platform-pct{color:#64748b;font-size:11px;min-width:38px;text-align:right;}
        .ub-platform-bar-track--compact{height:6px;margin-top:4px;}
        .ub-section-card .ub-platform-bars{padding:2px 0;}
        .ub-status-ring-layout--compact{align-items:center;gap:12px;padding:4px 0;}
        .ub-status-ring--sm{width:72px;height:72px;}
        .ub-status-ring-hole--sm{width:52px;height:52px;}
        .ub-status-stats-col{display:flex;flex-direction:column;gap:6px;flex:1;min-width:0;}
        .ub-status-stat-line{display:flex;flex-direction:column;align-items:flex-start;border:none;background:transparent;padding:4px 0;cursor:pointer;font-family:inherit;text-align:left;}
        .ub-status-stat-line.is-active .ub-status-stat-num{color:#2563eb;}
        .ub-status-stat-num{font-size:18px;font-weight:800;color:#0f172a;line-height:1.1;}
        .ub-status-stat-label{font-size:11px;font-weight:600;color:#64748b;}
        .ub-breakdown-grid .ub-section-card,.ub-breakdown-grid .ub-top-hosts-card{align-self:stretch;}
        .ub-breakdown-grid .adm-card{min-height:0;}
        .ub-system-health--compact{padding:12px 14px;}
        .ub-system-health-grid--2col{gap:6px 14px;}
        .ub-health-cell{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;font-size:12px;padding:5px 0;border-bottom:1px solid #f1f5f9;}
        .ub-health-cell--span2{grid-column:1/-1;}
        .ub-health-value--stack{display:flex;flex-direction:column;align-items:flex-end;gap:2px;font-size:11.5px;}
        .ub-recall-mcp-details .ub-health-row{border-bottom:1px solid #f1f5f9;}
        .ub-table-fit .adm-mui-table,.ub-table-fit .MuiTableContainer-root{max-width:100%;}
        .ub-table-fit--hosts .MuiTableContainer-root{overflow-x:hidden!important;}
        .ub-table-fit table{width:100%!important;min-width:0!important;table-layout:fixed;}
        .ub-table-fit .MuiTableHead-root .MuiTableCell-root{font-size:10.5px!important;line-height:1.25!important;}
        .ub-cost-est{text-align:right;line-height:1.2;}
        .ub-chart-axis-hint{text-align:left!important;}
        /* Usage dashboard: lock PC layout (1025px+); responsive only on tablet/phone (≤1024px). */
        @media (min-width: 1025px) {
          .adm-usage-redesign .ub-kpi-row{grid-template-columns:repeat(5,minmax(0,1fr))!important;}
          .adm-usage-redesign .ub-bottom-tables-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
          .adm-usage-redesign .ub-billing-health-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
          .adm-usage-redesign .ub-breakdown-grid{
            grid-template-columns:minmax(0,min(400px,36%)) minmax(0,1fr)!important;
          }
          .adm-usage-redesign .ub-page-header-main{flex-wrap:nowrap!important;align-items:center!important;}
          .adm-usage-redesign .ub-page-header-toolbar{flex-wrap:nowrap!important;width:auto!important;max-width:none!important;}
          .adm-usage-redesign .ub-table-fit--hosts .MuiTableContainer-root{overflow-x:hidden!important;}
        }
        @media (max-width: 1024px) {
          .adm-page-scroll--usage{padding:12px 14px!important;}
          .adm-usage-redesign .ub-kpi-row{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
          .adm-usage-redesign .ub-billing-health-grid,
          .adm-usage-redesign .ub-breakdown-grid,
          .adm-usage-redesign .ub-bottom-tables-grid{grid-template-columns:1fr!important;}
          .adm-usage-redesign .ub-platform-status-card{max-width:none!important;}
          .adm-usage-redesign .ub-breakdown-grid > .ub-top-hosts-card{max-height:none;}
          .adm-usage-redesign .ub-page-header-main{flex-wrap:wrap!important;align-items:flex-start!important;}
          .adm-usage-redesign .ub-page-header-toolbar{
            flex-wrap:wrap!important;justify-content:flex-start!important;width:100%!important;max-width:100%!important;
          }
          .adm-usage-redesign .ub-usage-date-range{width:100%;}
          .adm-usage-redesign .ub-usage-date-input{flex:1;max-width:none;width:auto;min-width:0;}
          .adm-usage-redesign .ub-page-header-periods{width:100%;justify-content:flex-start;}
          .adm-usage-redesign .ub-period-chips{flex-wrap:wrap;width:100%;}
          .adm-usage-redesign .ub-period-chip{flex:1;text-align:center;min-width:0;padding:8px 6px;}
          .adm-usage-redesign .ub-trend-chart-card .ub-chart-series-toggles{width:100%;}
          .adm-usage-redesign .ub-chart-series-toggle{flex:1;justify-content:center;min-width:0;font-size:10px;padding:5px 6px;}
          .adm-usage-redesign .ub-trend-layout{gap:12px;}
          .adm-usage-redesign .ub-chart-panel{
            overflow-x:auto!important;-webkit-overflow-scrolling:touch;
          }
          .adm-usage-redesign .ResponsiveContainer.ub-chart-responsive,
          .adm-usage-redesign .ub-chart-panel .recharts-responsive-container{
            min-height:240px!important;height:240px!important;min-width:min(100%,520px);
          }
          .adm-usage-redesign .ub-usage-table-scroll,
          .adm-usage-redesign .ub-usage-table-scroll .MuiTableContainer-root,
          .adm-usage-redesign .ub-table-fit--monthly .MuiTableContainer-root,
          .adm-usage-redesign .ub-table-fit--sessions .MuiTableContainer-root,
          .adm-usage-redesign .ub-table-fit--hosts .MuiTableContainer-root{
            overflow-x:auto!important;-webkit-overflow-scrolling:touch;
          }
          .adm-usage-redesign .ub-bottom-tables-grid .adm-card-header{
            flex-direction:column;align-items:flex-start!important;
          }
          .adm-usage-redesign .ub-top-hosts-toolbar{flex-wrap:wrap;}
          .adm-usage-redesign .ub-sessions-pager{
            overflow-x:auto!important;-webkit-overflow-scrolling:touch;
          }
        }
        @media (max-width: 768px) {
          .adm-usage-redesign .ub-kpi-row{grid-template-columns:1fr!important;}
        }
        @media (max-width: 480px) {
          .adm-usage-redesign .ub-trend-layout{gap:10px;}
          .adm-usage-redesign .ResponsiveContainer.ub-chart-responsive,
          .adm-usage-redesign .ub-chart-panel .recharts-responsive-container{
            min-height:200px!important;height:200px!important;min-width:440px;
          }
        }
        .ub-meeting-title-truncate{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;}
        .ub-platform-icon-only{width:28px;height:28px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;margin:0 auto;}
        .ub-session-status-badge{display:inline-flex;align-items:center;padding:2px 7px;border-radius:999px;font-size:10px;font-weight:700;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .ub-btn-secondary{display:inline-flex;align-items:center;justify-content:center;padding:6px 12px;border-radius:9px;border:1px solid #E6EBF2;background:#fff;color:#334155;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;transition:background .12s,border-color .12s;}
        .ub-btn-secondary:hover{background:#f8fafc;border-color:#cbd5e1;}
        .ub-btn-secondary--sm{padding:4px 10px;font-size:10.5px;border-radius:8px;}
        .ub-status-ring-center-num{font-size:13px;font-weight:800;color:#111827;line-height:1.1;}
        .ub-status-ring-center-label{font-size:9px;font-weight:600;color:#64748B;margin-top:2px;}
        .ub-status-ring-hole--sm{flex-direction:column;align-items:center;justify-content:center;}
        .ub-status-stat-line--row{flex-direction:row;align-items:center;gap:8px;width:100%;padding:5px 0;}
        .ub-status-stat-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
        .ub-status-stat-dot--completed{background:#0FAE9B;}
        .ub-status-stat-dot--live{background:#2563EB;}
        .ub-status-stat-line--row .ub-status-stat-label{flex:1;font-size:12px;color:#64748B;font-weight:600;}
        .ub-status-stat-num--inline{font-size:13px;font-weight:750;color:#111827;}
        .ub-meeting-cell--compact{align-items:center;gap:8px;}
        .ub-meeting-icon--sm{width:28px;height:28px;border-radius:9px;flex-shrink:0;}
        .ub-platform-col-label{font-size:11px;color:#64748B;text-transform:capitalize;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block;}
        .ub-empty-state{padding:28px 20px;text-align:center;}
        .ub-empty-state-title{font-size:14px;font-weight:700;color:#111827;margin:0;}
        .ub-empty-state-sub{margin:8px 0 0;font-size:12.5px;color:#64748B;line-height:1.5;}
        .ub-empty-state-actions{margin-top:14px;display:flex;justify-content:center;gap:8px;}
        .ub-change-badge{display:inline-block;padding:2px 7px;border-radius:6px;font-size:10.5px;font-weight:700;}
        .ub-date-cal-icon{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
        .ub-top-hosts-card .adm-scroll-panel{overflow-x:hidden;}
        .adm-cal-shell-premium{border-radius:18px!important;box-shadow:0 4px 24px rgba(15,23,42,0.08)!important;}
        .bot-admin-root.admin-theme-light .adm-stat-card.adm-kpi-style::before{content:"";position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,${PALETTE.navy},${PALETTE.cold});}
        .bot-admin-root.admin-theme-light .adm-table-row:hover{background:rgba(31,107,99,0.05);}
        .bot-admin-root.admin-theme-light .adm-nav{color:#aab6c8;border-radius:10px;font-weight:500;}
        .bot-admin-root.admin-theme-light .adm-nav:hover{
          background:rgba(23,120,212,0.18);
          color:#E8F0F7;
        }
        .bot-admin-root.admin-theme-light .adm-nav.active{
          background:rgba(37,99,235,0.2);
          color:#FFFFFF;font-weight:650;
          box-shadow:inset 3px 0 0 #2563EB;
        }
        .bot-admin-root.admin-theme-light .adm-nav-icon{background:rgba(255,255,255,0.06);color:rgba(232,240,247,0.7);}
        .bot-admin-root.admin-theme-light .adm-nav:hover .adm-nav-icon{
          background:rgba(23,120,212,0.28);color:#BFDBFE;
        }
        .bot-admin-root.admin-theme-light .adm-nav.active .adm-nav-icon{
          background:rgba(23,120,212,0.4);
          color:#FFFFFF;box-shadow:none;
        }
        .adm-sidebar-brand-block{padding:18px 16px 14px;border-bottom:1px solid rgba(255,255,255,0.1);}
        .bot-admin-root.admin-theme-light .adm-sidebar-brand-block{border-bottom-color:rgba(255,255,255,0.1);}
        .adm-brand-card{
          padding:0;border-radius:0;background:transparent;border:none;box-shadow:none;
        }
        .bot-admin-root.admin-theme-light .adm-brand-card{
          background:transparent;border:none;box-shadow:none;
        }
        .adm-brand-row{display:flex;align-items:center;gap:12px;margin-bottom:0;}
        .adm-brand-logo-wrap{
          height:40px;width:auto;min-width:40px;padding:6px 8px;border-radius:10px;
          background:#FFFFFF;border:1px solid rgba(255,255,255,0.2);
          display:flex;align-items:center;justify-content:center;flex-shrink:0;
          box-sizing:border-box;
        }
        .bot-admin-root.admin-theme-light .adm-brand-logo-wrap{
          background:#FFFFFF;border:1px solid rgba(255,255,255,0.2);box-shadow:none;
        }
        .adm-brand-logo-wrap img{height:26px!important;width:auto!important;max-width:118px;object-fit:contain;display:block;}
        .adm-brand-title{font-size:15px;font-weight:700;letter-spacing:-0.02em;line-height:1.2;color:#E8F0F7;}
        .bot-admin-root.admin-theme-light .adm-brand-title{color:#E8F0F7;}
        .adm-brand-sub{display:block;font-size:10px;font-weight:700;letter-spacing:.12em;color:rgba(232,240,247,0.55);margin-top:2px;}
        .adm-online-pill{display:none;}
        .adm-online-dot{display:none;}
        .adm-topbar-user{
          position:relative;
          display:inline-flex;align-items:center;flex-shrink:0;
        }
        .adm-topbar-user-btn{
          width:34px;height:34px;border-radius:50%;padding:0;border:none;cursor:pointer;
          display:inline-flex;align-items:center;justify-content:center;
          background:linear-gradient(145deg,#1F6B63 0%,#1D546C 55%,#0C8F55 100%);
          color:#fff;font-size:11px;font-weight:800;letter-spacing:.02em;
          box-shadow:0 2px 8px rgba(31,107,99,0.22);
          transition:transform .12s ease,box-shadow .12s ease;
        }
        .adm-topbar-user-btn:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(31,107,99,0.28);}
        .adm-topbar-user-btn[aria-expanded="true"]{
          box-shadow:0 0 0 3px rgba(31,107,99,0.22),0 4px 12px rgba(31,107,99,0.28);
        }
        .adm-topbar-user-menu{
          position:fixed;z-index:5000;
          min-width:260px;max-width:min(300px,82vw);
          padding:0;border-radius:14px;overflow:hidden;
          background:#fff;border:1px solid rgba(12,43,78,0.10);
          box-shadow:0 4px 8px rgba(12,43,78,0.05),0 22px 52px rgba(12,43,78,0.14);
          animation:admUserMenuIn .2s cubic-bezier(.22,1,.36,1);
        }
        @keyframes admUserMenuIn{
          from{opacity:0;transform:translateY(-8px) scale(0.97);}
          to{opacity:1;transform:translateY(0) scale(1);}
        }
        .adm-topbar-user-menu--dark{
          background:#132337;border-color:#243447;
          box-shadow:0 12px 28px rgba(0,0,0,0.45);
        }
        .adm-topbar-user-menu-head{
          display:flex;align-items:center;gap:12px;
          padding:14px 16px 13px;
          background:linear-gradient(135deg,#F8FBFF 0%,#EFF6FF 55%,#F0F9FF 100%);
          border-bottom:1px solid rgba(12,43,78,0.08);
          margin-bottom:0;
        }
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-head{
          background:linear-gradient(135deg,rgba(255,255,255,0.06) 0%,rgba(255,255,255,0.03) 100%);
          border-bottom-color:rgba(255,255,255,0.1);
        }
        .adm-topbar-user-menu-head-avatar{
          width:40px;height:40px;border-radius:50%;flex-shrink:0;
          background:linear-gradient(145deg,#1F6B63 0%,#1D546C 55%,#0C8F55 100%);
          color:#fff;display:flex;align-items:center;justify-content:center;
          font-size:13px;font-weight:700;overflow:hidden;
          box-shadow:0 2px 8px rgba(12,43,78,0.22),inset 0 1px 0 rgba(255,255,255,0.15);
        }
        .adm-topbar-user-menu-name{
          font-size:13.5px;font-weight:700;color:#0C2B4E;line-height:1.25;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px;
        }
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-name{color:#E8F0F7;}
        .adm-topbar-user-menu-email{
          margin-top:3px;font-size:11.5px;font-weight:500;color:#64748b;line-height:1.35;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px;
        }
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-email{color:rgba(232,240,247,0.55);}
        .adm-topbar-user-menu-section{padding:6px;}
        .adm-topbar-user-menu-item{
          width:100%;display:flex;align-items:center;gap:10px;
          padding:9px 10px;border:none;border-radius:9px;background:transparent;
          color:#334155;font-size:13px;font-weight:500;text-align:left;cursor:pointer;
          font-family:inherit;transition:background .14s ease,color .14s ease;
        }
        .adm-topbar-user-menu-item:hover{background:#F1F5F9;color:#0C2B4E;}
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-item{color:rgba(232,240,247,0.85);}
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-item:hover{background:rgba(255,255,255,0.08);color:#fff;}
        .adm-topbar-user-menu-item--danger{color:#B91C1C;}
        .adm-topbar-user-menu-item--danger:hover{background:#FEF2F2;color:#991B1B;}
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-item--danger{color:#FCA5A5;}
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-item--danger:hover{background:rgba(220,38,38,0.15);color:#FEE2E2;}
        .adm-topbar-user-menu-item-icon{
          width:32px;height:32px;border-radius:8px;flex-shrink:0;
          display:inline-flex;align-items:center;justify-content:center;
          background:#F1F5F9;color:#2563EB;
          transition:background .14s ease;
        }
        .adm-topbar-user-menu-item:hover .adm-topbar-user-menu-item-icon{background:#E0E7FF;}
        .adm-topbar-user-menu-item--danger .adm-topbar-user-menu-item-icon{background:#FEE2E2;color:#DC2626;}
        .adm-topbar-user-menu-item--danger:hover .adm-topbar-user-menu-item-icon{background:#FECACA;}
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-item-icon{background:rgba(255,255,255,0.08);color:#BFDBFE;}
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-item--danger .adm-topbar-user-menu-item-icon{background:rgba(220,38,38,0.2);color:#FCA5A5;}
        .adm-topbar-user-menu-divider{
          height:1px;background:rgba(12,43,78,0.08);margin:2px 12px 4px;
        }
        .adm-topbar-user-menu--dark .adm-topbar-user-menu-divider{background:rgba(255,255,255,0.1);}
        .adm-topbar-user-avatar{display:none;}
        .adm-topbar-user-meta{display:none;}
        .adm-topbar-user-name{display:none;}
        .adm-topbar-user-email{display:none;}
        .adm-topbar-icon-btn{
          width:34px;height:34px;border-radius:10px;padding:0;
          display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
          border:1px solid rgba(255,255,255,0.14);background:rgba(255,255,255,0.05);
          color:#E8F0F7;transition:background .15s,border-color .15s;
        }
        .bot-admin-root.admin-theme-light .adm-topbar-icon-btn{
          border-color:#e2e8f0;background:#fff;color:#334155;
        }
        .adm-topbar-icon-btn:hover{border-color:rgba(23,120,212,0.45);}
        .bot-admin-root.admin-theme-light .adm-topbar-icon-btn:hover{
          background:#f8fafc;border-color:#cbd5e1;
        }
        .adm-topbar-icon-btn:disabled{opacity:.6;cursor:wait;}
        .adm-topbar-portal-btn{
          height:34px;padding:0 12px;border-radius:10px;gap:7px;
          display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
          border:1px solid #e2e8f0;background:#fff;color:#334155;
          font-size:12px;font-weight:650;letter-spacing:-0.01em;white-space:nowrap;
          transition:background .15s,border-color .15s,color .15s;
        }
        .adm-topbar-portal-btn:hover{
          background:#f8fafc;border-color:#cbd5e1;color:#0f2a4a;
        }
        .adm-sidebar-nav{
          padding:12px 10px 14px!important;gap:3px!important;
          /* Allow the middle nav section to scroll when the sidebar is taller than the viewport
             (short laptops, zoomed window). Brand block and footer stay pinned. */
          min-height:0;
          overflow-y:auto;
          scrollbar-width:thin;
          scrollbar-color:rgba(148,163,184,0.55) transparent;
        }
        .adm-sidebar-nav::-webkit-scrollbar{width:8px;}
        .adm-sidebar-nav::-webkit-scrollbar-track{background:transparent;}
        .adm-sidebar-nav::-webkit-scrollbar-thumb{
          background:rgba(148,163,184,0.45);border-radius:4px;border:2px solid transparent;background-clip:content-box;
        }
        .adm-sidebar-nav::-webkit-scrollbar-thumb:hover{background:rgba(100,116,139,0.7);background-clip:content-box;}
        .bot-admin-root.admin-theme-light .adm-sidebar-nav{
          scrollbar-color:rgba(100,116,139,0.5) transparent;
        }
        .adm-sidebar-nav-label{
          padding:14px 12px 6px!important;font-size:10px!important;font-weight:700!important;
          letter-spacing:.12em!important;color:rgba(232,240,247,0.45)!important;text-transform:uppercase!important;
        }
        .bot-admin-root.admin-theme-light .adm-sidebar-nav-label{color:rgba(232,240,247,0.45)!important;}
        .adm-sidebar-foot{padding:14px 12px 16px!important;border-top:1px solid rgba(255,255,255,0.1)!important;}
        .bot-admin-root.admin-theme-light .adm-sidebar-foot{border-top-color:rgba(255,255,255,0.1)!important;background:transparent;}
        @keyframes admUserPulse{
          0%,100%{box-shadow:0 0 0 2px rgba(34,197,94,0.22);}
          50%{box-shadow:0 0 0 5px rgba(34,197,94,0.12);}
        }
        .adm-user-card{
          padding:10px 11px;border-radius:12px;margin-bottom:10px;
          background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);
          transition:border-color .18s ease,background .18s ease;
        }
        .adm-user-card:hover{
          border-color:#CBD5E1;
          background:#F8FAFC;
          box-shadow:none;
          transform:none;
        }
        .bot-admin-root.admin-theme-light .adm-user-card{
          background:#ffffff;
          border:1px solid #E5E9EE;box-shadow:none;
        }
        .bot-admin-root.admin-theme-light .adm-user-card:hover{
          border-color:#CBD5E1;
          background:#F8FAFC;
          box-shadow:none;
        }
        .adm-user-row{display:flex;align-items:center;gap:10px;}
        .adm-user-meta{min-width:0;flex:1;overflow:hidden;}
        .adm-user-avatar{
          width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;
          font-size:11px;font-weight:800;color:#fff;flex-shrink:0;letter-spacing:.02em;
          background:linear-gradient(145deg,#1F6B63 0%,#1D546C 55%,#0C8F55 100%);
          box-shadow:0 2px 8px rgba(31,107,99,0.2);
          border:none;
          transition:none;
        }
        .adm-user-card:hover .adm-user-avatar{
          transform:none;
          background-position:initial;
          box-shadow:0 2px 8px rgba(31,107,99,0.22);
        }
        .bot-admin-root.admin-theme-light .adm-user-avatar{
          border:none;
          box-shadow:0 2px 8px rgba(31,107,99,0.16);
        }
        .adm-user-label{display:none;}
        .adm-user-name{
          display:block;
          font-size:12.5px;font-weight:700;color:#E8F0F7;line-height:1.25;
          min-width:0;
        }
        .adm-user-name-text{
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;display:block;
        }
        .bot-admin-root.admin-theme-light .adm-user-name{color:#0f2a4a;}
        .adm-user-status-dot{display:none;}
        .adm-user-email{
          font-size:10.5px;font-weight:500;color:rgba(255,255,255,0.42);line-height:1.3;margin-top:2px;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
          transition:none;
        }
        .adm-user-card:hover .adm-user-email{color:rgba(255,255,255,0.55);}
        .bot-admin-root.admin-theme-light .adm-user-email{color:#64748b;}
        .bot-admin-root.admin-theme-light .adm-user-card:hover .adm-user-email{color:#475569;}
        .adm-foot-actions{display:flex;flex-direction:column;gap:8px;}
        .adm-foot-btn{
          width:100%;padding:9px 12px;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer;
          transition:background .18s ease,border-color .18s ease,transform .14s ease,box-shadow .18s ease,color .18s ease;
          font-family:inherit;
        }
        .adm-foot-btn:active{transform:scale(0.98);}
        .adm-foot-btn--primary{
          background:#1778D4;
          border:none;color:#fff;box-shadow:0 4px 12px rgba(23,120,212,0.32);
        }
        .adm-foot-btn--primary:hover{
          transform:translateY(-1px);
          box-shadow:0 6px 16px rgba(23,120,212,0.4);
          filter:brightness(1.05);
          background:#1565C0;
        }
        .adm-foot-btn--ghost{
          background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.18);color:rgba(232,240,247,0.75);
        }
        .adm-foot-btn--ghost:hover{
          background:rgba(255,255,255,0.1);color:#E8F0F7;border-color:rgba(147,197,253,0.4);
          transform:translateY(-1px);
        }
        .bot-admin-root.admin-theme-light .adm-foot-btn--ghost{
          background:transparent;border-color:rgba(255,255,255,0.18);color:rgba(232,240,247,0.75);
        }
        .bot-admin-root.admin-theme-light .adm-foot-btn--ghost:hover{
          background:rgba(255,255,255,0.08);color:#FFFFFF;border-color:rgba(147,197,253,0.45);
          box-shadow:none;
        }
        .bot-admin-root.admin-theme-light .adm-ghost-btn{
          background:#ffffff;
          border-color:#e2e8f0;
          color:#334155;
        }
        .bot-admin-root.admin-theme-light .adm-ghost-btn:hover{
          background:#f8fafc;
          color:#0f172a;
          border-color:#cbd5e1;
        }
        .bot-admin-root.admin-theme-light .adm-primary-btn{
          background:#1F6B63;
          box-shadow:0 4px 12px rgba(31,107,99,0.22);
        }
        .bot-admin-root.admin-theme-light .adm-primary-btn:hover{
          filter:brightness(1.06);
          background:#16554F;
          box-shadow:0 6px 16px rgba(31,107,99,0.28);
        }
        .bot-admin-root.admin-theme-light .adm-input{
          border-color:#e2e8f0;
          color:var(--adm-text);
          background:#ffffff;
          border-radius:10px;
        }
        .bot-admin-root.admin-theme-light .adm-input:focus:not(.adm-search-input){
          border-color:#1F6B63;
          box-shadow:0 0 0 3px rgba(31,107,99,0.14);
        }
        .bot-admin-root.admin-theme-light .adm-input::placeholder{color:#94a3b8;}
        .bot-admin-root.admin-theme-light .adm-theme-chip{
          background:#ffffff;
          border:1px solid #e2e8f0;
          color:#334155;
          box-shadow:0 1px 2px rgba(15,23,42,0.04);
        }
        .bot-admin-root.admin-theme-light .adm-cal-shell{
          background:#ffffff !important;
          border:1px solid #e2e8f0 !important;
          border-radius:16px !important;
          box-shadow:0 8px 32px rgba(15,23,42,0.06) !important;
        }
        /* Embedded CalPage in admin — main-app calendar styles (not in admin <style> by default) */
        .bot-admin-root .adm-cal-page.apage{
          color:${T.text};
          max-width:100%;
        }
        .bot-admin-root .adm-cal-page .content-well{
          background:transparent;
          border:none;
          box-shadow:none;
          padding:0;
        }
        @keyframes admShimmer{
          0%{background-position:200% 0;}
          100%{background-position:-200% 0;}
        }
        .bot-admin-root .adm-skel{
          border-radius:10px;
          background:linear-gradient(90deg,rgba(148,163,184,0.14) 0%,rgba(148,163,184,0.28) 45%,rgba(148,163,184,0.14) 90%);
          background-size:200% 100%;
          animation:admShimmer 1.15s ease-in-out infinite;
        }
        .bot-admin-root.admin-theme-light .adm-skel{
          background:linear-gradient(90deg,#eef2f6 0%,#f8fafc 35%,#e2e8f0 55%,#f8fafc 75%,#eef2f6 100%);
          background-size:200% 100%;
        }
        .bot-admin-root .adm-skel--flat{min-height:0;border-radius:4px;}
        .bot-admin-root .adm-skel--cell{min-height:72px;}
        .bot-admin-root .adm-cal-skel-page{animation:fadeIn .2s ease;}
        .bot-admin-root .adm-cal-page .page{animation:fadeIn .2s ease;}
        .bot-admin-root .adm-cal-page .seg-group{
          display:flex;gap:4px;align-items:stretch;padding:4px;
          background:linear-gradient(180deg,#EEF2F6 0%,#E2E8F0 100%);
          border:1px solid ${T.borderMid};border-radius:11px;
          box-shadow:inset 0 1px 3px rgba(12,43,78,0.08);
        }
        .bot-admin-root .adm-cal-page .cal-view-seg{
          font-family:inherit;border:none;cursor:pointer;font-size:12.5;font-weight:500;
          color:#64748b;background:transparent;padding:7px 12px;border-radius:8px;
          transition:background .18s ease,color .18s ease,transform .12s ease,box-shadow .18s ease;
        }
        .bot-admin-root .adm-cal-page .cal-view-seg:hover:not(.active){
          color:#0f172a;background:rgba(255,255,255,0.85);
        }
        .bot-admin-root .adm-cal-page .cal-view-seg.active{
          font-weight:700;color:#fff !important;
          background:linear-gradient(165deg,#2563EB 0%,#1D4ED8 100%);
          box-shadow:0 2px 8px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.15);
        }
        .bot-admin-root .adm-cal-page .cal-tb-btn{
          font-family:inherit;padding:7px 14px;border:1.5px solid ${T.border};background:${T.bg};
          border-radius:8px;cursor:pointer;font-size:13;font-weight:500;color:${T.textSub};
        }
        .bot-admin-root .adm-cal-page .cal-tb-btn:hover{
          color:${T.text};border-color:${T.accentMid};background:${T.surface};
        }
        .bot-admin-root .adm-cal-page .cal-nav-icon{
          font-family:inherit;padding:7px 9px;border:1.5px solid ${T.border};background:${T.bg};
          cursor:pointer;color:${T.textSub};display:flex;align-items:center;justify-content:center;
        }
        .bot-admin-root .adm-cal-toolbar{
          display:flex;align-items:center;justify-content:space-between;gap:12px 16px;
          flex-wrap:wrap;margin-bottom:14px;padding-bottom:14px;
          border-bottom:1px solid #e8eef4;
        }
        .bot-admin-root .adm-cal-toolbar-left,
        .bot-admin-root .adm-cal-toolbar-right{
          display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0;
        }
        .bot-admin-root .adm-cal-toolbar-right{margin-left:auto;}
        .bot-admin-root .adm-cal-toolbar-title{
          font-size:15px;font-weight:700;letter-spacing:-0.02em;color:${T.text};
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:min(420px,56vw);
        }
        .bot-admin-root .adm-cal-toolbar-count{
          font-size:11px;font-weight:600;color:${T.textMuted};
          background:#f8fafc;border:1px solid #e2e8f0;border-radius:999px;padding:4px 9px;
          white-space:nowrap;
        }
        .bot-admin-root .adm-cal-filters{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
        @media (max-width:900px){
          .bot-admin-root .adm-cal-toolbar{flex-direction:column;align-items:stretch;}
          .bot-admin-root .adm-cal-toolbar-right{margin-left:0;width:100%;}
          .bot-admin-root .adm-cal-toolbar-title{max-width:100%;white-space:normal;}
          .bot-admin-root .adm-cal-page .seg-group{width:100%;}
          .bot-admin-root .adm-cal-page .cal-view-seg{flex:1 1 0;text-align:center;}
        }
        @media (max-width:640px){
          .bot-admin-root .adm-cal-filters{width:100%;}
          .bot-admin-root .adm-cal-filters button{flex:1 1 auto;min-height:36px;}
        }
        .bot-admin-root .adm-cal-day-meta{
          margin-bottom:12px;font-size:12px;font-weight:500;color:${T.textMuted};
        }
        .adm-cal-people-scroll{
          scrollbar-width:thin;
          scrollbar-color:rgba(100,116,139,0.45) transparent;
        }
        .adm-cal-people-scroll::-webkit-scrollbar{width:7px;}
        .adm-cal-people-scroll::-webkit-scrollbar-track{background:transparent;}
        .adm-cal-people-scroll::-webkit-scrollbar-thumb{
          background:rgba(100,116,139,0.4);border-radius:999px;
        }
        .adm-cal-people-scroll::-webkit-scrollbar-thumb:hover{background:rgba(100,116,139,0.6);}
        @media(max-width:560px){
          .adm-cal-attendance-grid{grid-template-columns:1fr !important;}
        }
        .bot-admin-root .adm-cal-page .cal-nav-icon:hover{background:${T.surface};border-color:${T.accentMid};}
        .bot-admin-root .adm-cal-page .cal-nav-split-l{border-right:none;border-radius:8px 0 0 8px;}
        .bot-admin-root .adm-cal-page .cal-nav-split-r{border-radius:0 8px 8px 0;margin-left:-1px;}
        .bot-admin-root .adm-cal-page .btn-join-primary,
        .bot-admin-cal-popup .btn-join-primary{
          font-family:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:8px;
          padding:11px 20px;background:linear-gradient(180deg,#2563EB 0%,#1D4ED8 100%);
          color:#FFFFFF !important;font-size:13.5px;font-weight:700;border:none;border-radius:10px;
          box-shadow:0 4px 12px rgba(37,99,235,0.30),inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .bot-admin-root .adm-cal-page .btn-join-primary:hover,
        .bot-admin-cal-popup .btn-join-primary:hover{
          filter:brightness(1.05);transform:translateY(-1px);
        }
        .bot-admin-root .adm-cal-page .btn-outline,
        .bot-admin-cal-popup .btn-outline{
          font-family:inherit;display:inline-flex;align-items:center;justify-content:center;gap:6px;
          min-height:36px;padding:0 14px;background:#FFFFFF;color:#0C2B4E !important;
          border:1px solid rgba(12,43,78,0.18);border-radius:10px;cursor:pointer;
          font-size:13.5px;font-weight:600;
          box-shadow:0 1px 2px rgba(12,43,78,0.06);
        }
        .bot-admin-root .adm-cal-page .btn-outline:hover,
        .bot-admin-cal-popup .btn-outline:hover{
          background:#F8FAFC;border-color:rgba(12,43,78,0.32);
        }
        .bot-admin-cal-popup .cal-view-seg.active{color:#fff !important;}
        .adm-form-label{
          display:block;
          font-size:12px;
          margin-bottom:5px;
          color:rgba(255,255,255,0.55);
        }
        .bot-admin-root.admin-theme-light .adm-form-label{color:#64748b;}
        .adm-form-hint{
          font-size:11.5px;
          margin-top:5px;
          color:rgba(255,255,255,0.35);
        }
        .bot-admin-root.admin-theme-light .adm-form-hint{color:#94a3b8;}
        .adm-smtp-title{
          font-size:13.5px;
          font-weight:600;
          color:#E8F0F7;
          margin-bottom:3px;
        }
        .bot-admin-root.admin-theme-light .adm-smtp-title{color:#0f172a;}
        .adm-smtp-sub{
          font-size:12px;
          color:rgba(255,255,255,0.45);
          margin-bottom:18px;
          line-height:1.45;
        }
        .bot-admin-root.admin-theme-light .adm-smtp-sub{color:#64748b;}
        .adm-smtp-email{color:#93C5FD;font-family:monospace;}
        .bot-admin-root.admin-theme-light .adm-smtp-email{color:#1d4ed8;}
        .adm-sec-chip{
          padding:7px 18px;
          border-radius:8px;
          border:1.5px solid #334155;
          background:rgba(255,255,255,0.04);
          color:rgba(255,255,255,0.72);
          font-size:12.5;
          font-weight:500;
          cursor:pointer;
          font-family:inherit;
          transition:all .14s ease;
        }
        .adm-sec-chip:hover{
          color:#E8F0F7;
          border-color:#475569;
          background:rgba(255,255,255,0.08);
        }
        .adm-sec-chip.active{
          border-color:${PALETTE.brandBlue};
          background:linear-gradient(135deg,rgba(23,120,212,0.22),rgba(12,143,85,0.1));
          color:#BFDBFE;
          font-weight:600;
        }
        .bot-admin-root.admin-theme-light .adm-sec-chip{
          border-color:#e2e8f0;
          background:#ffffff;
          color:#475569;
        }
        .bot-admin-root.admin-theme-light .adm-sec-chip:hover{
          border-color:#cbd5e1;
          color:#0f172a;
          background:#f8fafc;
        }
        .bot-admin-root.admin-theme-light .adm-sec-chip.active{
          border-color:${PALETTE.brandBlue};
          background:linear-gradient(135deg,#eff6ff 0%,#ecfdf5 100%);
          color:${PALETTE.brandBlue};
          font-weight:600;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page){
          color:#0F172A;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) .adm-card-header{
          color:#0F172A !important;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) .MuiChip-root,
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) .MuiChip-label{
          color:inherit !important;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) [style*="color:#93C5FD"]{
          color:#1d4ed8 !important;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) [style*="color:#86EFAC"]{
          color:#15803d !important;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) [style*="color:#F87171"],
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) [style*="color:#FCA5A5"]{
          color:#b91c1c !important;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) .adm-table-row{
          border-bottom:1px solid #dde7f3 !important;
        }
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) [style*="background:\"rgba(255,255,255,0.03)\""],
        .bot-admin-root.admin-theme-light .apage:not(.adm-cal-page) [style*="background:rgba(255,255,255,0.03)"]{
          background:#f6f9fd !important;
          border-color:#dbe6f2 !important;
        }
        .bot-admin-sidebar{
          width:252px;
          flex-shrink:0;
          order:-1;
          display:flex;
          flex-direction:column;
          background-color:#0C2B4E;
          background:linear-gradient(180deg,#0C2B4E 0%,#0a2548 55%,#082038 100%);
          border-right:1px solid rgba(255,255,255,0.1);
          height:100vh;
          overflow-y:auto;
          overflow-x:hidden;
          /* see .gmail-app-shell — 100vh overshoots on mobile browsers */
          transition:width .22s ease;
        }
        @supports (height: 100dvh){
          .bot-admin-sidebar{height:100dvh;}
        }
        .bot-admin-sidebar.is-collapsed{
          width:76px;
        }
        .bot-admin-sidebar.is-collapsed .adm-brand-text,
        .bot-admin-sidebar.is-collapsed .adm-sidebar-nav-label,
        .bot-admin-sidebar.is-collapsed .adm-nav-label,
        .bot-admin-sidebar.is-collapsed .adm-foot-btn-label{
          display:none !important;
        }
        .bot-admin-sidebar.is-collapsed .adm-sidebar-brand-block{
          padding:14px 10px 12px;
        }
        .bot-admin-sidebar.is-collapsed .adm-brand-head{
          flex-direction:column;
          align-items:center;
          gap:8px;
        }
        .bot-admin-sidebar.is-collapsed .adm-brand-row{
          flex-direction:column;
          gap:0;
          align-items:center;
          width:auto;
        }
        .bot-admin-sidebar.is-collapsed .adm-brand-logo-wrap{
          padding:6px;
          min-width:40px;
        }
        .bot-admin-sidebar.is-collapsed .adm-brand-logo-wrap img{
          height:22px!important;max-width:40px;
        }
        .bot-admin-sidebar.is-collapsed .adm-sidebar-collapse-btn{
          margin:0;
        }
        .bot-admin-sidebar.is-collapsed .adm-sidebar-nav{
          padding:8px 10px !important;
          align-items:center;
        }
        .bot-admin-sidebar.is-collapsed .adm-nav{
          justify-content:center;
          padding:9px;
          width:44px;
        }
        .bot-admin-sidebar.is-collapsed .adm-nav-icon{
          margin:0;
        }
        .bot-admin-sidebar.is-collapsed .adm-sidebar-foot{
          padding:10px 10px 14px !important;
        }
        .bot-admin-sidebar.is-collapsed .adm-foot-actions{
          align-items:center;
        }
        .bot-admin-sidebar.is-collapsed .adm-foot-btn{
          width:44px;height:40px;padding:0;
          display:inline-flex;align-items:center;justify-content:center;
        }
        .adm-sidebar-collapse-btn{
          width:28px;height:28px;border-radius:8px;padding:0;cursor:pointer;
          display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;
          border:1px solid rgba(255,255,255,0.16);
          background:rgba(255,255,255,0.06);
          color:rgba(232,240,247,0.85);
          transition:background .15s ease,border-color .15s ease,color .15s ease;
        }
        .adm-sidebar-collapse-btn:hover{
          background:rgba(23,120,212,0.28);
          border-color:rgba(96,165,250,0.5);
          color:#fff;
        }
        /* Admin route does not mount main-app tip CSS — keep collapsed-nav tips styled */
        .sidebar-hover-tip-anchor{
          z-index:10060;
          pointer-events:none;
        }
        .sidebar-hover-tip{
          position:relative;
          width:max-content;
          max-width:min(calc(100vw - 24px),280px);
          padding:7px 12px;
          border-radius:8px;
          background:#0F172A;
          color:#F8FAFC;
          font-family:'DM Sans',system-ui,sans-serif;
          font-size:13px;
          font-weight:600;
          letter-spacing:0.01em;
          line-height:1.35;
          white-space:nowrap;
          overflow:visible;
          border:none;
          box-shadow:0 8px 22px rgba(15,23,42,0.28);
        }
        .sidebar-hover-tip::before{
          content:"";
          position:absolute;
          left:-6px;top:50%;
          width:0;height:0;
          transform:translateY(-50%);
          border-style:solid;
          border-width:6px 6px 6px 0;
          border-color:transparent #0F172A transparent transparent;
          pointer-events:none;
        }
        .sidebar-hover-tip__shine,
        .sidebar-hover-tip__arrow{display:none !important;}
        .sidebar-hover-tip__label{
          position:relative;z-index:1;
          display:block;
          overflow:visible;
        }
        .adm-brand-text{min-width:0;flex:1;}
        .adm-brand-head{
          display:flex;align-items:flex-start;justify-content:space-between;gap:8px;width:100%;
        }
        .bot-admin-sidebar::-webkit-scrollbar{width:6px;}
        .bot-admin-sidebar::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.22);border-radius:999px;}
        .bot-admin-sidebar::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.35);}
        .bot-admin-sidebar::-webkit-scrollbar-track{background:transparent;}
        .bot-admin-sidebar{scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.22) transparent;}
        .bot-admin-root.admin-theme-light .bot-admin-sidebar{
          background:linear-gradient(180deg,#0b1728 0%,#122238 100%);
          border-right:1px solid rgba(255,255,255,0.08);
          box-shadow:none;
          scrollbar-color:rgba(255,255,255,0.22) transparent;
        }
        .bot-admin-root.admin-theme-light .bot-admin-sidebar::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.22);}
        .bot-admin-root.admin-theme-light .bot-admin-sidebar::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.35);}
        .bot-admin-root.admin-theme-light .adm-brand-title{color:#E8F0F7!important;}
        .bot-admin-root.admin-theme-light .adm-foot-btn--primary{
          background:#1778D4!important;color:#fff!important;border:none!important;
          box-shadow:0 4px 12px rgba(23,120,212,0.35)!important;
        }
        .bot-admin-root.admin-theme-light .adm-foot-btn--ghost{
          background:transparent!important;color:rgba(232,240,247,0.75)!important;border:1px solid rgba(255,255,255,0.18)!important;
        }
        .bot-admin-main{
          flex:1;
          min-width:0;
          display:flex;
          flex-direction:column;
          overflow:hidden;
        }
        @media (max-width: 1024px){
          .bot-admin-root{
            flex-direction:row !important;
            height:100vh !important;
            height:100dvh !important;
            min-height:100dvh;
            overflow:hidden !important;
            position:relative;
          }
          .bot-admin-nav-backdrop{
            position:fixed;inset:0;background:rgba(8,20,35,0.45);
            z-index:1199;border:none;padding:0;cursor:pointer;
            animation:admBackdropIn .18s ease-out;
          }
          @keyframes admBackdropIn{from{opacity:0}to{opacity:1}}
          .bot-admin-sidebar{
            position:fixed !important;
            top:0 !important;left:0 !important;bottom:0 !important;
            width:min(84vw,280px) !important;
            height:100dvh !important;
            max-height:none !important;
            z-index:1200 !important;
            transform:translateX(-100%);
            transition:transform .24s ease;
            border-right:1px solid rgba(255,255,255,0.08) !important;
            border-bottom:none !important;
            box-shadow:2px 0 24px rgba(8,20,35,0.18) !important;
          }
          .bot-admin-sidebar.is-mobile-open{transform:translateX(0);visibility:visible;pointer-events:auto;}
          .bot-admin-sidebar:not(.is-mobile-open){
            transform:translateX(-100%) !important;
            visibility:hidden;
            pointer-events:none;
          }
          .bot-admin-sidebar.is-collapsed{width:min(84vw,280px) !important;}
          .bot-admin-sidebar.is-collapsed .adm-brand-text,
          .bot-admin-sidebar.is-collapsed .adm-sidebar-nav-label,
          .bot-admin-sidebar.is-collapsed .adm-nav-label,
          .bot-admin-sidebar.is-collapsed .adm-foot-btn-label{
            display:inline !important;
          }
          .bot-admin-sidebar.is-collapsed .adm-sidebar-brand-block{padding:14px 16px 12px !important;}
          .bot-admin-sidebar.is-collapsed .adm-brand-head{
            flex-direction:row !important;align-items:flex-start !important;
          }
          .bot-admin-sidebar.is-collapsed .adm-brand-row{
            flex-direction:row !important;align-items:center !important;width:100% !important;
          }
          .bot-admin-sidebar.is-collapsed .adm-sidebar-nav,
          .bot-admin-sidebar.is-collapsed .adm-sidebar-foot{display:flex !important;}
          .bot-admin-sidebar.is-collapsed .adm-sidebar-nav{padding:8px 12px !important;align-items:stretch !important;}
          .bot-admin-sidebar.is-collapsed .adm-nav{
            justify-content:flex-start !important;width:100% !important;padding:9px 12px !important;
          }
          .bot-admin-sidebar .adm-sidebar-brand{padding:12px 14px !important;}
          .bot-admin-sidebar .adm-sidebar-nav{
            flex:1 1 auto !important;
            flex-direction:column !important;
            flex-wrap:nowrap !important;
            overflow-x:hidden !important;
            overflow-y:auto !important;
            max-height:none !important;
            padding:6px 10px 10px !important;
            gap:2px !important;
            -webkit-overflow-scrolling:touch;
          }
          .bot-admin-sidebar .adm-sidebar-nav-label{
            display:block !important;
            padding:12px 12px 4px !important;
          }
          .bot-admin-sidebar .adm-sidebar-nav .adm-nav{
            width:100% !important;
            flex-shrink:0;
            justify-content:flex-start !important;
            min-height:44px;
            padding:9px 12px !important;
            border-left:none !important;
          }
          .bot-admin-sidebar .adm-sidebar-nav .adm-nav.active{
            border-left:none !important;
            border-bottom:none !important;
            box-shadow:inset 3px 0 0 #0f9d8f!important;
            background:rgba(15,157,143,0.14)!important;
          }
          .bot-admin-sidebar .adm-sidebar-nav .adm-nav-icon{
            width:28px;height:28px;border-radius:8px;
          }
          .bot-admin-main{
            flex:1 1 auto !important;
            width:100% !important;
            min-width:0 !important;
            overflow:hidden !important;
          }
          .adm-topbar{
            flex-wrap:wrap !important;
            padding:10px 14px !important;
            gap:8px !important;
          }
          .adm-topbar > div:last-child{
            flex-wrap:wrap;
            width:100%;
            justify-content:flex-start;
          }
          .bot-admin-main .adm-page-scroll{
            padding:14px 12px !important;
          }
          .admin-overview-stat-grid,
          .admin-system-grid{
            grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          }
        }
        @media (max-width: 520px){
          .admin-overview-stat-grid,
          .admin-system-grid{
            grid-template-columns:1fr !important;
          }
          .bot-admin-main .adm-topbar{
            padding:0 12px !important;
            height:auto !important;
            min-height:48px;
            flex-wrap:wrap;
            gap:8px;
            padding-top:8px !important;
            padding-bottom:8px !important;
          }
        }

        /* ── Overflow guards ─────────────────────────────────────────
           A single wide child (table, chart, long email address) used to
           stretch its ancestors and push the whole portal sideways, so the
           page scrolled horizontally and cards ran off screen. Each level
           is capped at its parent, and anything genuinely wider scrolls
           inside its own frame instead. */
        .bot-admin-root{max-width:100vw;overflow-x:hidden;}
        .bot-admin-main,.bot-admin-main .adm-page-scroll,.bot-admin-root .apage{
          min-width:0;max-width:100%;box-sizing:border-box;
        }
        .bot-admin-root .apage > *{min-width:0;max-width:100%;}
        .bot-admin-root .adm-card,
        .bot-admin-root .adm-org-panel,
        .bot-admin-root .adm-cal-shell,
        .bot-admin-root .MuiPaper-root{max-width:100%;box-sizing:border-box;}
        .bot-admin-root .adm-mui-table,
        .bot-admin-root .MuiTableContainer-root{
          max-width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;
        }
        /* Was overflow-x:hidden, which cut the last columns off with no way
           to reach them. Scroll the panel instead. */
        .bot-admin-root .adm-scroll-panel{overflow-x:auto;}
        .bot-admin-root img,
        .bot-admin-root svg:not(.adm-spark),
        .bot-admin-root canvas{max-width:100%;}
        .bot-admin-root pre{white-space:pre-wrap;word-break:break-word;}
        /* Seven day-columns cannot stack. The user shell scrolls these inside
           their own frame; the admin portal has its own stylesheet, so the
           calendar grids had nothing to scroll against here. */
        .bot-admin-root .mobile-scroll-x,
        .bot-admin-root .cal-week-view{
          max-width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;
        }

        /* Shared responsive contract for every admin tab. */
        .bot-admin-root .apage,
        .bot-admin-root .apage > div,
        .bot-admin-root .adm-premium-page,
        .bot-admin-root .adm-premium-page > div{min-width:0;}
        .bot-admin-root .adm-card,
        .bot-admin-root .adm-chart-card,
        .bot-admin-root .adm-usage-table-card{width:100%;max-width:100%;}
        @media (max-width: 900px){
          .bot-admin-root .adm-users-toolbar,
          .bot-admin-root .adm-users-toolbar--oneline{flex-wrap:wrap;overflow-x:visible;}
          .bot-admin-root .adm-users-search{flex:1 1 100%;max-width:none;}
          .bot-admin-root .adm-users-toolbar-meta{width:100%;margin-left:0;justify-content:flex-start;}
          .bot-admin-root .adm-usage-columns{grid-template-columns:1fr !important;}
          .bot-admin-root .adm-calendar-toolbar,
          .bot-admin-root .adm-cal-toolbar{flex-wrap:wrap;max-width:100%;}
          .bot-admin-root .adm-cal-shell{width:100%;}
        }
        @media (max-width: 640px){
          .bot-admin-root .adm-page-scroll{padding:10px !important;}
          .bot-admin-root .adm-hero-inner{padding:14px !important;}
          .bot-admin-root .adm-hero-title,
          .bot-admin-root .adm-premium-hero h1{font-size:19px !important;line-height:1.2;}
          .bot-admin-root .adm-hero-desc{font-size:11.5px !important;line-height:1.4;}
          .bot-admin-root .adm-card-header,
          .bot-admin-root .adm-org-panel-head{padding:11px 12px !important;}
          .bot-admin-root .adm-card-sub{font-size:10.5px !important;line-height:1.4;}
          .bot-admin-root .admin-overview-stat-grid,
          .bot-admin-root .admin-system-grid{grid-template-columns:1fr !important;}
          .bot-admin-root .adm-email-preview{max-width:100%;overflow-wrap:anywhere;}
        }

        /* ── Detail drawer (transcript + recap email) ──────────────── */
        .adm-detail-drawer{min-width:0;box-sizing:border-box;}
        .adm-detail-head > div{min-width:0;}
        .adm-detail-head > div > div{overflow-wrap:anywhere;}
        /* Email HTML is built for a 640px mail client column; inside a phone
           drawer its tables and long links would otherwise spill sideways. */
        .adm-email-preview{max-width:100%;box-sizing:border-box;}
        .adm-email-preview table{max-width:100% !important;}
        .adm-email-preview td,.adm-email-preview th{overflow-wrap:anywhere;}
        .adm-email-preview a{word-break:break-word;}
        @media (max-width: 640px){
          /* Drawer goes full-bleed on phones; tighten the header so the
             transcript keeps usable reading width. Body padding stays on the
             inline styles — the email preview needs a 0-padding full bleed. */
          .adm-detail-head{padding-left:12px !important;padding-right:12px !important;}
          .adm-email-preview{min-height:0 !important;}
          .adm-email-preview td,.adm-email-preview th{
            padding-left:10px !important;padding-right:10px !important;
          }
          /* The action-items grid keeps "Due" on one line; on a 360px screen
             that alone forces a sideways scroll. */
          .adm-email-preview td[style*="nowrap"]{white-space:normal !important;}
        }

        /* ── Phone tuning for the portal chrome ───────────────────── */
        @media (max-width: 640px){
          .bot-admin-main .adm-page-scroll{padding:12px 10px !important;}
          .adm-hero-inner{padding:16px 14px 14px !important;}
          .adm-hero-title,.adm-premium-hero h1{font-size:19px !important;}
          .adm-hero-desc{font-size:12px !important;}
          .adm-card-header{padding:12px 14px !important;}
          .adm-card-header--search{flex-wrap:wrap !important;}
          .adm-premium-page{padding-bottom:8px;}
          .adm-premium-page .adm-card-header--search > div:first-child{flex:1 1 100% !important;}
          .adm-premium-page .adm-card-header--search .adm-search-toolbar{flex:1 1 100% !important;width:100%;}
          .adm-table-row{padding:12px 14px !important;}
          .adm-topbar-portal-btn .adm-topbar-portal-label{display:none;}
          .adm-org-legend{padding:10px 12px !important;}
          .adm-org-legend--cards .adm-org-legend-item{flex:1 1 calc(50% - 8px);padding:8px 9px;font-size:10.5px;}
          .adm-org-legend--cards .adm-org-legend-hint{font-size:9.5px;}
          .adm-scroll-panel--meetings{overflow-x:hidden !important;}
          .adm-scroll-panel--meetings .adm-mui-table{overflow-x:hidden !important;}
          .adm-scroll-panel--meetings .adm-mui-table > table{min-width:100% !important;table-layout:fixed !important;}
          .adm-scroll-panel--meetings .adm-mui-table .MuiTableCell-root{padding-left:8px !important;padding-right:8px !important;font-size:11px;}
          .adm-scroll-panel--meetings .adm-mui-table .MuiTableCell-root:first-of-type{padding-left:10px !important;}
          .adm-scroll-panel--meetings .adm-mui-table .MuiTableCell-root:last-of-type{padding-right:10px !important;}
          .adm-list-pagination{flex-wrap:wrap !important;gap:8px !important;}
          /* Page title, subtitle and four actions cannot share a 360px row.
             The subtitle is the one piece that repeats the nav label. */
          .adm-topbar-sub{display:none;}
          .adm-topbar-title{
            font-size:16px !important;
            white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
          }
          .adm-topbar > div:last-child{gap:6px !important;}
        }

        .adm-org-legend{
          display:flex;flex-wrap:wrap;gap:10px 18px;align-items:center;
          padding:12px 16px;margin-bottom:14px;border-radius:12px;border:1px solid;
          font-size:12px;line-height:1.4;
        }
        .adm-org-legend-item{display:inline-flex;align-items:center;gap:7px;min-width:0;}
        .adm-org-legend-swatch{
          width:10px;height:10px;border-radius:3px;border:1px solid;flex-shrink:0;
        }
        .adm-org-legend--cards{
          background:transparent !important;border:0 !important;padding:0 !important;
          gap:8px;margin-bottom:14px;align-items:stretch;
        }
        .adm-org-legend--cards .adm-org-legend-item{
          flex:1 1 150px;display:flex;align-items:flex-start;gap:7px;
          padding:9px 10px;border:1px solid;border-radius:9px;
          background:${isLight ? "#fff" : "rgba(255,255,255,0.035)"};
          font-size:11px;line-height:1.3;
        }
        .adm-org-legend--cards .adm-org-legend-copy{min-width:0;display:flex;flex-direction:column;gap:3px;}
        .adm-org-legend--cards .adm-org-legend-hint{display:block;font-size:10px;font-weight:500;opacity:.72;}
        .adm-org-legend--cards .adm-org-legend-hint::before{content:"";}
        .adm-users-table-wrap{
          max-width:100%;
          overflow-x:auto;
          -webkit-overflow-scrolling:touch;
          padding:12px 16px 16px;
          box-sizing:border-box;
          background:transparent;
        }
        .adm-users-table-wrap .adm-mui-table--joy{
          margin:0 !important;
          width:100%;
          max-width:100%;
          box-sizing:border-box;
        }
        .adm-joy-user-cell{
          display:flex;align-items:center;gap:12px;min-width:0;
        }
        .adm-joy-user-meta{min-width:0;}
        .adm-joy-user-name{
          font-size:13.5px;font-weight:700;letter-spacing:-0.01em;color:#0f172a;
          line-height:1.25;margin-bottom:2px;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .adm-joy-user-email{
          font-size:11.5px;color:#64748b;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .bot-admin-root:not(.admin-theme-light) .adm-joy-user-name{color:#E8F0F7;}
        .bot-admin-root:not(.admin-theme-light) .adm-joy-user-email{color:rgba(232,240,247,0.55);}
        .adm-joy-metric{
          font-variant-numeric:tabular-nums;font-size:13.5px;font-weight:750;
        }
        .adm-joy-metric--blue{color:#1d4ed8;}
        .adm-joy-metric--green{color:#15803d;}
        .adm-joy-metric--total{color:#0f172a;font-weight:800;}
        .bot-admin-root:not(.admin-theme-light) .adm-joy-metric--blue{color:#93C5FD;}
        .bot-admin-root:not(.admin-theme-light) .adm-joy-metric--green{color:#86EFAC;}
        .bot-admin-root:not(.admin-theme-light) .adm-joy-metric--total{color:#E8F0F7;}
        .adm-joy-since{
          font-size:12px;font-weight:550;color:#64748b;white-space:nowrap;
        }
        .bot-admin-root:not(.admin-theme-light) .adm-joy-since{color:rgba(232,240,247,0.55);}
        .adm-users-table-wrap .adm-org-role-pill{
          border-radius:999px;padding:4px 10px;font-size:10.5px;
        }
        .adm-users-controls{
          display:flex;flex-direction:column;gap:0;
          padding:14px 16px;border-bottom:1px solid #e8eef4;
          background:linear-gradient(180deg,#f8fafc 0%,#ffffff 72%);
        }
        .bot-admin-root:not(.admin-theme-light) .adm-users-controls{
          border-bottom-color:#1E3448;
          background:linear-gradient(180deg,rgba(255,255,255,0.03) 0%,transparent 100%);
        }
        .adm-users-toolbar{
          display:flex;align-items:flex-end;justify-content:flex-start;gap:10px 12px;flex-wrap:nowrap;
          width:100%;min-width:0;
        }
        .adm-users-toolbar--oneline{
          overflow-x:auto;-webkit-overflow-scrolling:touch;
          padding-bottom:2px;
        }
        .adm-users-toolbar-meta{
          display:inline-flex;align-items:center;gap:8px;flex:0 0 auto;margin-left:auto;
          align-self:flex-end;padding-bottom:2px;
        }
        .adm-users-result-chip{
          display:inline-flex;align-items:center;height:36px;padding:0 11px;border-radius:999px;
          border:1px solid #e2e8f0;background:#fff;color:#475569;
          font-size:12px;font-weight:700;white-space:nowrap;
        }
        .bot-admin-root:not(.admin-theme-light) .adm-users-result-chip{
          background:rgba(255,255,255,0.05);border-color:#1E3448;color:rgba(232,240,247,0.75);
        }
        .adm-users-search{
          position:relative;display:flex;align-items:center;
          flex:1 1 220px;min-width:180px;max-width:280px;
          min-height:38px;height:38px;border-radius:10px;border:1px solid #d0d7e2;background:#fff;
          box-sizing:border-box;
          transition:border-color .12s ease;
        }
        .bot-admin-root:not(.admin-theme-light) .adm-users-search{
          background:rgba(255,255,255,0.05);border-color:#1E3448;box-shadow:none;
        }
        .adm-users-search-icon{
          display:inline-flex;align-items:center;justify-content:center;
          width:36px;flex-shrink:0;pointer-events:none;
        }
        .adm-users-search input{
          flex:1;min-width:0;border:0!important;outline:none!important;box-shadow:none!important;
          background:transparent;
          font:inherit;font-size:13px;color:var(--adm-text,#0f172a);
          padding:8px 6px 8px 0;height:36px;
        }
        .adm-users-search input:focus,
        .adm-users-search input:focus-visible{
          outline:none!important;box-shadow:none!important;border:0!important;
        }
        .adm-users-search input::-webkit-search-cancel-button,
        .adm-users-search input::-webkit-search-decoration,
        .adm-users-search input::-ms-clear{
          display:none;-webkit-appearance:none;
        }
        .adm-users-search-clear{
          display:inline-flex;align-items:center;justify-content:center;
          width:32px;height:32px;margin-right:2px;border:0;border-radius:8px;
          background:transparent;cursor:pointer;flex-shrink:0;
        }
        .adm-users-search-clear:hover{background:rgba(15,23,42,0.06);}
        .adm-users-toolbar .adm-users-filter{
          display:flex;flex-direction:column;gap:4px;min-width:0;flex:0 0 132px;
          font-size:10px;font-weight:750;letter-spacing:.05em;text-transform:uppercase;
          color:var(--adm-muted,#64748b);
        }
        .adm-users-toolbar .adm-users-filter select,
        .adm-users-toolbar .adm-users-date-range-fields input[type="date"]{
          width:100%;min-width:0;height:38px;padding:0 10px;border-radius:10px;
          border:1px solid #d0d7e2;background:#fff;color:#0f172a;
          font:inherit;font-size:12.5px;font-weight:500;text-transform:none;letter-spacing:0;
          box-sizing:border-box;
        }
        .adm-users-toolbar .adm-users-filter select:focus,
        .adm-users-toolbar .adm-users-date-range-fields input[type="date"]:focus{
          outline:none;border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,0.12);
        }
        .bot-admin-root:not(.admin-theme-light) .adm-users-toolbar .adm-users-filter select,
        .bot-admin-root:not(.admin-theme-light) .adm-users-toolbar .adm-users-date-range-fields input[type="date"]{
          background:rgba(255,255,255,0.05);border-color:#1E3448;color:#E8F0F7;
        }
        .adm-users-toolbar .adm-users-date-range{
          display:flex;flex-direction:column;gap:4px;min-width:0;flex:0 0 260px;
        }
        .adm-users-date-range-label{
          font-size:10px;font-weight:750;letter-spacing:.05em;text-transform:uppercase;
          color:var(--adm-muted,#64748b);
        }
        .adm-users-date-range-fields{
          display:grid;grid-template-columns:1fr auto 1fr;gap:6px;align-items:center;min-width:0;
        }
        .adm-users-date-sep{
          font-size:11px;font-weight:600;color:#94a3b8;text-transform:lowercase;
        }
        .adm-users-filters-reset{
          height:36px;padding:0 12px;border-radius:999px;border:1px solid #cbd5e1;
          background:#fff;color:#334155;font:inherit;font-size:12px;font-weight:650;
          cursor:pointer;white-space:nowrap;
        }
        .adm-users-filters-reset:hover{background:#f8fafc;border-color:#94a3b8;}
        .apage.adm-org-form .adm-org-panel{overflow:hidden;}
        @media (max-width: 1100px){
          .adm-users-toolbar--oneline{flex-wrap:nowrap;}
          .adm-users-search{max-width:220px;flex-basis:200px;}
          .adm-users-toolbar .adm-users-filter{flex-basis:120px;}
          .adm-users-toolbar .adm-users-date-range{flex-basis:240px;}
        }
        @media (max-width: 900px){
          .adm-page-scroll{padding:14px 12px !important;}
          .adm-premium-page{width:100%;max-width:100%;}
          .adm-premium-page .adm-card-header{gap:10px;}
          .adm-premium-page .adm-card-sub{max-width:100%;}
          .adm-users-controls{padding:12px 14px;}
          .adm-users-toolbar--oneline{
            flex-wrap:wrap;align-items:stretch;
          }
          .adm-users-search{flex:1 1 100%;max-width:none;min-width:0;}
          .adm-users-toolbar .adm-users-filter{flex:1 1 140px;}
          .adm-users-toolbar .adm-users-date-range{flex:1 1 220px;}
          .adm-users-toolbar-meta{margin-left:0;width:100%;justify-content:space-between;}
          .adm-org-legend{padding:10px 12px;gap:8px 12px;font-size:11.5px;margin-bottom:12px;}
          .adm-org-panel .adm-org-panel-head{padding:14px 14px 12px !important;}
          .adm-card-header{padding:14px 14px !important;}
          .adm-scroll-panel--meetings{max-height:min(70vh,560px);}
          .adm-list-pagination{flex-wrap:wrap;gap:8px;}
        }
        @media (max-width: 640px){
          .adm-users-toolbar .adm-users-filter,
          .adm-users-toolbar .adm-users-date-range{flex:1 1 100%;}
          .adm-users-date-range-fields{grid-template-columns:1fr;gap:8px;}
          .adm-users-date-sep{display:none;}
          .adm-org-legend{flex-direction:column;align-items:flex-start;}
          .adm-org-legend-item{width:100%;}
          .adm-premium-hero h1,.adm-hero-title{font-size:22px !important;}
          .adm-card-sub{font-size:11px !important;line-height:1.45;}
          .adm-users-table-wrap{padding:10px 12px 14px;}
          .adm-users-table-wrap .MuiTableCell-root{padding-left:14px !important;padding-right:14px !important;}
          .adm-users-table-wrap .MuiTableCell-root:first-of-type{padding-left:16px !important;}
          .adm-users-table-wrap .MuiTableCell-root:last-of-type{padding-right:16px !important;}
          .adm-org-role-pill{font-size:10px;padding:3px 8px;}
        }
        .adm-org-panel .adm-org-panel-head{
          background:linear-gradient(180deg,rgba(12,43,78,0.04),transparent);
        }
        .bot-admin-root:not(.admin-theme-light) .adm-org-panel .adm-org-panel-head{
          background:linear-gradient(180deg,rgba(255,255,255,0.03),transparent);
        }
        /* Header-inline search: icon only until clicked, no row of its own. */
        .adm-card-header--search{
          display:flex;align-items:flex-start;justify-content:space-between;gap:12px;
        }
        .adm-search-icon-btn{
          display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
          width:30px;height:30px;flex:0 0 auto;padding:0;border-radius:8px;
          border:1px solid transparent;background:transparent;
          transition:background 0.15s ease,border-color 0.15s ease;
        }
        .adm-search-icon-btn:hover{
          background:#f1f5f9;border-color:var(--adm-border,#e2e8f0);
        }
        .bot-admin-root:not(.admin-theme-light) .adm-search-icon-btn:hover{
          background:rgba(255,255,255,0.08);border-color:#1E3448;
        }
        .adm-search-inline{
          display:inline-flex;align-items:center;gap:8px;min-width:0;
        }
        .adm-search-inline--static{align-items:stretch;width:100%;}
        .adm-all-meetings-toolbar{
          display:flex;flex-wrap:wrap;align-items:flex-end;gap:10px 14px;
          padding:12px 14px 14px;
        }
        .adm-all-meetings-toolbar .adm-meetings-list-controls{margin-left:0;flex:1 1 auto;justify-content:flex-end;}
        .adm-all-meetings-toolbar-search{
          flex:1 1 260px;min-width:min(100%,240px);max-width:480px;
          display:flex;flex-direction:column;gap:4px;min-height:0;
        }
        .adm-all-meetings-toolbar-search .adm-search-inline{width:100%;}
        .adm-all-meetings-toolbar-search .adm-search-inline-field{
          width:100%;height:32px;box-sizing:border-box;min-height:32px;
        }
        .adm-all-meetings-toolbar-search .adm-search-inline-field input{
          width:100%;max-width:none;flex:1;min-width:0;
        }
        .adm-toolbar-field-label{
          font-size:10.5px;font-weight:600;line-height:1.2;
        }
        .adm-search-inline-count{
          font-size:11px;font-weight:700;white-space:nowrap;
          color:var(--adm-muted,#64748b);
        }
        .adm-search-inline-field{
          display:inline-flex;align-items:center;gap:7px;min-width:0;
          height:30px;padding:0 10px;border-radius:8px;
          border:1px solid var(--adm-border,#e2e8f0);background:#ffffff;
          transition:border-color .12s ease;
        }
        /* Plain admin search — no green/teal/blue hover rings (all tabs). */
        .bot-admin-root .adm-users-search:hover,
        .bot-admin-root .adm-search-inline-field:hover,
        .bot-admin-root .adm-input.adm-search-input:hover{
          border-color:#cbd5e1;
          box-shadow:none;
        }
        .bot-admin-root .adm-users-search:focus-within,
        .bot-admin-root .adm-search-inline-field:focus-within,
        .bot-admin-root .adm-input.adm-search-input:focus{
          border-color:#94a3b8;
          box-shadow:none;
          outline:none;
        }
        .bot-admin-root:not(.admin-theme-light) .adm-users-search:focus-within,
        .bot-admin-root:not(.admin-theme-light) .adm-search-inline-field:focus-within,
        .bot-admin-root:not(.admin-theme-light) .adm-input.adm-search-input:focus{
          border-color:#64748b;
        }
        .bot-admin-root .adm-input.adm-search-input{
          box-shadow:none;
        }
        .adm-search-inline-field input{
          width:210px;max-width:40vw;min-width:0;border:0;outline:0;background:transparent;
          font:inherit;font-size:12.5px;color:var(--adm-text,#0f172a);padding:0;
        }
        .bot-admin-root:not(.admin-theme-light) .adm-search-inline-field{
          background:rgba(255,255,255,0.05);border-color:#1E3448;
        }
        @media (max-width:640px){
          .adm-search-inline-field input{width:130px;}
          .adm-search-inline-count{display:none;}
        }
        .adm-org-meta-line{
          font-size:11.5px;font-weight:500;margin-top:6px;line-height:1.4;
          color:var(--adm-muted,#64748b);
        }
        .adm-org-role-pill{
          display:inline-flex;align-items:center;padding:2px 8px;border-radius:6px;
          font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
          background:rgba(23,120,212,0.12);color:#1d4ed8;border:1px solid rgba(23,120,212,0.22);
        }
        .bot-admin-root:not(.admin-theme-light) .adm-org-role-pill{
          background:rgba(59,130,246,0.18);color:#BFDBFE;border-color:rgba(147,197,253,0.3);
        }
        .adm-meeting-row{
          border-radius:12px!important;
          transition:border-color .15s ease,box-shadow .15s ease;
        }
        .bot-admin-root.admin-theme-light .adm-meeting-row:hover{
          border-color:#CBD5E1!important;
          box-shadow:0 2px 10px rgba(15,23,42,0.06);
        }
      `}</style>

      {/* Main — first in the DOM on mobile so the page is never pushed down by the nav */}
      <div className="bot-admin-main" style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:ADM.pageBg}}>
        <div
          className="adm-topbar"
          style={{
            minHeight: 64,
            borderBottom: `1px solid ${ADM.border}`,
            display: "flex",
            alignItems: "center",
            padding: "10px 22px",
            justifyContent: "space-between",
            flexShrink: 0,
            background: "#FFFFFF",
            gap: 12,
          }}
        >
          {isAdminMobile ? (
            <button
              type="button"
              className="adm-topbar-icon-btn"
              aria-label={adminMobileNavOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={adminMobileNavOpen}
              title="Menu"
              onClick={() => setAdminMobileNavOpen((v) => !v)}
              style={{ borderRadius: "50%", width: 36, height: 36, flexShrink: 0 }}
            >
              <Svg d={IC.menu} size={18} color="currentColor" stroke={2} />
            </button>
          ) : null}
          <div className="adm-topbar-heading" style={{ minWidth: 0, flex: isAdminMobile ? 1 : undefined }}>
            <div
              className="adm-topbar-title"
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: ADM.text,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              {ADMIN_NAV.find((n) => n.id === page)?.label || "Admin"}
            </div>
            <div className="adm-topbar-sub" style={{ fontSize: 12, color: ADM.textMuted, marginTop: 2, fontWeight: 500 }}>
              {page === "overview"
                ? "Live platform health and recent MeetAI activity"
                : page === "transcriptsEmail"
                  ? "Stored transcripts and recap email delivery"
                  : page === "usage"
                    ? "Session minutes, budget, and billing"
                    : page === "users"
                      ? "People signed in to Avaada MeetAI"
                      : page === "feedback"
                        ? "User ratings, messages, and screenshots"
                      : page === "calendar"
                        ? "Org-wide MeetAI calendar sessions"
                        : page === "smtp"
                          ? "Recap email SMTP and delivery"
                          : "Avaada MeetAI admin portal"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {adminTopbarUpdating ? (
              <span style={{ fontSize: 11, color: adminTextMuted, fontWeight: 600 }}>Refreshing…</span>
            ) : null}
            {adminCalendarRefreshing ? (
              <span style={{ fontSize: 11, color: ADM.teal, fontWeight: 600 }}>
                Calendar sync…
              </span>
            ) : null}
            <button
              type="button"
              className="adm-topbar-icon-btn"
              onClick={() => setAdminReloadTick((v) => v + 1)}
              disabled={adminTopbarUpdating}
              title="Refresh admin data"
              aria-label="Refresh admin data"
              style={{ borderRadius: "50%", width: 36, height: 36 }}
            >
              <Svg d={IC.refresh} size={15} stroke={2.2} color="currentColor" />
            </button>
            <button
              type="button"
              ref={adminLiveBtnRef}
              onClick={() => {
                setAdminUserMenuOpen(false);
                setAdminLiveListOpen((open) => !open);
              }}
              title="View live meetings"
              aria-label={`${liveMeetings.length} live meetings`}
              aria-haspopup="true"
              aria-expanded={adminLiveListOpen}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: liveMeetings.length > 0 ? (adminLiveListOpen ? "rgba(22,163,74,0.18)" : "rgba(22,163,74,0.1)") : "rgba(148,163,184,0.12)",
                border: liveMeetings.length > 0 ? "1px solid rgba(22,163,74,0.25)" : "1px solid rgba(148,163,184,0.35)",
                borderRadius: 20,
                padding: "5px 10px",
                fontSize: 11.5,
                color: liveMeetings.length > 0 ? "#15803d" : "#64748b",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: liveMeetings.length > 0 ? "#22C55E" : "#94a3b8",
                  animation: liveMeetings.length > 0 ? "pulseA 1.6s ease infinite" : "none",
                }}
              />
              {liveMeetings.length} live
            </button>
            {adminLiveListOpen
              ? createPortal(
                  <div
                    ref={adminLiveListRef}
                    role="menu"
                    aria-label="Live meetings"
                    style={{
                      position: "fixed",
                      top: adminLiveListPos.top,
                      right: adminLiveListPos.right,
                      zIndex: 10050,
                      width: "min(400px, calc(100vw - 24px))",
                      maxHeight: "min(440px, calc(100vh - 80px))",
                      overflow: "auto",
                      background: "#fff",
                      border: "1px solid rgba(12,43,78,0.12)",
                      borderRadius: 14,
                      boxShadow: "0 16px 40px rgba(15,23,42,0.18)",
                      padding: "8px 0",
                    }}
                  >
                    <div style={{ padding: "8px 14px 10px", borderBottom: "1px solid #eef2f7", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>Live now</div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                          {liveMeetings.length === 0
                            ? "No meetings in a live time window"
                            : `${liveMeetings.length} meeting${liveMeetings.length === 1 ? "" : "s"} in live window`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setAdminLiveListOpen(false); goAdminPage("allMeetings"); }}
                        style={{ border: "1px solid #e2e8f0", background: "#f8fafc", borderRadius: 8, padding: "5px 9px", fontSize: 11, fontWeight: 600, color: "#334155", cursor: "pointer", fontFamily: "inherit" }}
                      >
                        All meetings
                      </button>
                    </div>
                    {liveMeetings.length === 0 ? (
                      <div style={{ padding: "16px 14px", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>
                        Nothing is live right now. Old rows still marked &quot;live&quot; in DB (past end time) are hidden — open All meetings to review them.
                      </div>
                    ) : (
                      liveMeetings.map((m) => {
                        const meetKey = String(m._id || m.id || "");
                        const title = String(m.title || "Untitled meeting").trim() || "Untitled meeting";
                        const host = meetingHostName(m) || "—";
                        const when = [formatMeetingTimeForDisplay(m.time), m.end && m.end !== "—" ? `– ${formatMeetingTimeForDisplay(m.end)}` : ""].filter(Boolean).join(" ") || "—";
                        const joinUrl = joinUrlFromMeeting(m);
                        const ready = meetingLiveTranscriptReady(m);
                        const openMeeting = () => {
                          setAdminLiveListOpen(false);
                          setAdminTabSearch("allMeetings", "");
                          setAdminMeetingsDateFrom("");
                          setAdminMeetingsDateTo("");
                          setAdminMeetingsPage(1);
                          goAdminPage("allMeetings");
                          if (meetKey) startTransition(() => { setAllMeetingsOpenId(meetKey); setAllMeetingsListsId(null); });
                        };
                        return (
                          <div key={meetKey || title} role="menuitem" style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 6 }}>
                            <button type="button" onClick={openMeeting} style={{ border: "none", background: "transparent", padding: 0, textAlign: "left", cursor: "pointer", fontFamily: "inherit", width: "100%" }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                <span style={{ width: 7, height: 7, borderRadius: "50%", background: ready ? "#22C55E" : "#F59E0B", marginTop: 5, flexShrink: 0, boxShadow: ready ? "0 0 0 3px rgba(34,197,94,0.18)" : "0 0 0 3px rgba(245,158,11,0.18)" }} />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 650, color: "#0f172a", lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={title}>{title}</div>
                                  <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3, lineHeight: 1.4 }}>
                                    {host} · {when} · <span style={{ color: ready ? "#15803d" : "#b45309", fontWeight: 600 }}>{ready ? "MeetAI in call" : "MeetAI pending"}</span>
                                  </div>
                                </div>
                              </div>
                            </button>
                            <div style={{ display: "flex", gap: 6, paddingLeft: 15 }}>
                              <button type="button" onClick={openMeeting} style={{ border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, padding: "4px 8px", fontSize: 11, fontWeight: 600, color: "#334155", cursor: "pointer", fontFamily: "inherit" }}>Open in admin</button>
                              {joinUrl ? (
                                <a href={joinUrl} target="_blank" rel="noopener noreferrer" onClick={() => setAdminLiveListOpen(false)} style={{ border: "1px solid rgba(22,163,74,0.3)", background: "rgba(22,163,74,0.08)", borderRadius: 7, padding: "4px 8px", fontSize: 11, fontWeight: 600, color: "#15803d", textDecoration: "none" }}>Join call</a>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>,
                  document.body
                )
              : null}
            <button
              type="button"
              className="adm-topbar-portal-btn"
              onClick={() => {
                setAdminUserMenuOpen(false);
                setAdminLiveListOpen(false);
                setPortalAdminsOpen(true);
              }}
              title="Manage admins"
              aria-label="Manage admins"
            >
              <Svg d={IC.shield} size={14} stroke={2.2} color="currentColor" />
              <span className="adm-topbar-portal-label">Manage admins</span>
            </button>
            <div className="adm-topbar-user">
              <button
                type="button"
                ref={adminUserBtnRef}
                className="adm-topbar-user-btn"
                aria-label="Account"
                aria-haspopup="true"
                aria-expanded={adminUserMenuOpen}
                title="Account"
                onClick={() => setAdminUserMenuOpen((open) => !open)}
              >
                {adminProfile.initials}
              </button>
              {adminUserMenuOpen
                ? createPortal(
                    <div
                      ref={adminUserMenuRef}
                      className={`adm-topbar-user-menu${!isLight ? " adm-topbar-user-menu--dark" : ""}`}
                      role="menu"
                      aria-label="Account menu"
                      style={{ top: adminUserMenuPos.top, right: adminUserMenuPos.right }}
                    >
                      <div className="adm-topbar-user-menu-head">
                        <div className="adm-topbar-user-menu-head-avatar" aria-hidden>
                          {adminProfile.initials}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="adm-topbar-user-menu-name" title={adminProfile.displayName}>
                            {adminProfile.displayName}
                          </div>
                          <div className="adm-topbar-user-menu-email" title={adminProfile.email || "Admin"}>
                            {adminProfile.email || "Admin"}
                          </div>
                        </div>
                      </div>
                      {onOpenMainApp ? (
                        <div className="adm-topbar-user-menu-section">
                          <button
                            type="button"
                            className="adm-topbar-user-menu-item"
                            role="menuitem"
                            onClick={() => {
                              setAdminUserMenuOpen(false);
                              onOpenMainApp();
                            }}
                          >
                            <span className="adm-topbar-user-menu-item-icon" aria-hidden>
                              <Svg d={IC.launch} size={15} color="#2563EB" stroke={2} />
                            </span>
                            Open MeetAI app
                          </button>
                        </div>
                      ) : null}
                      <div className="adm-topbar-user-menu-divider" aria-hidden />
                      <div className="adm-topbar-user-menu-section" style={{ paddingTop: 2, paddingBottom: 8 }}>
                        <button
                          type="button"
                          className="adm-topbar-user-menu-item adm-topbar-user-menu-item--danger"
                          role="menuitem"
                          onClick={() => {
                            setAdminUserMenuOpen(false);
                            setSignOutConfirmOpen(true);
                          }}
                        >
                          <span className="adm-topbar-user-menu-item-icon" aria-hidden>
                            <Svg d={IC.logout} size={15} color="#DC2626" stroke={2} />
                          </span>
                          Logout
                        </button>
                      </div>
                    </div>,
                    document.body
                  )
                : null}
            </div>
          </div>
        </div>

        <PortalAdminsModal
          open={portalAdminsOpen}
          onClose={() => setPortalAdminsOpen(false)}
          sessionEmail={sessionEmail}
        />

        <div className={`adm-page-scroll${page === "usage" ? " adm-page-scroll--usage" : ""}`} style={{flex:1,overflow:"auto",padding:"20px 24px"}}>
          {loading && !adminReady && page !== "calendar" && (
            <div style={{ textAlign: "center", padding: 40, color: adminTextSoft, fontSize: 13 }}>Loading admin data…</div>
          )}

          {adminError && !adminReady && (
            <div style={{marginBottom:16,padding:"12px 16px",background:"rgba(220,38,38,0.12)",border:"1px solid rgba(220,38,38,0.25)",borderRadius:10,fontSize:13,color:"#FCA5A5",lineHeight:1.5}}>
              {adminError}
            </div>
          )}
          {adminError && adminReady && (
            <div style={{marginBottom:16,padding:"10px 14px",background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:10,fontSize:12,color:"#FCA5A5",lineHeight:1.5}}>
              Some admin data could not refresh — showing last loaded data. {adminError}
            </div>
          )}

          {adminReady && adminTabSeen("overview") && (
            <AdminTabPanel active={page === "overview"} className="apage adm-premium-page adm-org-form">
              <AdminControlBanner
                operational={
                  status
                    ? status?.recall?.ok !== false &&
                      status?.smtp?.ok !== false &&
                      status?.database?.ok !== false
                    : true
                }
                title="Avaada control center"
                desc="Live snapshot of meetings, users, and secured platform services."
              />
              <div
                className="adm-kpi-grid adm-kpi-grid--4 admin-overview-stat-grid"
                style={{ marginBottom: 16 }}
              >
                <AdminMetricCard
                  tone="teal"
                  icon={<Svg d={IC.meet} size={18} color="currentColor" stroke={2} />}
                  value={adminMeetingsLoading && !status?.totalMeetings ? "…" : (status?.totalMeetings ?? allMeetings.length)}
                  label="Total meetings · all users"
                  trend={overviewWeekOverWeekTrend(allMeetings, "count")}
                  series={overviewSparkFromMeetings(allMeetings, "count")}
                  sparkColor={ADM.teal}
                />
                <AdminMetricCard
                  tone="rose"
                  icon={<Svg d={IC.clock} size={18} color="currentColor" stroke={2} />}
                  value={adminMeetingsLoading && allMeetings.length === 0 ? "…" : liveMeetings.length}
                  label="Live now · active sessions"
                  sublabel={liveMeetings.length > 0 ? undefined : "No active sessions"}
                  trend={liveMeetings.length > 0 ? "Live" : undefined}
                  series={overviewSparkFromMeetings(allMeetings, "live")}
                  sparkColor={ADM.rose}
                  showSpark={liveMeetings.length > 0 || overviewSparkFromMeetings(allMeetings, "live").some((n) => n > 0)}
                />
                <AdminMetricCard
                  tone="blue"
                  icon={<Svg d={IC.users} size={18} color="currentColor" stroke={2} />}
                  value={status?.totalUsers ?? allUsers.length}
                  label="Total users · participants"
                  trend={overviewWeekOverWeekTrend(allUsers, "users")}
                  series={overviewSparkFromMeetings(allUsers, "users")}
                />
                <AdminMetricCard
                  tone="amber"
                  icon={<Svg d={IC.mail} size={18} color="currentColor" stroke={2} />}
                  value={
                    adminMeetingsLoading && allMeetings.length === 0
                      ? "…"
                      : allMeetings.filter((m) => m.emailSent || m.email_sent).length
                  }
                  label="Emails sent · delivered"
                  trend={overviewWeekOverWeekTrend(allMeetings, "email")}
                  series={overviewSparkFromMeetings(allMeetings, "email")}
                  sparkColor={ADM.amber}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
                  gap: 14,
                  alignItems: "start",
                }}
                className="admin-overview-split adm-side-grid"
              >
                <AdminListCard
                  title="Recent MeetAI meetings"
                  actionLabel="View all"
                  onAction={() => goAdminPage("allMeetings")}
                  toolbar={
                    <AdminSearchToolbar
                      value={overviewAdminSearch}
                      onChange={(arg) => setAdminTabSearch("overview", arg)}
                      placeholder="Search recent meetings by title, owner, participant…"
                      adminTextMuted={ADM.textMuted}
                      filteredCount={filteredRecentFive.length}
                      totalCount={recentFive.length}
                    />
                  }
                >
                  <div style={{ maxHeight: 360, overflowY: "auto" }}>
                    {filteredRecentFive.length === 0 ? (
                      <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 12.5, color: ADM.textMuted }}>
                        {adminMeetingsLoading && recentFive.length === 0
                          ? "Loading recent meetings…"
                          : recentFive.length === 0
                            ? "No MeetAI meetings yet."
                            : "No recent meetings match your search."}
                      </div>
                    ) : (
                      filteredRecentFive.map((m) => {
                        const host = meetingHostName(m) || "—";
                        const mins = meetingDurationMinutes(m);
                        const chip = overviewMeetingChip(m);
                        const timeRange = formatMeetingTimeRangeIst(m);
                        const dateLabel = m.date ? formatYmdDisplay(m.date) : "—";
                        const meta = [host, timeRange || null, `${mins || 0} min`, dateLabel]
                          .filter(Boolean)
                          .join(" · ");
                        const initials = String(host)
                          .split(/\s+/)
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((p) => p[0])
                          .join("")
                          .toUpperCase() || "M";
                        return (
                          <AdminMeetingRow
                            key={m._id || m.id}
                            initials={initials}
                            title={m.title || "Untitled Meeting"}
                            meta={meta}
                            chipLabel={chip.label}
                            chipVariant={chip.variant}
                          />
                        );
                      })
                    )}
                  </div>
                </AdminListCard>
                <AdminListCard title="System status" actionLabel="Details">
                  {[
                    { label: "MeetAI API", ok: status?.recall?.ok, detail: status?.recall?.ok ? "Secured endpoint healthy" : undefined },
                    { label: "SMTP", ok: status?.smtp?.ok, detail: status?.smtp?.ok ? "Authenticated mail relay" : undefined },
                    { label: "Summary AI", ok: status?.claude?.ok, detail: status?.claude?.ok ? "Provider key configured" : undefined },
                    { label: "Database", ok: status?.database?.ok ?? (status ? true : undefined), detail: "Encrypted connection pool" },
                    { label: "Webhook Listener", ok: status ? true : undefined, detail: "Signed webhook verification" },
                    { label: "Email Queue", ok: status ? true : undefined, detail: "Queued delivery with retry" },
                  ].map((row) => (
                    <AdminSystemRow key={row.label} label={row.label} detail={row.detail} ok={row.ok} />
                  ))}
                </AdminListCard>
              </div>
              <style>{`
                @media (max-width: 960px) {
                  .admin-overview-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
                  .admin-overview-split { grid-template-columns: 1fr !important; }
                }
              `}</style>
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("chatbotUsage") && (
            <AdminTabPanel active={page === "chatbotUsage"} className="apage adm-premium-page adm-org-form">
            <AdminChatbotUsagePanel
              report={assistantUsageApi}
              isLight={isLight}
              adminTextMain={adminTextMain}
              adminTextMuted={adminTextMuted}
              adminTextSoft={adminTextSoft}
              adminBorder={adminBorder}
              adminChipBg={adminChipBg}
              adminPanelBg={adminPanelBg}
            />
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("usage") && (
            <AdminTabPanel active={page === "usage"} className="apage adm-premium-page adm-org-form adm-usage-redesign">
              <div className="adm-stack">
              <UsagePageHeader
                dateRangeLabel={usageHeaderRangeLabel}
                usageRange={usageRange}
                setUsageRange={applyUsagePeriod}
                dateFrom={usageDateFrom}
                dateTo={usageDateTo}
                onDateFromChange={onUsageDateFromChange}
                onDateToChange={onUsageDateToChange}
                calendarIcon={<Svg d={IC.cal} size={15} color="#64748b" stroke={2} />}
              />
              {usageApiLoading && usageApi ? (
                <div className="ub-inline-refresh-note" role="status">
                  Refreshing usage…
                </div>
              ) : null}
              {usageApiErr && !usageApi ? (
                <div
                  className="adm-card"
                  style={{
                    padding: "12px 16px",
                    borderColor: "rgba(220,38,38,0.35)",
                    background: isLight ? "rgba(254,242,242,0.88)" : "rgba(127,29,29,0.18)",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#DC2626" }}>
                    Usage API data is not loaded
                  </div>
                  <div style={{ marginTop: 5, fontSize: 12.5, color: adminTextMuted, lineHeight: 1.55 }}>
                    {usageApiErr
                      ? usageApiErr
                      : "The browser has not received /api/admin/usage yet. Recall status, pricing, MCP tools, and credits below may show placeholders until the backend is running and restarted with backend/.env loaded."}
                  </div>
                </div>
              ) : usageApi && usageApiErr ? (
                <div
                  className="adm-card ub-inline-alert ub-inline-alert--warn"
                  style={{
                    padding: "10px 14px",
                    borderColor: "rgba(217,119,6,0.35)",
                    background: isLight ? "rgba(255,251,235,0.92)" : "rgba(120,53,15,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontSize: 12.5, color: adminTextMuted, lineHeight: 1.45, minWidth: 0 }}>
                    <strong style={{ color: "#B45309" }}>Billing API temporarily unavailable</strong>
                    {" — showing last saved values"}
                    {usageApi?._cachedAt ? ` (${new Date(usageApi._cachedAt).toLocaleString()})` : ""}
                  </div>
                  <button type="button" className="ub-btn-secondary ub-btn-secondary--sm" onClick={() => setAdminReloadTick((v) => v + 1)}>
                    Retry
                  </button>
                </div>
              ) : usageApiLoading && !usageApi && !SHOW_RECALL_MCP_USAGE ? (
                <div className="adm-card" style={{ padding: "12px 16px", fontSize: 12.5, color: adminTextMuted }}>
                  Loading usage, Recall, and MCP status…
                </div>
              ) : usageApiLoading && usageApi && recallMcpHasRealBilling ? (
                <div style={{ fontSize: 11.5, color: adminTextSoft, padding: "0 4px" }}>
                  Updating with live data…
                  {usageApi?._cachedAt ? ` (last saved ${new Date(usageApi._cachedAt).toLocaleString()})` : ""}
                </div>
              ) : null}

              {usageApiLoading && !usageApi ? (
                <UsageKpiSkeletonRow />
              ) : (
              <div className="ub-kpi-row" aria-label="Key usage metrics">
                <UsageKpiCard
                  accent="mint"
                  icon={<Svg d={IC.zap} size={17} color="currentColor" stroke={2} />}
                  title="Remaining Credits"
                  value={
                    recallMcpBillingLoading
                      ? "…"
                      : recallMcpCreditUsd == null
                        ? "—"
                        : `$${recallMcpCreditUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                  }
                  sublabel={recallMcpHasRealBilling ? "Live via Recall MCP" : recallMcpCreditSublabel}
                  onClick={
                    recallMcpHasRealBilling
                      ? () => {
                          setRecallCreditHistoryOpenedAt(new Date());
                          setRecallCreditHistoryOpen(true);
                        }
                      : undefined
                  }
                  onHistoryClick={
                    recallMcpHasRealBilling
                      ? () => {
                          setRecallCreditHistoryOpenedAt(new Date());
                          setRecallCreditHistoryOpen(true);
                        }
                      : undefined
                  }
                  historyIcon={<Svg d={IC.history} size={12} color="currentColor" stroke={1.75} />}
                  titleAttr="Open credit balance history"
                />
                <UsageKpiCard
                  accent="blue"
                  icon={<Svg d={IC.meet} size={17} color="currentColor" stroke={2} />}
                  title="MeetAI Sessions"
                  value={Number(rangeTotals.meetings || 0).toLocaleString()}
                  sublabel="MeetAI dispatched"
                  trend={usageKpiSessionsTrend}
                />
                <UsageKpiCard
                  accent="purple"
                  icon={<Svg d={IC.clock} size={17} color="currentColor" stroke={2} />}
                  title="Local Minutes"
                  value={Number(rangeTotals.minutes || 0).toLocaleString()}
                  sublabel={usageLocalDurationSublabel.replace(/ · MeetAI DB.*$/i, "").trim()}
                />
                <UsageKpiCard
                  accent="orange"
                  icon={<Svg d={IC.mic} size={17} color="currentColor" stroke={2} />}
                  title="Transcript Rows"
                  value={Number(rangeTotals.transcriptRows || 0).toLocaleString()}
                  sublabel="Captured transcript lines"
                />
                <UsageKpiCard
                  accent="rose"
                  icon={<Svg d={IC.task} size={17} color="currentColor" stroke={2} />}
                  title="Estimated Cost (USD)"
                  value={usageKpiEstimatedCost.display}
                  sublabel={
                    /this month/i.test(usageKpiEstimatedCost.sub)
                      ? "This month"
                      : usageKpiEstimatedCost.sub
                  }
                />
              </div>
              )}

              {recallMcpHasRealBilling ? (
              <div className="ub-hero" style={{ display: "none" }} aria-hidden>
                <div
                  className="ub-hero-primary ub-hero-primary--tiered"
                  style={{
                    background: recallCreditHero.cardBg,
                    border: `1px solid ${recallCreditHero.border}`,
                    color: recallCreditHero.valueColor,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <span
                      className="ub-hero-badge"
                      style={{
                        borderColor: recallCreditHero.badgeBorder,
                        background: recallCreditHero.badgeBg,
                        color: recallCreditHero.badgeText,
                      }}
                    >
                      <span className="ub-hero-badge-dot" style={{ background: realBillingAlertStatus.color }} />
                      {realBillingAlertStatus.label}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      <button
                        type="button"
                        aria-label="Open credit balance history"
                        title="Credit balance history"
                        onClick={() => {
                          setRecallCreditHistoryOpenedAt(new Date());
                          setRecallCreditHistoryOpen(true);
                        }}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          border: `1px solid ${recallCreditHero.badgeBorder}`,
                          background: recallCreditHero.badgeBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          color: recallCreditHero.badgeText,
                        }}
                      >
                        <Svg d={IC.history} size={20} color={recallCreditHero.iconFg} stroke={2} />
                      </button>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: recallCreditHero.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        aria-hidden
                      >
                        <Svg d={IC.zap} size={20} color={recallCreditHero.iconFg} stroke={2} />
                      </div>
                    </div>
                  </div>
                  <div
                    className="ub-hero-sub"
                    style={{
                      marginTop: 12,
                      color: recallCreditHero.titleColor,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      fontSize: 11,
                    }}
                  >
                    Remaining Credits
                  </div>
                  <div className="ub-hero-balance" style={{ color: recallCreditHero.valueColor }}>
                    {recallMcpCreditUsd == null ? "—" : `$${recallMcpCreditUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                  </div>
                  <div className="ub-hero-sub" style={{ color: recallCreditHero.subColor }}>
                    Live via Recall MCP get_billed_usage · {recallMcpCreditSublabel}
                  </div>
                  <div className="ub-hero-stats" style={{ borderTopColor: recallCreditHero.divider }}>
                    <div className="ub-hero-stat">
                      <div className="ub-hero-stat-label" style={{ color: recallCreditHero.statLabel }}>Billed Bot Usage</div>
                      <div className="ub-hero-stat-value" style={{ color: recallCreditHero.statValue }}>
                        {recallMcpBotUsageHours == null ? "—" : `${Number(recallMcpBotUsageHours).toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`}
                      </div>
                    </div>
                    <div className="ub-hero-stat" style={{ borderLeftColor: recallCreditHero.divider }}>
                      <div className="ub-hero-stat-label" style={{ color: recallCreditHero.statLabel }}>Storage Usage</div>
                      <div className="ub-hero-stat-value" style={{ color: recallCreditHero.statValue }}>
                        {recallMcpStorageHours == null ? "—" : `${Number(recallMcpStorageHours).toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`}
                      </div>
                    </div>
                    <div className="ub-hero-stat" style={{ borderLeftColor: recallCreditHero.divider }}>
                      <div className="ub-hero-stat-label" style={{ color: recallCreditHero.statLabel }}>Last MCP Update</div>
                      <div className="ub-hero-stat-value" style={{ fontSize: 14, color: recallCreditHero.statValue }}>{recallMcpLastUpdatedLabel}</div>
                    </div>
                  </div>
                </div>
                <div className="ub-hero-side">
                  <div className="adm-card ub-side-card">
                    <div className="ub-side-title">Alert thresholds</div>
                    <div className="ub-side-sub">
                      Email admins once per level ($20 · $15 · $10 · $5 · $2) until balance &gt; $20
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {(usageApi?.recallCreditAlerts?.thresholds || [20, 15, 10, 5, 2]).map((t) => {
                        const breached = recallMcpCreditUsd != null && recallMcpCreditUsd <= t;
                        const emailedAt = usageApi?.recallCreditAlerts?.sent?.[String(t)];
                        const dot = breached ? (t <= 10 ? "#b0403f" : "#c98a2c") : "#0f9d8f";
                        let stateLabel = "OK";
                        if (recallMcpCreditUsd == null) stateLabel = "Unknown";
                        else if (emailedAt) stateLabel = "Emailed";
                        else if (breached) stateLabel = "Breached";
                        return (
                          <div key={t} className="ub-threshold-row">
                            <span className="ub-threshold-dot" style={{ background: dot }} />
                            <span className="ub-threshold-val">${t}</span>
                            <span
                              className="ub-threshold-state"
                              style={{ color: dot }}
                              title={emailedAt ? `Sent ${new Date(emailedAt).toLocaleString()}` : undefined}
                            >
                              {stateLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="adm-card ub-side-card">
                    <div className="ub-side-title">This month</div>
                    <div className="ub-side-sub">
                      {(() => {
                        const d = new Date();
                        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                        const cur = (usageMonths || []).find((m) => m.month === k);
                        return cur ? `Session & minute totals · ${cur.month}` : "Session & minute totals";
                      })()}
                    </div>
                    {(() => {
                      const d = new Date();
                      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                      const cur = (usageMonths || []).find((m) => m.month === k);
                      const sess = cur ? cur.meetings : usageTotals.totalMeetings;
                      const mins = cur ? cur.minutes : usageTotals.totalMinutes;
                      return (
                        <div className="ub-mini-stats">
                          <div>
                            <div className="ub-mini-value ub-num">{Number(sess || 0).toLocaleString()}</div>
                            <div className="ub-mini-label">Sessions</div>
                          </div>
                          <div>
                            <div className="ub-mini-value ub-num">{Number(mins || 0).toLocaleString()}</div>
                            <div className="ub-mini-label">Minutes</div>
                          </div>
                          <div>
                            <div className="ub-mini-value ub-num">{Number(usageTotals.totalTranscriptRows || 0).toLocaleString()}</div>
                            <div className="ub-mini-label">Transcript rows</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              ) : recallMcpBillingLoading ? (
              <div className="ub-hero" style={{ display: "none" }} aria-hidden>
                <div
                  className="ub-hero-primary ub-hero-primary--tiered ub-hero-primary--loading"
                  style={{
                    background: recallCreditHeroLoading.cardBg,
                    border: `1px solid ${recallCreditHeroLoading.border}`,
                    color: recallCreditHeroLoading.valueColor,
                  }}
                  aria-busy="true"
                  aria-label="Loading Recall MCP billing"
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <span
                      className="ub-hero-badge"
                      style={{
                        borderColor: recallCreditHeroLoading.badgeBorder,
                        background: recallCreditHeroLoading.badgeBg,
                        color: recallCreditHeroLoading.badgeText,
                      }}
                    >
                      <span className="ub-hero-badge-dot" style={{ background: "#0f9d8f" }} />
                      Loading Recall MCP…
                    </span>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: recallCreditHeroLoading.iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      <Svg d={IC.zap} size={20} color={recallCreditHeroLoading.iconFg} stroke={2} />
                    </div>
                  </div>
                  <div
                    className="ub-hero-sub"
                    style={{
                      marginTop: 12,
                      color: recallCreditHeroLoading.titleColor,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      fontSize: 11,
                    }}
                  >
                    Remaining Credits
                  </div>
                  <div className="ub-hero-balance" style={{ color: recallCreditHeroLoading.valueColor }}>
                    …
                  </div>
                  <div className="ub-hero-sub" style={{ color: recallCreditHeroLoading.subColor }}>
                    Fetching live credits &amp; billed usage from Recall MCP get_billed_usage
                  </div>
                  <div className="ub-hero-stats" style={{ borderTopColor: recallCreditHeroLoading.divider }}>
                    <div className="ub-hero-stat">
                      <div className="ub-hero-stat-label" style={{ color: recallCreditHeroLoading.statLabel }}>Billed Bot Usage</div>
                      <div className="ub-hero-stat-value" style={{ color: recallCreditHeroLoading.statValue }}>—</div>
                    </div>
                    <div className="ub-hero-stat" style={{ borderLeftColor: recallCreditHeroLoading.divider }}>
                      <div className="ub-hero-stat-label" style={{ color: recallCreditHeroLoading.statLabel }}>Storage Usage</div>
                      <div className="ub-hero-stat-value" style={{ color: recallCreditHeroLoading.statValue }}>—</div>
                    </div>
                    <div className="ub-hero-stat" style={{ borderLeftColor: recallCreditHeroLoading.divider }}>
                      <div className="ub-hero-stat-label" style={{ color: recallCreditHeroLoading.statLabel }}>Last MCP Update</div>
                      <div className="ub-hero-stat-value" style={{ fontSize: 14, color: recallCreditHeroLoading.statValue }}>—</div>
                    </div>
                  </div>
                </div>
                <div className="ub-hero-side">
                  <div className="adm-card ub-side-card" style={{ opacity: 0.72 }}>
                    <div className="ub-side-title">Alert thresholds</div>
                    <div className="ub-side-sub">Loading MCP balance…</div>
                  </div>
                  <div className="adm-card ub-side-card" style={{ opacity: 0.72 }}>
                    <div className="ub-side-title">This month</div>
                    <div className="ub-side-sub">Session totals appear after usage API loads</div>
                  </div>
                </div>
              </div>
              ) : (
              <div className="adm-card" style={{ padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: adminTextMain }}>Manual budget tracker</div>
                      <button
                        type="button"
                        onClick={() => {
                          setBudgetHistoryOpen(true);
                          loadBudgetHistory();
                        }}
                        title="View history of target budget changes"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          borderRadius: 7,
                          border: `1px solid ${adminBorder}`,
                          background: adminChipBg,
                          color: adminTextMuted,
                          cursor: "pointer",
                        }}
                      >
                        <Svg d={IC.clock} size={13} color="currentColor" stroke={2} />
                      </button>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: adminTextMuted, lineHeight: 1.5 }}>
                      Manual tracker — set your own target below; it tracks spend against it (Recall billing API when connected) but is <strong>not synced from Recall automatically</strong>. <strong>Does not block recording</strong> — alerts only at <strong>75% · 90% · 100%</strong> to admin emails. Not the same as your Recall prepaid credit balance.
                    </div>
                    {usageBudget?.updatedAt ? (
                      <div style={{ marginTop: 6, fontSize: 11, color: adminTextSoft }}>
                        Target set on <strong style={{ color: adminTextMuted }}>{new Date(usageBudget.updatedAt).toLocaleString()}</strong>
                        {usageBudget.updatedBy ? <> by <strong style={{ color: adminTextMuted }}>{usageBudget.updatedBy}</strong></> : null}
                      </div>
                    ) : null}
                  </div>
                  {usageBudget ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: "0.04em",
                        padding: "5px 10px",
                        borderRadius: 999,
                        border: `1px solid ${adminBorder}`,
                        background: adminChipBg,
                        color:
                          usageBudget.healthStatus === "exceeded"
                            ? "#FB7185"
                            : usageBudget.healthStatus === "critical"
                            ? "#FB923C"
                            : usageBudget.healthStatus === "warning"
                            ? "#38BDF8"
                            : usageBudget.healthStatus === "ok"
                            ? "#4ADE80"
                            : adminTextMuted,
                        textTransform: "uppercase",
                      }}
                    >
                      {String(usageBudget.healthStatus || "unset").replace(/_/g, " ")}
                    </span>
                  ) : null}
                </div>
                {usageApi?.budget?.error ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#F87171" }}>Budget data unavailable: {String(usageApi.budget.error)}</div>
                ) : null}
                {budgetErr ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#F87171" }}>{budgetErr}</div>
                ) : null}
                {usageBudget?.controlledByEnv ? (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "8px 10px",
                      borderRadius: 9,
                      fontSize: 12,
                      fontWeight: 600,
                      color: isLight ? "#1d4ed8" : "#BFDBFE",
                      background: isLight ? "rgba(37,99,235,0.10)" : "rgba(37,99,235,0.16)",
                      border: isLight ? "1px solid rgba(37,99,235,0.20)" : "1px solid rgba(147,197,253,0.26)",
                    }}
                  >
                    Budget is controlled by <strong>USAGE_BUDGET_USD_MONTHLY</strong> in <code>backend/.env</code>{" "}
                    (currently <strong>${Number(usageBudget.envBudgetUsd ?? 0).toFixed(2)}</strong>). Edit the value
                    and restart the backend to change it. Alert flags reset automatically when the env value changes.
                  </div>
                ) : null}
                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "end" }}>
                  <div>
                    {(() => {
                      // The tracking window resets to "now" every time the target is saved (so a
                      // mid-month top-up doesn't retroactively count prior spend) — if that reset
                      // point isn't the 1st of the month, "September 2026" alone reads as if the
                      // whole month is being measured, when only the window since the reset is.
                      const cycleYmd = usageBudget?.cycleStartAt ? String(usageBudget.cycleStartAt).slice(0, 10) : null;
                      const startsAtMonthBegin = !cycleYmd || cycleYmd === usageBudget?.periodStart;
                      return (
                        <div style={{ fontSize: 11, color: adminTextMuted, marginBottom: 4 }}>
                          Target budget (USD) · {usageBudget?.periodLabel || "—"}
                          {!startsAtMonthBegin && usageBudget?.cycleStartAt ? (
                            <span style={{ color: "#F59E0B" }}> (tracking since {new Date(usageBudget.cycleStartAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}, not the 1st)</span>
                          ) : null}
                        </div>
                      );
                    })()}
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={budgetInput}
                      disabled={!!usageBudget?.controlledByEnv}
                      onChange={(e) => setBudgetInput(e.target.value)}
                      style={{
                        width: "100%",
                        maxWidth: 200,
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: `1px solid ${adminBorder}`,
                        background: usageBudget?.controlledByEnv ? (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)") : adminPanelBg,
                        color: adminTextMain,
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: usageBudget?.controlledByEnv ? "not-allowed" : "text",
                        opacity: usageBudget?.controlledByEnv ? 0.7 : 1,
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button
                    type="button"
                    disabled={budgetSaving || !!usageBudget?.controlledByEnv}
                    onClick={saveUsageBudget}
                    title={usageBudget?.controlledByEnv ? "Budget is controlled by USAGE_BUDGET_USD_MONTHLY in .env" : undefined}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: "none",
                      background: "#1F3D7A",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 12.5,
                      cursor: budgetSaving || usageBudget?.controlledByEnv ? "not-allowed" : "pointer",
                      opacity: budgetSaving || usageBudget?.controlledByEnv ? 0.6 : 1,
                    }}
                  >
                    Save target
                  </button>
                </div>
                {usageBudget ? (
                  <>
                    <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", fontSize: 12.5, color: adminTextMuted }}>
                      <span>
                        Spent:{" "}
                        <strong style={{ color: adminTextMain }}>
                          ${Number(usageBudget.spentUsd || 0).toFixed(2)}
                        </strong>{" "}
                        of{" "}
                        <strong style={{ color: adminTextMain }}>
                          ${Number(usageBudget.budgetUsd || 0).toFixed(2)}
                        </strong>
                      </span>
                      <span>
                        Remaining:{" "}
                        <strong style={{ color: adminTextMain }}>${Number(usageBudget.remainingUsd || 0).toFixed(2)}</strong>
                        {" · "}
                        {Math.round(Number(usageBudget.percent) || 0)}%
                      </span>
                    </div>
                    <div style={{ marginTop: 8, height: 10, borderRadius: 999, background: isLight ? "#E2E8F0" : "rgba(255,255,255,0.1)", overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${budgetBarPct}%`,
                          height: "100%",
                          borderRadius: 999,
                          background:
                            usageBudget.healthStatus === "paused" || usageBudget.healthStatus === "exceeded"
                              ? "#DC2626"
                              : usageBudget.healthStatus === "critical"
                              ? "#EA580C"
                              : usageBudget.healthStatus === "warning"
                              ? "#0284C7"
                              : "#16A34A",
                          transition: "width 0.25s ease",
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 10, fontSize: 11.5, color: adminTextSoft, lineHeight: 1.55 }}>
                      Tracking since{" "}
                      <strong style={{ color: adminTextMuted }}>
                        {usageBudget.cycleStartAt
                          ? new Date(usageBudget.cycleStartAt).toLocaleString()
                          : "—"}
                      </strong>{" "}
                      · Next period starts{" "}
                      <strong style={{ color: adminTextMuted }}>{usageBudget.nextResetLabel || "—"}</strong>{" "}
                      ({formatYmdDisplay(usageBudget.nextResetYmd || "")}) · Minutes:{" "}
                      <strong style={{ color: adminTextMuted }}>{Math.round(Number(usageBudget.minutes) || 0)}</strong>
                      {" · "}
                      Spend source:{" "}
                      <strong style={{ color: adminTextMuted }}>
                        {usageBudget.spendSource === "recall_api"
                          ? "Recall billing API"
                          : "Local duration estimate"}
                      </strong>
                      {usageBudget.spendSource === "recall_api" &&
                      usageBudget.localEstimateUsd != null ? (
                        <span>
                          {" "}
                          (local est. ${Number(usageBudget.localEstimateUsd || 0).toFixed(2)})
                        </span>
                      ) : null}
                    </div>
                    <div style={{ marginTop: 10, fontSize: 11, color: adminTextSoft }}>
                      Alerts sent: 75% {usageBudget.alerts?.pct75 ? "· yes" : "· no"}
                      {usageBudget.alerts?.pct75At ? ` (${new Date(usageBudget.alerts.pct75At).toLocaleString()})` : ""} · 90%{" "}
                      {usageBudget.alerts?.pct90 ? "· yes" : "· no"}
                      {usageBudget.alerts?.pct90At ? ` (${new Date(usageBudget.alerts.pct90At).toLocaleString()})` : ""} · 100%{" "}
                      {usageBudget.alerts?.pct100 ? "· yes" : "· no"}
                      {usageBudget.alerts?.pct100At ? ` (${new Date(usageBudget.alerts.pct100At).toLocaleString()})` : ""}
                    </div>
                  </>
                ) : null}
              </div>
              )}

              <div style={{ display: "none" }} />

              {recallCreditHistoryOpen ? (
                <div
                  role="presentation"
                  onClick={() => setRecallCreditHistoryOpen(false)}
                  style={{
                    position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.46)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
                  }}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Recall credit balance history"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%", maxWidth: 520, maxHeight: "82vh", display: "flex", flexDirection: "column",
                      background: adminPanelBg, border: `1px solid ${adminBorder}`, borderRadius: 14,
                      boxShadow: "0 20px 50px rgba(0,0,0,0.25)", overflow: "hidden",
                    }}
                  >
                    <div style={{ padding: "14px 16px", borderBottom: `1px solid ${adminBorder}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: adminTextMain }}>Recall credits history</div>
                        <div style={{ marginTop: 4, fontSize: 11.5, color: adminTextSoft, lineHeight: 1.45 }}>
                          {recallCreditHistoryOpenedAt
                            ? `Opened ${recallCreditHistoryOpenedAt.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`
                            : "Movement log and MCP polls"}
                          {recallMcpCreditUsd != null ? (
                            <span>
                              {" · "}
                              Current balance <strong style={{ color: adminTextMain }}>${recallMcpCreditUsd.toFixed(2)}</strong>
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={refreshRecallCreditsNow}
                          disabled={recallCreditRefreshing}
                          title="Force a live Recall MCP fetch now (captures a just-made top-up immediately)"
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "6px 11px", borderRadius: 8, cursor: recallCreditRefreshing ? "wait" : "pointer",
                            background: isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)",
                            border: `1px solid ${isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"}`,
                            color: adminTextMain, fontSize: 12, fontWeight: 650, fontFamily: "inherit",
                            opacity: recallCreditRefreshing ? 0.7 : 1,
                          }}
                        >
                          <span style={{ display: "inline-flex", animation: recallCreditRefreshing ? "spin 1s linear infinite" : "none" }}>
                            <Svg d={IC.refresh} size={13} color="currentColor" stroke={2} />
                          </span>
                          {recallCreditRefreshing ? "Refreshing…" : "Refresh now"}
                        </button>
                        <button type="button" onClick={() => setRecallCreditHistoryOpen(false)} aria-label="Close" style={{ border: "none", background: "transparent", color: adminTextMuted, cursor: "pointer", padding: 4 }}>
                          <Svg d={IC.close} size={16} color="currentColor" />
                        </button>
                      </div>
                    </div>
                    {recallCreditRefreshErr ? (
                      <div style={{ padding: "8px 16px", fontSize: 12, color: "#B91C1C", background: isLight ? "#FEF2F2" : "rgba(185,28,28,0.12)", borderBottom: `1px solid ${adminBorder}` }}>
                        {recallCreditRefreshErr}
                      </div>
                    ) : null}
                    <div style={{ padding: "10px 16px", overflowY: "auto", flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: adminTextSoft, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                        Credit movements ($5+)
                      </div>
                      {mcpValueEvents.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: adminTextSoft, padding: "6px 0 14px", lineHeight: 1.5 }}>
                          No ledger rows yet. The backend records a row when MCP reports a balance change of at least $5 (top-up, usage drop, or first baseline).
                        </div>
                      ) : (
                        mcpValueEvents.map((ev, i) => {
                          const type = String(ev.eventType || "baseline");
                          const typeLabel =
                            type === "topup" ? "Top-up" : type === "drop" ? "Spent / usage" : "Baseline";
                          const typeColor =
                            type === "topup" ? "#0f9d8f" : type === "drop" ? "#b0403f" : "#6b7688";
                          const when = ev.fetchedAt
                            ? new Date(ev.fetchedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                            : "—";
                          const bal = ev.creditBalanceUsd;
                          const prev = ev.previousBalanceUsd;
                          const delta = ev.deltaUsd != null ? Number(ev.deltaUsd) : null;
                          return (
                            <div key={`mcp-ev-${ev.fetchedAt}-${i}`} style={{ padding: "10px 0", borderBottom: `1px solid ${adminBorder}` }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${typeColor}18`, color: typeColor }}>
                                  {typeLabel}
                                </span>
                                <span style={{ fontSize: 11.5, color: adminTextSoft }}>{when}</span>
                              </div>
                              <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: adminTextMain }}>
                                {prev != null ? (
                                  <>
                                    ${Number(prev).toFixed(2)} → ${Number(bal).toFixed(2)}
                                  </>
                                ) : (
                                  <>Balance ${Number(bal).toFixed(2)}</>
                                )}
                                {delta != null && delta !== 0 ? (
                                  <span style={{ fontWeight: 650, color: delta > 0 ? "#0f9d8f" : "#b0403f", marginLeft: 8 }}>
                                    {delta > 0 ? "+" : ""}${delta.toFixed(2)}
                                  </span>
                                ) : null}
                              </div>
                              {ev.billedHours != null ? (
                                <div style={{ marginTop: 3, fontSize: 11.5, color: adminTextSoft }}>
                                  Billed bot usage at poll: {Number(ev.billedHours).toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                      <div style={{ fontSize: 11, fontWeight: 700, color: adminTextSoft, textTransform: "uppercase", letterSpacing: "0.05em", margin: "16px 0 8px" }}>
                        MCP balance polls
                      </div>
                      {recallCreditSnapshotHistory.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: adminTextSoft, padding: "6px 0", lineHeight: 1.5 }}>
                          No stored snapshots with a credit balance yet (saved when Admin Usage polls MCP).
                        </div>
                      ) : (
                        recallCreditSnapshotHistory.slice(0, 40).map((row, i) => {
                          const when = new Date(row.fetchedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
                          return (
                            <div key={`mcp-snap-${row.fetchedAt}-${i}`} style={{ padding: "8px 0", borderBottom: i < Math.min(recallCreditSnapshotHistory.length, 40) - 1 ? `1px solid ${adminBorder}` : "none" }}>
                              <div style={{ fontSize: 11.5, color: adminTextSoft }}>{when}</div>
                              <div style={{ marginTop: 3, fontSize: 12.5, fontWeight: 650, color: adminTextMain }}>
                                ${row.creditBalanceUsd.toFixed(2)}
                                {row.deltaUsd != null && row.deltaUsd !== 0 ? (
                                  <span style={{ marginLeft: 8, fontWeight: 600, color: row.deltaUsd > 0 ? "#0f9d8f" : "#b0403f" }}>
                                    {row.deltaUsd > 0 ? "+" : ""}${row.deltaUsd.toFixed(2)} vs prior poll
                                  </span>
                                ) : (
                                  <span style={{ marginLeft: 8, fontWeight: 500, color: adminTextSoft }}>first snapshot in window</span>
                                )}
                              </div>
                              {row.billedHours != null && row.billedHours > 0 ? (
                                <div style={{ marginTop: 2, fontSize: 11, color: adminTextSoft }}>
                                  {Number(row.billedHours).toLocaleString(undefined, { maximumFractionDigits: 2 })} billed hrs
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {budgetHistoryOpen ? (
                <div
                  role="presentation"
                  onClick={() => setBudgetHistoryOpen(false)}
                  style={{
                    position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.46)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
                  }}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Target budget history"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%", maxWidth: 480, maxHeight: "80vh", display: "flex", flexDirection: "column",
                      background: adminPanelBg, border: `1px solid ${adminBorder}`, borderRadius: 14,
                      boxShadow: "0 20px 50px rgba(0,0,0,0.25)", overflow: "hidden",
                    }}
                  >
                    <div style={{ padding: "14px 16px", borderBottom: `1px solid ${adminBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: adminTextMain }}>Target budget history</div>
                      <button type="button" onClick={() => setBudgetHistoryOpen(false)} aria-label="Close" style={{ border: "none", background: "transparent", color: adminTextMuted, cursor: "pointer", padding: 4 }}>
                        <Svg d={IC.close} size={16} color="currentColor" />
                      </button>
                    </div>
                    <div style={{ padding: "10px 16px", overflowY: "auto", flex: 1 }}>
                      {budgetHistoryLoading ? (
                        <div style={{ fontSize: 12.5, color: adminTextMuted, padding: "10px 0" }}>Loading history…</div>
                      ) : budgetHistoryErr ? (
                        <div style={{ fontSize: 12.5, color: "#F87171", padding: "10px 0" }}>{budgetHistoryErr}</div>
                      ) : !budgetHistory || budgetHistory.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: adminTextSoft, padding: "10px 0" }}>No manual budget changes recorded yet — this only logs changes made from this screen.</div>
                      ) : (
                        budgetHistory.map((h, i) => (
                          <div key={h.id || i} style={{ padding: "10px 0", borderBottom: i < budgetHistory.length - 1 ? `1px solid ${adminBorder}` : "none" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: adminTextMain }}>
                              {h.previousUsd != null ? `$${h.previousUsd.toFixed(2)} → ` : "Set to "}
                              <span style={{ color: "#4ADE80" }}>${h.budgetUsd.toFixed(2)}</span>
                              <span style={{ fontWeight: 500, color: adminTextMuted }}> · {h.periodLabel}</span>
                            </div>
                            <div style={{ marginTop: 3, fontSize: 11.5, color: adminTextSoft }}>
                              {new Date(h.changedAt).toLocaleString()}{h.changedBy ? ` · ${h.changedBy}` : ""}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="ub-trend-layout">
              <div className="adm-card ub-trend-chart-card">
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:800,color:adminTextMain}}>Usage trend</div>
                    <div className="ub-trend-subtitle">Meetings, bot hours and billed cost (USD) over time</div>
                  </div>
                  <UsageChartSeriesToggles
                    showMeetings={usageChartShowMeetings}
                    showCost={usageChartShowCost}
                    showTranscript={usageChartShowTranscript}
                    showBotHours={usageChartShowBotHours}
                    onToggle={(key) => {
                      if (key === "meetings") setUsageChartShowMeetings((v) => !v);
                      if (key === "cost") setUsageChartShowCost((v) => !v);
                      if (key === "transcript") setUsageChartShowTranscript((v) => !v);
                      if (key === "botHours") setUsageChartShowBotHours((v) => !v);
                    }}
                  />
                </div>
                <div style={{marginTop:10,fontSize:12.5,color:adminTextMuted}}>
                  Meetings: <strong style={{color:"#93C5FD"}}>{rangeTotals.meetings}</strong> · Minutes: <strong style={{color:"#93C5FD"}}>{rangeTotals.minutes}</strong> · Transcript Lines: <strong style={{color:"#93C5FD"}}>{rangeTotals.transcriptRows}</strong>
                  {usageChartShowBotHours && hasMcpDaily ? (
                    <> · Bot Hours: <strong style={{color:"#C4B5FD"}}>{(rangeChartData.reduce((s, p) => s + (Number(p.botHours) || 0), 0)).toFixed(2)}</strong></>
                  ) : null}
                </div>
                {rangeInfoMessage ? (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 10px",
                      borderRadius: 9,
                      fontSize: 12,
                      fontWeight: 600,
                      color: isLight ? "#1d4ed8" : "#BFDBFE",
                      background: isLight ? "rgba(37,99,235,0.10)" : "rgba(37,99,235,0.16)",
                      border: isLight ? "1px solid rgba(37,99,235,0.20)" : "1px solid rgba(147,197,253,0.26)",
                    }}
                  >
                    {rangeInfoMessage}
                  </div>
                ) : null}
                <div className="ub-chart-panel" style={{marginTop:12,border:`1px solid ${adminBorder}`,borderRadius:10,padding:"12px 10px 8px",background:adminPanelBg}}>
                  <div className="ub-chart-axis-hint" style={{ fontSize: 10.5, fontWeight: 600, color: adminTextSoft, marginBottom: 4 }}>
                    {usageChartDayMode
                      ? "Hour of day"
                      : usageRange === "weekly"
                        ? "Day of week"
                        : usageRange === "monthly"
                          ? "Day of month"
                          : usageRange === "custom"
                            ? "Day in range"
                            : "Month"}
                  </div>
                  <AdminChartScroll points={rangeChartData.length} pointWidth={90}>
                  <ResponsiveContainer width="100%" height={300} className="ub-chart-responsive">
                    <ComposedChart data={rangeChartData} margin={{ top: 8, right: 12, left: 0, bottom: rangeChartData.length > 6 ? 8 : 4 }}>
                      <defs>
                        <linearGradient id="billedCostFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0f9d8f" stopOpacity={0.22} />
                          <stop offset="100%" stopColor="#0f9d8f" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={adminBorder} strokeDasharray="0" vertical={false} />
                       <XAxis
                          dataKey="label"
                          tick={{ fontSize: 9, fill: adminTextSoft }}
                          axisLine={{ stroke: adminBorder }}
                          tickLine={false}
                           interval={0}
                           angle={rangeChartData.length > 6 ? -40 : 0}
                           textAnchor={rangeChartData.length > 6 ? "end" : "middle"}
                           height={rangeChartData.length > 6 ? 50 : 30}
                        />
                      <YAxis
                        yAxisId="meetings"
                        hide={!usageChartShowMeetings}
                        tick={{ fontSize: 10, fill: adminTextSoft }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        domain={[0, (dataMax) => Math.max(4, dataMax)]}
                        width={36}
                        label={{
                          value: "Meetings",
                          angle: -90,
                          position: "insideLeft",
                          fontSize: 10.5,
                          fill: "#2563EB",
                          style: { textAnchor: "middle" },
                        }}
                      />
                      <YAxis
                        yAxisId="cost"
                        hide={!usageChartShowCost && !usageChartShowTranscript}
                        orientation="right"
                        tick={{ fontSize: 10, fill: adminTextSoft }}
                        axisLine={false}
                        tickLine={false}
                        domain={[0, (dataMax) => Math.max(1, dataMax)]}
                        tickFormatter={(v) => `$${Number(v) % 1 === 0 ? Number(v) : Number(v).toFixed(2)}`}
                        width={48}
                        label={{
                          value: "Cost (USD)",
                          angle: 90,
                          position: "insideRight",
                          fontSize: 10.5,
                          fill: "#0f9d8f",
                          style: { textAnchor: "middle" },
                        }}
                      />
                      <YAxis yAxisId="transcript" hide domain={[0, (dataMax) => Math.max(4, dataMax)]} />
                      <YAxis
                        yAxisId="hours"
                        hide={!usageChartShowBotHours}
                        orientation="right"
                        tick={{ fontSize: 10, fill: adminTextSoft }}
                        axisLine={false}
                        tickLine={false}
                        domain={[0, (dataMax) => Math.max(1, Math.ceil(Number(dataMax) || 0))]}
                        tickFormatter={(v) => `${Number(v)}h`}
                        width={44}
                        label={{
                          value: "Bot hours",
                          angle: 90,
                          position: "insideRight",
                          fontSize: 10.5,
                          fill: "#a855f7",
                          style: { textAnchor: "middle" },
                        }}
                      />
                      <RechartsLegend
                        wrapperStyle={{ fontSize: 11 }}
                        formatter={(value) => <span style={{ color: adminTextMuted }}>{value}</span>}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          background: isLight ? "#0F172A" : "#1E293B",
                          border: "none",
                          borderRadius: 8,
                          boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
                        }}
                        labelStyle={{ color: "#fff", fontWeight: 700, fontSize: 11.5, marginBottom: 2 }}
                        itemStyle={{ fontSize: 11.5 }}
                        formatter={(value, name) => {
                          if (name === "Billed cost (USD)" || name === "Billed cost") return [`$${Number(value).toFixed(2)}`, name === "Billed cost" ? "Billed cost (USD)" : name];
                          if (name === "Bot hours") return [`${Number(value).toFixed(2)}h`, name];
                          if (name === "Transcript lines") return [Number(value).toLocaleString(), name];
                          return [value, name];
                        }}
                      />
                      {usageChartShowCost ? (
                      <Area
                        yAxisId="cost"
                        dataKey={hasMcpActuals || hasMcpDaily ? "actualUsd" : "costUsd"}
                        name="Billed cost (USD)"
                        type="monotone"
                        stroke="#0f9d8f"
                        strokeWidth={1.5}
                        fill="url(#billedCostFill)"
                        connectNulls
                        dot={false}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: adminPanelBg, fill: "#0f9d8f" }}
                      />
                      ) : null}
                      {usageChartShowMeetings ? (
                      <Line
                        yAxisId="meetings"
                        dataKey="meetings"
                        name="Meetings"
                        type="monotone"
                        stroke="#2563EB"
                        strokeWidth={2.5}
                        dot={{ r: 3.5, strokeWidth: 2, stroke: adminPanelBg, fill: "#2563EB" }}
                        activeDot={{ r: 6, strokeWidth: 2, stroke: adminPanelBg, fill: "#1D4ED8" }}
                      />
                      ) : null}
                      {usageChartShowTranscript ? (
                      <Line
                        yAxisId="transcript"
                        dataKey="transcriptLines"
                        name="Transcript lines"
                        type="monotone"
                        stroke="#ea580c"
                        strokeWidth={1.75}
                        strokeDasharray="5 4"
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 2, stroke: adminPanelBg, fill: "#ea580c" }}
                      />
                      ) : null}
                      {usageChartShowBotHours ? (
                      <Line
                        yAxisId="hours"
                        dataKey="botHours"
                        name="Bot hours"
                        type="monotone"
                        stroke="#a855f7"
                        strokeWidth={2.5}
                        connectNulls
                        dot={{ r: 3.5, strokeWidth: 2, stroke: adminPanelBg, fill: "#a855f7" }}
                        activeDot={{ r: 6, strokeWidth: 2, stroke: adminPanelBg, fill: "#7e22ce" }}
                      />
                      ) : null}
                     </ComposedChart>
                    </ResponsiveContainer>
                  </AdminChartScroll>
                  <div style={{ fontSize: 10.5, color: adminTextSoft, marginTop: 2, textAlign: "center" }}>
                    {hasMcpDaily || hasMcpActuals
                      ? "Billed cost (shaded area) and Bot hours (purple line) are metered Recall values from stored billing snapshots (IST days)."
                      : "Billed cost (shaded area) is a local estimate based on meeting duration and configured rates until metered billing history is available."}
                  </div>
                </div>
              </div>
              </div>

              <div className="ub-billing-health-grid">
                <UsageBillingAlertStatusCard
                  healthStatusLabel={recallMcpHasRealBilling ? realBillingAlertStatus.label : "Unavailable"}
                  healthStatusColor={recallMcpHasRealBilling ? realBillingAlertStatus.color : "#64748b"}
                  loading={recallMcpBillingLoading}
                  recallMcpLabel={recallApiStatusLabel}
                  recallMcpConnected={Boolean(usageApi?.recall?.connected)}
                  billingApiLabel={recallMcpBillingOk ? "Healthy" : recallMcpConnected ? "Degraded" : "Unavailable"}
                  billingApiOk={recallMcpBillingOk}
                  thresholdRows={usageCreditThresholdRows}
                  creditUsd={recallMcpCreditUsd}
                  onEditThresholds={() => {
                    setCreditThresholdErr(null);
                    setCreditThresholdDraft(
                      (usageApi?.recallCreditAlerts?.thresholds || [20, 15, 10, 5, 2]).join(", ")
                    );
                    setCreditThresholdEditorOpen(true);
                  }}
                  onOpenRecipients={() => {
                    setCreditRecipientErr(null);
                    setRecipientPickerQuery("");
                    setCreditRecipientsOpen(true);
                    loadCreditAlertRecipients();
                    loadAdminUsersForPicker();
                  }}
                  onOpenHistory={() => {
                    setCreditAlertHistoryErr(null);
                    setCreditAlertHistoryOpen(true);
                    loadCreditAlertHistory();
                  }}
                  recipientsIcon={<Svg d={IC.users} size={15} color="currentColor" stroke={1.9} />}
                  historyIcon={<Svg d={IC.history} size={15} color="currentColor" stroke={1.9} />}
                  recipientsCount={creditRecipients.length}
                />
                <UsageSystemHealth
                  recallConnected={Boolean(usageApi?.recall?.connected)}
                  recallLabel={recallApiStatusLabel}
                  region={recallRegionLabel}
                  pricingBot={usagePricingBotLabel}
                  pricingTranscription={usagePricingTranscriptionLabel}
                  billingApiLabel={recallMcpBillingOk ? "Healthy" : recallMcpConnected ? "Degraded" : "Unavailable"}
                  billingApiOk={recallMcpBillingOk}
                  getBilledUsageStatus={recallMcpBillingOk ? "OK" : "DOWN"}
                  retryLabel={recallMcpRetryLabel}
                  lastUpdated={recallMcpLastUpdatedLabel}
                  cacheLabel={`${recallMcpCacheLabel} · ${recallMcpRedisLabel}`}
                  workspaceLabel={recallMcp?.workspacePinned ? "Pinned" : "Default"}
                  layout="horizontal"
                />
              </div>

              {creditThresholdEditorOpen ? (
                <div
                  role="presentation"
                  onClick={() => (!creditThresholdSaving ? setCreditThresholdEditorOpen(false) : null)}
                  style={{
                    position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.46)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
                  }}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Edit alert thresholds"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%", maxWidth: 440, maxHeight: "82vh", display: "flex", flexDirection: "column",
                      background: adminPanelBg, border: `1px solid ${adminBorder}`, borderRadius: 14,
                      boxShadow: "0 20px 50px rgba(0,0,0,0.25)", overflow: "hidden",
                      fontFamily: "inherit",
                    }}
                  >
                    <div style={{ padding: "14px 16px", borderBottom: `1px solid ${adminBorder}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: adminTextMain }}>Edit alert thresholds</div>
                        <div style={{ marginTop: 4, fontSize: 11.5, color: adminTextSoft, lineHeight: 1.45 }}>
                          Dollar amounts separated by commas, from 0 to 100000, up to 7 values. Saving resets sent state so the new values evaluate fresh from the current balance.
                        </div>
                      </div>
                      <button type="button" onClick={() => (!creditThresholdSaving ? setCreditThresholdEditorOpen(false) : null)} aria-label="Close" style={{ border: "none", background: "transparent", color: adminTextMuted, cursor: "pointer", padding: 4, flexShrink: 0 }}>
                        <Svg d={IC.close} size={16} color="currentColor" />
                      </button>
                    </div>
                    <div style={{ padding: "14px 16px", overflowY: "auto", flex: 1 }}>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: adminTextMuted, marginBottom: 6 }}>
                        Thresholds (USD)
                      </label>
                      <input
                        value={creditThresholdDraft}
                        onChange={(e) => setCreditThresholdDraft(e.target.value)}
                        placeholder="20, 15, 10, 5, 2"
                        inputMode="decimal"
                        aria-label="Alert thresholds in USD"
                        onFocus={(e) => {
                          e.target.style.borderColor = "#0875E1";
                          e.target.style.boxShadow = "0 0 0 3px rgba(8,117,225,0.12)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#DCE6EF";
                          e.target.style.boxShadow = "none";
                        }}
                        style={{
                          width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
                          border: "1px solid #DCE6EF", background: isLight ? "#fff" : "rgba(255,255,255,0.04)",
                          color: adminTextMain, fontSize: 13, fontFamily: "inherit", outline: "none",
                        }}
                      />
                      {creditThresholdErr ? (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#B91C1C" }}>{creditThresholdErr}</div>
                      ) : null}
                      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => setCreditThresholdEditorOpen(false)}
                          disabled={creditThresholdSaving}
                          style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            minHeight: 34, padding: "0 14px", borderRadius: 8,
                            border: "1px solid #DCE6EF", background: isLight ? "#fff" : "transparent",
                            color: adminTextMain, fontSize: 12.5, fontWeight: 700, cursor: creditThresholdSaving ? "default" : "pointer",
                            fontFamily: "inherit", appearance: "none", WebkitAppearance: "none",
                            opacity: creditThresholdSaving ? 0.6 : 1,
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={saveCreditAlertThresholds}
                          disabled={creditThresholdSaving}
                          style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            minHeight: 34, padding: "0 14px", borderRadius: 8,
                            border: "1px solid #0875E1", background: "#0875E1",
                            color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: creditThresholdSaving ? "default" : "pointer",
                            fontFamily: "inherit", appearance: "none", WebkitAppearance: "none",
                            opacity: creditThresholdSaving ? 0.7 : 1,
                          }}
                        >
                          {creditThresholdSaving ? "Saving…" : "Save thresholds"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {creditRecipientsOpen
                ? createPortal(
                    <div
                      role="dialog"
                      aria-modal="true"
                      aria-label="Alert recipients"
                      onClick={(e) => {
                        if (e.target === e.currentTarget) setCreditRecipientsOpen(false);
                      }}
                      style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 10000,
                        background: "rgba(15,23,42,0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                      }}
                    >
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: "min(520px, 100%)",
                          maxHeight: "min(84vh, 720px)",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          background: "#fff",
                          borderRadius: 14,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 20px 48px rgba(15,23,42,0.22)",
                          padding: "18px 20px 16px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f2a4a", letterSpacing: "-0.02em" }}>
                              Alert recipients
                            </div>
                            <div style={{ marginTop: 4, fontSize: 12.5, color: "#64748b", lineHeight: 1.45 }}>
                              Low-balance mails go to everyone listed — admin users plus extra addresses you add below.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setCreditRecipientsOpen(false)}
                            aria-label="Close"
                            style={{
                              border: "1px solid #e2e8f0",
                              background: "#fff",
                              borderRadius: 8,
                              width: 32,
                              height: 32,
                              cursor: "pointer",
                              color: "#475569",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Svg d={IC.close} size={16} color="currentColor" />
                          </button>
                        </div>

                        <div style={{ marginTop: 16, flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", scrollbarWidth: "thin", paddingRight: 2 }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: 8,
                              flexShrink: 0,
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Current recipients</div>
                            {creditRecipientsLoading || recipientSaving ? (
                              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                                {recipientSaving ? "Saving…" : "Loading…"}
                              </span>
                            ) : null}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                              maxHeight: 220,
                              overflowY: "auto",
                              overflowX: "hidden",
                              paddingRight: 4,
                              WebkitOverflowScrolling: "touch",
                              scrollbarWidth: "thin",
                            }}
                          >
                            {!creditRecipientsLoading && creditRecipients.length === 0 ? (
                              <div style={{ fontSize: 12.5, color: "#64748b", padding: "10px 0", lineHeight: 1.55 }}>
                                No recipients yet — add an address below, otherwise no alert mails can be sent.
                              </div>
                            ) : null}
                            {creditRecipients.map((r) => {
                              const sourceLabel =
                                r.source === "extra" ? "Added" : r.source === "env" ? "System" : "Admin";
                              const locked = r.source !== "extra";
                              return (
                                <div
                                  key={r.email}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    rowGap: 8,
                                    gap: 10,
                                    padding: "10px 12px",
                                    borderRadius: 10,
                                    border: "1px solid #e2e8f0",
                                    background: "#f8fafc",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: "50%",
                                      background: "linear-gradient(145deg,#1778D4 0%,#1D4ED8 100%)",
                                      color: "#fff",
                                      fontSize: 11,
                                      fontWeight: 800,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                    aria-hidden
                                  >
                                    {empDirectoryInitials(r.name || r.email)}
                                  </div>
                                  <div style={{ flex: "1 1 140px", minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 650,
                                        color: "#0f2a4a",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={r.email}
                                    >
                                      {r.name || r.email?.split("@")[0] || "Recipient"}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 11.5,
                                        color: "#64748b",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {r.email}
                                    </div>
                                  </div>
                                  <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                  <span
                                    style={{
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      letterSpacing: "0.02em",
                                      padding: "3px 8px",
                                      borderRadius: 999,
                                      background: locked && r.source === "env" ? "#e2e8f0" : "rgba(23,120,212,0.12)",
                                      color: locked && r.source === "env" ? "#475569" : "#1d4ed8",
                                      flexShrink: 0,
                                    }}
                                    title={
                                      r.source === "extra"
                                        ? "Added manually"
                                        : r.source === "env"
                                          ? "Managed by system settings"
                                          : "Admin user"
                                    }
                                  >
                                    {sourceLabel}
                                  </span>
                                  {!locked ? (
                                    <button
                                      type="button"
                                      disabled={recipientSaving}
                                      onClick={() => removeCreditAlertRecipient(r.email)}
                                      title="Remove"
                                      style={{
                                        border: "1px solid #fecaca",
                                        background: "#fff",
                                        color: "#b91c1c",
                                        borderRadius: 8,
                                        padding: "5px 8px",
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        cursor: recipientSaving ? "not-allowed" : "pointer",
                                        opacity: recipientSaving ? 0.55 : 1,
                                        flexShrink: 0,
                                        fontFamily: "inherit",
                                      }}
                                    >
                                      Remove
                                    </button>
                                  ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div style={{ marginTop: 16, flexShrink: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 6 }}>Find people</div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <input
                              value={recipientPickerQuery}
                              onChange={(e) => setRecipientPickerQuery(e.target.value)}
                              placeholder="Search users by name or email…"
                              aria-label="Search users"
                              onFocus={(e) => {
                                e.target.style.borderColor = "#0875E1";
                                e.target.style.boxShadow = "0 0 0 3px rgba(8,117,225,0.12)";
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = "#e2e8f0";
                                e.target.style.boxShadow = "none";
                              }}
                              style={{
                                flex: "1 1 180px", minWidth: 0, padding: "9px 12px", borderRadius: 8,
                                border: "1px solid #e2e8f0", background: "#fff",
                                color: "#0f2a4a", fontSize: 13, fontFamily: "inherit", outline: "none",
                                minHeight: 38, boxSizing: "border-box",
                              }}
                            />
                            <button
                              type="button"
                              onClick={addNewRecipientFromSearch}
                              disabled={recipientSaving}
                              style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                minHeight: 38, padding: "0 14px", borderRadius: 8, flexShrink: 0,
                                border: "1px solid #0875E1", background: "#0875E1",
                                color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: recipientSaving ? "default" : "pointer",
                                fontFamily: "inherit", appearance: "none", WebkitAppearance: "none",
                                opacity: recipientSaving ? 0.7 : 1,
                              }}
                            >
                              <Svg d={IC.users} size={13} color="#fff" stroke={1.9} />
                              Add
                            </button>
                          </div>
                          {recipientPickerQuery.trim() ? (
                            <div style={{ marginTop: 6, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                              {adminUsersForPicker
                                .filter((u) => {
                                  const q = recipientPickerQuery.trim().toLowerCase();
                                  return (
                                    (u.name || "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
                                  ) && !creditRecipients.some((r) => r.email.toLowerCase() === u.email.toLowerCase());
                                })
                                .slice(0, 6)
                                .map((u) => (
                                  <button
                                    key={u.email}
                                    type="button"
                                    onClick={() => addRecipientByEmail(u.email, u.name || "")}
                                    disabled={recipientSaving}
                                    style={{
                                      width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                      border: "none", background: "transparent", cursor: "pointer", textAlign: "left",
                                      color: "#0f2a4a", fontSize: 12.5, fontFamily: "inherit",
                                    }}
                                  >
                                    <Svg d={IC.users} size={14} color="#0875E1" stroke={1.9} />
                                    <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {u.name || u.email}
                                    </span>
                                    <span style={{ color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {u.email}
                                    </span>
                                  </button>
                                ))}
                            </div>
                          ) : null}
                        </div>

                        {creditRecipientErr ? (
                          <div
                            style={{
                              marginTop: 12,
                              padding: "8px 10px",
                              borderRadius: 8,
                              background: "rgba(220,38,38,0.08)",
                              border: "1px solid rgba(220,38,38,0.2)",
                              color: "#b91c1c",
                              fontSize: 12.5,
                              lineHeight: 1.4,
                              flexShrink: 0,
                            }}
                          >
                            {creditRecipientErr}
                          </div>
                        ) : null}
                        </div>
                      </div>
                    </div>,
                    document.body
                  )
                : null}

              {creditAlertHistoryOpen ? (
                <div
                  role="presentation"
                  onClick={() => setCreditAlertHistoryOpen(false)}
                  style={{
                    position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.46)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
                  }}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Alert mail history"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%", maxWidth: 560, maxHeight: "82vh", display: "flex", flexDirection: "column",
                      background: adminPanelBg, border: `1px solid ${adminBorder}`, borderRadius: 14,
                      boxShadow: "0 20px 50px rgba(0,0,0,0.25)", overflow: "hidden",
                      fontFamily: "inherit",
                    }}
                  >
                    <div style={{ padding: "14px 16px", borderBottom: `1px solid ${adminBorder}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: adminTextMain }}>Alert mail history</div>
                        <div style={{ marginTop: 4, fontSize: 11.5, color: adminTextSoft, lineHeight: 1.45 }}>
                          When each low-balance mail was sent, for which threshold, and who received it.
                        </div>
                      </div>
                      <button type="button" onClick={() => setCreditAlertHistoryOpen(false)} aria-label="Close" style={{ border: "none", background: "transparent", color: adminTextMuted, cursor: "pointer", padding: 4, flexShrink: 0 }}>
                        <Svg d={IC.close} size={16} color="currentColor" />
                      </button>
                    </div>
                    <div style={{ padding: "10px 16px", overflowY: "auto", flex: 1 }}>
                      {creditAlertHistoryLoading ? (
                        <div style={{ fontSize: 12.5, color: adminTextSoft, padding: "6px 0" }}>Loading history…</div>
                      ) : creditAlertHistoryErr ? (
                        <div style={{ fontSize: 12.5, color: "#B91C1C", padding: "6px 0" }}>{creditAlertHistoryErr}</div>
                      ) : creditAlertHistory.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: adminTextSoft, padding: "10px 4px", lineHeight: 1.55, textAlign: "center" }}>
                          No alert mails recorded yet. Entries appear here each time a threshold mail is sent.
                        </div>
                      ) : (
                        <AdminMuiTable
                          isLight={isLight}
                          fitColumns
                          columns={[
                            { id: "threshold", label: "Threshold", width: 92 },
                            { id: "balance", label: "Balance", width: 92, align: "right" },
                            { id: "sent", label: "Sent", width: 150 },
                            { id: "recipients", label: "Recipients" },
                          ]}
                          rows={creditAlertHistory.map((h, i) => ({
                            key: `${h.sentAt || "na"}-${h.thresholdUsd}-${i}`,
                            cells: [
                              `≤ $${h.thresholdUsd}`,
                              `$${Number(h.balanceUsd).toFixed(2)}`,
                              h.sentAt
                                ? new Date(h.sentAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                                : "—",
                              (h.recipients || []).length ? (h.recipients || []).join(", ") : "—",
                            ],
                          }))}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="ub-breakdown-grid">
                <UsagePlatformStatusCard
                  byPlatform={usageByPlatform}
                  renderPlatformIcon={(platform) => <PlatformBrandIcon platform={platform} size={14} />}
                  completed={usageTotals.completed}
                  live={usageTotals.live}
                  completedActive={usageStatusFilter === "completed"}
                  liveActive={usageStatusFilter === "live"}
                  statusFilterHint={
                    usageStatusFilter ? "Filtering sessions below — click again to clear." : undefined
                  }
                  onViewDetails={() => goAdminPage("allMeetings")}
                  onCompletedClick={() => {
                    setUsageStatusFilter((prev) => (prev === "completed" ? "" : "completed"));
                    setUsageSessionsPage(0);
                  }}
                  onLiveClick={() => {
                    setUsageStatusFilter((prev) => (prev === "live" ? "" : "live"));
                    setUsageSessionsPage(0);
                  }}
                />

                <div className="adm-card adm-usage-table-card ub-top-hosts-card" style={{ overflow: "hidden", minWidth: 0, display: "flex", flexDirection: "column" }}>
                  <div className="adm-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 800, fontSize: 13 }}>
                      Top Hosts by Usage
                      <span style={{ fontWeight: 600, color: adminTextMuted, marginLeft: 6 }}>
                        {topHostsLimit === "all"
                          ? `${allUsageOwnersRanked.length} hosts`
                          : `Top ${topHostsLimit} of ${allUsageOwnersRanked.length}`}
                      </span>
                    </div>
                  </div>
                  <div className="ub-top-hosts-toolbar">
                    <div className="ub-top-hosts-chips" role="group" aria-label="Top hosts limit">
                      {[5, 10, 20, "all"].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`ub-top-hosts-chip${topHostsLimit === n ? " is-active" : ""}`}
                          aria-pressed={topHostsLimit === n}
                          onClick={() => setTopHostsLimit(n)}
                        >
                          {n === "all" ? "All" : `Top ${n}`}
                        </button>
                      ))}
                    </div>
                    <AdminSearchToolbar
                      value={usageHostsAdminSearch}
                      onChange={(arg) => setAdminTabSearch("usageHosts", arg)}
                      placeholder="Search hosts…"
                      adminTextMuted={adminTextMuted}
                      alwaysExpanded
                      filteredCount={usageHostsAdminSearch.trim() ? filteredTopUsageOwners.length : undefined}
                      totalCount={usageHostsAdminSearch.trim() ? topUsageOwners.length : undefined}
                    />
                  </div>
                  {filteredTopUsageOwners.length === 0 ? (
                    <div style={{ padding: "20px", fontSize: 12.5, color: adminTextSoft }}>
                      {topUsageOwners.length === 0 ? "No host usage yet." : "No hosts match your search."}
                    </div>
                  ) : (
                    <div className="adm-scroll-panel ub-table-fit ub-table-fit--hosts">
                      <AdminMuiTable
                        isLight={isLight}
                        stickyHeader
                        maxHeight={240}
                        fitColumns
                        variant="plain"
                        columns={[
                          { id: "rank", label: "#", width: "8%", align: "right", minWidth: 24 },
                          { id: "user", label: "Host", width: "44%", minWidth: 88 },
                          { id: "meetings", label: "Sessions", align: "right", width: "22%", minWidth: 52 },
                          { id: "minutes", label: "Minutes", align: "right", width: "26%", minWidth: 56 },
                        ]}
                        rows={filteredTopUsageOwners.map((u, i) => ({
                          key: `${u.email}-${i}`,
                          cells: [
                            <span key="r" style={{ color: adminTextMuted, fontWeight: 700 }}>{i + 1}</span>,
                            <div key="u" style={{ minWidth: 0 }} title={u.email || u.name || ""}>
                              <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {u.email?.split("@")[0] || u.name}
                              </div>
                            </div>,
                            <span key="m" style={{ fontVariantNumeric: "tabular-nums" }}>{u.meetings}</span>,
                            <span key="min" style={{ fontVariantNumeric: "tabular-nums" }}>{u.minutes.toLocaleString()}</span>,
                          ],
                        }))}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="ub-bottom-tables-grid">
                <div className="adm-card adm-usage-table-card ub-bottom-table-card" style={{ overflow: "hidden", minWidth: 0 }}>
                  <div className="adm-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <span>Monthly Usage</span>
                    {usageMonths.length > 0 ? (
                      <select
                        value={monthlyUsageFilter}
                        onChange={(e) => { setMonthlyUsageFilter(e.target.value); setMonthlyUsagePage(0); }}
                        style={{
                          fontSize: 11.5, fontWeight: 600, padding: "4px 8px", borderRadius: 7,
                          border: `1px solid ${adminBorder}`, background: adminChipBg, color: adminTextMuted,
                        }}
                      >
                        <option value="">All months ({usageMonths.length})</option>
                        {usageMonths.map((m) => (
                          <option key={m.month} value={m.month}>{trendMonthLabel(m.month)}</option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  {usageMonths.length > 0 ? (
                    <div
                      className="ub-monthly-legend-compact"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px 14px",
                        padding: "0 14px 10px",
                        fontSize: 11,
                        color: adminTextSoft,
                        lineHeight: 1.45,
                        borderBottom: `1px solid ${adminBorder}`,
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span className="ub-status-dot" style={{ background: "#10B981", width: 7, height: 7 }} />
                        <strong style={{ color: adminTextMuted, fontWeight: 650 }}>MCP</strong>
                        <span className="ub-legend-long">Recall metered cost (stored per-bot usage)</span>
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span className="ub-status-dot" style={{ background: "#9CA3AF", width: 7, height: 7 }} />
                        <strong style={{ color: adminTextMuted, fontWeight: 650 }}>Est</strong>
                        <span className="ub-legend-long">Local estimate from minutes × rates — not an invoice</span>
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span className="ub-status-dot" style={{ background: "#2563EB", width: 7, height: 7 }} />
                        <strong style={{ color: adminTextMuted, fontWeight: 650 }}>Meetings</strong>
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span className="ub-status-dot" style={{ background: "#7c3aed", width: 7, height: 7 }} />
                        <strong style={{ color: adminTextMuted, fontWeight: 650 }}>Minutes</strong>
                      </span>
                    </div>
                  ) : null}
                  {usageMonths.length === 0 ? (
                    <div style={{ padding: "20px", fontSize: 12.5, color: adminTextSoft, flex: 1 }}>No monthly usage data yet.</div>
                  ) : filteredUsageMonths.length === 0 ? (
                    <div style={{ padding: "20px", fontSize: 12.5, color: adminTextSoft, flex: 1 }}>No data for that month.</div>
                  ) : (
                    <div className="ub-bottom-table-body">
                      <div className="adm-scroll-panel ub-usage-table-scroll ub-table-fit ub-table-fit--monthly" style={{ minWidth: 0 }}>
                        <AdminMuiTable
                          isLight={isLight}
                          stickyHeader
                          maxHeight={360}
                          variant="plain"
                          minWidth={500}
                          columns={[
                            {
                              id: "month",
                              label: "Month",
                              width: 88,
                              headerWrap: true,
                              cellSx: { px: 1, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" },
                            },
                            {
                              id: "meetings",
                              label: "Meetings",
                              align: "right",
                              width: 72,
                              headerWrap: true,
                              cellSx: { px: 0.75, fontSize: 11.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
                            },
                            {
                              id: "minutes",
                              label: "Minutes",
                              align: "right",
                              width: 76,
                              headerWrap: true,
                              cellSx: { px: 0.75, fontSize: 11.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
                            },
                            {
                              id: "lines",
                              label: "Transcript",
                              align: "right",
                              width: 76,
                              headerWrap: true,
                              cellSx: { px: 0.75, fontSize: 11.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
                            },
                            {
                              id: "actual",
                              label: "Cost",
                              align: "right",
                              width: 88,
                              headerWrap: true,
                              cellSx: { px: 0.75, fontSize: 11.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
                            },
                            {
                              id: "change",
                              label: "Change",
                              align: "right",
                              width: 68,
                              headerWrap: true,
                              cellSx: { px: 0.75, fontSize: 11.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
                            },
                          ]}
                          rows={pagedUsageMonths.map((m) => {
                            const mcpActual = mcpMonthBurn(m.month);
                            const estUsd = Math.round((Number(m.costUsd) || 0) * 100) / 100;
                            const showMcp = mcpActual != null && Number.isFinite(Number(mcpActual));
                            const showEst = !showMcp && estUsd > 0;
                            const costTitle = showMcp
                              ? "Metered MCP cost from stored per-bot usage (whole Recall account)"
                              : showEst
                                ? `Local estimate (bot + transcription rates), not Recall invoice. Est. $${estUsd.toFixed(2)}`
                                : "No MCP metered data stored for this month yet";
                            const sortedForChange = [...(usageMonths || [])].sort((a, b) =>
                              String(a.month).localeCompare(String(b.month))
                            );
                            const monthIdx = sortedForChange.findIndex((row) => row.month === m.month);
                            const prevMonthRow = monthIdx > 0 ? sortedForChange[monthIdx - 1] : null;
                            const changePct =
                              prevMonthRow && prevMonthRow.meetings
                                ? Math.round(
                                    ((Number(m.meetings) - Number(prevMonthRow.meetings)) /
                                      Number(prevMonthRow.meetings)) *
                                      100
                                  )
                                : null;
                            return {
                              key: m.month,
                              cells: [
                                <span key="mo" title={trendMonthLabel(m.month)} style={{ display: "block", whiteSpace: "nowrap" }}>
                                  <strong>{usageMonthTableLabel(m.month)}</strong>
                                </span>,
                                <span key="mt" style={{ display: "block", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                  {m.meetings}
                                </span>,
                                <span key="mi" style={{ display: "block", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                  {m.minutes.toLocaleString()}
                                </span>,
                                <span key="tr" style={{ display: "block", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                  {m.transcriptRows.toLocaleString()}
                                </span>,
                                <span
                                  key="ac"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 5,
                                    whiteSpace: "nowrap",
                                  }}
                                  title={costTitle}
                                >
                                  {showMcp ? (
                                    <strong style={{ color: "#10B981", fontWeight: 700 }}>
                                      ${Number(mcpActual).toFixed(2)}
                                    </strong>
                                  ) : showEst ? (
                                    <span className="ub-cost-est" style={{ color: "#6b7688", fontWeight: 600, fontSize: 11 }}>
                                      ~${estUsd.toFixed(2)}
                                      <span style={{ display: "block", fontSize: 9.5, fontWeight: 500, color: adminTextSoft }}>est</span>
                                    </span>
                                  ) : (
                                    <strong style={{ color: adminTextSoft }}>—</strong>
                                  )}
                                </span>,
                                <span key="ch" style={{ display: "block", textAlign: "right" }}>
                                  {changePct == null || !Number.isFinite(changePct) ? (
                                    <span style={{ color: adminTextSoft, fontWeight: 500, fontSize: 11 }}>—</span>
                                  ) : (
                                    <span
                                      className="ub-change-badge"
                                      style={{
                                        color: changePct >= 0 ? "#15803d" : "#b91c1c",
                                        background: changePct >= 0 ? "rgba(21,128,61,0.1)" : "rgba(185,28,28,0.1)",
                                      }}
                                    >
                                      {changePct >= 0 ? "↑" : "↓"} {Math.abs(changePct)}%
                                    </span>
                                  )}
                                </span>,
                              ],
                            };
                          })}
                        />
                      </div>
                      <div className="ub-table-card-footer">
                        {monthlyUsagePageCount > 1 ? (
                          <div className="ub-sessions-pager" style={{ gridTemplateColumns: "auto 1fr auto" }}>
                            <button
                              type="button"
                              className="ub-sessions-pager-btn"
                              disabled={monthlyUsagePageSafe === 0}
                              onClick={() => setMonthlyUsagePage((p) => Math.max(0, p - 1))}
                            >
                              ← Prev
                            </button>
                            <span className="ub-sessions-pager-meta">
                              Page {monthlyUsagePageSafe + 1}/{monthlyUsagePageCount} · {filteredUsageMonths.length} months
                            </span>
                            <button
                              type="button"
                              className="ub-sessions-pager-btn"
                              disabled={monthlyUsagePageSafe >= monthlyUsagePageCount - 1}
                              onClick={() => setMonthlyUsagePage((p) => Math.min(monthlyUsagePageCount - 1, p + 1))}
                            >
                              Next →
                            </button>
                          </div>
                        ) : (
                          <div className="ub-sessions-pager" aria-hidden style={{ visibility: "hidden", pointerEvents: "none" }}>
                            <span className="ub-sessions-pager-meta">—</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="adm-card adm-usage-table-card ub-bottom-table-card" style={{ overflow: "hidden", minWidth: 0 }}>
                  <div className="adm-card-header adm-card-header--search">
                    <div style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 800, fontSize: 13 }}>
                      All MeetAI Sessions
                      <span style={{ fontWeight: 600, color: adminTextMuted, marginLeft: 6 }}>
                        {usageSessionsAdminSearch.trim() || usageStatusFilter
                          ? `${filteredUsageMeetings.length} of ${usageMeetings.length}`
                          : `${usageMeetings.length} sessions`}
                      </span>
                    </div>
                    <AdminSearchToolbar
                      value={usageSessionsAdminSearch}
                      onChange={(arg) => setAdminTabSearch("usageSessions", arg)}
                      placeholder="Search meetings…"
                      adminTextMuted={adminTextMuted}
                      filteredCount={filteredUsageMeetings.length}
                      totalCount={usageMeetings.length}
                    />
                    <button
                      type="button"
                      className="ub-header-text-btn"
                      title="Filter by session status"
                      onClick={() => document.getElementById("ub-usage-status")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    >
                      Filter
                    </button>
                  </div>
                  {filteredUsageMeetings.length === 0 ? (
                    <div className="ub-bottom-table-body">
                    <div className="ub-empty-state" style={{ flex: 1 }}>
                      <p className="ub-empty-state-title">No MeetAI sessions found</p>
                      <p className="ub-empty-state-sub">
                        {usageMeetings.length === 0
                          ? "Sessions will appear here once MeetAI has dispatched to meetings in this range."
                          : "Try changing the selected date range or filters."}
                      </p>
                      {usageMeetings.length > 0 && (usageSessionsAdminSearch.trim() || usageStatusFilter) ? (
                        <div className="ub-empty-state-actions">
                          <button
                            type="button"
                            className="ub-btn-secondary"
                            onClick={() => {
                              setAdminTabSearch("usageSessions", "");
                              setUsageStatusFilter("");
                              setUsageSessionsPage(0);
                            }}
                          >
                            Clear Filters
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="ub-table-card-footer" aria-hidden style={{ visibility: "hidden", pointerEvents: "none" }}>
                      <div className="ub-sessions-pager">
                        <span className="ub-sessions-pager-meta">—</span>
                      </div>
                    </div>
                    </div>
                  ) : (
                    <div className="ub-bottom-table-body">
                    <div className="adm-scroll-panel ub-usage-table-scroll ub-sessions-table-wrap ub-table-fit ub-table-fit--sessions" style={{ minWidth: 0 }}>
                    <AdminMuiTable
                      isLight={isLight}
                      stickyHeader
                      maxHeight={360}
                      variant="plain"
                      size="small"
                      minWidth={640}
                      columns={[
                        { id: "meeting", label: "Meeting", width: 172, headerWrap: true },
                        { id: "platform", label: "Platform", width: 64, align: "left", headerWrap: true, hideOnPhone: true },
                        { id: "users", label: "Users", align: "right", width: 52, headerWrap: true },
                        { id: "actions", label: "Actions", align: "right", width: 58, headerWrap: true, hideOnPhone: true },
                        { id: "mins", label: "Minutes", align: "right", width: 76, headerWrap: true },
                        { id: "date", label: "Date", width: 96, headerWrap: true },
                        { id: "status", label: "Status", width: 108, headerWrap: true },
                      ]}
                      rows={pagedUsageMeetings.map((m, i) => {
                        const participantCount = joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m));
                        const transcriptCount = adminMeetingTranscriptCount(m);
                        const actionCount = adminMeetingActionCount(m);
                        const minuteCount = meetingDurationMinutes(m);
                        let rowStatus = "";
                        if (meetingBotJoinedCall(m)) {
                          if (transcriptCount === 0 && actionCount === 0) rowStatus = "MeetAI joined · processing";
                          else if (transcriptCount > 0 && actionCount === 0) rowStatus = "Transcript available";
                          else rowStatus = "Processed";
                        } else if (participantCount === 0) {
                          rowStatus = "MeetAI pending / not joined";
                        } else if (transcriptCount === 0 && actionCount === 0) {
                          rowStatus = "No transcript/action yet";
                        } else if (transcriptCount > 0 && actionCount === 0) {
                          rowStatus = "Transcript available";
                        } else {
                          rowStatus = "Processed";
                        }
                        const statusBadge =
                          rowStatus === "Processed"
                            ? { label: "Completed", color: "#15803d", bg: "rgba(21,128,61,0.1)" }
                            : rowStatus === "Transcript available"
                              ? { label: "Transcript Available", color: "#1d4ed8", bg: "rgba(29,78,216,0.1)" }
                              : /processing|pending|not joined/i.test(rowStatus)
                                ? { label: "Processing", color: "#b45309", bg: "rgba(180,83,9,0.12)" }
                                : { label: "No Transcript", color: "#64748b", bg: "rgba(100,116,139,0.12)" };
                        const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
                        const platformIconPath =
                          m.platform === "zoom"
                            ? IC.video
                            : m.platform === "teams"
                              ? IC.users
                              : m.platform === "meet"
                                ? IC.meet
                                : IC.cal;
                        const minsLabel = formatAdminUsageMinutes(m);
                        return {
                          key: m.id || m._id || i,
                          cells: [
                            <div key="mtg" className="ub-meeting-cell ub-meeting-cell--compact" title={m.title || "Untitled Meeting"}>
                              <span
                                className="ub-meeting-icon ub-meeting-icon--sm"
                                style={{ background: pl.bg, boxShadow: `inset 0 0 0 1px ${pl.color}22` }}
                                aria-hidden
                              >
                                <Svg d={platformIconPath} color={pl.color} size={14} stroke={2} />
                              </span>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="ub-meeting-title-truncate" style={{ fontWeight: 600 }}>
                                  {m.title || "Untitled Meeting"}
                                </div>
                              </div>
                            </div>,
                            <span key="pl" className="ub-platform-col-label" title={pl.label}>
                              {pl.label}
                            </span>,
                            <span key="u" style={participantCount === 0 && !meetingHasHappened(m) ? { opacity: 0.5 } : undefined}>
                              {participantCount === 0 && !meetingHasHappened(m) ? "—" : participantCount}
                            </span>,
                            <span key="a">{actionCount}</span>,
                            <span key="m" style={{ fontVariantNumeric: "tabular-nums", fontSize: 11.5, color: adminTextMain, fontWeight: 600 }}>
                              {minsLabel}
                            </span>,
                            <span key="dt" style={{ fontSize: 11.5, color: adminTextMuted, whiteSpace: "nowrap" }}>
                              {m.date ? formatYmdDisplay(m.date) : "—"}
                            </span>,
                            <span
                              key="st"
                              className="ub-session-status-badge"
                              style={{
                                color: statusBadge.color,
                                background: statusBadge.bg,
                              }}
                              title={rowStatus}
                            >
                              {statusBadge.label}
                            </span>,
                          ],
                        };
                      })}
                    />
                    </div>
                    <div className="ub-table-card-footer">
                      <div className="ub-sessions-pager">
                        <button
                          type="button"
                          className="ub-sessions-pager-btn"
                          disabled={usageSessionsPageSafe === 0}
                          onClick={() => setUsageSessionsPage((p) => Math.max(0, p - 1))}
                        >
                          ← Prev
                        </button>
                        <span
                          className="ub-sessions-pager-meta"
                          title={`Showing ${usageSessionsPageSafe * usageSessionsPageSize + 1}–${Math.min(filteredUsageMeetings.length, (usageSessionsPageSafe + 1) * usageSessionsPageSize)} of ${filteredUsageMeetings.length} sessions · Page ${usageSessionsPageSafe + 1} of ${usageSessionsPageCount}`}
                        >
                          {usageSessionsPageSafe * usageSessionsPageSize + 1}–
                          {Math.min(filteredUsageMeetings.length, (usageSessionsPageSafe + 1) * usageSessionsPageSize)} of{" "}
                          {filteredUsageMeetings.length} · Pg {usageSessionsPageSafe + 1}/{usageSessionsPageCount}
                        </span>
                        <select
                          className="ub-sessions-pager-size"
                          aria-label="Sessions per page"
                          value={usageSessionsPageSize}
                          onChange={(e) => {
                            setUsageSessionsPageSize(Number(e.target.value) || 25);
                            setUsageSessionsPage(0);
                          }}
                        >
                          {[5, 10, 25, 50].map((n) => (
                            <option key={n} value={n}>
                              {n}/pg
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="ub-sessions-pager-btn"
                          disabled={usageSessionsPageSafe >= usageSessionsPageCount - 1}
                          onClick={() => setUsageSessionsPage((p) => Math.min(usageSessionsPageCount - 1, p + 1))}
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                    </div>
                  )}
	                </div>
	              </div>

              <details className="adm-card ub-metrics-more">
                <summary>
                  <span>Additional usage metrics</span>
                  <span className="ub-metrics-more-chevron" aria-hidden>
                    ▼
                  </span>
                </summary>
              <div className="adm-kpi-grid adm-kpi-grid--4" style={{ padding: "0 12px 14px" }}>
                <AdminMetricCard
                  tone="amber"
                  icon={<Svg d={IC.task} size={18} color="currentColor" stroke={2} />}
                  value={usageTotals.totalActionItems}
                  label="Action Items"
                  sublabel="Generated tasks"
                  sparkColor={ADM.amber}
                showSpark={false}
                />
                <AdminMetricCard
                  tone="blue"
                  icon={<Svg d={IC.clock} size={18} color="currentColor" stroke={2} />}
                  value={
                    typeof (usageApi?.recall?.monthBotHours ?? 0) === "number"
                      ? Number(usageApi?.recall?.monthBotHours || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                      : usageApi?.recall?.monthBotHours ?? 0
                  }
                  label="Recall Month Hours"
                  sublabel={
                    usageApi?.recall?.note?.includes("MeetAI DB")
                      ? `${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} · local minutes proxy (REST skipped; MCP live)`
                      : `${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} UTC window · Recall REST /billing/usage`
                  }
                  sparkColor={ADM.blue}
                showSpark={false}
                />
                <AdminMetricCard
                  tone="teal"
                  icon={<Svg d={IC.cal} size={18} color="currentColor" stroke={2} />}
                  value={
                    typeof (usageApi?.recall?.allTimeBotHours ?? 0) === "number"
                      ? Number(usageApi?.recall?.allTimeBotHours || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                      : usageApi?.recall?.allTimeBotHours ?? 0
                  }
                  label="Recall All-Time Hours"
                  sublabel={
                    Number(usageApi?.recall?.allTimeBotHours) > 0
                      ? "Recall workspace bot total · cached REST (background refresh)"
                      : "Loading from Recall cache — refreshes about every minute on this tab"
                  }
                  sparkColor={ADM.teal}
                showSpark={false}
                />
                {!recallMcpHasRealBilling ? <>
                <div title="This is a local estimate based on configured rates, not an invoice value.">
                  <AdminMetricCard
                    tone="amber"
                    icon={<Svg d={IC.zap} size={18} color="currentColor" stroke={2} />}
                    value={
                      typeof (usageApi?.recall?.estimatedMonthCostUsd ?? 0) === "number"
                        ? Number(usageApi?.recall?.estimatedMonthCostUsd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : usageApi?.recall?.estimatedMonthCostUsd ?? 0
                    }
                    label="Est. Month Cost (USD)"
                    sublabel={`${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} to date · whole Recall account`}
                    sparkColor={ADM.amber}
                  showSpark={false}
                  />
                </div>
                <div title="This is a local estimate based on configured rates, not an invoice value.">
                  <AdminMetricCard
                    tone="rose"
                    icon={<Svg d={IC.task} size={18} color="currentColor" stroke={2} />}
                    value={
                      typeof (usageApi?.recall?.estimatedAllTimeCostUsd ?? 0) === "number"
                        ? Number(usageApi?.recall?.estimatedAllTimeCostUsd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : usageApi?.recall?.estimatedAllTimeCostUsd ?? 0
                    }
                    label="Est. All-Time Cost (USD)"
                    sublabel="Whole Recall account, all-time"
                    sparkColor={ADM.rose}
                  showSpark={false}
                  />
                </div>
                </> : null}
                {SHOW_RECALL_MCP_USAGE && (
                <>
                <div title="Live credit balance from Recall MCP get_billed_usage. Needs RECALL_MCP_API_KEY (scopes mcp.billing.read + mcp.account.read). Invoices/PDFs are dashboard-only.">
                  <AdminMetricCard
                    tone={recallCreditUi.metricTone}
                    icon={<Svg d={IC.zap} size={18} color="currentColor" stroke={2} />}
                    value={
                      usageApi?.recallBilled?.connected && typeof usageApi.recallBilled.creditBalanceUsd === "number"
                        ? Number(usageApi.recallBilled.creditBalanceUsd).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : "—"
                    }
                    label="Remaining Credits (USD)"
                    sublabel={recallMcpCreditSublabel}
                    sparkColor={ADM.green}
                  showSpark={false}
                  />
                </div>
                <div title="Invoice-grade billed usage from Recall MCP get_billed_usage when available; otherwise this may be summed from list_bot_usage.">
                  <AdminMetricCard
                    tone="violet"
                    icon={<Svg d={IC.clock} size={18} color="currentColor" stroke={2} />}
                    value={
                      typeof recallMcpBotUsageHours === "number"
                        ? Number(recallMcpBotUsageHours).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : "—"
                    }
                    label="Billed Hours (MCP)"
                    sublabel={recallMcpBilledHoursSublabel}
                    sparkColor={ADM.violet}
                  showSpark={false}
                  />
                </div>
                </>
                )}
              </div>
              </details>

              {SHOW_RECALL_MCP_USAGE && (
              <>
              <div className="adm-card" style={{padding:"16px 20px"}}>
                <button
                  type="button"
                  onClick={() => setRecallMcpFeaturesOpen((v) => !v)}
                  aria-expanded={recallMcpFeaturesOpen}
                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:recallMcpFeaturesOpen ? 10 : 0,width:"100%",background:"transparent",border:"none",cursor:"pointer",padding:0,textAlign:"left"}}
                >
                  <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                    <span style={{color:adminTextMuted,fontSize:12,fontWeight:800,transition:"transform .15s ease",transform:recallMcpFeaturesOpen ? "rotate(90deg)" : "none",display:"inline-block"}}>
                      ▶
                    </span>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:adminTextMain}}>Recall MCP</div>
                      <div style={{fontSize:12,color:adminTextMuted,marginTop:3}}>
                        Connected through configured Recall MCP key. Balance and billing use get_billed_usage.
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      border:`1px solid ${recallMcpStateColor}`,
                      color:recallMcpStateColor,
                      background:isLight ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.04)",
                      borderRadius:999,
                      padding:"5px 10px",
                      fontSize:11.5,
                      fontWeight:800,
                    }}
                  >
                    {recallMcpStateLabel}
                  </span>
                </button>
                {recallMcpFeaturesOpen ? (
                <>
                <div className="ub-system-health-grid ub-recall-mcp-details" style={{ marginTop: 8 }}>
                  {[
                    ["Region", recallRegionLabel],
                    ["Pricing", `${usagePricingBotLabel}${usagePricingTranscriptionLabel ? `\n${usagePricingTranscriptionLabel}` : ""}`],
                    ["Billing endpoint", "get_billed_usage"],
                    ["Connection status", recallMcpStateLabel],
                    ["Last successful update", recallMcpLastUpdatedLabel],
                    ["Retry policy", recallMcpRetryLabel],
                  ].map(([label, value]) => (
                    <div key={label} className="ub-health-row">
                      <span className="ub-health-label">{label}</span>
                      <span className="ub-health-value ub-health-value--wrap ub-health-value--pre" title={String(value).replace(/\n/g, " · ")}>
                        {String(value).split("\n").map((line, i) => (
                          <span key={i} style={{ display: "block" }}>{line}</span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
                </>
                ) : null}
              </div>

              {SHOW_RECALL_PER_BOT_MCP_USAGE ? (
              <div className="adm-card" style={{padding:"16px 20px"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: ADM.violetSoft,
                      color: ADM.violet,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Svg d={IC.task} size={18} color="currentColor" stroke={2} />
                  </span>
                  <div style={{fontSize:14,fontWeight:800,color:adminTextMain}}>Recall Per-Bot Usage (MCP)</div>
                </div>
                <div style={{fontSize:12,color:adminTextMuted,marginBottom:10}}>
                  {usageApi?.recallBotUsage?.connected
                    ? `Live metered records via list_bot_usage${usageApi?.recallBotUsage?.fetchedAt ? ` · ${new Date(usageApi.recallBotUsage.fetchedAt).toLocaleString()}` : ""}`
                    : (usageApi?.recallBotUsage?.note || (!usageApi ? "Waiting for /api/admin/usage before checking Recall MCP." : recallMcpConfigured ? "list_bot_usage is unavailable right now." : "RECALL_MCP_API_KEY is not configured on the running backend."))}
                </div>
                {(usageApi?.recallBotUsage?.items || []).length > 0 ? (
                  (() => {
                    const rows = (usageApi.recallBotUsage.items || []).slice(0, 10).map((r, i) => {
                          const botId = String(r.bot_id || r.botId || r.id || "").trim();
                          const shortBotId = botId.length > 14 ? `${botId.slice(0, 8)}…${botId.slice(-4)}` : botId;
                          const botMap = usageApi.botUsageMeetings || {};
                          const srv = botMap[botId] || botMap[botId.toLowerCase()] || {};
                          const local = botId
                            ? (usageMeetings || []).find((m) => {
                                const id = String(m?.recall_bot_id || m?.recallBotId || "").trim();
                                return id === botId || id.toLowerCase() === botId.toLowerCase();
                              })
                            : null;
                          const mcpTitle =
                            r.meeting_title ||
                            r.meeting_name ||
                            r.bot_name ||
                            r.name ||
                            (r.raw && typeof r.raw === "object" ? r.raw.meeting_title || r.raw.title : null);
                          const title = srv.title || local?.title || mcpTitle || null;
                          const dateLabel = srv.date || local?.date || null;
                          const platformLabel = srv.platform || local?.platform || r.platform || null;
                          const hostPayload = {
                            host_name: srv.host_name || local?.host_name || local?.hostName,
                            host_email: srv.host_email || local?.host_email || local?.hostEmail,
                            owner_name: srv.owner_name || local?.owner_name || local?.ownerName,
                            owner_email: srv.owner_email || local?.owner_email || local?.ownerEmail,
                            participants:
                              (Array.isArray(srv.participants) && srv.participants.length
                                ? srv.participants
                                : null) ||
                              local?.participants ||
                              [],
                          };
                          const hostName = meetingHostName(hostPayload);
                          const hostEmail = meetingHostEmail(hostPayload);
                          const usageLabel = String(r.seconds != null ? `${r.seconds}s` : r.hours != null ? `${r.hours}h` : r.usage || JSON.stringify(r)).slice(0,80);
                          return { id: `${botId || "row"}-${i}`, title, dateLabel, platformLabel, shortBotId, botId, hostName, hostEmail, usageLabel };
                    });
                    return (
                      <AdminDataGrid
                        rows={rows}
                        isLight={isLight}
                        height={360}
                        pageSize={10}
                        pageSizeOptions={[10, 25, 50]}
                        columns={[
                          { field: "meeting", headerName: "Meeting", minWidth: 280, flex: 1.5, valueGetter: (_value, row) => row.title || row.shortBotId, renderCell: ({ row }) => <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.title || row.botId}>{row.title || row.shortBotId}</div><div style={{ fontSize: 10.5, color: adminTextSoft, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[row.dateLabel ? formatYmdDisplay(row.dateLabel) : null, row.platformLabel ? String(row.platformLabel).replace(/_/g, " ") : null].filter(Boolean).join(" · ") || row.shortBotId}</div></div> },
                          { field: "host", headerName: "Host", minWidth: 210, flex: 1, valueGetter: (_value, row) => row.hostName || row.hostEmail || "—", renderCell: ({ row }) => <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.hostName || row.hostEmail || "—"}</div>{row.hostName && row.hostEmail ? <div style={{ fontSize: 10.5, color: adminTextSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.hostEmail}</div> : null}</div> },
                          { field: "usageLabel", headerName: "Usage", minWidth: 115, flex: .5, align: "right", headerAlign: "right" },
                        ]}
                      />
                    );
                  })()
                ) : null}
              </div>
              ) : null}
              </>
              )}
	              </div>
	            </AdminTabPanel>
          )}

          {adminCalendarBootLoading && (
            <AdminCalendarSkeleton isLight={isLight} />
          )}

          {adminReady && adminTabSeen("calendar") && (
            <AdminTabPanel
              active={page === "calendar" && !adminCalendarBootLoading}
              className="apage adm-cal-page adm-premium-page adm-org-form"
            >
              <AdminPremiumHero
                badge="Avaada · Calendar"
                badgeIconD={IC.cal}
                title="MeetAI calendar"
                desc={`Org-wide MeetAI sessions: joined (green), pending dispatch (blue), and missed. Loads by view range in pages of ${ADMIN_CALENDAR_FETCH_PAGE_SIZE} to avoid timeouts.`}
                isLight={isLight}
                meta={
                  <span>
                    {adminCalendarBootLoading || (adminCalendarFetching && !adminCalendarDataReady)
                      ? "Loading sessions…"
                      : calendarAdminSearch.trim()
                        ? `${filteredCalendarMeetings.length} of ${adminCalendarMeetings.length} sessions`
                        : `${adminCalendarMeetings.length} sessions${
                            adminCalendarTotal > adminCalendarMeetings.length
                              ? ` (of ${adminCalendarTotal} in range)`
                              : ""
                          }`}
                  </span>
                }
              />
              {adminCalendarError ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "11px 14px",
                    background: isLight ? "rgba(254,242,242,0.95)" : "rgba(220,38,38,0.12)",
                    border: isLight ? "1px solid #FECACA" : "1px solid rgba(220,38,38,0.25)",
                    borderRadius: 10,
                    fontSize: 12.5,
                    color: isLight ? "#B91C1C" : "#FCA5A5",
                    lineHeight: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span>Calendar could not load: {adminCalendarError}</span>
                  <button
                    type="button"
                    className="adm-ghost-btn"
                    onClick={() => setAdminReloadTick((v) => v + 1)}
                    style={{ fontSize: 12, padding: "6px 10px" }}
                  >
                    Retry
                  </button>
                </div>
              ) : null}
              <div
                className={isLight ? "adm-cal-shell adm-cal-shell-premium" : "adm-cal-shell-premium"}
                style={{
                  position: "relative",
                  background: isLight ? "#ffffff" : "#F4F7FB",
                  borderRadius: 16,
                  padding: 20,
                  border: isLight ? "1px solid #e2e8f0" : "1px solid #1E3448",
                  width: "100%",
                  maxWidth: "100%",
                  margin: 0,
                  boxShadow: isLight ? "0 8px 32px rgba(15,23,42,0.06)" : "none",
                  opacity: 1,
                  transition: "opacity 0.15s ease",
                }}
              >
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <AdminSearchToolbar
                    value={calendarAdminSearch}
                    onChange={(arg) => setAdminTabSearch("calendar", arg)}
                    placeholder="Search MeetAI sessions by title, host, participant, platform…"
                    adminTextMuted={adminTextMuted}
                    filteredCount={filteredCalendarMeetings.length}
                    totalCount={adminCalendarMeetings.length}
                  />
                </div>
                {adminCalendarRefreshing ? (
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 14,
                      zIndex: 2,
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: isLight ? "#1d4ed8" : "#1e3a5f",
                      background: isLight ? "rgba(239,246,255,0.95)" : "rgba(255,255,255,0.9)",
                      border: isLight ? "1px solid #bfdbfe" : "1px solid #cbd5e1",
                      borderRadius: 999,
                      padding: "5px 10px",
                    }}
                  >
                    Syncing in background…
                  </div>
                ) : null}
                <CalPage
                  meetings={filteredCalendarMeetings}
                  onOpen={adminCalendarOpen}
                  adminCalendar
                  year={calYear}
                  month={calMonth}
                  setYear={setCalYear}
                  setMonth={setCalMonth}
                  view={calView}
                  setView={setCalView}
                  selDate={calSelDate}
                  setSelDate={setCalSelDate}
                  calendarHint={
                    adminCalendarDataReady && !adminCalendarFetching && adminCalendarMeetings.length === 0
                      ? "No MeetAI sessions in this view yet. Sessions appear after Google Calendar sync or a Record meeting dispatch."
                      : adminCalendarDataReady &&
                          adminCalendarTotal > adminCalendarMeetings.length
                        ? `Loaded ${adminCalendarMeetings.length} of ${adminCalendarTotal} sessions in this range — later days may still be incomplete. Switch Day/Week or click Retry if meetings look missing.`
                        : null
                  }
                  calendarWarning={adminCalendarError}
                />
              </div>
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("allMeetings") && (
            <AdminTabPanel active={page === "allMeetings"} className="apage adm-premium-page adm-org-form" style={{ color: adminTextMain }}>
              <AdminPremiumHero
                badge="Avaada · Sessions register"
                badgeIconD={IC.meet}
                title="All MeetAI meetings"
                desc="Org register of meetings where MeetAI was dispatched. Calendar-only events without Record / auto-join are not listed here."
                isLight={isLight}
                meta={
                  <span>
                    {adminMeetingsLoading
                      ? "Loading sessions…"
                      : allMeetingsAdminSearch.trim() || adminMeetingsDateFrom || adminMeetingsDateTo
                        ? `${filteredAllMeetings.length} of ${dedupedAllMeetings.length} sessions`
                        : `${dedupedAllMeetings.length} sessions`}
                  </span>
                }
              />
              {adminMeetingsError ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "11px 14px",
                    background: isLight ? "rgba(254,242,242,0.95)" : "rgba(220,38,38,0.12)",
                    border: isLight ? "1px solid #FECACA" : "1px solid rgba(220,38,38,0.25)",
                    borderRadius: 10,
                    fontSize: 12.5,
                    color: isLight ? "#B91C1C" : "#FCA5A5",
                    lineHeight: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span>Meetings could not load: {adminMeetingsError}</span>
                  <button
                    type="button"
                    className="adm-ghost-btn"
                    onClick={() => setAdminReloadTick((v) => v + 1)}
                    style={{ fontSize: 12, padding: "6px 10px" }}
                  >
                    Retry
                  </button>
                </div>
              ) : null}
              <AdminOrgPanel
                title={
                  <>
                    Session register (
                    {adminMeetingsLoading
                      ? "…"
                      : allMeetingsAdminSearch.trim() || adminMeetingsDateFrom || adminMeetingsDateTo
                        ? `${filteredAllMeetings.length} of ${dedupedAllMeetings.length}`
                        : dedupedAllMeetings.length}
                    )
                  </>
                }
                subtitle="Expand a row for host, auto-join, recording retention, and participant lists."
              >
                <div
                  className="adm-all-meetings-toolbar"
                  style={{
                    borderBottom: `1px solid ${adminBorder}`,
                    background: isLight ? "#f8fafc" : "rgba(255,255,255,0.02)",
                  }}
                >
                  <div className="adm-all-meetings-toolbar-search">
                    <span className="adm-toolbar-field-label" style={{ color: adminTextMuted }}>
                      Search
                    </span>
                    <AdminSearchToolbar
                      value={allMeetingsAdminSearch}
                      onChange={(arg) => setAdminTabSearch("allMeetings", arg)}
                      debounceMs={ADMIN_SEARCH_DEBOUNCE_MS}
                      placeholder="Title, host, participant, platform…"
                      adminTextMuted={adminTextMuted}
                      filteredCount={filteredAllMeetings.length}
                      totalCount={dedupedAllMeetings.length}
                      alwaysExpanded
                    />
                  </div>
                  <AdminMeetingsListControls
                    dateFrom={adminMeetingsDateFrom}
                    dateTo={adminMeetingsDateTo}
                    sortOrder={adminMeetingsSortOrder}
                    onDateFromChange={setAdminMeetingsDateFrom}
                    onDateToChange={setAdminMeetingsDateTo}
                    onSortOrderChange={setAdminMeetingsSortOrder}
                    onClearDates={() => {
                      setAdminMeetingsDateFrom("");
                      setAdminMeetingsDateTo("");
                    }}
                    isLight={isLight}
                    adminBorder={adminBorder}
                    adminTextMuted={adminTextMuted}
                    adminTextMain={adminTextMain}
                  />
                </div>
                <div className="adm-scroll-panel adm-scroll-panel--meetings" style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {adminMeetingsLoading && dedupedAllMeetings.length === 0 ? (
                    <AdminMeetingsListSkeleton isLight={isLight} rows={5} />
                  ) : null}
                  {!adminMeetingsLoading && filteredAllMeetings.length === 0 && (
                    <div style={{ fontSize: 12.5, color: adminTextMuted, padding: "18px 8px", textAlign: "center", lineHeight: 1.55 }}>
                      {dedupedAllMeetings.length === 0
                        ? "No MeetAI meetings yet. They appear here after a calendar auto-join or a Record meeting dispatch completes."
                        : adminMeetingsDateFrom || adminMeetingsDateTo
                          ? "No meetings match the selected date range."
                          : "No meetings match your search."}
                    </div>
                  )}
                  {filteredAllMeetings.length > 0 ? (
                    <AdminMuiTable
                      isLight={isLight}
                      stickyHeader
                      size="medium"
                      maxHeight="calc(100dvh - 300px)"
                      minWidth={780}
                      columns={[
                        { id: "meeting", label: "Meeting" },
                        { id: "datetime", label: "Date & Time", width: 168, hideOnPhone: true },
                        { id: "host", label: "Host", width: 168, hideOnPhone: true },
                        { id: "meetai", label: "Meet AI", width: 132 },
                        { id: "cost", label: "Cost", align: "right", width: 112, hideOnPhone: true },
                        { id: "joined", label: "Joined", align: "right", width: 84 },
                      ]}
                      rows={pagedAllMeetings.map((m) => {
                            const botId = String(m?.recall_bot_id || m?.recallBotId || "").trim();
                            const actual = botId && usageApi?.meetingActualUsage ? usageApi.meetingActualUsage[botId] : null;
                            const hostLabel = meetingHostName(m) || meetingHostEmail(m) || (!isBotServiceAccountEmail(m?.owner_email || m?.ownerEmail) ? String(m?.owner_name || m?.owner_email || "").trim() : "");
                            const joined = meetingBotJoinedCall(m);
                            const ended = isEndedMeetingStatus(effectiveMeetingStatus(m), m);
                            const pCount = joinedPlusNotJoinedCount(m.participants, attendanceContextFromMeeting(m));
                            const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
                            const meetKey = String(m._id || m.id);
                            return {
                              key: m._id || m.id,
                              onClick: () => setAdminMeetingDrawerKey(meetKey),
                              selected: adminMeetingDrawerKey === meetKey,
                              cells: [
                                    <div key="mt" style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                      <span style={{ width: 28, height: 28, borderRadius: 8, background: isLight ? "rgba(37,99,235,0.12)" : "rgba(21,101,192,0.2)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, color: isLight ? "#1d4ed8" : "#93C5FD", flexShrink: 0 }}>{pl.short}</span>
                                      <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.title}>{m.title}</strong>
                                    </div>,
                                    <span key="dt" style={{ whiteSpace: "nowrap" }}>{formatAdminMeetingDateLabel(m.date || meetingAdminSortYmd(m))} · {formatMeetingTimeForDisplay(m.time)}</span>,
                                    <span key="ho" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={hostLabel || "No host info"}>{hostLabel || "—"}</span>,
                                    <span key="ai">{joined ? (<Badge label="Joined" bg={isLight ? "rgba(22,163,74,0.12)" : "rgba(34,197,94,0.12)"} color={isLight ? "#166534" : "#86EFAC"} />) : ended ? (<Badge label="Didn't Join" bg={isLight ? "rgba(254,243,199,0.55)" : "rgba(251,191,36,0.1)"} color={isLight ? "#A16207" : "#FCD34D"} />) : (<Badge label="Pending" bg={adminChipBg} color={adminTextMuted} />)}</span>,
                                    <strong key="co" style={{ color: "#10B981" }} title={actual ? `Actual billed usage (bot ${botId}${actual.usageTimestamp ? ` · metered ${new Date(actual.usageTimestamp).toLocaleString()}` : ""})` : (usageApi ? "No stored metered usage for this bot" : "Open the Usage tab once to load actuals")}>{actual ? `$${Number(actual.costUsd || 0).toFixed(2)}` : "—"}</strong>,
                                    <span key="pt">{pCount}</span>,
                              ],
                            };
                      })}
                    />
                  ) : null}
                </div>
                <div style={{ padding: "0 14px 12px" }}>
                  <AdminListPagination
                    page={adminMeetingsSafePage}
                    pageSize={adminMeetingsPageSize}
                    total={filteredAllMeetings.length}
                    onPageChange={setAdminMeetingsPage}
                    onPageSizeChange={setAdminMeetingsPageSize}
                    isLight={isLight}
                    adminBorder={adminBorder}
                    adminTextMuted={adminTextMuted}
                    adminTextMain={adminTextMain}
                    label="meetings"
                  />
                </div>
                {(() => {
                  const dm = (dedupedAllMeetings || []).find((x) => String(x._id || x.id) === String(adminMeetingDrawerKey));
                  if (!dm) return null;
                  const m = dm;
                  const pill = (overrides = {}) => ({
                    display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 999,
                    fontSize: 11, fontWeight: 600, border: `1px solid ${adminBorder}`,
                    background: adminChipBg, color: adminTextMain, ...overrides,
                  });
                  const aj = Array.isArray(m.autoJoinOptInUsers) ? m.autoJoinOptInUsers : [];
                  const isCalendarAuto = String(m.type || "").toLowerCase() === "calendar_auto";
                  const attendanceCtx = attendanceContextFromMeeting(m);
                  const { joined: joinedPeople, notJoined: notJoinedPeople } = splitJoinedVsNotJoinedInvitees(m.participants || [], attendanceCtx);
                  const botId = String(m?.recall_bot_id || m?.recallBotId || "").trim();
                  const actual = botId && usageApi?.meetingActualUsage ? usageApi.meetingActualUsage[botId] : null;
                  const dHost = meetingHostName(m) || meetingHostEmail(m) || "";
                  const dChip = (p, idx) => {
                    const lab = formatAdminPersonLabel(p);
                    return (
                      <span key={`${adminMeetingDrawerKey}-dp-${idx}-${String(p.email || p.name || idx)}`} {...personChipHoverHandlers(p, [], tipApi)} style={{ ...pill(), maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
                        {lab}
                      </span>
                    );
                  };
                  const secLabel = (t) => (
                    <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: adminTextMuted, marginBottom: 6 }}>
                      {t}
                    </div>
                  );
                  return (
                    <AdminDetailDrawer
                      open
                      onClose={() => setAdminMeetingDrawerKey(null)}
                      title={m.title}
                      subtitle={`${formatAdminMeetingDateLabel(m.date || meetingAdminSortYmd(m))} · ${formatMeetingTimeForDisplay(m.time)}${dHost ? ` · ${dHost}` : ""}`}
                      isLight={isLight}
                      width={440}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0, maxWidth: "100%", overflowX: "hidden", overflowWrap: "anywhere" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                          {meetingBotJoinedCall(m) ? (
                            <span style={pill({ background: isLight ? "rgba(22,163,74,0.12)" : "rgba(34,197,94,0.12)", borderColor: isLight ? "#86efac" : "rgba(34,197,94,0.35)", color: isLight ? "#166534" : "#86EFAC" })}>MeetAI Joined</span>
                          ) : isEndedMeetingStatus(effectiveMeetingStatus(m), m) ? (
                            <span style={pill({ background: isLight ? "rgba(254,243,199,0.55)" : "rgba(251,191,36,0.1)", borderColor: isLight ? "#fcd34d" : "rgba(251,191,36,0.4)", color: isLight ? "#A16207" : "#FCD34D" })} title={getMeetingBotJoinReasonLong(m)}>MeetAI Didn't Join</span>
                          ) : (
                            <span style={pill()}>Pending</span>
                          )}
                          {actual ? (
                            <span style={pill({ background: "rgba(16,185,129,0.12)", borderColor: "#6ee7b7", color: isLight ? "#047857" : "#6EE7B7" })} title={`Bot ${botId}`}>Cost ${Number(actual.costUsd || 0).toFixed(2)} · {actual.hours}h</span>
                          ) : null}
                          <span style={pill()}>{joinedPeople.length + notJoinedPeople.length} participants</span>
                        </div>
                        {(() => {
                          let displayName = meetingHostName(m);
                          let displayEmail = meetingHostEmail(m);
                          if (!displayName && !displayEmail) {
                            const ownerName = String(m.owner_name || "").trim();
                            const ownerEmail = String(m.owner_email || "").trim();
                            if (ownerEmail && !isBotServiceAccountEmail(ownerEmail)) {
                              displayName = ownerName;
                              displayEmail = ownerEmail;
                            }
                          }
                          if (!displayName && !displayEmail) return null;
                          return (
                            <div>
                              {secLabel("Meeting Host")}
                              <span style={{ ...pill({ fontWeight: 500 }), maxWidth: "100%" }} title={`${displayName} ${displayEmail}`.trim()}>
                                {displayName || "—"}{displayEmail ? ` · ${displayEmail}` : ""}
                              </span>
                            </div>
                          );
                        })()}
                        {(() => {
                          const ajSum = adminMeetingAutoJoinSummary(m, aj);
                          return (
                            <div>
                              {secLabel("Auto Join")}
                              <div style={{ fontSize: 12, color: adminTextMain, fontWeight: 600 }}>{ajSum.on ? ajSum.label : "Off"}</div>
                              <div style={{ fontSize: 11.5, color: adminTextMuted, lineHeight: 1.5, marginTop: 3 }}>{ajSum.detail}</div>
                            </div>
                          );
                        })()}
                        {(() => {
                          const rec = adminMeetingRecordingSummary(m);
                          const s3 = adminMeetingS3RetentionSummary(m);
                          const rm = adminMeetingRecallMediaSummary(m);
                          return (
                            <div>
                              {secLabel("Recording & Retention")}
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                {rm.show ? (<span style={pill(adminMeetingRetentionPillStyle(rm.tone, isLight))} title={rm.title}>{rm.label}</span>) : (<span style={{ fontSize: 11.5, color: adminTextMuted }}>Recall: — (not joined)</span>)}
                                {s3.show ? (<span style={pill(adminMeetingRetentionPillStyle(s3.tone, isLight))} title={s3.title}>{s3.label}</span>) : (<span style={{ fontSize: 11.5, color: adminTextMuted }}>S3: — (Video Rec off)</span>)}
                              </div>
                              {rec.videoRecOn ? (
                                <div style={{ fontSize: 11.5, color: adminTextMain, marginTop: 6 }}>Video Rec: {rec.statusLabel}{rec.enabledBy ? ` · by ${rec.enabledBy}` : ""}</div>
                              ) : null}
                            </div>
                          );
                        })()}
                        {(() => {
                          const isEnded = isEndedMeetingStatus(effectiveMeetingStatus(m), m);
                          const joined = meetingBotJoinedCall(m);
                          const reasonLong = getMeetingBotJoinReasonLong(m);
                          if (!isEnded || (joined && !reasonLong)) return null;
                          return (
                            <div>
                              {secLabel("MeetAI Join Status")}
                              <div style={{ fontSize: 12, color: adminTextMain, fontWeight: 600 }}>{joined ? "Joined and recorded." : "Didn't join."}</div>
                              {reasonLong ? (<div style={{ fontSize: 11.5, color: adminTextMuted, marginTop: 3, lineHeight: 1.5 }}>{reasonLong}</div>) : null}
                            </div>
                          );
                        })()}
                        <div>
                          {secLabel(`Joined (${joinedPeople.length})`)}
                          {joinedPeople.length === 0 ? (<span style={{ fontSize: 12, color: adminTextSoft }}>—</span>) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                              {joinedPeople.map((p, idx) => dChip(p, idx))}
                            </div>
                          )}
                        </div>
                        <div>
                          {secLabel(`Not Joined (${notJoinedPeople.length})`)}
                          {notJoinedPeople.length === 0 ? (<span style={{ fontSize: 12, color: adminTextSoft }}>—</span>) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                              {notJoinedPeople.map((p, idx) => dChip(p, idx))}
                            </div>
                          )}
                        </div>
                        {aj.length > 0 ? (
                          <div>
                            {secLabel(`Auto-join Opt-in (${aj.length})`)}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 140, overflowY: "auto" }}>
                              {aj.map((p, idx) => dChip(p, idx))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </AdminDetailDrawer>
                  );
                })()}
              </AdminOrgPanel>
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("transcriptsEmail") && (
            <AdminTabPanel active={page === "transcriptsEmail"} className="apage adm-premium-page adm-org-form">
              <AdminTranscriptsEmailTab
                meetings={dedupedAllMeetings}
                smtpReport={smtpUsageApi}
                smtpLoading={smtpUsageLoading}
                smtpError={smtpUsageErr}
                isLight={isLight}
                adminTextMain={adminTextMain}
                adminTextMuted={adminTextMuted}
                adminTextSoft={adminTextSoft}
                adminBorder={adminBorder}
                adminSearch={transcriptsEmailAdminSearch}
                setAdminSearch={(arg) => setAdminTabSearch("transcriptsEmail", arg)}
                tabActive={page === "transcriptsEmail"}
                sessionEmail={sessionEmail || adminProfile?.email || ""}
                sessionName={adminProfile?.displayName || ""}
              />
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("users") && (
            <AdminTabPanel active={page === "users"} className="apage adm-premium-page adm-org-form">
              <AdminPremiumHero
                badge="Avaada · Directory"
                badgeIconD={IC.users}
                title="Users"
                desc={ADMIN_USERS_DIRECTORY_NOTE}
                isLight={isLight}
                meta={
                  <span>
                    {adminUsersFiltersActive
                      ? `${sortedAllUsers.length} of ${allUsers.length} accounts`
                      : `${allUsers.length} accounts`}
                  </span>
                }
              />
              <p
                style={{
                  margin: "0 0 14px",
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  color: adminTextMuted,
                  maxWidth: 720,
                }}
              >
                {ADMIN_USERS_DIRECTORY_NOTE}
              </p>
              <AdminOrgPanel
                title={
                  <>
                    Directory (
                    {adminUsersFiltersActive
                      ? `${sortedAllUsers.length} of ${allUsers.length}`
                      : allUsers.length}
                    )
                  </>
                }
                subtitle={
                  adminUsersSortBy === "user" && adminUsersSortDir === "asc" && !adminUsersFiltersActive
                    ? "Search by name or email, filter by member since, or click a row for details. MeetAI service account is excluded."
                    : `Showing ${sortedAllUsers.length} user${sortedAllUsers.length === 1 ? "" : "s"}. Sorted by ${
                        { user: "name", role: "role", owned: "MeetAI joined", attended: "attended", total: "total", since: "member since" }[
                          adminUsersSortBy
                        ] || "name"
                      } (${adminUsersSortDir === "asc" ? "A→Z / low→high / oldest first" : "Z→A / high→low / newest first"}).`
                }
              >
                <div className="adm-users-controls">
                  <div className="adm-users-toolbar adm-users-toolbar--oneline" role="group" aria-label="Search and filters">
                    <label className="adm-users-search" htmlFor="adm-users-search-input">
                      <span className="adm-users-search-icon" aria-hidden>
                        <Svg d={ADM_SEARCH_ICON_D} color={adminTextMuted} size={16} />
                      </span>
                      <input
                        id="adm-users-search-input"
                        type="text"
                        value={usersAdminSearch}
                        onChange={(e) => setAdminTabSearch("users", e.target.value)}
                        placeholder="Search by name or email…"
                        autoComplete="off"
                      />
                      {String(usersAdminSearch || "").trim() ? (
                        <button
                          type="button"
                          className="adm-users-search-clear"
                          onClick={() => setAdminTabSearch("users", "")}
                          aria-label="Clear search"
                          title="Clear search"
                        >
                          <Svg d="M18 6L6 18M6 6l12 12" color={adminTextMuted} size={14} />
                        </button>
                      ) : null}
                    </label>
                    <div className="adm-users-date-range" role="group" aria-label="Member since date range">
                      <span className="adm-users-date-range-label">Member since</span>
                      <div className="adm-users-date-range-fields">
                        <input
                          type="date"
                          value={adminUsersSinceFrom}
                          max={adminUsersSinceTo || undefined}
                          onChange={(e) =>
                            adminDateRangeOnFromChange(
                              e.target.value,
                              adminUsersSinceTo,
                              setAdminUsersSinceFrom,
                              setAdminUsersSinceTo
                            )
                          }
                          aria-label="Member since from"
                        />
                        <span className="adm-users-date-sep" aria-hidden>to</span>
                        <input
                          type="date"
                          value={adminUsersSinceTo}
                          min={adminUsersSinceFrom || undefined}
                          onChange={(e) =>
                            adminDateRangeOnToChange(
                              adminUsersSinceFrom,
                              e.target.value,
                              setAdminUsersSinceFrom,
                              setAdminUsersSinceTo
                            )
                          }
                          aria-label="Member since to"
                        />
                      </div>
                    </div>
                    <div className="adm-users-toolbar-meta">
                      <span className="adm-users-result-chip">
                        {sortedAllUsers.length === allUsers.length
                          ? `${allUsers.length} users`
                          : `${sortedAllUsers.length} of ${allUsers.length}`}
                      </span>
                      {adminUsersFiltersActive ? (
                        <button
                          type="button"
                          className="adm-users-filters-reset"
                          onClick={() => {
                            setAdminTabSearch("users", "");
                            setAdminUsersSinceFrom("");
                            setAdminUsersSinceTo("");
                            setAdminUsersSortBy("user");
                            setAdminUsersSortDir("asc");
                          }}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="adm-scroll-panel adm-scroll-panel--meetings adm-users-table-wrap">
                {sortedAllUsers.length === 0 ? (
                  <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 12.5, color: adminTextMuted }}>
                    {allUsers.length === 0
                      ? "No users have signed in yet."
                      : "No users match your search or filters."}
                  </div>
                ) : (
                  <AdminMuiTable
                    variant="joy"
                    isLight={isLight}
                    stickyHeader
                    maxHeight="min(58vh, 600px)"
                    minWidth={720}
                    sortBy={adminUsersSortBy}
                    sortDir={adminUsersSortDir}
                    onSort={(colId) => {
                      if (adminUsersSortBy === colId) {
                        setAdminUsersSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      } else {
                        setAdminUsersSortBy(colId);
                        setAdminUsersSortDir(colId === "user" || colId === "role" || colId === "since" ? "asc" : "desc");
                      }
                    }}
                    columns={[
                      { id: "user", label: "Name / email", sortable: true },
                      { id: "role", label: "Role", width: 108, sortable: true },
                      { id: "owned", label: "MeetAI joined", align: "right", width: 118, sortable: true },
                      { id: "attended", label: "Attended", align: "right", width: 104, hideOnPhone: true, sortable: true },
                      { id: "total", label: "Total", align: "right", width: 88, hideOnPhone: true, sortable: true },
                      { id: "since", label: "Member since", align: "right", width: 128, hideOnPhone: true, sortable: true },
                    ]}
                    rows={pagedAllUsers.map((u) => {
                      const email = u.email || u;
                      const name = u.name || (typeof u === "string" ? u.split("@")[0] : email);
                      const uid = u.id;
                      const avatarUrl = u.avatar_url || u.avatarUrl || "";
                      const owned = adminUserOwnedCount(u);
                      const attended = adminUserAttendedCount(u);
                      const total = adminUserTotalCount(u);
                      const roleLabel = adminUserDisplayRole(u);
                      let since = "—";
                      try {
                        if (u.created_at) {
                          since = new Date(u.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          });
                        }
                      } catch (_) {}
                      const rowKey = uid || email;
                      return {
                        key: rowKey,
                        onClick: () => setAdminUserDrawerId(uid || null),
                        selected: String(adminUserDrawerId) === String(uid),
                        cells: [
                          <div key="user" className="adm-joy-user-cell">
                            <Avatar
                              initials={name.slice(0, 2).toUpperCase()}
                              size={36}
                              color="#1565C0"
                              imageUrl={avatarUrl}
                              title={`${name}${email ? ` (${email})` : ""}`}
                            />
                            <div className="adm-joy-user-meta">
                              <div className="adm-joy-user-name">{name}</div>
                              <div className="adm-joy-user-email">{email}</div>
                            </div>
                          </div>,
                          <span key="role" className="adm-org-role-pill">{roleLabel}</span>,
                          <strong
                            key="o"
                            className="adm-joy-metric adm-joy-metric--blue"
                            title="Meetings where this user had Auto Join toggle ON (global or per-meeting)"
                          >
                            {owned}
                          </strong>,
                          <strong
                            key="a"
                            className="adm-joy-metric adm-joy-metric--green"
                            title="Dispatched MeetAI sessions where this person was a participant"
                          >
                            {attended}
                          </strong>,
                          <span
                            key="t"
                            className="adm-joy-metric adm-joy-metric--total"
                            title={`Auto Join ON: ${owned}\nAttended: ${attended}\nUnique total: ${total}`}
                          >
                            {total}
                          </span>,
                          <span key="since" className="adm-joy-since">
                            {since}
                          </span>,
                        ],
                      };
                    })}
                  />
                )}
                </div>
                <div style={{ padding: "0 14px 12px" }}>
                  <AdminListPagination
                    page={adminUsersSafePage}
                    pageSize={adminUsersPageSize}
                    total={sortedAllUsers.length}
                    onPageChange={setAdminUsersPage}
                    onPageSizeChange={setAdminUsersPageSize}
                    isLight={isLight}
                    adminBorder={adminBorder}
                    adminTextMuted={adminTextMuted}
                    adminTextMain={adminTextMain}
                    label="users"
                  />
                </div>
              </AdminOrgPanel>
              {adminUserDrawer ? (
                <AdminDetailDrawer
                  open
                  onClose={() => setAdminUserDrawerId(null)}
                  title={adminUserDrawer.name || adminUserDrawer.email || "User"}
                  subtitle={adminUserDrawer.email || ""}
                  isLight={isLight}
                  width={480}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: adminTextMuted }}>{ADMIN_USERS_DIRECTORY_NOTE}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                      <span className="adm-org-role-pill">{adminUserDisplayRole(adminUserDrawer)}</span>
                      {adminUserDrawer.created_at ? (
                        <span style={{ fontSize: 11.5, color: adminTextMuted }}>
                          Member since{" "}
                          {new Date(adminUserDrawer.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      ) : null}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 8,
                      }}
                    >
                      {[
                        { label: "MeetAI joined", val: adminUserOwnedCount(adminUserDrawer), tone: "#1d4ed8" },
                        { label: "Attended", val: adminUserAttendedCount(adminUserDrawer), tone: "#16a34a" },
                        { label: "Total sessions", val: adminUserTotalCount(adminUserDrawer), tone: adminTextMain },
                      ].map((chip) => (
                        <div
                          key={chip.label}
                          style={{
                            border: `1px solid ${adminBorder}`,
                            borderRadius: 10,
                            padding: "10px 10px",
                            background: isLight ? "#f8fafc" : "rgba(255,255,255,0.04)",
                          }}
                        >
                          <div style={{ fontSize: 18, fontWeight: 800, color: chip.tone }}>{chip.val}</div>
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: adminTextMuted, marginTop: 4 }}>{chip.label}</div>
                        </div>
                      ))}
                    </div>
                    <AdminUserMeetingUsageGrid
                      usage={adminUserDrawerMeetingUsage}
                      loading={adminUserDrawerMeetingUsageLoading}
                      error={adminUserDrawerMeetingUsageError}
                      isLight={isLight}
                      adminBorder={adminBorder}
                      adminTextMain={adminTextMain}
                      adminTextMuted={adminTextMuted}
                    />
                  </div>
                </AdminDetailDrawer>
              ) : null}
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("authority") && (
            <AdminTabPanel active={page === "authority"} className="apage adm-premium-page">
              <AuthorityLevels isLight={isLight} />
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("approvals") && (
            <AdminTabPanel active={page === "approvals"} className="apage adm-premium-page">
              <ApprovalsTab isLight={isLight} />
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("feedback") && (
            <AdminTabPanel active={page === "feedback"} className="apage adm-premium-page adm-org-form">
              <AdminPremiumHero
                badge="Avaada · Product feedback"
                badgeIconD={IC.star}
                title="Feedback"
                desc="All user ratings and messages submitted from the app. Screenshots are stored securely and viewable here."
                isLight={isLight}
                meta={
                  <span>
                    {adminFeedbackLoading
                      ? "Loading…"
                      : feedbackAdminSearch.trim()
                        ? `${filteredAdminFeedback.length} of ${adminFeedbackTotal} entries`
                        : `${adminFeedbackTotal} entries`}
                  </span>
                }
              />
              <AdminOrgPanel
                toolbar={
                  <AdminSearchToolbar
                    value={feedbackAdminSearch}
                    onChange={(arg) => setAdminTabSearch("feedback", arg)}
                    placeholder="Search feedback by user, message, rating…"
                    adminTextMuted={adminTextMuted}
                    filteredCount={filteredAdminFeedback.length}
                    totalCount={adminFeedbackTotal}
                  />
                }
                title={
                  <>
                    All feedback (
                    {feedbackAdminSearch.trim()
                      ? `${filteredAdminFeedback.length} of ${adminFeedbackTotal}`
                      : adminFeedbackTotal}
                    )
                  </>
                }
                subtitle="Newest first. Expand a row to read the full message and open screenshots."
              >
                <div className="adm-scroll-panel">
                  {adminFeedbackLoading && adminFeedbackItems.length === 0 ? (
                    <div style={{ padding: 20, fontSize: 12.5, color: adminTextSoft }}>Loading feedback…</div>
                  ) : adminFeedbackErr && adminFeedbackItems.length === 0 ? (
                    <div style={{ padding: 20, fontSize: 12.5, color: "#b91c1c" }}>{adminFeedbackErr}</div>
                  ) : filteredAdminFeedback.length === 0 ? (
                    <div style={{ padding: "28px 18px", textAlign: "center", fontSize: 13, color: adminTextSoft, lineHeight: 1.6 }}>
                      {adminFeedbackItems.length === 0
                        ? "No feedback yet. When users submit ratings from the profile menu, they appear here."
                        : "No feedback matches your search."}
                    </div>
                  ) : (
                    <AdminMuiTable
                      isLight={isLight}
                      stickyHeader
                      maxHeight={520}
                      minWidth={760}
                      columns={[
                        { id: "when", label: "When", width: 128 },
                        { id: "user", label: "User" },
                        { id: "rating", label: "Rating", width: 100 },
                        { id: "message", label: "Message" },
                        { id: "imgs", label: "Images", width: 88, align: "right", hideOnPhone: true },
                        { id: "mail", label: "Email", width: 88, hideOnPhone: true },
                      ]}
                      rows={filteredAdminFeedback.map((f) => {
                        const open = adminFeedbackOpenId === f.id;
                        const stars = "★".repeat(Math.max(0, Math.min(5, Number(f.rating) || 0)));
                        const when = f.createdAt
                          ? new Date(f.createdAt).toLocaleString("en-IN", {
                              timeZone: APP_DISPLAY_TZ,
                              day: "2-digit",
                              month: "short",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : "—";
                        const msgPreview = String(f.message || "").trim() || "—";
                        const atts = Array.isArray(f.attachments) ? f.attachments : [];
                        return {
                          key: f.id,
                          onClick: () => setAdminFeedbackOpenId(open ? null : f.id),
                          selected: open,
                          cells: [
                            <span key="w" style={{ fontSize: 12, color: adminTextMuted, whiteSpace: "nowrap" }}>
                              {when}
                            </span>,
                            <div key="u" style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {f.userName || "User"}
                              </div>
                              <div style={{ fontSize: 10.5, color: adminTextSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {f.userEmail || ""}
                              </div>
                            </div>,
                            <span key="r" style={{ color: "#CA8A04", fontWeight: 700, letterSpacing: 0.5, whiteSpace: "nowrap" }} title={`${f.rating}/5`}>
                              {stars || "—"}
                            </span>,
                            <span
                              key="m"
                              style={{
                                display: "block",
                                fontSize: 12.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 280,
                              }}
                              title={msgPreview}
                            >
                              {msgPreview}
                            </span>,
                            <strong key="i" style={{ fontSize: 12.5 }}>
                              {atts.length || "—"}
                            </strong>,
                            <span
                              key="e"
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: f.emailSent ? "#15803d" : f.emailError ? "#b91c1c" : "#b45309",
                              }}
                              title={f.emailError || (f.emailSent ? "Developer email sent" : "Email not sent")}
                            >
                              {f.emailSent ? "Sent" : f.emailError ? "Failed" : "Saved"}
                            </span>,
                          ],
                          detail: open ? (
                            <div style={{ padding: "12px 4px 4px", display: "flex", flexDirection: "column", gap: 12 }}>
                              {f.page ? (
                                <div style={{ fontSize: 11.5, color: adminTextMuted }}>
                                  Page: <strong style={{ color: adminTextMain }}>{f.page}</strong>
                                </div>
                              ) : null}
                              <div
                                style={{
                                  fontSize: 13,
                                  lineHeight: 1.55,
                                  color: adminTextMain,
                                  whiteSpace: "pre-wrap",
                                  background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.04)",
                                  border: `1px solid ${adminBorder}`,
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                }}
                              >
                                {String(f.message || "").trim() || "No message"}
                              </div>
                              {atts.length ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                  {atts.map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAdminFeedbackPreview({
                                          path: a.url,
                                          filename: a.filename,
                                          contentType: a.contentType,
                                        });
                                      }}
                                      style={{
                                        border: `1px solid ${adminBorder}`,
                                        background: "#fff",
                                        borderRadius: 10,
                                        padding: 4,
                                        cursor: "pointer",
                                      }}
                                      title={a.filename || "Open image"}
                                    >
                                      <AdminAuthImage
                                        path={a.url}
                                        alt={a.filename || "Screenshot"}
                                        style={{ width: 96, height: 72 }}
                                      />
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ fontSize: 12, color: adminTextSoft }}>No screenshots attached.</div>
                              )}
                            </div>
                          ) : null,
                        };
                      })}
                    />
                  )}
                </div>
              </AdminOrgPanel>
              {adminFeedbackPreview
                ? createPortal(
                    <div
                      role="dialog"
                      aria-modal="true"
                      aria-label="Feedback image"
                      onClick={() => setAdminFeedbackPreview(null)}
                      style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 6200,
                        background: "rgba(15,23,42,0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 20,
                      }}
                    >
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          background: "#fff",
                          borderRadius: 14,
                          padding: 12,
                          maxWidth: "min(920px, 96vw)",
                          maxHeight: "90vh",
                          overflow: "auto",
                          boxShadow: "0 20px 50px rgba(15,23,42,0.28)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                            {adminFeedbackPreview.filename || "Screenshot"}
                          </div>
                          <button
                            type="button"
                            onClick={() => setAdminFeedbackPreview(null)}
                            aria-label="Close"
                            title="Close"
                            style={{
                              flexShrink: 0,
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              border: "1px solid #E2E8F0",
                              background: "#F8FAFC",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "#64748B",
                              padding: 0,
                            }}
                          >
                            <Svg d={IC.close} size={16} color="currentColor" stroke={2} />
                          </button>
                        </div>
                        <AdminAuthImage
                          path={adminFeedbackPreview.path}
                          alt={adminFeedbackPreview.filename || "Screenshot"}
                          style={{ width: "100%", minHeight: 240, maxHeight: "75vh" }}
                        />
                      </div>
                    </div>,
                    document.body
                  )
                : null}
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("summary") && (
            <AdminTabPanel active={page === "summary"} className="apage adm-premium-page adm-org-form">
              <AdminSummarySettings
                isLight={isLight}
                adminTextMain={adminTextMain}
                adminTextMuted={adminTextMuted}
                adminTextSoft={adminTextSoft}
                adminBorder={adminBorder}
                adminChipBg={adminChipBg}
                adminPanelBg={adminPanelBg}
              />
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("smtp") && (
            <AdminTabPanel active={page === "smtp"} className="apage adm-premium-page adm-org-form">
              <AdminSMTP
                isLight={isLight}
                adminTextMain={adminTextMain}
                adminTextMuted={adminTextMuted}
                adminTextSoft={adminTextSoft}
                adminBorder={adminBorder}
                adminChipBg={adminChipBg}
                adminPanelBg={adminPanelBg}
                smtpReport={smtpUsageApi}
                smtpLoading={smtpUsageLoading}
                smtpError={smtpUsageErr}
              />
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("access") && (
            <AdminTabPanel active={page === "access"} className="apage adm-premium-page adm-org-form">
              <AdminPremiumHero
                badge="Avaada · Access control"
                badgeIconD={IC.shield}
                title="Availability — who can use MeetAI"
                desc="Payroll companies from ai_public.emp_master. Paused companies see 'temporarily unavailable' and get no login, no bot, no mail — until you allow the company or grant a single person below."
                isLight={isLight}
              />
              <div style={{ marginTop: 14 }}>
                <AdminAccessTab
                  active={page === "access"}
                  isLight={isLight}
                  adminTextMain={adminTextMain}
                  adminTextMuted={adminTextMuted}
                  adminTextSoft={adminTextSoft}
                  adminBorder={adminBorder}
                  adminPanelBg={adminPanelBg}
                />
              </div>
            </AdminTabPanel>
          )}

          {adminReady && adminTabSeen("logs") && (
            <AdminTabPanel active={page === "logs"} className="apage adm-premium-page adm-org-form">
              <AdminPremiumHero
                badge="Avaada · Audit trail"
                badgeIconD={IC.actionList}
                title="Activity logs"
                desc="Org audit of MeetAI dispatch, meeting end, and recap email events. Expand a row for participants and auto-join details."
                isLight={isLight}
                meta={
                  <span>
                    {logsAdminSearch.trim() || adminLogsFiltersActive
                      ? `${filteredLogs.length} of ${logs.length} entries`
                      : `${logs.length} entries`}
                  </span>
                }
              />
               <AdminOrgPanel
                  toolbar={
                   <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                     <AdminSearchToolbar
                       value={logsAdminSearch}
                       onChange={(arg) => setAdminTabSearch("logs", arg)}
                       placeholder="Search logs (e.g. kanchan test19)…"
                       adminTextMuted={adminTextMuted}
                       filteredCount={filteredLogs.length}
                       totalCount={logs.length}
                     />
                     <select
                       value={logFilterAction}
                       onChange={(e) => setLogFilterAction(e.target.value)}
                       style={{
                         padding: "4px 8px",
                         fontSize: 11,
                         borderRadius: 6,
                         border: `1px solid ${adminBorder}`,
                         background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                         color: adminTextMain,
                         cursor: "pointer",
                       }}
                     >
                       <option value="all">All Actions</option>
                       <option value="Bot dispatched">Bot dispatched</option>
                       <option value="Meeting ended">Meeting ended</option>
                       <option value="Emails sent">Emails sent</option>
                     </select>
                     <select
                       value={logFilterType}
                       onChange={(e) => setLogFilterType(e.target.value)}
                       style={{
                         padding: "4px 8px",
                         fontSize: 11,
                         borderRadius: 6,
                         border: `1px solid ${adminBorder}`,
                         background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                         color: adminTextMain,
                         cursor: "pointer",
                       }}
                     >
                       <option value="all">All Types</option>
                       <option value="join">Join</option>
                       <option value="end">End</option>
                       <option value="email">Email</option>
                     </select>
                     <input
                       type="text"
                       placeholder="Owner…"
                       value={logFilterOwner}
                       onChange={(e) => setLogFilterOwner(e.target.value)}
                       style={{
                         padding: "4px 8px",
                         fontSize: 11,
                         borderRadius: 6,
                         border: `1px solid ${adminBorder}`,
                         background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                         color: adminTextMain,
                         width: 120,
                       }}
                     />
                     <input
                       type="text"
                       placeholder="Meeting…"
                       value={logFilterMeeting}
                       onChange={(e) => setLogFilterMeeting(e.target.value)}
                       style={{
                         padding: "4px 8px",
                         fontSize: 11,
                         borderRadius: 6,
                         border: `1px solid ${adminBorder}`,
                         background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                         color: adminTextMain,
                         width: 140,
                       }}
                     />
                     <input
                       type="date"
                       value={logFilterDateFrom}
                       max={logFilterDateTo || undefined}
                       onChange={(e) =>
                         adminDateRangeOnFromChange(
                           e.target.value,
                           logFilterDateTo,
                           setLogFilterDateFrom,
                           setLogFilterDateTo
                         )
                       }
                       style={{
                         padding: "4px 8px",
                         fontSize: 11,
                         borderRadius: 6,
                         border: `1px solid ${adminBorder}`,
                         background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                         color: adminTextMain,
                       }}
                     />
                     <span style={{ fontSize: 11, color: adminTextMuted }}>to</span>
                     <input
                       type="date"
                       value={logFilterDateTo}
                       min={logFilterDateFrom || undefined}
                       onChange={(e) =>
                         adminDateRangeOnToChange(
                           logFilterDateFrom,
                           e.target.value,
                           setLogFilterDateFrom,
                           setLogFilterDateTo
                         )
                       }
                       style={{
                         padding: "4px 8px",
                         fontSize: 11,
                         borderRadius: 6,
                         border: `1px solid ${adminBorder}`,
                         background: isLight ? "#fff" : "rgba(255,255,255,0.06)",
                         color: adminTextMain,
                       }}
                     />
                     {(adminLogsFiltersActive || logsAdminSearch.trim()) ? (
                       <button
                         type="button"
                         onClick={() => {
                           setAdminTabSearch("logs", "");
                           setLogFilterAction("all");
                           setLogFilterType("all");
                           setLogFilterOwner("");
                           setLogFilterMeeting("");
                           setLogFilterDateFrom("");
                           setLogFilterDateTo("");
                         }}
                         style={{
                           padding: "4px 8px",
                           fontSize: 11,
                           borderRadius: 6,
                           border: `1px solid ${adminBorder}`,
                           background: "transparent",
                           color: "#DC2626",
                           cursor: "pointer",
                         }}
                       >
                         Clear filters
                       </button>
                     ) : null}
                   </div>
                 }
                 title={
                   <>
                     Activity Logs (
                     {logsAdminSearch.trim() || adminLogsFiltersActive
                       ? `${filteredLogs.length} of ${logs.length}`
                       : logs.length}
                     )
                   </>
                 }
                 subtitle="Dispatch, meeting end, and recap emails. Expand a row for participant names and auto-join opt-ins."
               >
                 <div className="adm-scroll-panel">
                  {logsLoading ? (
                    <div style={{padding:"28px 18px",textAlign:"center",fontSize:13,color:isLight?"#64748b":"rgba(255,255,255,0.35)",lineHeight:1.6}}>
                      Loading activity logs…
                    </div>
                  ) : filteredLogs.length===0 ? (
                   <div style={{padding:"28px 18px",textAlign:"center",fontSize:13,color:isLight?"#64748b":"rgba(255,255,255,0.35)",lineHeight:1.6}}>
                     {logs.length === 0
                       ? "No activity yet. Logs are built from real meetings (dispatch, end, emails sent)."
                       : "No activity logs match your search or filters."}
                   </div>
                 ) : (
                   <AdminMuiTable
                     isLight={isLight}
                     columns={[
                       { id: "date", label: "Date", width: 100 },
                       { id: "time", label: "Time", width: 80 },
                       { id: "type", label: "", width: 28 },
                       { id: "meeting", label: "Meeting", width: 150 },
                       { id: "action", label: "Activity" },
                       { id: "chev", label: "", width: 36, align: "center" },
                     ]}
                     rows={pagedFilteredLogs.map((log, i) => {
                       const { date: d, time: t } = formatActivityLogDateTime(log);
                       const meetingDate = log.meetingDate || "";
                       const meetingTitle = log.meetingTitle || log.detail || "";
                       const humans = Array.isArray(log.humanParticipants) ? log.humanParticipants : [];
                       const parts = Array.isArray(log.participants) ? log.participants : [];
                       const { joined: logJoined, notJoined: logNotJoined } = splitJoinedVsNotJoinedInvitees(
                         parts,
                         {
                           transcriptSpeakers: [],
                           hostEmail: log.ownerEmail || log.hostEmail || "",
                           hostName: log.ownerName || log.hostName || "",
                         }
                       );
                       const botsInLog = (parts || []).filter(participantIsBot);
                       const aj = Array.isArray(log.autoJoinOptInUsers) ? log.autoJoinOptInUsers : [];
                       const hasDetail = Boolean(
                         log.ownerEmail ||
                           log.ownerName ||
                           humans.length ||
                           parts.length ||
                           aj.length ||
                           log.googleEventId ||
                           log.meetingTitle
                       );
                       const open = expandedLogIdx === i;
                       const soft = isLight ? "#64748b" : "rgba(255,255,255,0.35)";
                       const ink = isLight ? "#0f172a" : "#E8F0F7";
                       const sectionLabel = {
                         fontWeight: 700,
                         color: isLight ? "#334155" : "rgba(255,255,255,0.45)",
                         fontSize: 10,
                         textTransform: "uppercase",
                         letterSpacing: "0.06em",
                         marginBottom: 4,
                       };
                       return {
                         key: i,
                         selected: open,
                         onClick: hasDetail
                           ? () => setExpandedLogIdx(open ? null : i)
                           : undefined,
                         onKeyDown: hasDetail
                           ? (e) => {
                               if (e.key === "Enter" || e.key === " ") {
                                 e.preventDefault();
                                 setExpandedLogIdx(open ? null : i);
                               }
                             }
                           : undefined,
                         cells: [
                           <span key="d" style={{ fontSize: 11, color: soft }}>{d || "—"}</span>,
                           <span key="t" style={{ fontSize: 11, color: soft, fontFamily: "monospace" }}>{t}</span>,
                           <div
                             key="dot"
                             style={{
                               width: 6,
                               height: 6,
                               borderRadius: "50%",
                               background:
                                 log.type === "join"
                                   ? "#1565C0"
                                   : log.type === "email"
                                     ? "#22C55E"
                                     : "rgba(255,255,255,0.2)",
                             }}
                           />,
                           <div key="mt" style={{ minWidth: 0, fontSize: 11, color: ink }}>
                             {meetingTitle || "—"}
                             {meetingDate ? (
                               <div style={{ fontSize: 9, color: soft }}>{meetingDate}</div>
                             ) : null}
                           </div>,
                           <div key="act" style={{ minWidth: 0 }}>
                             <div>
                               <span style={{ fontSize: 12.5, fontWeight: 600, color: ink, marginRight: 8 }}>{log.action}</span>
                               <span style={{ fontSize: 12, color: soft }}>{log.detail}</span>
                             </div>
                             {hasDetail && !open ? (
                               <div style={{ fontSize: 10.5, color: soft, marginTop: 4, lineHeight: 1.45 }}>
                                 {[
                                   parts.length ? `${logJoined.length} joined` : null,
                                   logNotJoined.length ? `${logNotJoined.length} not joined` : null,
                                   aj.length ? `Auto join ${aj.length}` : null,
                                 ]
                                   .filter(Boolean)
                                   .join(" · ") || "Tap for details"}
                               </div>
                             ) : null}
                           </div>,
                           hasDetail ? (
                             <Svg key="ch" d={open ? IC.chevUp : IC.chevDown} color={soft} size={16} stroke={2} />
                           ) : (
                             <span key="ch" />
                           ),
                         ],
                         detail: hasDetail ? (
                           <div>
                             <div style={{ ...sectionLabel, display: "flex", gap: 16, flexWrap: "wrap" }}>
                               <span><strong>Meeting:</strong> {log.meetingTitle || "—"}</span>
                               <span><strong>Meeting ID:</strong> {log.meetingId || "—"}</span>
                             </div>
                             <div style={sectionLabel}>Owner</div>
                             <div style={{ color: ink, marginBottom: 12 }}>
                               {formatAdminPersonLabel({ name: log.ownerName, email: log.ownerEmail })}
                             </div>
                             {log.scheduledStart || log.scheduledEnd || log.actualStart || log.actualEnd ? (
                               <div style={sectionLabel}>Meeting Times</div>
                             ) : null}
                             {log.scheduledStart ? (
                               <div style={{ color: ink, marginBottom: 4, fontSize: 11 }}>
                                 <span style={{ opacity: 0.7 }}>Scheduled start:</span> {formatDateTimeInAppTz(log.scheduledStart) || log.scheduledStart}
                               </div>
                             ) : null}
                             {log.scheduledEnd ? (
                               <div style={{ color: ink, marginBottom: 4, fontSize: 11 }}>
                                 <span style={{ opacity: 0.7 }}>Scheduled end:</span> {formatDateTimeInAppTz(log.scheduledEnd) || log.scheduledEnd}
                               </div>
                             ) : null}
                             {log.actualStart ? (
                               <div style={{ color: ink, marginBottom: 4, fontSize: 11 }}>
                                 <span style={{ opacity: 0.7 }}>Actual start:</span> {formatDateTimeInAppTz(log.actualStart) || log.actualStart}
                               </div>
                             ) : null}
                             {log.actualEnd ? (
                               <div style={{ color: ink, marginBottom: 10, fontSize: 11 }}>
                                 <span style={{ opacity: 0.7 }}>Actual end:</span> {formatDateTimeInAppTz(log.actualEnd) || log.actualEnd}
                               </div>
                             ) : null}
                             <div style={sectionLabel}>Joined ({logJoined.length})</div>
                             <div style={{ color: ink, marginBottom: 10 }}>
                               {logJoined.length
                                 ? logJoined.map((p, j) => (
                                     <div key={`lj-${j}`} style={{ marginBottom: 3 }}>
                                       {formatAdminPersonLabel(p)}
                                     </div>
                                   ))
                                 : "—"}
                             </div>
                             <div style={sectionLabel}>Not joined ({logNotJoined.length})</div>
                             <div style={{ color: ink, marginBottom: 10, fontSize: 11 }}>
                               {logNotJoined.length
                                 ? logNotJoined.map((p, j) => (
                                     <div key={`nj-${j}`} style={{ marginBottom: 3 }}>
                                       {formatAdminPersonLabel(p)}
                                     </div>
                                   ))
                                 : "—"}
                               <div style={{ marginTop: 6, opacity: 0.75, fontSize: 10 }}>
                                 Inferred: invite list without Recall join signal (see All Meetings for notes).
                               </div>
                             </div>
                             {botsInLog.length ? (
                               <>
                                 <div style={sectionLabel}>MeetAI ({botsInLog.length})</div>
                                 <div style={{ color: ink, marginBottom: 12 }}>
                                   {botsInLog.map((p, j) => (
                                     <div key={`b-${j}`} style={{ marginBottom: 3 }}>
                                       {formatAdminPersonLabel(p)}
                                     </div>
                                   ))}
                                 </div>
                               </>
                             ) : null}
                             <div style={sectionLabel}>Auto join opt-in ({aj.length})</div>
                             <div style={{ color: ink, marginBottom: log.googleEventId ? 10 : 0 }}>
                               {aj.length ? (
                                 aj.map((u, j) => (
                                   <div key={j} style={{ marginBottom: 3 }}>
                                     {formatAdminPersonLabel(u)}
                                     <span style={{ fontSize: 10, opacity: 0.75 }}>
                                       {" "}
                                       · {u.via === "override" ? "per meeting" : "global on"}
                                     </span>
                                   </div>
                                 ))
                               ) : (
                                 "No matching calendar rows for this meeting's Google event."
                               )}
                             </div>
                             {log.googleEventId ? (
                               <div style={{ fontSize: 10, fontFamily: "monospace", opacity: 0.75 }}>
                                 google_event_id: {log.googleEventId}
                               </div>
                             ) : null}
                           </div>
                         ) : null,
                       };
                     })}
                   />
                 )}
                 {!logsLoading && filteredLogs.length > adminLogsPageSize ? (
                   <div
                     style={{
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "space-between",
                       gap: 12,
                       flexWrap: "wrap",
                       padding: "10px 4px 4px",
                       fontSize: 12,
                       color: adminTextMuted,
                     }}
                   >
                     <span>
                       Page {adminLogsSafePage} of {adminLogsTotalPages} · {filteredLogs.length} entries
                     </span>
                     <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                       <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                         Rows
                         <select
                           value={adminLogsPageSize}
                           onChange={(e) => setAdminLogsPageSize(Number(e.target.value) || ADMIN_LOGS_PAGE_SIZE_DEFAULT)}
                           style={{
                             padding: "4px 8px",
                             fontSize: 11,
                             borderRadius: 6,
                             border: `1px solid ${adminBorder}`,
                           }}
                         >
                           <option value={25}>25</option>
                           <option value={50}>50</option>
                           <option value={100}>100</option>
                         </select>
                       </label>
                       <button
                         type="button"
                         className="adm-ghost-btn"
                         disabled={adminLogsSafePage <= 1}
                         onClick={() => setAdminLogsPage((p) => Math.max(1, p - 1))}
                       >
                         Previous
                       </button>
                       <button
                         type="button"
                         className="adm-ghost-btn"
                         disabled={adminLogsSafePage >= adminLogsTotalPages}
                         onClick={() => setAdminLogsPage((p) => Math.min(adminLogsTotalPages, p + 1))}
                       >
                         Next
                       </button>
                     </div>
                   </div>
                 ) : null}
                 </div>
               </AdminOrgPanel>
            </AdminTabPanel>
          )}
        </div>
      </div>

      {/* Sidebar — off-canvas drawer on mobile; rendered after main so it never stacks above content */}
      {isAdminMobile && adminMobileNavOpen ? (
        <button
          type="button"
          className="bot-admin-nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setAdminMobileNavOpen(false)}
        />
      ) : null}
      <aside
        className={`bot-admin-sidebar bot-admin-sidebar--premium${adminSidebarCollapsed && !isAdminMobile ? " is-collapsed" : ""}${adminMobileNavOpen ? " is-mobile-open" : ""}`}
        aria-expanded={isAdminMobile ? adminMobileNavOpen : !adminSidebarCollapsed}
        aria-hidden={isAdminMobile && !adminMobileNavOpen}
      >
        <div className="adm-sidebar-brand-block">
          <div className="adm-brand-card">
            <div className="adm-brand-head">
              <div className="adm-brand-row" style={{ flex: 1, minWidth: 0 }}>
                <div className="adm-brand-logo-wrap">
                  <img src={APP_LOGO} alt="Avaada" />
                </div>
                <div className="adm-brand-text">
                  <div className="adm-brand-title">MeetAI Admin</div>
                  <div className="adm-brand-sub">AVAADA</div>
                </div>
              </div>
              {isAdminMobile ? (
                <button
                  type="button"
                  className="adm-sidebar-collapse-btn"
                  onClick={() => setAdminMobileNavOpen(false)}
                  title="Close menu"
                  aria-label="Close navigation"
                >
                  <Svg d={IC.close} size={16} color="currentColor" stroke={2.2} />
                </button>
              ) : (
                <button
                  type="button"
                  className="adm-sidebar-collapse-btn"
                  onClick={() => {
                    (tipApi.hidePersonHoverTipNow || tipApi.hidePersonHoverTipSoon)();
                    setAdminSidebarCollapsed((v) => !v);
                  }}
                  title={adminSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  aria-label={adminSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  <Svg d={IC.menu} size={16} color="currentColor" stroke={2.2} />
                </button>
              )}
            </div>
          </div>
        </div>
        <nav className="adm-sidebar-nav" style={{ flex: 1, display: "flex", flexDirection: "column" }} aria-label="Admin navigation">
          {ADMIN_NAV_GROUPS.map((group) => (
            <div key={group.label} style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: adminSidebarCollapsed && !isAdminMobile ? "center" : "stretch" }}>
              <div className="adm-sidebar-nav-label">{group.label}</div>
              {group.items.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={`adm-nav${page === n.id ? " active" : ""}`}
                  onClick={() => {
                    goAdminPage(n.id);
                    if (isAdminMobile) setAdminMobileNavOpen(false);
                  }}
                  aria-label={n.label}
                  {...bindSidebarHoverTip(tipApi, n.label, adminSidebarCollapsed && !isAdminMobile)}
                >
                  <span className="adm-nav-icon" aria-hidden>
                    <Svg d={n.icon} color="currentColor" size={17} stroke={page === n.id ? 2.2 : 1.9} />
                  </span>
                  <span className="adm-nav-label">{n.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <SignOutConfirmModal
        open={signOutConfirmOpen}
        onCancel={()=>setSignOutConfirmOpen(false)}
        onConfirm={()=>{ setSignOutConfirmOpen(false); onLogout(); }}
        dark
      />
      <AdminCalendarMeetingDetailPopup
        meeting={adminCalDetailMeeting}
        onClose={() => setAdminCalDetailMeeting(null)}
        onOpenJoin={(m) => adminCalendarOpenJoinLink(m)}
        onOpenCalendar={(m) => adminCalendarOpenCalendarLink(m)}
      />
    </div>
  );
}

function smtpChartNiceMax(values) {
  const peak = Math.max(0, ...values);
  if (peak <= 0) return 5;
  if (peak <= 5) return 5;
  if (peak <= 10) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(peak)));
  const norm = peak / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * mag;
}

/** Hover + tooltip title for SMTP daily buckets (full calendar date, not just "Fri 19"). */
function smtpChartBucketTitle(p) {
  const ymd = String(p?.ymd || p?.key || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const d = new Date(`${ymd}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      const long = new Intl.DateTimeFormat("en-IN", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(d);
      const short = String(p?.label || "").trim();
      return short && short !== long ? `${long} · ${short}` : long;
    }
  }
  return p?.label || ymd || "Point";
}

/** Line chart for SMTP recap trends. */
function SmtpUsageLineChart({ series, isLight, adminTextMuted }) {
  const points = Array.isArray(series) ? series : [];
  const emptyColor = isLight ? "#94a3b8" : "rgba(255,255,255,0.35)";
  const markerFill = isLight ? "#ffffff" : "#1e293b";
  const [hoverTip, setHoverTip] = useState(null);
  const wrapRef = useRef(null);

  const lineDefs = [
    { key: "sent", label: "Recap sent", color: "#16a34a" },
    { key: "notSent", label: "Recap not sent", color: "#f97316" },
  ];

  if (points.length === 0) {
    return (
      <div className="smtp-line-chart-wrap">
        <div className="cu-chart-empty" style={{ color: emptyColor, padding: 24, textAlign: "center", fontSize: 13 }}>
          No recap email activity for this period.
        </div>
      </div>
    );
  }

  const allVals = points.flatMap((p) => [Number(p.sent) || 0, Number(p.notSent) || 0]);
  const maxY = smtpChartNiceMax(allVals);
  const W = 1200;
  const H = 280;
  const padL = 52;
  const padR = 24;
  const padT = 20;
  const padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const gridColor = isLight ? "#e8eef4" : "rgba(255,255,255,0.08)";
  const axisColor = isLight ? "#94a3b8" : "rgba(255,255,255,0.4)";
  const yTicks = 5;

  const xAt = (i) =>
    padL + (points.length <= 1 ? plotW / 2 : (i / Math.max(1, points.length - 1)) * plotW);
  const yAt = (v) => padT + plotH - (Math.max(0, v) / maxY) * plotH;

  const showPointTip = (e, p, ln) => {
    const host = wrapRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();
    const x = Math.min(r.width - 12, Math.max(12, e.clientX - r.left));
    const y = Math.max(10, e.clientY - r.top);
    setHoverTip({
      x,
      y,
      label: smtpChartBucketTitle(p),
      lineLabel: ln.label,
      value: Number(p[ln.key]) || 0,
      color: ln.color,
    });
  };

  return (
    <div className="smtp-line-chart-wrap" ref={wrapRef}>
      <div className="smtp-line-legend">
        {lineDefs.map((ln) => (
          <span key={ln.key} className="smtp-line-legend-item" style={{ color: ln.color }}>
            <span className="smtp-line-legend-swatch" style={{ borderColor: ln.color }} aria-hidden />
            {ln.label}
          </span>
        ))}
      </div>
      <AdminChartScroll points={points.length} pointWidth={110} minVisible={8}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        className="cu-chart-svg"
        role="img"
        aria-label="SMTP recap line chart"
        onMouseLeave={() => setHoverTip(null)}
      >
        {Array.from({ length: yTicks + 1 }, (_, ti) => {
          const yv = (maxY / yTicks) * ti;
          const y = yAt(yv);
          return (
            <g key={ti}>
              <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke={gridColor} strokeWidth={1} />
              <text x={padL - 8} y={y + 4} textAnchor="end" fontSize={10} fill={axisColor}>
                {Number.isInteger(yv) ? yv : yv.toFixed(1)}
              </text>
            </g>
          );
        })}
        {points.map((_, i) => {
          // Thin vertical gridlines when months/points grow so they don't merge.
          if (points.length > 10 && i % Math.ceil(points.length / 10) !== 0) return null;
          return (
            <line
              key={`vx-${i}`}
              x1={xAt(i)}
              y1={padT}
              x2={xAt(i)}
              y2={padT + plotH}
              stroke={gridColor}
              strokeWidth={1}
            />
          );
        })}
        {lineDefs.map((ln) => {
          const pathD = points
            .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(Number(p[ln.key]) || 0).toFixed(1)}`)
            .join(" ");
          return (
            <g key={ln.key}>
              <path d={pathD} fill="none" stroke={ln.color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
              {points.map((p, i) => (
                <circle
                  key={`${ln.key}-${i}`}
                  cx={xAt(i)}
                  cy={yAt(Number(p[ln.key]) || 0)}
                  r={hoverTip?.label === smtpChartBucketTitle(p) && hoverTip?.lineLabel === ln.label ? 6 : 5}
                  fill={markerFill}
                  stroke={ln.color}
                  strokeWidth={2}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => showPointTip(e, p, ln)}
                  onMouseMove={(e) => showPointTip(e, p, ln)}
                  onFocus={(e) => showPointTip(e, p, ln)}
                  onBlur={() => setHoverTip(null)}
                />
              ))}
            </g>
          );
        })}
        {points.map((p, i) => {
          // Thin x labels when months/points grow — every point stays hoverable via its dot.
          if (points.length > 10 && i % Math.ceil(points.length / 10) !== 0) return null;
          return (
            <text
              key={`xl-${p.ymd || p.key || i}`}
              x={xAt(i)}
              y={H - 10}
              textAnchor="middle"
              fontSize={10}
              fill={axisColor}
            >
              {p.label}
            </text>
          );
        })}
      </svg>
      </AdminChartScroll>
      {hoverTip ? (
        <div
          className="smtp-chart-tooltip"
          style={{
            left: hoverTip.x,
            top: hoverTip.y,
            background: "#111827",
            borderColor: "rgba(255,255,255,0.24)",
            color: "#FFFFFF",
            opacity: 1,
            mixBlendMode: "normal",
            WebkitTextFillColor: "#FFFFFF",
          }}
        >
          <div className="smtp-chart-tooltip-title" style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}>{hoverTip.label}</div>
          <div className="smtp-chart-tooltip-row" style={{ color: "#E5E7EB", WebkitTextFillColor: "#E5E7EB" }}>
            <span className="smtp-chart-tooltip-dot" style={{ background: hoverTip.color }} />
            <span style={{ color: "#E5E7EB", WebkitTextFillColor: "#E5E7EB", fontWeight: 700 }}>{`${hoverTip.lineLabel}: ${hoverTip.value}`}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function smtpMeetingSearchParts(m) {
  const sentParts = (m?.recapSentTo || []).flatMap((r) => [r.name, r.email, r.reason]);
  const notParts = (m?.recapNotSent || []).flatMap((r) => [r.name, r.email, r.reason]);
  return [
    m?.title,
    m?.date,
    m?.time,
    m?.ownerName,
    m?.ownerEmail,
    m?.emailStatusLabel,
    m?.emailStatusMessage,
    m?.emailStatusCode,
    ...sentParts,
    ...notParts,
  ];
}

function SmtpIconChip({ icon, label, color, bg, title }) {
  return (
    <span className="smtp-icon-chip" style={{ color, background: bg }} title={title || label}>
      <Svg d={icon} size={12} color={color} stroke={2} />
      {label}
    </span>
  );
}

function smtpMeetingStatusIcon(m, variant) {
  const sent = variant === "sent" || (variant === "all" && m._recapVariant === "sent");
  if (sent) return { icon: IC.check, color: "#16a34a", bg: "rgba(22,163,74,0.12)", label: "Sent" };
  const code = meetingEmailStatusCode(m);
  if (code === "no_discussion") {
    return { icon: IC.check, color: "#16a34a", bg: "rgba(22,163,74,0.12)", label: "No discussion notice" };
  }
  if (code === "no_transcript") {
    return { icon: IC.mic, color: "#dc2626", bg: "rgba(220,38,38,0.1)", label: "No transcript" };
  }
  if (code === "smtp_failed") {
    return { icon: IC.mail, color: "#dc2626", bg: "rgba(220,38,38,0.1)", label: "SMTP failed" };
  }
  if (code === "smtp_queued") {
    return { icon: IC.clock, color: "#d97706", bg: "rgba(217,119,6,0.12)", label: "Queued" };
  }
  if (code === "finalizing") {
    return { icon: IC.clock, color: "#d97706", bg: "rgba(217,119,6,0.12)", label: "Processing" };
  }
  if (code === "no_deliverable_recipients") {
    return { icon: IC.users, color: "#d97706", bg: "rgba(217,119,6,0.12)", label: "No Avaada email" };
  }
  if (code === "no_participants") {
    return { icon: IC.users, color: "#dc2626", bg: "rgba(220,38,38,0.1)", label: "No participants" };
  }
  return { icon: IC.info, color: "#64748b", bg: "rgba(100,116,139,0.12)", label: getMeetingEmailReasonShort(m) || "Not sent" };
}

function formatSmtpRecipientLabel(r) {
  return personChipDisplayName(r || {});
}

function SmtpRecipientList({ title, items, accent, isLight, adminTextMuted, sentVariant }) {
  const tipApi = usePersonHoverTip();
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  const rowIcon = sentVariant ? IC.check : IC.close;

  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: accent,
          marginBottom: 6,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Svg d={rowIcon} size={12} color={accent} stroke={2.5} />
        {title} ({list.length})
      </div>
      <div className="smtp-recipient-chip-scroll" aria-label={title}>
        {list.map((r, i) => {
          const lab = formatSmtpRecipientLabel(r);
          const email = String(r.email || "").trim();
          const guest = /@meet\.link|guest\.recall/i.test(email);
          const pillBg = sentVariant
            ? isLight
              ? "rgba(219,234,254,0.95)"
              : "rgba(37,99,235,0.18)"
            : isLight
              ? "rgba(241,245,249,0.98)"
              : "rgba(255,255,255,0.06)";
          const pillBorder = sentVariant
            ? isLight
              ? "#93c5fd"
              : "rgba(96,165,250,0.45)"
            : isLight
              ? "#e2e8f0"
              : "rgba(255,255,255,0.12)";
          return (
            <span
              key={`${r.email}-${r.name}-${i}`}
              className="smtp-recipient-pill"
              {...personChipHoverHandlers(r, r.reason ? [r.reason] : [], tipApi)}
              style={{
                color: isLight ? "#0f2a4a" : "#E8F0F7",
                background: pillBg,
                border: `1px solid ${pillBorder}`,
                borderStyle: guest ? "dashed" : "solid",
                opacity: guest ? 0.95 : 1,
              }}
            >
              {lab}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function SmtpUsageCollapsibleList({
  title,
  items,
  totalCount,
  variant,
  open,
  onToggle,
  search,
  onSearch,
  searchPlaceholder,
  emptyMessage,
  noMatchMessage,
  isLight,
  adminBorder,
  adminTextMain,
  adminTextMuted,
  adminTextSoft,
  adminChipBg,
  approvalRows = [],
  onResendApproval,
  resendMeetingId = null,
}) {
  const all = Array.isArray(items) ? items : [];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SMTP_TABLE_PAGE_SIZE_DEFAULT);
  const [openId, setOpenId] = useState(null);
  const [meetingDrawer, setMeetingDrawer] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  // Fixed search: trim + case-insensitive match across title/owner/recipients/status.
  const needle = String(search || "").trim();
  const filtered = useMemo(
    () => all.filter((m) => {
      const statusMatches = statusFilter === "all" || (statusFilter === "sent" ? m._recapVariant === "sent" : m._recapVariant !== "sent");
      return statusMatches && adminSearchMatches(needle, smtpMeetingSearchParts(m));
    }),
    [all, needle, statusFilter]
  );
  // Reset to first page + close expanded row whenever the search or list changes.
  useEffect(() => {
    setPage(1);
    setOpenId(null);
    setMeetingDrawer(null);
  }, [needle, all.length, pageSize]);
  useEffect(() => {
    if (!open) setOpenId(null);
  }, [open]);
  // Lists are capped server-side; totalCount carries the true total.
  const trueTotal = Number(totalCount) >= 0 ? Number(totalCount) : all.length;
  const truncated = trueTotal > all.length;
  const headerCount = needle
    ? `${filtered.length} / ${all.length}`
    : truncated
      ? `${all.length} of ${trueTotal}`
      : String(all.length);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const rows = paged.map((m) =>
    buildSmtpTableRow(m, {
      variant,
      isLight,
      adminTextMain,
      adminTextMuted,
      adminTextSoft,
      adminBorder,
      approval: approvalRows.find((row) => String(row.meeting_id) === String(m.id)),
      onResendApproval,
      resendMeetingId,
      openId,
      onToggleRow: (id) => {
        const selected = paged.find((meeting) => meeting.id === id);
        setOpenId(null);
        setMeetingDrawer(selected || null);
      },
    })
  );

  return (
    <div className="adm-card" style={{ padding: 0, overflow: "hidden", marginBottom: 0, minWidth: 0, height: "100%" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: open ? adminChipBg : "transparent",
          border: "none",
          cursor: "pointer",
          color: adminTextMain,
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        <span>
          {title} ({headerCount})
        </span>
        <Svg d={open ? IC.chevUp : IC.chevDown} size={14} color={adminTextMuted} />
      </button>
      {open ? (
        <>
          <div className="smtp-usage-list-toolbar">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {[['all', 'All'], ['sent', 'Sent'], ['notSent', 'Not sent']].map(([value, label]) => (
                <button key={value} type="button" className={`adm-sec-chip${statusFilter === value ? " active" : ""}`} onClick={() => { setStatusFilter(value); setPage(1); setOpenId(null); setMeetingDrawer(null); }}>{label}</button>
              ))}
            </div>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                <Svg d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" color={adminTextMuted} size={14} />
              </div>
              <input
                className="adm-input adm-search-input"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
                style={{ paddingLeft: 36, width: "100%" }}
                aria-label={searchPlaceholder}
              />
            </div>
            {needle ? (
              <div style={{ marginTop: 8, fontSize: 11, color: adminTextMuted }}>
                Showing {filtered.length} of {all.length} meetings matching “{needle}”
              </div>
            ) : truncated ? (
              <div style={{ marginTop: 8, fontSize: 11, color: adminTextMuted }}>
                Showing latest {all.length} of {trueTotal} — headline totals are in the KPI cards above.
              </div>
            ) : null}
          </div>
          {all.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: adminTextMuted }}>{emptyMessage}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: adminTextMuted }}>{noMatchMessage}</div>
          ) : (
            <div className="adm-scroll-panel" style={{ maxHeight: 480 }}>
              <AdminMuiTable
                isLight={isLight}
                stickyHeader
                maxHeight={480}
                minWidth={680}
                columns={[
                  { id: "meeting", label: "Meeting" },
                  { id: "host", label: "Host", width: 150, hideOnPhone: true },
                  { id: "status", label: "Status", width: 132 },
                  { id: "to", label: "Recipients", width: 92, align: "right", hideOnPhone: true },
                  { id: "when", label: "When", width: 118, hideOnPhone: true },
                  { id: "view", label: "", width: 56, align: "right" },
                ]}
                rows={rows}
              />
            </div>
          )}
          {filtered.length > 0 ? (
            <div style={{ padding: "0 14px 12px" }}>
              <AdminListPagination
                page={safePage}
                pageSize={pageSize}
                total={filtered.length}
                onPageChange={setPage}
                onPageSizeChange={(next) => {
                  setPageSize(next);
                  setPage(1);
                }}
                pageSizeOptions={SMTP_TABLE_PAGE_SIZE_OPTIONS}
                isLight={isLight}
                adminBorder={adminBorder}
                adminTextMuted={adminTextMuted}
                adminTextMain={adminTextMain}
                label="meetings"
              />
            </div>
          ) : null}
        </>
      ) : null}
      {meetingDrawer ? (() => {
        const drawerRow = buildSmtpTableRow(meetingDrawer, { variant, isLight, adminTextMain, adminTextMuted, adminTextSoft, adminBorder, approval: approvalRows.find((row) => String(row.meeting_id) === String(meetingDrawer.id)), onResendApproval, resendMeetingId, openId: meetingDrawer.id, onToggleRow: () => setMeetingDrawer(null) });
        return (
          <>
            <div onClick={() => setMeetingDrawer(null)} aria-label="Close meeting drawer" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.28)", zIndex: 1200 }} />
            <div role="dialog" aria-modal="true" aria-label="Meeting details drawer" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(560px, 94vw)", zIndex: 1201, background: isLight ? "#fff" : "#0f172a", boxShadow: "-14px 0 34px rgba(15,23,42,0.2)", overflowY: "auto", paddingBottom: 24 }}>
              <div style={{ position: "sticky", top: 0, zIndex: 2, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", background: isLight ? "#fff" : "#0f172a", borderBottom: `1px solid ${adminBorder}` }}>
                <div style={{ fontWeight: 700, color: adminTextMain, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meetingDrawer.title || "Meeting details"}</div>
                <button type="button" className="adm-btn" onClick={() => setMeetingDrawer(null)}>Close</button>
              </div>
              <div style={{ padding: "16px 18px", color: adminTextMuted }}>{drawerRow.detail}</div>
            </div>
          </>
        );
      })() : null}
    </div>
  );
}

function formatSmtpWhenShort(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: APP_DISPLAY_TZ,
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AdminRecapSendControl({ meeting }) {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState((meeting.recapSentTo || []).map((item, index) => ({ id: item.email || index, name: item.name || "", email: item.email || item })));
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  async function send() {
    const clean = recipients.filter((item) => /\S+@\S+\.\S+/.test(String(item.email || ""))).map(({ name, email }) => ({ name, email: email.trim().toLowerCase() }));
    if (!clean.length) return setError("Add at least one recipient email.");
    if (!window.confirm("This recap was already sent. Continue with resend?")) return;
    if (!window.confirm("Confirm again: send this recap to the selected recipients?")) return;
    setSending(true); setError("");
    try {
      const result = await apiFetch(`/admin/meetings/${encodeURIComponent(String(meeting.id))}/recap-email`, { method: "POST", body: { recipients: clean, customMessage: message, confirmResend: true } });
      window.alert(`Recap resend completed: ${result.sentCount || 0}/${result.totalCount || clean.length} sent.`); setOpen(false);
    } catch (err) { setError(err.message || "Failed to resend recap."); } finally { setSending(false); }
  }
  return <div onClick={(event) => event.stopPropagation()} style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
    <button type="button" className="adm-btn adm-btn--primary" style={{ marginTop: 2 }} onClick={() => setOpen((value) => !value)}>{open ? "Close recap sender" : "Resend recap"}</button>
    {open ? <div style={{ width: "100%", padding: 10, border: "1px solid #dbe5ef", borderRadius: 10, background: "rgba(248,250,252,0.75)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>Recipients</div>
      <EmpParticipantChipPicker selected={recipients} onChange={setRecipients} directoryHint="Search employees or type a full email address" />
      <textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Optional custom message" rows={3} style={{ width: "100%", marginTop: 8, resize: "vertical", border: "1px solid #cbd5e1", borderRadius: 8, padding: 8, font: "inherit" }} />
      {error ? <div style={{ color: "#b91c1c", fontSize: 11, marginTop: 6 }}>{error}</div> : null}
      <button type="button" className="adm-btn adm-btn--primary" disabled={sending} style={{ marginTop: 8 }} onClick={send}>{sending ? "Sending…" : "Send recap"}</button>
    </div> : null}
  </div>;
}

function buildSmtpTableRow(
  m,
  { variant, isLight, adminTextMain, adminTextMuted, adminTextSoft, adminBorder, openId, onToggleRow, approval, onResendApproval, resendMeetingId }
) {
  const sent = variant === "sent" || (variant === "all" && m._recapVariant === "sent");
  const code = String(m.emailStatusCode || "").toLowerCase();
  const isSent = m.emailSent === true || code === "sent" || code === "no_discussion";
  const failed = code.includes("fail") || code === "smtp_failed";
  const statusLabel = m.emailStatusLabel || (isSent ? "Sent" : failed ? "Failed" : "Pending");
  const pillTone = adminTranscriptAvailabilityPillStyle(
    isSent ? "available" : failed ? "failed" : "unavailable",
    isLight
  );
  const sentCount =
    Number(m.emailRecipientsCount) ||
    (Array.isArray(m.recapSentTo) ? m.recapSentTo.length : 0) ||
    0;
  const notCount = Array.isArray(m.recapNotSent) ? m.recapNotSent.length : 0;
  const recipLabel = sent ? sentCount || "—" : notCount || sentCount || "—";
  const recipTitle = sent
    ? sentCount
      ? `${sentCount} recipient${sentCount === 1 ? "" : "s"}`
      : "No recipients recorded"
    : notCount || sentCount
      ? `Sent: ${sentCount} · Not sent: ${notCount}`
      : "No recipients recorded";
  const whenShort = formatSmtpWhenShort(m.emailStatusAt);
  const reason = getMeetingEmailReasonLong(m);
  // Real calendar host via shared helpers (SMTP rows now carry hostName/
  // hostEmail from the backend); raw owner would show the "AI Team" system
  // user for auto-dispatched meetings.
  const hostEmail = meetingHostEmail(m) || "";
  const hostName = meetingHostName(m) || hostEmail || "—";
  const isOpen = openId === m.id;
  const dateLine = m.date ? formatYmdDisplay(m.date) : "—";

  return {
    key: m.id,
    onClick: () => onToggleRow(m.id),
    selected: isOpen,
    cells: [
      <div key="t" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }} title={m.title || "Meeting"}>
        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {m.title || "Meeting"}
        </div>
        <div style={{ fontSize: 10.5, color: adminTextSoft, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {dateLine}
          {m.emailStatusMessage ? ` · ${m.emailStatusMessage}` : ""}
        </div>
      </div>,
      <div key="h" style={{ fontSize: 12.5, minWidth: 0 }} title={hostEmail && hostEmail !== hostName ? `${hostName} (${hostEmail})` : hostName}>
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hostName}</div>
        {hostEmail && hostEmail !== hostName ? (
          <div style={{ fontSize: 10.5, color: adminTextSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {hostEmail}
          </div>
        ) : null}
      </div>,
      <span
        key="s"
        title={m.emailStatusMessage || statusLabel}
        style={{
          display: "inline-block",
          maxWidth: 124,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 11,
          fontWeight: 700,
          borderRadius: 999,
          padding: "3px 9px",
          ...pillTone,
        }}
      >
        {statusLabel}
      </span>,
      <span
        key="n"
        title={recipTitle}
        style={{ display: "inline-block", fontSize: 12.5, fontWeight: 700, color: adminTextMain, whiteSpace: "nowrap" }}
      >
        {recipLabel}
      </span>,
      <span
        key="w"
        title={m.emailStatusAt ? String(m.emailStatusAt) : undefined}
        style={{
          display: "inline-block",
          fontSize: 11.5,
          color: adminTextMuted,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 110,
        }}
      >
        {whenShort}
      </span>,
      <span key="v" style={{ display: "inline-flex", justifyContent: "flex-end", width: "100%" }}>
        <Svg d={isOpen ? IC.chevUp : IC.chevDown} size={16} color={adminTextMuted} />
      </span>,
    ],
    detail: (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {hostName && hostName !== "—" ? (
          <div style={{ fontSize: 11.5, color: adminTextMuted }}>
            {hostName}
            {hostEmail && hostEmail !== hostName ? <span style={{ color: adminTextSoft }}> · {hostEmail}</span> : null}
          </div>
        ) : null}
        {!sent && reason ? (
          <div
            style={{
              fontSize: 11,
              color: adminTextSoft,
              lineHeight: 1.5,
              padding: "8px 10px",
              borderRadius: 8,
              background: isLight ? "rgba(248,250,252,0.9)" : "rgba(0,0,0,0.2)",
              border: `1px solid ${adminBorder}`,
            }}
          >
            <span style={{ fontWeight: 700, color: adminTextMuted }}>Why: </span>
            {reason}
          </div>
        ) : null}
        {approval ? (
          <div style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #BFDBFE", background: isLight ? "#EFF6FF" : "rgba(30,64,175,.18)", color: isLight ? "#1E3A8A" : "#DBEAFE", fontSize: 11.5 }}>
            <div style={{ fontWeight: 800 }}>Approval status: {approval.status || "pending"}</div>
            <div style={{ marginTop: 4 }}>Approver: {approval.approver_name || approval.approver_email || "—"}</div>
            <div style={{ marginTop: 4 }}>Approval email: {approval.approval_email_status || "not sent"}</div>
            {approval.approval_email_error ? <div style={{ marginTop: 4, color: isLight ? "#B91C1C" : "#FCA5A5", overflowWrap: "anywhere" }}>{approval.approval_email_error}</div> : null}
            {approval.approval_email_status === "failed" && onResendApproval ? (
              <button type="button" className="adm-btn adm-btn--primary" style={{ marginTop: 8 }} disabled={resendMeetingId === approval.meeting_id} onClick={(event) => { event.stopPropagation(); onResendApproval(approval.meeting_id); }}>
                {resendMeetingId === approval.meeting_id ? "Sending…" : "Send again"}
              </button>
            ) : null}
          </div>
        ) : null}
        <div style={{ fontSize: 11, color: adminTextSoft }}>Finalized: {whenShort}</div>
        <SmtpRecipientList
          title="Sent to"
          items={m.recapSentTo}
          accent={isLight ? "#1d4ed8" : "#93c5fd"}
          isLight={isLight}
          adminTextMuted={adminTextMuted}
          sentVariant
        />
        {sent ? <AdminRecapSendControl meeting={m} /> : null}
        <SmtpRecipientList
          title="Not sent"
          items={m.recapNotSent}
          accent={isLight ? "#0f2a4a" : "#cbd5e1"}
          isLight={isLight}
          adminTextMuted={adminTextMuted}
          sentVariant={false}
        />
      </div>
    ),
  };
}

function SmtpUsageMeetingRow({ m, isLight, adminBorder, adminTextMain, adminTextMuted, adminTextSoft, variant }) {
  const [open, setOpen] = useState(false);
  const sent = variant === "sent";
  const reason = getMeetingEmailReasonLong(m);
  const sentCount = (m.recapSentTo?.length ?? m.emailRecipientsCount) || 0;
  const notCount = m.recapNotSent?.length ?? 0;
  // Real calendar host — never the raw owner ("AI Team" system user).
  const cardHostEmail = meetingHostEmail(m) || "";
  const cardHostName = meetingHostName(m) || cardHostEmail || "";
  const at = m.emailStatusAt
    ? new Date(m.emailStatusAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";
  const status = smtpMeetingStatusIcon(m, variant);
  const dateShort = m.date ? String(m.date).slice(5) : "";
  const timeShort = m.time ? String(m.time).slice(0, 5) : "";

  return (
    <div className="smtp-meeting-card" style={{ borderColor: adminBorder }}>
      <button
        type="button"
        className={`smtp-meeting-card-btn${open ? " smtp-meeting-card-btn--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, background: status.bg }}>
          <Svg d={status.icon} size={15} color={status.color} stroke={2} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontWeight: 600, fontSize: 13, color: adminTextMain, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {m.title}
          </span>
          <span style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5, alignItems: "center" }}>
            {dateShort ? (
              <SmtpIconChip
                icon={IC.cal}
                label={`${dateShort}${timeShort ? ` ${timeShort}` : ""}`}
                color={adminTextMuted}
                bg={isLight ? "rgba(241,245,249,0.95)" : "rgba(255,255,255,0.06)"}
                title={at}
              />
            ) : null}
            {sentCount > 0 ? (
              <SmtpIconChip
                icon={IC.mail}
                label={`${sentCount}`}
                color="#16a34a"
                bg={isLight ? "rgba(22,163,74,0.12)" : "rgba(34,197,94,0.15)"}
                title={`${sentCount} recap email(s) sent`}
              />
            ) : null}
            {notCount > 0 ? (
              <SmtpIconChip
                icon={IC.close}
                label={`${notCount}`}
                color="#dc2626"
                bg={isLight ? "rgba(220,38,38,0.1)" : "rgba(248,113,113,0.12)"}
                title={`${notCount} not sent`}
              />
            ) : null}
            <SmtpIconChip
              icon={status.icon}
              label={status.label}
              color={status.color}
              bg={status.bg}
              title={reason}
            />
          </span>
        </span>
        <Svg d={open ? IC.chevUp : IC.chevDown} size={16} color={adminTextMuted} />
      </button>
      {open ? (
        <div className="smtp-meeting-card-body">
          {cardHostName ? (
            <div style={{ fontSize: 11.5, color: adminTextMuted, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <Svg d={IC.users} size={13} color={adminTextMuted} />
              {cardHostName}
              {cardHostEmail && cardHostEmail !== cardHostName ? <span style={{ color: adminTextSoft }}>· {cardHostEmail}</span> : null}
            </div>
          ) : null}
          {!sent && reason ? (
            <div
              style={{
                fontSize: 11,
                color: adminTextSoft,
                marginBottom: 8,
                lineHeight: 1.5,
                padding: "8px 10px",
                borderRadius: 8,
                background: isLight ? "rgba(248,250,252,0.9)" : "rgba(0,0,0,0.2)",
                border: `1px solid ${adminBorder}`,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1, display: "inline-flex" }}>
                <Svg d={IC.info} size={14} color={adminTextMuted} />
              </span>
              <span>
                <span style={{ fontWeight: 700, color: adminTextMuted }}>Why: </span>
                {reason}
              </span>
            </div>
          ) : null}
          <div style={{ fontSize: 10, color: adminTextSoft, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Svg d={IC.clock} size={12} color={adminTextSoft} />
            Finalized: {at}
          </div>
          <SmtpRecipientList
            title="Sent to"
            items={m.recapSentTo}
            accent={isLight ? "#1d4ed8" : "#93c5fd"}
            isLight={isLight}
            adminTextMuted={adminTextMuted}
            sentVariant
          />
          <SmtpRecipientList
            title="Not sent"
            items={m.recapNotSent}
            accent={isLight ? "#0f2a4a" : "#cbd5e1"}
            isLight={isLight}
            adminTextMuted={adminTextMuted}
            sentVariant={false}
          />
        </div>
      ) : null}
    </div>
  );
}

function AdminSMTPUsagePanel({
  isLight,
  adminTextMain,
  adminTextMuted,
  adminTextSoft,
  adminBorder,
  adminChipBg,
  report = null,
  loading = false,
  err = null,
}) {
  const [period, setPeriod] = useState("weekly");
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [sentSearch, setSentSearch] = useState("");
  const [notSentSearch, setNotSentSearch] = useState("");
  const [queueSearch, setQueueSearch] = useState("");
  const [queuePage, setQueuePage] = useState(1);
  const [queuePageSize, setQueuePageSize] = useState(SMTP_TABLE_PAGE_SIZE_DEFAULT);
  const [queueOpenId, setQueueOpenId] = useState(null);

  const bucket = report?.periods?.[period] || {};
  const sentAll = Array.isArray(report?.sentMeetings) ? report.sentMeetings : [];
  const notSentAll = Array.isArray(report?.notSentMeetings) ? report.notSentMeetings : [];
  const recapActivity = useMemo(() => [
    ...sentAll.map((meeting) => ({ ...meeting, _recapVariant: "sent" })),
    ...notSentAll.map((meeting) => ({ ...meeting, _recapVariant: "notSent" })),
  ], [sentAll, notSentAll]);
  const queuedMeetings = useMemo(
    () => notSentAll.filter((m) => meetingEmailStatusCode(m) === "smtp_queued"),
    [notSentAll]
  );
  const queueNeedle = String(queueSearch || "").trim();
  const filteredQueuedMeetings = useMemo(
    () => queuedMeetings.filter((m) => adminSearchMatches(queueNeedle, smtpMeetingSearchParts(m))),
    [queuedMeetings, queueNeedle]
  );
  const queueTotalPages = Math.max(1, Math.ceil(filteredQueuedMeetings.length / queuePageSize));
  const queueSafePage = Math.min(Math.max(1, queuePage), queueTotalPages);
  const pagedQueuedMeetings = useMemo(() => {
    const start = (queueSafePage - 1) * queuePageSize;
    return filteredQueuedMeetings.slice(start, start + queuePageSize);
  }, [filteredQueuedMeetings, queueSafePage, queuePageSize]);
  const queueRows = pagedQueuedMeetings.map((m) =>
    buildSmtpTableRow(m, {
      variant: "notSent",
      isLight,
      adminTextMain,
      adminTextMuted,
      adminTextSoft,
      adminBorder,
      openId: queueOpenId,
      onToggleRow: (id) => setQueueOpenId((prev) => (prev === id ? null : id)),
    })
  );

  useEffect(() => {
    setQueuePage(1);
    setQueueOpenId(null);
  }, [queueNeedle, queuePageSize]);
  useEffect(() => {
    if (queueModalOpen) {
      setQueuePage(1);
      setQueueOpenId(null);
    }
  }, [queueModalOpen]);

  useEffect(() => {
    if (!queueModalOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") setQueueModalOpen(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [queueModalOpen]);

  if (loading && !report) {
    return (
      <div className="adm-card" style={{ padding: 32, textAlign: "center", color: adminTextMuted, fontSize: 13 }}>
        Loading SMTP usage…
      </div>
    );
  }
  if (err && !report) {
    return (
      <div className="adm-card" style={{ padding: 20, color: "#f87171", fontSize: 13 }}>
        {err}
      </div>
    );
  }
  if (!report) return null;

  const smtpOk = report?.smtp?.ok;
  const periodLabels = { today: "Today", weekly: "This week", monthly: "This month", overall: "All time" };
  const chartRangeByPeriod = { today: "daily", weekly: "daily", monthly: "weekly", overall: "monthly" };
  const activeChartRange = chartRangeByPeriod[period] || "daily";
  const chartSeries =
    activeChartRange === "weekly"
      ? report?.charts?.weekly
      : activeChartRange === "monthly"
      ? report?.charts?.monthly
      : report?.charts?.daily || report?.dailyChart;
  const chartTitles = {
    daily: "Last 7 days",
    weekly: "Last 8 weeks",
    monthly: "Last 6 months",
  };

  return (
    <>
      {loading ? (
        <div style={{ fontSize: 11.5, color: adminTextMuted, marginBottom: 10 }}>Updating SMTP stats…</div>
      ) : null}
      {err ? (
        <div
          style={{
            marginBottom: 10,
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 12,
            color: "#f87171",
            background: "rgba(220,38,38,0.08)",
            border: "1px solid rgba(220,38,38,0.2)",
          }}
        >
          Could not refresh SMTP stats — showing last loaded data. {err}
        </div>
      ) : null}
      <div className="adm-card" style={{ padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: adminTextMain }}>SMTP connection</div>
            <div style={{ fontSize: 12, color: adminTextMuted, marginTop: 4 }}>
              {smtpOk ? (
                <>
                  Credentials on file · <span className="adm-smtp-email">{report?.smtp?.user || BOT_EMAIL_DISPLAY}</span>
                  {report?.smtp?.host ? ` · ${report.smtp.host}:${report.smtp.port}` : ""}
                  {report?.smtp?.source === "database"
                    ? " · Admin config"
                    : report?.smtp?.source === "env"
                      ? " · .env fallback"
                      : ""}
                </>
              ) : (
                "SMTP is not configured — open Admin → SMTP Configuration, or set SMTP_USER / SMTP_PASS in the server environment."
              )}
            </div>
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "4px 12px",
              borderRadius: 999,
              background: smtpOk ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)",
              color: smtpOk ? "#16a34a" : "#dc2626",
            }}
          >
            {smtpOk ? "Configured" : "Not configured"}
          </span>
        </div>
        <div style={{ fontSize: 11, color: adminTextSoft, marginTop: 10, lineHeight: 1.5 }}>
          {report?.pricingNote} Counts use <strong>email_status_at</strong> (when recap was finalized).
          {" "}TZ: {report?.timezone || "—"}.
          {" "}Successful SMTP sends (24h): <strong>{report?.smtp?.callsLast24h ?? 0}</strong>
          {report?.smtp?.dailyLimit ? <> / <strong>{report.smtp.dailyLimit}</strong> daily cap</> : null}.
          {" "}Queue open: <strong>{report?.smtp?.queuePending ?? 0}</strong>.
          {" "}If daily cap is reached, queued recaps retry automatically.
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {["today", "weekly", "monthly", "overall"].map((rk) => (
          <button
            key={rk}
            type="button"
            className={`adm-sec-chip${period === rk ? " active" : ""}`}
            onClick={() => setPeriod(rk)}
          >
            {periodLabels[rk]}
          </button>
        ))}
      </div>

      <div className="adm-kpi-grid adm-kpi-grid--6 admin-overview-stat-grid" style={{ marginBottom: 14 }}>
        <ChatUsageKpi
          label="Completed meetings"
          value={String(bucket.completed ?? 0)}
          sub={periodLabels[period]}
          accent={ADM.blue}
          tone="blue"
          iconD={IC.meet}
        />
        <ChatUsageKpi
          label="Recap sent"
          value={String(bucket.sent ?? 0)}
          sub={`${bucket.recipients ?? 0} recipient${(bucket.recipients ?? 0) === 1 ? "" : "s"}`}
          accent={ADM.green}
          tone="green"
          iconD={IC.mail}
        />
        <ChatUsageKpi
          label="Recap not sent"
          value={String(bucket.notSent ?? 0)}
          sub="See reasons below"
          accent={ADM.rose}
          tone="rose"
          iconD={IC.close}
        />
        <ChatUsageKpi
          label="SMTP calls"
          value={String(bucket.smtpCalls ?? 0)}
          sub="SMTP send attempts (rate-limit monitor)"
          accent={ADM.amber}
          tone="amber"
          iconD={IC.zap}
        />
        <ChatUsageKpi
          label="Queue pending"
          value={String(report?.smtp?.queuePending ?? 0)}
          sub="Click to open queued meetings"
          accent={ADM.amber}
          tone="amber"
          iconD={IC.clock}
          onClick={() => setQueueModalOpen(true)}
          active={queueModalOpen}
        />
        <ChatUsageKpi
          label="Success rate"
          value={
            bucket.completed
              ? `${Math.round(((bucket.sent || 0) / bucket.completed) * 100)}%`
              : "—"
          }
          sub="Sent ÷ completed"
          accent={ADM.violet}
          tone="violet"
          iconD={IC.check}
        />
      </div>

      <div className="adm-card cu-chart-panel" style={{ marginBottom: 14, padding: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: adminTextMain, marginBottom: 4 }}>
          Recap email trend — {chartTitles[activeChartRange] || chartTitles.daily}
        </div>
        <div style={{ fontSize: 11.5, color: adminTextMuted, marginBottom: 4 }}>
          Meetings finalized per period — line shows count of recap sent vs not sent
        </div>
        <SmtpUsageLineChart
          series={chartSeries}
          isLight={isLight}
          adminTextMuted={adminTextMuted}
        />
        <div className="adm-chart-note" style={{ color: adminTextSoft }}>
          Live counts from finalized meetings — no sample data. Hover any dot for exact numbers.
        </div>
      </div>

      {Array.isArray(report?.statusBreakdown) && report.statusBreakdown.length > 0 ? (
        <div className="adm-card" style={{ padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: adminTextMuted, marginBottom: 8 }}>All-time breakdown</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {report.statusBreakdown.map((s) => (
              <span
                key={s.code}
                style={{
                  fontSize: 11,
                  padding: "5px 10px",
                  borderRadius: 8,
                  border: `1px solid ${adminBorder}`,
                  background: adminChipBg,
                  color: adminTextMain,
                }}
              >
                {s.label}: <strong>{s.count}</strong>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {queueModalOpen
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Queued recap emails"
              onClick={() => setQueueModalOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 2000,
                background: "rgba(15,23,42,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "min(980px, 96vw)",
                  maxHeight: "min(86vh, 760px)",
                  background: isLight ? "#ffffff" : "#0f172a",
                  border: `1px solid ${adminBorder}`,
                  borderRadius: 14,
                  boxShadow: "0 20px 50px rgba(2,6,23,0.35)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: `1px solid ${adminBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: adminTextMain }}>Queue pending ({report?.smtp?.queuePending ?? queuedMeetings.length})</div>
                    <div style={{ fontSize: 11, color: adminTextMuted, marginTop: 3 }}>
                      SMTP cap reached items. Queue retries automatically every hour.
                      {(report?.smtp?.queuePending ?? 0) > queuedMeetings.length
                        ? ` Showing latest ${queuedMeetings.length} below.`
                        : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setQueueModalOpen(false)}
                    style={{
                      border: `1px solid ${adminBorder}`,
                      background: adminChipBg,
                      color: adminTextMain,
                      borderRadius: 8,
                      padding: "5px 10px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </div>
                <div style={{ padding: "10px 14px", borderBottom: `1px solid ${adminBorder}` }}>
                  <div style={{ position: "relative" }}>
                    <div style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <Svg d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" color={adminTextMuted} size={14} />
                    </div>
                    <input
                      className="adm-input adm-search-input"
                      value={queueSearch}
                      onChange={(e) => setQueueSearch(e.target.value)}
                      placeholder="Search queued meetings by title, owner, reason…"
                      style={{ paddingLeft: 36, width: "100%" }}
                      aria-label="Search queued meetings"
                    />
                  </div>
                </div>
                <div style={{ overflowY: "auto", padding: 0 }}>
                  {queuedMeetings.length === 0 ? (
                    <div style={{ padding: 16, fontSize: 12, color: adminTextMuted }}>No queued recap emails right now.</div>
                  ) : filteredQueuedMeetings.length === 0 ? (
                    <div style={{ padding: 16, fontSize: 12, color: adminTextMuted }}>No queued meetings match your search.</div>
                  ) : (
                    <>
                      <AdminMuiTable
                        isLight={isLight}
                        stickyHeader
                        minWidth={680}
                        columns={[
                          { id: "meeting", label: "Meeting" },
                          { id: "host", label: "Host", width: 150, hideOnPhone: true },
                          { id: "status", label: "Status", width: 132 },
                          { id: "to", label: "Recipients", width: 92, align: "right", hideOnPhone: true },
                          { id: "when", label: "When", width: 118, hideOnPhone: true },
                          { id: "view", label: "", width: 56, align: "right" },
                        ]}
                        rows={queueRows}
                      />
                      <div style={{ padding: "0 14px 12px" }}>
                        <AdminListPagination
                          page={queueSafePage}
                          pageSize={queuePageSize}
                          total={filteredQueuedMeetings.length}
                          onPageChange={setQueuePage}
                          onPageSizeChange={(next) => {
                            setQueuePageSize(next);
                            setQueuePage(1);
                          }}
                          pageSizeOptions={SMTP_TABLE_PAGE_SIZE_OPTIONS}
                          isLight={isLight}
                          adminBorder={adminBorder}
                          adminTextMuted={adminTextMuted}
                          adminTextMain={adminTextMain}
                          label="meetings"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <SmtpUsageCollapsibleList
        title="Recap email activity"
        items={recapActivity}
        totalCount={Number(report?.totals?.sent || 0) + Number(report?.totals?.notSent || 0)}
        variant="all"
        open={true}
        onToggle={() => {}}
        search={sentSearch}
        onSearch={setSentSearch}
        searchPlaceholder="Search meetings, hosts, recipients, dates, or status…"
        emptyMessage="No recap email activity yet."
        noMatchMessage="No meetings match the selected filters."
        isLight={isLight}
        adminBorder={adminBorder}
        adminTextMain={adminTextMain}
        adminTextMuted={adminTextMuted}
        adminTextSoft={adminTextSoft}
        adminChipBg={adminChipBg}
      />
    </>
  );
}

const SUMMARY_PROVIDER_META = {
  anthropic: { label: "Claude (Anthropic)", short: "Claude" },
  openai: { label: "OpenAI", short: "OpenAI" },
  gemini: { label: "Gemini (Google)", short: "Gemini" },
};

const SUMMARY_PROVIDER_THEME = {
  anthropic: { label: "Anthropic", accent: "#d97706", accentCost: "#f59e0b", accentSoft: "rgba(217,119,6,0.14)" },
  openai: { label: "OpenAI", accent: "#10a37f", accentCost: "#0891b2", accentSoft: "rgba(16,163,127,0.14)" },
  gemini: { label: "Gemini", accent: "#4285f4", accentCost: "#7c3aed", accentSoft: "rgba(66,133,244,0.14)" },
};

function SummaryUsagePeriodGrid({ today, weekly, monthly, overall, isLight, adminTextMuted }) {
  const rows = [
    { key: "today", label: "Today", data: today },
    { key: "weekly", label: "This week", data: weekly },
    { key: "monthly", label: "This month", data: monthly },
    { key: "overall", label: "Overall", data: overall },
  ];
  return (
    <div className="cu-period-grid">
      {rows.map((row) => {
        const d = row.data || {};
        const tok = d.totalTokens ?? (Number(d.inputTokens) || 0) + (Number(d.outputTokens) || 0);
        return (
          <div key={row.key} className="cu-period-stat">
            <div className="cu-period-label">{row.label}</div>
            <div className="cu-period-tokens">{formatChatTokens(tok)}</div>
            <div className="cu-period-meta">
              <span className="cu-period-meta-label">{d.requests ?? 0} req</span>
              <span className="cu-period-cost">{formatChatUsd(d.costUsd)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdminSummaryUsagePanel({
  isLight = true,
  adminTextMain = "#0f2a4a",
  adminTextMuted = "#64748b",
  adminBorder = "#e2e8f0",
  adminChipBg = "#f1f5f9",
}) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);
  const [chartMode, setChartMode] = useState("daily");
  const reportRef = useRef(null);
  reportRef.current = report;

  const loadReport = useCallback(async () => {
    const hasCache = Boolean(reportRef.current);
    if (!hasCache) {
      setLoading(true);
      setErr(null);
    }
    try {
      const data = await apiFetch("/admin/summary/usage");
      setReport(data);
      setErr(null);
    } catch (e) {
      if (!hasCache) setErr(e.message || "Failed to load summary usage");
      else setErr(e.message || "Failed to refresh summary usage");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const combined = report?.combined || {};
  const life = combined.overall || {};
  const lifeTok = life.totalTokens ?? (Number(life.inputTokens) || 0) + (Number(life.outputTokens) || 0);
  const categoryEntries = Object.entries(report?.categories || {}).filter(([, block]) => block?.hasData);

  if (loading && !report) {
    return (
      <div className="adm-card" style={{ padding: 24, color: adminTextMuted, fontSize: 13 }}>
        Loading summary API usage…
      </div>
    );
  }

  if (err && !report) {
    return (
      <div className="adm-card" style={{ padding: 20, color: "#FCA5A5", fontSize: 13 }}>
        {err}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="adm-ghost-btn" onClick={loadReport}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading ? (
        <div style={{ fontSize: 11.5, color: adminTextMuted, marginBottom: 10 }}>Updating summary usage…</div>
      ) : null}
      {err && report ? (
        <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 8, fontSize: 12, color: "#FCA5A5", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)" }}>
          Could not refresh — showing last loaded data. {err}
        </div>
      ) : null}
      <div className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 4 }}>
          All platforms
        </div>
        <div style={{ fontSize: 12, color: adminTextMuted, marginBottom: 16, lineHeight: 1.5 }}>
          All OpenAI, Gemini, and Anthropic usage — MoM summaries, chatbot, vision, middleware, translate, and related Summary APIs.
        </div>
        <div className="adm-kpi-grid admin-overview-stat-grid" style={{ marginBottom: 16 }}>
          <ChatUsageKpi
            label="Total requests"
            value={(life.requests ?? 0).toLocaleString("en-IN")}
            sub="All LLM API calls"
            accent={ADM.blue}
            tone="blue"
            iconD={IC.zap}
          />
          <ChatUsageKpi
            label="Total tokens"
            value={formatChatTokens(lifeTok)}
            sub={`In ${formatChatTokens(life.inputTokens)} · Out ${formatChatTokens(life.outputTokens)}`}
            accent={ADM.green}
            tone="green"
            iconD={IC.task}
          />
          <ChatUsageKpi
            label="Est. cost"
            value={formatChatUsd(life.costUsd)}
            sub="Lifetime (all providers)"
            accent={ADM.amber}
            tone="amber"
            iconD={IC.mail}
          />
        </div>
        <SummaryUsagePeriodGrid
          today={combined.today}
          weekly={combined.weekly}
          monthly={combined.monthly}
          overall={combined.overall || life}
          isLight={isLight}
          adminTextMuted={adminTextMuted}
        />
        {report?.pricingNote ? (
          <div style={{ fontSize: 11, color: adminTextMuted, lineHeight: 1.45 }}>{report.pricingNote}</div>
        ) : null}
      </div>

      {categoryEntries.length > 0 ? (
        <div className="adm-card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 10 }}>
            By feature
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {categoryEntries.map(([cat, block]) => (
              <div
                key={cat}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${adminBorder}`,
                  background: isLight ? "#f8fafc" : "rgba(15,23,42,0.4)",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7" }}>
                  {block.label || cat}
                </div>
                <div style={{ fontSize: 10.5, color: adminTextMuted, marginTop: 3, marginBottom: 8 }}>
                  {block.credential || ""}
                </div>
                <div style={{ fontSize: 11.5, color: adminTextMuted }}>
                  {(block.overall?.requests ?? 0).toLocaleString("en-IN")} requests ·{" "}
                  {formatChatTokens(block.overall?.totalTokens)} tok · {formatChatUsd(block.overall?.costUsd)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: adminTextMuted, marginRight: 4 }}>Chart range:</span>
        <button
          type="button"
          className={`adm-sec-chip${chartMode === "daily" ? " active" : ""}`}
          onClick={() => setChartMode("daily")}
        >
          Last 14 days
        </button>
        <button
          type="button"
          className={`adm-sec-chip${chartMode === "monthly" ? " active" : ""}`}
          onClick={() => setChartMode("monthly")}
        >
          Last 6 months
        </button>
        <button type="button" className="adm-ghost-btn" style={{ marginLeft: "auto", fontSize: 12 }} onClick={loadReport}>
          Refresh
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        {["anthropic", "openai", "gemini"].map((id) => {
          const block = report?.providers?.[id] || {};
          const theme = SUMMARY_PROVIDER_THEME[id];
          const meta = SUMMARY_PROVIDER_META[id];
          const series = chartMode === "monthly" ? block.chartMonthly : block.chartDaily;
          return (
            <div key={id} className="adm-card" style={{ padding: 18, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7" }}>
                    {meta.label}
                  </div>
                  <div style={{ fontSize: 11, color: adminTextMuted, marginTop: 3 }}>
                    {block.hasData ? `${block.overall?.requests ?? 0} lifetime requests` : "No usage logged yet"}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    padding: "4px 8px",
                    borderRadius: 6,
                    background: theme.accentSoft || `${theme.accent}22`,
                    color: theme.accent,
                  }}
                >
                  {theme.label}
                </span>
              </div>
              <SummaryUsagePeriodGrid
                today={block.today}
                weekly={block.weekly}
                monthly={block.monthly}
                overall={block.overall || block.lifetime}
                isLight={isLight}
                adminTextMuted={adminTextMuted}
              />
              <div style={{ marginTop: 8 }}>
                <DualAxisUsageChart
                  series={series}
                  isLight={isLight}
                  accent={theme.accent}
                  accentCost={theme.accentCost}
                  note="Live provider usage from logged API calls — no sample data."
                />
              </div>
              {block.lastUsedAt ? (
                <div style={{ marginTop: 10, fontSize: 10.5, color: adminTextMuted }}>
                  Last used {formatChatRecentAt(block.lastUsedAt)}
                  {block.lastModel ? ` · ${block.lastModel}` : ""}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}

function emptySummarySettingsForm() {
  return {
    primaryProvider: "anthropic",
    fallbackEnabled: true,
    usageByProvider: null,
    transcriptMiddleware: {
      enabled: true,
      provider: "openai",
      model: "gpt-4o-mini",
      storeRawSummary: false,
    },
    presentationVision: {
      provider: "openai",
      model: "gpt-4o-mini",
      fallbackModel: "gpt-4o",
      composeModel: "gpt-4o-mini",
    },
    providers: {
      anthropic: { enabled: true, model: "", apiKey: "", clearApiKey: false, models: [], modelsSource: "none" },
      openai: { enabled: true, model: "", apiKey: "", clearApiKey: false, models: [], modelsSource: "none" },
      gemini: { enabled: false, model: "", apiKey: "", clearApiKey: false, models: [], modelsSource: "none" },
    },
  };
}

/** Admin Summary — API key from this form / database only (not server .env). */
function summaryProviderHasAdminKey(p) {
  const typed = String(p?.apiKey || "").trim();
  if (typed.length >= 12) return true;
  return p?.apiKeyMeta?.configured === true && p?.apiKeyMeta?.source === "database";
}

/** Models dropdown only after a successful live API fetch — never from saved defaults. */
function summaryProviderModelsReady(p) {
  return p?.modelsSource === "api" && Array.isArray(p?.models) && p.models.length > 0;
}

function summaryProviderSavedModel(p) {
  return String(p?.model || p?.savedModel || "").trim();
}

function summaryProviderShowModelUI(p, modelsBusy = false) {
  const saved = summaryProviderSavedModel(p);
  if (!summaryProviderHasAdminKey(p) && !saved) return false;
  return summaryProviderModelsReady(p) || modelsBusy || Boolean(saved);
}

function summaryProviderModelList(p) {
  if (summaryProviderModelsReady(p)) return [...p.models];
  const saved = summaryProviderSavedModel(p);
  return saved ? [saved] : [];
}

const MIDDLEWARE_DEFAULT_MODELS = {
  anthropic: "claude-haiku-4-5",
  openai: "gpt-4o-mini",
  gemini: "gemini-1.5-flash",
};

const VISION_DEFAULT_MODELS = {
  anthropic: "claude-haiku-4-5",
  openai: "gpt-4o-mini",
  gemini: "gemini-2.0-flash",
};

const VISION_FALLBACK_DEFAULT_MODELS = {
  anthropic: "claude-sonnet-4-6",
  openai: "gpt-4o",
  gemini: "gemini-2.0-flash",
};

const COMPOSE_DEFAULT_MODELS = {
  anthropic: "claude-haiku-4-5",
  openai: "gpt-4o-mini",
  gemini: "gemini-2.0-flash",
};

function middlewareProviderId(form) {
  const p = String(form?.transcriptMiddleware?.provider || "openai").toLowerCase();
  return ["anthropic", "openai", "gemini"].includes(p) ? p : "openai";
}

function middlewareModelOptions(form) {
  const provider = middlewareProviderId(form);
  const prov = form?.providers?.[provider];
  const fromApi = summaryProviderModelList(prov);
  const saved = String(form?.transcriptMiddleware?.model || "").trim();
  return [...new Set([saved, ...fromApi].filter(Boolean))];
}

function visionProviderId(form) {
  const p = String(form?.presentationVision?.provider || "openai").toLowerCase();
  return ["anthropic", "openai", "gemini"].includes(p) ? p : "openai";
}

function summaryModelMatchesProvider(provider, modelId) {
  const m = String(modelId || "").trim().toLowerCase();
  if (!m) return false;
  if (provider === "anthropic") return /claude/i.test(m);
  if (provider === "openai") return /^(gpt-|o[0-9]-|chatgpt-)/i.test(m);
  if (provider === "gemini") return /^gemini-/i.test(m);
  return false;
}

function visionFieldModel(form, field, defaultsMap) {
  const provider = visionProviderId(form);
  const raw = String(form?.presentationVision?.[field] || "").trim();
  if (raw && summaryModelMatchesProvider(provider, raw)) return raw;
  return defaultsMap[provider] || defaultsMap.openai;
}

function visionModelOptions(form) {
  const provider = visionProviderId(form);
  const prov = form?.providers?.[provider];
  const fromApi = summaryProviderModelList(prov).filter((m) => summaryModelMatchesProvider(provider, m));
  const saved = [
    visionFieldModel(form, "model", VISION_DEFAULT_MODELS),
    visionFieldModel(form, "fallbackModel", VISION_FALLBACK_DEFAULT_MODELS),
    visionFieldModel(form, "composeModel", COMPOSE_DEFAULT_MODELS),
  ];
  return [...new Set([...saved, ...fromApi].filter(Boolean))];
}

function summaryModelDisplayName(provider, modelId) {
  const raw = String(modelId || "").trim();
  if (!raw) return "";
  const m = raw.toLowerCase();
  if (provider === "openai") {
    if (m.includes("gpt-4o-mini")) return "OpenAI GPT-4o Mini";
    if (m.includes("gpt-4o")) return "OpenAI GPT-4o";
    if (m.includes("gpt-4-turbo")) return "OpenAI GPT-4 Turbo";
    if (/^o[0-9]/.test(m)) return `OpenAI ${raw.toUpperCase()}`;
    if (m.startsWith("gpt-")) return `OpenAI ${raw}`;
    return `OpenAI ${raw}`;
  }
  if (provider === "anthropic") {
    const modern = m.match(/^claude-(haiku|sonnet|opus)-(\d+)-(\d+)/);
    if (modern) {
      const tier = modern[1].charAt(0).toUpperCase() + modern[1].slice(1);
      return `Claude ${tier} ${modern[2]}.${modern[3]}`;
    }
    const legacy35 = m.match(/^claude-3-5-(haiku|sonnet|opus)/);
    if (legacy35) {
      const tier = legacy35[1].charAt(0).toUpperCase() + legacy35[1].slice(1);
      return `Claude 3.5 ${tier}`;
    }
    if (m.includes("opus")) return "Claude Opus";
    if (m.includes("haiku")) return "Claude Haiku";
    if (m.includes("sonnet")) return "Claude Sonnet";
    return raw.replace(/^claude-/i, "Claude ").replace(/-/g, " ");
  }
  if (provider === "gemini") {
    return raw
      .replace(/^gemini-/i, "Gemini ")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return raw;
}

function summaryModelIsRecommended(provider, modelId) {
  const m = String(modelId || "").toLowerCase();
  if (/image|dall-e|embedding|whisper|tts|audio|realtime|transcribe|moderation|instruct/.test(m)) {
    return false;
  }
  if (provider === "anthropic") return m.includes("sonnet") || m.includes("haiku") || m.includes("opus");
  if (provider === "openai") return m.includes("gpt-4o") || /^o[0-9]/.test(m);
  if (provider === "gemini") return m.includes("flash") || m.includes("pro");
  return false;
}

/** Why this model fits meeting summary / MoM — not pricing or speed marketing copy. */
function summaryModelUseHint(provider, modelId) {
  const m = String(modelId || "").toLowerCase();
  if (/image|dall-e|embedding|whisper|tts|audio|realtime|transcribe|moderation/.test(m)) {
    return "Not designed for meeting text summaries";
  }
  if (provider === "anthropic") {
    if (m.includes("opus")) return "Best for detailed MoM on long or complex meetings";
    if (m.includes("haiku")) return "Best for short meeting recap summaries";
    if (m.includes("sonnet")) return "Best for balanced meeting summaries and action items";
    if (m.includes("fable")) return "Best for narrative-style meeting summaries";
    return "Use for meeting summary (MoM) generation";
  }
  if (provider === "openai") {
    if (m.includes("gpt-4o-mini")) return "Best for concise meeting summaries";
    if (m.includes("gpt-4o")) return "Best for structured MoM and action items";
    if (/^o[0-9]/.test(m)) return "Best for reasoning-heavy meeting summaries";
    if (m.includes("gpt-4")) return "Best for high-quality meeting summaries";
    if (m.includes("3.5")) return "Suitable for basic meeting summaries";
    if (m.startsWith("gpt-")) return "Use for meeting summary generation";
    return "Use for meeting summary (MoM)";
  }
  if (provider === "gemini") {
    if (m.includes("flash")) return "Best for fast meeting summary generation";
    if (m.includes("pro")) return "Best for detailed MoM from long transcripts";
    if (m.includes("2.0") || m.includes("2.5")) return "Best for high-quality meeting summaries";
    return "Use for meeting summary (MoM) generation";
  }
  return "Best for meeting summary generation";
}

function SummaryModelPicker({
  provider,
  models = [],
  value = "",
  onChange,
  isLight = true,
  adminTextMain = "#0f2a4a",
  adminTextMuted = "#64748b",
  adminBorder = "#e2e8f0",
}) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const rootRef = useRef(null);

  const filtered = useMemo(() => {
    let list = Array.isArray(models) ? [...models] : [];
    if (filter === "recommended") {
      list = list.filter((m) => summaryModelIsRecommended(provider, m));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((m) => {
        const hay = [m, summaryModelDisplayName(provider, m), summaryModelUseHint(provider, m)]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [models, filter, search, provider]);

  useEffect(() => {
    if (!open) return;
    const syncPanelPlacement = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const panelH = Math.min(320, window.innerHeight * 0.45);
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < panelH && rect.top > panelH);
    };
    syncPanelPlacement();
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", syncPanelPlacement);
    window.addEventListener("scroll", syncPanelPlacement, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", syncPanelPlacement);
      window.removeEventListener("scroll", syncPanelPlacement, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setFilter("all");
    }
  }, [open]);

  const shellBg = isLight ? "#fff" : "rgba(15,23,42,0.45)";
  const rowHover = isLight ? "#F8FAFC" : "rgba(255,255,255,0.04)";
  const rowActive = isLight ? "#E8F6F4" : "rgba(45,212,191,0.12)";
  const selectedModel = String(value || "").trim();
  const selectedLabel = selectedModel
    ? summaryModelDisplayName(provider, selectedModel)
    : "Select a model";

  function pickModel(m) {
    onChange?.(m);
    setOpen(false);
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          border: `1px solid ${open ? "#2563EB" : adminBorder}`,
          borderRadius: 10,
          background: shellBg,
          cursor: "pointer",
          textAlign: "left",
          boxShadow: open ? "0 0 0 3px rgba(37,99,235,0.12)" : "none",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: selectedModel ? adminTextMain : adminTextMuted }}>
            {selectedLabel}
          </div>
          {selectedModel ? (
            <div style={{ fontSize: 11, color: adminTextMuted, marginTop: 2, fontFamily: "monospace", opacity: 0.85 }}>
              {selectedModel}
            </div>
          ) : null}
        </div>
        <span style={{ color: adminTextMuted, flexShrink: 0, display: "inline-flex", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s ease" }}>
          <Svg d={IC.chevDown} size={16} stroke={2} />
        </span>
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            ...(dropUp
              ? { bottom: "calc(100% + 6px)", top: "auto" }
              : { top: "calc(100% + 6px)" }),
            zIndex: 200,
            border: `1px solid ${adminBorder}`,
            borderRadius: 12,
            overflow: "hidden",
            background: shellBg,
            boxShadow: "0 12px 32px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.08)",
          }}
        >
          <div style={{ padding: "10px 12px", borderBottom: `1px solid ${adminBorder}` }}>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: adminTextMuted,
                  display: "inline-flex",
                }}
              >
                <Svg d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={15} stroke={1.8} />
              </span>
              <input
                className="adm-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search a model"
                style={{ paddingLeft: 34, fontSize: 13 }}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 12.5, fontWeight: 600 }}>
              <button
                type="button"
                onClick={() => setFilter("all")}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: filter === "all" ? adminTextMain : adminTextMuted,
                  padding: 0,
                }}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setFilter("recommended")}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: filter === "recommended" ? "#0D9488" : adminTextMuted,
                  padding: 0,
                }}
              >
                Recommended
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12.5, color: adminTextMuted, textAlign: "center" }}>
                No models match your search.
              </div>
            ) : (
              filtered.map((m) => {
                const selected = value === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => pickModel(m)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "11px 14px",
                      border: "none",
                      borderBottom: `1px solid ${adminBorder}`,
                      background: selected ? rowActive : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: adminTextMain, lineHeight: 1.3 }}>
                        {summaryModelDisplayName(provider, m)}
                      </div>
                      <div style={{ fontSize: 12, color: adminTextMuted, marginTop: 3, lineHeight: 1.4 }}>
                        {summaryModelUseHint(provider, m)}
                      </div>
                      <div style={{ fontSize: 10.5, color: adminTextMuted, marginTop: 3, opacity: 0.7, fontFamily: "monospace" }}>
                        {m}
                      </div>
                    </div>
                    {selected ? (
                      <span style={{ color: "#0D9488", flexShrink: 0, marginTop: 2 }}>
                        <Svg d={IC.check} size={18} stroke={2.2} />
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function summarySettingsFromApi(data) {
  const base = emptySummarySettingsForm();
  if (!data) return base;
  base.primaryProvider = data.primaryProvider || base.primaryProvider;
  base.fallbackEnabled = data.fallbackEnabled !== false;
  base.usageByProvider = data.usageByProvider || null;
  const mw = data.transcriptMiddleware || {};
  base.transcriptMiddleware = {
    enabled: mw.enabled !== false,
    provider: ["anthropic", "openai", "gemini"].includes(String(mw.provider || "").toLowerCase())
      ? String(mw.provider).toLowerCase()
      : "openai",
    model: String(mw.model || MIDDLEWARE_DEFAULT_MODELS.openai).trim() || MIDDLEWARE_DEFAULT_MODELS.openai,
    storeRawSummary: mw.storeRawSummary === true,
  };
  const pv = data.presentationVision || {};
  const pvProvider = ["anthropic", "openai", "gemini"].includes(String(pv.provider || "").toLowerCase())
    ? String(pv.provider).toLowerCase()
    : "openai";
  base.presentationVision = {
    provider: pvProvider,
    model: summaryModelMatchesProvider(pvProvider, pv.model)
      ? String(pv.model).trim()
      : VISION_DEFAULT_MODELS[pvProvider],
    fallbackModel: summaryModelMatchesProvider(pvProvider, pv.fallbackModel)
      ? String(pv.fallbackModel).trim()
      : VISION_FALLBACK_DEFAULT_MODELS[pvProvider],
    composeModel: summaryModelMatchesProvider(pvProvider, pv.composeModel)
      ? String(pv.composeModel).trim()
      : COMPOSE_DEFAULT_MODELS[pvProvider],
  };
  for (const id of ["anthropic", "openai", "gemini"]) {
    const p = data.providers?.[id];
    if (!p) continue;
    const hasKey = p.apiKey?.configured === true && p.apiKey?.source === "database";
    base.providers[id].enabled = p.enabled === true;
    base.providers[id].apiKeyMeta = hasKey ? p.apiKey : null;
    base.providers[id].apiKey = hasKey ? String(p.apiKeyValue || "").trim() : "";
    const saved = String(p.savedModel || p.model || "").trim();
    base.providers[id].savedModel = saved;
    base.providers[id].model = saved;
    base.providers[id].clearApiKey = false;
    base.providers[id].models = [];
    base.providers[id].modelsSource = "none";
    base.providers[id].modelsError = null;
  }
  return base;
}

function AdminTranscriptGlossaryPanel({ isLight = true, adminTextMain = "#0f2a4a", hintColor = "#64748b" }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [wrong, setWrong] = useState("");
  const [correct, setCorrect] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/admin/transcript-glossary");
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch (e) {
      setMsg(e?.message || "Could not load glossary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function addEntry(e) {
    e.preventDefault();
    const w = String(wrong).trim();
    const c = String(correct).trim();
    if (!w || !c) return;
    setBusy(true);
    setMsg("");
    try {
      await apiFetch("/admin/transcript-glossary", {
        method: "POST",
        body: { wrongText: w, correctText: c, notes: String(notes).trim() || undefined },
      });
      setWrong("");
      setCorrect("");
      setNotes("");
      setMsg("Added.");
      await load();
    } catch (err) {
      setMsg(err?.message || "Add failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeEntry(id) {
    if (!id || busy) return;
    setBusy(true);
    setMsg("");
    try {
      await apiFetch(`/admin/transcript-glossary/${encodeURIComponent(id)}`, { method: "DELETE" });
      await load();
    } catch (err) {
      setMsg(err?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 6 }}>
        Transcript glossary
      </div>
      <div style={{ fontSize: 12, color: hintColor, marginBottom: 14, lineHeight: 1.5 }}>
        Fix recurring ASR mistakes before middleware and summaries (e.g. Avada → Avaada). Global entries apply to all speakers; use participant scope only when two people share a similar name.
      </div>
      <form onSubmit={addEntry} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 120px" }}>
          <label className="adm-form-label">Heard (wrong)</label>
          <input className="adm-form-input" value={wrong} onChange={(e) => setWrong(e.target.value)} placeholder="Avada" />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <label className="adm-form-label">Correct</label>
          <input className="adm-form-input" value={correct} onChange={(e) => setCorrect(e.target.value)} placeholder="Avaada" />
        </div>
        <div style={{ flex: "2 1 160px" }}>
          <label className="adm-form-label">Notes (optional)</label>
          <input className="adm-form-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Company name" />
        </div>
        <button type="submit" className="adm-btn adm-btn-primary" disabled={busy} style={{ height: 38 }}>
          Add
        </button>
      </form>
      {msg ? <div style={{ fontSize: 12, marginBottom: 8, color: msg.startsWith("Could") || msg.includes("fail") ? "#b91c1c" : "#047857" }}>{msg}</div> : null}
      {loading ? (
        <div style={{ fontSize: 12, color: hintColor }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ fontSize: 12, color: hintColor }}>No glossary entries yet. Defaults are seeded on first migration when the table is empty.</div>
      ) : (
        <AdminDataGrid
          rows={entries}
          isLight={isLight}
          height={300}
          pageSize={5}
          pageSizeOptions={[5, 10, 25]}
          getRowId={(row) => row.id}
          columns={[
            { field: "wrong_text", headerName: "Wrong", minWidth: 150, flex: 1 },
            { field: "correct_text", headerName: "Correct", minWidth: 150, flex: 1 },
            { field: "scope", headerName: "Scope", minWidth: 145, flex: 1, valueGetter: (_value, row) => row.participant_id ? row.participant_name || "Participant" : "Global" },
            { field: "actions", headerName: "", width: 100, sortable: false, renderCell: ({ row }) => <button type="button" className="adm-btn adm-btn-ghost" disabled={busy} onClick={(event) => { event.stopPropagation(); removeEntry(row.id); }}>Remove</button> },
          ]}
        />
      )}
    </div>
  );
}

function AdminSummarySettings({
  isLight = true,
  adminTextMain = "#0f2a4a",
  adminTextMuted = "#64748b",
  adminTextSoft = "#94a3b8",
  adminBorder = "#e2e8f0",
  adminChipBg = "#f1f5f9",
  adminPanelBg = "#f8fafc",
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(emptySummarySettingsForm);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [testState, setTestState] = useState({});
  const [modelsLoading, setModelsLoading] = useState({});
  const [apiKeyVisible, setApiKeyVisible] = useState({});
  const [keySaving, setKeySaving] = useState({});
  const [keyMsgState, setKeyMsgState] = useState({});
  const [summaryTab, setSummaryTab] = useState("settings");
  const summaryFormRef = useRef(form);
  const testDismissTimersRef = useRef({});
  const SUMMARY_TEST_MSG_MS = 3 * 60 * 1000;

  function clearTestDismiss(id) {
    const t = testDismissTimersRef.current[id];
    if (t) {
      clearTimeout(t);
      delete testDismissTimersRef.current[id];
    }
  }

  function dismissTestMessage(id) {
    clearTestDismiss(id);
    setTestState((s) => ({ ...s, [id]: { ...(s[id] || {}), ok: null, msg: "" } }));
  }

  function scheduleTestDismiss(id) {
    clearTestDismiss(id);
    testDismissTimersRef.current[id] = setTimeout(() => {
      setTestState((s) => ({ ...s, [id]: { ...(s[id] || {}), ok: null, msg: "" } }));
      delete testDismissTimersRef.current[id];
    }, SUMMARY_TEST_MSG_MS);
  }

  useEffect(() => {
    const timers = testDismissTimersRef.current;
    return () => {
      Object.keys(timers).forEach((k) => {
        clearTimeout(timers[k]);
        delete timers[k];
      });
    };
  }, []);

  function setProviderKeyMsg(id, type, msg) {
    if (!type || !msg) {
      setKeyMsgState((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
      return;
    }
    setKeyMsgState((s) => ({ ...s, [id]: { type, msg } }));
  }
  summaryFormRef.current = form;

  const fetchProviderModels = useCallback(async (id, apiKeyOverride) => {
    const p = summaryFormRef.current.providers[id];
    const typedKey = String(apiKeyOverride ?? p?.apiKey ?? "").trim();
    if (!summaryProviderHasAdminKey(p) && !typedKey) return;
    setModelsLoading((s) => ({ ...s, [id]: true }));
    try {
      const data = await apiFetch("/admin/summary-settings/models", {
        method: "POST",
        body: {
          provider: id,
          apiKey: typedKey || undefined,
          model: p?.savedModel || p?.model || undefined,
        },
      });
      if (data?.source !== "api" || !Array.isArray(data?.models) || !data.models.length) {
        setForm((prev) => {
          const cur = prev.providers[id];
          const keepModel = summaryProviderSavedModel(cur);
          return {
            ...prev,
            providers: {
              ...prev.providers,
              [id]: {
                ...cur,
                models: keepModel ? [keepModel] : [],
                model: keepModel,
                modelsSource: data?.source || "error",
                modelsError: data?.error || "No models returned from provider",
              },
            },
          };
        });
        return;
      }
      setForm((prev) => {
        const cur = prev.providers[id];
        const models = [...data.models];
        const prefer = String(cur?.savedModel || cur?.model || "").trim();
        const nextModel = prefer && models.includes(prefer) ? prefer : models[0] || "";
        return {
          ...prev,
          providers: {
            ...prev.providers,
            [id]: {
              ...cur,
              models,
              model: nextModel,
              modelsSource: "api",
              modelsError: null,
            },
          },
        };
      });
    } catch (e) {
      setForm((prev) => {
        const cur = prev.providers[id];
        const keepModel = summaryProviderSavedModel(cur);
        return {
          ...prev,
          providers: {
            ...prev.providers,
            [id]: {
              ...cur,
              models: keepModel ? [keepModel] : [],
              model: keepModel,
              modelsSource: "error",
              modelsError: e.message || "Failed to fetch models",
            },
          },
        };
      });
    } finally {
      setModelsLoading((s) => ({ ...s, [id]: false }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await apiFetch("/admin/summary-settings");
        if (cancelled) return;
        const nextForm = summarySettingsFromApi(data);
        setForm(nextForm);
        setApiKeyVisible({});
        setUpdatedAt(data?.updatedAt || null);
        setUpdatedBy(data?.updatedBy || null);
        if (!cancelled) {
          for (const pid of ["anthropic", "openai", "gemini"]) {
            if (summaryProviderHasAdminKey(nextForm.providers[pid])) {
              fetchProviderModels(pid);
            }
          }
          const mwProv = middlewareProviderId(nextForm);
          if (summaryProviderHasAdminKey(nextForm.providers[mwProv])) {
            fetchProviderModels(mwProv);
          }
          const visProv = visionProviderId(nextForm);
          if (summaryProviderHasAdminKey(nextForm.providers[visProv])) {
            fetchProviderModels(visProv);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load summary settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchProviderModels]);

  function patchProvider(id, patch) {
    setForm((prev) => ({
      ...prev,
      providers: {
        ...prev.providers,
        [id]: { ...prev.providers[id], ...patch },
      },
    }));
  }

  function buildSummarySettingsBody(overrides = {}) {
    const body = {
      primaryProvider: form.primaryProvider,
      fallbackEnabled: form.fallbackEnabled,
      transcriptMiddleware: {
        enabled: form.transcriptMiddleware?.enabled !== false,
        provider: middlewareProviderId(form),
        model: form.transcriptMiddleware?.model || MIDDLEWARE_DEFAULT_MODELS[middlewareProviderId(form)],
        storeRawSummary: form.transcriptMiddleware?.storeRawSummary === true,
      },
      presentationVision: {
        provider: visionProviderId(form),
        model: visionFieldModel(form, "model", VISION_DEFAULT_MODELS),
        fallbackModel: visionFieldModel(form, "fallbackModel", VISION_FALLBACK_DEFAULT_MODELS),
        composeModel: visionFieldModel(form, "composeModel", COMPOSE_DEFAULT_MODELS),
      },
      providers: {},
    };
    for (const pid of ["anthropic", "openai", "gemini"]) {
      const prov = form.providers[pid];
      const o = overrides[pid] || {};
      // Only send apiKey when explicitly saving/clearing a key — bulk save keeps existing DB secrets.
      const rawKey = o.apiKey != null ? String(o.apiKey).trim() : "";
      body.providers[pid] = {
        enabled: o.enabled != null ? o.enabled === true : prov.enabled === true,
        model: o.model != null ? o.model : (prov.model || prov.savedModel),
        apiKey: rawKey,
        clearApiKey: o.clearApiKey != null ? o.clearApiKey === true : prov.clearApiKey === true,
      };
    }
    return body;
  }

  async function clearProviderKey(id) {
    setProviderKeyMsg(id, null, "");
    setKeySaving((s) => ({ ...s, [id]: true }));
    try {
      const data = await apiFetch("/admin/summary-settings", {
        method: "PUT",
        body: buildSummarySettingsBody({
          [id]: { apiKey: "", clearApiKey: true },
        }),
      });
      const nextForm = summarySettingsFromApi(data);
      setForm((prev) => ({
        ...nextForm,
        providers: {
          ...nextForm.providers,
          [id]: {
            ...nextForm.providers[id],
            apiKey: "",
            clearApiKey: false,
            models: [],
            model: "",
            savedModel: "",
            modelsSource: "none",
            modelsError: null,
          },
        },
      }));
      setUpdatedAt(data?.updatedAt || null);
      setUpdatedBy(data?.updatedBy || null);
      setProviderKeyMsg(id, "success", "API key removed from database.");
      setTimeout(() => setProviderKeyMsg(id, null, ""), 4000);
    } catch (e) {
      setProviderKeyMsg(id, "error", e.message || "Failed to clear API key.");
    } finally {
      setKeySaving((s) => ({ ...s, [id]: false }));
    }
  }

  async function saveProviderKey(id) {
    const p = form.providers[id];
    const key = String(p?.apiKey || "").trim();
    setProviderKeyMsg(id, null, "");

    if (!key) {
      setProviderKeyMsg(id, "error", "Please paste an API key before saving.");
      return;
    }
    if (key.length < 12) {
      setProviderKeyMsg(id, "error", "API key looks too short. Check the key and try again.");
      return;
    }

    setKeySaving((s) => ({ ...s, [id]: true }));
    try {
      let modelData;
      try {
        modelData = await apiFetch("/admin/summary-settings/models", {
          method: "POST",
          body: { provider: id, apiKey: key },
        });
      } catch (e) {
        const msg = String(e?.message || "").toLowerCase();
        if (msg.includes("401") || msg.includes("403") || msg.includes("invalid") || msg.includes("auth")) {
          setProviderKeyMsg(id, "error", "Invalid API key — authentication failed with the provider.");
        } else {
          setProviderKeyMsg(id, "error", e.message || "Could not verify API key with the provider.");
        }
        return;
      }

      if (modelData?.source !== "api" || !Array.isArray(modelData?.models) || !modelData.models.length) {
        setProviderKeyMsg(
          id,
          "error",
          modelData?.error || "Invalid API key — no models returned from the provider."
        );
        return;
      }

      const data = await apiFetch("/admin/summary-settings", {
        method: "PUT",
        body: buildSummarySettingsBody({
          [id]: { apiKey: key, clearApiKey: false },
        }),
      });
      const nextForm = summarySettingsFromApi(data);
      const prefer = String(p?.savedModel || p?.model || "").trim();
      const models = [...modelData.models];
      const nextModel = prefer && models.includes(prefer) ? prefer : models[0] || "";
      const savedInBox = String(nextForm.providers[id]?.apiKey || key).trim() || key;

      setForm((prev) => ({
        ...nextForm,
        providers: {
          ...nextForm.providers,
          [id]: {
            ...nextForm.providers[id],
            apiKey: savedInBox,
            models,
            model: nextModel,
            modelsSource: "api",
            modelsError: null,
          },
        },
      }));
      setUpdatedAt(data?.updatedAt || null);
      setUpdatedBy(data?.updatedBy || null);
      setProviderKeyMsg(
        id,
        "success",
        `API key saved. ${models.length} model${models.length === 1 ? "" : "s"} loaded.`
      );
      setTimeout(() => setProviderKeyMsg(id, null, ""), 5000);
    } catch (e) {
      setProviderKeyMsg(id, "error", e.message || "Failed to save API key. Please try again.");
    } finally {
      setKeySaving((s) => ({ ...s, [id]: false }));
    }
  }

  async function save(e) {
    e?.preventDefault?.();
    setSaving(true);
    setErr(null);
    try {
      const body = buildSummarySettingsBody();
      const data = await apiFetch("/admin/summary-settings", { method: "PUT", body });
      const nextForm = summarySettingsFromApi(data);
      setForm(nextForm);
      setUpdatedAt(data?.updatedAt || null);
      setUpdatedBy(data?.updatedBy || null);
      for (const id of ["anthropic", "openai", "gemini"]) {
        if (summaryProviderHasAdminKey(nextForm.providers[id])) fetchProviderModels(id);
      }
      const mwProv = middlewareProviderId(nextForm);
      if (summaryProviderHasAdminKey(nextForm.providers[mwProv])) fetchProviderModels(mwProv);
      const visProv = visionProviderId(nextForm);
      if (summaryProviderHasAdminKey(nextForm.providers[visProv])) fetchProviderModels(visProv);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function testProvider(id) {
    clearTestDismiss(id);
    setTestState((s) => ({ ...s, [id]: { loading: true, ok: null, msg: "" } }));
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), ADMIN_SUMMARY_TEST_TIMEOUT_MS);
    try {
      const p = form.providers[id];
      const model = summaryProviderSavedModel(p) || p.model;
      const data = await apiFetch("/admin/summary-settings/test", {
        method: "POST",
        signal: controller.signal,
        body: {
          provider: id,
          model,
          apiKey: summarySettingsTestApiKey(p),
        },
      });
      const methodNote =
        data?.testMethod === "models"
          ? " (checked via provider model catalog)"
          : data?.testMethod === "ping"
            ? " (checked via tiny chat request)"
            : "";
      setTestState((s) => ({
        ...s,
        [id]: {
          loading: false,
          ok: true,
          msg:
            (data?.message ||
              "Connection successful. The API key is valid and the selected provider/model is reachable.") +
            methodNote,
        },
      }));
      scheduleTestDismiss(id);
      apiFetch("/admin/summary-settings")
        .then((refreshed) => {
          setForm((prev) => ({
            ...prev,
            usageByProvider: refreshed.usageByProvider || prev.usageByProvider,
          }));
        })
        .catch(() => {});
    } catch (e) {
      const aborted = e?.name === "AbortError" || controller.signal.aborted;
      setTestState((s) => ({
        ...s,
        [id]: {
          loading: false,
          ok: false,
          msg: formatSummaryProviderTestError(e, { aborted }),
        },
      }));
      scheduleTestDismiss(id);
    } finally {
      clearTimeout(abortTimer);
    }
  }

  const cardBg = isLight ? "#fff" : "rgba(15,23,42,0.55)";
  const hintColor = isLight ? adminTextMuted : "rgba(255,255,255,0.45)";
  const mwProvider = middlewareProviderId(form);
  const mwProviderCard = form.providers[mwProvider];
  const mwModels = middlewareModelOptions(form);
  const mwModelsBusy = modelsLoading[mwProvider] === true;
  const visProvider = visionProviderId(form);
  const visProviderCard = form.providers[visProvider];
  const visModels = visionModelOptions(form);
  const visModelsBusy = modelsLoading[visProvider] === true;

  return (
    <>
      <AdminPremiumHero
        badge="Meeting summaries"
        badgeIconD={IC.mic}
        title="Summary AI"
        desc="Choose which LLM providers generate meeting summaries (MoM). Configure transcript cleanup middleware, screen-recording vision, API keys, and models. Falls back to other enabled providers when allowed."
        isLight={isLight}
        meta={
          summaryTab === "settings" && updatedAt ? (
            <span>
              Updated {new Date(updatedAt).toLocaleString()}
              {updatedBy ? ` by ${updatedBy}` : ""}
            </span>
          ) : null
        }
      />

      <div className="adm-smtp-tabs" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button
          type="button"
          className={`adm-sec-chip${summaryTab === "settings" ? " active" : ""}`}
          onClick={() => setSummaryTab("settings")}
        >
          Configuration
        </button>
        <button
          type="button"
          className={`adm-sec-chip${summaryTab === "usage" ? " active" : ""}`}
          onClick={() => setSummaryTab("usage")}
        >
          Usage
        </button>
      </div>

      <div style={{ display: summaryTab === "usage" ? "block" : "none" }}>
        <AdminSummaryUsagePanel
          isLight={isLight}
          adminTextMain={adminTextMain}
          adminTextMuted={adminTextMuted}
          adminBorder={adminBorder}
          adminChipBg={adminChipBg}
        />
      </div>

      <div style={{ display: summaryTab === "settings" ? "block" : "none" }}>
      {loading ? (
        <div className="adm-card" style={{ padding: 24, color: hintColor, fontSize: 13 }}>
          Loading summary settings…
        </div>
      ) : (
        <form onSubmit={save} style={{ paddingBottom: 24 }}>
          <div className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 6 }}>
              Primary provider
            </div>
            <div style={{ fontSize: 12, color: hintColor, marginBottom: 12 }}>
              Used first for every new meeting summary. Other enabled providers are tried only if fallback is on.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {["anthropic", "openai", "gemini"].map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`adm-sec-chip${form.primaryProvider === id ? " active" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, primaryProvider: id }))}
                >
                  {SUMMARY_PROVIDER_META[id].short}
                </button>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: isLight ? adminTextMain : "#E8F0F7", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.fallbackEnabled !== false}
                onChange={(e) => setForm((f) => ({ ...f, fallbackEnabled: e.target.checked }))}
              />
              Allow fallback to other enabled providers if primary fails
            </label>
          </div>

          <div className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 6 }}>
              Transcript AI middleware
            </div>
            <div style={{ fontSize: 12, color: hintColor, marginBottom: 14, lineHeight: 1.5 }}>
              Cleans noisy transcripts (wrong names, filler, ASR errors) before meeting summary, who-spoke recap, and presentation summary. Pick a provider and model — uses the API key saved in that provider&apos;s card below.
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: isLight ? adminTextMain : "#E8F0F7", cursor: "pointer", marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={form.transcriptMiddleware?.enabled !== false}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    transcriptMiddleware: { ...f.transcriptMiddleware, enabled: e.target.checked },
                  }))
                }
              />
              Enable AI transcript cleanup before summaries
            </label>
            {form.transcriptMiddleware?.enabled !== false ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label className="adm-form-label">Cleanup provider</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {["anthropic", "openai", "gemini"].map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={`adm-sec-chip${mwProvider === id ? " active" : ""}`}
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            transcriptMiddleware: {
                              ...f.transcriptMiddleware,
                              provider: id,
                            },
                          }));
                          if (summaryProviderHasAdminKey(form.providers[id])) {
                            fetchProviderModels(id);
                          }
                        }}
                      >
                        {SUMMARY_PROVIDER_META[id].short}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                    <label className="adm-form-label" style={{ margin: 0 }}>
                      Cleanup model ({SUMMARY_PROVIDER_META[mwProvider]?.short || mwProvider})
                    </label>
                    {summaryProviderHasAdminKey(mwProviderCard) ? (
                      <button
                        type="button"
                        className="adm-ghost-btn"
                        style={{ padding: "2px 8px", fontSize: 11 }}
                        disabled={mwModelsBusy}
                        onClick={() => fetchProviderModels(mwProvider)}
                      >
                        {mwModelsBusy ? "Loading…" : "Refresh models"}
                      </button>
                    ) : null}
                  </div>
                  {summaryProviderHasAdminKey(mwProviderCard) ? (
                    <SummaryModelPicker
                      provider={mwProvider}
                      models={mwModels}
                      value={form.transcriptMiddleware?.model || MIDDLEWARE_DEFAULT_MODELS[mwProvider]}
                      onChange={(m) =>
                        setForm((f) => ({
                          ...f,
                          transcriptMiddleware: { ...f.transcriptMiddleware, model: m },
                        }))
                      }
                      isLight={isLight}
                      adminTextMain={adminTextMain}
                      adminTextMuted={adminTextMuted}
                      adminBorder={adminBorder}
                    />
                  ) : (
                    <input
                      className="adm-input"
                      value={form.transcriptMiddleware?.model || MIDDLEWARE_DEFAULT_MODELS[mwProvider]}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          transcriptMiddleware: { ...f.transcriptMiddleware, model: e.target.value },
                        }))
                      }
                      placeholder={MIDDLEWARE_DEFAULT_MODELS[mwProvider]}
                    />
                  )}
                  <div className="adm-form-hint" style={{ marginTop: 8 }}>
                    {summaryProviderHasAdminKey(mwProviderCard)
                      ? summaryProviderModelsReady(mwProviderCard)
                        ? `${mwModels.length} model${mwModels.length === 1 ? "" : "s"} available — switch All / Recommended in the list.`
                        : "Models load automatically from your API key when this page opens. Click Refresh models if the list looks stale."
                      : `Save a ${SUMMARY_PROVIDER_META[mwProvider]?.label || mwProvider} API key below, or type a model id manually.`}
                  </div>
                  {!summaryProviderHasAdminKey(mwProviderCard) ? (
                    <div className="adm-form-hint" style={{ marginTop: 6, color: "#F59E0B" }}>
                      To use Claude for cleanup: select Claude above, then save your Anthropic API key in the Claude card.
                    </div>
                  ) : null}
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: isLight ? adminTextMain : "#E8F0F7", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.transcriptMiddleware?.storeRawSummary === true}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        transcriptMiddleware: { ...f.transcriptMiddleware, storeRawSummary: e.target.checked },
                      }))
                    }
                  />
                  Also generate and store raw-path MoM for comparison (extra API cost)
                </label>
              </>
            ) : (
              <div className="adm-form-hint">
                When off, only basic transcript cleanup runs (translation + dedupe). Summaries use that lighter pass.
              </div>
            )}
          </div>

          <AdminTranscriptGlossaryPanel isLight={isLight} adminTextMain={adminTextMain} hintColor={hintColor} />

          <div className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 6 }}>
              Screen recording vision
            </div>
            <div style={{ fontSize: 12, color: hintColor, marginBottom: 14, lineHeight: 1.5 }}>
              Analyzes video frames from Recall screen recordings to describe slides, apps, and shared content for the Presentation Summary. Uses the API key saved in the selected provider&apos;s card below — models load from the provider API when you open this page or click Refresh.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="adm-form-label">Vision provider</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["anthropic", "openai", "gemini"].map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`adm-sec-chip${visProvider === id ? " active" : ""}`}
                    onClick={() => {
                      setForm((f) => ({
                        ...f,
                        presentationVision: {
                          ...f.presentationVision,
                          provider: id,
                          model: VISION_DEFAULT_MODELS[id],
                          fallbackModel: VISION_FALLBACK_DEFAULT_MODELS[id],
                          composeModel: COMPOSE_DEFAULT_MODELS[id],
                        },
                      }));
                      if (summaryProviderHasAdminKey(form.providers[id])) {
                        fetchProviderModels(id);
                      }
                    }}
                  >
                    {SUMMARY_PROVIDER_META[id].short}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <label className="adm-form-label" style={{ margin: 0 }}>
                  Frame analysis model ({SUMMARY_PROVIDER_META[visProvider]?.short || visProvider})
                </label>
                {summaryProviderHasAdminKey(visProviderCard) ? (
                  <button
                    type="button"
                    className="adm-ghost-btn"
                    style={{ padding: "2px 8px", fontSize: 11 }}
                    disabled={visModelsBusy}
                    onClick={() => fetchProviderModels(visProvider)}
                  >
                    {visModelsBusy ? "Loading…" : "Refresh models"}
                  </button>
                ) : null}
              </div>
              {summaryProviderHasAdminKey(visProviderCard) ? (
                <SummaryModelPicker
                  provider={visProvider}
                  models={visModels}
                  value={visionFieldModel(form, "model", VISION_DEFAULT_MODELS)}
                  onChange={(m) =>
                    setForm((f) => ({
                      ...f,
                      presentationVision: { ...f.presentationVision, model: m },
                    }))
                  }
                  isLight={isLight}
                  adminTextMain={adminTextMain}
                  adminTextMuted={adminTextMuted}
                  adminBorder={adminBorder}
                />
              ) : (
                <input
                  className="adm-input"
                  value={visionFieldModel(form, "model", VISION_DEFAULT_MODELS)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      presentationVision: { ...f.presentationVision, model: e.target.value },
                    }))
                  }
                  placeholder={VISION_DEFAULT_MODELS[visProvider]}
                />
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="adm-form-label">Fallback frame model (optional retry)</label>
              {summaryProviderHasAdminKey(visProviderCard) ? (
                <SummaryModelPicker
                  provider={visProvider}
                  models={visModels}
                  value={visionFieldModel(form, "fallbackModel", VISION_FALLBACK_DEFAULT_MODELS)}
                  onChange={(m) =>
                    setForm((f) => ({
                      ...f,
                      presentationVision: { ...f.presentationVision, fallbackModel: m },
                    }))
                  }
                  isLight={isLight}
                  adminTextMain={adminTextMain}
                  adminTextMuted={adminTextMuted}
                  adminBorder={adminBorder}
                />
              ) : (
                <input
                  className="adm-input"
                  value={visionFieldModel(form, "fallbackModel", VISION_FALLBACK_DEFAULT_MODELS)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      presentationVision: { ...f.presentationVision, fallbackModel: e.target.value },
                    }))
                  }
                  placeholder={VISION_FALLBACK_DEFAULT_MODELS[visProvider]}
                />
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <label className="adm-form-label">Presentation summary compose model</label>
              {summaryProviderHasAdminKey(visProviderCard) ? (
                <SummaryModelPicker
                  provider={visProvider}
                  models={visModels}
                  value={visionFieldModel(form, "composeModel", COMPOSE_DEFAULT_MODELS)}
                  onChange={(m) =>
                    setForm((f) => ({
                      ...f,
                      presentationVision: { ...f.presentationVision, composeModel: m },
                    }))
                  }
                  isLight={isLight}
                  adminTextMain={adminTextMain}
                  adminTextMuted={adminTextMuted}
                  adminBorder={adminBorder}
                />
              ) : (
                <input
                  className="adm-input"
                  value={visionFieldModel(form, "composeModel", COMPOSE_DEFAULT_MODELS)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      presentationVision: { ...f.presentationVision, composeModel: e.target.value },
                    }))
                  }
                  placeholder={COMPOSE_DEFAULT_MODELS[visProvider]}
                />
              )}
              <div className="adm-form-hint" style={{ marginTop: 8 }}>
                {summaryProviderHasAdminKey(visProviderCard)
                  ? summaryProviderModelsReady(visProviderCard)
                    ? `${visModels.length} model${visModels.length === 1 ? "" : "s"} available for vision and compose.`
                    : "Models load automatically from your API key when this page opens. Click Refresh models if the list looks stale."
                  : `Save a ${SUMMARY_PROVIDER_META[visProvider]?.label || visProvider} API key below, or type model ids manually.`}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, alignItems: "start" }}>
            {["anthropic", "openai", "gemini"].map((id) => {
              const p = form.providers[id];
              const meta = SUMMARY_PROVIDER_META[id];
              const models = summaryProviderModelList(p);
              const chosenModel = summaryProviderSavedModel(p);
              const keyMeta = p.apiKeyMeta;
              const test = testState[id];
              const modelsBusy = modelsLoading[id] === true;
              const isPrimary = form.primaryProvider === id;
              const keyMsg = keyMsgState[id];
              return (
                <div
                  key={id}
                  className="adm-card"
                  style={{
                    padding: 18,
                    background: cardBg,
                    border: isPrimary ? "1px solid rgba(37,99,235,0.35)" : undefined,
                    overflow: "visible",
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7" }}>
                        {meta.label}
                        {isPrimary ? (
                          <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: "#2563EB", textTransform: "uppercase" }}>
                            Primary
                          </span>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 11, color: hintColor, marginTop: 4 }}>
                        {id === "anthropic" ? "Anthropic Claude API" : id === "openai" ? "OpenAI Chat Completions" : "Google Gemini API"}
                      </div>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: hintColor, cursor: "pointer", flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={p.enabled === true}
                        onChange={(e) => patchProvider(id, { enabled: e.target.checked })}
                      />
                      On
                    </label>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                      <label className="adm-form-label" style={{ margin: 0 }}>API key</label>
                      {keyMeta?.configured ? (
                        <button
                          type="button"
                          className="adm-ghost-btn"
                          style={{ padding: "2px 8px", fontSize: 11 }}
                          onClick={() => clearProviderKey(id)}
                          disabled={keySaving[id]}
                        >
                          Clear key
                        </button>
                      ) : null}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                        <input
                          className="adm-input"
                          type={apiKeyVisible[id] ? "text" : "password"}
                          value={p.apiKey || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const patch = {
                              apiKey: v,
                              clearApiKey: false,
                            };
                            patch.models = [];
                            patch.model = "";
                            patch.modelsSource = "none";
                            patch.modelsError = null;
                            if (!v.trim() && !keyMeta?.configured) {
                              patch.savedModel = "";
                            }
                            setProviderKeyMsg(id, null, "");
                            patchProvider(id, patch);
                          }}
                          placeholder="Paste API key here"
                          autoComplete="off"
                          style={{ paddingRight: 40, width: "100%" }}
                        />
                        <button
                          type="button"
                          onClick={() => setApiKeyVisible((s) => ({ ...s, [id]: !s[id] }))}
                          title={apiKeyVisible[id] ? "Hide API key" : "Show API key"}
                          aria-label={apiKeyVisible[id] ? "Hide API key" : "Show API key"}
                          style={{
                            position: "absolute",
                            right: 4,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 32,
                            height: 32,
                            border: "none",
                            borderRadius: 6,
                            background: "transparent",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: adminTextMuted,
                          }}
                        >
                          <Svg d={apiKeyVisible[id] ? IC.eyeOff : IC.eye} size={17} stroke={1.8} />
                        </button>
                      </div>
                      <button
                        type="button"
                        title="Save API key"
                        aria-label="Save API key"
                        disabled={keySaving[id]}
                        onClick={() => saveProviderKey(id)}
                        style={{
                          flexShrink: 0,
                          width: 36,
                          height: 36,
                          border: `1px solid ${adminBorder}`,
                          borderRadius: 8,
                          background: keyMsg?.type === "success" ? "rgba(34,197,94,0.12)" : isLight ? "#fff" : "rgba(255,255,255,0.06)",
                          cursor: keySaving[id] ? "wait" : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: keyMsg?.type === "success" ? "#16A34A" : "#2563EB",
                        }}
                      >
                        {keySaving[id] ? (
                          <span style={{ width: 14, height: 14, border: "2px solid #93C5FD", borderTopColor: "#2563EB", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                        ) : keyMsg?.type === "success" ? (
                          <Svg d={IC.check} size={17} stroke={2.2} />
                        ) : (
                          <Svg d={IC.save} size={17} stroke={1.9} />
                        )}
                      </button>
                    </div>
                    {keyMsg ? (
                      <div
                        style={{
                          marginTop: 8,
                          padding: "8px 10px",
                          borderRadius: 8,
                          fontSize: 12,
                          lineHeight: 1.45,
                          background: keyMsg.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(220,38,38,0.1)",
                          border: keyMsg.type === "success" ? "1px solid rgba(34,197,94,0.25)" : "1px solid rgba(220,38,38,0.25)",
                          color: keyMsg.type === "success" ? "#15803D" : "#DC2626",
                        }}
                      >
                        {keyMsg.msg}
                      </div>
                    ) : keyMeta?.configured ? (
                      <div className="adm-form-hint" style={{ marginTop: 6, color: keyMeta.readableOnServer === false ? "#B45309" : "#15803D" }}>
                        {keyMeta.readableOnServer === false
                          ? "Key is in the database but was encrypted on another server — paste and save once here."
                          : `Stored in database for meeting summaries${keyMeta.hint ? ` (${keyMeta.hint})` : ""}.`}
                      </div>
                    ) : (
                      <div className="adm-form-hint" style={{ marginTop: 6 }}>
                        Paste your API key and click the save icon. Keys from server .env are not used here.
                      </div>
                    )}
                  </div>

                  {summaryProviderHasAdminKey(p) ? (
                    <div style={{ marginBottom: 12 }}>
                      {summaryProviderShowModelUI(p, modelsBusy) ? (
                        <>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                            <label className="adm-form-label" style={{ margin: 0 }}>Model</label>
                            <button
                              type="button"
                              className="adm-ghost-btn"
                              style={{ padding: "2px 8px", fontSize: 11 }}
                              disabled={modelsBusy}
                              onClick={() => fetchProviderModels(id)}
                            >
                              {modelsBusy ? "Loading…" : "Refresh"}
                            </button>
                          </div>
                          {modelsBusy ? (
                            <div className="adm-form-hint" style={{ padding: "10px 0" }}>
                              Fetching models from provider API…
                            </div>
                          ) : (
                            <>
                              <SummaryModelPicker
                                provider={id}
                                models={models}
                                value={chosenModel}
                                onChange={(m) => patchProvider(id, { model: m })}
                                isLight={isLight}
                                adminTextMain={adminTextMain}
                                adminTextMuted={adminTextMuted}
                                adminBorder={adminBorder}
                              />
                              <div className="adm-form-hint" style={{ marginTop: 8 }}>
                                {summaryProviderModelsReady(p)
                                  ? `${models.length} model${models.length === 1 ? "" : "s"} from your API key`
                                  : chosenModel
                                    ? "Saved model shown — click Refresh to load the full list from the provider."
                                    : "Select a model after the list loads."}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <div style={{ marginTop: 4 }}>
                          <button
                            type="button"
                            className="adm-ghost-btn"
                            disabled={modelsBusy}
                            onClick={() => fetchProviderModels(id)}
                          >
                            {modelsBusy ? "Loading models…" : "Load models from API"}
                          </button>
                          {p.modelsError ? (
                            <div className="adm-form-hint" style={{ marginTop: 8, color: "#FCA5A5" }}>
                              {p.modelsError}
                            </div>
                          ) : (
                            <div className="adm-form-hint" style={{ marginTop: 8 }}>
                              Save your API key first, then load models from the provider.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="adm-ghost-btn"
                    disabled={!p.enabled || test?.loading}
                    onClick={() => testProvider(id)}
                  >
                    {test?.loading ? `Testing ${meta.label}…` : "Test connection"}
                  </button>
                  <AdminProviderTestStatus test={test} onDismiss={() => dismissTestMessage(id)} />
                </div>
              );
            })}
          </div>

          {err ? (
            <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.12)", color: "#FCA5A5", fontSize: 12.5 }}>
              {err}
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button type="submit" className="adm-primary-btn" disabled={saving}>
              {saving ? "Saving…" : saved ? "Saved!" : "Save summary settings"}
            </button>
          </div>
        </form>
      )}
      </div>
    </>
  );
}

function AdminSMTP({
  isLight = true,
  adminTextMain = "#0f2a4a",
  adminTextMuted = "#64748b",
  adminTextSoft = "#94a3b8",
  adminBorder = "#e2e8f0",
  adminChipBg = "#f1f5f9",
  adminPanelBg = "#f8fafc",
  smtpReport = null,
  smtpLoading = false,
  smtpError = null,
}) {
  const [smtpTab, setSmtpTab] = useState("usage");
  const [host, setHost] = useState("smtp.gmail.com");
  const [port, setPort] = useState("587");
  const [user, setUser] = useState(BOT_EMAIL_DISPLAY);
  const [pass, setPass] = useState("");
  const [fromName, setFromName] = useState("Avaada MeetAI");
  const [sec, setSec] = useState("TLS");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [activeSource, setActiveSource] = useState("none");
  const [smtpEnabled, setSmtpEnabled] = useState(true);
  const [passwordHint, setPasswordHint] = useState(null);
  const [passwordConfigured, setPasswordConfigured] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [approvalRows, setApprovalRows] = useState([]);
  const [resendMeetingId, setResendMeetingId] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);
  const [passVisible, setPassVisible] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testOk, setTestOk] = useState(null);
  const testFailed = typeof testOk === "string" && testOk.toLowerCase().startsWith("error:");

  const applySettings = useCallback((data) => {
    if (!data) return;
    setHost(data.host || "smtp.gmail.com");
    setPort(String(data.port ?? 587));
    setUser(data.user || "");
    setFromName(data.fromName || "Avaada MeetAI");
    const security = String(data.security || "TLS").toUpperCase();
    setSec(security === "SSL" ? "SSL" : security === "NONE" ? "None" : "TLS");
    setActiveSource(data.activeSource || "none");
    setSmtpEnabled(data.enabled !== false);
    setPasswordHint(data.password?.hint || null);
    setPasswordConfigured(data.password?.configured === true);
    setUpdatedAt(data.updatedAt || null);
    // Never hydrate the live secret into the form — blank means keep existing.
    setPass("");
    setPassVisible(false);
    setClearPassword(false);
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const data = await apiFetch("/admin/smtp/settings");
      applySettings(data);
    } catch (e) {
      setLoadErr(e.message || "Failed to load SMTP settings");
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { apiFetch("/admin/transcript-approvals").then((data) => setApprovalRows(Array.isArray(data?.rows) ? data.rows : [])).catch(() => {}); }, []);
  async function resendApproval(meetingId) { setResendMeetingId(meetingId); setResendMessage(null); try { await apiFetch(`/admin/transcript-approvals/${encodeURIComponent(meetingId)}/resend-email`, { method: "POST" }); setApprovalRows((rows) => rows.filter((r) => r.meeting_id !== meetingId)); setResendMessage("Approval email sent again."); } catch (e) { setResendMessage(e.message || "Could not resend approval email."); } finally { setResendMeetingId(null); } }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setSaveErr(null);
    try {
      const data = await apiFetch("/admin/smtp/settings", {
        method: "PUT",
        body: {
          host,
          port: Number(port) || 587,
          security: sec === "SSL" ? "SSL" : sec === "None" ? "NONE" : "TLS",
          user,
          fromName,
          password: pass,
          clearPassword,
          enabled: smtpEnabled,
        },
      });
      applySettings(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveErr(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestOk(null);
    try {
      const body = {
        testEmail: user,
        host,
        port: Number(port) || 587,
        security: sec === "SSL" ? "SSL" : sec === "None" ? "NONE" : "TLS",
        user,
        fromName,
      };
      if (pass.trim()) body.password = pass.trim();
      const data = await apiFetch("/admin/smtp/test", { method: "POST", body });
      setTestOk(data?.message || true);
    } catch (err) {
      setTestOk("error: " + err.message);
    } finally {
      setTesting(false);
      setTimeout(() => setTestOk(null), 8000);
    }
  }

  const senderLabel = user || smtpReport?.smtp?.user || BOT_EMAIL_DISPLAY;
  const sourceLabel =
    activeSource === "database"
      ? "Admin config"
      : activeSource === "env"
      ? ".env"
      : activeSource === "stopped"
      ? "Stopped"
      : "Not set";
  const hintColor = isLight ? adminTextMuted : "rgba(255,255,255,0.45)";

  return (
    <>
      <AdminPremiumHero
        badge="Email delivery"
        badgeIconD={IC.mail}
        title="SMTP"
        desc="Recap email delivery stats and server configuration for AvaadaMeetAI."
        isLight={isLight}
        meta={
          <>
            <span>From {senderLabel}</span>
            {smtpTab === "config" && updatedAt ? (
              <>
                <span className="adm-hero-dot" />
                <span>
                  Updated {new Date(updatedAt).toLocaleString("en-IN", {
                    timeZone: APP_DISPLAY_TZ,
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                  {` · Active: ${sourceLabel}`}
                </span>
              </>
            ) : null}
          </>
        }
      />
      <div className="adm-smtp-tabs" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button
          type="button"
          className={`adm-sec-chip${smtpTab === "usage" ? " active" : ""}`}
          onClick={() => setSmtpTab("usage")}
        >
          SMTP Usage
        </button>
        <button
          type="button"
          className={`adm-sec-chip${smtpTab === "config" ? " active" : ""}`}
          onClick={() => setSmtpTab("config")}
        >
          Configuration
        </button>
        <button
          type="button"
          className={`adm-sec-chip${smtpTab === "attention" ? " active" : ""}`}
          onClick={() => setSmtpTab("attention")}
        >
          SMTP Attention{approvalRows.some((row) => row.approval_email_status === "failed") ? ` (${approvalRows.filter((row) => row.approval_email_status === "failed").length})` : ""}
        </button>
      </div>
      {smtpTab === "attention" && approvalRows.length ? <div className="adm-card" style={{ marginBottom: 16, padding: 16, border: "1px solid #FECACA", background: "#FFF7F7", borderRadius: 12 }}><div style={{ fontWeight: 800, color: "#991B1B", marginBottom: 6 }}>Approval emails needing attention</div><div style={{ fontSize: 12, color: "#7F1D1D", marginBottom: 10 }}>SMTP failed before. Fix SMTP configuration, then send the approval email again.</div>{approvalRows.map((row) => <div key={row.meeting_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid #FECACA", flexWrap: "wrap" }}><div style={{ minWidth: 0 }}><strong style={{ color: "#0F2A4A" }}>{row.approver_name || row.approver_email}</strong><div style={{ fontSize: 12, color: "#64748B" }}>{row.approver_email} · {row.status || "pending"}</div><div style={{ fontSize: 11, color: "#B91C1C", overflowWrap: "anywhere" }}>{row.approval_email_error || "Delivery failed"}</div></div><button type="button" className="adm-btn adm-btn--primary" disabled={resendMeetingId === row.meeting_id} onClick={() => resendApproval(row.meeting_id)}>{resendMeetingId === row.meeting_id ? "Sending…" : "Send again"}</button></div>)}{resendMessage ? <div style={{ marginTop: 10, fontSize: 12, color: resendMessage.includes("sent") ? "#047857" : "#B91C1C" }}>{resendMessage}</div> : null}</div> : null}
      <div style={{ display: smtpTab === "usage" ? "block" : "none" }}>
        <AdminSMTPUsagePanel
          isLight={isLight}
          adminTextMain={adminTextMain}
          adminTextMuted={adminTextMuted}
          adminTextSoft={adminTextSoft}
          adminBorder={adminBorder}
          adminChipBg={adminChipBg}
          report={smtpReport}
          loading={smtpLoading}
          err={smtpError}
          approvalRows={approvalRows}
          onResendApproval={resendApproval}
          resendMeetingId={resendMeetingId}
        />
      </div>
      <div style={{ display: smtpTab === "config" ? "block" : "none" }}>
        {loadErr ? (
          <div className="adm-card" style={{ padding: 16, marginBottom: 14, fontSize: 12, color: "#f87171", background: "rgba(220,38,38,0.08)" }}>
            {loadErr}
            <button type="button" className="adm-ghost-btn" style={{ marginLeft: 10 }} onClick={loadSettings}>Retry</button>
          </div>
        ) : null}
        {loading ? (
          <div className="adm-card" style={{ padding: 24, color: hintColor, fontSize: 13 }}>
            Loading SMTP settings…
          </div>
        ) : (
          <form onSubmit={save} style={{ paddingBottom: 24 }}>
            <div className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: isLight ? adminTextMain : "#E8F0F7", marginBottom: 6 }}>
                SMTP server
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0", color: adminTextMain, fontWeight: 700 }}>
                <input type="checkbox" checked={smtpEnabled} onChange={(e) => setSmtpEnabled(e.target.checked)} />
                SMTP sending enabled
              </label>
              {!smtpEnabled ? <div style={{ padding: 10, marginBottom: 12, color: "#b45309", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, fontSize: 12 }}>SMTP stopped. All approval, reminder, recap, and other outbound emails will be marked as not sent.</div> : null}
              <div style={{ fontSize: 12, color: hintColor, marginBottom: 16, lineHeight: 1.5 }}>
                Recap email settings saved here (encrypted). On send, MeetAI uses this first; if it fails, the server retries with <code style={{ fontSize: 11 }}>.env</code>.
              </div>
              <div className="smtp-grid" style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="adm-form-label">SMTP host</label>
                  <input className="adm-input" value={host} onChange={(e) => setHost(e.target.value)} />
                </div>
                <div>
                  <label className="adm-form-label">Port</label>
                  <input className="adm-input" value={port} onChange={(e) => setPort(e.target.value)} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="adm-form-label">Security</label>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {["TLS", "SSL", "None"].map((o) => (
                    <button
                      type="button"
                      key={o}
                      className={`adm-sec-chip${sec === o ? " active" : ""}`}
                      onClick={() => {
                        setSec(o);
                        if (o === "SSL") setPort("465");
                        else if (o === "TLS") setPort("587");
                      }}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                <div className="adm-form-hint" style={{ marginTop: 8 }}>
                  Gmail: <strong>TLS</strong> with port <strong>587</strong>, or <strong>SSL</strong> with port <strong>465</strong>. Use None only for internal/test servers.
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="adm-form-label">Sender email</label>
                <input className="adm-input" value={user} onChange={(e) => setUser(e.target.value)} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="adm-form-label">Sender display name</label>
                <input className="adm-input" value={fromName} onChange={(e) => setFromName(e.target.value)} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <label className="adm-form-label" style={{ margin: 0 }}>App password</label>
                  {passwordConfigured ? (
                    <button
                      type="button"
                      className="adm-ghost-btn"
                      style={{ padding: "2px 8px", fontSize: 11 }}
                      title="Clears the saved DB password and disables Admin SMTP so the server can fall back to .env"
                      onClick={() => {
                        setPass("");
                        setClearPassword(true);
                        setPasswordConfigured(false);
                        setPasswordHint(null);
                      }}
                    >
                      Clear password
                    </button>
                  ) : null}
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    className="adm-input"
                    type={passVisible ? "text" : "password"}
                    value={pass}
                    onChange={(e) => {
                      setPass(e.target.value);
                      setClearPassword(false);
                    }}
                    placeholder={passwordConfigured && !pass ? `Saved (${passwordHint}) — leave blank to keep` : "Gmail app password"}
                    autoComplete="off"
                    style={{ paddingRight: 40, width: "100%" }}
                  />
                  <button
                    type="button"
                    onClick={() => setPassVisible((v) => !v)}
                    title={passVisible ? "Hide password" : "Show password"}
                    aria-label={passVisible ? "Hide password" : "Show password"}
                    style={{
                      position: "absolute",
                      right: 4,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 32,
                      height: 32,
                      border: "none",
                      borderRadius: 6,
                      background: "transparent",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTextMuted,
                    }}
                  >
                    <Svg d={passVisible ? IC.eyeOff : IC.eye} size={17} stroke={1.8} />
                  </button>
                </div>
                <div className="adm-form-hint">Google Account → Security → 2FA → App Passwords</div>
              </div>
            </div>
            {saveErr ? (
              <div style={{ marginBottom: 12, fontSize: 12, color: "#f87171" }}>{saveErr}</div>
            ) : null}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="adm-ghost-btn" onClick={test} disabled={testing || saving}>
                {testing ? "Sending…" : "Send test email"}
              </button>
              <button type="submit" className="adm-primary-btn" disabled={saving || testing}>
                {saving ? "Saving…" : saved ? "Saved!" : "Save SMTP config"}
              </button>
            </div>
            {testOk ? (
              <div
                style={{
                  marginTop: 10,
                  padding: "9px 13px",
                  background: testFailed ? "rgba(220,38,38,0.12)" : "rgba(34,197,94,0.1)",
                  border: testFailed ? "1px solid rgba(220,38,38,0.25)" : "1px solid rgba(34,197,94,0.2)",
                  borderRadius: 8,
                  fontSize: 12.5,
                  color: testFailed ? "#FCA5A5" : "#86EFAC",
                }}
              >
                {testFailed ? testOk : typeof testOk === "string" ? testOk : `Test email sent from ${user}`}
              </div>
            ) : null}
          </form>
        )}
      </div>
    </>
  );
}

function calendarMeetingJoinUrl(m) {
  const direct = (
    joinUrlFromMeeting(m) ||
    String(m?.meetingUrl || m?.meeting_url || m?.meet_link || m?.hangoutLink || "").trim()
  );
  if (direct) return direct;
  // Backend usually normalizes conference/location links into meetingUrl, but
  // raw Google events can carry the URL only in location/description.
  const hay = `${m?.location || ""}\n${m?.description || ""}`;
  const found = hay.match(/https?:\/\/[^\s"'<>]+/i);
  return found ? found[0].replace(/[.,;:!?)]+$/, "") : "";
}

function calendarCanJoinMeeting(m, nowMs) {
  const st = effectiveMeetingStatus(m, nowMs);
  const url = calendarMeetingJoinUrl(m);
  return (
    st !== "cancelled" &&
    st !== "completed" &&
    st !== "ended" &&
    url &&
    /^https?:\/\//i.test(url)
  );
}

/** Calendar sidebar — click opens meeting; Join opens Meet link; auto-join toggle on Google events. */
function CalSidebarMeetingCard({
  m,
  nowMs,
  onOpen,
  onToggleMeetingAutoJoin,
  meetingAutoJoinSaving,
  showDate = false,
  compact = false,
  withAutoJoin = false,
  inDayPopup = false,
  onCardClick,
  showBotStatus = false,
}) {
  const effSt = effectiveMeetingStatus(m, nowMs);
  const cancelled = effSt === "cancelled";
  const evtColor = eventColorForMeeting(m, { adminBotPending: showBotStatus });
  const denseColor = cancelled ? STATUS_META.cancelled.fg : evtColor.border;
  const st = STATUS_META[effSt] || STATUS_META.upcoming;
  const googleEventId = String(m?.googleEventId || "").trim();
  const showAutoJoin =
    !showBotStatus &&
    (!compact || withAutoJoin) &&
    (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
    !!googleEventId &&
    effSt !== "cancelled" &&
    !!String(calendarMeetingJoinUrl(m) || "").trim() &&
    (inDayPopup || effSt === "upcoming" || effSt === "live");
  const saving = !!meetingAutoJoinSaving?.[googleEventId];
  const autoOn = m?.autoJoinEnabled === true;
  const botPending = showBotStatus && meetingBotDispatchPending(m);
  const botJoined = showBotStatus && !botPending && meetingBotJoinedCall(m);
  const canJoin = calendarCanJoinMeeting(m, nowMs);
  const joinUrl = calendarMeetingJoinUrl(m);
  const hasJoinUrl = !!joinUrl && /^https?:\/\//i.test(joinUrl);
  const showJoin = canJoin || (inDayPopup && hasJoinUrl && !cancelled);
  const joinIconPath =
    m.platform === "zoom"
      ? IC.video
      : m.platform === "teams"
        ? IC.users
        : m.platform === "meet" || m.platform === "gcal"
          ? IC.meet
          : IC.video;
  const openCtx = { source: "calendar" };
  const open = () => onOpen?.(m, openCtx);
  const activateCard = () => {
    if (onCardClick) onCardClick(m);
    else open();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={activateCard}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activateCard();
        }
      }}
      style={{
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${denseColor}`,
        borderRadius: "0 7px 7px 0",
        cursor: "pointer",
        background: cancelled ? T.surface : evtColor.bg,
        transition: "background .1s",
        opacity: cancelled ? 0.92 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = cancelled ? T.accentBg : evtColor.bgHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = cancelled ? T.surface : evtColor.bg;
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: cancelled ? T.textMuted : evtColor.text,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginBottom: 3,
          textDecoration: cancelled ? "line-through" : "none",
        }}
      >
        {m.title}
      </div>
      <div style={{ fontSize: 10, color: T.textMuted, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", lineHeight: 1.3 }}>
        {showDate ? (
          <>
            <span>{formatYmdDisplay(m.date)}</span>
            <span style={{ color: T.border }}>·</span>
          </>
        ) : null}
        <span>
          {formatMeetingTimeForDisplay(m.time)}–{formatMeetingTimeForDisplay(m.end)}
        </span>
        <span style={{ width: 3, height: 3, borderRadius: "50%", background: st.dot, display: "inline-block" }} />
        <span style={{ color: st.fg, fontWeight: 500 }}>{st.label}</span>
        {showBotStatus ? (
          <>
            <span style={{ color: T.border }}>·</span>
            <span
              style={{
                fontWeight: 600,
                color: botPending ? "#475569" : botJoined ? "#15803D" : "#B45309",
              }}
            >
              {botPending ? "MeetAI pending" : botJoined ? "MeetAI joined" : "MeetAI missed"}
            </span>
          </>
        ) : null}
      </div>
      {!compact && meetingHasDisplayableHost(m) ? (
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Host: {meetingHostName(m)}
        </div>
      ) : null}
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}
        onClick={(e) => e.stopPropagation()}
      >
        {showBotStatus ? (
          <>
            <button
              type="button"
              className="btn-outline"
              title="View meeting details"
              aria-label="View meeting details"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 28,
                padding: 0,
                lineHeight: 1,
                borderColor: T.border,
                color: T.accent,
              }}
              onClick={open}
            >
              <Svg d={IC.eye} color={T.accent} size={14} stroke={2.2} />
            </button>
            <button
              type="button"
              className={hasJoinUrl ? "btn-join-primary" : "btn-outline"}
              title={hasJoinUrl ? "Join meeting" : "No join link"}
              aria-label="Join meeting"
              disabled={!hasJoinUrl}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 28,
                padding: 0,
                lineHeight: 1,
                opacity: hasJoinUrl ? 1 : 0.5,
                cursor: hasJoinUrl ? "pointer" : "not-allowed",
                borderColor: hasJoinUrl ? undefined : T.border,
              }}
              onClick={() => {
                if (hasJoinUrl) window.open(joinUrl, "_blank", "noopener,noreferrer");
              }}
            >
              <Svg d={IC.video} color={hasJoinUrl ? "#fff" : T.textMuted} size={14} stroke={2.2} />
            </button>
          </>
        ) : (
          <>
            {showJoin ? (
              inDayPopup ? (
                <button
                  type="button"
                  className={canJoin ? "btn-join-primary" : "btn-outline"}
                  title={canJoin ? "Join meeting" : "Open meeting link"}
                  aria-label="Join meeting"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 28,
                    padding: 0,
                    lineHeight: 1,
                    borderColor: canJoin ? undefined : T.border,
                  }}
                  onClick={() => window.open(joinUrl, "_blank", "noopener,noreferrer")}
                >
                  <Svg d={joinIconPath} color={canJoin ? "#fff" : T.accent} size={14} stroke={2.2} />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-join-primary"
                  style={{ fontSize: 10.5, padding: "5px 12px", lineHeight: 1.2 }}
                  onClick={() => window.open(joinUrl, "_blank", "noopener,noreferrer")}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <Svg d={joinIconPath} color="#fff" size={12} stroke={2.2} />
                    Join meeting
                  </span>
                </button>
              )
            ) : null}
            <button
              type="button"
              className="btn-outline"
              title="Open meeting"
              aria-label="Open meeting"
              style={
                inDayPopup
                  ? {
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 28,
                      padding: 0,
                      lineHeight: 1,
                      borderColor: T.border,
                      color: T.accent,
                    }
                  : { fontSize: 10.5, padding: "5px 10px", lineHeight: 1.2, borderColor: T.border, color: T.accent }
              }
              onClick={open}
            >
              {inDayPopup ? <Svg d={IC.launch} color={T.accent} size={14} stroke={2.2} /> : "Open"}
            </button>
          </>
        )}
      </div>
      {showAutoJoin ? (
        <div
          style={{
            marginTop: 8,
            paddingTop: inDayPopup || (compact && withAutoJoin) ? 6 : 0,
            borderTop: inDayPopup || (compact && withAutoJoin) ? `1px solid ${T.border}` : "none",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ToggleBar
            checked={autoOn}
            disabled={saving}
            label={saving ? "Saving…" : autoOn ? JOIN_MEET_AI_LABEL + " on" : JOIN_MEET_AI_LABEL + " off"}
            onChange={(next) => onToggleMeetingAutoJoin?.(googleEventId, next, m)}
            stopPropagation
          />
        </div>
      ) : null}
    </div>
  );
}

/** Month grid / mini-calendar day click — full list for that date in a modal. */
function AdminCalendarMeetingDetailPopup({ meeting, onClose, onOpenJoin, onOpenCalendar }) {
  useEffect(() => {
    if (!meeting) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [meeting, onClose]);

  if (!meeting) return null;

  const nowMs = Date.now();
  const m = meeting;
  const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
  const regSt = adminSessionRegisterStatus(m, nowMs);
  const regMeta = STATUS_META[regSt] || STATUS_META.upcoming;
  const botJoined = meetingBotJoinedCall(m);
  const botPending = meetingBotDispatchPending(m);
  const meetAiLabel = botJoined
    ? "MeetAI joined"
    : botPending || regSt === "bot_pending"
      ? "MeetAI pending"
      : isEndedMeetingStatus(effectiveMeetingStatus(m, nowMs), m)
        ? "MeetAI missed"
        : "MeetAI not joined yet";
  const meetAiColor = botJoined ? "#15803D" : botPending || regSt === "bot_pending" ? "#B45309" : "#64748b";
  const hostName = meetingHasDisplayableHost(m) ? meetingHostName(m) : "";
  const hostEmail = meetingHostEmail(m);
  const ownerEmailRaw = String(m?.owner_email || m?.ownerEmail || "").trim();
  const ownerNameRaw = String(m?.owner_name || m?.ownerName || "").trim();
  const ownerIsBot =
    isBotServiceAccountEmail(ownerEmailRaw) || isServiceAccountDisplayName(ownerNameRaw);
  const ownerName = ownerIsBot ? "" : formatDisplayPersonName(ownerNameRaw) || ownerNameRaw;
  const ownerEmail = ownerIsBot ? "" : ownerEmailRaw;
  const attendanceCtx = attendanceContextFromMeeting(m);
  const { joined, notJoined } = splitJoinedVsNotJoinedInvitees(m.participants || [], attendanceCtx);
  const joinedPeople = joined.filter((p) => !participantIsBot(p));
  const notJoinedPeople = notJoined.filter((p) => !participantIsBot(p));
  const aj = meetingAutoJoinOptInUsers(m);
  const joinUrl = joinUrlFromMeeting(m);
  const calUrl = String(m?.htmlLink || m?.html_link || "").trim();
  const dateLabel = m.date
    ? formatAdminMeetingDateLabel(m.date) || formatYmdDisplay(m.date)
    : "—";
  const timeLabel = `${formatMeetingTimeForDisplay(m.time) || "—"}${
    m.end && String(m.end).trim() && m.end !== "—"
      ? ` – ${formatMeetingTimeForDisplay(m.end)}`
      : ""
  }`;

  const initialsFor = (name, email) => {
    const n = String(name || "").trim();
    if (n) {
      const parts = n.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
      return n.slice(0, 2).toUpperCase();
    }
    const em = String(email || "").split("@")[0] || "?";
    return em.slice(0, 2).toUpperCase();
  };

  const personCard = (p, i, tone) => {
    const email = String(p?.email || "").trim();
    const displayName = personChipDisplayName(p);
    const name = displayName !== "—" ? displayName : "";
    const bg = tone === "joined" ? "#ECFDF5" : "#F8FAFC";
    const accent = tone === "joined" ? "#15803D" : "#64748b";
    return (
      <div
        key={`${email || name || i}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "8px 9px",
          borderRadius: 9,
          background: bg,
          border: `1px solid ${tone === "joined" ? "#D1FAE5" : "#E2E8F0"}`,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 800,
            color: "#fff",
            background: tone === "joined"
              ? "linear-gradient(145deg,#16A34A,#0D9488)"
              : "linear-gradient(145deg,#64748B,#94A3B8)",
          }}
          aria-hidden
        >
          {initialsFor(name, email)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: T.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={displayName}
          >
            {displayName}
          </div>
          {email ? (
            <div
              style={{
                fontSize: 11,
                color: accent,
                marginTop: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={email}
            >
              {email}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const attendancePanel = (title, people, tone) => (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 140,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "9px 11px",
          borderBottom: `1px solid ${T.border}`,
          background: tone === "joined" ? "#F0FDF4" : "#F8FAFC",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: tone === "joined" ? "#15803D" : "#475569",
          }}
        >
          {title}
        </span>
        <span
          style={{
            minWidth: 22,
            height: 22,
            padding: "0 7px",
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 800,
            background: tone === "joined" ? "#DCFCE7" : "#E2E8F0",
            color: tone === "joined" ? "#166534" : "#334155",
          }}
        >
          {people.length}
        </span>
      </div>
      <div
        className="adm-cal-people-scroll"
        style={{
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          maxHeight: 220,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          flex: 1,
        }}
      >
        {people.length ? (
          people.map((p, i) => personCard(p, i, tone))
        ) : (
          <div
            style={{
              fontSize: 12.5,
              color: T.textMuted,
              textAlign: "center",
              padding: "22px 8px",
              lineHeight: 1.4,
            }}
          >
            No one listed
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div
      role="presentation"
      className="bot-admin-cal-popup"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10060,
        background: "rgba(15, 23, 42, 0.48)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="adm-cal-meeting-title"
        style={{
          width: "100%",
          maxWidth: 580,
          maxHeight: "min(90vh, 760px)",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          boxShadow: "0 24px 60px rgba(12,43,78,0.24)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "16px 18px 14px",
            borderBottom: `1px solid ${T.border}`,
            background: "linear-gradient(180deg,#F8FBFF 0%,#FFFFFF 100%)",
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              id="adm-cal-meeting-title"
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.text,
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
              }}
            >
              {m.title || "Meeting"}
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: T.textMuted,
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                alignItems: "center",
              }}
            >
              <span>{dateLabel}</span>
              <span style={{ color: T.border }}>·</span>
              <span>{timeLabel}</span>
              <span style={{ color: T.border }}>·</span>
              <span style={{ color: pl.color, fontWeight: 600 }}>{pl.label}</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 9px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: regMeta.bg,
                  color: regMeta.fg,
                  border: `1px solid ${regMeta.bg}`,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: regMeta.dot }} />
                {regMeta.label}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 9px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: botJoined ? "#F0FDF4" : "#FFFBEB",
                  color: meetAiColor,
                  border: `1px solid ${botJoined ? "#BBF7D0" : "#FDE68A"}`,
                }}
              >
                {meetAiLabel}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              title={calUrl ? "Open in Google Calendar" : "Open meeting link"}
              aria-label="View calendar details"
              onClick={() => onOpenCalendar?.(m)}
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                border: `1px solid ${T.border}`,
                background: "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Svg d={IC.eye} color={T.accent} size={16} stroke={2} />
            </button>
            <button
              type="button"
              title={joinUrl ? "Join meeting" : "No join link"}
              aria-label="Join meeting"
              disabled={!joinUrl}
              onClick={() => onOpenJoin?.(m)}
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                border: `1px solid ${joinUrl ? "#BBCFEE" : T.border}`,
                background: joinUrl ? "#E3EEFF" : "#F8FAFC",
                cursor: joinUrl ? "pointer" : "not-allowed",
                opacity: joinUrl ? 1 : 0.55,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Svg d={IC.video} color={joinUrl ? "#1565C0" : T.textMuted} size={16} stroke={2} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                border: `1px solid ${T.border}`,
                background: "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Svg d={IC.close} color={T.textMuted} size={16} />
            </button>
          </div>
        </div>

        <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 18px 18px", minHeight: 0 }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6 }}>
              Whose meeting
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "11px 12px",
                borderRadius: 12,
                border: `1px solid ${T.border}`,
                background: "#F8FAFC",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#fff",
                  background: "linear-gradient(145deg,#2563EB,#0D9488)",
                }}
                aria-hidden
              >
                {initialsFor(hostName || ownerName, hostEmail || ownerEmail)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>
                  {hostName || ownerName || "Host not listed"}
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                  {hostEmail || ownerEmail || "No host email on this session"}
                </div>
                {ownerEmail && hostEmail && normAppEmail(ownerEmail) !== normAppEmail(hostEmail) ? (
                  <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 4 }}>
                    Owner: {ownerName || ownerEmail}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6 }}>
              MeetAI
            </div>
            <div
              style={{
                fontSize: 13,
                color: T.text,
                lineHeight: 1.45,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${botJoined ? "#BBF7D0" : "#FDE68A"}`,
                background: botJoined ? "#F0FDF4" : "#FFFBEB",
              }}
            >
              <strong style={{ color: meetAiColor }}>{meetAiLabel}</strong>
              {getMeetingBotJoinReasonShort(m) ? (
                <span style={{ color: T.textMuted }}> · {getMeetingBotJoinReasonShort(m)}</span>
              ) : null}
              <MeetAiHostDetailBlock
                meeting={m}
                nowMs={nowMs}
                tone={botJoined ? "ok" : "warn"}
              />
            </div>
          </div>

          <div
            className="adm-cal-attendance-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: aj.length > 0 ? 14 : 0,
            }}
          >
            {attendancePanel("Joined", joinedPeople, "joined")}
            {attendancePanel("Not joined", notJoinedPeople, "missed")}
          </div>

          {aj.length > 0 ? (
            <div
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "9px 11px",
                  borderBottom: `1px solid ${T.border}`,
                  background: "#EFF6FF",
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#1D4ED8" }}>
                  Auto Join opt-in
                </span>
                <span
                  style={{
                    minWidth: 22,
                    height: 22,
                    padding: "0 7px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 800,
                    background: "#DBEAFE",
                    color: "#1E40AF",
                  }}
                >
                  {aj.length}
                </span>
              </div>
              <div
                className="adm-cal-people-scroll"
                style={{
                  padding: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  maxHeight: 140,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                }}
              >
                {aj.map((u, i) =>
                  personCard(
                    { name: u?.name, email: u?.email },
                    i,
                    "missed"
                  )
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function CalDayMeetingsPopup({
  open,
  dateYmd,
  meetings,
  onClose,
  onOpen,
  nowMs,
  onToggleMeetingAutoJoin,
  meetingAutoJoinSaving,
  fmtDate,
  title,
  embedInAdmin = false,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !dateYmd) return null;

  const sorted = sortMeetingsChronologically(meetings);
  const heading = title || (fmtDate ? fmtDate(dateYmd) : dateYmd);

  return createPortal(
    <div
      role="presentation"
      className={embedInAdmin ? "bot-admin-cal-popup" : undefined}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10050,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cal-day-popup-title"
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "min(88vh, 680px)",
          background: T.bg,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(12,43,78,0.22)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "16px 18px 12px",
            borderBottom: `1px solid ${T.border}`,
            background: T.surface,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div id="cal-day-popup-title" style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: "-0.02em" }}>
              {heading}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
              {sorted.length === 0
                ? "No meetings on this day"
                : `${sorted.length} meeting${sorted.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: T.bg,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Svg d={IC.close} color={T.textMuted} size={16} />
          </button>
        </div>
        <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 16px 16px" }}>
          {sorted.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "28px 12px",
                color: T.textMuted,
                fontSize: 13,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Svg d={IC.cal} color={T.textMuted} size={28} stroke={1.5} />
              <span>No meetings scheduled for this day</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sorted.map((m) => (
                <CalSidebarMeetingCard
                  key={String(m.id ?? m._id ?? `${m.title}-${m.time}`)}
                  m={m}
                  inDayPopup
                  showBotStatus={embedInAdmin}
                  onOpen={(meeting) => {
                    onClose?.();
                    onOpen?.(meeting, { source: "calendar" });
                  }}
                  nowMs={nowMs}
                  onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
                  meetingAutoJoinSaving={meetingAutoJoinSaving}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function fmtGmailShortDate(ymd) {
  const dt = ymdToDate(ymd);
  if (!dt) return "";
  const yr = new Date().getFullYear();
  return dt.toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
    ...(dt.getFullYear() !== yr ? { year: "numeric" } : {}),
    timeZone: APP_DISPLAY_TZ,
  });
}

function fmtGmailSideDate(ymd, today) {
  const dt = ymdToDate(ymd);
  if (!dt) return ymd || "";
  return dt.toLocaleDateString("en-IN", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: APP_DISPLAY_TZ,
  });
}

function fmtGmailDaySection(ymd, today) {
  const dt = ymdToDate(ymd);
  if (!dt) return ymd || "";
  const isT = ymd === today;
  const isTomorrow = ymd === addDaysYmd(today, 1);
  const isYesterday = ymd === addDaysYmd(today, -1);
  const base = dt.toLocaleDateString("en-IN", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: APP_DISPLAY_TZ,
  });
  if (isT) return `Today · ${base}`;
  if (isTomorrow) return `Tomorrow · ${base}`;
  if (isYesterday) return `Yesterday · ${base}`;
  return base;
}

function fmtGmailEventTime(m) {
  const start = formatMeetingTimeForDisplay(m?.time);
  const end = formatMeetingTimeForDisplay(m?.end);
  if (start && end) return `${start} – ${end}`;
  return start || end || "All day";
}

function sortMeetingsInboxOrder(list) {
  const now = Date.now();
  return [...(list || [])].sort((a, b) => {
    const ma = meetingDateTimeMs(a);
    const mb = meetingDateTimeMs(b);
    const aFuture = ma >= now;
    const bFuture = mb >= now;
    if (aFuture && bFuture) return ma - mb;
    if (!aFuture && !bFuture) return mb - ma;
    return aFuture ? -1 : 1;
  });
}

/** Gmail-style meeting rows — center panel on Calendar (Day view). */
function GmailMeetingInboxList({ meetings, onOpen, selDate, setSelDate, nowMs, selectedMeetingId }) {
  const inboxMeetings = useMemo(() => sortMeetingsInboxOrder(meetings), [meetings]);
  const rowRefs = useRef({});

  useEffect(() => {
    if (!selDate) return;
    const first = inboxMeetings.find((m) => String(m?.date || "") === String(selDate));
    if (!first) return;
    const key = String(first.id ?? first._id ?? "");
    const el = rowRefs.current[key];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selDate, inboxMeetings]);

  return (
    <div className="cal-gmail-inbox-scroll panel-scroll" role="list" aria-label="Meetings">
      {inboxMeetings.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: T.textMuted, fontSize: 13 }}>
          No meetings to show
        </div>
      ) : (
        inboxMeetings.map((m, idx) => {
          const mid = String(m.id ?? m._id ?? idx);
          const effSt = effectiveMeetingStatus(m, nowMs);
          const cancelled = effSt === "cancelled";
          const host = meetingHasDisplayableHost(m) ? meetingHostName(m) : "Avaada MeetAI";
          const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
          const isSelDay = selDate && String(m.date || "") === String(selDate);
          const isSelRow = selectedMeetingId && mid === String(selectedMeetingId);
          const snippet = `${fmtGmailEventTime(m)} · ${pl.short}${cancelled ? " · Cancelled" : ""}`;
          return (
            <div
              key={mid}
              ref={(el) => {
                if (el) rowRefs.current[mid] = el;
                else delete rowRefs.current[mid];
              }}
              role="listitem"
              className={`cal-gmail-row${isSelRow ? " is-selected" : ""}${isSelDay && !isSelRow ? " is-sel-day" : ""}`}
              onClick={() => {
                if (m.date) setSelDate?.(m.date);
                onOpen?.(m, { source: "calendar" });
              }}
            >
              <span style={{ display: "flex", justifyContent: "center", color: "#9aa0a6" }} aria-hidden>
                <Svg d={IC.cal} size={16} color="#9aa0a6" stroke={2} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: cancelled ? T.textMuted : "#202124",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textDecoration: cancelled ? "line-through" : "none",
                  }}
                >
                  {host}
                </div>
              </div>
              <div className="cal-gmail-row-snippet" style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: cancelled ? T.textMuted : "#202124",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textDecoration: cancelled ? "line-through" : "none",
                  }}
                >
                  {m.title || "Meeting"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: T.textMuted,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginTop: 2,
                  }}
                >
                  {snippet}
                </div>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtGmailShortDate(m.date)}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/** Gmail-style right rail — Calendar + AI icons, collapsible drawer. */
function GmailRightRail({
  drawerOpen,
  onDrawerOpenChange,
  activePanel,
  onActivePanelChange,
  calendarProps,
  assistantProps,
  onInteract,
}) {
  const MIN_DRAWER_WIDTH = 280;
  const MAX_DRAWER_WIDTH = 520;
  const DEFAULT_DRAWER_WIDTH = 380;
  /** Older thin defaults — bump to the new static width on load. */
  const LEGACY_DRAWER_WIDTHS = [190, 272, 300, 320];
  const [drawerWidth, setDrawerWidth] = useState(() => {
    try {
      const stored = Number(window.localStorage?.getItem?.("meetai.gmail.rightRail.width"));
      if (Number.isFinite(stored)) {
        if (LEGACY_DRAWER_WIDTHS.some((w) => Math.abs(stored - w) < 1) || stored < DEFAULT_DRAWER_WIDTH) {
          return DEFAULT_DRAWER_WIDTH;
        }
        return Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, stored));
      }
    } catch (_) {}
    return DEFAULT_DRAWER_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeCleanupRef = useRef(null);

  useEffect(() => {
    try {
      window.localStorage?.setItem?.("meetai.gmail.rightRail.width", String(drawerWidth));
    } catch (_) {}
  }, [drawerWidth]);

  useEffect(() => () => resizeCleanupRef.current?.(), []);

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = drawerWidth;
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (moveEvent) => {
      const next = startWidth + (startX - moveEvent.clientX);
      setDrawerWidth(Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, next)));
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", cleanup);
      window.removeEventListener("pointercancel", cleanup);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizing(false);
      resizeCleanupRef.current = null;
    };
    resizeCleanupRef.current?.();
    resizeCleanupRef.current = cleanup;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", cleanup);
    window.addEventListener("pointercancel", cleanup);
  };

  const resizeWithKeyboard = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const change = event.key === "ArrowLeft" ? 16 : -16;
    setDrawerWidth((width) =>
      Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, width + change))
    );
  };

  const togglePanel = (panel) => {
    onInteract?.();
    if (activePanel === panel && drawerOpen) {
      onDrawerOpenChange?.(false);
      return;
    }
    onActivePanelChange?.(panel);
    onDrawerOpenChange?.(true);
  };
  const handleCollapse = () => {
    onInteract?.();
    onDrawerOpenChange?.(!drawerOpen);
  };
  return (
    <div
      className="gmail-right-stack"
      style={{ "--gmail-drawer-width": `${drawerWidth}px` }}
    >
      {drawerOpen ? (
        <aside
          className="gmail-right-drawer"
          aria-label={activePanel === "ai" ? BOT_DISPLAY_NAME : "Calendar"}
        >
          <div
            className={`gmail-right-resize-handle${isResizing ? " is-resizing" : ""}`}
            role="separator"
            aria-label="Resize side panel"
            aria-orientation="vertical"
            aria-valuemin={MIN_DRAWER_WIDTH}
            aria-valuemax={MAX_DRAWER_WIDTH}
            aria-valuenow={Math.round(drawerWidth)}
            tabIndex={0}
            title="Drag to resize · Double-click to reset"
            onPointerDown={startResize}
            onKeyDown={resizeWithKeyboard}
            onDoubleClick={() => setDrawerWidth(DEFAULT_DRAWER_WIDTH)}
          />
          {activePanel === "calendar" ? (
            <GmailCalendarSidePanel
              {...calendarProps}
              panelActive={drawerOpen && activePanel === "calendar"}
              onClose={() => onDrawerOpenChange?.(false)}
            />
          ) : null}
          {activePanel === "ai" ? (
            <WorkspaceAssistantFab
              {...assistantProps}
              variant="drawer"
              open
              onOpenChange={(v) => {
                if (!v) onDrawerOpenChange?.(false);
              }}
            />
          ) : null}
        </aside>
      ) : null}
      <nav className="gmail-right-rail" aria-label="Side panels">
        <button
          type="button"
          className={`gmail-rail-btn${activePanel === "calendar" && drawerOpen ? " active" : ""}`}
          aria-label="Calendar"
          title="Calendar"
          onClick={() => togglePanel("calendar")}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="gmail-rail-cal-glyph" aria-hidden>
            <img src={GOOGLE_CALENDAR_ICON} alt="" width={24} height={24} />
          </span>
        </button>
        <button
          type="button"
          className={`gmail-rail-btn${activePanel === "ai" && drawerOpen ? " active" : ""}`}
          aria-label={BOT_DISPLAY_NAME}
          title={BOT_DISPLAY_NAME}
          data-tour="workspace-chat-fab"
          onClick={() => togglePanel("ai")}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="gmail-rail-ai-icon" aria-hidden>
            <video src={ROBO_ASSISTANT_VIDEO} autoPlay loop muted playsInline />
          </span>
        </button>
        <div className="gmail-rail-spacer" />
        <button
          type="button"
          className="gmail-rail-collapse"
          aria-label={drawerOpen ? "Collapse side panel" : "Expand side panel"}
          title={drawerOpen ? "Collapse" : "Expand"}
          onClick={handleCollapse}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Svg d={drawerOpen ? IC.chevR : IC.chevL} size={18} color="currentColor" stroke={2} />
        </button>
      </nav>
    </div>
  );
}

function calSideMeetingVisible(m) {
  const title = String(m?.title || "").trim();
  if (title) return true;
  if (m?.time || m?.end || m?.start) return true;
  return false;
}

/** Gmail-style scrollable calendar panel — right side on Calendar (Day view). */
function GmailCalendarSidePanel({ meetings, today, selDate, setSelDate, onOpen, onCreateEvent, onClose, goToday, activeMeetingId, panelActive = true }) {
  const scrollRef = useRef(null);
  const dayElRefs = useRef({});
  const scrollLockRef = useRef(false);
  const scrollLockTimer = useRef(null);
  const anchorYmdRef = useRef(today);
  const panelSessionRef = useRef(0);
  const userBrowsingRef = useRef(false);
  const ioScrollPickRef = useRef(null);
  const ioScrollRafRef = useRef(null);
  const nowMs = Date.now();

  const days = useMemo(() => {
    const start = addDaysYmd(today, -14);
    const end = addDaysYmd(today, 45);
    const list = [];
    let cur = start;
    let guard = 0;
    while (cur <= end && guard < 120) {
      list.push(cur);
      cur = addDaysYmd(cur, 1);
      guard += 1;
    }
    return list;
  }, [today]);

  const mByDate = useMemo(() => indexMeetingsByDate(meetings), [meetings]);
  const anchorYmd = selDate || today;
  anchorYmdRef.current = anchorYmd;

  const scrollToDay = useCallback((targetYmd, smooth = true) => {
    const el = dayElRefs.current[targetYmd];
    const root = scrollRef.current;
    if (!el || !root) return false;
    scrollLockRef.current = true;
    if (scrollLockTimer.current) clearTimeout(scrollLockTimer.current);
    const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    root.scrollTo({ top: Math.max(0, top - 8), behavior: smooth ? "smooth" : "auto" });
    scrollLockTimer.current = setTimeout(() => {
      scrollLockRef.current = false;
    }, smooth ? 480 : 80);
    return true;
  }, []);

  const jumpToTodayInstant = useCallback(() => {
    const el = dayElRefs.current[today];
    const root = scrollRef.current;
    if (!el || !root) return false;
    scrollLockRef.current = true;
    const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    root.scrollTop = Math.max(0, top - 8);
    scrollLockTimer.current = setTimeout(() => {
      scrollLockRef.current = false;
    }, 80);
    setSelDate?.(today);
    return true;
  }, [today, setSelDate]);

  useEffect(() => {
    if (!panelActive) {
      userBrowsingRef.current = false;
      panelSessionRef.current += 1;
    }
  }, [panelActive]);

  useLayoutEffect(() => {
    if (!panelActive || userBrowsingRef.current) return undefined;
    const session = panelSessionRef.current;
    let cancelled = false;
    let attempts = 0;
    const run = () => {
      if (cancelled || panelSessionRef.current !== session || userBrowsingRef.current) return;
      if (!jumpToTodayInstant() && attempts < 12) {
        attempts += 1;
        requestAnimationFrame(run);
      }
    };
    run();
    const retry = setTimeout(run, 120);
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [panelActive, today, jumpToTodayInstant]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !panelActive) return undefined;
    const onScroll = () => {
      if (!scrollLockRef.current) userBrowsingRef.current = true;
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, [panelActive]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !panelActive) return undefined;
    const headers = root.querySelectorAll("[data-gmail-cal-day]");
    if (!headers.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (scrollLockRef.current) return;
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = vis[0];
        if (!top) return;
        const nextYmd = top.target.getAttribute("data-gmail-cal-day");
        if (!nextYmd || nextYmd === anchorYmdRef.current) return;
        ioScrollPickRef.current = nextYmd;
        if (ioScrollRafRef.current != null) return;
        ioScrollRafRef.current = requestAnimationFrame(() => {
          ioScrollRafRef.current = null;
          const ymd = ioScrollPickRef.current;
          if (!ymd || ymd === anchorYmdRef.current) return;
          startTransition(() => setSelDate?.(ymd));
        });
      },
      { root, rootMargin: "-4px 0px -65% 0px", threshold: [0, 0.25] }
    );
    headers.forEach((h) => io.observe(h));
    return () => {
      io.disconnect();
      if (ioScrollRafRef.current != null) {
        cancelAnimationFrame(ioScrollRafRef.current);
        ioScrollRafRef.current = null;
      }
    };
  }, [days, panelActive, setSelDate]);

  const handleGoToday = useCallback(() => {
    userBrowsingRef.current = false;
    goToday?.();
    requestAnimationFrame(() => {
      scrollToDay(today, true);
    });
  }, [goToday, scrollToDay, today]);

  const showNowLine = (dayYmd, dayList) => {
    if (dayYmd !== today || !dayList.length) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const firstStart = dayList[0]?.time;
    const lastEnd = dayList[dayList.length - 1]?.end || dayList[dayList.length - 1]?.time;
    const parseMin = (t) => {
      if (!t || typeof t !== "string") return null;
      const [h, m] = t.split(":").map(Number);
      return Number.isFinite(h) ? h * 60 + (m || 0) : null;
    };
    const startMin = parseMin(firstStart);
    const endMin = parseMin(lastEnd);
    if (startMin == null || endMin == null) return true;
    return nowMin >= startMin - 30 && nowMin <= endMin + 60;
  };

  return (
    <div className="cal-gmail-side">
      <div className="cal-gmail-side-head">
        <div className="cal-gmail-side-head-top">
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: "0.1em" }}>
            CALENDAR
          </div>
          {onClose ? (
            <button
              type="button"
              className="cal-gmail-side-close"
              onClick={onClose}
              aria-label="Close calendar panel"
              title="Close"
            >
              <Svg d={IC.close} size={16} color="#5f6368" stroke={2} />
            </button>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{fmtGmailSideDate(anchorYmd, today)}</div>
          <button
            type="button"
            className="cal-tb-btn"
            onClick={handleGoToday}
            style={{ fontSize: 12, padding: "5px 12px" }}
          >
            Today
          </button>
        </div>
        {onCreateEvent ? (
          <button type="button" className="cal-gmail-create-evt" onClick={onCreateEvent}>
            <span className="cal-gmail-create-evt-icon" aria-hidden>
              <Svg d={IC.plus} size={14} color="currentColor" stroke={2.25} />
            </span>
            Create an event
          </button>
        ) : null}
      </div>
      <div ref={scrollRef} className="cal-gmail-side-scroll panel-scroll" role="region" aria-label="Calendar agenda">
        {days.map((d) => {
          const dayList = (mByDate[d] || [])
            .filter(calSideMeetingVisible)
            .slice()
            .sort((a, b) => meetingDateTimeMs(a) - meetingDateTimeMs(b));
          const isToday = d === today;
          return (
            <section
              key={d}
              ref={(el) => {
                if (el) dayElRefs.current[d] = el;
                else delete dayElRefs.current[d];
              }}
              data-gmail-cal-day={d}
            >
              <div className="cal-gmail-day-label" style={{ color: isToday ? T.accent : "#3c4043" }}>
                {fmtGmailDaySection(d, today)}
              </div>
              {dayList.length === 0 ? (
                <div style={{ fontSize: 12, color: T.textMuted, padding: "4px 4px 12px" }}>No events</div>
              ) : (
                dayList.map((m, mi) => {
                  const mid = String(m.id ?? m._id ?? `${d}-${mi}`);
                  const isActive = activeMeetingId && mid === String(activeMeetingId);
                  const isLive = effectiveMeetingStatus(m, nowMs) === "live";
                  const showNow = isToday && showNowLine(d, dayList) && mi === 0;
                  const evtTitle = String(m.title || "").trim() || "Meeting";
                  const evtTime = fmtGmailEventTime(m);
                  return (
                    <div key={mid}>
                      {showNow ? <div className="cal-gmail-now-line" aria-hidden /> : null}
                      <div
                        role="button"
                        tabIndex={0}
                        className={`cal-gmail-evt${isActive ? " is-active" : ""}${isLive ? " is-live" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelDate?.(d);
                          onOpen?.(m, { source: "calendar" });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelDate?.(d);
                            onOpen?.(m, { source: "calendar" });
                          }
                        }}
                      >
                        <div className="cal-gmail-evt-title">{evtTitle}</div>
                        <div className="cal-gmail-evt-time">{evtTime}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function CalPage({
  meetings,
  myActions,
  onOpen,
  year,
  month,
  setYear,
  setMonth,
  calendarHint,
  calendarWarning,
  view,
  setView,
  selDate,
  setSelDate,
  onToggleMeetingAutoJoin,
  meetingAutoJoinSaving,
  onToggleMeetingRecording,
  meetingRecordingSaving,
  onCreateEvent,
  adminCalendar = false,
}) {
  const today=todayStr();

  const [dayPopupDate, setDayPopupDate] = useState(null);
  const [dayPopupMeetingsOverride, setDayPopupMeetingsOverride] = useState(null);
  const [dayPopupTitle, setDayPopupTitle] = useState(null);
  const calBodyRef = useRef(null);
  const calSideRef = useRef(null);
  const showWeekends = true;
  const showDeclinedEvents = true;
  const showCompletedTasks = true;

  useLayoutEffect(() => {
    if (adminCalendar) return undefined;
    const side = calSideRef.current;
    const body = calBodyRef.current;
    if (!side || !body) return undefined;
    const sync = () => {
      const h = Math.round(side.getBoundingClientRect().height);
      if (h > 0) body.style.setProperty("--cal-side-height", `${h}px`);
    };
    sync();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    if (ro) ro.observe(side);
    window.addEventListener("resize", sync);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [adminCalendar, year, month, view]);

  const anchorYmd = selDate || today;

  function syncYMFromYmd(ymd){
    const dt=ymdToDate(ymd);
    if(!dt) return;
    setYear(dt.getFullYear());
    setMonth(dt.getMonth());
  }
  function shiftDays(deltaDays){
    const next = addDaysYmd(anchorYmd, deltaDays);
    setSelDate(next);
    syncYMFromYmd(next);
  }
  function shiftMonths(deltaMonths){
    const dt = ymdToDate(anchorYmd);
    if(!dt) return;
    dt.setMonth(dt.getMonth() + deltaMonths);
    const next = dateToYmd(dt);
    setSelDate(next);
    setYear(dt.getFullYear());
    setMonth(dt.getMonth());
  }
  function shiftYears(deltaYears){
    const dt = ymdToDate(anchorYmd);
    if(!dt){ setYear(y=>y+deltaYears); return; }
    dt.setFullYear(dt.getFullYear() + deltaYears);
    const next = dateToYmd(dt);
    setSelDate(next);
    setYear(dt.getFullYear());
    setMonth(dt.getMonth());
  }
  const prevNav=()=>{
    if(view==="month") return shiftMonths(-1);
    if(view==="year") return shiftYears(-1);
    if(view==="day") return shiftDays(-1);
    if(view==="week") return shiftDays(-7);
    if(view==="schedule") return shiftDays(-31);
    if(view==="fourDays") return shiftDays(-4);
    return shiftMonths(-1);
  };
  const nextNav=()=>{
    if(view==="month") return shiftMonths(1);
    if(view==="year") return shiftYears(1);
    if(view==="day") return shiftDays(1);
    if(view==="week") return shiftDays(7);
    if(view==="schedule") return shiftDays(31);
    if(view==="fourDays") return shiftDays(4);
    return shiftMonths(1);
  };
  const calNavStepLabel = (dir) => {
    const prev = dir === "prev";
    if (view === "month") return prev ? "Previous month" : "Next month";
    if (view === "year") return prev ? "Previous year" : "Next year";
    if (view === "day") return prev ? "Previous day" : "Next day";
    if (view === "week") return prev ? "Previous week" : "Next week";
    if (view === "schedule") return prev ? "Previous month" : "Next month";
    if (view === "fourDays") return prev ? "Previous 4 days" : "Next 4 days";
    return prev ? "Previous month" : "Next month";
  };
  const prevM=()=>{ if(month===0){ setMonth(11); setYear(y=>y-1);} else setMonth(m=>m-1); };
  const nextM=()=>{ if(month===11){ setMonth(0); setYear(y=>y+1);} else setMonth(m=>m+1); };
  const goToday=()=>{ const n=new Date(); const t=todayStr(); setYear(n.getFullYear()); setMonth(n.getMonth()); setSelDate(t); };

  const DAYS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const MINI_DOW=["S","M","T","W","T","F","S"];

  const nowMs = Date.now();
  const isDeclinedMeeting = (m) => {
    const statusKey = String(m?.status || "").toLowerCase();
    if (statusKey === "declined") return true;
    const rs = String(m?.responseStatus || m?.response_status || "").toLowerCase();
    if (rs === "declined") return true;
    return false;
  };
  const isWeekendYmd = (ymd) => {
    const dt = ymdToDate(ymd);
    if (!dt) return false;
    const dow = dt.getDay();
    return dow === 0 || dow === 6;
  };
  const filteredMeetings = (meetings||[]).filter((m)=>{
    const statusKey = String(m?.status||"").toLowerCase();
    if(!showDeclinedEvents && isDeclinedMeeting(m)) return false;
    const eff = effectiveMeetingStatus(m, nowMs);
    const isCompleted = eff==="completed" || statusKey==="completed" || statusKey==="ended";
    if(!showCompletedTasks && isCompleted) return false;
    if(!showWeekends && m?.date && isWeekendYmd(String(m.date))) return false;
    return true;
  });
  const mByDate = indexMeetingsByDate(filteredMeetings);
  const cells = buildMonthCalendarCells(year, month);
  const monthEventCount = filteredMeetings.filter((m) => meetingOnCalendarMonth(m, year, month)).length;
  const next7End = addDaysYmd(today, 6);
  const actionDueYmd = (a) => {
    const raw = String(a?.due || a?.dueDate || a?.due_date || a?.meetingDate || "").trim();
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return "";
    return dateToYmd(dt);
  };
  const priorityActionCount = (myActions || []).filter((a) => {
    if (!a || a.status !== "pending") return false;
    const pr = String(a?.priority || "").toLowerCase();
    if (pr !== "high" && pr !== "urgent" && pr !== "critical") return false;
    const d = actionDueYmd(a);
    if (!d) return true;
    return d >= today && d <= next7End;
  }).length;
  const eventsTodayCount = (mByDate[today] || []).length;
  const hoursTodayMinutes = (mByDate[today] || []).reduce(
    (sum, m) => sum + (Number(meetingDurationMinutes(m)) || 0),
    0
  );
  const hoursTodayCount = Math.max(0, Math.round((hoursTodayMinutes / 60) * 10) / 10);
  const hoursTodayDisplay = Number.isInteger(hoursTodayCount) ? String(hoursTodayCount) : hoursTodayCount.toFixed(1);
  const hoursTodayParts = `${Math.floor(hoursTodayMinutes / 60)} hr ${hoursTodayMinutes % 60} min`;
  const eventsNext7Count = filteredMeetings.filter((m) => {
    const d = String(m?.date || "");
    return d >= today && d <= next7End;
  }).length;

  const timelineDays = (() => {
    const allowDay = (ymd) => showWeekends || !isWeekendYmd(ymd);
    if (view === "month") {
      const total = daysInMonth(year, month);
      const prefix = `${year}-${pad(month + 1)}-`;
      return Array.from({ length: total }, (_, i) => `${prefix}${pad(i + 1)}`).filter(allowDay);
    }
    if (view === "week") {
      const start = startOfWeekYmd(anchorYmd);
      return Array.from({ length: 7 }, (_, i) => addDaysYmd(start, i)).filter(allowDay);
    }
    if (!allowDay(anchorYmd)) return [];
    return [anchorYmd];
  })();
  const weekNum = isoWeekNumberFromYmd(anchorYmd);
  const timelineShownCount = timelineDays.reduce((n, d) => n + ((mByDate[d] || []).length), 0);
  function fmtDate(ds){
    if(!ds) return "";
    const [y,m,d]=ds.split("-").map(Number);
    return new Date(y,m-1,d).toLocaleDateString("en-IN",{ weekday:"long", day:"numeric", month:"long", timeZone: APP_DISPLAY_TZ });
  }
  function fmtTimelineDayLabel(ds){
    if (ds === today) return "Today";
    const dt = ymdToDate(ds);
    if (!dt) return ds;
    return dt.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", timeZone: APP_DISPLAY_TZ });
  }
  function openDayPopup(ds) {
    if (!ds) return;
    setSelDate(ds);
    setDayPopupDate(ds);
    setDayPopupMeetingsOverride(null);
    setDayPopupTitle(null);
  }
  function openWeekAllMeetingsPopup() {
    const s = startOfWeekYmd(anchorYmd);
    const e = endOfWeekYmd(anchorYmd);
    setDayPopupDate(s);
    setDayPopupMeetingsOverride(filteredMeetings.slice());
    setDayPopupTitle(
      fmtDate && s && e ? `${fmtDate(s)} — ${fmtDate(e)}` : "All meetings this week"
    );
  }
  function closeDayPopup() {
    setDayPopupDate(null);
    setDayPopupMeetingsOverride(null);
    setDayPopupTitle(null);
  }
  function selectMiniDay(ds, isCurMonth) { if (!ds) return; setSelDate(ds); if (!isCurMonth) syncYMFromYmd(ds); }

  const filterBtnStyle = (active) => ({
    border: `1px solid ${active ? T.accent : T.border}`,
    background: active ? "rgba(21,101,192,0.1)" : T.bg,
    color: active ? T.accent : T.textMuted,
    borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", transition: "all .14s ease",
  });

  const dashHeaderTitle =
    view === "month"
      ? `${MONTHS[month]} ${year}`
      : view === "week"
      ? (() => {
          const s = startOfWeekYmd(anchorYmd);
          const e = endOfWeekYmd(anchorYmd);
          return `${fmtDate(s).replace(/,.*/, "")} – ${fmtDate(e)}`;
        })()
      : fmtDate(anchorYmd);
  const dashWeekRangeStart = startOfWeekYmd(anchorYmd);
  const dashWeekRangeEnd = endOfWeekYmd(anchorYmd);
  const dashWeekTooltip = `Week ${weekNum} · ${fmtDate(dashWeekRangeStart)} – ${fmtDate(dashWeekRangeEnd)}`;
  const dashWeekBadge =
    view === "month"
      ? ""
      : view === "week"
      ? `Week ${weekNum}`
      : (ymdToDate(anchorYmd)?.toLocaleDateString("en-IN", { weekday: "long", timeZone: APP_DISPLAY_TZ }) || "");

  return (
    <div className={`page app-page-shell${!adminCalendar ? " cal-dash-page avaada-page-calendar" : ""}`}>
      <div className={`content-well${!adminCalendar ? " cal-dash-well" : ""}`}>
      {(calendarHint || calendarWarning) && (
        <div style={{marginBottom:14,display:"flex",flexDirection:"column",gap:10}}>
          {calendarHint && (
            <div style={{padding:"11px 14px",background:T.accentBg,border:`1px solid ${T.accentMid}`,borderRadius:10,fontSize:12.5,color:"#1565C0",lineHeight:1.55}}>{calendarHint}</div>
          )}
          {calendarWarning && (
            <div style={{padding:"11px 14px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,fontSize:12.5,color:"#B91C1C",lineHeight:1.55}}>{calendarWarning}</div>
          )}
        </div>
      )}

      {!adminCalendar ? (
        <div className="cal-dash">
          <div className="cal-dash-head">
            <img
              className="cal-dash-head-illustration"
              src={CALENDAR_HERO_ILLUSTRATION}
              alt=""
              aria-hidden="true"
              draggable="false"
            />
            <div className="cal-dash-head-main">
              <p className="cal-dash-kicker">
                <span className="cal-dash-kicker-icon" aria-hidden>
                  <Svg d={IC.cal} size={14} color="#0875E1" stroke={2} />
                </span>
                Calendar
              </p>
              <div className="cal-dash-title-row">
                <h1 className="cal-dash-title">{dashHeaderTitle}</h1>
                {dashWeekBadge ? (
                  <span className="cal-dash-wk" title={view === "week" ? dashWeekTooltip : undefined}>{dashWeekBadge}</span>
                ) : null}
              </div>
              <div className="cal-dash-stats">
                <span className="cal-dash-stat cal-dash-stat--accent"><strong>{monthEventCount}</strong> events this month</span>
                {priorityActionCount > 0 ? (
                  <span className="cal-dash-stat cal-dash-stat--warn"><strong>{priorityActionCount}</strong> priority actions</span>
                ) : null}
                <span className="cal-dash-stat"><strong>{eventsTodayCount}</strong> today</span>
              </div>
            </div>
            <div className="cal-dash-toolbar">
              <div className="cal-dash-controls">
                <div className="cal-nav-pair">
                  <button type="button" className="cal-nav-icon cal-nav-split-l" onClick={prevNav} aria-label={calNavStepLabel("prev")} title={calNavStepLabel("prev")}><Svg d={IC.chevL} size={18} color="currentColor" stroke={2.25} /></button>
                  <button type="button" className="cal-tb-btn" onClick={goToday} title={`Jump to today (${fmtDate(today)})`}>Today</button>
                  <button type="button" className="cal-nav-icon cal-nav-split-r" onClick={nextNav} aria-label={calNavStepLabel("next")} title={calNavStepLabel("next")}><Svg d={IC.chevR} size={18} color="currentColor" stroke={2.25} /></button>
                </div>
                {onCreateEvent ? (
                  <button type="button" className="cal-tb-btn cal-tb-btn--create" onClick={onCreateEvent}>
                    <span className="ui-btn-ico" aria-hidden>
                      <Svg d={IC.plus} size={15} color="currentColor" stroke={2.25} />
                    </span>
                    <span className="cal-tb-create-full">Create Meeting</span>
                    <span className="cal-tb-create-short" aria-hidden>Create</span>
                  </button>
                ) : null}
              </div>
              <div className="seg-group cal-dash-view-switch" role="group" aria-label="Calendar view">
                {[{ id: "month", label: "Month" }, { id: "week", label: "Week" }, { id: "day", label: "Day" }].map((opt) => (
                  <button key={opt.id} type="button" className={`cal-view-seg${view === opt.id ? " active" : ""}`} onClick={() => setView(opt.id)} aria-pressed={view === opt.id}>{opt.label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="cal-dash-body" ref={calBodyRef}>
            <aside className="cal-dash-side" ref={calSideRef} aria-label="Calendar date picker">
              <div className="cal-dash-card cal-dash-card--mini">
                <div className="cal-dash-mini-head">
                  <button type="button" className="mini-cal-nav" onClick={prevM} aria-label="Previous month" title="Previous month"><Svg d={IC.chevL} size={18} color="currentColor" stroke={2.25} /></button>
                  <div className="cal-dash-mini-month">{MONTHS[month]} {year}</div>
                  <button type="button" className="mini-cal-nav" onClick={nextM} aria-label="Next month" title="Next month"><Svg d={IC.chevR} size={18} color="currentColor" stroke={2.25} /></button>
                </div>
                <div className="cal-dash-mini-grid" role="grid" aria-label="Select date">
                  {MINI_DOW.map((d, i) => (<div key={`${d}-${i}`} className="cal-dash-mini-dow" aria-hidden>{d}</div>))}
                  {cells.map((c, i) => {
                    const ds = ymdForMonthCell(c, year, month);
                    const dayMeets = ds ? (mByDate[ds] || []) : [];
                    const eventCount = dayMeets.length;
                    const isSel = ds === selDate;
                    const isToday = ds === today;
                    const isWeekend = i % 7 === 0 || i % 7 === 6;
                    const weekendBlocked = !showWeekends && isWeekend;
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`cal-dash-mini-day${c.cur ? "" : " is-out"}${isSel ? " is-sel" : ""}${isToday ? " is-today" : ""}${isWeekend ? " is-weekend" : ""}${weekendBlocked ? " is-out" : ""}`}
                        onClick={() => {
                          if (weekendBlocked) return;
                          selectMiniDay(ds, c.cur);
                        }}
                        disabled={weekendBlocked}
                        aria-label={ds ? `${ds}${eventCount ? `, ${eventCount} events` : ""}${weekendBlocked ? ", weekend hidden by filter" : ""}` : undefined}
                        aria-current={isSel ? "date" : undefined}
                        title={weekendBlocked ? "Weekends are hidden by Filter" : undefined}
                      >
                        <span className="cal-dash-mini-num">{c.day}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="cal-dash-mini-foot">
                  <div className="cal-dash-mini-foot-label">
                    <strong>{selDate === today ? "Today" : (fmtTimelineDayLabel(selDate || today) || "Select a day")}</strong>
                    {(mByDate[selDate || today] || []).length} event{(mByDate[selDate || today] || []).length === 1 ? "" : "s"}
                    <span aria-hidden> • </span>
                    Tap a date to open its timeline
                  </div>
                </div>
              </div>

              <div className="cal-dash-card cal-dash-card--overview">
                <h2 className="cal-dash-card-title">Today Overview</h2>
                <div className="cal-dash-metrics cal-dash-metrics--three">
                  <div
                    className="cal-dash-metric cal-dash-metric--blue"
                    data-tip={`${eventsTodayCount} meeting${eventsTodayCount === 1 ? "" : "s"} scheduled today`}
                    aria-label={`${eventsTodayCount} meeting${eventsTodayCount === 1 ? "" : "s"} scheduled today`}
                  >
                    <span className="cal-dash-metric-icon" aria-hidden><Svg d={IC.cal} size={14} color="#0875E1" stroke={2} /></span>
                    <p className="cal-dash-metric-val">{eventsTodayCount}</p>
                    <p className="cal-dash-metric-label">Meetings</p>
                  </div>
                  <div
                    className="cal-dash-metric cal-dash-metric--green"
                    data-tip={
                      hoursTodayMinutes > 0
                        ? `${hoursTodayDisplay} hours — sum of today's meeting durations (${hoursTodayParts})`
                        : "Hours is the sum of today's meeting start-to-end times"
                    }
                    aria-label={
                      hoursTodayMinutes > 0
                        ? `${hoursTodayDisplay} hours is the sum of today's meeting durations (${hoursTodayParts})`
                        : "Hours is the sum of today's meeting start-to-end times"
                    }
                  >
                    <span className="cal-dash-metric-icon" aria-hidden><Svg d={IC.clock} size={14} color="#05B887" stroke={2} /></span>
                    <p className="cal-dash-metric-val">{hoursTodayDisplay}</p>
                    <p className="cal-dash-metric-label">Hours</p>
                  </div>
                  <div
                    className="cal-dash-metric cal-dash-metric--next"
                    data-tip={`${eventsNext7Count} meeting${eventsNext7Count === 1 ? "" : "s"} in the next 7 days`}
                    aria-label={`${eventsNext7Count} meeting${eventsNext7Count === 1 ? "" : "s"} in the next 7 days`}
                  >
                    <span className="cal-dash-metric-icon" aria-hidden><Svg d={IC.cal} size={14} color="#0875E1" stroke={2} /></span>
                    <p className="cal-dash-metric-val">{eventsNext7Count}</p>
                    <p className="cal-dash-metric-label">Next 7 days</p>
                  </div>
                </div>
              </div>
            </aside>

            <section className="cal-dash-timeline" aria-label="Timeline">
              <div className="cal-dash-timeline-head">
                <h2 className="cal-dash-timeline-head-title">
                  {timelineDays.length === 1
                    ? `${fmtTimelineDayLabel(timelineDays[0])} • ${timelineShownCount} meeting${timelineShownCount === 1 ? "" : "s"}`
                    : `Timeline • ${timelineShownCount} meeting${timelineShownCount === 1 ? "" : "s"}`}
                </h2>
              </div>
              <div className="cal-dash-timeline-scroll">
                {timelineShownCount === 0 ? (
                  <div className="cal-dash-empty">No meetings in this period.</div>
                ) : (
                  timelineDays.map((d) => {
                    const dayList = sortMeetingsChronologically(mByDate[d] || []);
                    if (dayList.length === 0) return null;
                    return (
                      <div key={d} className="cal-dash-day-group">
                        {timelineDays.length > 1 ? (
                          <div className="cal-dash-day-head">
                            <div className="cal-dash-day-label">{fmtTimelineDayLabel(d)}</div>
                          </div>
                        ) : null}
                        {dayList.map((m, mi) => {
                            const status = effectiveMeetingStatus(m, nowMs);
                            const cancelled = status === "cancelled";
                            const tag = meetingTimelineCategory(m);
                            const organizer = meetingTimelineOrganizer(m);
                            const attendeeCount = meetingTimelineAttendeeCount(m);
                            const startLabel = formatReferenceListTime(m.time) || formatMeetingTimeForDisplay(m.time) || "All day";
                            const endRaw = m?.end ?? m?.end_time ?? m?.endTime;
                            const endLabel = endRaw != null && String(endRaw).trim() && String(endRaw).trim() !== "—"
                              ? (formatReferenceListTime(endRaw) || formatMeetingTimeForDisplay(endRaw))
                              : "";
                            const durMins = Math.max(0, Math.round(Number(meetingDurationMinutes(m)) || 0));
                            const durLabel = durMins > 0
                              ? (durMins < 60 ? `${durMins} min` : `${Math.floor(durMins / 60)} hr${durMins % 60 ? ` ${durMins % 60} min` : ""}`)
                              : "";
                            const googleEventId = String(m?.googleEventId || "").trim();
                            const joinUrl = calendarMeetingJoinUrl(m);
                            const hasJoinUrl = !!joinUrl && /^https?:\/\//i.test(joinUrl);
                            const isGoogleToggleable =
                              !adminCalendar &&
    (m?.type === "google_calendar" || m?.type === "calendar_auto") &&
                              !!googleEventId &&
                              (status === "upcoming" || status === "live") &&
                              typeof onToggleMeetingAutoJoin === "function";
                            const canToggle = isGoogleToggleable && hasJoinUrl && !cancelled;
                            const showNoLink = isGoogleToggleable && !hasJoinUrl && !cancelled;
                            const meetingToggleSaving = !!meetingAutoJoinSaving?.[googleEventId];
                            const recApiId = meetingRecordingApiId(m);
                            const recordingSaving = !!meetingRecordingSaving?.[recApiId];
                            const meetingAutoJoinEnabled = meetingAutoJoinIsOn(m);
                            const recordingOn = effectiveRecordingEnabled(m);
                            const canToggleRecording =
                              canToggle && typeof onToggleMeetingRecording === "function";
                            const joinLive = status === "live";
                            const joinEnded = status === "completed" || status === "ended";
                            const showJoinState = (joinEnded || joinLive) && !cancelled;
                            const botJoined = meetingBotJoinedCall(m);
                            const botJoining = meetingMeetAiIsJoining(m);
                            const joinPill = !showJoinState ? null
                              : botJoined
                                ? { cls: "cal-join-pill--joined", dot: "#16A34A", label: "MeetAI Joined", tip: "MeetAI joined this meeting — open it for details" }
                                : botJoining
                                  ? { cls: "cal-join-pill--joining", dot: "#D97706", label: "Joining…", tip: "MeetAI is joining this meeting" }
                                  : { cls: "cal-join-pill--not cal-join-pill--missed", missed: true, tip: meetingAutoJoinEnabled ? "MeetAI did not join this meeting. Open for details." : "Invite MeetAI is off for this meeting." };
                            return (
                              <div key={meetingListReactKey(m, mi)} className={`cal-dash-evt${joinLive ? " is-live" : ""}${cancelled ? " is-cancelled" : ""}`} role="button" tabIndex={0}
                                onClick={() => { setSelDate(d); onOpen?.(m); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelDate(d); onOpen?.(m); } }}
                                style={{ opacity: cancelled ? 0.72 : 1 }}>
                                <div className="cal-dash-evt-rail" aria-hidden>
                                  <span className="cal-dash-evt-node" />
                                </div>
                                <div className="cal-dash-evt-timeblock">
                                  <div className="cal-dash-evt-time">{startLabel}</div>
                                  {endLabel ? <div className="cal-dash-evt-time-end">{endLabel}</div> : null}
                                  {durLabel ? <div className="cal-dash-evt-dur">{durLabel}</div> : null}
                                </div>
                                <div className="cal-dash-evt-card">
                                  <span className="cal-dash-evt-platform" aria-hidden>
                                    <MeetingListPlatformIcon platform={m?.platform || "meet"} size={22} />
                                  </span>
                                  <div className="cal-dash-evt-body">
                                    <div className="cal-dash-evt-title" style={{ textDecoration: cancelled ? "line-through" : "none" }}>{m.title || "Meeting"}</div>
                                    <div className="cal-dash-evt-meta">{organizer}{attendeeCount > 0 ? ` • ${attendeeCount} attendee${attendeeCount === 1 ? "" : "s"}` : ""}</div>
                                    <span className="cal-dash-evt-tags">
                                      <span className="cal-dash-evt-tag" style={{ background: tag.bg, color: tag.fg }}>{tag.label}</span>
                                    </span>
                                  </div>
                                  <div className="cal-dash-evt-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    {joinPill ? (
                                      <span className={`cal-join-pill ${joinPill.cls}`} title={joinPill.tip} aria-label={joinPill.tip}>
                                        {joinPill.missed ? (
                                          <>
                                            <MeetAiNotJoinedIcon size={14} color="#94A3B8" />
                                            MeetAI Not Joined
                                          </>
                                        ) : (
                                          <>
                                            <span className="cal-join-dot" style={{ background: joinPill.dot }} aria-hidden />
                                            {joinPill.label}
                                          </>
                                        )}
                                      </span>
                                    ) : null}
                                    {canToggle ? (
                                      <div className="cal-dash-evt-toggle">
                                        <ToggleBar
                                          checked={meetingAutoJoinEnabled}
                                          disabled={meetingToggleSaving}
                                          label={JOIN_MEET_AI_LABEL}
                                          switchWidth={26}
                                          switchHeight={15}
                                          thumbSize={11}
                                          title={
                                            meetingToggleSaving
                                              ? "Saving…"
                                              : JOIN_MEET_AI_TOGGLE_HINT
                                          }
                                          onChange={(next) => onToggleMeetingAutoJoin?.(googleEventId, next, m)}
                                          stopPropagation
                                          tipHorizontal="center"
                                          tipPrefer="below"
                                          tipMaxWidth={220}
                                        />
                                        {canToggleRecording ? (
                                          <ToggleBar
                                            checked={meetingAutoJoinEnabled && recordingOn}
                                            disabled={recordingSaving || !meetingAutoJoinEnabled}
                                            label="Video Rec"
                                            switchWidth={26}
                                            switchHeight={15}
                                            thumbSize={11}
                                            title={
                                              recordingSaving
                                                ? "Saving…"
                                                : !meetingAutoJoinEnabled
                                                  ? VIDEO_REC_NEED_AUTO_JOIN_HINT
                                                  : VIDEO_REC_ENABLE_HINT_COMPACT
                                            }
                                            onChange={(next) => {
                                              if (next && !meetingAutoJoinEnabled) return;
                                              onToggleMeetingRecording?.(m, next);
                                            }}
                                            stopPropagation
                                            tipHorizontal="center"
                                            tipPrefer="below"
                                            tipMaxWidth={220}
                                          />
                                        ) : null}
                                      </div>
                                    ) : showNoLink ? (
                                      <span className="cal-join-pill cal-join-pill--nolink" title="This Google event has no meeting link — add a video link to enable Join AI">No Meeting Link</span>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </div>
      ) : (
        <>
      <div className="adm-cal-toolbar">
        <div className="adm-cal-toolbar-left">
          <button type="button" className="cal-tb-btn" onClick={goToday}>Today</button>
          <div style={{display:"flex"}}>
            <button type="button" className="cal-nav-icon cal-nav-split-l" onClick={prevNav} aria-label={calNavStepLabel("prev")} title={calNavStepLabel("prev")}><Svg d={IC.chevL} size={18} color="currentColor" stroke={2.25}/></button>
            <button type="button" className="cal-nav-icon cal-nav-split-r" onClick={nextNav} aria-label={calNavStepLabel("next")} title={calNavStepLabel("next")}><Svg d={IC.chevR} size={18} color="currentColor" stroke={2.25}/></button>
          </div>
          <span className="adm-cal-toolbar-title">
            {view==="month" && `${MONTHS[month]} ${year}`}
            {view==="year" && `${year}`}
            {view==="day" && fmtDate(anchorYmd)}
            {view!=="month" && view!=="year" && view!=="day" && (()=>{ const s=startOfWeekYmd(anchorYmd); const e=endOfWeekYmd(anchorYmd); return view==="week" ? `${fmtDate(s)} — ${fmtDate(e)}` : `${fmtDate(anchorYmd)}`; })()}
          </span>
          {view === "week" && filteredMeetings.length > 0 ? (
            <button
              type="button"
              className="cal-tb-btn"
              onClick={openWeekAllMeetingsPopup}
              title="Open all meetings in this week"
            >
              See more · {filteredMeetings.length}
            </button>
          ) : null}
        </div>
        <div className="adm-cal-toolbar-right">
          <div className="seg-group" role="group" aria-label="Calendar view">
            {[{id:"day", label:"Day"},{id:"week", label:"Week"},{id:"month", label:"Month"}].map((opt)=>(
              <button key={opt.id} type="button" className={`cal-view-seg${view === opt.id ? " active" : ""}`} onClick={() => setView(opt.id)} aria-pressed={view === opt.id}>{opt.label}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="cal-main-grid" style={{display:"block"}}>
        <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
          {view==="month" ? (
            <div className="mobile-scroll-x" style={{ maxHeight: "min(72vh, 640px)", overflowY: "auto", overflowX: "auto" }}>
            <div style={{ minWidth: 560 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", background: T.surface, borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, zIndex: 1 }}>
                {DAYS.map((d,i)=>(<div key={d} style={{textAlign:"center",padding:"10px 0",fontSize:11,fontWeight:700,color:i===0||i===6?"#C0C4D0":T.textMuted,letterSpacing:.7,textTransform:"uppercase",borderRight:i<6?`1px solid ${T.border}`:"none",background:T.surface}}>{d}</div>))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gridAutoRows: "minmax(112px, auto)" }}>
                {cells.map((c,i)=>{
                  const ds=ymdForMonthCell(c, year, month);
                  const dayMeets=ds?(mByDate[ds]||[]):[];
                  const isToday=ds===today; const isSel=ds===selDate; const isWknd=i%7===0||i%7===6; const col=i%7;
                  const cellBg=!c.cur?"#FAFBFC":isSel?"#E3EEFF":isToday?"#E8F4FD":isWknd?"#FAFAFA":T.bg;
                  const previewCap = CAL_MONTH_DAY_EVENT_PREVIEW_ADMIN;
                  const hiddenCount = Math.max(0, dayMeets.length - previewCap);
                  return (
                    <div
                      key={i}
                      onClick={()=>ds&&openDayPopup(ds)}
                      style={{
                        minHeight: 112,
                        height: "100%",
                        overflow: "hidden",
                        borderRight: col < 6 ? `1px solid ${T.border}` : "none",
                        borderBottom: i < 35 ? `1px solid ${T.border}` : "none",
                        background: cellBg,
                        cursor: c.cur ? "pointer" : "default",
                        padding: "6px 5px 5px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2, flexShrink: 0 }}>
                        <span style={{
                          width: 24, height: 24, borderRadius: "50%",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12.5, fontWeight: isToday || isSel ? 700 : 400,
                          background: isSel ? T.accent : isToday ? "rgba(21,101,192,0.14)" : "transparent",
                          color: isSel ? "#fff" : isToday ? T.accent : c.cur ? T.text : T.textMuted,
                        }}>{c.day}</span>
                        {dayMeets.length > previewCap ? (
                          <span style={{ fontSize: 9.5, fontWeight: 700, color: T.accent, opacity: 0.85 }}>{dayMeets.length}</span>
                        ) : null}
                      </div>
                      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 3, overflow: "hidden" }}>
                        {dayMeets.slice(0, previewCap).map((m, mi) => {
                          const cancelled = effectiveMeetingStatus(m, nowMs) === "cancelled";
                          const evtColor = eventColorForMeeting(m, { adminBotPending: true });
                          const denseColor = cancelled ? STATUS_META.cancelled.fg : evtColor.border;
                          return (
                            <div
                              key={meetingListReactKey(m, mi)}
                              onClick={(e)=>{e.stopPropagation();openDayPopup(ds);}}
                              style={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                padding: "3px 6px",
                                borderRadius: 5,
                                background: cancelled ? "#F1F5F9" : evtColor.bg,
                                borderLeft: `3px solid ${denseColor}`,
                                color: cancelled ? T.textMuted : evtColor.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              <span style={{ color: denseColor, fontWeight: 700, fontSize: 9, marginRight: 3 }}>{formatMeetingTimeForDisplay(m.time)}</span>
                              <span style={{ textDecoration: cancelled ? "line-through" : "none" }}>{m.title}</span>
                            </div>
                          );
                        })}
                      </div>
                      {hiddenCount > 0 ? (
                        <button
                          type="button"
                          onClick={(e)=>{e.stopPropagation();openDayPopup(ds);}}
                          style={{
                            flexShrink: 0,
                            border: "none",
                            background: "rgba(21,101,192,0.06)",
                            color: T.accent,
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: "pointer",
                            textAlign: "left",
                            padding: "4px 6px",
                            borderRadius: 5,
                            marginTop: 1,
                          }}
                          title={`See all ${dayMeets.length} meetings`}
                        >
                          See more · +{hiddenCount}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          ) : view==="week" ? (
            <WeekView
              meetings={filteredMeetings}
              onOpen={onOpen}
              today={today}
              anchorYmd={anchorYmd}
              onSelectDate={setSelDate}
              onSeeMoreDay={openDayPopup}
              maxPerDay={3}
            />
          ) : view==="day" ? (
            <DayView meetings={filteredMeetings} onOpen={onOpen} today={today} ymd={anchorYmd} onSelectDate={setSelDate} compactHeader={!!adminCalendar}/>
          ) : view==="year" ? (
            <YearView meetings={filteredMeetings} onSelectMonth={(m)=>{ setYear(year); setMonth(m); setSelDate(`${year}-${pad(m+1)}-01`); setView("month"); }} year={year} selDate={selDate} />
          ) : (
            <ScheduleView meetings={filteredMeetings} onOpen={onOpen} onSelectDate={setSelDate} today={today} anchorYmd={anchorYmd} rangeDays={view==="fourDays"?4:31} />
          )}
        </div>
      </div>
        </>
      )}
      </div>
      <CalDayMeetingsPopup
        open={!!dayPopupDate}
        dateYmd={dayPopupDate}
        meetings={
          dayPopupMeetingsOverride != null
            ? dayPopupMeetingsOverride
            : dayPopupDate
              ? mByDate[dayPopupDate] || []
              : []
        }
        title={dayPopupTitle || undefined}
        onClose={closeDayPopup}
        onOpen={onOpen}
        nowMs={nowMs}
        onToggleMeetingAutoJoin={onToggleMeetingAutoJoin}
        meetingAutoJoinSaving={meetingAutoJoinSaving}
        fmtDate={fmtDate}
        embedInAdmin={adminCalendar}
      />
    </div>
  );
}

function meetingHourFromTime(m) {
  const t = m?.time;
  if (!t || typeof t !== "string") return 12;
  const h = parseInt(t.split(":")[0], 10);
  return Number.isFinite(h) ? h : 12;
}

function WeekView({ meetings, onOpen, today, anchorYmd, onSelectDate, onSeeMoreDay, maxPerDay = 3 }) {
  const DNAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const nowMs = Date.now();
  const previewCap = Math.max(1, Math.floor(Number(maxPerDay) || 3));

  const base = ymdToDate(anchorYmd) || new Date();
  const weekStart = new Date(base);
  weekStart.setDate(base.getDate() - base.getDay());
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });

  const mByDate = indexMeetingsByDate(meetings);

  return (
    <div className="cal-week-view mobile-scroll-x">
      <div className="cal-week-inner" style={{ minWidth: 640 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            borderBottom: `1px solid ${T.border}`,
            background: T.surface,
          }}
        >
          {weekDates.map((date, i) => {
            const isToday = date === today;
            const num = parseInt(date.split("-")[2], 10);
            const dayCount = (mByDate[date] || []).length;
            return (
              <div
                key={date}
                onClick={() => onSelectDate && onSelectDate(date)}
                style={{
                  textAlign: "center",
                  padding: "10px 6px 8px",
                  minWidth: 0,
                  overflow: "hidden",
                  borderRight: i < 6 ? `1px solid ${T.border}` : "none",
                  cursor: onSelectDate ? "pointer" : "default",
                  background: isToday ? "rgba(21,101,192,0.04)" : undefined,
                }}
                title="Select day"
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: isToday ? T.accent : T.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    marginBottom: 4,
                  }}
                >
                  {DNAMES[i]}
                </div>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: isToday ? T.accent : "transparent",
                    color: isToday ? "#fff" : T.text,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    fontSize: 13,
                    fontWeight: isToday ? 700 : 500,
                  }}
                >
                  {num}
                </div>
                {dayCount > 0 ? (
                  onSeeMoreDay ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSeeMoreDay(date);
                      }}
                      style={{
                        marginTop: 6,
                        border: "none",
                        background: "transparent",
                        fontSize: 10,
                        fontWeight: 600,
                        color: isToday ? T.accent : T.textMuted,
                        cursor: "pointer",
                        padding: 0,
                      }}
                      title={`See all ${dayCount} meetings`}
                    >
                      {dayCount} meeting{dayCount === 1 ? "" : "s"}
                    </button>
                  ) : (
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 10,
                        fontWeight: 600,
                        color: isToday ? T.accent : T.textMuted,
                      }}
                    >
                      {dayCount} meeting{dayCount === 1 ? "" : "s"}
                    </div>
                  )
                ) : (
                  <div style={{ marginTop: 6, fontSize: 10, color: T.textMuted, opacity: 0.7 }}>Free</div>
                )}
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            minHeight: 280,
            maxHeight: 440,
            overflowY: "auto",
            background: T.bg,
          }}
        >
          {weekDates.map((date, di) => {
            const dayMeets = mByDate[date] || [];
            const isToday = date === today;
            const shown = dayMeets.slice(0, previewCap);
            const hidden = Math.max(0, dayMeets.length - shown.length);
            return (
              <div
                key={date}
                style={{
                  minWidth: 0,
                  padding: "8px 6px 10px",
                  borderRight: di < 6 ? `1px solid ${T.border}` : "none",
                  background: isToday ? "rgba(21,101,192,0.03)" : undefined,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {dayMeets.length === 0 ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      color: T.textMuted,
                      opacity: 0.65,
                      padding: "24px 4px",
                    }}
                  >
                    —
                  </div>
                ) : (
                  <>
                    {shown.map((m, mi) => {
                      const cancelled = effectiveMeetingStatus(m, nowMs) === "cancelled";
                      const evtColor = eventColorForMeeting(m);
                      const denseColor = cancelled ? STATUS_META.cancelled.fg : evtColor.border;
                      const timeLabel = formatMeetingTimeForDisplay(m.time);
                      const endLabel = formatMeetingTimeForDisplay(m.end);
                      return (
                        <button
                          key={meetingListReactKey(m, mi)}
                          type="button"
                          onClick={() => {
                            onSelectDate && onSelectDate(date);
                            onOpen?.(m);
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            background: cancelled ? "#F1F5F9" : evtColor.bg,
                            border: "none",
                            borderLeft: `3px solid ${denseColor}`,
                            borderRadius: "0 8px 8px 0",
                            padding: "8px 8px 7px",
                            cursor: "pointer",
                            overflow: "hidden",
                            transition: "background .12s, box-shadow .12s",
                            boxShadow: "0 1px 2px rgba(12,43,78,0.04)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = cancelled ? "#E2E8F0" : evtColor.bgHover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = cancelled ? "#F1F5F9" : evtColor.bg;
                          }}
                          title={m.title || "Meeting"}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: cancelled ? T.textMuted : denseColor,
                              marginBottom: 3,
                              letterSpacing: "0.01em",
                            }}
                          >
                            {timeLabel}
                            {endLabel ? `–${endLabel}` : ""}
                          </div>
                          <div
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              color: cancelled ? T.textMuted : T.text,
                              lineHeight: 1.3,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textDecoration: cancelled ? "line-through" : "none",
                            }}
                          >
                            {m.title || "Untitled"}
                          </div>
                        </button>
                      );
                    })}
                    {hidden > 0 && onSeeMoreDay ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSeeMoreDay(date);
                        }}
                        style={{
                          marginTop: 2,
                          width: "100%",
                          border: `1px dashed ${T.accentMid || "rgba(21,101,192,0.35)"}`,
                          background: "rgba(21,101,192,0.06)",
                          color: T.accent,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                          textAlign: "center",
                          padding: "8px 6px",
                          borderRadius: 8,
                          transition: "background .12s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(21,101,192,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(21,101,192,0.06)";
                        }}
                        title={`See all ${dayMeets.length} meetings`}
                      >
                        See more · +{hidden}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DayView({meetings,onOpen,today,ymd,onSelectDate,compactHeader=false}){
  const nowMs = Date.now();
  const list = (meetings||[])
    .filter(m=>m?.date===ymd)
    .slice()
    .sort((a,b)=>meetingDateTimeMs(a)-meetingDateTimeMs(b));

  const isToday = ymd === today;

  function fmt(ds){
    const dt = ymdToDate(ds);
    if(!dt) return ds;
    return dt.toLocaleDateString("en-IN",{
      weekday:"long",
      year:"numeric",
      month:"long",
      day:"numeric",
      timeZone: APP_DISPLAY_TZ,
    });
  }

  return (
    <div style={{padding: compactHeader ? "4px 4px 8px" : 14}}>
      {compactHeader ? (
        <div className="adm-cal-day-meta">
          {list.length === 0
            ? "No meetings this day"
            : `${list.length} meeting${list.length === 1 ? "" : "s"}`}
          {isToday ? " · Today" : ""}
        </div>
      ) : (
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:T.text}}>{fmt(ymd)}</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{list.length===0?"No meetings":"Meetings for this day"}</div>
          </div>
          {isToday&&<div style={{fontSize:10.5,fontWeight:700,color:T.accent,background:T.accentBg,border:`1px solid ${T.accentMid}`,padding:"6px 10px",borderRadius:10}}>Today</div>}
        </div>
      )}

      {list.length===0 ? (
        <div style={{textAlign:"center",padding:"28px 0",color:T.textMuted,fontSize:12.5}}>
          Free day
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {list.map(m=>{
            const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
            const effSt = effectiveMeetingStatus(m, nowMs);
            const cancelled = effSt === "cancelled";
            const evtColor = eventColorForMeeting(m);
            const denseColor = cancelled ? STATUS_META.cancelled.fg : evtColor.border;
            const st = STATUS_META[effSt] || STATUS_META.upcoming;
            const joinUrl = joinUrlFromMeeting(m) || String(m?.meetingUrl || m?.meeting_url || m?.hangoutLink || "").trim();
            const hasJoin = !!joinUrl && /^https?:\/\//i.test(joinUrl);
            return (
              <div
                key={m.id}
                onClick={()=>{ onSelectDate && onSelectDate(ymd); onOpen(m); }}
                style={{padding:"12px 12px",border:`1px solid ${T.border}`,borderLeft:`3px solid ${denseColor}`,borderRadius:10,cursor:"pointer",background:cancelled?T.surface:evtColor.bg,transition:"background .12s",opacity:cancelled?0.92:1}}
                onMouseEnter={e=>e.currentTarget.style.background=cancelled?T.accentBg:evtColor.bgHover}
                onMouseLeave={e=>e.currentTarget.style.background=cancelled?T.surface:evtColor.bg}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,fontWeight:600,color:cancelled?T.textMuted:T.text,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",marginBottom:4,textDecoration:cancelled?"line-through":"none"}}>{m.title}</div>
                    <div style={{fontSize:11,color:T.textMuted,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontFamily:"monospace"}}>{formatMeetingTimeForDisplay(m.time)}–{formatMeetingTimeForDisplay(m.end)}</span>
                      <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                        <span style={{width:6,height:6,borderRadius:"50%",background:st.dot,display:"inline-block"}}/>
                        <span style={{color:st.fg,fontWeight:700}}>{st.label}</span>
                      </span>
                      {compactHeader && meetingHasDisplayableHost(m) ? (
                        <span>Host: {meetingHostName(m)}</span>
                      ) : null}
                    </div>
                  </div>
                  <div style={{flexShrink:0, display:"flex", alignItems:"center", gap:6}} onClick={(e)=>e.stopPropagation()}>
                    {compactHeader ? (
                      <>
                        <button
                          type="button"
                          title="View details"
                          aria-label="View details"
                          onClick={() => onOpen?.(m)}
                          style={{
                            width: 30, height: 30, borderRadius: 8, border: `1px solid ${T.border}`,
                            background: "#fff", cursor: "pointer", display: "inline-flex",
                            alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <Svg d={IC.eye} color={T.accent} size={14} stroke={2.1} />
                        </button>
                        <button
                          type="button"
                          title={hasJoin ? "Join meeting" : "No join link"}
                          aria-label="Join meeting"
                          disabled={!hasJoin}
                          onClick={() => {
                            if (hasJoin) window.open(joinUrl, "_blank", "noopener,noreferrer");
                          }}
                          style={{
                            width: 30, height: 30, borderRadius: 8,
                            border: `1px solid ${hasJoin ? "#BBCFEE" : T.border}`,
                            background: hasJoin ? "#E3EEFF" : "#F8FAFC",
                            cursor: hasJoin ? "pointer" : "not-allowed",
                            opacity: hasJoin ? 1 : 0.55,
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <Svg d={IC.video} color={hasJoin ? "#1565C0" : T.textMuted} size={14} stroke={2.1} />
                        </button>
                      </>
                    ) : (
                      <span style={{fontSize:10.5,fontWeight:800,color:denseColor,background:evtColor.bg,border:`1px solid ${T.border}`,padding:"5px 8px",borderRadius:10}}>{pl.short}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function YearView({meetings,onSelectMonth,year,selDate}){
  const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const selDt = ymdToDate(selDate);
  const selMonth = selDt ? selDt.getMonth() : null;
  const counts = Array.from({length:12},()=>0);

  (meetings||[]).forEach(m=>{
    if(!m?.date) return;
    const dt = ymdToDate(m.date);
    if(!dt) return;
    if(dt.getFullYear()!==year) return;
    counts[dt.getMonth()] += 1;
  });

  return (
    <div style={{padding:14}}>
      <div style={{fontSize:12.5,color:T.textMuted,fontWeight:600,marginBottom:10}}>Year overview</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:10}}>
        {MONTHS.map((name,i)=>(
          <button
            key={name}
            type="button"
            onClick={()=>onSelectMonth && onSelectMonth(i)}
            style={{
              padding:"14px 12px",
              borderRadius:12,
              border:`1px solid ${T.border}`,
              background:selMonth===i?"rgba(21,101,192,0.08)":T.surface,
              cursor:"pointer",
              textAlign:"left",
              transition:"background .15s",
            }}
          >
            <div style={{fontSize:12,fontWeight:800,color:selMonth===i?T.accent:T.textSub,marginBottom:6}}>{name.slice(0,3)}</div>
            <div style={{fontSize:22,fontWeight:800,color:T.text,letterSpacing:-.4}}>{counts[i]}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ScheduleView({meetings,onOpen,onSelectDate,today,anchorYmd,rangeDays}){
  const start = anchorYmd;
  const end = addDaysYmd(anchorYmd, Math.max(0,rangeDays-1));
  const startDt = ymdToDate(start);
  const endDt = ymdToDate(end);

  const days = [];
  for(let i=0;i<rangeDays;i++){
    days.push(addDaysYmd(start, i));
  }

  const inRange = (ymd)=>{
    const dt = ymdToDate(ymd);
    if(!dt || !startDt || !endDt) return false;
    return dt >= startDt && dt <= endDt;
  };

  const mByDate = {};
  (meetings||[]).filter(m=>inRange(m?.date)).forEach(m=>{
    const d=m?.date;
    if(!d) return;
    if(!mByDate[d]) mByDate[d]=[];
    mByDate[d].push(m);
  });

  function fmtDay(ds){
    const dt = ymdToDate(ds);
    if(!dt) return ds;
    const isT = ds===today;
    return dt.toLocaleDateString("en-IN",{
      weekday:"short",
      month:"short",
      day:"numeric",
      year:"numeric",
      timeZone: APP_DISPLAY_TZ,
    }) + (isT?" (Today)":"");
  }

  return (
    <div style={{padding:14}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div style={{fontSize:13.5,fontWeight:800,color:T.text}}>Schedule</div>
        <div style={{fontSize:11,color:T.textMuted}}>{start} to {end}</div>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {days.map(d=>{
          const dayList = (mByDate[d]||[]).slice().sort((a,b)=>meetingDateTimeMs(a)-meetingDateTimeMs(b));
          return (
            <div key={d} style={{border:`1px solid ${T.border}`,borderRadius:12,background:T.surface,overflow:"hidden"}}>
              <div style={{padding:"10px 12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div
                  style={{fontSize:12.5,fontWeight:800,color: d===today ? T.accent : T.textSub,cursor:onSelectDate?"pointer":"default"}}
                  onClick={()=>onSelectDate && onSelectDate(d)}
                >
                  {fmtDay(d)}
                </div>
                <div style={{fontSize:11,color:T.textMuted}}>{dayList.length===0?"No meetings":`${dayList.length} meeting${dayList.length>1?"s":""}`}</div>
              </div>
              {dayList.length===0 ? (
                <div style={{textAlign:"center",padding:"16px 12px",color:T.textMuted,fontSize:12.5}}>Free day</div>
              ) : (
                <div style={{padding:"10px 12px",display:"flex",flexDirection:"column",gap:8}}>
                  {dayList.map(m=>{
                    const pl = PLATFORM_META[m.platform] || PLATFORM_META.meet;
                    const effSt = effectiveMeetingStatus(m);
                    const cancelled = effSt === "cancelled";
                    const evtColor = eventColorForMeeting(m);
                    const denseColor = cancelled ? STATUS_META.cancelled.fg : evtColor.border;
                    const st = STATUS_META[effSt] || STATUS_META.upcoming;
                    return (
                      <div
                        key={m.id}
                        onClick={()=>{ onSelectDate && onSelectDate(d); onOpen(m); }}
                        style={{padding:"10px 10px",borderRadius:10,border:`1px solid ${T.border}`,borderLeft:`3px solid ${denseColor}`,cursor:"pointer",background:cancelled?T.bg:evtColor.bg,transition:"background .12s",opacity:cancelled?0.92:1}}
                        onMouseEnter={e=>e.currentTarget.style.background=cancelled?T.accentBg:evtColor.bgHover}
                        onMouseLeave={e=>e.currentTarget.style.background=cancelled?T.bg:evtColor.bg}
                      >
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
                          <div style={{fontSize:12.5,fontWeight:700,color:cancelled?T.textMuted:T.text,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",flex:1,textDecoration:cancelled?"line-through":"none"}}>{m.title}</div>
                          <span style={{fontSize:9.5,fontWeight:700,color:st.fg,background:st.bg,padding:"2px 6px",borderRadius:6,flexShrink:0}}>{st.label}</span>
                        </div>
                        <div style={{fontSize:11,color:T.textMuted,fontFamily:"monospace"}}>{formatMeetingTimeForDisplay(m.time)}–{formatMeetingTimeForDisplay(m.end)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MEETING DETAIL
════════════════════════════════════════════════════════════════ */

/** Strip ```json { "summary": "..." } blobs saved by mistake (legacy rows + parse failures). */
function unwrapMoMJsonSummary(raw) {
  let s = String(raw || "").trim();
  if (!s) return s;
  const looksWrapped =
    /^```(?:json)?\s*/i.test(s) ||
    (s.includes('"summary"') && s.includes('"actionItems"') && /[{[]/.test(s));
  if (!looksWrapped) return s;

  s = s.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();
  try {
    const start = s.indexOf("{");
    const end = s.lastIndexOf("}");
    if (start >= 0 && end > start) {
      const parsed = JSON.parse(s.slice(start, end + 1));
      if (parsed?.summary != null && String(parsed.summary).trim()) {
        return String(parsed.summary);
      }
    }
  } catch (_) {
    /* LLM sometimes returns invalid JSON — try regex below */
  }

  const loose = s.match(/"summary"\s*:\s*"([\s\S]*?)"\s*,\s*"actionItems"/i);
  if (loose) {
    return loose[1].replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  }

  return s.startsWith("{") ? "" : s;
}

function stripRetiredSummarySections(summary) {
  const sectionRe =
    /^(Context|Key discussion|Decisions made|Risks and blockers|Next steps):\s*$/i;
  const dropRe = /^(Risks and blockers|Next steps):\s*$/i;
  const kept = [];
  let skipping = false;
  for (const line of String(summary || "").split("\n")) {
    const trimmed = line.trim();
    if (sectionRe.test(trimmed)) {
      skipping = dropRe.test(trimmed);
      if (!skipping) kept.push(trimmed);
      continue;
    }
    if (!skipping) kept.push(line);
  }
  return kept.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function sanitizeApprovalHtml(raw) {
  // Chromium's `hiliteColor` command creates a <font> element. Keeping that
  // safe formatting tag lets highlights survive save, reload, and recipient view.
  // DOMPurify still removes scripts, event handlers, URLs, and disallowed attributes.
  return DOMPurify.sanitize(String(raw || ""), {
    ALLOWED_TAGS: ["section", "h3", "span", "ul", "ol", "li", "p", "strong", "b", "em", "i", "u", "mark", "font", "br", "svg", "path"],
    ALLOWED_ATTR: ["class", "aria-hidden", "style", "viewBox", "fill", "stroke", "stroke-width", "stroke-linecap", "stroke-linejoin", "d", "width", "height"],
  });
}

function normalizeMeetingSummaryDisplay(raw) {
  let s = stripRetiredSummarySections(
    unwrapMoMJsonSummary(raw).replace(/\*\*/g, "")
  ).trim();
  if (!s) return s;
  if ((s.match(/\n/g) || []).length >= 3) return s;
  const labels = ["Context:", "Key discussion:", "Decisions made:"];
  for (const label of labels) {
    const esc = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    s = s.replace(new RegExp(`([^\\n])\\s+${esc}`, "gi"), `$1\n\n${label}`);
  }
  return s;
}

const MEETING_SUMMARY_SECTION_RE =
  /^(Context|Key discussion|Decisions made|Presented on screen|Risks and blockers|Next steps|Action items by participant):\s*(.*)$/i;

function meetingSummarySectionKey(header) {
  const h = String(header || "")
    .toLowerCase()
    .replace(/:$/, "")
    .trim();
  if (!h) return null;
  if (h === "context" || h.startsWith("context")) return "context";
  if (h.includes("key discussion") || h === "discussion" || h.includes("discussion point")) return "discussion";
  if (h.includes("decision")) return "decisions";
  if (h.includes("presented on screen") || h.includes("screen share") || h === "on screen") return "presented";
  if (h.includes("action item") || h.includes("next step") || h.includes("risk") || h.includes("blocker")) {
    return "omit";
  }
  return null;
}

function parseMeetingSummarySections(rawText) {
  const normalized = normalizeMeetingSummaryDisplay(rawText || "");
  const lines = normalized.split("\n");
  const out = { context: [], discussion: [], decisions: [], presented: [], actions: [] };
  let current = "context";
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const inline = trimmed.match(MEETING_SUMMARY_SECTION_RE);
    if (inline) {
      const key = meetingSummarySectionKey(inline[1]) || current;
      current = key;
      if (key !== "omit" && inline[2]?.trim()) out[key].push(inline[2].trim());
      continue;
    }
    const colon = trimmed.match(/^([^:]{2,64}):\s*(.*)$/);
    if (colon) {
      const key = meetingSummarySectionKey(colon[1]);
      if (key) {
        current = key;
        if (key !== "omit" && colon[2]?.trim()) out[key].push(colon[2].trim());
        continue;
      }
    }
    if (current === "omit") continue;
    const body = trimmed.replace(/^[-•*]\s+/, "");
    out[current].push(body);
  }
  return out;
}

function meetingOverviewItemsFromPresentation(meeting) {
  if (!presentationSummarySafeToShow(meeting)) return [];
  const presRaw = meeting?.presentationSummary || meeting?.presentation_summary || "";
  return extractPresentationSummaryBullets(presRaw);
}

function MeetingDetailStructuredSummary({ meeting, actionItems = [] }) {
  const main = normalizeMeetingSummaryDisplay(meeting?.summary || "");
  const sections = parseMeetingSummarySections(main);
  const overviewFromRecording = meetingOverviewItemsFromPresentation(meeting);
  const overviewItems = [...overviewFromRecording, ...sections.presented];
  const taskLines = (actionItems || [])
    .map((it) => String(it?.title || it?.text || it?.description || it?.name || "").trim())
    .filter(Boolean);
  const followUpBlocks = [];
  if (taskLines.length > 0) {
    followUpBlocks.push({ id: "next-steps", title: "Next Steps", items: taskLines });
  }
  const blocks = [
    ...(overviewItems.length ? [{ id: "overview", title: "Overview", items: overviewItems }] : []),
    { id: "context", title: "Context", items: sections.context },
    { id: "discussion", title: "Key Discussion Points", items: sections.discussion },
    { id: "decisions", title: "Decisions / Outcomes", items: sections.decisions },
    ...followUpBlocks,
  ].filter((b) => b.items.length > 0);

  if (!blocks.length && main.trim() && !overviewFromRecording.length) {
    return <MeetingSummaryBlock text={main} />;
  }

  if (!blocks.length) {
    return (
      <p className="avaada-detail-summary-empty">
        Summary will appear here when MeetAI finishes processing this meeting.
      </p>
    );
  }

  return (
    <div className="avaada-detail-summary-sections">
      {blocks.map((block) => {
        const icon =
          block.id === "discussion" || block.id === "decisions"
            ? IC.check
            : block.id === "next-steps"
              ? IC.listChecks
              : IC.info;
        const iconTone =
          block.id === "discussion" || block.id === "decisions"
            ? "is-success"
            : "is-info";
        return (
        <section key={block.id} className="avaada-detail-summary-section">
          <h3 className="avaada-detail-summary-section__title">
            <span className={`avaada-detail-summary-section__icon ${iconTone}`} aria-hidden>
              <Svg d={icon} size={13} color="currentColor" stroke={2.2} />
            </span>
            {block.title}
          </h3>
          <ul className="avaada-detail-summary-section__list">
            {block.items.map((item, idx) => (
              <li key={`${block.id}-${idx}`}>{item}</li>
            ))}
          </ul>
        </section>
        );
      })}
    </div>
  );
}

function MeetingSummaryBlock({ text }) {
  const normalized = normalizeMeetingSummaryDisplay(text);
  const lines = normalized.split("\n");
  return (
    <div
      className="meeting-summary-block"
      style={{
        fontSize: 13.5,
        color: T.text,
        lineHeight: 1.65,
        padding: "12px 14px",
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
      }}
    >
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} style={{ height: 8 }} />;
        const m = trimmed.match(MEETING_SUMMARY_SECTION_RE);
        if (m) {
          return (
            <div key={i} style={{ marginTop: i === 0 ? 0 : 14, marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: T.text }}>{m[1]}:</span>
              {m[2] ? <span> {m[2]}</span> : null}
            </div>
          );
        }
        const isBullet = /^[-•]\s+/.test(trimmed);
        if (isBullet) {
          const body = trimmed.replace(/^[-•]\s+/, "");
          return (
            <div key={i} style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "flex-start" }}>
              <span style={{ color: T.textMuted, flexShrink: 0, lineHeight: 1.65 }}>•</span>
              <span style={{ flex: 1 }}>{body}</span>
            </div>
          );
        }
        return (
          <p key={i} style={{ margin: "6px 0 0", paddingLeft: 0 }}>
            {trimmed}
          </p>
        );
      })}
    </div>
  );
}

const PRESENTATION_SECTION_SHORT_LABELS = {
  overview: "Overview",
  "on screen": "On screen",
  summary: "Summary",
  "questions and discussion": "Questions & discussion",
  "what was visible on screen (from meeting video snapshots)":
    "On screen (video snapshots)",
  "on screen (from meeting video snapshots)": "On screen (video snapshots)",
  "on screen (verified from video)": "On screen (verified)",
  "on screen (verified)": "On screen (verified)",
  "what was said (from meeting transcript)": "What was said",
  "what they explained (from meeting transcript — not from video ocr)":
    "What was said (transcript)",
  "what they explained (from meeting transcript - not from video ocr)":
    "What was said (transcript)",
  "what was said (transcript — not from video ocr)": "What was said (transcript)",
  "what was said (transcript - not from video ocr)": "What was said (transcript)",
};

/** Collapsed presentation summary body height (single Show more for entire block). */
const PRES_SUMMARY_COLLAPSED_MAX_PX = 280;

function presentationSectionShortLabel(fullHeading) {
  const raw = String(fullHeading || "").replace(/:$/, "").trim();
  const key = raw.toLowerCase();
  if (PRESENTATION_SECTION_SHORT_LABELS[key]) return PRESENTATION_SECTION_SHORT_LABELS[key];
  if (key.length > 52) {
    if (key.includes("on screen") && key.includes("snapshot")) return "On screen (video snapshots)";
    if (key.includes("while") && key.includes("sharing")) return "On screen while sharing";
    if (key.includes("said") && key.includes("transcript")) return "What was said (transcript)";
    if (key.includes("visible on screen")) return "On screen";
    if (key.includes("explained") && key.includes("transcript")) return "What was said";
    return raw.length > 56 ? `${raw.slice(0, 53).trim()}…` : raw;
  }
  return raw;
}

function normalizePresentationSummaryStatus(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "ready" || s === "limited") return s;
  if (s === "ready_limited") return "limited";
  return s;
}

/** Hide old "ready" summaries that are clearly low-confidence (code dumps / OCR walls). */
function isLegacyLowConfidencePresentationSummary(text) {
  const s = stripPresentationSummaryFooter(text);
  if (!s || s.length < 500) return false;
  const devSignals = [
    /\bconst\s+\w+\s*=/,
    /\buseState\b/,
    /compiled successfully/i,
    /\blocalhost:\d+/i,
    /webpack compiled/i,
  ];
  if (devSignals.filter((re) => re.test(s)).length >= 2) return true;
  const bullets = s.split("\n").filter((l) => /^\s*[-•]\s+/.test(l));
  return bullets.length > 20;
}

function presentationSummarySafeToShow(meeting) {
  const status = normalizePresentationSummaryStatus(
    meeting?.presentationSummaryStatus || meeting?.presentation_summary_status
  );
  const text = String(
    meeting?.presentationSummary || meeting?.presentation_summary || ""
  ).trim();
  if (!text) return false;
  if (status !== "ready" && status !== "limited") return false;
  if (status === "ready" && isLegacyLowConfidencePresentationSummary(text)) return false;
  return true;
}

/** Remove legacy snapshot footer from stored presentation summaries. */
function stripPresentationSummaryFooter(raw) {
  return String(raw || "")
    .replace(
      /\n?\([^)]*(?:generated from|no video snapshots|screen-share|Recall screen-share)[^)]*\)\s*$/gi,
      ""
    )
    .trim();
}

function parsePresentationSummary(raw) {
  const lines = stripPresentationSummaryFooter(raw).split("\n");
  const result = { segments: [], footer: "" };
  let currentSegment = null;
  let currentSection = null;

  const pushSegment = (title) => {
    currentSegment = { title: String(title || "").trim(), sections: [] };
    result.segments.push(currentSegment);
    currentSection = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (
      trimmed.startsWith("(") &&
      (/generated from/i.test(trimmed) ||
        /no video snapshots/i.test(trimmed) ||
        /screen-share/i.test(trimmed))
    ) {
      result.footer = trimmed;
      currentSection = null;
      continue;
    }

    if (/^OVERVIEW$/i.test(trimmed)) {
      pushSegment("Overview");
      currentSection = {
        label: "Overview",
        fullLabel: "OVERVIEW",
        bullets: [],
      };
      currentSegment.sections.push(currentSection);
      continue;
    }

    if (/^SCREEN SHARE \d+/i.test(trimmed)) {
      pushSegment(trimmed);
      currentSection = null;
      continue;
    }

    const presenterLine = trimmed.match(/^Presenter:\s*(.+)$/i);
    if (presenterLine) {
      pushSegment(`Presenter: ${presenterLine[1].trim()}`);
      currentSection = null;
      continue;
    }

    const noteLine = trimmed.match(/^Note:\s*(.+)$/i);
    if (noteLine) {
      pushSegment("");
      currentSection = {
        label: "Note",
        fullLabel: "Note",
        bullets: [noteLine[1].trim()],
      };
      currentSegment.sections.push(currentSection);
      continue;
    }

    const bulletMatch = trimmed.match(/^[-•]\s+(.*)$/);
    if (bulletMatch) {
      if (!currentSegment) pushSegment("");
      if (!currentSection) {
        currentSection = {
          label: "On screen",
          fullLabel: "What was visible on screen",
          bullets: [],
        };
        currentSegment.sections.push(currentSection);
      }
      currentSection.bullets.push(bulletMatch[1].trim());
      continue;
    }

    const unifiedSection = trimmed.match(
      /^(On screen|Summary|Questions and discussion):\s*$/i
    );
    if (unifiedSection) {
      if (!currentSegment) pushSegment("");
      const fullLabel = `${unifiedSection[1]}:`;
      currentSection = {
        label: presentationSectionShortLabel(fullLabel.replace(/:$/, "")),
        fullLabel,
        bullets: [],
      };
      currentSegment.sections.push(currentSection);
      continue;
    }

    const headingOnly = trimmed.match(
      /^(Note:[^:]*|What was visible on screen[^:]*|What they explained[^:]*|On screen[^:]*|Presenter:[^:]*|What was said[^:]*|While [^:]+|What [^:]+ said[^:]*):\s*$/i
    );
    if (headingOnly) {
      if (!currentSegment) pushSegment("");
      const fullLabel = trimmed;
      currentSection = {
        label: presentationSectionShortLabel(fullLabel),
        fullLabel,
        bullets: [],
      };
      currentSegment.sections.push(currentSection);
      continue;
    }

    const headingWithBody = trimmed.match(
      /^(Note:[^:]*|What was visible on screen[^:]*|What they explained[^:]*|On screen[^:]*|Presenter:[^:]*|What was said[^:]*|While [^:]+|What [^:]+ said[^:]*):\s*(.+)$/i
    );
    if (headingWithBody) {
      if (!currentSegment) pushSegment("");
      const fullLabel = `${headingWithBody[1]}:`;
      currentSection = {
        label: presentationSectionShortLabel(fullLabel),
        fullLabel,
        bullets: [headingWithBody[2].trim()],
      };
      currentSegment.sections.push(currentSection);
      continue;
    }

    if (!currentSegment) {
      pushSegment(trimmed);
      continue;
    }
    if (!currentSection && currentSegment.sections.length === 0 && !currentSegment.title) {
      currentSegment.title = trimmed;
      continue;
    }
    if (
      currentSection?.bullets.length &&
      !/^(Presenter|Note|On screen|Summary|Questions|What was said|SCREEN SHARE|OVERVIEW)/i.test(
        trimmed
      )
    ) {
      currentSection.bullets[currentSection.bullets.length - 1] += ` ${trimmed}`;
    } else if (!currentSection) {
      pushSegment(trimmed);
    } else {
      pushSegment(trimmed);
      currentSection = null;
    }
  }

  return result;
}

/** Flatten video-rec summary to main talking points only (no SCREEN SHARE headers / on-screen OCR). */
function extractPresentationSummaryBullets(raw) {
  const parsed = parsePresentationSummary(raw);
  const summaryBullets = [];
  const overviewBullets = [];
  const skipBullet = (body) => {
    const s = String(body || "").trim();
    if (!s) return true;
    if (/^no questions captured/i.test(s)) return true;
    if (/^on-screen content could not be verified/i.test(s)) return true;
    return false;
  };

  for (const seg of parsed.segments) {
    const segTitle = String(seg.title || "").trim();
    const isOverviewSeg = /^overview$/i.test(segTitle) || segTitle === "Overview";
    for (const section of seg.sections || []) {
      const label = String(section.label || section.fullLabel || "").toLowerCase().replace(/:$/, "");
      const isSummary = label === "summary";
      const isOverview = label === "overview" || isOverviewSeg;
      if (!isSummary && !isOverview) continue;
      for (const b of section.bullets || []) {
        const body = String(b).trim();
        if (skipBullet(body)) continue;
        if (isSummary) summaryBullets.push(body);
        else if (isOverview) overviewBullets.push(body);
      }
    }
  }

  let chosen = summaryBullets.length ? summaryBullets : overviewBullets;
  if (!chosen.length) {
    for (const seg of parsed.segments) {
      if (/^overview$/i.test(String(seg.title || "").trim())) continue;
      for (const section of seg.sections || []) {
        const label = String(section.label || section.fullLabel || "")
          .toLowerCase()
          .replace(/:$/, "");
        if (!label.includes("on screen") && label !== "on screen") continue;
        for (const b of section.bullets || []) {
          const body = String(b).trim();
          if (!skipBullet(body)) {
            chosen.push(body);
            break;
          }
        }
        if (chosen.length >= 4) break;
      }
      if (chosen.length >= 4) break;
    }
  }
  const seen = new Set();
  return chosen.filter((b) => {
    const key = b.toLowerCase().replace(/\s+/g, " ").slice(0, 120);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Main transcript summary with recording highlights in briefing preamble (Overview). */
function buildCombinedMeetingSummaryDisplay(meeting) {
  const main = normalizeMeetingSummaryDisplay(meeting?.summary || "");
  const presBullets = meetingOverviewItemsFromPresentation(meeting);
  if (!presBullets.length) return main;
  const extra = presBullets.map((b) => `- ${b}`).join("\n");
  if (!main.trim()) return extra;
  const lines = main.split("\n");
  const sectionRe = /^(Context|Key discussion|Decisions made|Risks and blockers|Next steps):\s*$/i;
  const firstSectionIdx = lines.findIndex((l) => sectionRe.test(String(l).trim()));
  if (firstSectionIdx === -1) return `${extra}\n\n${main}`;
  const preamble = lines
    .slice(0, firstSectionIdx)
    .map((l) => l.trim())
    .filter(Boolean);
  const rest = lines.slice(firstSectionIdx);
  return `${[...preamble, extra].join("\n")}\n${rest.join("\n")}`.replace(/\n{3,}/g, "\n\n").trim();
}

function formatPresentationSegmentTitle(rawTitle) {
  const t = String(rawTitle || "").trim();
  if (!t) return { heading: "", sub: "" };
  if (/^overview$/i.test(t)) return { heading: "Overview", sub: "" };
  const m = t.match(/^SCREEN SHARE\s+(\d+)\s*\(([^)]+)\)\s*(?:[-—–]\s*(.+))?$/i);
  if (m) {
    const who = String(m[3] || "").trim();
    return {
      heading: who ? `Screen share ${m[1]} · ${who}` : `Screen share ${m[1]}`,
      sub: String(m[2] || "").trim(),
    };
  }
  return {
    heading: t.replace(/^SCREEN SHARE/i, "Screen share"),
    sub: "",
  };
}

function PresentationSummaryBlock({ text }) {
  const parsed = parsePresentationSummary(text);
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef(null);
  const [needsToggle, setNeedsToggle] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      setNeedsToggle(el.scrollHeight > PRES_SUMMARY_COLLAPSED_MAX_PX + 8);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
    };
  }, [text]);

  const cardStyle = {
    fontSize: 13.5,
    color: T.text,
    lineHeight: 1.65,
    padding: "12px 14px",
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: 10,
  };

  if (!parsed.segments.length) {
    return <MeetingSummaryBlock text={stripPresentationSummaryFooter(text)} />;
  }

  const collapsed = needsToggle && !expanded;

  return (
    <div style={cardStyle} className="presentation-summary-block">
      <div style={{ position: "relative" }}>
        <div
          ref={bodyRef}
          style={{
            maxHeight: collapsed ? PRES_SUMMARY_COLLAPSED_MAX_PX : "none",
            overflow: collapsed ? "hidden" : "visible",
          }}
        >
          {parsed.segments.map((seg, segIdx) => (
            <div
              key={`seg-${segIdx}`}
              style={{
                marginTop: segIdx === 0 ? 0 : 16,
                paddingTop: segIdx === 0 ? 0 : 14,
                borderTop: segIdx === 0 ? "none" : `1px solid ${T.border}`,
              }}
            >
              {seg.title ? (() => {
                const { heading, sub } = formatPresentationSegmentTitle(seg.title);
                if (!heading) return null;
                return (
                  <div className="presentation-summary-segment-title" style={{ marginBottom: 10 }}>
                    <div
                      title={seg.title}
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#0b6e78",
                        lineHeight: 1.35,
                      }}
                    >
                      {heading}
                    </div>
                    {sub ? (
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginTop: 4 }}>
                        {sub}
                      </div>
                    ) : null}
                  </div>
                );
              })() : null}
              {seg.sections.map((section, secIdx) => {
                const sectionKey = `${segIdx}-${secIdx}`;
                const bullets = section.bullets || [];

                return (
                  <div key={sectionKey} style={{ marginTop: secIdx === 0 ? 0 : 14 }}>
                    <div
                      className="presentation-summary-section-heading"
                      title={section.fullLabel || section.label}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: T.textSub,
                        marginBottom: 6,
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {section.label}
                    </div>
                    {bullets.length ? (
                      <div>
                        {bullets.map((b, bi) => {
                          const body = String(b || "").trim();
                          if (!body) return null;
                          return (
                            <div
                              key={`${sectionKey}-b-${bi}`}
                              style={{
                                display: "flex",
                                gap: 8,
                                marginTop: 6,
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  color: T.textMuted,
                                  flexShrink: 0,
                                  lineHeight: 1.55,
                                  marginTop: 2,
                                }}
                              >
                                •
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  fontSize: 13,
                                  lineHeight: 1.55,
                                  color: T.textSub,
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {body}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ margin: 0, fontSize: 13, color: T.textMuted, lineHeight: 1.55 }}>
                        No readable on-screen content from snapshots.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {collapsed ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 48,
              background: `linear-gradient(to bottom, transparent, ${T.surface})`,
              pointerEvents: "none",
            }}
          />
        ) : null}
      </div>
      {needsToggle ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginTop: 10,
            fontSize: 12,
            fontWeight: 600,
            color: expanded ? T.textMuted : "#1D4ED8",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
            textDecoration: "underline",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

function participantBarDisplayName(person) {
  return String(person?.name || person?.email || "Participant").trim();
}

function participantBarSummaryText(people, maxNames = 3) {
  const names = (people || []).map(participantBarDisplayName).filter(Boolean);
  if (!names.length) return "";
  if (names.length <= maxNames) return names.join(", ");
  return `${names.slice(0, maxNames).join(", ")}…`;
}

function MeetingDetailParticipantBar({
  people,
  meeting,
  onViewAll,
  onRequestApproval,
  approvalRequestSending,
  approvalMessage,
  hidePendingApprovalStatus = false,
}) {
  const visiblePeople = (people || []).filter((p) => !shouldHideMeetAiFromParticipantRoster(p));
  const count = visiblePeople.length;
  const approvalAccess = meeting?.approvalAccess;
  const approvalPending = approvalAccess?.required && approvalAccess.status === "pending";
  const approvalComplete = approvalAccess?.required && (approvalAccess.status === "approved" || approvalAccess.approved);
  const pendingApproverNames = (approvalAccess?.approvers || [])
    .filter((approver) => approver.status === "pending")
    .map((approver) => approver.name || approver.authority || approver.email)
    .filter(Boolean);
  if (count === 0 && !approvalAccess?.required) return null;
  const avatarPeople = visiblePeople.slice(0, 3);
  const overflowAvatarCount = Math.max(0, count - avatarPeople.length);
  const summaryText = participantBarSummaryText(visiblePeople, 3);
  const fullSummaryTitle = visiblePeople.map(participantBarDisplayName).join(", ");
  const barFont = { fontSize: 13, fontWeight: 600, color: "#0B2A4A", fontFamily: "inherit" };
  const approvedAt = approvalAccess?.approvedAt ? new Date(approvalAccess.approvedAt) : null;
  const approvedAtLabel = approvedAt && !Number.isNaN(approvedAt.getTime()) ? approvedAt.toLocaleString() : "";
  return (
    <div className="avaada-detail-participants-stack">
    <div className="avaada-detail-participants-bar" style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 8, padding: "8px 12px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10 }}>
      {count > 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          <div className="avaada-detail-participants-bar__count" style={barFont}>
            <Svg d={IC.users} size={18} color="#0B2A4A" stroke={2} />
            {count} Participant{count === 1 ? "" : "s"}
          </div>
          <div className="avaada-detail-participants-bar__divider" aria-hidden style={{ width: 1, height: 20, background: "#E2E8F0" }} />
          <div className="avaada-detail-participants-bar__middle" style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <div className="avaada-detail-participants-bar__avatars" aria-hidden style={{ display: "flex", alignItems: "center" }}>
              {avatarPeople.map((p, index) => {
                const name = participantBarDisplayName(p);
                const showOverflow = overflowAvatarCount > 0 && index === avatarPeople.length - 1;
                return (
                  <div
                    key={p.id || p.email || `bar-av-${index}`}
                    className={`avaada-detail-participants-bar__avatar-slot${index > 0 ? " is-overlap" : ""}`}
                    style={{ position: "relative", marginLeft: index > 0 ? -8 : 0 }}
                  >
                    <Avatar
                      initials={p.initials || String(name).slice(0, 2).toUpperCase()}
                      size={32}
                      color="#94a3b8"
                      imageUrl={p.avatar_url || p.avatarUrl || p.picture || ""}
                    />
                    {showOverflow ? (
                      <span className="avaada-detail-participants-bar__avatar-more" style={{ position: "absolute", bottom: 0, right: -6, fontSize: 10, fontWeight: 700, color: "#64748B", background: "#fff", borderRadius: 999, padding: "0 4px", border: "1px solid #E2E8F0" }}>
                        +{overflowAvatarCount}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <p className="avaada-detail-participants-bar__summary" title={fullSummaryTitle} style={{ ...barFont, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300, margin: 0 }}>
              {summaryText}
            </p>
          </div>
          <button type="button" className="avaada-mui-btn avaada-mui-btn--text avaada-detail-participants-bar__view" onClick={onViewAll} style={{ ...barFont, fontSize: 12, padding: "4px 8px" }}>
            View all participants ({count}) →
          </button>
        </div>
      ) : <div style={barFont}>Participants</div>}
    </div>
      {approvalPending && !hidePendingApprovalStatus ? (
        <div className="avaada-detail-approval-status" role="status" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "12px 14px", border: "1px solid #93C5FD", borderRadius: 10, background: "#EFF6FF", color: "#1E3A8A" }}>
          <span style={{ fontSize: 12, lineHeight: 1.45 }}>
            <strong>Approval needed from any one approver:</strong>{" "}
            {pendingApproverNames.length ? pendingApproverNames.join(", ") : approvalAccess.approverName || approvalAccess.authority || "the designated authority"}. The first approval completes the review.
          </span>
          {!approvalAccess.approved && approvalAccess.status === "pending" && !approvalAccess.visible && approvalAccess.canRequestApproval !== false ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button className="avaada-request-approval-btn" type="button" onClick={onRequestApproval} disabled={approvalRequestSending || approvalAccess.requestStatus === "sent"} style={{ border: 0, borderRadius: 7, padding: "7px 11px", background: "#0875E1", color: "#FFFFFF", fontSize: 12, fontWeight: 700, cursor: approvalRequestSending || approvalAccess.requestStatus === "sent" ? "default" : "pointer" }}>
                {approvalRequestSending ? "Sending…" : approvalAccess.requestStatus === "sent" ? <>Request sent <span aria-hidden>→</span></> : <>Request approval <span aria-hidden>→</span></>}
              </button>
              {approvalMessage ? <span style={{ fontSize: 12, color: /could not|failed|only a meeting participant/i.test(approvalMessage) ? "#B91C1C" : "#1D4ED8" }}>{approvalMessage}</span> : null}
            </span>
          ) : null}
        </div>
      ) : null}
      {approvalComplete ? (
        <div className="avaada-detail-approval-status" role="status" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "12px 14px", border: "1px solid #A7F3D0", borderRadius: 10, background: "#ECFDF5", color: "#065F46" }}>
          <strong style={{ fontSize: 12 }}>Approved</strong>
          <span style={{ fontSize: 12, lineHeight: 1.45 }}>
            by {approvalAccess.approvers?.find((approver) => approver.status === "approved")?.name || approvalAccess.approverName || approvalAccess.authority || "the designated authority"}
            {approvedAtLabel ? ` · ${approvedAtLabel}` : ""}
          </span>
          {approvalAccess.visible ? <span style={{ fontSize: 12 }}>The approved meeting content is available below.</span> : <span style={{ fontSize: 12 }}>Contact the meeting owner to request access to the approved content.</span>}
        </div>
      ) : null}
    </div>
  );
}

function GuestParticipantEmailRow({ meetingId, participant, onSaved }) {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  async function save() {
    setErr("");
    const t = email.trim();
    if (!/\S+@\S+\.\S+/.test(t)) {
      setErr("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const m = await apiFetch(`/meetings/${meetingId}/participants/${participant.id}`, {
        method: "PATCH",
        body: { email: t },
      });
      onSaved(m);
      setEmail("");
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 8 }}>
      <span style={{ fontSize: 12, color: T.text, minWidth: 100 }}>{participant.name}</span>
      <input
        className="input"
        type="email"
        placeholder="Email for meeting recap"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ flex: "1 1 180px", minWidth: 160, maxWidth: 280, padding: "6px 10px", fontSize: 12 }}
      />
      <button
        type="button"
        className="btn"
        onClick={save}
        disabled={loading}
        style={{ padding: "6px 12px", fontSize: 12, fontWeight: 500 }}
      >
        {loading ? "Saving…" : "Save"}
      </button>
      {err ? <span style={{ fontSize: 11, color: "#B91C1C", width: "100%" }}>{err}</span> : null}
    </div>
  );
}

const DETAIL_TAB_PANEL_IDS = {
  overview: "detail-section-summary",
  transcript: "detail-section-transcript",
  recording: "detail-section-recording",
  "action-items": "detail-section-action-items",
};

function DetailPage({
  meeting,
  activeTab,
  setActiveTab,
  onBack,
  currentUser,
  onMeetingUpdated,
  openedFrom,
  onToggleMeetingAutoJoin,
  meetingAutoJoinSaving,
  onToggleMeetingRecording,
  meetingRecordingSaving,
  rescheduleInfo = null,
  rescheduleSources = null,
  shellLayout = false,
}) {
  // "Auto Join" entries are pre-call scheduling cards. Hide Tasks/transcript/bot here so
  // that flow stays focused on the meeting invite — transcript + AI live under "Recorded Meetings".
  const joinOnly = openedFrom === "autojoin";
  const referenceDetail = shellLayout && !joinOnly;
  const [participantsExpanded, setParticipantsExpanded] = useState(!!referenceDetail);
  const detailTabIds = useMemo(
    () => new Set(["overview", "transcript", "recording", "action-items"]),
    []
  );
  const detailMeetingKey = useMemo(
    () => String(meeting?.id ?? meeting?._id ?? extractGoogleEventId(meeting) ?? ""),
    [meeting]
  );
  const lastDetailMeetingKeyRef = useRef("");
  useEffect(() => {
    if (referenceDetail) {
      setActiveTab((tab) => (detailTabIds.has(tab) ? tab : "overview"));
    }
  }, [referenceDetail, setActiveTab, detailTabIds]);
  useEffect(() => {
    if (!referenceDetail || !detailMeetingKey) return;
    const prevKey = lastDetailMeetingKeyRef.current;
    if (prevKey && prevKey !== detailMeetingKey) {
      setParticipantsExpanded(!!referenceDetail);
    }
    lastDetailMeetingKeyRef.current = detailMeetingKey;
  }, [detailMeetingKey, referenceDetail, openedFrom, setActiveTab]);

  useEffect(() => {
    if (referenceDetail) return;
    if (activeTab !== "action-items") setActiveTab("action-items");
  }, [referenceDetail, activeTab, setActiveTab]);
  const backLabel =
    openedFrom === "calendar"
      ? "Back to Calendar"
      : openedFrom === "autojoin"
        ? `Back to ${JOIN_MEET_AI_LABEL}`
        : openedFrom === "actions"
          ? "Back to Actions"
          : openedFrom === "dashboard"
            ? "Back to Dashboard"
            : openedFrom === "transcripts"
              ? "Back to Recorded Meetings"
              : openedFrom === "meetings"
                ? "Back to All Meetings"
                : "Back";
  const effectiveStatus = effectiveMeetingStatus(meeting);
  const meetingUrl = meeting.meetingUrl || meeting.meeting_url || meeting.hangoutLink || joinUrlFromMeeting(meeting) || "";
  const pendingApproval = meeting?.approvalAccess?.required && meeting?.approvalAccess?.status === "pending";
  const pendingApprovalNames = (meeting?.approvalAccess?.approvers || [])
    .filter((approver) => approver.status === "pending")
    .map((approver) => approver.name || approver.authority || approver.email)
    .filter(Boolean);
  const transcriptApprovalNames = pendingApprovalNames.length
    ? pendingApprovalNames.join(", ")
    : meeting?.approvalAccess?.approverName || "the designated authority";
  const isApprovalViewer = meeting?.approvalAccess?.required && (
    meeting?.approvalAccess?.approvers?.some((approver) => String(currentUser?.email || "").trim().toLowerCase() === String(approver.email || "").trim().toLowerCase())
    || String(currentUser?.email || "").trim().toLowerCase() === String(meeting?.approvalAccess?.approverEmail || "").trim().toLowerCase()
  );
  const isAssignedApprover = pendingApproval && isApprovalViewer;
  const [approvalDraft, setApprovalDraft] = useState("");
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");
  const [approvalEditorMessage, setApprovalEditorMessage] = useState("");
  const [approvalRequestSending, setApprovalRequestSending] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editView, setEditView] = useState("edited");
  const [editSection, setEditSection] = useState("overview");
  const [editContent, setEditContent] = useState({ overview: "", transcript: [], recording: null, actions: [] });
  const [approvalDirty, setApprovalDirty] = useState(false);
  const [approvalClosePrompt, setApprovalClosePrompt] = useState(false);
  const [approverMainView, setApproverMainView] = useState("edited");
  const [approvalContextMenu, setApprovalContextMenu] = useState(null);
  const [approvalOverviewHtml, setApprovalOverviewHtml] = useState("");
  const approvalDrawerRef = useRef(null);
  const approvalPointInsertRef = useRef(false);
  const [approvalFormatState, setApprovalFormatState] = useState({ bold: false, italic: false, underline: false, highlight: false });
  const [recipientDrawerOpen, setRecipientDrawerOpen] = useState(false);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const approvalApproverEmails = useMemo(() => {
    const emails = new Set((meeting?.approvalAccess?.approvers || []).map((approver) => String(approver.email || "").trim().toLowerCase()));
    if (meeting?.approvalAccess?.approverEmail) emails.add(String(meeting.approvalAccess.approverEmail).trim().toLowerCase());
    return emails;
  }, [meeting?.approvalAccess?.approvers, meeting?.approvalAccess?.approverEmail]);
  useEffect(() => { if (pendingApproval) setRecipientEmails((meeting?.participants || []).filter((p) => p.email && p.is_bot !== true && !/@meet\.link$/i.test(String(p.email).trim()) && !/recall|bot/i.test(String(p.name || '')) && !approvalApproverEmails.has(String(p.email || "").trim().toLowerCase())).map((p) => String(p.email || '').trim().toLowerCase()).filter(Boolean)); }, [pendingApproval, meeting?.participants, approvalApproverEmails]);
  useEffect(() => { if (editDrawerOpen) { resetApprovalDraft(); window.setTimeout(() => { if (approvalDrawerRef.current) approvalDrawerRef.current.scrollTop = 0; }, 0); } }, [editDrawerOpen]);
  useEffect(() => { if (!pendingApproval) return; const transcript = Array.isArray(meeting?.transcript) ? meeting.transcript.map((row) => ({ ...row })) : []; const actions = Array.isArray(meeting?.actionItems) ? meeting.actionItems.map((row) => ({ ...row })) : []; setApprovalDraft(String(meeting?.editedSummary || meeting?.summary || "")); setApprovalOverviewHtml(String(meeting?.editedSummaryHtml || "")); setEditContent({ overview: String(meeting?.editedSummary || meeting?.summary || ""), transcript, recording: meeting?.recording || null, actions }); }, [pendingApproval, meeting?.id, meeting?.editedSummary, meeting?.editedSummaryHtml]);
  useEffect(() => {
    if (!isAssignedApprover || !meeting?.id || meeting?.editedSummary != null || !onMeetingUpdated) return;
    let cancelled = false;
    apiFetch("/meetings/" + encodeURIComponent(meeting.id))
      .then((fresh) => { if (!cancelled && fresh) onMeetingUpdated(fresh); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAssignedApprover, meeting?.id, meeting?.editedSummary, onMeetingUpdated]);
  const resetApprovalDraft = () => { const transcript = Array.isArray(meeting?.transcript) ? meeting.transcript.map((row) => ({ ...row })) : []; const actions = Array.isArray(meeting?.actionItems) ? meeting.actionItems.map((row) => ({ ...row })) : []; const overview = String(meeting?.editedSummary || meeting?.summary || ""); setApprovalDraft(overview); setApprovalOverviewHtml(String(meeting?.editedSummaryHtml || "")); setEditContent({ overview, transcript, recording: meeting?.recording || null, actions }); setApprovalFormatState({ bold: false, italic: false, underline: false, highlight: false }); setApprovalEditorMessage(""); };
  const updateApprovalContent = (section, value) => { setApprovalDirty(true); setEditContent((old) => ({ ...old, [section]: value })); };
  const updateApprovalRow = (section, index, key, value) => { setApprovalDirty(true); setEditContent((old) => ({ ...old, [section]: (old[section] || []).map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row) })); };
  const syncApprovalOverviewFromDom = () => { const root = approvalDrawerRef.current && approvalDrawerRef.current.querySelector(".avaada-detail-summary-sections"); if (!root) return; const next = Array.from(root.querySelectorAll("section")).map((section) => { const title = section.querySelector("h3")?.innerText || ""; const items = Array.from(section.querySelectorAll("li")).map((li) => "- " + li.innerText.trim()).join("\n"); return title + ":\n" + items; }).join("\n\n"); setApprovalOverviewHtml(root.innerHTML); updateApprovalContent("overview", next); };
  const refreshApprovalFormatState = () => { try { const sel = window.getSelection?.(); const node = sel?.anchorNode; const el = node && (node.nodeType === 1 ? node : node.parentElement); const root = approvalDrawerRef.current?.querySelector(".avaada-detail-summary-sections"); if (!el || !root?.contains(el)) return; const hasAncestor = (selector, styleTest) => { let current = el; while (current && current !== root.parentElement) { if (current.matches?.(selector) || (styleTest && styleTest(current))) return true; current = current.parentElement; } return false; }; const highlighted = hasAncestor("mark, font[style*='background'], span[style*='background']", (current) => { const color = String(current.style?.backgroundColor || current.style?.background || "").toLowerCase(); return Boolean(color && !["transparent", "inherit", "initial"].includes(color)); }); const commandHighlight = String(document.queryCommandValue("hiliteColor") || "").trim().toLowerCase(); const hasCommandHighlight = !!commandHighlight && !["transparent", "rgba(0, 0, 0, 0)", "inherit", "initial"].includes(commandHighlight); setApprovalFormatState({ bold: document.queryCommandState("bold") || hasAncestor("strong, b", (current) => Number.parseInt(current.style?.fontWeight || "0", 10) >= 600 || current.style?.fontWeight === "bold"), italic: document.queryCommandState("italic") || hasAncestor("em, i", (current) => current.style?.fontStyle === "italic"), underline: document.queryCommandState("underline") || hasAncestor("u", (current) => String(current.style?.textDecoration || "").includes("underline")), highlight: highlighted || hasCommandHighlight }); } catch (_) {} };
  useEffect(() => { if (!editDrawerOpen || editView !== "edited") return undefined; const onSelectionChange = () => refreshApprovalFormatState(); document.addEventListener("selectionchange", onSelectionChange); return () => document.removeEventListener("selectionchange", onSelectionChange); }, [editDrawerOpen, editView, editSection]);
  const runApprovalCommand = (command, value = null) => { try { document.execCommand(command, false, value); setApprovalDirty(true); window.setTimeout(refreshApprovalFormatState, 0); } catch (_) {} };
  const insertApprovalPoint = () => { if (approvalPointInsertRef.current) return; const sel = window.getSelection && window.getSelection(); const node = sel && sel.anchorNode; const el = node && (node.nodeType === 1 ? node : node.parentElement); const current = el && el.closest("li"); const root = approvalDrawerRef.current && approvalDrawerRef.current.querySelector(".avaada-detail-summary-sections"); if (!current || !root || !root.contains(current)) { setApprovalEditorMessage("Place the cursor inside an existing point, then choose Add point."); return; } const focusNewPoint = (point) => { point.focus(); const range = document.createRange(); range.selectNodeContents(point); const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range); }; if (current.hasAttribute("data-new-approval-point")) { focusNewPoint(current); return; } approvalPointInsertRef.current = true; setApprovalEditorMessage(""); const point = document.createElement("li"); point.textContent = "New point"; point.contentEditable = "true"; point.setAttribute("data-new-approval-point", "true"); point.style.color = "#94A3B8"; point.style.fontStyle = "italic"; point.addEventListener("focus", () => { if (point.hasAttribute("data-new-approval-point")) focusNewPoint(point); }, { once: true }); point.addEventListener("input", () => { if (point.hasAttribute("data-new-approval-point")) { point.removeAttribute("data-new-approval-point"); point.style.color = ""; point.style.fontStyle = ""; } syncApprovalOverviewFromDom(); }); current.parentElement.insertBefore(point, current.nextSibling); requestAnimationFrame(() => focusNewPoint(point)); approvalPointInsertRef.current = false; }; const openApprovalContextMenu = (event) => { if (editView !== "edited") return; event.preventDefault(); setApprovalContextMenu({ x: event.clientX, y: event.clientY }); };
  const renderApprovalOverview = (value, editable) => {
    const sections = parseMeetingSummarySections(normalizeMeetingSummaryDisplay(value || ""));
    const blocks = [
      { id: "context", title: "Context", items: sections.context },
      { id: "discussion", title: "Key Discussion Points", items: sections.discussion },
      { id: "decisions", title: "Decisions / Outcomes", items: sections.decisions },
      { id: "next-steps", title: "Next Steps", items: [...sections.actions, ...sections.presented] },
    ].filter((b) => b.items.length);
    const syncOnBlur = (event) => {
      const root = event.currentTarget.closest(".avaada-detail-summary-sections");
      if (!root) return;
      setApprovalOverviewHtml(root.innerHTML);
      updateApprovalContent("overview", Array.from(root.querySelectorAll("section")).map((section) => `${section.querySelector("h3")?.innerText || ""}:\n${Array.from(section.querySelectorAll("li")).map((li) => `- ${li.innerText}`).join("\n")}`).join("\n\n"));
    };
    return <div className="avaada-detail-summary-sections">{blocks.map((block) => {
      const icon = block.id === "discussion" || block.id === "decisions" ? IC.check : block.id === "next-steps" ? IC.listChecks : IC.info;
      const tone = block.id === "discussion" || block.id === "decisions" ? "is-success" : "is-info";
      return <section key={block.id} className="avaada-detail-summary-section"><h3 className="avaada-detail-summary-section__title" contentEditable={editable} suppressContentEditableWarning={editable} onBlur={editable ? syncOnBlur : undefined}><span className={`avaada-detail-summary-section__icon ${tone}`} aria-hidden><Svg d={icon} size={13} color="currentColor" stroke={2.2} /></span>{block.title}</h3><ul className="avaada-detail-summary-section__list">{block.items.map((item, index) => {
        const isNewPoint = editable && String(item).trim() === "New point";
        return <li key={index} contentEditable={editable} suppressContentEditableWarning={editable} style={isNewPoint ? { color: "#94A3B8", fontStyle: "italic" } : undefined} onFocus={isNewPoint ? (event) => { const range = document.createRange(); range.selectNodeContents(event.currentTarget); const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range); } : undefined} onBlur={editable ? syncOnBlur : undefined}>{item}</li>;
      })}</ul></section>;
    })}</div>;
  };
  const getApprovalOverviewHtml = () => { const root = approvalDrawerRef.current; const nodes = root ? root.querySelectorAll(".avaada-approval-editor-card .avaada-detail-summary-sections") : []; return nodes.length ? nodes[nodes.length - 1].innerHTML : approvalOverviewHtml; };
  const saveApprovalEdit = async () => { setApprovalSaving(true); setApprovalMessage(""); try { const overviewHtml = getApprovalOverviewHtml(); setApprovalOverviewHtml(overviewHtml); await apiFetch(`/meetings/${encodeURIComponent(meeting.id ?? meeting._id)}/approval-edit`, { method: "POST", body: { summary: editContent.overview, content: { overviewHtml, transcript: editContent.transcript, recording: editContent.recording, actionItems: editContent.actions } } }); setApprovalDraft(editContent.overview); setApprovalDirty(false); setApprovalMessage("Changes saved."); const refreshed = await apiFetch("/meetings/" + encodeURIComponent(meeting.id ?? meeting._id)); await onMeetingUpdated?.(refreshed); } catch (e) { setApprovalMessage(e.message || "Could not save changes."); } finally { setApprovalSaving(false); } };
  const requestCloseApprovalDrawer = () => { if (approvalDirty) setApprovalClosePrompt(true); else setEditDrawerOpen(false); };
  const discardApprovalDraft = () => { resetApprovalDraft(); setApprovalDirty(false); setApprovalClosePrompt(false); setEditDrawerOpen(false); };
  const approvalRecipients = (meeting?.participants || []).filter((p) => p.email && p.is_bot !== true && !/@meet\.link$/i.test(String(p.email).trim()) && !/recall|bot/i.test(String(p.name || ''))).map((p) => ({ ...p, email: String(p.email).trim().toLowerCase(), isApprover: approvalApproverEmails.has(String(p.email).trim().toLowerCase()) })).filter((p, i, a) => a.findIndex((x) => x.email === p.email || (x.name && p.name && String(x.name).trim().toLowerCase() === String(p.name).trim().toLowerCase())) === i);
  const selectableApprovalRecipients = approvalRecipients.filter((p) => !p.isApprover);
  const allApprovalRecipientsSelected = selectableApprovalRecipients.length > 0 && selectableApprovalRecipients.every((p) => recipientEmails.includes(p.email));
  const toggleAllApprovalRecipients = () => setRecipientEmails(allApprovalRecipientsSelected ? [] : selectableApprovalRecipients.map((p) => p.email));
  const approveThisMeeting = async () => { setApprovalSaving(true); setApprovalMessage(""); try { const overviewHtml = getApprovalOverviewHtml(); const result = await apiFetch(`/meetings/${encodeURIComponent(meeting.id ?? meeting._id)}/approval-approve`, { method: "POST", body: { summary: editContent.overview, content: { overviewHtml, transcript: editContent.transcript, recording: editContent.recording, actionItems: editContent.actions }, recipientEmails } }); if (result?.status !== "approved") throw new Error(result?.message || "Approval was not completed. Please try again."); const refreshed = await apiFetch("/meetings/" + encodeURIComponent(meeting.id ?? meeting._id)); const refreshedStatus = String(refreshed?.approvalAccess?.status || refreshed?.approvalStatus || "").toLowerCase(); if (refreshedStatus && !["approved", "available"].includes(refreshedStatus)) throw new Error("The server did not confirm approval. Your changes were not published."); setApprovalDirty(false); setRecipientDrawerOpen(false); setApprovalMessage("Meeting is now available. Refreshing access…"); await onMeetingUpdated?.(refreshed); } catch (e) { setApprovalMessage(e.message || "Could not approve meeting."); } finally { setApprovalSaving(false); } };
  const requestMeetingApproval = async () => {
    setApprovalRequestSending(true);
    setApprovalMessage("");
    try {
      const result = await apiFetch(`/meetings/${encodeURIComponent(meeting.id ?? meeting._id)}/approval-request`, { method: "POST" });
      setApprovalMessage(result?.message || (result?.status === "sent" ? "Approval request sent to all assigned approvers." : "Could not send approval request."));
      await onMeetingUpdated?.(await apiFetch("/meetings/" + encodeURIComponent(meeting.id ?? meeting._id)));
    } catch (e) {
      setApprovalMessage(e.message || "Could not send approval request.");
    } finally {
      setApprovalRequestSending(false);
    }
  };
  const supersededSibling = rescheduleInfo?.direction === "to" ? rescheduleInfo.siblingMeeting : null;
  const supersededSiblingStatus = supersededSibling ? effectiveMeetingStatus(supersededSibling) : null;
  const supersededSiblingUrl = supersededSibling
    ? (supersededSibling.meetingUrl ||
        supersededSibling.meeting_url ||
        supersededSibling.hangoutLink ||
        joinUrlFromMeeting(supersededSibling) ||
        "")
    : "";
  const canJoinThisSlot =
    effectiveStatus !== "cancelled" &&
    effectiveStatus !== "ended" &&
    effectiveStatus !== "completed" &&
    !!meetingUrl &&
    /^https?:\/\//i.test(meetingUrl);
  const canJoinViaRescheduleSibling =
    !canJoinThisSlot &&
    supersededSibling &&
    (supersededSiblingStatus === "live" || supersededSiblingStatus === "upcoming") &&
    !!supersededSiblingUrl &&
    /^https?:\/\//i.test(supersededSiblingUrl);
  const canJoinMeeting = canJoinThisSlot || canJoinViaRescheduleSibling;
  const activeJoinUrl = canJoinThisSlot ? meetingUrl : canJoinViaRescheduleSibling ? supersededSiblingUrl : meetingUrl;
  const [linkCopied, setLinkCopied] = useState(false);
  const [transcriptPreviewRequest, setTranscriptPreviewRequest] = useState(0);
  const handleViewAllParticipants = useCallback(() => {
    if (referenceDetail) setActiveTab("overview");
    setParticipantsExpanded(true);
    window.setTimeout(() => {
      document.getElementById("detail-participants-attendance")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, [referenceDetail, setActiveTab]);
  const handleCopyLink = useCallback(() => {
    if (!meetingUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(meetingUrl).then(() => {
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 1600);
        }).catch(() => {});
      }
    } catch (_) {}
  }, [meetingUrl]);
  const [shareDialogScope, setShareDialogScope] = useState(null);
  // Live tick — re-renders every 30s so the "Starts in / Ended X min ago" chip stays fresh.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const timeChip = meetingRelativeTimeChip(meeting, nowTick);
  const rescheduleSlotsCollected = useMemo(
    () => rescheduleSlotsForDetailView(meeting, rescheduleSources || {}),
    [meeting, rescheduleSources]
  );
  const rescheduleTimeline = useMemo(
    () => orderRescheduleTimelineForDisplay(meeting, rescheduleSlotsCollected),
    [meeting, rescheduleSlotsCollected]
  );
  const headerDisplaySchedule = useMemo(
    () => meetingLatestDisplaySchedule(
      { ...meeting, rescheduleTimeline: rescheduleSlotsCollected },
      rescheduleSources
    ),
    [meeting, rescheduleSlotsCollected, rescheduleSources]
  );
  const headerTimeRange =
    headerDisplaySchedule.range ||
    meetingScheduleRangeLabel({
      ...meeting,
      time: headerDisplaySchedule.time || meeting.time,
    });
  const headerRescheduleLine =
    headerDisplaySchedule.chainNoteCompact ||
    headerDisplaySchedule.chainNote ||
    "";
  const rescheduleChangeCount = rescheduleTimeline ? Math.max(0, rescheduleTimeline.length - 1) : 0;
  const pl=PLATFORM_META[meeting.platform]||PLATFORM_META.meet;
  const st=STATUS_META[effectiveStatus]||STATUS_META.upcoming;
  const isOwner =
    currentUser?.id != null &&
    meeting?.owner_id != null &&
    String(meeting.owner_id) === String(currentUser.id);
  const guestRowsForEmail = humanParticipants(meeting.participants || []).filter(
    (p) =>
      !participantIsBot(p) &&
      isGuestMeetLinkEmail(p.email) &&
      !guestMeetLinkRowAbsorbedByCalendarParticipant(
        p,
        meeting.participants || [],
        attendanceContextFromMeeting(meeting)
      )
  );
  const googleEventId = String(meeting?.googleEventId || "").trim();
  const showAutoJoin =
    meeting?.type === "google_calendar" &&
    !!googleEventId &&
    effectiveStatus !== "cancelled" &&
    (effectiveStatus === "upcoming" || effectiveStatus === "live");
  const autoJoinSaving = !!meetingAutoJoinSaving?.[googleEventId];
  const autoJoinOn = meeting?.autoJoinEnabled === true;
  const recordingSaving = !!meetingRecordingSaving?.[meetingRecordingApiId(meeting)];
  const recordingOn = effectiveRecordingEnabled(meeting);
  const hasTranscript = meetingHasTranscriptData(meeting);
  const hasTranscriptLines = Array.isArray(meeting?.transcript) && meeting.transcript.length > 0;
  const recordingReadyDetail = meetingRecordingFileReady(meeting, { requireAutoJoin: false });
  const meetingDbIdForRec = meeting.id ?? meeting._id;
  const attendanceContext = attendanceContextFromMeeting(meeting);
  const rosterPeople = participantsForMeetingDetailChips(meeting.participants || [], attendanceContext);
  const { joined: rosterJoined, notJoined: rosterNotJoined } = splitJoinedVsNotJoinedInvitees(
    meeting.participants || [],
    attendanceContext
  );
  const showBotJoinChip = isEndedMeetingStatus(effectiveStatus, meeting);
  const botJoinedCall = meetingBotJoinedCall(meeting);
  const meetingDbIdRaw = String(meeting?.id ?? meeting?._id ?? "").trim();
  const meetingDbId = isUuidLike(meetingDbIdRaw) ? meetingDbIdRaw : "";
  const meetingPollId = meetingDbId || meetingDbIdRaw;
  const presStatus = normalizePresentationSummaryStatus(
    meeting?.presentationSummaryStatus || meeting?.presentation_summary_status
  );

  useEffect(() => {
    if (!meetingPollId || !onMeetingUpdated) return undefined;
    const shouldPollLive =
      effectiveStatus === "live" ||
      effectiveStatus === "ended" ||
      (effectiveStatus !== "completed" &&
        effectiveStatus !== "cancelled" &&
        !hasTranscriptLines &&
        (meetingBotJoinedCall(meeting) || hasTranscript));
    const shouldPollPres = presStatus === "processing";
    if (!shouldPollLive && !shouldPollPres) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        // While Live, force a Recall lifecycle check so kick/end flips status quickly.
        if (meetingDbId && (effectiveStatus === "live" || effectiveStatus === "ended")) {
          const synced = await apiFetch(
            `/meetings/${encodeURIComponent(meetingPollId)}/sync-bot-status`,
            { method: "POST", body: {} }
          );
          if (!cancelled && synced?.meeting && meetingDetailMatchesOpened(meeting, synced.meeting)) {
            onMeetingUpdated(synced.meeting);
            return;
          }
        }
        const m = await apiFetch(`/meetings/${encodeURIComponent(meetingPollId)}`);
        if (!cancelled && m && meetingDetailMatchesOpened(meeting, m)) onMeetingUpdated(m);
      } catch (_) {
        try {
          const m = await apiFetch(`/meetings/${encodeURIComponent(meetingPollId)}`);
          if (!cancelled && m && meetingDetailMatchesOpened(meeting, m)) onMeetingUpdated(m);
        } catch (__) {}
      }
    };
    poll();
    const ms = effectiveStatus === "live" ? 4000 : 10000;
    const id = setInterval(poll, ms);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [
    meetingPollId,
    meetingDbId,
    effectiveStatus,
    presStatus,
    hasTranscriptLines,
    onMeetingUpdated,
    meeting?.bot_join_status_code,
    meeting?.transcriptRowCount,
    meeting?.id,
    meeting?.date,
    meeting?.time,
    meeting?.title,
  ]);

  const detailActionItemCount = (meeting.actionItems || []).length;
  const detailRecordingCount = useMemo(() => {
    const extras = meeting?.recordings || meeting?.recordingFiles || meeting?.videoRecordings;
    if (Array.isArray(extras) && extras.length > 0) return extras.length;
    return meetingRecordingStoredOn(meeting) ? 1 : 0;
  }, [meeting]);

  const detailTabs = useMemo(
    () => [
      { id: "overview", label: "Overview", icon: IC.layoutDashboard, badge: null },
      { id: "transcript", label: "Transcript", icon: IC.messageSquare, badge: null },
      {
        id: "recording",
        label: "Recording",
        icon: IC.play,
        badge: detailRecordingCount > 1 ? detailRecordingCount : null,
      },
      {
        id: "action-items",
        label: "Action Items",
        icon: IC.listChecks,
        badge: detailActionItemCount > 0 ? detailActionItemCount : null,
      },
    ],
    [detailActionItemCount, detailRecordingCount]
  );

  return (
    <div className={`page app-page-shell${referenceDetail ? " avaada-meeting-detail" : ""}${openedFrom === "meetings" ? " avaada-meeting-detail--meetings" : ""}`}>
      <button type="button" className="link-back" onClick={onBack}>
        <Svg d={IC.chevL} size={14}/> {backLabel}
      </button>

      <div
        className={`content-well${referenceDetail ? " avaada-meeting-detail__content-well" : ""}`}
        style={referenceDetail ? undefined : { width: "100%", maxWidth: 900, margin: "0 auto" }}
      >
      <div
        className={`card${referenceDetail ? " avaada-meeting-detail__header-card" : ""}`}
        style={
          referenceDetail
            ? undefined
            : {
                padding: "20px 22px",
                marginBottom: 14,
                background: "linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%)",
                borderColor: "rgba(12,43,78,0.12)",
                boxShadow: "0 4px 18px rgba(12,43,78,0.06)",
              }
        }
      >
        <div
          className={referenceDetail ? "avaada-meeting-detail__header-top" : undefined}
          style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:18,flexWrap:"wrap",marginBottom:referenceDetail ? 8 : 16}}
        >
          <div
            className={referenceDetail ? "avaada-meeting-detail__header-main" : undefined}
            style={{display:"flex",alignItems:"center",gap:14,minWidth:0,flex:"1 1 420px"}}
          >
            {referenceDetail ? (
              <div className="avaada-detail-platform-icon">
                <MeetListRowPlatformIcon meeting={meeting} detail />
              </div>
            ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: meetingIsInstantJoin(meeting) ? "transparent" : pl.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 20,
                color: pl.color,
                flexShrink: 0,
                position: "relative",
              }}
            >
              {meetingIsInstantJoin(meeting) ? (
                <MeetListRowPlatformIcon meeting={meeting} detail />
              ) : meeting.platform === "gcal" || meeting.platform === "meet" ? (
                <Svg d={IC.cal} color={pl.color} size={24} stroke={2} />
              ) : (
                pl.short
              )}
            </div>
            )}
            <div style={{minWidth:0}}>
              <div className={referenceDetail ? "avaada-meeting-detail__title-row" : undefined} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                <h1 className={referenceDetail ? "avaada-meeting-detail__title" : undefined} style={referenceDetail ? undefined : {fontSize:18,fontWeight:600,letterSpacing:-.2}}>{meeting.title}</h1>
                {meetingIsInstantJoin(meeting) ? <InstantJoinLabelChip compact /> : null}
              </div>
              <div className={referenceDetail ? "avaada-detail-meta" : undefined} style={referenceDetail ? undefined : {fontSize:13,color:T.textMuted,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                {referenceDetail ? (
                  <>
                    <span className="avaada-detail-meta__item">
                      <Svg d={IC.cal} size={14} color="#5F7690" stroke={2} />
                      {formatDetailHeaderDate(meeting.date)}
                    </span>
                    <span
                      className="avaada-detail-meta__item"
                      title={headerDisplaySchedule.rescheduled ? "Latest scheduled time" : undefined}
                    >
                      <Svg d={IC.clock} size={14} color={headerDisplaySchedule.rescheduled ? "#CA8A04" : "#5F7690"} stroke={2} />
                      <span className={headerDisplaySchedule.rescheduled ? "meet-list-time-rescheduled" : undefined}>
                        {headerTimeRange}
                        {(() => {
                          const mins = meetingDurationMinutes({
                            ...meeting,
                            time: headerDisplaySchedule.time || meeting.time,
                          });
                          return Number.isFinite(mins) && mins > 0 ? ` (${mins} mins)` : "";
                        })()}
                      </span>
                    </span>
                    {meetingHasDisplayableHost(meeting) ? (
                      <>
                        <span className="avaada-detail-meta__sep" aria-hidden />
                        <span className="avaada-detail-host-row">
                          <Avatar initials={meetingHostInitials(meeting)} size={22} color="#64748b" />
                          <span>
                            Host: <strong>{meetingHostName(meeting)}</strong>
                          </span>
                        </span>
                      </>
                    ) : null}
                    <span className="avaada-detail-platform-name">
                      {meeting.platform === "gcal" || meeting.platform === "meet"
                        ? "Google Meet"
                        : meetingIsInstantJoin(meeting)
                          ? "Instant join"
                          : (pl.label || "")}
                    </span>
                  </>
                ) : (
                  <>
                    <span>{formatYmdDisplay(meeting.date)}</span><span style={{color:T.border}}>·</span>
                    <span
                      className={headerDisplaySchedule.rescheduled ? "meet-list-time-rescheduled" : undefined}
                      style={
                        headerDisplaySchedule.rescheduled
                          ? { color: "#CA8A04", fontWeight: 700 }
                          : undefined
                      }
                      title={headerDisplaySchedule.rescheduled ? "Latest scheduled time" : undefined}
                    >
                      {headerTimeRange}
                    </span>
                    {(() => {
                      const durLabel = meetingDetailMetaDurationLabel(meeting, effectiveStatus);
                      if (!durLabel) return null;
                      return (
                        <>
                          <span style={{ color: T.border }}>·</span>
                          <span>{durLabel}</span>
                        </>
                      );
                    })()}
                    {meetingHasDisplayableHost(meeting) ? (
                      <>
                        <span style={{color:T.border}}>·</span>
                        <span>Host: {meetingHostName(meeting)}</span>
                      </>
                    ) : null}
                  </>
                )}
                {headerRescheduleLine && !rescheduleTimeline ? (
                  <RescheduleChip
                    line={headerDisplaySchedule.chainNote || headerRescheduleLine}
                    style={{ marginLeft: 2 }}
                  />
                ) : null}
                {!referenceDetail && meeting.platform !== "gcal" && meeting.platform !== "meet" ? (
                  <PlatformChip platform={meeting.platform} />
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={referenceDetail ? "avaada-meeting-detail__header-aside" : undefined}
            style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}
          >
            {showBotJoinChip ? (() => {
              const reasonLong = botJoinedCall ? "" : getMeetingBotJoinReasonLong(meeting);
              return referenceDetail ? (
                <span
                  className={`avaada-detail-status-chip${botJoinedCall ? " is-success" : " is-muted"}`}
                  title={
                    botJoinedCall
                      ? "Meeting completed and MeetAI joined"
                      : reasonLong || "MeetAI did not join this meeting"
                  }
                >
                  <Svg d={botJoinedCall ? IC.check : IC.close} size={11} color="currentColor" stroke={2.2} />
                  {botJoinedCall ? "MeetAI joined" : "MeetAI didn't join"}
                </span>
              ) : (
                <span
                  title={
                    botJoinedCall
                      ? "Meeting completed and MeetAI joined"
                      : reasonLong || "MeetAI did not join this meeting"
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 9px",
                    borderRadius: 999,
                    border: `1px solid ${botJoinedCall ? "#D1EBDD" : "#F1DFC1"}`,
                    background: botJoinedCall ? "#F1FAF4" : "#FFF8EB",
                    color: botJoinedCall ? "#237A4B" : "#A16207",
                    fontSize: 10.5,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Svg d={botJoinedCall ? IC.check : IC.close} size={11} color="currentColor" stroke={2.2} />
                  {st.label} · {botJoinedCall ? "MeetAI joined" : "MeetAI didn't join"}
                </span>
              );
            })() : (
              <Badge label={st.label} bg={st.bg} color={st.fg} dot={st.dot}/>
            )}
            {timeChip && (
              <span className={`time-chip time-chip-${timeChip.tone}`}>
                {timeChip.tone === "live" && <span className="time-chip-dot" />}
                {timeChip.tone === "imminent" && <span className="time-chip-dot" style={{ background: "#D97706" }} />}
                <Svg
                  d={timeChip.tone === "live" || timeChip.tone === "imminent" ? IC.zap : IC.clock}
                  size={11}
                  color="currentColor"
                  stroke={2.1}
                />
                {timeChip.label}
              </span>
            )}
            {!rescheduleTimeline && rescheduleInfo ? (
              <span
                className={referenceDetail ? "avaada-detail-reschedule-chip" : undefined}
                title={
                  rescheduleInfo.direction === "to"
                    ? "This meeting was moved — the current session is at the time shown."
                    : "This meeting was moved from an earlier time."
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 9px",
                  borderRadius: referenceDetail ? 4 : 999,
                  border: "1px solid #F1DFC1",
                  background: "#FFF8EB",
                  color: "#A16207",
                  fontSize: 10.5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <Svg d={IC.clock} size={11} color="currentColor" stroke={2.1} />
                {rescheduleBadgeLabel(rescheduleInfo)}
              </span>
            ) : null}
          </div>
        </div>

        {rescheduleTimeline ? (
          <div
            className={referenceDetail ? "avaada-detail-reschedule-panel" : undefined}
            style={{
              marginTop: 12,
              padding: "12px 14px",
              borderRadius: referenceDetail ? 4 : 12,
              border: "1px solid #F1DFC1",
              background: "#FFFBF5",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#92400E",
                }}
              >
                <Svg d={IC.clock} size={12} color="#B45309" stroke={2.1} />
                Schedule changes
              </div>
              {rescheduleChangeCount > 0 ? (
                <span style={{ fontSize: 10.5, fontWeight: 600, color: "#B45309", whiteSpace: "nowrap" }}>
                  {rescheduleChangeCount} previous time{rescheduleChangeCount === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>
            <ul className="meeting-schedule-slot-list">
              {rescheduleTimeline.map((slot, idx) => (
                <li
                  key={`${slot.meetingId || slot.ms || idx}-${slot.range || slot.time}`}
                  className={`meeting-schedule-slot${slot.isCurrent ? " meeting-schedule-slot--current" : " meeting-schedule-slot--prior"}`}
                  title={
                    slot.isCurrent
                      ? `Latest scheduled time: ${slot.range || slot.time}`
                      : `Earlier invite: ${slot.range || slot.time}`
                  }
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: slot.isCurrent ? "#92400E" : T.textMuted,
                        fontWeight: slot.isCurrent ? 700 : 500,
                      }}
                    >
                      {formatYmdDisplay(slot.date)} · {slot.range || slot.time}
                    </div>
                    {!slot.isCurrent ? (
                      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>
                        Original invite{rescheduleTimeline.length > 2 ? ` ${idx}` : ""}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: "#B45309", marginTop: 2, fontWeight: 600 }}>
                        Meeting time (rescheduled)
                      </div>
                    )}
                  </div>
                  {slot.isCurrent ? (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "#FEF3C7",
                        color: "#B45309",
                        flexShrink: 0,
                      }}
                    >
                      Current time
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#9CA3AF",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Original time
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(hasTranscript || meetingUrl) ? (
          referenceDetail ? (
            <div
              className="avaada-detail-action-row"
              style={{ position: "relative", width: "100%" }}
            >
              <div className="avaada-detail-action-strip">
                {!joinOnly && (hasTranscript || (botJoinedCall && isEndedMeetingStatus(effectiveStatus, meeting))) ? (
                  <button
                    type="button"
                    className="avaada-mui-btn avaada-mui-btn--primary"
                    onClick={() => {
                      setActiveTab("transcript");
                      setTranscriptPreviewRequest((value) => value + 1);
                    }}
                  >
                    <Svg d={IC.mic} size={14} color="currentColor" stroke={1.9} />
                    Preview transcript
                  </button>
                ) : null}
                {recordingReadyDetail ? (
                  <button
                    type="button"
                    className="avaada-mui-btn avaada-mui-btn--recording"
                    onClick={() => {
                      setActiveTab("recording");
                      window.setTimeout(() => {
                        document.getElementById("detail-section-recording")?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 100);
                    }}
                  >
                    <Svg d={IC.video} size={14} color="currentColor" stroke={1.9} />
                    Recorded video available
                  </button>
                ) : null}
                {meetingUrl ? (
                  <button type="button" className="avaada-mui-btn avaada-mui-btn--outlined" onClick={handleCopyLink}>
                    <Svg d={linkCopied ? IC.check : IC.link} size={14} color="currentColor" stroke={1.9} />
                    {linkCopied ? "Copied" : "Copy link"}
                  </button>
                ) : null}
                {canJoinMeeting ? (
                  <a
                    href={activeJoinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="avaada-mui-btn avaada-mui-btn--primary"
                    title={activeJoinUrl}
                  >
                    <Svg d={IC.launch} size={14} color="currentColor" stroke={1.9} />
                    {canJoinViaRescheduleSibling
                      ? `Join at ${rescheduleInfo.time || "new time"}`
                      : effectiveStatus === "live"
                        ? "Join live meeting"
                        : "Join meeting"}
                  </a>
                ) : meetingUrl && !canJoinMeeting ? (
                  <span
                    className="avaada-detail-ended-status"
                    role="status"
                    title={
                      effectiveStatus === "cancelled"
                        ? "This meeting was cancelled"
                        : "This meeting has ended — use Copy link if you still need the URL"
                    }
                  >
                    <Svg d={IC.clock} size={14} color="currentColor" stroke={1.9} />
                    {effectiveStatus === "cancelled" ? "Cancelled" : "Meeting ended"}
                  </span>
                ) : null}
                {recordingReadyDetail && meetingDbIdForRec ? (
                  <button
                    type="button"
                    className="avaada-mui-btn avaada-mui-btn--outlined"
                    onClick={() =>
                      window.open(meetingRecordingBrandedUrl(meetingDbIdForRec, { download: true }), "_blank", "noopener,noreferrer")
                    }
                  >
                    <Svg d={IC.download} size={14} color="currentColor" stroke={1.9} />
                    Download recording
                  </button>
                ) : null}
              </div>
              {showAutoJoin ? (
                <div
                  className="avaada-detail-header-toggles"
                  style={{ position: "absolute", top: 0, right: 0, marginLeft: 0 }}
                >
                  <div className="avaada-detail-toggle">
                    <ToggleBar
                      checked={autoJoinOn}
                      disabled={autoJoinSaving}
                      label={JOIN_MEET_AI_LABEL}
                      switchWidth={36}
                      switchHeight={20}
                      thumbSize={16}
                      labelStyle={{ fontSize: 13, fontWeight: 700, color: "#0B2A4A" }}
                      onChange={(next) => onToggleMeetingAutoJoin?.(googleEventId, next, meeting)}
                    />
                    <span className="avaada-detail-toggle__hint">Allow MeetAI to join this meeting</span>
                  </div>
                  <div className="avaada-detail-toggle">
                    <ToggleBar
                      checked={recordingOn}
                      disabled={recordingSaving || !autoJoinOn}
                      label="Video Rec"
                      title={!autoJoinOn ? `Turn on ${JOIN_MEET_AI_LABEL} first` : "Video Rec"}
                      switchWidth={36}
                      switchHeight={20}
                      thumbSize={16}
                      labelStyle={{ fontSize: 13, fontWeight: 700, color: "#0B2A4A" }}
                      onChange={(next) => {
                        if (next && !autoJoinOn) return;
                        onToggleMeetingRecording?.(meeting, next);
                      }}
                    />
                    <span className="avaada-detail-toggle__hint">Record the meeting video</span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              paddingTop: 14,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            {!joinOnly &&
            (hasTranscript || (botJoinedCall && isEndedMeetingStatus(effectiveStatus, meeting))) ? (
              <button
                type="button"
                onClick={() => {
                  setActiveTab("transcript");
                  setTranscriptPreviewRequest((value) => value + 1);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  minHeight: 34,
                  padding: "7px 12px",
                  borderRadius: 8,
                  border: "1px solid #172554",
                  background: "#172554",
                  color: "#FFFFFF",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <Svg d={IC.mic} size={14} color="currentColor" stroke={2} />
                Preview transcript
              </button>
            ) : !joinOnly && effectiveStatus === "live" ? (
              <span
                title={
                  meetingBotJoinedCall(meeting)
                    ? "Transcript lines will appear here as people speak. This page refreshes automatically."
                    : "MeetAI is joining the call. Transcript appears after MeetAI is admitted and speech starts."
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  minHeight: 34,
                  padding: "7px 12px",
                  borderRadius: 8,
                  border: "1px solid #BBCFEE",
                  background: "#E3EEFF",
                  color: "#1565C0",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#1565C0",
                    display: "inline-block",
                    animation: "pulse 1.4s ease infinite",
                  }}
                />
                {meetingBotJoinedCall(meeting) ? "Live transcript updating…" : "Waiting for MeetAI to join…"}
              </span>
            ) : null}
            {meetingUrl ? (
              <>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  title={linkCopied ? "Copied!" : "Copy meeting link"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    minHeight: 34,
                    padding: "7px 11px",
                    borderRadius: 8,
                    border: `1px solid ${T.border}`,
                    background: "#FFFFFF",
                    color: T.textSub,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <Svg d={linkCopied ? IC.check : IC.link} size={14} color={linkCopied ? "#15803D" : "currentColor"} stroke={2} />
                  {linkCopied ? "Copied" : "Copy link"}
                </button>
                {canJoinMeeting ? (
                  <a
                    href={activeJoinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={activeJoinUrl}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      minHeight: 34,
                      padding: "7px 11px",
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: "#FFFFFF",
                      color: T.textSub,
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <Svg d={IC.launch} size={13} color="currentColor" stroke={2} />
                    {canJoinViaRescheduleSibling
                      ? `Join at ${rescheduleInfo.time || "new time"}`
                      : effectiveStatus === "live"
                        ? "Join live meeting"
                        : "Join meeting"}
                  </a>
                ) : (
                  <span
                    title={
                      effectiveStatus === "cancelled"
                        ? "This meeting was cancelled"
                        : "This meeting has ended — link is for reference only (use Copy link)"
                    }
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      minHeight: 34,
                      padding: "7px 11px",
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: "#F8FAFC",
                      color: T.textMuted,
                      fontSize: 12,
                      fontWeight: 600,
                      boxSizing: "border-box",
                      cursor: "default",
                    }}
                  >
                    <Svg d={IC.clock} size={13} color="currentColor" stroke={2} />
                    {effectiveStatus === "cancelled" ? "Cancelled" : "Meeting ended"}
                  </span>
                )}
              </>
            ) : null}
          </div>
          )
        ) : null}

        {showAutoJoin && !referenceDetail ? (
          <div
            style={{
              marginTop: meetingUrl ? 12 : 0,
              marginBottom: 16,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 20,
            }}
          >
            <ToggleBar
              checked={autoJoinOn}
              disabled={autoJoinSaving}
              label={autoJoinSaving ? "Saving…" : autoJoinOn ? `${JOIN_MEET_AI_LABEL} on` : `${JOIN_MEET_AI_LABEL} off`}
              onChange={(next) => onToggleMeetingAutoJoin?.(googleEventId, next, meeting)}
            />
            <ToggleBar
              checked={recordingOn}
              disabled={recordingSaving || !autoJoinOn}
              label={
                recordingSaving
                  ? "Saving…"
                  : recordingOn
                    ? "Video Rec on"
                    : "Video Rec off"
              }
              title={!autoJoinOn ? `Turn on ${JOIN_MEET_AI_LABEL} first to enable Video Rec` : "Video Rec"}
              onChange={(next) => {
                if (next && !autoJoinOn) return;
                onToggleMeetingRecording?.(meeting, next);
              }}
            />
          </div>
        ) : null}
      </div>

      {referenceDetail && (rosterPeople.length > 0 || meeting?.approvalAccess?.required) ? (
        <MeetingDetailParticipantBar
          people={rosterPeople}
          meeting={meeting}
          onViewAll={handleViewAllParticipants}
          onRequestApproval={requestMeetingApproval}
          approvalRequestSending={approvalRequestSending}
          approvalMessage={approvalMessage}
          hidePendingApprovalStatus={isAssignedApprover}
        />
      ) : null}

      <div className={referenceDetail ? "avaada-meeting-detail__grid avaada-meeting-detail__grid--full" : undefined}>
      <div className={referenceDetail ? "avaada-meeting-detail__main" : undefined}>

              {referenceDetail && meeting?.approvalAccess?.required && isAssignedApprover ? (
          <div role="status" style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14, padding: "16px 18px", borderRadius: 12, border: "1px solid #93C5FD", background: "#EFF6FF", color: "#1E3A8A" }}>
            <span aria-hidden style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 10, background: "#DBEAFE", color: "#0875E1", flex: "0 0 auto" }}>
              <LockClockOutlinedIcon sx={{ fontSize: 18, color: "currentColor" }} />
            </span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 5 }}>Waiting for approval from {(meeting.approvalAccess.approvers || []).filter((approver) => approver.status === "pending").map((approver) => approver.name || approver.authority || approver.email).filter(Boolean).join(", ") || meeting.approvalAccess.approverName || "the designated authority"}</div>
              <div style={{ fontSize: 13, lineHeight: 1.55 }}>{meeting.approvalAccess.lockedMessage || "Transcript, overview, recording, and action items are waiting for approval. Please wait until the designated authority approves this meeting. Thank you for your patience."}</div>
              {isAssignedApprover ? (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #BFDBFE" }}>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <button type="button" onClick={() => setEditDrawerOpen(true)} disabled={approvalSaving} style={{ border: "1px solid #0875E1", borderRadius: 7, padding: "8px 12px", background: "#FFFFFF", color: "#0875E1", fontWeight: 700 }}>Edit</button>
                    <button type="button" onClick={() => setRecipientDrawerOpen(true)} disabled={approvalSaving} style={{ border: 0, borderRadius: 7, padding: "9px 14px", background: "#0875E1", color: "#FFFFFF", fontWeight: 700 }}>Approve meeting</button>
                    {approvalMessage ? <span style={{ fontSize: 12, color: "#1D4ED8" }}>{approvalMessage}</span> : null}
                  </div>
                  {editDrawerOpen ? createPortal(<div role="dialog" aria-label="Edit meeting approval content" style={{ position: "fixed", inset: 0, zIndex: 2147483646, background: "rgba(15,23,42,.32)" }}><div className="avaada-approval-drawer" ref={approvalDrawerRef} onContextMenu={openApprovalContextMenu} style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "min(760px, 94vw)", background: "#FFFFFF", boxShadow: "-10px 0 34px rgba(15,23,42,.22)", padding: 28, overflowY: "auto", overflowX: "hidden", boxSizing: "border-box" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}><div><div style={{ fontSize: 11, letterSpacing: ".08em", color: "#64748B", fontWeight: 800 }}>APPROVER EDIT MODE</div><h2 style={{ margin: "4px 0 0", color: "#0F2747" }}>{meeting.title || "Meeting"}</h2></div><button type="button" onClick={requestCloseApprovalDrawer} style={{ border: "1px solid #CBD5E1", background: "#FFF", borderRadius: 8, padding: "8px 12px" }}>Close</button></div>
                  {approvalClosePrompt ? <div role="alertdialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 31000, background: "rgba(15,23,42,.38)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}><div style={{ width: "min(430px, 94vw)", background: "white", borderRadius: 12, padding: 24, boxShadow: "0 18px 50px rgba(15,23,42,.28)" }}><h3>Save your changes?</h3><p>You have unsaved approval edits. Save a draft, discard changes, or continue editing.</p><div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}><button className="avaada-approval-btn avaada-approval-btn--neutral" type="button" onClick={() => setApprovalClosePrompt(false)}>Cancel</button><button className="avaada-approval-btn avaada-approval-btn--danger" type="button" onClick={discardApprovalDraft}>Discard changes</button><button className="avaada-approval-btn avaada-approval-btn--primary" type="button" onClick={async () => { setApprovalClosePrompt(false); await saveApprovalEdit(); setEditDrawerOpen(false); }}>Save draft</button></div></div></div> : null}
                    <div style={{ display: "flex", gap: 6, borderBottom: "1px solid #E2E8F0", marginBottom: 18, flexWrap: "wrap", position: "sticky", top: 0, zIndex: 3, background: "#FFFFFF", paddingTop: 8 }}>{[["overview","Overview"],["transcript","Transcript"],["recording","Recording"],["actions","Action Items"]].map(([key,label]) => <button key={key} type="button" onClick={() => setEditSection(key)} style={{ border: 0, borderBottom: editSection === key ? "3px solid #0875E1" : "3px solid transparent", background: "transparent", color: editSection === key ? "#0875E1" : "#475569", padding: "10px 12px", fontWeight: 700 }}>{label}</button>)}</div>
                    {editView === "edited" ? <div role="toolbar" aria-label="Formatting tools" style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "8px", marginBottom: 10, border: "1px solid #E2E8F0", borderRadius: 8, background: "#F8FAFC" }}><button className={`avaada-approval-toolbar-btn${approvalFormatState.bold ? " is-active" : ""}`} aria-pressed={approvalFormatState.bold} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runApprovalCommand("bold")} title="Bold"><FormatBoldIcon fontSize="small" /></button><button className={`avaada-approval-toolbar-btn${approvalFormatState.italic ? " is-active" : ""}`} aria-pressed={approvalFormatState.italic} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runApprovalCommand("italic")} title="Italic"><FormatItalicIcon fontSize="small" /></button><button className={`avaada-approval-toolbar-btn${approvalFormatState.underline ? " is-active" : ""}`} aria-pressed={approvalFormatState.underline} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runApprovalCommand("underline")} title="Underline"><FormatUnderlinedIcon fontSize="small" /></button><button className="avaada-approval-toolbar-btn" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertApprovalPoint()} title="Add point after the current bullet"><AddCircleOutlineIcon fontSize="small" /><span>Add point</span></button><button className={`avaada-approval-toolbar-btn avaada-approval-toolbar-btn--highlight${approvalFormatState.highlight ? " is-active" : ""}`} aria-pressed={approvalFormatState.highlight} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runApprovalCommand("hiliteColor", "#FEF08A")} title={approvalFormatState.highlight ? "Yellow highlight is applied" : "Apply yellow highlight"} aria-label={approvalFormatState.highlight ? "Yellow highlight is applied" : "Apply yellow highlight"}><BorderColorIcon fontSize="small" /></button><button className="avaada-approval-toolbar-btn" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runApprovalCommand("undo")} title="Undo"><UndoIcon fontSize="small" /></button><button className="avaada-approval-toolbar-btn" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => runApprovalCommand("redo")} title="Redo"><RedoIcon fontSize="small" /></button></div> : null}
                    {approvalEditorMessage ? <div role="status" style={{ margin: "-2px 0 10px", padding: "8px 10px", borderRadius: 7, border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", fontSize: 12, fontWeight: 600 }}>{approvalEditorMessage}</div> : null}
                    {approvalContextMenu ? <div className="avaada-approval-context-menu" style={{ left: approvalContextMenu.x, top: approvalContextMenu.y }} onMouseLeave={() => setApprovalContextMenu(null)}><button type="button" onClick={() => { runApprovalCommand("bold"); setApprovalContextMenu(null); }}><FormatBoldIcon fontSize="small" /> <span>Bold selection</span></button><button type="button" onClick={() => { runApprovalCommand("hiliteColor", "#FEF08A"); setApprovalContextMenu(null); }}><FormatColorFillIcon fontSize="small" /> <span>Highlight selection</span></button><button type="button" onClick={() => { runApprovalCommand("insertUnorderedList"); setApprovalContextMenu(null); }}><FormatListBulletedIcon fontSize="small" /> <span>Turn into bullet list</span></button><button type="button" onClick={() => { insertApprovalPoint(); setApprovalContextMenu(null); }}><span style={{ fontSize: 16, fontWeight: 800 }}>＋</span> <span>Insert new point</span></button></div> : null}
                    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}><button type="button" onClick={() => setEditView("original")} style={{ border: "1px solid #CBD5E1", borderRadius: 7, padding: "8px 12px", background: editView === "original" ? "#0F2747" : "#FFF", color: editView === "original" ? "#FFF" : "#0F2747" }}>Original</button><button type="button" onClick={() => setEditView("edited")} style={{ border: "1px solid #CBD5E1", borderRadius: 7, padding: "8px 12px", background: editView === "edited" ? "#0F2747" : "#FFF", color: editView === "edited" ? "#FFF" : "#0F2747" }}>Edited</button></div>
                    {editView === "original" ? <div className={editSection === "transcript" ? "avaada-approval-editor-card--transcript" : "avaada-approval-editor-card"} style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, background: "#F8FAFC", padding: 14, borderRadius: 8 }}>{editSection === "overview" ? renderApprovalOverview(meeting?.originalSummary || meeting?.approvalAccess?.originalSummary || "No original summary available.", false) : editSection === "transcript" ? (meeting.transcript || []).map((row, index) => <div key={index} style={{ padding: 10, marginBottom: 8, minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word", background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8 }}><strong>{row.speaker || "Speaker"}</strong><div>{row.text || ""}</div></div>) : editSection === "actions" ? (meeting.actionItems || []).map((row, index) => <div key={index} style={{ padding: 10, marginBottom: 8, minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word", background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8 }}><strong>{row.title || `Action item ${index + 1}`}</strong><div>{row.description || row.text || ""}</div></div>) : (meeting.recording_enabled ? "Recording available" : "No recording available")}</div> : <div className={editSection === "transcript" ? "avaada-approval-editor-card--transcript" : "avaada-approval-editor-card"} style={{ display: "grid", gap: 10 }} onMouseUp={refreshApprovalFormatState} onKeyUp={refreshApprovalFormatState}>{editSection === "overview" ? ((approvalOverviewHtml || meeting.editedSummaryHtml) ? <div className="avaada-detail-summary-sections avaada-persisted-summary-html" contentEditable suppressContentEditableWarning dangerouslySetInnerHTML={{ __html: sanitizeApprovalHtml(approvalOverviewHtml || meeting.editedSummaryHtml) }} onInput={(e) => { setApprovalOverviewHtml(e.currentTarget.innerHTML); updateApprovalContent("overview", e.currentTarget.innerText || ""); refreshApprovalFormatState(); }} /> : renderApprovalOverview(editContent.overview, true)) : editSection === "transcript" ? editContent.transcript.map((row, index) => <div key={index} style={{ padding: 10, background: "#FFFFFF", border: "1px solid #93C5FD", borderRadius: 8 }}><strong>{row.speaker || "Speaker"}</strong><div contentEditable suppressContentEditableWarning onBlur={(e) => updateApprovalRow("transcript", index, "text", e.currentTarget.textContent || "")} style={{ outline: "none" }}>{row.text || ""}</div></div>) : editSection === "actions" ? editContent.actions.map((row, index) => <div key={index} style={{ padding: 10, background: "#FFFFFF", border: "1px solid #93C5FD", borderRadius: 8 }}><strong>{row.title || `Action item ${index + 1}`}</strong><div contentEditable suppressContentEditableWarning onBlur={(e) => updateApprovalRow("actions", index, "description", e.currentTarget.textContent || "")} style={{ outline: "none" }}>{row.description || row.text || ""}</div></div>) : <div contentEditable suppressContentEditableWarning onInput={(e) => updateApprovalContent("recording", { ...(editContent.recording || {}), note: e.currentTarget.textContent || "" })} style={{ padding: 14, border: "1px solid #93C5FD", borderRadius: 8, outline: "none" }}>{meeting.recording_enabled ? "Recording available" : "No recording available"}</div>}</div>}
                    <div className="avaada-approval-drawer-footer" style={{ display: "flex", gap: 8, marginTop: 16 }}><button type="button" onClick={saveApprovalEdit} disabled={approvalSaving} style={{ border: 0, borderRadius: 7, padding: "10px 14px", background: "#0875E1", color: "#FFF", fontWeight: 700 }}>{approvalSaving ? "Saving…" : "Save changes"}</button><button type="button" onClick={requestCloseApprovalDrawer} style={{ border: "1px solid #CBD5E1", borderRadius: 7, padding: "10px 14px", background: "#FFF" }}>Done</button></div>
                  </div></div>, document.body) : null}
                  {recipientDrawerOpen ? createPortal(<div role="dialog" aria-modal="true" aria-label="Choose approval recipients" className="avaada-recipient-drawer" style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 2147483647, width: "min(520px, 94vw)", padding: 28, overflowY: "auto", background: "#FFFFFF", boxShadow: "-12px 0 36px rgba(15,23,42,.24)", borderLeft: "1px solid #CBD5E1" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 6 }}><div><div style={{ fontWeight: 800, fontSize: 18, color: "#0F2747" }}>Choose recipients</div><div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>Only people who attended or were invited to this meeting can receive the approved content.</div></div><span style={{ padding: "5px 9px", borderRadius: 999, background: "#EFF6FF", color: "#075985", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>{recipientEmails.length} selected</span></div>
                    <div className="avaada-recipient-select-all"><label><input type="checkbox" checked={allApprovalRecipientsSelected} onChange={toggleAllApprovalRecipients} /> <strong>{allApprovalRecipientsSelected ? "Clear all" : "Select all"}</strong></label><span>{approvalRecipients.length} eligible participants</span></div>
                    <div className="avaada-recipient-list">{approvalRecipients.map((p) => { const email = p.email; const locked = p.isApprover; const checked = !locked && recipientEmails.includes(email); const rowTitle = locked ? "You are the approver. Your access is already granted and cannot be selected as a recipient." : undefined; return <label className={`avaada-recipient-row${checked ? " is-selected" : ""}${locked ? " is-locked" : ""}`} key={email} title={rowTitle}><input type="checkbox" checked={checked} disabled={locked} onChange={() => locked ? null : setRecipientEmails((old) => checked ? old.filter((e) => e !== email) : [...old, email])} /><span className="avaada-recipient-avatar">{String(p.name || email).slice(0, 1).toUpperCase()}</span><span className="avaada-recipient-copy"><strong>{p.name || "Participant"}{locked ? " · You (approver)" : ""}</strong><small>{locked ? "Already has approver access" : email}</small></span></label>; })}</div>
                    <div className="avaada-recipient-footer"><button type="button" onClick={() => setRecipientDrawerOpen(false)} className="avaada-approval-btn avaada-approval-btn--neutral">Cancel</button><button type="button" onClick={approveThisMeeting} disabled={approvalSaving || !recipientEmails.length} className="avaada-approval-btn avaada-approval-btn--primary">{approvalSaving ? "Approving…" : `Approve for ${recipientEmails.length}`}</button></div>
                  </div>, document.body) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      {referenceDetail ? (
        <div className="avaada-meeting-detail__tab-shell">
          <div
            className="avaada-meeting-detail__tabs avaada-meeting-detail__tabs--nav"
            role="tablist"
            aria-label="Meeting detail sections"
          >
            {detailTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`detail-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={DETAIL_TAB_PANEL_IDS[tab.id]}
                  tabIndex={isActive ? 0 : -1}
                  className={`avaada-meeting-detail__tab${isActive ? " is-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="avaada-detail-tab-icon" aria-hidden>
                    <Svg d={tab.icon} size={18} color="currentColor" stroke={1.75} />
                  </span>
                  <span className="avaada-detail-tab-label">{tab.label}</span>
                  {tab.badge != null ? (
                    <span className={`avaada-detail-tab-badge${isActive ? " is-active" : ""}`}>{tab.badge}</span>
                  ) : null}
                </button>
              );
            })}
            {isApprovalViewer ? <span className="avaada-main-original-edited-toggle" style={{ marginLeft: "auto", display: "inline-flex", gap: 3, border: "1px solid #BFDBFE", borderRadius: 7, padding: 2, background: "white" }}> <button className="avaada-main-original-edited-btn" type="button" onClick={() => setApproverMainView("original")} style={{ border: 0, borderRadius: 5, padding: "4px 8px", fontSize: 12, lineHeight: 1.25, background: approverMainView === "original" ? "#0F2747" : "transparent", color: approverMainView === "original" ? "white" : "#0F2747", fontWeight: 700 }}>Original</button> <button className="avaada-main-original-edited-btn" type="button" onClick={() => setApproverMainView("edited")} style={{ border: 0, borderRadius: 5, padding: "4px 8px", fontSize: 12, lineHeight: 1.25, background: approverMainView === "edited" ? "#0F2747" : "transparent", color: approverMainView === "edited" ? "white" : "#0F2747", fontWeight: 700 }}>Edited</button></span> : null}
          </div>
          <div className="avaada-meeting-detail__tab-body">
            {activeTab === "overview" ? (
              <>
                <div
                  id="detail-section-summary"
                  className="avaada-meeting-detail__panel avaada-meeting-detail__summary-panel"
                  role="tabpanel"
                  aria-labelledby="detail-tab-overview"
                >
                  <div className="avaada-meeting-detail__summary-head">
                    <div className="avaada-meeting-detail__summary-head-main">
                      <span className="avaada-detail-summary-head-icon" aria-hidden>
                        <Svg d={IC.actionList} size={16} color="#0875E1" stroke={2} />
                      </span>
                      <h2>Meeting Summary</h2>
                    </div>
                    <div className="avaada-detail-summary-head-actions">
                      <span className="avaada-meeting-detail__meetai-badge">
                        <span className="avaada-meeting-detail__meetai-badge-dot" aria-hidden />
                        Generated by MeetAI
                      </span>
                      <button
                        type="button"
                        className="avaada-mui-btn avaada-mui-btn--outlined"
                        onClick={() => setShareDialogScope("overview")}
                        title="Share this meeting"
                      >
                        <Svg d={IC.share} size={14} color="currentColor" stroke={2} />
                        Share
                      </button>
                    </div>
                  </div>
                  <div className="avaada-detail-summary-body">
                    {meeting?.approvalAccess?.required && !meeting?.approvalAccess?.visible ? (
                      <div role="status" style={{ display: "flex", minHeight: 180, alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, border: "1px dashed #93C5FD", borderRadius: 12, background: "#F8FBFF", color: "#1E3A8A", textAlign: "center", padding: 24 }}>
                        <LockClockOutlinedIcon sx={{ fontSize: 30, color: "#0875E1" }} />
                        <strong>Overview locked</strong>
                        <span style={{ fontSize: 13, color: "#4B6B8F" }}>This meeting summary will appear after approval.</span>
                      </div>
                    ) : (
                      isApprovalViewer ? (approverMainView === "edited" && meeting.editedSummaryHtml ? <div className="avaada-detail-summary-sections avaada-persisted-summary-html" dangerouslySetInnerHTML={{ __html: sanitizeApprovalHtml(meeting.editedSummaryHtml) }} /> : renderApprovalOverview(approverMainView === "original" ? (meeting.originalSummary || "No original summary available.") : (meeting.editedSummary || editContent.overview || meeting.summary || "No edited summary available."), false)) : (meeting.editedSummaryHtml ? <div className="avaada-detail-summary-sections avaada-persisted-summary-html" dangerouslySetInnerHTML={{ __html: sanitizeApprovalHtml(meeting.editedSummaryHtml) }} /> : <MeetingDetailSummarySection meeting={meeting} referenceLayout actionItems={meeting.actionItems || []} />)
                    )}
                  </div>
                </div>
              </>
            ) : null}

            {activeTab === "transcript" ? (
              <div
                id="detail-section-transcript"
                className="avaada-meeting-detail__panel avaada-meeting-detail__transcript-panel"
                role="tabpanel"
                aria-labelledby="detail-tab-transcript"
              >
                {meeting?.approvalAccess?.required && !meeting?.approvalAccess?.visible ? (
                  <div role="status" style={{ display: "flex", minHeight: 150, alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, margin: 18, border: "1px dashed #93C5FD", borderRadius: 12, background: "#F8FBFF", color: "#1E3A8A", textAlign: "center", padding: 24 }}>
                    <LockClockOutlinedIcon sx={{ fontSize: 30, color: "#0875E1" }} />
                    <strong>Transcript locked</strong>
                    <span style={{ fontSize: 13, color: "#4B6B8F" }}>
                      {pendingApprovalNames.length > 1
                        ? `The transcript will be available after any one of these approvers approves this meeting: ${transcriptApprovalNames}.`
                        : `The transcript will be available after ${transcriptApprovalNames} approves this meeting.`}
                    </span>
                  </div>
                ) : <ActionTab
                  embedInAvaadaDetail
                  referenceTranscriptLayout
                  panelMode="transcript-only"
                  items={meeting.actionItems || []}
                  transcript={meeting.transcript || []}
                  meeting={meeting}
                  meetingTitle={meeting.title || "Meeting transcript"}
                  status={effectiveStatus}
                  meetingId={meeting.id ?? meeting._id}
                  onMeetingUpdated={onMeetingUpdated}
                  currentUserEmail={currentUser?.email || ""}
                  currentUserName={currentUser?.name || ""}
                  participants={meeting.participants || []}
                  previewRequest={transcriptPreviewRequest}
                  onShare={() => setShareDialogScope("transcript")}
                  shareLabel="Share"
                />}
              </div>
            ) : null}

            {activeTab === "recording" ? (
              <div
                id="detail-section-recording"
                className="avaada-meeting-detail__panel avaada-meeting-detail__recording-panel"
                role="tabpanel"
                aria-labelledby="detail-tab-recording"
              >
                <div className="avaada-meeting-detail__summary-head">
                  <div className="avaada-meeting-detail__summary-head-main">
                    <span className="avaada-detail-summary-head-icon" aria-hidden>
                      <Svg d={IC.playCircle} size={16} color="#0875E1" stroke={2} />
                    </span>
                    <h2>Recording</h2>
                  </div>
                </div>
                <div className="avaada-detail-recording-body">
                  <MeetingRecordingInlinePanel meeting={meeting} />
                </div>
              </div>
            ) : null}

            {activeTab === "action-items" ? (
              <section
                id="detail-section-action-items"
                className="avaada-meeting-detail__panel avaada-meeting-detail__action-panel"
                aria-labelledby="detail-tab-action-items"
                role="tabpanel"
              >
                {meeting?.approvalAccess?.required && !meeting?.approvalAccess?.visible ? (
                  <div role="status" style={{ display: "flex", minHeight: 180, alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, margin: 18, border: "1px dashed #93C5FD", borderRadius: 12, background: "#F8FBFF", color: "#1E3A8A", textAlign: "center", padding: 24 }}>
                    <LockClockOutlinedIcon sx={{ fontSize: 30, color: "#0875E1" }} />
                    <strong>Action items locked</strong>
                    <span style={{ fontSize: 13, color: "#4B6B8F" }}>
                      {pendingApprovalNames.length > 1
                        ? `Action items will appear after any one of these approvers approves this meeting: ${transcriptApprovalNames}.`
                        : `Action items will appear after ${transcriptApprovalNames} approves this meeting.`}
                    </span>
                  </div>
                ) : <ActionTab
                  embedInAvaadaDetail
                  panelMode="tasks-only"
                  meetingActionCount={(meeting.actionItems || []).length}
                  items={meeting.actionItems || []}
                  transcript={meeting.transcript || []}
                  meeting={meeting}
                  meetingTitle={meeting.title || "Meeting transcript"}
                  status={effectiveStatus}
                  meetingId={meeting.id ?? meeting._id}
                  onMeetingUpdated={onMeetingUpdated}
                  currentUserEmail={currentUser?.email || ""}
                  currentUserName={currentUser?.name || ""}
                  participants={meeting.participants || []}
                  previewRequest={transcriptPreviewRequest}
                />}
              </section>
            ) : null}
          </div>
        </div>
      ) : null}

      {referenceDetail && activeTab === "overview" && participantsExpanded ? (
        <JoinedNotJoinedAttendance
          participants={meeting.participants || []}
          meeting={meeting}
          autoJoinOptInUsers={meeting.autoJoinOptInUsers || []}
          hasGoogleEventId={Boolean(
            String(meeting.google_event_id || meeting.googleEventId || "").trim()
          )}
          referenceLayout
          defaultDetailsOpen
        />
      ) : null}

      {!referenceDetail && activeTab !== "overview" ? null : !referenceDetail && rosterPeople.length > 0 ? (
        <section
          className="card"
          aria-label={`Roster, ${rosterPeople.length} participants`}
          style={{
            padding: "16px 20px",
            marginBottom: 14,
            borderColor: "rgba(12,43,78,0.12)",
            boxShadow: "0 2px 10px rgba(12,43,78,0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
              Roster · {rosterPeople.length} participant{rosterPeople.length === 1 ? "" : "s"}
            </span>
            <span style={{ fontSize: 11, color: T.textMuted }}>Hover a chip for details</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, marginBottom: 11 }}>
            <div className="people-stack" aria-hidden>
              {rosterPeople.slice(0, 6).map((p, index) => (
                <span
                  key={p.id || p.email || `roster-${index}`}
                  className={`people-stack-item${participantIsBot(p) ? " is-bot" : ""}`}
                >
                  <Avatar
                    initials={p.initials}
                    size={30}
                    color="#9CA3AF"
                    isBot={participantIsBot(p)}
                    imageUrl={p.avatar_url || p.avatarUrl || p.picture || ""}
                  />
                </span>
              ))}
            </div>
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 12,
                color: T.textSub,
              }}
              title={rosterPeople.map((p) => p.name || p.email || "Participant").join(", ")}
            >
              {rosterPeople
                .map((p) =>
                  participantIsBot(p)
                    ? BOT_DISPLAY_NAME
                    : `${p.name || p.email || "Participant"}${isGuestMeetLinkEmail(p.email) ? " (guest)" : ""}`
                )
                .join(", ")}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
            {rosterJoined.map((p, index) => {
              const guest = isAttendanceGuestParticipant(p);
              return (
                <span
                  key={`roster-joined-${p.id || p.email || index}`}
                  title={`${p.name || p.email || "Participant"} joined`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 9px",
                    borderRadius: 7,
                    border: guest ? "1px dashed #34A16F" : "1px solid #D1EBDD",
                    background: guest ? "#F4FBF7" : "#EEF9F2",
                    color: "#237A4B",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  <Svg d={guest ? IC.users : IC.check} size={11} color="currentColor" stroke={2} />
                  {p.name || p.email || "Participant"}{guest ? " · guest" : ""}
                </span>
              );
            })}
            {rosterNotJoined.map((p, index) => (
              <span
                key={`roster-missed-${p.id || p.email || index}`}
                title={`${p.name || p.email || "Participant"} did not join`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 9px",
                  borderRadius: 7,
                  border: "1px solid #F1DFC1",
                  background: "#FFF8EB",
                  color: "#A16207",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                <Svg d={IC.clock} size={11} color="currentColor" stroke={2} />
                {p.name || p.email || "Participant"} · not joined
              </span>
            ))}
          </div>
        </section>
      ) : referenceDetail ? null : null}

      {!referenceDetail ? (
        <>
        <MeetingDetailSummarySection meeting={meeting} />

        <div style={{ marginBottom: 4 }}>
          <JoinedNotJoinedAttendance
            participants={meeting.participants || []}
            meeting={meeting}
            autoJoinOptInUsers={meeting.autoJoinOptInUsers || []}
            hasGoogleEventId={Boolean(String(meeting.google_event_id || meeting.googleEventId || "").trim())}
          />
          {isOwner && guestRowsForEmail.length > 0 && onMeetingUpdated && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Guest recap email
              </div>
              <p style={{ fontSize: 12, color: T.textSub, margin: "0 0 8px", lineHeight: 1.5 }}>
                If someone joined with the meeting link and did not get the summary, add their address here. Future meetings will use participant data when the platform provides it.
              </p>
              {guestRowsForEmail.map((p) => (
                <GuestParticipantEmailRow
                  key={p.id}
                  meetingId={meeting.id ?? meeting._id}
                  participant={p}
                  onSaved={onMeetingUpdated}
                />
              ))}
            </div>
          )}
        </div>

        <MeetingRecapEmailBanner meeting={meeting} />
        </>
      ) : null}

        {joinOnly && (
          <div className="autojoin-footer-strip">
            <Svg d={IC.info} size={14} color="#1D4ED8" stroke={2} />
            <span>
              After this meeting ends, transcript, tasks and {BOT_DISPLAY_NAME} will appear under{" "}
              <strong>Meetings → Recorded Meetings</strong>.
            </span>
          </div>
        )}

      {joinOnly ? null : !referenceDetail ? (
        <section
          aria-labelledby="detail-tasks-heading"
          style={{
            marginTop: 14,
            background: T.bg,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusLg,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${T.border}`,
              background: "#F8FAFC",
            }}
          >
            <h2 id="detail-tasks-heading" style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: T.text }}>
              Tasks
            </h2>
            <span style={{ fontSize: 11, color: T.textMuted }}>
              {(meeting.actionItems || []).length} from this meeting
            </span>
          </div>
          <div style={{ padding: 18 }}>
            <ActionTab items={meeting.actionItems || []} transcript={meeting.transcript || []} meeting={meeting} meetingTitle={meeting.title || "Meeting transcript"} status={effectiveStatus} meetingId={meeting.id ?? meeting._id} onMeetingUpdated={onMeetingUpdated} currentUserEmail={currentUser?.email || ""} currentUserName={currentUser?.name || ""} participants={meeting.participants || []} previewRequest={transcriptPreviewRequest}/>
          </div>
        </section>
      ) : null}

      <MeetingShareDialog
        open={!!shareDialogScope}
        onClose={() => setShareDialogScope(null)}
        meeting={meeting}
        defaultScope={shareDialogScope || "meeting"}
        sharerName={currentUser?.name || ""}
        sharerEmail={currentUser?.email || ""}
      />

      </div>
      </div>
      </div>
    </div>
  );
}

const ROBO_ASSISTANT_VIDEO = "/assets/robohi.mp4";

function localTodayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localTomorrowYmd() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function meetingRowForSnapshot(m) {
  const st = effectiveMeetingStatus(m);
  const transcriptRows = Array.isArray(m?.transcript) ? m.transcript : [];
  return {
    id: String(m?.id ?? m?._id ?? ""),
    title: String(m?.title || "Meeting").slice(0, 120),
    date: String(m?.date || ""),
    time: formatMeetingTimeForDisplay(m?.time) || "",
    status: st,
    type: m?.type || "",
    hasTranscript: transcriptRows.length > 0,
    hasSummary: Boolean(String(m?.summary || "").trim()),
  };
}

/** Compact summary + transcript excerpt for workspace chatbot grounding. */
function meetingContentForSnapshot(m, { summaryMax = 900, transcriptMax = 2200 } = {}) {
  const id = String(m?.id ?? m?._id ?? "");
  const title = String(m?.title || "Meeting").slice(0, 140);
  const date = String(m?.date || "");
  const time = formatMeetingTimeForDisplay(m?.time) || "";
  const summary = String(m?.summary || "").replace(/\s+/g, " ").trim().slice(0, summaryMax);
  const rows = Array.isArray(m?.transcript) ? m.transcript : [];
  const lines = [];
  let used = 0;
  for (const r of rows) {
    const speaker = String(r?.speaker || "Speaker").trim() || "Speaker";
    const text = String(r?.text || "").replace(/\s+/g, " ").trim();
    if (!text) continue;
    const line = `${speaker}: ${text}`;
    if (used + line.length + 1 > transcriptMax) break;
    lines.push(line);
    used += line.length + 1;
  }
  if (!summary && !lines.length) return null;
  const participants = humanParticipants(m?.participants || [])
    .map((p) => String(p?.name || p?.email || "").trim().slice(0, 80))
    .filter(Boolean)
    .slice(0, 20);
  return {
    id,
    title,
    date,
    time,
    status: effectiveMeetingStatus(m),
    summary: summary || undefined,
    transcriptExcerpt: lines.length ? lines.join("\n") : undefined,
    transcriptLineCount: rows.length,
    participants: participants.length ? participants : undefined,
  };
}

function userJoinedRecordedMeeting(m, userEmail) {
  const ue = String(userEmail || "").trim().toLowerCase();
  if (!ue) return false;
  const { joined } = splitJoinedVsNotJoinedInvitees(m?.participants || [], attendanceContextFromMeeting(m));
  return joined.some((p) => String(p?.email || "").trim().toLowerCase() === ue);
}

function userInvitedNotJoinedMeeting(m, userEmail) {
  const ue = String(userEmail || "").trim().toLowerCase();
  if (!ue) return false;
  const { notJoined } = splitJoinedVsNotJoinedInvitees(m?.participants || [], attendanceContextFromMeeting(m));
  return notJoined.some((p) => String(p?.email || "").trim().toLowerCase() === ue);
}

function buildWorkspaceSnapshot({ user, meetings, calendarMeetings, myActions, usage, currentMeeting }) {
  const userEmail = String(user?.email || "").trim().toLowerCase();
  const todayYmd = localTodayYmd();
  const tomorrowYmd = localTomorrowYmd();
  const nowMs = Date.now();
  const list = (Array.isArray(calendarMeetings) && calendarMeetings.length ? calendarMeetings : meetings) || [];
  const dbMeetings = Array.isArray(meetings) ? meetings : [];
  const currentMeetingId = String(currentMeeting?.id ?? currentMeeting?._id ?? "").trim();

  const futureOrToday = list.filter((m) => {
    const st = effectiveMeetingStatus(m, nowMs);
    if (st === "cancelled") return false;
    const ms = meetingDateTimeMs(m);
    return ms >= Date.now() - 6 * 60 * 60 * 1000;
  });

  const meetingsToday = futureOrToday.filter((m) => String(m?.date || "") === todayYmd).map(meetingRowForSnapshot);
  const meetingsTomorrow = futureOrToday.filter((m) => String(m?.date || "") === tomorrowYmd).map(meetingRowForSnapshot);
  const upcoming = futureOrToday
    .filter((m) => {
      const st = effectiveMeetingStatus(m, nowMs);
      return st === "upcoming" || st === "live";
    })
    .sort((a, b) => meetingDateTimeMs(a) - meetingDateTimeMs(b))
    .slice(0, 20)
    .map(meetingRowForSnapshot);

  const weekStart = new Date();
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekStartMs = weekStart.getTime();

  const completedThisWeek = dbMeetings.filter((m) => {
    const st = effectiveMeetingStatus(m, nowMs);
    if (!(st === "completed" || st === "ended")) return false;
    const ms = meetingDateTimeMs(m);
    return ms >= weekStartMs;
  }).length;

  const meetingsThisWeek = list.filter((m) => {
    const ms = meetingDateTimeMs(m);
    return ms >= weekStartMs && ms <= nowMs + 14 * 24 * 60 * 60 * 1000;
  }).length;

  const recordedWithParts = dbMeetings.filter(
    (m) => (m?.recall_bot_id || m?.recallBotId) && Array.isArray(m.participants) && m.participants.length > 0
  );
  let myMeetingsJoined = 0;
  let myMeetingsNotJoined = 0;
  const recentAttendance = [];
  const joinedTodayTitles = [];
  for (const m of recordedWithParts) {
    const title = String(m?.title || "Meeting");
    const date = String(m?.date || "");
    const mid = String(m?.id ?? m?._id ?? "");
    if (userJoinedRecordedMeeting(m, userEmail)) {
      myMeetingsJoined += 1;
      if (date === todayYmd) joinedTodayTitles.push(title);
      if (recentAttendance.length < 12) {
        recentAttendance.push({ id: mid, title, date, attendance: "joined" });
      }
    } else if (userInvitedNotJoinedMeeting(m, userEmail)) {
      myMeetingsNotJoined += 1;
      if (recentAttendance.length < 12) {
        recentAttendance.push({ id: mid, title, date, attendance: "invited, not joined yet" });
      }
    }
  }

  const actions = Array.isArray(myActions) ? myActions : [];
  const myOpenActions = actions
    .filter((a) => String(a?.status || "").toLowerCase() !== "completed")
    .slice(0, 25)
    .map((a) => ({
      text: String(a?.text || "").slice(0, 200),
      status: String(a?.status || "pending"),
      due: String(a?.due || ""),
      meetingId: String(a?.meetingId || ""),
      meetingTitle: String(a?.meetingTitle || ""),
    }));
  const myDoneActions = actions
    .filter((a) => String(a?.status || "").toLowerCase() === "completed")
    .slice(0, 15)
    .map((a) => ({
      text: String(a?.text || "").slice(0, 200),
      status: "completed",
      meetingId: String(a?.meetingId || ""),
      meetingTitle: String(a?.meetingTitle || ""),
    }));

  const liveCount = list.filter((m) => effectiveMeetingStatus(m, nowMs) === "live").length;

  let recentContentMeetings = dbMeetings
    .filter((m) => {
      const st = effectiveMeetingStatus(m, nowMs);
      if (st === "cancelled") return false;
      const hasSummary = Boolean(String(m?.summary || "").trim());
      return hasSummary || meetingHasTranscriptData(m);
    })
    .sort((a, b) => meetingDateTimeMs(b) - meetingDateTimeMs(a))
    .slice(0, 5)
    .map((m) => meetingContentForSnapshot(m))
    .filter(Boolean);

  // Prefer the meeting the user is currently viewing
  if (currentMeetingId && currentMeeting) {
    const curBlock =
      meetingContentForSnapshot(currentMeeting) ||
      recentContentMeetings.find((m) => String(m.id) === currentMeetingId);
    if (curBlock) {
      recentContentMeetings = [
        curBlock,
        ...recentContentMeetings.filter((m) => String(m.id) !== currentMeetingId),
      ].slice(0, 5);
    }
  }

  return {
    userEmail,
    userName: user?.name || "User",
    assistantName: BOT_DISPLAY_NAME,
    todayYmd,
    currentMeetingId: currentMeetingId || undefined,
    stats: {
      meetingsToday: meetingsToday.length,
      meetingsTomorrow: meetingsTomorrow.length,
      upcomingCount: upcoming.length,
      liveCount,
      completedThisWeek,
      meetingsThisWeek,
      recordedMeetings: usage?.meetings ?? dbMeetings.filter((m) => m?.recall_bot_id || m?.recallBotId).length,
      usageHours: usage?.usageHours ?? 0,
      transcriptLines: usage?.transcriptRows ?? 0,
      actionItemsGenerated: usage?.actionItems ?? actions.length,
      myActionItemsOpen: myOpenActions.length,
      myActionItemsDone: myDoneActions.length,
      myMeetingsJoined,
      myMeetingsNotJoined,
      meetingsWithContent: recentContentMeetings.length,
    },
    meetingsToday,
    meetingsTomorrow,
    upcoming,
    myOpenActions,
    myDoneActions,
    recentAttendance,
    joinedTodayTitles,
    recentContentMeetings,
    uiHints: {
      meetingUsage: "Profile picture (top right) → Meeting usage",
      actionItems: "My Tasks in the left sidebar",
      actionItemsSupported: "status, remarks, mark done — not manual due-date edit",
      calendar: "Calendar tab in the left navigation",
      transcripts: "Recorded Meetings tab — open a meeting for full transcript chat",
    },
    productCapabilities: {
      myTasksDueDateEditable: false,
      myTasksDueDateSource: "AI extraction from meeting when a deadline was stated in the call",
    },
  };
}

/**
 * Normalize assistant markdown for display:
 * - break "1. … 2. …" onto separate lines
 * - break packed markdown tables ("| 1 | … | | 2 | …") into real rows
 */
function buildAssistantMeetingTitleMap(meetingPool) {
  const map = new Map();
  for (const m of Array.isArray(meetingPool) ? meetingPool : []) {
    const id = String(m?.id ?? m?._id ?? "").trim();
    const title = String(m?.title || "").trim();
    if (id && title) map.set(id.toLowerCase(), title);
    const gid = String(m?.googleEventId || m?.google_event_id || "").trim();
    if (gid && title) map.set(gid.toLowerCase(), title);
  }
  return map;
}

function formatAssistantChatForDisplay(text, titleById) {
  let s = String(text || "").trim();
  if (!s) return s;
  s = s.replace(/^understood your question[.!?\s·-]*/i, "");

  // Unwrap accidentally returned raw JSON payloads: ```json { "answer": "..." } ```
  const codeFenceMatch = s.match(/^```(?:json)?\s*([\s\S]*?)```$/i);
  if (codeFenceMatch) {
    s = codeFenceMatch[1].trim();
  }
  if (s.includes('"answer"')) {
    try {
      let candidate = s;
      const start = candidate.indexOf('{');
      const end = candidate.lastIndexOf('}');
      if (start >= 0 && end > start) candidate = candidate.slice(start, end + 1);
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed.answer === "string") {
        s = parsed.answer.trim();
      }
    } catch (_) {
      const match = s.match(/"answer"\s*:\s*"([\s\S]*?)"(?:\s*,\s*"(?:confidence|citations)|\s*\}\s*$)/i)
        || s.match(/"answer"\s*:\s*"([\s\S]*)/i);
      if (match && match[1]) {
        s = match[1]
          .replace(/"\s*,\s*"(?:confidence|citations)[\s\S]*$/i, "")
          .replace(/"\s*\}\s*$/i, "")
          .replace(/\\n/g, "\n")
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, "\\")
          .trim();
      }
    }
  }

  s = normalizeAssistantAnswerLayout(s);
  s = s
    .replace(/(?<!\n)\s+(?=\d{1,2}\.\s+(?:\*\*|[A-Za-z"(•\-]|\[\[meet:))/g, "\n")
    .replace(/(?<!\n)\s+(?=[\u2022\u25CF•]\s+(?:\*\*|[A-Za-z"(]))/g, "\n");
  // Packed pipe-tables: "| cell | | 2 |" or "| --- | | # |" → new row
  s = s.replace(/\|\s*\|\s*(?=(?:\d{1,2}|#{1,6}|:?-{3,}:?)\s*\|)/g, "|\n|");
  // Ensure a blank line before a table that starts mid-paragraph
  s = s.replace(/([^\n|])\s*(\n?\|[^\n]+\|)/g, (_, a, b) => {
    if (String(b).startsWith("\n")) return `${a}${b}`;
    return `${a}\n${b}`;
  });
  s = normalizeAssistantAnswerLayout(s);
  return stripMeetLinkGarbage(repairAssistantMeetTokens(s, titleById));
}

function parseMarkdownTableRow(line) {
  const t = String(line || "").trim();
  // Only real markdown table rows (must start with |). Avoid treating [[meet:id|title]] as a table.
  if (!t.startsWith("|")) return null;
  const pipeCount = (t.match(/\|/g) || []).length;
  if (pipeCount < 2) return null;
  // Split row by pipes, but keep pipes inside [[meet:id|title]] intact.
  const cells = [];
  let cur = "";
  let inMeetToken = false;
  for (let i = 0; i < t.length; i += 1) {
    const ch = t[i];
    const ahead = t.slice(i);
    if (!inMeetToken && ahead.startsWith("[[meet:")) {
      inMeetToken = true;
      cur += ch;
      continue;
    }
    if (inMeetToken && ahead.startsWith("]]")) {
      inMeetToken = false;
      cur += "]]";
      i += 1;
      continue;
    }
    if (ch === "|" && !inMeetToken) {
      cells.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  cells.push(cur.trim());
  let normalized = cells;
  if (t.startsWith("|")) normalized = normalized.slice(1);
  if (t.endsWith("|")) normalized = normalized.slice(0, -1);
  normalized = normalized.map((c) => String(c || "").trim());
  // Join any accidentally split meet token fragments (defensive)
  const merged = [];
  for (let i = 0; i < normalized.length; i += 1) {
    let cell = normalized[i];
    if (cell.includes("[[meet:") && !cell.includes("]]")) {
      while (i + 1 < normalized.length && !cell.includes("]]")) {
        i += 1;
        cell += `|${normalized[i]}`;
      }
    }
    merged.push(cell);
  }
  if (!merged.length) return null;
  return merged;
}

function isBrokenPipeArtifactLine(line) {
  const t = String(line || "").trim();
  // Broken fragments from malformed markdown table serialization.
  // Examples: "|---", "|1", "|2"
  return /^\|\s*(?:-+|\d+)\s*$/.test(t);
}

function isMarkdownTableSeparator(cells) {
  if (!cells || !cells.length) return false;
  return cells.every((c) => /^:?-{3,}:?$/.test(String(c || "").replace(/\s/g, "")) || c === "");
}

/** Inline meeting links [[meet:id|title]], **bold**, __bold__, and `code`. */
function renderAssistantChatInline(line, keyBase, onOpenMeeting, titleById = null) {
  const src = String(line || "");
  if (!src) return null;
  // Strip a leading markdown heading marker leftover inside a cell/line
  const cleaned = stripMeetDisplayArtifacts(
    stripMeetLinkGarbage(
      repairAssistantMeetTokens(
        src
          .replace(/^#{1,6}\s+/, "")
          .replace(/\]\](\s*\]\])+/g, "]]"),
        titleById
      )
    )
  );
  // Supports both [[meet:id|title]] and [[meet:id]]
  const re = /\[\[meet:([^\]|]+)(?:\|([^\]]+))?\]\]|\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`/g;
  const nodes = [];
  let last = 0;
  let n = 0;
  let m;
  while ((m = re.exec(cleaned)) !== null) {
    if (m.index > last) {
      const chunk = cleaned.slice(last, m.index);
      if (!isMeetDisplayTailJunk(chunk)) nodes.push(chunk);
    }
    if (m[1] != null) {
      const meetId = String(m[1] || "").trim();
      const meetTitle = meetingTitleFromCatalog(meetId, titleById, m[2] || "Open meeting");
      nodes.push(
        <button
          key={`${keyBase}-m${n}`}
          type="button"
          className="assistant-meet-link"
          title={`Open ${meetTitle}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenMeeting?.(meetId, meetTitle);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            maxWidth: "100%",
            padding: "3px 10px",
            margin: "0 2px 2px 0",
            border: "1px solid #93C5FD",
            borderRadius: 999,
            background: "#EFF6FF",
            color: "#1D4ED8",
            fontWeight: 650,
            fontSize: "11.5px",
            fontFamily: "inherit",
            textDecoration: "none",
            cursor: onOpenMeeting ? "pointer" : "default",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            textAlign: "left",
            verticalAlign: "middle",
          }}
        >
          {meetTitle}
        </button>
      );
    } else if (m[5] != null) {
      nodes.push(
        <code
          key={`${keyBase}-c${n}`}
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: "0.92em",
            padding: "1px 5px",
            borderRadius: 4,
            background: "rgba(15,23,42,0.06)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {m[5]}
        </code>
      );
    } else {
      nodes.push(
        <strong key={`${keyBase}-b${n}`} style={{ fontWeight: 700, color: T.text }}>
          {m[3] || m[4]}
        </strong>
      );
    }
    last = m.index + m[0].length;
    n += 1;
  }
  if (last < cleaned.length) {
    const tail = cleaned.slice(last);
    if (!isMeetDisplayTailJunk(tail)) nodes.push(tail);
  }
  return nodes.length ? nodes : cleaned;
}

function AssistantChatTable({ headers, rows, blockKey, onOpenMeeting }) {
  const cols = Math.max(
    headers.length,
    ...rows.map((r) => (Array.isArray(r) ? r.length : 0)),
    1
  );
  const pad = (cells) => {
    const out = Array.isArray(cells) ? cells.slice(0, cols) : [];
    while (out.length < cols) out.push("");
    return out;
  };
  const prettyHeader = (h) => {
    const t = String(h || "").trim();
    if (t === "#" || t === "No" || t === "No.") return "#";
    return t || "—";
  };
  // Narrow chat: stack as cards when many columns (avoids horizontal clipping)
  if (cols >= 3) {
    return (
      <div key={blockKey} style={{ display: "flex", flexDirection: "column", gap: 8, margin: "8px 0 12px", maxWidth: "100%" }}>
        {rows.map((row, ri) => {
          const cells = pad(row);
          return (
            <div
              key={ri}
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: "8px 10px",
                background: ri % 2 ? "#FAFBFC" : "#fff",
                maxWidth: "100%",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {pad(headers).map((h, ci) => {
                const label = prettyHeader(h);
                const val = cells[ci];
                if (!String(val || "").trim() && !String(label || "").trim()) return null;
                return (
                  <div key={ci} style={{ marginBottom: ci === cols - 1 ? 0 : 6, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 2 }}>
                      {renderAssistantChatInline(label, `${blockKey}-cl${ri}h${ci}`, onOpenMeeting)}
                    </div>
                    <div style={{ fontSize: 12, color: T.text, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                      {renderAssistantChatInline(val, `${blockKey}-cl${ri}c${ci}`, onOpenMeeting)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div
      key={blockKey}
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        margin: "8px 0 12px",
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        background: "#fff",
      }}
    >
      <table
        style={{
          width: "100%",
          maxWidth: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
          fontSize: 11.5,
          lineHeight: 1.45,
        }}
      >
        <thead>
          <tr style={{ background: "linear-gradient(90deg,#EAF4FF 0%,#EAF8F1 100%)" }}>
            {pad(headers).map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: "left",
                  padding: "8px 10px",
                  borderBottom: `1px solid ${T.border}`,
                  fontWeight: 700,
                  color: T.text,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {renderAssistantChatInline(prettyHeader(h), `${blockKey}-h${i}`, onOpenMeeting)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 ? "#FAFBFC" : "#fff" }}>
              {pad(row).map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: "7px 10px",
                    borderBottom: `1px solid ${T.border}`,
                    verticalAlign: "top",
                    color: T.text,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {renderAssistantChatInline(cell, `${blockKey}-r${ri}c${ci}`, onOpenMeeting)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Parse assistant markdown into plain block descriptors.
 *
 * Kept free of JSX and callbacks so the result can be memoised on `text` alone —
 * during streaming this runs once per frame instead of once per render.
 */
function mergeRecentMeetingsBlocks(blocks, recentCatalog) {
  const catalog = Array.isArray(recentCatalog) ? recentCatalog : [];
  if (!catalog.length) return blocks;
  const out = [];
  for (let i = 0; i < blocks.length; i += 1) {
    const b = blocks[i];
    const heading = b.type === "h" ? String(b.content || "").replace(/\*\*/g, "").trim() : "";
    if (/recent meetings/i.test(heading)) {
      const next = blocks[i + 1];
      if (next && (next.type === "ul" || next.type === "ol")) {
        out.push({ type: "recentMeetings", meetings: catalog.slice(0, 6) });
        i += 1;
        continue;
      }
      if (/with content/i.test(heading)) {
        out.push({ type: "recentMeetings", meetings: catalog.slice(0, 6) });
        continue;
      }
    }
    out.push(b);
  }
  return out;
}

function assistantMeetingCardRow(block, meetingPool) {
  const id = String(block?.id || "").trim();
  const pool = Array.isArray(meetingPool) ? meetingPool : [];
  const full =
    pool.find((m) => String(m?.id ?? m?._id ?? "") === id) ||
    pool.find((m) => String(m?.googleEventId || m?.google_event_id || "") === id) ||
    null;
  const participants = full
    ? humanParticipants(full.participants || []).filter((p) => !participantIsBot(p)).length
    : Array.isArray(block?.participants)
      ? block.participants.length
      : 0;
  return {
    id,
    title: String(block?.title || full?.title || "Meeting").trim(),
    date: String(block?.date || full?.date || ""),
    time: String(block?.time || formatMeetingTimeForDisplay(full?.time) || ""),
    participantCount: participants,
    hasSummary: Boolean(String(block?.summary || full?.summary || "").trim()),
    hasTranscript:
      Boolean(block?.transcriptExcerpt) ||
      meetingHasTranscriptData(full || block),
  };
}

function AssistantRecentMeetingsCards({ meetings, meetingPool, onOpenMeeting, onViewAll }) {
  const rows = (Array.isArray(meetings) ? meetings : [])
    .map((m) => assistantMeetingCardRow(m, meetingPool))
    .filter((r) => r.id && r.title);
  if (!rows.length) return null;
  const btnStyle = {
    flex: 1,
    minWidth: 0,
    padding: "7px 8px",
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: "#fff",
    color: T.text,
    fontSize: 11,
    fontWeight: 600,
    cursor: onOpenMeeting ? "pointer" : "default",
  };
  return (
    <div style={{ margin: "4px 0 10px", maxWidth: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 800, color: "#0F4C81" }}>
          <Svg d={IC.clock} size={14} color="#0F4C81" stroke={2.1} />
          Recent meetings
        </div>
        {typeof onViewAll === "function" ? (
          <button
            type="button"
            onClick={onViewAll}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              fontSize: 11,
              fontWeight: 700,
              color: T.accent,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            View all →
          </button>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((row) => (
          <div
            key={row.id}
            style={{
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: "10px 11px",
              background: "#fff",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.35, minWidth: 0, overflowWrap: "anywhere" }}>
                {row.title}
              </div>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 9.5,
                  fontWeight: 700,
                  padding: "3px 8px",
                  borderRadius: 999,
                  background: "#DCFCE7",
                  color: "#166534",
                  border: "1px solid #BBF7D0",
                }}
              >
                With content
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Svg d={IC.cal} size={11} color={T.textMuted} stroke={2} />
                {formatYmdDisplay(row.date)}
                {row.time ? ` · ${row.time}` : ""}
              </span>
              {row.participantCount > 0 ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Svg d={IC.users} size={11} color={T.textMuted} stroke={2} />
                  {row.participantCount}
                </span>
              ) : null}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" style={btnStyle} onClick={() => onOpenMeeting?.(row.id, row.title, "action-items")}>
                Open
              </button>
              <button type="button" style={btnStyle} onClick={() => onOpenMeeting?.(row.id, row.title, "transcript")}>
                Summary
              </button>
              <button type="button" style={btnStyle} onClick={() => onOpenMeeting?.(row.id, row.title, "action-items")}>
                Tasks
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildAssistantChatBlocks(text, titleById) {
  const normalized = formatAssistantChatForDisplay(text, titleById);
  const lines = normalized.split("\n");
  const blocks = [];
  let paraLines = [];
  let olItems = [];
  let ulItems = [];
  let tableRows = [];

  const flushPara = () => {
    if (!paraLines.length) return;
    const content = paraLines.join("\n").trim();
    paraLines = [];
    if (!content) return;
    blocks.push({ type: "p", content });
  };
  const flushOl = () => {
    if (!olItems.length) return;
    blocks.push({ type: "ol", items: olItems.slice() });
    olItems = [];
  };
  const flushUl = () => {
    if (!ulItems.length) return;
    blocks.push({ type: "ul", items: ulItems.slice() });
    ulItems = [];
  };
  const flushTable = () => {
    if (!tableRows.length) return;
    let header = tableRows[0] || [];
    let body = tableRows.slice(1);
    if (body.length && isMarkdownTableSeparator(body[0])) {
      body = body.slice(1);
    }
    // If first row looks like a separator, skip it
    if (isMarkdownTableSeparator(header) && body.length) {
      header = body[0];
      body = body.slice(1);
      if (body.length && isMarkdownTableSeparator(body[0])) body = body.slice(1);
    }
    const allRows = [header, ...body];
    const hasUsefulCell = allRows.some((r) =>
      (Array.isArray(r) ? r : []).some((c) => {
        const v = String(c || "").trim();
        if (!v) return false;
        return !/^[-:#\d]+$/.test(v.replace(/\s/g, ""));
      })
    );
    if (!hasUsefulCell) {
      tableRows = [];
      return;
    }
    blocks.push({
      type: "table",
      headers: header.map((h) => String(h || "")),
      rows: body.map((r) => r.map((c) => String(c || ""))),
    });
    tableRows = [];
  };

  for (const rawLine of lines) {
    const trimmed = String(rawLine || "").trim();
    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    const ol = trimmed.match(/^(\d{1,2})\.\s+(.+)$/);
    const ul = trimmed.match(/^[-*•]\s+(.+)$/);
    const tableCells = parseMarkdownTableRow(trimmed);

    if (!trimmed) {
      flushPara();
      flushOl();
      flushUl();
      flushTable();
      continue;
    }

    if (isBrokenPipeArtifactLine(trimmed)) {
      // Ignore malformed table residue instead of rendering raw pipe lines.
      continue;
    }

    if (tableCells) {
      flushPara();
      flushOl();
      flushUl();
      tableRows.push(tableCells);
      continue;
    }

    flushTable();

    if (heading) {
      flushPara();
      flushOl();
      flushUl();
      blocks.push({
        type: "h",
        level: Math.min(heading[1].length, 4),
        content: heading[2],
      });
      continue;
    }
    const boldSection = trimmed.match(/^\*\*([^*]+)\*\*:?\s*$/);
    if (boldSection) {
      flushPara();
      flushOl();
      flushUl();
      blocks.push({ type: "h", level: 3, content: boldSection[1].trim() });
      continue;
    }
    if (ol) {
      flushPara();
      flushUl();
      olItems.push(ol[2]);
      continue;
    }
    if (ul) {
      flushPara();
      flushOl();
      ulItems.push(ul[1]);
      continue;
    }
    flushOl();
    flushUl();
    paraLines.push(trimmed);
  }
  flushPara();
  flushOl();
  flushUl();
  flushTable();

  return { blocks, normalized };
}

/** Merge consecutive ordered-list lines only (do not split on ul — that caused every row to show "1."). */
function mergeFragmentedOrderedLists(blocks) {
  const out = [];
  let olAcc = [];
  const flushOl = () => {
    if (olAcc.length) out.push({ type: "ol", items: olAcc.slice() });
    olAcc = [];
  };
  for (const b of blocks) {
    if (b.type === "ol") {
      olAcc.push(...b.items);
      continue;
    }
    flushOl();
    out.push(b);
  }
  flushOl();
  return out;
}

/** Pattern: 1. meet link + bullet meta → one numbered meeting block. */
function pairOlUlMeetingBlocks(blocks) {
  const out = [];
  let i = 0;
  while (i < blocks.length) {
    const b = blocks[i];
    if (b.type === "ol" && b.items?.length === 1 && i + 1 < blocks.length && blocks[i + 1].type === "ul") {
      out.push({ type: "meetingEntry", link: b.items[0], meta: blocks[i + 1].items || [] });
      i += 2;
      continue;
    }
    if (b.type === "ol" && b.items?.length === 1) {
      out.push({ type: "meetingEntry", link: b.items[0], meta: [] });
      i += 1;
      continue;
    }
    out.push(b);
    i += 1;
  }
  return out;
}

function assistantNoticeVisible(notice) {
  const n = String(notice || "").trim();
  if (!n) return false;
  return !/^(quick reply|understood your question)$/i.test(n);
}

/** Structured assistant reply: headings, tables, lists, bold, meeting links. */
function AssistantChatMessageBody({
  text,
  onOpenMeeting,
  streaming = false,
  recentMeetingsCatalog = null,
  meetingPool = null,
  onViewAllWithContent = null,
}) {
  const titleById = useMemo(() => buildAssistantMeetingTitleMap(meetingPool), [meetingPool]);
  const { blocks, normalized } = useMemo(() => {
    const built = buildAssistantChatBlocks(text, titleById);
    const merged = mergeRecentMeetingsBlocks(built.blocks, recentMeetingsCatalog);
    const listed = mergeFragmentedOrderedLists(merged);
    return {
      blocks: pairOlUlMeetingBlocks(listed),
      normalized: built.normalized,
    };
  }, [text, titleById, recentMeetingsCatalog]);

  const caret = streaming ? (
    <>
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: 2,
          height: "0.95em",
          marginLeft: 2,
          verticalAlign: "-0.12em",
          background: "currentColor",
          opacity: 0.75,
          animation: "assistantStreamCaret 0.85s step-end infinite",
        }}
      />
      <style>{`
        @keyframes assistantStreamCaret {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 0; }
        }
      `}</style>
    </>
  ) : null;

  const inline = (line, key) => renderAssistantChatInline(line, key, onOpenMeeting, titleById);

  if (!blocks.length) {
    return (
      <div style={{ fontSize: 12.5, lineHeight: 1.62, color: T.text }}>
        {inline(normalized, "empty")}
        {caret}
      </div>
    );
  }

  const listStyle = { margin: "0 0 6px", paddingLeft: 18, lineHeight: 1.48, maxWidth: "100%" };
  const liStyle = {
    marginBottom: 3,
    paddingLeft: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const headingSizes = { 1: 15, 2: 14, 3: 13, 4: 12.5 };
  let meetingSerial = 0;

  return (
    <div
      className="assistant-chat-body"
      style={{
        fontSize: 12.5,
        lineHeight: 1.62,
        color: T.text,
        maxWidth: "100%",
        minWidth: 0,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        overflowX: "hidden",
      }}
    >
      {blocks.map((b, bi) => {
        if (b.type === "h") {
          return (
            <div
              key={`h-${bi}`}
              style={{
                margin: bi === 0 ? "0 0 4px" : "10px 0 4px",
                fontSize: headingSizes[b.level] || 13,
                fontWeight: 800,
                color: "#0F4C81",
                letterSpacing: 0.1,
              }}
            >
              {inline(b.content, `h-${bi}`)}
            </div>
          );
        }
        if (b.type === "table") {
          return (
            <AssistantChatTable
              key={`t-${bi}`}
              blockKey={`t-${bi}`}
              headers={b.headers}
              rows={b.rows}
              onOpenMeeting={onOpenMeeting}
            />
          );
        }
        if (b.type === "recentMeetings") {
          return (
            <AssistantRecentMeetingsCards
              key={`rm-${bi}`}
              meetings={b.meetings}
              meetingPool={meetingPool}
              onOpenMeeting={onOpenMeeting}
              onViewAll={onViewAllWithContent}
            />
          );
        }
        if (b.type === "meetingEntry") {
          meetingSerial += 1;
          const n = meetingSerial;
          return (
            <div key={`me-${bi}`} style={{ margin: "0 0 10px", maxWidth: "100%" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: b.meta?.length ? 4 : 0 }}>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: "#64748B",
                    minWidth: 22,
                    flexShrink: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {n}.
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>{inline(b.link, `me-link-${bi}`)}</div>
              </div>
              {b.meta?.length ? (
                <ul style={{ ...listStyle, margin: "0 0 0 30px", paddingLeft: 14 }}>
                  {b.meta.map((item, ii) => (
                    <li key={ii} style={liStyle}>
                      {inline(item, `me-meta-${bi}-${ii}`)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        }
        if (b.type === "p") {
          return (
            <p
              key={`p-${bi}`}
              style={{
                margin: bi === blocks.length - 1 ? "0" : "0 0 8px",
                whiteSpace: "pre-wrap",
              }}
            >
              {inline(b.content, `p-${bi}`)}
            </p>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={`ol-${bi}`} style={{ ...listStyle, listStyle: "none", paddingLeft: 0 }}>
              {b.items.map((item, ii) => {
                meetingSerial += 1;
                const num = meetingSerial;
                return (
                  <li
                    key={ii}
                    style={{
                      ...liStyle,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#64748B",
                        minWidth: 22,
                        flexShrink: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {num}.
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>{inline(item, `ol-${bi}-${ii}`)}</div>
                  </li>
                );
              })}
            </ol>
          );
        }
        return (
          <ul key={`ul-${bi}`} style={listStyle}>
            {b.items.map((item, ii) => (
              <li key={ii} style={liStyle}>
                {inline(item, `ul-${bi}-${ii}`)}
              </li>
            ))}
          </ul>
        );
      })}
      {caret}
    </div>
  );
}

/** Cycling status phrases while Companion waits on the API / tools. */
const ASSISTANT_STATUS_PHRASES = [
  "Understanding",
  "Analyzing",
  "Searching",
  "Thinking",
  "Gathering context",
  "Preparing answer",
];

/**
 * Letter-by-letter typewriter that cycles: Searching → Thinking → …
 */
function AssistantTypingStatus({ active, label = "" }) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [visibleLen, setVisibleLen] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing | hold | deleting

  useEffect(() => {
    if (!active) {
      setPhraseIdx(0);
      setVisibleLen(0);
      setPhase("typing");
      return undefined;
    }
    // A real progress label from the server replaces the guessed phrases, and
    // stops the per-frame timers from re-rendering the panel while streaming.
    if (label) return undefined;
    const phrase = ASSISTANT_STATUS_PHRASES[phraseIdx % ASSISTANT_STATUS_PHRASES.length];
    let timer;
    if (phase === "typing") {
      if (visibleLen < phrase.length) {
        timer = setTimeout(() => setVisibleLen((n) => n + 1), 48);
      } else {
        timer = setTimeout(() => setPhase("hold"), 650);
      }
    } else if (phase === "hold") {
      timer = setTimeout(() => setPhase("deleting"), 380);
    } else if (phase === "deleting") {
      if (visibleLen > 0) {
        timer = setTimeout(() => setVisibleLen((n) => n - 1), 24);
      } else {
        setPhraseIdx((i) => (i + 1) % ASSISTANT_STATUS_PHRASES.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(timer);
  }, [active, phraseIdx, visibleLen, phase, label]);

  if (!active) return null;

  const phrase = label || ASSISTANT_STATUS_PHRASES[phraseIdx % ASSISTANT_STATUS_PHRASES.length];
  const shown = label || phrase.slice(0, visibleLen);

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minHeight: 22,
        fontSize: 12.5,
        fontWeight: 600,
        color: T.accent || "#1778D4",
        letterSpacing: 0.2,
      }}
      aria-live="polite"
      aria-label={phrase}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "linear-gradient(90deg,#1778D4 0%,#0C8F55 100%)",
          boxShadow: "0 0 0 0 rgba(23,120,212,0.45)",
          animation: "assistantStatusPulse 1.2s ease-in-out infinite",
          flexShrink: 0,
        }}
      />
      <span style={{ fontVariantNumeric: "tabular-nums", minWidth: 128 }}>
        {shown}
        <span
          style={{
            display: "inline-block",
            width: 2,
            height: "0.95em",
            marginLeft: 1,
            verticalAlign: "-0.12em",
            background: "currentColor",
            opacity: 0.75,
            animation: "assistantStatusCaret 0.85s step-end infinite",
          }}
        />
      </span>
      <style>{`
        @keyframes assistantStatusPulse {
          0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(23,120,212,0.4); }
          50% { transform: scale(1.25); opacity: 0.85; box-shadow: 0 0 0 6px rgba(23,120,212,0); }
        }
        @keyframes assistantStatusCaret {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/** Global FAB — workspace assistant (meetings, action items, attendance, usage). */
function WorkspaceAssistantFab({
  user,
  meetings,
  calendarMeetings,
  myActions,
  currentMeeting = null,
  variant = "floating",
  open: controlledOpen,
  onOpenChange,
  onOpenMeeting,
  onViewAllWithContent = null,
  launcherHidden = false,
}) {
  const { t } = useI18n();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };
  const isDrawer = variant === "drawer";
  const isMobileAssistant = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [err, setErr] = useState("");
  const [usage, setUsage] = useState(null);
  const [streamStatus, setStreamStatus] = useState("");
  const chatListRef = useRef(null);
  const streamAbortRef = useRef(null);
  const chatStorageKey = user?.email ? `meetai_workspace_chat_${String(user.email).toLowerCase()}` : "";
  const assistantMeetingPool = useMemo(
    () => [...(Array.isArray(meetings) ? meetings : []), ...(Array.isArray(calendarMeetings) ? calendarMeetings : [])],
    [meetings, calendarMeetings]
  );
  const recentMeetingsCatalog = useMemo(() => {
    const snap = buildWorkspaceSnapshot({
      user,
      meetings,
      calendarMeetings,
      myActions,
      usage,
      currentMeeting,
    });
    return Array.isArray(snap.recentContentMeetings) ? snap.recentContentMeetings : [];
  }, [user, meetings, calendarMeetings, myActions, usage, currentMeeting]);

  // Stop generation if the panel closes or the component unmounts.
  useEffect(() => {
    if (open) return;
    streamAbortRef.current?.abort();
  }, [open]);
  useEffect(() => () => streamAbortRef.current?.abort(), []);

  function nowTs() {
    return new Date().toISOString();
  }

  function formatTs(ts) {
    const dt = ts ? new Date(ts) : new Date();
    if (Number.isNaN(dt.getTime())) return "";
    return formatTimeInAppTz(dt, { hour: "2-digit", minute: "2-digit" });
  }

  function defaultWelcomeMessage() {
    return {
      role: "assistant",
      text:
        `Hi — I'm your ${BOT_DISPLAY_NAME}. Ask about your meetings (today, tomorrow, upcoming), what was discussed in recent calls, action items, Instant join, or usage stats.`,
      notice: "",
      confidence: "",
      sources: [],
      ts: nowTs(),
    };
  }

  function resetConversation() {
    const base = [defaultWelcomeMessage()];
    setMessages(base);
    setErr("");
    if (chatStorageKey) {
      try {
        localStorage.setItem(chatStorageKey, JSON.stringify(base));
      } catch (_) {}
    }
  }

  useEffect(() => {
    if (!chatStorageKey) {
      setMessages([defaultWelcomeMessage()]);
      return;
    }
    try {
      const raw = localStorage.getItem(chatStorageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length > 0) {
        setMessages(parsed);
        return;
      }
    } catch (_) {}
    setMessages([defaultWelcomeMessage()]);
    // defaultWelcomeMessage is a stable local helper for this mount path
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatStorageKey]);

  useEffect(() => {
    if (!chatStorageKey || !Array.isArray(messages) || messages.length === 0) return;
    // Skip while a reply is streaming: serialising the whole conversation on every
    // frame would stall the UI, and a partial answer is not worth persisting.
    if (messages[messages.length - 1]?.streaming) return;
    try {
      localStorage.setItem(chatStorageKey, JSON.stringify(messages));
    } catch (_) {}
  }, [chatStorageKey, messages]);

  useEffect(() => {
    if (!open) return;
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, loading]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiFetch("/meetings/usage")
      .then((d) => {
        if (!cancelled) setUsage(d?.local && typeof d.local === "object" ? d.local : null);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, meetings.length]);

  async function askWorkspaceQuestion() {
    const q = String(question || "").trim();
    if (!q) return;
    const historyForApi = buildChatHistoryForApi(messages);
    setMessages((prev) => [...prev, { role: "user", text: q, ts: nowTs() }]);
    setQuestion("");
    setLoading(true);
    setErr("");
    setStreamStatus("Understanding your question");

    const snapshot = buildWorkspaceSnapshot({
      user,
      meetings,
      calendarMeetings,
      myActions,
      usage,
      currentMeeting,
    });

    // The streaming placeholder is appended on the first delta so the status
    // indicator stays visible during the (non-streamable) tool rounds.
    let placeholderAdded = false;
    let streamedText = "";

    // Tokens arrive far faster than the screen refreshes; coalesce them into
    // one state update per frame so the panel never stutters.
    let pending = "";
    let frame = 0;
    const flush = () => {
      frame = 0;
      if (!pending) return;
      const chunk = pending;
      pending = "";
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (!last || !last.streaming) return prev;
        next[next.length - 1] = { ...last, text: last.text + chunk };
        return next;
      });
    };
    const queueDelta = (text) => {
      if (!placeholderAdded) {
        placeholderAdded = true;
        setStreamStatus("");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: "", streaming: true, sources: [], ts: nowTs() },
        ]);
      }
      streamedText += text;
      pending += text;
      if (!frame) frame = requestAnimationFrame(flush);
    };

    // The model emitted preamble and then decided to call a tool; drop the draft.
    const resetStreamedText = () => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      pending = "";
      streamedText = "";
      if (!placeholderAdded) return;
      placeholderAdded = false;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (!last || !last.streaming) return prev;
        return prev.slice(0, -1);
      });
    };

    const finalizeWith = (data) => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      pending = "";
      const finalMsg = {
        role: "assistant",
        // Server post-processing (meeting deep links, list spacing) only works on the
        // complete text, so the streamed draft is swapped for the final version here.
        text: ensureMeetingUsageUiHint(
          String(data?.answer || streamedText || ""),
          q
        ),
        notice: String(data?.notice || ""),
        confidence: String(data?.confidence || ""),
        sources: Array.isArray(data?.citations) ? data.citations : [],
        ts: nowTs(),
      };
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last && last.streaming) next[next.length - 1] = finalMsg;
        else next.push(finalMsg);
        return next;
      });
    };

    const controller = new AbortController();
    streamAbortRef.current = controller;

    try {
      let done = null;
      await apiFetchStream("/meetings/assistant/chat/stream", {
        signal: controller.signal,
        body: { question: q, snapshot, history: historyForApi },
        onEvent: (name, payload) => {
          if (name === "status") setStreamStatus(String(payload?.label || ""));
          else if (name === "delta") queueDelta(String(payload?.text || ""));
          else if (name === "reset") resetStreamedText();
          else if (name === "done") done = payload;
          else if (name === "error") throw new Error(payload?.message || "Assistant failed");
        },
      });
      finalizeWith(done || { answer: streamedText });
    } catch (e) {
      if (controller.signal.aborted) return;
      if (streamedText) {
        // Partial answer is already on screen — keep it rather than losing the content.
        finalizeWith({
          answer: streamedText,
          notice: "Response was interrupted",
          confidence: "limited",
        });
        setErr(userFacingMessage(e?.message || "The answer was interrupted."));
      } else {
        // Nothing rendered yet: retry once on the original blocking endpoint.
        try {
          const data = await apiFetch("/meetings/assistant/chat", {
            method: "POST",
            body: { question: q, snapshot, history: historyForApi },
          });
          finalizeWith(data);
        } catch (e2) {
          const msg = userFacingMessage(e2?.message || "Could not get answer.");
          setErr(msg);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              text: "Sorry, I could not answer right now. Try: “How many meetings today?” or “My open action items”.",
              notice: msg,
              confidence: "limited",
              sources: [],
              ts: nowTs(),
            },
          ]);
        }
      }
    } finally {
      if (frame) cancelAnimationFrame(frame);
      streamAbortRef.current = null;
      setStreamStatus("");
      setLoading(false);
    }
  }

  function openMeetingFromChat(meetId, meetTitle, preferTab) {
    const id = String(meetId || "").trim();
    if (!id || typeof onOpenMeeting !== "function") return;
    const found =
      assistantMeetingPool.find((m) => String(m?.id ?? m?._id ?? "") === id) ||
      assistantMeetingPool.find((m) => String(m?.googleEventId || m?.google_event_id || "") === id) ||
      { id, title: meetTitle || "Meeting" };
    onOpenMeeting(found, preferTab ? { preferTab } : undefined);
    setOpen(false);
  }

  function renderBotAvatar(size = 48) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: "0 8px 18px rgba(12,43,78,0.25)",
          border: "2px solid rgba(255,255,255,0.95)",
          flexShrink: 0,
          background: "#0F172A",
        }}
      >
        <video
          src={ROBO_ASSISTANT_VIDEO}
          autoPlay
          loop
          muted
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
    );
  }

  const quickPrompts = [
    "Meetings today?",
    "Meetings tomorrow?",
    "My open action items",
    "What was discussed recently?",
    "What have I completed?",
    "Meetings I joined",
    "My usage stats",
  ];

  const chatPanel = open ? (
    <div
      className={isDrawer ? "workspace-assistant-drawer" : undefined}
      style={
        isDrawer
          ? {
              position: "relative",
              width: "100%",
              height: "100%",
              background: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }
          : isMobileAssistant
          ? {
              // On phones the assistant takes the full screen.
              position: "fixed",
              inset: 0,
              width: "100%",
              maxWidth: "100%",
              background: "#fff",
              border: "none",
              borderRadius: 0,
              overflow: "hidden",
              zIndex: 1300,
              display: "flex",
              flexDirection: "column",
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }
          : {
              position: "fixed",
              right: expanded ? 12 : 24,
              bottom: expanded ? 12 : 20,
              top: expanded ? 12 : "auto",
              width: expanded ? "min(760px, calc(100vw - 24px))" : 380,
              maxWidth: "calc(100vw - 24px)",
              height: expanded ? "auto" : "min(560px, calc(100vh - 40px))",
              maxHeight: expanded ? "calc(100vh - 24px)" : "calc(100vh - 40px)",
              background: "#fff",
              border: `1px solid ${T.border}`,
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(15,23,42,0.25)",
              zIndex: 1200,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }
      }
    >
          <div
            style={{
              background: "linear-gradient(90deg,#1778D4 0%,#0C8F55 100%)",
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {renderBotAvatar(38)}
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.1 }}>{BOT_DISPLAY_NAME}</div>
                <div style={{ fontSize: 10.5, opacity: 0.92 }}>Your meetings, tasks & attendance</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!isDrawer && !isMobileAssistant ? (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  style={{
                    border: "none",
                    background: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                  aria-label={expanded ? "Shrink" : "Expand"}
                >
                  {expanded ? "⤡" : "⤢"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={resetConversation}
                style={{
                  border: "none",
                  background: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  width: isMobileAssistant ? 38 : 28,
                  height: isMobileAssistant ? 38 : 28,
                  borderRadius: 999,
                  cursor: "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                }}
                aria-label="Refresh chat"
              >
                ↻
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  border: "none",
                  background: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  width: isMobileAssistant ? 38 : 28,
                  height: isMobileAssistant ? 38 : 28,
                  borderRadius: 999,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Close assistant"
              >
                <svg width={14} height={14} fill="none" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <div
            ref={chatListRef}
            style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "12px 14px", background: "#F3F4F6", minWidth: 0, minHeight: 0 }}
          >
            {err ? (
              <div
                style={{
                  marginBottom: 10,
                  fontSize: 12,
                  color: "#991B1B",
                  background: "#FEF2F2",
                  border: "1px solid #FECACA",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                {err}
              </div>
            ) : null}
            {messages.length <= 1 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {quickPrompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setQuestion(p)}
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    padding: "5px 9px",
                    borderRadius: 999,
                    border: `1px solid ${T.border}`,
                    background: "#F8FBFF",
                    color: T.accent,
                    cursor: "pointer",
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
            ) : null}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, maxWidth: "100%" }}>
              {messages.map((m, idx) =>
                m.role === "user" ? (
                  <div key={idx} style={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                    <div
                      style={{
                        maxWidth: "85%",
                        minWidth: 0,
                        background: "linear-gradient(180deg,#E8F8F0 0%,#DCFCE7 100%)",
                        border: "1px solid #BBF7D0",
                        borderRadius: "14px 14px 4px 14px",
                        padding: "10px 12px",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      <div style={{ fontSize: 12.5, lineHeight: 1.58, color: T.text, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                        {m.text}
                      </div>
                      <div style={{ fontSize: 10, color: T.textMuted, marginTop: 5, textAlign: "right" }}>{formatTs(m.ts)}</div>
                    </div>
                  </div>
                ) : (
                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "flex-start", minWidth: 0, maxWidth: "100%" }}>
                    {renderBotAvatar(26)}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: "100%",
                        background: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "14px 14px 14px 4px",
                        padding: "10px 12px",
                        boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                        overflow: "visible",
                      }}
                    >
                      {assistantNoticeVisible(m.notice) ? (
                        <div
                          style={{
                            fontSize: 10.5,
                            fontWeight: 600,
                            color: "#475569",
                            marginBottom: 6,
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: "#F1F5F9",
                            overflowWrap: "anywhere",
                            display: "inline-block",
                          }}
                        >
                          {m.notice}
                        </div>
                      ) : null}
                      <AssistantChatMessageBody
                        text={m.text}
                        streaming={Boolean(m.streaming)}
                        onOpenMeeting={openMeetingFromChat}
                        recentMeetingsCatalog={recentMeetingsCatalog}
                        meetingPool={assistantMeetingPool}
                        onViewAllWithContent={
                          onViewAllWithContent
                            ? () => {
                                onViewAllWithContent();
                                setOpen(false);
                              }
                            : null
                        }
                      />
                      <div style={{ fontSize: 10, color: T.textMuted, marginTop: 6 }}>{formatTs(m.ts)}</div>
                    </div>
                  </div>
                )
              )}
              {loading && !messages[messages.length - 1]?.streaming ? (
                <div style={{ display: "flex", gap: 8, minWidth: 0 }} aria-busy="true">
                  {renderBotAvatar(28)}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: T.surface,
                      border: `1px solid ${T.border}`,
                      borderRadius: "12px 12px 12px 3px",
                      padding: "12px 14px",
                    }}
                  >
                    <AssistantTypingStatus active={loading} label={streamStatus} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div
            style={{
              flexShrink: 0,
              padding: "10px 12px",
              borderTop: "1px solid #E5E7EB",
              background: "#fff",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                className="input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask about your meetings, tasks, joins…"
                style={{ flex: 1, minWidth: 0, borderRadius: 12, padding: "10px 12px", fontSize: 13 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!loading) askWorkspaceQuestion();
                  }
                }}
              />
              <button
                type="button"
                onClick={askWorkspaceQuestion}
                disabled={loading || !String(question || "").trim()}
                style={{
                  border: "none",
                  background: "linear-gradient(90deg,#1778D4 0%,#0C8F55 100%)",
                  color: "#fff",
                  borderRadius: 999,
                  width: 40,
                  height: 40,
                  fontSize: 16,
                  cursor: "pointer",
                  opacity: loading ? 0.8 : 1,
                }}
                aria-label="Send question"
              >
                {loading ? "…" : "➤"}
              </button>
            </div>
          </div>
        </div>
  ) : null;

  if (isDrawer) return chatPanel;

  return (
    <>
      {!open && !launcherHidden ? (
      <button
        type="button"
        data-tour="workspace-chat-fab"
        onClick={() => setOpen(true)}
        aria-label={`Open ${BOT_DISPLAY_NAME}`}
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 1200,
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#0F3B66",
            background: "rgba(255,255,255,0.96)",
            border: "1px solid #C9D7E6",
            borderRadius: 999,
            padding: "7px 10px",
            boxShadow: "0 6px 16px rgba(15,23,42,0.12)",
            whiteSpace: "nowrap",
          }}
        >
          {t("askYourCompanion", COMPANION_FAB_LABEL)}
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 66,
            height: 66,
            borderRadius: "50%",
            background: "radial-gradient(circle at 30% 25%, #EAF4FF 0%, #DDEBFA 45%, #D2E3F6 100%)",
            border: "1px solid #C7D8EA",
            boxShadow: "0 10px 24px rgba(15,23,42,0.2), inset 0 1px 0 rgba(255,255,255,0.9)",
            padding: 3,
          }}
        >
          {renderBotAvatar(58)}
        </span>
      </button>
      ) : null}
      {chatPanel}
    </>
  );
}

/** Last N chat turns for API thread context (workspace Companion). */
function buildChatHistoryForApi(priorMessages) {
  const arr = Array.isArray(priorMessages) ? priorMessages : [];
  return arr
    .filter((m) => m && (m.role === "user" || m.role === "assistant") && String(m.text || "").trim())
    .slice(-14)
    .map((m) => ({
      role: m.role,
      content: String(m.text || "").trim().slice(0, 6000),
    }));
}


// Legacy transcript tab kept for reference; Meeting Details uses the redesign layout instead.
// eslint-disable-next-line no-unused-vars -- retained for potential restore / deep-link experiments
function TranscriptTab({meeting, meetingId}){
  const effectiveStatus = effectiveMeetingStatus(meeting);
  const lines = Array.isArray(meeting.transcript) ? meeting.transcript : [];
  const [dlOpen, setDlOpen] = useState(false);
  const [dlFmt, setDlFmt] = useState("pdf");
  const [dlBusy, setDlBusy] = useState(false);
  const [dlIncludeActions, setDlIncludeActions] = useState(true);
  const [dlAnchor, setDlAnchor] = useState(null);
  const dlBtnRef = useRef(null);

  const canExport = !!meetingId && lines.length > 0;

  async function doExport() {
    if (!canExport || dlBusy) return;
    setDlBusy(true);
    try {
      await downloadTranscriptExport({
        meetingId,
        format: dlFmt,
        meetingTitle: meeting?.title || "Meeting transcript",
        includeActionItems: dlIncludeActions,
      });
      setDlOpen(false);
    } catch (e) {
      window.alert(e?.message || "Export failed.");
    } finally {
      setDlBusy(false);
    }
  }

  const updateDownloadAnchor = useCallback(() => {
    const btn = dlBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const right = Math.max(12, window.innerWidth - r.right);
    setDlAnchor({ top: r.bottom + 8, right });
  }, []);

  useEffect(() => {
    if (!dlOpen) return;
    updateDownloadAnchor();
    const onMove = () => updateDownloadAnchor();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [dlOpen, updateDownloadAnchor]);

  return (
    <div>
      <TranscriptDownloadModal
        open={dlOpen}
        format={dlFmt}
        setFormat={setDlFmt}
        includeActionItems={dlIncludeActions}
        setIncludeActionItems={setDlIncludeActions}
        loading={dlBusy}
        disabled={!canExport}
        anchor={dlAnchor}
        anchorRef={dlBtnRef}
        onClose={() => (dlBusy ? null : setDlOpen(false))}
        onDownload={doExport}
      />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          ref={dlBtnRef}
          type="button"
          className="btn"
          onClick={() => {
            updateDownloadAnchor();
            setDlOpen(true);
          }}
          disabled={!canExport}
          title={!canExport ? "Transcript not available yet" : "Download transcript"}
          style={{
            padding: "8px 12px",
            borderRadius: 10,
            border: `1px solid ${T.border}`,
            background: "#fff",
            color: T.text,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: canExport ? "pointer" : "not-allowed",
            opacity: canExport ? 1 : 0.6,
          }}
        >
          Download
        </button>
      </div>
      {effectiveStatus==="live"&&<div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.accent,background:T.accentBg,padding:"9px 14px",borderRadius:8,marginBottom:16,border:`1px solid ${T.accentMid}`}}><span style={{width:6,height:6,borderRadius:"50%",background:T.accent,display:"inline-block",animation:"pulse 1.4s ease infinite"}}/>Live transcription in progress…</div>}
      {effectiveStatus==="ended"&&<div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#B45309",background:"#FEF3C7",padding:"9px 14px",borderRadius:8,marginBottom:16,border:"1px solid #FCD34D"}}>Call ended — finalizing transcript and summary…</div>}
      {lines.length===0&&<Empty
        icon={IC.mic}
        text={meetingTranscriptEmptyMessage(meeting, effectiveStatus)}
      />}
      <div style={{display:"flex",flexDirection:"column",gap:18}}>
        {lines.map((e,i)=>{
          const sp = e.speaker || "Unknown";
          const initials = sp.split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?";
          return (
            <div key={i} style={{display:"flex",gap:12}}>
              <Avatar initials={initials} size={34} color="#9CA3AF"/>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}><span style={{fontWeight:500,fontSize:13}}>{sp}</span><span style={{fontSize:11,color:T.textMuted,fontFamily:"monospace"}}>{formatTranscriptLineTime(e.time)}</span></div>
                <div style={{fontSize:13.5,color:T.text,lineHeight:1.7,background:T.surface,padding:"10px 15px",borderRadius:"4px 12px 12px 12px",border:`1px solid ${T.border}`}}>{e.text}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TranscriptDownloadModal({
  open,
  onClose,
  onDownload,
  format,
  setFormat,
  includeActionItems,
  setIncludeActionItems,
  loading,
  disabled,
  anchor,
  anchorRef,
}) {
  const panelRef = useRef(null);
  const [computedTop, setComputedTop] = useState(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const onPointer = (e) => {
      const panel = panelRef.current;
      const anchorEl = anchorRef?.current;
      const target = e.target;
      if (panel && panel.contains(target)) return;
      if (anchorEl && anchorEl.contains(target)) return;
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [open, onClose, anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const fallbackTop = Math.max(10, Number(anchor?.top || 80));
    const panelHeight = panel.getBoundingClientRect().height || 0;
    const viewportH = window.innerHeight || 0;
    let top = fallbackTop;
    if (top + panelHeight > viewportH - 10) {
      const flipped = Math.max(10, fallbackTop - panelHeight - 12);
      top = flipped;
    }
    setComputedTop(top);
  }, [open, anchor?.top, anchor?.right, format, includeActionItems, loading]);

  if (!open) return null;

  const formats = [
    { id: "pdf", label: "PDF" },
    { id: "docx", label: "DOCX" },
    { id: "csv", label: "CSV" },
    { id: "txt", label: "TXT" },
  ];

  return createPortal(
    <div
      style={{
        position: "fixed",
        zIndex: 10020,
        top: computedTop != null ? computedTop : Math.max(10, Number(anchor?.top || 80)),
        right: Math.max(10, Number(anchor?.right || 24)),
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        style={{
          width: 380,
          maxWidth: "calc(100vw - 24px)",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 12px 28px rgba(15,23,42,0.22)",
          padding: "18px 18px 14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>Download transcript</div>
          <button
            type="button"
            onClick={() => (loading ? null : onClose?.())}
            style={{
              border: "none",
              background: "transparent",
              cursor: loading ? "not-allowed" : "pointer",
              color: T.textMuted,
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close"
            disabled={loading}
          >
            <svg width={16} height={16} fill="none" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12.5, color: T.textMuted, lineHeight: 1.5 }}>
          Choose a format. We’ll download the current meeting transcript.
        </div>

        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {formats.map((f) => {
            const active = String(format) === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFormat?.(f.id)}
                disabled={loading}
                style={{
                  textAlign: "left",
                  padding: "11px 12px",
                  borderRadius: 12,
                  border: active ? `1px solid ${T.accent}` : `1px solid ${T.border}`,
                  background: active ? "rgba(23,120,212,0.08)" : "#fff",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{f.label}</div>
                <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
                  {f.id === "pdf" ? "Best for sharing" : f.id === "docx" ? "Editable document" : f.id === "csv" ? "Spreadsheet-friendly" : "Plain text"}
                </div>
              </button>
            );
          })}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12.5, color: T.textSub }}>
          <input
            type="checkbox"
            checked={includeActionItems === true}
            onChange={(e) => setIncludeActionItems?.(e.target.checked)}
            disabled={loading}
            style={{ width: 14, height: 14, accentColor: T.accent, cursor: loading ? "not-allowed" : "pointer" }}
          />
          Include action items in export
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button
            type="button"
            onClick={() => (loading ? null : onClose?.())}
            disabled={loading}
            style={{
              padding: "9px 14px",
              borderRadius: 10,
              border: `1px solid ${T.border}`,
              background: "#fff",
              color: T.textSub,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={loading || disabled}
            style={{
              padding: "9px 14px",
              borderRadius: 10,
              border: "none",
              background: T.accent,
              color: "#fff",
              fontWeight: 800,
              cursor: loading || disabled ? "not-allowed" : "pointer",
              opacity: loading || disabled ? 0.6 : 1,
              minWidth: 110,
            }}
          >
            {loading ? "Downloading…" : "Download"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function FeedbackPopover({
  open,
  onClose,
  onSubmit,
  rating,
  hoverRating,
  setRating,
  setHoverRating,
  message,
  setMessage,
  attachments,
  onPickFiles,
  onRemoveFile,
  loading,
  developerEmail,
  developerExtension,
  anchor,
  anchorRef,
  statusText,
  statusKind,
}) {
  const panelRef = useRef(null);
  const attachInputRef = useRef(null);
  const [computedTop, setComputedTop] = useState(null);
  const [isCompact, setIsCompact] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 640 : false));

  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth <= 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const onPointer = (e) => {
      const panel = panelRef.current;
      const anchorEl = anchorRef?.current;
      const target = e.target;
      if (panel && panel.contains(target)) return;
      if (anchorEl && anchorEl.contains(target)) return;
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [open, onClose, anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const fallbackTop = Math.max(10, Number(anchor?.top || 80));
    const panelHeight = panel.getBoundingClientRect().height || 0;
    const viewportH = window.innerHeight || 0;
    let top = fallbackTop;
    if (top + panelHeight > viewportH - 10) {
      top = Math.max(10, fallbackTop - panelHeight - 12);
    }
    setComputedTop(top);
  }, [open, anchor?.top, anchor?.right, message, rating, loading, statusText, attachments?.length]);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        zIndex: 10020,
        top: isCompact ? 10 : (computedTop != null ? computedTop : Math.max(10, Number(anchor?.top || 80))),
        left: isCompact ? "50%" : "auto",
        right: isCompact ? "auto" : Math.max(10, Number(anchor?.right || 24)),
        transform: isCompact ? "translateX(-50%)" : "none",
        width: isCompact ? "calc(100vw - 16px)" : "auto",
        maxHeight: isCompact ? "calc(100vh - 20px)" : "none",
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        style={{
          width: isCompact ? "100%" : 380,
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isCompact ? "calc(100vh - 20px)" : "none",
          overflowY: isCompact ? "auto" : "visible",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 12px 28px rgba(15,23,42,0.22)",
          padding: isCompact ? "14px 12px 12px" : "16px 16px 14px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>Feedback</div>
          <button
            type="button"
            onClick={() => (loading ? null : onClose?.())}
            style={{
              border: "none",
              background: "transparent",
              cursor: loading ? "not-allowed" : "pointer",
              color: T.textMuted,
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close"
            disabled={loading}
          >
            <svg width={16} height={16} fill="none" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div style={{ marginTop: 10, fontSize: 12.5, color: T.textMuted }}>Rate your experience</div>
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 2 }}>
          {Array.from({ length: 5 }).map((_, i) => {
            const value = i + 1;
            const active = value <= (hoverRating || rating);
            return (
              <button
                key={value}
                type="button"
                onMouseEnter={() => setHoverRating?.(value)}
                onMouseLeave={() => setHoverRating?.(0)}
                onClick={() => setRating?.(value)}
                aria-label={`${value} star`}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: "2px 3px",
                  cursor: loading ? "not-allowed" : "pointer",
                  color: active ? "#F59E0B" : "#D1D5DB",
                  fontSize: 24,
                  lineHeight: 1,
                }}
                disabled={loading}
              >
                ★
              </button>
            );
          })}
        </div>

        <textarea
          placeholder="Add your comments (optional)"
          value={message}
          onChange={(e) => setMessage?.(e.target.value)}
          disabled={loading}
          rows={4}
          style={{
            width: "100%",
            marginTop: 10,
            borderRadius: 10,
            border: `1px solid ${T.border}`,
            padding: "10px 11px",
            fontSize: 12.5,
            resize: "vertical",
            fontFamily: "inherit",
            color: T.text,
            background: loading ? "#F8FAFC" : "#fff",
            boxSizing: "border-box",
          }}
        />
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 12, color: T.textMuted }}>Attach screenshots (JPG/JPEG/PNG, max 5)</div>
            <button
              type="button"
              onClick={() => attachInputRef.current?.click()}
              disabled={loading || (attachments?.length || 0) >= FEEDBACK_MAX_ATTACHMENTS}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: `1px solid ${T.border}`,
                background: "#fff",
                color: T.textSub,
                fontSize: 12,
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              Attach
            </button>
          </div>
          <input
            ref={attachInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              onPickFiles?.(e.target.files);
              e.target.value = "";
            }}
          />
          {(attachments?.length || 0) > 0 ? (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {(attachments || []).map((file) => (
                <div key={file.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 8px", background: "#F8FAFC" }}>
                  <div style={{ fontSize: 12, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.filename}</div>
                  <button
                    type="button"
                    onClick={() => onRemoveFile?.(file.id)}
                    disabled={loading}
                    style={{ border: "none", background: "transparent", color: "#DC2626", fontSize: 12, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 12, fontSize: isCompact ? 11.5 : 12, color: T.textSub, lineHeight: 1.55 }}>
          <div><strong>Developer:</strong> <span style={{ overflowWrap: "anywhere" }}>{developerEmail || "Not available"}</span></div>
          <div><strong>Extension Number:</strong> {developerExtension || "Not available"}</div>
        </div>

        {statusText ? (
          <div style={{ marginTop: 10, fontSize: 12, color: statusKind === "success" ? "#15803D" : statusKind === "error" ? "#B91C1C" : "#B45309", fontWeight: statusKind === "success" ? 700 : 500 }}>
            {statusText}
          </div>
        ) : null}

        {!rating ? (
          <div style={{ marginTop: 8, fontSize: 11.5, color: "#64748b" }}>
            Select at least 1 star to enable Send.
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14, position: "sticky", bottom: 0, background: "#fff", paddingTop: 8 }}>
          <button
            type="button"
            onClick={() => (loading ? null : onClose?.())}
            disabled={loading}
            style={{
              padding: "9px 14px",
              borderRadius: 10,
              border: `1px solid ${T.border}`,
              background: "#fff",
              color: T.textSub,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={loading || !rating}
            style={{
              padding: "9px 14px",
              borderRadius: 10,
              border: "none",
              background: T.accent,
              color: "#fff",
              fontWeight: 800,
              cursor: loading || !rating ? "not-allowed" : "pointer",
              opacity: loading || !rating ? 0.6 : 1,
              minWidth: 96,
            }}
          >
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function actionStatusMeta(status) {
  const s = String(status || "pending").toLowerCase();
  if (s === "completed") return { label: "Done", bg: "#EAF8F2", color: "#087A58", border: "#B7E6D4" };
  if (s === "in_progress") return { label: "In Process", bg: "#EAF4FF", color: "#0875E1", border: "#B9D7F5" };
  if (s === "blocked") return { label: "Blocked", bg: "#FEF0F0", color: "#D9363E", border: "#F6C7C9" };
  return { label: "Pending", bg: "#FFF7E8", color: "#B86B00", border: "#F6D88A" };
}

function ActionStatusPill({ status }) {
  const m = actionStatusMeta(status);
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 999,
        background: m.bg,
        color: m.color,
        border: `1px solid ${m.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
}

const ACTION_STATUS_OPTIONS = [
  { id: "pending", label: "Pending" },
  { id: "in_progress", label: "In Process" },
  { id: "blocked", label: "Blocked" },
  { id: "completed", label: "Done" },
];

/* In-app confirm popup (replaces the browser default confirm dialog). */
function AppConfirmDialog({ open, title, message, confirmLabel, cancelLabel, hideCancel, onConfirm, onCancel }) {
  const confirmBtnRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    confirmBtnRef.current?.focus?.();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel?.();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);
  if (!open) return null;
  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10050,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: "'DM Sans',system-ui,sans-serif",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || "Please confirm"}
        style={{
          width: 380,
          maxWidth: "calc(100vw - 32px)",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 12px 28px rgba(15,23,42,0.25)",
          padding: "18px",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>
          {title || "Please confirm"}
        </div>
        <div style={{ marginTop: 8, fontSize: 13, color: T.textMuted, lineHeight: 1.55 }}>
          {message}
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          {hideCancel ? null : (
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1px solid ${T.border}`,
                background: "#fff",
                color: T.text,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {cancelLabel || "Cancel"}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              border: `1px solid ${T.accent}`,
              background: T.accent,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {confirmLabel || "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* Promise-based wrapper around AppConfirmDialog: `await confirm(msg)` → true (OK) / false (Cancel). */
function useAppConfirm() {
  const [pending, setPending] = useState(null);
  const resolverRef = useRef(null);
  const confirm = useCallback(
    (message, opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setPending({
          message,
          title: opts?.title || "Mark this task as done?",
          confirmLabel: opts?.confirmLabel || "OK",
          cancelLabel: opts?.cancelLabel || "Cancel",
          hideCancel: !!opts?.hideCancel,
        });
      }),
    []
  );
  const settle = useCallback((val) => {
    setPending(null);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(val);
  }, []);
  const handleConfirm = useCallback(() => settle(true), [settle]);
  const handleCancel = useCallback(() => settle(false), [settle]);
  const dialog = (
    <AppConfirmDialog
      open={!!pending}
      title={pending?.title}
      message={pending?.message}
      confirmLabel={pending?.confirmLabel}
      cancelLabel={pending?.cancelLabel}
      hideCancel={pending?.hideCancel}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
  return [confirm, dialog];
}

const __actionStatusOpenSetters = new Set();

function ActionStatusSelect({ value, disabled, onChange }) {
  const normalized = (() => {
    const s = String(value || "pending").toLowerCase();
    return ["completed", "in_progress", "blocked"].includes(s) ? s : "pending";
  })();
  const meta = actionStatusMeta(normalized);
  const [open, setOpen] = useState(false);
  const [anchorPos, setAnchorPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const optionRefs = useRef([]);
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  const selectedIdx = ACTION_STATUS_OPTIONS.findIndex((o) => o.id === normalized);
  const [activeIdx, setActiveIdx] = useState(selectedIdx >= 0 ? selectedIdx : 0);

  const PANEL_W = 168;
  const PANEL_EST_H = 44 + ACTION_STATUS_OPTIONS.length * 32;

  const updateAnchor = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom;
    const flipUp = spaceBelow < PANEL_EST_H + 12 && r.top > spaceBelow;
    let left = Math.min(r.right - PANEL_W, vw - PANEL_W - 8);
    if (left < 8) left = 8;
    const top = flipUp ? Math.max(8, r.top - PANEL_EST_H - 6) : r.bottom + 6;
    setAnchorPos({ top, left, placement: flipUp ? "top" : "bottom" });
  }, [PANEL_EST_H]);

  const openDropdown = useCallback(() => {
    __actionStatusOpenSetters.forEach((fn) => {
      if (fn !== setOpenRef.current) fn(false);
    });
    __actionStatusOpenSetters.add(setOpenRef.current);
    setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
    setOpen(true);
  }, [selectedIdx]);

  const closeDropdown = useCallback(() => {
    __actionStatusOpenSetters.delete(setOpenRef.current);
    setOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      __actionStatusOpenSetters.delete(setOpenRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    updateAnchor();
    const onScroll = () => updateAnchor();
    const onResize = () => updateAnchor();
    const onDocMouseDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      closeDropdown();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, updateAnchor, closeDropdown]);

  useEffect(() => {
    if (!open) return;
    const el = optionRefs.current[activeIdx];
    if (el && typeof el.focus === "function") el.focus();
  }, [open, activeIdx]);

  const commitChoice = useCallback(
    (idx) => {
      const opt = ACTION_STATUS_OPTIONS[idx];
      if (!opt) return;
      closeDropdown();
      btnRef.current?.focus();
      if (opt.id !== normalized) onChange?.(opt.id);
    },
    [normalized, onChange, closeDropdown]
  );

  const handleTriggerKey = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openDropdown();
    }
  };

  const handlePanelKey = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
      btnRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % ACTION_STATUS_OPTIONS.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + ACTION_STATUS_OPTIONS.length) % ACTION_STATUS_OPTIONS.length);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(ACTION_STATUS_OPTIONS.length - 1);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      commitChoice(activeIdx);
    }
  };

  const panel =
    open && anchorPos ? (
      <ul
        ref={panelRef}
        role="listbox"
        tabIndex={0}
        aria-activedescendant={`action-status-opt-${ACTION_STATUS_OPTIONS[activeIdx]?.id || ""}`}
        onKeyDown={handlePanelKey}
        style={{
          position: "fixed",
          top: anchorPos.top,
          left: anchorPos.left,
          width: PANEL_W,
          zIndex: 11000,
          listStyle: "none",
          margin: 0,
          padding: 5,
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          boxShadow: "0 10px 28px rgba(15,23,42,0.16)",
          outline: "none",
        }}
      >
        {ACTION_STATUS_OPTIONS.map((opt, idx) => {
          const m = actionStatusMeta(opt.id);
          const isSelected = opt.id === normalized;
          const isActive = idx === activeIdx;
          return (
            <li key={opt.id}>
              <button
                ref={(el) => {
                  optionRefs.current[idx] = el;
                }}
                id={`action-status-opt-${opt.id}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => commitChoice(idx)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  width: "100%",
                  padding: "7px 9px",
                  border: "none",
                  background: isActive ? (isSelected ? "#EAF0F5" : "#F4F8FB") : isSelected ? "#F4F8FB" : "transparent",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.text,
                  textAlign: "left",
                  outline: "none",
                  transition: "background 0.12s ease",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    background: m.bg,
                    border: `1px solid ${m.border}`,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: m.color,
                    }}
                  />
                </span>
                <span style={{ flex: 1 }}>{opt.label}</span>
                {isSelected ? <Svg d={IC.check} color={PALETTE.navy} size={13} /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={!!disabled}
        onClick={() => (open ? closeDropdown() : openDropdown())}
        onKeyDown={handleTriggerKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          height: 28,
          padding: "0 9px 0 10px",
          borderRadius: 999,
          border: `1px solid ${meta.border}`,
          background: meta.bg,
          color: meta.color,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          fontFamily: "inherit",
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: 0.15,
          whiteSpace: "nowrap",
          transition: "filter 0.15s ease, box-shadow 0.15s ease",
          boxShadow: open ? `0 0 0 3px ${meta.bg}` : "none",
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.filter = "brightness(0.97)";
        }}
        onMouseLeave={(e) => {
          if (!disabled) e.currentTarget.style.filter = "none";
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: meta.color,
            flexShrink: 0,
          }}
        />
        <span>{meta.label}</span>
        <Svg d={open ? IC.chevUp : IC.chevDown} color={meta.color} size={12} />
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </>
  );
}

function actionItemRemarksText(it) {
  return String(it?.remarks ?? "").trim();
}

function ActionRemarksField({ itemKey, value, disabled, saving, onSave }) {
  const [local, setLocal] = useState(value ?? "");
  const [savedAt, setSavedAt] = useState(null);
  const lastSavedRef = useRef(String(value ?? ""));

  useEffect(() => {
    const v = String(value ?? "");
    setLocal(v);
    lastSavedRef.current = v;
    setSavedAt(null);
  }, [itemKey, value]);

  const isDirty = String(local ?? "") !== lastSavedRef.current;

  const handleSave = async () => {
    const trimmed = String(local ?? "");
    if (!isDirty || saving || disabled) return;
    try {
      await onSave?.(trimmed);
      lastSavedRef.current = trimmed;
      setSavedAt(Date.now());
    } catch {
      /* parent shows alert */
    }
  };

  return (
    <div>
      <textarea
        className="input"
        rows={2}
        value={local}
        maxLength={4000}
        disabled={disabled || saving}
        placeholder="Add remarks — progress notes, blockers, next steps…"
        onChange={(e) => {
          setLocal(e.target.value);
          setSavedAt(null);
        }}
        style={{
          width: "100%",
          resize: "vertical",
          minHeight: 52,
          fontSize: 12.5,
          lineHeight: 1.45,
          padding: "8px 10px",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 6,
        }}
      >
        {savedAt && !isDirty ? (
          <span style={{ fontSize: 11, fontWeight: 600, color: "#15803D" }}>Saved</span>
        ) : null}
        <button
          type="button"
          disabled={disabled || saving || !isDirty}
          onClick={handleSave}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "5px 14px",
            borderRadius: 8,
            border: "none",
            cursor: disabled || saving || !isDirty ? "not-allowed" : "pointer",
            background: disabled || saving || !isDirty ? "#CBD5E1" : T.accent,
            color: "#fff",
            opacity: disabled || saving || !isDirty ? 0.85 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function ActionItemTrackingFields({
  itemKey,
  status,
  remarks,
  canEdit,
  saving,
  onStatusChange,
  onRemarksSave,
  layout = "default",
}) {
  const remarkText = String(remarks ?? "").trim();
  const [remarksOpen, setRemarksOpen] = useState(() => remarkText.length > 0);

  useEffect(() => {
    setRemarksOpen(remarkText.length > 0);
  }, [itemKey, remarkText.length]);

  useEffect(() => {
    if (remarkText.length > 0) setRemarksOpen(true);
  }, [remarkText]);

  if (layout === "reference") {
    return (
      <div className="avaada-action-card__tracking">
        <div className="avaada-action-card__tracking-row">
          <span className="avaada-action-card__tracking-label">Status</span>
          {canEdit ? (
            <ActionStatusSelect value={status} disabled={saving} onChange={onStatusChange} />
          ) : (
            <ActionStatusPill status={status} />
          )}
          <span className="avaada-action-card__tracking-divider" aria-hidden />
          {canEdit ? (
            <button
              type="button"
              className="avaada-action-card__remark-btn"
              disabled={saving}
              onClick={() => setRemarksOpen((o) => !o)}
              aria-expanded={remarksOpen}
            >
              <Svg d={IC.chat} color="currentColor" size={14} stroke={2} />
              Remark
            </button>
          ) : remarkText ? (
            <span className="avaada-action-card__remark-btn avaada-action-card__remark-btn--readonly">
              <Svg d={IC.chat} color="currentColor" size={14} stroke={2} />
              Remark
            </span>
          ) : null}
        </div>
        {canEdit && remarksOpen ? (
          <div className="avaada-action-card__remarks">
            <ActionRemarksField
              itemKey={itemKey}
              value={remarks}
              saving={saving}
              onSave={onRemarksSave}
            />
          </div>
        ) : null}
        {!canEdit && remarkText ? (
          <div className="avaada-action-card__remarks avaada-action-card__remarks--readonly">
            {remarkText}
          </div>
        ) : null}
      </div>
    );
  }

  if (!canEdit && !remarkText) {
    return (
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>Status</span>
        <ActionStatusPill status={status} />
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 10,
        paddingTop: 10,
        borderTop: `1px solid ${T.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, minWidth: 52 }}>Status</span>
        {canEdit ? (
          <ActionStatusSelect value={status} disabled={saving} onChange={onStatusChange} />
        ) : (
          <ActionStatusPill status={status} />
        )}
        {canEdit ? (
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 2,
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: 12,
              fontWeight: 500,
              color: T.textMuted,
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={remarksOpen}
              disabled={saving}
              onChange={(e) => setRemarksOpen(e.target.checked)}
              style={{
                width: 14,
                height: 14,
                accentColor: T.accent,
                cursor: saving ? "not-allowed" : "pointer",
                flexShrink: 0,
              }}
            />
            Remark
          </label>
        ) : null}
      </div>
      {canEdit && remarksOpen ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>Remarks</span>
          <ActionRemarksField
            itemKey={itemKey}
            value={remarks}
            saving={saving}
            onSave={onRemarksSave}
          />
        </div>
      ) : null}
      {!canEdit && remarkText ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>Remarks</span>
          <div
            style={{
              fontSize: 12.5,
              color: T.text,
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
              padding: "2px 0",
            }}
          >
            {remarkText}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ActionTab({
  items,
  transcript,
  meetingTitle,
  status,
  meetingId,
  meeting,
  onMeetingUpdated,
  currentUserEmail,
  currentUserName,
  participants,
  previewRequest = 0,
  seriesTasksPath = null,
  readOnly = false,
  panelMode = "full",
  referenceTranscriptLayout = false,
  embedInAvaadaDetail = false,
  meetingActionCount = null,
  onShare = null,
  shareLabel = "Share",
  initialScope = "mine",
}) {
  const [seriesData, setSeriesData] = useState(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [seriesErr, setSeriesErr] = useState("");
  const [scope, setScope] = useState(initialScope);
  const [savingId, setSavingId] = useState(null);
  const [confirmTaskDone, confirmTaskDoneDialog] = useAppConfirm();
  const [personGroupsOpen, setPersonGroupsOpen] = useState({});
  const [taskQuery, setTaskQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterWrapRef = useRef(null);
  const sortWrapRef = useRef(null);
  const referenceTranscript =
    referenceTranscriptLayout && panelMode === "transcript-only";
  const referenceActionLayout = embedInAvaadaDetail && panelMode === "tasks-only";
  const fromMeetingCount =
    meetingActionCount != null ? meetingActionCount : (items || []).length;
  const [previewOpen, setPreviewOpen] = useState(() => referenceTranscript);
  const [dlOpen, setDlOpen] = useState(false);
  const [dlFmt, setDlFmt] = useState("pdf");
  const [dlBusy, setDlBusy] = useState(false);
  const [dlIncludeActions, setDlIncludeActions] = useState(true);
  const [dlAnchor, setDlAnchor] = useState(null);
  useEffect(() => {
    if (!previewRequest) return undefined;
    setPreviewOpen(true);
    const id = setTimeout(() => {
      document.getElementById("meeting-transcript-preview")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
    return () => clearTimeout(id);
  }, [previewRequest]);
  const dlBtnRef = useRef(null);

  const myEmail = String(currentUserEmail || "").trim().toLowerCase();
  const myName = String(currentUserName || "").trim().toLowerCase();
  const myFirstName = myName.split(/\s+/)[0] || "";

  // Map name -> roster email so we can attribute items even when `email` column is empty.
  const rosterByName = useMemo(() => {
    const out = {};
    for (const p of participants || []) {
      const nm = String(p?.name || "").trim().toLowerCase();
      const em = String(p?.email || "").trim().toLowerCase();
      if (nm && em) out[nm] = em;
    }
    return out;
  }, [participants]);

  // Lookup: email/name -> { name, email, initials, avatar_url } from the meeting roster.
  const personMeta = useMemo(() => {
    const out = new Map();
    for (const p of participants || []) {
      const em = String(p?.email || "").trim().toLowerCase();
      const nm = String(p?.name || "").trim();
      const ini =
        p?.initials ||
        (nm
          ? nm.split(/\s+/).map((x) => x[0]).join("").slice(0, 2).toUpperCase()
          : em
          ? em.slice(0, 2).toUpperCase()
          : "?");
      const meta = {
        name: nm || (em ? em.split("@")[0] : "Participant"),
        email: em,
        initials: ini,
        avatar_url: p?.avatar_url || p?.avatarUrl || p?.picture || "",
      };
      if (em) out.set(em, meta);
      if (nm) out.set(nm.toLowerCase(), meta);
    }
    return out;
  }, [participants]);

  // Guest / placeholder emails that should NOT be treated as a person's real identity.
  // Carry-over tasks from older meetings sometimes carry these — we'd rather resolve
  // them to the real Avaada email via the roster so the same person doesn't appear twice.
  function isPlaceholderEmail(em) {
    const e = String(em || "").toLowerCase();
    if (!e) return true;
    if (e.startsWith("guest-")) return true;
    if (e.endsWith("@gmeet.link")) return true;
    if (e.endsWith("@meet.google.com")) return true;
    if (e.includes("noreply") || e.includes("no-reply")) return true;
    return false;
  }

  // Split a name on any non-letter separator so "venkata.hemanth", "venkata hemanth kumar",
  // and "venkata-hemanth" all yield first token = "venkata".
  function firstToken(s) {
    return String(s || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)[0] || "";
  }

  function emailForAssignee(it) {
    const direct = String(it?.email || "").trim().toLowerCase();
    const nm = String(it?.assignee || "").trim().toLowerCase();
    // Prefer the roster-resolved real email when the row only has a placeholder.
    if (direct && !isPlaceholderEmail(direct)) return direct;
    if (nm && rosterByName[nm]) return rosterByName[nm];
    // First-token match in the roster (handles different display formats for the same
    // person: "venkata.hemanth" vs "Venkata Hemanth Kumar"). Only applied when EXACTLY
    // ONE roster member shares that first token (avoids "Ankit Sharma" → "Ankit Chauhan"
    // mis-attribution when both are on the same call).
    if (nm) {
      const first = firstToken(nm);
      if (first) {
        const matches = Object.keys(rosterByName).filter((k) => firstToken(k) === first);
        if (matches.length === 1) return rosterByName[matches[0]];
      }
    }
    // If we got here and the direct email is a placeholder, return "" so the caller can
    // fall back to name-based grouping instead of keying by the unstable placeholder.
    return isPlaceholderEmail(direct) ? "" : direct;
  }

  /**
   * Match an action item to the signed-in user via any of:
   *  - item.email == my email
   *  - roster(item.assignee) == my email
   *  - item.assignee name == my name (or starts with my first name, when AI dropped surname)
   * This avoids "My Tasks (0)" for legacy rows where AI only stored a display name.
   */
  function isMyItem(it) {
    if (!myEmail && !myName) return false;
    const itEmail = emailForAssignee(it);
    if (myEmail && itEmail && itEmail === myEmail) return true;
    const assigneeName = String(it?.assignee || "").trim().toLowerCase();
    if (!assigneeName) return false;
    if (myName && assigneeName === myName) return true;
    if (myFirstName && assigneeName.split(/\s+/)[0] === myFirstName) {
      // Only accept first-name-only match when the email column is empty (otherwise we'd
      // mis-attribute "Ankit Sharma" tasks to "Ankit Chauhan").
      if (!itEmail) return true;
    }
    return false;
  }

  function groupByPerson(list) {
    const map = new Map();
    for (const it of list) {
      const email = emailForAssignee(it);
      const nm = String(it.assignee || "").trim();
      const key = email || nm.toLowerCase() || "__unassigned__";
      if (!map.has(key)) {
        const meta =
          personMeta.get(email) ||
          (nm ? personMeta.get(nm.toLowerCase()) : null) ||
          null;
        const fallbackName =
          meta?.name || nm || (email ? email.split("@")[0] : "Unassigned");
        const fallbackInitials =
          meta?.initials ||
          (nm
            ? nm.split(/\s+/).map((x) => x[0]).join("").slice(0, 2).toUpperCase()
            : email
            ? email.slice(0, 2).toUpperCase()
            : "?");
        map.set(key, {
          key,
          name: fallbackName,
          email: meta?.email || email || "",
          initials: fallbackInitials,
          avatar_url: meta?.avatar_url || "",
          items: [],
        });
      }
      map.get(key).items.push(it);
    }
    const order = (g) => {
      // Current user first, then by total tasks desc, then by name.
      if (g.items.some(isMyItem)) return -1;
      return 0;
    };
    return Array.from(map.values()).sort((a, b) => {
      const ao = order(a);
      const bo = order(b);
      if (ao !== bo) return ao - bo;
      const at = b.items.length - a.items.length;
      if (at !== 0) return at;
      return String(a.name).localeCompare(String(b.name));
    });
  }

  function personCounts(items) {
    const counts = { total: items.length, done: 0, open: 0 };
    for (const it of items) {
      const s = String(it.status || "").toLowerCase();
      if (s === "completed") counts.done++;
      else counts.open++;
    }
    return counts;
  }

  const seriesLoadSeqRef = useRef(0);
  const loadSeries = useCallback(async () => {
    if (!meetingId) return;
    const seq = ++seriesLoadSeqRef.current;
    setLoadingSeries(true);
    setSeriesErr("");
    try {
      const path =
        seriesTasksPath ||
        `/meetings/${encodeURIComponent(meetingId)}/series-tasks`;
      const data = await apiFetch(path);
      // Ignore outdated responses so a slow pre-click fetch cannot reopen a just-closed task.
      if (seq !== seriesLoadSeqRef.current) return;
      setSeriesData((prev) => {
        if (!prev?.items?.length || !data?.items) return data;
        return {
          ...data,
          items: mergeActionItemsByUpdatedAt(prev.items, data.items),
        };
      });
    } catch (e) {
      if (seq !== seriesLoadSeqRef.current) return;
      setSeriesErr(e?.message || "Failed to load tasks");
      setSeriesData(null);
    } finally {
      if (seq === seriesLoadSeqRef.current) setLoadingSeries(false);
    }
  }, [meetingId, seriesTasksPath]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries, items]);

  // Always derive a guaranteed-visible list from the meeting prop (covers any
  // network error or empty series response — items from THIS meeting must still render).
  const fallbackItems = useMemo(
    () =>
      (items || []).map((it) => ({
        ...it,
        meeting_id: meetingId,
        meeting_title: meetingTitle,
        is_carryover: false,
      })),
    [items, meetingId, meetingTitle]
  );

  // Merge series response with the meeting's own items so nothing ever disappears.
  const seriesItems = useMemo(() => {
    const fromApi = Array.isArray(seriesData?.items) ? seriesData.items : [];
    const seen = new Set(fromApi.map((it) => String(it.id)));
    const merged = [...fromApi];
    for (const it of fallbackItems) {
      if (!seen.has(String(it.id))) {
        merged.push(it);
        seen.add(String(it.id));
      }
    }
    return merged;
  }, [seriesData, fallbackItems]);

  // Main list: hide completed tasks that belong to a *previous* meeting — closure lives
  // in the "Previous meeting" recap bars only (cycle UX). Current-meeting items always show.
  const listItems = useMemo(() => {
    const isCompleted = (it) => String(it.status || "").toLowerCase() === "completed";
    return seriesItems.filter((it) => {
      if (String(it.meeting_id) === String(meetingId)) return true;
      return !isCompleted(it);
    });
  }, [seriesItems, meetingId]);

  const carryoverItems = listItems.filter(
    (it) => it.is_carryover && String(it.status || "").toLowerCase() !== "completed"
  );

  const myItems = listItems.filter(isMyItem);

  const allItems = listItems;

  const visibleItems = scope === "mine" ? myItems : allItems;

  const filteredVisibleItems = useMemo(() => {
    let list = [...visibleItems];
    const q = taskQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (it) =>
          String(it.text || "")
            .toLowerCase()
            .includes(q) ||
          String(it.assignee || "")
            .toLowerCase()
            .includes(q)
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((it) => {
        const s = String(it.status || "pending").toLowerCase();
        if (statusFilter === "open") return s !== "completed";
        return s === statusFilter;
      });
    }
    const priRank = { high: 3, medium: 2, low: 1 };
    if (sortBy === "priority") {
      list.sort(
        (a, b) =>
          (priRank[String(b.priority || "medium").toLowerCase()] || 2) -
          (priRank[String(a.priority || "medium").toLowerCase()] || 2)
      );
    } else if (sortBy === "status") {
      list.sort((a, b) =>
        String(a.status || "pending").localeCompare(String(b.status || "pending"))
      );
    } else if (sortBy === "due") {
      list.sort((a, b) => String(a.due || "").localeCompare(String(b.due || "")));
    }
    return list;
  }, [visibleItems, taskQuery, statusFilter, sortBy]);

  useEffect(() => {
    if (!referenceActionLayout) return undefined;
    const onDoc = (e) => {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
      if (sortWrapRef.current && !sortWrapRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [referenceActionLayout]);

  // Build the previous-meeting recaps with a "task lifecycle" cycle:
  //   • M-1 (immediately previous): show ALL tasks (done + pending) — closure feedback.
  //   • M-2 / M-3 / … (older meetings): show ONLY still-open tasks — once a task is closed,
  //     it drops off the radar from the next-next meeting onwards.
  // Result is an array, most-recent meeting first.
  const previousMeetingRecaps = useMemo(() => {
    const meetings = Array.isArray(seriesData?.meetings) ? seriesData.meetings : [];
    if (meetings.length <= 1 || !meetingId) return [];
    const idx = meetings.findIndex((mt) => String(mt.id) === String(meetingId));
    if (idx <= 0) return [];

    const isCompleted = (it) => String(it.status || "").toLowerCase() === "completed";
    const out = [];

    // The "immediate" recap = the FIRST previous meeting that actually had tasks.
    // (Skips empty stand-ups so closure feedback still surfaces.)
    let firstWithTasksAssigned = false;

    for (let i = idx - 1; i >= 0; i--) {
      const prev = meetings[i];
      const rawItems = seriesItems.filter(
        (it) => String(it.meeting_id) === String(prev.id)
      );
      if (rawItems.length === 0) continue; // empty meeting — silently skip

      const isImmediate = !firstWithTasksAssigned;
      firstWithTasksAssigned = true;

      // Cycle rule:
      //   • First non-empty previous meeting → show ALL (done + pending) for closure feedback.
      //   • Older meetings → hide completed (only still-actionable items remain visible).
      const prevItems = isImmediate
        ? rawItems
        : rawItems.filter((it) => !isCompleted(it));
      if (prevItems.length === 0) continue;

      let done = 0;
      let open = 0;
      for (const it of prevItems) {
        if (isCompleted(it)) done += 1;
        else open += 1;
      }
      out.push({
        meeting: prev,
        items: prevItems,
        done,
        open,
        total: prevItems.length,
        isImmediate,
      });
    }
    return out;
  }, [seriesData, seriesItems, meetingId]);

  const immediateRecap = useMemo(
    () => previousMeetingRecaps.find((r) => r.isImmediate) || null,
    [previousMeetingRecaps]
  );
  const olderRecaps = useMemo(
    () => previousMeetingRecaps.filter((r) => !r.isImmediate),
    [previousMeetingRecaps]
  );
  const olderRecapsSummary = useMemo(() => {
    const meetings = olderRecaps.length;
    const open = olderRecaps.reduce((sum, r) => sum + r.open, 0);
    return { meetings, open };
  }, [olderRecaps]);
  const olderSeriesTitle = useMemo(() => {
    if (!olderRecaps.length) return "";
    const titles = new Set(
      olderRecaps.map((r) => String(r.meeting?.title || "").trim()).filter(Boolean)
    );
    return titles.size === 1 ? [...titles][0] : "";
  }, [olderRecaps]);
  const OLDER_RECAPS_GROUP_KEY = "__older_recaps__";

  const [recapOpenMap, setRecapOpenMap] = useState({});
  // Reset accordion state when the user navigates to a different meeting so the
  // panel doesn't remember stale open/closed positions across pages.
  useEffect(() => {
    setRecapOpenMap({});
  }, [meetingId]);
  const toggleRecapOpen = (id) =>
    setRecapOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));

  async function patchActionItem(id, patch, { reloadSeries = true } = {}) {
    if (!meetingId || !onMeetingUpdated) return;
    setSavingId(id);
    const stamped = { ...patch, updated_at: new Date().toISOString() };
    setSeriesData((prev) => {
      if (!prev) {
        return {
          seriesKey: "",
          meetings: [],
          items: seriesItems.map((a) =>
            String(a.id) === String(id) ? { ...a, ...stamped } : a
          ),
          summary: {},
        };
      }
      return {
        ...prev,
        items: (prev.items || []).map((a) =>
          String(a.id) === String(id) ? { ...a, ...stamped } : a
        ),
      };
    });
    try {
      const m = await apiFetch(`/meetings/${meetingId}/action-items/${id}`, {
        method: "PATCH",
        body: patch,
      });
      onMeetingUpdated(normalizeMeeting(m));
      // Keep local series status even when the PATCH meeting payload is for a
      // different meeting than the carry-over item's home meeting.
      setSeriesData((prev) => {
        if (!prev?.items) return prev;
        return {
          ...prev,
          items: (prev.items || []).map((a) =>
            String(a.id) === String(id) ? { ...a, ...stamped } : a
          ),
        };
      });
      if (reloadSeries) await loadSeries();
    } catch (e) {
      window.alert(e?.message || "Could not update task");
      await loadSeries();
    } finally {
      setSavingId(null);
    }
  }

  async function updateStatus(id, next) {
    const row = seriesItems.find((a) => String(a.id) === String(id));
    const prev = String(row?.status || "").toLowerCase();
    if (prev !== "completed" && String(next || "").toLowerCase() === "completed") {
      const ok = await confirmTaskDone("This will move the task to Done. Continue?");
      if (!ok) return;
    }
    await patchActionItem(id, { status: next });
  }

  async function saveRemarks(id, remarks) {
    await patchActionItem(id, { remarks }, { reloadSeries: true });
  }

  async function toggle(id) {
    const row = seriesItems.find((a) => String(a.id) === String(id));
    if (!row) return;
    const next = String(row.status || "").toLowerCase() === "completed" ? "pending" : "completed";
    await updateStatus(id, next);
  }
  const previewLines = Array.isArray(transcript) ? transcript : [];
  const canExport = !!meetingId && previewLines.length > 0;

  const recordingMeetingForToolbar = meeting
    ? normalizeMeeting({
        ...meeting,
        id: meetingId || meeting?.id || meeting?._id,
        transcript: previewLines,
      })
    : null;

  const updateDownloadAnchor = useCallback(() => {
    const btn = dlBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const right = Math.max(12, window.innerWidth - r.right);
    setDlAnchor({ top: r.bottom + 8, right });
  }, []);

  const renderTranscriptPreviewLines = () =>
    previewLines.map((e, idx) =>
      referenceTranscript ? (
        <div key={`pv-${idx}`} className="avaada-detail-transcript-line">
          <Avatar
            initials={
              String(e?.speaker || "Unknown")
                .split(/\s+/)
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "?"
            }
            size={30}
            color="#9CA3AF"
          />
          <div className="avaada-detail-transcript-line__body">
            <div className="avaada-detail-transcript-line__meta">
              <span className="avaada-detail-transcript-line__speaker">
                {String(e?.speaker || "Unknown")}
              </span>
              <span className="avaada-detail-transcript-line__time">
                {formatTranscriptLineTime(e?.time)}
              </span>
            </div>
            <div className="avaada-detail-transcript-line__bubble">
              {String(e?.text || "").trim() || "—"}
            </div>
          </div>
        </div>
      ) : (
        <div key={`pv-${idx}`} style={{ display: "flex", gap: 12 }}>
          <Avatar
            initials={
              String(e?.speaker || "Unknown")
                .split(/\s+/)
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "?"
            }
            size={30}
            color="#9CA3AF"
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
              <span style={{ fontWeight: 500, fontSize: 12.5 }}>{String(e?.speaker || "Unknown")}</span>
              <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "monospace" }}>
                {formatTranscriptLineTime(e?.time)}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                color: T.text,
                lineHeight: 1.65,
                background: T.surface,
                padding: "10px 14px",
                borderRadius: "4px 12px 12px 12px",
                border: `1px solid ${T.border}`,
              }}
            >
              {String(e?.text || "").trim() || "—"}
            </div>
          </div>
        </div>
      )
    );

  const transcriptDownloadButton = (
    <button
      ref={dlBtnRef}
      type="button"
      className={referenceTranscript ? "avaada-mui-btn avaada-mui-btn--outlined" : undefined}
      onClick={() => {
        updateDownloadAnchor();
        setDlOpen(true);
      }}
      disabled={!canExport}
      title="Download transcript"
      style={
        referenceTranscript
          ? undefined
          : {
              padding: "8px 12px",
              borderRadius: 10,
              border: `1px solid ${T.border}`,
              background: "#fff",
              color: T.text,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer",
            }
      }
    >
      Download Transcript
    </button>
  );

  const transcriptToolbar = referenceTranscript ? (
    canExport || onShare ? (
      <div className="avaada-detail-transcript-head-actions">
        {onShare ? (
          <button
            type="button"
            className="avaada-mui-btn avaada-mui-btn--outlined"
            onClick={onShare}
            title="Share meeting"
          >
            <Svg d={IC.share} size={14} color="currentColor" stroke={2} />
            {shareLabel}
          </button>
        ) : null}
        {transcriptDownloadButton}
      </div>
    ) : null
  ) : canExport || recordingMeetingForToolbar ? (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 2,
        }}
      >
        {recordingMeetingForToolbar ? (
          <MeetingRecordingButton m={recordingMeetingForToolbar} style={{ justifyContent: "flex-end" }} />
        ) : null}
        {canExport ? transcriptDownloadButton : null}
      </div>
    ) : null;

  useEffect(() => {
    if (!dlOpen) return;
    updateDownloadAnchor();
    const onMove = () => updateDownloadAnchor();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [dlOpen, updateDownloadAnchor]);

  async function doExport() {
    if (!canExport || dlBusy) return;
    setDlBusy(true);
    try {
      await downloadTranscriptExport({
        meetingId,
        format: dlFmt,
        meetingTitle: meetingTitle || "Meeting transcript",
        includeActionItems: dlIncludeActions,
      });
      setDlOpen(false);
    } catch (e) {
      window.alert(e?.message || "Export failed.");
    } finally {
      setDlBusy(false);
    }
  }

  function assigneeDisplayForItem(item) {
    const email = emailForAssignee(item);
    const nm = String(item.assignee || "").trim();
    const meta =
      personMeta.get(email) ||
      (nm ? personMeta.get(nm.toLowerCase()) : null) ||
      null;
    const name = meta?.name || nm || (email ? email.split("@")[0] : "Unassigned");
    const initials =
      meta?.initials ||
      (name !== "Unassigned"
        ? name
            .split(/\s+/)
            .map((x) => x[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()
        : "?");
    return { name, initials, avatar_url: meta?.avatar_url || "" };
  }

  function renderReferencePriorityBadge(p) {
    const pr = String(p || "medium").toLowerCase();
    const label = pr === "high" ? "High" : pr === "low" ? "Low" : "Medium";
    return (
      <span className={`avaada-action-pri avaada-action-pri--${pr}`}>
        <Svg d={IC.flag} color="currentColor" size={12} stroke={2} />
        {label}
      </span>
    );
  }

  const renderReferenceActionItem = (item) => {
    const isDone = String(item.status || "").toLowerCase() === "completed";
    const isMine = isMyItem(item);
    const saving = savingId != null && String(savingId) === String(item.id);
    const { name, initials, avatar_url } = assigneeDisplayForItem(item);
    const fromThisMeeting =
      String(item.meeting_id) === String(meetingId) && !item.is_carryover;
    return (
      <article
        key={item.id}
        className={`avaada-action-card${isDone ? " avaada-action-card--done" : ""}${
          item.is_carryover ? " avaada-action-card--carryover" : ""
        }`}
      >
        <input
          type="checkbox"
          className="avaada-action-card__check"
          checked={isDone}
          disabled={!isMine || saving}
          onChange={isMine ? () => toggle(item.id) : undefined}
          aria-label={isMine ? "Toggle done" : "Only the assignee can update this task"}
          title={isMine ? "" : "Only the assignee can update this task"}
        />
        <div className="avaada-action-card__body">
          <h3 className="avaada-action-card__title">{item.text}</h3>
          <div className="avaada-action-card__meta">
            <Avatar initials={initials} size={22} color="#9CA3AF" imageUrl={avatar_url} />
            <span className="avaada-action-card__assignee">{name}</span>
            {item.due ? (
              <span className="avaada-action-card__due">
                <Svg d={IC.cal} size={11} color="#5f7690" stroke={2} aria-hidden />
                Due {item.due}
              </span>
            ) : null}
            {fromThisMeeting ? (
              <span className="avaada-action-card__meetai">MeetAI</span>
            ) : item.is_carryover ? (
              <span className="avaada-action-card__carry-tag" title={item.meeting_title || ""}>
                Carry-over
              </span>
            ) : null}
          </div>
          <ActionItemTrackingFields
            layout="reference"
            itemKey={item.id}
            status={item.status}
            remarks={item.remarks}
            canEdit={isMine && !readOnly && !!onMeetingUpdated}
            saving={saving}
            onStatusChange={(v) => updateStatus(item.id, v)}
            onRemarksSave={(remarks) => saveRemarks(item.id, remarks)}
          />
        </div>
        <div className="avaada-action-card__aside">
          {renderReferencePriorityBadge(item.priority)}
          {isMine && !readOnly ? (
            <button
              type="button"
              className="avaada-action-card__menu"
              aria-label="Mark task done"
              disabled={saving}
              onClick={() => toggle(item.id)}
              title={isDone ? "Mark pending" : "Mark done"}
            >
              <Svg d={IC.check} color="#64748B" size={16} stroke={2.25} />
            </button>
          ) : (
            <span className="avaada-action-card__menu avaada-action-card__menu--placeholder" aria-hidden />
          )}
        </div>
      </article>
    );
  };

  const renderItem = (item, { showAssignee = true } = {}) => {
    const isDone = String(item.status || "").toLowerCase() === "completed";
    const isMine = isMyItem(item);
    const showCarrySource =
      item.is_carryover &&
      item.meeting_title &&
      previousMeetingRecaps.length === 0;
    const carrySource = showCarrySource
      ? `From: ${item.meeting_title}${item.meeting_date ? ` · ${formatYmdDisplay(item.meeting_date)}` : ""}`
      : "";
    return (
      <div
        key={item.id}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "11px 14px",
          background: isDone ? T.surface : T.bg,
          border: `1px solid ${item.is_carryover ? "#FFE3A8" : T.border}`,
          borderRadius: 10,
          transition: "background .15s",
        }}
      >
        <input
          type="checkbox"
          checked={isDone}
          disabled={!isMine || (savingId != null && String(savingId) === String(item.id))}
          onChange={isMine ? () => toggle(item.id) : undefined}
          aria-label={isMine ? "Toggle done" : "Only the assignee can update this task"}
          title={isMine ? "" : "Only the assignee can update this task"}
          style={{
            marginTop: 2,
            cursor: isMine ? "pointer" : "not-allowed",
            accentColor: T.accent,
            width: 15,
            height: 15,
            flexShrink: 0,
            opacity: isMine ? 1 : 0.5,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              color: isDone ? T.textMuted : T.text,
              textDecoration: isDone ? "line-through" : "none",
              marginBottom: 6,
              lineHeight: 1.55,
              wordBreak: "break-word",
            }}
          >
            {item.text}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {showAssignee ? (
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <Avatar
                  initials={(() => {
                    const s = String(item.assignee || "").trim();
                    return s ? s.split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "?";
                  })()}
                  size={20}
                  color="#9CA3AF"
                />
                <span style={{ fontSize: 11, color: T.textMuted }}>
                  {item.assignee || "Unassigned"}
                  {isMine ? " · You" : ""}
                </span>
              </div>
            ) : null}
            {item.due ? (
              <span className="actions-item-due" style={{ fontSize: 11, color: T.textMuted }}>
                <Svg d={IC.cal} size={11} color={T.textMuted} stroke={2} aria-hidden />
                Due {item.due}
              </span>
            ) : null}
            {item.meeting_date ? (
              <span
                title={
                  item.is_carryover
                    ? `From ${item.meeting_title || "earlier meeting"} on ${formatYmdDisplay(item.meeting_date)}`
                    : "Captured in this meeting"
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 10.5,
                  fontWeight: 600,
                  padding: "2px 7px",
                  borderRadius: 999,
                  background: item.is_carryover ? "#FFF6E0" : "#EEF2F6",
                  color: item.is_carryover ? "#B26A00" : "#475569",
                  border: `1px solid ${item.is_carryover ? "#FFE3A8" : "#D7DEE8"}`,
                  letterSpacing: "0.01em",
                  lineHeight: 1.2,
                }}
              >
                <Svg d={IC.cal} size={10} color="currentColor" stroke={2.2} aria-hidden />
                {formatYmdDisplay(item.meeting_date)}
              </span>
            ) : null}
            <PriBadge p={item.priority} />
            {item.is_carryover ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background: "#FFF6E0",
                  color: "#B26A00",
                  border: "1px solid #FFE3A8",
                }}
                title={carrySource}
              >
                Carry-over
              </span>
            ) : null}
          </div>
          {carrySource ? (
            <div style={{ marginTop: 4, fontSize: 10.5, color: T.textMuted }}>{carrySource}</div>
          ) : null}
          <ActionItemTrackingFields
            itemKey={item.id}
            status={item.status}
            remarks={item.remarks}
            canEdit={isMine && !readOnly && !!onMeetingUpdated}
            saving={savingId != null && String(savingId) === String(item.id)}
            onStatusChange={(v) => updateStatus(item.id, v)}
            onRemarksSave={(remarks) => saveRemarks(item.id, remarks)}
          />
        </div>
      </div>
    );
  };

  const renderPersonGroup = (group) => {
    const c = personCounts(group.items);
    const isMe = group.items.some(isMyItem);
    // Default: expand "YOU" group, collapse others. User can toggle freely after.
    const isOpen = personGroupsOpen[group.key] ?? isMe;
    const toggle = () =>
      setPersonGroupsOpen((prev) => ({ ...prev, [group.key]: !(prev[group.key] ?? isMe) }));
    return (
      <div
        key={group.key}
        style={{
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          background: T.bg,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          aria-controls={`person-group-body-${group.key}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 14px",
            background: isMe ? T.accentBg : T.surface,
            borderBottom: isOpen ? `1px solid ${T.border}` : "none",
            width: "100%",
            border: "none",
            borderRadius: 0,
            textAlign: "left",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <Avatar
            initials={group.initials}
            size={30}
            color="#9CA3AF"
            imageUrl={group.avatar_url}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: T.text,
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {group.name}
              </span>
              {isMe ? (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 7px",
                    borderRadius: 999,
                    background: T.accent,
                    color: "#fff",
                  }}
                >
                  YOU
                </span>
              ) : null}
            </div>
            {group.email ? (
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{group.email}</div>
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 999,
                background: "#EEF2F6",
                color: T.textSub,
              }}
              title="Total tasks"
            >
              {c.total} total
            </span>
            {c.done > 0 ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "#E6F4EA",
                  color: "#1E8E3E",
                  border: "1px solid #B7E1C7",
                }}
              >
                {c.done} done
              </span>
            ) : null}
            {c.open > 0 ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "#EEF1F6",
                  color: "#475569",
                  border: "1px solid #CBD5E1",
                }}
              >
                {c.open} open
              </span>
            ) : null}
            <Svg
              d={isOpen ? IC.chevUp : IC.chevDown}
              color={T.textMuted}
              size={14}
            />
          </div>
        </button>
        {isOpen ? (
          <div
            id={`person-group-body-${group.key}`}
            style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 12px" }}
          >
            {group.items.map((it) => renderItem(it, { showAssignee: false }))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderRecapTaskList = (recap) => (
    <ul style={{ listStyle: "none", margin: 0, padding: "8px 14px 12px" }}>
      {recap.items.map((it) => {
        const isDone = String(it.status || "").toLowerCase() === "completed";
        return (
          <li
            key={`recap-${recap.meeting.id}-${it.id}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "6px 0",
              borderBottom: `1px dashed ${T.border}`,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: isDone ? "#DCFCE7" : "#EEF1F6",
                color: isDone ? "#15803D" : "#64748B",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              {isDone ? "✓" : "•"}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12.5,
                  color: isDone ? T.textMuted : T.text,
                  textDecoration: isDone ? "line-through" : "none",
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                }}
              >
                {it.text}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 2,
                }}
              >
                <span style={{ fontSize: 10.5, color: T.textMuted }}>
                  {it.assignee || "Unassigned"}
                </span>
                <ActionStatusPill status={it.status} />
              </div>
              {actionItemRemarksText(it) ? (
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11.5,
                    color: T.textMuted,
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, color: T.text }}>Remark: </span>
                  {actionItemRemarksText(it)}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );

  const renderRecapBar = (recap, { compact = false } = {}) => {
    const isOpen = !!recapOpenMap[recap.meeting.id];
    const headerLabel = recap.isImmediate
      ? "Previous meeting"
      : "Still open from earlier meeting";
    return (
      <div
        key={`recap-bar-${recap.meeting.id}`}
        style={{
          background: "#fff",
          border: `1px solid ${recap.isImmediate ? T.border : "#FFE3A8"}`,
          borderRadius: compact ? 8 : 10,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => toggleRecapOpen(recap.meeting.id)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: compact ? "8px 12px" : "10px 14px",
            background: recap.isImmediate ? "#F8FAFC" : "#FFF8E1",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            fontFamily: "inherit",
          }}
          aria-expanded={isOpen}
        >
          <Svg
            d={IC.actionList}
            color={recap.isImmediate ? PALETTE.navy : "#B26A00"}
            size={compact ? 14 : 15}
          />
          <div style={{ fontSize: compact ? 12 : 12.5, color: T.text, flex: 1, minWidth: 0 }}>
            {compact ? null : (
              <>
                {headerLabel}:{" "}
              </>
            )}
            <strong>{recap.meeting.title || "Untitled meeting"}</strong>
            {recap.meeting.date ? (
              <span style={{ color: T.textMuted }}>
                {" "}
                · {formatYmdDisplay(recap.meeting.date)}
              </span>
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {recap.isImmediate && recap.done > 0 ? (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "#DCFCE7",
                  color: "#15803D",
                  border: "1px solid #B7E1C7",
                }}
              >
                {recap.done} done
              </span>
            ) : null}
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 999,
                background: "#EEF1F6",
                color: "#475569",
                border: "1px solid #CBD5E1",
              }}
            >
              {recap.open} open
            </span>
            <Svg
              d={isOpen ? IC.chevDown : IC.chevR}
              color={T.textMuted}
              size={14}
            />
          </div>
        </button>
        {isOpen ? (
          <>
            {recap.isImmediate && recap.meeting.summary ? (
              <div
                style={{
                  margin: "10px 14px 0",
                  padding: "10px 12px",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: T.textMuted,
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  Previous meeting summary
                </div>
                <MeetingSummaryBlock text={recap.meeting.summary} />
              </div>
            ) : null}
            {recap.isImmediate &&
            recap.items.some((it) => actionItemRemarksText(it)) ? (
              <div
                style={{
                  margin: "10px 14px 0",
                  padding: "10px 12px",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: T.textMuted,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Action item remarks
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {recap.items
                    .filter((it) => actionItemRemarksText(it))
                    .map((it) => (
                      <div key={`recap-remarks-${it.id}`}>
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: T.text,
                            marginBottom: 4,
                            lineHeight: 1.4,
                          }}
                        >
                          {it.text}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: T.textMuted,
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.5,
                          }}
                        >
                          {actionItemRemarksText(it)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
            <div
              style={{
                margin: "10px 14px 0",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: T.textMuted,
                textTransform: "uppercase",
              }}
            >
              {recap.isImmediate
                ? "Previous meeting tasks"
                : "Still-open tasks (completed ones hidden)"}
            </div>
            {renderRecapTaskList(recap)}
          </>
        ) : null}
      </div>
    );
  };

  function SubTab({ id, label, count }) {
    if (referenceActionLayout) {
      return (
        <button
          type="button"
          role="tab"
          className={`avaada-action-subtab${scope === id ? " avaada-action-subtab--active" : ""}`}
          onClick={() => setScope(id)}
          aria-selected={scope === id}
        >
          {label} ({count})
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setScope(id)}
        style={{
          padding: "6px 12px",
          borderRadius: 999,
          border: `1px solid ${scope === id ? T.accent : T.border}`,
          background: scope === id ? T.accentBg : "#fff",
          color: scope === id ? T.accent : T.textSub,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {label} <span style={{ opacity: 0.7, fontWeight: 500 }}>({count})</span>
      </button>
    );
  }

  const actionListForRender = referenceActionLayout ? filteredVisibleItems : visibleItems;

  const emptyTasksMessage = (() => {
    if (referenceActionLayout && visibleItems.length > 0 && taskQuery.trim()) {
      return "No action items match your search.";
    }
    if (referenceActionLayout && visibleItems.length > 0 && statusFilter !== "all") {
      return "No action items match this filter.";
    }
    if (scope === "mine" && allItems.length > 0) {
      return "No tasks are assigned to you in this series yet.";
    }
    if (status === "upcoming" || status === "live" || status === "ended") {
      return "Action items appear after the meeting ends";
    }
    return "No action items";
  })();

  function renderMainTaskList() {
    if (panelMode !== "full" && panelMode !== "tasks-only") return null;
    if (actionListForRender.length === 0) {
      return <Empty icon={IC.actionList} text={emptyTasksMessage} />;
    }
    if (referenceActionLayout) {
      return (
        <div className="avaada-action-card-list">
          {actionListForRender.map((it) => renderReferenceActionItem(it))}
        </div>
      );
    }
    if (scope === "all") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {groupByPerson(visibleItems).map(renderPersonGroup)}
        </div>
      );
    }
    return visibleItems.map((it) => renderItem(it));
  }

  return (
    <div
      className={
        referenceTranscript
          ? "avaada-detail-transcript-tab"
          : embedInAvaadaDetail
            ? `avaada-detail-action-tab-root${
                referenceActionLayout ? " avaada-detail-action-tab-root--reference" : ""
              }`
            : undefined
      }
      style={{ display: "flex", flexDirection: "column", gap: referenceTranscript ? 0 : 12 }}
    >
      {confirmTaskDoneDialog}
      <TranscriptDownloadModal
        open={dlOpen}
        format={dlFmt}
        setFormat={setDlFmt}
        includeActionItems={dlIncludeActions}
        setIncludeActionItems={setDlIncludeActions}
        loading={dlBusy}
        disabled={!canExport}
        anchor={dlAnchor}
        anchorRef={dlBtnRef}
        onClose={() => (dlBusy ? null : setDlOpen(false))}
        onDownload={doExport}
      />

      {referenceActionLayout ? (
        <header className="avaada-action-items-head">
          <div className="avaada-action-items-head__main">
            <span className="avaada-action-items-head__icon" aria-hidden>
              <Svg d={IC.actionList} color="#0875E1" size={22} stroke={2} />
            </span>
            <div>
              <h2 id="detail-tasks-heading" className="avaada-action-items-head__title">
                Action Items
              </h2>
              <p className="avaada-action-items-head__subtitle">Tasks identified from this meeting.</p>
            </div>
          </div>
          <span className="avaada-action-items-head__count">
            {fromMeetingCount} from this meeting
          </span>
        </header>
      ) : null}

      {(panelMode === "full" || panelMode === "tasks-only") ? (
        referenceActionLayout ? (
          <div className="avaada-action-subtabs" role="tablist" aria-label="Task scope">
            <SubTab id="mine" label="My Tasks" count={myItems.length} />
            <SubTab id="all" label="All Tasks" count={allItems.length} />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <SubTab id="mine" label="My Tasks" count={myItems.length} />
            <SubTab id="all" label="All Tasks" count={allItems.length} />
            {loadingSeries ? (
              <span style={{ fontSize: 11, color: T.textMuted }}>Loading…</span>
            ) : null}
            {seriesErr ? (
              <span style={{ fontSize: 11, color: "#B91C1C" }}>{seriesErr}</span>
            ) : null}
          </div>
        )
      ) : null}

      {(panelMode === "full" || panelMode === "tasks-only") && referenceActionLayout ? (
        <div className="avaada-action-toolbar">
          <label className="avaada-action-search">
            <Svg d={IC.search} color="#94A3B8" size={18} stroke={2} />
            <input
              type="search"
              value={taskQuery}
              onChange={(e) => setTaskQuery(e.target.value)}
              placeholder="Search action items..."
              aria-label="Search action items"
            />
          </label>
          <div className="avaada-action-toolbar__actions">
            <div className="avaada-action-popover-wrap" ref={filterWrapRef}>
              <button
                type="button"
                className="avaada-action-tool-btn"
                aria-expanded={filterOpen}
                onClick={() => {
                  setSortOpen(false);
                  setFilterOpen((o) => !o);
                }}
              >
                <Svg d={IC.filter} color="currentColor" size={16} stroke={2} />
                Filter
              </button>
              {filterOpen ? (
                <div className="avaada-action-popover" role="menu">
                  {[
                    { id: "all", label: "All statuses" },
                    { id: "open", label: "Open only" },
                    { id: "pending", label: "Pending" },
                    { id: "in_progress", label: "In Process" },
                    { id: "blocked", label: "Blocked" },
                    { id: "completed", label: "Done" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={statusFilter === opt.id}
                      className={
                        statusFilter === opt.id ? "avaada-action-popover__item--active" : undefined
                      }
                      onClick={() => {
                        setStatusFilter(opt.id);
                        setFilterOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="avaada-action-popover-wrap" ref={sortWrapRef}>
              <button
                type="button"
                className="avaada-action-tool-btn"
                aria-expanded={sortOpen}
                onClick={() => {
                  setFilterOpen(false);
                  setSortOpen((o) => !o);
                }}
              >
                <Svg d={IC.sort} color="currentColor" size={16} stroke={2} />
                Sort
              </button>
              {sortOpen ? (
                <div className="avaada-action-popover" role="menu">
                  {[
                    { id: "default", label: "Default order" },
                    { id: "priority", label: "Priority" },
                    { id: "due", label: "Due date" },
                    { id: "status", label: "Status" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={sortBy === opt.id}
                      className={sortBy === opt.id ? "avaada-action-popover__item--active" : undefined}
                      onClick={() => {
                        setSortBy(opt.id);
                        setSortOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          {loadingSeries ? (
            <span className="avaada-action-toolbar__hint">Loading series tasks…</span>
          ) : null}
          {seriesErr ? (
            <span className="avaada-action-toolbar__hint avaada-action-toolbar__hint--err">
              {seriesErr}
            </span>
          ) : null}
        </div>
      ) : null}

      {(panelMode === "full" || panelMode === "tasks-only") && previousMeetingRecaps.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {immediateRecap ? renderRecapBar(immediateRecap) : null}
          {olderRecaps.length > 0 ? (
            <div
              style={{
                background: "#fff",
                border: "1px solid #FFE3A8",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                onClick={() => toggleRecapOpen(OLDER_RECAPS_GROUP_KEY)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  background: "#FFF8E1",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
                aria-expanded={!!recapOpenMap[OLDER_RECAPS_GROUP_KEY]}
              >
                <Svg d={IC.actionList} color="#B26A00" size={15} />
                <div style={{ fontSize: 12.5, color: T.text, flex: 1, minWidth: 0 }}>
                  <strong>Earlier open tasks</strong>
                  {olderSeriesTitle ? (
                    <span style={{ color: T.textMuted }}> · {olderSeriesTitle}</span>
                  ) : null}
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                    {olderRecapsSummary.meetings} earlier meeting
                    {olderRecapsSummary.meetings === 1 ? "" : "s"} in this series
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "#EEF1F6",
                      color: "#475569",
                      border: "1px solid #CBD5E1",
                    }}
                  >
                    {olderRecapsSummary.open} open
                  </span>
                  <Svg
                    d={recapOpenMap[OLDER_RECAPS_GROUP_KEY] ? IC.chevDown : IC.chevR}
                    color={T.textMuted}
                    size={14}
                  />
                </div>
              </button>
              {recapOpenMap[OLDER_RECAPS_GROUP_KEY] ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: "8px 10px 10px",
                    borderTop: "1px solid #FFE3A8",
                    background: "#FFFCF5",
                  }}
                >
                  {olderRecaps.map((recap) => renderRecapBar(recap, { compact: true }))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {(panelMode === "full" || panelMode === "tasks-only") && carryoverItems.length > 0 && previousMeetingRecaps.length === 0 ? (
        <div
          style={{
            background: "#FFF8E1",
            border: "1px solid #FFE3A8",
            borderRadius: 10,
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <Svg d={IC.actionList} color="#B26A00" size={16} />
          <div style={{ fontSize: 12.5, color: "#7A4A00", flex: 1 }}>
            <strong>{carryoverItems.length}</strong> open task{carryoverItems.length > 1 ? "s" : ""}{" "}
            carried over from previous meeting{carryoverItems.length > 1 ? "s" : ""} in this series.
            Mark them Done / In Process / Blocked below.
          </div>
        </div>
      ) : null}

      {renderMainTaskList()}

      {referenceTranscript ? (
        <>
          <div className="avaada-meeting-detail__summary-head avaada-meeting-detail__transcript-head">
            <div className="avaada-meeting-detail__summary-head-main">
              <span className="avaada-detail-summary-head-icon" aria-hidden>
                <Svg d={IC.messageSquare} size={16} color="#0875E1" stroke={2} />
              </span>
              <h2>Transcript</h2>
            </div>
            {transcriptToolbar}
          </div>
          {canExport ? (
            <div
              id="meeting-transcript-preview"
              className="avaada-detail-transcript-preview"
              style={{ scrollMarginTop: 18 }}
            >
              <div className="avaada-detail-transcript-preview__lines">{renderTranscriptPreviewLines()}</div>
            </div>
          ) : (
            <div className="avaada-detail-transcript-empty">
              {meetingTranscriptEmptyMessage(meeting, status)}
            </div>
          )}
        </>
      ) : (panelMode === "full" || panelMode === "transcript-only") && canExport ? (
        <>
          {transcriptToolbar}

          <div
            id="meeting-transcript-preview"
            style={{
              marginTop: 6,
              scrollMarginTop: 18,
              border: `1px solid ${T.border}`,
              borderRadius: 10,
              overflow: "hidden",
              background: T.bg,
            }}
          >
            <button
              type="button"
              onClick={() => setPreviewOpen((v) => !v)}
              style={{
                width: "100%",
                border: "none",
                background: T.surface,
                color: T.text,
                textAlign: "left",
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Preview Transcript</span>
              <span style={{ color: T.textMuted, fontSize: 11 }}>{previewOpen ? "▴" : "▾"}</span>
            </button>
            {previewOpen ? (
              <div
                style={{
                  borderTop: `1px solid ${T.border}`,
                  padding: "12px 14px",
                  maxHeight: 420,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                {renderTranscriptPreviewLines()}
              </div>
            ) : null}
          </div>
        </>
      ) : panelMode === "full" || panelMode === "transcript-only" ? (
        <div
          style={{
            marginTop: 14,
            paddingTop: panelMode === "transcript-only" ? 0 : 14,
            borderTop: panelMode === "transcript-only" ? "none" : `1px solid ${T.border}`,
            fontSize: 12,
            color: T.textMuted,
            lineHeight: 1.55,
          }}
        >
          {meetingTranscriptEmptyMessage(meeting, status)}
        </div>
      ) : null}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   ACTIONS PAGE
════════════════════════════════════════════════════════════════ */
function meetingFromActionGroupId(meetings, mid) {
  const k = String(mid ?? "");
  return (Array.isArray(meetings) ? meetings : []).find((m) => String(m?.id ?? m?._id ?? "") === k);
}

function ActionsPage({ myActions, meetings, onOpen, onReloadMeetings, onMeetingUpdated, externalSearch = "", focusTarget = null, onFocusConsumed }) {
  const [filter, setFilter] = useState("all");
  const { containerRef: actionFilterTabsRef, registerTab: registerActionFilterTab, thumbStyle: actionFilterThumbStyle } =
    useSlidingTabIndicator(filter);
  const effectiveQ = String(externalSearch || "").trim();
  const [openMeetingIds, setOpenMeetingIds] = useState(() => new Set());
  const [focusedActionId, setFocusedActionId] = useState(null);
  const focusMeetingKeepRef = useRef("");
  const pendingFocusScrollRef = useRef(null);
  const [savingId, setSavingId] = useState(null);
  const [confirmTaskDone, confirmTaskDoneDialog] = useAppConfirm();
  const [statusMap, setStatusMap] = useState(() =>
    Object.fromEntries(myActions.map((a) => [String(a.id), String(a.status || "pending").toLowerCase()]))
  );
  const [remarksMap, setRemarksMap] = useState(() =>
    Object.fromEntries(myActions.map((a) => [String(a.id), String(a.remarks ?? "")]))
  );
  useEffect(() => {
    setStatusMap(
      Object.fromEntries(myActions.map((a) => [String(a.id), String(a.status || "pending").toLowerCase()]))
    );
    setRemarksMap(
      Object.fromEntries(myActions.map((a) => [String(a.id), String(a.remarks ?? "")]))
    );
  }, [myActions]);

  const effectiveStatus = (a) => statusMap[String(a.id)] || String(a.status || "pending").toLowerCase();
  const effectiveRemarks = (a) =>
    remarksMap[String(a.id)] ?? String(a.remarks ?? "");

  const searchFiltered = useMemo(() => {
    if (!effectiveQ) return myActions;
    const q = effectiveQ.toLowerCase();
    return myActions.filter((a) => {
      const text = String(a.text || a.task || "").toLowerCase();
      const title = String(a.meetingTitle || "").toLowerCase();
      const host = String(a.host || a.organizer || "").toLowerCase();
      return text.includes(q) || title.includes(q) || host.includes(q);
    });
  }, [myActions, effectiveQ]);

  const filterCounts = useMemo(() => {
    const count = (id) =>
      searchFiltered.filter((a) => {
        const stat = statusMap[String(a.id)] || String(a.status || "pending").toLowerCase();
        return id === "all" || stat === id;
      }).length;
    return {
      all: count("all"),
      pending: count("pending"),
      in_progress: count("in_progress"),
      blocked: count("blocked"),
      completed: count("completed"),
    };
  }, [searchFiltered, statusMap]);

  const shown = useMemo(
    () =>
      searchFiltered.filter((a) => {
        const stat = statusMap[String(a.id)] || String(a.status || "pending").toLowerCase();
        return filter === "all" || stat === filter;
      }),
    [searchFiltered, filter, statusMap]
  );

  const grouped = useMemo(() => {
    const g = {};
    shown.forEach((a) => {
      const mid = String(a.meetingId ?? "");
      if (!mid) return;
      if (!g[mid]) g[mid] = { title: a.meetingTitle, date: a.meetingDate, items: [] };
      g[mid].items.push(a);
    });
    return g;
  }, [shown]);

  const groupEntries = useMemo(() => {
    const entries = Object.entries(grouped);
    entries.sort((a, b) => String(b[1].date || "").localeCompare(String(a[1].date || "")));
    return entries;
  }, [grouped]);

  const pagingResetKey = `${filter}|${effectiveQ}`;
  const [listPage, setListPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  useEffect(() => {
    setListPage(0);
  }, [pagingResetKey]);
  const pageSize = Math.max(1, Number(rowsPerPage) || 8);
  const maxListPage = Math.max(0, Math.ceil(groupEntries.length / pageSize) - 1);
  const safeListPage = Math.min(Math.max(0, listPage), maxListPage);
  const pagedGroupEntries = groupEntries.slice(safeListPage * pageSize, safeListPage * pageSize + pageSize);
  const totalPages = Math.max(1, Math.ceil(groupEntries.length / pageSize) || 1);
  const pageFrom = groupEntries.length === 0 ? 0 : safeListPage * pageSize + 1;
  const pageTo = Math.min(groupEntries.length, (safeListPage + 1) * pageSize);

  useEffect(() => {
    const valid = new Set(Object.keys(grouped));
    const keep = String(focusMeetingKeepRef.current || "").trim();
    setOpenMeetingIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id) || (keep && id === keep)));
      if (keep) next.add(keep);
      return next.size === prev.size && [...prev].every((id) => next.has(id)) ? prev : next;
    });
  }, [grouped]);

  useEffect(() => {
    const mid = String(focusTarget?.meetingId || "").trim();
    const aid = String(focusTarget?.actionId || "").trim();
    if (!mid && !aid) return;

    const matched = aid
      ? myActions.find((a) => String(a.id) === aid)
      : myActions.find((a) => String(a.meetingId) === mid);
    const targetMid = String(matched?.meetingId || mid || "").trim();
    const targetAid = String(matched?.id || aid || "").trim();
    if (!targetMid) {
      onFocusConsumed?.();
      return;
    }

    focusMeetingKeepRef.current = targetMid;
    pendingFocusScrollRef.current = { mid: targetMid, aid: targetAid };
    setFilter("all");
    setOpenMeetingIds((prev) => {
      const next = new Set(prev);
      next.add(targetMid);
      return next;
    });
    if (targetAid) setFocusedActionId(targetAid);

    const clearFocus = window.setTimeout(() => {
      setFocusedActionId(null);
      focusMeetingKeepRef.current = "";
    }, 2600);
    return () => {
      window.clearTimeout(clearFocus);
    };
  }, [focusTarget?.meetingId, focusTarget?.actionId, myActions, onFocusConsumed]);

  useEffect(() => {
    const pending = pendingFocusScrollRef.current;
    if (!pending?.mid) return;
    if (filter !== "all") return;
    const midIdx = groupEntries.findIndex(([id]) => id === pending.mid);
    if (midIdx < 0) {
      pendingFocusScrollRef.current = null;
      onFocusConsumed?.();
      return;
    }
    const nextPage = Math.floor(midIdx / pageSize);
    if (safeListPage !== nextPage) {
      setListPage(nextPage);
      return;
    }
    pendingFocusScrollRef.current = null;
    const timer = window.setTimeout(() => {
      const esc = (v) => String(v).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      const sel = pending.aid
        ? `[data-action-id="${esc(pending.aid)}"]`
        : `[data-meeting-group="${esc(pending.mid)}"]`;
      document.querySelector(sel)?.scrollIntoView({ behavior: "smooth", block: "center" });
      onFocusConsumed?.();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [filter, groupEntries, pageSize, safeListPage, onFocusConsumed]);

  const toggleMeetingOpen = (mid) => {
    const k = String(mid);
    setOpenMeetingIds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const aggregateGroupStatus = (items) => {
    const stats = items.map((it) => effectiveStatus(it));
    if (stats.some((s) => s === "blocked")) return "blocked";
    if (stats.some((s) => s === "in_progress")) return "in_progress";
    if (stats.length > 0 && stats.every((s) => s === "completed")) return "completed";
    return "pending";
  };

  async function setItemStatus(item, nextStatus) {
    const id = String(item.id);
    const mid = item.meetingId;
    if (!mid || (!onReloadMeetings && !onMeetingUpdated)) return;
    const prevStatus = effectiveStatus(item);
    if (prevStatus === nextStatus) return;
    if (nextStatus === "completed" && prevStatus !== "completed") {
      const ok = await confirmTaskDone("This will move the task to Done. Continue?");
      if (!ok) return;
    }
    if (nextStatus === "in_progress" && prevStatus !== "in_progress") {
      const ok = await confirmTaskDone("This will move the task to In Process. Continue?");
      if (!ok) return;
    }
    if (nextStatus === "blocked" && prevStatus !== "blocked") {
      const ok = await confirmTaskDone("This will move the task to Blocked. Continue?");
      if (!ok) return;
    }
    const optimisticAt = new Date().toISOString();
    setStatusMap((m) => ({ ...m, [id]: nextStatus }));
    setSavingId(id);
    try {
      const updated = await apiFetch(`/meetings/${mid}/action-items/${id}`, {
        method: "PATCH",
        body: { status: nextStatus },
      });
      const row = (updated?.actionItems || []).find((a) => String(a.id) === id);
      if (row) {
        setStatusMap((m) => ({
          ...m,
          [id]: String(row.status || nextStatus).toLowerCase(),
        }));
      }
      if (updated && typeof onMeetingUpdated === "function") {
        const withFreshStamp = {
          ...updated,
          actionItems: (updated.actionItems || []).map((a) =>
            String(a.id) === id
              ? { ...a, status: nextStatus, updated_at: a.updated_at || optimisticAt }
              : a
          ),
        };
        onMeetingUpdated(withFreshStamp);
      } else if (onReloadMeetings) {
        await onReloadMeetings();
      }
    } catch (e) {
      setStatusMap((m) => ({ ...m, [id]: prevStatus }));
      window.alert(e?.message || "Could not update task");
    } finally {
      setSavingId(null);
    }
  }

  async function setItemRemarks(item, remarks) {
    const id = String(item.id);
    const mid = item.meetingId;
    if (!mid || (!onReloadMeetings && !onMeetingUpdated)) return;
    const prev = effectiveRemarks(item);
    const next = String(remarks ?? "");
    if (prev === next) return;
    setRemarksMap((m) => ({ ...m, [id]: next }));
    setSavingId(id);
    try {
      const updated = await apiFetch(`/meetings/${mid}/action-items/${id}`, {
        method: "PATCH",
        body: { remarks: next },
      });
      if (updated && typeof onMeetingUpdated === "function") {
        onMeetingUpdated(updated);
      } else if (onReloadMeetings) {
        await onReloadMeetings();
      }
    } catch (e) {
      setRemarksMap((m) => ({ ...m, [id]: prev }));
      window.alert(e?.message || "Could not save remarks");
    } finally {
      setSavingId(null);
    }
  }

  const statusPillMeta = (stat) => {
    if (stat === "completed") return { label: "Done", tone: "done", icon: IC.check };
    if (stat === "in_progress") return { label: "In Process", tone: "process", icon: IC.refresh };
    if (stat === "blocked") return { label: "Blocked", tone: "blocked", icon: IC.info };
    return { label: "Pending", tone: "pending", icon: IC.clock };
  };

  return (
    <div className="page app-page-shell">
      {confirmTaskDoneDialog}
      <div className="content-well actions-page">
        <div className="actions-page-header">
          <div className="actions-page-header-text">
            <h1>My Tasks</h1>
            <p className="actions-page-header-sub">Stay organized with your meeting tasks and actions</p>
          </div>
          <div
            ref={actionFilterTabsRef}
            className="seg-group sliding-tabs-host actions-page-filters"
            role="tablist"
            aria-label="Action item status"
          >
            <SlidingTabThumb style={actionFilterThumbStyle} className="sliding-tab-thumb sliding-tab-thumb--blue" />
            {[
              { id: "all", label: "All" },
              { id: "pending", label: "Pending" },
              { id: "in_progress", label: "In Process" },
              { id: "blocked", label: "Blocked" },
              { id: "completed", label: "Done" },
            ].map(({ id, label }) => (
              <button
                key={id}
                ref={registerActionFilterTab(id)}
                type="button"
                role="tab"
                aria-selected={filter === id}
                className={`seg-pill sliding-tab-btn${filter === id ? " active" : ""}`}
                onClick={() => setFilter(id)}
              >
                <span className="actions-filter-label">{label}</span>
                <span className="actions-filter-bubble" aria-hidden>
                  {filterCounts[id] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {groupEntries.length === 0 ? (
          <Empty
            icon={IC.actionList}
            text={effectiveQ ? "No action items match your search." : "No action items found"}
          />
        ) : (
          <div className="actions-table-card">
            <div className="actions-table-head" role="row">
              <div className="actions-th actions-th--meeting">Meeting / Task</div>
              <div className="actions-th actions-th--datetime">Date &amp; Time</div>
              <div className="actions-th actions-th--assignees">Assigned to</div>
              <div className="actions-th actions-th--link">Meeting link</div>
              <div className="actions-th actions-th--actions">Actions</div>
              <div className="actions-th actions-th--status">Status</div>
              <div className="actions-th actions-th--menu" aria-hidden />
            </div>

            <div className="actions-table-body">
              {pagedGroupEntries.map(([mid, g]) => {
                const isOpen = openMeetingIds.has(mid);
                const meetingRow = meetingFromActionGroupId(meetings, mid);
                const openCount = g.items.filter((it) => effectiveStatus(it) !== "completed").length;
                const doneCount = g.items.length - openCount;
                const startRaw = meetingRow?.time;
                const endRaw = meetingRow?.endTime || meetingRow?.end_time || meetingRow?.end;
                const startLabel =
                  startRaw != null && String(startRaw).trim() !== ""
                    ? formatMeetingTimeForDisplay(startRaw)
                    : "";
                const endLabel =
                  endRaw != null && String(endRaw).trim() !== ""
                    ? formatMeetingTimeForDisplay(endRaw)
                    : "";
                const timePart =
                  startLabel && endLabel
                    ? `${startLabel} – ${endLabel}`
                    : startLabel || endLabel || "";
                const datePart = g.date ? formatDetailHeaderDate(g.date) : "";
                const platformKey = String(meetingRow?.platform || "meet").toLowerCase();
                const platformLabel =
                  platformKey === "meet"
                    ? "Google Meet"
                    : (PLATFORM_META[platformKey]?.label || "Meeting");
                const groupStat = aggregateGroupStatus(g.items);
                const pill = statusPillMeta(groupStat);
                return (
                  <div
                    key={mid}
                    className={`actions-table-row-wrap${isOpen ? " is-open" : ""}`}
                    data-meeting-group={mid}
                  >
                    <div
                      className="actions-table-row"
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => toggleMeetingOpen(mid)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleMeetingOpen(mid);
                        }
                      }}
                    >
                      <div className="actions-td actions-td--meeting">
                        <div className="actions-meeting-icon" aria-hidden>
                          <img src={GOOGLE_CALENDAR_ICON} alt="" width={28} height={28} />
                        </div>
                        <div className="actions-meeting-text">
                          <div className="actions-meeting-title">{g.title || "Meeting"}</div>
                          <div className="actions-meeting-platform">{platformLabel}</div>
                        </div>
                      </div>

                      <div className="actions-td actions-td--datetime">
                        <span className="actions-datetime-ico" aria-hidden>
                          <Svg d={IC.cal} color="#8A9BB0" size={14} stroke={2} />
                        </span>
                        <div className="actions-datetime-text">
                          <div className="actions-datetime-date">{datePart || "—"}</div>
                          {timePart ? <div className="actions-datetime-time">{timePart}</div> : null}
                        </div>
                      </div>

                      <div className="actions-td actions-td--assignees">
                        <MeetListParticipantStack
                          participants={meetingRow?.participants}
                          meeting={meetingRow}
                          maxShow={3}
                          menuTitle="Assigned to"
                          countNoun="person"
                          showCountLabel={false}
                          avatarSize={22}
                          className="actions-assignee-stack-wrap"
                          emptyClassName="actions-assignee-empty"
                        />
                      </div>

                      <div className="actions-td actions-td--link">
                        {meetingRow ? (
                          <button
                            type="button"
                            className="actions-meeting-open-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpen(meetingRow);
                            }}
                          >
                            <Svg d={IC.video} size={14} color="currentColor" stroke={2} />
                            Open meeting
                          </button>
                        ) : (
                          <span className="actions-assignee-empty">—</span>
                        )}
                      </div>

                      <div className="actions-td actions-td--actions">
                        <button
                          type="button"
                          className={`actions-actions-btn${openCount > 0 ? " has-open" : doneCount > 0 ? " is-done" : ""}`}
                          aria-expanded={isOpen}
                          aria-label={`${g.items.length} actions`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMeetingOpen(mid);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 56 56" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 36a3 3 0 1 1 0 6a3 3 0 0 1 0-6m35.998 1c1.106 0 2.002.888 2.002 2c0 1.105-.89 2-2.002 2H18.002A1.996 1.996 0 0 1 16 39c0-1.105.89-2 2.002-2zM10 26a3 3 0 1 1 0 6a3 3 0 0 1 0-6m35.998 1c1.106 0 2.002.888 2.002 2s-.89 2-2.002 2H18.002A1.996 1.996 0 0 1 16 29c0-1.105.89-2 2.002-2zM10 16a3 3 0 1 1 0 6a3 3 0 0 1 0-6m35.998 1c1.106 0 2.002.888 2.002 2c0 1.105-.89 2-2.002 2H18.002A1.996 1.996 0 0 1 16 19c0-1.105.89-2 2.002-2z" />
                          </svg>
                          <span className="actions-actions-btn__label">
                            {g.items.length} action{g.items.length === 1 ? "" : "s"}
                            {openCount > 0 ? ` · ${openCount} open` : doneCount > 0 ? " · all done" : ""}
                          </span>
                        </button>
                      </div>

                      <div className="actions-td actions-td--status">
                        <span className={`actions-status-badge actions-status-badge--${pill.tone}`}>
                          <Svg d={pill.icon} color="currentColor" size={12} stroke={2.2} />
                          <span className="actions-status-badge__label">{pill.label}</span>
                        </span>
                      </div>

                      <div className="actions-td actions-td--menu">
                        <button
                          type="button"
                          className={`actions-row-chev${isOpen ? " is-open" : ""}`}
                          aria-label={isOpen ? "Collapse meeting tasks" : "Expand meeting tasks"}
                          aria-expanded={isOpen}
                          title={isOpen ? "Collapse" : "Expand"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMeetingOpen(mid);
                          }}
                        >
                          <Svg
                            d={isOpen ? IC.chevUp : IC.chevDown}
                            color="#5F7690"
                            size={18}
                            stroke={2}
                          />
                        </button>
                      </div>
                    </div>

                    {isOpen ? (
                      <div className="actions-meeting-body" role="region" aria-label="Task details">
                        <div className="actions-meeting-body-inner">
                          {g.items.map((item) => {
                            const stat = effectiveStatus(item);
                            const isDone = stat === "completed";
                            const isSaving = savingId === String(item.id);
                            return (
                              <div
                                key={item.id}
                                data-action-id={String(item.id)}
                                className={`actions-item-row${isDone ? " is-done" : ""}${
                                  focusedActionId && String(item.id) === String(focusedActionId)
                                    ? " is-focus"
                                    : ""
                                }`}
                              >
                                <div className="actions-item-main">
                                  <div className={`actions-item-text${isDone ? " is-done" : ""}`}>
                                    {item.text || item.task || "—"}
                                  </div>
                                  <div className="actions-item-meta">
                                    {item.due ? (
                                      <span className="actions-item-due">Due {item.due}</span>
                                    ) : null}
                                    <PriBadge p={item.priority} />
                                  </div>
                                  <ActionItemTrackingFields
                                    itemKey={item.id}
                                    status={stat}
                                    remarks={effectiveRemarks(item)}
                                    canEdit
                                    saving={isSaving}
                                    onStatusChange={(v) => setItemStatus(item, v)}
                                    onRemarksSave={(remarks) => setItemRemarks(item, remarks)}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="actions-table-footer">
              <div className="actions-table-footer-left">
                Showing {pageFrom}–{pageTo} of {groupEntries.length} tasks
              </div>
              <div className="actions-table-footer-right">
                <label className="actions-rows-per-page">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10) || 8);
                      setListPage(0);
                    }}
                    aria-label="Rows per page"
                  >
                    {[8, 10, 25, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="actions-page-numbers" role="navigation" aria-label="Pagination">
                  <button
                    type="button"
                    className="actions-page-nav"
                    disabled={safeListPage <= 0}
                    aria-label="Previous page"
                    onClick={() => setListPage((p) => Math.max(0, p - 1))}
                  >
                    <Svg d={IC.chevL} color="currentColor" size={16} stroke={2} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i)
                    .filter((i) => {
                      if (totalPages <= 7) return true;
                      if (i === 0 || i === totalPages - 1) return true;
                      return Math.abs(i - safeListPage) <= 1;
                    })
                    .reduce((acc, i, idx, arr) => {
                      if (idx > 0 && i - arr[idx - 1] > 1) acc.push("…");
                      acc.push(i);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "…" ? (
                        <span key={`e${idx}`} className="actions-page-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          className={`actions-page-num${safeListPage === item ? " is-active" : ""}`}
                          aria-current={safeListPage === item ? "page" : undefined}
                          onClick={() => setListPage(item)}
                        >
                          {item + 1}
                        </button>
                      )
                    )}
                  <button
                    type="button"
                    className="actions-page-nav"
                    disabled={safeListPage >= totalPages - 1}
                    aria-label="Next page"
                    onClick={() => setListPage((p) => Math.min(totalPages - 1, p + 1))}
                  >
                    <Svg d={IC.chevR} color="currentColor" size={16} stroke={2} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   APP TOUR — interactive feature walkthrough
════════════════════════════════════════════════════════════════ */
function appTourStorageKey(email) {
  const e = String(email || "").trim().toLowerCase();
  return e ? `meetai.appTour.completed.${e}` : "meetai.appTour.completed";
}

const APP_TOUR_STEPS = [
  {
    id: "welcome",
    page: "dashboard",
    title: `Welcome to ${BOT_DISPLAY_NAME}`,
    body: `Quick tour of the app — Upcoming meetings, Dashboard, Calendar, All Meetings, My Tasks, and Instant join. The main focus is Invite MeetAI and Video Rec toggles so ${BOT_DISPLAY_NAME} joins and records your Google Calendar meetings.`,
    placement: "center",
  },
  {
    id: "nav",
    page: "dashboard",
    target: '[data-tour="app-nav-menu"], [data-tour="app-nav"]',
    title: "About the app — navigation",
    body: "Upcoming meetings — configure upcoming Google events (default landing). Dashboard — today’s overview. All Meetings — full searchable list. Recorded Meetings — summaries after meetings. My Tasks — tasks from all your meetings.",
    placement: "right",
  },
  {
    id: "instant-join",
    page: "dashboard",
    target: '[data-tour="btn-record"]',
    title: "Instant join",
    body: `Use ${HEADER_INSTANT_JOIN_CTA} to paste a live meeting link and start recording immediately — no calendar invite required.`,
    placement: "right",
  },
  {
    id: "autojoin-tab",
    page: "autojoin",
    target: '[data-tour="nav-autojoin"]',
    title: "Upcoming meetings tab",
    body: "Upcoming Google Calendar events you can still configure. Past or ended meetings appear under Recorded Meetings or All Meetings instead.",
    placement: "right",
    featured: true,
  },
  {
    id: "global-autojoin",
    page: "autojoin",
    target: '[data-tour="auto-join-banner"]',
    title: `Global ${JOIN_MEET_AI_LABEL} switch`,
    body: `Turn this on to enable ${JOIN_MEET_AI_LABEL} for all eligible meetings. ${BOT_DISPLAY_NAME} joins as a participant at the scheduled start time.`,
    placement: "bottom",
    featured: true,
  },
  {
    id: "card-autojoin",
    page: "autojoin",
    target: '[data-tour="meeting-autojoin-toggle"]',
    title: `Per-meeting ${JOIN_MEET_AI_LABEL} toggle`,
    body: `Each meeting card has a green ${JOIN_MEET_AI_LABEL} toggle — turn it on or off for one event only (overrides the global switch for that meeting).`,
    placement: "top",
    featured: true,
  },
  {
    id: "card-video-rec",
    page: "autojoin",
    target: '[data-tour="meeting-video-rec-toggle"], [data-tour="meeting-autojoin-toggle"]',
    title: `Video Rec with ${JOIN_MEET_AI_LABEL}`,
    body: `Each row also has a Video Rec toggle — turn it on (after ${JOIN_MEET_AI_LABEL}) to save a recording and screen-share summary. The global switch may still ask about Video Rec once for all meetings.`,
    placement: "top",
    featured: true,
  },
  {
    id: "transcripts",
    page: "transcripts",
    target: '[data-tour="nav-transcripts"]',
    title: "Recorded Meetings",
    body: "Completed meetings with transcript data — combined Meeting Summary, downloads, recap email status, and the same Companion FAB for questions.",
    placement: "right",
  },
  {
    id: "all-meetings",
    page: "meetings",
    target: '[data-tour="meet-toolbar"]',
    title: "All Meetings",
    body: "Search by title, filter by day or status, and switch Grid / List. Week sections group Today, This Week, Next Week, and more.",
    placement: "bottom",
  },
  {
    id: "actions",
    page: "actions",
    target: '[data-tour="nav-actions"]',
    title: "My Tasks",
    body: "Every task captured across your meetings — filter by Pending, In Process, Blocked, or Done, and open the source meeting for full context.",
    placement: "right",
  },
  {
    id: "assistant",
    page: "dashboard",
    target: '[data-tour="workspace-chat-fab"]',
    title: "Ask your Companion",
    body: `The workspace assistant answers questions about today's meetings, your tasks, usage stats, and how to use features — from any page.`,
    placement: "left",
  },
  {
    id: "finish",
    page: "howto",
    title: "You're all set",
    body: "How it Works has full written guides. This tour runs once on first login — replay anytime from How it Works → Start App Tour or profile menu → App tour.",
    placement: "center",
  },
];

const TOUR_MARGIN = 16;
const TOUR_GAP = 12;
const TOUR_EST_CARD_H = 260;

function tourClamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function tourRectsOverlap(a, b, pad = 0) {
  if (!a || !b) return false;
  return !(
    a.left + a.width + pad <= b.left ||
    b.left + b.width + pad <= a.left ||
    a.top + a.height + pad <= b.top ||
    b.top + b.height + pad <= a.top
  );
}

function tourSidebarNavRect() {
  if (typeof document === "undefined") return null;
  const aside = document.querySelector('[data-tour="app-nav"]');
  if (!aside) return null;
  const r = aside.getBoundingClientRect();
  if (r.width < 40 || r.height < 40) return null;
  return r;
}

/** Short nav rows: pin the card beside the sidebar (avoid overlap logic pushing it over the list). */
function computeTourSidebarNavCardPosition({ targetRect, tipW, tipH, vw, vh, sidebarRect }) {
  if (!targetRect || !sidebarRect) return null;
  const margin = TOUR_MARGIN;
  const gap = TOUR_GAP;
  const left = tourClamp(sidebarRect.right + gap, margin, vw - tipW - margin);
  const top = tourClamp(targetRect.top, margin, vh - tipH - margin);
  return { top, left, placement: "right" };
}

/** Keep tooltip fully inside the viewport (never clipped at the bottom). */
function fitTourCardInViewport(top, left, tipW, tipH, vw, vh) {
  const margin = TOUR_MARGIN;
  const maxH = Math.max(120, vh - margin * 2);
  const h = Math.min(tipH, maxH);
  const maxLeft = Math.max(margin, vw - tipW - margin);
  const maxTop = Math.max(margin, vh - h - margin);
  return {
    top: tourClamp(top, margin, maxTop),
    left: tourClamp(left, margin, maxLeft),
    tipH: h,
  };
}

/** Prefer requested side; flip/clamp so the card stays in-viewport and clear of the spotlight. */
function computeTourCardPosition({ preferred, targetRect, tipW, tipH, vw, vh, sidebarRect, compactNavTarget }) {
  const margin = TOUR_MARGIN;
  const gap = TOUR_GAP;
  const fitted0 = fitTourCardInViewport(margin, margin, tipW, tipH, vw, vh);
  tipH = fitted0.tipH;

  if (compactNavTarget && sidebarRect && targetRect) {
    const pinned = computeTourSidebarNavCardPosition({ targetRect, tipW, tipH, vw, vh, sidebarRect });
    if (pinned) return pinned;
  }

  if (!targetRect || preferred === "center") {
    const c = fitTourCardInViewport((vh - tipH) / 2, (vw - tipW) / 2, tipW, tipH, vw, vh);
    return { top: c.top, left: c.left, placement: "center" };
  }

  const t = targetRect;
  const tallTarget = t.height > t.width * 1.35;
  const wideTarget = t.width > t.height * 1.6;
  let pref = preferred || "bottom";
  if (tallTarget && (pref === "bottom" || pref === "top")) pref = "right";
  if (wideTarget && (pref === "left" || pref === "right")) pref = "bottom";

  const anchorX = t.left + t.width / 2;
  // For side placements, top-align with the highlight so tall cards don't drop off-screen
  const sideTop = tourClamp(t.top, margin, Math.max(margin, vh - tipH - margin));

  const candidates = {
    bottom: {
      top: t.top + t.height + gap,
      left: anchorX - tipW / 2,
    },
    top: {
      top: t.top - tipH - gap,
      left: anchorX - tipW / 2,
    },
    left: {
      top: sideTop,
      left: t.left - tipW - gap,
    },
    right: {
      top: sideTop,
      left: t.left + t.width + gap,
    },
  };

  const sideFirst = tallTarget
    ? [pref, "right", "left", "bottom", "top"]
    : [pref, "bottom", "top", "right", "left"];
  const order = sideFirst.filter((p, i, arr) => p && candidates[p] && arr.indexOf(p) === i);

  let best = null;
  for (const placement of order) {
    const raw = candidates[placement];
    const fitted = fitTourCardInViewport(raw.top, raw.left, tipW, tipH, vw, vh);
    const card = { top: fitted.top, left: fitted.left, width: tipW, height: tipH };
    const fullyVisible =
      fitted.top >= margin - 0.5 &&
      fitted.top + tipH <= vh - margin + 0.5 &&
      fitted.left >= margin - 0.5 &&
      fitted.left + tipW <= vw - margin + 0.5;
    const overlaps = tourRectsOverlap(card, t, 6);
    let score = 0;
    if (placement === pref) score += 35;
    if (fullyVisible) score += 45;
    if (overlaps) score -= 120;
    else score += 50;
    // Prefer staying near the highlight top (readable, not bottom-docked)
    score -= Math.abs(fitted.top - t.top) / 25;
    score -= Math.abs(fitted.left - raw.left) / 12;
    if (!best || score > best.score) {
      best = { top: fitted.top, left: fitted.left, placement, score };
    }
  }

  if (best && tourRectsOverlap({ top: best.top, left: best.left, width: tipW, height: tipH }, t, 4)) {
    const forced = ["right", "left", "bottom", "top"]
      .map((placement) => {
        const raw = candidates[placement];
        const fitted = fitTourCardInViewport(raw.top, raw.left, tipW, tipH, vw, vh);
        const card = { top: fitted.top, left: fitted.left, width: tipW, height: tipH };
        if (tourRectsOverlap(card, t, 4)) return null;
        return { top: fitted.top, left: fitted.left, placement, score: 0 };
      })
      .filter(Boolean);
    if (forced.length) best = forced[0];
  }

  if (!best) {
    const c = fitTourCardInViewport((vh - tipH) / 2, (vw - tipW) / 2, tipW, tipH, vw, vh);
    return { top: c.top, left: c.left, placement: "center" };
  }
  return best;
}

function findTourTarget(selector) {
  if (!selector || typeof document === "undefined") return null;
  const parts = String(selector)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  for (const sel of parts) {
    const primary = document.querySelector(`${sel}[data-tour-primary]`);
    if (primary) {
      const r = primary.getBoundingClientRect();
      if (r.width > 2 && r.height > 2) return primary;
    }
    const nodes = document.querySelectorAll(sel);
    for (const el of nodes) {
      const r = el.getBoundingClientRect();
      if (r.width > 4 && r.height > 4 && r.bottom > 8 && r.top < window.innerHeight - 8) {
        return el;
      }
    }
    if (nodes[0]) return nodes[0];
  }
  return null;
}

function clampTourSpotlightRect(r, pad) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = Math.max(8, r.top - pad);
  let left = Math.max(8, r.left - pad);
  let width = r.width + pad * 2;
  let height = r.height + pad * 2;
  if (left + width > vw - 8) width = Math.max(24, vw - 8 - left);
  if (top + height > vh - 8) height = Math.max(24, vh - 8 - top);
  // Cap oversized targets but keep the TOP visible (don't slide the window to the bottom)
  const maxH = Math.min(vh * 0.62, vh - 160);
  const maxW = Math.min(vw * 0.92, vw - TOUR_MARGIN * 2);
  if (height > maxH) {
    height = maxH;
    top = tourClamp(top, 8, vh - height - 8);
  }
  if (width > maxW) {
    width = maxW;
    left = tourClamp(left, 8, vw - width - 8);
  }
  // Final: spotlight itself must stay fully on-screen
  if (top + height > vh - 8) {
    top = Math.max(8, vh - 8 - height);
  }
  if (left + width > vw - 8) {
    left = Math.max(8, vw - 8 - width);
  }
  return { top, left, width, height };
}

function AppTourOverlay({ open, stepIndex, onStepIndex, onClose, onNavigate, onComplete, storageKey }) {
  const step = APP_TOUR_STEPS[stepIndex] || null;
  const [targetRect, setTargetRect] = useState(null);
  const [cardPos, setCardPos] = useState(() => {
    if (typeof window === "undefined") return { top: 80, left: 16, placement: "center" };
    const tipW = Math.min(380, window.innerWidth - TOUR_MARGIN * 2);
    return {
      top: Math.max(TOUR_MARGIN, (window.innerHeight - TOUR_EST_CARD_H) / 2),
      left: Math.max(TOUR_MARGIN, (window.innerWidth - tipW) / 2),
      placement: "center",
    };
  });
  const cardRef = useRef(null);
  const total = APP_TOUR_STEPS.length;
  const isFirst = stepIndex <= 0;
  const isLast = stepIndex >= total - 1;
  const isCenter = !step?.target || step?.placement === "center";

  useEffect(() => {
    if (!open || !step) return undefined;
    onNavigate?.(step);

    const measureTarget = () => {
      if (!step.target) {
        setTargetRect(null);
        return;
      }
      const el = findTourTarget(step.target);
      if (!el) {
        setTargetRect(null);
        return;
      }
      const inSidebar = Boolean(el.closest?.('[data-tour="app-nav"]'));
      try {
        el.scrollIntoView({
          block: step.featured && !inSidebar ? "center" : "nearest",
          behavior: inSidebar ? "auto" : "smooth",
          inline: "nearest",
        });
      } catch (_) {}
      const r = el.getBoundingClientRect();
      const pad = step.featured ? 12 : 8;
      setTargetRect(clampTourSpotlightRect(r, pad));
    };

    const t1 = window.setTimeout(measureTarget, 60);
    const t2 = window.setTimeout(measureTarget, 260);
    const t3 = window.setTimeout(measureTarget, 520);
    window.addEventListener("resize", measureTarget);
    window.addEventListener("scroll", measureTarget, true);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.removeEventListener("resize", measureTarget);
      window.removeEventListener("scroll", measureTarget, true);
    };
  }, [open, stepIndex, step, onNavigate]);

  const repositionCard = useCallback(() => {
    if (!open || !step || typeof window === "undefined") return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tipW = Math.min(step.featured ? 400 : 380, vw - TOUR_MARGIN * 2);
    const el = cardRef.current;
    let tipH = TOUR_EST_CARD_H;
    if (el) {
      const rectH = el.getBoundingClientRect().height;
      const layoutH = Math.max(el.offsetHeight || 0, el.scrollHeight || 0);
      tipH = Math.max(rectH, layoutH, 120);
    }
    // Never position using a height taller than the viewport allows
    tipH = Math.min(tipH, Math.max(120, vh - TOUR_MARGIN * 2));
    const preferred = isCenter || !targetRect ? "center" : step.placement || "bottom";
    const sidebarRect = tourSidebarNavRect();
    const targetSel = String(step.target || "");
    const compactNavTarget =
      Boolean(targetRect && targetRect.height > 0 && targetRect.height < 88) &&
      (targetSel.includes("nav-") || targetSel.includes("btn-record"));
    const next = computeTourCardPosition({
      preferred,
      targetRect: isCenter ? null : targetRect,
      tipW,
      tipH,
      vw,
      vh,
      sidebarRect: compactNavTarget ? sidebarRect : null,
      compactNavTarget,
    });
    setCardPos((prev) =>
      prev && Math.abs(prev.top - next.top) < 1 && Math.abs(prev.left - next.left) < 1 && prev.placement === next.placement
        ? prev
        : next
    );
  }, [open, step, targetRect, isCenter]);

  useLayoutEffect(() => {
    repositionCard();
  }, [repositionCard, stepIndex, targetRect]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => repositionCard();
    window.addEventListener("resize", onResize);
    const el = cardRef.current;
    let ro = null;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => repositionCard());
      ro.observe(el);
    }
    const t = window.setTimeout(repositionCard, 40);
    const t2 = window.setTimeout(repositionCard, 280);
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [open, repositionCard, stepIndex]);

  const persistTourDone = useCallback(() => {
    const key = storageKey || appTourStorageKey("");
    try {
      localStorage.setItem(key, "1");
    } catch (_) {}
  }, [storageKey]);

  const finishTour = useCallback(() => {
    persistTourDone();
    onComplete?.();
    onClose?.();
  }, [onClose, onComplete, persistTourDone]);

  const skipTour = useCallback(() => {
    persistTourDone();
    onClose?.();
  }, [onClose, persistTourDone]);

  if (!open || !step || typeof document === "undefined") return null;

  const tipW = Math.min(step.featured ? 400 : 380, window.innerWidth - TOUR_MARGIN * 2);
  const tooltipStyle = {
    position: "fixed",
    zIndex: 10102,
    width: tipW,
    maxWidth: `calc(100vw - ${TOUR_MARGIN * 2}px)`,
    top: cardPos.top,
    left: cardPos.left,
    transform: "none",
  };

  return createPortal(
    <div className="app-tour-root" role="dialog" aria-modal="true" aria-label="App feature tour">
      {isCenter || !targetRect ? (
        <div className="app-tour-backdrop app-tour-backdrop--full" aria-hidden />
      ) : (
        <div
          className={`app-tour-spotlight${step.featured ? " app-tour-spotlight--featured" : ""}`}
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
          aria-hidden
        />
      )}
      <div
        ref={cardRef}
        className={`app-tour-card${step.featured ? " app-tour-card--featured" : ""}`}
        style={tooltipStyle}
        data-tour-placement={cardPos.placement || step.placement || "center"}
      >
        {!isCenter && targetRect ? <span className="app-tour-caret" aria-hidden /> : null}
        <div className="app-tour-card-head">
          <span className="app-tour-step-pill">
            Step {stepIndex + 1} of {total}
          </span>
          <div className="app-tour-card-head-right">
            <button type="button" className="app-tour-skip" onClick={skipTour}>
              Skip
            </button>
          </div>
        </div>
        {step.featured ? (
          <span className="app-tour-featured-badge">Key feature — Auto Join &amp; Video Rec</span>
        ) : null}
        <div className="app-tour-card-title">{step.title}</div>
        <p className="app-tour-card-body">{step.body}</p>
        {!step.target ? null : !targetRect ? (
          <p className="app-tour-card-hint">Tip: click Next — we will navigate to the right screen if needed.</p>
        ) : null}
        <div className="app-tour-card-actions">
          <button
            type="button"
            className="app-tour-btn app-tour-btn--ghost"
            disabled={isFirst}
            onClick={() => onStepIndex?.(Math.max(0, stepIndex - 1))}
          >
            Previous
          </button>
          <div className="app-tour-card-actions-right">
            {isLast ? (
              <button type="button" className="app-tour-btn app-tour-btn--primary" onClick={finishTour}>
                Finish
              </button>
            ) : (
              <button
                type="button"
                className="app-tour-btn app-tour-btn--primary"
                onClick={() => onStepIndex?.(stepIndex + 1)}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
   HOW IT WORKS
════════════════════════════════════════════════════════════════ */
function HowToPage({ onStartAppTour }){
  const [activeMode,setActiveMode]=useState("instant");
  const { containerRef: howtoModeTabsRef, registerTab: registerHowtoModeTab, thumbStyle: howtoModeThumbStyle } =
    useSlidingTabIndicator(activeMode);
  const howtoModeOptions = [
    { id: "instant", icon: IC.zap, label: "Instant Meeting", sub: "Paste link, record now", color: "#1565C0" },
    { id: "scheduled", icon: IC.cal, label: "Scheduled Meeting", sub: "Toggle on · Admit Companion", color: "#1565C0" },
  ];
  const howtoHeroArt = `${process.env.PUBLIC_URL || ""}/assets/ai_meeting_green_tech.png`;
  const appTabs = [
    {
      icon: IC.layoutDashboard,
      label: "Dashboard",
      desc: "Your dashboard — today’s meetings, live sessions, recent completions, and quick stats for the week.",
    },
    {
      icon: IC.cal,
      label: "Upcoming meetings",
      desc: "Default landing — configure upcoming Google Calendar meetings so MeetAI joins automatically.",
    },
    {
      icon: IC.mic,
      label: "Recorded Meetings",
      desc: "Completed meetings with transcript data, summaries, recordings, and recap email status.",
    },
    {
      icon: IC.cal,
      label: "Calendar",
      desc: `Month, week, and day views of Google Calendar events. Open any event or toggle ${JOIN_MEET_AI_LABEL} from the calendar.`,
    },
    {
      icon: IC.video,
      label: "All Meetings",
      desc: "Full calendar list — search, filter by day or status, and switch Grid or List layout.",
    },
    {
      icon: IC.task,
      label: "My Tasks",
      desc: "All tasks captured across your meetings — Pending, In Process, Blocked, and Done — with links back to each meeting.",
    },
  ];
  const meetingsSubTabs = [
    {
      icon: IC.cal,
      label: "Upcoming meetings",
      color: "#0875E1",
      summary: "Configure upcoming Google Calendar meetings so " + BOT_DISPLAY_NAME + " joins automatically.",
      bullets: [
        "Shows only upcoming Google Calendar events (today if still live/upcoming, then tomorrow through the next 30 days). Past or ended meetings are not listed here.",
        `Use the global ${JOIN_MEET_AI_LABEL} switch at the top, or turn it on per meeting on each card (green toggle).`,
        `Optional Video Rec: when you turn ${JOIN_MEET_AI_LABEL} on, check “Also enable Video Rec” to save a recording and generate a screen-share summary.`,
        "Click a card to open a join-focused view with the meeting link — full transcript and tasks appear under Recorded Meetings after the meeting ends.",
      ],
    },
    {
      icon: IC.mic,
      label: "Recorded Meetings",
      color: "#0875E1",
      summary: "Every completed meeting where speech was captured — summaries, recordings, and recap email status.",
      bullets: [
        "Lists meetings that have transcript data, newest first, grouped by Today / This Week / Last Week, etc.",
        "Meeting Summary combines what was said in the room plus key points from screen share (when Video Rec was on).",
        "Open a meeting to see tasks, joined vs not-joined attendance, transcript download, and recording download when available.",
        "Recap email banner shows whether the summary was emailed, how many recipients, and why if it was not sent.",
      ],
    },
    {
      icon: IC.video,
      label: "All Meetings",
      color: "#0875E1",
      summary: "The full calendar list — browse, search, and filter every meeting in the app window.",
      bullets: [
        "Recent past through the next 30 days in one place (yesterday’s meetings stay visible).",
        "Filter by day (Today, Tomorrow, custom date), status (All, Upcoming, Done), or search by title.",
        "Switch Grid or List layout anytime; both use the same week-based section headers.",
        `Per-meeting ${JOIN_MEET_AI_LABEL} toggles appear on upcoming Google Calendar rows (Video Rec is offered when you turn ${JOIN_MEET_AI_LABEL} on).`,
      ],
    },
  ];
  const scheduled=[
    {num:"01",title:"Schedule your meeting",desc:"Create a Google Meet, Zoom, or Microsoft Teams meeting in Google Calendar (or Outlook synced to Google).",icon:IC.cal,color:"#1565C0"},
    {num:"02",title:`Turn on ${JOIN_MEET_AI_LABEL} toggle`,desc:`Open the Upcoming meetings tab. Use the green toggle on that meeting card (or the global switch at the top) so ${BOT_DISPLAY_NAME} can join at the meeting start time.`,icon:IC.zap,color:"#0C8F55"},
    {num:"03",title:"Optional: enable Video Rec",desc:`When you turn ${JOIN_MEET_AI_LABEL} on, you can also enable Video Rec to save a recording and screen-share summary.`,icon:IC.video,color:"#1565C0"},
    {num:"04",title:"Admit in the meeting",desc:`When the meeting starts, ${BOT_DISPLAY_NAME} requests to join. In the meeting (Google Meet / Zoom / Teams), click Admit — then ${BOT_DISPLAY_NAME} enters the call.`,icon:IC.meet,color:"#1565C0"},
    {num:"05",title:"Real-time transcription",desc:`Once admitted, ${BOT_DISPLAY_NAME} transcribes with speaker labels while the meeting is in progress.`,icon:IC.mic,color:"#1565C0"},
    {num:"06",title:"View summary & action items",desc:"When the meeting ends, open Recorded Meetings for the Meeting Summary and tasks. Recap emails go to @avaada.com participants on the invite.",icon:IC.mail,color:"#1565C0"},
  ];
  const instant=[
    {num:"01",title:"Start or join an instant meeting",desc:"Open any Google Meet, Zoom, or Microsoft Teams meeting that was started spontaneously — no calendar invite.",icon:IC.video,color:"#1565C0"},
    {num:"02",title:"Copy the meeting link",desc:"Copy the meeting URL from your browser address bar or from the meeting invite that was shared via chat/Slack.",icon:IC.copy,color:"#1565C0"},
    {num:"03",title:`Click "${RECORD_MEETING_CTA}" in the app`,desc:`Use the ${RECORD_MEETING_CTA} button (lightning icon) in the top bar. Paste the meeting link and add participant emails — ${BOT_DISPLAY_NAME} joins automatically.`,icon:IC.zap,color:"#1565C0"},
    {num:"04",title:`${BOT_DISPLAY_NAME} joins within seconds`,desc:`${BOT_DISPLAY_NAME} joins your meeting immediately as a participant. Participants will see "${BOT_DISPLAY_NAME}" in the meeting.`,icon:IC.mic,color:"#1565C0"},
    {num:"05",title:"Transcription starts automatically",desc:`All speech is captured with speaker identification from the moment ${BOT_DISPLAY_NAME} joins.`,icon:IC.mic,color:"#1565C0"},
    {num:"06",title:"Transcript & actions delivered",desc:"When the meeting ends, the summary and action items are saved to your dashboard and emailed to specified recipients.",icon:IC.mail,color:"#1565C0"},
  ];
  const steps = activeMode==="scheduled" ? scheduled : instant;

  return (
    <div className="page app-page-shell">
      <div className="content-well howto-page">
        <div className="howto-stack">
          <section className="howto-hero" aria-labelledby="howto-hero-title">
            <div className="howto-hero-copy">
              <p className="howto-hero-eyebrow">HOW AVAADA MEET COMPANION WORKS</p>
              <h1 id="howto-hero-title" className="howto-hero-title">
                How Avaada Meet Companion works
              </h1>
              <p className="howto-hero-sub">
                Scheduled meetings via calendar (with Invite MeetAI), instant recordings from a link, and three views under Meetings to manage everything.
              </p>
            </div>
            <div className="howto-hero-art" aria-hidden>
              <img
                src={howtoHeroArt}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </div>
          </section>

          <section className="howto-tour" aria-label="App tour">
            <div className="howto-tour-left">
              <span className="howto-tour-play" aria-hidden>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d={IC.playFill} fill="#FFFFFF" />
                </svg>
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="howto-tour-title">
                  Full app walkthrough (navigation, Instant Join, My Tasks, and more)
                </p>
                <p className="howto-tour-sub">
                  <strong>{JOIN_MEET_AI_LABEL} &amp; Video Rec</strong> highlighted. Runs once on first login — replay anytime with Previous, Next, or Skip.
                </p>
              </div>
            </div>
            <button type="button" className="howto-tour-btn" onClick={() => onStartAppTour?.()}>
              Start App Tour
              <Svg d={IC.chevR} size={14} color="#FFFFFF" stroke={2} />
            </button>
          </section>

          <section className="howto-section" aria-labelledby="howto-app-tabs-title">
            <h2 id="howto-app-tabs-title" className="howto-section-title">App tabs</h2>
            <p className="howto-section-sub">
              Use the left sidebar to move between sections (Dashboard, Upcoming meetings, All Meetings, Recorded Meetings, Calendar, My Tasks, and more).
            </p>
            <div className="howto-tabs-grid">
              {appTabs.map((tab) => (
                <div key={tab.label} className="howto-tab-card">
                  <span className="howto-tab-ico" aria-hidden>
                    <Svg d={tab.icon} color="#0875E1" size={16} stroke={2} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="howto-tab-title">{tab.label}</div>
                    <p className="howto-tab-desc">{tab.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="howto-section" aria-labelledby="howto-meetings-title">
            <h2 id="howto-meetings-title" className="howto-section-title">Meetings</h2>
            <p className="howto-section-sub">
              Inside <strong>Meetings</strong>, pick a pill tab under the header. Each tab is a different lens on your calendar and recordings.
            </p>
            <div className="howto-meet-grid">
              {meetingsSubTabs.map((row) => (
                <article key={row.label} className="howto-meet-card">
                  <span className="howto-meet-ico" aria-hidden>
                    <Svg d={row.icon} color={row.color} size={17} stroke={2} />
                  </span>
                  <h3 className="howto-meet-title">{row.label}</h3>
                  <p className="howto-meet-summary">{row.summary}</p>
                  <ul className="howto-meet-list">
                    {row.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="howto-tip">
              <strong>Video Rec tip:</strong> When turning on Auto Join, check <strong>Also enable Video Rec</strong> to record. Screen-share highlights then merge into <strong>Meeting Summary</strong> under Recorded Meetings.
            </p>
          </section>

          <section className="howto-section" aria-label="Instant and scheduled guides">
            <div
              ref={howtoModeTabsRef}
              className="howto-mode-seg sliding-tabs-host"
              role="tablist"
              aria-label="Meeting type"
              style={{ marginBottom: 14 }}
            >
              <SlidingTabThumb style={howtoModeThumbStyle} className="sliding-tab-thumb sliding-tab-thumb--howto" />
              {howtoModeOptions.map((m) => {
                const active = activeMode === m.id;
                return (
                  <button
                    key={m.id}
                    ref={registerHowtoModeTab(m.id)}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`howto-mode-tab sliding-tab-btn${active ? " active" : ""}`}
                    onClick={() => setActiveMode(m.id)}
                  >
                    <div
                      className="howto-mode-tab-icon"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                        background: active ? "rgba(255,255,255,0.2)" : "#EAF3FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Svg d={m.icon} color={active ? "#FFFFFF" : "#0875E1"} size={17} />
                    </div>
                    <div>
                      <div className="howto-mode-tab-title">{m.label}</div>
                      <div className="howto-mode-tab-sub">{m.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div key={activeMode} className="howto-steps-slide">
              {activeMode === "instant" ? (
                <div className="howto-try-banner">
                  <strong>How to try it:</strong> Use the{" "}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 700 }}>
                    <Svg d={IC.zap} color="#0875E1" size={14} stroke={2} />
                    {RECORD_MEETING_CTA}
                  </span>{" "}
                  button in the top right of the app to record any live meeting instantly.
                </div>
              ) : null}

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {steps.map((s, i) => (
                  <div key={i} className="card howto-step-card" style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          background: "#EAF3FF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Svg d={s.icon} color="#0875E1" size={18} />
                      </div>
                      {i < steps.length - 1 ? (
                        <div style={{ width: 1, height: 14, background: "#DCE6EF", marginTop: 5 }} />
                      ) : null}
                    </div>
                    <div style={{ flex: 1, paddingTop: 2, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#0875E1",
                            background: "#EAF3FF",
                            padding: "2px 7px",
                            borderRadius: 5,
                            letterSpacing: 0.3,
                          }}
                        >
                          STEP {s.num}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: "#0B2A4A" }}>{s.title}</span>
                      </div>
                      <p style={{ fontSize: 13, color: "#5F7690", lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="howto-section" aria-labelledby="howto-platforms-title">
            <h2 id="howto-platforms-title" className="howto-section-title" style={{ fontSize: 16 }}>
              Supported Platforms
            </h2>
            <div className="howto-platforms" style={{ marginTop: 12 }}>
              {Object.entries(PLATFORM_META).map(([k, v]) => (
                <div key={k} className="howto-platform-chip">
                  <div className="howto-platform-icon" aria-hidden>
                    <MeetingListPlatformIcon platform={k} size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#0B2A4A" }}>{v.label}</div>
                    <div style={{ fontSize: 10, color: "#71869B" }}>Instant + Scheduled</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
